// OMNI v11.6 BUILD — 30 Apr 2026
// v11.6 — Site-default sweep + Julie multi-site fix + Add Staff/Add Login copy
//   • fallbackSite() helper replaces 23 hardcoded "sale" runtime fallbacks
//   • Compliance delegation now respects multiSite[] (Julie at Timperley)
//   • Add Staff vs Add Login Only — modal copy now distinguishes the two flows
// v11.5 — Multi-tenant magic links, cinematic six-beat, My Hours tab,
//         User Accounts SITE_ORDER fix, dashboard flash, top-center toast.
import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";

/* ═══════════════════════════════════════════════════
   PULSEROTA v8.3.0
   Built by Conor O'Neill · © 2026 All rights reserved

   v8.3.0 — True Multi-Tenancy
   + Subdomain routing: dco.omni-rota.com → practice_id = "dco"
   + Practices table in Supabase — name, logo, color per tenant
   + Login page branded per practice (name + logo loaded before auth)
   + SITES built from state.practiceSites (Penny-populated) not hardcoded
   + NURSES_BASE / CLINICIANS_BASE / RECS_BASE empty for non-DCO tenants
   + Removed all PRACTICE_ID === "dco" hardcoded checks
   + New practice joins → create row in practices table → subdomain live

   v8.2.0 — Hours & Contracts, Find a Slot, Surgery Planner,
   Holiday Report, Free Sessions search, CPD↔Events sync,
   archived staff fix (76 locations), partner 42d entitlement,
   leave year filter on all displays, emergency PIN removed

   ── HOW A NEW PRACTICE JOINS ─────────────────────────
   1. Create row in Supabase `practices` table (slug, name, logo_url)
   2. Add subdomain in Vercel: theirpractice.omni-rota.com
   3. Add CNAME in Namecheap: *.omni-rota.com → cname.vercel-dns.com
      (one-time DNS setup — covers all future subdomains)
   4. Practice hits their URL → branded login → Penny onboards them
   ─────────────────────────────────────────────────────

   ── SUPABASE TABLES REQUIRED ─────────────────────────
   practices: id, slug, name, logo_url, primary_color, active, created_at
   pulserota_data: practice_id, key, value, updated_at  (already exists)
   passkeys: (already exists)
   ─────────────────────────────────────────────────────
═══════════════════════════════════════════════════ */

const VERSION = "11.6";

/* ── AUTHORSHIP ── */
const META = {
  author:  "Conor O'Neill",
  product: "Omni",
  created: "2025",
  version: "8.3.0",
  rights:  "© Conor O'Neill 2026. All rights reserved.",
};

// ── Supabase ──────────────────────────────────────────────────────────────────
const SB_URL = "https://ditrrhclhckomjmdtyfm.supabase.co";
const SB_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImRpdHJyaGNsaGNrb21qbWR0eWZtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzMzNTY0NzAsImV4cCI6MjA4ODkzMjQ3MH0.BEc6t9c6aGkb2XQ8C60vH0satwxiDM3l757RjRVrRKY";

/* ── MULTI-TENANCY — resolve practice from subdomain only ── */
// SECURITY: bare omni-rota.com returns null → blank wall, no data exposed
function resolvePracticeId() {
  try {
    const host = window.location.hostname;

    // localhost / 127.0.0.1 — dev only, never reaches production
    if (host === "localhost" || host === "127.0.0.1") {
      const p = new URLSearchParams(window.location.search).get("practice");
      if (p && /^[a-z0-9_-]{2,32}$/.test(p)) return p;
      return "dco"; // dev fallback — safe because this is local only
    }

    // Production: ONLY accept a valid subdomain — dco.omni-rota.com → "dco"
    const subdomainMatch = host.match(/^([a-z0-9_-]{2,32})\.omni-rota\.com$/);
    if (subdomainMatch) {
      const slug = subdomainMatch[1];
      if (slug !== "www" && slug !== "pulse" && slug !== "app") {
        localStorage.setItem("pr_pid", slug);
        return slug;
      }
    }

    // No valid subdomain — return null → app renders blank wall
    return null;
  } catch(e) { return null; }
}
const PRACTICE_ID = resolvePracticeId();
const IS_DCO = PRACTICE_ID === "dco"; // used only for data seeding
const STORAGE_KEY = `omnirota_v5_${PRACTICE_ID}`;
const API_KEY_STORAGE = `pr_api_key_${PRACTICE_ID}`;
const getStoredApiKey = () => { try { return localStorage.getItem(API_KEY_STORAGE)||""; } catch(e) { return ""; } };
const setStoredApiKey = (k) => { try { localStorage.setItem(API_KEY_STORAGE, k); } catch(e) {} };

/* ── DATA PROXY — all pulserota_data calls go through /api/db ────────────────
   Service key lives in Vercel env vars, never in the browser.
   SB_KEY below is the anon key — kept only for Supabase Auth (passkeys, OAuth).
   ─────────────────────────────────────────────────────────────────────────── */
const DB_PROXY = "/api/db";

/* ── PRACTICE BRANDING — via proxy ── */
let PRACTICE_CONFIG = { name: "Omni", logo_url: null, primary_color: "#3aad98" };
async function loadPracticeConfig() {
  try {
    const r = await fetch(`${DB_PROXY}?table=practices`);
    if (r.ok) {
      const json = await r.json();
      if (json?.data) PRACTICE_CONFIG = { ...PRACTICE_CONFIG, ...json.data };
    }
  } catch(e) {
    console.warn("Could not load practice config — using defaults", e);
  }
}
// Fire immediately, non-blocking — login page reads PRACTICE_CONFIG when it renders
loadPracticeConfig();

async function sbSet(key, value) {
  try {
    const r = await fetch(DB_PROXY, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key, value }),
    });
    if (!r.ok) {
      const err = await r.text();
      console.warn("sbSet failed", key, r.status, err);
    }
  } catch(e) { console.warn("sbSet network error", key, e); }
}

async function sbGetAll() {
  try {
    const r = await fetch(DB_PROXY);
    if (!r.ok) {
      console.warn("sbGetAll failed", r.status, await r.text());
      return null;
    }
    const json = await r.json();
    if (!json?.data) { console.warn("sbGetAll unexpected response", json); return null; }
    console.log(`✅ Supabase loaded ${json.count} keys`);
    return json.data;
  } catch(e) { console.warn("sbGetAll network error", e); return null; }
}

// ── SUPABASE AUTH — uses @supabase/supabase-js loaded from CDN in index.html ──
// window.supabase is available after the CDN script loads
function getSB() {
  if (window._sbClient) return window._sbClient;
  if (!window.supabase) { console.warn("Supabase JS not loaded yet"); return null; }
  window._sbClient = window.supabase.createClient(SB_URL, SB_KEY);
  return window._sbClient;
}

async function sbAuthOAuth(provider) {
  const sb = getSB();
  if (!sb) return;
  await sb.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: window.location.origin + window.location.pathname,
    },
  });
}

async function sbAuthEmail(email, password) {
  const sb = getSB();
  if (!sb) throw new Error("Auth not available");
  const { data, error } = await sb.auth.signInWithPassword({ email, password });
  if (error) throw new Error(error.message);
  return data;
}

async function sbAuthResetPassword(email) {
  const sb = getSB();
  if (!sb) return;
  await sb.auth.resetPasswordForEmail(email, {
    redirectTo: window.location.origin,
  });
}

async function sbAuthMagicLink(email, redirectTo) {
  const sb = getSB();
  if (!sb) throw new Error("Auth not available");
  const { error } = await sb.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: redirectTo || window.location.href },
  });
  if (error) throw new Error(error.message);
}

async function sbAuthGetSession() {
  const sb = getSB();
  if (!sb) return null;
  const { data } = await sb.auth.getSession();
  return data?.session || null;
}

async function sbAuthSignOut() {
  const sb = getSB();
  if (sb) await sb.auth.signOut();
  localStorage.removeItem("pr_auth_token");
  localStorage.removeItem("pr_refresh_token");
}

async function sbAuthSetPassword(password) {
  const sb = getSB();
  if (!sb) throw new Error("Auth not available");
  const { error } = await sb.auth.updateUser({ password });
  if (error) throw new Error(error.message);
}

// Match a Supabase email to a Omni Rota user record
function matchEmailToUser(email, state) {
  if (!email) return null;
  const users = resolveUsers(state);
  const low = email.toLowerCase();
  return users.find(u => u.email && u.email.toLowerCase() === low) || null;
}

// One-shot connection check — runs once on first load
async function sbEnsureTable() {
  try {
    const r = await fetch(`${DB_PROXY}?ping=1`);
    const json = await r.json();
    return json?.ok === true;
  } catch(e) { return false; }
}


// Leave year: Jan 1 – Dec 31
const LEAVE_YEAR_START = "2026-01-01";
const LEAVE_YEAR_END   = "2026-12-31";

/* ── DESIGN TOKENS — warm clinical light mode ── */
const C = {
  bg:        "#100f1a",
  surface:   "#181626",
  card:      "#181626",
  border:    "rgba(255,255,255,0.07)",
  border2:   "rgba(255,255,255,0.04)",
  muted:     "rgba(255,255,255,0.18)",
  dim:       "rgba(255,255,255,0.35)",
  mid:       "rgba(255,255,255,0.52)",
  soft:      "rgba(255,255,255,0.7)",
  text:      "rgba(255,255,255,0.82)",
  heading:   "rgba(255,255,255,0.95)",
  teal:      "#3aad98",
  tealLight: "#00e5a0",
  tealPale:  "rgba(0,229,160,0.08)",
  tealBorder:"rgba(0,229,160,0.2)",
  sale:      "#2563eb",
  brook:     "#7c3aed",
  timp:      "#059669",
  green:     "#059669",
  warn:      "#f5c518",
  danger:    "#ff4646",
  lime:      "#65a30d",
  grad1:     "#5bc4d4",
  grad2:     "#a78bfa",
  grad3:     "#f472b6",
  navGrad:   "#181626",
  cardGrad:  "#181626",
};

/* ── NOTIFICATION SYSTEM ─────────────────────────────────────────────
   Dual delivery: local PWA push + Resend email via Vercel function
   T1 → site head nurse + manager
   T2 → site manager + principal
   T3 → principal only
──────────────────────────────────────────────────────────────────── */
function showLocalNotification(title, body) {
  try {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    navigator.serviceWorker?.ready.then(reg => {
      reg.showNotification(title, { body, icon:"/logo192.png", badge:"/logo192.png", vibrate:[200,100,200] });
    }).catch(() => { try { new Notification(title, {body}); } catch(e) {} });
  } catch(e) { try { new Notification(title, {body}); } catch(e) {} }
}

async function sendIssueEmail({tier, category, site, reporterName, anonymous, recipientEmails}) {
  if (!recipientEmails?.length) return;
  const siteName = SITES[site]?.name||site;
  const tierLabel = tier===1?"T1 Operational":tier===2?"T2 Concern":"T3 Serious";
  const subject = `[Pulse] ${tierLabel} issue — ${siteName}`;
  const reviewUrl = PRACTICE_ID ? `https://${PRACTICE_ID}.omni-rota.com` : "https://omni-rota.com";
  const text = `A ${tierLabel} issue has been reported at ${siteName}.\nCategory: ${category}\n${anonymous?"Reporter: Anonymous":reporterName?`Reporter: ${reporterName}`:""}\n\nLog in to review: ${reviewUrl}\n\n— Pulse`;
  try {
    await fetch("/api/send-issue-notification", {
      method:"POST",
      headers:{"Content-Type":"application/json"},
      body: JSON.stringify({to: recipientEmails, subject, text}),
    });
  } catch(e) { console.warn("Notification email failed:", e); }
}

function getIssueRecipientEmails(tier, site, state) {
  const users = state.users||[];
  const nurses = state.nurses||[];
  const principal = users.find(u=>u.role==="principal");
  const managers = users.filter(u=>u.role==="manager"&&(u.site===site||u.site==="all"));
  const hnStaff = nurses.find(n=>n.site===site&&(n.headNurse||(state.staffRoles||{})[n.id]==="headNurse"));
  const hnUser = hnStaff ? users.find(u=>u.staffId===hnStaff.id) : null;
  const emails = [];
  if (tier===1) {
    if (hnUser?.email) emails.push(hnUser.email);
    managers.forEach(m=>{ if (m.email) emails.push(m.email); });
  } else if (tier===2) {
    managers.forEach(m=>{ if (m.email) emails.push(m.email); });
    if (principal?.email) emails.push(principal.email);
  } else if (tier===3) {
    if (principal?.email) emails.push(principal.email);
  }
  return [...new Set(emails)].filter(Boolean);
}

function getIssueRecipients(tier, site, state) {
  const users = state.users||[];
  const nurses = state.nurses||[];
  const principal = users.find(u=>u.role==="principal");
  const managers = users.filter(u=>u.role==="manager"&&(u.site===site||u.site==="all"));
  const hnStaff = nurses.find(n=>n.site===site&&(n.headNurse||(state.staffRoles||{})[n.id]==="headNurse"));
  const hnUser = hnStaff ? users.find(u=>u.staffId===hnStaff.id) : null;
  if (tier===1) return [...new Set([hnUser?.name, ...managers.map(m=>m.name)].filter(Boolean))];
  if (tier===2) return [...new Set([...managers.map(m=>m.name), principal?.name].filter(Boolean))];
  if (tier===3) return [principal?.name].filter(Boolean);
  return [];
}

// ── SITES — built from state.practiceSites (Penny-populated) ──────────────────
// DCO hardcoded as fallback only. All other tenants get sites from Penny onboarding.
const DCO_SITES = {
  sale:       { name:"Sale",       color:"#818cf8", surgeries:5 },
  brooklands: { name:"Brooklands", color:"#a78bfa", surgeries:4 },
  timperley:  { name:"Timperley",  color:"#00e5a0", surgeries:4 },
};

// Build SITES object from state — called after state loads
function buildSites(state) {
  const ps = state?.practiceSites;
  if (ps && Array.isArray(ps) && ps.length > 0) {
    // Penny/Iris format: [{id:"sale",name:"Sale",surgeries:5,color:"#818cf8"}]
    const obj = {};
    ps.forEach(s => {
      obj[s.id] = {
        name: s.name,
        color: s.color || "#3aad98",
        surgeries: s.surgeries || 4,
      };
    });
    return obj;
  }
  // Fallback: infer sites from customClinicians if practiceSites not set
  // This recovers gracefully when onboarding basics phase was incomplete
  if (!IS_DCO && state?.customClinicians?.length > 0) {
    const obj = {};
    const siteIds = [...new Set(
      state.customClinicians
        .filter(c=>!c._deleted && c.site && c.site.length > 0)
        .map(c=>c.site)
    )];
    siteIds.forEach(sid => {
      obj[sid] = {
        name: state.practiceProfile?.practiceName || sid,
        color: "#3aad98",
        surgeries: 4,
      };
    });
    if (Object.keys(obj).length > 0) return obj;
  }
  // DCO fallback — only used for practice_id "dco" before state loads
  return IS_DCO ? DCO_SITES : {};
}

// SITES starts as DCO_SITES for DCO, empty for others — updated after state loads
let SITES = IS_DCO ? DCO_SITES : {};

// ── fallbackSite — tenant-aware default site picker ──────────────────────────
// Use this anywhere the code needs a default site key. Prefers the user's own
// site, falls back to this tenant's first known site, never to a DCO-specific
// hardcoded value. DCO is a tenant, not the default. Replaces ~23 places that
// previously fell back to literal "sale".
//   • fallbackSite(user)               — user's site or first tenant site
//   • fallbackSite({site: someSiteId}) — works for any object with .site
//   • fallbackSite(null)               — first tenant site or "main"
function fallbackSite(u) {
  return (u && u.site) || Object.keys(SITES)[0] || "main";
}

// Surgery rooms — derived from SITES
const SURGERY_ROOMS = new Proxy({}, {
  get(_, siteId) {
    return { count: SITES[siteId]?.surgeries || 4 };
  }
});


const STATUS = {
  newStarter:{ label:"New Starter",     color:"rgba(255,255,255,0.4)", score:0,   shadowing:true  },
  trainee:   { label:"Trainee",         color:"#c2410c", score:0.3, shadowing:true  },
  developing:{ label:"Developing",      color:"#d97706", score:0.6, shadowing:false },
  nearQual:  { label:"Near Qualifying", color:"#818cf8", score:0.8, shadowing:false },
  qualified: { label:"Qualified",       color:"#00e5a0", score:1.0, shadowing:false },
};

// Overtime / early finish / lateness reasons
const OT_REASONS     = ["Dentist ran late","Emergency patient","Equipment issue","Decontamination overrun","Staff cover","Other"];
const EF_REASONS     = ["Personal emergency","Felt unwell","Dentist finished early","Appointment","Other"];
const LATE_REASONS = [
  "Traffic / transport delay",
  "Childcare issue",
  "Medical / dental appointment",
  "Personal emergency",
  "Overslept",
  "Public transport failure",
  "No reason given",
  "Other",
];
const UNAUTH_REASONS = ["No call no show","Left without authorisation","Arrived significantly late","Other"];
const ABSENCE_REASONS = [
  {id:"illness",      label:"Illness (unspecified)"},
  {id:"stress",       label:"Stress / anxiety"},
  {id:"mentalHealth", label:"Mental health"},
  {id:"msk",          label:"Musculoskeletal"},
  {id:"childcare",    label:"Childcare / family"},
  {id:"recurring",    label:"Recurring condition"},
  {id:"medical",      label:"Medical appointment"},
  {id:"leftEarly",    label:"Left early — unwell"},
  {id:"surgery",      label:"Planned surgery"},
  {id:"other",        label:"Other"},
];

const BF_BANDS = [
  { min:0,   max:44,   label:"Low",      color:"#00e5a0" },
  { min:45,  max:99,   label:"Caution",  color:"#d97706" },
  { min:100, max:199,  label:"High",     color:"#c2410c" },
  { min:200, max:9999, label:"Critical", color:"#ff4646" },
];

const TASK_TYPES = [
  "Decontamination","Stockroom check","Reception cover",
  "Sterilisation","Training review","Instrument audit",
  "Deep clean","Admin support",
  "Fire alarm test","Emergency drugs check","Step wedge test",
];

/* ── Receptionist training status (Feature 6) ── */
const REC_STATUS = {
  trainee:   { label:"Trainee",       color:"#c2410c", score:0.4 },
  learning:  { label:"Learning",      color:"#d97706", score:0.7 },
  competent: { label:"Competent",     color:"#00e5a0", score:1.0 },
};

/* ── Gold for Unsung Heroes ── */
const C_GOLD = "#f59e0b";

const CPD_TYPES = [
  {id:"training",      label:"Training / CPD",    icon:"book",        color:"#00e5a0"},
  {id:"cpr",           label:"CPR",               icon:"book",        color:"#ff4646"},
  {id:"practice_mtg",  label:"Practice Meeting",  icon:"clipboard",   color:"#818cf8"},
  {id:"coaching",      label:"Business Coaching", icon:"lightbulb",   color:"#a78bfa"},
  {id:"safeguarding",  label:"Safeguarding",       icon:"shield",     color:"#00e5a0"},
  {id:"radiography",   label:"Radiography",        icon:"book",       color:"#d97706"},
  {id:"appraisal",     label:"Appraisal",          icon:"chat",       color:"#22d3ee"},
  {id:"induction",     label:"Induction",          icon:"book",       color:"#65a30d"},
  {id:"other",         label:"Other CPD",          icon:"book",       color:"rgba(255,255,255,0.4)"},
];

/* ── COMPLIANCE TASKS ── */
const COMPLIANCE_TASKS = [
  {
    id: "fire_alarm",
    label: "Fire Alarm Test",
    icon: "fire",
    color: "#dc2626",
    freq: "weekly",
    freqLabel: "Weekly",
    roles: ["headNurse","manager","principal"],
    description: "Test all fire alarm call points and update fire logbook",
    reg: "Regulatory Reform (Fire Safety) Order 2005 -- CQC expects weekly call point tests and a fire logbook (Mythbuster 21, Reg 15)",
  },
  {
    id: "emergency_drugs",
    label: "Emergency Drugs Check",
    icon: "bandage",
    color: "#d97706",
    freq: "weekly",
    freqLabel: "Weekly",
    roles: ["headNurse","manager","principal"],
    description: "Check emergency drug kit -- expiry dates, quantities, kit seal intact",
    reg: "CQC Reg 12 (Safe Care) + Reg 17 (Governance) -- Mythbuster 4. GDC Standards require all staff trained for medical emergencies with up-to-date drugs.",
  },
  {
    id: "step_wedge",
    label: "Step Wedge Test",
    icon: "radiation",
    color: "#7c3aed",
    freq: "monthly",
    freqLabel: "Monthly",
    roles: ["headNurse","manager","principal"],
    description: "Radiography QA -- step wedge test and reference image check per local rules",
    reg: "Ionising Radiations Regulations 2017 (IRR 2017) + IR(ME)R 2017 require a QA programme. CQC Mythbuster 3 inspects under Reg 12 and Reg 15.",
  },
  {
    id: "legionella",
    label: "Legionella Check",
    icon: "droplet",
    color: "#0891b2",
    freq: "monthly",
    freqLabel: "Monthly",
    roles: ["headNurse","manager","principal"],
    description: "Waterline temperature check, flushing log, and visual inspection per control scheme",
    reg: "Health and Safety at Work Act 1974 + HSE ACOP L8. CQC Mythbuster 5 links to Reg 15 (Premises). Risk-based frequency -- monthly minimum for temperature checks.",
  },
  {
    id: "sharps_audit",
    label: "Sharps Audit",
    icon: "needle",
    color: "#059669",
    freq: "quarterly",
    freqLabel: "Quarterly",
    roles: ["headNurse","manager","principal"],
    description: "Audit sharps handling, disposal, and safer sharps compliance across all surgeries",
    reg: "CQC Reg 12 + Reg 15. Mythbuster 7 (Safer Sharps). Health and Safety (Sharp Instruments in Healthcare) Regulations 2013.",
  },
  {
    id: "coshh",
    label: "COSHH Review",
    icon: "flask",
    color: "#7c3aed",
    freq: "annual",
    freqLabel: "Annual",
    roles: ["headNurse","manager","principal"],
    description: "Review COSHH register, safety data sheets, and staff awareness for hazardous substances",
    reg: "Control of Substances Hazardous to Health Regulations 2002 (COSHH). CQC Mythbuster 41. Covers disinfectants, amalgam, nitrous oxide, X-ray chemicals.",
  },
];

function getComplianceDueDate(task, fromDate) {
  const d = new Date(fromDate || new Date());
  if (task.freq === "weekly")    { d.setDate(d.getDate() + 7); }
  else if (task.freq === "monthly")   { d.setMonth(d.getMonth() + 1); }
  else if (task.freq === "quarterly") { d.setMonth(d.getMonth() + 3); }
  else if (task.freq === "annual")    { d.setFullYear(d.getFullYear() + 1); }
  return d.toISOString().split("T")[0];
}

function getLastCompletion(state, taskId, siteId) {
  const logs = (state.complianceTasks||[]).filter(l=>l.taskId===taskId&&l.site===siteId);
  if (!logs.length) return null;
  return logs.sort((a,b)=>b.date.localeCompare(a.date))[0];
}

function isComplianceOverdue(task, lastDone) {
  if (!lastDone) return true;
  const diffDays = Math.floor((Date.now() - new Date(lastDone.date).getTime()) / 86400000);
  if (task.freq === "weekly")    return diffDays >= 7;
  if (task.freq === "monthly")   return diffDays >= 28;
  if (task.freq === "quarterly") return diffDays >= 90;
  if (task.freq === "annual")    return diffDays >= 365;
  return diffDays >= 28;
}

function isComplianceDueSoon(task, lastDone) {
  if (!lastDone) return false;
  const diffDays = Math.floor((Date.now() - new Date(lastDone.date).getTime()) / 86400000);
  if (task.freq === "weekly")    return diffDays >= 5;
  if (task.freq === "monthly")   return diffDays >= 21;
  if (task.freq === "quarterly") return diffDays >= 75;
  if (task.freq === "annual")    return diffDays >= 330;
  return diffDays >= 21;
}

const EXTRA_QUALS = [
  {id:"xray",       label:"Radiography",    icon:"radiation",  color:"#d97706"},
  {id:"oralHyg",    label:"Oral Hygiene",   icon:"tooth",  color:"#00e5a0"},
  {id:"implants",   label:"Implants",       icon:"settings",  color:"#a78bfa"},
];

/* ── UK BANK HOLIDAYS 2026 ── */
const UK_BH_2026 = {
  "2026-01-01":"New Year's Day",
  "2026-04-03":"Good Friday",
  "2026-04-06":"Easter Monday",
  "2026-05-04":"Early May Bank Holiday",
  "2026-05-25":"Spring Bank Holiday",
  "2026-08-31":"Summer Bank Holiday",
  "2026-12-25":"Christmas Day",
  "2026-12-26":"Boxing Day",
  "2026-12-28":"Boxing Day (substitute)",
};

/* ── NURSES ── */
const NURSES_BASE = [
  {id:"n_hwatson",  name:"Hannah Watson",        site:"sale",       area:"MOS",    status:"qualified",ra:true, iv:true, hrs:37.5,pref:"c_matt",    headNurse:true, keyholder:true, firstAider:true, safeguardingL3:true, fireMarshal:true,  note:"Head Nurse Sale"},
  {id:"n_sblack",   name:"Sophie Black",          site:"sale",       area:"GDS",    status:"nearQual", ra:false,iv:false,hrs:37.5,pref:"c_conor"},
  {id:"n_obg",      name:"Olive Brookes Gardner", site:"sale",       area:"GDS",    status:"nearQual", ra:false,iv:false,hrs:37.5,pref:"c_sadiyah"},
  {id:"n_kgregory", name:"Kate Gregory",          site:"sale",       area:"GDS",    status:"qualified",ra:false,iv:false,hrs:37.5,pref:"c_mhealey",moving:"timperley"},
  {id:"n_shussain", name:"Safaa Hussain",          site:"sale",       area:"GDS",    status:"trainee",    ra:false,iv:false,hrs:37.5,pref:"c_beth",shadowingDone:0,shadowingRequired:4},
  {id:"n_mmoussaid",name:"Maryam Moussaid",        site:"sale",       area:"GDS",    status:"newStarter", ra:false,iv:false,hrs:37.5,shadowingDone:0,shadowingRequired:8},
  {id:"n_cdunne",   name:"Clare Dunne",            site:"sale",       area:"MOS",    status:"qualified",ra:true, iv:true, hrs:19.5,pref:"c_cwaith",noOT:true,flexible:true, firstAider:true},
  {id:"n_sriley",   name:"Sarah Riley",            site:"sale",       area:"MOS",    status:"qualified",ra:true, iv:true, hrs:12.5,pref:"c_cwaith",noOT:true,flexible:true},
  {id:"n_cgrimwood",name:"Caitlin Grimwood",        site:"sale",       area:"MOS",    status:"qualified",ra:false,iv:false,hrs:37.5,noFri:true,actingManager:true, safeguardingL3:true, note:"Acting MOS Manager (Gemma + Alysha on maternity)"},
  {id:"n_ellie",    name:"Ellie Wilkinson",        site:"sale",       area:"GDS",    status:"developing", ra:false,iv:false,hrs:37.5},
  {id:"n_gchalcroft",name:"Gemma Chalcroft",       site:"sale",       area:"MOS",    status:"qualified",ra:false,iv:false,hrs:37.5,maternity:true,note:"MOS Manager · Maternity"},
  {id:"n_arourke",  name:"Alysha Rourke",          site:"sale",       area:"GDS",    status:"qualified",ra:false,iv:false,hrs:37.5,maternity:true,note:"Maternity"},
  {id:"n_chart",    name:"Christina Hart",         site:"brooklands", area:"MOS/GDS",status:"qualified",ra:true, iv:true, hrs:36.5,headNurse:true, keyholder:true, firstAider:true, safeguardingL3:true, fireMarshal:true, note:"Head Nurse Brooklands"},
  {id:"n_daniela",  name:"Daniela",                site:"brooklands", area:"MOS/GDS",status:"qualified",ra:false,iv:false,hrs:37.5,pref:"c_simon"},
  {id:"n_sinem",    name:"Sinem",                  site:"brooklands", area:"MOS/GDS",status:"qualified",ra:false,iv:false,hrs:12.5,flexible:true},
  {id:"n_jlea",     name:"Jenny Lea",              site:"brooklands", area:"MOS",    status:"qualified",ra:false,iv:true, hrs:14.5,noOT:true,flexible:true},
  {id:"n_ofrisby",  name:"Olivia Frisby",          site:"brooklands", area:"GDS",    status:"qualified",ra:false,iv:false,hrs:37.5},
  {id:"n_lbrennan", name:"Liv Brennan",            site:"timperley",  area:"GDS",    status:"qualified",ra:false,iv:false,hrs:37.5,headNurse:true, keyholder:true, firstAider:true, safeguardingL3:true, fireMarshal:true, note:"Head Nurse Timperley"},
  {id:"n_zfitton",  name:"Zara Fitton",            site:"timperley",  area:"GDS",    status:"qualified",ra:false,iv:false,hrs:10,  workDays:["Mon","Wed","Fri"],flexible:true},
  {id:"n_myoung",   name:"Maisie Young",           site:"timperley",  area:"GDS",    status:"nearQual", ra:false,iv:false,hrs:37.5},
  {id:"n_ayoung",   name:"Ava Young",              site:"timperley",  area:"GDS",    status:"qualified",ra:false,iv:false,hrs:37.5},
  {id:"n_tursa",    name:"Tyla Ursa",              site:"timperley",  area:"GDS",    status:"nearQual", ra:false,iv:false,hrs:37.5},
  {id:"n_ddrew",    name:"Darcey Drew",            site:"timperley",  area:"GDS",    status:"nearQual", ra:false,iv:false,hrs:37.5},
  {id:"n_cmccusker",name:"Caitlin McCusker",       site:"timperley",  area:"GDS",    status:"qualified",ra:false,iv:false,hrs:37.5,moving:"sale"},
];

/* ── CLINICIANS ── */
const CLINICIANS_BASE = [
  {id:"c_conor",  name:"Conor O'Neill", site:"sale",       type:"Partner/GDS",color:"#818cf8",sched:{Mon:"14:00-20:00",Wed:"08:00-14:00",Thu:"08:00-17:00",Fri:"08:00-16:30"},prefNurse:"n_sblack"},
  {id:"c_cwaith", name:"Chris Waith",   site:"sale",       type:"Partner/MOS",color:"#b45309",sched:{Mon:"08:00-18:30",Tue:"08:00-14:00",Wed:"08:00-18:30"},prefNurse:"n_cdunne"},
  {id:"c_mhealey",name:"Mark Healey",   site:"sale",       type:"GDS",        color:"#c2410c",sched:{Mon:"08:00-17:30",Tue:"08:00-17:30",Wed:"08:00-14:00"},prefNurse:"n_kgregory"},
  {id:"c_sadiyah",name:"Sadiyah Naeem", site:"sale",       type:"GDS",        color:"#a78bfa",sched:{Mon:"08:00-17:30",Tue:"08:00-17:30",Wed:"08:00-14:00"},prefNurse:"n_obg"},
  {id:"c_beth",   name:"Beth Hodgeson", site:"sale",       type:"GDS",        color:"#be185d",sched:{Mon:"08:00-18:00",Tue:"08:00-18:00",Thu:"08:00-18:00",Fri:"08:00-14:00"},prefNurse:"n_shussain",altWeekFri:true},
  {id:"c_mayghan",name:"Mayghan Mitra", site:"sale",       type:"GDS",        color:"#a78bfa",sched:{Tue:"14:00-20:00",Wed:"08:00-17:30",Thu:"08:00-17:30"},prefNurse:null},
  {id:"c_mollys", name:"Molly Crompton",site:"sale",       type:"Therapist",  color:"#3aad98",sched:{Mon:"08:00-14:00",Wed:"14:00-20:00",Thu:"08:00-17:30"},prefNurse:null},
  {id:"c_matt",   name:"Matt Dingle",   site:"sale",       type:"MOS",        color:"#f5c518",sched:{Tue:"08:00-14:00",Wed:"08:00-14:00",Thu:"08:00-14:00"},prefNurse:"n_hwatson"},
  {id:"c_katy",   name:"Katy Turner",   site:"sale",       type:"MOS",        color:"#9d174d",sched:{Mon:"14:00-20:00",Fri:"08:00-16:30"},prefNurse:"n_hwatson"},
  {id:"c_simon",  name:"Simon Cove",    site:"brooklands", type:"Partner/GDS",color:"#a78bfa",sched:{Mon:"09:00-15:30",Tue:"09:00-14:00",Wed:"08:00-17:30",Thu:"09:00-14:00",Fri:"09:00-14:00"},prefNurse:"n_daniela"},
  {id:"c_smera",  name:"Smera",         site:"brooklands", type:"FD",         color:"#a78bfa",sched:{Mon:"08:00-17:30",Tue:"08:00-17:30",Wed:"08:00-17:30",Thu:"08:00-17:30",Fri:"08:00-14:00"},prefNurse:null},
  {id:"c_jodie",  name:"Jodie",         site:"brooklands", type:"MOS",        color:"#ff4646",sched:{Mon:"08:00-14:30",Wed:"08:00-14:00"},prefNurse:null},
  {id:"c_louisa", name:"Louisa Hartley",site:"brooklands", type:"GDS",        color:"#818cf8",sched:{Mon:"14:00-20:00",Tue:"08:00-17:30"},prefNurse:null},
  {id:"c_mollyb", name:"Molly Barden",  site:"brooklands", type:"Therapist",  color:"#3aad98",sched:{Thu:"08:00-17:30",Fri:"08:00-14:00"},prefNurse:null},
  // Chris Lakin — every other Wednesday (altWeek:true). Week-number parity: even ISO weeks = working
  {id:"c_lakin",  name:"Chris Lakin",   site:"brooklands", type:"GDS",        color:"#60a5fa",sched:{Wed:"09:00-17:30"},prefNurse:null,altWeek:true,note:"Every other Wednesday"},
  {id:"c_markd",  name:"Mark Dale",     site:"timperley",  type:"Partner/GDS",color:"#00e5a0",sched:{Mon:"14:00-20:00",Tue:"08:00-17:30",Wed:"08:00-14:00",Thu:"08:00-17:30"},prefNurse:null},
  {id:"c_pete",   name:"Pete McDonald", site:"timperley",  type:"GDS",        color:"#00e5a0",sched:{Mon:"08:00-17:30",Tue:"14:00-20:00",Wed:"08:00-14:00",Thu:"14:00-20:00",Fri:"08:00-12:30"},prefNurse:null},
  {id:"c_izzy",   name:"Izzy",          site:"timperley",  type:"FD",         color:"#00e5a0",sched:{Mon:"08:00-17:30",Tue:"08:00-18:30",Wed:"14:00-20:00",Thu:"14:00-17:30",Fri:"08:00-16:30"},prefNurse:null},
  {id:"c_laura",  name:"Laura Rooney",  site:"timperley",  type:"GDS",        color:"#3aad98",sched:{Tue:"08:30-17:30",Wed:"08:30-17:30",Thu:"08:30-17:30",Fri:"08:30-16:30"},prefNurse:null},
  {id:"c_claret", name:"Clare",        site:"timperley",  type:"GDS", color:"#475569",sched:{Mon:"08:00-14:00",Tue:"08:00-14:00",Wed:"14:00-20:00",Fri:"08:00-16:00"},prefNurse:null,leaving:"2026-03-31"},
  {id:"c_will",   name:"Will Holmes",  site:"timperley",  type:"GDS", color:"#3aad98",sched:{Mon:"08:00-14:00",Tue:"08:00-14:00",Wed:"14:00-20:00",Fri:"08:00-16:00"},prefNurse:null,startDate:"2026-04-01"},
];

/* ── RECEPTIONISTS ── */
const RECS_BASE = [
  {id:"r_schalcroft", name:"Susan Chalcroft",  site:"sale",       hrs:25,   type:"Reception",noFri:true, sick:true, sickReturn:"2026-03-23", latestFinish:"17:00"},
  {id:"r_ahowell",    name:"Amanda Howell",    site:"sale",       hrs:37.5, type:"Reception"},
  {id:"r_jhargreaves",name:"Jade Hargreaves",  site:"sale",       hrs:37.5, type:"Reception"},
  {id:"r_cmurray",    name:"Chloe Murray",     site:"sale",       hrs:37.5, type:"Reception"},
  {id:"r_krennison",  name:"Karen Rennison",   site:"brooklands", hrs:37.5, type:"Reception", note:"Only dedicated receptionist — high risk"},
  {id:"r_etaylor",    name:"Elaine Taylor",    site:"timperley",  hrs:37.5, type:"Reception"},
  {id:"r_svesey",     name:"Sue Vesey",        site:"timperley",  hrs:25.25,type:"Reception"},
  {id:"r_mhoulker",   name:"Mel Houlker",      site:"timperley",  hrs:33,   type:"Reception"},
  {id:"r_ljohns",     name:"Leanne Johns",     site:"sale",       hrs:34,   type:"TCO",       note:"Cannot work past 3pm"},
  {id:"r_mhart",      name:"Marie Hart",       site:"brooklands", hrs:40.25,type:"TCO",       note:"TCO + reception backup"},
  {id:"r_mhampson",   name:"Megan Hampson",    site:"sale",       hrs:37.5, type:"Manager",   note:"Practice Manager · 08:00–16:00", manager:true},
  {id:"r_jwhelan",    name:"Julie Whelan",     site:"brooklands", hrs:37.5, type:"Manager",   note:"Practice Manager · Brooklands & Timperley · 08:00–16:00", manager:true, multiSite:["brooklands","timperley"]},
];

/* ── NON-DCO TENANTS: base arrays are empty — data comes from state/Supabase ──
   For DCO, the above arrays are the fallback while Supabase syncs.
   For any other practice, staff come entirely from Penny onboarding → state.
   ──────────────────────────────────────────────────────────────────────────── */
if (!IS_DCO) {
  // Clear hardcoded DCO data for other tenants
  NURSES_BASE.length = 0;
  CLINICIANS_BASE.length = 0;
  RECS_BASE.length = 0;
}

/* ── CALENDAR ── */
const BH = UK_BH_2026; // full 2026 UK bank holidays — see UK_BH_2026 above
const SCHOOL_HOLS = [
  ["2026-04-03","2026-04-17"],["2026-05-25","2026-05-29"],["2026-07-18","2026-09-04"],
];
const isSH = d => SCHOOL_HOLS.some(([s,e]) => d >= s && d <= e);

function buildCal() {
  const weeks=[], days=[];
  const months=[
    {m:0,y:2026,n:"January",  s:"Jan"},
    {m:1,y:2026,n:"February", s:"Feb"},
    {m:2,y:2026,n:"March",    s:"Mar"},
    {m:3,y:2026,n:"April",    s:"Apr"},
    {m:4,y:2026,n:"May",      s:"May"},
    {m:5,y:2026,n:"June",     s:"Jun"},
    {m:6,y:2026,n:"July",     s:"Jul"},
    {m:7,y:2026,n:"August",   s:"Aug"},
    {m:8,y:2026,n:"September",s:"Sep"},
    {m:9,y:2026,n:"October",  s:"Oct"},
    {m:10,y:2026,n:"November",s:"Nov"},
    {m:11,y:2026,n:"December",s:"Dec"},
  ];
  const DOW = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  months.forEach(({m,y,n,s}) => {
    const dim = new Date(y, m+1, 0).getDate();
    for (let d=1; d<=dim; d++) {
      const date = `${y}-${String(m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
      const js = new Date(date).getDay();
      const dow = DOW[js];
      const isWeekend = js===0||js===6;
      const bh = UK_BH_2026[date]||BH[date]||null;
      days.push({ date, dow, month:n, short:s, bh, sh:isSH(date), label:`${dow} ${d} ${s}`, isWeekend, isSun:js===0, isSat:js===6 });
    }
  });
  // Build weeks — weekdays only for standard rota view
  const weekdays = days.filter(d=>!d.isWeekend);
  let wk=[], ws=null;
  weekdays.forEach(day => {
    if (day.dow==="Mon"||wk.length===0) { if(wk.length) weeks.push({start:ws,days:wk}); wk=[day]; ws=day.date; }
    else wk.push(day);
  });
  if (wk.length) weeks.push({start:ws,days:wk});
  return { weeks, days, weekdays, allDays:days };
}
const CAL = buildCal();

/* ── HELPERS ── */
const DEFAULT_DAYS = 28;
const ent = hrs => Math.round((hrs/37.5)*DEFAULT_DAYS*10)/10;
const fmt = n => "£"+Math.round(n).toLocaleString();
const isXmas = d => d>="2026-12-24"&&d<="2027-01-01";

const isAbsent = (state, id, date) => {
  const lv = ((state.leave||{})[id]||[]).filter(l=>l.status!=="declined").map(l=>l.date);
  if (lv.includes(date)) return true;
  // Clinician annual leave
  const clv = (state.clinicianLeave||{})[id]||[];
  if (clv.filter(l=>l.status!=="declined"&&l.type==="annual").map(l=>l.date).includes(date)) return true;
  // Full-day CPD events
  const cpd = (state.cpdEvents||[]).filter(e=>e.allDay&&e.date===date&&(e.staffIds||[]).includes(id));
  if (cpd.length>0) return true;
  // College day blocks the person
  const college = (state.collegeDates||[]).some(e=>e.date===date&&(e.staffIds||[]).includes(id));
  if (college) return true;
  return (state.sick||[]).some(s => {
    if (s.staffId!==id) return false;
    const st=new Date(s.date), en=new Date(s.date);
    en.setDate(en.getDate()+s.days-1);
    const dt=new Date(date);
    return dt>=st && dt<=en;
  });
};

// Is this a bank holiday with the practice closed?
const isBHClosed = (state, date) => {
  if (!UK_BH_2026[date] && !BH[date]) return false;
  return !(state.bhOverrides||{})[date];
};

// Get individual entitlement for a person
// Model A: 28 days inclusive of bank holidays
// Returns how many UK bank holidays fall on this person's working days
const getBankHolidayDays = (person) => {
  const bhDates = Object.keys(UK_BH_2026);
  const noFri = person?.noFri;
  const workDays = person?.workDays; // ["Mon","Tue","Wed","Thu","Fri"] or subset
  const DAY_NAMES = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  return bhDates.filter(d => {
    const dow = new Date(d+"T12:00:00").getDay(); // 0=Sun, 6=Sat
    if (dow===0||dow===6) return false; // weekend BH — not deducted (substitute day applies)
    const dayName = DAY_NAMES[dow];
    if (workDays) return workDays.includes(dayName);
    if (noFri && dayName==="Fri") return false;
    return true; // default: works Mon–Fri
  }).length;
};

// historicalLeave can be stored as a plain number (legacy) or {days, note, loggedBy}
// Always use this helper to read the days value safely
const getHistDays = (state, id) => {
  const raw = (state.historicalLeave||{})[id];
  if (!raw) return 0;
  if (typeof raw === "object") return raw.days||0;
  return raw||0;
};

const getEntitlement = (state, person) => {
  // Custom override always wins
  const custom = (state.individualEntitlements||{})[person?.id];
  if (custom !== undefined) {
    return typeof custom === "object" ? custom.days : custom;
  }

  // Partners (type contains "Partner") get 42 days; everyone else uses defaultDays setting
  const isPartner = (person?.type||"").toLowerCase().includes("partner");
  const defaultDays = isPartner ? (state.settings?.partnerDays||42) : (state.settings?.defaultDays||28);

  // Mid-year starter pro-rating — only if startDate is within the CURRENT leave year
  // (not for long-standing staff with historic start dates)
  if (person?.startDate) {
    const roleType0 = roleTypeForPerson(person);
    const indOv0 = (state.leaveYearOverrides||{})[person.id];
    const lyCfg0 = indOv0 ? {[roleType0]:indOv0} : (state.leaveYearConfig || state.settings?.leaveYearConfig);
    const {start:ly0Start, end:ly0End} = getLeaveYear(roleType0, lyCfg0);
    const ly0StartStr = ly0Start.toISOString().split("T")[0];
    const ly0EndStr   = ly0End.toISOString().split("T")[0];
    // Only pro-rate if they genuinely started mid-way through THIS leave year
    if (person.startDate > ly0StartStr && person.startDate <= ly0EndStr) {
      const ly0Days = Math.round((ly0End - ly0Start) / 86400000) + 1;
      const start = new Date(person.startDate+"T12:00:00");
      const remaining = Math.round((ly0End - start) / 86400000) + 1;
      const base = Math.round(((person.hrs||37.5)/37.5)*defaultDays*10)/10;
      return Math.round((remaining / ly0Days) * base * 10) / 10;
    }
  }

  // If no hours history, simple calculation
  const history = person?.hrsHistory; // [{hrs, from:"YYYY-MM-DD"}, ...]
  if (!history || history.length === 0) {
    return Math.round(((person?.hrs||37.5)/37.5)*defaultDays*10)/10;
  }

  // Pro-rate across the current leave year
  // Work out leave year start/end (default Jan-Dec)
  const roleType = roleTypeForPerson(person);
  // Check for individual leave year override
  const individualOverride = (state.leaveYearOverrides||{})[person.id];
  const leaveYearCfg = individualOverride
    ? {[roleType]: individualOverride}
    : (state.leaveYearConfig || state.settings?.leaveYearConfig);
  const { start: lyStart, end: lyEnd } = getLeaveYear(roleType, leaveYearCfg);
  const lyDays = Math.round((lyEnd - lyStart) / 86400000) + 1;

  // Build full timeline for this leave year: [{from, hrs}]
  // Start with whatever hrs was at the beginning of the leave year
  // History is sorted oldest-first; find the rate at lyStart
  const sorted = [...history].sort((a,b)=>a.from.localeCompare(b.from));
  const lyStartStr = lyStart.toISOString().split("T")[0];
  const lyEndStr   = lyEnd.toISOString().split("T")[0];

  // Rate in effect at start of leave year = last entry with from <= lyStart, or first entry
  let baseHrs = person.hrs || 37.5;
  for (const h of sorted) {
    if (h.from <= lyStartStr) baseHrs = h.hrs;
  }

  // Periods within the leave year
  const periods = []; // [{startStr, endStr, hrs}]
  let currentHrs = baseHrs;
  let periodStart = lyStartStr;

  for (const h of sorted) {
    if (h.from <= lyStartStr) continue; // before leave year, already accounted for
    if (h.from > lyEndStr) break;       // after leave year
    // Close previous period
    const dayBefore = new Date(h.from);
    dayBefore.setDate(dayBefore.getDate() - 1);
    periods.push({ startStr: periodStart, endStr: dayBefore.toISOString().split("T")[0], hrs: currentHrs });
    currentHrs = h.hrs;
    periodStart = h.from;
  }
  // Final period to end of leave year
  periods.push({ startStr: periodStart, endStr: lyEndStr, hrs: currentHrs });

  // Sum weighted entitlement across periods
  let total = 0;
  for (const p of periods) {
    const pStart = new Date(p.startStr + "T12:00:00");
    const pEnd   = new Date(p.endStr   + "T12:00:00");
    const days   = Math.round((pEnd - pStart) / 86400000) + 1;
    const weight = days / lyDays;
    total += weight * (p.hrs / 37.5) * defaultDays;
  }
  return Math.round(total * 10) / 10;
};

// Bookable days = total entitlement minus bank holidays (Model A)
const getBookableDays = (state, person) => {
  const total = getEntitlement(state, person);
  const bhDays = getBankHolidayDays(person);
  // Pro-rate BH for part-time: scale by hours proportion
  const bhProratedFull = Math.round(bhDays * ((person?.hrs||37.5)/37.5) * 10) / 10;
  return Math.max(0, Math.round((total - bhProratedFull) * 10) / 10);
};

// Get leave year start/end for a person based on their role and practice config
// roleType: "nurses" | "reception" | "dentists"
const getLeaveYear = (roleType, leaveYearConfig) => {
  const cfg = (leaveYearConfig||{})[roleType] || "jan-dec";
  const now = new Date();
  const y = now.getFullYear();
  if (cfg === "apr-mar") {
    const startYear = now.getMonth() < 3 ? y - 1 : y;
    return { start: new Date(startYear, 3, 1), end: new Date(startYear + 1, 2, 31) };
  }
  return { start: new Date(y, 0, 1), end: new Date(y, 11, 31) };
};

// Work out which leaveYearConfig key applies to a staff member
const CLINICIAN_TYPES = new Set(["gds","mos","fd","therapist","specialist","partner","partner/gds","partner/mos","dentist","clinician"]);
const roleTypeForPerson = (person) => {
  if (!person) return "nurses";
  const type = (person.type||"").toLowerCase();
  if (type === "reception" || type === "tco") return "reception";
  if (CLINICIAN_TYPES.has(type)) return "dentists";
  // Clinicians added via getClinicians have role field, nurses have level field
  if (person.role && !person.level) return "dentists";
  return "nurses";
};

const getAbsenceReason = (state, id, date) => {
  // Sickness first (highest priority label)
  const sick = (state.sick||[]).find(s => {
    if (s.staffId!==id) return false;
    const st=new Date(s.date), en=new Date(s.date);
    en.setDate(en.getDate()+s.days-1);
    return new Date(date)>=st && new Date(date)<=en;
  });
  if (sick) return sick.reason==="illness"?"Sickness":sick.reason||"Sickness";
  // Annual / approved leave
  const lv = ((state.leave||{})[id]||[]).find(l=>l.date===date&&l.status!=="declined");
  if (lv) {
    const t = lv.leaveType||lv.reason||"";
    if (t==="annual") return "Annual Leave";
    if (t==="medical") return "Medical Appointment";
    if (t==="compassionate") return "Compassionate Leave";
    if (t==="cpd") return lv.note ? `CPD — ${lv.note}` : "CPD / Training";
    if (t==="maternity") return "Maternity";
    return lv.reason||"Annual Leave";
  }
  const clinLeave = ((state.clinicianLeave||{})[id]||[]).find(l=>l.date===date&&l.status!=="declined");
  if (clinLeave) return clinLeave.reason||"Annual Leave";
  // CPD events
  const cpd = (state.cpdEvents||[]).find(e=>e.allDay&&e.date===date&&(e.staffIds||[]).includes(id));
  if (cpd) return cpd.title ? `CPD — ${cpd.title}` : "CPD / Training";
  // College day
  const college = (state.collegeDates||[]).find(e=>e.date===date&&(e.staffIds||[]).includes(id));
  if (college) return college.groupLabel ? `College — ${college.groupLabel}` : "College Day";
  return null;
};

/* ── KEYHOLDER HELPERS ── */
// Get list of keyholder staff IDs for a site
// Self-healing nurse filter: includes nurses with unknown/empty site in single-site practices
// Prevents Flourish-style breakage where Iris setup ran before practiceSites was populated
function getNursesForSite(state, siteId) {
  const knownSiteIds = new Set(Object.keys(SITES));
  const onlyOneSite = knownSiteIds.size <= 1;
  return (state.nurses||NURSES_BASE).filter(n=>{
    if (n.maternity||n.archived) return false;
    const unknown = !n.site || !knownSiteIds.has(n.site);
    return n.site===siteId || (unknown && onlyOneSite);
  });
}

// Self-healing reception filter — same logic as getNursesForSite
function getRecsForSite(state, siteId, excludeManager) {
  const knownSiteIds = new Set(Object.keys(SITES));
  const onlyOneSite = knownSiteIds.size <= 1;
  const base = IS_DCO ? RECS_BASE : [];
  const custom = state.customRecs||[];
  return [...base,...custom].filter(r=>{
    if (r.archived) return false;
    if (excludeManager && r.type==="Manager") return false;
    const unknown = !r.site || !knownSiteIds.has(r.site);
    return r.site===siteId || (unknown && onlyOneSite);
  });
}

function getSiteKeyholders(state, siteId) {
  const kh = (state.siteKeyholders||{})[siteId]||{};
  // Support both old model (opener/closer) and new list model
  if (Array.isArray(kh.holders) && kh.holders.length > 0) return kh.holders;
  // Migrate old model on the fly
  const ids = [kh.opener, kh.closer, kh.backup_opener, kh.backup_closer].filter(Boolean);
  if (ids.length > 0) return [...new Set(ids)];
  // Fallback: any nurse or clinician with keyholder:true at this site
  // This means setting keyholder in Staff Records or Clinicians is picked up automatically
  const khKnownSiteIds = new Set(Object.keys(SITES));
  const khOnlyOne = khKnownSiteIds.size <= 1;
  const nurseIds = (state.nurses||NURSES_BASE)
    .filter(n=>{
      if (n.maternity||n.archived||!n.keyholder) return false;
      const unknown = !n.site || !khKnownSiteIds.has(n.site);
      return n.site===siteId || (unknown && khOnlyOne);
    })
    .map(n=>n.id);
  const clinIds = getClinicians(state)
    .filter(c=>{
      if (c._deleted||!c.keyholder) return false;
      const unknown = !c.site || !khKnownSiteIds.has(c.site);
      return c.site===siteId || (unknown && khOnlyOne);
    })
    .map(c=>c.id);
  return [...new Set([...nurseIds,...clinIds])];
}

function getSiteKeyholderPeople(state, siteId) {
  const ids = getSiteKeyholders(state, siteId);
  // Include all staff sources — customClinicians and customRecs for non-DCO tenants
  const allStaff = [
    ...(state.nurses||NURSES_BASE),
    ...(IS_DCO ? RECS_BASE : (state.customRecs||[])),
    ...getClinicians(state).filter(c=>!c._deleted),
  ];
  return ids.map(id=>allStaff.find(s=>s.id===id)).filter(Boolean);
}

// Is there at least one keyholder available on a given date?
function getKeyholderCoverage(state, siteId, date) {
  const overKey = `${date}_${siteId}_any`;
  const override = (state.keyholderOverrides||{})[overKey];
  if (override) return {status:"overridden", person:override};
  const holders = getSiteKeyholderPeople(state, siteId);
  if (holders.length===0) return {status:"no_keyholder"};
  const available = holders.filter(p=>!isAbsent(state, p.id, date));
  if (available.length>0) return {status:"ok", people:available};
  return {status:"absent", people:holders};
}

// Legacy shim — keep anything calling getKeyholder/getKeyholderStatus working
function getKeyholder(state, siteId, role) {
  const kh = (state.siteKeyholders||{})[siteId]||{};
  if (Array.isArray(kh.holders)) return kh.holders[0]||null;
  return kh[role]||null;
}
function getKeyholderPerson(state, siteId, role) {
  const id = getKeyholder(state, siteId, role);
  if (!id) return null;
  const allStaff = [...(state.nurses||NURSES_BASE),...RECS_BASE,...CLINICIANS_BASE];
  return allStaff.find(s=>s.id===id)||null;
}
function getKeyholderStatus(state, siteId, date, role) {
  const cov = getKeyholderCoverage(state, siteId, date);
  if (cov.status==="overridden") return {status:"overridden", person:cov.person};
  if (cov.status==="no_keyholder") return {status:"no_keyholder", person:null};
  if (cov.status==="ok") return {status:"ok", person:cov.people[0]};
  return {status:"absent", person:cov.people[0]};
}

// Build keyholder RAG for upcoming dates
function buildKeyholderRAG(state) {
  const today = new Date().toISOString().split("T")[0];
  const in4weeks = new Date(); in4weeks.setDate(in4weeks.getDate()+28);
  const in2months = new Date(); in2months.setDate(in2months.getDate()+60);
  const f4w = in4weeks.toISOString().split("T")[0];
  const f2m = in2months.toISOString().split("T")[0];

  const issues = [];
  Object.keys(SITES).forEach(siteId => {
    const holders = getSiteKeyholderPeople(state, siteId);
    if (holders.length===0) {
      issues.push({siteId, rag:"red", msg:`No keyholders assigned at ${SITES[siteId].name}`, date:null});
      return;
    }
    CAL.days.filter(d=>d.date>=today&&d.date<=f2m&&!d.bh).forEach(d => {
      const cov = getKeyholderCoverage(state, siteId, d.date);
      if (cov.status==="ok"||cov.status==="overridden") return;
      const rag = d.date<=f4w ? "red" : "amber";
      issues.push({siteId, rag, msg:`All keyholders absent`, date:d.date, dateLabel:d.label});
    });
  });
  return issues;
}

// ── RECEPTION SHIFTS ──────────────────────────────────────────────────────────
const REC_SHIFTS = [
  { id:"early", label:"Early",    start:"08:00", end:"14:00", hours:6   },
  { id:"full",  label:"Full day", start:"08:00", end:"17:30", hours:9.5 },
  { id:"late",  label:"Late",     start:"14:00", end:"20:00", hours:6   },
];

// Site open hours per day — used to determine which shifts apply
const SITE_OPEN = {
  sale:       { open:"08:00", close:"20:00" },
  brooklands: { open:"08:00", close:"17:30" },
  timperley:  { open:"08:00", close:"20:00" },
};

// Auto-assign reception shifts for a given site + week
function buildReceptionRota(siteId, weekDays, state) {
  const allRecs = getRecsForSite(state, siteId, true); // excludeManager=true

  const keyholderIds = new Set(getSiteKeyholders(state, siteId));
  const manualOverrides = state.recShiftOverrides || {};
  const contracts = state.recContracts || {};

  const result = {};

  weekDays.forEach(day => {
    const { date, dow, isWeekend, bh } = day;
    if (isWeekend || isBHClosed(state, date)) return;

    const overrideKey = `${date}_${siteId}`;
    if (manualOverrides[overrideKey]) {
      result[date] = manualOverrides[overrideKey];
      return;
    }

    // Who's available today
    const available = allRecs.filter(r => {
      if (isAbsent(state, r.id, date)) return false;
      if (r.noFri && dow === "Fri") return false;
      if (r.sick) return false;
      return true;
    });

    // Build assignments from contracts first
    const assignments = [];
    const assignedIds = new Set();

    // Contract-based assignments
    available.forEach(r => {
      const contract = contracts[r.id];
      if (!contract) return;
      const shiftId = contract[dow];
      if (!shiftId || shiftId === "off") return;
      const shiftDef = REC_SHIFTS.find(s => s.id === shiftId);
      if (!shiftDef) return;
      assignments.push({
        staffId: r.id,
        name: r.name,
        shift: shiftId,
        start: shiftDef.start,
        end: shiftDef.end,
        keyholder: keyholderIds.has(r.id),
        role: r.type === "TCO" ? "TCO" : undefined,
        fromContract: true,
      });
      assignedIds.add(r.id);
    });

    // Fallback auto-assign for anyone available but not in a contract
    const unassigned = available.filter(r => !assignedIds.has(r.id));
    if (assignments.length === 0 && unassigned.length > 0) {
      // Legacy auto-assign logic as fallback
      const needLate = SITE_OPEN[siteId]?.close >= "18:00";
      const keyholders = unassigned.filter(r => keyholderIds.has(r.id));
      const opener = keyholders[0] || unassigned[0];
      if (opener && opener.id !== "r_ljohns") {
        assignments.push({ staffId: opener.id, name: opener.name, shift: "early", start:"08:00", end:"14:00", keyholder: keyholderIds.has(opener.id) });
        assignedIds.add(opener.id);
      }
      const fullDayPerson = unassigned.find(r => !assignedIds.has(r.id) && r.id !== "r_ljohns");
      if (fullDayPerson) {
        assignments.push({ staffId: fullDayPerson.id, name: fullDayPerson.name, shift: "full", start:"08:00", end:"17:30", keyholder: keyholderIds.has(fullDayPerson.id) });
        assignedIds.add(fullDayPerson.id);
      }
      if (needLate) {
        const lateCloser = unassigned.find(r => !assignedIds.has(r.id) && r.id !== "r_ljohns");
        if (lateCloser) assignments.push({ staffId: lateCloser.id, name: lateCloser.name, shift: "late", start:"14:00", end:"20:00", keyholder: keyholderIds.has(lateCloser.id) });
      }
    }

    // Always add Leanne (TCO) if available and not already assigned
    const leanneAvail = available.find(r => r.id === "r_ljohns");
    if (leanneAvail && !assignedIds.has(leanneAvail.id)) {
      const lContract = contracts["r_ljohns"];
      const lShift = lContract?.[dow];
      if (!lContract || (lShift && lShift !== "off")) {
        assignments.push({ staffId: leanneAvail.id, name: leanneAvail.name, shift: "early", start:"08:00", end:"14:00", keyholder: false, role: "TCO" });
      }
    }

    // Flag issues
    const flags = [];
    const hasKeyholder = assignments.some(a => a.keyholder);
    if (!hasKeyholder && assignments.length > 0) flags.push({ type: "no_keyholder", msg: "No keyholder on shift" });
    if (assignments.length === 0) flags.push({ type: "no_cover", msg: "No reception cover" });
    if (siteId === "brooklands" && assignments.length <= 1) flags.push({ type: "single_point", msg: "Karen only — single point of failure" });

    result[date] = { assignments, flags };
  });

  return result;
}



// Get all CPD events for a person (any type, full or partial day)
function getCpdForPerson(state, id) {
  return (state.cpdEvents||[]).filter(e=>(e.staffIds||[]).includes(id));
}

// Get all CPD events for a site on a given date
function getCpdForSiteDate(state, siteId, date) {
  return (state.cpdEvents||[]).filter(e=>e.site===siteId&&e.date===date);
}

function pickShift(clinEnd) {
  const [h,m] = clinEnd.split(":").map(Number);
  const mins = h*60+m;
  if (mins<=870)  return {start:"08:00",end:"14:00"};
  if (mins<=1080) return {start:"08:00",end:"17:30"};
  return {start:"08:00",end:"18:00"};
}

// Merge base clinicians with any custom additions/edits from state
function getClinicians(state) {
  const custom = state.customClinicians||[];
  const FALLBACK_COLORS = ["#818cf8","#a78bfa","#3aad98","#f472b6","#f5c518","#60a5fa"];
  // custom entries with existing id = override; new id = addition
  const baseIds = new Set(CLINICIANS_BASE.map(c=>c.id));
  const overrides = {};
  custom.forEach(c=>{ if(baseIds.has(c.id)) overrides[c.id]=c; });
  const additions = custom.filter(c=>!baseIds.has(c.id));
  // If custom override has blank name, preserve the base name
  const base = CLINICIANS_BASE.map(c=>{
    const ov = overrides[c.id];
    if (!ov) return c;
    return {...ov, name: ov.name||c.name};
  }).filter(c=>!c._deleted);
  // Ensure every clinician has a color
  const all = [...base,...additions];
  return all.map((c,i)=>c.color ? c : {...c, color: FALLBACK_COLORS[i % FALLBACK_COLORS.length]});
}

// Estimate weekly contracted staff cost for a site
function estimateWeeklyCost(siteId, state) {
  const nurseRate  = state.settings?.nurseHourlyRate || 14;
  const recRate    = state.settings?.recHourlyRate   || 12;
  const nurses  = getNursesForSite(state, siteId);
  const recs    = RECS_BASE.filter(r=>r.site===siteId);
  const nurseCost = nurses.reduce((s,n)=>s+(n.hrs||37.5)*nurseRate,0);
  const recCost   = recs.reduce((s,r)=>s+(r.hrs||37.5)*recRate,0);
  return { nurseCost, recCost, total: nurseCost+recCost, nurses:nurses.length, recs:recs.length };
}

// Surgeries open per week day for a site (uses getClinicians)
function surgeriesPerWeek(siteId, state) {
  const clins = getClinicians(state).filter(c=>{
    const clinSites = c.sites||[c.site];
    return clinSites.includes(siteId)&&!c._deleted&&!c.leaving;
  });
  const days = ["Mon","Tue","Wed","Thu","Fri"];
  return days.map(d=>({
    dow:d,
    count: clins.filter(c=>{
      const sched = c.sites&&c.siteScheds ? c.siteScheds[siteId]||c.sched : c.sched;
      return !!sched?.[d];
    }).length
  }));
}

// Parse a sched value — supports straight "08:00-17:30" and split "08:00-13:00|14:00-17:30"
function parseSched(val) {
  if (!val) return null;
  // Guard: if val is an object {start, end} from new table onboarding, convert
  if (typeof val === "object") {
    const s = val.start||""; const e = val.end||"";
    if (!s) return null;
    val = e ? s+"-"+e : s;
  }
  if (typeof val !== "string") return null;
  if (val.includes("|")) {
    const [am, pm] = val.split("|");
    const [amStart, amEnd] = am.split("-");
    const [pmStart, pmEnd] = pm.split("-");
    return { clinStart:amStart, clinEnd:pmEnd, hasLunch:true, lunchStart:amEnd, lunchEnd:pmStart, amEnd, pmStart };
  }
  const [clinStart, clinEnd] = val.split("-");
  return { clinStart, clinEnd, hasLunch:false, lunchStart:null, lunchEnd:null };
}

function buildDayRota(date, siteId, nurses, state) {
  const js = new Date(date).getDay();
  const dow = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][js];
  const overrides = (state.rotaOverrides||{})[date]||{};

  // Week key for room assignments (Mon of that week)
  const dateObj = new Date(date);
  const dayOfWeek = dateObj.getDay();
  const monday = new Date(dateObj);
  monday.setDate(dateObj.getDate() - (dayOfWeek===0?6:dayOfWeek-1));
  const weekKey = `${monday.toISOString().split("T")[0]}_${siteId}`;
  const weekRoomAssign = (state.roomAssignments||{})[weekKey]||{};

  // ISO week number helper for altWeek (every other week) clinicians
  const isoWeek = d => {
    const dt = new Date(d);
    dt.setHours(0,0,0,0);
    dt.setDate(dt.getDate()+3-(dt.getDay()+6)%7);
    const w1 = new Date(dt.getFullYear(),0,4);
    return 1+Math.round(((dt-w1)/86400000-3+(w1.getDay()+6)%7)/7);
  };

  const pairHistory = state.pairHistory||{};

  const allClinicians = getClinicians(state);
  const knownSiteIds = new Set(Object.keys(SITES));
  const onlyOneSite = knownSiteIds.size <= 1;
  const clinicians = allClinicians.filter(c => {
    // Multi-site support: clinician may have sites[] array OR single site
    const clinSites = c.sites || [c.site];
    // Self-healing: if clinician has unknown/empty site and there's only one site,
    // include them (happens when Iris setup ran before practiceSites was populated)
    const siteUnknown = !c.site || !knownSiteIds.has(c.site);
    if (!clinSites.includes(siteId) && !(siteUnknown && onlyOneSite)) return false;
    const schedForSite = c.siteScheds?.[siteId] || c.sched;
    if (!schedForSite?.[dow]) return false;
    if (c.leaving && date>c.leaving) return false;
    if (c.startDate && date<c.startDate) return false;
    if (isAbsent(state,c.id,date)) return false;
    // Per-day frequency (multi-tenant) — stored as c.freq = {Mon:"always", Fri:"weekA"}
    const dayFreq = (c.freq||{})[dow] || "always";
    if (dayFreq==="weekA" && getWeekParity(date,state)!=="A") return false;
    if (dayFreq==="weekB" && getWeekParity(date,state)!=="B") return false;
    // Legacy hardcoded flags (DCO NURSES_BASE) — keep backward compat
    if (c.altWeek && getWeekParity(date,state)!=="A") return false;
    if (c.altWeekFri && dow==="Fri" && getWeekParity(date,state)!=="A") return false;
    return true;
  }).map(c => {
    // Resolve correct schedule: multi-site → siteScheds; alternate week → schedB
    const baseSched = (c.sites && c.siteScheds) ? (c.siteScheds[siteId]||c.sched) : c.sched;
    const dayFreq = (c.freq||{})[dow] || "always";
    const parity = getWeekParity(date, state);
    // If day is weekA and we're in Week B, OR day is weekB and we're in Week A → use schedB
    const useAlt = (dayFreq==="weekA" && parity==="B") || (dayFreq==="weekB" && parity==="A");
    const resolvedSched = useAlt && c.schedB?.[dow]
      ? {...baseSched, [dow]: c.schedB[dow]}
      : baseSched;
    return {...c, sched: resolvedSched};
  });

  const toMins = t => { if(!t) return 9999; const [h,m]=(t+":00").split(":").map(Number); return h*60+m; };
  const availNurses = nurses.filter(n => {
    if (n.maternity) return false;
    // Self-healing: include nurses with unknown/empty site for single-site practices
    const nurseUnknown = !n.site || !knownSiteIds.has(n.site);
    if (n.site!==siteId && !(nurseUnknown && onlyOneSite)) return false;
    if (isAbsent(state,n.id,date)) return false;
    if (n.noFri&&dow==="Fri") return false;
    if (n.workDays&&!n.workDays.includes(dow)) return false;
    return true;
  });

  const assigned = new Set();
  const pairings = [];
  const flags = [];

  // Helper: consistency score for a nurse/clinician pair
  const consistencyScore = (nId, cId) => {
    const key = `${nId}_${cId}`;
    return Math.min((pairHistory[key]||0) * 0.15, 0.6); // max +0.6 bonus for established pairs
  };

  // Step 1: Manual overrides
  clinicians.forEach(c => {
    if (!overrides[c.id]) return;
    const n = overrides[c.id]==="none" ? null : availNurses.find(n=>n.id===overrides[c.id]);
    const ps = parseSched((c.siteScheds?.[siteId]||c.sched)[dow]);
    if (!ps) return;
    assigned.add(overrides[c.id]);
    pairings.push({clinician:c,nurse:n||null,shift:pickShift(ps.clinEnd),autoAssigned:false,manual:true,clinStart:ps.clinStart,clinEnd:ps.clinEnd,hasLunch:ps.hasLunch,lunchStart:ps.lunchStart,lunchEnd:ps.lunchEnd,roomAssigned:false});
    if (!n) flags.push({type:"gap",msg:`Gap: ${c.name} (manual)`,color:C.danger});
  });

  // Step 2: Week room assignments (manager-set pairings)
  clinicians.forEach(c => {
    if (pairings.find(p=>p.clinician.id===c.id)) return;
    const assignedNurseId = weekRoomAssign[c.id];
    if (!assignedNurseId) return;
    const ps = parseSched((c.siteScheds?.[siteId]||c.sched)[dow]);
    if (!ps) return;
    const n = availNurses.find(n=>n.id===assignedNurseId&&!assigned.has(n.id));
    if (n) {
      assigned.add(n.id);
      pairings.push({clinician:c,nurse:n,shift:pickShift(ps.clinEnd),autoAssigned:false,manual:false,roomAssigned:true,clinStart:ps.clinStart,clinEnd:ps.clinEnd,hasLunch:ps.hasLunch,lunchStart:ps.lunchStart,lunchEnd:ps.lunchEnd});
    }
  });

  // Step 3: Preferred pairings
  clinicians.forEach(c => {
    if (pairings.find(p=>p.clinician.id===c.id)) return;
    const ps = parseSched((c.siteScheds?.[siteId]||c.sched)[dow]);
    if (!ps) return;
    const pref = availNurses.find(n=>n.id===c.prefNurse&&!assigned.has(n.id));
    if (pref) { assigned.add(pref.id); pairings.push({clinician:c,nurse:pref,shift:pickShift(ps.clinEnd),autoAssigned:false,manual:false,roomAssigned:false,clinStart:ps.clinStart,clinEnd:ps.clinEnd,hasLunch:ps.hasLunch,lunchStart:ps.lunchStart,lunchEnd:ps.lunchEnd}); }
  });

  // Step 4: Auto-assign
  // Pass 1: MOS clinicians
  clinicians.filter(c=>c.type.includes("MOS")).forEach(c => {
    if (pairings.find(p=>p.clinician.id===c.id)) return;
    const ps = parseSched((c.siteScheds?.[siteId]||c.sched)[dow]);
    if (!ps) return;
    const mosAvail = availNurses.filter(n=>!assigned.has(n.id)&&n.area==="MOS");
    const gdsAvail = availNurses.filter(n=>!assigned.has(n.id)&&n.area!=="MOS");
    const pool = mosAvail.length>0 ? mosAvail : gdsAvail;
    const score = n => (STATUS[n.status]?.score||0) + (n.flexible?0.1:0) + consistencyScore(n.id,c.id);
    const best = [...pool].sort((a,b)=>score(b)-score(a))[0];
    if (best) {
      assigned.add(best.id);
      pairings.push({clinician:c,nurse:best,shift:pickShift(ps.clinEnd),autoAssigned:true,manual:false,roomAssigned:false,clinStart:ps.clinStart,clinEnd:ps.clinEnd,hasLunch:ps.hasLunch,lunchStart:ps.lunchStart,lunchEnd:ps.lunchEnd});
      if (best.area!=="MOS") flags.push({type:"autoAssign",msg:`${best.name} (GDS) covering MOS — no MOS nurse available`,color:C.danger});
      else flags.push({type:"autoAssign",msg:`${best.name} auto-assigned to ${c.name}`,color:C.warn});
    } else {
      pairings.push({clinician:c,nurse:null,shift:pickShift(ps.clinEnd),autoAssigned:false,manual:false,roomAssigned:false,clinStart:ps.clinStart,clinEnd:ps.clinEnd,hasLunch:ps.hasLunch,lunchStart:ps.lunchStart,lunchEnd:ps.lunchEnd});
      flags.push({type:"gap",msg:`No nurse for ${c.name}`,color:C.danger});
    }
  });
  // Pass 2: GDS/Therapy/FD
  clinicians.filter(c=>!c.type.includes("MOS")).forEach(c => {
    if (pairings.find(p=>p.clinician.id===c.id)) return;
    const ps = parseSched((c.siteScheds?.[siteId]||c.sched)[dow]);
    if (!ps) return;
    const gdsAvail = availNurses.filter(n=>!assigned.has(n.id)&&n.area!=="MOS");
    const mosAvail = availNurses.filter(n=>!assigned.has(n.id)&&n.area==="MOS");
    const pool = gdsAvail.length>0 ? gdsAvail : mosAvail;
    const score = n => (STATUS[n.status]?.score||0) + (n.flexible?0.1:0) + consistencyScore(n.id,c.id);
    const best = [...pool].sort((a,b)=>score(b)-score(a))[0];
    if (best) {
      assigned.add(best.id);
      pairings.push({clinician:c,nurse:best,shift:pickShift(ps.clinEnd),autoAssigned:true,manual:false,roomAssigned:false,clinStart:ps.clinStart,clinEnd:ps.clinEnd,hasLunch:ps.hasLunch,lunchStart:ps.lunchStart,lunchEnd:ps.lunchEnd});
      if (best.area==="MOS") flags.push({type:"autoAssign",msg:`MOS nurse ${best.name} covering GDS — last resort`,color:C.danger});
      else flags.push({type:"autoAssign",msg:`${best.name} auto-assigned to ${c.name}`,color:C.warn});
    } else {
      pairings.push({clinician:c,nurse:null,shift:pickShift(ps.clinEnd),autoAssigned:false,manual:false,roomAssigned:false,clinStart:ps.clinStart,clinEnd:ps.clinEnd,hasLunch:ps.hasLunch,lunchStart:ps.lunchStart,lunchEnd:ps.lunchEnd});
      flags.push({type:"gap",msg:`No nurse for ${c.name}`,color:C.danger});
    }
  });

  const spares = availNurses.filter(n=>!assigned.has(n.id));
  if (spares.length) flags.push({type:"spare",msg:`Spare: ${spares.map(n=>n.name).join(", ")}`,color:C.lime});

  // Enrich all pairings with resolved session requirements for this day
  pairings.forEach(p => { p.reqs = getClinicianReqs(state, p.clinician.id, dow); });

  // ── Capability checks ──
  pairings.forEach(p => {
    if (!p.reqs.length) return;
    const rl = reqLabel(p.reqs);
    if (!p.nurse) {
      flags.push({type:"capability",msg:`${p.clinician.name} needs ${rl.label} nurse — no nurse assigned`,clinId:p.clinician.id,color:C.danger});
      return;
    }
    const missing = p.reqs.filter(r => !p.nurse[r]);
    if (missing.length) {
      const betterSpare = spares.find(n => missing.every(r=>n[r]));
      flags.push({type:"capability",
        msg:`${p.clinician.name} needs ${rl.label} — ${p.nurse.name} not qualified${betterSpare?` · ${betterSpare.name} available`:""}`,
        clinId:p.clinician.id, nurseId:p.nurse.id, missing, altNurse:betterSpare||null, color:C.danger});
    }
  });

  // ── Head nurse / keyholder absent check ──
  const headNurse = nurses.find(n => n.headNurse && n.site===siteId);
  if (headNurse && isAbsent(state, headNurse.id, date)) {
    const sedCover = availNurses.filter(n => n.id!==headNurse.id && n.ra && n.iv);
    const raCover  = availNurses.filter(n => n.id!==headNurse.id && n.ra);
    const needsIV  = pairings.some(p => p.reqs.includes("iv"));
    const needsRA  = pairings.some(p => p.reqs.includes("ra"));
    if (needsIV && sedCover.length===0) {
      flags.push({type:"keyholder",msg:`${headNurse.name} absent — NO RA+IV cover for sedation sessions`,color:C.danger,staffId:headNurse.id});
    } else if (needsRA && raCover.length===0) {
      flags.push({type:"keyholder",msg:`${headNurse.name} absent — no RA nurse on site`,color:C.danger,staffId:headNurse.id});
    } else if (sedCover.length>0) {
      flags.push({type:"keyholder",msg:`${headNurse.name} absent — ${sedCover[0].name} covering IV sedation`,color:C.warn,staffId:headNurse.id});
    } else {
      flags.push({type:"keyholder",msg:`${headNurse.name} (Head Nurse) absent today`,color:C.warn,staffId:headNurse.id});
    }
  }

  // Room clash check — find the peak number of CONCURRENT sessions at any point in the day
  // Two sessions clash only if they genuinely overlap in time (not back-to-back AM/PM)
  const roomCount = (SURGERY_ROOMS[siteId]||{}).count || 4;
  if (clinicians.length > 0 && roomCount > 0) {
    // Build list of session intervals in minutes since midnight
    const toMins = t => { const [h,m]=(t||"00:00").split(":").map(Number); return h*60+(m||0); };
    const intervals = clinicians.flatMap(c => {
      const ps = parseSched((c.siteScheds?.[siteId]||c.sched)?.[dow]||"08:00-17:30");
      if (!ps) return [];
      if (ps.hasLunch) {
        return [
          { start: toMins(ps.clinStart), end: toMins(ps.lunchStart) },
          { start: toMins(ps.lunchEnd),  end: toMins(ps.clinEnd) },
        ];
      }
      return [{ start: toMins(ps.clinStart), end: toMins(ps.clinEnd) }];
    });
    // Find max concurrent sessions at any breakpoint
    const breakpoints = [...new Set(intervals.flatMap(i=>[i.start,i.end]))].sort((a,b)=>a-b);
    let peak = 0;
    breakpoints.forEach(bp => {
      const concurrent = intervals.filter(i => i.start <= bp && i.end > bp).length;
      if (concurrent > peak) peak = concurrent;
    });
    if (peak > roomCount) {
      flags.push({type:"roomOverflow",msg:`${peak} simultaneous sessions, only ${roomCount} surgeries available`,color:C.warn});
    }
  }

  // ── BREAK FLAGS — Working Time Regulations 1998 ──────────────────────────
  // Any session over 6 hours requires a 20-minute rest break
  const breakThresholdHours = state.settings?.breakThresholdHours ?? 6;
  pairings.forEach(p => {
    if (!p.nurse || !p.clinStart || !p.clinEnd) return;
    const sessionMins = toMins(p.clinEnd) - toMins(p.clinStart);
    if (sessionMins > breakThresholdHours * 60) {
      const confirmed = (state.breakConfirmed||{})[`${date}_${p.nurse.id}`];
      if (!confirmed) {
        flags.push({
          type:"breakRequired",
          msg:`${p.nurse.name} — ${Math.round(sessionMins/60*10)/10}h session. WTR rest break required (Working Time Regulations 1998).`,
          color:C.warn,
          nurseId: p.nurse.id,
          date,
        });
      }
    }
  });

  return { date, dow, pairings, spares, flags, clinicians, weekKey, weekRoomAssign };
}

/* ── SESSION REQUIREMENTS HELPERS ── */
function getWeekParity(dateStr, state) {
  if (!dateStr) return "A";
  const weekAStart = state?.settings?.weekAStart;
  if (weekAStart) {
    // Calculate parity relative to the principal's chosen Week A start date
    const ref = new Date(weekAStart+"T12:00:00");
    const d   = new Date(dateStr+"T12:00:00");
    const diffDays = Math.round((d - ref) / 86400000);
    const diffWeeks = Math.floor(diffDays / 7);
    return diffWeeks % 2 === 0 ? "A" : "B";
  }
  // Fallback: week-of-year parity
  const d = new Date(dateStr+"T12:00:00");
  const startOfYear = new Date(d.getFullYear(), 0, 1);
  const weekNum = Math.ceil(((d - startOfYear) / 86400000 + startOfYear.getDay() + 1) / 7);
  return weekNum % 2 === 1 ? "A" : "B";
}
function getClinicianReqs(state, clinId, dow, dateStr) {
  const entry = (state.clinicianRequirements||{})[clinId];
  if (!entry) return [];
  // Ad hoc overrides first — one-off date
  if (dateStr && entry.adHoc?.length) {
    const hit = entry.adHoc.find(a => a.date === dateStr);
    if (hit) return hit.type === "iv" ? ["ra","iv"] : hit.type === "ra" ? ["ra"] : [];
  }
  const ov = entry.overrides?.[dow];
  if (ov !== undefined) {
    if (Array.isArray(ov)) return ov; // legacy
    const { reqs=[], freq="always" } = ov;
    if (freq === "always") return reqs;
    if (freq === "weekA") return getWeekParity(dateStr,state)==="A" ? reqs : (entry.default||[]);
    if (freq === "weekB") return getWeekParity(dateStr,state)==="B" ? reqs : (entry.default||[]);
    return reqs;
  }
  return entry.default || [];
}
function reqLabel(reqs) {
  if (!reqs || !reqs.length) return null;
  if (reqs.includes("iv")) return { label:"IV Sedation", short:"IV", color:"#ff4646" };
  if (reqs.includes("ra")) return { label:"RA Required", short:"RA", color:"#d97706" };
  return { label:reqs.join("+").toUpperCase(), short:reqs.join("+").toUpperCase(), color:"rgba(255,255,255,0.4)" };
}

/* ── SEED & PERSIST ── */
// Sanitise loaded state — fixes corrupted/stale cache from old versions
function migrateState(s) {
  if (!s) return s;
  const out = {...s};
  // nurses must be an array — old versions may have stored as object
  if (out.nurses && !Array.isArray(out.nurses)) out.nurses = NURSES_BASE;
  // leave must be an object
  if (!out.leave || typeof out.leave !== "object" || Array.isArray(out.leave)) out.leave = {};
  // sick must be an array
  if (!Array.isArray(out.sick)) out.sick = [];
  // settings must be an object
  if (!out.settings || typeof out.settings !== "object") out.settings = {};
  return out;
}

function seed() {
  return {
    sick: IS_DCO ? [{id:"s1",staffId:"r_schalcroft",name:"Susan Chalcroft",site:"sale",role:"receptionist",date:"2026-03-09",days:10,contact:"called",certified:true,rtw:false,reason:"illness",notes:"GP fit note. Return 23 March."}] : [],
    leave: {},
    clinicianLeave: {},  // { clinicianId: [{id, date, status, type:"annual"|"earlyFinish", hrsEarly}] }
    locums: [],
    nurses: IS_DCO ? NURSES_BASE : [],
    settings: { locumRate:20, defaultDays:28, partnerDays:42, nurseHourlyRate:14, recHourlyRate:12, financeMonth:"2026-04", anthropicKey:"", aiMasterEnabled:true,
      // HMRC employer NI — April 2025 rates
      niRate: 0.15,           // 15% (was 13.8% before Apr 2025)
      niThresholdWeekly: 96.15, // Secondary threshold £96.15/wk (was £175.96)
      // Auto-enrolment pension — 2025/26
      pensionRate: 0.03,      // 3% statutory minimum employer contribution
      pensionLowerWeekly: 123, // Lower earnings limit £123/wk (2025/26)
      pensionUpperWeekly: 967, // Upper earnings limit £967/wk
    },
    customClinicians: [],
    agencies: [
      {id:"a1",name:"Dental Nurses Direct",phone:"0161 000 0001",email:"book@dnd.co.uk",nurseRate:20,recRate:18,notes:"Preferred agency"},
      {id:"a2",name:"Medics on the Move",  phone:"0161 000 0002",email:"info@motm.co.uk",nurseRate:22,recRate:20,notes:""},
    ],
    rotaOverrides: {},
    staffRoles: {},
    spareTasks: {},
    nurseOnReception: {},
    rotaBreaks: {},
    roomAssignments: {},
    pairHistory: {},
    surgeryModes: {},
    activityLog: [],
    bankStaff: [],
    weeklyAvailability: {},
    staffOTPrefs: {},    // { staffId: { general: "happy"|"sometimes"|"noThanks", payReason, teamReason, hoursReason } }
    overtime: [],
    earlyFinish: [],
    latenessLog: [],
    welfareLog: [],
    lostKeys: [],
    historicalLeave: {},
    extraShiftRequests: [],
    unpaidLeaveRequests: [], // { id, staffId, name, site, dates[], reason, status }
    delegations: [],         // { id, fromStaffId, toStaffId, toName, site, startDate, endDate, active }
    betaFeedback: [],        // { id, userId, userName, date, text, status:"open"|"reviewed" }
    unsungHeroes: [],         // { staffId, name, site, reason, nominatedBy, date, type, status, dateISO, month }
    dismissedDrafts: [],      // draft IDs dismissed by principal
    siteKeyholders: {},       // { siteId: { opener: staffId, closer: staffId, backupOpener: staffId, backupCloser: staffId } }
    keyholderOverrides: {},   // { "date_siteId_role": { staffId, note } }
    recShiftOverrides: {},    // { "date_siteId": { assignments, flags } } — manual reception overrides
    recContracts: {},         // { staffId: { Mon:"early"|"full"|"late"|"off", Tue:..., ... } } — routine patterns
    userLastLogin: {},        // { userId: isoString } — tracks when each user last logged in
    welcomedAt: {},           // { userId: isoString } — first time arrival cinematic was shown. Distinct from userLastLogin so principals can replay arrival without resetting login history.
    userInvitedAt: {},        // { userId: isoString } — tracks when invite was sent
    wages: {},                 // { staffId: hourlyRate }
    archivedStaff: [],         // { id, name, site, archivedDate, reason }
    cpdEvents: [],           // { id, title, cpdType, site, staffIds[], date, allDay, startTime, endTime, notes, loggedBy }
    extraQuals: {},           // { staffId: { xray:bool, oralHyg:bool, implants:bool, dualArea:bool } }
    individualEntitlements: {}, // { staffId: { days, _lockedByContract } } — overrides default calc
    leaveYearConfig: {
      nurses:    "jan-dec",  // Jan 1 – Dec 31
      reception: "jan-dec",
      dentists:  "apr-mar",  // Apr 1 – Mar 31
    },
    bhOverrides: {},          // { date: bool } — true = practice open on this BH
    weekendSessions: [],      // { id, date, site, clinicianId, nurseId, notes }
    adHocClinics: [],         // { id, date, site, clinicianId, clinicianName, type, sedation:"none"|"ra"|"iv", nurseReq, notes, loggedBy }
    surgeryMaintenance: [],   // { id, site, surgeryNum, note, startDate, endDate, loggedBy }
    undoStack: [],            // last 10 snapshots for undo
    collegeDates: [],         // { id, date, site, groupLabel, staffIds[] }
    goodWorkLogs: [],         // { id, staffId, name, site, loggedBy, date, note, status:"pending"|"seen" }
    practiceEvents: [],       // { id, title, type, scope, sites[], staffIds[], date, endDate, attendance:"mandatory"|"expected"|"optional", notes, createdBy, appraisalPrivate }
    extraPatients: [],        // { id, staffId, name, site, date, count, reason, loggedBy }
    clinicianRequirements: {
      // { clinicianId: { default: ["ra","iv"], overrides: { Mon: [], Fri: ["ra"] }, note: "" } }
      "c_cwaith": { default:["ra","iv"], overrides:{}, note:"All sessions — IV sedation" },
      "c_matt":   { default:["ra","iv"], overrides:{}, note:"All MOS sessions — IV sedation" },
      "c_katy":   { default:["ra","iv"], overrides:{}, note:"All MOS sessions — IV sedation" },
      "c_jodie":  { default:["ra"],      overrides:{}, note:"All MOS sessions — RA required" },
      "c_beth":   { default:[], overrides:{ Fri:["ra"] }, note:"RA on Fridays (sedation alt week)" },
    },
    _saved: null,

    /* ── MULTI-TENANCY ──────────────────────────────────────────────────────
       setup_phase: "pending"     → Iris onboarding fires (new practice)
       setup_phase: "basics_done" → practice + sites saved, staff setup pending
       setup_phase: "complete"    → fully onboarded, Iris skipped on load
       setup_complete kept for backward compat with existing tenants
       DCO: both default to complete — data already seeded
    ──────────────────────────────────────────────────────────────────────── */
    setup_complete: IS_DCO,
    setup_phase: IS_DCO ? "complete" : "pending",
    practiceProfile: IS_DCO ? {
      name: "DCO Dental Group",
      sites: ["sale","brooklands","timperley"],
      principalName: "Conor O'Neill",
      onboardedAt: "2025-01-01",
    } : null,
    practiceSites: IS_DCO ? [
      {id:"sale",       name:"Sale",       color:"#818cf8", surgeries:5},
      {id:"brooklands", name:"Brooklands", color:"#a78bfa", surgeries:4},
      {id:"timperley",  name:"Timperley",  color:"#00e5a0", surgeries:4},
    ] : [],

    /* ── DATA FOUNDATIONS v8.3.0 ── */
    _meta: {
      author:    "Conor O'Neill",
      product:   "Omni Rota",
      created:   "2025",
      version:   "8.3.0",
      rights:    "© Conor O'Neill 2026. All rights reserved.",
    },
    rotaWeeks: {},
    briefings: [],
    notifications: [],
    leaveUndoLog: [],
    complianceTasks: [],
    complianceDelegations: {},
    skipComplianceTasks: false,
    // Issue viewers — staff IDs the principal has granted manager-level Issues
    // visibility to (T1+T2+repeat patterns). Tenant-agnostic. Set in
    // Admin → Settings → Issues. Mentioned in onboarding policy phase.
    issueViewers: [],
    chatLogs: [],          // { id, userId, userName, role, site, date, msgs[], tokensIn, tokensOut }
    gdprConsents: [],      // { userId, userName, email, timestamp }
    aiUsage: [],           // { id, userId, userName, role, site, date, tokensIn, tokensOut, costGBP }
    // ── NEWS FEED — managed via Omni Console ─────────────────────────────────
    // category: "law" | "dental" | "howto" | "podcast" | "omni"
    // pinned: true = always shows first regardless of date
    newsItems: [
      {id:"news_001",title:"Paternity leave is now a day-one right",category:"law",summary:"From April 2026 employees can take paternity leave from day one — no qualifying service period required. Same applies to unpaid parental leave.",date:"2026-04-01",pinned:true,link:""},
      {id:"news_002",title:"SSP now starts from day one of sickness",category:"law",summary:"The 3-day SSP waiting period has been removed. Every day of sickness is now potentially SSP-eligible from the first day.",date:"2026-04-01",pinned:true,link:""},
      {id:"news_003",title:"Whistleblowing protection extended",category:"law",summary:"Reporting sexual harassment in the workplace now qualifies for full whistleblowing protection. Omni Tier 3 reporting covers this.",date:"2026-04-01",pinned:false,link:""},
      {id:"news_004",title:"Holiday pay records must be kept for 6 years",category:"law",summary:"Employers must now retain detailed records showing compliance with holiday pay and entitlement rules for 6 years.",date:"2026-04-01",pinned:false,link:""},
      {id:"news_005",title:"How Iris spots a quiet week before it costs you",category:"howto",summary:"Quiet weeks aren't just overstaffing — they're a revenue opportunity. Here's how Omni turns idle time into booked clinics.",date:"2026-04-02",pinned:false,link:""},
      {id:"news_006",title:"Understanding your Iris Score",category:"howto",summary:"Your Iris Score isn't just a number — it's a weighted view of nurse competency, Bradford risk and lateness patterns. Tap any score card to drill down.",date:"2026-03-28",pinned:false,link:""},
    ],
    toilPolicy: {
      enabled: false,          // off until owner enables at onboarding
      rate: 1,                 // 1 = straight time, 1.5 = enhanced
      weekendRate: 1,          // separate rate for weekend/ad hoc work
      expiry: "yearEnd",       // "yearEnd" | "6months"
      alertThresholdHrs: 7.5,  // flag when balance exceeds this (1 day)
      maxBalanceHrs: 37.5,     // hard cap (1 week) — 0 = no cap
      payoutOnLeaving: true,   // pay unused TOIL when staff leaves
      approvalRequired: true,  // manager must approve TOIL requests to use
      onboarded: false,        // true once Iris has walked owner through policy
    },
    toilBalances: {},     // { staffId: [{id, hrs, rate, earnedDate, expiryDate, source, loggedBy}] }
    toilRequests: [],     // { id, staffId, name, site, dates[], hrsUsed, status, requestedAt, decidedBy, note }
    // ── CARERS LEAVE tracking (5 days statutory per leave year) ─────────────
    carersLeaveUsed: {},  // { staffId: { [leaveYear]: daysUsed } }
  };
}

function usePersist() {
  const [state, setState] = useState(null);
  const [ok, setOk] = useState(true);
  const [_sbStatus, _setSbStatus] = useState("connecting");
  const [_sbError, _setSbError] = useState(null);
  const [_syncToast, _setSyncToast] = useState(null); // "syncing" | "done:HH:MM" | null

  useEffect(() => {
    // 1. Load from localStorage immediately so UI appears fast
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      setState(raw ? migrateState({...seed(),...JSON.parse(raw)}) : seed());
    } catch(e) { setState(seed()); setOk(false); }

    // 2. Then pull from Supabase and merge on top
    _setSbStatus("connecting");
    sbGetAll().then(remote => {
      if (remote !== null) {
        setState(local => {
          if (!local) return local;
          const UI_KEYS = ["selWeek","selMonth","selDay","selDate","selDayIdx","selView","undoStack"];
          const safeRemote = Object.fromEntries(Object.entries(remote).filter(([k])=>!UI_KEYS.includes(k)));
          const _m = migrateState({...seed(),...local,...safeRemote});
          const merged = {..._m, settings: {..._m.settings, anthropicKey: getStoredApiKey()||local?.settings?.anthropicKey||_m.settings?.anthropicKey||""}};
          // ── SETUP STATE: always trust Supabase value if present ────────────────
          // Prevents onboarding re-firing when localStorage is cleared
          if (safeRemote.setup_complete !== undefined) {
            merged.setup_complete = safeRemote.setup_complete;
          } else if (IS_DCO) {
            merged.setup_complete = true;
            sbSet("setup_complete", true);
          }
          // setup_phase — backward compat: existing tenants with setup_complete=true
          // but no setup_phase get treated as complete automatically
          if (safeRemote.setup_phase !== undefined) {
            merged.setup_phase = safeRemote.setup_phase;
          } else if (safeRemote.setup_complete === true) {
            merged.setup_phase = "complete";
          } else if (IS_DCO) {
            merged.setup_phase = "complete";
          }
          // ── PASSKEY OFFERED FLAG: persist in state not just localStorage ────────
          const passkeyOfferedKey = `pr_passkey_offered`;
          if (safeRemote[passkeyOfferedKey] !== undefined) {
            merged[passkeyOfferedKey] = safeRemote[passkeyOfferedKey];
          }
          // ────────────────────────────────────────────────────────────────────────
          try { localStorage.setItem(STORAGE_KEY, JSON.stringify(merged)); } catch(e) {}
          return merged;
        });
        _setSbStatus("live");
        _setSbError(null);
        console.log("✅ Supabase sync complete");
      } else {
        _setSbStatus("offline");
        _setSbError("Could not reach Supabase — running on local storage only");
        console.warn("Supabase offline — local only");
      }
    }).catch(e => {
      _setSbStatus("offline");
      _setSbError(e?.message||"Network error");
    });

    // 3. Poll every 15 seconds for changes from other users
    const poll = setInterval(()=>{
      sbGetAll().then(remote=>{
        if (remote===null) return;
        setState(local=>{
          if (!local) return local;
          const UI_KEYS = ["selWeek","selMonth","selDay","selDate","selDayIdx","selView","undoStack"];
          const safeRemote = Object.fromEntries(Object.entries(remote).filter(([k])=>!UI_KEYS.includes(k)));
          const _m = migrateState({...seed(),...local,...safeRemote});
          const merged = {..._m, settings: {..._m.settings, anthropicKey: getStoredApiKey()||local?.settings?.anthropicKey||_m.settings?.anthropicKey||""}};
          // ── SETUP STATE: never downgrade in polling ──────────────────────────
          // React state may have been updated before Supabase write completes
          if (local?.setup_complete === true) {
            merged.setup_complete = true;
          }
          // Never downgrade setup_phase — complete > basics_done > pending
          const phaseRank = { pending: 0, basics_done: 1, complete: 2 };
          const localRank = phaseRank[local?.setup_phase] ?? 0;
          const mergedRank = phaseRank[merged.setup_phase] ?? 0;
          if (localRank > mergedRank) merged.setup_phase = local.setup_phase;
          // ─────────────────────────────────────────────────────────────────────
          try { localStorage.setItem(STORAGE_KEY, JSON.stringify(merged)); } catch(e) {}
          return merged;
        });
        _setSbStatus("live");
      }).catch(()=>{});
    }, 15000);

    return () => clearInterval(poll);
  }, []);

  const set = useCallback((fn, skipUndo=false) => {
    setState(prev => {
      const next = fn(prev);
      const snap = skipUndo ? (prev.undoStack||[]) : [...(prev.undoStack||[]).slice(-19), {...prev, undoStack:undefined}];
      const UI_SKIP = ["selWeek","selMonth","selDay","selDayIdx","selDate"];
      const withUndo = {...next, undoStack: snap};
      const toSave = Object.fromEntries(Object.entries(withUndo).filter(([k])=>!UI_SKIP.includes(k)));
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify({...toSave,_saved:new Date().toISOString()})); } catch(e) {}
      const changed = Object.keys(toSave).filter(k=>!k.startsWith("_")&&JSON.stringify(toSave[k])!==JSON.stringify(prev?.[k]));
      if (changed.length>0) {
        changed.forEach(k => {
          if (k==="settings") {
            // anthropicKey lives ONLY in localStorage — never sent to Supabase
            // so polling can never overwrite it
            const {anthropicKey:_ak, ...safeSettings} = (withUndo[k]||{});
            sbSet(k, safeSettings);
          } else {
            sbSet(k, withUndo[k]);
          }
        });
      }
      return withUndo;
    });
  }, []);

  const undo = useCallback(() => {
    setState(prev => {
      const stack = prev.undoStack||[];
      if (!stack.length) return prev;
      const snap = stack[stack.length-1];
      const restored = {...snap, undoStack: stack.slice(0,-1)};
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify({...restored,_saved:new Date().toISOString()})); } catch(e) {}
      return restored;
    });
  }, []);

  const sync = useCallback(() => {
    _setSbStatus("connecting");
    _setSyncToast("syncing");
    sbGetAll().then(remote=>{
      if (remote===null) { _setSbStatus("offline"); _setSyncToast(null); return; }
      setState(local=>{
        if (!local) return local;
        const UI_KEYS = ["selWeek","selMonth","selDay","selDate","selDayIdx","selView","undoStack"];
        const safeRemote = Object.fromEntries(Object.entries(remote).filter(([k])=>!UI_KEYS.includes(k)));
        const _m = migrateState({...seed(),...local,...safeRemote});
          const merged = {..._m, settings: {..._m.settings, anthropicKey: getStoredApiKey()||local?.settings?.anthropicKey||_m.settings?.anthropicKey||""}};
        if (!merged.leave || typeof merged.leave !== "object") merged.leave = {};
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(merged)); } catch(e) {}
        return merged;
      });
      _setSbStatus("live");
      const t = new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"});
      _setSyncToast(`done:${t}`);
      setTimeout(()=>_setSyncToast(null), 3000);
    }).catch(()=>{ _setSbStatus("offline"); _setSyncToast(null); });
  }, []);

  return { state, set, undo, ok, sbStatus: _sbStatus, sbError: _sbError, sync, syncToast: _syncToast };
}

// Helper — call inside any set() to append an activity log entry
// Usage: set(s => ({ ...yourChanges(s), ...addLog(s, user, type, msg) }))
/* ══════════════════════════════════
   INTELLIGENCE ENGINE
   Absence patterns · Swap analysis · Hiring alerts
══════════════════════════════════ */

function analyseAbsencePatterns(state) {
  const alerts = [];
  const sick = state.sick||[];
  const allStaff = [...(state.nurses||NURSES_BASE),...RECS_BASE].filter(s=>!s.maternity&&!s.archived);
  const dismissed = state.absenceAlerts||[];
  // Medical appointments and "left early ill" don't count towards Bradford Factor
  const isBradfordCounted = r => r.reason !== "medical" && r.reason !== "leftEarly";

  allStaff.forEach(s => {
    const recs = sick.filter(r=>r.staffId===s.id);
    const bfRecs = recs.filter(isBradfordCounted);
    if (bfRecs.length < 2) return;

    // Pattern: repeated same day of week
    const dowCounts = {};
    recs.forEach(r => {
      const dow = new Date(r.date).toLocaleDateString("en-GB",{weekday:"short"});
      dowCounts[dow] = (dowCounts[dow]||0) + 1;
    });
    Object.entries(dowCounts).forEach(([dow,count]) => {
      if (count >= 3) {
        const id = `abs_${s.id}_${dow}`;
        if (!dismissed.includes(id)) {
          alerts.push({
            id, type:"pattern", staffId:s.id, name:s.name, site:s.site,
            msg:`${s.name} has been absent ${count} ${dow}s — consider a welfare chat`,
            icon:"calendar", color:C.warn, action:"welfare",
            visibleTo:["principal","manager","managerLite"],
          });
        }
      }
    });

    // Pattern: frequent short absences (Bradford trigger)
    const episodes = bfRecs.length;
    const totalDays = bfRecs.reduce((a,r)=>a+r.days,0);
    const bf = episodes*episodes*totalDays;
    if (bf >= 100) {
      const id = `abs_bf_${s.id}`;
      if (!dismissed.includes(id)) {
        alerts.push({
          id, type:"bradford", staffId:s.id, name:s.name, site:s.site,
          msg:`${s.name} Bradford score ${bf} — formal review recommended`,
          icon:"⚠️", color:C.danger, action:"review",
          visibleTo:["principal","manager","managerLite"],
        });
      }
    }

    // Pattern: 3+ absences in last 8 weeks
    const eightWeeksAgo = new Date(); eightWeeksAgo.setDate(eightWeeksAgo.getDate()-56);
    const recentRecs = recs.filter(r=>new Date(r.date)>=eightWeeksAgo);
    if (recentRecs.length >= 3) {
      const id = `abs_recent_${s.id}`;
      if (!dismissed.includes(id)) {
        alerts.push({
          id, type:"frequent", staffId:s.id, name:s.name, site:s.site,
          msg:`${s.name} — ${recentRecs.length} absences in 8 weeks`,
          icon:"refresh", color:C.warn, action:"welfare",
          visibleTo:["principal","manager","managerLite"],
        });
      }
    }
  });

  // Site-level: above average sick rate
  Object.keys(SITES).forEach(siteId => {
    const siteStaff = allStaff.filter(s=>s.site===siteId);
    const siteSick  = sick.filter(s=>siteStaff.find(st=>st.id===s.staffId));
    const avgPerPerson = siteSick.length / Math.max(siteStaff.length,1);
    if (avgPerPerson > 3) {
      const id = `abs_site_${siteId}`;
      if (!(state.absenceAlerts||[]).includes(id)) {
        alerts.push({
          id, type:"site", site:siteId,
          msg:`${SITES[siteId].name} — high absence rate (avg ${avgPerPerson.toFixed(1)} episodes/person)`,
          icon:"hospital", color:C.danger, action:"review",
          visibleTo:["principal","manager","managerLite"],
        });
      }
    }
  });

  return alerts;
}

function analyseSwapPatterns(state) {
  const alerts = [];
  const swaps = state.swapRequests||[];
  if (swaps.length < 3) return alerts;
  const dismissed = state.absenceAlerts||[];

  // Per person frequency
  const personCounts = {};
  swaps.forEach(s => { personCounts[s.requesterId] = (personCounts[s.requesterId]||[]).concat(s); });
  Object.entries(personCounts).forEach(([id,reqs]) => {
    if (reqs.length >= 4) {
      const person = reqs[0];
      const alertId = `swap_person_${id}`;
      if (!dismissed.includes(alertId)) {
        alerts.push({
          id:alertId, type:"swap_person", staffId:id, name:person.requesterName, site:person.site,
          msg:`${person.requesterName} has requested ${reqs.length} swaps — consider a welfare conversation`,
          icon:"refresh", color:C.warn, action:"welfare",
          visibleTo:["principal","manager","managerLite"],
        });
      }
    }
  });

  // Multiple people avoiding same slot/day
  const slotCounts = {};
  swaps.forEach(s => {
    const key = `${s.site}_${s.originalDow}`;
    slotCounts[key] = (slotCounts[key]||0)+1;
  });
  Object.entries(slotCounts).forEach(([key,count]) => {
    if (count >= 4) {
      const [site,dow] = key.split("_");
      const alertId = `swap_slot_${key}`;
      if (!dismissed.includes(alertId)) {
        alerts.push({
          id:alertId, type:"swap_slot", site,
          msg:`${SITES[site]?.name} ${dow}s — ${count} swap requests. Consider reviewing that session.`,
          icon:"clipboard", color:C.warn, action:"rota_review",
          visibleTo:["principal","manager","managerLite"],
        });
      }
    }
  });

  // Swaps away from specific clinician — PRINCIPAL + PARTNERS ONLY
  const clinCounts = {};
  swaps.forEach(s => {
    if (s.avoidClinId) clinCounts[s.avoidClinId] = (clinCounts[s.avoidClinId]||[]).concat(s);
  });
  Object.entries(clinCounts).forEach(([clinId,reqs]) => {
    if (reqs.length >= 3) {
      const clin = CLINICIANS_BASE.find(c=>c.id===clinId);
      if (!clin) return;
      const alertId = `swap_clin_${clinId}`;
      if (!dismissed.includes(alertId)) {
        alerts.push({
          id:alertId, type:"swap_clinician", clinId, clinName:clin.name, site:clin.site,
          msg:`${reqs.length} swap requests involve sessions with ${clin.name} — flagged for principal review`,
          icon:"info", color:C.danger, action:"principal_review",
          visibleTo:["principal"],  // PRINCIPAL ONLY
          sensitive:true,
        });
      }
    }
  });

  return alerts;
}

function analyseHiringNeed(state) {
  const alerts = [];
  const dismissed = state.hiringAlerts||[];
  const locums = state.locums||[];

  // Locum spend > salary equivalent for 3 months running
  const MONTHS = ["2026-04","2026-05","2026-06","2026-07","2026-08","2026-09"];
  Object.keys(SITES).forEach(siteId => {
    const monthSpends = MONTHS.map(m => locums.filter(l=>l.site===siteId&&l.date.startsWith(m)).reduce((s,l)=>s+l.cost,0));
    const highMonths = monthSpends.filter(s=>s>800); // £800/month = ~£9600/year ~ part time nurse
    if (highMonths.length >= 2) {
      const totalSpend = monthSpends.reduce((a,b)=>a+b,0);
      const id = `hire_${siteId}_locum`;
      if (!dismissed.includes(id)) {
        alerts.push({
          id, type:"hiring_locum", site:siteId,
          msg:`${SITES[siteId].name} — £${Math.round(totalSpend)} locum spend. A part-time nurse (£${Math.round(totalSpend*0.8)}/yr contracted) may be more cost-effective.`,
          icon:"lightbulb", color:C.teal, action:"consider_hiring",
          visibleTo:["principal"],
        });
      }
    }
  });

  // Staff member leaving — assess impact
  const leaving = [...(state.nurses||NURSES_BASE),...RECS_BASE].filter(s=>s.leaving&&!s.archived);
  leaving.forEach(s => {
    const siteNurses = (state.nurses||NURSES_BASE).filter(n=>n.site===s.site&&!n.maternity&&!n.archived&&n.id!==s.id);
    const site = SITES[s.site];
    if (!site) return;
    const ratio = siteNurses.length / site.surgeries;
    if (ratio < 1.2) {
      const id = `hire_${s.id}_leaving`;
      if (!dismissed.includes(id)) {
        // Find which days they work
        const workDays = s.workDays ? s.workDays.join("/") : s.noFri ? "Mon-Thu" : "Mon-Fri";
        alerts.push({
          id, type:"hiring_leaving", site:s.site, staffId:s.id, name:s.name,
          msg:`${s.name} leaving ${s.leaving} — ${SITES[s.site].name} will drop to ${siteNurses.length} nurses for ${site.surgeries} surgeries (${workDays}). Replacement recommended.`,
          icon:"warning", color:C.danger, action:"consider_hiring",
          visibleTo:["principal","manager"],
        });
      }
    }
  });

  return alerts;
}

function analyseLeaveClashes(date, siteId, state) {
  const nurses = getNursesForSite(state, siteId);
  const recs = RECS_BASE.filter(r=>r.site===siteId);
  // Self-healing site lookup — for single-site practices (Flourish) where staff
  // site IDs may not match SITES keys, fall back to the only known site.
  const site = SITES[siteId] || Object.values(SITES)[0] || {surgeries:4,name:siteId||"Practice",color:"#3aad98"};
  const offNurses = nurses.filter(n=>isAbsent(state,n.id,date));
  const offRecs   = recs.filter(r=>isAbsent(state,r.id,date));
  const availNurses = nurses.length - offNurses.length;
  const availRecs   = recs.length   - offRecs.length;
  const nurseShort  = availNurses < site.surgeries;
  const recShort    = availRecs   < Math.ceil(site.surgeries/2);
  return { nurseShort, recShort, availNurses, availRecs, required:site.surgeries, offNurses, offRecs };
}


function addLog(state, actorName, type, msg) {
  const entry = {
    id: "log"+Date.now()+Math.random().toString(36).slice(2,6),
    ts: new Date().toISOString(),
    actor: actorName,
    type,   // "leave" | "absence" | "rota" | "pin" | "training" | "role"
    msg,
  };
  const existing = state.activityLog||[];
  return { activityLog: [entry, ...existing].slice(0, 200) }; // keep last 200
}

/* ── AUTH ── */
// Add email addresses here to enable Google/Apple login for each user.
// Principal can also manage these via the User Accounts panel in Directory.
const DEFAULT_USERS = [
  {id:"u_conor",    pin:"0000",role:"principal",   name:"Conor O'Neill",     site:"all",         active:true, email:"conor.oneill79@gmail.com"},
  {id:"u_msale",    pin:"1111",role:"manager",     name:"Megan Hampson",     site:"sale",        active:true, staffId:"r_mhampson", email:""},
  {id:"u_jbrook",   pin:"2222",role:"manager",     name:"Julie Whelan",      site:"brooklands",  active:true, staffId:"r_jwhelan",  email:"", managerSites:["brooklands","timperley"]},
  {id:"u_cgrimw",   pin:"8888",role:"managerLite", name:"Caitlin Grimwood",  site:"sale",        active:true, staffId:"n_cgrimwood", note:"Acting MOS Manager", email:""},
  {id:"u_hannah",   pin:"4444",role:"headNurse",   name:"Hannah Watson",     site:"sale",        active:true, staffId:"n_hwatson",   email:""},
  {id:"u_sophie",   pin:"5555",role:"staff",       name:"Sophie Black",      site:"sale",        active:true, staffId:"n_sblack",    email:""},
  {id:"u_liv",      pin:"6666",role:"headNurse",   name:"Liv Brennan",       site:"timperley",   active:true, staffId:"n_lbrennan",  email:""},
  {id:"u_christina",pin:"7777",role:"headNurse",   name:"Christina Hart",    site:"brooklands",  active:true, staffId:"n_chart",     email:""},
  {id:"u_cwaith",   pin:"9111",role:"clinician",   name:"Chris Waith",        site:"sale",        active:true, staffId:"c_cwaith",    email:""},
  {id:"u_matt",     pin:"9222",role:"clinician",   name:"Matt Dingle",        site:"sale",        active:true, staffId:"c_matt",      email:""},
  {id:"u_katy",     pin:"9333",role:"clinician",   name:"Katy Turner",        site:"sale",        active:true, staffId:"c_katy",      email:""},
  {id:"u_beth",     pin:"9444",role:"clinician",   name:"Beth Hodgeson",      site:"sale",        active:true, staffId:"c_beth",      email:""},
  {id:"u_simon",    pin:"9555",role:"clinician",   name:"Simon Cove",         site:"brooklands",  active:true, staffId:"c_simon",     email:""},
  {id:"u_markd",    pin:"9666",role:"clinician",   name:"Mark Dale",          site:"timperley",   active:true, staffId:"c_markd",     email:""},
];

// Half-day aware day counter — deduplicates by date to protect against accidental double entries
// Safe for records without a date field (e.g. sel array used in LeaveModal)
const countDays = (records) => {
  const seen = new Set();
  return records.reduce((a, l) => {
    if (l.date) {
      if (seen.has(l.date)) return a; // skip duplicate dates
      seen.add(l.date);
    }
    return a + (l.days||1);
  }, 0);
};

// Single source of truth for "is this person archived".
// A user is archived if EITHER their user record is inactive
// OR their linked staff record (nurse/rec) is marked archived.
// Without this, archiving in PeopleHub leaves the user record active
// and they continue to show in User Accounts and in the email whitelist.
function isStaffArchived(staffId, state) {
  if (!staffId) return false;
  const allNurses = state?.nurses || NURSES_BASE;
  const n = allNurses.find(x => x.id === staffId);
  if (n && n.archived) return true;
  const allRecs = [...RECS_BASE, ...((state?.customRecs)||[])];
  const r = allRecs.find(x => x.id === staffId);
  if (r && r.archived) return true;
  return false;
}

function resolveUsers(state, includeAll=false) {
  const saved = state?.users||[];

  // Normaliser — applied to whatever set we end up with.
  // Forces active:false for any user whose linked staff record is archived.
  const normalise = (list) => list.map(u =>
    isStaffArchived(u.staffId, state) ? {...u, active: false} : u
  );

  // Non-DCO tenants: only use state users — no hardcoded DCO staff
  if (!IS_DCO) {
    const out = normalise(saved);
    return out.filter(u => includeAll || u.active !== false);
  }

  // Merge DEFAULT_USERS with any overrides from state
  const merged = DEFAULT_USERS.map(u=>{
    const ov=saved.find(s=>s.id===u.id);
    return ov?{...u,...ov}:u;
  });
  // Append any state-only users (added via Add User — not in DEFAULT_USERS)
  const defaultIds = new Set(DEFAULT_USERS.map(u=>u.id));
  const stateOnly = saved.filter(u=>!defaultIds.has(u.id));
  const explicitIds = new Set([...DEFAULT_USERS.map(u=>u.id),...saved.map(u=>u.id)]);
  // Also include explicit staffIds already covered
  const coveredStaffIds = new Set([...DEFAULT_USERS,...saved].map(u=>u.staffId).filter(Boolean));

  // Auto-generate user records for all nurses not already in the system.
  // Skip archived nurses entirely — they shouldn't get an auto-user record.
  const autoNurses = NURSES_BASE
    .filter(n=>!coveredStaffIds.has(n.id) && !n.archived)
    .map(n=>({
      id:"u_"+n.id, staffId:n.id, name:n.name, site:n.site,
      role: n.headNurse?"headNurse":"staff",
      pin:"", email:"", active:true, _auto:true
    }));

  // Auto-generate for receptionists / TCOs / managers — same exclusion.
  const autoRecs = RECS_BASE
    .filter(r=>!coveredStaffIds.has(r.id) && !r.archived)
    .map(r=>({
      id:"u_"+r.id, staffId:r.id, name:r.name, site:r.site,
      role: r.manager?"manager":"staff",
      pin:"", email:"", active:true, _auto:true
    }));

  // Auto-generate for clinicians (clinicians use _deleted not archived)
  const autoClinicians = CLINICIANS_BASE
    .filter(c=>!coveredStaffIds.has(c.id) && !c._deleted)
    .map(c=>({
      id:"u_"+c.id, staffId:c.id, name:c.name, site:c.site,
      role:"clinician",
      pin:"", email:"", active:true, _auto:true
    }));

  const all = normalise([...merged, ...stateOnly, ...autoNurses, ...autoRecs, ...autoClinicians]);
  return includeAll ? all : all.filter(u=>u.active!==false);
}

/* ══════════════════════════════════
   DESIGN COMPONENTS
══════════════════════════════════ */

function Pill({c,t,sm}) {
  return <span style={{background:c+"18",color:c,border:`1px solid ${c}33`,borderRadius:99,padding:sm?"2px 8px":"3px 11px",fontSize:sm?10:11,fontWeight:600,whiteSpace:"nowrap",display:"inline-block"}}>{t}</span>;
}

const CS = {background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:20};
const IS = {width:"100%",background:"rgba(255,255,255,0.05)",border:`1px solid ${C.border}`,borderRadius:10,color:C.text,fontFamily:"inherit",fontSize:13,padding:"10px 14px",outline:"none"};
const LB = {fontSize:10,fontWeight:700,color:C.dim,letterSpacing:"0.08em",textTransform:"uppercase",display:"block",marginBottom:6};
const BTN = (c=C.teal,ghost=false) => ({padding:"9px 18px",borderRadius:10,border:`1px solid ${ghost?c+"44":c}`,background:ghost?"transparent":c,color:ghost?c:"#07060a",fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer"});

// Settings/cog gear-icon path — reused 4× across TAB_ICONS and the principal-only Settings button
const SVG_GEAR_PATH = "M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z";

/* ── Tab Icons — clean flat SVG ── */
const TAB_ICONS = {
  vision:     ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  pulsecheck: ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
  dashboard:  ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
  planning:   ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  sickness:   ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"/></svg>,
  attendance: ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  clinicians: ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  directory:  ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  wages:      ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
  finance:    ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  bank:       ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="22" x2="21" y2="22"/><line x1="6" y1="18" x2="6" y2="11"/><line x1="10" y1="18" x2="10" y2="11"/><line x1="14" y1="18" x2="14" y2="11"/><line x1="18" y1="18" x2="18" y2="11"/><polygon points="12 2 20 7 4 7"/></svg>,
  people:     ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  agencies:   ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  adhoc:      ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="14" x2="8" y2="18"/><line x1="12" y1="14" x2="12" y2="18"/></svg>,
  reports:    ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  myleave:    ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="16" y1="2" x2="16" y2="6"/></svg>,
  settings:   ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d={SVG_GEAR_PATH}/></svg>,
  issues:     ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  // v8.0 aliases
  today:  ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
  clock:  ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  rota:   ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  therm:  ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"/></svg>,
  users:  ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  admin:  ({c})=><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d={SVG_GEAR_PATH}/></svg>,
};

/* ── Icon — flat SVG icon library (Stripe/Linear style, 2px stroke) ── */
function Icon({name,size=16,color="currentColor",sw=2,style:extraStyle}) {
  const s={width:size,height:size,display:"inline-block",flexShrink:0,verticalAlign:"middle",...extraStyle};
  const p={fill:"none",stroke:color,strokeWidth:sw,strokeLinecap:"round",strokeLinejoin:"round"};
  switch(name){
    case "warning":      return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
    case "check":        return <svg style={s} viewBox="0 0 24 24" {...p}><polyline points="20 6 9 17 4 12"/></svg>;
    case "check-circle": return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>;
    case "x":            return <svg style={s} viewBox="0 0 24 24" {...p}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
    case "x-circle":     return <svg style={s} viewBox="0 0 24 24" {...p}><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>;
    case "calendar":     return <svg style={s} viewBox="0 0 24 24" {...p}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
    case "plane":        return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M17.8 19.2L16 11l3.5-3.5C21 6 21 4 19.5 2.5S18 2 16.5 3.5L13 7 4.8 5.2l-1.4 1.4 6.1 3.8L7.2 14H4l-1.5 1.5 3 3 3 3L10 20v-3.2l3.5-2.3 3.8 6.1 1.5-1.4"/></svg>;
    case "thermometer":  return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"/></svg>;
    case "key":          return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>;
    case "hospital":     return <svg style={s} viewBox="0 0 24 24" {...p}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8M8 12h8"/></svg>;
    case "bell":         return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>;
    case "refresh":      return <svg style={s} viewBox="0 0 24 24" {...p}><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>;
    case "user":         return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
    case "users":        return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
    case "phone":        return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.35 2 2 0 0 1 3.59 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.82a16 16 0 0 0 5.27 5.27l1.88-1.88a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>;
    case "clipboard":    return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>;
    case "syringe":      return <svg style={s} viewBox="0 0 24 24" {...p}><line x1="19" y1="2" x2="22" y2="5"/><path d="M17 5l3 3-2 2-3-3"/><path d="M4.5 19.5l10-10"/><path d="M14.5 9.5L9 15l-4 1 1-4 5.5-5.5"/></svg>;
    case "shield":       return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
    case "fire":         return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M8.5 14.5A2.5 2.5 0 0011 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 0 1-7 7 7 7 0 0 1-7-7c0-1.61.77-3.33 2-4.5"/></svg>;
    case "transfer":     return <svg style={s} viewBox="0 0 24 24" {...p}><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>;
    case "mic":          return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>;
    case "stop":         return <svg style={s} viewBox="0 0 24 24" fill={color} stroke="none"><rect x="4" y="4" width="16" height="16" rx="2"/></svg>;
    case "keyboard":     return <svg style={s} viewBox="0 0 24 24" {...p}><rect x="2" y="6" width="20" height="12" rx="2"/><line x1="6" y1="10" x2="6" y2="10"/><line x1="10" y1="10" x2="10" y2="10"/><line x1="14" y1="10" x2="14" y2="10"/><line x1="18" y1="10" x2="18" y2="10"/><line x1="8" y1="14" x2="16" y2="14"/></svg>;
    case "lightbulb":    return <svg style={s} viewBox="0 0 24 24" {...p}><line x1="9" y1="18" x2="15" y2="18"/><line x1="10" y1="22" x2="14" y2="22"/><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/></svg>;
    case "star":         return <svg style={s} viewBox="0 0 24 24" {...p}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
    case "star-filled":  return <svg style={s} viewBox="0 0 24 24" fill={color} stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
    case "trophy":       return <svg style={s} viewBox="0 0 24 24" {...p}><polyline points="8 20 12 16 16 20"/><path d="M2 7h20"/><path d="M5 7l1 12a2 2 0 002 2h8a2 2 0 002-2l1-12"/><path d="M12 7V3"/><path d="M8 3h8"/></svg>;
    case "award":        return <svg style={s} viewBox="0 0 24 24" {...p}><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg>;
    case "sun":          return <svg style={s} viewBox="0 0 24 24" {...p}><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>;
    case "umbrella":     return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M23 12a11.05 11.05 0 0 0-22 0zm-5 7a3 3 0 0 1-6 0v-7"/></svg>;
    case "lock":         return <svg style={s} viewBox="0 0 24 24" {...p}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;
    case "book":         return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>;
    case "sparkle":      return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M12 3l1.5 4.5H18l-3.75 2.75 1.5 4.75-3.75-2.75L8.25 15l1.5-4.75L6 7.5h4.5z"/></svg>;
    case "bolt":         return <svg style={s} viewBox="0 0 24 24" {...p}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>;
    case "plus":         return <svg style={s} viewBox="0 0 24 24" {...p}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
    case "chat":         return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>;
    case "home":         return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
    case "tooth":        return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M12 2C7.5 2 4 5.5 4 8c0 1.5.3 2.5.8 3.5L7 22h2l1-5 2 3 2-3 1 5h2l2.2-10.5c.5-1 .8-2 .8-3.5 0-3.5-3.5-6-8-6z"/></svg>;
    case "grad-cap":     return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>;
    case "person-md":    return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/><line x1="12" y1="11" x2="12" y2="15"/><line x1="10" y1="13" x2="14" y2="13"/></svg>;
    case "mail":         return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>;
    case "settings":     return <svg style={s} viewBox="0 0 24 24" {...p}><circle cx="12" cy="12" r="3"/><path d={SVG_GEAR_PATH}/></svg>;
    case "robot":        return <svg style={s} viewBox="0 0 24 24" {...p}><rect x="3" y="11" width="18" height="10" rx="2"/><path d="M9 11V9a3 3 0 0 1 6 0v2"/><circle cx="9" cy="16" r="1" fill={color}/><circle cx="15" cy="16" r="1" fill={color}/><path d="M12 7V5"/></svg>;
    case "archive":      return <svg style={s} viewBox="0 0 24 24" {...p}><polyline points="21 8 21 21 3 21 3 8"/><rect x="1" y="3" width="22" height="5"/><line x1="10" y1="12" x2="14" y2="12"/></svg>;
    case "restore":      return <svg style={s} viewBox="0 0 24 24" {...p}><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.51"/></svg>;
    case "sunrise":      return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M17 18a5 5 0 0 0-10 0"/><line x1="12" y1="2" x2="12" y2="9"/><line x1="4.22" y1="10.22" x2="5.64" y2="11.64"/><line x1="1" y1="18" x2="3" y2="18"/><line x1="21" y1="18" x2="23" y2="18"/><line x1="18.36" y1="11.64" x2="19.78" y2="10.22"/><line x1="23" y1="22" x2="1" y2="22"/><polyline points="8 6 12 2 16 6"/></svg>;
    case "sunset":       return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M17 18a5 5 0 0 0-10 0"/><line x1="12" y1="9" x2="12" y2="2"/><line x1="4.22" y1="10.22" x2="5.64" y2="11.64"/><line x1="1" y1="18" x2="3" y2="18"/><line x1="21" y1="18" x2="23" y2="18"/><line x1="18.36" y1="11.64" x2="19.78" y2="10.22"/><line x1="23" y1="22" x2="1" y2="22"/><polyline points="16 5 12 9 8 5"/></svg>;
    case "clock":        return <svg style={s} viewBox="0 0 24 24" {...p}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
    case "arrow-left":   return <svg style={s} viewBox="0 0 24 24" {...p}><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>;
    case "repeat":       return <svg style={s} viewBox="0 0 24 24" {...p}><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>;
    case "run":          return <svg style={s} viewBox="0 0 24 24" {...p}><circle cx="13" cy="4" r="2"/><path d="M7 22l2-7 3 3 4-9"/><path d="M17 22l-2-7"/><path d="M6 12l2-4 4 1 2 5"/></svg>;
    case "globe":        return <svg style={s} viewBox="0 0 24 24" {...p}><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>;
    case "happy":        return <svg style={s} viewBox="0 0 24 24" {...p}><circle cx="12" cy="12" r="10"/><path d="M8 13s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>;
    case "meh":          return <svg style={s} viewBox="0 0 24 24" {...p}><circle cx="12" cy="12" r="10"/><line x1="8" y1="15" x2="16" y2="15"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>;
    case "frown":        return <svg style={s} viewBox="0 0 24 24" {...p}><circle cx="12" cy="12" r="10"/><path d="M16 16s-1.5-2-4-2-4 2-4 2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg>;
    case "building":     return <svg style={s} viewBox="0 0 24 24" {...p}><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M3 15h18M9 3v18M15 3v18"/></svg>;
    case "bank-cols":    return <svg style={s} viewBox="0 0 24 24" {...p}><line x1="3" y1="22" x2="21" y2="22"/><line x1="6" y1="18" x2="6" y2="11"/><line x1="10" y1="18" x2="10" y2="11"/><line x1="14" y1="18" x2="14" y2="11"/><line x1="18" y1="18" x2="18" y2="11"/><polygon points="12 2 20 7 4 7"/></svg>;
    case "stethoscope":  return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6 6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3"/><path d="M8 15v1a6 6 0 0 0 6 6 6 6 0 0 0 6-6v-4"/><circle cx="20" cy="10" r="2"/></svg>;
    case "bandage":      return <svg style={s} viewBox="0 0 24 24" {...p}><rect x="5.636" y="5.636" width="12.728" height="12.728" rx="3" transform="rotate(45 12 12)"/><line x1="12" y1="10" x2="12" y2="14"/><line x1="10" y1="12" x2="14" y2="12"/></svg>;
    case "droplet":      return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>;
    case "needle":       return <svg style={s} viewBox="0 0 24 24" {...p}><line x1="2" y1="22" x2="22" y2="2"/><path d="M7.5 13.5 11 10l3 3-3.5 3.5"/><path d="M14 6l4 4"/><path d="M18 2l4 4-1.5 1.5-4-4z"/></svg>;
    case "flask":        return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M9 3h6v8l3 6a2 2 0 0 1-1.8 3H7.8A2 2 0 0 1 6 17l3-6V3z"/><line x1="6" y1="11" x2="18" y2="11"/></svg>;
    case "radiation":    return <svg style={s} viewBox="0 0 24 24" {...p}><circle cx="12" cy="12" r="2"/><path d="M12 10V2M12 14v8M10.27 11l-6.93-4M13.73 13l6.93 4M13.73 11l6.93-4M10.27 13l-6.93 4"/></svg>;
    case "cash":         return <svg style={s} viewBox="0 0 24 24" {...p}><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01M18 12h.01"/></svg>;
    case "scale":        return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M16 16l3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1z"/><path d="M2 16l3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1z"/><path d="M7 21h10M12 3v18M3 7h2.5m13 0H21"/></svg>;
    case "info":         return <svg style={s} viewBox="0 0 24 24" {...p}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
    case "chart-bar":    return <svg style={s} viewBox="0 0 24 24" {...p}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>;
    default:             return null;
  }
}

/* ── Dot — inline status dot (replaces colored circle emojis 🔴🟡🟢🔵) ── */
const Dot = ({color,size=10}) => <span style={{display:"inline-block",width:size,height:size,borderRadius:"50%",background:color,flexShrink:0,verticalAlign:"middle"}}/>;

/* ── Omni. Logo Mark ── */
function LogoMark({size=36, onTeal=false}) {
  const fs = Math.round(size * 0.52);
  if (onTeal) {
    return (
      <div style={{width:size,height:size,borderRadius:"50%",background:"#181626",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:"0 4px 24px rgba(0,0,0,0.18)"}}>
        <span style={{fontFamily:"'Instrument Serif',Georgia,serif",fontStyle:"italic",fontWeight:700,fontSize:Math.round(size*0.5),color:"#3aad98",letterSpacing:"-0.03em",lineHeight:1}}>O</span>
      </div>
    );
  }
  return (
    <div style={{display:"flex",alignItems:"baseline",flexShrink:0}}>
      <span style={{fontFamily:"'Instrument Serif',Georgia,serif",fontStyle:"italic",fontWeight:700,fontSize:fs,color:C.text,letterSpacing:"-0.04em",lineHeight:1}}>Omni</span>
      <span style={{fontFamily:"'Instrument Serif',Georgia,serif",fontStyle:"italic",fontWeight:700,fontSize:fs,color:"#3aad98",marginLeft:"0.08em",lineHeight:1}}>.</span>
    </div>
  );
}

/* ── Overlay ── */
function Overlay({children,onClose,wide}) {
  const isMob = window.innerWidth < 600;
  return (
    <div onClick={e=>e.target===e.currentTarget&&onClose()} style={{position:"fixed",inset:0,background:"rgba(7,6,18,0.75)",zIndex:600,display:"flex",alignItems:isMob?"flex-end":"center",justifyContent:"center",padding:isMob?0:20,overflowY:isMob?"hidden":"auto",backdropFilter:"blur(6px)"}}>
      <div style={{background:"#1c1a28",border:`1px solid ${C.border}`,borderRadius:isMob?"20px 20px 0 0":"16px",padding:isMob?"24px 20px 36px":"32px",maxWidth:wide?700:540,width:"100%",boxShadow:"0 24px 64px rgba(0,0,0,0.2)",maxHeight:isMob?"94vh":"90vh",overflowY:"auto"}}>
        {isMob&&<div style={{width:40,height:4,borderRadius:99,background:C.border,margin:"0 auto 18px"}}/>}
        {children}
      </div>
    </div>
  );
}

/* ── Login ── */
// Mode: "main" | "email" | "pin" | "claim" | "reset"
/* ── ERROR BOUNDARY ─────────────────────────────────────────────── */
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = {error:null, info:null}; }
  static getDerivedStateFromError(e) { return {error:e}; }
  componentDidCatch(e, info) {
    this.setState({info});
    console.error("Omni crash:", e, info);
    // Auto-copy error on crash — don't make them click anything
    const msg = `${e?.message}\n\n${e?.stack?.slice(0,600)}`;
    try {
      navigator.clipboard.writeText(msg).catch(()=>{
        const ta = document.createElement("textarea");
        ta.value = msg; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        try { document.execCommand("copy"); } catch(e) {}
        document.body.removeChild(ta);
      });
    } catch(e) {}
  }
  render() {
    if (this.state.error) {
      const msg = `${this.state.error?.message}\n\n${this.state.error?.stack?.slice(0,600)}`;
      return (
        <div style={{padding:24,fontFamily:"sans-serif",background:"#181626",minHeight:"100vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
          <div style={{maxWidth:480,width:"100%"}}>
            <div style={{fontSize:32,marginBottom:12,textAlign:"center"}}>⚠️</div>
            <div style={{fontSize:18,fontWeight:700,color:"#ff4646",marginBottom:8,textAlign:"center"}}>Omni ran into a problem</div>
            <div style={{fontSize:13,color:"rgba(255,255,255,0.4)",marginBottom:16,textAlign:"center"}}>Error auto-copied — paste it to Team Omni</div>
            <pre style={{fontSize:10,background:"#181626",padding:12,borderRadius:8,overflowX:"auto",whiteSpace:"pre-wrap",wordBreak:"break-all",marginBottom:16,maxHeight:200,overflowY:"auto"}}>
              {msg}
            </pre>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>{
                try {
                  navigator.clipboard.writeText(msg).catch(()=>{
                    // Fallback for mobile/no-permission
                    const ta = document.createElement("textarea");
                    ta.value = msg; ta.style.position = "fixed"; ta.style.opacity = "0";
                    document.body.appendChild(ta); ta.focus(); ta.select();
                    document.execCommand("copy"); document.body.removeChild(ta);
                  });
                } catch(e) {
                  const ta = document.createElement("textarea");
                  ta.value = msg; ta.style.position = "fixed"; ta.style.opacity = "0";
                  document.body.appendChild(ta); ta.focus(); ta.select();
                  try { document.execCommand("copy"); } catch(e) {}
                  document.body.removeChild(ta);
                }
              }} style={{flex:1,padding:"10px",background:"#181626",border:"1px solid rgba(255,255,255,0.15)",borderRadius:8,fontSize:13,cursor:"pointer",color:"rgba(255,255,255,0.7)",fontFamily:"inherit"}}>
                Copy error
              </button>
              <button onClick={()=>window.location.reload()} style={{flex:1,padding:"10px",background:"#3aad98",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:600,cursor:"pointer"}}>
                Reload Omni
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ═══════════════════════════════════════════════════════════
   TRAFFIC LIGHT ENGINE — v8.0
   Returns {color:"red"|"amber"|"green", reasons:[]}
   Visibility: headNurse = attendance/Bradford only (no welfare)
               manager   = own site, all flags
               principal = all sites, all flags
═══════════════════════════════════════════════════════════ */
function getStaffFlag(person, state, viewerRole, viewerSite) {
  if (!person) return {color:"green", reasons:[]};
  const id = person.id;

  // Visibility gate
  const isHN      = viewerRole === "headNurse";
  const isManager = viewerRole === "manager" || viewerRole === "managerLite";
  const isPrinc   = viewerRole === "principal";
  const canSeeWelfare = isManager || isPrinc;

  const reasons = [];
  let highest = "green"; // green < amber < red
  const bump = (level, msg) => {
    reasons.push({level, msg});
    if (level === "red") highest = "red";
    else if (level === "amber" && highest !== "red") highest = "amber";
  };

  // ── Bradford Factor ─────────────────────────────────────
  const sickRecords = (state.sick||[]).filter(s=>s.staffId===id||s.name===person.name);
  const episodes = sickRecords.length;
  const totalDays = sickRecords.reduce((s,r)=>s+(r.days||1),0);
  const bf = episodes * episodes * totalDays;
  if (bf >= 200) bump("red",   `Bradford score ${bf} — critical`);
  else if (bf >= 100) bump("amber", `Bradford score ${bf} — review recommended`);

  // ── RTW outstanding — nurses/reception only, not clinicians ─
  const isClinician = !!(person.sched||["GDS","MOS","FD","Therapist","Partner","Specialist"].includes(person.type));
  if (!isClinician) {
    const rtwPending = sickRecords.filter(s=>!s.rtw);
    if (rtwPending.length > 0) {
      const oldest = rtwPending.sort((a,b)=>a.date?.localeCompare(b.date))[0];
      const daysSince = oldest?.date
        ? Math.round((Date.now() - new Date(oldest.date+"T12:00:00")) / 86400000)
        : 0;
      if (daysSince >= 2) bump("red",   `RTW interview ${daysSince}d overdue`);
      else bump("amber", "RTW interview outstanding");
    }
  }

  // ── 3+ absences in rolling 90 days ──────────────────────
  const ninetyDaysAgo = new Date(Date.now() - 90*86400000).toISOString().split("T")[0];
  const recentAbsences = sickRecords.filter(s=>(s.date||"")>=ninetyDaysAgo).length;
  if (recentAbsences >= 3) bump("amber", `${recentAbsences} absences in last 90 days`);

  // ── Welfare / manual flags (manager+ only) ───────────────
  if (canSeeWelfare) {
    const wf = (state.welfareAccess||{})[id];
    if (wf) bump("red", "Welfare flag — manager review needed");
    const mFlag = (state.staffManualFlags||{})[id];
    if (mFlag?.color === "red")   bump("red",   mFlag.reason||"Manual red flag");
    if (mFlag?.color === "amber") bump("amber", mFlag.reason||"Manual amber flag");
  }

  // ── Compliance expiry ────────────────────────────────────
  const comp = person.compliance||{};
  const today = new Date().toISOString().split("T")[0];
  const in30  = new Date(Date.now()+30*86400000).toISOString().split("T")[0];
  if (comp.firstAiderExpiry) {
    if (comp.firstAiderExpiry < today)   bump("amber", "First Aid cert expired");
    else if (comp.firstAiderExpiry < in30) bump("amber", "First Aid cert expiring soon");
  }
  if (comp.fireMarshalExpiry) {
    if (comp.fireMarshalExpiry < today)  bump("amber", "Fire Marshal cert expired");
    else if (comp.fireMarshalExpiry < in30) bump("amber", "Fire Marshal cert expiring soon");
  }

  return {color: highest, reasons};
}

const FLAG_COLORS = {
  red:   {bg:"#fef2f2", border:"#fecaca", dot:"#dc2626", text:"#991b1b"},
  amber: {bg:"#fff7ed", border:"#fed7aa", dot:"#d97706", text:"#f5c518"},
  green: {bg:"#f0fdf4", border:"#bbf7d0", dot:"#16a34a", text:"#166534"},
};

function FlagDot({color, size=10, style={}}) {
  const fc = FLAG_COLORS[color]||FLAG_COLORS.green;
  return (
    <div style={{
      width:size, height:size, borderRadius:"50%",
      background:fc.dot, flexShrink:0,
      boxShadow:color==="red"?"0 0 0 3px #fecaca":color==="amber"?"0 0 0 3px #fed7aa":"none",
      ...style
    }}/>
  );
}

/* ═══════════════════════════════════════════════════════════
   PERSON FILE PANEL — full staff record in one place
═══════════════════════════════════════════════════════════ */
function PersonFilePanel({person, state, set, user, onClose}) {
  const [subTab, setSubTab] = React.useState("overview");
  const isPrincipal = user.role==="principal";
  const isManager   = user.role==="manager"||user.role==="managerLite";
  const canEdit     = isPrincipal||isManager;
  const flag = getStaffFlag(person, state, user.role, user.site);
  const fc   = FLAG_COLORS[flag.color];

  // Leave data — filtered to current leave year
  // Clinicians use clinicianLeave, staff use leave
  const isClin = !!(person._isClinician || person.type?.toLowerCase().includes("partner") ||
    ["gds","mos","fd","therapist","specialist"].includes((person.type||"").toLowerCase()));
  const rawLeave = isClin
    ? ((state.clinicianLeave||{})[person.id]||[])
    : (state.leave?.[person.id]||[]);
  const hist       = getHistDays(state, person.id);
  const entit      = getEntitlement(state, person);
  const bhDays     = getBankHolidayDays(person);
  const bhProrated = Math.round(bhDays * ((person.hrs||37.5)/37.5) * 10) / 10;
  const bookable   = Math.max(0, Math.round((entit - bhProrated)*10)/10);
  const lyCardType = roleTypeForPerson(person);
  const lyCardOv = (state.leaveYearOverrides||{})[person.id];
  const lyCardCfg = lyCardOv?{[lyCardType]:lyCardOv}:state.leaveYearConfig;
  const {start:lyCardS,end:lyCardE} = getLeaveYear(lyCardType, lyCardCfg);
  const lyCardStart = lyCardS.toISOString().split("T")[0];
  const lyCardEnd   = lyCardE.toISOString().split("T")[0];
  // For clinicians: annual leave within this leave year; for staff: same
  const existing = rawLeave.filter(l=>l.date>=lyCardStart&&l.date<=lyCardEnd);
  const used = isClin
    ? existing.filter(l=>l.type==="annual"&&l.status!=="declined").length
    : countDays(existing.filter(l=>l.status!=="declined"&&(!l.leaveType||l.leaveType==="annual")));
  const remaining  = Math.round((bookable - used - hist)*10)/10;

  // Sickness data
  const sickRecords = (state.sick||[]).filter(s=>s.staffId===person.id||s.name===person.name)
    .sort((a,b)=>b.date?.localeCompare(a.date));
  const episodes = sickRecords.length;
  const totalDays = sickRecords.reduce((s,r)=>s+(r.days||1),0);
  const bf = episodes * episodes * totalDays;
  const bfBand = bf>=200?"Critical":bf>=100?"High":bf>=45?"Caution":"Low";
  const bfColor = bf>=200?C.danger:bf>=100?C.warn:bf>=45?"#d97706":C.green;

  // Manual flag setter
  function setManualFlag(color, reason) {
    set(s=>({...s,
      staffManualFlags:{...(s.staffManualFlags||{}),[person.id]:{color,reason,setBy:user.name,setAt:new Date().toISOString()}}
    }));
  }
  function clearManualFlag() {
    set(s=>{const f={...(s.staffManualFlags||{})};delete f[person.id];return{...s,staffManualFlags:f};});
  }

  const manualFlag = (state.staffManualFlags||{})[person.id];
  const [flagNote, setFlagNote] = React.useState(manualFlag?.reason||"");
  const [flagging, setFlagging] = React.useState(false);
  const [showHistLeave, setShowHistLeave] = React.useState(false);
  const [editMode, setEditMode] = React.useState(false);
  const [form, setForm] = React.useState({
    name:person.name||"", hrs:person.hrs||37.5, phone:person.phone||"",
    email:person.email||"", note:person.note||"", noOT:!!person.noOT,
    startDate:person.startDate||"", effectiveDate:new Date().toISOString().split("T")[0],
    hrsChangeNote:"", workDays:person.workDays||["Mon","Tue","Wed","Thu","Fri"],
    extraSites:person.extraSites||[], site:person.site||Object.keys(SITES)[0],
    status:person.status||"qualified", ra:!!person.ra, iv:!!person.iv,
    area:person.area||"GDS", safeguardingL3:!!person.safeguardingL3,
    fireMarshal:!!person.fireMarshal, firstAider:!!person.firstAider,
    keyholder:!!person.keyholder,
  });
  const sf=(k,v)=>setForm(p=>({...p,[k]:v}));
  const isNurse=!!(person.area||person.status);

  function saveEdit() {
    const hrsChanged=+form.hrs!==(person.hrs||37.5);
    const effectiveDate=form.effectiveDate||new Date().toISOString().split("T")[0];
    let hrsHist={};
    if(hrsChanged){
      const existing=person.hrsHistory||[];
      const seed=existing.length===0?[{hrs:person.hrs||37.5,from:"2025-01-01",changedBy:"original contract",note:""}]:existing;
      hrsHist={hrsHistory:[...seed,{hrs:+form.hrs,from:effectiveDate,changedBy:user?.name||"Unknown",note:form.hrsChangeNote||""}].sort((a,b)=>a.from.localeCompare(b.from))};
    }
    const topLevel={safeguardingL3:!!form.safeguardingL3,fireMarshal:!!form.fireMarshal,firstAider:!!form.firstAider,keyholder:!!form.keyholder};
    const updates={name:form.name,hrs:+form.hrs,phone:form.phone,email:form.email,note:form.note,noOT:form.noOT,startDate:form.startDate,workDays:form.workDays,extraSites:form.extraSites,site:form.site,...(isNurse?{status:form.status,ra:form.ra,iv:form.iv,area:form.area,...topLevel}:{}),...hrsHist};
    if(isNurse){set(s=>({...s,nurses:(s.nurses||NURSES_BASE).map(n=>n.id===person.id?{...n,...updates}:n)}));}
    else{set(s=>({...s,customRecs:(s.customRecs||[]).map(r=>r.id===person.id?{...r,...updates}:r)}));}
    setEditMode(false);
  }

  function archivePerson() {
    if(!window.confirm(`Archive ${person.name}? They won't appear on the rota.`)) return;
    if(isNurse){set(s=>({...s,nurses:(s.nurses||NURSES_BASE).map(n=>n.id===person.id?{...n,archived:true,archivedAt:new Date().toISOString()}:n)}));}
    else{set(s=>({...s,customRecs:(s.customRecs||[]).map(r=>r.id===person.id?{...r,archived:true,archivedAt:new Date().toISOString()}:r)}));}
    onClose();
  }

  const SUB_TABS = [
    {id:"overview",   label:"Overview"},
    {id:"leave",      label:"Leave"},
    {id:"sickness",   label:"Sickness"},
    {id:"compliance", label:"Compliance"},
    ...(isPrincipal?[{id:"pay",   label:"Pay"}]:[]),
    ...(canEdit?      [{id:"flags",label:"Flags"}]:[]),
  ];

  const sd = SITES[person.site];
  const st = person.status ? STATUS[person.status] : null;

  return (
    <div style={{position:"fixed",inset:0,zIndex:600,display:"flex",alignItems:"flex-end",justifyContent:"center",background:"rgba(7,6,18,0.75)",backdropFilter:"blur(6px)"}}>
      <div style={{background:C.surface,borderRadius:"28px 28px 0 0",width:"100%",maxWidth:640,height:"88vh",overflowY:"auto",boxShadow:"0 -8px 40px rgba(0,0,0,0.2)"}}>
        {/* Drag handle */}
        <div style={{width:40,height:4,borderRadius:99,background:C.border,margin:"14px auto 0"}}/>

        {/* Header */}
        <div style={{padding:"16px 22px 0",display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <div style={{width:48,height:48,borderRadius:16,background:fc.bg,border:`1.5px solid ${fc.border}`,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <FlagDot color={flag.color} size={14}/>
            </div>
            <div>
              <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:18,fontWeight:700,color:C.heading}}>{person.name}</div>
              <div style={{fontSize:12,color:sd?.color||C.dim,fontWeight:500}}>
                {sd?.name} · {person.type||person.area||"Staff"} · {person.hrs||37.5}h/wk
              </div>
            </div>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            {canEdit&&!isClin&&(
              <button onClick={()=>setEditMode(m=>!m)} style={{
                padding:"6px 14px",borderRadius:8,fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer",
                background:editMode?C.teal:"transparent",border:`1px solid ${editMode?C.teal:C.border}`,
                color:editMode?"#fff":C.mid,transition:"all 0.15s",
              }}>{editMode?"Cancel edit":"✏️ Edit"}</button>
            )}
            <button onClick={onClose} style={{background:"none",border:"none",fontSize:22,color:C.dim,cursor:"pointer",padding:4,lineHeight:1}}>×</button>
          </div>
        </div>

        {/* Flag reasons */}
        {flag.reasons.length>0&&(
          <div style={{margin:"10px 22px 0",display:"flex",flexWrap:"wrap",gap:6}}>
            {flag.reasons.map((r,i)=>(
              <span key={i} style={{
                fontSize:11,fontWeight:600,padding:"3px 9px",borderRadius:99,
                background:FLAG_COLORS[r.level].bg,
                color:FLAG_COLORS[r.level].text,
                border:`1px solid ${FLAG_COLORS[r.level].border}`,
              }}>{r.msg}</span>
            ))}
          </div>
        )}

        {/* Sub-tabs — hidden in edit mode */}
        {!editMode&&(
          <div style={{display:"flex",gap:0,padding:"14px 22px 0",borderBottom:`1px solid ${C.border}`}}>
            {SUB_TABS.map(t=>(
              <button key={t.id} onClick={()=>setSubTab(t.id)} style={{
                background:"none",border:"none",fontFamily:"inherit",fontSize:13,fontWeight:600,
                color:subTab===t.id?C.teal:C.mid,cursor:"pointer",padding:"6px 14px",
                borderBottom:subTab===t.id?`2px solid ${C.teal}`:"2px solid transparent",
                transition:"color 0.15s",
              }}>{t.label}</button>
            ))}
          </div>
        )}

        {/* ── EDIT MODE FORM ── */}
        {editMode&&(
          <div style={{padding:"20px 22px 36px"}}>
            {/* Section: Basic info */}
            <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Basic info</div>
            <div style={{marginBottom:12}}><label style={LB}>Full name</label><input value={form.name} onChange={e=>sf("name",e.target.value)} style={IS}/></div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
              <div><label style={LB}>Phone</label><input type="tel" value={form.phone} onChange={e=>sf("phone",e.target.value)} placeholder="07700 000000" style={IS}/></div>
              <div><label style={LB}>Email</label><input type="email" value={form.email} onChange={e=>sf("email",e.target.value)} placeholder="name@email.com" style={IS}/></div>
            </div>
            <div style={{marginBottom:16}}><label style={LB}>Notes</label><input value={form.note} onChange={e=>sf("note",e.target.value)} placeholder="e.g. RA trained, no Fridays" style={IS}/></div>

            {/* Section: Contract */}
            <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10,marginTop:4}}>Contract</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
              <div>
                <label style={LB}>Primary site</label>
                <select value={form.site} onChange={e=>sf("site",e.target.value)} style={IS}>
                  {Object.entries(SITES).map(([id,s])=><option key={id} value={id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label style={LB}>Hours / week</label>
                <input type="number" step="0.5" min="1" max="50" value={form.hrs}
                  onChange={e=>sf("hrs",+e.target.value)}
                  style={{...IS,borderColor:+form.hrs!==(person.hrs||37.5)?C.teal:undefined}}/>
              </div>
            </div>
            {+form.hrs!==(person.hrs||37.5)&&(
              <div style={{background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:10,padding:"12px 14px",marginBottom:12}}>
                <div style={{fontSize:12,fontWeight:700,color:C.green,marginBottom:8}}>Hours changing: {person.hrs||37.5}h → {form.hrs}h/wk</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                  <div><label style={LB}>Effective from</label><input type="date" value={form.effectiveDate} onChange={e=>sf("effectiveDate",e.target.value)} style={IS}/></div>
                  <div><label style={LB}>Reason (audit log)</label><input value={form.hrsChangeNote} onChange={e=>sf("hrsChangeNote",e.target.value)} placeholder="e.g. Agreed reduction" style={IS}/></div>
                </div>
              </div>
            )}
            <div style={{marginBottom:12}}>
              <label style={LB}>Start date <span style={{fontWeight:400,color:C.dim}}>(pro-rates holiday)</span></label>
              <input type="date" value={form.startDate} onChange={e=>sf("startDate",e.target.value)} style={IS}/>
            </div>
            <div style={{marginBottom:12,padding:"12px 14px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:10}}>
              <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Working days</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {["Mon","Tue","Wed","Thu","Fri"].map(d=>{
                  const on=(form.workDays||[]).includes(d);
                  return (
                    <button key={d} onClick={()=>sf("workDays",on?(form.workDays||[]).filter(x=>x!==d):[...(form.workDays||[]),d].sort((a,b)=>["Mon","Tue","Wed","Thu","Fri"].indexOf(a)-["Mon","Tue","Wed","Thu","Fri"].indexOf(b)))}
                      style={{padding:"7px 14px",borderRadius:99,fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer",border:`1px solid ${on?C.teal:C.border}`,background:on?C.teal+"18":"transparent",color:on?C.teal:C.mid}}>
                      {d}
                    </button>
                  );
                })}
              </div>
            </div>
            {Object.keys(SITES).length>1&&(
              <div style={{marginBottom:12}}>
                <label style={LB}>Also works at</label>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {Object.entries(SITES).filter(([id])=>id!==form.site).map(([id,s])=>{
                    const on=(form.extraSites||[]).includes(id);
                    return (
                      <button key={id} onClick={()=>sf("extraSites",on?(form.extraSites||[]).filter(x=>x!==id):[...(form.extraSites||[]),id])}
                        style={{padding:"6px 14px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${on?s.color:C.border}`,background:on?s.color+"18":"transparent",color:on?s.color:C.mid}}>
                        {on?"✓ ":""}{s.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {isNurse&&(
              <label style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,cursor:"pointer"}}>
                <input type="checkbox" checked={form.noOT} onChange={e=>sf("noOT",e.target.checked)} style={{width:16,height:16,accentColor:C.teal}}/>
                <div><div style={{fontSize:12,fontWeight:600,color:C.text}}>No overtime</div><div style={{fontSize:11,color:C.dim}}>Excluded from overtime suggestions</div></div>
              </label>
            )}

            {/* Section: Qualifications (nurses only) */}
            {isNurse&&(
              <div>
                <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10,marginTop:4}}>Qualifications</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
                  <div>
                    <label style={LB}>Competency level</label>
                    <select value={form.status} onChange={e=>sf("status",e.target.value)} style={IS}>
                      {Object.entries(STATUS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={LB}>Area</label>
                    <select value={form.area} onChange={e=>sf("area",e.target.value)} style={IS}>
                      <option value="GDS">GDS</option><option value="MOS">MOS</option><option value="MOS/GDS">Both</option>
                    </select>
                  </div>
                </div>
                <div style={{display:"flex",gap:16,marginBottom:16}}>
                  {[["ra","Relative Anaesthesia"],["iv","IV Sedation"]].map(([k,label])=>(
                    <label key={k} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
                      <input type="checkbox" checked={!!form[k]} onChange={e=>sf(k,e.target.checked)} style={{width:15,height:15,accentColor:C.teal}}/>
                      <span style={{fontSize:12,fontWeight:600,color:C.text}}>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Section: Compliance roles (nurses only) */}
            {isNurse&&(
              <div>
                <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Compliance roles</div>
                <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
                  {[
                    ["safeguardingL3","🛡 Level 3 Safeguarding","CQC Reg 13"],
                    ["firstAider","🧯 First Aider","H&S First-Aid Regs 1981"],
                    ["fireMarshal","🔥 Fire Marshal","Fire Safety Order 2005"],
                    ["keyholder","🔑 Keyholder","Practice access"],
                  ].map(([k,label,basis])=>(
                    <label key={k} style={{display:"flex",alignItems:"center",gap:12,padding:"9px 12px",borderRadius:9,border:`1px solid ${form[k]?"rgba(0,229,160,0.3)":C.border}`,background:form[k]?"rgba(0,229,160,0.06)":"transparent",cursor:"pointer"}}>
                      <input type="checkbox" checked={!!form[k]} onChange={e=>sf(k,e.target.checked)} style={{width:16,height:16,accentColor:C.teal,flexShrink:0}}/>
                      <div>
                        <div style={{fontSize:12,fontWeight:700,color:form[k]?C.green:C.text}}>{label}</div>
                        <div style={{fontSize:10,color:C.dim}}>{basis}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Save / Archive */}
            <div style={{display:"flex",gap:8,marginTop:8}}>
              <button onClick={saveEdit} style={{...BTN(C.teal),flex:1,padding:"11px 0",fontSize:13}}>Save changes</button>
              <button onClick={()=>setEditMode(false)} style={BTN(C.dim,true)}>Cancel</button>
            </div>
            {isPrincipal&&(
              <button onClick={archivePerson}
                style={{...BTN(C.danger,true),width:"100%",marginTop:10,fontSize:12,padding:"9px 0"}}>
                Archive {person.name.split(" ")[0]} — removes from rota
              </button>
            )}
          </div>
        )}

        <div style={{padding:"20px 22px 36px",display:editMode?"none":"block"}}>

          {/* ── OVERVIEW ── */}
          {subTab==="overview"&&(
            <div>
              {/* Clinician schedule this week */}
              {isClin&&person.sched&&(
                <div style={{marginBottom:18}}>
                  <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>Schedule</div>
                  <div style={{display:"flex",flexDirection:"column",gap:4}}>
                    {["Mon","Tue","Wed","Thu","Fri"].filter(d=>person.sched[d]).map(d=>(
                      <div key={d} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 10px",borderRadius:8,background:C.bg,border:`1px solid ${C.border}`}}>
                        <span style={{fontSize:12,fontWeight:700,color:C.mid,minWidth:32}}>{d}</span>
                        <span style={{fontSize:12,color:C.text}}>{person.sched[d]}</span>
                        {person.prefNurse&&<span style={{fontSize:10,color:C.dim,marginLeft:"auto"}}>Pref: {(state.nurses||NURSES_BASE).find(n=>n.id===person.prefNurse)?.name?.split(" ")[0]||"—"}</span>}
                      </div>
                    ))}
                  </div>
                  <button onClick={()=>window.dispatchEvent(new CustomEvent("omni:navigate",{detail:{tab:"admin",subTab:"clinicians"}}))}
                    style={{...BTN(C.teal,true),fontSize:11,padding:"6px 14px",marginTop:10}}>
                    Edit schedule →
                  </button>
                </div>
              )}
              {/* Stat cards — adapted for clinicians */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:18}}>
                {[
                  ["Holiday remaining", remaining+"d", remaining<5?C.danger:remaining<10?C.warn:C.green],
                  ["Bradford score",    bf.toString(), bfColor],
                  isClin
                    ? ["Type", person.type||"Clinician", sd?.color||C.teal]
                    : ["Contract", (person.hrs||37.5)+"h/wk", C.teal],
                ].map(([label,val,color])=>(
                  <div key={label} style={{background:C.bg,borderRadius:12,padding:"12px",border:`1px solid ${C.border}`}}>
                    <div style={{fontSize:10,color:C.dim,marginBottom:4,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em"}}>{label}</div>
                    <div style={{fontSize:22,fontWeight:800,color,fontFamily:"'Instrument Sans',system-ui,sans-serif"}}>{val}</div>
                  </div>
                ))}
              </div>
              {/* Bradford context note for clinicians */}
              {isClin&&bf>0&&(
                <div style={{background:"rgba(245,197,24,0.07)",border:"1px solid rgba(245,197,24,0.2)",borderRadius:8,padding:"9px 12px",marginBottom:14,fontSize:11,color:C.warn}}>
                  ⚠️ {episodes} absence episode{episodes!==1?"s":""} · {totalDays} day{totalDays!==1?"s":""} · Each episode likely means cancelled patients. Bradford: {bfBand}.
                </div>
              )}
              {st&&<div style={{marginBottom:8,display:"flex",gap:6}}><Pill c={st.color} t={st.label} sm/></div>}
              {person.note&&<div style={{fontSize:12,color:C.dim,marginBottom:10,background:C.bg,borderRadius:8,padding:"8px 12px"}}>{person.note}</div>}
              {person.phone&&<div style={{fontSize:12,color:C.teal,marginBottom:4}}>📞 {person.phone}</div>}
              {person.email&&<div style={{fontSize:12,color:C.mid,marginBottom:4}}>✉️ {person.email}</div>}
              {/* Hours history */}
              {(person.hrsHistory||[]).length>0&&(
                <div style={{marginTop:14}}>
                  <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>Hours history</div>
                  {[...person.hrsHistory].sort((a,b)=>b.from.localeCompare(a.from)).map((h,i)=>(
                    <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:11,padding:"5px 0",borderBottom:`1px solid ${C.border2}`}}>
                      <span style={{fontWeight:600}}>{h.hrs}h/wk</span>
                      <span style={{color:C.dim}}>from {new Date(h.from+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</span>
                      <span style={{color:C.muted}}>{h.changedBy}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── LEAVE ── */}
          {subTab==="leave"&&(
            <div>
              {/* Onboarding / pre-system setup banner */}
              {(isPrincipal||(user.role==="manager"||user.role==="managerLite"))&&(
                <div style={{background:hist>0?"#f0fdf4":"#eff6ff",border:`1px solid ${hist>0?"#bbf7d0":"#bfdbfe"}`,borderRadius:12,padding:"12px 14px",marginBottom:14,display:"flex",justifyContent:"space-between",alignItems:"center",gap:12}}>
                  <div>
                    <div style={{fontSize:12,fontWeight:700,color:hist>0?C.green:"#1d4ed8",marginBottom:2}}>
                      {hist>0?`${hist}d pre-system leave recorded`:"⚠️ Pre-system leave not set"}
                    </div>
                    <div style={{fontSize:11,color:C.dim}}>
                      {hist>0?"Days taken before Omni Rota — counted against entitlement":"Enter days taken before Omni Rota was set up"}
                    </div>
                  </div>
                  <button onClick={()=>setShowHistLeave(true)}
                    style={{...BTN(hist>0?C.teal:"#1d4ed8"),fontSize:11,padding:"6px 14px",flexShrink:0,whiteSpace:"nowrap"}}>
                    {hist>0?"Edit":"Set up"}
                  </button>
                </div>
              )}

              <div style={{marginBottom:16}}>
                {[
                  ["Entitlement",  entit+"d",      C.text],
                  ["Bank holidays",bhProrated+"d", C.dim],
                  ["Bookable",     bookable+"d",   C.text],
                  ["Used",         (used+hist)+"d",C.warn],
                  ["Remaining",    remaining+"d",  remaining<5?C.danger:remaining<10?C.warn:C.green],
                ].map(([l,v,c])=>(
                  <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${C.border2}`,fontSize:13}}>
                    <span style={{color:C.dim}}>{l}</span>
                    <span style={{fontWeight:700,color:c}}>{v}</span>
                  </div>
                ))}
              </div>
              <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Leave history</div>
              {(()=>{
                const approved=existing.filter(l=>l.status!=="declined").map(l=>l.date).sort();
                if(approved.length===0) return <div style={{fontSize:12,color:C.muted}}>No leave records yet.</div>;
                // Group strictly consecutive calendar days only (no weekend bridging)
                const groups=[];
                let gs=approved[0],ge=approved[0];
                for(let i=1;i<approved.length;i++){
                  const diff=Math.round((new Date(approved[i]+"T12:00:00")-new Date(ge+"T12:00:00"))/86400000);
                  if(diff===1){ge=approved[i];}
                  else{groups.push({s:gs,e:ge});gs=approved[i];ge=approved[i];}
                }
                groups.push({s:gs,e:ge});
                const fmt=d=>new Date(d+"T12:00:00").toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"});
                return groups.sort((a,b)=>b.s.localeCompare(a.s)).map(({s,e},i)=>{
                  const days=Math.round((new Date(e+"T12:00:00")-new Date(s+"T12:00:00"))/86400000)+1;
                  const label=s===e?fmt(s):`${fmt(s)} – ${fmt(e)} · ${days}d`;
                  return (
                    <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:11,padding:"6px 0",borderBottom:`1px solid ${C.border2}`}}>
                      <span style={{color:C.text,fontWeight:600}}>{label}</span>
                      <span style={{color:C.green,fontWeight:600}}>approved</span>
                    </div>
                  );
                });
              })()}

              {/* Historical leave modal */}
              {showHistLeave&&<HistoricalLeaveModal person={person} state={state} set={set} onClose={()=>setShowHistLeave(false)}/>}
            </div>
          )}

          {/* ── SICKNESS ── */}
          {subTab==="sickness"&&(
            <div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:16}}>
                {[
                  ["Episodes",      episodes.toString(), C.text],
                  ["Total days",    totalDays.toString(), C.warn],
                  ["Bradford",      bf.toString(),       bfColor],
                ].map(([l,v,c])=>(
                  <div key={l} style={{background:C.bg,borderRadius:10,padding:"10px 12px",border:`1px solid ${C.border}`}}>
                    <div style={{fontSize:10,color:C.dim,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:3}}>{l}</div>
                    <div style={{fontSize:20,fontWeight:800,color:c,fontFamily:"'Instrument Sans',system-ui,sans-serif"}}>{v}</div>
                  </div>
                ))}
              </div>
              <div style={{background:FLAG_COLORS[bfBand==="Critical"?"red":bfBand==="High"?"red":bfBand==="Caution"?"amber":"green"].bg,
                border:`1px solid ${FLAG_COLORS[bfBand==="Critical"?"red":bfBand==="High"?"red":bfBand==="Caution"?"amber":"green"].border}`,
                borderRadius:8,padding:"8px 12px",marginBottom:14,fontSize:12,
                color:FLAG_COLORS[bfBand==="Critical"?"red":bfBand==="High"?"red":bfBand==="Caution"?"amber":"green"].text,
                fontWeight:600}}>Bradford band: {bfBand}</div>
              <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Absence history</div>
              {sickRecords.length===0
                ? <div style={{fontSize:12,color:C.muted}}>No sickness records.</div>
                : sickRecords.map((s,i)=>(
                  <div key={i} style={{padding:"8px 0",borderBottom:`1px solid ${C.border2}`}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:11}}>
                      <span style={{color:C.text,fontWeight:600}}>{s.date}</span>
                      <span style={{color:C.dim}}>{s.days||1}d · {s.reason||"Illness"}</span>
                      <span style={{color:s.rtw?C.green:C.warn,fontWeight:600}}>{s.rtw?"RTW done":"RTW pending"}</span>
                    </div>
                    {(s.fitNotes||[]).length>0&&(
                      <div style={{fontSize:10,color:C.green,marginTop:3}}>📄 {s.fitNotes.length} fit note{s.fitNotes.length!==1?"s":""} on file</div>
                    )}
                    {s.rtwDate&&(
                      <div style={{fontSize:10,color:C.dim,marginTop:2}}>RTW interview: {s.rtwDate} · {s.rtwBy}</div>
                    )}
                  </div>
                ))
              }
              {sickRecords.some(s=>!s.rtw)&&canEdit&&(
                <div style={{marginTop:12,padding:"10px 12px",background:"rgba(255,70,70,0.08)",border:"1px solid rgba(255,70,70,0.2)",borderRadius:8,fontSize:12,color:C.danger,fontWeight:600}}>
                  RTW interview outstanding — go to Sickness tab to complete it
                </div>
              )}
            </div>
          )}

          {/* ── COMPLIANCE ── */}
          {subTab==="compliance"&&(
            <div>
              {/* Competency level */}
              <div style={{marginBottom:18}}>
                <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>Competency</div>
                {(()=>{
                  const lvlMap={qualified:{label:"Qualified",col:C.green},nearQual:{label:"Near Qualifying",col:C.teal},developing:{label:"Developing",col:"#60a5fa"},trainee:{label:"Trainee",col:C.warn},newStarter:{label:"New Starter",col:C.danger}};
                  const lvl=lvlMap[person.status]||{label:person.status||"Not set",col:C.dim};
                  return (
                    <div style={{display:"inline-flex",alignItems:"center",gap:8,background:lvl.col+"18",border:`1px solid ${lvl.col}40`,borderRadius:10,padding:"8px 14px"}}>
                      <div style={{width:8,height:8,borderRadius:"50%",background:lvl.col,flexShrink:0}}/>
                      <span style={{fontSize:13,fontWeight:700,color:lvl.col}}>{lvl.label}</span>
                    </div>
                  );
                })()}
              </div>
              {/* Qualifications */}
              <div style={{marginBottom:18}}>
                <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>Qualifications</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                  {[
                    ["Relative Anaesthesia", person.ra,     C.teal],
                    ["Intravenous Sedation",  person.iv,     "#a78bfa"],
                    ["MOS",                   person.area?.includes("MOS"), "#60a5fa"],
                  ].map(([label,held,col])=>(
                    <div key={label} style={{display:"flex",alignItems:"center",gap:6,padding:"7px 12px",borderRadius:9,border:`1px solid ${held?col+"40":C.border}`,background:held?col+"10":"transparent"}}>
                      <span style={{fontSize:12,color:held?col:C.muted}}>{held?"✓":"–"}</span>
                      <span style={{fontSize:12,fontWeight:600,color:held?col:C.muted}}>{label}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* Compliance roles */}
              <div style={{marginBottom:18}}>
                <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>Compliance roles</div>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {[
                    ["🛡 Level 3 Safeguarding",  person.safeguardingL3, "CQC Reg 13"],
                    ["🧯 First Aider",            person.firstAider,     "H&S First-Aid Regs 1981"],
                    ["🔥 Fire Marshal",            person.fireMarshal,    "Fire Safety Order 2005"],
                    ["🔑 Keyholder",               person.keyholder,      "Practice access"],
                    ["👩‍⚕️ Head Nurse",              person.headNurse,     "Site lead"],
                  ].map(([label,held,basis])=>(
                    <div key={label} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 12px",borderRadius:9,border:`1px solid ${held?"rgba(0,229,160,0.2)":C.border}`,background:held?"rgba(0,229,160,0.05)":"transparent"}}>
                      <div>
                        <span style={{fontSize:12,fontWeight:700,color:held?C.green:C.muted}}>{label}</span>
                        <span style={{fontSize:10,color:C.dim,marginLeft:8}}>{basis}</span>
                      </div>
                      <span style={{fontSize:11,fontWeight:700,color:held?C.green:C.muted}}>{held?"Held":"–"}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* CPD this year */}
              {(()=>{
                const yr=new Date().getFullYear()+"";
                const cpdDone=(state.cpdEvents||[]).filter(e=>(e.staffIds||[]).includes(person.id)&&(e.date||"").startsWith(yr));
                return (
                  <div>
                    <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>CPD this year</div>
                    {cpdDone.length===0
                      ? <div style={{fontSize:12,color:C.muted}}>No CPD sessions logged yet.</div>
                      : cpdDone.sort((a,b)=>b.date.localeCompare(a.date)).map((e,i)=>(
                        <div key={i} style={{display:"flex",justifyContent:"space-between",fontSize:11,padding:"6px 0",borderBottom:`1px solid ${C.border2}`}}>
                          <span style={{color:C.text,fontWeight:600}}>{e.title}</span>
                          <span style={{color:C.dim}}>{e.date}</span>
                        </div>
                      ))
                    }
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── PAY (principal only) ── */}
          {subTab==="pay"&&isPrincipal&&(
            <div>
              {isClin?(
                <div>
                  <div style={{background:"rgba(58,173,152,0.08)",border:"1px solid rgba(58,173,152,0.2)",borderRadius:12,padding:"16px",marginBottom:16}}>
                    <div style={{fontSize:13,fontWeight:700,color:C.teal,marginBottom:6}}>Associate / clinician pay</div>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.6)",lineHeight:1.7}}>
                      Clinician pay is managed separately — associate percentages, lab fee deductions and UDA reconciliation will be part of Omni Finance when it launches.
                    </div>
                  </div>
                  <button onClick={()=>window.dispatchEvent(new CustomEvent("omni:navigate",{detail:{tab:"admin",subTab:"finance"}}))}
                    style={{...BTN(C.teal,true),fontSize:12,padding:"9px 16px"}}>
                    View Finance →
                  </button>
                </div>
              ):(()=>{
                const raw=state.wages?.[person.id];
                const w=typeof parseWage==="function"?parseWage(raw):{type:"hourly",rate:null,annual:null};
                const monthly=typeof wageMonthly==="function"?wageMonthly(w,person.hrs||37.5):0;
                const annual=typeof wageAnnual==="function"?wageAnnual(w,person.hrs||37.5):0;
                const hasWage=w.rate||w.annual;
                return (
                  <div>
                    {!hasWage&&(
                      <div style={{background:"rgba(245,197,24,0.08)",border:"1px solid rgba(245,197,24,0.2)",borderRadius:10,padding:"12px 14px",marginBottom:16,fontSize:12,color:C.warn}}>
                        No pay rate set. Set it in Finance → Wages.
                      </div>
                    )}
                    {hasWage&&(
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:18}}>
                        {[
                          [w.type==="salaried"?"Annual salary":"Hourly rate", w.type==="salaried"?`£${(w.annual||0).toLocaleString()}`:`£${w.rate}/hr`, C.teal],
                          ["Est. monthly",  `£${monthly.toLocaleString()}`, C.text],
                          ["Est. annual",   `£${annual.toLocaleString()}`,  C.dim],
                        ].map(([lbl,val,col])=>(
                          <div key={lbl} style={{background:C.bg,borderRadius:12,padding:"12px",border:`1px solid ${C.border}`}}>
                            <div style={{fontSize:10,color:C.dim,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:4}}>{lbl}</div>
                            <div style={{fontSize:18,fontWeight:800,color:col,fontFamily:"'Instrument Sans',system-ui,sans-serif"}}>{val}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{display:"flex",gap:8,marginTop:8}}>
                      <button onClick={()=>window.dispatchEvent(new CustomEvent("omni:navigate",{detail:{tab:"admin",subTab:"wages"}}))}
                        style={{...BTN(C.teal,true),fontSize:12,padding:"9px 16px"}}>
                        {hasWage?"Edit in Finance →":"Set pay rate →"}
                      </button>
                    </div>
                    <div style={{fontSize:10,color:C.dim,marginTop:12,lineHeight:1.6}}>
                      Monthly estimate based on contracted hours × hourly rate × 52 weeks ÷ 12. Actual payroll in Finance → Payroll.
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── FLAGS (manager+) ── */}
          {subTab==="flags"&&canEdit&&(
            <div>
              {manualFlag?(
                <div style={{background:FLAG_COLORS[manualFlag.color].bg,border:`1px solid ${FLAG_COLORS[manualFlag.color].border}`,borderRadius:12,padding:"14px",marginBottom:14}}>
                  <div style={{fontSize:13,fontWeight:700,color:FLAG_COLORS[manualFlag.color].text,marginBottom:4}}>
                    {manualFlag.color==="red"?"🔴":"🟠"} Current flag: {manualFlag.reason||"No reason set"}
                  </div>
                  <div style={{fontSize:11,color:C.dim}}>Set by {manualFlag.setBy} · {manualFlag.setAt?new Date(manualFlag.setAt).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}):""}</div>
                  <button onClick={clearManualFlag} style={{...BTN(C.danger,true),fontSize:11,marginTop:10,padding:"5px 12px"}}>Clear flag</button>
                </div>
              ):(
                <div style={{fontSize:12,color:C.dim,marginBottom:14}}>No manual flag set. Automatic flags from Bradford, RTW and compliance still apply.</div>
              )}
              {!flagging?(
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setFlagging(true)} style={{...BTN(C.warn),padding:"9px 18px",fontSize:12}}>🟠 Set amber flag</button>
                  <button onClick={()=>setFlagging("red")} style={{...BTN(C.danger),padding:"9px 18px",fontSize:12}}>🔴 Set red flag</button>
                </div>
              ):(
                <div style={{background:C.bg,borderRadius:12,padding:"14px",border:`1px solid ${C.border}`}}>
                  <label style={LB}>Reason for flag</label>
                  <input value={flagNote} onChange={e=>setFlagNote(e.target.value)}
                    placeholder="e.g. Attendance pattern under review" style={{...IS,marginBottom:10}}/>
                  <div style={{display:"flex",gap:8}}>
                    <button onClick={()=>{setManualFlag(flagging==="red"?"red":"amber",flagNote);setFlagging(false);}}
                      style={{...BTN(flagging==="red"?C.danger:C.warn),flex:1,padding:"9px 0",fontSize:12}}>Save flag</button>
                    <button onClick={()=>setFlagging(false)} style={BTN(C.dim,true)}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PEOPLE HUB — v8.0
   Traffic light staff list + person file on tap
═══════════════════════════════════════════════════════════ */
function PeopleHub({state, set, user, isPrincipal}) {
  const [selectedPerson, setSelectedPerson] = React.useState(null);
  const [q, setQ] = React.useState("");
  const [siteFilter, setSiteFilter] = React.useState(isPrincipal?"all":user.site);
  const [roleFilter, setRoleFilter] = React.useState("all");
  const [flagFilter, setFlagFilter] = React.useState("all");
  const [showPP, setShowPP] = React.useState(false);
  const [addStaffM, setAddStaffM] = React.useState(false);
  const [showArchived, setShowArchived] = React.useState(false);
  // v10.x — User Accounts moved here from Settings. Owner + manager only.
  // "team" = the flagged people list (current default). "accounts" = login/invite management.
  const isManagerRole = user.role==="manager"||user.role==="managerLite";
  const canSeeAccounts = isPrincipal || isManagerRole;
  const [peopleView, setPeopleView] = React.useState("team");

  function addStaffFromHub(f) {
    if(f.type==="clinician"){
      window.dispatchEvent(new CustomEvent("omni:navigate",{detail:{tab:"admin",subTab:"clinicians"}}));
      return;
    }
    const id=(f.type==="nurse"?"n_":"r_")+f.name.toLowerCase().replace(/\s+/g,"_")+"_"+Date.now().toString().slice(-4);
    const uId="u_"+id;
    const userRecord={id:uId,staffId:id,name:f.name,site:f.site,role:"staff",pin:"",email:f.email||"",active:true};
    const common={...(f.phone?{phone:f.phone}:{}),...(f.email?{email:f.email}:{}),...(f.startDate?{startDate:f.startDate}:{}),note:f.notes||undefined};
    if(f.type==="nurse"){
      const newNurse={id,name:f.name,site:f.site,area:f.area||"GDS",status:f.status||"trainee",hrs:f.hrs,...common,...(f.workDays?.length<5?{workDays:f.workDays}:{})};
      set(s=>{const ns={...s,nurses:[...(s.nurses||NURSES_BASE),newNurse],users:[...(s.users||[]),userRecord]};
        if(f.proRatedEntitlement!=null) ns.individualEntitlements={...(s.individualEntitlements||{}),[id]:{days:f.proRatedEntitlement,_lockedByContract:false}};
        return ns;
      });
    } else {
      const typeLabel=f.type==="tco"?"TCO":f.type==="manager"?"Manager":"Reception";
      const newRec={id,name:f.name,site:f.site,hrs:f.hrs,type:typeLabel,...common,recStatus:"active",...(f.workDays?.length<5?{workDays:f.workDays}:{})};
      set(s=>{const ns={...s,customRecs:[...(s.customRecs||[]),newRec],users:[...(s.users||[]),userRecord]};
        if(f.proRatedEntitlement!=null) ns.individualEntitlements={...(s.individualEntitlements||{}),[id]:{days:f.proRatedEntitlement,_lockedByContract:false}};
        return ns;
      });
    }
    setAddStaffM(false);
  }

  const isHN      = user.role==="headNurse";
  const isManager = user.role==="manager"||user.role==="managerLite";

  // Build person list based on role
  const allNurses = (state.nurses||NURSES_BASE).filter(n=>!n.archived);
  const allRecs   = [...RECS_BASE,...(state.customRecs||[])].filter(r=>!r.archived);
  const allClins  = getClinicians(state).filter(c=>!c._deleted);

  // Head nurses only see nurses on their site
  let people = isHN
    ? allNurses.filter(n=>n.site===user.site)
    : isPrincipal||isManager
      ? [
          ...( roleFilter==="all"||roleFilter==="nurses"      ? allNurses : []),
          ...( roleFilter==="all"||roleFilter==="reception"   ? allRecs   : []),
          ...( roleFilter==="all"||roleFilter==="clinicians"  ? allClins.map(c=>({...c, _isClinician:true})) : []),
        ]
      : [];

  // Filter
  if (siteFilter!=="all") people = people.filter(p=>p.site===siteFilter);
  if (q) people = people.filter(p=>p.name.toLowerCase().includes(q.toLowerCase()));

  // Sort: red first, then amber, then green
  const flagOrder = {red:0,amber:1,green:2};
  const flagged = people.map(p=>({p, flag:getStaffFlag(p,state,user.role,user.site)}))
    .sort((a,b)=>flagOrder[a.flag.color]-flagOrder[b.flag.color]||(a.p.name.localeCompare(b.p.name)));

  // Apply flag filter after sorting
  const filtered = flagFilter==="all" ? flagged : flagged.filter(f=>f.flag.color===flagFilter);

  const redCount   = flagged.filter(f=>f.flag.color==="red").length;
  const amberCount = flagged.filter(f=>f.flag.color==="amber").length;

  return (
    <div>
      {/* v10.x — People sub-nav: Your Team | User Accounts (owner + manager only) */}
      {canSeeAccounts && (
        <div style={{
          display:"flex",gap:0,marginBottom:18,
          borderBottom:`1px solid ${C.border}`,
        }}>
          {[
            {id:"team",     label:"Your Team"},
            {id:"accounts", label:"User Accounts"},
          ].map(t=>(
            <button key={t.id} onClick={()=>setPeopleView(t.id)} style={{
              background:"none",border:"none",fontFamily:"inherit",
              fontSize:13,fontWeight:600,
              color:peopleView===t.id?C.teal:C.mid,
              cursor:"pointer",padding:"8px 18px",
              borderBottom:peopleView===t.id?`2px solid ${C.teal}`:"2px solid transparent",
            }}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {peopleView === "accounts" && canSeeAccounts ? (
        <UserAccounts state={state} set={set} user={user}/>
      ) : (
        <>
      {/* ── PulsePeople full-screen panel ── */}
      {showPP&&isPrincipal&&(
        <div style={{position:"fixed",inset:0,background:"#100f1a",zIndex:300,overflowY:"auto"}}>
          <div style={{maxWidth:900,margin:"0 auto",padding:"24px 24px 80px"}}>
            <button onClick={()=>setShowPP(false)} style={{
              display:"flex",alignItems:"center",gap:6,marginBottom:20,
              background:"transparent",border:"none",cursor:"pointer",
              fontFamily:"inherit",fontSize:13,fontWeight:600,
              color:"rgba(255,255,255,0.45)",padding:0,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12"/>
                <polyline points="12 19 5 12 12 5"/>
              </svg>
              Back to Your Team
            </button>
            <PulsePeople state={state} set={set} user={user}/>
          </div>
        </div>
      )}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16,flexWrap:"wrap",gap:10}}>
        <div>
          <h3 style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading,fontWeight:700,marginBottom:4}}>Your Team</h3>
          <div style={{fontSize:12,color:C.dim}}>Welfare flags, Bradford scores and attendance patterns. Tap any person to see their record.</div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          {isPrincipal&&(
            <button onClick={()=>setAddStaffM(true)} style={{padding:"9px 16px",borderRadius:10,cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:700,background:C.teal,border:"none",color:"#fff",flexShrink:0,display:"flex",alignItems:"center",gap:6}}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add staff
            </button>
          )}
          {isPrincipal&&<button onClick={()=>setShowPP(true)} style={{
            display:"flex",alignItems:"center",gap:7,
            padding:"9px 16px",borderRadius:10,cursor:"pointer",
            fontFamily:"inherit",fontSize:12,fontWeight:700,
            background:"linear-gradient(135deg,rgba(245,197,24,0.12),rgba(167,139,250,0.08))",
            border:"1px solid rgba(245,197,24,0.3)",
            color:"#f5c518",flexShrink:0,
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
            Unsung Heroes
          </button>}
        </div>
      </div>
      {/* Summary bar */}
      {(redCount>0||amberCount>0)&&(
        <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
          {redCount>0&&(
            <button onClick={()=>setFlagFilter(f=>f==="red"?"all":"red")}
              style={{background:flagFilter==="red"?FLAG_COLORS.red.bg:"transparent",border:`1px solid ${FLAG_COLORS.red.border}`,borderRadius:10,padding:"8px 14px",fontSize:12,fontWeight:700,color:FLAG_COLORS.red.text,display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontFamily:"inherit",transition:"all 0.15s"}}>
              <FlagDot color="red" size={8}/>{redCount} needs attention {flagFilter==="red"&&"✓"}
            </button>
          )}
          {amberCount>0&&(
            <button onClick={()=>setFlagFilter(f=>f==="amber"?"all":"amber")}
              style={{background:flagFilter==="amber"?FLAG_COLORS.amber.bg:"transparent",border:`1px solid ${FLAG_COLORS.amber.border}`,borderRadius:10,padding:"8px 14px",fontSize:12,fontWeight:700,color:FLAG_COLORS.amber.text,display:"flex",alignItems:"center",gap:6,cursor:"pointer",fontFamily:"inherit",transition:"all 0.15s"}}>
              <FlagDot color="amber" size={8}/>{amberCount} on radar {flagFilter==="amber"&&"✓"}
            </button>
          )}
          {flagFilter!=="all"&&(
            <button onClick={()=>setFlagFilter("all")} style={{background:"transparent",border:`1px solid ${C.border}`,borderRadius:10,padding:"8px 14px",fontSize:12,color:C.dim,cursor:"pointer",fontFamily:"inherit"}}>
              Show all
            </button>
          )}
        </div>
      )}

      {/* Role filter tabs */}
      {(isPrincipal||isManager)&&(
        <div style={{display:"flex",gap:6,marginBottom:14,flexWrap:"wrap"}}>
          {[["all","Everyone"],["nurses","Nurses"],["clinicians","Clinicians"],["reception","Reception"]].map(([id,label])=>(
            <button key={id} onClick={()=>setRoleFilter(id)} style={{
              padding:"5px 14px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",
              border:`1px solid ${roleFilter===id?C.teal:C.border}`,
              background:roleFilter===id?C.teal+"18":"transparent",
              color:roleFilter===id?C.teal:C.mid,
            }}>{label}</button>
          ))}
        </div>
      )}

      {/* Search + site filter */}
      <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search staff…"
          style={{...IS,maxWidth:200,marginBottom:0,flex:1}}/>
        {(isPrincipal||isManager)&&Object.entries(SITES).map(([id,s])=>(
          <button key={id} onClick={()=>setSiteFilter(siteFilter===id?"all":id)} style={{
            padding:"5px 14px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",
            border:`1px solid ${siteFilter===id?s.color:C.border}`,
            background:siteFilter===id?s.color+"18":"transparent",
            color:siteFilter===id?s.color:C.mid,
          }}>{s.name}</button>
        ))}
      </div>

      {/* Staff grid */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:8}}>
        {filtered.map(({p,flag})=>{
          const fc  = FLAG_COLORS[flag.color];
          const sd  = SITES[p.site];
          const st  = p.status?STATUS[p.status]:null;
          const sickRecords = (state.sick||[]).filter(s=>s.staffId===p.id||s.name===p.name);
          const ep = sickRecords.length;
          const td = sickRecords.reduce((s,r)=>s+(r.days||1),0);
          const bf = ep*ep*td;
          const rtwPending = sickRecords.filter(s=>!s.rtw).length;
          return (
            <button key={p.id} onClick={()=>setSelectedPerson(p)} style={{
              background:"#181626",border:`1.5px solid ${flag.color!=="green"?fc.border:C.border}`,
              borderRadius:14,padding:"12px 14px",cursor:"pointer",textAlign:"left",
              fontFamily:"'Instrument Sans',system-ui,sans-serif",
              boxShadow:flag.color==="red"?"0 2px 12px rgba(220,38,38,0.1)":
                        flag.color==="amber"?"0 2px 12px rgba(217,119,6,0.08)":"none",
              transition:"transform 0.1s",
            }}
            onMouseEnter={e=>e.currentTarget.style.transform="translateY(-1px)"}
            onMouseLeave={e=>e.currentTarget.style.transform="translateY(0)"}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                <div style={{fontSize:13,fontWeight:700,color:C.heading,flex:1,paddingRight:8}}>{p.name}</div>
                <FlagDot color={flag.color} size={10}/>
              </div>
              <div style={{fontSize:11,color:sd?.color||C.dim,marginBottom:6,fontWeight:500}}>
                {sd?.name} · {p.type||p.area||"Staff"}
              </div>
              <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                {st&&<Pill c={st.color} t={st.label} sm/>}
                {p.headNurse&&<Pill c={C.warn} t="Head Nurse" sm/>}
                {rtwPending>0&&<Pill c={C.danger} t="RTW due" sm/>}
                {bf>=100&&<Pill c={bf>=200?C.danger:C.warn} t={`BF ${bf}`} sm/>}
              </div>
            </button>
          );
        })}
        {filtered.length===0&&(
          <div style={{gridColumn:"1/-1",padding:"40px 0",textAlign:"center",color:C.muted,fontSize:13}}>
            {flagFilter!=="all" ? `No ${flagFilter==="red"?"red":"amber"} flags right now.` : "No staff found."}
          </div>
        )}
      </div>

      {/* ── Archived staff section ── */}
      {isPrincipal&&(()=>{
        const archivedNurses=(state.nurses||NURSES_BASE).filter(n=>n.archived&&(siteFilter==="all"||n.site===siteFilter));
        const archivedRecs=[...RECS_BASE,...(state.customRecs||[])].filter(r=>r.archived&&(siteFilter==="all"||r.site===siteFilter));
        const archivedAll=[...archivedNurses,...archivedRecs];
        if(archivedAll.length===0) return null;
        return (
          <div style={{marginTop:24}}>
            <button onClick={()=>setShowArchived(v=>!v)} style={{display:"flex",alignItems:"center",gap:8,background:"transparent",border:"none",cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:600,color:C.dim,padding:0,marginBottom:showArchived?12:0}}>
              <span style={{fontSize:14}}>{showArchived?"▾":"▸"}</span>
              {archivedAll.length} archived staff member{archivedAll.length!==1?"s":""}
            </button>
            {showArchived&&(
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:8,opacity:0.6}}>
                {archivedAll.map(p=>(
                  <button key={p.id} onClick={()=>setSelectedPerson(p)}
                    style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:"12px 14px",cursor:"pointer",textAlign:"left",fontFamily:"inherit"}}>
                    <div style={{fontSize:13,fontWeight:700,color:C.mid,marginBottom:4}}>{p.name}</div>
                    <div style={{fontSize:11,color:C.dim}}>{SITES[p.site]?.name} · {p.type||p.area||"Staff"}</div>
                    <div style={{marginTop:6}}><Pill c={C.dim} t="Archived" sm/></div>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {selectedPerson&&(
        <PersonFilePanel
          person={selectedPerson}
          state={state}
          set={set}
          user={user}
          onClose={()=>setSelectedPerson(null)}
        />
      )}
      {addStaffM&&<AddStaffModal onSave={addStaffFromHub} onClose={()=>setAddStaffM(false)}/>}
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   NEWS FEED MANAGER — Principal only, Admin → News Feed tab
   Manages state.newsItems[] — displayed as scrolling strip on dashboard
═══════════════════════════════════════════════════════════ */
function NewsFeedManager({state, set}) {
  const EMPTY = {
    title:"", category:"law", summary:"", body:"", link:"", pinned:false,
    date:new Date().toISOString().split("T")[0],
  };
  const [form, setForm]   = useState(null); // null = list, {} = editing
  const [editId, setEditId] = useState(null);
  const sf = (k,v) => setForm(f=>({...f,[k]:v}));

  const CAT_OPTIONS = [
    {id:"law",     label:"Employment Law",  color:"#f472b6"},
    {id:"dental",  label:"Dental News",     color:"#60a5fa"},
    {id:"howto",   label:"How-to",          color:"#00e5a0"},
    {id:"podcast", label:"Podcast",         color:"#a78bfa"},
    {id:"omni",    label:"Omni Update",     color:"#f5c518"},
  ];

  const items = [...(state.newsItems||[])]
    .sort((a,b)=>{
      if(a.pinned&&!b.pinned) return -1;
      if(!a.pinned&&b.pinned) return 1;
      return b.date.localeCompare(a.date);
    });

  function save() {
    if(!form.title.trim()||!form.summary.trim()) return;
    if(editId) {
      set(s=>({...s, newsItems:(s.newsItems||[]).map(n=>
        n.id===editId?{...n,...form}:n
      )}));
    } else {
      const newItem = {
        ...form,
        id:"news_"+Date.now(),
        title:form.title.trim(),
        summary:form.summary.trim(),
      };
      set(s=>({...s, newsItems:[newItem,...(s.newsItems||[])]}));
    }
    setForm(null); setEditId(null);
  }

  function del(id) {
    if(!window.confirm("Remove this news item?")) return;
    set(s=>({...s,newsItems:(s.newsItems||[]).filter(n=>n.id!==id)}));
  }

  function togglePin(id) {
    set(s=>({...s,newsItems:(s.newsItems||[]).map(n=>
      n.id===id?{...n,pinned:!n.pinned}:n
    )}));
  }

  function startEdit(item) {
    setEditId(item.id);
    setForm({
      title:item.title, category:item.category,
      summary:item.summary, body:item.body||"",
      link:item.link||"",
      pinned:!!item.pinned, date:item.date,
    });
  }

  if (form!==null) return (
    <div style={{maxWidth:640}}>
      <button onClick={()=>{setForm(null);setEditId(null);}}
        style={{...BTN(C.dim,true),fontSize:12,marginBottom:20,
        display:"flex",alignItems:"center",gap:6}}>
        ← Back to news feed
      </button>
      <h3 style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",
        fontSize:20,color:C.heading,marginBottom:20}}>
        {editId?"Edit news item":"Add news item"}
      </h3>

      {/* Category */}
      <div style={{marginBottom:14}}>
        <label style={LB}>Category</label>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {CAT_OPTIONS.map(c=>(
            <button key={c.id} onClick={()=>sf("category",c.id)} style={{
              padding:"5px 12px",borderRadius:99,fontSize:11,fontWeight:600,
              fontFamily:"inherit",cursor:"pointer",
              background:form.category===c.id?c.color+"20":"transparent",
              border:`1px solid ${form.category===c.id?c.color:C.border}`,
              color:form.category===c.id?c.color:C.mid,
            }}>{c.label}</button>
          ))}
        </div>
      </div>

      {/* Title */}
      <div style={{marginBottom:14}}>
        <label style={LB}>Headline</label>
        <input value={form.title} onChange={e=>sf("title",e.target.value)}
          placeholder="Keep it short and punchy"
          style={IS}/>
      </div>

      {/* Summary */}
      <div style={{marginBottom:14}}>
        <label style={LB}>Summary (1-2 sentences — shown on card)</label>
        <textarea value={form.summary} onChange={e=>sf("summary",e.target.value)}
          rows={3} placeholder="What do users need to know?"
          style={{...IS,resize:"vertical",lineHeight:1.5}}/>
      </div>

      {/* Body */}
      <div style={{marginBottom:14}}>
        <label style={LB}>Article body (shown when user taps the card)</label>
        <textarea value={form.body||""} onChange={e=>sf("body",e.target.value)}
          rows={6} placeholder="Full article content — can be several paragraphs. Leave blank to show summary only."
          style={{...IS,resize:"vertical",lineHeight:1.7}}/>
      </div>

      {/* Date */}
      <div style={{marginBottom:14}}>
        <label style={LB}>Date</label>
        <input type="date" value={form.date} onChange={e=>sf("date",e.target.value)}
          style={IS}/>
      </div>

      {/* Link */}
      <div style={{marginBottom:14}}>
        <label style={LB}>Link (optional — adds "Read more →")</label>
        <input value={form.link} onChange={e=>sf("link",e.target.value)}
          placeholder="https://…"
          style={IS}/>
      </div>

      {/* Pinned */}
      <div style={{marginBottom:20,display:"flex",alignItems:"center",gap:10}}>
        <button onClick={()=>sf("pinned",!form.pinned)} style={{
          width:20,height:20,borderRadius:4,flexShrink:0,
          border:`2px solid ${form.pinned?C.warn:C.border}`,
          background:form.pinned?C.warn:"transparent",
          cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",
        }}>
          {form.pinned&&<span style={{color:"#fff",fontSize:11,fontWeight:800}}>✓</span>}
        </button>
        <div>
          <div style={{fontSize:12,fontWeight:700,color:form.pinned?C.warn:C.text}}>
            Pin this item
          </div>
          <div style={{fontSize:10,color:C.dim}}>
            Pinned items always show first and are marked ★ NEW
          </div>
        </div>
      </div>

      {/* Preview */}
      {form.title&&form.summary&&(
        <div style={{marginBottom:20}}>
          <div style={{fontSize:11,fontWeight:700,color:C.dim,
            textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>
            Preview
          </div>
          <div style={{
            width:260,padding:"12px 14px",borderRadius:12,
            background:"rgba(255,255,255,0.03)",
            border:"1px solid rgba(255,255,255,0.07)",
          }}>
            <div style={{
              display:"inline-block",fontSize:9,fontWeight:700,
              letterSpacing:"0.1em",textTransform:"uppercase",
              color:CAT_OPTIONS.find(c=>c.id===form.category)?.color||C.teal,
              background:(CAT_OPTIONS.find(c=>c.id===form.category)?.color||C.teal)+"15",
              borderRadius:99,padding:"2px 8px",marginBottom:7,
            }}>
              {CAT_OPTIONS.find(c=>c.id===form.category)?.label}
            </div>
            <div style={{fontSize:12,fontWeight:700,
              color:"rgba(255,255,255,0.85)",lineHeight:1.35,marginBottom:5}}>
              {form.title}
            </div>
            <div style={{fontSize:10,color:"rgba(255,255,255,0.38)",lineHeight:1.5}}>
              {form.summary}
            </div>
          </div>
        </div>
      )}

      <div style={{display:"flex",gap:8}}>
        <button onClick={save}
          disabled={!form.title.trim()||!form.summary.trim()}
          style={{...BTN(C.teal),flex:1,padding:"11px 0",
            opacity:!form.title.trim()||!form.summary.trim()?0.4:1}}>
          {editId?"Save changes":"Publish item"}
        </button>
        <button onClick={()=>{setForm(null);setEditId(null);}}
          style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </div>
  );

  return (
    <div style={{maxWidth:720}}>
      <div style={{display:"flex",justifyContent:"space-between",
        alignItems:"flex-start",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <h3 style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",
            fontSize:22,color:C.heading,marginBottom:4}}>Updates</h3>
          <div style={{fontSize:12,color:C.dim}}>
            Manage what appears in the news strip on the dashboard.
            Pinned items always show first. Up to 8 items shown.
          </div>
        </div>
        <button onClick={()=>{setEditId(null);setForm({...EMPTY});}}
          style={BTN(C.teal)}>+ Add item</button>
      </div>

      {items.length===0&&(
        <div style={{...CS,textAlign:"center",padding:40,color:C.dim}}>
          No news items yet — add one above.
        </div>
      )}

      {items.map(item=>{
        const cs = CAT_OPTIONS.find(c=>c.id===item.category)||CAT_OPTIONS[0];
        return (
          <div key={item.id} style={{
            ...CS,marginBottom:8,padding:"12px 16px",
            display:"flex",alignItems:"flex-start",gap:12,
            borderLeft:`3px solid ${cs.color}`,
          }}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"center",
                gap:8,marginBottom:4,flexWrap:"wrap"}}>
                <span style={{
                  fontSize:9,fontWeight:700,letterSpacing:"0.1em",
                  textTransform:"uppercase",color:cs.color,
                  background:cs.color+"15",borderRadius:99,padding:"2px 8px",
                }}>
                  {cs.label}
                </span>
                {item.pinned&&<span style={{
                  fontSize:9,fontWeight:700,color:C.warn,
                  background:"rgba(245,197,24,0.1)",
                  borderRadius:99,padding:"2px 8px",
                }}>★ PINNED</span>}
                <span style={{fontSize:10,color:C.muted}}>{item.date}</span>
              </div>
              <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:3}}>
                {item.title}
              </div>
              <div style={{fontSize:11,color:C.dim,lineHeight:1.5}}>
                {item.summary}
              </div>
              {item.link&&(
                <div style={{fontSize:10,color:C.teal,marginTop:4}}>
                  🔗 {item.link.slice(0,50)}{item.link.length>50?"…":""}
                </div>
              )}
            </div>
            <div style={{display:"flex",gap:6,flexShrink:0}}>
              <button onClick={()=>togglePin(item.id)} title={item.pinned?"Unpin":"Pin"}
                style={{
                  padding:"5px 10px",borderRadius:7,cursor:"pointer",
                  fontFamily:"inherit",fontSize:11,fontWeight:600,
                  background:item.pinned?"rgba(245,197,24,0.12)":"rgba(255,255,255,0.04)",
                  border:`1px solid ${item.pinned?"rgba(245,197,24,0.3)":"rgba(255,255,255,0.08)"}`,
                  color:item.pinned?C.warn:C.dim,
                }}>
                {item.pinned?"★":"☆"}
              </button>
              <button onClick={()=>startEdit(item)}
                style={{...BTN(C.dim,true),fontSize:11,padding:"5px 10px"}}>
                Edit
              </button>
              <button onClick={()=>del(item.id)}
                style={{...BTN(C.danger,true),fontSize:11,padding:"5px 10px"}}>
                ✕
              </button>
            </div>
          </div>
        );
      })}

      <div style={{marginTop:16,padding:"12px 16px",borderRadius:10,
        background:"rgba(99,102,241,0.06)",
        border:"1px solid rgba(99,102,241,0.15)"}}>
        <div style={{fontSize:11,fontWeight:700,color:"#a5b4fc",marginBottom:4}}>
          Coming soon — AI auto-population
        </div>
        <div style={{fontSize:11,color:C.dim,lineHeight:1.6}}>
          A daily job will use Iris to search for relevant dental and employment
          law news and suggest items for your approval. You'll review and publish
          with one tap. For now, add items manually above.
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   ADMIN HUB — v8.0
   Occasional-use tabs in one place
═══════════════════════════════════════════════════════════ */
function AdminHub({state, set, user, isPrincipal, initialAdminTab, onAdminTabConsumed}) {
  const isManager = user.role==="manager"||user.role==="managerLite";
  const canSeeCompliance = isPrincipal||isManager||user.role==="headNurse";

  // Badge: count overdue compliance tasks across all visible sites
  const complianceOverdueBadge = (()=>{
    if (!canSeeCompliance) return 0;
    const userSite = fallbackSite(user);
    const sitesToCheck = isPrincipal ? Object.keys(SITES) : [userSite];
    let count = 0;
    COMPLIANCE_TASKS.forEach(task=>{
      sitesToCheck.forEach(sid=>{
        const last = getLastCompletion(state, task.id, sid);
        if (isComplianceOverdue(task, last)) count++;
      });
    });
    return count;
  })();

  const tabs = [
    {id:"clinicians", label:"Clinicians"},
    ...(isPrincipal||isManager?[
      {id:"wages",    label:"Wages"},
      {id:"hours",    label:"⏱ Hours & Contracts"},
      {id:"finance",  label:"Finance"},
    ]:[]),
    {id:"agencies",   label:"Agencies"},
    {id:"bank",       label:"Omni Bank"},
    ...(canSeeCompliance?[{id:"comply", label:"Compliance", badge:complianceOverdueBadge}]:[]),
    ...(isPrincipal||isManager||user.role==="managerLite"?[
      {id:"adhoc",    label:"Ad Hoc"},
      {id:"reports",  label:"Reports"},
    ]:[]),
    ...(user.staffId&&(isManager||user.role==="managerLite")?[{id:"myleave",label:"My Leave"}]:[]),
    // Settings is principal-only from v10.x. Managers manage user accounts via People > User Accounts.
    ...(isPrincipal?[{id:"settings",label:"Settings"}]:[]),
    ...(isPrincipal?[{id:"newsmgr",label:"Updates"}]:[]),
  ];
  const [adminTab, setAdminTab] = React.useState(initialAdminTab||tabs[0]?.id||"clinicians");
  React.useEffect(()=>{ if(initialAdminTab){ setAdminTab(initialAdminTab); if(onAdminTabConsumed) onAdminTabConsumed(); } },[initialAdminTab]);

  return (
    <div>
      {/* Sub-tab bar */}
      <div style={{display:"flex",gap:0,marginBottom:20,borderBottom:`1px solid ${C.border}`,overflowX:"auto",WebkitOverflowScrolling:"touch",scrollbarWidth:"none"}}>
        {tabs.map(t=>(
          <button key={t.id} onClick={()=>setAdminTab(t.id)} style={{
            background:"none",border:"none",fontFamily:"inherit",fontSize:13,fontWeight:600,
            color:adminTab===t.id?C.teal:C.mid,cursor:"pointer",padding:"8px 16px",whiteSpace:"nowrap",
            borderBottom:adminTab===t.id?`2px solid ${C.teal}`:"2px solid transparent",
            flexShrink:0,display:"flex",alignItems:"center",gap:5,
          }}>
            {t.label}
            {t.badge>0&&<span style={{background:C.danger,color:"#fff",borderRadius:99,fontSize:9,fontWeight:800,padding:"1px 6px"}}>{t.badge}</span>}
          </button>
        ))}
      </div>
      {adminTab==="directory"  &&<Directory   state={state} set={set} isPrincipal={isPrincipal} user={user}/>}
      {adminTab==="clinicians" &&<CliniciansManagement state={state} set={set} isPrincipal={isPrincipal} user={user}/>}
      {adminTab==="wages"      &&<Wages       state={state} set={set} isPrincipal={isPrincipal} user={user}/>}
      {adminTab==="hours"      &&<HoursContracts state={state} set={set} isPrincipal={isPrincipal} user={user}/>}
      {adminTab==="finance"    &&<Finance     state={state} set={set} isPrincipal={isPrincipal}/>}
      {adminTab==="agencies"   &&<Agencies    state={state} set={set} user={user}/>}
      {adminTab==="bank"       &&<PulseBank   state={state} set={set} user={user}/>}
      {adminTab==="comply"     &&canSeeCompliance&&<ComplianceTasks state={state} set={set} user={user} isPrincipal={isPrincipal}/>}
      {adminTab==="adhoc"      &&<AdHocClinics state={state} set={set} user={user} isPrincipal={isPrincipal}/>}
      {adminTab==="reports"    &&<Reports     state={state} set={set} isPrincipal={isPrincipal} userRole={user.role}/>}
      {adminTab==="myleave"    &&user.staffId&&<ManagerLeaveView staffId={user.staffId} state={state} set={set} user={user}/>}
      {adminTab==="settings"   &&<SettingsTab state={state} set={set} user={user}/>}
      {adminTab==="newsmgr"    &&isPrincipal&&<NewsFeedManager state={state} set={set}/>}
    </div>
  );
}

function PasskeySetupModal({user, state, onDone, onRegister}) {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
  const biometricName = isIOS ? "Face ID" : "fingerprint / Face ID";

  return (
    <Overlay onClose={onDone}>
      <div style={{textAlign:"center",padding:"8px 0"}}>
        <div style={{fontSize:48,marginBottom:16}}>{isIOS?"🔐":"👆"}</div>
        <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,fontWeight:800,color:C.heading,marginBottom:8}}>
          Set up {biometricName}
        </div>
        <div style={{fontSize:14,color:C.dim,lineHeight:1.6,marginBottom:24}}>
          Next time you open Pulse, just use {biometricName} — no PIN needed. Takes 2 seconds, every time.
        </div>
        {done ? (
          <div style={{background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:12,padding:"16px",marginBottom:20}}>
            <div style={{fontSize:15,fontWeight:700,color:C.green}}>{biometricName} set up!</div>
            <div style={{fontSize:12,color:C.dim,marginTop:4}}>You're all set for next time.</div>
          </div>
        ) : (
          <button onClick={async()=>{
            setLoading(true);
            await onRegister();
            setDone(true);
            setLoading(false);
          }} style={{...BTN(C.teal),width:"100%",padding:"14px 0",fontSize:15,marginBottom:12}}>
            {loading ? "Setting up…" : `Set up ${biometricName}`}
          </button>
        )}
        <button onClick={onDone} style={{background:"none",border:"none",color:C.dim,fontSize:13,cursor:"pointer",fontFamily:"inherit",padding:"8px 0",width:"100%"}}>
          {done ? "Done" : "Not now"}
        </button>
        {!done&&<div style={{fontSize:11,color:C.muted,marginTop:8}}>Your PIN still works as a backup</div>}
      </div>
    </Overlay>
  );
}

/* ── PASSKEY / WEBAUTHN ──────────────────────────────────────────────
   Face ID on iPhone, fingerprint on Android, Windows Hello on laptop.
   Registration: after first login, offer to save a passkey.
   Authentication: on return visits, show Face ID prompt first.
   Falls back to PIN or magic link if biometrics unavailable.
──────────────────────────────────────────────────────────────────── */
/* eslint-disable no-undef */

const SUPABASE_URL_PK = SB_URL; // ── same URL as SB_URL — do not duplicate
const SUPABASE_ANON_KEY_PK = (() => {
  // Read from state at call time — avoids top-level reference issues
  try { return window.__pulseAnonKey || ""; } catch(e) { return ""; }
})();

function isPlatformAuthAvailable() {
  return typeof window !== "undefined" &&
    typeof window.PublicKeyCredential !== "undefined" &&
    typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === "function";
}

async function checkPlatformAuth() {
  try {
    if (!isPlatformAuthAvailable()) return false;
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch(e) { return false; }
}

function bufToB64(buf) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)));
}
function b64ToBuf(b64) {
  const bin = atob(b64.replace(/-/g,"+").replace(/_/g,"/"));
  return Uint8Array.from(bin, c=>c.charCodeAt(0)).buffer;
}

// Register a passkey for this user — called after successful first login
async function registerPasskey(userId, userName, anonKey) {
  try {
    const challenge = crypto.getRandomValues(new Uint8Array(32));
    const cred = await navigator.credentials.create({
      publicKey: {
        challenge,
        rp: { name: "Omni", id: window.location.hostname },
        user: {
          id: new TextEncoder().encode(userId),
          name: userName,
          displayName: userName,
        },
        pubKeyCredParams: [
          { alg: -7,  type: "public-key" }, // ES256
          { alg: -257,type: "public-key" }, // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: "platform", // device-bound (Face ID, Touch ID)
          userVerification: "required",
          residentKey: "preferred",
        },
        timeout: 60000,
      }
    });
    if (!cred) return false;

    const credentialId = bufToB64(cred.rawId);
    const publicKey    = bufToB64(cred.response.getPublicKey?.() || cred.response.attestationObject);

    // Store in Supabase passkeys table
    const r = await fetch(`${SUPABASE_URL_PK}/rest/v1/passkeys`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "apikey": anonKey,
        "Authorization": `Bearer ${anonKey}`,
        "Prefer": "return=minimal",
      },
      body: JSON.stringify({ user_id: userId, credential_id: credentialId, public_key: publicKey }),
    });
    return r.ok;
  } catch(e) {
    console.warn("Passkey registration failed:", e);
    return false;
  }
}

// Get stored passkeys for a user from Supabase
async function getPasskeys(userId, anonKey) {
  try {
    const r = await fetch(`${SUPABASE_URL_PK}/rest/v1/passkeys?user_id=eq.${encodeURIComponent(userId)}&select=credential_id`, {
      headers: { "apikey": anonKey, "Authorization": `Bearer ${anonKey}` },
    });
    if (!r.ok) return [];
    return await r.json();
  } catch(e) { return []; }
}

// Authenticate with passkey — called on return visit
async function authenticatePasskey(userId, anonKey) {
  try {
    const stored = await getPasskeys(userId, anonKey);
    if (!stored.length) return false;

    const challenge = crypto.getRandomValues(new Uint8Array(32));
    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge,
        allowCredentials: stored.map(p=>({
          id: b64ToBuf(p.credential_id),
          type: "public-key",
        })),
        userVerification: "required",
        timeout: 60000,
      }
    });
    return !!assertion;
  } catch(e) {
    console.warn("Passkey auth failed:", e);
    return false;
  }
}

/* ══════════════════════════════════════════════════════════════════════
   FIRST-TOUCH WELCOME — v10.4
   The single screen a brand-new staff member sees after the cinematic.
   Shown inside the Login component when mode === "claim".

   Replaces the old two-step (privacy notice wall → PIN keypad) with a
   single, calm welcome card on the dark cinematic background.

   Structure:
   - "Omni." wordmark (Instrument Serif italic, teal dot)
   - "Hi {firstName}."
   - "{ownerName} at {practiceName} has invited you to Omni."
   - Plain-English GDPR summary (3 lines)
   - "Read full notice" expandable accordion (collapsed by default)
   - Two checkboxes: read + consent
   - "Enter Omni →" button (disabled until both ticked)
   - Subtle "back" link

   The eye remains subtly visible behind via a low-opacity radial
   gradient — the whole component sits inside a dark wrapper that
   continues the cinematic mood rather than snapping to a clean light
   surface.
══════════════════════════════════════════════════════════════════════ */
function FirstTouchWelcome({
  claimEmail, state, onEnter, onBack,
  gdprErr, readConsent, setReadConsent, gdprConsent, setGdprConsent, setGdprErr,
}) {
  /* eslint-disable no-undef */
  const [showFullNotice, setShowFullNotice] = useState(false);
  /* eslint-enable no-undef */

  // Personalisation
  const firstName = (() => {
    if (!claimEmail) return "there";
    // Try to find the matching user record for a real first name
    const users = resolveUsers(state);
    const u = users.find(usr => (usr.email||"").toLowerCase().trim() === (claimEmail||"").toLowerCase().trim());
    if (u && u.name) {
      const trimmed = u.name.trim();
      // v11.1 — handle test-style names like "Test 5" (word + number).
      // For these, return the whole thing so the greeting reads "Hi Test 5"
      // not "Hi Test." For real human names ("Hannah Watson", "Mary Jane Smith")
      // we still want just the first word. The split-on-space approach loses
      // context for single-word names with trailing tokens.
      if (/^\S+\s+\d+$/.test(trimmed)) return trimmed;
      return trimmed.split(/\s+/)[0];
    }
    // Fall back to email local-part, capitalised
    const local = (claimEmail.split("@")[0]||"").replace(/[._+\-]/g, " ").trim();
    if (!local) return "there";
    return local.charAt(0).toUpperCase() + local.slice(1).split(" ")[0];
  })();

  const principalName = state?.practiceProfile?.principalName
    || state?.practiceProfile?.ownerName
    || "Your manager";
  const principalFirst = (principalName||"").split(" ")[0] || "your manager";
  const practiceName = state?.practiceProfile?.practiceName
    || state?.practiceProfile?.name
    || PRACTICE_CONFIG.name
    || "the practice";

  const bothTicked = readConsent && gdprConsent;

  function tryEnter() {
    if (!bothTicked) { setGdprErr(true); return; }
    setGdprErr(false);
    onEnter();
  }

  return (
    <div style={{
      width:"100%", maxWidth:440,
      background:"rgba(24,22,38,0.78)",
      backdropFilter:"blur(14px)",
      WebkitBackdropFilter:"blur(14px)",
      border:"1px solid rgba(255,255,255,0.08)",
      borderRadius:22,
      padding:"36px 28px 28px",
      boxShadow:"0 24px 80px rgba(0,0,0,0.45), 0 2px 8px rgba(0,0,0,0.2)",
      position:"relative", zIndex:2,
      animation:"loginFadeIn 0.5s ease both",
    }}>

      {/* Wordmark */}
      <div style={{textAlign:"center", marginBottom:20}}>
        <div style={{display:"inline-flex", alignItems:"baseline", lineHeight:1}}>
          <span style={{
            fontFamily:"'Instrument Serif',Georgia,serif",
            fontStyle:"italic", fontWeight:700,
            fontSize:38, color:"rgba(255,255,255,0.95)",
            letterSpacing:"-0.02em",
          }}>Omni</span>
          <span style={{
            fontFamily:"'Instrument Serif',Georgia,serif",
            fontStyle:"italic", fontWeight:700,
            fontSize:38, color:"#3aad98",
            letterSpacing:"-0.02em",
            marginLeft:"0.05em",
          }}>.</span>
        </div>
      </div>

      {/* Greeting */}
      <div style={{textAlign:"center", marginBottom:14}}>
        <div style={{
          fontFamily:"'Instrument Serif',Georgia,serif",
          fontStyle:"italic", fontWeight:500,
          fontSize:36, color:"rgba(255,255,255,0.96)",
          letterSpacing:"-0.5px", lineHeight:1.1,
        }}>
          Hi {firstName}.
        </div>
      </div>

      {/* Invitation line */}
      <div style={{textAlign:"center", marginBottom:24}}>
        <div style={{
          fontSize:14, color:"rgba(255,255,255,0.7)",
          lineHeight:1.6, fontFamily:"'Instrument Sans',system-ui,sans-serif",
        }}>
          <strong style={{color:"rgba(255,255,255,0.9)", fontWeight:600}}>{principalName}</strong> at{" "}
          <strong style={{color:"rgba(255,255,255,0.9)", fontWeight:600}}>{practiceName}</strong>{" "}
          has invited you to Omni.
        </div>
        <div style={{
          fontSize:11, color:"rgba(255,255,255,0.35)",
          marginTop:8, fontFamily:"'Instrument Sans',system-ui,sans-serif",
        }}>
          Signed in as {claimEmail}
        </div>
      </div>

      {/* Plain-English GDPR summary */}
      <div style={{
        background:"rgba(58,173,152,0.06)",
        border:"1px solid rgba(58,173,152,0.18)",
        borderRadius:14,
        padding:"16px 18px",
        marginBottom:14,
      }}>
        <div style={{
          fontSize:11, fontWeight:700,
          color:"#3aad98",
          letterSpacing:"0.08em", textTransform:"uppercase",
          marginBottom:10,
          fontFamily:"'Instrument Sans',system-ui,sans-serif",
        }}>
          How we look after your data
        </div>
        <div style={{
          fontSize:13, color:"rgba(255,255,255,0.78)",
          lineHeight:1.65,
          fontFamily:"'Instrument Sans',system-ui,sans-serif",
        }}>
          <div style={{marginBottom:8, display:"flex", gap:10}}>
            <span style={{color:"#3aad98", flexShrink:0}}>•</span>
            <span>We hold your work info — your name, role, rota, leave, and absences. Nothing about patients, no payroll, no medical records.</span>
          </div>
          <div style={{marginBottom:8, display:"flex", gap:10}}>
            <span style={{color:"#3aad98", flexShrink:0}}>•</span>
            <span>Only your manager and {principalFirst} can see your records. Other staff can't.</span>
          </div>
          <div style={{display:"flex", gap:10}}>
            <span style={{color:"#3aad98", flexShrink:0}}>•</span>
            <span>Stored securely on EU servers. Kept while you work here, plus 6 years after you leave (UK employment law).</span>
          </div>
        </div>

        {/* Expandable full notice */}
        <button
          onClick={() => setShowFullNotice(v => !v)}
          style={{
            marginTop:12, background:"transparent", border:"none",
            color:"rgba(255,255,255,0.55)", fontSize:11, cursor:"pointer",
            padding:0, fontFamily:"'Instrument Sans',system-ui,sans-serif",
            textDecoration:"underline", letterSpacing:"0.02em",
          }}
        >
          {showFullNotice ? "Hide full notice" : "Read full notice"}
        </button>

        {showFullNotice && (
          <div style={{
            marginTop:14, paddingTop:14,
            borderTop:"1px solid rgba(255,255,255,0.08)",
            fontSize:12, color:"rgba(255,255,255,0.7)",
            lineHeight:1.7,
            fontFamily:"'Instrument Sans',system-ui,sans-serif",
          }}>
            <p style={{margin:"0 0 10px"}}><strong style={{color:"rgba(255,255,255,0.85)"}}>Who we are:</strong> {practiceName} is the Data Controller for your personal data in Omni. Responsible person: {principalName}, Principal.</p>
            <p style={{margin:"0 0 8px"}}><strong style={{color:"rgba(255,255,255,0.85)"}}>What we store:</strong></p>
            <ul style={{margin:"0 0 10px 18px", padding:0, color:"rgba(255,255,255,0.62)"}}>
              <li style={{marginBottom:3}}>Your name, job role, and practice site</li>
              <li style={{marginBottom:3}}>Work email address (for login)</li>
              <li style={{marginBottom:3}}>Shift and rota schedule</li>
              <li style={{marginBottom:3}}>Annual leave requests and balance</li>
              <li style={{marginBottom:3}}>Sickness absence records and Bradford Factor score</li>
              <li style={{marginBottom:3}}>Overtime, lateness, and attendance log entries</li>
              <li>Training level and clinical qualifications</li>
            </ul>
            <p style={{margin:"0 0 10px", padding:"8px 12px", background:"rgba(255,70,70,0.08)", border:"1px solid rgba(255,70,70,0.2)", borderRadius:8, fontSize:11, color:"#ff8b8b"}}>
              We do <strong>not</strong> store patient data, payroll, NI numbers, home addresses, or medical records.
            </p>
            <p style={{margin:"0 0 10px"}}><strong style={{color:"rgba(255,255,255,0.85)"}}>Why:</strong> To manage your rota, process leave, handle absence, and maintain a secure audit trail — all necessary for your employment.</p>
            <p style={{margin:"0 0 10px"}}><strong style={{color:"rgba(255,255,255,0.85)"}}>How long:</strong> During your employment, and for 6 years after you leave in line with UK employment law.</p>
            <p style={{margin:"0 0 10px"}}><strong style={{color:"rgba(255,255,255,0.85)"}}>Your rights:</strong> You can access, correct, or request deletion of your data. Contact {practiceName} directly. We respond within 30 days. You can also complain to the ICO at ico.org.uk.</p>
            <p style={{margin:0, color:"rgba(255,255,255,0.45)", fontSize:11}}>Questions? Speak to {principalFirst} or your site manager.</p>
          </div>
        )}
      </div>

      {/* Two checkboxes */}
      <div style={{
        marginBottom:18,
        padding:"14px 16px",
        background: gdprErr ? "rgba(255,70,70,0.08)" : "rgba(255,255,255,0.03)",
        border: `1px solid ${gdprErr ? "rgba(255,70,70,0.35)" : "rgba(255,255,255,0.08)"}`,
        borderRadius:14,
      }}>
        <label style={{display:"flex", alignItems:"flex-start", gap:11, cursor:"pointer", marginBottom:10}}>
          <input
            type="checkbox"
            checked={readConsent}
            onChange={e => { setReadConsent(e.target.checked); if (e.target.checked && gdprConsent) setGdprErr(false); }}
            style={{marginTop:3, accentColor:"#3aad98", flexShrink:0, cursor:"pointer", width:16, height:16}}
          />
          <span style={{
            fontSize:13, color:"rgba(255,255,255,0.85)",
            lineHeight:1.5,
            fontFamily:"'Instrument Sans',system-ui,sans-serif",
          }}>
            I've read the privacy notice.
          </span>
        </label>
        <label style={{display:"flex", alignItems:"flex-start", gap:11, cursor:"pointer"}}>
          <input
            type="checkbox"
            checked={gdprConsent}
            onChange={e => { setGdprConsent(e.target.checked); if (e.target.checked && readConsent) setGdprErr(false); }}
            style={{marginTop:3, accentColor:"#3aad98", flexShrink:0, cursor:"pointer", width:16, height:16}}
          />
          <span style={{
            fontSize:13, color:"rgba(255,255,255,0.85)",
            lineHeight:1.5,
            fontFamily:"'Instrument Sans',system-ui,sans-serif",
          }}>
            I consent to my data being processed for staff management as described above.
          </span>
        </label>
        {gdprErr && (
          <div style={{
            marginTop:10, fontSize:11, color:"#ff8b8b",
            fontFamily:"'Instrument Sans',system-ui,sans-serif",
          }}>
            Please tick both boxes to continue.
          </div>
        )}
      </div>

      {/* Enter button */}
      <button
        onClick={tryEnter}
        disabled={!bothTicked}
        style={{
          width:"100%", padding:"15px",
          borderRadius:14, border:"none",
          background: bothTicked ? "#3aad98" : "rgba(255,255,255,0.08)",
          color: bothTicked ? "#fff" : "rgba(255,255,255,0.35)",
          fontFamily:"'Instrument Sans',system-ui,sans-serif",
          fontSize:15, fontWeight:700, letterSpacing:"0.01em",
          cursor: bothTicked ? "pointer" : "not-allowed",
          transition:"all 0.2s ease",
          boxShadow: bothTicked ? "0 4px 18px rgba(58,173,152,0.4)" : "none",
        }}
      >
        Enter Omni →
      </button>

      {/* Back link */}
      <button
        onClick={onBack}
        style={{
          width:"100%", marginTop:10,
          background:"transparent", border:"none",
          color:"rgba(255,255,255,0.35)", fontSize:11, cursor:"pointer",
          fontFamily:"'Instrument Sans',system-ui,sans-serif",
          padding:"8px",
        }}
      >
        Not you? Sign out
      </button>
    </div>
  );
}

function Login({onLogin, state, pendingEmail, onEmailLinked, initialMode}) {
/* eslint-enable no-undef */
  const [mode,    setMode]    = useState(pendingEmail ? "claim" : (initialMode||"main"));
  const [email,   setEmail]   = useState("");
  const [pass,    setPass]    = useState("");
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState("");
  const [resetSent,setResetSent] = useState(false);

  // Passkey state
  const [passkeyAvailable, setPasskeyAvailable] = useState(false);
  const [passkeyChecking,  setPasskeyChecking]  = useState(true);
  const [passkeyUser,      setPasskeyUser]      = useState(null); // user with stored passkey
  const anonKey = state?.settings?.supabaseAnonKey || window.__pulseAnonKey || "";

  // PIN state (claim + emergency fallback)
  const [pin,     setPin]     = useState("");
  const [pinErr,  setPinErr]  = useState(false);
  const [pinTries,setPinTries]= useState(0);
  const [pinLocked,setPinLocked]=useState(false);
  const [lockT,   setLockT]   = useState(0);
  const [pressed, setPressed] = useState(null);

  // GDPR consent state for claim flow
  // v10.4 — split into TWO checkboxes:
  //   readConsent  — "I've read the privacy notice"
  //   gdprConsent  — "I consent to my data being processed"
  // Both must be ticked before "Enter Omni" enables.
  const [gdprConsent, setGdprConsent] = useState(false);
  const [readConsent, setReadConsent] = useState(false);
  const [gdprErr, setGdprErr] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(true); // claim flow: show notice first

  // Claim mode — triggered when OAuth email doesn't match any user
  // pendingEmail is passed down from App when Google auth succeeds but no user match
  const claimEmail = pendingEmail || email;

  useEffect(()=>{
    if (!pinLocked) return;
    const t = setInterval(()=>{
      setLockT(s=>{ if(s<=1){setPinLocked(false);setPinTries(0);clearInterval(t);return 0;} return s-1; });
    },1000);
    return ()=>clearInterval(t);
  },[pinLocked]);

  // Check if device supports Face ID / platform biometrics
  useEffect(()=>{
    try {
      checkPlatformAuth().then(available=>{
        setPasskeyAvailable(available);
        if (available && anonKey) {
          const users = resolveUsers(state);
          const lastUser = localStorage.getItem("pr_last_user_id");
          const u = lastUser ? users.find(u=>u.id===lastUser) : null;
          if (u) setPasskeyUser(u);
        }
        setPasskeyChecking(false);
      }).catch(()=>setPasskeyChecking(false));
    } catch(e) { setPasskeyChecking(false); }
  },[]);

  function handlePinPress(k) {
    if (pinLocked||!k) return;
    setPressed(k);
    setTimeout(()=>setPressed(null),120);
    if (k==="⌫") { setPin(p=>p.slice(0,-1)); return; }
    const np = pin+k;
    setPin(np);
    if (np.length===4) {
      if (mode==="claim") tryClaimPin(np);
    }
  }

  function tryPinLogin(p) { // PIN login removed — Google SSO only
    return;
  }

  // v10.4 — Direct claim, no PIN required.
  // The magic link IS the verification: only someone with access to the user's
  // email could have clicked the link to land here. We match the authenticated
  // email against the user record and log them in. PIN becomes a post-onboarding
  // optional quick-login method, never a barrier on first login.
  //
  // v11.0 (26 Apr 2026) — DEFENSIVE REWRITE:
  // Yesterday's bug: fresh users hit "Record not found" after GDPR consent.
  // Likely cause: state hasn't loaded the new user yet when claimAccountDirect
  // runs (race condition between addStaff persistence and new user's state fetch).
  //
  // New behaviour:
  //  1. Try lookup against current state. If found, proceed (fast path).
  //  2. If not found, log full diagnostic info, then refresh state from /api/db
  //     and retry lookup against fresh data.
  //  3. If still not found, show actionable error message AND log everything
  //     to console for debugging.
  //
  // Most legitimate failures resolve at step 2. Step 3 only fires for genuine
  // bad data (wrong email entered, archived user, etc.).
  async function claimAccountDirect() {
    if (!readConsent || !gdprConsent) { setGdprErr(true); return; }

    const targetEmail = (claimEmail||"").toLowerCase().trim();

    // v11.2 — match against email OR any value in linkedEmails array.
    // Some users have multiple emails (work + personal). Previously only
    // checked the primary email field; users with the auth email in
    // linkedEmails would fail lookup.
    const matchUser = (usr) => {
      const primary = (usr.email||"").toLowerCase().trim();
      if (primary && primary === targetEmail) return true;
      const linked = Array.isArray(usr.linkedEmails) ? usr.linkedEmails : [];
      return linked.some(e => (e||"").toLowerCase().trim() === targetEmail);
    };

    // v11.2 — pre-check: search ALL users including inactive ones, so we
    // can distinguish "no record" from "record exists but deactivated".
    const allUsersIncludingInactive = resolveUsers(state, true);
    const matchedIncludingInactive = allUsersIncludingInactive.find(matchUser);

    // ── Attempt 1 — lookup against current React state (active only) ─────
    let users = resolveUsers(state);
    let u = users.find(matchUser);

    // ── Attempt 2 — refresh from server and retry if first attempt failed ──
    if (!u) {
      console.warn("[claim] First lookup failed. claimEmail:", JSON.stringify(targetEmail),
                   "| users in state:", users.length,
                   "| sample emails:", users.slice(0, 5).map(usr => usr.email||"(empty)"));
      console.log("[claim] Refreshing state from /api/db and retrying...");

      try {
        const freshData = await sbGetAll();
        if (freshData) {
          const mergedState = {...state, ...freshData};
          users = resolveUsers(mergedState);
          u = users.find(matchUser);

          if (u) {
            console.log("[claim] Retry succeeded. User found in refreshed state:", u.id, u.name);
          } else {
            console.warn("[claim] Retry also failed. Refreshed users:", users.length,
                         "| emails:", users.map(usr => usr.email||"(empty)").filter(e => e !== "(empty)"));
          }
        } else {
          console.warn("[claim] sbGetAll returned null — server fetch failed");
        }
      } catch(refreshErr) {
        console.error("[claim] State refresh threw:", refreshErr);
      }
    }

    // ── Final result ─────────────────────────────────────────────────────
    if (!u) {
      // Distinguish "inactive account" from "no account at all" — much more
      // actionable error message for the user.
      if (matchedIncludingInactive) {
        setErr("This account has been deactivated. Please contact your manager to reactivate it.");
        console.error("[claim] FINAL: user found but inactive for", targetEmail,
                      "| user:", matchedIncludingInactive.id, matchedIncludingInactive.name);
      } else {
        setErr("We couldn't find a profile for this email. If you were just added, please refresh and try again. Otherwise, contact your manager.");
        console.error("[claim] FINAL: no user found for", targetEmail, "after server refresh");
      }
      return;
    }

    console.log("[claim] Login proceeding for", u.id, u.name, "via email", targetEmail);

    if (onEmailLinked) onEmailLinked(u.id, claimEmail);

    // Audit record — both consent boxes captured separately
    const consentRecord = {
      userId: u.id,
      userName: u.name,
      email: claimEmail,
      readConsent: true,
      processConsent: true,
      timestamp: new Date().toISOString(),
    };
    try {
      const existing = JSON.parse(localStorage.getItem("pr_gdpr_consents")||"[]");
      localStorage.setItem("pr_gdpr_consents", JSON.stringify([...existing, consentRecord]));
    } catch(e) {}

    onLogin({...u, email: claimEmail, gdprConsented: true, gdprConsentDate: new Date().toISOString()});
  }

  // v10.3.x — kept for backwards compat. Old PIN-claim path is no longer
  // wired into the UI but the function remains in case any state still
  // references it. It just no-ops now.
  async function tryClaimPin(_p) { return; }

  async function handleEmailLogin(e) {
    e?.preventDefault();
    if (!email||!pass) { setErr("Please enter your email and password"); return; }
    setLoading(true); setErr("");
    try {
      const data = await sbAuthEmail(email, pass);
      const userEmail = data.user?.email || email;
      const appUser = matchEmailToUser(userEmail, state);
      if (appUser) { onLogin(appUser); }
      else { setErr("Email not linked to an account. Check the address or contact your practice manager."); }
    } catch(ex) { setErr(ex.message||"Sign in failed"); }
    finally { setLoading(false); }
  }

  async function handleResetPassword() {
    if (!email) { setErr("Enter your email address first"); return; }
    setLoading(true);
    try { await sbAuthResetPassword(email); setResetSent(true); setErr(""); }
    catch(e) { setErr("Could not send reset email. Check the address and try again."); }
    finally { setLoading(false); }
  }

  const inputStyle = {
    width:"100%",padding:"13px 16px",borderRadius:12,
    border:`1px solid ${C.border}`,background:C.surface,
    fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:15,
    color:C.text,outline:"none",boxSizing:"border-box",
    transition:"border-color 0.15s",
  };
  const btnPrimary = {
    width:"100%",padding:"14px",borderRadius:12,border:"none",
    background:C.teal,color:"#fff",
    fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:15,fontWeight:600,
    cursor:"pointer",transition:"opacity 0.15s",
  };
  const btnOutline = {
    width:"100%",padding:"13px",borderRadius:12,
    border:`1px solid ${C.border}`,background:C.surface,
    color:C.text,fontFamily:"'Instrument Sans',system-ui,sans-serif",
    fontSize:15,fontWeight:500,cursor:"pointer",
    display:"flex",alignItems:"center",justifyContent:"center",gap:10,
    transition:"background 0.15s, border-color 0.15s",
  };

  // Inline PIN pad — NOT a component (avoids remount blink on every keystroke)
  function renderPinPad(title, subtitle, onCancelLabel) {
    return (
      <div style={{width:"100%",maxWidth:280}}>
        <div style={{textAlign:"center",marginBottom:20}}>
          <div style={{fontSize:14,fontWeight:600,color:C.heading,marginBottom:4}}>{title}</div>
          <div style={{fontSize:12,color:C.dim,marginBottom:16}}>{subtitle}</div>
          <div style={{display:"flex",justifyContent:"center",gap:12,marginBottom:4}}>
            {[0,1,2,3].map(i=>(
              <div key={i} style={{
                width:12,height:12,borderRadius:99,
                background:pin.length>i?C.teal:C.border,
                transition:"background 0.15s",
                border:`1.5px solid ${pin.length>i?C.teal:C.border}`,
              }}/>
            ))}
          </div>
        </div>

        {/* GDPR consent — shown ABOVE keypad in claim flow */}
        {mode==="claim"&&(
          <div style={{marginBottom:14,padding:"12px 14px",background:gdprErr?"#fef2f2":C.bg,border:`1px solid ${gdprErr?C.danger:C.border}`,borderRadius:12}}>
            <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer"}}>
              <input type="checkbox" checked={gdprConsent} onChange={e=>{setGdprConsent(e.target.checked);setGdprErr(false);}}
                style={{marginTop:2,width:15,height:15,accentColor:C.teal,flexShrink:0}}/>
              <span style={{fontSize:11,color:gdprErr?C.danger:C.mid,lineHeight:1.5}}>
                I agree that my data may be stored and processed for staff management purposes.
              </span>
            </label>
            {gdprErr&&<div style={{fontSize:11,color:C.danger,marginTop:6,fontWeight:600}}>Please tick to continue</div>}
          </div>
        )}

        {pinLocked ? (
          <div style={{textAlign:"center",padding:"20px 0"}}>
            <div style={{fontSize:13,fontWeight:600,color:C.danger}}>Too many attempts</div>
            <div style={{fontSize:12,color:C.dim,marginTop:4}}>Locked for {lockT}s</div>
          </div>
        ) : (
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
            {["1","2","3","4","5","6","7","8","9","","0","⌫"].map(k=>(
              <button key={k} onClick={()=>handlePinPress(k)} style={{
                padding:"20px 0",borderRadius:16,
                border:k?`1px solid ${pressed===k?"#9de8df":C.border}`:"none",
                background:!k?"transparent":pressed===k?"#e8faf7":C.surface,
                color:k==="⌫"?C.mid:C.heading,
                fontFamily:"'Instrument Sans',system-ui,sans-serif",
                fontSize:k==="⌫"?18:22,fontWeight:300,
                cursor:k?"pointer":"default",
                transition:"background 0.1s",
                userSelect:"none",WebkitUserSelect:"none",
              }}>{k}</button>
            ))}
          </div>
        )}
        {pinErr&&!pinLocked&&(
          <div style={{textAlign:"center",fontSize:12,color:C.danger,marginTop:14}}>
            Incorrect PIN
          </div>
        )}
        <button onClick={()=>{setMode("main");setPin("");setPinErr(false);setPinTries(0);setErr("");setGdprConsent(false);setGdprErr(false);setShowPrivacy(true);}} style={{
          width:"100%",marginTop:20,padding:"10px",borderRadius:10,
          border:"none",background:"transparent",color:C.dim,
          fontSize:12,cursor:"pointer",fontFamily:"inherit",
        }}>{onCancelLabel||"Back"}</button>
      </div>
    );
  }

  return (
    <div style={{
      minHeight:"100vh",
      background:"#07060a",
      display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
      padding:"24px 20px",position:"relative",
      fontFamily:"'Instrument Sans',system-ui,sans-serif",
      overflow:"hidden",
    }}>
      {/* Eye background */}
      <video autoPlay muted loop playsInline style={{position:"fixed",inset:0,width:"100%",height:"100%",objectFit:"cover",opacity:1,zIndex:0,pointerEvents:"none"}} onError={e=>e.target.style.display="none"}><source src="https://prima-luce.com/eye.mp4" type="video/mp4"/></video>
      <div style={{position:"fixed",inset:0,zIndex:1,pointerEvents:"none",background:"linear-gradient(to bottom,rgba(7,6,10,0.35) 0%,rgba(7,6,10,0.6) 60%,rgba(7,6,10,0.88) 100%)"}}></div>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Instrument+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap');
        @keyframes loginFadeIn {
          from { opacity:0; transform:translateY(10px); }
          to   { opacity:1; transform:translateY(0); }
        }
        .auth-btn-outline:hover { background: rgba(255,255,255,0.06) !important; border-color: rgba(255,255,255,0.2) !important; } .auth-input { background: rgba(255,255,255,0.05) !important; border: 1px solid rgba(255,255,255,0.1) !important; color: rgba(255,255,255,0.88) !important; } .auth-input::placeholder { color: rgba(255,255,255,0.25) !important; }
        .auth-input:focus { border-color: #3aad98 !important; box-shadow: 0 0 0 3px rgba(58,173,152,0.15) !important; }
        .auth-btn-primary:hover { opacity: 0.88; }
      `}</style>

      {/* Omni. wordmark */}
      <div style={{marginBottom:40,animation:"loginFadeIn 0.7s ease both",textAlign:"center",position:"relative",zIndex:2}}>
        <div style={{display:"flex",alignItems:"baseline",justifyContent:"center",marginBottom:16}}>
          <span style={{fontFamily:"'Instrument Serif',Georgia,serif",fontStyle:"italic",fontWeight:700,fontSize:88,color:"#fff",letterSpacing:"-0.04em",lineHeight:1}}>Omni</span>
          <span style={{fontFamily:"'Instrument Serif',Georgia,serif",fontStyle:"italic",fontWeight:700,fontSize:88,color:"#3aad98",marginLeft:"0.1em",lineHeight:1}}>.</span>
        </div>
        <div style={{fontSize:10,fontWeight:600,letterSpacing:"0.22em",textTransform:"uppercase",color:"rgba(255,255,255,0.22)",marginBottom:4}}>
          Always present
        </div>
        {PRACTICE_CONFIG.name && PRACTICE_CONFIG.name !== "Omni" && (
          <div style={{fontSize:12,fontWeight:500,letterSpacing:"0.06em",color:"rgba(255,255,255,0.45)",marginTop:2,marginBottom:4}}>
            {PRACTICE_CONFIG.name}
          </div>
        )}
        <div style={{marginTop:12,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
          <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.28em",textTransform:"uppercase",color:"rgba(58,173,152,0.7)"}}>
            <span style={{color:"rgba(255,255,255,0.7)",fontSize:11,fontWeight:600,letterSpacing:"0.08em"}}>Owners</span>
          <span style={{color:"rgba(255,255,255,0.35)",fontSize:10,fontWeight:400,margin:"0 4px"}}>and</span>
          <span style={{color:"rgba(255,255,255,0.7)",fontSize:11,fontWeight:600,letterSpacing:"0.08em"}}>Managers</span>
          <span style={{color:"rgba(255,255,255,0.35)",fontSize:10,fontWeight:400,margin:"0 4px"}}>Need</span>
          <span style={{color:"#3aad98",fontSize:12,fontWeight:700,letterSpacing:"0.06em"}}>Iris</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <div style={{width:20,height:1,background:"rgba(58,173,152,0.2)"}}/>
            <div style={{width:6,height:6,borderRadius:"50%",border:"1.5px solid rgba(58,173,152,0.4)",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <div style={{width:2,height:2,borderRadius:"50%",background:"#3aad98"}}/>
            </div>
            <div style={{width:20,height:1,background:"rgba(58,173,152,0.2)"}}/>
          </div>
        </div>
      </div>

      {/* ── MAIN MODE ── */}
      {mode==="main"&&(
        <div style={{width:"100%",maxWidth:360,background:"rgba(255,255,255,0.04)",borderRadius:24,padding:"32px 28px 28px",boxShadow:"0 8px 48px rgba(0,0,0,0.4)",border:"1px solid rgba(255,255,255,0.08)",position:"relative",zIndex:2,animation:"loginFadeIn 0.4s ease both",animationDelay:"0.1s"}}>

          {/* Face ID / Passkey — shown if available and user has registered one */}
          {!passkeyChecking&&passkeyAvailable&&passkeyUser&&(
            <button onClick={async()=>{
              setLoading(true);
              const ok = await authenticatePasskey(passkeyUser.id, anonKey);
              setLoading(false);
              if (ok) {
                localStorage.setItem("pr_last_user_id", passkeyUser.id);
                onLogin(passkeyUser);
              } else {
                setErr("Face ID failed — use another sign in method below");
              }
            }} style={{...btnOutline, marginBottom:14, borderColor:C.teal, background:"rgba(58,173,152,0.08)", color:C.teal, fontWeight:700, borderRadius:14, padding:"13px 16px"}}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 3H5a2 2 0 0 0-2 2v4"/><path d="M15 3h4a2 2 0 0 1 2 2v4"/>
                <path d="M9 21H5a2 2 0 0 1-2-2v-4"/><path d="M15 21h4a2 2 0 0 0 2-2v-4"/>
                <circle cx="12" cy="10" r="3"/><path d="M12 13v2"/><path d="M9 17h6"/>
              </svg>
              {loading ? "Verifying…" : `Sign in as ${passkeyUser.name.split(" ")[0]}`}
            </button>
          )}

          {/* Google */}
          <button className="auth-btn-outline" onClick={()=>sbAuthOAuth("google")} style={{...btnOutline,marginBottom:10,borderRadius:14,padding:"13px 16px",fontWeight:600}}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908C16.12 14.013 17.64 11.834 17.64 9.2z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.18L12.048 13.56C11.24 14.1 10.21 14.42 9 14.42c-2.34 0-4.32-1.58-5.028-3.704H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
              <path d="M3.972 10.716A5.413 5.413 0 013.75 9c0-.593.101-1.17.222-1.716V4.952H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.048l3.015-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.952L3.972 7.284C4.68 5.16 6.66 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          {/* Apple */}
          <button className="auth-btn-outline" onClick={()=>sbAuthOAuth("apple")} style={{...btnOutline,marginBottom:20,borderRadius:14,padding:"13px 16px",fontWeight:600}}>
            <svg width="16" height="18" viewBox="0 0 16 18" fill="none">
              <path d="M13.455 9.582c-.02-2.08 1.695-3.082 1.773-3.13-0.964-1.41-2.467-1.602-3.003-1.624-1.276-.13-2.496.754-3.144.754-.648 0-1.648-.737-2.712-.717-1.394.02-2.686.812-3.402 2.062-1.454 2.52-.373 6.253 1.044 8.3.69 1.003 1.516 2.127 2.602 2.086 1.044-.042 1.44-.674 2.704-.674 1.265 0 1.62.674 2.726.654 1.126-.02 1.838-.99 2.525-1.996.798-1.148 1.128-2.267 1.149-2.325-.026-.012-2.233-.858-2.262-3.39z" fill={C.heading}/>
              <path d="M11.335 3.21c.567-.693.954-1.651.849-2.61-.82.034-1.818.548-2.405 1.24-.525.608-.988 1.584-.864 2.52.916.07 1.853-.467 2.42-1.15z" fill={C.heading}/>
            </svg>
            Continue with Apple
          </button>

          {/* Divider */}
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
            <div style={{flex:1,height:1,background:C.border}}/>
            <span style={{fontSize:11,color:C.muted,fontWeight:600,letterSpacing:"0.05em",textTransform:"uppercase"}}>or</span>
            <div style={{flex:1,height:1,background:C.border}}/>
          </div>

          {/* Email */}
          <button className="auth-btn-outline" onClick={()=>setMode("email")} style={{...btnOutline,marginBottom:12,borderRadius:14,padding:"13px 16px",fontWeight:600}}>
            Sign in with email
          </button>

          {err&&<div style={{marginTop:14,fontSize:12,color:C.danger,textAlign:"center",background:"rgba(255,70,70,0.06)",border:"1px solid rgba(255,70,70,0.2)",borderRadius:8,padding:"8px 12px"}}>{err}</div>}
        </div>
      )}

      {/* ── EMAIL MODE ── */}
      {mode==="email"&&(
        <div style={{width:"100%",maxWidth:340,background:"#181626",borderRadius:20,padding:"28px 24px",boxShadow:"0 2px 20px rgba(0,0,0,0.07)",border:`1px solid ${C.border}`,animation:"loginFadeIn 0.3s ease both"}}>
          {resetSent ? (
            <div style={{textAlign:"center",padding:"20px 0"}}>
              <div style={{fontSize:15,fontWeight:600,color:C.green,marginBottom:8}}>Check your inbox</div>
              <div style={{fontSize:13,color:C.dim,marginBottom:24}}>We've sent a password reset link to {email}</div>
              <button onClick={()=>{setResetSent(false);setMode("main");}} style={{...btnPrimary,background:C.teal}}>Back to sign in</button>
            </div>
          ) : (
            <>
              <input className="auth-input" type="email" placeholder="Email address" value={email}
                onChange={e=>setEmail(e.target.value)} style={{...inputStyle,marginBottom:10}}
                onKeyDown={e=>e.key==="Enter"&&document.getElementById("pr-pass-input")?.focus()}
              />
              <input id="pr-pass-input" className="auth-input" type="password" placeholder="Password" value={pass}
                onChange={e=>setPass(e.target.value)} style={{...inputStyle,marginBottom:6}}
                onKeyDown={e=>e.key==="Enter"&&handleEmailLogin()}
              />
              <div style={{textAlign:"right",marginBottom:20}}>
                <button onClick={handleResetPassword} style={{background:"none",border:"none",color:C.teal,fontSize:12,cursor:"pointer",fontFamily:"inherit",padding:"4px 0"}}>
                  Forgot password?
                </button>
              </div>
              {err&&<div style={{fontSize:12,color:C.danger,marginBottom:14,textAlign:"center"}}>{err}</div>}
              <button className="auth-btn-primary" onClick={handleEmailLogin} disabled={loading} style={{...btnPrimary,opacity:loading?0.6:1,marginBottom:12}}>
                {loading?"Signing in…":"Sign in"}
              </button>
              <button onClick={()=>{setMode("main");setErr("");}} style={{
                width:"100%",padding:"10px",background:"none",border:"none",
                color:C.dim,fontSize:13,cursor:"pointer",fontFamily:"inherit",
              }}>Back</button>
            </>
          )}
        </div>
      )}

      {/* ── CLAIM MODE — magic link verified, GDPR consent + enter ──────────
         v10.4 redesign: PIN demand removed (the magic link IS the verification).
         New flow is: cinematic plays first (handled in App.js — fires before
         this component renders for first-time users), then this welcome card,
         then the user is in. Face ID setup is offered post-entry by the
         existing PasskeySetupModal. PIN becomes a post-onboarding optional
         quick-login method, never a barrier on first login.
      */}
      {mode==="claim"&&(
        <FirstTouchWelcome
          claimEmail={claimEmail}
          state={state}
          onEnter={() => claimAccountDirect()}
          onBack={() => {
            setMode("main");
            setErr("");
            setGdprConsent(false);
            setReadConsent(false);
            setGdprErr(false);
          }}
          gdprErr={gdprErr}
          readConsent={readConsent}
          setReadConsent={setReadConsent}
          gdprConsent={gdprConsent}
          setGdprConsent={setGdprConsent}
          setGdprErr={setGdprErr}
        />
      )}

      {/* Footer */}
      <div style={{position:"absolute",bottom:24,fontSize:10,color:C.muted,letterSpacing:"0.08em"}}>
        Omni · v{VERSION}
      </div>
    </div>
  );
}

/* ══════════════════════════════════
   AI SICK COVER
══════════════════════════════════ */
function parseSick(msg, allNurses, allRecs, allClins) {
  const lower = msg.toLowerCase();
  const all = [...allNurses, ...allRecs, ...(allClins||[])];
  return all.find(s =>
    lower.includes(s.name.split(" ")[0].toLowerCase()) ||
    lower.includes(s.name.split(" ").pop().toLowerCase())
  ) || null;
}

/* ── INTENT PARSER ── */
// Detects what the user means from natural speech
const SICK_WORDS    = ["sick","ill","unwell","not coming","called in","ringing in","phoned in","off sick","poorly","off today","won't be in","can't make it","not in"];
const RETURN_WORDS  = ["coming in","back in","coming back","is in","will be in","phoned back","called back","actually coming","is coming","on their way","heading in","she's in","he's in","fine now","feeling better","recovered"];
const COVER_WORDS   = ["cover","covering","someone cover","need cover","can anyone","who can","swap","replace"];
const QUERY_WORDS   = ["is in","is she","is he","working today","coming in today","scheduled","on today","in today?"];

function parseIntent(text, allNurses, allRecs, allClins) {
  const lower = text.toLowerCase();
  const words = lower.split(/\s+/);
  const all = [...allNurses, ...allRecs, ...(allClins||[])];

  // Find person — whole-word match only to avoid "lea" matching "leanne"
  const person = all.find(s => {
    const first = s.name.split(" ")[0].toLowerCase();
    const last  = s.name.split(" ").pop().toLowerCase();
    return words.includes(first) || words.includes(last);
  }) || null;

  // Detect intent — return beats sick (handles "Sophie phoned back she's coming in")
  const isReturn = RETURN_WORDS.some(w => lower.includes(w));
  const isSick   = !isReturn && SICK_WORDS.some(w => lower.includes(w));
  const isCover  = COVER_WORDS.some(w => lower.includes(w));
  const isQuery  = !isSick && !isReturn && QUERY_WORDS.some(w => lower.includes(w));

  if (isReturn) return { intent:"return",  person };
  if (isSick)   return { intent:"sick",    person };
  if (isCover)  return { intent:"cover",   person };
  if (isQuery)  return { intent:"query",   person };
  // Fallback — if we found a person but no clear intent, ask
  if (person)   return { intent:"unclear", person };
  return { intent:"unknown", person:null };
}

function getWeekStart(dateStr) {
  const d = new Date(dateStr);
  const day = d.getDay();
  const diff = day===0 ? -6 : 1-day;
  d.setDate(d.getDate()+diff);
  return d.toISOString().split("T")[0];
}

function hoursWorkedThisWeek(staffId, weekStart, state) {
  // Count approved non-absent days this week as proxy for hours
  const week = CAL.weeks.find(w=>w.start===weekStart);
  if (!week) return 0;
  return week.days.filter(d=>!isAbsent(state,staffId,d.date)).length * 7.5;
}

function findCover(person, date, allNurses, state) {
  const js = new Date(date).getDay();
  const dow = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][js];
  const weekStart = getWeekStart(date);
  const bankStaff = state.bankStaff||[];
  const agencies  = state.agencies||[];
  const locumRate = state.settings?.locumRate||20;

  // --- TIER 1: Spares already in that day ---
  const dr = buildDayRota(date, person.site, allNurses, state);
  const spares = dr.spares;

  // --- TIER 2: Flexible pool (contracted part-timers not already scheduled) ---
  const flexPool = allNurses.filter(n => {
    if (n.id===person.id||n.site!==person.site||n.maternity||!n.flexible) return false;
    if (isAbsent(state,n.id,date)) return false;
    if (n.noFri&&dow==="Fri") return false;
    if (n.workDays&&!n.workDays.includes(dow)) return false;
    if (spares.find(s=>s.id===n.id)) return false; // already in spares
    return true;
  }).map(n=>({
    ...n,
    hoursThisWeek: hoursWorkedThisWeek(n.id, weekStart, state),
    _tier:"flex"
  }));

  // Also check weekly availability opt-ins
  const availKey = `${person.id}_${weekStart}`;
  const extraAvail = allNurses.filter(n => {
    if (n.id===person.id||n.site!==person.site||n.maternity) return false;
    if (isAbsent(state,n.id,date)) return false;
    const avKey = `${n.id}_${weekStart}`;
    const avDays = state.weeklyAvailability?.[avKey]||[];
    return avDays.includes(dow);
  }).filter(n=>!flexPool.find(f=>f.id===n.id)&&!spares.find(s=>s.id===n.id))
    .map(n=>({...n, hoursThisWeek: hoursWorkedThisWeek(n.id,weekStart,state), _tier:"available"}));

  // --- TIER 3: Cross-site internal ---
  const crossSite = Object.keys(SITES).filter(s=>s!==person.site).flatMap(siteId=>{
    const sNurses = allNurses.filter(n=>n.site===person.site&&!n.maternity&&!n.archived);
    const siteDr  = buildDayRota(date, siteId, sNurses, state);
    return siteDr.spares.map(n=>({
      ...n,
      _tier:"crossSite",
      _fromSite: siteId,
      _leavesShort: siteDr.pairings.filter(p=>!p.nurse).length > 0,
    }));
  });

  // --- TIER 4: Omni Bank ---
  // Role match: nurse absence → show only nurses; reception absence → show only reception
  const isRecAbsence = person.type==="Reception"||person.type==="TCO"||person.type==="Manager";
  const bank = bankStaff.filter(b=>{
    if (b.status==="dnr") return false;
    const todayForBank = new Date().toISOString().split("T")[0];
    if (b.availFrom && todayForBank < b.availFrom) return false;
    if (b.availTo && todayForBank > b.availTo) return false;
    if (b.availDays?.length>0) {
      const dow = new Date(todayForBank+"T12:00:00").toLocaleDateString("en-GB",{weekday:"short"}).slice(0,3);
      if (!b.availDays.includes(dow)) return false;
    }
    const role = (b.role||"").toLowerCase();
    if (isRecAbsence) return role==="receptionist"||role==="reception"||role==="tco";
    // Nurse absence — only show nurses, never receptionists or TCOs
    return role==="nurse"||role==="dental nurse"||role==="qualified nurse"||role==="trainee nurse";
  }).filter(b=>!b.sites||b.sites.includes(person.site)).map(b=>({...b, _tier:"bank"}));

  // --- TIER 5: Agency locum (last resort) ---
  const agencyOptions = agencies.map(ag=>({
    ...ag,
    _tier:"agency",
    estimatedCost: (ag.nurseRate||locumRate) * 7.5,
  }));

  // Score internal candidates
  const scoreN = n => {
    let s = STATUS[n.status]?.score||0;
    if (n.area===person.area) s+=0.3;
    if (n.flexible) s+=0.1;
    if ((n.hoursThisWeek||0) > 30) s-=0.2; // fatigue penalty
    return s;
  };

  return {
    spares,
    flexPool:   [...flexPool,...extraAvail].sort((a,b)=>scoreN(b)-scoreN(a)),
    crossSite:  crossSite.filter(n=>!n._leavesShort),
    crossSiteRisky: crossSite.filter(n=>n._leavesShort),
    bank,
    agencyOptions,
    dr,
    weekStart,
  };
}

function SickCoverAI({state,set,onClose,user}) {
  const [mode,    setMode]   = useState("home");
  const [msg,     setMsg]    = useState("");
  const [trans,   setTrans]  = useState("");
  const [listen,  setListen] = useState(false);
  const [date,    setDate]   = useState(new Date().toISOString().split("T")[0]);
  const [result,  setResult] = useState(null);
  const [done,    setDone]   = useState(false);
  const [doneMsg, setDoneMsg]= useState("");
  const recRef = useRef(null);
  const isPrincipal = user?.role==="principal";
  const todayStr = new Date().toISOString().split("T")[0];
  const userSites = isPrincipal ? Object.keys(SITES) : (user?.managerSites||[user?.site]).filter(Boolean);
  const allNurses = state.nurses||NURSES_BASE;
  const allClins  = getClinicians(state).filter(c=>!c._deleted);

  // Staff rostered today at user's sites — safe wrapper around buildDayRota
  const rosteredToday = userSites.flatMap(siteId=>{
    try {
      const dr = buildDayRota(date, siteId, allNurses, state);
      return [...(dr.pairings||[]).map(p=>p.nurse).filter(Boolean), ...(dr.spares||[])];
    } catch(e) { return []; }
  }).filter((s,i,arr)=>arr.findIndex(x=>x.id===s.id)===i);
  const rosteredIds = new Set(rosteredToday.map(s=>s.id));

  const allSiteStaff = [
    ...allNurses.filter(n=>userSites.includes(n.site)&&!n.maternity&&!n.archived),
    ...RECS_BASE.filter(r=>userSites.includes(r.site)&&!r.archived),
    ...allClins.filter(c=>userSites.some(s=>c.site===s||(c.sites||[]).includes(s))),
  ].filter((s,i,arr)=>arr.findIndex(x=>x.id===s.id)===i);

  const [sickSearch, setSickSearch] = useState("");
  const sickSearchResults = sickSearch.length>1
    ? allSiteStaff.filter(s=>s.name.toLowerCase().includes(sickSearch.toLowerCase())).slice(0,6)
    : [];

  function selectSickPerson(person) {
    const t = person.name.split(" ")[0]+" is sick today";
    setSickSearch("");
    setMsg(t);
    analyse(t);
  }

  function startVoice() {
    const SR = window.SpeechRecognition||window.webkitSpeechRecognition;
    if (!SR) { alert("Voice input needs Chrome or Safari 14.1+"); return; }
    const r = new SR(); r.lang="en-GB"; r.continuous=false; r.interimResults=true;
    r.onresult = e => { const t=Array.from(e.results).map(r=>r[0].transcript).join(""); setTrans(t); setMsg(t); };
    r.onend = () => { setListen(false); };
    r.start(); setListen(true); recRef.current=r;
  }
  function stopVoice() { recRef.current?.stop(); setListen(false); }

  function analyse(text) {
    const liveDay = CAL.days.find(d=>d.date>=date) || CAL.days[0];
    const { intent, person } = parseIntent(text, allNurses, RECS_BASE, allClins);

    if (!person) {
      setResult({intent:"unknown", error:"Couldn't figure out who you mean — try saying their first name clearly."});
      setMode("result"); return;
    }

    if (intent==="unclear") {
      // Ask for clarification
      setResult({intent:"unclear", person, liveDay});
      setMode("result"); return;
    }

    if (intent==="return") {
      // Find and remove today's sick record for this person
      const existingSick = (state.sick||[]).find(s=>s.staffId===person.id&&s.date===liveDay.date&&!s.rtw);
      setResult({intent:"return", person, liveDay, existingSick});
      setMode("result"); return;
    }

    if (intent==="sick") {
      const cover = findCover(person, liveDay.date, allNurses, state);
      setResult({intent:"sick", person, liveDay, ...cover});
      setMode("result"); return;
    }

    if (intent==="cover") {
      const cover = findCover(person, liveDay.date, allNurses, state);
      setResult({intent:"cover", person, liveDay, ...cover});
      setMode("result"); return;
    }

    if (intent==="query") {
      const absent = isAbsent(state, person.id, liveDay.date);
      const dr = buildDayRota(liveDay.date, person.site, allNurses, state);
      const pairing = dr.pairings.find(p=>p.nurse?.id===person.id);
      setResult({intent:"query", person, liveDay, absent, pairing});
      setMode("result"); return;
    }
  }

  function confirmSick() {
    const p = result.person, d = result.liveDay;
    const isClin = !!(p.sched||p.type&&["GDS","MOS","FD","Therapist","Partner","Specialist"].includes(p.type));
    const role = isClin ? "clinician" : (p.area||p.status) ? "nurse" : "reception";
    set(s=>({
      ...s,
      sick:[...s.sick,{id:"s"+Date.now(),staffId:p.id,name:p.name,site:p.site,role,date:d.date,days:1,contact:"called",reason:"illness",certified:false,rtw:isClin,notes:"Logged via Iris"}],
      ...addLog(s, user?.name||"Unknown", "absence", `Absence logged via Iris: ${p.name} · ${d.date}`)
    }));
    setDoneMsg(`${p.name.split(" ")[0]} marked absent — rota updated`);
    setDone(true);
  }

  function confirmReturn() {
    const p = result.person, d = result.liveDay;
    if (result.existingSick) {
      // Mark RTW on existing sick record
      set(s=>({...s,sick:s.sick.map(r=>r.id===result.existingSick.id?{...r,rtw:true}:r)}));
    }
    // Also remove any leave for today
    set(s=>({...s,leave:{...s.leave,[p.id]:(s.leave[p.id]||[]).filter(l=>l.date!==d.date)}}));
    setDoneMsg(`${p.name.split(" ")[0]} is back — absence cleared`);
    setDone(true);
  }

  function clarifyIntent(chosenIntent) {
    const updated = {...result, intent:chosenIntent};
    if (chosenIntent==="sick") {
      const cover = findCover(result.person, result.liveDay.date, allNurses, state);
      setResult({...updated, ...cover});
    } else if (chosenIntent==="return") {
      const existingSick = (state.sick||[]).find(s=>s.staffId===result.person.id&&s.date===result.liveDay.date&&!s.rtw);
      setResult({...updated, existingSick});
    } else {
      setResult(updated);
    }
  }

  return (
    <Overlay onClose={onClose}>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
        <div style={{width:44,height:44,borderRadius:14,background:`linear-gradient(135deg,${C.teal},${C.grad2})`,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
          <LogoMark size={36}/>
        </div>
        <div>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading}}>Iris</div>
          <div style={{fontSize:12,color:C.dim}}>Sick, back in, need cover — just say it</div>
        </div>
      </div>

      {mode==="home"&&(
        <div>
          <div style={{marginBottom:14}}>
            <label style={LB}>Date</label>
            <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={IS}/>
          </div>

          {/* Quick-tap person picker */}
          <div style={{marginBottom:14}}>
            <input value={sickSearch} onChange={e=>setSickSearch(e.target.value)}
              placeholder="Type a name…"
              style={{...IS,marginBottom:sickSearch.length>1&&sickSearchResults.length>0?8:0,fontSize:14}}/>
            {sickSearch.length>1&&sickSearchResults.length>0&&(
              <div style={{display:"flex",flexDirection:"column",gap:4,marginTop:8}}>
                {sickSearchResults.map(s=>(
                  <button key={s.id} onClick={()=>selectSickPerson(s)}
                    style={{padding:"10px 14px",borderRadius:10,border:`1px solid ${C.border}`,background:C.bg,color:C.text,fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer",textAlign:"left",display:"flex",justifyContent:"space-between"}}>
                    <span>{s.name}</span>
                    <span style={{fontSize:11,color:C.dim}}>{SITES[s.site]?.name}</span>
                  </button>
                ))}
              </div>
            )}
            {!sickSearch&&(
              <div>
                {rosteredToday.length>0&&(
                  <>
                    <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8,marginTop:4}}>On site today</div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:12}}>
                      {rosteredToday.map(n=>(
                        <button key={n.id} onClick={()=>selectSickPerson(n)} style={{
                          padding:"9px 16px",borderRadius:99,fontFamily:"inherit",fontSize:13,fontWeight:700,cursor:"pointer",
                          border:`1.5px solid ${SITES[n.site]?.color||C.teal}44`,
                          background:`${SITES[n.site]?.color||C.teal}12`,color:SITES[n.site]?.color||C.teal,
                        }}>{n.name.split(" ")[0]}</button>
                      ))}
                    </div>
                  </>
                )}
                {allSiteStaff.filter(s=>!rosteredIds.has(s.id)).length>0&&(
                  <>
                    <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>All staff</div>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>
                      {allSiteStaff.filter(s=>!rosteredIds.has(s.id)).slice(0,10).map(n=>(
                        <button key={n.id} onClick={()=>selectSickPerson(n)} style={{
                          padding:"7px 13px",borderRadius:99,fontFamily:"inherit",fontSize:12,fontWeight:600,cursor:"pointer",
                          border:`1px solid ${C.border}`,background:"transparent",color:C.mid,
                        }}>{n.name.split(" ")[0]}</button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Type / Voice */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
            {[["keyboard","Type","type","#e8faf7",C.teal],["mic","Voice","voice","#f5f3ff",C.grad2]].map(([ico,lbl,m,bg,col])=>(
              <button key={m} onClick={()=>setMode(m)} style={{padding:16,borderRadius:14,border:"1.5px solid "+col+"33",background:bg,color:col,fontFamily:"inherit",fontSize:14,fontWeight:700,cursor:"pointer",textAlign:"center",display:"flex",flexDirection:"column",alignItems:"center",gap:8}}>
                <Icon name={ico} size={28} color={col}/>
                {lbl}
              </button>
            ))}
          </div>
          <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 12px"}}>
            <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>You can say things like…</div>
            {["Sophie has called in sick","Sophie phoned back, she's coming in","Can someone cover for Hannah this afternoon","Is Liv in today?"].map(eg=>(
              <div key={eg} onClick={()=>{setMsg(eg);setMode("type");}} style={{fontSize:12,color:C.teal,padding:"4px 0",cursor:"pointer",borderBottom:`1px solid ${C.border2}`}}>"{eg}"</div>
            ))}
          </div>
        </div>
      )}

      {mode==="type"&&(
        <div>
          <label style={LB}>Tell Omni what happened</label>
          <textarea value={msg} onChange={e=>setMsg(e.target.value)}
            placeholder="e.g. Sophie has called in sick this morning"
            rows={3} style={{...IS,resize:"none",lineHeight:1.6,marginBottom:6}}/>
          <div style={{fontSize:11,color:C.dim,marginBottom:14}}>Just type naturally — sick, coming back, need cover, just ask.</div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>analyse(msg)} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Go →</button>
            <button onClick={()=>setMode("home")} style={BTN(C.dim,true)}>Back</button>
          </div>
        </div>
      )}

      {mode==="voice"&&(
        <div style={{textAlign:"center",padding:"16px 0"}}>
          <button onClick={listen?stopVoice:startVoice}
            style={{width:96,height:96,borderRadius:99,border:"none",background:listen?`linear-gradient(135deg,${C.danger},${C.grad3})`:`linear-gradient(135deg,${C.teal},${C.grad2})`,color:"#fff",fontSize:36,cursor:"pointer",marginBottom:14,boxShadow:listen?"0 0 0 10px rgba(220,38,38,0.15)":"0 4px 20px rgba(42,125,110,0.25)",transition:"all 0.3s",display:"flex",alignItems:"center",justifyContent:"center"}}>
            {listen?<Icon name="stop" size={36} color="#fff"/>:<Icon name="mic" size={36} color="#fff"/>}
          </button>
          <div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:4}}>{listen?"Listening…":"Tap to speak"}</div>
          <div style={{fontSize:11,color:C.dim,marginBottom:12}}>Works with Manchester accents · Edit below if needed</div>
          {trans&&(
            <div style={{background:C.tealPale,border:`1px solid ${C.tealBorder}`,borderRadius:10,padding:12,fontSize:13,color:C.teal,marginBottom:12,textAlign:"left"}}>
              "{trans}"
            </div>
          )}
          {msg&&<textarea value={msg} onChange={e=>setMsg(e.target.value)} rows={2} style={{...IS,resize:"none",marginBottom:12}}/>}
          <div style={{display:"flex",gap:8,justifyContent:"center"}}>
            {msg&&<button onClick={()=>analyse(msg)} style={BTN(C.teal)}>Go →</button>}
            <button onClick={()=>setMode("home")} style={BTN(C.dim,true)}>Back</button>
          </div>
        </div>
      )}

      {mode==="result"&&!done&&(
        <div>
          {/* UNKNOWN */}
          {result?.intent==="unknown"&&(
            <div>
              <div style={{background:"rgba(255,70,70,0.08)",border:"1px solid rgba(255,70,70,0.2)",borderRadius:12,padding:14,color:C.danger,marginBottom:16}}>{result.error}</div>
              <button onClick={()=>setMode("home")} style={{...BTN(C.teal),width:"100%",padding:"11px 0"}}>Try again</button>
            </div>
          )}

          {/* UNCLEAR — ask what they meant */}
          {result?.intent==="unclear"&&(
            <div>
              <div style={{...CS,marginBottom:16,padding:"14px 16px"}}>
                <div style={{fontWeight:700,color:C.text,fontSize:14,marginBottom:4}}>{result.person.name}</div>
                <div style={{fontSize:12,color:C.dim}}>{result.liveDay.label} · {SITES[result.person.site]?.name}</div>
              </div>
              <div style={{fontSize:13,color:C.soft,marginBottom:12}}>What's happening with {result.person.name.split(" ")[0]}?</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
                {[["thermometer","They're sick","sick",C.danger],["check-circle","They're coming in","return",C.green],["refresh","Need cover","cover",C.teal]].map(([ico,lbl,intent,col])=>(
                  <button key={intent} onClick={()=>clarifyIntent(intent)}
                    style={{padding:"14px 10px",borderRadius:12,border:`1.5px solid ${col}33`,background:col+"08",color:col,fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer",textAlign:"center",display:"flex",flexDirection:"column",alignItems:"center",gap:6}}>
                    <Icon name={ico} size={24} color={col}/>{lbl}
                  </button>
                ))}
              </div>
              <button onClick={()=>{setMode("home");setResult(null);}} style={{...BTN(C.dim,true),width:"100%",padding:"9px 0"}}>Cancel</button>
            </div>
          )}

          {/* RETURN — coming back in */}
          {result?.intent==="return"&&(
            <div>
              <div style={{background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:12,padding:14,marginBottom:16}}>
                <div style={{fontWeight:700,color:C.green,fontSize:14,marginBottom:2,display:"flex",alignItems:"center",gap:6}}><Icon name="check-circle" size={16} color={C.green}/> {result.person.name} is coming in</div>
                <div style={{fontSize:12,color:C.mid}}>{result.liveDay.label} · {SITES[result.person.site]?.name}</div>
                {result.existingSick
                  ? <div style={{fontSize:12,color:C.green,marginTop:6}}>Will mark today's absence as RTW ✓</div>
                  : <div style={{fontSize:12,color:C.dim,marginTop:6}}>No absence on record for today — will clear any leave entry</div>}
              </div>
              <button onClick={confirmReturn} style={{...BTN(C.green),width:"100%",padding:"11px 0",marginBottom:8}}>
                Confirm — clear absence &amp; update rota
              </button>
              <button onClick={()=>{setMode("home");setResult(null);}} style={{...BTN(C.dim,true),width:"100%",padding:"9px 0"}}>Cancel</button>
            </div>
          )}

          {/* SICK — log absence + find cover */}
          {(result?.intent==="sick"||result?.intent==="cover")&&(
            <div>
              <div style={{background:"rgba(245,197,24,0.08)",border:`1px solid ${C.warn}44`,borderRadius:12,padding:14,marginBottom:16}}>
                <div style={{fontWeight:700,color:C.warn,fontSize:14,marginBottom:2,display:"flex",alignItems:"center",gap:6}}><Icon name="thermometer" size={16} color={C.warn}/> {result.person.name} · {result.liveDay.label}</div>
                <div style={{fontSize:12,color:C.mid}}>{SITES[result.person.site]?.name} · {result.person.area||"Reception"}</div>
              </div>

              {/* TIER 1 — Spares */}
              {result?.spares?.length>0&&(
                <div style={{background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:12,padding:14,marginBottom:10}}>
                  <div style={{fontWeight:700,color:C.green,fontSize:13,marginBottom:4,display:"flex",alignItems:"center",gap:6}}><Dot color={C.green} size={10}/> Covered — spare staff already on site</div>
                  <div style={{fontSize:12,color:C.mid}}>{result.spares.map(n=>n.name).join(", ")} {result.spares.length===1?"is":"are"} in today and can cover</div>
                </div>
              )}

              {/* TIER 2 — Flexible pool + availability opt-ins */}
              {result?.flexPool?.length>0&&(
                <div style={{marginBottom:10}}>
                  <div style={{fontSize:10,fontWeight:700,color:C.teal,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6,display:"flex",alignItems:"center",gap:5}}><Dot color={C.teal} size={8}/> Tier 2 — Flexible / available staff</div>
                  {result.flexPool.slice(0,3).map((n,i)=>{
                    const st=STATUS[n.status];
                    const tired = (n.hoursThisWeek||0)>30;
                    return (
                      <div key={n.id} style={{...CS,marginBottom:5,padding:"10px 12px",borderLeft:`3px solid ${i===0?C.teal:C.border}`}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                          <div>
                            <div style={{fontWeight:700,color:C.text,fontSize:12}}>{i===0?<><Icon name="star-filled" size={12} color={C.warn}/>{" "}</>:""}{n.name}</div>
                            <div style={{fontSize:10,color:C.dim,marginTop:1}}>{n.area} · {n.hoursThisWeek||0}h this week{tired?<><span style={{marginLeft:4,display:"inline-flex"}}><Icon name="warning" size={10} color={C.warn}/></span> near limit</>:""}</div>
                          </div>
                          <div style={{display:"flex",gap:4,flexWrap:"wrap",justifyContent:"flex-end"}}>
                            {n._tier==="available"&&<Pill c={C.lime} t="Opted in" sm/>}
                            {n.flexible&&<Pill c={C.teal} t="Flexible" sm/>}
                            {st&&<Pill c={st.color} t={st.label} sm/>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* TIER 3 — Cross-site */}
              {result?.crossSite?.length>0&&(
                <div style={{marginBottom:10}}>
                  <div style={{fontSize:10,fontWeight:700,color:C.brook,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6,display:"flex",alignItems:"center",gap:5}}><Dot color={C.brook} size={8}/> Tier 3 — Cross-site internal</div>
                  {result.crossSite.slice(0,2).map(n=>(
                    <div key={n.id} style={{...CS,marginBottom:5,padding:"10px 12px",borderLeft:`3px solid ${C.brook}`}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div>
                          <div style={{fontWeight:700,color:C.text,fontSize:12}}>{n.name}</div>
                          <div style={{fontSize:10,color:C.dim,marginTop:1}}>Spare at {SITES[n._fromSite]?.name} · {n.area}</div>
                        </div>
                        <Pill c={C.brook} t={SITES[n._fromSite]?.name} sm/>
                      </div>
                    </div>
                  ))}
                  {result.crossSiteRisky?.length>0&&(
                    <div style={{fontSize:10,color:C.warn,marginTop:4,display:"flex",alignItems:"center",gap:4}}><Icon name="warning" size={10} color={C.warn}/> {result.crossSiteRisky.length} cross-site option{result.crossSiteRisky.length!==1?"s":""} would leave other site short — use with caution</div>
                  )}
                </div>
              )}

              {/* TIER 4 — Omni Bank */}
              {result?.bank?.length>0&&(
                <div style={{marginBottom:10}}>
                  <div style={{fontSize:10,fontWeight:700,color:C.warn,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6,display:"flex",alignItems:"center",gap:5}}><Icon name="star" size={12} color={C.warn}/> Tier 4 — Omni Bank</div>
                  {result.bank.slice(0,3).map(b=>(
                    <div key={b.id} style={{...CS,marginBottom:5,padding:"10px 12px",borderLeft:`3px solid ${C.warn}`,background:"rgba(245,197,24,0.05)"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div>
                          <div style={{fontWeight:700,color:C.text,fontSize:12,display:"flex",alignItems:"center",gap:5}}><Icon name="star-filled" size={11} color={C.warn}/> {b.name}</div>
                          <div style={{fontSize:10,color:C.dim,marginTop:1}}>{b.role} · {b.type||"Bank"}{b.rate?` · £${b.rate}/hr`:""}</div>
                        </div>
                        <div style={{display:"flex",gap:4,flexDirection:"column",alignItems:"flex-end"}}>
                          {b.phone&&(
                            <button onClick={()=>{
                              const practiceName=state?.practiceProfile?.name||PRACTICE_CONFIG.name||"the practice";
                              const msg=`Hi ${b.name.split(" ")[0]}, we're short at ${SITES[result.person.site]?.name} on ${result.liveDay.label}. Are you available? Thanks, ${practiceName}`;
                              navigator.clipboard.writeText(msg);
                            }} style={{...BTN(C.warn,true),fontSize:9,padding:"2px 8px",display:"flex",alignItems:"center",gap:3}}><Icon name="clipboard" size={10} color={C.warn}/> Copy WhatsApp</button>
                          )}
                          {b.knownLocum&&<Pill c={C.lime} t="Known locum" sm/>}
                          {b.exStaff&&<Pill c={C.teal} t="Ex-staff" sm/>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* TIER 5 — Agency last resort */}
              {result?.spares?.length===0&&result?.flexPool?.length===0&&result?.crossSite?.length===0&&result?.bank?.length===0&&(
                <div style={{background:"rgba(255,70,70,0.08)",border:"1px solid rgba(255,70,70,0.2)",borderRadius:12,padding:14,marginBottom:10}}>
                  <div style={{fontWeight:700,color:C.danger,fontSize:13,marginBottom:4,display:"flex",alignItems:"center",gap:6}}><Dot color={C.danger} size={10}/> Tier 5 — Agency locum required</div>
                  <div style={{fontSize:11,color:C.dim,marginBottom:8}}>No internal cover available. Estimated cost:</div>
                  {result.agencyOptions?.slice(0,2).map(ag=>{
                    const [waCopied,setWaCopied] = [false,()=>{}];
                    const practiceName = state?.practiceProfile?.name||PRACTICE_CONFIG.name||"the practice";
                    const waMsg = `Hi ${ag.name},\n\nWe urgently need a qualified dental nurse at ${SITES[result.person.site]?.name} today (${result.liveDay.label}), approx 08:00–17:30.\n\nCan you help? Please reply ASAP.\n\nThanks, ${practiceName}`;
                    return (
                      <div key={ag.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${C.border2}`}}>
                        <div>
                          <div style={{fontSize:12,fontWeight:700,color:C.text}}>{ag.name}</div>
                          <div style={{fontSize:10,color:C.dim}}>{ag.phone} · Est. {fmt(ag.estimatedCost)}</div>
                        </div>
                        <button onClick={()=>navigator.clipboard.writeText(waMsg)} style={{...BTN(C.danger,true),fontSize:9,padding:"3px 8px",display:"flex",alignItems:"center",gap:3}}><Icon name="clipboard" size={10} color={C.danger}/> WhatsApp</button>
                      </div>
                    );
                  })}
                  {result.agencyOptions?.length===0&&<div style={{fontSize:11,color:C.dim}}>No agencies saved — add them in the Agencies tab.</div>}
                </div>
              )}

              {/* Partial cover hint */}
              {result?.spares?.length===0&&(result?.flexPool?.length>0||result?.bank?.length>0||result?.crossSite?.length>0)&&(
                <div style={{fontSize:11,color:C.teal,marginBottom:10,padding:"8px 12px",background:C.tealPale,borderRadius:8,display:"flex",alignItems:"center",gap:6}}>
                  <Icon name="lightbulb" size={13} color={C.teal}/> Internal options available above — try these before calling an agency
                </div>
              )}

              {result?.intent==="sick"&&(
                <button onClick={confirmSick} style={{...BTN(C.danger),width:"100%",padding:"11px 0",marginBottom:8}}>
                  Confirm {result.person.name.split(" ")[0]} as sick &amp; log absence
                </button>
              )}
              <button onClick={()=>{setMode("home");setResult(null);}} style={{...BTN(C.dim,true),width:"100%",padding:"9px 0"}}>Cancel</button>
            </div>
          )}

          {/* QUERY — is X in today? */}
          {result?.intent==="query"&&(
            <div>
              <div style={{...CS,marginBottom:16,padding:"16px"}}>
                <div style={{fontWeight:700,color:C.text,fontSize:14,marginBottom:8}}>{result.person.name}</div>
                <div style={{fontSize:13,color:result.absent?C.danger:C.green,fontWeight:600,marginBottom:6,display:"flex",alignItems:"center",gap:6}}>
                  <Icon name={result.absent?"x-circle":"check-circle"} size={16} color={result.absent?C.danger:C.green}/>
                  {result.absent?"Not in today":"In today"}
                </div>
                {result.pairing&&(
                  <div style={{fontSize:12,color:C.mid}}>
                    Assigned to {result.pairing.clinician.name} · {result.pairing.clinStart}–{result.pairing.clinEnd}
                  </div>
                )}
                <div style={{fontSize:11,color:C.dim,marginTop:4}}>{result.liveDay.label} · {SITES[result.person.site]?.name}</div>
              </div>
              <button onClick={()=>{setMode("home");setResult(null);}} style={{...BTN(C.teal),width:"100%",padding:"11px 0"}}>Done</button>
            </div>
          )}
        </div>
      )}

      {done&&(
        <div style={{textAlign:"center",padding:"24px 0"}}>
          <div style={{marginBottom:12,display:"flex",justifyContent:"center"}}>
            {doneMsg.includes("back")?<Icon name="check-circle" size={52} color={C.green}/>:<Icon name="thermometer" size={52} color={C.warn}/>}
          </div>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading,marginBottom:8}}>Done</div>
          <div style={{fontSize:13,color:C.mid,marginBottom:20}}>{doneMsg}</div>
          <button onClick={onClose} style={{...BTN(C.teal),padding:"11px 28px"}}>Close</button>
        </div>
      )}
    </Overlay>
  );
}

/* ══════════════════════════════════
   MODALS
══════════════════════════════════ */

/* Inline version of SickModal — no Overlay wrapper, used when picker is above */
function SickModalInline({person, onSave, onClose}) {
  const [f,setF] = useState({date:new Date().toISOString().split("T")[0],days:1,contact:"called",reason:"illness",certified:false,rtw:false,notes:"",generalNotes:"",fitNotes:[]});
  const s = (k,v) => setF(p=>({...p,[k]:v}));
  return (
    <>
      {[
        {l:"Start date",  e:<input type="date" value={f.date} onChange={e=>s("date",e.target.value)} style={IS}/>},
        {l:"Working days absent", e:<input type="number" min="1" value={f.days} onChange={e=>s("days",+e.target.value)} style={IS}/>},
        {l:"Contact",     e:<select value={f.contact} onChange={e=>s("contact",e.target.value)} style={IS}><option value="called">Called in</option><option value="texted">Texted</option><option value="emailed">Emailed</option><option value="planned">Planned absence</option></select>},
        {l:"Reason",      e:<select value={f.reason} onChange={e=>s("reason",e.target.value)} style={IS}>
          {ABSENCE_REASONS.map(r=><option key={r.id} value={r.id}>{r.label}</option>)}
        </select>},
      ].map(({l,e})=><div key={l} style={{marginBottom:12}}><label style={LB}>{l}</label>{e}</div>)}
      <div style={{marginBottom:12}}>
        <label style={LB}>Internal notes <span style={{fontWeight:400,color:C.dim}}>(manager only — never shown to staff)</span></label>
        <textarea value={f.generalNotes} onChange={e=>s("generalNotes",e.target.value)} rows={2}
          placeholder="e.g. Called at 7:30am, said stomach bug, hopes to be back Thursday"
          style={{...IS,resize:"none",lineHeight:1.5}}/>
      </div>
      <div style={{display:"flex",gap:20,marginBottom:16}}>
        {[["certified","GP / Fit Note received"],["rtw","RTW Done"]].map(([k,lab])=>(
          <label key={k} style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:C.soft,cursor:"pointer"}}>
            <input type="checkbox" checked={f[k]} onChange={e=>s(k,e.target.checked)} style={{width:16,height:16,accentColor:C.teal}}/>{lab}
          </label>
        ))}
      </div>
      {f.certified&&(
        <div style={{background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:8,padding:"10px 12px",marginBottom:14}}>
          <div style={{fontSize:11,fontWeight:700,color:C.green,marginBottom:8}}>Fit note log</div>
          {(f.fitNotes||[]).map((fn,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,fontSize:12}}>
              <span style={{color:C.dim,minWidth:20}}>#{i+1}</span>
              <input type="date" value={fn.date} onChange={e=>{const n=[...f.fitNotes];n[i]={...n[i],date:e.target.value};s("fitNotes",n);}} style={{...IS,marginBottom:0,flex:1,fontSize:11,padding:"4px 8px"}}/>
              <input value={fn.note||""} placeholder="Note e.g. covers 2 weeks" onChange={e=>{const n=[...f.fitNotes];n[i]={...n[i],note:e.target.value};s("fitNotes",n);}} style={{...IS,marginBottom:0,flex:2,fontSize:11,padding:"4px 8px"}}/>
              <button onClick={()=>s("fitNotes",f.fitNotes.filter((_,j)=>j!==i))} style={{background:"transparent",border:"none",color:C.danger,cursor:"pointer",fontSize:14,padding:"0 4px"}}>×</button>
            </div>
          ))}
          <button onClick={()=>s("fitNotes",[...(f.fitNotes||[]),{date:new Date().toISOString().split("T")[0],note:""}])}
            style={{...BTN(C.green,true),fontSize:11,padding:"4px 10px",marginTop:4}}>+ Add fit note</button>
        </div>
      )}
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>{
          const isClin=!!(person.sched||["GDS","MOS","FD","Therapist","Partner","Specialist"].includes(person.type));
          const role=isClin?"clinician":(person.area||person.status)?"nurse":"reception";
          onSave({...f,id:"s"+Date.now(),staffId:person.id,name:person.name,site:person.site,role,rtw:isClin?true:f.rtw});
        }} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Save</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </>
  );
}

/* Head Nurse absence reporting modal — single Overlay, person picker + inline form */
function HnAbsenceModal({allSiteStaff,latPerson,setLatPerson,nurseName,siteName,onSave,onClose}) {
  const person = allSiteStaff.find(s=>s.id===latPerson)||allSiteStaff[0];
  const [f,setF] = useState({date:new Date().toISOString().split("T")[0],days:1,contact:"called",reason:"illness",certified:false,rtw:false,notes:""});
  const s = (k,v) => setF(p=>({...p,[k]:v}));
  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Report Absence</div>
      <div style={{fontSize:12,color:C.dim,marginBottom:16}}>{siteName} · logged by {nurseName}</div>
      <div style={{marginBottom:14}}>
        <label style={LB}>Who is absent?</label>
        <select value={person?.id||""} onChange={e=>setLatPerson(e.target.value)} style={IS}>
          {allSiteStaff.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      {person&&(
        <>
          {[
            {l:"Date",    e:<input type="date" value={f.date} onChange={e=>s("date",e.target.value)} style={IS}/>},
            {l:"Days",    e:<input type="number" min="1" value={f.days} onChange={e=>s("days",+e.target.value)} style={IS}/>},
            {l:"Contact", e:<select value={f.contact} onChange={e=>s("contact",e.target.value)} style={IS}><option value="called">Called in</option><option value="texted">Texted</option><option value="emailed">Emailed</option><option value="planned">Planned absence</option></select>},
            {l:"Reason",  e:<select value={f.reason} onChange={e=>s("reason",e.target.value)} style={IS}><option value="illness">Illness</option><option value="medical">Medical appointment</option><option value="leftEarly">Left early — unwell</option><option value="surgery">Planned surgery</option><option value="childcare">Childcare</option><option value="other">Other</option></select>},
            {l:"Notes",   e:<input value={f.notes} onChange={e=>s("notes",e.target.value)} style={IS}/>},
          ].map(({l,e})=><div key={l} style={{marginBottom:12}}><label style={LB}>{l}</label>{e}</div>)}
          <div style={{display:"flex",gap:20,marginBottom:16}}>
            {[["certified","GP Certified"],["rtw","RTW Done"]].map(([k,lab])=>(
              <label key={k} style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:C.soft,cursor:"pointer"}}>
                <input type="checkbox" checked={f[k]} onChange={e=>s(k,e.target.checked)} style={{width:16,height:16,accentColor:C.teal}}/>{lab}
              </label>
            ))}
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>onSave({...f,id:"s"+Date.now(),staffId:person.id,name:person.name,site:person.site})} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Save</button>
            <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
          </div>
        </>
      )}
    </Overlay>
  );
}

function SickModal({person,onSave,onClose}) {
  const [f,setF] = useState({date:new Date().toISOString().split("T")[0],days:1,contact:"called",reason:"illness",certified:false,rtw:false,generalNotes:"",leaveRejectedRecently:false});
  const s = (k,v) => setF(p=>({...p,[k]:v}));
  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Record Absence</div>
      <div style={{fontSize:13,color:C.dim,marginBottom:16}}>{person.name}</div>
      {[
        {l:"Date",    e:<input type="date" value={f.date} onChange={e=>s("date",e.target.value)} style={IS}/>},
        {l:"Days",    e:<input type="number" min="1" value={f.days} onChange={e=>s("days",+e.target.value)} style={IS}/>},
        {l:"Contact", e:<select value={f.contact} onChange={e=>s("contact",e.target.value)} style={IS}><option value="called">Called in</option><option value="texted">Texted</option><option value="emailed">Emailed</option></select>},
        {l:"Reason",  e:<select value={f.reason} onChange={e=>s("reason",e.target.value)} style={IS}><option value="illness">Illness</option><option value="childcare">Childcare</option><option value="other">Other</option></select>},
      ].map(({l,e})=><div key={l} style={{marginBottom:12}}><label style={LB}>{l}</label>{e}</div>)}
      <div style={{marginBottom:12}}>
        <label style={LB}>Notes <span style={{color:C.dim,fontWeight:400}}>(add now or fill in later)</span></label>
        <textarea value={f.generalNotes} onChange={e=>s("generalNotes",e.target.value)} rows={2}
          placeholder="e.g. Called at 7:30am, said stomach bug, expects to be back Thursday"
          style={{...IS,resize:"none",lineHeight:1.5}}/>
      </div>
      <div style={{display:"flex",gap:20,marginBottom:12}}>
        {[["certified","GP / Fit Note"],["rtw","RTW Done"]].map(([k,lab])=>(
          <label key={k} style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:C.soft,cursor:"pointer"}}>
            <input type="checkbox" checked={f[k]} onChange={e=>s(k,e.target.checked)} style={{width:16,height:16,accentColor:C.teal}}/>{lab}
          </label>
        ))}
      </div>
      <label style={{display:"flex",alignItems:"flex-start",gap:10,marginBottom:12,padding:"10px 12px",background:"rgba(245,197,24,0.05)",border:"1px solid #fde68a",borderRadius:8,cursor:"pointer"}}>
        <input type="checkbox" checked={f.leaveRejectedRecently} onChange={e=>s("leaveRejectedRecently",e.target.checked)} style={{width:16,height:16,accentColor:C.warn,marginTop:1,flexShrink:0}}/>
        <div>
          <div style={{fontSize:12,fontWeight:700,color:C.warn,marginBottom:2}}>Leave request was recently declined</div>
          <div style={{fontSize:10,color:C.dim,lineHeight:1.4}}>Flag for RTW — not an accusation, just context.</div>
        </div>
      </label>
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>onSave({...f,id:"s"+Date.now(),staffId:person.id,name:person.name,site:person.site})} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Save</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}

const LEAVE_TYPES = [
  { id:"annual",     label:"Annual Leave",         color:C.teal,    countsAgainst:true,  rtwNeeded:false, bradfordCounts:true,  noteRequired:false, note:"Counts against holiday entitlement" },
  { id:"cpd",        label:"CPD / Training Day",   color:"#00e5a0", countsAgainst:false, rtwNeeded:false, bradfordCounts:false, noteRequired:true,  note:"Employer-funded — doesn't affect balance. Add course name below." },
  { id:"medical",    label:"Medical Appointment",  color:"#60a5fa", countsAgainst:false, rtwNeeded:false, bradfordCounts:false, noteRequired:true,  note:"Planned appointment — doesn't affect Bradford Factor. Note required." },
  { id:"maternity",  label:"Maternity Leave",      color:"#d97706", countsAgainst:false, rtwNeeded:false, bradfordCounts:false, noteRequired:true,  note:"Set start date and expected return. Statutory provisions apply." },
  { id:"paternity",  label:"Paternity Leave",      color:"#d97706", countsAgainst:false, rtwNeeded:false, bradfordCounts:false, noteRequired:false, note:"1–2 weeks statutory — doesn't affect holiday entitlement." },
  { id:"unpaid",     label:"Unpaid Leave",          color:C.dim,    countsAgainst:false, rtwNeeded:false, bradfordCounts:false, noteRequired:false, note:"Manager discretion — no pay" },
  { id:"carers",     label:"Carer's Leave",        color:"#a78bfa", countsAgainst:false, rtwNeeded:false, bradfordCounts:false, noteRequired:false, maxDays:5, note:"Statutory right — 5 days unpaid per leave year. Cannot be refused, only delayed up to 1 month. No Bradford Factor impact." },
  { id:"toil",       label:"TOIL (Time in Lieu)",  color:"#f472b6", countsAgainst:false, rtwNeeded:false, bradfordCounts:false, noteRequired:false, note:"Draws from banked TOIL hours. Balance must cover the days requested." },
];

function LeaveModal({person,state,onSubmit,onClose}) {
  const lyLMType = roleTypeForPerson(person);
  const lyLMOv = (state.leaveYearOverrides||{})[person.id];
  const lyLMCfg = lyLMOv?{[lyLMType]:lyLMOv}:state.leaveYearConfig;
  const {start:lyLMS,end:lyLME} = getLeaveYear(lyLMType, lyLMCfg);
  const lyLMStart = lyLMS.toISOString().split("T")[0];
  const lyLMEnd   = lyLME.toISOString().split("T")[0];
  const existing = (state.leave[person.id]||[]).filter(l=>l.date>=lyLMStart&&l.date<=lyLMEnd);
  const used = countDays(existing.filter(l=>l.status!=="declined"&&(!l.leaveType||l.leaveType==="annual")));
  const hist = getHistDays(state, person.id);
  const entit = getEntitlement(state, person);
  const bhDays = getBankHolidayDays(person);
  const bhProrated = Math.round(bhDays * ((person.hrs||37.5)/37.5) * 10) / 10;
  const bookable = Math.max(0, Math.round((entit - bhProrated) * 10) / 10);
  const remaining = Math.round((bookable - used - hist) * 10) / 10;
  const pending = (state.leave[person.id]||[]).filter(l=>l.status==="pending"&&l.date>=lyLMStart&&l.date<=lyLMEnd).length;
  const [sel,setSel]       = useState([]);
  const [halfDays,setHalfDays] = useState({}); // {[date]: "AM"|"PM"} — only for individually picked days
  const [leaveType,setLeaveType] = useState("annual");
  const [note,setNote]     = useState("");
  const [step,setStep]     = useState("type"); // "type" | "dates"
  const [rangeFrom,setRangeFrom] = useState("");
  const [rangeTo,setRangeTo]     = useState("");
  const selDays = countDays(sel.map(d=>({days:halfDays[d]?0.5:1}))); // total days including half-days
  const tog = date => {
    if (isXmas(date)) return;
    if (UK_BH_2026[date]) return; // block bank holidays
    const dayObj = CAL.days.find(d=>d.date===date);
    if (dayObj?.isWeekend) return; // block weekends
    setSel(s=>s.includes(date)?s.filter(d=>d!==date):[...s,date]);
    // Clear half-day when deselecting
    setHalfDays(h=>{const n={...h};delete n[date];return n;});
  };
  const lt = LEAVE_TYPES.find(t=>t.id===leaveType);

  // Who else is currently on approved leave this month
  const today = new Date().toISOString().split("T")[0];
  const nextMonth = new Date(); nextMonth.setMonth(nextMonth.getMonth()+2);
  const nextMonthStr = nextMonth.toISOString().slice(0,7);
  const siteStaff = [...getNursesForSite(state, person.site).filter(n=>n.id!==person.id),...RECS_BASE.filter(r=>r.site===person.site&&r.id!==person.id)];
  const othersWithLeave = siteStaff.filter(s=>(state.leave[s.id]||[]).some(l=>l.status==="approved"&&l.date>=today&&l.date<=nextMonthStr)).map(s=>s.name.split(" ")[0]);

  const [blocks, setBlocks] = useState([]); // [{from, to, days[]}] for multi-block mode

  function addBlock() {
    if (!rangeFrom||!rangeTo||rangeFrom>rangeTo) return;
    const days = [];
    const cur = new Date(rangeFrom+"T12:00:00");
    const end = new Date(rangeTo+"T12:00:00");
    while (cur<=end) {
      const d = cur.toISOString().slice(0,10);
      const dow = cur.getDay();
      if (dow!==0&&dow!==6&&!UK_BH_2026[d]&&!isXmas(d)) days.push(d);
      cur.setDate(cur.getDate()+1);
    }
    if (days.length===0) return;
    setBlocks(b=>[...b,{from:rangeFrom, to:rangeTo, days}]);
    setRangeFrom(""); setRangeTo("");
    // Merge all block days into sel
    setSel(s=>[...new Set([...s,...days])]);
  }

  function removeBlock(i) {
    const removed = blocks[i].days;
    setBlocks(b=>b.filter((_,j)=>j!==i));
    setSel(s=>s.filter(d=>!removed.includes(d)));
  }

  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Request Leave — {person.name}</div>
      <div style={{fontSize:12,color:C.dim,marginBottom:16}}>{SITES[person.site]?.name}</div>

      {step==="type"&&(
        <div>
          {/* Smart guidance panel */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:16}}>
            <div style={{...CS,padding:"10px 12px",textAlign:"center"}}>
              <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:remaining<5?C.danger:remaining<10?C.warn:C.teal}}>{remaining}d</div>
              <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.05em",marginTop:2}}>Remaining</div>
              <div style={{fontSize:10,color:C.dim,marginTop:1}}>{(used+hist).toFixed(1)}d of {entit.toFixed(1)}d used</div>
            </div>
            <div style={{...CS,padding:"10px 12px",textAlign:"center"}}>
              <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:pending>0?C.warn:C.dim}}>{pending}d</div>
              <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.05em",marginTop:2}}>Pending</div>
              <div style={{fontSize:10,color:C.dim,marginTop:1}}>awaiting approval</div>
            </div>
          </div>
          {remaining<=0&&(
            <div style={{background:"rgba(255,70,70,0.08)",border:"1px solid rgba(255,70,70,0.2)",borderRadius:8,padding:"8px 12px",marginBottom:12,fontSize:12,color:C.danger,fontWeight:600}}>
              No annual leave remaining — manager will need to review
            </div>
          )}
          {othersWithLeave.length>0&&(
            <div style={{background:C.tealPale,border:`1px solid ${C.tealBorder}`,borderRadius:8,padding:"8px 12px",marginBottom:12,fontSize:12,color:C.teal}}>
              Also off soon: {othersWithLeave.slice(0,4).join(", ")}{othersWithLeave.length>4?` +${othersWithLeave.length-4} more`:""}
            </div>
          )}
          <label style={LB}>Type of leave</label>
          <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:16}}>
            {LEAVE_TYPES.map(t=>(
              <button key={t.id} onClick={()=>setLeaveType(t.id)}
                style={{padding:"12px 14px",borderRadius:10,border:`1.5px solid ${leaveType===t.id?t.color:C.border}`,background:leaveType===t.id?t.color+"12":"transparent",fontFamily:"inherit",cursor:"pointer",textAlign:"left",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:leaveType===t.id?t.color:C.text}}>{t.label}</div>
                  <div style={{fontSize:11,color:C.dim,marginTop:1}}>{t.note}</div>
                </div>
                {t.noteRequired&&leaveType!==t.id&&<span style={{fontSize:9,fontWeight:700,color:C.dim,background:C.bg,border:`1px solid ${C.border}`,borderRadius:4,padding:"1px 6px",flexShrink:0}}>Note req.</span>}
                {leaveType===t.id&&<span style={{width:8,height:8,borderRadius:99,background:t.color,flexShrink:0}}/>}
              </button>
            ))}
          </div>
          {leaveType==="medical"&&(
            <div style={{background:"rgba(99,102,241,0.08)",border:"1px solid rgba(99,102,241,0.2)",borderRadius:8,padding:10,marginBottom:12,fontSize:12,color:"#60a5fa"}}>
              Medical appointments don't affect your Bradford Factor or holiday entitlement. A note is required.
            </div>
          )}
          {leaveType==="cpd"&&(
            <div style={{background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:8,padding:10,marginBottom:12,fontSize:12,color:"#00e5a0"}}>
              CPD days are employer-funded and don't come off your annual leave. Please add the course or training name below.
            </div>
          )}
          {(leaveType==="maternity"||leaveType==="paternity")&&(
            <div style={{background:"rgba(245,197,24,0.08)",border:"1px solid #fed7aa",borderRadius:8,padding:10,marginBottom:12,fontSize:12,color:"#f5c518"}}>
              Maternity/paternity leave requires a conversation with the principal. This request will be flagged for their direct review. Include expected start and return dates in the note.
            </div>
          )}
          {leaveType==="carers"&&(()=>{
            const used = getCarersLeaveUsed(person.id, state);
            const remaining = Math.max(0, 5 - used);
            return (
              <div style={{background:"rgba(167,139,250,0.08)",
                border:"1px solid rgba(167,139,250,0.25)",
                borderRadius:8,padding:12,marginBottom:12}}>
                <div style={{fontSize:12,color:"#a78bfa",fontWeight:600,
                  marginBottom:6}}>
                  Carer's Leave — statutory right
                </div>
                <div style={{fontSize:11,color:"rgba(167,139,250,0.8)",
                  lineHeight:1.6,marginBottom:8}}>
                  You have <strong>{remaining} of 5 days</strong> remaining
                  this leave year. Carer's leave is unpaid and does not
                  affect your Bradford Factor or holiday balance.
                </div>
                {remaining===0&&(
                  <div style={{fontSize:11,color:"#ff6b6b",fontWeight:600,
                    marginBottom:8}}>
                    ⚠ You have used all 5 statutory carer's leave days
                    this year. Further requests will need manager approval
                    as unpaid leave.
                  </div>
                )}
                <div style={{fontSize:11,color:"rgba(167,139,250,0.6)",
                  padding:"8px 10px",borderRadius:6,
                  background:"rgba(167,139,250,0.06)",
                  border:"1px solid rgba(167,139,250,0.15)"}}>
                  By requesting this leave you confirm you are providing
                  or arranging care for a dependant with a long-term
                  care need, disability, or illness.
                </div>
              </div>
            );
          })()}
          {leaveType==="toil"&&(()=>{
            const bal = getTOILBalance(person.id, state);
            const policy = state.toilPolicy||{};
            return (
              <div style={{background:"rgba(244,114,182,0.07)",
                border:"1px solid rgba(244,114,182,0.2)",
                borderRadius:8,padding:12,marginBottom:12}}>
                <div style={{fontSize:12,color:"#f472b6",fontWeight:600,
                  marginBottom:6}}>
                  TOIL Balance
                </div>
                <div style={{fontSize:13,color:"rgba(255,255,255,0.85)",
                  fontWeight:700,marginBottom:4}}>
                  {bal.toFixed(1)} hours banked
                  <span style={{fontSize:10,color:"rgba(255,255,255,0.35)",
                    fontWeight:400,marginLeft:6}}>
                    (≈ {(bal/7.5).toFixed(1)} days)
                  </span>
                </div>
                {bal<7.5&&(
                  <div style={{fontSize:11,color:"#ff6b6b",marginBottom:6}}>
                    ⚠ Less than 1 day banked — not enough for a full day
                    off. Check with your manager.
                  </div>
                )}
                <div style={{fontSize:11,color:"rgba(244,114,182,0.6)"}}>
                  Expires{" "}
                  {policy.expiry==="6months"
                    ?"within 6 months of being earned"
                    :"at end of the leave year"}
                </div>
              </div>
            );
          })()}
          <div style={{marginBottom:14}}>
            <label style={LB}>{LEAVE_TYPES.find(t=>t.id===leaveType)?.noteRequired?"Note (required)":"Note (optional)"}</label>
            <input value={note} onChange={e=>setNote(e.target.value)}
              placeholder={leaveType==="cpd"?"e.g. First Aid Refresher — Manchester Central":leaveType==="medical"?"e.g. Hospital appointment — outpatient":leaveType==="maternity"||leaveType==="paternity"?"e.g. Start: 1 May 2026, expected return: Nov 2026":"e.g. details, context…"}
              style={IS}/>
          </div>
          <button onClick={()=>setStep("dates")} disabled={LEAVE_TYPES.find(t=>t.id===leaveType)?.noteRequired&&!note.trim()}
            style={{...BTN(C.teal),width:"100%",padding:"11px 0",opacity:LEAVE_TYPES.find(t=>t.id===leaveType)?.noteRequired&&!note.trim()?0.4:1}}>Next — pick dates →</button>
        </div>
      )}

      {step==="dates"&&(
        <div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
            <button onClick={()=>setStep("type")} style={{...BTN(C.dim,true),fontSize:11,padding:"4px 10px"}}>← Back</button>
            <Pill c={C.teal} t={lt?.label||leaveType} sm/>
            {lt?.rtwNeeded&&<Pill c={C.warn} t="RTW on return" sm/>}
          </div>

          {leaveType==="annual"&&(
            <div style={{marginBottom:14}}>
              <div style={{display:"flex",gap:8,marginBottom:6}}>
                {[[bookable.toFixed(1)+"d","Bookable",C.text],[used.toFixed(1)+"d","Used",C.warn],[remaining.toFixed(1)+"d","Remaining",remaining<5?C.danger:C.green],[selDays%1===0?selDays+"d":selDays.toFixed(1)+"d","Selecting",C.teal]].map(([v,l,c])=>(
                  <div key={l} style={{...CS,flex:1,textAlign:"center",padding:"8px 4px"}}>
                    <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:18,color:c}}>{v}</div>
                    <div style={{fontSize:9,color:C.dim,marginTop:2,textTransform:"uppercase",letterSpacing:"0.06em"}}>{l}</div>
                  </div>
                ))}
              </div>
              <div style={{fontSize:10,color:C.dim,padding:"4px 8px",background:C.bg,borderRadius:6}}>
                Total entitlement {entit.toFixed(1)}d · {bhProrated.toFixed(1)}d bank holidays deducted (Model A) · {bookable.toFixed(1)}d to book freely
              </div>
            </div>
          )}

          {/* Unified date picker — range blocks + individual day tapping */}
          <div style={{marginBottom:14}}>

            {/* Range blocks */}
            <div style={{marginBottom:10}}>
              <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Add date ranges</div>
              {/* Existing blocks */}
              {blocks.map((b,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,background:C.tealPale,border:`1px solid ${C.tealBorder}`,borderRadius:8,padding:"7px 12px"}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,fontWeight:700,color:C.teal}}>{new Date(b.from+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short"})} → {new Date(b.to+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short"})}</div>
                    <div style={{fontSize:10,color:C.mid}}>{b.days.length} working day{b.days.length!==1?"s":""}</div>
                  </div>
                  <button onClick={()=>removeBlock(i)} style={{background:"transparent",border:"none",color:C.danger,cursor:"pointer",fontSize:18,padding:"0 4px",lineHeight:1}}>×</button>
                </div>
              ))}
              {/* Add new range */}
              <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
                <div style={{flex:1}}>
                  <label style={{...LB,marginBottom:3}}>From</label>
                  <input type="date" value={rangeFrom} onChange={e=>setRangeFrom(e.target.value)} style={{...IS,marginBottom:0}}/>
                </div>
                <div style={{flex:1}}>
                  <label style={{...LB,marginBottom:3}}>To</label>
                  <input type="date" value={rangeTo} min={rangeFrom||undefined} onChange={e=>setRangeTo(e.target.value)} style={{...IS,marginBottom:0}}/>
                </div>
                <button onClick={addBlock} disabled={!rangeFrom||!rangeTo||rangeFrom>rangeTo}
                  style={{...BTN(C.teal),padding:"9px 14px",fontSize:12,flexShrink:0,opacity:(!rangeFrom||!rangeTo||rangeFrom>rangeTo)?0.4:1,whiteSpace:"nowrap"}}>
                  + Add
                </button>
              </div>
            </div>

            {/* Individual day picker */}
            <div style={{borderTop:`1px solid ${C.border}`,paddingTop:10,marginTop:4}}>
              <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Or tap individual days</div>
              <div style={{maxHeight:200,overflowY:"auto",marginBottom:sel.length>0?10:0}}>
                {CAL.weeks.map(wk=>(
                  <div key={wk.start} style={{marginBottom:8}}>
                    <div style={{fontSize:9,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{wk.days[0]?.label.split(" ").slice(1).join(" ")}</div>
                    <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                      {wk.days.map(d=>{
                        const bh=!!d.bh, xm=isXmas(d.date), on=sel.includes(d.date), taken=existing.some(l=>l.date===d.date&&l.status!=="declined");
                        const hd=halfDays[d.date];
                        const isWeekend = d.isWeekend;
                        return (
                          <button key={d.date} onClick={()=>tog(d.date)} style={{padding:"4px 8px",borderRadius:7,fontFamily:"inherit",fontSize:10,fontWeight:600,
                            cursor:bh||xm||isWeekend?"default":"pointer",
                            border:`1px solid ${xm?"#fecaca":bh?"#fed7aa":on&&taken?"#dc2626":on?C.teal:taken?"#fca5a5":C.border}`,
                            background:xm?"#fef2f2":bh?"#fff7ed":on&&taken?"#fef2f2":on?C.tealPale:taken?"#fff5f5":"transparent",
                            color:xm?C.danger:bh?C.warn:on&&taken?C.danger:on?C.teal:taken?"#ef4444":C.soft}}>
                            {d.label.split(" ").slice(0,2).join(" ")}{bh?" BH":xm?" 🎄":taken?" ⚠":hd?` ½${hd}`:on?" ✓":""}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Half-day picker for individually selected days */}
              {sel.filter(d=>!blocks.some(b=>b.days.includes(d))).length>0&&(
                <div style={{background:C.bg,borderRadius:10,padding:"10px 12px",border:`1px solid ${C.border}`,marginTop:6}}>
                  <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Half day? (optional)</div>
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {sel.filter(d=>!blocks.some(b=>b.days.includes(d))).map(date=>{
                      const hd=halfDays[date];
                      const lbl=CAL.days.find(d=>d.date===date)?.label||date;
                      return (
                        <div key={date} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8}}>
                          <span style={{fontSize:12,color:C.text,minWidth:80}}>{lbl.split(" ").slice(0,2).join(" ")}</span>
                          <div style={{display:"flex",gap:4}}>
                            {["full","AM","PM"].map(opt=>(
                              <button key={opt} onClick={()=>setHalfDays(h=>opt==="full"?{...h,[date]:undefined}:{...h,[date]:opt})} style={{padding:"3px 10px",borderRadius:6,border:`1px solid ${(opt==="full"&&!hd)||(hd===opt)?C.teal:C.border}`,background:(opt==="full"&&!hd)||(hd===opt)?C.tealPale:"transparent",color:(opt==="full"&&!hd)||(hd===opt)?C.teal:C.mid,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer"}}>
                                {opt==="full"?"Full":opt}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Total summary */}
            {sel.length>0&&(
              <div style={{background:C.tealPale,border:`1px solid ${C.tealBorder}`,borderRadius:8,padding:"10px 14px",marginTop:10,fontSize:12,color:C.teal}}>
                <strong>{selDays%1===0?selDays:selDays.toFixed(1)} day{selDays!==1?"s":""}</strong> selected
                {blocks.length>0&&<span style={{color:C.mid}}> · {blocks.length} range{blocks.length!==1?"s":""}{sel.filter(d=>!blocks.some(b=>b.days.includes(d))).length>0?` + ${sel.filter(d=>!blocks.some(b=>b.days.includes(d))).length} individual day${sel.filter(d=>!blocks.some(b=>b.days.includes(d))).length!==1?"s":""}`:""}</span>}
              </div>
            )}
          </div>

          {leaveType==="annual"&&used+selDays>entit&&<div style={{background:"rgba(245,197,24,0.08)",border:`1px solid ${C.warn}44`,borderRadius:8,padding:10,marginBottom:12,fontSize:12,color:C.warn}}>Exceeds entitlement — manager review required</div>}
          {/* Duplicate date warning */}
          {sel.length>0&&(()=>{
            const dupes = sel.filter(date=>existing.some(l=>l.date===date&&l.status!=="declined"));
            if (!dupes.length) return null;
            return <div style={{background:"rgba(255,70,70,0.08)",border:"1px solid rgba(255,70,70,0.2)",borderRadius:8,padding:10,marginBottom:12,fontSize:12,color:C.danger,fontWeight:600}}>
              {dupes.length} date{dupes.length!==1?"s":""} already booked — {dupes.slice(0,3).map(d=>CAL.days.find(x=>x.date===d)?.label.split(" ").slice(0,2).join(" ")||d).join(", ")}{dupes.length>3?` +${dupes.length-3} more`:""}.
              <span style={{fontWeight:400,color:C.danger}}> These will create duplicate entries. Remove them before submitting.</span>
            </div>;
          })()}
          {sel.length>0&&(()=>{
            const clashes=sel.map(date=>{const cl=analyseLeaveClashes(date,person.site,state);return{date,...cl};}).filter(c=>c.nurseShort||c.recShort);
            if(!clashes.length) return null;
            return <div style={{background:"rgba(255,70,70,0.08)",border:"1px solid rgba(255,70,70,0.2)",borderRadius:8,padding:10,marginBottom:12,fontSize:12,color:C.danger}}>
              <div style={{fontWeight:700,marginBottom:4}}>Staffing impact on {clashes.length} day{clashes.length!==1?"s":""} — manager will review</div>
              {clashes.slice(0,2).map(c=><div key={c.date} style={{marginBottom:1}}>{CAL.days.find(d=>d.date===c.date)?.label||c.date}{c.nurseShort?` · nurses ${c.availNurses-1}/${c.required}`:""}{ c.recShort?" · reception low":""}</div>)}
              {clashes.length>2&&<div style={{color:C.dim}}>+{clashes.length-2} more</div>}
            </div>;
          })()}
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>sel.length&&onSubmit(person.id,sel,leaveType,note,halfDays)} style={{...BTN(sel.length?C.teal:C.muted),flex:1,padding:"11px 0"}}>
              Submit {selDays % 1 === 0 ? selDays : selDays.toFixed(1)} day{selDays!==1?"s":""}
            </button>
            <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
          </div>
        </div>
      )}
    </Overlay>
  );
}

/* ── Smart leave analysis engine ── */
function analyseLeaveRequest(req, person, state) {
  // Self-healing site lookup — see analyseLeaveClashes for context (Flourish fix)
  const site = SITES[person.site] || Object.values(SITES)[0] || {surgeries:4,name:person.site||"Practice",color:"#3aad98"};
  const allNurses = getNursesForSite(state, person.site);
  const leaveType = req.leaveType||"annual";

  // Per-day checks
  const dayChecks = req.dates.map(date => {
    const js = new Date(date).getDay();
    const dow = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][js];

    // Who else is off
    const othersOff = [...allNurses, ...RECS_BASE.filter(r=>r.site===person.site)]
      .filter(s=>s.id!==person.id && isAbsent(state,s.id,date))
      .map(s=>s.name);

    // Nurses available (excl. this person if approved)
    const nursesAvail = allNurses.filter(n=>n.id!==person.id&&!isAbsent(state,n.id,date));
    const qualNurses  = nursesAvail.filter(n=>STATUS[n.status]?.score>=0.8);
    const mosNurses   = nursesAvail.filter(n=>n.area==="MOS");
    const recepAvail  = RECS_BASE.filter(r=>r.site===person.site&&r.id!==person.id&&!isAbsent(state,r.id,date));

    const nurseRatio  = nursesAvail.length / site.surgeries;
    const recepRatio  = recepAvail.length / Math.ceil(site.surgeries/2);

    // Competency gap — if this person is qualified/RA and removal leaves a gap
    const personStatus = STATUS[person.status];
    const compGap = person.ra && mosNurses.length===0 && nursesAvail.filter(n=>n.ra).length===0;
    const mosGap  = person.area==="MOS" && mosNurses.length===0;

    // Clinicians scheduled that day
    const clins = getClinicians(state).filter(c=>c.site===person.site&&c.sched?.[dow]&&!isAbsent(state,c.id,date));

    const flags = [];
    if (nurseRatio < 1)   flags.push({type:"critical", msg:`Only ${nursesAvail.length} nurse${nursesAvail.length!==1?"s":""} for ${site.surgeries} surgeries`});
    else if (nurseRatio < 1.2) flags.push({type:"tight", msg:`Tight — ${nursesAvail.length} nurses, ${site.surgeries} surgeries`});
    if (recepRatio < 1)   flags.push({type:"critical", msg:`Reception understaffed (${recepAvail.length})`});
    if (mosGap)           flags.push({type:"critical", msg:"No MOS nurse remaining — MOS sessions at risk"});
    if (compGap)          flags.push({type:"warning",  msg:"No RA-qualified nurse remaining"});
    if (othersOff.length) flags.push({type:"info",     msg:`Also off: ${othersOff.slice(0,3).join(", ")}${othersOff.length>3?" +more":""}`});

    const locumNeeded = nurseRatio < 1 || mosGap;
    const severity = flags.some(f=>f.type==="critical") ? "critical" : flags.some(f=>f.type==="warning") ? "warning" : "ok";

    return { date, dow, nursesAvail:nursesAvail.length, recepAvail:recepAvail.length, flags, locumNeeded, severity, othersOff, clins:clins.length };
  });

  // Priority score vs others with pending leave same dates
  const allStaff = [...allNurses, ...RECS_BASE.filter(r=>r.site===person.site)];
  const competing = allStaff.filter(s=>s.id!==person.id).map(s=>{
    const sLeave = state.leave[s.id]||[];
    const clash = sLeave.filter(l=>l.status==="pending"&&req.dates.includes(l.date));
    if (!clash.length) return null;
    const sEntit = ent(s.hrs||37.5);
    const sUsed  = sLeave.filter(l=>l.status==="approved").length;
    const sRem   = sEntit - sUsed;
    return { person:s, clashDates:clash.map(l=>l.date), remaining:sRem };
  }).filter(Boolean);

  const myEntit  = ent(person.hrs||37.5);
  const myUsed   = countDays((state.leave[person.id]||[]).filter(l=>l.status==="approved"&&(!l.leaveType||l.leaveType==="annual")));
  const myRem    = myEntit - myUsed;

  // AI verdict
  const hasCritical  = dayChecks.some(d=>d.severity==="critical");
  const hasLocum     = dayChecks.some(d=>d.locumNeeded);
  const hasWarning   = dayChecks.some(d=>d.severity==="warning");
  const isOverstaffed = dayChecks.every(d=>d.nursesAvail > site.surgeries + 1);

  let verdict, verdictColor, verdictNote;
  if (leaveType==="medical") {
    verdict="approve"; verdictColor=C.green;
    verdictNote="Medical leave — approve and trigger RTW interview";
  } else if (leaveType==="cpd") {
    verdict="approve"; verdictColor=C.green;
    verdictNote="CPD leave — approve, doesn't affect rota balance";
  } else if (leaveType==="maternity") {
    verdict="escalate"; verdictColor=C.warn;
    verdictNote="Maternity/paternity — escalate to principal for formal process";
  } else if (hasCritical && !hasLocum) {
    verdict="decline"; verdictColor=C.danger;
    verdictNote="Staffing would be critical — consider declining or rescheduling";
  } else if (hasCritical && hasLocum) {
    verdict="approve_locum"; verdictColor=C.warn;
    verdictNote="Can approve if locum arranged first — prompt agencies now";
  } else if (isOverstaffed) {
    verdict="approve_unpaid_option"; verdictColor=C.teal;
    verdictNote="Overstaffed on these days — could offer unpaid leave to others too";
  } else if (hasWarning) {
    verdict="approve_caution"; verdictColor=C.warn;
    verdictNote="Approvable but worth a note — competency cover is slim";
  } else {
    verdict="approve"; verdictColor=C.green;
    verdictNote="No staffing issues — safe to approve";
  }

  return { dayChecks, competing, myRem, myEntit, myUsed, verdict, verdictColor, verdictNote, hasLocum, leaveType };
}

function ApprovalModal({req,person,state,set,onDecide,onClose,user}) {
  // Self-healing site lookup — see analyseLeaveClashes for context (Flourish fix)
  const site = SITES[person.site] || Object.values(SITES)[0] || {surgeries:4,name:person.site||"Practice",color:"#3aad98"};
  const entit = ent(person.hrs||37.5);
  const approved = (state.leave[person.id]||[]).filter(l=>l.status==="approved").length;
  const leaveType = req.leaveType||"annual";
  const lt = LEAVE_TYPES.find(t=>t.id===leaveType);
  const analysis = analyseLeaveRequest(req, person, state);
  const [managerNote,setManagerNote] = useState("");
  const [showLocumPrompt,setShowLocumPrompt] = useState(false);
  const [copied,setCopied] = useState(null);
  const [_showDelegation,setShowDelegation] = useState(false); // eslint-disable-line
  const [delegateTo,setDelegateTo] = useState("");
  const agencies = state.agencies||[];

  // Available staff for delegation — not on leave on any of the requested dates
  const siteStaff = [
    ...(state.nurses||NURSES_BASE).filter(n=>n.site===person.site&&!n.maternity&&!n.archived&&n.id!==person.id),
    ...RECS_BASE.filter(r=>r.site===person.site&&r.id!==person.id),
  ].filter(s=>!req.dates.some(d=>isAbsent(state,s.id,d)));

  function buildWAMessage(agency, date, role) {
    const siteName = site?.name||"the practice";
    const dayLabel = CAL.days.find(d=>d.date===date)?.label||date;
    return `Hi ${agency.name},\n\nWe need a ${role==="nurse"?"qualified dental nurse":"receptionist"} at our *${siteName}* practice on *${dayLabel}*.\n\nHours: 08:00–17:30 (approx)\nRole: ${role==="nurse"?"Dental Nurse":"Receptionist"}\nSite: ${siteName}, Greater Manchester\n\nAre you able to help? Please reply with availability and rate.\n\nThanks,\nOmni Rota`;
  }

  function handleDecide(decision) {
    if (decision==="approved") {
      if (lt?.rtwNeeded) {
        const days = req.dates.length;
        set(s=>({...s,sick:[...s.sick,{id:"s"+Date.now(),staffId:person.id,name:person.name,site:person.site,role:"nurse",date:req.dates.sort()[0],days,contact:"self-certified",reason:"medical leave",certified:false,rtw:false,notes:`Medical leave approved by ${user?.name||"manager"} — RTW required`}]}));
      }
      if (delegateTo) {
        const delegate = siteStaff.find(s=>s.id===delegateTo);
        set(s=>({...s,
          delegations:[...(s.delegations||[]),{id:"del"+Date.now(),siteId:person.site,fromStaffId:person.id,toStaffId:delegateTo,toName:delegate?.name||delegateTo,startDate:req.dates.sort()[0],endDate:req.dates.sort().slice(-1)[0],active:true,loggedBy:user?.name||"Manager"}],
          ...addLog(s,user?.name||"Manager","leave",`Duties delegated to ${delegate?.name||delegateTo} while ${person.name} is on leave`)
        }));
      }
    }
    onDecide(decision, managerNote);
  }

  const VERDICT_ICONS = { approve:"✓", approve_locum:"⚠", approve_caution:"⚠", approve_unpaid_option:"✓", decline:"✕", escalate:"!" };

  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading,marginBottom:2}}>Leave Request</div>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
        <span style={{fontSize:13,color:C.dim}}>{person.name} · {(()=>{const d=countDays((state.leave[req.person.id]||[]).filter(l=>req.dates.includes(l.date)));return d%1===0?d+'d':d.toFixed(1)+'d';})()}</span>
        {lt&&<Pill c={C.teal} t={lt.label} sm/>}
        {lt?.rtwNeeded&&<Pill c={C.warn} t="RTW required" sm/>}
      </div>

      {/* AI Verdict */}
      <div style={{background:analysis.verdictColor+"12",border:`1.5px solid ${analysis.verdictColor}44`,borderRadius:12,padding:"12px 14px",marginBottom:16}}>
        <div style={{fontWeight:700,color:analysis.verdictColor,fontSize:14,marginBottom:4}}>
          {VERDICT_ICONS[analysis.verdict]} AI Recommendation — {
            analysis.verdict==="approve"?"Approve":
            analysis.verdict==="approve_locum"?"Approve + arrange locum":
            analysis.verdict==="approve_caution"?"Approve with caution":
            analysis.verdict==="approve_unpaid_option"?"Approve — overstaffed":
            analysis.verdict==="decline"?"Suggest decline":
            "Escalate to principal"
          }
        </div>
        <div style={{fontSize:12,color:C.mid}}>{analysis.verdictNote}</div>
      </div>

      {/* Entitlement strip */}
      {leaveType==="annual"&&(
        <div style={{display:"flex",gap:6,marginBottom:14}}>
          {[[entit+"d","Entitlement"],[approved+"d","Used"],[(analysis.myRem)+"d","Remaining",analysis.myRem<5?C.danger:C.green],[(approved+req.dates.length)+"d","After this",approved+req.dates.length>entit?C.danger:C.text]].map(([v,l,c])=>(
            <div key={l} style={{...CS,flex:1,textAlign:"center",padding:"8px 4px"}}>
              <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:16,color:c||C.text}}>{v}</div>
              <div style={{fontSize:9,color:C.dim,marginTop:2,textTransform:"uppercase",letterSpacing:"0.06em"}}>{l}</div>
            </div>
          ))}
        </div>
      )}

      {/* Keyholder warning */}
      {(()=>{
        const khWarnings = [];
        Object.keys(SITES).forEach(sid => {
          const holders = getSiteKeyholders(state, sid);
          if (holders.includes(person.id)) {
            const gapDates = req.dates.filter(date => {
              const cov = getKeyholderCoverage(state, sid, date);
              return cov.status==="absent";
            });
            if (gapDates.length>0) khWarnings.push({siteId:sid, gapDates});
          }
        });
        if (khWarnings.length===0) return null;
        return (
          <div style={{background:"rgba(245,197,24,0.08)",border:"1px solid #fed7aa",borderRadius:12,padding:"12px 14px",marginBottom:14}}>
            <div style={{fontWeight:700,color:C.warn,fontSize:13,marginBottom:6}}>Keyholder gap — no other keyholders on the rota</div>
            {khWarnings.map((w,i)=>(
              <div key={i} style={{fontSize:12,color:C.mid,marginBottom:2}}>
                <strong>{person.name.split(" ")[0]}</strong> is a keyholder at {SITES[w.siteId]?.name} · {w.gapDates.length} day{w.gapDates.length!==1?"s":""} with no other keyholder available
              </div>
            ))}
            <div style={{fontSize:10,color:C.dim,marginTop:4}}>Check cover in Planning → Keyholders before approving</div>
          </div>
        );
      })()}

      {/* Priority vs competing */}
      {analysis.competing.length>0&&(
        <div style={{background:"rgba(245,197,24,0.08)",border:`1px solid ${C.warn}33`,borderRadius:10,padding:"10px 12px",marginBottom:14}}>
          <div style={{fontSize:11,fontWeight:700,color:C.warn,marginBottom:6}}>⚖ Competing requests same dates</div>
          {analysis.competing.map(c=>(
            <div key={c.person.id} style={{display:"flex",justifyContent:"space-between",fontSize:11,color:C.mid,marginBottom:2}}>
              <span>{c.person.name} — {c.clashDates.length} day clash</span>
              <span style={{color:c.remaining<analysis.myRem?C.danger:C.green,fontWeight:700}}>
                {c.remaining}d left {c.remaining<analysis.myRem?"← less than requester":"← more remaining"}
              </span>
            </div>
          ))}
          <div style={{fontSize:10,color:C.dim,marginTop:4}}>
            {person.name.split(" ")[0]} has <strong>{analysis.myRem}d</strong> remaining.
            {analysis.competing.some(c=>c.remaining<analysis.myRem)
              ? " They have more leave left — lower priority."
              : " They have less leave remaining — higher priority."}
          </div>
        </div>
      )}

      {/* Day by day */}
      <label style={{...LB,marginBottom:8}}>Day-by-day impact</label>
      <div style={{maxHeight:200,overflowY:"auto",marginBottom:14}}>
        {analysis.dayChecks.map(c=>{
          const col = c.severity==="critical"?C.danger:c.severity==="warning"?C.warn:C.green;
          return (
            <div key={c.date} style={{...CS,marginBottom:5,padding:"8px 12px",borderLeft:`3px solid ${col}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:c.flags.length?4:0}}>
                <span style={{fontSize:12,fontWeight:700,color:C.text}}>{CAL.days.find(d=>d.date===c.date)?.label||c.date}</span>
                <div style={{display:"flex",gap:6,fontSize:11}}>
                  <span style={{color:c.nursesAvail>=site.surgeries?C.green:C.danger}}>
                    {c.nursesAvail}/{site.surgeries} nurses
                  </span>
                  <span style={{color:c.recepAvail>=Math.ceil(site.surgeries/2)?C.green:C.danger}}>
                    {c.recepAvail} recep
                  </span>
                </div>
              </div>
              {c.flags.map((f,i)=>(
                <div key={i} style={{fontSize:10,color:f.type==="critical"?C.danger:f.type==="warning"?C.warn:C.dim,marginTop:2}}>
                  {f.type==="critical"?"✕":f.type==="warning"?"⚠":"i"} {f.msg}
                </div>
              ))}
              {c.locumNeeded&&(
                <div style={{fontSize:10,color:C.warn,marginTop:2,fontWeight:700}}>Locum needed this day</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Locum WhatsApp prompt */}
      {analysis.hasLocum&&(
        <div style={{marginBottom:14}}>
          <button onClick={()=>setShowLocumPrompt(p=>!p)} style={{...BTN(C.warn,true),width:"100%",padding:"9px 0",fontSize:12,marginBottom:showLocumPrompt?8:0}}>
            {showLocumPrompt?"Hide":"Show"} WhatsApp agency messages
          </button>
          {showLocumPrompt&&(
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {agencies.length===0&&<div style={{fontSize:12,color:C.dim,padding:"8px 0"}}>No agencies saved — add them in the Agencies tab first.</div>}
              {agencies.map(ag=>{
                const msg = buildWAMessage(ag, req.dates.sort()[0], "nurse");
                const isCopied = copied===ag.id;
                return (
                  <div key={ag.id} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 12px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                      <span style={{fontSize:12,fontWeight:700,color:C.text}}>{ag.name}</span>
                      <button onClick={()=>{navigator.clipboard.writeText(msg);setCopied(ag.id);setTimeout(()=>setCopied(null),2000);}} style={{...BTN(isCopied?C.green:C.teal,true),fontSize:10,padding:"3px 10px"}}>
                        {isCopied?"Copied!":"Copy"}
                      </button>
                    </div>
                    <div style={{fontSize:10,color:C.dim,whiteSpace:"pre-wrap",lineHeight:1.5,background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,padding:"6px 8px"}}>{msg}</div>
                    <div style={{fontSize:10,color:C.teal,marginTop:4}}>{ag.phone||"No number saved"} · {ag.nurseRate?`£${ag.nurseRate}/hr`:""}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Unpaid offer prompt */}
      {analysis.verdict==="approve_unpaid_option"&&(
        <div style={{background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:10,padding:"10px 12px",marginBottom:14,fontSize:12,color:C.green}}>
          You're overstaffed on these days. Consider offering unpaid leave to others — good for morale and saves wages.
        </div>
      )}

      {/* Delegation prompt */}
      <div style={{background:C.tealPale,border:`1px solid ${C.tealBorder}`,borderRadius:10,padding:"10px 14px",marginBottom:14}}>
        <div style={{fontSize:12,fontWeight:700,color:C.teal,marginBottom:6}}>Delegate duties while {person.name.split(" ")[0]} is off?</div>
        <select value={delegateTo} onChange={e=>setDelegateTo(e.target.value)}
          style={{...IS,marginBottom:0,fontSize:12}}>
          <option value="">— No delegation needed —</option>
          {siteStaff.map(s=>{
            const hasConflict = req.dates.some(d=>isAbsent(state,s.id,d));
            return <option key={s.id} value={s.id}>{s.name}{s.headNurse?" (Head Nurse)":s.manager?" (Manager)":""}{hasConflict?" also off some dates":""}</option>;
          })}
        </select>
        {delegateTo&&(()=>{
          const delegate = siteStaff.find(s=>s.id===delegateTo);
          const conflictDates = delegate ? req.dates.filter(d=>isAbsent(state,delegate.id,d)) : [];
          return (
            <div style={{marginTop:6}}>
              {conflictDates.length===0&&<div style={{fontSize:11,color:C.teal}}>{delegate?.name?.split(" ")[0]} is available for all {req.dates.length} days</div>}
              {conflictDates.length>0&&(
                <div style={{padding:"6px 10px",background:"rgba(255,70,70,0.08)",border:`1px solid #fecaca`,borderRadius:8,fontSize:11,color:C.danger}}>
                  {delegate?.name?.split(" ")[0]} is also off on {conflictDates.map(dt=>CAL.days.find(x=>x.date===dt)?.label?.split(" ")[1]||dt).join(", ")} — delegation gap on those days
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Manager note */}
      <div style={{marginBottom:16}}>
        <label style={LB}>Manager note (optional — shown to staff)</label>
        <input value={managerNote} onChange={e=>setManagerNote(e.target.value)} placeholder="e.g. Please arrange cover before this is confirmed…" style={IS}/>
      </div>

      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>handleDecide("approved")} style={{...BTN(analysis.verdict==="decline"?C.warn:C.green),flex:1,padding:"11px 0"}}>
          {analysis.verdict==="decline"?"Approve anyway":"Approve"}
        </button>
        <button onClick={()=>handleDecide("declined")} style={{...BTN(C.danger,true),flex:1,padding:"11px 0"}}>✕ Decline</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Later</button>
      </div>
    </Overlay>
  );
}

function LocumModal({onSave, onClose, rate, agencies, state}) {
  const today = new Date().toISOString().split("T")[0];
  const [f, setF] = useState({
    date: today, site:"sale", role:"nurse",
    agencyId:"", locumName:"", hrs:7.5, rate:rate||20,
    planned: true, // planned vs last-minute
    clinicianId:"", // rota placement — which dentist they cover
    notes:"",
  });
  const sf = (k,v) => setF(p=>({...p,[k]:v}));

  // Duplicate detection — fuzzy match on name + date + site
  const existingLocums = (state?.locums||[]);
  const fuzzyMatch = (a,b) => {
    if (!a||!b) return false;
    const norm = s => s.toLowerCase().replace(/[^a-z0-9]/g,"");
    const na=norm(a), nb=norm(b);
    if (na===nb) return true;
    // Check if one contains the other (handles "Dr Smith" vs "Smith")
    if (na.includes(nb)||nb.includes(na)) return true;
    // Levenshtein-lite: if >70% chars match
    const longer = na.length>nb.length?na:nb;
    const shorter = na.length>nb.length?nb:na;
    let matches=0;
    for (const ch of shorter) if (longer.includes(ch)) matches++;
    return matches/longer.length > 0.7;
  };
  const duplicates = existingLocums.filter(l =>
    l.date===f.date && l.site===f.site &&
    fuzzyMatch(l.locumName, f.locumName)
  );
  const sameWeekSameName = f.locumName.length>2 ? existingLocums.filter(l => {
    if (l.site!==f.site||!fuzzyMatch(l.locumName,f.locumName)) return false;
    const lDate=new Date(l.date), fDate=new Date(f.date);
    return Math.abs(lDate-fDate)<7*24*60*60*1000;
  }) : [];

  const ag = (agencies||[]).find(a=>a.id===f.agencyId);

  // Clinicians working on selected date at selected site
  const dow = f.date ? ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][new Date(f.date+"T12:00:00").getDay()] : null;
  const siteClinicians = getClinicians(state||{}).filter(c=>
    c.site===f.site && !c._deleted && dow && c.sched?.[dow]
  );

  const canSave = f.date && f.locumName.trim() && f.hrs>0;

  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading,marginBottom:4}}>
        {f.planned?"Add Planned Locum":"Log Last-Minute Locum"}
      </div>
      <div style={{fontSize:12,color:C.dim,marginBottom:16}}>Book in advance or log an emergency cover</div>

      {/* Planned vs Last-minute toggle */}
      <div style={{display:"flex",gap:6,marginBottom:20,background:C.bg,padding:4,borderRadius:10}}>
        {[[true,"Planned","Booked in advance"],[false,"Last minute","Emergency / day-of cover"]].map(([v,label,desc])=>(
          <button key={String(v)} onClick={()=>sf("planned",v)}
            style={{flex:1,padding:"10px 8px",borderRadius:8,border:"none",
              background:f.planned===v?C.teal:"transparent",
              color:f.planned===v?"white":C.mid,
              fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer",transition:"all 0.15s"}}>
            <div>{label}</div>
            <div style={{fontSize:10,fontWeight:400,opacity:0.8,marginTop:2}}>{desc}</div>
          </button>
        ))}
      </div>

      {/* Date + Site row */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
        <div>
          <label style={LB}>Date</label>
          <input type="date" value={f.date} onChange={e=>sf("date",e.target.value)} style={IS}
            min={f.planned?today:undefined}/>
        </div>
        <div>
          <label style={LB}>Site</label>
          <select value={f.site} onChange={e=>sf("site",e.target.value)} style={IS}>
            {Object.entries(SITES).map(([id,si])=><option key={id} value={id}>{si.name}</option>)}
          </select>
        </div>
      </div>

      {/* Role */}
      <div style={{marginBottom:12}}>
        <label style={LB}>Role</label>
        <div style={{display:"flex",gap:6}}>
          {[["nurse","Nurse"],["receptionist","Reception"],["dentist","Dentist"]].map(([v,l])=>(
            <button key={v} onClick={()=>sf("role",v)}
              style={{flex:1,padding:"8px 4px",borderRadius:8,border:`1.5px solid ${f.role===v?C.teal:C.border}`,
                background:f.role===v?C.tealPale:"transparent",color:f.role===v?C.teal:C.mid,
                fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer"}}>{l}
            </button>
          ))}
        </div>
      </div>

      {/* Locum name + Agency */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
        <div>
          <label style={LB}>Locum name</label>
          <input value={f.locumName} onChange={e=>sf("locumName",e.target.value)}
            placeholder="Full name" style={{...IS,borderColor:duplicates.length>0?"#ef4444":undefined}}/>
        </div>
        <div>
          <label style={LB}>Agency</label>
          <select value={f.agencyId} onChange={e=>{
            sf("agencyId",e.target.value);
            const a=(agencies||[]).find(a=>a.id===e.target.value);
            if(a) sf("rate",f.role==="nurse"?a.nurseRate:f.role==="dentist"?a.dentistRate||a.nurseRate:a.recRate);
          }} style={IS}>
            <option value="">Direct / no agency</option>
            {(agencies||[]).map(a=><option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
      </div>

      {/* Duplicate warning */}
      {duplicates.length>0&&(
        <div style={{background:"rgba(255,70,70,0.08)",border:"1.5px solid #ef4444",borderRadius:10,padding:"10px 14px",marginBottom:12,fontSize:12}}>
          <div style={{fontWeight:700,color:"#ff4646",marginBottom:4}}>Possible duplicate</div>
          <div style={{color:"#991b1b"}}>A locum with a similar name is already logged for this date at this site:<br/>
            {duplicates.map(d=><span key={d.id} style={{fontWeight:600}}>"{d.locumName}" · {d.hrs}h · {d.role}</span>)}
          </div>
        </div>
      )}
      {duplicates.length===0&&sameWeekSameName.length>0&&(
        <div style={{background:"rgba(245,197,24,0.05)",border:"1.5px solid #fcd34d",borderRadius:10,padding:"10px 14px",marginBottom:12,fontSize:12}}>
          <div style={{fontWeight:700,color:"#b45309",marginBottom:2}}>ℹ Same locum this week</div>
          <div style={{color:"#f5c518"}}>{sameWeekSameName.map(d=>d.date).join(", ")} — same name already booked nearby. Just a heads up.</div>
        </div>
      )}

      {/* Hours + Rate */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
        <div>
          <label style={LB}>Hours</label>
          <div style={{display:"flex",gap:4}}>
            {[7.5,4,3].map(h=>(
              <button key={h} onClick={()=>sf("hrs",h)}
                style={{flex:1,padding:"7px 0",borderRadius:8,border:`1.5px solid ${f.hrs===h?C.teal:C.border}`,
                  background:f.hrs===h?C.tealPale:"transparent",color:f.hrs===h?C.teal:C.mid,
                  fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer"}}>{h}h
              </button>
            ))}
            <input type="number" step="0.5" value={f.hrs} onChange={e=>sf("hrs",+e.target.value)}
              style={{...IS,width:52,textAlign:"center",padding:"7px 6px"}}/>
          </div>
        </div>
        <div>
          <label style={LB}>Rate £/hr</label>
          <input type="number" value={f.rate} onChange={e=>sf("rate",+e.target.value)} style={IS}/>
        </div>
      </div>

      {/* Rota placement — who are they covering */}
      {siteClinicians.length>0&&(
        <div style={{marginBottom:12}}>
          <label style={LB}>Covering which dentist? <span style={{fontWeight:400,color:C.dim}}>(optional — places them on the rota)</span></label>
          <select value={f.clinicianId} onChange={e=>sf("clinicianId",e.target.value)} style={IS}>
            <option value="">Not assigned to a surgery</option>
            {siteClinicians.map(cl=><option key={cl.id} value={cl.id}>{cl.name} · {cl.type} · {cl.sched?.[dow]}</option>)}
          </select>
        </div>
      )}

      {/* Notes */}
      <div style={{marginBottom:16}}>
        <label style={LB}>Notes <span style={{fontWeight:400,color:C.dim}}>(optional)</span></label>
        <input value={f.notes} onChange={e=>sf("notes",e.target.value)}
          placeholder={f.planned?"e.g. covering Hannah's maternity leave":"e.g. Safaa called in sick — emergency cover"}
          style={IS}/>
      </div>

      {/* Cost summary */}
      <div style={{background:C.tealPale,border:`1px solid ${C.tealBorder}`,borderRadius:10,padding:"12px 16px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:12,color:C.teal,fontWeight:600}}>Total cost</div>
          <div style={{fontSize:11,color:C.dim,marginTop:1}}>{f.hrs}h × £{f.rate}/hr{ag?` · ${ag.name}`:""}</div>
        </div>
        <span style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:28,color:C.teal}}>{fmt(f.hrs*f.rate)}</span>
      </div>

      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>{
          if(!canSave) return;
          onSave({...f, id:"l"+Date.now(), cost:f.hrs*f.rate, agency:ag?.name||"",
            clinicianName: siteClinicians.find(c=>c.id===f.clinicianId)?.name||""});
        }} disabled={!canSave}
          style={{...BTN(canSave?C.teal:C.muted),flex:1,padding:"11px 0",opacity:canSave?1:0.5}}>
          {f.planned?"Book Locum":"Log Emergency Cover"}
        </button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}

function ShiftOverrideModal({pairing,date,siteId,nurses,state,onSave,onClose}) {
  const available = getNursesForSite(state, siteId).filter(n=>!isAbsent(state,n.id,date));
  const [sel,setSel] = useState(pairing.nurse?.id||"");
  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Override Pairing</div>
      <div style={{fontSize:13,color:C.dim,marginBottom:16}}>{pairing.clinician.name} · {CAL.days.find(d=>d.date===date)?.label||date}</div>
      <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:20,maxHeight:320,overflowY:"auto"}}>
        <button onClick={()=>setSel("none")} style={{padding:"10px 14px",borderRadius:10,border:`1px solid ${sel==="none"?C.danger:C.border}`,background:sel==="none"?"#fef2f2":"transparent",color:sel==="none"?C.danger:C.mid,fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer",textAlign:"left"}}>Remove nurse (flag gap)</button>
        {available.map(n=>{
          const st = STATUS[n.status];
          return (
            <button key={n.id} onClick={()=>setSel(n.id)} style={{padding:"10px 14px",borderRadius:10,border:`1px solid ${sel===n.id?C.teal:C.border}`,background:sel===n.id?C.tealPale:"transparent",color:sel===n.id?C.teal:C.soft,fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer",textAlign:"left",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span>{n.name} {n.id===pairing.clinician.prefNurse?"⭐":""}</span>
              {st&&<Pill c={st.color} t={st.label} sm/>}
            </button>
          );
        })}
      </div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>onSave(pairing.clinician.id,sel,date)} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Save Override</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}

function TrainingModal({nurse,onSave,onClose}) {
  const [status,setStatus] = useState(nurse.status);
  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Update Training Level</div>
      <div style={{fontSize:13,color:C.dim,marginBottom:16}}>{nurse.name}</div>
      <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:20}}>
        {Object.entries(STATUS).map(([k,v])=>(
          <button key={k} onClick={()=>setStatus(k)} style={{padding:"12px 14px",borderRadius:10,border:`1px solid ${status===k?v.color:C.border}`,background:status===k?v.color+"18":"transparent",color:status===k?v.color:C.soft,fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer",textAlign:"left",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span>{v.label}</span><span style={{fontSize:11,color:C.dim}}>Score {(v.score*100).toFixed(0)}</span>
          </button>
        ))}
      </div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>onSave(nurse.id,status)} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Update</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}

function RoleModal({person, state, set, onSave, onClose}) {
  const current = state.staffRoles?.[person.id]||"staff";
  const [role,setRole] = useState(current);
  const hasWelfareAccess = !!(state.welfareAccess||{})[person.id];
  const roles = [
    ["staff",       "Standard Staff",    C.mid,    "Leave requests, my rota"],
    ["headNurse",   "Head Nurse",        C.teal,   "Rota view, spare tasks, swap approvals"],
    ["managerLite", "Acting Manager",    C.brook,  "Full manager — leave, sickness, locums"],
    ["manager",     "Site Manager",      C.sale,   "Full site management access"],
    ["admin",       "Admin",             C.warn,   "Cross-site admin (for office staff)"],
  ];

  function toggleWelfareAccess() {
    set(s=>({...s, welfareAccess:{...(s.welfareAccess||{}),
      [person.id]: !hasWelfareAccess || undefined
    }}));
  }

  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:2}}>Update Role — {person.name}</div>
      <div style={{fontSize:12,color:C.dim,marginBottom:4}}>{SITES[person.site]?.name} · {person.type||person.area||"Nurse"}</div>
      <div style={{background:"rgba(245,197,24,0.08)",border:"1px solid #fed7aa",borderRadius:8,padding:"8px 12px",fontSize:11,color:C.warn,marginBottom:16}}>
        If you need to give this person a login PIN, manage that via the login system.
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:20}}>
        {roles.map(([k,label,col,desc])=>(
          <button key={k} onClick={()=>setRole(k)} style={{padding:"14px",borderRadius:12,border:"1.5px solid "+(role===k?col:C.border),background:role===k?col+"18":"transparent",color:role===k?C.heading:C.soft,fontFamily:"inherit",cursor:"pointer",textAlign:"left",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontSize:13,fontWeight:700,marginBottom:2}}>{label}</div>
              <div style={{fontSize:11,color:C.dim}}>{desc}</div>
            </div>
            {role===k&&<span style={{fontSize:16,color:col}}>✓</span>}
          </button>
        ))}
      </div>

      {/* Welfare access grant — only shown for managerLite/headNurse */}
      {(role==="managerLite"||role==="headNurse")&&(
        <div style={{marginBottom:16,padding:"12px 14px",background:hasWelfareAccess?"#f5f3ff":"#fafafa",border:`1px solid ${hasWelfareAccess?"#ddd6fe":C.border}`,borderRadius:10}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:hasWelfareAccess?"#7c3aed":C.text}}>💜 Welfare access</div>
              <div style={{fontSize:11,color:C.dim,marginTop:2}}>Can see welfare notes and the Welfare tab. Granted by principal only.</div>
            </div>
            <button onClick={toggleWelfareAccess}
              style={{...BTN(hasWelfareAccess?"#7c3aed":C.dim,true),fontSize:11,padding:"5px 12px",flexShrink:0}}>
              {hasWelfareAccess?"Granted":"Grant access"}
            </button>
          </div>
          {hasWelfareAccess&&<div style={{fontSize:10,color:"#a78bfa",fontStyle:"italic"}}>This person can see welfare-flagged absence records for their site.</div>}
        </div>
      )}

      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>onSave(person.id,role)} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Save Role</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}

// ── WELFARE TRIGGER CHECK ──
function checkWelfareTrigger(staffId, state) {
  const sick = (state.sick||[]).filter(s=>s.staffId===staffId);
  const lateness = (state.latenessLog||[]).filter(l=>l.staffId===staffId);
  const now = new Date();
  const weeks12ago = new Date(now); weeks12ago.setDate(now.getDate()-84);
  const w12 = weeks12ago.toISOString().split("T")[0];
  const bfSick = sick.filter(s=>s.reason!=="medical"&&s.reason!=="leftEarly");
  const recentSick = bfSick.filter(s=>s.date>=w12);
  const recentLate = lateness.filter(l=>l.date>=w12);
  const totalDays = bfSick.reduce((a,s)=>a+s.days,0);
  const bfScore = bfSick.length * bfSick.length * totalDays;
  const triggers = [];
  if (recentSick.length>=3) triggers.push(`3+ absences in 12 weeks (${recentSick.length})`);
  if (recentSick.some(s=>s.days>=5)) triggers.push("Single absence ≥5 days");
  if (bfScore>=45) triggers.push(`Bradford Factor ${bfScore} (Caution+)`);
  if (recentLate.length>=3) triggers.push(`3+ lateness incidents in 12 weeks`);
  return triggers;
}

// ── OVERTIME MODAL ──
function OvertimeModal({person, state, onSave, onClose}) {
  const clins = getClinicians(state).filter(c=>c.site===person.site&&!c._deleted);
  const today = new Date().toISOString().split("T")[0];

  // Get scheduled finish time from clinician rota if available
  const dow = new Date(today+"T12:00:00").toLocaleDateString("en-GB",{weekday:"short"});
  const personClin = clins.find(c=>c.id===person.prefClin||c.prefNurse===person.id);
  const scheduledFinish = personClin?.sched?.[dow]?.split("-")?.[1] || "17:30";

  const LATE_REASONS_FULL = [
    "Extra patients",
    "Dentist ran late",
    "Emergency patient",
    "Treatment overran",
    "Equipment issue",
    "Decontamination overrun",
    "Staff cover",
    "Other"
  ];

  const [f,setF] = useState({
    date: today,
    scheduledFinish,
    actualFinish: "",
    reason: "Extra patients",
    clinicianId: "",
    notes: "",
    type: "lateFinish"
  });
  const s=(k,v)=>setF(p=>({...p,[k]:v}));

  // Calculate extra time
  const calcExtraHrs = () => {
    if (!f.actualFinish||!f.scheduledFinish) return 0;
    const [sh,sm]=f.scheduledFinish.split(":").map(Number);
    const [ah,am]=f.actualFinish.split(":").map(Number);
    const diff=((ah*60+am)-(sh*60+sm))/60;
    return Math.max(0,Math.round(diff*10)/10);
  };
  const extraHrs = calcExtraHrs();

  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Finished Late</div>
      <div style={{fontSize:13,color:C.dim,marginBottom:16}}>{person.name} · Needs manager approval</div>

      <div style={{marginBottom:12}}><label style={LB}>Date</label><input type="date" value={f.date} onChange={e=>s("date",e.target.value)} style={IS}/></div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
        <div><label style={LB}>Scheduled finish</label><input type="time" value={f.scheduledFinish} onChange={e=>s("scheduledFinish",e.target.value)} style={IS}/></div>
        <div><label style={LB}>Actual finish</label><input type="time" value={f.actualFinish} onChange={e=>s("actualFinish",e.target.value)} style={IS}/></div>
      </div>

      {extraHrs>0&&<div style={{background:"rgba(245,197,24,0.08)",border:"1px solid #fed7aa",borderRadius:8,padding:"8px 12px",marginBottom:12,fontSize:12,color:C.warn,fontWeight:600}}>
        {extraHrs} extra hour{extraHrs!==1?"s":""} — will be added to payroll on approval
      </div>}

      <div style={{marginBottom:12}}><label style={LB}>Reason</label>
        <select value={f.reason} onChange={e=>s("reason",e.target.value)} style={IS}>
          {LATE_REASONS_FULL.map(r=><option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      {(f.reason==="Dentist ran late"||f.reason==="Extra patients"||f.reason==="Emergency patient")&&(
        <div style={{marginBottom:12}}><label style={LB}>Which dentist?</label>
          <select value={f.clinicianId} onChange={e=>s("clinicianId",e.target.value)} style={IS}>
            <option value="">— select if applicable —</option>
            {clins.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}

      <div style={{marginBottom:16}}><label style={LB}>Notes</label>
        <textarea value={f.notes} onChange={e=>s("notes",e.target.value)} rows={2}
          placeholder="e.g. Mrs Johnson needed extra time, emergency crown" style={{...IS,resize:"none",lineHeight:1.5}}/>
      </div>

      <div style={{display:"flex",gap:8}}>
        <button
          onClick={()=>onSave({
            ...f, id:"ot"+Date.now(), staffId:person.id, name:person.name,
            site:person.site, status:"pending", hrs:extraHrs, type:"lateFinish"
          })}
          disabled={!f.actualFinish}
          style={{...BTN(C.teal),flex:1,padding:"11px 0",opacity:!f.actualFinish?0.5:1}}
        >Submit for approval</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}

// ── EARLY FINISH MODAL ──
function EarlyFinishModal({person, onSave, onClose}) {
  const [f,setF] = useState({date:new Date().toISOString().split("T")[0],hrsEarly:0.5,reason:"Dentist finished early",notes:""});
  const s=(k,v)=>setF(p=>({...p,[k]:v}));
  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Log Early Finish</div>
      <div style={{fontSize:13,color:C.dim,marginBottom:16}}>{person.name}</div>
      <div style={{marginBottom:12}}><label style={LB}>Date</label><input type="date" value={f.date} onChange={e=>s("date",e.target.value)} style={IS}/></div>
      <div style={{marginBottom:12}}><label style={LB}>Hours early</label><input type="number" step="0.5" min="0.5" value={f.hrsEarly} onChange={e=>s("hrsEarly",+e.target.value)} style={IS}/></div>
      <div style={{marginBottom:12}}><label style={LB}>Reason</label>
        <select value={f.reason} onChange={e=>s("reason",e.target.value)} style={IS}>
          {EF_REASONS.map(r=><option key={r} value={r}>{r}</option>)}
        </select>
      </div>
      <div style={{marginBottom:16}}><label style={LB}>Notes</label><input value={f.notes} onChange={e=>s("notes",e.target.value)} style={IS}/></div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>onSave({...f,id:"ef"+Date.now(),staffId:person.id,name:person.name,site:person.site,status:"pending"})} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Submit</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}

// ── LATENESS MODAL ──
function LatenessModal({person, user, onSave, onClose}) {
  const [f,setF] = useState({date:new Date().toISOString().split("T")[0],type:"late",minsLate:15,reason:"No reason given",notes:""});
  const s=(k,v)=>setF(p=>({...p,[k]:v}));
  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Log Attendance Issue</div>
      <div style={{fontSize:13,color:C.dim,marginBottom:16}}>{person.name}</div>
      <div style={{marginBottom:12}}><label style={LB}>Date</label><input type="date" value={f.date} onChange={e=>s("date",e.target.value)} style={IS}/></div>
      <div style={{marginBottom:12}}><label style={LB}>Type</label>
        <select value={f.type} onChange={e=>s("type",e.target.value)} style={IS}>
          <option value="late">Late arrival</option>
          <option value="unauthorised">Unauthorised absence</option>
        </select>
      </div>
      {f.type==="late"&&<div style={{marginBottom:12}}><label style={LB}>Minutes late</label><input type="number" min="5" step="5" value={f.minsLate} onChange={e=>s("minsLate",+e.target.value)} style={IS}/></div>}
      <div style={{marginBottom:12}}><label style={LB}>Reason</label>
        <select value={f.reason} onChange={e=>s("reason",e.target.value)} style={IS}>
          {(f.type==="late"?LATE_REASONS:UNAUTH_REASONS).map(r=><option key={r} value={r}>{r}</option>)}
        </select>
      </div>
      <div style={{marginBottom:16}}>
        <label style={LB}>Internal notes <span style={{fontWeight:400,color:C.dim}}>(manager only — never shown to staff)</span></label>
        <textarea value={f.notes} onChange={e=>s("notes",e.target.value)} rows={3}
          placeholder="e.g. Seemed flustered when arrived, mentioned childcare difficulties. Worth monitoring."
          style={{...IS,resize:"none",lineHeight:1.5,fontSize:12}}/>
      </div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>onSave({...f,id:"lat"+Date.now(),staffId:person.id,name:person.name,site:person.site,loggedBy:user?.name||"Manager"})} style={{...BTN(C.danger),flex:1,padding:"11px 0"}}>Log</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}

// ── LOST KEYS MODAL ──
function LostKeysModal({user, onSave, onClose}) {
  const [f,setF] = useState({date:new Date().toISOString().split("T")[0],site:"sale",reportedBy:"",locksChanged:false,notes:""});
  const s=(k,v)=>setF(p=>({...p,[k]:v}));
  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Log Lost Keys</div>
      <div style={{marginBottom:12}}><label style={LB}>Date reported</label><input type="date" value={f.date} onChange={e=>s("date",e.target.value)} style={IS}/></div>
      <div style={{marginBottom:12}}><label style={LB}>Site</label>
        <select value={f.site} onChange={e=>s("site",e.target.value)} style={IS}>
          {Object.entries(SITES).map(([id,si])=><option key={id} value={id}>{si.name}</option>)}
        </select>
      </div>
      <div style={{marginBottom:12}}><label style={LB}>Reported by</label><input value={f.reportedBy} onChange={e=>s("reportedBy",e.target.value)} placeholder="Staff member name" style={IS}/></div>
      <div style={{marginBottom:12}}><label style={LB}>Notes</label><input value={f.notes} onChange={e=>s("notes",e.target.value)} placeholder="Which keys, circumstances…" style={IS}/></div>
      <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:C.soft,cursor:"pointer",marginBottom:16}}>
        <input type="checkbox" checked={f.locksChanged} onChange={e=>s("locksChanged",e.target.checked)} style={{width:16,height:16,accentColor:C.teal}}/>
        Locks changed / new keys issued
      </label>
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>onSave({...f,id:"key"+Date.now(),loggedBy:user?.name||"Manager"})} style={{...BTN(C.warn),flex:1,padding:"11px 0"}}>Log</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}


function exportICal(entries, name, cpdEntries, rotaSessions) {
  const lines = ["BEGIN:VCALENDAR","VERSION:2.0","PRODID:-//Omni Rota//DCO Dental//EN","X-WR-CALNAME:Omni Rota — "+name,"X-WR-TIMEZONE:Europe/London"];

  // Leave entries
  (entries||[]).forEach(e=>{
    const d = e.date.replace(/-/g,"");
    const lt = e.leaveType==="medical"?"Medical Leave":e.leaveType==="cpd"?"CPD / Training":"Annual Leave";
    lines.push("BEGIN:VEVENT",`DTSTART;VALUE=DATE:${d}`,`DTEND;VALUE=DATE:${d}`,`SUMMARY:${lt}`,`DESCRIPTION:${lt} · Approved · Omni Rota`,`UID:pr-leave-${e.id}@dco`,`END:VEVENT`);
  });

  // CPD events
  (cpdEntries||[]).forEach(e=>{
    const ct = CPD_TYPES.find(t=>t.id===e.cpdType)||CPD_TYPES[CPD_TYPES.length-1];
    const d = e.date.replace(/-/g,"");
    if (e.allDay) {
      lines.push("BEGIN:VEVENT",`DTSTART;VALUE=DATE:${d}`,`DTEND;VALUE=DATE:${d}`,`SUMMARY:${ct.icon} ${e.title||ct.label}`,`DESCRIPTION:${ct.label}${e.notes?` · ${e.notes}`:""} · Omni Rota`,`UID:pr-cpd-${e.id}@dco`,`END:VEVENT`);
    } else {
      // Timed event
      const fmtDT = (dateStr,time) => {
        const [y,m,dd] = dateStr.split("-");
        const [h,min] = (time||"09:00").split(":");
        return `${y}${m}${dd}T${h}${min}00`;
      };
      lines.push("BEGIN:VEVENT",`DTSTART;TZID=Europe/London:${fmtDT(e.date,e.startTime)}`,`DTEND;TZID=Europe/London:${fmtDT(e.date,e.endTime)}`,`SUMMARY:${ct.icon} ${e.title||ct.label}`,`DESCRIPTION:${ct.label}${e.notes?` · ${e.notes}`:""} · Omni Rota`,`UID:pr-cpd-${e.id}@dco`,`END:VEVENT`);
    }
  });

  // Rota sessions
  (rotaSessions||[]).forEach(s=>{
    const [y,m,d] = s.date.split("-");
    const fmtT = t => { const [h,min]=(t||"08:00").split(":"); return `${y}${m}${d}T${h}${min}00`; };
    lines.push("BEGIN:VEVENT",`DTSTART;TZID=Europe/London:${fmtT(s.start)}`,`DTEND;TZID=Europe/London:${fmtT(s.end)}`,`SUMMARY:${s.label}`,`DESCRIPTION:${s.label} · ${SITES[s.site]?.name} · Omni Rota`,`UID:pr-rota-${s.date}-${s.id}@dco`,`END:VEVENT`);
  });

  lines.push("END:VCALENDAR");
  const a=document.createElement("a");
  a.href=URL.createObjectURL(new Blob([lines.join("\r\n")],{type:"text/calendar"}));
  a.download=`pulserota-${name.replace(/ /g,"-").toLowerCase()}.ics`;
  a.click();
}

// Build rota sessions for a staff member over a date range (for .ics export)
function buildRotaSessions(personId, isClinician, siteId, state, days) {
  const sessions = [];
  const nurses = getNursesForSite(state, siteId);
  days.forEach(day=>{
    if (day.bh) return;
    if (isClinician) {
      const clin = getClinicians(state).find(c=>c.id===personId);
      if (!clin) return;
      const sched = clin.sched?.[day.dow];
      if (!sched||isAbsent(state,personId,day.date)) return;
      const [cs,ce] = sched.split("-");
      sessions.push({id:personId,date:day.date,start:cs,end:ce,label:`Surgery — ${clin.name}`,site:siteId});
    } else {
      const dr = buildDayRota(day.date, siteId, nurses, state);
      const pair = dr.pairings.find(p=>p.nurse?.id===personId);
      if (pair) sessions.push({id:personId,date:day.date,start:pair.shift.start,end:pair.shift.end,label:`With ${pair.clinician.name}`,site:siteId});
    }
  });
  return sessions;
}

/* ══════════════════════════════════
   ROTA COMPONENTS
══════════════════════════════════ */

function WhosOffStrip({days, siteId, state}) {
  const allStaff = [
    ...getClinicians(state).filter(c=>!c._deleted&&(c.site===siteId||(c.sites||[]).includes(siteId))),
    ...getNursesForSite(state, siteId),
    ...RECS_BASE.filter(r=>r.site===siteId),
  ];
  const reasonColor = r => {
    if (!r) return C.teal;
    const rl = r.toLowerCase();
    if (rl.includes("sick")) return C.danger;
    if (rl.includes("annual")||rl.includes("holiday")) return C.teal;
    if (rl.includes("cpd")||rl.includes("training")||rl.includes("college")) return "#7c3aed";
    if (rl.includes("maternity")) return C.warn;
    if (rl.includes("medical")||rl.includes("compassionate")) return C.mid;
    return C.teal;
  };
  const reasonShort = r => {
    if (!r) return "Off";
    const rl = r.toLowerCase();
    if (rl.includes("sick")) return "Sick";
    if (rl.includes("annual")||rl.includes("holiday")) return "AL";
    if (rl.includes("cpd")) return "CPD";
    if (rl.includes("college")) return "College";
    if (rl.includes("maternity")) return "Mat";
    if (rl.includes("medical")) return "Medical";
    if (rl.includes("compassionate")) return "CL";
    return "Off";
  };
  return (
    <div style={{marginTop:6,marginBottom:4}}>
      <div style={{fontSize:9,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Who's Off</div>
      <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:2}}>
        {days.map(d=>{
          if (d.isWeekend) return <div key={d.date} style={{minWidth:148,flex:"1 1 148px"}}/>;
          const off = allStaff.filter(s=>isAbsent(state,s.id,d.date)).map(s=>({
            ...s, reason: getAbsenceReason(state,s.id,d.date)||"Annual Leave"
          }));
          if (off.length===0) return (
            <div key={d.date} style={{minWidth:148,flex:"1 1 148px",background:"rgba(0,229,160,0.08)",border:`1px solid #bbf7d0`,borderRadius:8,padding:"5px 8px",minHeight:28,display:"flex",alignItems:"center"}}>
              <span style={{fontSize:9,color:C.green,fontWeight:700}}>All in</span>
            </div>
          );
          const show = off.slice(0,3);
          const more = off.length-3;
          return (
            <div key={d.date} style={{minWidth:148,flex:"1 1 148px",background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,padding:"5px 8px"}}>
              {show.map(s=>{
                const col = reasonColor(s.reason);
                return (
                  <div key={s.id} style={{display:"flex",alignItems:"center",gap:4,marginBottom:2}}>
                    <span style={{fontSize:9,fontWeight:700,color:col,background:col+"18",border:`1px solid ${col}33`,borderRadius:3,padding:"1px 4px",flexShrink:0,lineHeight:1.4}}>{reasonShort(s.reason)}</span>
                    <span style={{fontSize:10,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.name.split(" ")[0]} {s.name.split(" ").slice(-1)[0]?.charAt(0)}</span>
                  </div>
                );
              })}
              {more>0&&<div style={{fontSize:9,color:C.dim,fontWeight:600,marginTop:1}}>+{more} more</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DayCard({dayRota,date,siteId,nurses,state,set,canOverride,user}) {
  const {pairings,flags,spares} = dayRota;
  const [overM,setOverM] = useState(null);
  const [taskM,setTaskM] = useState(null);
  const dayObj = CAL.days.find(d=>d.date===date);

  function saveOverride(clinId,nurseId,date) {
    const clin = dayRota.pairings.find(p=>p.clinician.id===clinId)?.clinician;
    const nurse = nurses.find(n=>n.id===nurseId);
    const msg = nurseId==="none"
      ? `✎ Rota override: removed nurse from ${clin?.name||clinId} on ${date}`
      : `✎ Rota override: ${nurse?.name||nurseId} → ${clin?.name||clinId} on ${date}`;
    set(s=>({
      ...s,
      rotaOverrides:{...s.rotaOverrides,[date]:{...(s.rotaOverrides?.[date]||{}),[clinId]:nurseId}},
      ...addLog(s, user?.name||"Unknown", "rota", msg)
    }));
    setOverM(null);
  }
  function saveTask(staffId,task) {
    set(s=>({...s,spareTasks:{...s.spareTasks,[`${date}_${staffId}`]:task}}));
    setTaskM(null);
  }

  const bhClosed = isBHClosed(state, date);
  const collegesToday = (state.collegeDates||[]).filter(e=>e.date===date&&e.site===siteId);
  const cpdToday = (state.cpdEvents||[]).filter(e=>e.date===date&&e.site===siteId);
  const isBH = !!(dayObj?.bh);
  const isWknd = !!(dayObj?.isWeekend);

  // Rota change banner — staff who were scheduled but are now absent
  const rotaAbsent = pairings
    .filter(p=>p.nurse&&isAbsent(state,p.nurse.id,date))
    .map(p=>p.nurse);
  const hasRotaChange = rotaAbsent.length > 0;
  const headerBg = bhClosed?"rgba(245,166,35,0.08)":isWknd?"rgba(167,139,250,0.06)":C.bg;
  const headerBorder = bhClosed?"rgba(245,166,35,0.25)":isWknd?"rgba(167,139,250,0.2)":C.border;
  const headerColor = bhClosed?C.warn:isWknd?"#a78bfa":C.text;
  const hasNoCover = flags.some(f=>f.type==="gap");
  const hasNoKeyholder = flags.some(f=>f.type==="no_keyholder");
  const singlePoint = flags.some(f=>f.type==="singlePoint");
  const borderColor = hasNoCover||hasNoKeyholder ? "rgba(255,70,70,0.35)" : singlePoint ? "rgba(245,197,24,0.3)" : C.border;
  const bgColor = hasNoCover||hasNoKeyholder ? "rgba(255,70,70,0.07)" : singlePoint ? "rgba(245,197,24,0.06)" : C.surface;

  return (
    <div style={{minWidth:152,flex:"1 1 152px"}}>
      <div style={{background:headerBg,border:`1px solid ${headerBorder}`,borderRadius:"12px 12px 0 0",padding:"9px 10px",textAlign:"center"}}>
        <div style={{fontSize:13,fontWeight:600,color:headerColor,fontFamily:"'Instrument Serif',serif",fontStyle:"italic"}}>{dayObj?.dow}</div>
        <div style={{fontSize:10,color:C.dim,marginTop:1,fontFamily:"'Instrument Sans',system-ui,sans-serif"}}>{dayObj?.label.split(" ").slice(1,3).join(" ")}</div>
        {isBH&&<div style={{fontSize:9,color:bhClosed?C.warn:C.green,marginTop:2,fontWeight:600}}>{dayObj.bh}{bhClosed?" · Closed":" · Open"}</div>}
        {isWknd&&!isBH&&<div style={{fontSize:9,color:"#a78bfa",marginTop:2,fontWeight:500}}>Weekend</div>}
        {collegesToday.length>0&&<div style={{fontSize:9,color:C.teal,marginTop:2,fontWeight:500}}>College</div>}
        {cpdToday.length>0&&<div style={{fontSize:9,color:"#f5c518",marginTop:2,fontWeight:600,letterSpacing:"0.04em"}}>CPD{cpdToday.length>1?` ×${cpdToday.length}`:""}: {cpdToday.map(e=>e.title||"Training").join(", ")}</div>}
      </div>
      <div style={{background:bgColor,border:`1px solid ${borderColor}`,borderTop:"none",borderRadius:"0 0 12px 12px",minHeight:60,overflow:"hidden"}}>
        {/* Rota change banner — visible to all staff on this shift */}
        {hasRotaChange&&(
          <div style={{background:"rgba(245,197,24,0.1)",borderBottom:`1px solid rgba(245,197,24,0.25)`,padding:"5px 8px",display:"flex",alignItems:"center",gap:5,flexWrap:"wrap"}}>
            <span style={{fontSize:9,fontWeight:700,color:C.warn,textTransform:"uppercase",letterSpacing:"0.06em"}}>Rota updated</span>
            {rotaAbsent.map(n=>(
              <span key={n.id} style={{fontSize:9,color:"rgba(255,255,255,0.6)",background:"rgba(245,197,24,0.12)",borderRadius:4,padding:"1px 5px",fontWeight:600}}>
                {n.name.split(" ")[0]} absent
              </span>
            ))}
          </div>
        )}
        {bhClosed ? <div style={{padding:10,textAlign:"center",fontSize:11,color:C.dim}}>Bank Holiday — Closed</div>
        : pairings.length===0 ? <div style={{padding:10,textAlign:"center",fontSize:11,color:C.dim}}>No sessions</div>
        : pairings.map((p,i)=>{
          const isSed = (p.reqs||[]).length>0;
          const isIV  = (p.reqs||[]).includes("iv");
          const isRA  = (p.reqs||[]).includes("ra") && !isIV;
          const nurseUnqualified = isSed&&p.nurse&&!(isIV?p.nurse.iv:p.nurse.ra);
          const sedColor = isIV?"#b91c1c":isRA?"#d97706":null;
          const cardBg = p.manual?"#f0fdf4":p.autoAssigned?"rgba(245,197,24,0.06)":"transparent";
          return (
          <div key={i} style={{display:"flex",borderBottom:`1px solid ${C.border2}`,background:cardBg,overflow:"hidden"}}>
            {/* Sedation stripe — left edge */}
            {isSed&&<div style={{width:3,flexShrink:0,background:sedColor,borderRadius:0}}/>}
            <div style={{flex:1,padding:"6px 8px"}}>
              <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:2}}>
                <div style={{width:7,height:7,borderRadius:99,background:p.clinician.color,flexShrink:0}}/>
                <span style={{fontSize:11,fontWeight:600,color:p.clinician.color,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontFamily:"'Instrument Sans',system-ui,sans-serif"}}>
                  {p.clinician.name.split(" ").pop()}
                  {getSiteKeyholders(state,siteId).includes(p.clinician.id)&&<span style={{marginLeft:3,fontSize:8}}>🔑</span>}
                </span>
                <span style={{fontSize:8,color:C.dim,flexShrink:0,fontVariantNumeric:"tabular-nums"}}>{p.clinStart}–{p.clinEnd}</span>
              </div>
              {/* Sedation badge — full row, prominent */}
              {isSed&&(
                <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:3,paddingLeft:10}}>
                  <span style={{fontSize:9,fontWeight:800,color:sedColor,background:sedColor+"15",border:`1px solid ${sedColor}33`,borderRadius:4,padding:"1px 5px",letterSpacing:"0.02em"}}>
                    {isIV?"IV Sedation":"RA"}
                  </span>
                </div>
              )}
              {p.nurse ? (
                <div style={{paddingLeft:10,display:"flex",alignItems:"center",gap:4}}>
                  <span title={p.autoAssigned?"Auto-assigned by Omni (no preferred nurse available)":p.manual?"Manually set by manager":p.roomAssigned?"Room assignment":"Preferred pairing"} style={{fontSize:10,color:p.autoAssigned?C.warn:p.manual?C.green:p.roomAssigned?C.lime:C.mid,flex:1}}>
                    {p.autoAssigned?"⚡":p.manual?"✎":p.roomAssigned?"":""}  {p.nurse.name.split(" ")[0]}
                  </span>
                  {nurseUnqualified&&<span style={{fontSize:8,color:C.danger,fontWeight:800,background:"rgba(255,70,70,0.08)",border:"1px solid rgba(255,70,70,0.2)",borderRadius:3,padding:"1px 4px"}}>Not qualified</span>}
                  {(()=>{const nc=cpdToday.filter(e=>!e.allDay&&(e.staffIds||[]).includes(p.nurse?.id));return nc.length>0&&<span style={{fontSize:7,fontWeight:700,color:"#f5c518",background:"rgba(245,197,24,0.1)",border:"1px solid rgba(245,197,24,0.2)",borderRadius:3,padding:"1px 4px",marginLeft:2}}>CPD {nc[0].startTime}–{nc[0].endTime}</span>;})()}
                  {canOverride&&<button onClick={()=>setOverM(p)} style={{fontSize:8,padding:"1px 5px",borderRadius:4,border:`1px solid ${C.border}`,background:"transparent",color:C.dim,cursor:"pointer"}}>edit</button>}
                </div>
              ) : (
                <div style={{paddingLeft:10,fontSize:10,color:C.danger,fontWeight:700}}>No nurse
                  {canOverride&&<button onClick={()=>setOverM(p)} style={{marginLeft:4,fontSize:8,padding:"1px 5px",borderRadius:4,border:`1px solid ${C.danger}33`,background:"transparent",color:C.danger,cursor:"pointer"}}>fix</button>}
                </div>
              )}
              {/* Lunch row — shown when clinician has a split schedule */}
              {p.hasLunch&&(()=>{
                const toM=t=>{if(!t)return 0;const[h,m]=(t+":00").split(":").map(Number);return h*60+(m||0);};
                const lunchMins=toM(p.lunchEnd)-toM(p.lunchStart);
                if(lunchMins<=0) return null; // guard against 0m or invalid lunch
                const bp=state.settings?.breakPolicy||{};
                const isPaid=bp.lunchPaid||false;
                return (
                  <div style={{paddingLeft:10,marginTop:2,marginBottom:2,display:"flex",alignItems:"center",gap:4,background:"rgba(58,173,152,0.04)",borderRadius:4,padding:"2px 6px 2px 10px"}}>
                    <span style={{fontSize:8}}>🍽</span>
                    <span style={{fontSize:8,color:C.dim,fontWeight:600}}>{p.lunchStart}–{p.lunchEnd}</span>
                    <span style={{fontSize:8,color:C.dim}}>{lunchMins}m lunch</span>
                    <span style={{fontSize:7,fontWeight:700,color:isPaid?"#22d3ee":C.warn,background:isPaid?"rgba(34,211,238,0.1)":C.warn+"12",borderRadius:3,padding:"1px 4px"}}>
                      {isPaid?"paid":"unpaid"}
                    </span>
                    {canOverride&&(
                      <button onClick={()=>{
                        const newTime=window.prompt(`Lunch time for ${p.clinician.name} on ${date} (e.g. 13:00-14:00):`,`${p.lunchStart}-${p.lunchEnd}`);
                        if(newTime&&newTime.includes("-")){const[ls,le]=newTime.split("-");set(s=>({...s,rotaBreaks:{...(s.rotaBreaks||{}),[`${date}_${p.clinician.id}_lunch`]:{lunchStart:ls,lunchEnd:le}}}));}
                      }} style={{fontSize:7,padding:"1px 4px",borderRadius:3,border:`1px solid ${C.border}`,background:"transparent",color:C.dim,cursor:"pointer",marginLeft:"auto"}}>adjust</button>
                    )}
                  </div>
                );
              })()}
              {/* Break annotation — WTR 20-min break for long sessions (suppressed when lunch shown) */}
              {(()=>{
                if (!p.clinStart||!p.clinEnd) return null;
                if (p.hasLunch) return null; // lunch row covers the break requirement
                const toMins=t=>{if(!t)return 0;const[h,m]=(t+":00").split(":").map(Number);return h*60+(m||0);};
                const sessionMins=Math.max(0,toMins(p.clinEnd)-toMins(p.clinStart));
                const sessionHrs=sessionMins/60;
                const bp=state.settings?.breakPolicy||{};
                const threshold=bp.thresholdHrs||6;
                if (sessionHrs<=threshold) return null;
                const breakKey=`${date}_${p.clinician.id}`;
                const override=state.rotaBreaks?.[breakKey];
                const breakTime=override?.time||(()=>{
                  const defaultT=bp.defaultTime||"13:00";
                  return defaultT;
                })();
                const breakMins=override?.durationMins||bp.durationMins||30;
                const isPaid=override?.paid!==undefined?override.paid:(bp.paid||false);
                return (
                  <div style={{paddingLeft:10,marginTop:2,display:"flex",alignItems:"center",gap:4,flexWrap:"wrap"}}>
                    <span style={{fontSize:8,color:C.teal}}>☕</span>
                    <span style={{fontSize:8,color:C.dim}}>{breakTime} · {breakMins}m {isPaid?"paid":"unpaid"}</span>
                    {canOverride&&(
                      <button onClick={()=>{
                        const newTime=window.prompt(`Break time for ${p.clinician.name} on ${date} (HH:MM):`,breakTime);
                        if (newTime) set(s=>({...s,rotaBreaks:{...(s.rotaBreaks||{}),[breakKey]:{time:newTime,durationMins:breakMins,paid:isPaid}}}));
                      }} style={{fontSize:7,padding:"1px 4px",borderRadius:3,border:`1px solid ${C.border}`,background:"transparent",color:C.dim,cursor:"pointer"}}>adjust</button>
                    )}
                    {!bp.paid&&<span style={{fontSize:7,fontWeight:700,color:C.warn,background:C.warn+"12",borderRadius:3,padding:"1px 4px"}}>WTR</span>}
                  </div>
                );
              })()}
            </div>
          </div>
          );
        })}
        {cpdToday.filter(e=>e.allDay).length>0&&(
          <div style={{marginTop:8,paddingTop:8,borderTop:`1px solid ${C.border2}`}}>
            <div style={{fontSize:9,fontWeight:700,color:"#f5c518",letterSpacing:"0.08em",marginBottom:4}}>CPD / TRAINING</div>
            {cpdToday.filter(e=>e.allDay).map(e=>(
              <div key={e.id} style={{fontSize:9,color:"rgba(245,197,24,0.7)",marginBottom:2}}>
                {(e.staffIds||[]).map(id=>{
                  const p = [...(state.nurses||NURSES_BASE),...RECS_BASE,...getClinicians(state)].find(s=>s.id===id);
                  return p ? <span key={id} style={{marginRight:4}}>{p.name.split(" ")[0]}</span> : null;
                })}
                <span style={{color:"rgba(245,197,24,0.45)",marginLeft:2}}>— {e.title||"Training"}</span>
              </div>
            ))}
          </div>
        )}
        {spares.length>0&&(
          <div style={{padding:"5px 8px",background:"rgba(0,229,160,0.08)",borderTop:`1px solid ${C.border2}`}}>
            <div style={{fontSize:9,color:C.lime,fontWeight:700,marginBottom:2}}>SPARE</div>
            {spares.map(n=>{
              const task = state.spareTasks?.[`${date}_${n.id}`];
              const onRec = state.nurseOnReception?.[`${date}_${n.id}`];
              return (
                <div key={n.id} style={{fontSize:10,color:onRec?C.brook:C.green,display:"flex",alignItems:"center",gap:4,marginBottom:2,flexWrap:"wrap"}}>
                  <span>{onRec?"📋":"+"} {n.name.split(" ")[0]}</span>
                  {onRec
                    ? <><span style={{fontSize:9,color:C.brook}}>— on reception</span>
                        {canOverride&&<button onClick={()=>set(s=>{const r={...(s.nurseOnReception||{})};delete r[`${date}_${n.id}`];return{...s,nurseOnReception:r};})} style={{fontSize:8,padding:"1px 4px",borderRadius:4,border:`1px solid ${C.border}`,background:"transparent",color:C.dim,cursor:"pointer"}}>undo</button>}</>
                    : task ? <span style={{fontSize:9,color:C.dim}}>— {task}</span>
                    : canOverride&&(
                      <>
                        <button onClick={()=>setTaskM(n)} style={{fontSize:8,padding:"1px 4px",borderRadius:4,border:`1px solid ${C.border}`,background:"transparent",color:C.dim,cursor:"pointer"}}>+task</button>
                        <button title="Move this nurse to reception cover for today" onClick={()=>set(s=>({...s,nurseOnReception:{...(s.nurseOnReception||{}),[`${date}_${n.id}`]:true}}))} style={{fontSize:8,padding:"1px 5px",borderRadius:4,border:`1px solid ${C.brook}44`,background:C.brook+"12",color:C.brook,cursor:"pointer",fontWeight:700}}>→ Reception</button>
                      </>
                    )
                  }
                </div>
              );
            })}
          </div>
        )}
        {flags.filter(f=>f.type==="gap"||f.type==="roomOverflow").map((f,i)=>(
          <div key={i} style={{padding:"4px 8px",background:f.type==="roomOverflow"?"#fff7ed":"#fef2f2",fontSize:9,color:f.type==="roomOverflow"?C.warn:C.danger,fontWeight:700}}>{f.msg}</div>
        ))}
        {flags.filter(f=>f.type==="capability").map((f,i)=>(
          <div key={i} style={{padding:"4px 8px",background:"rgba(245,197,24,0.08)",fontSize:9,color:C.danger,fontWeight:700}}>💉 {f.msg}</div>
        ))}
        {flags.filter(f=>f.type==="keyholder").map((f,i)=>(
          <div key={i} style={{padding:"4px 8px",background:f.color===C.danger?"#fef2f2":"rgba(245,197,24,0.06)",fontSize:9,color:f.color,fontWeight:700}}>{f.msg}</div>
        ))}
        <div style={{padding:"0 6px 6px"}}><ReceptionPanel siteId={siteId} date={date} state={state} set={set} canOverride={canOverride}/></div>
      </div>
      {overM&&<ShiftOverrideModal pairing={overM} date={date} siteId={siteId} nurses={nurses} state={state} onSave={saveOverride} onClose={()=>setOverM(null)}/>}
      {taskM&&(
        <Overlay onClose={()=>setTaskM(null)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:18,color:C.heading,marginBottom:14}}>Assign task — {taskM.name}</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:12}}>
            {TASK_TYPES.map(t=>(
              <button key={t} onClick={()=>saveTask(taskM.id,t)} style={{padding:"10px 12px",borderRadius:10,border:`1px solid ${C.border}`,background:C.bg,color:C.soft,fontFamily:"inherit",fontSize:12,fontWeight:600,cursor:"pointer",textAlign:"left"}}>{t}</button>
            ))}
          </div>
          <div style={{borderTop:`1px solid ${C.border}`,paddingTop:12}}>
            <div style={{fontSize:11,color:C.dim,marginBottom:6}}>Or type a custom task</div>
            <div style={{display:"flex",gap:8}}>
              <input
                placeholder="e.g. Sharps audit, Patient recall calls..."
                style={{flex:1,background:C.bg,border:`1px solid ${C.border}`,
                  borderRadius:8,padding:"8px 12px",color:C.text,
                  fontFamily:"inherit",fontSize:12,outline:"none"}}
                onKeyDown={e=>{
                  if(e.key==="Enter"&&e.target.value.trim()){
                    saveTask(taskM.id,e.target.value.trim());
                  }
                }}
              />
              <button
                onClick={e=>{
                  const inp=e.target.previousSibling;
                  if(inp&&inp.value.trim()) saveTask(taskM.id,inp.value.trim());
                }}
                style={{padding:"8px 14px",borderRadius:8,cursor:"pointer",
                  fontFamily:"inherit",fontSize:12,fontWeight:600,
                  background:"rgba(58,173,152,0.15)",
                  border:"1px solid rgba(58,173,152,0.3)",color:"#3aad98"}}>
                Assign
              </button>
            </div>
          </div>
        </Overlay>
      )}

    </div>
  );
}


/* ══════════════════════════════════
   RECEPTION PANEL
══════════════════════════════════ */
function ReceptionPanel({siteId, date, state, set, canOverride}) {
  const [showOverride, setShowOverride] = useState(false);
  const dayObj = CAL.days.find(d=>d.date===date);
  if (!dayObj || dayObj.isWeekend || isBHClosed(state, date)) return null;

  // Use auto-assigned rota — check for manual override first
  const overrideKey = `${date}_${siteId}`;
  const manualOverride = (state.recShiftOverrides||{})[overrideKey];

  // Get the week containing this date
  const [yr,mo,dy] = date.split("-").map(Number);
  const dt = new Date(yr,mo-1,dy);
  const monOffset = dt.getDay()===0 ? -6 : 1-dt.getDay();
  const mon = new Date(dt); mon.setDate(dt.getDate()+monOffset);
  const sun = new Date(mon); sun.setDate(mon.getDate()+6);
  const monStr = mon.toISOString().split("T")[0];
  const sunStr = sun.toISOString().split("T")[0];
  const weekDaysForBuild = CAL.days.filter(d=>d.date>=monStr&&d.date<=sunStr);

  // Build or get assignments for this day
  const builtRota = manualOverride ? {[date]: manualOverride} : buildReceptionRota(siteId, weekDaysForBuild, state);
  const dayRota = builtRota[date] || { assignments:[], flags:[] };

  const assignments = dayRota?.assignments||[];
  const flags = dayRota?.flags||[];
  const hasNoCover = flags.some(f=>f.type==="no_cover");
  const hasNoKeyholder = flags.some(f=>f.type==="no_keyholder");
  const singlePoint = flags.some(f=>f.type==="single_point");
  const borderColor = hasNoCover||hasNoKeyholder ? "rgba(255,70,70,0.35)" : singlePoint ? "rgba(245,197,24,0.3)" : C.border2;
  const bgColor = hasNoCover||hasNoKeyholder ? "rgba(255,70,70,0.1)" : singlePoint ? "rgba(245,197,24,0.06)" : "transparent";

  const SHIFT_COLORS = { early: C.teal, full: "#7c3aed", late: C.brook };

  return (
    <div style={{marginTop:8,padding:"8px 10px",borderRadius:10,border:`1px solid ${borderColor}`,background:bgColor}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
        <div style={{fontSize:9,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em"}}>Reception</div>
        <div style={{display:"flex",alignItems:"center",gap:4}}>
          {hasNoCover && <span style={{fontSize:9,fontWeight:700,color:C.danger}}>No cover</span>}
          {hasNoKeyholder && <span style={{fontSize:9,fontWeight:700,color:C.danger}}>No keyholder</span>}
          {singlePoint && <span style={{fontSize:9,fontWeight:700,color:C.warn}}>Single point</span>}
          {canOverride && <button onClick={()=>setShowOverride(true)} style={{fontSize:8,padding:"1px 5px",borderRadius:4,border:`1px solid ${C.border}`,background:"transparent",color:C.dim,cursor:"pointer"}}>edit</button>}
        </div>
      </div>
      {assignments.length===0 ? (
        <div style={{fontSize:10,color:C.danger,fontWeight:700}}>No reception assigned</div>
      ) : assignments.map((a,i)=>{
        const shift = REC_SHIFTS.find(s=>s.id===a.shift)||REC_SHIFTS[1];
        return (
          <div key={i} style={{fontSize:10,color:C.text,display:"flex",alignItems:"center",gap:4,marginBottom:2}}>
            <div style={{width:5,height:5,borderRadius:99,background:SHIFT_COLORS[a.shift]||C.teal,flexShrink:0}}/>
            <span style={{fontWeight:600}}>{a.name.split(" ")[0]}</span>
            <span style={{fontSize:9,color:SHIFT_COLORS[a.shift]||C.dim}}>{shift.start}–{shift.end}</span>
            {a.keyholder && <span style={{fontSize:8,color:C.warn}}>🔑</span>}
            {a.role==="TCO" && <span style={{fontSize:8,color:C.dim}}>TCO</span>}
          </div>
        );
      })}
      {/* Nurses covering reception today */}
      {(()=>{
        const allNurses = getNursesForSite(state, siteId);
        const nursesOnRec = allNurses.filter(n=>state.nurseOnReception?.[`${date}_${n.id}`]);
        if(!nursesOnRec.length) return null;
        return nursesOnRec.map(n=>(
          <div key={n.id} style={{fontSize:10,color:C.brook,display:"flex",alignItems:"center",gap:4,marginBottom:2}}>
            <div style={{width:5,height:5,borderRadius:99,background:C.brook,flexShrink:0}}/>
            <span style={{fontWeight:600}}>{n.name.split(" ")[0]}</span>
            <span style={{fontSize:9,color:C.brook,background:C.brook+"15",borderRadius:3,padding:"1px 4px",fontWeight:700}}>nurse cover</span>
          </div>
        ));
      })()}
      {manualOverride && <div style={{fontSize:8,color:C.dim,marginTop:3,fontStyle:"italic"}}>✎ manually overridden</div>}
      {showOverride && canOverride && (
        <RecShiftOverrideModal siteId={siteId} date={date} state={state} set={set} current={assignments} onClose={()=>setShowOverride(false)}/>
      )}
    </div>
  );
}

function RecShiftOverrideModal({siteId, date, state, set, current, onClose}) {
  const allRecs = [
    ...RECS_BASE.filter(r=>r.site===siteId&&r.type!=="Manager"),
    ...(state.customRecs||[]).filter(r=>r.site===siteId&&!r.archived&&r.type!=="Manager"),
  ];
  const keyholderIds = new Set(getSiteKeyholders(state, siteId));
  const [assignments, setAssignments] = useState(current.map(a=>({...a})));


  function removeSlot(i) { setAssignments(a=>a.filter((_,idx)=>idx!==i)); }
  function updateSlot(i, key, val) {
    setAssignments(a=>a.map((s,idx)=>{
      if (idx!==i) return s;
      if (key==="staffId") {
        const person = allRecs.find(r=>r.id===val);
        return {...s, staffId:val, name:person?.name||"", keyholder:keyholderIds.has(val)};
      }
      return {...s,[key]:val};
    }));
  }
  function addSlot() {
    setAssignments(a=>[...a,{staffId:"",name:"",start:"08:00",end:"17:30",keyholder:false}]);
  }
  function save() {
    const overrideKey = `${date}_${siteId}`;
    set(s=>({...s, recShiftOverrides:{...(s.recShiftOverrides||{}), [overrideKey]:{assignments,flags:[]}}}));
    onClose();
  }
  function reset() {
    const overrideKey = `${date}_${siteId}`;
    set(s=>{const r={...(s.recShiftOverrides||{})};delete r[overrideKey];return {...s,recShiftOverrides:r};});
    onClose();
  }

  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:18,color:C.heading,marginBottom:4}}>Reception shifts — {date}</div>
      <div style={{fontSize:11,color:C.dim,marginBottom:16}}>{SITES[siteId]?.name} · Set actual shift times — as flexible as needed</div>
      {assignments.map((a,i)=>(
        <div key={i} style={{display:"flex",gap:6,alignItems:"center",marginBottom:8,flexWrap:"wrap"}}>
          <select value={a.staffId} onChange={e=>updateSlot(i,"staffId",e.target.value)} style={{...IS,flex:2,minWidth:120,padding:"8px 10px",fontSize:12}}>
            <option value="">— Select person —</option>
            {allRecs.map(r=><option key={r.id} value={r.id}>{r.name}{keyholderIds.has(r.id)?" 🔑":""}</option>)}
          </select>
          <input
            type="time" value={a.start||"08:00"}
            onChange={e=>updateSlot(i,"start",e.target.value)}
            style={{...IS,width:90,padding:"8px 6px",fontSize:12,textAlign:"center"}}
          />
          <span style={{fontSize:12,color:C.dim}}>–</span>
          <input
            type="time" value={a.end||"17:30"}
            onChange={e=>updateSlot(i,"end",e.target.value)}
            style={{...IS,width:90,padding:"8px 6px",fontSize:12,textAlign:"center"}}
          />
          <button onClick={()=>removeSlot(i)} style={{...BTN(C.danger,true),padding:"6px 10px",fontSize:11}}>X</button>
        </div>
      ))}
      <div style={{display:"flex",gap:6,marginBottom:16}}>
        <button onClick={addSlot} style={{...BTN(C.teal,true),flex:1,padding:"8px 0",fontSize:12}}>+ Add person</button>
      </div>
      <div style={{fontSize:10,color:C.dim,marginBottom:14}}>Typical shifts: Early 08:00–14:00 · Full 08:00–17:30 · Late 14:00–20:00 · but enter any times needed</div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={save} style={{...BTN(C.teal),flex:2,padding:"10px 0"}}>Save</button>
        <button onClick={reset} style={{...BTN(C.dim,true),flex:1,padding:"10px 0"}}>Reset to auto</button>
      </div>
    </Overlay>
  );
}

/* ══════════════════════════════════
   WEEKLY ROOM PLANNER
══════════════════════════════════ */
function WeeklyRoomPlanner({siteId, weekKey, weekDays, nurses, state, set, canOverride}) {
  const site = SITES[siteId];
  const roomCount = (SURGERY_ROOMS[siteId]||{}).count || 4;
  const weekAssign = (state.roomAssignments||{})[weekKey]||{};
  const availNurses = nurses.filter(n=>!n.maternity&&!n.archived);
  const pairHistory = state.pairHistory||{};

  // Get clinicians working this week at this site
  const weekClinicians = getClinicians(state).filter(c=>{
    if (c._deleted) return false;
    const clinSites = c.sites||[c.site];
    const clinUnknown = !c.site || !new Set(Object.keys(SITES)).has(c.site);
    const siteMatch = clinSites.includes(siteId) || (clinUnknown && Object.keys(SITES).length<=1);
    if (!siteMatch) return false;
    if (c.leaving && weekDays[0]?.date > c.leaving) return false;
    if (c.startDate && weekDays[weekDays.length-1]?.date < c.startDate) return false;
    const schedForSite = c.siteScheds?.[siteId]||c.sched;
    return weekDays.some(d=>schedForSite?.[d.dow]);
  });

  // AI suggestions — pairs with most history that aren't yet assigned
  const suggestions = weekClinicians.map(c=>{
    if (weekAssign[c.id]) return null; // already assigned
    const ranked = availNurses
      .map(n=>({nurse:n, count:(pairHistory[`${n.id}_${c.id}`]||0)}))
      .filter(x=>x.count>0)
      .sort((a,b)=>b.count-a.count);
    if (ranked.length===0) return null;
    return {clinician:c, nurse:ranked[0].nurse, count:ranked[0].count};
  }).filter(Boolean);

  function assign(clinId, nurseId) {
    set(s=>({...s,roomAssignments:{...s.roomAssignments,[weekKey]:{...(s.roomAssignments?.[weekKey]||{}),[clinId]:nurseId}}}));
  }
  function clearAssign(clinId) {
    set(s=>{
      const wa = {...(s.roomAssignments?.[weekKey]||{})};
      delete wa[clinId];
      return {...s,roomAssignments:{...s.roomAssignments,[weekKey]:wa}};
    });
  }
  function applySuggestion(s) { assign(s.clinician.id, s.nurse.id); }

  // Record a session worked (called when week passes — manager can trigger)
  // eslint-disable-next-line no-unused-vars
  function recordPairHistory(clinId, nurseId) {
    const key = `${nurseId}_${clinId}`;
    set(s=>({...s,pairHistory:{...s.pairHistory,[key]:(s.pairHistory?.[key]||0)+1}}));
  }

  const assignedNurseIds = new Set(Object.values(weekAssign));

  return (
    <div style={{...CS, marginBottom:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
        <div>
          <div style={{fontWeight:700,color:site.color,fontSize:14}}>Weekly Room Assignments</div>
          <div style={{fontSize:11,color:C.dim,marginTop:2}}>Set nurse–dentist pairings for the week. Builds consistency over time.</div>
        </div>
        {!canOverride&&<div style={{fontSize:11,color:C.dim}}>View only</div>}
      </div>

      {/* AI suggestions */}
      {canOverride && suggestions.length>0 && (
        <div style={{background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:10,padding:"10px 12px",marginBottom:14}}>
          <div style={{fontSize:10,fontWeight:700,color:C.lime,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>AI Suggestions — based on past sessions</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {suggestions.slice(0,4).map(s=>(
              <button key={s.clinician.id} onClick={()=>applySuggestion(s)}
                style={{padding:"6px 10px",borderRadius:8,border:`1px solid ${C.lime}44`,background:"rgba(0,229,160,0.08)",color:C.green,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:4}}>
                <span style={{color:s.clinician.color}}>●</span>
                {s.clinician.name.split(" ").pop()} + {s.nurse.name.split(" ")[0]}
                <span style={{fontSize:9,color:C.dim}}>×{s.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Room grid */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:8}}>
        {weekClinicians.map(c=>{
          const assignedNurseId = weekAssign[c.id];
          const assignedNurse = availNurses.find(n=>n.id===assignedNurseId);
          const historyCount = assignedNurse ? (pairHistory[`${assignedNurse.id}_${c.id}`]||0) : 0;
          const days = weekDays.filter(d=>c.sched[d.dow]).map(d=>d.dow).join(", ");
          return (
            <div key={c.id} style={{border:`1.5px solid ${assignedNurse?site.color+"44":C.border}`,borderRadius:10,padding:"10px 12px",background:assignedNurse?site.color+"08":C.bg}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                <div style={{width:8,height:8,borderRadius:99,background:c.color,flexShrink:0}}/>
                <span style={{fontWeight:700,color:c.color,fontSize:12,flex:1}}>{c.name}</span>
              </div>
              <div style={{fontSize:10,color:C.dim,marginBottom:8}}>{c.type} · {days}</div>
              {assignedNurse ? (
                <div>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                    <span style={{fontSize:12,fontWeight:600,color:C.text,flex:1}}>{assignedNurse.name.split(" ")[0]}</span>
                    {historyCount>0&&<span style={{fontSize:9,color:C.lime,background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:99,padding:"1px 6px"}}>×{historyCount} together</span>}
                  </div>
                  {canOverride&&(
                    <div style={{display:"flex",gap:4}}>
                      <select onChange={e=>assign(c.id,e.target.value)} value={assignedNurseId}
                        style={{...IS,fontSize:10,padding:"4px 8px",flex:1}}>
                        {availNurses.map(n=><option key={n.id} value={n.id}>{n.name.split(" ")[0]}{assignedNurseIds.has(n.id)&&n.id!==assignedNurseId?" (busy)":""}</option>)}
                      </select>
                      <button onClick={()=>clearAssign(c.id)} style={{...BTN(C.dim,true),fontSize:10,padding:"4px 8px"}}>X</button>
                    </div>
                  )}
                </div>
              ) : (
                canOverride ? (
                  <select onChange={e=>e.target.value&&assign(c.id,e.target.value)} defaultValue=""
                    style={{...IS,fontSize:11,padding:"6px 8px",color:C.dim}}>
                    <option value="">Assign nurse…</option>
                    {availNurses.map(n=><option key={n.id} value={n.id}>{n.name}{assignedNurseIds.has(n.id)?" (busy)":""}</option>)}
                  </select>
                ) : <span style={{fontSize:11,color:C.danger,fontWeight:700}}>Unassigned</span>
              )}
            </div>
          );
        })}
      </div>

      {/* Pair history summary */}
      {Object.keys(pairHistory).length>0&&(
        <details style={{marginTop:14}}>
          <summary style={{fontSize:11,color:C.dim,cursor:"pointer",userSelect:"none"}}>Pair consistency history</summary>
          <div style={{marginTop:10,display:"flex",flexWrap:"wrap",gap:6}}>
            {Object.entries(pairHistory)
              .filter(([k])=>{
                const [nId,cId]=k.split("_");
                const n=availNurses.find(x=>x.id===nId);
                const c=weekClinicians.find(x=>x.id===cId);
                return n&&c;
              })
              .sort(([,a],[,b])=>b-a)
              .slice(0,12)
              .map(([k,count])=>{
                const [nId,cId]=k.split("_");
                const n=availNurses.find(x=>x.id===nId);
                const c=getClinicians(state).find(x=>x.id===cId);
                if(!n||!c) return null;
                return (
                  <div key={k} style={{fontSize:10,color:C.soft,background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:"3px 8px"}}>
                    {n.name.split(" ")[0]} + {c.name.split(" ").pop()} <span style={{color:C.lime,fontWeight:700}}>×{count}</span>
                  </div>
                );
              })}
          </div>
        </details>
      )}
    </div>
  );
}

/* ══════════════════════════════════
   ROTA PDF PRINT MODAL
   No branding — clean export for managers
══════════════════════════════════ */
function RotaPrintModal({siteId, state, weekIdx, mode, onClose}) {
  const site = SITES[siteId];
  const weeks = CAL.weeks.filter(w=>w.days.length>0);
  const [printMode, setPrintMode] = useState(mode||"week");
  const [selWeek, setSelWeek] = useState(weekIdx||0);
  const [selMonth, setSelMonth] = useState(()=>{
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`;
  });
  const [practiceName, setPracticeName] = useState(state.settings?.practiceName||PRACTICE_CONFIG.name||"My Practice");

  // Month options
  const monthOpts = [];
  for (let i=0; i<12; i++) {
    const d = new Date(new Date().getFullYear(), new Date().getMonth()+i, 1);
    monthOpts.push([`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`, d.toLocaleDateString("en-GB",{month:"long",year:"numeric"})]);
  }

  // Build the weeks to print
  const weeksToPrint = printMode==="calmonth"
    ? weeks.filter(wk=>wk.days.some(d=>d.date.slice(0,7)===selMonth))
    : printMode==="month"
      ? weeks.slice(selWeek, selWeek+4)
      : weeks.slice(selWeek, selWeek+1);

  function doPrint() {
    window.print();
  }

  // Build rota data for a day
  function getDayData(date, dow) {
    const allNurses = getNursesForSite(state, siteId);
    const recs = [...RECS_BASE,...(state.customRecs||[])].filter(r=>r.site===siteId&&!r.archived);
    // Use buildDayRota for accurate nurse-clinician pairings (same as live rota view)
    const dr = buildDayRota(date, siteId, allNurses, state);
    const pairings = dr.pairings.map(p=>({
      clinician: p.clinician,
      hours: p.clinStart&&p.clinEnd?`${p.clinStart}–${p.clinEnd}`:"",
      nurse: p.nurse||null,
      hasSed: (p.sedReq||[]).length>0,
      sedReq: p.sedReq||[],
    })).filter(p=>p.clinician);

    // Who's off + spare from buildDayRota
    const offNurses = dr.offNurses||allNurses.filter(n=>isAbsent(state,n.id,date));
    const offRecs = recs.filter(r=>isAbsent(state,r.id,date));
    const sickToday = (state.sick||[]).filter(s=>{
      const st=new Date(s.date), en=new Date(s.date);
      en.setDate(en.getDate()+s.days-1);
      const dt=new Date(date);
      return s.site===siteId && dt>=st && dt<=en;
    });

    // Keyholders
    const khIds = getSiteKeyholders(state,siteId);
    const allStaff = [...allNurses,...recs];
    const keyholders = khIds.map(id=>allStaff.find(s=>s.id===id)).filter(Boolean).filter(p=>!isAbsent(state,p.id,date));

    // Spare nurses from buildDayRota
    const spareNurses = dr.spareNurses||[];

    // Reception on duty
    const recsOnDuty = recs.filter(r=>!isAbsent(state,r.id,date));

    return {pairings, offNurses, offRecs, sickToday, keyholders, spareNurses, recsOnDuty};
  }

  const printStyle = `
    #pulse-print-area { display: none; }
    @media print {
      #root { display: none !important; }
      #pulse-print-area { display: block !important; position: static !important; width: 100%; background: #fff; color: #111; }
      #pulse-print-area * { color: #111 !important; border-color: #ccc !important; }
      @page { size: A4 landscape; margin: 12mm; }
    }
  `;

  return (
    <Overlay onClose={onClose}>
      <style>{printStyle}</style>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Print Rota — {site.name}</div>
      <div style={{fontSize:12,color:C.dim,marginBottom:16}}>Simplified format, no system branding.</div>

      {/* Controls */}
      <div style={{display:"flex",gap:10,marginBottom:14,flexWrap:"wrap"}}>
        <div style={{flex:1}}>
          <label style={LB}>Period</label>
          <div style={{display:"flex",gap:6}}>
            {[["week","1 week"],["month","4 weeks"],["calmonth","Calendar month"]].map(([m,l])=>(
              <button key={m} onClick={()=>setPrintMode(m)}
                style={{flex:1,padding:"8px",borderRadius:8,border:`1.5px solid ${printMode===m?C.teal:C.border}`,
                  background:printMode===m?C.tealPale:"transparent",color:printMode===m?C.teal:C.mid,
                  fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer"}}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <div style={{flex:1}}>
          <label style={LB}>{printMode==="calmonth" ? "Month" : "Starting week"}</label>
          {printMode==="calmonth" ? (
            <select value={selMonth} onChange={e=>setSelMonth(e.target.value)} style={IS}>
              {monthOpts.map(([v,l])=><option key={v} value={v}>{l}</option>)}
            </select>
          ) : (
            <select value={selWeek} onChange={e=>setSelWeek(+e.target.value)} style={IS}>
              {weeks.map((wk,i)=>{
                const fmt = d => d ? `${d.date.slice(8)}/${d.date.slice(5,7)}` : "";
                return (
                  <option key={i} value={i}>
                    {wk.days[0]?.dow} {fmt(wk.days[0])} – {wk.days[wk.days.length-1]?.dow} {fmt(wk.days[wk.days.length-1])}
                  </option>
                );
              })}
            </select>
          )}
        </div>
      </div>
      <div style={{marginBottom:14}}>
        <label style={LB}>Practice name on document</label>
        <input value={practiceName} onChange={e=>setPracticeName(e.target.value)} style={IS} placeholder="e.g. City Dental Practice"/>
      </div>

      <button onClick={doPrint} style={{...BTN(C.teal),width:"100%",padding:"12px 0",fontSize:14,marginBottom:12}}>
        🖨 Print / Save as PDF
      </button>
      <div style={{fontSize:11,color:C.dim,textAlign:"center",marginBottom:16}}>
        In the print dialog, choose "Save as PDF" to download. Select "Landscape" and "A4" for best results.
      </div>

      {/* PRINT AREA — portaled to <body> so it escapes the fixed modal and paginates per week */}
      {createPortal(<div id="pulse-print-area" style={{fontFamily:"Arial,sans-serif",fontSize:9,color:"#111",background:"#fff"}}>
        {weeksToPrint.map((wk,wi)=>(
          <div key={wk.start} style={{pageBreakAfter: wi<weeksToPrint.length-1?"always":"auto",marginBottom:wi<weeksToPrint.length-1?0:0}}>
            {/* Header */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",borderBottom:"2px solid #111",paddingBottom:4,marginBottom:8}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontFamily:"Georgia,serif",fontStyle:"italic",fontWeight:700,fontSize:18,color:"#1a1612",letterSpacing:"-0.04em"}}>Omni<span style={{color:"#3aad98"}}>.</span></span>
                <div>
                <div style={{fontSize:14,fontWeight:700}}>{practiceName} — {site.name}</div>
                <div style={{fontSize:10,color:"#444"}}>
                  Weekly Rota · {wk.days[0]?.label.split(" ").slice(1).join(" ")} – {wk.days[wk.days.length-1]?.label.split(" ").slice(1).join(" ")}
                </div>
              </div></div>
              <div style={{fontSize:9,color:"#666"}}>Printed {new Date().toLocaleDateString("en-GB")}</div>
            </div>

            {/* Day columns */}
            <div style={{display:"grid",gridTemplateColumns:`repeat(${wk.days.length},1fr)`,gap:4}}>
              {wk.days.map(d=>{
                const {pairings, offNurses, offRecs, sickToday, keyholders, spareNurses, recsOnDuty} = getDayData(d.date, d.dow);
                const isBH = !!d.bh;
                return (
                  <div key={d.date} style={{border:"1px solid #ccc",borderRadius:3,padding:4,background:isBH?"#f5f5f5":"#fff",minHeight:200}}>
                    {/* Day header */}
                    <div style={{fontWeight:700,fontSize:10,borderBottom:"1px solid #ddd",paddingBottom:3,marginBottom:4,display:"flex",justifyContent:"space-between"}}>
                      <span>{d.dow} {d.date.slice(8)} {d.short}</span>
                      {isBH&&<span style={{color:"#666",fontSize:8}}>BH</span>}
                    </div>

                    {isBH ? (
                      <div style={{color:"#666",fontSize:9,fontStyle:"italic"}}>Bank Holiday</div>
                    ) : (
                      <>
                        {/* Clinician pairings */}
                        {pairings.length>0&&(
                          <div style={{marginBottom:5}}>
                            <div style={{fontSize:7,fontWeight:700,color:"#666",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:2}}>Clinicians</div>
                            {pairings.map((p,i)=>(
                              <div key={i} style={{marginBottom:3,paddingBottom:3,borderBottom:i<pairings.length-1?"1px dashed #eee":"none"}}>
                                <div style={{fontWeight:700,fontSize:9}}>{p.clinician.name.split(" ").slice(-1)[0]}</div>
                                <div style={{fontSize:8,color:"#555"}}>{p.clinician.type} · {p.hours}</div>
                                {p.nurse&&<div style={{fontSize:8,color:"#111"}}>↳ {p.nurse.name.split(" ")[0]}</div>}
                                {p.hasSed&&<div style={{fontSize:7,background:"#fee2e2",color:"#b91c1c",borderRadius:2,padding:"1px 4px",display:"inline-block",marginTop:1}}>
                                  {p.sedReq.map(r=>r.type?.toUpperCase()||r).join(" + ")}
                                </div>}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Spare nurses */}
                        {spareNurses.length>0&&(
                          <div style={{marginBottom:4}}>
                            <div style={{fontSize:7,fontWeight:700,color:"#666",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:1}}>Spare</div>
                            <div style={{fontSize:8,color:"#555"}}>{spareNurses.map(n=>n.name.split(" ")[0]).join(", ")}</div>
                          </div>
                        )}

                        {/* Reception */}
                        {recsOnDuty.length>0&&(
                          <div style={{marginBottom:4}}>
                            <div style={{fontSize:7,fontWeight:700,color:"#666",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:1}}>Reception</div>
                            <div style={{fontSize:8,color:"#555"}}>{recsOnDuty.map(r=>r.name.split(" ")[0]).join(", ")}</div>
                          </div>
                        )}

                        {/* Keyholders */}
                        {keyholders.length>0&&(
                          <div style={{marginBottom:4}}>
                            <div style={{fontSize:7,fontWeight:700,color:"#666",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:1}}>Keys</div>
                            <div style={{fontSize:8,color:"#555"}}>{keyholders.map(k=>k.name.split(" ")[0]).join(", ")}</div>
                          </div>
                        )}

                        {/* Off today */}
                        {(offNurses.length>0||offRecs.length>0||sickToday.length>0)&&(
                          <div style={{marginTop:4,paddingTop:3,borderTop:"1px solid #eee"}}>
                            <div style={{fontSize:7,fontWeight:700,color:"#666",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:1}}>Off</div>
                            {sickToday.map(s=>(
                              <div key={s.id} style={{fontSize:8,color:"#ff4646"}}>
                                {(state.nurses||NURSES_BASE).find(n=>n.id===s.staffId)?.name?.split(" ")[0]||s.staffId} (sick)
                              </div>
                            ))}
                            {offNurses.filter(n=>!sickToday.find(s=>s.staffId===n.id)).map(n=>(
                              <div key={n.id} style={{fontSize:8,color:"#666"}}>{n.name.split(" ")[0]} (leave)</div>
                            ))}
                            {offRecs.map(r=>(
                              <div key={r.id} style={{fontSize:8,color:"#666"}}>{r.name.split(" ")[0]} (leave)</div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer notes */}
            <div style={{marginTop:6,paddingTop:4,borderTop:"1px solid #ddd",display:"flex",gap:16,fontSize:8,color:"#666"}}>
              <span>= Keyholder on duty</span>
              <span>IV/RA = Sedation required that day</span>
              <span>↳ = Assigned nurse</span>
              <span style={{marginLeft:"auto"}}>Confidential — for internal use only</span>
            </div>
          </div>
        ))}
      </div>, document.body)}
    </Overlay>
  );
}

function RotaView({siteId,state,set,canOverride,user,onGoToAdminClinicians}) {
  const site = SITES[siteId];
  const nurses = getNursesForSite(state, siteId);
  const [view,setView] = useState("2 Weeks");
  const [printM,setPrintM] = useState(null); // {week, mode:"week"|"month"}
  const _getThisWeek = () => {
    const today = new Date().toISOString().split("T")[0];
    const idx = CAL.weeks.findIndex(w=>w.days.some(d=>d.date===today));
    if(idx>-1) return idx;
    const idx2 = CAL.weeks.findIndex(w=>w.days.some(d=>d.date>today));
    return idx2>-1 ? idx2 : 0;
  };
  const [selWeek,setSelWeek] = useState(_getThisWeek);
  // Always snap to current week on first load
  useEffect(()=>{
    setSelWeek(_getThisWeek());
  },[]);
  const [selMonth,setSelMonth] = useState(()=>{
    const now = new Date();
    return Math.max(0,(now.getFullYear()-2026)*12+now.getMonth());
  });
  const [selDate,setSelDate] = useState(()=>{const t=new Date().toISOString().split('T')[0];return CAL.days.find(d=>d.date===t)?.date||CAL.days.find(d=>d.date>t)?.date||CAL.days[0]?.date;});
  const MONTHS = ["April","May","June","July","August","September"];
  const weeks = CAL.weeks.filter(w=>w.days.length>0);

  return (
    <div>
      {printM&&<RotaPrintModal siteId={siteId} state={state} weekIdx={printM.week} mode={printM.mode} onClose={()=>setPrintM(null)}/>}
      {/* Clinician schedule signpost */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:"#f5c51811",border:"1px solid #f5c51833",borderRadius:10,padding:"8px 14px",marginBottom:12,gap:10,flexWrap:"wrap"}}>
        <span style={{fontSize:11,color:"#f5c518",fontWeight:600}}>⏱ This view shows the rota — clinician shift times are set in <strong>Admin → Clinicians</strong></span>
        {onGoToAdminClinicians&&<button onClick={onGoToAdminClinicians} style={{background:"#f5c51820",border:"1px solid #f5c51855",borderRadius:7,padding:"4px 12px",fontSize:11,fontWeight:700,color:"#f5c518",cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap"}}>Go to Clinicians →</button>}
      </div>
      <div style={{display:"flex",gap:6,marginBottom:14,flexWrap:"wrap",alignItems:"center"}}>
        {["Week","2 Weeks","Month","Day","All Sites","Rooms"].map(v=>(
          <button key={v} onClick={()=>setView(v)} style={{padding:"6px 16px",borderRadius:99,fontFamily:"inherit",fontSize:12,fontWeight:600,cursor:"pointer",border:`1px solid ${view===v?site.color:C.border}`,background:view===v?site.color+"18":"transparent",color:view===v?site.color:C.mid}}>{v}</button>
        ))}
        <div style={{marginLeft:"auto",display:"flex",gap:4,flexWrap:"wrap",alignItems:"center"}}>
          {[
            [C.teal,"●  Preferred pair"],
            [C.warn,"⚡ Auto-assigned"],
            [C.green,"✎  Manual override"],
            [C.lime,"Room assigned"],
            [C.danger,"Gap — no nurse"],
            [C.lime,"➕ Spare"],
          ].map(([c,l])=>(
            <span key={l} style={{fontSize:10,color:c,background:c+"15",border:`1px solid ${c}22`,borderRadius:99,padding:"2px 7px"}}>{l}</span>
          ))}
          <span title="WTR: Working Time Regulations 1998 — staff working over 6 hours are entitled to a 20-minute rest break" style={{fontSize:10,color:C.warn,background:C.warn+"15",border:`1px solid ${C.warn}22`,borderRadius:99,padding:"2px 7px",cursor:"default"}}>☕ WTR break</span>
          <span title="Lunch break — clinician has a split AM/PM schedule. Nurses take the same break as their paired clinician." style={{fontSize:10,color:C.dim,background:"rgba(255,255,255,0.04)",border:`1px solid ${C.border}`,borderRadius:99,padding:"2px 7px",cursor:"default"}}>🍽 Lunch</span>
          <button onClick={()=>setPrintM({week:selWeek,mode:"week"})}
            style={{...BTN(C.dim,true),fontSize:11,padding:"5px 10px",display:"flex",alignItems:"center",gap:4}}>
            🖨 PDF
          </button>
        </div>
      </div>

      {view==="2 Weeks"&&(
        <div>
          <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:12}}>
            {(()=>{
              const start = Math.max(0, selWeek - 2);
              const end   = Math.min(weeks.length, start + 8);
              return weeks.slice(start, end).map((wk, offset)=>{
                const i = start + offset;
                return (
                  <button key={wk.start} onClick={()=>setSelWeek(i)}
                    style={{padding:"5px 12px",borderRadius:8,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",
                      border:`1px solid ${i===selWeek?site.color:C.border}`,
                      background:i===selWeek?site.color+"18":"transparent",
                      color:i===selWeek?site.color:C.mid}}>
                    {wk.days[0]?.label.split(" ").slice(1,3).join(" ")}
                  </button>
                );
              });
            })()}
            {selWeek>2&&<button onClick={()=>setSelWeek(v=>Math.max(0,v-1))} style={{padding:"5px 10px",borderRadius:8,fontFamily:"inherit",fontSize:11,cursor:"pointer",border:`1px solid ${C.border}`,background:"transparent",color:C.mid}}>‹</button>}
            <button onClick={()=>setSelWeek(v=>Math.min(weeks.length-2,v+1))} style={{padding:"5px 10px",borderRadius:8,fontFamily:"inherit",fontSize:11,cursor:"pointer",border:`1px solid ${C.border}`,background:"transparent",color:C.mid}}>›</button>
          </div>
          {[selWeek, selWeek+1].map(wi=>{
            const wk = weeks[wi];
            if (!wk) return null;
            const firstDay = wk.days[0];
            const dateObj = new Date(firstDay.date);
            const dayOfWeek = dateObj.getDay();
            const monday = new Date(dateObj);
            monday.setDate(dateObj.getDate()-(dayOfWeek===0?6:dayOfWeek-1));
            const weekKey = `${monday.toISOString().split("T")[0]}_${siteId}`;
            const isThisWeek = wi===selWeek;
            return (
              <div key={wi} style={{marginBottom:20}}>
                <div style={{fontSize:11,fontWeight:700,color:site.color,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8,paddingBottom:6,borderBottom:`1px solid ${site.color}22`,display:"flex",alignItems:"center",gap:8}}>
                  <span>{isThisWeek?"This week":"Next week"} · {firstDay.label.split(" ").slice(1).join(" ")}</span>
                  {(()=>{
                    const parity = getWeekParity(firstDay.date, state);
                    return <span style={{fontSize:10,fontWeight:800,color:parity==="A"?C.teal:C.brook,background:parity==="A"?C.tealPale:C.brook+"18",border:`1px solid ${parity==="A"?C.teal:C.brook}44`,borderRadius:5,padding:"1px 7px",letterSpacing:"0.08em"}}>WEEK {parity}</span>;
                  })()}
                </div>
                <WeeklyRoomPlanner siteId={siteId} weekKey={weekKey} weekDays={wk.days} nurses={nurses} state={state} set={set} canOverride={canOverride}/>
                <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:4}}>
                  {wk.days.map(d=>{
                    const dr = buildDayRota(d.date,siteId,nurses,state);
                    return <DayCard key={d.date} dayRota={dr} date={d.date} siteId={siteId} nurses={nurses} state={state} set={set} canOverride={canOverride} user={user}/>;
                  })}
                </div>
                <WhosOffStrip days={wk.days} siteId={siteId} state={state}/>
              </div>
            );
          })}
        </div>
      )}

      {view==="Week"&&(
        <div>
          <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:12}}>
            {(()=>{
              // Show 2 weeks before and 5 weeks after current selection
              const start = Math.max(0, selWeek - 2);
              const end   = Math.min(weeks.length, start + 8);
              return weeks.slice(start, end).map((wk, offset)=>{
                const i = start + offset;
                const isToday = i===selWeek;
                return (
                  <button key={wk.start} onClick={()=>setSelWeek(i)}
                    style={{padding:"5px 12px",borderRadius:8,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",
                      border:`1px solid ${isToday?site.color:C.border}`,
                      background:isToday?site.color+"18":"transparent",
                      color:isToday?site.color:C.mid}}>
                    {wk.days[0]?.label.split(" ").slice(1,3).join(" ")}
                  </button>
                );
              });
            })()}
            {selWeek>2&&<button onClick={()=>setSelWeek(v=>Math.max(0,v-1))} style={{padding:"5px 10px",borderRadius:8,fontFamily:"inherit",fontSize:11,cursor:"pointer",border:`1px solid ${C.border}`,background:"transparent",color:C.mid}}>‹</button>}
            <button onClick={()=>setSelWeek(v=>Math.min(weeks.length-1,v+1))} style={{padding:"5px 10px",borderRadius:8,fontFamily:"inherit",fontSize:11,cursor:"pointer",border:`1px solid ${C.border}`,background:"transparent",color:C.mid}}>›</button>
          </div>
          {weeks[selWeek]&&(()=>{
            const wk = weeks[selWeek];
            const firstDay = wk.days[0];
            const parity = getWeekParity(firstDay.date, state);
            const dateObj = new Date(firstDay.date);
            const dayOfWeek = dateObj.getDay();
            const monday = new Date(dateObj);
            monday.setDate(dateObj.getDate()-(dayOfWeek===0?6:dayOfWeek-1));
            const weekKey = `${monday.toISOString().split("T")[0]}_${siteId}`;
            return (
              <div>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                  <span style={{fontSize:10,fontWeight:800,color:parity==="A"?C.teal:C.brook,background:parity==="A"?C.tealPale:C.brook+"18",border:`1px solid ${parity==="A"?C.teal:C.brook}44`,borderRadius:5,padding:"2px 8px",letterSpacing:"0.08em"}}>WEEK {parity}</span>
                  <span style={{fontSize:10,color:C.dim}}>{firstDay.label.split(" ").slice(1).join(" ")}</span>
                </div>
                <WeeklyRoomPlanner siteId={siteId} weekKey={weekKey} weekDays={wk.days} nurses={nurses} state={state} set={set} canOverride={canOverride}/>
                <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:4}}>
                  {wk.days.map(d=>{
                    const dr = buildDayRota(d.date,siteId,nurses,state);
                    return <DayCard key={d.date} dayRota={dr} date={d.date} siteId={siteId} nurses={nurses} state={state} set={set} canOverride={canOverride} user={user}/>;
                  })}
                </div>
                <WhosOffStrip days={wk.days} siteId={siteId} state={state}/>
              </div>
            );
          })()}
        </div>
      )}

      {view==="Rooms"&&(
        <div>
          <div style={{fontSize:12,color:C.dim,marginBottom:14}}>Manage weekly nurse–dentist room assignments and build consistent teams over time.</div>
          <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:14}}>
            {(()=>{
              const start = Math.max(0, selWeek - 2);
              const end   = Math.min(weeks.length, start + 8);
              return weeks.slice(start, end).map((wk, offset)=>{
                const i = start + offset;
                return (
                  <button key={wk.start} onClick={()=>setSelWeek(i)}
                    style={{padding:"5px 12px",borderRadius:8,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",
                      border:`1px solid ${selWeek===i?site.color:C.border}`,
                      background:selWeek===i?site.color+"18":"transparent",
                      color:selWeek===i?site.color:C.mid}}>
                    {wk.days[0]?.label.split(" ").slice(1,3).join(" ")}
                  </button>
                );
              });
            })()}
          </div>
          {weeks[selWeek]&&(()=>{
            const wk = weeks[selWeek];
            const firstDay = wk.days[0];
            const dateObj = new Date(firstDay.date);
            const dayOfWeek = dateObj.getDay();
            const monday = new Date(dateObj);
            monday.setDate(dateObj.getDate()-(dayOfWeek===0?6:dayOfWeek-1));
            const weekKey = `${monday.toISOString().split("T")[0]}_${siteId}`;
            return <WeeklyRoomPlanner siteId={siteId} weekKey={weekKey} weekDays={wk.days} nurses={nurses} state={state} set={set} canOverride={canOverride}/>;
          })()}
          {/* Surgery room reference */}
          <div style={{...CS,marginTop:4}}>
            <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Surgery Rooms — {SITES[siteId].name}</div>
            <div style={{fontSize:13,color:C.text}}>
              <span style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:28,color:C.teal}}>{(SURGERY_ROOMS[siteId]||{}).count||4}</span>
              <span style={{fontSize:12,color:C.dim,marginLeft:8}}>surgeries available · flexible designation (MOS/GDS/Therapy as needed, AM/PM handovers fine)</span>
            </div>
          </div>
        </div>
      )}

      {view==="Month"&&(
        <div>
          <div style={{display:"flex",gap:5,marginBottom:12,flexWrap:"wrap"}}>
            {MONTHS.map((m,i)=>(
              <button key={m} onClick={()=>setSelMonth(i)} style={{padding:"5px 12px",borderRadius:8,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${selMonth===i?site.color:C.border}`,background:selMonth===i?site.color+"18":"transparent",color:selMonth===i?site.color:C.mid}}>{m}</button>
            ))}
          </div>
          <div style={{fontSize:10,color:C.dim,marginBottom:10}}>All preferred pairings &nbsp; Auto-assigned nurse &nbsp; 🔴 Gap — no nurse available &nbsp;&nbsp; Tap a day to see detail</div>
          {CAL.weeks.filter(w=>w.days.some(d=>d.month===MONTHS[selMonth])).map(wk=>(
            <div key={wk.start} style={{...CS,marginBottom:8}}>
              <div style={{fontSize:11,fontWeight:700,color:C.mid,marginBottom:8}}>{wk.days[0]?.label.split(" ").slice(1).join(" ")} – {wk.days[wk.days.length-1]?.label.split(" ").slice(1).join(" ")}</div>
              <div style={{display:"flex",gap:5,overflowX:"auto"}}>
                {wk.days.map(d=>{
                  const dr = buildDayRota(d.date,siteId,nurses,state);
                  const hasGap=dr.flags.some(f=>f.type==="gap"),hasAuto=dr.flags.some(f=>f.type==="autoAssign");
                  const col=hasGap?C.danger:hasAuto?C.warn:C.green;
                  const numClins = dr.clinicians.length;
                  const numGaps = dr.flags.filter(f=>f.type==="gap").length;
                  return (
                    <div key={d.date}
                      onClick={()=>{setSelDate(d.date);setView("Day");}}
                      style={{minWidth:54,background:col+"15",border:`1px solid ${col}33`,borderRadius:8,padding:"6px 6px",textAlign:"center",cursor:"pointer",transition:"transform 0.1s"}}
                      onMouseEnter={e=>e.currentTarget.style.transform="scale(1.05)"}
                      onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}
                    >
                      <div style={{fontSize:10,fontWeight:700,color:col}}>{d.dow.slice(0,2)}</div>
                      <div style={{fontSize:9,color:C.dim}}>{d.label.split(" ")[1]}</div>
                      <div style={{fontSize:9,color:col,marginTop:2,fontWeight:700}}>
                        {numGaps>0 ? `${numGaps} gap${numGaps>1?"s":""}` : `${numClins} in`}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {view==="Day"&&(
        <div>
          <div style={{marginBottom:12}}>
            <input type="date" value={selDate} onChange={e=>setSelDate(e.target.value)} style={{...IS,maxWidth:220}}/>
          </div>
          {(()=>{
            const dayObj = CAL.days.find(d=>d.date===selDate);
            if (!dayObj) return <div style={{...CS,color:C.dim,fontSize:13}}>Date not in range (Apr–Sep 2026)</div>;
            if (dayObj.bh) return <div style={{...CS,color:C.warn}}>{dayObj.bh} — Bank Holiday</div>;
            const dr = buildDayRota(selDate,siteId,nurses,state);
            return (
              <div style={CS}>
                <div style={{fontWeight:700,color:site.color,fontSize:14,marginBottom:12}}>{dayObj.label} — {site.name}</div>
                {dr.pairings.map((p,i)=>{
                  const st=p.nurse?STATUS[p.nurse.status]:null;
                  const isSed=(p.reqs||[]).length>0;
                  const isIV=(p.reqs||[]).includes("iv");
                  const isRA=(p.reqs||[]).includes("ra")&&!isIV;
                  const nurseUnqualified=p.nurse&&isSed&&!(isIV?p.nurse.iv:p.nurse.ra);
                  const sedColor=isIV?"#b91c1c":isRA?"#d97706":null;
                  return (
                    <div key={i} style={{display:"flex",borderBottom:`1px solid ${C.border2}`,margin:"0 -12px",overflow:"hidden"}}>
                      {/* Sedation stripe */}
                      {isSed&&<div style={{width:4,flexShrink:0,background:sedColor}}/>}
                      <div style={{flex:1,display:"flex",alignItems:"center",gap:10,padding:"10px 12px",flexWrap:"wrap",background:isSed?sedColor+"08":"transparent"}}>
                        <div style={{width:10,height:10,borderRadius:99,background:p.clinician.color,flexShrink:0}}/>
                        <div style={{flex:1,minWidth:120}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                            <span style={{fontWeight:700,color:p.clinician.color,fontSize:13}}>{p.clinician.name}</span>
                            <span style={{fontSize:11,color:C.dim}}>{p.clinStart}–{p.clinEnd} · {p.clinician.type}</span>
                            {isSed&&<span style={{fontSize:11,fontWeight:800,color:sedColor,background:sedColor+"15",border:`1px solid ${sedColor}44`,borderRadius:5,padding:"2px 8px"}}>{isIV?"💉 IV Sedation":"⚗️ RA Sedation"}</span>}
                          </div>
                          {nurseUnqualified&&<div style={{fontSize:11,color:C.danger,fontWeight:700,marginTop:2}}>Nurse not qualified for this sedation type</div>}
                        </div>
                        {p.nurse ? (
                          <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                            {p.autoAssigned&&<Pill c={C.warn} t="⚡ Auto" sm/>}
                            {p.manual&&<Pill c={C.green} t="✎ Manual" sm/>}
                            <span style={{fontSize:12,fontWeight:600,color:C.text}}>{p.nurse.name}</span>
                            {st&&<Pill c={st.color} t={st.label} sm/>}
                          </div>
                        ) : <Pill c={C.danger} t="No nurse" sm/>}
                      </div>
                    </div>
                  );
                })}
                {dr.spares.length>0&&(
                  <div style={{marginTop:10,padding:"8px 12px",background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:8}}>
                    <span style={{fontSize:11,color:C.green,fontWeight:700}}>+ Spare: </span>
                    <span style={{fontSize:11,color:C.text}}>{dr.spares.map(n=>n.name).join(", ")}</span>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {view==="All Sites"&&(
        <div>
          <div style={{marginBottom:12}}>
            <input type="date" value={selDate} onChange={e=>setSelDate(e.target.value)} style={{...IS,maxWidth:220}}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:14}}>
            {Object.entries(SITES).map(([id,si])=>{
              const sNurses=getNursesForSite(state, siteId);
              const dr=buildDayRota(selDate,id,sNurses,state);
              const dayObj=CAL.days.find(d=>d.date===selDate);
              const gaps=dr.flags.filter(f=>f.type==="gap").length;
              const autos=dr.flags.filter(f=>f.type==="autoAssign").length;
              return (
                <div key={id} style={{...CS,borderTop:`3px solid ${gaps>0?C.danger:autos>0?C.warn:si.color}`}}>
                  <div style={{fontWeight:700,color:si.color,marginBottom:10,fontSize:14}}>{si.name}</div>
                  {dayObj?.bh ? <div style={{fontSize:12,color:C.warn}}>{dayObj.bh}</div>
                  : dr.pairings.length===0 ? <div style={{fontSize:12,color:C.dim}}>No sessions</div>
                  : dr.pairings.map((p,i)=>{
                    const st=p.nurse?STATUS[p.nurse.status]:null;
                    return (
                      <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 0",borderBottom:`1px solid ${C.border2}`}}>
                        <div style={{width:7,height:7,borderRadius:99,background:p.clinician.color,flexShrink:0}}/>
                        <span style={{fontSize:12,fontWeight:600,color:p.clinician.color,minWidth:80}}>{p.clinician.name.split(" ").pop()}</span>
                        {p.nurse ? <span style={{fontSize:11,color:p.autoAssigned?C.warn:C.mid,marginLeft:"auto"}}>{p.autoAssigned?"⚡":""}{p.nurse.name.split(" ")[0]}</span>
                        : <span style={{fontSize:11,color:C.danger,marginLeft:"auto",fontWeight:700}}>No nurse</span>}
                      </div>
                    );
                  })}
                  <div style={{marginTop:6,display:"flex",gap:4,flexWrap:"wrap"}}>
                    {gaps>0&&<Pill c={C.danger} t={`${gaps} gap`} sm/>}
                    {autos>0&&<Pill c={C.warn} t={`${autos} auto`} sm/>}
                    {dr.spares.length>0&&<Pill c={C.lime} t={`${dr.spares.length} spare`} sm/>}
                    {gaps===0&&autos===0&&<Pill c={C.green} t="Good" sm/>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════
   HOLIDAY KPI ENGINE
══════════════════════════════════ */
function buildHolidayKPI(state) {
  const today = new Date();
  const todayStr = today.toISOString().split("T")[0];
  const cfg = state.leaveYearConfig || { nurses:"jan-dec", reception:"jan-dec", dentists:"apr-mar" };

  const allStaff = [
    ...(state.nurses||NURSES_BASE).filter(n=>!n.maternity&&!n.archived),
    ...RECS_BASE,
  ];

  const staffKPI = allStaff.map(s => {
    // Each person's leave year is based on their role type
    const rType = roleTypeForPerson(s);
    const { start: leaveYearStart, end: leaveYearEnd } = getLeaveYear(rType, cfg);
    const totalDays  = (leaveYearEnd - leaveYearStart) / 86400000;
    const elapsed    = Math.max(0, (today - leaveYearStart) / 86400000);
    const paceTarget = Math.min(elapsed / totalDays, 1);

    const entit  = getEntitlement(state, s);
    const allLeave = state.leave[s.id]||[];
    // Days already taken (approved AND date is in the past)
    const taken   = allLeave.filter(l=>l.status==="approved"&&l.date<todayStr).length;
    // Days booked but not yet taken (approved future + pending)
    const bookedFuture = allLeave.filter(l=>l.status==="approved"&&l.date>=todayStr).length;
    const pending = allLeave.filter(l=>l.status==="pending").length;
    // Total committed = taken + booked + pending
    const totalCommitted = taken + bookedFuture + pending;
    const expected = Math.round(entit * paceTarget * 10) / 10;
    // Pace based on what's committed (taken + booked), not just taken
    const pace = entit > 0 ? totalCommitted / entit : 0;

    // RAG status — sensible rules:
    // Green if anything committed, or we're in Q1 (< 25% through year)
    // Amber only if past halfway AND committed < 40% of entitlement
    // Red (nothing booked) only if past Q1 AND genuinely zero committed
    // Red (running out) only if < 3 days left AND meaningfully far into the year
    let rag, ragLabel, ragNote;
    const inQ1 = paceTarget < 0.27; // before end of March roughly
    const pastHalfway = paceTarget >= 0.5;
    const remaining = entit - totalCommitted;

    if (totalCommitted === 0 && !inQ1) {
      // Nothing at all booked after Q1 — genuine concern
      rag = "red"; ragLabel = "Nothing booked"; ragNote = "No leave booked or taken yet — worth a conversation";
    } else if (remaining < 3 && paceTarget < 0.75 && entit > 5) {
      // Running out with lots of year left
      rag = "red"; ragLabel = "Running out"; ragNote = `Only ${Math.round(remaining*10)/10}d remaining`;
    } else if (pastHalfway && totalCommitted < entit * 0.4) {
      // Past halfway but committed less than 40% — behind
      rag = "amber"; ragLabel = "Behind pace"; ragNote = `Past halfway — ${totalCommitted}d committed of ${entit}d`;
    } else {
      rag = "green"; ragLabel = "On track"; ragNote = "";
    }

    return { person:s, entit, taken, bookedFuture, pending, totalCommitted, expected, pace, paceTarget, rag, ragLabel, ragNote };
  });

  // Site-level rollups — use average paceTarget across staff in site
  const siteKPI = Object.entries(SITES).map(([siteId, site]) => {
    const sStaff = staffKPI.filter(k => k.person.site === siteId);
    const totalEntit     = Math.round(sStaff.reduce((a,k) => a + (k.entit||0), 0) * 10) / 10;
    const totalCommitted = Math.round(sStaff.reduce((a,k) => a + (k.totalCommitted||0), 0) * 10) / 10;
    const totalApproved  = Math.round(sStaff.reduce((a,k) => a + (k.taken||0) + (k.bookedFuture||0), 0) * 10) / 10;
    const pct          = totalEntit > 0 ? Math.round((totalCommitted/totalEntit)*100) : 0;
    const avgPace      = sStaff.length > 0 ? sStaff.reduce((a,k)=>a+(k.paceTarget||0),0)/sStaff.length : 0;
    const targetPct    = Math.round(avgPace*100);
    const rag          = pct < targetPct - 15 ? "amber" : pct > targetPct + 20 ? "red" : "green";
    const alerts       = sStaff.filter(k=>k.rag!=="green").length;
    return { siteId, site, totalEntit, totalCommitted, totalApproved, pct, targetPct, rag, alerts };
  });

  // Overall pace target (nurses default, for backward-compat display)
  const { start: defStart, end: defEnd } = getLeaveYear("nurses", cfg);
  const defTotal = (defEnd - defStart) / 86400000;
  const defElapsed = Math.max(0, (today - defStart) / 86400000);
  const paceTarget = Math.min(defElapsed / defTotal, 1);

  return { staffKPI, siteKPI, paceTarget, todayStr };
}

/* ── Holiday KPI Detail (used in Planning tab) ── */
/* ══════════════════════════════════
   HOLIDAY REPORT MODAL
══════════════════════════════════ */
function HolidayReportModal({state, siteId, isPrincipal, onClose}) {
  const [view, setView] = useState("staff"); // "staff" | "clinicians" | "all"
  const [siteFilter, setSiteFilter] = useState(siteId||"all");
  const today = new Date().toISOString().split("T")[0];

  // Build staff rows
  const staffRows = (() => {
    const nurses = (state.nurses||NURSES_BASE).filter(n=>!n.maternity&&!n.archived);
    const recs = [...RECS_BASE,...(state.customRecs||[]).filter(r=>!r.archived)];
    const all = [...nurses,...recs];
    return all
      .filter(s=>siteFilter==="all"||s.site===siteFilter)
      .map(s=>{
        const rType = roleTypeForPerson(s);
        const indOv = (state.leaveYearOverrides||{})[s.id];
        const lyCfg = indOv?{[rType]:indOv}:state.leaveYearConfig;
        const {start:lyS,end:lyE} = getLeaveYear(rType, lyCfg);
        const lyStartStr = lyS.toISOString().split("T")[0];
        const lyEndStr = lyE.toISOString().split("T")[0];
        const entit = getEntitlement(state,s);
        const hist = getHistDays(state,s.id);
        const allLeave = state.leave[s.id]||[];
        const approved = allLeave.filter(l=>l.status==="approved"&&l.date>=lyStartStr&&l.date<=lyEndStr);
        const pending  = allLeave.filter(l=>l.status==="pending"&&l.date>=lyStartStr&&l.date<=lyEndStr);
        const taken    = approved.filter(l=>l.date<today).length;
        const booked   = approved.filter(l=>l.date>=today).length;
        const pend     = pending.length;
        const used     = countDays(approved);
        const rem      = Math.round((entit-hist-used)*10)/10;
        return {name:s.name, site:s.site, type:s.type||"Nurse", hrs:s.hrs||37.5, entit, hist, taken, booked, pend, used, rem,
          lyLabel:`${lyS.toLocaleDateString("en-GB",{day:"numeric",month:"short"})}–${lyE.toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"2-digit"})}`};
      })
      .sort((a,b)=>a.site.localeCompare(b.site)||a.name.localeCompare(b.name));
  })();

  // Build clinician rows
  const clinRows = (() => {
    const clins = getClinicians(state).filter(c=>!c._deleted&&(siteFilter==="all"||(c.sites||[c.site]).includes(siteFilter)));
    return clins.map(c=>{
      const {start:lyS,end:lyE} = getLeaveYear("dentists",state.leaveYearConfig);
      const lyStartStr = lyS.toISOString().split("T")[0];
      const lyEndStr = lyE.toISOString().split("T")[0];
      const entit = getEntitlement(state,c)||28;
      const cl = (state.clinicianLeave||{})[c.id]||[];
      const approved = cl.filter(l=>l.status==="approved"&&l.type==="annual"&&l.date>=lyStartStr&&l.date<=lyEndStr);
      const pending  = cl.filter(l=>l.status==="pending"&&l.date>=lyStartStr&&l.date<=lyEndStr);
      const taken    = approved.filter(l=>l.date<today).length;
      const booked   = approved.filter(l=>l.date>=today).length;
      const used     = approved.length;
      const rem      = entit-used;
      return {name:c.name, site:c.site, type:c.type, entit, taken, booked, pend:pending.length, used, rem,
        lyLabel:`${lyS.toLocaleDateString("en-GB",{day:"numeric",month:"short"})}–${lyE.toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"2-digit"})}`};
    }).sort((a,b)=>a.site.localeCompare(b.site)||a.name.localeCompare(b.name));
  })();

  const rows = view==="staff"?staffRows:view==="clinicians"?clinRows:[...staffRows,...clinRows];

  function downloadCSV() {
    const headers = ["Name","Site","Type","Entitlement","Taken","Booked (future)","Pending","Total Used","Remaining","Leave Year"];
    const lines = [headers.join(","),...rows.map(r=>[r.name,SITES[r.site]?.name||r.site,r.type,r.entit,r.taken,r.booked,r.pend,r.used,r.rem,r.lyLabel].join(","))];
    const blob = new Blob([lines.join("\n")],{type:"text/csv"});
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `holiday-report-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  }

  return (
    <Overlay onClose={onClose}>
      <style>{`@media print{body *{visibility:hidden!important}#hol-rep-print,#hol-rep-print *{visibility:visible!important}#hol-rep-print{position:fixed;top:0;left:0;width:100%;padding:16px;font-family:Arial,sans-serif;font-size:10px}@page{size:A4 landscape;margin:10mm}}`}</style>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Holiday Report</div>
      <div style={{fontSize:12,color:C.dim,marginBottom:16}}>Taken · booked · pending · remaining — as of today.</div>

      {/* Hidden print section */}
      <div id="hol-rep-print" style={{display:"none"}}>
        <div style={{borderBottom:"2px solid #111",paddingBottom:6,marginBottom:10,display:"flex",justifyContent:"space-between"}}>
          <div>
            <div style={{fontSize:16,fontWeight:700}}>Holiday Entitlement Report</div>
            <div style={{fontSize:10,color:"rgba(255,255,255,0.5)"}}>{view==="staff"?"Staff & Nurses":view==="clinicians"?"Dentists":"All Staff"} · {siteFilter==="all"?"All sites":SITES[siteFilter]?.name}</div>
          </div>
          <div style={{fontSize:9,color:"rgba(255,255,255,0.35)"}}>Generated {new Date().toLocaleDateString("en-GB")} · Confidential</div>
        </div>
        <table style={{borderCollapse:"collapse",width:"100%",fontSize:9}}>
          <thead><tr style={{borderBottom:"1px solid #ccc"}}>
            {["Name","Site","Type","Entitlement","Taken","Booked","Pending","Remaining","Leave Year"].map(h=>(
              <th key={h} style={{padding:"4px 8px",textAlign:"left",textTransform:"uppercase",fontSize:8,fontWeight:700,color:"#666"}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {rows.map((r,i)=>(
              <tr key={i} style={{background:i%2===0?"#fff":"#f9f9f9",borderBottom:"1px solid #eee"}}>
                <td style={{padding:"4px 8px",fontWeight:600}}>{r.name}</td>
                <td style={{padding:"4px 8px"}}>{SITES[r.site]?.name||r.site}</td>
                <td style={{padding:"4px 8px",color:"#666"}}>{r.type}</td>
                <td style={{padding:"4px 8px"}}>{r.entit}d</td>
                <td style={{padding:"4px 8px",color:"#d97706"}}>{r.taken}d</td>
                <td style={{padding:"4px 8px",color:"#3aad98"}}>{r.booked}d</td>
                <td style={{padding:"4px 8px",color:"#666"}}>{r.pend>0?r.pend+"d":"—"}</td>
                <td style={{padding:"4px 8px",fontWeight:700,color:r.rem<5?"#dc2626":r.rem<10?"#d97706":"#16a34a"}}>{r.rem}d</td>
                <td style={{padding:"4px 8px",color:"rgba(255,255,255,0.35)",fontSize:8}}>{r.lyLabel}</td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr style={{borderTop:"2px solid #111",fontWeight:700}}>
            <td colSpan={3} style={{padding:"4px 8px"}}>TOTAL ({rows.length} staff)</td>
            <td style={{padding:"4px 8px"}}>{rows.reduce((a,r)=>a+r.entit,0).toFixed(1)}d</td>
            <td style={{padding:"4px 8px",color:"#d97706"}}>{rows.reduce((a,r)=>a+r.taken,0)}d</td>
            <td style={{padding:"4px 8px",color:"#3aad98"}}>{rows.reduce((a,r)=>a+r.booked,0)}d</td>
            <td style={{padding:"4px 8px"}}>{rows.reduce((a,r)=>a+r.pend,0)}d</td>
            <td style={{padding:"4px 8px",fontWeight:700}}>{rows.reduce((a,r)=>a+(r.rem||0),0).toFixed(1)}d</td>
            <td/>
          </tr></tfoot>
        </table>
      </div>

      <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
        <div style={{display:"flex",gap:4}}>
          {[["staff","Staff & Nurses"],["clinicians","Dentists"],["all","Everyone"]].map(([v,l])=>(
            <button key={v} onClick={()=>setView(v)} style={{padding:"6px 14px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",
              border:`1px solid ${view===v?C.teal:C.border}`,background:view===v?C.tealPale:"transparent",color:view===v?C.teal:C.mid}}>
              {l}
            </button>
          ))}
        </div>
        {isPrincipal&&(
          <div style={{display:"flex",gap:4}}>
            {[["all","All sites"],...Object.entries(SITES).map(([id,s])=>[id,s.name])].map(([id,l])=>(
              <button key={id} onClick={()=>setSiteFilter(id)} style={{padding:"6px 14px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",
                border:`1px solid ${siteFilter===id?(SITES[id]?.color||C.teal):C.border}`,background:siteFilter===id?(SITES[id]?.color||C.teal)+"18":"transparent",
                color:siteFilter===id?(SITES[id]?.color||C.teal):C.mid}}>
                {l}
              </button>
            ))}
          </div>
        )}
        <button onClick={downloadCSV} style={{...BTN(C.dim,true),fontSize:11,padding:"6px 12px",marginLeft:"auto"}}>⬇ CSV</button>
        <button onClick={()=>{const el=document.getElementById("hol-rep-print");if(el){el.style.display="block";window.print();setTimeout(()=>el.style.display="none",500);}}} style={{...BTN(C.teal,true),fontSize:11,padding:"6px 12px"}}>🖨 PDF</button>
      </div>

      <div style={{overflowX:"auto",maxHeight:"60vh",overflowY:"auto"}}>
        <table style={{borderCollapse:"collapse",width:"100%",minWidth:600}}>
          <thead style={{position:"sticky",top:0,background:C.surface,zIndex:1}}>
            <tr>
              {["Name","Site","Type","Entit.","Taken","Booked","Pending","Remaining","Leave Year"].map(h=>(
                <th key={h} style={{padding:"6px 10px",fontSize:9,fontWeight:700,color:C.dim,textAlign:"left",textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:`1px solid ${C.border}`,whiteSpace:"nowrap"}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r,i)=>{
              const siteColor = SITES[r.site]?.color||C.dim;
              return (
                <tr key={i} style={{background:i%2===0?"transparent":C.bg}}>
                  <td style={{padding:"7px 10px",fontSize:12,fontWeight:600,color:C.text,whiteSpace:"nowrap"}}>{r.name}</td>
                  <td style={{padding:"7px 10px",fontSize:10}}>
                    <span style={{color:siteColor,fontWeight:700,background:siteColor+"18",borderRadius:4,padding:"1px 6px"}}>{SITES[r.site]?.name||r.site}</span>
                  </td>
                  <td style={{padding:"7px 10px",fontSize:11,color:C.mid}}>{r.type}</td>
                  <td style={{padding:"7px 10px",fontSize:12,color:C.text,fontWeight:600}}>{r.entit}d</td>
                  <td style={{padding:"7px 10px",fontSize:12,color:C.warn}}>{r.taken}d</td>
                  <td style={{padding:"7px 10px",fontSize:12,color:C.teal}}>{r.booked}d</td>
                  <td style={{padding:"7px 10px",fontSize:12,color:r.pend>0?C.lime:C.muted}}>{r.pend>0?r.pend+"d":"—"}</td>
                  <td style={{padding:"7px 10px",fontSize:12,fontWeight:700,color:r.rem<5?C.danger:r.rem<10?C.warn:C.green}}>{r.rem}d</td>
                  <td style={{padding:"7px 10px",fontSize:10,color:C.dim}}>{r.lyLabel}</td>
                </tr>
              );
            })}
            {rows.length===0&&(
              <tr><td colSpan={9} style={{padding:24,textAlign:"center",color:C.dim,fontSize:12}}>No data for this selection</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div style={{display:"flex",gap:12,marginTop:12,padding:"10px 14px",background:C.bg,borderRadius:10,flexWrap:"wrap"}}>
        {[
          ["Total entitlement",rows.reduce((a,r)=>a+r.entit,0).toFixed(1)+"d",C.text],
          ["Taken",rows.reduce((a,r)=>a+r.taken,0)+"d",C.warn],
          ["Booked future",rows.reduce((a,r)=>a+r.booked,0)+"d",C.teal],
          ["Pending",rows.reduce((a,r)=>a+r.pend,0)+"d",C.lime],
          ["Remaining",rows.reduce((a,r)=>a+(r.rem||0),0).toFixed(1)+"d",C.green],
        ].map(([l,v,c])=>(
          <div key={l} style={{textAlign:"center"}}>
            <div style={{fontSize:16,fontWeight:700,color:c}}>{v}</div>
            <div style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.05em"}}>{l}</div>
          </div>
        ))}
      </div>
    </Overlay>
  );
}

/* ══════════════════════════════════
   FREE SESSIONS REPORT
══════════════════════════════════ */
function FreeSessionsReport({state, siteId}) {
  const today = new Date().toISOString().split("T")[0];
  const [filterSite, setFilterSite] = useState(siteId || fallbackSite());
  const [filterMonth, setFilterMonth] = useState(()=>today.slice(0,7));
  const [filterSession, setFilterSession] = useState("all"); // all|am|pm|allday|after5
  const [filterNurse, setFilterNurse] = useState("any"); // any|yes|no
  const [viewMode, setViewMode] = useState("list"); // list|calendar

  const site = SITES[filterSite];
  const surgeryCount = site?.surgeries||4;

  // Build month options
  const monthOpts = [];
  for (let i=0; i<12; i++) {
    const d = new Date(new Date().getFullYear(), new Date().getMonth()+i, 1);
    monthOpts.push([`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`, d.toLocaleDateString("en-GB",{month:"long",year:"numeric"})]);
  }

  // Build all working days in selected month
  const monthDays = CAL.days.filter(d=>d.date.slice(0,7)===filterMonth&&!d.bh&&!d.isWeekend);

  // For each day, compute capacity
  const dayData = monthDays.map(d=>{
    const clins = getClinicians(state).filter(c=>{
      const clinSites = c.sites||[c.site];
      const sched = c.siteScheds?.[filterSite]||c.sched;
      return clinSites.includes(filterSite)&&!c._deleted&&sched?.[d.dow]&&!isAbsent(state,c.id,d.date);
    });
    const nurses = (state.nurses||NURSES_BASE).filter(n=>{
      if (n.site!==filterSite||n.maternity||n.archived) return false;
      if (isAbsent(state,n.id,d.date)) return false;
      // Check nurse is actually contracted to work this day
      // Look at their shift overrides first, then default to full week
      const shifts = (state.shiftOverrides||{})[n.id]||{};
      const dayShift = shifts[d.date];
      if (dayShift==="off") return false;
      // If nurse has explicit day contracts, check them
      const contract = n.workDays||n.contractDays;
      if (contract && Array.isArray(contract) && !contract.includes(d.dow)) return false;
      return true;
    });

    // Session type breakdown
    const sessions = {am:[], pm:[], allday:[], after5:[], other:[]};
    clins.forEach(c=>{
      const sched = c.siteScheds?.[filterSite]||c.sched||{};
      const hrs = sched[d.dow]||"";
      const [start,end] = hrs.split("-");
      if (start&&end) {
        const sh=parseInt(start.replace(":",""));
        const eh=parseInt(end.replace(":",""));
        if (eh>=1700) sessions.after5.push(c);
        if (sh<=900&&eh>=1600) sessions.allday.push(c);
        else if (eh<=1400) sessions.am.push(c);
        else if (sh>=1400) sessions.pm.push(c);
        else sessions.other.push(c);
      }
    });

    const totalClins = clins.length;
    const occupied = Math.min(totalClins, surgeryCount);
    const freeSurgeries = Math.max(0, surgeryCount - occupied);
    const spareNurses = Math.max(0, nurses.length - occupied);
    const amFree = Math.max(0, surgeryCount - sessions.am.length - sessions.allday.length);
    const pmFree = Math.max(0, surgeryCount - sessions.pm.length - sessions.allday.length);
    const alldayFree = Math.max(0, surgeryCount - sessions.allday.length);
    const after5Free = Math.max(0, surgeryCount - sessions.after5.length);

    return {
      date:d.date, dow:d.dow, label:d.label,
      clins, nurses, totalClins, freeSurgeries, spareNurses,
      amFree, pmFree, alldayFree, after5Free,
      sessions, surgeryCount,
      hasNurse: spareNurses > 0,
      isPast: d.date < today,
    };
  });

  // Apply filters
  const filtered = dayData.filter(d=>{
    if (filterSession==="am" && d.amFree===0) return false;
    if (filterSession==="pm" && d.pmFree===0) return false;
    if (filterSession==="allday" && d.alldayFree===0) return false;
    if (filterSession==="after5" && d.after5Free===0) return false;
    if (filterSession==="any" && d.freeSurgeries===0) return false;
    if (filterNurse==="yes" && !d.hasNurse) return false;
    if (filterNurse==="no" && d.hasNurse) return false;
    // For "all" session filter, show days with any capacity
    if (filterSession==="all" && d.freeSurgeries===0) return false;
    return true;
  });

  const upcoming = filtered.filter(d=>!d.isPast);
  const past = filtered.filter(d=>d.isPast);

  function sessionLabel(d) {
    const parts = [];
    if (filterSession==="am"||filterSession==="all") { if(d.amFree>0) parts.push(`AM: ${d.amFree} free`); }
    if (filterSession==="pm"||filterSession==="all") { if(d.pmFree>0) parts.push(`PM: ${d.pmFree} free`); }
    if (filterSession==="allday") { if(d.alldayFree>0) parts.push(`All day: ${d.alldayFree} free`); }
    if (filterSession==="after5") { if(d.after5Free>0) parts.push(`After 5: ${d.after5Free} free`); }
    if (parts.length===0) {
      if(d.amFree>0) parts.push(`AM: ${d.amFree}`);
      if(d.pmFree>0) parts.push(`PM: ${d.pmFree}`);
    }
    return parts.join(" · ");
  }

  function doPrint() {
    const printEl = document.getElementById("free-sessions-print");
    if (printEl) { printEl.style.display="block"; window.print(); setTimeout(()=>printEl.style.display="none",600); }
  }

  function doCSV() {
    const hdr = ["Date","Day","Site","AM Free","PM Free","All Day Free","After 5 Free","Spare Nurses","Dentists Working"];
    const rows = filtered.map(d=>[
      d.date, d.dow, site.name, d.amFree, d.pmFree, d.alldayFree, d.after5Free, d.spareNurses, d.totalClins
    ].join(","));
    const blob = new Blob([[hdr.join(","),...rows].join("\n")],{type:"text/csv"});
    const a = document.createElement("a"); a.href=URL.createObjectURL(blob);
    a.download=`free-sessions-${filterSite}-${filterMonth}.csv`; a.click();
  }

  const pillStyle = (active, color) => ({
    padding:"5px 14px", borderRadius:99, fontFamily:"inherit", fontSize:11, fontWeight:600,
    cursor:"pointer", border:`1px solid ${active?(color||C.teal):C.border}`,
    background:active?(color||C.teal)+"18":"transparent", color:active?(color||C.teal):C.mid,
  });

  return (
    <div>
      <style>{`@media print{body *{visibility:hidden!important}#free-sessions-print,#free-sessions-print *{visibility:visible!important}#free-sessions-print{position:fixed;top:0;left:0;width:100%;padding:16px;font-family:Arial,sans-serif}@page{size:A4;margin:12mm}}`}</style>

      {/* Hidden print area */}
      <div id="free-sessions-print" style={{display:"none"}}>
        <div style={{borderBottom:"2px solid #111",paddingBottom:6,marginBottom:10,display:"flex",justifyContent:"space-between"}}>
          <div>
            <div style={{fontSize:14,fontWeight:700}}>Free Sessions Report — {site?.name}</div>
            <div style={{fontSize:10,color:"rgba(255,255,255,0.5)"}}>
              {monthOpts.find(([v])=>v===filterMonth)?.[1]} ·
              {filterSession==="all"?" All sessions":filterSession==="am"?" AM clinics":filterSession==="pm"?" PM clinics":filterSession==="allday"?" All day":filterSession==="after5"?" After 5pm":""} ·
              {filterNurse==="yes"?" Nurse available":filterNurse==="no"?" No nurse":""}
            </div>
          </div>
          <div style={{fontSize:9,color:"rgba(255,255,255,0.35)"}}>Generated {new Date().toLocaleDateString("en-GB")} · Confidential</div>
        </div>
        <table style={{borderCollapse:"collapse",width:"100%",fontSize:9}}>
          <thead><tr style={{borderBottom:"1px solid #ccc"}}>
            {["Date","Day","AM Free","PM Free","All Day","After 5","Spare Nurses","Dentists"].map(h=>(
              <th key={h} style={{padding:"3px 6px",textAlign:"left",fontWeight:700,color:"#666",textTransform:"uppercase",fontSize:8}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {filtered.map((d,i)=>(
              <tr key={d.date} style={{background:i%2===0?"#fff":"#f9f9f9",borderBottom:"1px solid #eee"}}>
                <td style={{padding:"4px 6px",fontWeight:600}}>{d.date.slice(8)}/{d.date.slice(5,7)}/{d.date.slice(0,4)}</td>
                <td style={{padding:"4px 6px",color:"rgba(255,255,255,0.5)"}}>{d.dow}</td>
                <td style={{padding:"4px 6px",color:d.amFree>0?"#16a34a":"#999",fontWeight:d.amFree>0?700:400}}>{d.amFree>0?d.amFree+"✓":"—"}</td>
                <td style={{padding:"4px 6px",color:d.pmFree>0?"#16a34a":"#999",fontWeight:d.pmFree>0?700:400}}>{d.pmFree>0?d.pmFree+"✓":"—"}</td>
                <td style={{padding:"4px 6px",color:d.alldayFree>0?"#16a34a":"#999",fontWeight:d.alldayFree>0?700:400}}>{d.alldayFree>0?d.alldayFree+"✓":"—"}</td>
                <td style={{padding:"4px 6px",color:d.after5Free>0?"#16a34a":"#999",fontWeight:d.after5Free>0?700:400}}>{d.after5Free>0?d.after5Free+"✓":"—"}</td>
                <td style={{padding:"4px 6px",color:d.spareNurses>0?"#3aad98":"#dc2626",fontWeight:700}}>{d.spareNurses>0?d.spareNurses+" avail":"0"}</td>
                <td style={{padding:"4px 6px"}}>{d.totalClins}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{marginTop:8,fontSize:8,color:"rgba(255,255,255,0.35)"}}>
          {filtered.length} sessions found · {site?.name} · {surgeryCount} surgeries total · As of {new Date().toLocaleDateString("en-GB")}
        </div>
      </div>

      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:14,flexWrap:"wrap",gap:10}}>
        <div>
          <div style={{fontFamily:"'Instrument Serif',serif",fontSize:22,color:C.heading,fontWeight:400}}>🪑 Free Sessions</div>
          <div style={{fontSize:12,color:C.dim,marginTop:2}}>Search for available surgery slots across sites and months.</div>
        </div>
        <div style={{display:"flex",gap:6}}>
          <button onClick={doCSV} style={{...BTN(C.dim,true),fontSize:11,padding:"6px 10px"}}>⬇ CSV</button>
          <button onClick={doPrint} style={{...BTN(C.teal,true),fontSize:11,padding:"6px 10px"}}>🖨 PDF</button>
        </div>
      </div>

      {/* Filter bar */}
      <div style={{...CS,padding:"12px 16px",marginBottom:14}}>
        <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"flex-end"}}>

          {/* Site */}
          <div>
            <div style={{fontSize:9,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:5}}>Site</div>
            <div style={{display:"flex",gap:4}}>
              {Object.entries(SITES).map(([id,s])=>(
                <button key={id} onClick={()=>setFilterSite(id)} style={pillStyle(filterSite===id, s.color)}>{s.name}</button>
              ))}
            </div>
          </div>

          {/* Month */}
          <div>
            <div style={{fontSize:9,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:5}}>Month</div>
            <select value={filterMonth} onChange={e=>setFilterMonth(e.target.value)} style={{...IS,marginBottom:0,fontSize:12,width:"auto"}}>
              {monthOpts.map(([v,l])=><option key={v} value={v}>{l}</option>)}
            </select>
          </div>

          {/* Session type */}
          <div>
            <div style={{fontSize:9,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:5}}>Session</div>
            <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
              {[["all","Any"],["am","AM"],["pm","PM"],["allday","All day"],["after5","After 5"]].map(([v,l])=>(
                <button key={v} onClick={()=>setFilterSession(v)} style={pillStyle(filterSession===v)}>{l}</button>
              ))}
            </div>
          </div>

          {/* Nurse available */}
          <div>
            <div style={{fontSize:9,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:5}}>Nurse</div>
            <div style={{display:"flex",gap:4}}>
              {[["any","Any"],["yes","Available"],["no","None"]].map(([v,l])=>(
                <button key={v} onClick={()=>setFilterNurse(v)} style={pillStyle(filterNurse===v, v==="yes"?C.green:v==="no"?C.danger:undefined)}>{l}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Summary strip */}
      <div style={{display:"flex",gap:16,marginBottom:14,padding:"10px 16px",background:C.bg,borderRadius:10,flexWrap:"wrap",alignItems:"center"}}>
        <div><span style={{fontSize:20,fontWeight:700,color:C.teal}}>{upcoming.length}</span><span style={{fontSize:11,color:C.dim,marginLeft:6}}>upcoming slots</span></div>
        <div><span style={{fontSize:20,fontWeight:700,color:C.green}}>{upcoming.filter(d=>d.hasNurse).length}</span><span style={{fontSize:11,color:C.dim,marginLeft:6}}>with nurse available</span></div>
        <div><span style={{fontSize:20,fontWeight:700,color:C.warn}}>{upcoming.filter(d=>!d.hasNurse).length}</span><span style={{fontSize:11,color:C.dim,marginLeft:6}}>no nurse</span></div>
        <div style={{marginLeft:"auto",fontSize:11,color:C.dim}}>
          {site?.name} · {surgeryCount} surgeries · {monthOpts.find(([v])=>v===filterMonth)?.[1]}
        </div>
      </div>

      {/* Results list */}
      {upcoming.length===0 ? (
        <div style={{...CS,textAlign:"center",padding:40,color:C.dim}}>
          <div style={{fontSize:28,marginBottom:8}}>🔍</div>
          <div style={{fontSize:14,fontWeight:600,color:C.text}}>No sessions match your filters</div>
          <div style={{fontSize:12,marginTop:4}}>Try changing the month, session type, or site</div>
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {upcoming.map(d=>(
            <div key={d.date} style={{...CS,padding:"10px 16px",
              borderLeft:`4px solid ${d.hasNurse?C.green:C.warn}`,
              opacity:d.isPast?0.5:1}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:C.text}}>{d.dow} {d.date.slice(8)}/{d.date.slice(5,7)}</div>
                    <div style={{fontSize:11,color:C.dim,marginTop:1}}>{d.totalClins} dentist{d.totalClins!==1?"s":""} · {d.nurses.length} nurse{d.nurses.length!==1?"s":""} available</div>
                  </div>
                </div>

                {/* Session pills */}
                <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                  {d.amFree>0&&<span style={{padding:"3px 10px",borderRadius:99,background:C.teal+"15",color:C.teal,fontSize:11,fontWeight:700}}>🌅 AM: {d.amFree} free</span>}
                  {d.pmFree>0&&<span style={{padding:"3px 10px",borderRadius:99,background:C.brook+"15",color:C.brook,fontSize:11,fontWeight:700}}>🌆 PM: {d.pmFree} free</span>}
                  {d.alldayFree>0&&<span style={{padding:"3px 10px",borderRadius:99,background:C.warn+"15",color:C.warn,fontSize:11,fontWeight:700}}>☀️ All day: {d.alldayFree}</span>}
                  {d.after5Free>0&&<span style={{padding:"3px 10px",borderRadius:99,background:"#6d28d915",color:"#a78bfa",fontSize:11,fontWeight:700}}>🌙 After 5: {d.after5Free}</span>}
                  {d.hasNurse
                    ? <span style={{padding:"3px 10px",borderRadius:99,background:C.green+"15",color:C.green,fontSize:11,fontWeight:700}}>{d.spareNurses} nurse{d.spareNurses!==1?"s":""} spare</span>
                    : <span style={{padding:"3px 10px",borderRadius:99,background:C.warn+"15",color:C.warn,fontSize:11,fontWeight:700}}>No spare nurse</span>
                  }
                </div>
              </div>

              {/* Spare nurses names */}
              {d.hasNurse&&(
                <div style={{marginTop:5,fontSize:10,color:C.dim}}>
                  Spare: {d.nurses.slice(0, d.spareNurses).map(n=>n.name.split(" ")[0]).join(", ")}
                </div>
              )}
            </div>
          ))}

          {past.length>0&&(
            <div style={{marginTop:8,padding:"6px 12px",background:C.bg,borderRadius:8,fontSize:11,color:C.dim}}>
              + {past.length} past session{past.length!==1?"s":""} this month (hidden)
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HolidayKPI({state, siteId}) {
  const site = SITES[siteId]||{name:siteId,color:C.teal};
  const { staffKPI, siteKPI, paceTarget } = buildHolidayKPI(state);
  const sitePct = Math.round(paceTarget * 100);

  const siteStaff = staffKPI.filter(k => k.person.site === siteId);
  const thisSiteKPI = siteKPI.find(s => s.siteId === siteId);

  const ragColor = { green:C.green, amber:C.warn, red:C.danger };
  const ragBg    = { green:"rgba(0,229,160,0.07)", amber:"rgba(245,197,24,0.07)", red:"rgba(255,70,70,0.07)" };

  // Sort: red first, then amber, then green; alpha within
  const sorted = [...siteStaff].sort((a,b) => {
    const order = {red:0,amber:1,green:2};
    return order[a.rag] - order[b.rag] || a.person.name.localeCompare(b.person.name);
  });

  return (
    <div>
      {/* Site summary bar */}
      <div style={{...CS, marginBottom:16, padding:"14px 18px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,flexWrap:"wrap",gap:8}}>
          <div>
            <div style={{fontWeight:700,color:site?.color||C.teal,fontSize:14}}>{site?.name||siteId} — Holiday Pace</div>
            <div style={{fontSize:11,color:C.dim,marginTop:2}}>Expected {sitePct}% committed by today · {(thisSiteKPI?.totalCommitted||0).toFixed(1)}d of {(thisSiteKPI?.totalEntit||0).toFixed(1)}d total entitlement</div>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <span style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:28,color:ragColor[thisSiteKPI?.rag||"green"]}}>{thisSiteKPI?.pct}%</span>
            <Pill c={ragColor[thisSiteKPI?.rag||"green"]} t={`Target ${sitePct}%`} sm/>
          </div>
        </div>
        {/* Progress bar */}
        <div style={{height:8,background:C.border,borderRadius:99,overflow:"visible",position:"relative",marginBottom:4}}>
          <div style={{width:`${thisSiteKPI?.pct||0}%`,height:"100%",background:ragColor[thisSiteKPI?.rag||"green"],borderRadius:99,transition:"width 0.5s"}}/>
          {/* Target marker */}
          <div style={{position:"absolute",top:-3,left:`${sitePct}%`,width:2,height:14,background:C.dim,borderRadius:1}}/>
        </div>
        <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:C.dim,marginTop:2}}>
          <span>0%</span>
          <span style={{color:C.dim}}>▲ Today's target ({sitePct}%)</span>
          <span>100%</span>
        </div>
        {thisSiteKPI?.alerts>0&&(
          <div style={{marginTop:10,fontSize:12,color:C.warn}}>{thisSiteKPI.alerts} staff member{thisSiteKPI.alerts!==1?"s":""} need attention</div>
        )}
      </div>

      {/* Staff list */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:8}}>
        {sorted.map(({person,entit,totalCommitted,taken,bookedFuture,pending,expected,rag,ragLabel,ragNote})=>{
          const committed = totalCommitted||0;
          const pct = entit>0 ? Math.round((committed/entit)*100) : 0;
          const col = ragColor[rag];
          return (
            <div key={person.id} style={{...CS, background:ragBg[rag], borderColor:col+"33", padding:"12px 14px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                <div>
                  <div style={{fontWeight:700,color:C.text,fontSize:13}}>{person.name}</div>
                  <div style={{fontSize:10,color:C.dim,marginTop:1}}>{person.type||person.area} · {person.hrs}h/wk</div>
                </div>
                <Pill c={col} t={ragLabel} sm/>
              </div>
              <div style={{height:5,background:C.border,borderRadius:99,overflow:"hidden",marginBottom:6,position:"relative"}}>
                <div style={{width:`${Math.min(pct,100)}%`,height:"100%",background:col,borderRadius:99}}/>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:2}}>
                <span style={{color:C.dim}}>Taken</span>
                <span style={{color:C.warn}}>{taken||0}d</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:2}}>
                <span style={{color:C.dim}}>Booked (future)</span>
                <span style={{color:C.teal}}>{bookedFuture||0}d</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:4}}>
                <span style={{color:C.dim,fontWeight:600}}>Total committed</span>
                <span style={{fontWeight:700,color:col}}>{committed.toFixed(1)}d <span style={{color:C.dim,fontWeight:400}}>of {(entit||0).toFixed(1)}d</span></span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:ragNote?4:0}}>
                <span style={{color:C.dim}}>Expected by now</span>
                <span style={{color:C.mid}}>{(expected||0).toFixed(1)}d</span>
              </div>
              {pending>0&&(
                <div style={{fontSize:10,color:C.lime,marginBottom:4}}>+{pending}d pending approval</div>
              )}
              {ragNote&&<div style={{fontSize:10,color:col,marginTop:4,fontWeight:600}}>{ragNote}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ══════════════════════════════════
   DASHBOARD
══════════════════════════════════ */
/* ══════════════════════════════════
   ACTIVITY LOG
══════════════════════════════════ */
const LOG_ICONS = { leave:"→", absence:"·", rota:"✎", pin:"·", training:"·", role:"·" };
const LOG_COLORS = { leave:C.green, absence:C.danger, rota:C.teal, pin:C.warn, training:C.brook, role:C.brook };
const LOG_LABELS = { leave:"Leave", absence:"Absence", rota:"Rota", pin:"Security", training:"Training", role:"Role", all:"All" };

function ActivityLog({log,onClear}) {
  const [filter,setFilter] = useState("all");
  const [expanded,setExpanded] = useState(true);
  const types = ["all","leave","absence","rota","training","role","pin"];
  const visible = filter==="all" ? log : log.filter(e=>e.type===filter);

  function fmtTs(ts) {
    const d = new Date(ts);
    return d.toLocaleDateString("en-GB",{day:"numeric",month:"short"})
      + " · " + d.toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"});
  }

  return (
    <div style={{...CS,marginBottom:20}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:expanded?12:0,cursor:"pointer"}} onClick={()=>setExpanded(e=>!e)}>
        <div style={{fontSize:10,fontWeight:700,color:C.dim,letterSpacing:"0.08em",textTransform:"uppercase"}}>
          Activity Log · {log.length} entries
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <span style={{fontSize:11,color:C.dim}}>{expanded?"▲":"▼"}</span>
        </div>
      </div>

      {expanded&&(
        <>
          <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:12}}>
            {types.map(t=>(
              <button key={t} onClick={e=>{e.stopPropagation();setFilter(t);}} style={{padding:"3px 10px",borderRadius:99,fontFamily:"inherit",fontSize:10,fontWeight:600,cursor:"pointer",border:`1px solid ${filter===t?(LOG_COLORS[t]||C.teal):C.border}`,background:filter===t?(LOG_COLORS[t]||C.teal)+"18":"transparent",color:filter===t?(LOG_COLORS[t]||C.teal):C.dim,textTransform:"capitalize"}}>
                {t==="all"?`All (${log.length})`:(LOG_LABELS[t]||t)}
              </button>
            ))}
            <button onClick={e=>{e.stopPropagation();if(window.confirm("Clear activity log? This cannot be undone."))onClear();}} style={{marginLeft:"auto",padding:"3px 10px",borderRadius:99,fontFamily:"inherit",fontSize:10,fontWeight:600,cursor:"pointer",border:`1px solid ${C.border}`,background:"transparent",color:C.dim}}>
              Clear log
            </button>
          </div>

          {visible.length===0 && (
            <div style={{textAlign:"center",padding:"16px 0",color:C.dim,fontSize:12}}>No entries for this filter</div>
          )}

          <div style={{maxHeight:320,overflowY:"auto"}}>
            {visible.map((entry,i)=>{
              const col = LOG_COLORS[entry.type]||C.dim;
              return (
                <div key={entry.id} style={{display:"flex",gap:10,padding:"8px 0",borderBottom:i<visible.length-1?`1px solid ${C.border2}`:"none",alignItems:"flex-start"}}>
                  <div style={{width:28,height:28,borderRadius:8,background:col+"15",border:`1px solid ${col}22`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,flexShrink:0,marginTop:1}}>
                    {LOG_ICONS[entry.type]||"•"}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,color:C.text,fontWeight:500,lineHeight:1.4}}>{entry.msg}</div>
                    <div style={{fontSize:10,color:C.dim,marginTop:2,display:"flex",gap:8}}>
                      <span style={{color:col,fontWeight:600}}>{entry.actor}</span>
                      <span>{fmtTs(entry.ts)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* ══════════════════════════════════
   PRINCIPAL LEAVE MODAL
══════════════════════════════════ */
function PrincipalLeaveModal({user,state,set,onClose}) {
  const PRINCIPAL_ID = user?.staffId;
  const existing = (PRINCIPAL_ID && state.leave[PRINCIPAL_ID])||[];
  const [sel,setSel]         = useState([]);
  const [done,setDone]       = useState(false);
  const [pickMode,setPickMode] = useState("range");
  const [rangeFrom,setRangeFrom] = useState("");
  const [rangeTo,setRangeTo]     = useState("");

  const tog = date => isXmas(date) ? null : setSel(s=>s.includes(date)?s.filter(d=>d!==date):[...s,date]);

  function applyRange(from,to) {
    if (!from||!to||from>to) return;
    const days=[];
    const cur=new Date(from+"T12:00:00");
    const end=new Date(to+"T12:00:00");
    while(cur<=end){
      const d=cur.toISOString().slice(0,10);
      const dow=cur.getDay();
      if(dow!==0&&dow!==6&&!UK_BH_2026[d]&&!isXmas(d)) days.push(d);
      cur.setDate(cur.getDate()+1);
    }
    setSel(days);
  }

  function submit() {
    if (!sel.length || !PRINCIPAL_ID) return;
    const rid="req"+Date.now();
    set(s=>{
      const ex=s.leave[PRINCIPAL_ID]||[];
      const newDays=sel.map(d=>({id:"l"+Math.random().toString(36).slice(2),requestId:rid,date:d,status:"approved"}));
      const who=user?.name||"Principal";
      return {...s,leave:{...s.leave,[PRINCIPAL_ID]:[...ex,...newDays]},...addLog(s,who,"leave",`✈️ ${who} booked ${sel.length}d leave — ${sel.sort()[0]}${sel.length>1?" + "+(sel.length-1)+" more":""}`)};
    });
    setDone(true);
  }

  const approved=existing.filter(l=>l.status==="approved");
  const upcoming=approved.filter(l=>l.date>=new Date().toISOString().split("T")[0]).sort((a,b)=>a.date.localeCompare(b.date));

  if (done) return (
    <Overlay onClose={onClose}>
      <div style={{textAlign:"center",padding:"24px 0"}}>
        <div style={{fontSize:48,marginBottom:12}}></div>
        <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading,marginBottom:6}}>Leave booked</div>
        <div style={{fontSize:13,color:C.dim,marginBottom:4}}>{sel.length} day{sel.length!==1?"s":""} logged & approved</div>
        <div style={{fontSize:12,color:C.teal,marginBottom:20}}>Visible to all staff on the rota</div>
        <button onClick={onClose} style={{...BTN(C.teal),padding:"11px 28px"}}>Done</button>
      </div>
    </Overlay>
  );

  return (
    <Overlay onClose={onClose}>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:4}}>
        <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading}}>My Leave</div>
        <Pill c={C.teal} t="Self-approved" sm/>
      </div>
      <div style={{fontSize:12,color:C.dim,marginBottom:16}}>Your leave is instantly approved and visible to the whole team.</div>

      {upcoming.length>0&&(
        <div style={{background:C.tealPale,border:`1px solid ${C.tealBorder}`,borderRadius:10,padding:"10px 14px",marginBottom:16}}>
          <div style={{fontSize:10,fontWeight:700,color:C.teal,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Upcoming booked leave</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {upcoming.slice(0,8).map(l=>(
              <span key={l.id} style={{fontSize:11,color:C.teal,background:C.surface,border:`1px solid ${C.tealBorder}`,borderRadius:6,padding:"2px 8px",fontWeight:600}}>
                {CAL.days.find(d=>d.date===l.date)?.label||l.date}
              </span>
            ))}
            {upcoming.length>8&&<span style={{fontSize:11,color:C.dim}}>+{upcoming.length-8} more</span>}
          </div>
        </div>
      )}

      {/* Pick mode toggle */}
      <div style={{display:"flex",gap:6,marginBottom:14}}>
        {[["range","Date range"],["individual","Pick days"]].map(([m,label])=>(
          <button key={m} onClick={()=>{setPickMode(m);setSel([]);setRangeFrom("");setRangeTo("");}}
            style={{flex:1,padding:"8px",borderRadius:8,border:`1.5px solid ${pickMode===m?C.teal:C.border}`,background:pickMode===m?C.tealPale:"transparent",color:pickMode===m?C.teal:C.mid,fontFamily:"inherit",fontSize:12,fontWeight:600,cursor:"pointer"}}>
            {label}
          </button>
        ))}
      </div>

      {/* Range picker */}
      {pickMode==="range"&&(
        <div style={{marginBottom:14}}>
          <div style={{display:"flex",gap:10,marginBottom:10}}>
            <div style={{flex:1}}>
              <label style={LB}>From</label>
              <input type="date" value={rangeFrom} onChange={e=>{setRangeFrom(e.target.value);if(rangeTo)applyRange(e.target.value,rangeTo);}} style={IS}/>
            </div>
            <div style={{flex:1}}>
              <label style={LB}>To</label>
              <input type="date" value={rangeTo} min={rangeFrom||undefined} onChange={e=>{setRangeTo(e.target.value);if(rangeFrom)applyRange(rangeFrom,e.target.value);}} style={IS}/>
            </div>
          </div>
          {sel.length>0&&(
            <div style={{background:C.tealPale,border:`1px solid ${C.tealBorder}`,borderRadius:8,padding:"10px 14px",fontSize:12,color:C.teal}}>
              <strong>{sel.length} working day{sel.length!==1?"s":""}</strong> · excludes weekends &amp; bank holidays
              <div style={{fontSize:11,color:C.mid,marginTop:4,display:"flex",flexWrap:"wrap",gap:4}}>
                {sel.slice(0,5).map(d=><span key={d} style={{background:"#181626",border:`1px solid ${C.tealBorder}`,borderRadius:4,padding:"1px 6px"}}>{CAL.days.find(x=>x.date===d)?.label.split(" ").slice(0,2).join(" ")||d}</span>)}
                {sel.length>5&&<span style={{color:C.dim}}>+{sel.length-5} more</span>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Individual day picker */}
      {pickMode==="individual"&&(
        <div>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>
            Select dates — {sel.length} selected
          </div>
          <div style={{maxHeight:260,overflowY:"auto",marginBottom:16}}>
            {CAL.weeks.map(wk=>(
              <div key={wk.start} style={{marginBottom:10}}>
                <div style={{fontSize:9,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>
                  {wk.days[0]?.label.split(" ").slice(1).join(" ")}
                </div>
                <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                  {wk.days.map(d=>{
                    const bh=!!d.bh,xm=isXmas(d.date),on=sel.includes(d.date);
                    const already=existing.some(l=>l.date===d.date&&l.status==="approved");
                    return (
                      <button key={d.date} onClick={()=>tog(d.date)} style={{padding:"4px 8px",borderRadius:7,fontFamily:"inherit",fontSize:10,fontWeight:600,cursor:bh||xm||already?"default":"pointer",border:`1px solid ${xm?"#fecaca":bh?"#fed7aa":already?C.tealBorder:on?C.teal:C.border}`,background:xm?"#fef2f2":bh?"#fff7ed":already?C.tealPale:on?C.tealPale:"transparent",color:xm?C.danger:bh?C.warn:already?C.teal:on?C.teal:C.soft,opacity:already?0.6:1}}>
                        {d.label.split(" ").slice(0,2).join(" ")}{bh?" BH":xm?" 🎄":already?" ✓":on?" ✓":""}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{display:"flex",gap:8}}>
        <button onClick={submit} disabled={!sel.length} style={{...BTN(sel.length?C.teal:C.muted),flex:1,padding:"11px 0",fontSize:13}}>
          Book {sel.length} day{sel.length!==1?"s":""}
        </button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}

/* ══════════════════════════════════
   INTELLIGENCE ALERTS
   Partners + site managers only
══════════════════════════════════ */
/* ══════════════════════════════════
   MOBILE EMERGENCY COVER SCREEN
   "Pulled over on the M60" mode
══════════════════════════════════ */
function MobileEmergencyCover({state,set,user,onClose}) {
  const [step,setStep]   = useState("who");   // who → date → cover
  const [person,setPerson] = useState(null);
  const [date,setDate]   = useState(new Date().toISOString().split("T")[0]);
  const [confirmed,setConfirmed] = useState(false);
  const allNurses = (state.nurses||NURSES_BASE).filter(n=>!n.maternity&&!n.archived);
  const allStaff  = [...allNurses,...RECS_BASE];

  const liveDay = CAL.days.find(d=>d.date>=date&&!d.bh)||CAL.days[0];
  const liveDate = liveDay?.date||date;

  // Tiered cover options
  function getCoverTiers(p) {
    if (!p) return null;
    const js   = new Date(liveDate).getDay();
    const dow  = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][js];

    // Tier 1: same site, available
    const t1 = allNurses.filter(n=>{
      if (n.id===p.id||n.site!==p.site||n.maternity) return false;
      if (isAbsent(state,n.id,liveDate)) return false;
      if (n.noFri&&dow==="Fri") return false;
      if (n.workDays&&!n.workDays.includes(dow)) return false;
      return true;
    });
    const dr = buildDayRota(liveDate,p.site,allNurses.filter(n=>n.site===p.site),state);
    const spares = dr.spares||[];

    // Tier 2: other sites (flexible or part-time)
    const t2 = allNurses.filter(n=>{
      if (n.site===p.site||n.maternity) return false;
      if (isAbsent(state,n.id,liveDate)) return false;
      if (n.noFri&&dow==="Fri") return false;
      if (n.workDays&&!n.workDays.includes(dow)) return false;
      return n.flexible||n.hrs<30;
    });

    // Tier 3: Omni Bank — match role explicitly
    const isRecType = p.type==="Reception"||p.type==="TCO"||p.type==="Manager";
    const t3 = (state.bankStaff||[]).filter(b=>{
      if (b.status==="dnr"||b.inactive) return false;
      const role = (b.role||"").toLowerCase();
      if (isRecType) return role==="receptionist"||role==="reception"||role==="tco";
      return role==="nurse"||role==="dental nurse"||role==="qualified nurse"||role==="trainee nurse";
    });

    // Tier 4: agencies
    const agencies = state.agencies||[];

    return {spares,t1,t2,t3,agencies};
  }

  function confirmAbsence() {
    if (!person||!liveDate) return;
    set(s=>({...s,sick:[...s.sick,{id:"s"+Date.now(),staffId:person.id,name:person.name,site:person.site,role:"nurse",date:liveDate,days:1,contact:"called",reason:"illness",certified:false,rtw:false,notes:"Logged via Emergency Cover"}]}));
    setConfirmed(true);
  }

  const tiers = step==="cover"&&person ? getCoverTiers(person) : null;

  const BIG_BTN = ({onClick,children,color=C.teal,ghost=false}) => (
    <button onClick={onClick} style={{width:"100%",padding:"18px 16px",borderRadius:14,border:`1.5px solid ${ghost?color+"55":color}`,background:ghost?"transparent":color,color:ghost?color:"#fff",fontFamily:"inherit",fontSize:16,fontWeight:700,cursor:"pointer",marginBottom:10,textAlign:"left",display:"flex",alignItems:"center",gap:10}}>
      {children}
    </button>
  );

  return (
    <div style={{position:"fixed",inset:0,background:C.bg,zIndex:900,display:"flex",flexDirection:"column",maxWidth:480,margin:"0 auto"}}>
      {/* Header */}
      <div style={{background:C.danger,padding:"14px 18px",display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          <div>
            <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:18,color:"#fff"}}>Emergency Cover</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.7)"}}>Omni will find cover fast</div>
          </div>
        </div>
        <button onClick={onClose} style={{background:"rgba(255,255,255,0.2)",border:"none",color:"#fff",fontSize:20,borderRadius:8,padding:"4px 10px",cursor:"pointer"}}>X</button>
      </div>

      {/* Steps */}
      <div style={{flex:1,overflowY:"auto",padding:"16px 16px 24px"}}>

        {/* STEP 1: Who */}
        {step==="who"&&(
          <div>
            <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Who isn't coming in?</div>
            <div style={{fontSize:13,color:C.dim,marginBottom:16}}>Tap their name</div>
            {Object.entries(SITES).map(([siteId,site])=>{
              const siteStaff = allStaff.filter(s=>s.site===siteId);
              if (!siteStaff.length) return null;
              return (
                <div key={siteId} style={{marginBottom:16}}>
                  <div style={{fontSize:10,fontWeight:700,color:site.color,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>{site.name}</div>
                  {siteStaff.map(s=>(
                    <BIG_BTN key={s.id} onClick={()=>{setPerson(s);setStep("date");}} color={site.color}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"/></svg>
                      <div>
                        <div>{s.name}</div>
                        <div style={{fontSize:12,opacity:0.7,fontWeight:400}}>{s.type||s.area||"Nurse"}</div>
                      </div>
                    </BIG_BTN>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* STEP 2: Date confirm */}
        {step==="date"&&person&&(
          <div>
            <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Which date?</div>
            <div style={{fontSize:13,color:C.dim,marginBottom:16}}>{person.name} · {SITES[person.site]?.name}</div>
            <div style={{marginBottom:16}}>
              <label style={LB}>Date</label>
              <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={{...IS,fontSize:16,padding:"14px"}}/>
            </div>
            <BIG_BTN onClick={()=>setStep("cover")} color={C.danger}>
              Find cover for {person.name.split(" ")[0]}
            </BIG_BTN>
            <BIG_BTN onClick={()=>setStep("who")} color={C.dim} ghost>← Back</BIG_BTN>
          </div>
        )}

        {/* STEP 3: Cover options */}
        {step==="cover"&&person&&tiers&&!confirmed&&(
          <div>
            <div style={{background:"rgba(245,197,24,0.08)",border:`1px solid ${C.warn}44`,borderRadius:12,padding:12,marginBottom:16,display:"flex",gap:10,alignItems:"center"}}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"/></svg>
              <div>
                <div style={{fontWeight:700,color:C.warn,fontSize:14}}>{person.name}</div>
                <div style={{fontSize:12,color:C.mid}}>{SITES[person.site]?.name} · {liveDay?.label||liveDate}</div>
              </div>
            </div>

            {/* Spares — already covered */}
            {tiers.spares.length>0&&(
              <div style={{background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:12,padding:14,marginBottom:12}}>
                <div style={{fontWeight:700,color:C.green,fontSize:14,marginBottom:6}}>Already covered — spare nurses on site</div>
                {tiers.spares.map(n=>(
                  <div key={n.id} style={{fontSize:14,color:C.text,fontWeight:600,padding:"4px 0"}}>+ {n.name}</div>
                ))}
              </div>
            )}

            {/* Tier 1: same site */}
            {tiers.t1.length>0&&(
              <div style={{marginBottom:14}}>
                <div style={{fontSize:11,fontWeight:700,color:C.teal,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Same site — call first</div>
                {tiers.t1.slice(0,4).map((n,i)=>{
                  const st = STATUS[n.status];
                  return (
                    <div key={n.id} style={{...CS,marginBottom:6,padding:"12px 14px",borderLeft:`3px solid ${i===0?C.green:C.teal}`}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div>
                          <div style={{fontWeight:700,fontSize:14,color:C.text}}>{i===0?"":""}{n.name}</div>
                          <div style={{fontSize:11,color:C.dim,marginTop:2}}>{n.area} · {st?.label}</div>
                        </div>
                        <div style={{display:"flex",gap:4,flexWrap:"wrap",justifyContent:"flex-end"}}>
                          {n.flexible&&<Pill c={C.lime} t="Flexible" sm/>}
                          {st&&<Pill c={st.color} t={st.label} sm/>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Tier 2: other sites */}
            {tiers.t2.length>0&&(
              <div style={{marginBottom:14}}>
                <div style={{fontSize:11,fontWeight:700,color:C.brook,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>🔄 Other sites — flexible staff</div>
                {tiers.t2.slice(0,3).map(n=>{
                  const st = STATUS[n.status];
                  return (
                    <div key={n.id} style={{...CS,marginBottom:6,padding:"12px 14px",borderLeft:`3px solid ${C.brook}`}}>
                      <div style={{fontWeight:700,fontSize:13,color:C.text}}>{n.name}</div>
                      <div style={{fontSize:11,color:C.mid,marginTop:2}}>{SITES[n.site]?.name} · {n.area} · {n.hrs}h contract</div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Tier 3: Omni Bank */}
            {tiers.t3.length>0&&(
              <div style={{marginBottom:14}}>
                <div style={{fontSize:11,fontWeight:700,color:C.warn,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Omni Bank</div>
                {tiers.t3.slice(0,3).map(b=>{
                  const rat = b.rating ? LOCUM_RATING[b.rating] : null;
                  return (
                    <div key={b.id} style={{...CS,marginBottom:6,padding:"12px 14px",borderLeft:`3px solid ${rat?.color||C.warn}`}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div>
                          <div style={{fontWeight:700,fontSize:13,color:C.text}}>{b.name}</div>
                          <div style={{fontSize:11,color:C.dim,marginTop:2}}>{b.role} · {b.shiftCount||0} shifts done</div>
                        </div>
                        {rat&&<Pill c={rat.color} t={rat.short} sm/>}
                      </div>
                      {b.phone&&<div style={{fontSize:12,color:C.teal,marginTop:4}}>{b.phone}</div>}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Tier 4: Agencies */}
            {tiers.agencies.length>0&&(
              <div style={{marginBottom:14}}>
                <div style={{fontSize:11,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Agencies — last resort</div>
                {tiers.agencies.map(ag=>{
                  const agLocs = ((state.agencyLocums||{})[ag.id]||[])
                    .filter(l=>l.rating!=="dnr"&&!l.inactive)
                    .sort((a,b)=>(LOCUM_RATING[b.rating]?.score??3.5)-(LOCUM_RATING[a.rating]?.score??3.5))
                    .slice(0,2);
                  return (
                    <div key={ag.id} style={{...CS,marginBottom:6,padding:"12px 14px"}}>
                      <div style={{fontWeight:700,fontSize:13,color:C.text,marginBottom:4}}>{ag.name}</div>
                      {ag.phone&&<div style={{fontSize:13,color:C.teal,marginBottom:4}}>{ag.phone}</div>}
                      {ag.email&&<div style={{fontSize:12,color:C.mid,marginBottom:4}}>{ag.email}</div>}
                      <div style={{fontSize:11,color:C.mid}}>Nurse: £{ag.nurseRate}/hr</div>
                      {agLocs.length>0&&(
                        <div style={{marginTop:6}}>
                          <div style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>Known locums</div>
                          {agLocs.map(l=>{
                            const r = l.rating ? LOCUM_RATING[l.rating] : null;
                            return (
                              <div key={l.id} style={{fontSize:11,color:C.text,padding:"3px 0",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                                <span>{l.name}</span>
                                {r&&<Pill c={r.color} t={r.short} sm/>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {tiers.t1.length===0&&tiers.t2.length===0&&tiers.t3.length===0&&tiers.spares.length===0&&(
              <div style={{background:"rgba(255,70,70,0.08)",border:"1px solid rgba(255,70,70,0.2)",borderRadius:12,padding:14,marginBottom:14}}>
                <div style={{fontWeight:700,color:C.danger,fontSize:14}}>No internal cover available</div>
                <div style={{fontSize:12,color:C.mid,marginTop:4}}>Call agencies above or consider running with reduced capacity</div>
              </div>
            )}

            <div style={{borderTop:`1px solid ${C.border}`,paddingTop:14,marginTop:4}}>
              <BIG_BTN onClick={confirmAbsence} color={C.danger}>
                <span style={{fontSize:20}}></span> Log {person.name.split(" ")[0]} as absent
              </BIG_BTN>
              <BIG_BTN onClick={()=>setStep("date")} color={C.dim} ghost>← Back</BIG_BTN>
            </div>
          </div>
        )}

        {/* Confirmed */}
        {confirmed&&(
          <div style={{textAlign:"center",padding:"40px 16px"}}>
            <div style={{fontSize:56,marginBottom:16,display:"flex",justifyContent:"center"}}><svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg></div>
            <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:24,color:C.heading,marginBottom:8}}>Absence logged</div>
            <div style={{fontSize:14,color:C.mid,marginBottom:24}}>{person?.name} · {liveDay?.label||liveDate}<br/>Rota updated automatically</div>
            <BIG_BTN onClick={onClose} color={C.teal}><span style={{fontSize:20}}>✓</span> Done</BIG_BTN>
          </div>
        )}
      </div>
    </div>
  );
}

function IntelligenceAlerts({state,set,user,onNavigate}) {
  const role = user?.role||"staff";
  if (!["principal","manager","managerLite"].includes(role)) return null;

  const absAlerts  = analyseAbsencePatterns(state).filter(a=>a.visibleTo.includes(role));
  const swapAlerts = analyseSwapPatterns(state).filter(a=>a.visibleTo.includes(role));
  const hireAlerts = analyseHiringNeed(state).filter(a=>a.visibleTo.includes(role));
  const all = [...hireAlerts,...absAlerts,...swapAlerts];
  if (all.length===0) return null;

  function dismiss(id) {
    set(s=>({
      ...s,
      absenceAlerts:[...(s.absenceAlerts||[]),id],
      hiringAlerts:[...(s.hiringAlerts||[]),id],
    }));
  }

  const ACTION_BTN = ({a}) => {
    if (a.action==="welfare")       return <button onClick={()=>onNavigate&&onNavigate("directory")} style={{fontSize:10,padding:"2px 8px",borderRadius:6,border:`1px solid ${a.color}44`,background:"transparent",color:a.color,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>View staff →</button>;
    if (a.action==="rota_review")   return <button onClick={()=>onNavigate&&onNavigate("planning",a.site)} style={{fontSize:10,padding:"2px 8px",borderRadius:6,border:`1px solid ${a.color}44`,background:"transparent",color:a.color,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>View rota →</button>;
    if (a.action==="consider_hiring") return <span style={{fontSize:10,color:C.teal,fontWeight:600}}>Consider advertising the role</span>;
    if (a.action==="principal_review") return <span style={{fontSize:10,color:C.danger,fontWeight:600}}>Needs principal review</span>;
    return null;
  };

  return (
    <div style={{marginBottom:20}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
        <span style={{fontSize:14}}></span>
        <span style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em"}}>Omni Intelligence — {all.length} alert{all.length!==1?"s":""}</span>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:6}}>
        {all.map(a=>(
          <div key={a.id} style={{background:a.color+"0d",border:`1px solid ${a.color}33`,borderLeft:`3px solid ${a.color}`,borderRadius:10,padding:"10px 14px",display:"flex",alignItems:"flex-start",gap:10}}>
            <span style={{fontSize:18,flexShrink:0,lineHeight:1.3}}>{a.icon}</span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12,fontWeight:600,color:C.text,marginBottom:4,lineHeight:1.4}}>{a.msg}</div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
                {a.site&&<span style={{fontSize:10,color:SITES[a.site]?.color||C.dim,fontWeight:600}}>{SITES[a.site]?.name}</span>}
                {a.sensitive&&<span style={{fontSize:10,color:C.danger,fontWeight:700,background:"rgba(255,70,70,0.08)",border:"1px solid rgba(255,70,70,0.2)",borderRadius:4,padding:"1px 6px"}}> Sensitive</span>}
                <ACTION_BTN a={a}/>
              </div>
            </div>
            <button onClick={()=>dismiss(a.id)} title="Dismiss" style={{flexShrink:0,background:"transparent",border:"none",color:C.muted,fontSize:18,cursor:"pointer",lineHeight:1,padding:"0 2px"}}>×</button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════
   SHIFT SWAP REQUEST
══════════════════════════════════ */
function ShiftSwapModal({person, state, set, onClose}) {
  const allNurses = (state.nurses||NURSES_BASE).filter(n=>n.site===person.site&&!n.maternity&&!n.archived&&n.id!==person.id);
  const [f,setF] = useState({
    originalDate:"",
    originalDow:"Mon",
    wantDate:"",
    swapWithId:"",
    reason:"",
  });
  const s=(k,v)=>setF(p=>({...p,[k]:v}));

  function submit() {
    if (!f.originalDate||!f.wantDate) return;
    const swap = {
      id:"sw"+Date.now(),
      requesterId:person.id, requesterName:person.name, site:person.site,
      originalDate:f.originalDate, originalDow:f.originalDow,
      wantDate:f.wantDate, swapWithId:f.swapWithId||null,
      reason:f.reason, status:"pending", createdAt:new Date().toISOString(),
    };
    set(s=>({...s,swapRequests:[...(s.swapRequests||[]),swap]}));
    onClose();
  }

  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Request Shift Swap</div>
      <div style={{fontSize:13,color:C.dim,marginBottom:20}}>{person.name}</div>
      <div style={{marginBottom:12}}>
        <label style={LB}>Date I want to swap away from</label>
        <input type="date" value={f.originalDate} onChange={e=>{
          const dow=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][new Date(e.target.value).getDay()];
          s("originalDate",e.target.value); s("originalDow",dow);
        }} style={IS}/>
      </div>
      <div style={{marginBottom:12}}>
        <label style={LB}>Date I want to work instead</label>
        <input type="date" value={f.wantDate} onChange={e=>s("wantDate",e.target.value)} style={IS}/>
      </div>
      <div style={{marginBottom:12}}>
        <label style={LB}>Swap with (optional)</label>
        <select value={f.swapWithId} onChange={e=>s("swapWithId",e.target.value)} style={IS}>
          <option value="">No specific person — manager to arrange</option>
          {allNurses.map(n=><option key={n.id} value={n.id}>{n.name}</option>)}
        </select>
      </div>
      <div style={{marginBottom:20}}>
        <label style={LB}>Reason (optional)</label>
        <input value={f.reason} onChange={e=>s("reason",e.target.value)} placeholder="e.g. childcare, medical appointment" style={IS}/>
      </div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={submit} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Submit request</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}

/* ══════════════════════════════════
   AGENCY LOCUM ROSTER
══════════════════════════════════ */
function AgencyLocumCard({loc,onRate,onEdit,onRemove,canRate}) {
  const [rateM,setRateM] = useState(false);
  const rating = loc.rating ? LOCUM_RATING[loc.rating] : null;
  const lastRating = loc.ratingHistory?.slice(-1)[0];
  return (
    <div style={{...CS,padding:"10px 12px",borderLeft:rating?`3px solid ${rating.color}`:`3px solid ${C.border}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:2}}>
        <div style={{fontWeight:700,fontSize:12,color:loc.rating==="dnr"?C.danger:C.text,textDecoration:loc.inactive?"line-through":undefined}}>{loc.name}</div>
        {rating&&<span style={{fontSize:9,fontWeight:700,color:rating.color,background:rating.color+"18",borderRadius:5,padding:"2px 6px"}}>{rating.short}</span>}
      </div>
      <div style={{fontSize:10,color:C.mid,marginBottom:3,textTransform:"capitalize"}}>{loc.role}{loc.shiftCount>0?` · ${loc.shiftCount} shift${loc.shiftCount!==1?"s":""}`:" · no shifts yet"}</div>
      {loc.flags?.length>0&&<div style={{fontSize:9,color:C.dim,marginBottom:3}}>{loc.flags.slice(0,2).join(" · ")}{loc.flags.length>2?` +${loc.flags.length-2}`:""}</div>}
      {lastRating?.note&&<div style={{fontSize:9,color:C.dim,fontStyle:"italic",marginBottom:3}}>"{lastRating.note.slice(0,40)}{lastRating.note.length>40?"…":""}"</div>}
      {loc.inactive&&<div style={{fontSize:9,color:C.muted}}>⚫ Inactive</div>}
      <div style={{display:"flex",gap:4,marginTop:6}}>
        {canRate&&<button onClick={()=>setRateM(true)} style={{...BTN(C.warn,true),fontSize:9,padding:"2px 7px"}}>★ Rate</button>}
        <button onClick={onEdit} style={{...BTN(C.dim,true),fontSize:9,padding:"2px 7px"}}>Edit</button>
        <button onClick={onRemove} style={{...BTN(C.danger,true),fontSize:9,padding:"2px 7px"}}>X</button>
      </div>
      {rateM&&<LocumRateModal person={loc} onSave={r=>{onRate(loc.id,r);setRateM(false);}} onClose={()=>setRateM(false)}/>}
    </div>
  );
}

function AgencyLocumRoster({ag,state,set,user}) {
  const agLocums = (state.agencyLocums||{})[ag.id]||[];
  const [addM,setAddM]     = useState(false);
  const [editLoc,setEditLoc] = useState(null);
  const [newName,setNewName] = useState("");
  const [newRole,setNewRole] = useState("nurse");
  const canRate = ["principal","manager","managerLite","headNurse"].includes(user?.role);

  // Cross-agency duplicate detection
  const allAgencyLocums = Object.values(state.agencyLocums||{}).flat();
  const similar = newName.trim().length>2
    ? allAgencyLocums.filter(l=>l.agencyId!==ag.id&&l.name.toLowerCase().includes(newName.trim().toLowerCase().slice(0,5)))
    : [];

  function addLocum() {
    if (!newName.trim()) return;
    const loc = {id:"aloc"+Date.now(),agencyId:ag.id,name:newName.trim(),role:newRole,shiftCount:0};
    set(s=>({...s,agencyLocums:{...(s.agencyLocums||{}),[ag.id]:[...((s.agencyLocums||{})[ag.id]||[]),loc]}}));
    setNewName(""); setAddM(false);
  }
  function rateLocum(locId,r) {
    set(s=>({...s,agencyLocums:{...(s.agencyLocums||{}),[ag.id]:((s.agencyLocums||{})[ag.id]||[]).map(l=>l.id!==locId?l:{
      ...l,rating:r.rating,flags:r.flags,ratingNote:r.note,
      ratingHistory:[...(l.ratingHistory||[]),{...r,date:new Date().toLocaleDateString("en-GB")}],
      shiftCount:(l.shiftCount||0)+1,
    })}}));
  }
  function removeLocum(locId) {
    set(s=>({...s,agencyLocums:{...(s.agencyLocums||{}),[ag.id]:((s.agencyLocums||{})[ag.id]||[]).filter(l=>l.id!==locId)}}));
  }
  function saveEdit(updates) {
    set(s=>({...s,agencyLocums:{...(s.agencyLocums||{}),[ag.id]:((s.agencyLocums||{})[ag.id]||[]).map(l=>l.id!==updates.id?l:{...l,...updates})}}));
    setEditLoc(null);
  }

  const sorted = [...agLocums].sort((a,b)=>(LOCUM_RATING[b.rating]?.score??3.5)-(LOCUM_RATING[a.rating]?.score??3.5));
  const active   = sorted.filter(l=>!l.inactive);
  const inactive = sorted.filter(l=>l.inactive);

  return (
    <div style={{borderTop:`1px solid ${C.border2}`,marginTop:10,paddingTop:10}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em"}}>
          Roster ({active.length} active{inactive.length>0?`, ${inactive.length} inactive`:""})
        </div>
        <button onClick={()=>setAddM(v=>!v)} style={{...BTN(C.teal,true),fontSize:10,padding:"3px 10px"}}>+ Add</button>
      </div>

      {addM&&(
        <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:10,padding:12,marginBottom:10}}>
          <div style={{marginBottom:8}}>
            <label style={LB}>Full name</label>
            <input value={newName} onChange={e=>setNewName(e.target.value)} placeholder="e.g. Sarah Jones" style={IS}/>
            {similar.length>0&&(
              <div style={{background:"rgba(245,197,24,0.08)",border:`1px solid ${C.warn}44`,borderRadius:6,padding:"6px 10px",marginTop:4,fontSize:11,color:C.warn}}>
                Similar name at another agency: {similar.map(l=>l.name).join(", ")} — same person?
              </div>
            )}
          </div>
          <div style={{marginBottom:10}}>
            <label style={LB}>Role</label>
            <select value={newRole} onChange={e=>setNewRole(e.target.value)} style={IS}>
              <option value="nurse">Nurse</option>
              <option value="receptionist">Receptionist</option>
              <option value="therapist">Therapist</option>
              <option value="TCO">TCO</option>
            </select>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={addLocum} style={{...BTN(C.teal),flex:1,padding:"9px 0",fontSize:12}}>Add to roster</button>
            <button onClick={()=>{setAddM(false);setNewName("");}} style={{...BTN(C.dim,true),fontSize:12}}>Cancel</button>
          </div>
        </div>
      )}

      {agLocums.length===0&&!addM&&<div style={{fontSize:11,color:C.muted,marginBottom:8}}>No locums added yet — build your roster here</div>}

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(175px,1fr))",gap:6}}>
        {active.map(loc=><AgencyLocumCard key={loc.id} loc={loc} onRate={rateLocum} onEdit={()=>setEditLoc({...loc})} onRemove={()=>removeLocum(loc.id)} canRate={canRate}/>)}
      </div>

      {inactive.length>0&&(
        <div style={{marginTop:8}}>
          <div style={{fontSize:9,color:C.muted,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>Inactive / left agency</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(175px,1fr))",gap:6}}>
            {inactive.map(loc=><AgencyLocumCard key={loc.id} loc={loc} onRate={rateLocum} onEdit={()=>setEditLoc({...loc})} onRemove={()=>removeLocum(loc.id)} canRate={canRate}/>)}
          </div>
        </div>
      )}

      {editLoc&&(
        <Overlay onClose={()=>setEditLoc(null)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:18,color:C.heading,marginBottom:16}}>Edit — {editLoc.name}</div>
          {[{l:"Name",k:"name",t:"text"},{l:"Phone",k:"phone",t:"tel"},{l:"Notes",k:"notes",t:"text"}].map(({l,k,t})=>(
            <div key={k} style={{marginBottom:10}}>
              <label style={LB}>{l}</label>
              <input type={t} value={editLoc[k]||""} onChange={e=>setEditLoc(p=>({...p,[k]:e.target.value}))} style={IS}/>
            </div>
          ))}
          <div style={{marginBottom:10}}>
            <label style={LB}>Role</label>
            <select value={editLoc.role||"nurse"} onChange={e=>setEditLoc(p=>({...p,role:e.target.value}))} style={IS}>
              <option value="nurse">Nurse</option><option value="receptionist">Receptionist</option>
              <option value="therapist">Therapist</option><option value="TCO">TCO</option>
            </select>
          </div>
          <label style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:C.soft,cursor:"pointer",marginBottom:16}}>
            <input type="checkbox" checked={!!editLoc.inactive} onChange={e=>setEditLoc(p=>({...p,inactive:e.target.checked}))} style={{width:16,height:16,accentColor:C.teal}}/>
            Mark inactive (left agency)
          </label>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>saveEdit(editLoc)} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Save</button>
            <button onClick={()=>setEditLoc(null)} style={BTN(C.dim,true)}>Cancel</button>
          </div>
        </Overlay>
      )}
    </div>
  );
}

/* ── Pulse State Icons — from public folder ── */
function getPulseIcon(score, size=48) {
  const base = process.env.PUBLIC_URL || "";
  const src = score >= 90 ? `${base}/pulse_good.svg`
            : score >= 50 ? `${base}/pulse_middle.svg`
            : `${base}/pulse_bad.svg`;
  return <img src={src} alt="" style={{width:size,height:size,objectFit:"contain",display:"block",flexShrink:0}}/>;
}
/* Iris Score(TM) is a registered trademark. © Conor O'Neill 2026.
   Daily operational safety indicator for dental practices.
   Weighted components sum to 100.
══════════════════════════════════════════════════════════════════ */

const PULSE_WEIGHTS = {
  nurseExperience: 55,   // skill-mix coverage per open surgery
  surgeryCoverage: 20,   // raw nurse headcount vs open surgeries
  keyholder:       15,   // opener + closer presence
  stability:        7,   // locum reliance + sickness load
  attendance:       3,   // lateness & OT signals
};

// Nurse experience score per surgery (0–1)
function nurseExperienceRatio(nurse) {
  if (!nurse) return 0;
  if (nurse.locum)        return 0.75; // Locum = Unqualified Competent (like nearQual)
  switch (nurse.status) {
    case "qualified":   return nurse.headNurse ? 1.0 : 0.95;
    case "nearQual":    return 0.75;
    case "developing":  return 0.55;
    case "trainee":     return 0.25;
    case "newStarter":  return 0.0;
    default:            return 0.5;
  }
}


/* ═══════════════════════════════════════════════════════════════
   TOIL & CARERS LEAVE HELPERS
════════════════════════════════════════════════════════════════ */

// Get active (unexpired) TOIL balance in hours for a staff member
function getTOILBalance(staffId, state) {
  const todayStr = new Date().toISOString().split("T")[0];
  const entries = (state.toilBalances||{})[staffId]||[];
  return entries
    .filter(e=>!e.expiryDate||e.expiryDate>=todayStr)
    .reduce((s,e)=>s+e.hrs,0);
}

// Get all TOIL entries for a staff member (for display)
function getTOILEntries(staffId, state) {
  const todayStr = new Date().toISOString().split("T")[0];
  const entries = (state.toilBalances||{})[staffId]||[];
  return {
    active: entries.filter(e=>!e.expiryDate||e.expiryDate>=todayStr),
    expired: entries.filter(e=>e.expiryDate&&e.expiryDate<todayStr),
    totalHrs: entries
      .filter(e=>!e.expiryDate||e.expiryDate>=todayStr)
      .reduce((s,e)=>s+e.hrs,0),
  };
}

// Calculate TOIL expiry date from policy
function calcTOILExpiry(earnedDate, policy) {
  if (!policy?.expiry || policy.expiry==="yearEnd") {
    // Expires end of the leave year in which it was earned
    const yr = new Date(earnedDate+"T12:00").getFullYear();
    return `${yr}-12-31`;
  }
  if (policy.expiry==="6months") {
    const d = new Date(earnedDate+"T12:00");
    d.setMonth(d.getMonth()+6);
    return d.toISOString().split("T")[0];
  }
  return null; // no expiry
}

// Bank TOIL hours for a staff member (returns new state fragment)
function bankTOIL(staffId, name, hrs, earnedDate, source, loggedBy, state) {
  const policy = state.toilPolicy||{};
  const expiryDate = calcTOILExpiry(earnedDate, policy);
  const entry = {
    id: "toil"+Date.now()+Math.random().toString(36).slice(2,6),
    hrs, rate: policy.rate||1,
    earnedDate, expiryDate, source, loggedBy,
  };
  const existing = (state.toilBalances||{})[staffId]||[];
  return {
    toilBalances: {
      ...(state.toilBalances||{}),
      [staffId]: [...existing, entry],
    },
  };
}

// Get carers leave used this leave year for a staff member
function getCarersLeaveUsed(staffId, state) {
  const yr = new Date().getFullYear().toString();
  return (state.carersLeaveUsed||{})[staffId]?.[yr]||0;
}

// Get carers leave remaining (statutory 5 days)
function getCarersLeaveRemaining(staffId, state) {
  return Math.max(0, 5 - getCarersLeaveUsed(staffId, state));
}

/* ═══════════════════════════════════════════════════════════════
   COMPLIANCE SAFETY CHECKS
   Checks safeguarding L3, fire marshals, sedation cover per site/day
════════════════════════════════════════════════════════════════ */
function checkComplianceSafety(siteId, date, state) {
  const nurses = getNursesForSite(state, siteId);
  const onToday = nurses.filter(n=>!isAbsent(state,n.id,date));
  // Tenant-agnostic — also pull clinicians attached to this site
  const dowComp = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][
    new Date(date+"T12:00").getDay()];
  const siteClinicians = getClinicians(state).filter(c=>{
    if (c._deleted) return false;
    const sites = c.sites||[c.site];
    return sites.includes(siteId);
  });
  const clinsOnToday = siteClinicians.filter(c=>{
    if (isAbsent(state,c.id,date)) return false;
    const sched = c.siteScheds?.[siteId]||c.sched||{};
    return !!sched[dowComp];
  });
  // Combined pools — nurses are always "on staff" for the role-exists check;
  // clinicians count if they're attached to the site at all.
  const allRolePool = [...nurses, ...siteClinicians];
  const allOnToday  = [...onToday, ...clinsOnToday];
  const flags = [];

  // ── Level 3 Safeguarding ──────────────────────────────────
  // Check both top-level safeguardingL3 and nested compliance.safeguarding
  const hasL3 = n => n.safeguardingL3 || n.compliance?.safeguarding;
  const l3All  = allRolePool.filter(hasL3);
  const l3On   = allOnToday.filter(hasL3);
  if (l3All.length===0) {
    flags.push({
      sev:"red", area:"safeguarding",
      msg:"No Level 3 safeguarding lead logged — required for CQC",
      suggestion:"Add via Admin → Staff Records → Compliance",
    });
  } else if (l3On.length===0) {
    flags.push({
      sev:"red", area:"safeguarding",
      msg:`No L3 safeguarding cover today — `
        +`${l3All.map(n=>n.name.split(" ")[0]).join(", ")} all absent`,
      suggestion:"Consider who holds safeguarding responsibility today",
    });
  }

  // ── Fire Marshal ─────────────────────────────────────────
  // Check both top-level fireMarshal and nested compliance.fireMarshal
  const hasFM = n => n.fireMarshal || n.compliance?.fireMarshal;
  const fmAll = allRolePool.filter(hasFM);
  const fmOn  = allOnToday.filter(hasFM);
  if (fmAll.length===0) {
    flags.push({
      sev:"amber", area:"fire",
      msg:"No fire marshal logged for this site",
      suggestion:"Add via Admin → Staff Records → Compliance",
    });
  } else if (fmOn.length===0) {
    flags.push({
      sev:"amber", area:"fire",
      msg:`No fire marshal on site today — `
        +`${fmAll.map(n=>n.name.split(" ")[0]).join(", ")} absent`,
      suggestion:null,
    });
  }

  // ── Sedation session cover ────────────────────────────────
  const allClins = getClinicians(state).filter(c=>!c._deleted);
  const dow = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][
    new Date(date+"T12:00").getDay()];
  const clinsOn = allClins.filter(c=>{
    const sites = c.sites||[c.site];
    if (!sites.includes(siteId)) return false;
    if (isAbsent(state,c.id,date)) return false;
    const sched = c.siteScheds?.[siteId]||c.sched||{};
    return !!sched[dow];
  });
  clinsOn.forEach(c=>{
    const reqs = getClinicianReqs(state,c.id,dow);
    if (!reqs.length) return;
    const needsIV = reqs.includes("iv");
    const needsRA = reqs.includes("ra");
    const ivAvail = onToday.filter(n=>n.iv);
    const raAvail = onToday.filter(n=>n.ra);
    if (needsIV && ivAvail.length===0) {
      flags.push({
        sev:"red", area:"sedation",
        msg:`${c.name} running IV sedation — NO IV-qualified nurse on site`,
        suggestion:ivAvail.length===0
          ? "Cover needed urgently — check IV-qualified staff availability"
          : null,
        clinId: c.id,
      });
    } else if (needsRA && raAvail.length===0) {
      flags.push({
        sev:"red", area:"sedation",
        msg:`${c.name} running RA sedation — NO RA-qualified nurse on site`,
        clinId: c.id,
      });
    }
  });

  // ── First Aider ───────────────────────────────────────────
  // Check both top-level firstAider and nested compliance.firstAider
  const hasFA = n => n.firstAider || n.compliance?.firstAider;
  const faAll = nurses.filter(hasFA);
  const faOn  = onToday.filter(hasFA);
  if (faAll.length===0) {
    flags.push({
      sev:"amber", area:"firstaid",
      msg:"No first aider logged for this site",
      suggestion:"Add via Admin → Staff Records → Compliance",
    });
  } else if (faOn.length===0) {
    flags.push({
      sev:"amber", area:"firstaid",
      msg:`No first aider on site today — `
        +`${faAll.map(n=>n.name.split(" ")[0]).join(", ")} absent`,
      suggestion:null,
    });
  }

  return flags;
}

/* ═══════════════════════════════════════════════════════════════
   STAFFING CONFIDENCE SCORE  v1.0
   Separate from Iris Score (safety). Measures efficiency &
   stability. Three layers:
     1. Coverage ratio — competency-weighted nurses vs surgeries
     2. Bradford risk — high scorers reduce effective confidence
     3. Spare-sickness correlation — tracked for pattern detection
   Returns 0-100 with breakdown for drilldown.
════════════════════════════════════════════════════════════════ */
function calcStaffingConfidence(siteId, date, state) {
  const nurses   = getNursesForSite(state, siteId);
  const allClins = getClinicians(state).filter(c=>!c._deleted);
  const dow      = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][new Date(date+"T12:00").getDay()];
  const factors  = [];

  // Clinicians actually working this site today
  const clinsOn = allClins.filter(c=>{
    const s = c.siteScheds?.[siteId]||c.sched||{};
    return (c.sites||[c.site]).includes(siteId) && s[dow] && !isAbsent(state,c.id,date);
  });
  const surgeries = clinsOn.length;

  // Nurses not absent
  const nursesOn = nurses.filter(n=>!isAbsent(state,n.id,date));

  // ── LAYER 1: Competency-weighted coverage ratio (50 pts) ──────
  // Weight each nurse by competency — a trainee ≠ a qualified nurse
  const COMP_WEIGHT = {
    qualified:  1.0,
    nearQual:   0.75,
    developing: 0.55,
    trainee:    0.30,  // training opportunity, not full cover
    newStarter: 0.10,  // must not work unsupervised
  };
  const effectiveNurses = nursesOn.reduce((sum,n)=>{
    const w = n.locum ? 0.75 : (COMP_WEIGHT[n.status]||0.5);
    return sum + w;
  }, 0);

  let ratioScore = 50;
  let ratioLabel = "";
  let ratioDetail = "";

  if (surgeries === 0) {
    ratioScore = 50;
    ratioLabel = "No surgeries today";
    ratioDetail = "No clinical sessions running";
  } else {
    const ratio = effectiveNurses / surgeries;
    ratioDetail = `${nursesOn.length} nurses (${effectiveNurses.toFixed(1)} effective) vs ${surgeries} surgeries`;

    if (ratio < 0.8) {
      ratioScore = 8;
      ratioLabel = "Critically understaffed";
      factors.push({sev:"critical", msg:`Understaffed — effective nurse cover only ${Math.round(ratio*100)}% of surgeries`});
    } else if (ratio < 1.0) {
      ratioScore = 25;
      ratioLabel = "Under capacity";
      factors.push({sev:"high", msg:`Cover ratio ${ratio.toFixed(1)}x — some surgeries at risk`});
    } else if (ratio <= 1.25) {
      ratioScore = 50;
      ratioLabel = "Optimal";
    } else if (ratio <= 1.5) {
      ratioScore = 40;
      ratioLabel = "Slightly overstaffed";
      factors.push({sev:"low", msg:`Coverage ratio ${ratio.toFixed(1)}x — ${nursesOn.length-surgeries} nurses without a surgery`});
    } else {
      ratioScore = 25;
      ratioLabel = `Overstaffed (${ratio.toFixed(1)}x)`;
      const spare = nursesOn.length - surgeries;
      factors.push({sev:"medium", msg:`${spare} spare nurse${spare>1?"s":""} today — consider unpaid leave or training allocation`});
    }

    // Flag if spare nurses are all qualified (waste) vs trainee (opportunity)
    if (nursesOn.length > surgeries) {
      const spareNurses = nursesOn.slice(surgeries);
      const spareQual = spareNurses.filter(n=>n.status==="qualified"||n.status==="nearQual").length;
      const spareTrainee = spareNurses.filter(n=>n.status==="trainee"||n.status==="newStarter").length;
      if (spareTrainee > 0 && spareQual === 0) {
        factors.push({sev:"info", msg:`${spareTrainee} trainee${spareTrainee>1?"s":""} spare — good structured training opportunity`});
      }
    }
  }

  // ── LAYER 2: Bradford risk modifier (30 pts) ──────────────────
  let bradfordScore = 30;
  const highBFNurses = nursesOn.filter(n=>{
    const sickRecs = (state.sick||[]).filter(s=>s.staffId===n.id&&s.reason!=="medical");
    const totalDays = sickRecs.reduce((a,s)=>a+(s.days||1),0);
    const bf = sickRecs.length * sickRecs.length * totalDays;
    return bf >= 100;
  });
  const criticalBFNurses = nursesOn.filter(n=>{
    const sickRecs = (state.sick||[]).filter(s=>s.staffId===n.id&&s.reason!=="medical");
    const totalDays = sickRecs.reduce((a,s)=>a+(s.days||1),0);
    const bf = sickRecs.length * sickRecs.length * totalDays;
    return bf >= 200;
  });

  if (criticalBFNurses.length > 0) {
    bradfordScore -= Math.min(criticalBFNurses.length * 10, 20);
    factors.push({sev:"high", msg:`${criticalBFNurses.length} nurse${criticalBFNurses.length>1?"s":""} with critical Bradford score in today — ${criticalBFNurses.map(n=>n.name.split(" ")[0]).join(", ")}`});
  }
  if (highBFNurses.length > criticalBFNurses.length) {
    const caution = highBFNurses.length - criticalBFNurses.length;
    bradfordScore -= Math.min(caution * 5, 10);
    factors.push({sev:"medium", msg:`${caution} nurse${caution>1?"s":""} with high Bradford score in today`});
  }
  bradfordScore = Math.max(0, bradfordScore);

  // ── LAYER 3: Lateness pattern risk (20 pts) ───────────────────
  let patternsScore = 20;
  const latenessRisks = nursesOn.filter(n=>{
    const p = analyseLatenessPatterns(n.id, state);
    return p.riskLevel==="medium"||p.riskLevel==="high";
  });
  if (latenessRisks.length > 0) {
    patternsScore -= Math.min(latenessRisks.length * 5, 15);
    factors.push({sev:"low", msg:`${latenessRisks.length} nurse${latenessRisks.length>1?"s":""} with lateness welfare patterns — ${latenessRisks.map(n=>n.name.split(" ")[0]).join(", ")}`});
  }
  patternsScore = Math.max(0, patternsScore);

  const total = Math.min(100, Math.max(0, Math.round(ratioScore + bradfordScore + patternsScore)));

  // Confidence band
  const band = total >= 80 ? {label:"High confidence",    color:"#3aad98", tier:"high"}
             : total >= 55 ? {label:"Moderate",           color:"#f5c518", tier:"medium"}
             :               {label:"Low confidence",     color:"#ff4646", tier:"low"};

  return {
    score: total,
    band,
    breakdown: {
      coverageRatio: {score:ratioScore,    max:50, label:"Coverage ratio",    detail:ratioDetail,  ratioLabel},
      bradfordRisk:  {score:bradfordScore, max:30, label:"Bradford risk",     detail:`${highBFNurses.length} high-BF nurses working today`},
      latenessRisk:  {score:patternsScore, max:20, label:"Lateness patterns", detail:`${latenessRisks.length} welfare flags`},
    },
    factors,
    surgeries,
    nursesOn: nursesOn.length,
    effectiveNurses: Math.round(effectiveNurses*10)/10,
    spareNurses: Math.max(0, nursesOn.length - surgeries),
    spareNurseObjects: nursesOn.length > surgeries ? nursesOn.slice(surgeries) : [],
  };
}

function calcIrisScore(siteId, date, state) {
  const siteInfo   = SITES[siteId];
  const nurses     = state.nurses || NURSES_BASE;
  const dr         = buildDayRota(date, siteId, nurses, state);
  const pairings   = dr.pairings || [];
  const openSurgeries = pairings.length; // each pairing = one open surgery

  const factors    = [];   // things reducing the score
  const details    = [];   // per-surgery breakdown

  // ── 1. NURSE EXPERIENCE COVERAGE (55 pts) ─────────────────────
  let expScore = PULSE_WEIGHTS.nurseExperience; // start full, deduct
  if (openSurgeries > 0) {
    const ratios = pairings.map(p => {
      const n = p.nurse;
      const ratio = nurseExperienceRatio(n);
      const surgLabel = p.clinician?.name || "Surgery";
      const nurseName = n ? n.name : "No nurse assigned";
      const nurseLevel = n
        ? (n.locum ? "Locum (Unqualified Competent)"
          : STATUS[n.status]?.label || n.status)
        : null;

      // Build per-surgery flags
      let flag = null;
      if (!n) {
        flag = { sev: "critical", msg: `${surgLabel}: No nurse assigned — surgery should not open` };
      } else if (n.status === "newStarter") {
        flag = { sev: "critical", msg: `${surgLabel}: ${nurseName} is a New Starter — must not work unsupervised` };
      } else if (n.status === "trainee") {
        flag = { sev: "high", msg: `${surgLabel}: ${nurseName} is a Trainee — limited competency cover` };
      } else if (n.status === "developing") {
        flag = { sev: "medium", msg: `${surgLabel}: ${nurseName} is still Developing — monitor closely` };
      } else if (n.locum) {
        flag = { sev: "low", msg: `${surgLabel}: ${nurseName} is a Locum — competency unverified` };
      }
      if (flag) factors.push(flag);
      details.push({ clinician: p.clinician, nurse: n, ratio, nurseName, nurseLevel });
      return ratio;
    });
    const avgRatio = ratios.reduce((s,r)=>s+r,0) / openSurgeries;
    expScore = Math.round(avgRatio * PULSE_WEIGHTS.nurseExperience);
  } else {
    // No open surgeries today — score is neutral (full score, nothing to penalise)
    details.push({ clinician: null, nurse: null, ratio: 1, nurseName: "No surgeries open today", nurseLevel: null });
  }

  // ── 2. SURGERY COVERAGE (20 pts) ──────────────────────────────
  let covScore = PULSE_WEIGHTS.surgeryCoverage;
  if (openSurgeries > 0) {
    const nursesCovering = pairings.filter(p=>p.nurse).length;
    const covRatio = nursesCovering / openSurgeries;
    covScore = Math.round(covRatio * PULSE_WEIGHTS.surgeryCoverage);
    const uncovered = openSurgeries - nursesCovering;
    if (uncovered > 0) {
      factors.push({ sev: "critical", msg: `${uncovered} open surger${uncovered===1?"y":"ies"} with no nurse assigned` });
    }
  }

  // ── 3. KEYHOLDER PRESENCE (15 pts) ────────────────────────────
  const coverageOk = getKeyholderCoverage(state, siteId, date);
  const khOk = coverageOk.status==="ok"||coverageOk.status==="overridden";
  const khScore  = khOk ? 15 : 0;
  if (!khOk) factors.push({ sev: "high", msg: "No keyholder available — all key holders are absent" });

  // ── 4. STAFFING STABILITY (7 pts) ─────────────────────────────
  // Locums on site today
  const todayLocums = (state.locums||[]).filter(l=>l.date===date&&l.site===siteId).length;
  // Staff sick today
  const sickToday  = (state.sick||[]).filter(s=>{
    if (!s.staffId) return false;
    const person = [...nurses,...RECS_BASE,...getClinicians(state)].find(p=>p.id===s.staffId);
    if (!person || person.site!==siteId) return false;
    const st = new Date(s.date), en = new Date(s.date);
    en.setDate(en.getDate()+(s.days||1)-1);
    return new Date(date)>=st && new Date(date)<=en && !s.rtw;
  }).length;

  let stabScore = PULSE_WEIGHTS.stability;
  stabScore -= Math.min(todayLocums * 2, 6);
  stabScore -= Math.min(sickToday, 4);
  stabScore = Math.max(0, stabScore);

  if (todayLocums > 0)
    factors.push({ sev: "low", msg: `${todayLocums} locum${todayLocums>1?"s":""} on site today — competency unverified` });
  if (sickToday > 0)
    factors.push({ sev: "medium", msg: `${sickToday} staff member${sickToday>1?"s":""} currently off sick` });

  // ── 5. ATTENDANCE SIGNALS (3 pts) ─────────────────────────────
  const weekAgo = new Date(date); weekAgo.setDate(weekAgo.getDate()-7);
  const weekAgoStr = weekAgo.toISOString().split("T")[0];
  const siteStaffIds = new Set([
    ...nurses.filter(n=>n.site===siteId).map(n=>n.id),
    ...RECS_BASE.filter(r=>r.site===siteId).map(r=>r.id),
  ]);
  const recentLate  = (state.overtime||[]).filter(r=>
    r.type==="late" && r.date>=weekAgoStr && r.date<=date && siteStaffIds.has(r.staffId)
  ).length;
  const pendingOT   = (state.overtime||[]).filter(r=>
    r.type==="overtime" && r.status==="pending" && siteStaffIds.has(r.staffId)
  ).length;

  let attendScore = PULSE_WEIGHTS.attendance;
  attendScore -= Math.min(recentLate * 0.5, 2);
  attendScore -= Math.min(pendingOT * 0.25, 1);
  attendScore = Math.max(0, Math.round(attendScore * 10)/10);

  if (recentLate > 0)
    factors.push({ sev: "low", msg: `${recentLate} lateness incident${recentLate>1?"s":""} in the last 7 days` });

  // ── TOTAL ──────────────────────────────────────────────────────
  const total = Math.round(expScore + covScore + khScore + stabScore + attendScore);

  // ── SUGGESTIONS ───────────────────────────────────────────────
  const suggestions = [];
  if (expScore < PULSE_WEIGHTS.nurseExperience * 0.7) {
    const weakPairings = pairings.filter(p=>nurseExperienceRatio(p.nurse)<0.55);
    if (weakPairings.length)
      suggestions.push(`Pair ${weakPairings.map(p=>p.nurse?.name||"unstaffed surgery").join(", ")} with an experienced nurse for supervision`);
  }
  if (!khOk) suggestions.push(`Check keyholder cover for ${siteInfo.name} — all keyholders are absent`);
  if (todayLocums > 1) suggestions.push("Consider reducing locum reliance — check agency availability for next week");
  if (sickToday > 1) suggestions.push("Multiple absences today — review cover options and check Bradford Factor scores");

  // Score band — Good ≥90, Middle 50–89, Bad <50
  const band = total >= 90 ? { label: "Good",              color: "#059669", tier: "good"   }
             : total >= 50 ? { label: "Needs Attention",   color: "#d97706", tier: "middle" }
             :               { label: "Action Required",   color: "#dc2626", tier: "bad"    };

  return {
    score: Math.min(100, Math.max(0, total)),
    band,
    breakdown: {
      nurseExperience: { score: expScore,      max: PULSE_WEIGHTS.nurseExperience, label: "Nurse Experience" },
      surgeryCoverage: { score: covScore,      max: PULSE_WEIGHTS.surgeryCoverage, label: "Surgery Coverage" },
      keyholder:       { score: khScore,       max: PULSE_WEIGHTS.keyholder,       label: "Keyholder Presence" },
      stability:       { score: stabScore,     max: PULSE_WEIGHTS.stability,       label: "Staffing Stability" },
      attendance:      { score: attendScore,   max: PULSE_WEIGHTS.attendance,      label: "Attendance Signals" },
    },
    factors,
    suggestions,
    details,
    openSurgeries,
    meta: { khOk, todayLocums, sickToday, recentLate },
  };
}


/* ═══════════════════════════════════════════════════════════════
   CONFIDENCE DRILLDOWN — explains the confidence score in plain
   English with actions. Shown when user taps confidence indicator.
════════════════════════════════════════════════════════════════ */
function ConfidenceDrilldown({ siteId, date, state, onClose }) {
  const cs = calcStaffingConfidence(siteId, date, state);
  const site = SITES[siteId];
  const col = cs.band.tier==="high"?"#3aad98":cs.band.tier==="medium"?"#f5c518":"#ff4646";

  const LAYER_HELP = {
    coverageRatio: {
      title: "Coverage Ratio",
      what: "Compares nurses working today against surgeries running — weighted by competency. A trainee counts less than a qualified nurse because their scope is limited.",
      good: "Between 1.0x and 1.25x is optimal — enough cover without waste.",
      bad: "Above 1.4x means more nurses than needed. Below 1.0x means surgeries are at risk.",
    },
    bradfordRisk: {
      title: "Bradford Factor Risk",
      what: "Staff with high Bradford scores (repeated short absences) are statistically more likely to be absent again. When they're in, it reduces confidence because reliability is lower.",
      good: "Score stays high when staff in today have low Bradford scores.",
      bad: "Score drops when high-BF staff are rostered — consider the pattern, not just today.",
    },
    latenessRisk: {
      title: "Lateness Patterns",
      what: "Staff with detected lateness patterns (always late on same day, escalating frequency) create uncertainty. A nurse who is regularly late affects surgery start times.",
      good: "High score means no welfare lateness flags for staff in today.",
      bad: "Score drops when staff with amber lateness patterns are working — worth a welfare conversation.",
    },
  };

  const ACTIONS = {
    high: null,
    medium: "Monitor this week — consider a welfare conversation if patterns persist.",
    low: "Act now — review staffing levels, check Bradford scores, and consider adjusting the rota.",
  };

  return (
    <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif"}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
        <div>
          <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.3)",textTransform:"uppercase",letterSpacing:"0.12em",marginBottom:4}}>Staffing Confidence | {site?.name}</div>
          <div style={{display:"flex",alignItems:"baseline",gap:8}}>
            <span style={{fontFamily:"'Instrument Serif',Georgia,serif",fontStyle:"italic",fontSize:48,fontWeight:700,color:col,lineHeight:1}}>{cs.score}</span>
            <span style={{fontSize:13,color:"rgba(255,255,255,0.3)",fontWeight:600}}>/100</span>
          </div>
          <div style={{fontSize:13,fontWeight:700,color:col,marginTop:4}}>{cs.band.label}</div>
        </div>
        <button onClick={onClose} style={{background:"rgba(255,255,255,0.06)",border:"none",color:"rgba(255,255,255,0.5)",borderRadius:8,padding:"6px 12px",cursor:"pointer",fontFamily:"inherit",fontSize:13}}>X</button>
      </div>

      {/* What is this? */}
      <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:12,padding:"14px 16px",marginBottom:16}}>
        <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.5)",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.08em"}}>What is this?</div>
        <div style={{fontSize:12,color:"rgba(255,255,255,0.6)",lineHeight:1.6}}>
          Confidence is separate from the Iris Score. The Iris Score measures <strong style={{color:"rgba(255,255,255,0.8)"}}>safety</strong> - can you run today? Confidence measures <strong style={{color:"rgba(255,255,255,0.8)"}}>efficiency and stability</strong> - are you running smart? Overstaffed days cost money. High-Bradford staff in today reduce predictability. Lateness patterns create uncertainty.
        </div>
      </div>

      {/* Three layers */}
      {Object.entries(cs.breakdown).map(([key, layer])=>{
        const help = LAYER_HELP[key];
        const pct = Math.round((layer.score/layer.max)*100);
        const layerCol = pct>=80?"#3aad98":pct>=55?"#f5c518":"#ff4646";
        return (
          <div key={key} style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)",borderRadius:12,padding:"14px 16px",marginBottom:10}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
              <div style={{fontSize:13,fontWeight:700,color:"rgba(255,255,255,0.85)"}}>{help.title}</div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.3)"}}>{layer.score}/{layer.max}</div>
                <div style={{width:60,height:5,background:"rgba(255,255,255,0.08)",borderRadius:99}}>
                  <div style={{width:pct+"%",height:"100%",background:layerCol,borderRadius:99,transition:"width 0.4s"}}/>
                </div>
              </div>
            </div>
            {layer.detail&&<div style={{fontSize:11,color:"rgba(255,255,255,0.4)",marginBottom:8}}>{layer.detail}{layer.ratioLabel?` — ${layer.ratioLabel}`:""}</div>}
            <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",lineHeight:1.6,marginBottom:6}}>{help.what}</div>
            <div style={{fontSize:11,color:pct>=80?"#3aad98":"rgba(255,255,255,0.35)",lineHeight:1.5}}>{pct>=80?`✓ ${help.good}`:`→ ${help.bad}`}</div>
          </div>
        );
      })}

      {/* Flags */}
      {cs.factors.length>0&&(
        <div style={{marginBottom:16}}>
          <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.3)",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>What's dragging it down</div>
          {cs.factors.filter(f=>f.sev!=="info").map((f,i)=>{
            const isSpare=f.msg&&f.msg.includes("spare nurse");
            if(isSpare&&cs.spareNurseObjects&&cs.spareNurseObjects.length>0) {
              return (
                <div key={i} style={{padding:"10px 0",borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                  <div style={{display:"flex",gap:10,marginBottom:8}}>
                    <div style={{width:6,height:6,borderRadius:"50%",flexShrink:0,marginTop:4,background:"#f5c518"}}/>
                    <div style={{fontSize:12,color:"rgba(255,255,255,0.6)"}}>{f.msg}</div>
                  </div>
                  {cs.spareNurseObjects.map(n=>(
                    <div key={n.id} style={{marginLeft:16,marginBottom:6,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                      <span style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.7)",minWidth:120}}>{n.name.split(" ")[0]}</span>
                      <button onClick={()=>window.dispatchEvent(new CustomEvent("omni:navigate",{detail:{tab:"rota"}}))}
                        style={{fontSize:10,padding:"3px 10px",borderRadius:6,border:"1px solid rgba(58,173,152,0.4)",background:"rgba(58,173,152,0.1)",color:"#3aad98",cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>
                        → View on rota
                      </button>
                      <button onClick={()=>window.dispatchEvent(new CustomEvent("omni:navigate",{detail:{tab:"attendance",subTab:"unpaid"}}))}
                        style={{fontSize:10,padding:"3px 10px",borderRadius:6,border:"1px solid rgba(245,197,24,0.4)",background:"rgba(245,197,24,0.08)",color:"#f5c518",cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>
                        → Unpaid leave
                      </button>
                    </div>
                  ))}
                </div>
              );
            }
            return (
              <div key={i} style={{display:"flex",gap:10,padding:"8px 0",borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
                <div style={{width:6,height:6,borderRadius:"50%",flexShrink:0,marginTop:4,
                  background:f.sev==="critical"?"#ff4646":f.sev==="high"?"#f5a623":f.sev==="medium"?"#f5c518":"rgba(255,255,255,0.3)"}}/>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.6)",lineHeight:1.5}}>{f.msg}</div>
              </div>
            );
          })}
          {cs.factors.filter(f=>f.sev==="info").map((f,i)=>(
            <div key={i} style={{display:"flex",gap:10,padding:"8px 0",borderBottom:"1px solid rgba(255,255,255,0.04)"}}>
              <div style={{fontSize:11,color:"#3aad98"}}>(i)</div>
              <div style={{fontSize:12,color:"rgba(58,173,152,0.8)",lineHeight:1.5}}>{f.msg}</div>
            </div>
          ))}
        </div>
      )}

      {/* Suggested action */}
      {ACTIONS[cs.band.tier]&&(
        <div style={{background:`${col}12`,border:`1px solid ${col}30`,borderRadius:10,padding:"12px 16px"}}>
          <div style={{fontSize:11,fontWeight:700,color:col,marginBottom:4}}>Suggested action</div>
          <div style={{fontSize:12,color:"rgba(255,255,255,0.6)",lineHeight:1.5}}>{ACTIONS[cs.band.tier]}</div>
        </div>
      )}
    </div>
  );
}

/* ── Iris Score Drill-Down Overlay ──────────────────────────────── */
function IrisScoreDrilldown({ siteId, date, state, onClose }) {
  const ps        = calcIrisScore(siteId, date, state);
  const siteInfo  = SITES[siteId];
  const liveDay   = CAL.days.find(d=>d.date===date);
  const [aiText,  setAiText]  = useState("");
  const [aiLoad,  setAiLoad]  = useState(false);
  const [aiErr,   setAiErr]   = useState("");

  const SEV_COLOR = { critical:"#dc2626", high:"#c2410c", medium:"#d97706", low:"#6b7280" };
  const SEV_ICON  = { critical:"●", high:"●", medium:"●", low:"·" };

  const sortedFactors = [...ps.factors].sort((a,b)=>{
    const o = {critical:0,high:1,medium:2,low:3};
    return (o[a.sev]||9)-(o[b.sev]||9);
  });

  async function askAI() {
    setAiLoad(true); setAiErr(""); setAiText("");
    try {
      const breakdown  = Object.values(ps.breakdown).map(b=>`• ${b.label}: ${b.score}/${b.max}`).join("\n");
      const factorList = sortedFactors.length > 0
        ? sortedFactors.map(f=>`• [${f.sev.toUpperCase()}] ${f.msg}`).join("\n")
        : "None — excellent staffing today!";
      const suggestionList = ps.suggestions.length > 0
        ? ps.suggestions.map(s=>`• ${s}`).join("\n")
        : "Nothing urgent — keep it up!";
      const prompt = `You are the assistant for Omni Rota, a dental practice staff management system.
Explain the Iris Score™ for ${siteInfo.name} in a friendly, upbeat, and practical way.
Speak directly to the practice principal — confident, warm, no jargon.

Iris Score™ today: ${ps.score}/100 — ${ps.band.label}
Date: ${liveDay?.label || date}

Score breakdown:
${breakdown}

Factors reducing the score:
${factorList}

Suggestions:
${suggestionList}

Write 3–4 short, clear paragraphs. Start with an overall verdict. Then explain what's going well and what needs attention. End with one actionable next step. Keep it under 200 words. Use 1–2 relevant emojis. Never use bullet points — flowing paragraphs only.`;

      const apiKey = getStoredApiKey()||state?.settings?.anthropicKey||"";
      // apiKey may be empty — key lives in Vercel env var, proxy handles auth
      const resp = await fetch("/api/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": getStoredApiKey()||state?.settings?.anthropicKey||"",
        },
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 400,
          messages: [{ role: "user", content: prompt }],
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(()=>({}));
        setAiErr(`API error ${resp.status}: ${err?.error?.message||resp.statusText}`);
        return;
      }
      const data = await resp.json();
      const text = data.content?.map(b=>b.text||"").join("").trim();
      if (text) setAiText(text);
      else setAiErr(data.error?.message||"No response from AI. Try again.");
    } catch(e) {
      setAiErr(`AI error: ${e?.message||"Check connection and API key in Admin → Settings."}`);
    } finally {
      setAiLoad(false);
    }
  }

  const BarRow = ({ label, score, max, color }) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
        <span style={{ fontSize:12, color:C.text, fontWeight:500 }}>{label}</span>
        <span style={{ fontSize:12, fontWeight:700, color: score===max?C.green:score/max>0.7?C.teal:score/max>0.4?C.warn:C.danger }}>
          {score.toFixed(score%1===0?0:1)}<span style={{color:C.muted,fontWeight:400}}>/{max}</span>
        </span>
      </div>
      <div style={{ height:6, background:C.border, borderRadius:99, overflow:"hidden" }}>
        <div style={{
          width: (score/max*100)+"%",
          height:"100%", borderRadius:99,
          background: score===max?C.green:score/max>0.7?C.teal:score/max>0.4?C.warn:C.danger,
          transition:"width 0.5s ease",
        }}/>
      </div>
    </div>
  );

  return (
    <Overlay onClose={onClose} wide>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20 }}>
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
            <span style={{ fontSize:11, fontWeight:700, color:siteInfo.color, textTransform:"uppercase", letterSpacing:"0.08em" }}>{siteInfo.name}</span>
            <span style={{ fontSize:11, color:C.muted }}>.</span>
            <span style={{ fontSize:11, color:C.dim }}>{liveDay?.label || date}</span>
          </div>
          <div style={{ fontFamily:"'Instrument Sans',system-ui,sans-serif", fontSize:24, fontWeight:800, color:C.heading, marginBottom:2, letterSpacing:"-0.3px" }}>
            Iris Score<sup style={{fontSize:12,fontWeight:700,verticalAlign:"super",marginLeft:1}}>™</sup>
          </div>
          <div style={{ fontSize:12, color:C.dim, maxWidth:360, lineHeight:1.5 }}>
            Your daily practice safety indicator. A score of 100 means your practice is fully and safely staffed.
            Always aim for 100 — your team and patients deserve it.
          </div>
        </div>
        <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", color:C.muted, fontSize:22, lineHeight:1, padding:4 }}>×</button>
      </div>

      {/* Big score */}
      <div style={{ display:"flex", alignItems:"center", gap:20, marginBottom:24, padding:"20px 24px", background:ps.band.color+"0d", border:`2px solid ${ps.band.color}33`, borderRadius:16 }}>
        <div style={{ position:"relative", flexShrink:0 }}>
          <svg width={90} height={90} viewBox="0 0 90 90">
            <circle cx="45" cy="45" r="38" fill="none" stroke={C.border} strokeWidth="8"/>
            <circle cx="45" cy="45" r="38" fill="none" stroke={ps.band.color} strokeWidth="8"
              strokeDasharray={`${ps.score/100*239} 239`}
              strokeLinecap="round"
              transform="rotate(-90 45 45)"
              style={{transition:"stroke-dasharray 0.8s ease"}}
            />
          </svg>
          <div style={{ position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
            <span style={{ fontFamily:"'Instrument Sans',system-ui,sans-serif", fontSize:26, fontWeight:800, color:ps.band.color, lineHeight:1 }}>{ps.score}</span>
            <span style={{ fontSize:9, color:C.muted, fontWeight:600 }}>/ 100</span>
          </div>
        </div>
        <div>
          <div style={{ fontFamily:"'Instrument Sans',system-ui,sans-serif", fontSize:18, fontWeight:700, color:ps.band.color, marginBottom:4 }}>{ps.band.label}</div>
          <div style={{ marginBottom:8 }}>{getPulseIcon(ps.score, 56)}</div>
          <div style={{ fontSize:12, color:C.mid, lineHeight:1.5 }}>
            {sortedFactors.length===0 ? "Great job — fully and safely staffed today." :
             sortedFactors.some(f=>f.sev==="critical") ? "Critical issues need your attention now." :
             sortedFactors.some(f=>f.sev==="high") ? "Some important gaps to address today." :
             "Minor notes — practice is broadly safe."}
          </div>
          <div style={{ fontSize:11, color:C.muted, marginTop:6 }}>
            {ps.openSurgeries} surger{ps.openSurgeries===1?"y":"ies"} open today
          </div>
        </div>
      </div>

      {/* Score Breakdown */}
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:11, fontWeight:700, color:C.dim, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:12 }}>Score Breakdown</div>
        {Object.values(ps.breakdown).map(b=>(
          <BarRow key={b.label} label={b.label} score={b.score} max={b.max} />
        ))}
      </div>

      {/* Per-surgery detail */}
      {ps.details.filter(d=>d.clinician).length > 0 && (
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.dim, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:10 }}>Surgery Coverage Today</div>
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {ps.details.filter(d=>d.clinician).map((d,i)=>{
              const pct = Math.round(d.ratio*100);
              const col = pct===100?C.green:pct>=75?C.teal:pct>=50?C.warn:C.danger;
              return (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px", background:C.bg, border:`1px solid ${C.border}`, borderRadius:10 }}>
                  <div style={{ width:36, height:36, borderRadius:99, background:col+"18", border:`2px solid ${col}33`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <span style={{ fontFamily:"'Instrument Sans',system-ui,sans-serif", fontSize:13, fontWeight:800, color:col }}>{pct}</span>
                  </div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{d.clinician.name}</div>
                    <div style={{ fontSize:11, color:d.nurse?col:C.danger }}>
                      {d.nurse ? `${d.nurseName} · ${d.nurseLevel}` : "⚠️ No nurse assigned"}
                    </div>
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:2 }}>
                    <div style={{ fontSize:10, fontWeight:700, color:col }}>{pct}%</div>
                    <div style={{ fontSize:9, color:C.muted }}>cover quality</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Factors reducing the score */}
      {sortedFactors.length > 0 && (
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.dim, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:10 }}>What's reducing your score</div>
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {sortedFactors.map((f,i)=>(
              <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"10px 14px", background:SEV_COLOR[f.sev]+"0a", border:`1px solid ${SEV_COLOR[f.sev]}22`, borderRadius:10 }}>
                <span style={{ fontSize:14, flexShrink:0, marginTop:1 }}>{SEV_ICON[f.sev]}</span>
                <span style={{ fontSize:12, color:C.text, lineHeight:1.5 }}>{f.msg}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Suggestions */}
      {ps.suggestions.length > 0 && (
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.dim, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:10 }}>Suggested Actions</div>
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {ps.suggestions.map((s,i)=>(
              <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"10px 14px", background:C.tealPale, border:`1px solid ${C.tealBorder}`, borderRadius:10 }}>
                <span style={{ fontSize:14, flexShrink:0, display:"flex" }}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="9" y1="18" x2="15" y2="18"/><line x1="10" y1="22" x2="14" y2="22"/><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/></svg></span>
                <span style={{ fontSize:12, color:C.teal, lineHeight:1.5 }}>{s}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {sortedFactors.length === 0 && ps.score >= 95 && (
        <div style={{ textAlign:"center", padding:"16px 0 8px", marginBottom:16 }}>
          <div style={{ fontSize:28, marginBottom:6, display:"flex", justifyContent:"center" }}><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg></div>
          <div style={{ fontSize:14, fontWeight:700, color:C.green }}>Perfect score!</div>
          <div style={{ fontSize:12, color:C.dim }}>Your practice is fully and safely staffed today. Brilliant work.</div>
        </div>
      )}

      {/* AI Explanation */}
      <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:16, marginTop:4 }}>
        {!aiText && (
          <button
            onClick={askAI}
            disabled={aiLoad}
            style={{ width:"100%", padding:"12px 20px", borderRadius:12, border:`1px solid ${C.tealBorder}`, background:aiLoad?C.bg:C.tealPale, color:C.teal, fontFamily:"inherit", fontSize:13, fontWeight:600, cursor:aiLoad?"wait":"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}
          >
            {aiLoad ? (
              <><span style={{animation:"spin 1s linear infinite",display:"inline-block"}}>⟳</span> Analysing your score…</>
            ) : (
              <><Icon name="sparkle" size={14} color={C.teal}/> Ask AI to explain this score</>
            )}
          </button>
        )}
        {aiErr && <div style={{ fontSize:12, color:C.danger, textAlign:"center", marginTop:8 }}>{aiErr}</div>}
        {aiText && (
          <div style={{ background:C.tealPale, border:`1px solid ${C.tealBorder}`, borderRadius:14, padding:"16px 18px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
              <Icon name="sparkle" size={16} color={C.teal}/>
              <span style={{ fontSize:11, fontWeight:700, color:C.teal, textTransform:"uppercase", letterSpacing:"0.06em" }}>AI Analysis</span>
            </div>
            <div style={{ fontSize:13, color:C.text, lineHeight:1.7, whiteSpace:"pre-wrap" }}>{aiText}</div>
            <button onClick={()=>setAiText("")} style={{ marginTop:10, fontSize:11, color:C.dim, background:"none", border:"none", cursor:"pointer", padding:0 }}>↩ Ask again</button>
          </div>
        )}
      </div>

      <div style={{ marginTop:16, textAlign:"center", fontSize:10, color:C.muted }}>
        Iris Score™ · © Prima Luce Ltd 2026 · All rights reserved
      </div>
    </Overlay>
  );
}

/* ══════════════════════════════════
   PULSE VISION — Morning briefing
   The live picture. Right now.
══════════════════════════════════ */
function PulseVision({state, set, user, isPrincipal, onNavigate}) {
  const today  = new Date().toISOString().split("T")[0];
  const todayLabel = new Date().toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long"});
  const allNurses  = (state.nurses||NURSES_BASE).filter(n=>!n.maternity&&!n.archived);
  const allStaff   = [...allNurses,...RECS_BASE,...getClinicians(state).filter(c=>!c._deleted)];
  const sites = Object.entries(SITES);

  // Filter by site for managers
  const mySite = (!isPrincipal && user?.site) ? user.site : null;
  const visibleSites = mySite ? sites.filter(([id])=>id===mySite) : sites;

  // ── Per-site data ────────────────────────────────────────────
  const siteData = visibleSites.map(([siteId, site])=>{
    const siteNurses = allNurses.filter(n=>n.site===user?.site);
    const siteRecs   = RECS_BASE.filter(r=>r.site===siteId&&r.type!=="Manager");
    const siteClins  = getClinicians(state).filter(c=>{
      const clinSites = c.sites||[c.site];
      const schedForSite = c.siteScheds?.[siteId]||c.sched;
      return clinSites.includes(siteId)&&!c._deleted&&schedForSite?.[new Date(today+"T12:00:00").toLocaleDateString("en-GB",{weekday:"short"}).slice(0,3)];
    });
    const allSite    = [...siteNurses,...siteRecs,...siteClins];

    // Who's in / off
    const offToday   = allSite.filter(s=>isAbsent(state,s.id,today));
    const inToday    = allSite.filter(s=>!isAbsent(state,s.id,today));
    const offNurses  = siteNurses.filter(n=>isAbsent(state,n.id,today));
    const offRecs    = siteRecs.filter(r=>isAbsent(state,r.id,today));

    // RTW pending
    const rtwPending = (state.sick||[]).filter(s=>{
      const person = allSite.find(p=>p.id===s.staffId);
      return person && !s.rtw;
    });

    // Pending leave approvals
    const pendingLeave = allSite.flatMap(s=>(state.leave[s.id]||[]).filter(l=>l.status==="pending")).length;

    // Pending late finishes
    const pendingOT = (state.overtime||[]).filter(r=>r.status==="pending"&&r.site===siteId);

    // Open issues T1+T2
    const openIssues = (state.practiceEvents||[]);
    const openT1T2 = (state.issues||[]).filter(i=>i.site===siteId&&i.status!=="resolved"&&i.tier<=2);
    const openT3   = isPrincipal?(state.issues||[]).filter(i=>i.tier===3&&i.status!=="resolved"):[];

    // Upcoming events today/this week
    const weekEnd = new Date(today); weekEnd.setDate(weekEnd.getDate()+7);
    const weekEndStr = weekEnd.toISOString().split("T")[0];
    const upcomingEvents = (state.practiceEvents||[]).filter(e=>{
      if (e.appraisalPrivate && !isPrincipal) return false;
      if (e.sites?.length && !e.sites.includes(siteId) && e.type!=="partners" && e.type!=="groupmgmt" && e.type!=="groupmeeting") return false;
      return e.date>=today && e.date<=weekEndStr;
    });

    // Iris Score
    const ps = calcIrisScore(siteId, today, state);

    return {siteId,site,offToday,inToday,offNurses,offRecs,rtwPending,pendingLeave,pendingOT,openT1T2,openT3,upcomingEvents,ps};
  });

  // ── Welfare flags needing attention ──
  const canSeeWelfare = isPrincipal||user?.role==="manager"||
    (user?.role==="managerLite"&&!!(state.welfareAccess||{})[user?.staffId]);
  const welfareOpen = canSeeWelfare?(state.sick||[]).filter(s=>s.welfareFlag&&!s.rtw).filter(s=>{
    if (isPrincipal) return true;
    const person = allStaff.find(p=>p.id===s.staffId);
    return person?.site===mySite;
  }):[];

  // ── Omni Vision header colour — worst site score drives it ──
  const worstScore = Math.min(...siteData.map(d=>d.ps.score));
  const headerCol  = worstScore>=90?"#059669":worstScore>=70?C.teal:worstScore>=50?C.warn:"#dc2626";

  return (
    <div>
      {/* ── Header ── */}
      <div style={{background:`linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)`,borderRadius:16,padding:"24px 28px",marginBottom:24,position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:0,left:0,right:0,bottom:0,opacity:0.08,background:"radial-gradient(circle at 80% 50%, #22d3ee 0%, transparent 60%);"}}/>
        <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:16,position:"relative"}}>
          <LogoMark size={48} onTeal/>
          <div>
            <div style={{display:"flex",alignItems:"baseline",gap:8}}>
              <span style={{fontFamily:"'Instrument Serif',Georgia,serif",fontSize:28,color:"#fff",letterSpacing:"-0.5px",lineHeight:1}}>Pulse</span>
              <span style={{fontFamily:"'Instrument Serif',Georgia,serif",fontSize:28,color:"#22d3ee",letterSpacing:"-0.5px",lineHeight:1,fontStyle:"italic"}}>Vision</span>
            </div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.6)",marginTop:3}}>{todayLabel}</div>
          </div>
          <div style={{marginLeft:"auto",textAlign:"right"}}>
            <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:36,fontWeight:800,color:headerCol,lineHeight:1,letterSpacing:"-1px"}}>{worstScore}</div>
            <div style={{fontSize:10,color:"rgba(255,255,255,0.5)",marginTop:2}}>lowest site score</div>
          </div>
        </div>

        {/* Quick stat strip */}
        <div style={{display:"flex",gap:16,flexWrap:"wrap",position:"relative"}}>
          {[
            {v:siteData.reduce((s,d)=>s+d.offToday.length,0), l:"Off today",     col:siteData.some(d=>d.offNurses.length>2)?"#f87171":"rgba(255,255,255,0.7)"},
            {v:siteData.reduce((s,d)=>s+d.rtwPending.length,0),l:"RTW pending",  col:siteData.some(d=>d.rtwPending.length)?"#fbbf24":"rgba(255,255,255,0.7)"},
            {v:siteData.reduce((s,d)=>s+d.pendingLeave,0),     l:"Leave pending",col:siteData.some(d=>d.pendingLeave)?"#fbbf24":"rgba(255,255,255,0.7)"},
            {v:siteData.reduce((s,d)=>s+d.pendingOT.length,0), l:"Late finishes",col:siteData.some(d=>d.pendingOT.length)?"#fbbf24":"rgba(255,255,255,0.7)"},
            {v:siteData.reduce((s,d)=>s+d.openT1T2.length,0),  l:"Open issues",  col:siteData.some(d=>d.openT1T2.length)?"#f87171":"rgba(255,255,255,0.7)"},
            ...(isPrincipal&&welfareOpen.length>0?[{v:welfareOpen.length,l:"Welfare flags",col:"#c084fc"}]:[]),
          ].map(({v,l,col})=>(
            <div key={l} style={{textAlign:"center"}}>
              <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,fontWeight:800,color:col,lineHeight:1}}>{v}</div>
              <div style={{fontSize:9,color:"rgba(255,255,255,0.45)",textTransform:"uppercase",letterSpacing:"0.08em",marginTop:2}}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Per-site cards ── */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))",gap:16,marginBottom:20}}>
        {siteData.map(({siteId,site,offToday,offNurses,offRecs,rtwPending,pendingLeave,pendingOT,openT1T2,openT3,upcomingEvents,ps})=>{
          const col = ps.band.color;
          const dow = new Date(today+"T12:00:00").toLocaleDateString("en-GB",{weekday:"short"}).slice(0,3);
          return (
            <div key={siteId} style={{...CS,borderTop:`3px solid ${col}`,padding:18}}>
              {/* Site header */}
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10,gap:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:16,fontWeight:800,color:site.color,marginBottom:2}}>{site.name}</div>
                  <div style={{fontSize:10,color:col,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",lineHeight:1.3}}>{ps.band.label}</div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:32,fontWeight:800,color:col,lineHeight:1,letterSpacing:"-1px"}}>{ps.score}</div>
                  <div style={{fontSize:9,color:C.dim}}>/ 100</div>
                </div>
              </div>

              {/* Score bar */}
              <div style={{height:5,background:C.border,borderRadius:99,overflow:"hidden",marginBottom:14}}>
                <div style={{width:ps.score+"%",height:"100%",background:col,borderRadius:99,transition:"width 0.6s ease"}}/>
              </div>

              {/* Who's off */}
              {offToday.length>0?(
                <div style={{marginBottom:12}}>
                  <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>Off today ({offToday.length})</div>
                  <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                    {offToday.map(s=>{
                      const reason = getAbsenceReason(state,s.id,today)||"Off";
                      const isSick = reason.toLowerCase().includes("sick");
                      return <span key={s.id} style={{fontSize:10,padding:"2px 8px",borderRadius:99,background:isSick?"#fef2f2":"#f0f9ff",color:isSick?"#dc2626":"#0369a1",border:`1px solid ${isSick?"#fecaca":"#bae6fd"}`,fontWeight:600}}>{s.name.split(" ")[0]}</span>;
                    })}
                  </div>
                </div>
              ):(
                <div style={{marginBottom:12,padding:"6px 10px",background:"rgba(0,229,160,0.08)",borderRadius:8,fontSize:11,color:C.green,fontWeight:600}}>Everyone scheduled is in</div>
              )}

              {/* Action items */}
              <div style={{display:"flex",flexDirection:"column",gap:4}}>
                {rtwPending.length>0&&(
                  <button onClick={()=>onNavigate("sickness")} style={{textAlign:"left",padding:"7px 10px",borderRadius:8,border:"1px solid rgba(255,70,70,0.2)",background:"rgba(255,70,70,0.08)",cursor:"pointer",fontFamily:"inherit",fontSize:11,color:"#ff4646",fontWeight:700,display:"flex",justifyContent:"space-between"}}>
                    <span>RTW interview needed</span><span style={{background:"#dc2626",color:"#fff",borderRadius:99,padding:"1px 7px",fontSize:9}}>{rtwPending.length}</span>
                  </button>
                )}
                {pendingLeave>0&&(
                  <button onClick={()=>onNavigate("planning",siteId,"leave")} style={{textAlign:"left",padding:"7px 10px",borderRadius:8,border:"1px solid #fde68a",background:"rgba(245,197,24,0.05)",cursor:"pointer",fontFamily:"inherit",fontSize:11,color:C.warn,fontWeight:700,display:"flex",justifyContent:"space-between"}}>
                    <span>Leave requests pending</span><span style={{background:C.warn,color:"#fff",borderRadius:99,padding:"1px 7px",fontSize:9}}>{pendingLeave}</span>
                  </button>
                )}
                {pendingOT.length>0&&(
                  <button onClick={()=>onNavigate("attendance")} style={{textAlign:"left",padding:"7px 10px",borderRadius:8,border:"1px solid #fde68a",background:"rgba(245,197,24,0.05)",cursor:"pointer",fontFamily:"inherit",fontSize:11,color:C.warn,fontWeight:700,display:"flex",justifyContent:"space-between"}}>
                    <span>⏰ Late finishes to approve</span><span style={{background:C.warn,color:"#fff",borderRadius:99,padding:"1px 7px",fontSize:9}}>{pendingOT.length}</span>
                  </button>
                )}
                {openT1T2.length>0&&(
                  <button onClick={()=>onNavigate("issues")} style={{textAlign:"left",padding:"7px 10px",borderRadius:8,border:`1px solid ${C.border}`,background:C.bg,cursor:"pointer",fontFamily:"inherit",fontSize:11,color:C.text,fontWeight:700,display:"flex",justifyContent:"space-between"}}>
                    <span>🚨 Open issues</span><span style={{background:C.dim,color:"#fff",borderRadius:99,padding:"1px 7px",fontSize:9}}>{openT1T2.length}</span>
                  </button>
                )}
                {openT3.length>0&&(
                  <button onClick={()=>onNavigate("issues")} style={{textAlign:"left",padding:"7px 10px",borderRadius:8,border:"1px solid rgba(255,70,70,0.2)",background:"rgba(255,70,70,0.08)",cursor:"pointer",fontFamily:"inherit",fontSize:11,color:"#ff4646",fontWeight:700,display:"flex",justifyContent:"space-between"}}>
                    <span>🔴 Serious issues open</span><span style={{background:"#dc2626",color:"#fff",borderRadius:99,padding:"1px 7px",fontSize:9}}>{openT3.length}</span>
                  </button>
                )}
                {rtwPending.length===0&&pendingLeave===0&&pendingOT.length===0&&openT1T2.length===0&&openT3.length===0&&(
                  <div style={{padding:"7px 10px",borderRadius:8,background:"rgba(0,229,160,0.08)",fontSize:11,color:C.green,fontWeight:700}}>Nothing needs your attention</div>
                )}
              </div>

              {/* Upcoming events this week */}
              {upcomingEvents.length>0&&(
                <div style={{marginTop:12,paddingTop:12,borderTop:`1px solid ${C.border2}`}}>
                  <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>This week</div>
                  {upcomingEvents.slice(0,2).map(e=>{
                    const et = EVENT_TYPES.find(t=>t.id===e.type)||EVENT_TYPES[EVENT_TYPES.length-1];
                    return (
                      <div key={e.id} style={{display:"flex",alignItems:"center",gap:6,padding:"4px 0",borderBottom:`1px solid ${C.border2}`}}>
                        <span style={{fontSize:12}}>{et.icon}</span>
                        <span style={{fontSize:11,color:C.text,flex:1}}>{e.title}</span>
                        <span style={{fontSize:10,color:C.dim}}>{new Date(e.date+"T12:00:00").toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"})}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Welfare flags (managers/principal only) ── */}
      {canSeeWelfare&&welfareOpen.length>0&&(
        <div style={{...CS,marginBottom:16,borderLeft:"3px solid #7c3aed",padding:"14px 18px"}}>
          <div style={{fontSize:12,fontWeight:700,color:"#a78bfa",marginBottom:8}}>💜 Welfare flags — RTW pending</div>
          {welfareOpen.map(r=>(
            <div key={r.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"5px 0",borderBottom:`1px solid ${C.border2}`,fontSize:12}}>
              <div>
                <span style={{fontWeight:700,color:C.text}}>{r.name}</span>
                <span style={{color:C.dim,marginLeft:8}}>{r.date} · {r.reason}</span>
              </div>
              <button onClick={()=>onNavigate("sickness")} style={{...BTN("#7c3aed",true),fontSize:10,padding:"3px 10px"}}>View →</button>
            </div>
          ))}
        </div>
      )}

      {/* ── Footer note ── */}
      <div style={{fontSize:10,color:C.muted,textAlign:"center",padding:"8px 0"}}>
        Omni Vision · Live picture as of {new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})} · Tap any card to act
      </div>
    </div>
  );
}

/* ── QuietWeekUnpaidModal ─────────────────────────────────────────────── */
function QuietWeekUnpaidModal({flag, state, set, user, onClose}) {
  const {site, weekLabel, spareNurseList=[]} = flag;
  const [offered, setOffered] = useState({});
  const [done, setDone] = useState(false);

  function offerUnpaid(nurseId, dates) {
    if (offered[nurseId]) return;
    const rid = "req"+Date.now()+nurseId;
    set(s=>{
      const ex = s.leave[nurseId]||[];
      const newDays = dates.map(d=>({
        id:"l"+Math.random().toString(36).slice(2),
        requestId:rid, date:d,
        status:"pending", leaveType:"unpaid",
        note:"Unpaid leave offered — quiet week (overstaffed)",
      }));
      return {...s, leave:{...s.leave,[nurseId]:[...ex,...newDays]}};
    });
    setOffered(o=>({...o,[nurseId]:true}));
  }

  function offerAll() {
    spareNurseList.forEach(({nurse,dates})=>offerUnpaid(nurse.id,dates));
    setDone(true);
  }

  const allOffered = spareNurseList.length>0 &&
    spareNurseList.every(({nurse})=>offered[nurse.id]);

  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif"}}>
        <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.14em",
          textTransform:"uppercase",color:"rgba(160,190,255,0.6)",marginBottom:6}}>
          QUIET WEEK · {site.name.toUpperCase()}
        </div>
        <div style={{fontSize:18,fontWeight:700,color:"rgba(255,255,255,0.92)",
          marginBottom:4}}>Offer Unpaid Leave</div>
        <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginBottom:20}}>
          Week commencing {weekLabel} — these nurses are spare on overstaffed days.
        </div>
        {done||allOffered?(
          <div style={{textAlign:"center",padding:"24px 0"}}>
            <div style={{fontSize:36,marginBottom:10}}>✓</div>
            <div style={{fontSize:15,fontWeight:700,color:"rgba(255,255,255,0.88)",
              marginBottom:6}}>Offers sent</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginBottom:20}}>
              Leave requests added as pending — staff will see them in their portal.
            </div>
            <button onClick={onClose}
              style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.12)",
              borderRadius:10,padding:"10px 24px",color:"rgba(255,255,255,0.7)",
              fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer"}}>
              Done
            </button>
          </div>
        ):(
          <>
            <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:20}}>
              {spareNurseList.length===0?(
                <div style={{fontSize:12,color:"rgba(255,255,255,0.35)",padding:"12px 0"}}>
                  No spare nurse data — check rota is fully set up.
                </div>
              ):spareNurseList.map(({nurse,dates})=>{
                const isOffered = offered[nurse.id];
                const dateLabels = dates.slice(0,3)
                  .map(d=>new Date(d+"T12:00").toLocaleDateString("en-GB",
                    {weekday:"short",day:"numeric",month:"short"}))
                  .join(", ")+(dates.length>3?" + "+(dates.length-3)+" more":"");
                return (
                  <div key={nurse.id} style={{
                    display:"flex",alignItems:"center",justifyContent:"space-between",
                    padding:"12px 16px",borderRadius:12,gap:12,
                    background:isOffered
                      ?"rgba(0,229,160,0.06)":"rgba(255,255,255,0.04)",
                    border:`1px solid ${isOffered
                      ?"rgba(0,229,160,0.2)":"rgba(255,255,255,0.08)"}`,
                  }}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:600,
                        color:isOffered?"#00e5a0":"rgba(255,255,255,0.85)",
                        marginBottom:3}}>
                        {nurse.name}
                      </div>
                      <div style={{fontSize:10,color:"rgba(255,255,255,0.35)",
                        whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                        Spare: {dateLabels}
                      </div>
                    </div>
                    <button onClick={()=>offerUnpaid(nurse.id,dates)}
                      disabled={isOffered}
                      style={{
                        flexShrink:0,fontSize:11,fontWeight:600,
                        padding:"6px 14px",borderRadius:8,cursor:isOffered?"default":"pointer",
                        fontFamily:"inherit",whiteSpace:"nowrap",
                        background:isOffered?"rgba(0,229,160,0.12)":"rgba(80,110,255,0.15)",
                        border:`1px solid ${isOffered?"rgba(0,229,160,0.3)":"rgba(80,110,255,0.3)"}`,
                        color:isOffered?"#00e5a0":"rgba(160,190,255,0.9)",
                        opacity:isOffered?0.7:1,
                      }}>
                      {isOffered?"✓ Offered":"Offer leave"}
                    </button>
                  </div>
                );
              })}
            </div>
            {spareNurseList.length>1&&(
              <button onClick={offerAll} style={{
                width:"100%",padding:"11px 0",borderRadius:10,
                background:"rgba(80,110,255,0.15)",
                border:"1px solid rgba(80,110,255,0.3)",
                color:"rgba(160,190,255,0.9)",
                fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
              }}>
                Offer all {spareNurseList.length} nurses at once
              </button>
            )}
          </>
        )}
      </div>
    </Overlay>
  );
}

/* ── QuietWeekCPDModal ──────────────────────────────────────────────────── */
const CPD_TOPICS = [
  "First Aid Refresher","Safeguarding Level 2","Infection Control",
  "Radiography Update","Medical Emergencies","Decontamination",
  "Conscious Sedation","Child Protection","Fire Safety","Manual Handling",
];

function QuietWeekCPDModal({flag, state, set, user, onClose}) {
  const {site, weekLabel, spareNurseList=[], weekStart} = flag;
  const [selNurses, setSelNurses] = useState({});
  const [topic, setTopic]         = useState("");
  const [customTopic, setCustomTopic] = useState("");
  const [selDate, setSelDate]     = useState(weekStart);
  const [done, setDone]           = useState(false);

  const selectedCount = Object.values(selNurses).filter(Boolean).length;
  const finalTopic = topic==="custom" ? customTopic : topic;

  // Build weekday dates for this flag week
  const weekDates = [];
  for(let i=0;i<5;i++){
    const d = new Date(weekStart+"T12:00");
    d.setDate(d.getDate()+i);
    weekDates.push(d.toISOString().split("T")[0]);
  }

  function toggleNurse(id) {
    setSelNurses(s=>({...s,[id]:!s[id]}));
  }

  function scheduleAll() {
    if (!finalTopic.trim()||!selectedCount||!selDate) return;
    const staffIds = spareNurseList
      .filter(({nurse})=>selNurses[nurse.id])
      .map(({nurse})=>nurse.id);
    const ev = {
      id:"cpd"+Date.now(),
      title:finalTopic.trim(),
      cpdType:"training",
      site:flag.siteId,
      staffIds,
      date:selDate,
      allDay:true,
      notes:`Scheduled from quiet week — w/c ${weekLabel}. `
        +`Logged by ${user?.name||"Principal"}.`,
      loggedBy:user?.name||"Principal",
    };
    set(s=>({...s,cpdEvents:[...(s.cpdEvents||[]),ev]}));
    // Also add CPD leave entries for each nurse on that date
    set(s=>{
      let leave = {...s.leave};
      const rid = "req"+Date.now();
      staffIds.forEach(id=>{
        const ex = leave[id]||[];
        leave[id]=[...ex,{
          id:"l"+Math.random().toString(36).slice(2),
          requestId:rid, date:selDate,
          status:"approved", leaveType:"cpd",
          note:finalTopic.trim(),
        }];
      });
      return {...s,leave};
    });
    setDone(true);
  }

  if (done) return (
    <Overlay onClose={onClose}>
      <div style={{textAlign:"center",padding:"24px 0",
        fontFamily:"'Instrument Sans',system-ui,sans-serif"}}>
        <div style={{fontSize:36,marginBottom:10}}>✓</div>
        <div style={{fontSize:15,fontWeight:700,color:"rgba(255,255,255,0.88)",
          marginBottom:6}}>CPD Scheduled</div>
        <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginBottom:20}}>
          {finalTopic} added for {selectedCount} nurse{selectedCount!==1?"s":""} on{" "}
          {new Date(selDate+"T12:00").toLocaleDateString("en-GB",
            {weekday:"long",day:"numeric",month:"short"})}.
        </div>
        <button onClick={onClose}
          style={{background:"rgba(58,173,152,0.12)",border:"1px solid rgba(58,173,152,0.25)",
          borderRadius:10,padding:"10px 24px",color:"#3aad98",
          fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer"}}>
          Done
        </button>
      </div>
    </Overlay>
  );

  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif"}}>
        <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.14em",
          textTransform:"uppercase",color:"rgba(58,173,152,0.6)",marginBottom:6}}>
          QUIET WEEK · {site.name.toUpperCase()}
        </div>
        <div style={{fontSize:18,fontWeight:700,color:"rgba(255,255,255,0.92)",
          marginBottom:4}}>Schedule CPD</div>
        <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginBottom:20}}>
          Week commencing {weekLabel} — select nurses and a topic.
        </div>

        {/* Nurse selection */}
        <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.4)",
          letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:8}}>
          Spare nurses this week
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:18}}>
          {spareNurseList.map(({nurse,dates})=>{
            const sel = selNurses[nurse.id];
            return (
              <div key={nurse.id} onClick={()=>toggleNurse(nurse.id)}
                style={{
                  display:"flex",alignItems:"center",gap:12,
                  padding:"10px 14px",borderRadius:10,cursor:"pointer",
                  background:sel?"rgba(58,173,152,0.08)":"rgba(255,255,255,0.03)",
                  border:`1px solid ${sel?"rgba(58,173,152,0.3)":"rgba(255,255,255,0.07)"}`,
                }}>
                <div style={{
                  width:16,height:16,borderRadius:4,flexShrink:0,
                  background:sel?"#3aad98":"transparent",
                  border:`2px solid ${sel?"#3aad98":"rgba(255,255,255,0.2)"}`,
                  display:"flex",alignItems:"center",justifyContent:"center",
                }}>
                  {sel&&<svg width="9" height="9" viewBox="0 0 12 12">
                    <polyline points="2,6 5,9 10,3" fill="none"
                      stroke="#100f1a" strokeWidth="2" strokeLinecap="round"/>
                  </svg>}
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:12,fontWeight:600,
                    color:sel?"#3aad98":"rgba(255,255,255,0.8)"}}>{nurse.name}</div>
                  <div style={{fontSize:10,color:"rgba(255,255,255,0.3)"}}>
                    Spare {dates.length} day{dates.length!==1?"s":""} this week
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Date picker */}
        <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.4)",
          letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:8}}>
          Which day
        </div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:18}}>
          {weekDates.map(d=>{
            const label = new Date(d+"T12:00")
              .toLocaleDateString("en-GB",{weekday:"short",day:"numeric"});
            const active = selDate===d;
            return (
              <button key={d} onClick={()=>setSelDate(d)} style={{
                padding:"6px 12px",borderRadius:8,fontSize:11,fontWeight:600,
                fontFamily:"inherit",cursor:"pointer",
                background:active?"rgba(58,173,152,0.15)":"rgba(255,255,255,0.04)",
                border:`1px solid ${active?"rgba(58,173,152,0.4)":"rgba(255,255,255,0.08)"}`,
                color:active?"#3aad98":"rgba(255,255,255,0.5)",
              }}>{label}</button>
            );
          })}
        </div>

        {/* Topic picker */}
        <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.4)",
          letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:8}}>
          CPD topic
        </div>
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10}}>
          {CPD_TOPICS.map(t=>{
            const active = topic===t;
            return (
              <button key={t} onClick={()=>{setTopic(active?"":t);}}
                style={{
                  padding:"5px 11px",borderRadius:20,fontSize:11,fontWeight:600,
                  fontFamily:"inherit",cursor:"pointer",
                  background:active?"rgba(58,173,152,0.15)":"rgba(255,255,255,0.04)",
                  border:`1px solid ${active?"rgba(58,173,152,0.4)":"rgba(255,255,255,0.08)"}`,
                  color:active?"#3aad98":"rgba(255,255,255,0.45)",
                }}>
                {t}
              </button>
            );
          })}
          <button onClick={()=>setTopic(topic==="custom"?"":"custom")}
            style={{
              padding:"5px 11px",borderRadius:20,fontSize:11,fontWeight:600,
              fontFamily:"inherit",cursor:"pointer",
              background:topic==="custom"?"rgba(245,197,24,0.12)":"rgba(255,255,255,0.04)",
              border:`1px solid ${topic==="custom"?"rgba(245,197,24,0.4)":"rgba(255,255,255,0.08)"}`,
              color:topic==="custom"?"#f5c518":"rgba(255,255,255,0.45)",
            }}>
            + Custom
          </button>
        </div>
        {topic==="custom"&&(
          <input
            autoFocus
            value={customTopic}
            onChange={e=>setCustomTopic(e.target.value)}
            placeholder="e.g. Impression Techniques, CQC Mock Audit…"
            style={{
              width:"100%",padding:"10px 14px",borderRadius:10,marginBottom:16,
              background:"rgba(255,255,255,0.05)",
              border:"1px solid rgba(255,255,255,0.12)",
              color:"rgba(255,255,255,0.85)",fontSize:13,fontFamily:"inherit",
              outline:"none",boxSizing:"border-box",
            }}
          />
        )}

        <button
          onClick={scheduleAll}
          disabled={!finalTopic.trim()||!selectedCount||!selDate}
          style={{
            width:"100%",padding:"12px 0",borderRadius:10,marginTop:8,
            background:"rgba(58,173,152,0.15)",
            border:"1px solid rgba(58,173,152,0.3)",
            color:!finalTopic.trim()||!selectedCount?"rgba(255,255,255,0.25)":"#3aad98",
            fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
            opacity:!finalTopic.trim()||!selectedCount?0.5:1,
          }}>
          {selectedCount>0&&finalTopic.trim()
            ? `Schedule for ${selectedCount} nurse${selectedCount!==1?"s":""} →`
            : "Select nurses and a topic"}
        </button>
      </div>
    </Overlay>
  );
}

/* ── QuietWeekTaskModal ─────────────────────────────────────────────────── */
const TASK_PRESETS = [
  {label:"Sharps audit",       icon:"🩺"},
  {label:"Stock check",        icon:"📦"},
  {label:"Patient recall calls",icon:"📞"},
  {label:"Decon audit",        icon:"🧪"},
  {label:"Equipment check",    icon:"🔧"},
  {label:"Shadowing session",  icon:"👁️"},
  {label:"Policy review",      icon:"📋"},
  {label:"Locker clean-out",   icon:"🗄️"},
];

function QuietWeekTaskModal({flag, state, set, user, onClose}) {
  const {site, weekLabel, spareNurseList=[]} = flag;
  const [selNurse, setSelNurse] = useState(null);
  const [task, setTask]         = useState("");
  const [customTask, setCustomTask] = useState("");
  const [done, setDone]         = useState(false);

  const finalTask = task==="custom" ? customTask : task;

  function assignTask() {
    if (!selNurse||!finalTask.trim()) return;
    const todayISO = new Date().toISOString().split("T")[0];
    const entry = {
      id:"task"+Date.now(),
      staffId:selNurse.id,
      name:selNurse.name,
      site:flag.siteId,
      task:finalTask.trim(),
      assignedBy:user?.name||"Principal",
      date:todayISO,
      weekOf:flag.weekStart,
      status:"pending",
      note:`Assigned from quiet week — w/c ${weekLabel}`,
    };
    set(s=>({...s,spareTasks:[...(s.spareTasks||[]),entry]}));
    setDone(true);
  }

  if (done) return (
    <Overlay onClose={onClose}>
      <div style={{textAlign:"center",padding:"24px 0",
        fontFamily:"'Instrument Sans',system-ui,sans-serif"}}>
        <div style={{fontSize:36,marginBottom:10}}>✓</div>
        <div style={{fontSize:15,fontWeight:700,color:"rgba(255,255,255,0.88)",
          marginBottom:6}}>Task assigned</div>
        <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginBottom:20}}>
          {finalTask} → {selNurse?.name} for w/c {weekLabel}.
        </div>
        <button onClick={onClose}
          style={{background:"rgba(245,197,24,0.1)",border:"1px solid rgba(245,197,24,0.25)",
          borderRadius:10,padding:"10px 24px",color:"#f5c518",
          fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer"}}>
          Done
        </button>
      </div>
    </Overlay>
  );

  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif"}}>
        <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.14em",
          textTransform:"uppercase",color:"rgba(245,197,24,0.6)",marginBottom:6}}>
          QUIET WEEK · {site.name.toUpperCase()}
        </div>
        <div style={{fontSize:18,fontWeight:700,color:"rgba(255,255,255,0.92)",
          marginBottom:4}}>Assign a Task</div>
        <div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginBottom:20}}>
          Week commencing {weekLabel} — pick a spare nurse and a task.
        </div>

        {/* Nurse selector */}
        <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.4)",
          letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:8}}>
          Spare nurses
        </div>
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:18}}>
          {spareNurseList.map(({nurse,dates})=>{
            const active = selNurse?.id===nurse.id;
            return (
              <button key={nurse.id} onClick={()=>setSelNurse(active?null:nurse)}
                style={{
                  padding:"6px 14px",borderRadius:20,fontSize:12,fontWeight:600,
                  fontFamily:"inherit",cursor:"pointer",
                  background:active?"rgba(245,197,24,0.12)":"rgba(255,255,255,0.04)",
                  border:`1px solid ${active?"rgba(245,197,24,0.4)":"rgba(255,255,255,0.08)"}`,
                  color:active?"#f5c518":"rgba(255,255,255,0.6)",
                }}>
                {nurse.name.split(" ")[0]}
                <span style={{fontSize:9,opacity:0.6,marginLeft:5}}>
                  {dates.length}d spare
                </span>
              </button>
            );
          })}
        </div>

        {/* Task presets */}
        <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.4)",
          letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:8}}>
          Task
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",
          gap:6,marginBottom:10}}>
          {TASK_PRESETS.map(({label,icon})=>{
            const active = task===label;
            return (
              <button key={label} onClick={()=>setTask(active?"":label)}
                style={{
                  padding:"9px 12px",borderRadius:10,fontSize:11,fontWeight:600,
                  fontFamily:"inherit",cursor:"pointer",textAlign:"left",
                  display:"flex",alignItems:"center",gap:8,
                  background:active?"rgba(245,197,24,0.1)":"rgba(255,255,255,0.03)",
                  border:`1px solid ${active?"rgba(245,197,24,0.35)":"rgba(255,255,255,0.07)"}`,
                  color:active?"#f5c518":"rgba(255,255,255,0.5)",
                }}>
                <span>{icon}</span><span>{label}</span>
              </button>
            );
          })}
          <button onClick={()=>setTask(task==="custom"?"":"custom")}
            style={{
              padding:"9px 12px",borderRadius:10,fontSize:11,fontWeight:600,
              fontFamily:"inherit",cursor:"pointer",textAlign:"left",
              display:"flex",alignItems:"center",gap:8,
              background:task==="custom"?"rgba(245,197,24,0.1)":"rgba(255,255,255,0.03)",
              border:`1px solid ${task==="custom"?"rgba(245,197,24,0.35)":"rgba(255,255,255,0.07)"}`,
              color:task==="custom"?"#f5c518":"rgba(255,255,255,0.5)",
            }}>
            <span>✏️</span><span>Custom task</span>
          </button>
        </div>
        {task==="custom"&&(
          <input
            autoFocus
            value={customTask}
            onChange={e=>setCustomTask(e.target.value)}
            placeholder="e.g. Sharps audit, Patient recall calls…"
            style={{
              width:"100%",padding:"10px 14px",borderRadius:10,marginBottom:16,
              background:"rgba(255,255,255,0.05)",
              border:"1px solid rgba(255,255,255,0.12)",
              color:"rgba(255,255,255,0.85)",fontSize:13,fontFamily:"inherit",
              outline:"none",boxSizing:"border-box",
            }}
          />
        )}

        <button
          onClick={assignTask}
          disabled={!selNurse||!finalTask.trim()}
          style={{
            width:"100%",padding:"12px 0",borderRadius:10,marginTop:6,
            background:"rgba(245,197,24,0.1)",
            border:"1px solid rgba(245,197,24,0.25)",
            color:!selNurse||!finalTask.trim()?"rgba(255,255,255,0.25)":"#f5c518",
            fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"inherit",
            opacity:!selNurse||!finalTask.trim()?0.5:1,
          }}>
          {selNurse&&finalTask.trim()
            ? `Assign to ${selNurse.name.split(" ")[0]} →`
            : "Select a nurse and task"}
        </button>
      </div>
    </Overlay>
  );
}

function DashLatenessModal({state,set,user,isPrincipal,personId,setPersonId,onClose}) {
  const todayStr = new Date().toISOString().split("T")[0];
  const userSites = isPrincipal ? Object.keys(SITES) : (user?.managerSites||[user?.site]).filter(Boolean);
  const allNurses = state.nurses||NURSES_BASE;

  // Get staff rostered today across user's sites — safe wrapper
  const rosteredToday = userSites.flatMap(siteId=>{
    try {
      const dr = buildDayRota(todayStr, siteId, allNurses, state);
      return [...(dr.pairings||[]).map(p=>p.nurse).filter(Boolean),...(dr.spares||[])];
    } catch(e) { return []; }
  }).filter((s,i,arr)=>arr.findIndex(x=>x.id===s.id)===i);
  const rosteredIds = new Set(rosteredToday.map(s=>s.id));

  // All staff at user's sites (contracted), deduped
  const allSiteStaff = [
    ...allNurses.filter(n=>userSites.includes(n.site)&&!n.maternity&&!n.archived),
    ...RECS_BASE.filter(r=>userSites.includes(r.site)&&!r.archived),
    ...(state.customRecs||[]).filter(r=>userSites.includes(r.site)&&!r.archived),
    ...getClinicians(state).filter(c=>!c._deleted&&userSites.some(s=>c.site===s||(c.sites||[]).includes(s))),
  ].filter((s,i,arr)=>arr.findIndex(x=>x.id===s.id)===i);

  // Quick-tap: rostered today first, then others
  const quickStaff = [
    ...rosteredToday.filter((s,i,arr)=>arr.findIndex(x=>x.id===s.id)===i),
    ...allSiteStaff.filter(s=>!rosteredIds.has(s.id)).slice(0,6),
  ].filter((s,i,arr)=>arr.findIndex(x=>x.id===s.id)===i).slice(0,12);

  const [phase, setPhase] = useState("pick"); // pick | form | pattern
  const [search, setSearch] = useState("");
  const [person, setPerson] = useState(null);
  const [pattern, setPattern] = useState(null);
  const [f, setF] = useState({date:todayStr,type:"late",minsLate:15,reason:"No reason given",notes:""});
  const sf = (k,v) => setF(p=>({...p,[k]:v}));

  const searchResults = search.length>1
    ? allSiteStaff.filter(s=>s.name.toLowerCase().includes(search.toLowerCase())).slice(0,6)
    : [];

  function selectPerson(p) {
    setPerson(p);
    setSearch("");
    setPhase("form");
  }

  function save() {
    if (!person) return;
    const isClin=!!(person.sched||["GDS","MOS","FD","Therapist","Partner","Specialist"].includes(person.type));
    const role=isClin?"clinician":(person.area||person.status)?"nurse":"reception";
    const record={...f,id:"lat"+Date.now(),staffId:person.id,name:person.name,site:person.site,role,loggedBy:user?.name||"Manager"};
    set(s=>({...s,latenessLog:[...(s.latenessLog||[]),record]}));
    // Run pattern analysis immediately after save
    const updatedState={...state,latenessLog:[...(state.latenessLog||[]),record]};
    const analysis = analyseLatenessPatterns(person.id, updatedState);
    if (analysis.riskLevel==="medium"||analysis.riskLevel==="high") {
      setPattern({analysis, person});
      setPhase("pattern");
    } else {
      onClose();
    }
  }

  function flagWelfare() {
    if (!pattern) return;
    set(s=>({...s,staffManualFlags:{...(s.staffManualFlags||{}),[pattern.person.id]:{color:"amber",reason:`Lateness pattern: ${pattern.analysis.patterns[0]?.msg||"recurring lateness"}`,setBy:user?.name||"Manager",setAt:new Date().toISOString()}}}));
    onClose();
  }

  // Phase: person picker
  if (phase==="pick") return (
    <Overlay onClose={onClose}>
      {/* Iris header — matches Log Sick */}
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
        <div style={{width:44,height:44,borderRadius:14,background:`linear-gradient(135deg,${C.teal},${C.grad2})`,display:"flex",alignItems:"center",justifyContent:"center",overflow:"hidden"}}>
          <LogoMark size={36}/>
        </div>
        <div>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading}}>Log Lateness</div>
          <div style={{fontSize:12,color:C.dim}}>Who was late today?</div>
        </div>
      </div>

      {/* Date */}
      <div style={{marginBottom:14}}>
        <label style={LB}>Date</label>
        <input type="date" value={f.date} onChange={e=>sf("date",e.target.value)} style={IS}/>
      </div>

      {/* Search */}
      <input value={search} onChange={e=>setSearch(e.target.value)}
        placeholder="Type a name…"
        style={{...IS,marginBottom:search.length>1&&searchResults.length>0?8:0,fontSize:14}}/>
      {search.length>1&&searchResults.length>0&&(
        <div style={{marginBottom:14,display:"flex",flexDirection:"column",gap:4,marginTop:8}}>
          {searchResults.map(s=>(
            <button key={s.id} onClick={()=>selectPerson(s)}
              style={{padding:"10px 14px",borderRadius:10,border:`1px solid ${C.border}`,background:C.bg,color:C.text,fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer",textAlign:"left",display:"flex",justifyContent:"space-between"}}>
              <span>{s.name}</span>
              <span style={{fontSize:11,color:C.dim}}>{SITES[s.site]?.name}</span>
            </button>
          ))}
        </div>
      )}

      {/* Quick-tap pills */}
      {!search&&(
        <div style={{marginBottom:14}}>
          {rosteredToday.filter((s,i,arr)=>arr.findIndex(x=>x.id===s.id)===i).length>0&&(
            <>
              <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8,marginTop:4}}>On site today</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:12}}>
                {rosteredToday.filter((s,i,arr)=>arr.findIndex(x=>x.id===s.id)===i).map(s=>(
                  <button key={s.id} onClick={()=>selectPerson(s)} style={{
                    padding:"9px 16px",borderRadius:99,fontFamily:"inherit",fontSize:13,fontWeight:700,cursor:"pointer",
                    border:`1.5px solid ${SITES[s.site]?.color||C.teal}44`,
                    background:`${SITES[s.site]?.color||C.teal}12`,color:SITES[s.site]?.color||C.teal,
                  }}>{s.name.split(" ")[0]}</button>
                ))}
              </div>
            </>
          )}
          {allSiteStaff.filter(s=>!rosteredIds.has(s.id)).length>0&&(
            <>
              <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>All staff</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:14}}>
                {allSiteStaff.filter(s=>!rosteredIds.has(s.id)).slice(0,12).map(s=>(
                  <button key={s.id} onClick={()=>selectPerson(s)} style={{
                    padding:"7px 13px",borderRadius:99,fontFamily:"inherit",fontSize:12,fontWeight:600,cursor:"pointer",
                    border:`1px solid ${C.border}`,background:"transparent",color:C.mid,
                  }}>{s.name.split(" ")[0]}</button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <div style={{marginTop:4}}>
        <button onClick={onClose} style={{...BTN(C.dim,true),width:"100%"}}>Cancel</button>
      </div>
    </Overlay>
  );

  // Phase: form
  if (phase==="form"&&person) return (
    <Overlay onClose={onClose}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
        <button onClick={()=>setPhase("pick")} style={{background:"none",border:"none",color:C.dim,cursor:"pointer",fontSize:20,padding:0,lineHeight:1}}>←</button>
        <div>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading}}>{person.name}</div>
          <div style={{fontSize:12,color:SITES[person.site]?.color||C.dim}}>{SITES[person.site]?.name} · {person.type||person.area||"Staff"}</div>
        </div>
      </div>
      <div style={{marginBottom:12}}><label style={LB}>Date</label><input type="date" value={f.date} onChange={e=>sf("date",e.target.value)} style={IS}/></div>
      <div style={{marginBottom:12}}><label style={LB}>Type</label>
        <select value={f.type} onChange={e=>sf("type",e.target.value)} style={IS}>
          <option value="late">Late arrival</option>
          <option value="unauthorised">Unauthorised absence</option>
        </select>
      </div>
      {f.type==="late"&&<div style={{marginBottom:12}}><label style={LB}>Minutes late</label>
        <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:6}}>
          {[5,10,15,20,30,45,60].map(m=>(
            <button key={m} onClick={()=>sf("minsLate",m)} style={{padding:"6px 12px",borderRadius:8,fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer",border:`1px solid ${f.minsLate===m?C.warn:C.border}`,background:f.minsLate===m?"rgba(245,197,24,0.12)":"transparent",color:f.minsLate===m?C.warn:C.mid}}>{m}m</button>
          ))}
          <input type="number" min="1" value={f.minsLate} onChange={e=>sf("minsLate",+e.target.value)} style={{...IS,width:70,marginBottom:0}} placeholder="other"/>
        </div>
      </div>}
      <div style={{marginBottom:12}}><label style={LB}>Reason given</label>
        <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:6}}>
          {LATE_REASONS.map(r=>(
            <button key={r} onClick={()=>sf("reason",r)} style={{padding:"6px 12px",borderRadius:8,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${f.reason===r?C.teal:C.border}`,background:f.reason===r?C.teal+"18":"transparent",color:f.reason===r?C.teal:C.mid}}>{r}</button>
          ))}
        </div>
      </div>
      <div style={{marginBottom:16}}>
        <label style={LB}>Internal notes <span style={{fontWeight:400,color:C.dim}}>(manager only — never shown to staff)</span></label>
        <textarea value={f.notes} onChange={e=>sf("notes",e.target.value)} rows={3}
          placeholder="e.g. Arrived flustered, mentioned childcare difficulties. Worth monitoring."
          style={{...IS,resize:"none",lineHeight:1.5,fontSize:12}}/>
      </div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={save} style={{...BTN(C.warn),flex:1,padding:"11px 0",fontSize:13}}>Log lateness</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );

  // Phase: pattern alert
  if (phase==="pattern"&&pattern) return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Pattern detected</div>
      <div style={{fontSize:12,color:C.dim,marginBottom:16}}>Lateness logged for {pattern.person.name}</div>
      <div style={{background:"rgba(245,197,24,0.08)",border:"1px solid rgba(245,197,24,0.25)",borderRadius:12,padding:"14px 16px",marginBottom:16}}>
        {pattern.analysis.patterns.map((p,i)=>(
          <div key={i} style={{fontSize:12,color:C.warn,lineHeight:1.6,marginBottom:i<pattern.analysis.patterns.length-1?8:0}}>
            ⚠️ {p.msg}
          </div>
        ))}
      </div>
      <div style={{fontSize:12,color:C.dim,marginBottom:16,lineHeight:1.6}}>
        Omni has detected a lateness pattern for {pattern.person.name.split(" ")[0]}. You can flag this for a welfare conversation now, or leave it for later — it will still show on their profile.
      </div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={flagWelfare} style={{...BTN(C.warn),flex:1,padding:"11px 0",fontSize:13}}>🟠 Flag for welfare</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Not now</button>
      </div>
    </Overlay>
  );

  return null;
}

function Dashboard({state,set,isPrincipal,user,onNavigate,sbStatus,welcomeBanner}) {
  const [locumM,setLocumM]   = useState(false);
  const [settM,setSettM]     = useState(false);
  const [pinSearch,setPinSearch] = useState("");
  const [coverM,setCoverM]   = useState(false);
  const [emergM,setEmergM]   = useState(false);
  const [latenessM,setLatenessM] = useState(false);
  const [latenessPersonId,setLatenessPersonId] = useState("");
  const [myLeaveM,setMyLeaveM] = useState(false);
  const [bhM,setBhM]         = useState(false);
  const [collegeM,setCollegeM] = useState(false);
  const [drillSite,setDrillSite] = useState(null);
  const [confidenceDrill,setConfidenceDrill] = useState(null);
  const [quietUnpaidM,setQuietUnpaidM] = useState(null);
  const [quietCPDM,setQuietCPDM]     = useState(null);
  const [quietTaskM,setQuietTaskM]   = useState(null);
  const [openArticle,setOpenArticle] = useState(null);
  const [drillWeek,setDrillWeek]     = useState(null);
  const locums = state.locums||[];
  const todayStr = new Date().toISOString().split("T")[0];
  const liveDay = CAL.days.find(d=>d.date>=todayStr&&!d.bh)||CAL.days[0];
  const liveDate = liveDay?.date||"2026-04-07";

  // ── Inline leave approval helpers ──
  const allLeaveStaff = [
    ...(state.nurses||NURSES_BASE).filter(n=>!n.maternity&&!n.archived),
    ...(state.customRecs||[]).filter(r=>!r.archived),
    ...RECS_BASE,
    ...getClinicians(state).filter(c=>!c._deleted),
  ];
  const pendingLeaveGroups = Object.values(
    allLeaveStaff.flatMap(s=>(state.leave[s.id]||[]).filter(l=>l.status==="pending").map(l=>({...l,person:s})))
      .reduce((acc,l)=>{
        const key=l.requestId||l.id;
        if(!acc[key]) acc[key]={dates:[],person:l.person,requestId:key,leaveType:l.leaveType||"annual",note:l.note||""};
        acc[key].dates.push(l.date);
        return acc;
      },{})
  ).sort((a,b)=>a.dates[0]?.localeCompare(b.dates[0]));

  function quickApprove(requestId,personId) {
    set(s=>({...s,leave:{...s.leave,[personId]:(s.leave[personId]||[]).map(l=>
      (l.requestId===requestId||l.id===requestId)?{...l,status:"approved",decidedBy:user?.name||"Principal",decidedAt:new Date().toISOString()}:l
    )}}));
  }
  function quickDecline(requestId,personId) {
    set(s=>({...s,leave:{...s.leave,[personId]:(s.leave[personId]||[]).map(l=>
      (l.requestId===requestId||l.id===requestId)?{...l,status:"declined",decidedBy:user?.name||"Principal",decidedAt:new Date().toISOString()}:l
    )}}));
  }

  // Iris Score™ engine
  const siteScores = Object.entries(SITES).map(([id,site])=>{
    const ps = calcIrisScore(id, liveDate, state);
    const cs = calcStaffingConfidence(id, liveDate, state);
    const nurses = Array.isArray(state.nurses) ? state.nurses : NURSES_BASE;
    const dr = buildDayRota(liveDate, id, nurses, state);
    const roomOverflows = dr.flags.filter(f=>f.type==="roomOverflow");
    return { id, site, ps, cs, roomOverflows };
  });

  // Log today's Iris Scores to history (runs once per day)
  useEffect(()=>{
    if (!state || !liveDate || !set) return;
    const history = state.irisScoreHistory||[];
    if (history.some(h=>h.date===liveDate)) return;
    const newEntries = siteScores.map(({id,ps})=>({
      date: liveDate,
      site: id,
      score: ps.score,
      band: ps.band.tier,
      factors: ps.factors.map(f=>f.msg),
    }));
    set(s=>({
      irisScoreHistory: [...(s.irisScoreHistory||[]), ...newEntries]
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveDate]);

  const pending = Object.values(state.leave||{}).flat().filter(l=>l.status==="pending").length;
  const rtwP = (state.sick||[]).filter(s=>!s.rtw).length;
  const pendingLeaveCount = Object.values(state.leave||{}).flat().filter(l=>l.status==="pending").length;

  // ── ISSUE INBOX BANNER ──────────────────────────────────────────────────
  // Counts unacknowledged issues this user can see at their site, surfaced as
  // a top banner so it's caught on next login. Uses same canSee logic as the
  // Issues tab to stay consistent.
  const issueInbox = (()=>{
    const isManagerHere = user?.role==="manager"||user?.role==="managerLite";
    const isViewerHere  = (state.issueViewers||[]).includes(user?.staffId);
    if (!isPrincipal && !isManagerHere && !isViewerHere) return null;
    const allIssues = state.issues||[];
    const unacked = allIssues.filter(i=>{
      if (i.status==="resolved") return false;
      if (i.acknowledgedAt) return false;
      // Site filter — non-principals scoped to their site
      if (!isPrincipal && i.site!==user?.site) return false;
      // Tier filter — same as canSee
      if (i.tier===1) return true;
      if (isPrincipal) return true;
      if (isManagerHere && i.tier<=2) return true;
      if (isViewerHere && i.tier<=2) {
        // Trusted viewers don't see interpersonal T2s
        const scope = getCategoryScope(i.category);
        return scope === "operational";
      }
      return false;
    });
    if (unacked.length===0) return null;
    const t3Count = unacked.filter(i=>i.tier===3).length;
    const t2Count = unacked.filter(i=>i.tier===2).length;
    const t1Count = unacked.filter(i=>i.tier===1).length;
    return {total:unacked.length, t1:t1Count, t2:t2Count, t3:t3Count};
  })();

  const delegationBanner = user?._delegationId ? (
    <div style={{background:"rgba(139,92,246,0.1)",border:"1px solid rgba(139,92,246,0.3)",borderRadius:12,padding:"12px 18px",
      marginBottom:16,display:"flex",alignItems:"center",gap:12}}>
      <Icon name="award" size={20} color="#7c3aed"/>
      <div>
        <div style={{fontSize:13,fontWeight:700,color:"#a78bfa"}}>You're acting as manager today</div>
        <div style={{fontSize:11,color:"#a78bfa"}}>Delegated by {user._delegatedBy||"your manager"} · You have full leave approval and rota access for this site. Well done for stepping up.</div>
      </div>
    </div>
  ) : null;
  const monthStr = liveDate.slice(0,7);
  const monthCost = locums.filter(l=>l.date.startsWith(monthStr)).reduce((s,l)=>s+l.cost,0);
  const totCost = locums.reduce((s,l)=>s+l.cost,0);

  // ── Who's off today ──
  const allStaff = [
    ...getClinicians(state).filter(c=>!c._deleted),
    ...(state.nurses||NURSES_BASE).filter(n=>!n.maternity&&!n.archived),
    ...(state.customStaff||[]).filter(n=>!n.archived),
    ...RECS_BASE,
  ];
  const REASON_ICON = r => {
    if (!r) return "→";
    const rl = r.toLowerCase();
    if (rl.includes("annual")||rl.includes("holiday")) return "→";
    if (rl.includes("sick")||rl.includes("illness")) return "·";
    if (rl.includes("college")) return "→";
    if (rl.includes("cpd")||rl.includes("training")) return "→";
    if (rl.includes("maternity")) return "→";
    if (rl.includes("medical")||rl.includes("appointment")) return "·";
    if (rl.includes("compassionate")) return "→";
    return "→";
  };
  const offToday = allStaff.filter(s=>s.id&&isAbsent(state,s.id,liveDate)).map(s=>{
    const reason = getAbsenceReason(state,s.id,liveDate)||"Absent";
    return {...s,reason};
  });

  // ── Overtime/lateness insights for dashboard ──
  const pendingOT = (state.overtime||[]).filter(r=>r.status==="pending");
  const recentOT  = (state.overtime||[]).filter(r=>r.date>=liveDate.slice(0,7)+"-01");
  const latenessRecent = (state.latenessLog||[]).filter(r=>r.date>=liveDate.slice(0,7)+"-01");
  // Top late reasons this month
  const lateReasonCounts = {};
  recentOT.forEach(r=>{ lateReasonCounts[r.reason]=(lateReasonCounts[r.reason]||0)+1; });
  const topLateReasons = Object.entries(lateReasonCounts).sort((a,b)=>b[1]-a[1]).slice(0,3);
  // Which dentists are causing most overtime
  const dentistOTCounts = {};
  recentOT.filter(r=>r.clinicianId).forEach(r=>{ dentistOTCounts[r.clinicianId]=(dentistOTCounts[r.clinicianId]||0)+r.hrs; });
  const topDentistOT = Object.entries(dentistOTCounts).sort((a,b)=>b[1]-a[1]).slice(0,2);

  return (
    <div style={{
      minHeight:"100vh",
      background:"#080710",
      fontFamily:"'Instrument Sans',system-ui,sans-serif",
      position:"relative",
      overflow:"hidden",
    }}>
      {/* Ambient glow */}
      <div style={{position:"fixed",inset:0,zIndex:0,pointerEvents:"none",
        background:"radial-gradient(ellipse 80% 50% at 60% 10%, rgba(58,173,152,0.07) 0%, transparent 60%), radial-gradient(ellipse 40% 30% at 10% 80%, rgba(100,80,220,0.05) 0%, transparent 55%)"}}/>

      <div style={{position:"relative",zIndex:1,padding:"24px 28px",maxWidth:1100,margin:"0 auto"}}>

        {/* v10.3 — first-touch welcome banner shown after the cinematic */}
        {welcomeBanner && (
          <WelcomeBanner user={user} state={state} onDismiss={welcomeBanner.onDismiss}/>
        )}
        {/* ── ISSUES INBOX BANNER — visible to principals/managers/trusted viewers ── */}
        {issueInbox && (
          <div onClick={()=>onNavigate&&onNavigate("issues")}
            style={{
              cursor:"pointer",
              background:issueInbox.t3>0?"rgba(255,70,70,0.08)":"rgba(245,197,24,0.06)",
              border:`1px solid ${issueInbox.t3>0?"rgba(255,70,70,0.2)":"rgba(245,197,24,0.2)"}`,
              borderRadius:12,padding:"12px 18px",marginBottom:16,
              display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,
            }}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <Icon name="warning" size={20} color={issueInbox.t3>0?"#ff4646":"#f5c518"}/>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:issueInbox.t3>0?"#ff4646":"#f5c518"}}>
                  {issueInbox.total} issue{issueInbox.total!==1?"s":""} need{issueInbox.total===1?"s":""} your attention
                </div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.6)",marginTop:2}}>
                  {[
                    issueInbox.t3>0&&`${issueInbox.t3} Serious`,
                    issueInbox.t2>0&&`${issueInbox.t2} Concern`,
                    issueInbox.t1>0&&`${issueInbox.t1} Operational`,
                  ].filter(Boolean).join(" · ")} — tap to review
                </div>
              </div>
            </div>
            <div style={{fontSize:14,color:"rgba(255,255,255,0.5)"}}>→</div>
          </div>
        )}

        {/* ── IRIS ROTA SETUP BANNER — shows when no clinicians entered yet ── */}
        {isPrincipal && !IS_DCO && (getClinicians(state)||[]).filter(c=>!c._deleted).length === 0 && (
          <div style={{
            background:"linear-gradient(135deg,rgba(58,173,152,0.12),rgba(99,102,241,0.08))",
            border:"1px solid rgba(58,173,152,0.25)",borderRadius:14,
            padding:"16px 20px",marginBottom:24,
            display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12,
          }}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:36,height:36,borderRadius:"50%",background:"linear-gradient(135deg,#00d4ff,#a855f7,#f472b6)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              </div>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:"rgba(255,255,255,0.92)",marginBottom:2}}>Your rota isn't set up yet</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.45)"}}>Iris can walk you through adding your dentists, nurses, keyholders and competencies — takes about 5 minutes.</div>
              </div>
            </div>
            <button
              onClick={()=>{
                const ev = new CustomEvent("omni:iris-rota-setup");
                window.dispatchEvent(ev);
              }}
              style={{
                background:"linear-gradient(135deg,#3aad98,#a855f7)",border:"none",borderRadius:10,
                padding:"10px 20px",cursor:"pointer",color:"#fff",
                fontSize:12,fontWeight:700,fontFamily:"inherit",flexShrink:0,
                boxShadow:"0 4px 16px rgba(58,173,152,0.3)",
              }}>
              Set up rota with Iris →
            </button>
          </div>
        )}

        {/* ── TOP BAR ── */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:28,flexWrap:"wrap",gap:12}}>
          <div>
            <div style={{fontFamily:"'Instrument Serif',Georgia,serif",fontStyle:"italic",fontWeight:700,fontSize:28,color:"#fff",letterSpacing:"-0.02em",lineHeight:1.1}}>
              Good {new Date().getHours()<12?"morning":new Date().getHours()<17?"afternoon":"evening"}, {user.name.split(" ")[0]}.
            </div>
            <div style={{display:"flex",alignItems:"center",gap:10,marginTop:6}}>
              <span style={{fontSize:12,color:"rgba(255,255,255,0.35)"}}>{liveDay?.label}</span>
              <span style={{width:3,height:3,borderRadius:"50%",background:"rgba(255,255,255,0.15)"}}/>
              <span style={{fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:10,
                background:sbStatus==="live"?"rgba(58,173,152,0.15)":sbStatus==="offline"?"rgba(245,166,35,0.15)":"rgba(99,102,241,0.15)",
                color:sbStatus==="live"?"#3aad98":sbStatus==="offline"?"#f5a623":"#a5b4fc"}}>
                {sbStatus==="live"?"● LIVE":sbStatus==="offline"?"● LOCAL":"● SYNC"}
              </span>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
            <button onClick={()=>setCoverM(true)} style={{
              display:"flex",alignItems:"center",gap:7,
              padding:"9px 18px",borderRadius:10,
              border:"1px solid rgba(255,70,70,0.5)",
              background:"rgba(255,70,70,0.18)",color:"#ff5b5b",
              fontFamily:"inherit",fontSize:13,fontWeight:700,cursor:"pointer",
              boxShadow:"0 0 12px rgba(255,70,70,0.2)",
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
              Log Sick
            </button>
            <button onClick={()=>setLatenessM(true)} style={{
              display:"flex",alignItems:"center",gap:7,
              padding:"9px 16px",borderRadius:10,
              border:"1px solid rgba(245,197,24,0.4)",
              background:"rgba(245,197,24,0.1)",color:"#f5c518",
              fontFamily:"inherit",fontSize:13,fontWeight:700,cursor:"pointer",
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              Log Late
            </button>
            {isPrincipal&&<button onClick={()=>setMyLeaveM(true)} style={{
              display:"flex",alignItems:"center",gap:7,
              padding:"9px 16px",borderRadius:10,border:"1px solid rgba(58,173,152,0.25)",
              background:"rgba(58,173,152,0.08)",color:"#3aad98",
              fontFamily:"inherit",fontSize:12,fontWeight:600,cursor:"pointer",
            }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="16" y1="2" x2="16" y2="6"/></svg>
              My Leave
            </button>}
            <button onClick={()=>setLocumM(true)} style={{
              display:"flex",alignItems:"center",gap:7,
              padding:"9px 14px",borderRadius:10,border:"1px solid rgba(255,255,255,0.08)",
              background:"rgba(255,255,255,0.04)",color:"rgba(255,255,255,0.5)",
              fontFamily:"inherit",fontSize:12,fontWeight:600,cursor:"pointer",
            }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Log Locum
            </button>
            {isPrincipal&&<button onClick={()=>setSettM(true)} style={{
              padding:"9px 12px",borderRadius:10,border:"1px solid rgba(255,255,255,0.08)",
              background:"rgba(255,255,255,0.04)",color:"rgba(255,255,255,0.4)",cursor:"pointer",
              display:"flex",alignItems:"center",
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d={SVG_GEAR_PATH}/></svg>
            </button>}
          </div>
        </div>

        {/* ── UPDATES STRIP — principal only ── */}
        {isPrincipal&&(()=>{
          const items = [...(state.newsItems||[])]
            .sort((a,b)=>{
              if(a.pinned&&!b.pinned) return -1;
              if(!a.pinned&&b.pinned) return 1;
              return b.date.localeCompare(a.date);
            })
            .slice(0,8);
          if(!items.length) return null;
          const CAT_STYLE = {
            law:     {label:"Employment Law", color:"#f472b6"},
            dental:  {label:"Dental News",    color:"#60a5fa"},
            howto:   {label:"How-to",         color:"#00e5a0"},
            podcast: {label:"Podcast",        color:"#a78bfa"},
            omni:    {label:"Omni Update",    color:"#f5c518"},
          };
          return (
            <div style={{marginBottom:20}}>
              <div style={{
                fontSize:10,fontWeight:700,letterSpacing:"0.1em",
                textTransform:"uppercase",color:"rgba(255,255,255,0.22)",
                marginBottom:10,
              }}>Updates</div>
              <div style={{
                display:"flex",gap:10,overflowX:"auto",
                paddingBottom:4,scrollbarWidth:"none",
                WebkitOverflowScrolling:"touch",
              }}>
                {items.map(item=>{
                  const cs = CAT_STYLE[item.category]||CAT_STYLE.omni;
                  return (
                    <div key={item.id}
                      onClick={()=>setOpenArticle(item)}
                      style={{
                        flexShrink:0,width:260,
                        padding:"12px 14px",borderRadius:12,
                        background:"rgba(255,255,255,0.03)",
                        border:"1px solid rgba(255,255,255,0.07)",
                        cursor:"pointer",
                        transition:"border-color 0.15s",
                        position:"relative",
                      }}
                      onMouseEnter={e=>{
                        e.currentTarget.style.borderColor="rgba(255,255,255,0.18)";
                      }}
                      onMouseLeave={e=>{
                        e.currentTarget.style.borderColor="rgba(255,255,255,0.07)";
                      }}
                    >
                      {item.pinned&&(
                        <div style={{
                          position:"absolute",top:10,right:10,
                          fontSize:8,fontWeight:700,letterSpacing:"0.08em",
                          color:"rgba(245,197,24,0.6)",
                          textTransform:"uppercase",
                        }}>★ NEW</div>
                      )}
                      <div style={{
                        display:"inline-block",
                        fontSize:9,fontWeight:700,letterSpacing:"0.1em",
                        textTransform:"uppercase",
                        color:cs.color,background:cs.color+"18",
                        borderRadius:99,padding:"2px 8px",marginBottom:7,
                      }}>
                        {cs.label}
                      </div>
                      <div style={{
                        fontSize:12,fontWeight:700,
                        color:"rgba(255,255,255,0.85)",
                        lineHeight:1.35,marginBottom:5,
                      }}>
                        {item.title}
                      </div>
                      <div style={{
                        fontSize:10,color:"rgba(255,255,255,0.38)",
                        lineHeight:1.5,
                      }}>
                        {item.summary}
                      </div>
                      <div style={{
                        fontSize:9,color:"rgba(255,255,255,0.2)",
                        marginTop:6,fontWeight:600,
                      }}>
                        Read →
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* ── QUIET WEEK DRILL-DOWN MODAL ── */}
        {drillWeek&&(()=>{
          const f = drillWeek;
          const nurseHrRate = state.settings?.nurseHourlyRate||14;
          const hrsPerDay = 7.5;
          // Build day → nurses map from spareNurseList
          const dayMap = {};
          (f.spareNurseList||[]).forEach(({nurse,dates})=>{
            dates.forEach(d=>{
              if(!dayMap[d]) dayMap[d]=[];
              dayMap[d].push(nurse);
            });
          });
          const sortedDays = Object.keys(dayMap).sort();
          const DOW = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
          return (
            <Overlay onClose={()=>setDrillWeek(null)}>
              <div style={{maxWidth:520,width:"100%"}}>
                {/* Header */}
                <div style={{marginBottom:20}}>
                  <div style={{
                    fontSize:9,fontWeight:700,letterSpacing:"0.12em",
                    textTransform:"uppercase",color:"rgba(100,160,255,0.6)",
                    marginBottom:6,
                  }}>Overstaffing detail</div>
                  <div style={{
                    fontFamily:"'Instrument Serif',Georgia,serif",
                    fontStyle:"italic",fontSize:22,fontWeight:700,
                    color:C.heading,lineHeight:1.2,marginBottom:4,
                  }}>
                    {f.site.name} — w/c {f.weekLabel}
                  </div>
                  <div style={{
                    fontSize:13,color:"#ff9a9a",fontWeight:700,
                  }}>
                    ~£{f.wageCost.toLocaleString()} in idle wages this week
                  </div>
                </div>

                {/* Day-by-day breakdown */}
                <div style={{marginBottom:20}}>
                  <div style={{
                    fontSize:10,fontWeight:700,letterSpacing:"0.08em",
                    textTransform:"uppercase",color:C.dim,marginBottom:10,
                  }}>Day breakdown</div>
                  {sortedDays.map(date=>{
                    const nurses = dayMap[date];
                    const dayCost = Math.round(
                      nurses.length * hrsPerDay * nurseHrRate
                    );
                    const dow = DOW[new Date(date+"T12:00").getDay()];
                    const dateLabel = new Date(date+"T12:00")
                      .toLocaleDateString("en-GB",{
                        weekday:"short",day:"numeric",month:"short",
                      });
                    return (
                      <div key={date} style={{
                        display:"flex",alignItems:"center",gap:12,
                        padding:"9px 12px",borderRadius:8,marginBottom:4,
                        background:"rgba(255,255,255,0.03)",
                        border:"1px solid rgba(255,255,255,0.06)",
                      }}>
                        <div style={{
                          fontSize:10,fontWeight:700,color:C.dim,
                          minWidth:70,
                        }}>{dateLabel}</div>
                        <div style={{flex:1,fontSize:11,color:C.text}}>
                          {nurses.map(n=>n.name.split(" ")[0]).join(", ")}
                          <span style={{
                            fontSize:10,color:C.dim,marginLeft:6,
                          }}>
                            ({nurses.length} spare)
                          </span>
                        </div>
                        <div style={{
                          fontSize:11,fontWeight:700,color:"#ff9a9a",
                          flexShrink:0,
                        }}>
                          ~£{dayCost}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Per-nurse summary */}
                <div style={{marginBottom:20}}>
                  <div style={{
                    fontSize:10,fontWeight:700,letterSpacing:"0.08em",
                    textTransform:"uppercase",color:C.dim,marginBottom:10,
                  }}>Per nurse</div>
                  {(f.spareNurseList||[]).map(({nurse,dates})=>{
                    const nurseCost = Math.round(
                      dates.length * hrsPerDay * nurseHrRate
                    );
                    return (
                      <div key={nurse.id} style={{
                        display:"flex",alignItems:"center",gap:12,
                        padding:"8px 12px",borderRadius:8,marginBottom:4,
                        background:"rgba(255,255,255,0.03)",
                        border:"1px solid rgba(255,255,255,0.06)",
                      }}>
                        <div style={{
                          flex:1,fontSize:12,fontWeight:600,color:C.text,
                        }}>
                          {nurse.name}
                          <span style={{
                            fontSize:10,fontWeight:400,color:C.dim,marginLeft:6,
                          }}>
                            {dates.length} day{dates.length!==1?"s":""} spare ·{" "}
                            {nurse.status==="qualified"?"Qualified":
                             nurse.status==="nearQual"?"Near qualifying":"Trainee"}
                          </span>
                        </div>
                        <div style={{
                          fontSize:11,fontWeight:700,color:"#ff9a9a",flexShrink:0,
                        }}>
                          ~£{nurseCost}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Options */}
                {f.hasAdHocOpp&&(
                  <div style={{
                    padding:"12px 16px",borderRadius:10,marginBottom:12,
                    background:"rgba(0,229,160,0.07)",
                    border:"1px solid rgba(0,229,160,0.2)",
                  }}>
                    <div style={{
                      fontSize:11,fontWeight:700,color:"#00e5a0",marginBottom:4,
                    }}>
                      Revenue opportunity
                    </div>
                    <div style={{fontSize:11,color:C.dim,marginBottom:10}}>
                      {f.openSurgDays} surgery day{f.openSurgDays!==1?"s":""} free
                      {" — "}book an ad hoc clinic to convert idle wages into revenue.
                    </div>
                    <button
                      onClick={()=>{
                        setDrillWeek(null);
                        onNavigate&&onNavigate("rota",f.siteId,"adhoc");
                      }}
                      style={{...BTN("#00e5a0",true),fontSize:11,padding:"7px 14px"}}>
                      Open ad hoc clinic builder →
                    </button>
                  </div>
                )}
                <div style={{
                  fontSize:11,color:C.dim,lineHeight:1.6,
                  padding:"10px 0",
                  borderTop:`1px solid ${C.border}`,
                }}>
                  To clear this cost: offer unpaid leave, schedule CPD, or book
                  an ad hoc clinic using the action buttons on the dashboard.
                </div>
              </div>
            </Overlay>
          );
        })()}
        {openArticle&&(()=>{
          const CAT_STYLE = {
            law:     {label:"Employment Law", color:"#f472b6"},
            dental:  {label:"Dental News",    color:"#60a5fa"},
            howto:   {label:"How-to",         color:"#00e5a0"},
            podcast: {label:"Podcast",        color:"#a78bfa"},
            omni:    {label:"Omni Update",    color:"#f5c518"},
          };
          const cs = CAT_STYLE[openArticle.category]||CAT_STYLE.omni;
          const content = openArticle.body||openArticle.summary||"";
          return (
            <Overlay onClose={()=>setOpenArticle(null)}>
              <div style={{maxWidth:560,width:"100%"}}>
                <div style={{
                  display:"inline-block",fontSize:9,fontWeight:700,
                  letterSpacing:"0.1em",textTransform:"uppercase",
                  color:cs.color,background:cs.color+"18",
                  borderRadius:99,padding:"3px 10px",marginBottom:14,
                }}>
                  {cs.label}
                </div>
                <h2 style={{
                  fontFamily:"'Instrument Serif',Georgia,serif",
                  fontStyle:"italic",fontSize:22,fontWeight:700,
                  color:C.heading,lineHeight:1.3,marginBottom:8,
                }}>
                  {openArticle.title}
                </h2>
                <div style={{
                  fontSize:10,color:C.dim,marginBottom:20,
                }}>
                  {new Date(openArticle.date).toLocaleDateString("en-GB",{
                    day:"numeric",month:"long",year:"numeric",
                  })}
                </div>
                <div style={{
                  fontSize:13,color:"rgba(255,255,255,0.65)",
                  lineHeight:1.75,whiteSpace:"pre-wrap",marginBottom:24,
                }}>
                  {content}
                </div>
                {openArticle.link&&(
                  <button
                    onClick={()=>window.open(openArticle.link,"_blank")}
                    style={{
                      ...BTN(C.teal,true),
                      fontSize:12,padding:"10px 20px",
                      display:"flex",alignItems:"center",gap:6,
                    }}
                  >
                    Read full article
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.5"
                      strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                      <polyline points="15 3 21 3 21 9"/>
                      <line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                  </button>
                )}
              </div>
            </Overlay>
          );
        })()}

        {/* ── IRIS SCORE STRIP ── */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:20}}>
          {siteScores.map(({id,site,ps,cs})=>{
            const col = ps.band.tier==="good"?"#00e5a0":ps.band.tier==="middle"?"#f5c518":ps.band.tier==="bad"?"#ff4646":"rgba(255,255,255,0.3)";
            const bgCol = ps.band.tier==="good"?"rgba(0,229,160,0.07)":ps.band.tier==="middle"?"rgba(245,197,24,0.07)":ps.band.tier==="bad"?"rgba(255,70,70,0.07)":"rgba(255,255,255,0.03)";
            return (
              <div key={id} onClick={()=>setDrillSite(drillSite===id?null:id)} style={{
                background:bgCol,
                border:`1px solid ${col}30`,
                borderRadius:14,padding:"18px 20px",cursor:"pointer",
                position:"relative",overflow:"hidden",
                transition:"all 0.2s",
              }}>
                <div style={{position:"absolute",top:0,left:0,width:3,height:"100%",background:col,borderRadius:"14px 0 0 14px"}}/>
                <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.16em",textTransform:"uppercase",color:"rgba(255,255,255,0.3)",marginBottom:8}}>{site.name}</div>
                <div style={{display:"flex",alignItems:"baseline",gap:6}}>
                  <span style={{fontFamily:"'Instrument Serif',Georgia,serif",fontStyle:"italic",fontWeight:700,fontSize:42,color:col,letterSpacing:"-0.04em",lineHeight:1}}>{ps.score}</span>
                  <span style={{fontSize:10,color:"rgba(255,255,255,0.3)",fontWeight:600}}>/ 100</span>
                </div>
                <div style={{fontSize:10,fontWeight:600,color:col,marginTop:4,letterSpacing:"0.04em"}}>{ps.band.label}</div>
                <div style={{fontSize:9,color:"rgba(255,255,255,0.18)",marginTop:3,marginBottom:2}}>Tap to explain ↗</div>
                {ps.factors.length>0&&(
                  <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:3}}>
                    {ps.factors.slice(0,2).map((f,i)=>(
                      <div key={i} style={{fontSize:10,color:"rgba(255,255,255,0.4)",display:"flex",alignItems:"flex-start",gap:5}}>
                        <span style={{color:f.sev==="critical"?"#ff5b5b":f.sev==="high"?"#f5a623":"rgba(255,255,255,0.3)",flexShrink:0,marginTop:1}}>
                          <svg width="8" height="8" viewBox="0 0 8 8"><circle cx="4" cy="4" r="3" fill="currentColor"/></svg>
                        </span>
                        <span style={{lineHeight:1.3}}>{f.msg}</span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Confidence + IRIS SCORE — flex row, no overlap */}
                <div style={{marginTop:10,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div onClick={e=>{e.stopPropagation();setConfidenceDrill(confidenceDrill===id?null:id);}}
                    style={{display:"flex",alignItems:"center",gap:5,cursor:"pointer"}}>
                    <div style={{width:5,height:5,borderRadius:"50%",flexShrink:0,
                      background:cs.band.tier==="high"?"#3aad98":cs.band.tier==="medium"?"#f5c518":"#ff4646"}}>
                    </div>
                    <span style={{fontSize:9,fontWeight:600,color:"rgba(255,255,255,0.35)",letterSpacing:"0.04em"}}>
                      Confidence {cs.score}/100
                    </span>
                  </div>
                  <span style={{fontSize:9,color:"rgba(255,255,255,0.18)",fontWeight:600,letterSpacing:"0.08em"}}>IRIS SCORE</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── ALERTS STRIP ── */}
        {(()=>{
          const alerts = [];

          // RTW alerts
          (state.sick||[]).filter(s=>!s.rtw).forEach(s=>{
            const person = [...(state.nurses||NURSES_BASE),...RECS_BASE,...getClinicians(state)].find(p=>p.id===s.staffId);
            if (person) alerts.push({sev:"red",msg:`${person.name} — RTW interview not completed`,action:"Review",nav:()=>onNavigate&&onNavigate("sickness"),
              quickAction:"✓ RTW Done",quickFn:()=>set(ss=>({...ss,sick:(ss.sick||[]).map(sk=>sk.staffId===s.staffId&&!sk.rtw?{...sk,rtw:true,rtwDate:todayStr,rtwBy:user?.name||"Principal"}:sk)}))});
          });

          // Pending leave — handled by inline card below, not here

          // Lateness pattern welfare flags
          const allSiteStaff = [
            ...(state.nurses||NURSES_BASE).filter(n=>!n.maternity&&!n.archived),
            ...RECS_BASE,
          ];
          allSiteStaff.forEach(s=>{
            const p = analyseLatenessPatterns(s.id, state);
            if (p.riskLevel==="medium"||p.riskLevel==="high") {
              const topPattern = p.patterns.find(pt=>pt.sev==="amber");
              if(topPattern) alerts.push({sev:"amber",msg:`${s.name}: ${topPattern.msg}`,action:"View",nav:()=>onNavigate&&onNavigate("rota","","lateness")});
            }
          });

          // TOIL balance alerts — flag when building up
          if ((state.toilPolicy||{}).enabled) {
            const threshold = state.toilPolicy?.alertThresholdHrs||7.5;
            allSiteStaff.forEach(s=>{
              const bal = getTOILBalance(s.id, state);
              if (bal>=threshold) {
                alerts.push({
                  sev:"amber",
                  msg:`${s.name} has ${bal.toFixed(1)}h TOIL banked — consider scheduling time off`,
                  action:"View",
                  nav:()=>onNavigate&&onNavigate("rota","","overtime"),
                });
              }
            });
          }

          // Carers leave pattern detection
          allSiteStaff.forEach(s=>{
            const carersEntries = (state.leave[s.id]||[])
              .filter(l=>l.leaveType==="carers"&&l.status==="approved");
            if (carersEntries.length>=3) {
              const days = carersEntries.map(l=>new Date(l.date+"T12:00").getDay());
              const fridayCount = days.filter(d=>d===5).length;
              if (fridayCount>=2) {
                alerts.push({
                  sev:"amber",
                  msg:`${s.name}: ${fridayCount} of ${carersEntries.length} carer's leave days on Fridays — worth a quiet conversation`,
                  action:"View",
                  nav:()=>onNavigate&&onNavigate("people"),
                });
              }
            }
          });

          // Iris intelligence alerts
          siteScores.forEach(({id,site,ps})=>{
            ps.factors.filter(f=>f.sev==="critical").forEach(f=>{
              alerts.push({sev:"red",msg:`${site.name}: ${f.msg}`,action:"View rota",nav:()=>onNavigate&&onNavigate("planning",id)});
            });
          });

          // Compliance safety checks — sedation, safeguarding, fire, first aid
          Object.entries(SITES).forEach(([sid,siteInf])=>{
            const compFlags = checkComplianceSafety(sid, todayStr, state);
            compFlags.forEach(f=>{
              alerts.push({
                sev: f.sev,
                msg: `${siteInf.name}: ${f.msg}`,
                action: f.area==="sedation"?"View rota":"Staff Records",
                nav: ()=>onNavigate&&onNavigate(
                  f.area==="sedation"?"rota":"admin", sid,
                  f.area==="sedation"?undefined:"directory"
                ),
              });
            });
          });

          // Compliance tasks overdue -- fire alarm, emergency drugs, step wedge
          Object.entries(SITES).forEach(([sid,siteInf])=>{
            COMPLIANCE_TASKS.forEach(task=>{
              const last = getLastCompletion(state, task.id, sid);
              if (isComplianceOverdue(task, last)) {
                const daysSince = last
                  ? Math.floor((Date.now()-new Date(last.date).getTime())/86400000)
                  : null;
                const overdueMsg = last
                  ? `${daysSince}d overdue`
                  : "never completed";
                alerts.push({
                  sev: task.freq==="weekly"?"red":"amber",
                  msg: `${siteInf.name}: ${task.label} - ${overdueMsg}`,
                  action: "Compliance",
                  nav: ()=>onNavigate&&onNavigate("admin", sid, "comply"),
                });
              }
            });
          });

          if (alerts.length===0) return null;
          return (
            <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:20}}>
              {alerts.slice(0,5).map((a,i)=>(
                <div key={i} style={{
                  display:"flex",alignItems:"center",gap:12,
                  padding:"11px 16px",
                  background:"rgba(255,255,255,0.03)",
                  border:"1px solid rgba(255,255,255,0.06)",
                  borderRadius:10,
                }}>
                  <div style={{width:7,height:7,borderRadius:"50%",flexShrink:0,
                    background:a.sev==="red"?"#ff5b5b":"#f5a623",
                    boxShadow:`0 0 8px ${a.sev==="red"?"rgba(255,91,91,0.6)":"rgba(245,166,35,0.5)"}`}}/>
                  <div style={{fontSize:12,color:"rgba(255,255,255,0.75)",flex:1}}>{a.msg}</div>
                  <div style={{display:"flex",gap:6,alignItems:"center",flexShrink:0}}>
                    {a.quickAction&&<button onClick={e=>{e.stopPropagation();a.quickFn&&a.quickFn();}} style={{
                      fontSize:10,fontWeight:700,color:"#fff",background:"rgba(58,173,152,0.25)",
                      border:"1px solid rgba(58,173,152,0.4)",borderRadius:6,
                      padding:"3px 9px",cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap",
                    }}>{a.quickAction}</button>}
                    {a.action&&<button onClick={a.nav} style={{
                      fontSize:10,fontWeight:600,color:"#3aad98",
                      background:"none",border:"none",cursor:"pointer",
                      fontFamily:"inherit",letterSpacing:"0.04em",whiteSpace:"nowrap",
                    }}>{a.action} →</button>}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* ── INLINE LEAVE APPROVAL ── */}
        {pendingLeaveGroups.length>0&&(
          <div style={{marginBottom:20}}>
            <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.16em",textTransform:"uppercase",color:"rgba(245,166,35,0.7)",marginBottom:10}}>
              Leave requests · {pendingLeaveGroups.length} waiting
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {pendingLeaveGroups.slice(0,5).map((g,i)=>{
                const sorted = [...g.dates].sort();
                const dateLabel = sorted.length===1
                  ? new Date(sorted[0]+"T12:00").toLocaleDateString("en-GB",{day:"numeric",month:"short"})
                  : `${new Date(sorted[0]+"T12:00").toLocaleDateString("en-GB",{day:"numeric",month:"short"})} – ${new Date(sorted[sorted.length-1]+"T12:00").toLocaleDateString("en-GB",{day:"numeric",month:"short"})} (${sorted.length}d)`;
                const typeLabel = g.leaveType==="annual"?"Annual leave":g.leaveType==="sick"?"Sick day":g.leaveType==="cpd"?"CPD/Training":g.leaveType||"Leave";
                return (
                  <div key={g.requestId} style={{
                    display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",
                    padding:"12px 16px",
                    background:"rgba(245,166,35,0.06)",
                    border:"1px solid rgba(245,166,35,0.18)",
                    borderRadius:10,
                  }}>
                    <div style={{width:30,height:30,borderRadius:"50%",flexShrink:0,
                      background:"rgba(245,166,35,0.15)",display:"flex",alignItems:"center",
                      justifyContent:"center",fontSize:10,fontWeight:700,color:"#f5a623"}}>
                      {g.person.name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()}
                    </div>
                    <div style={{flex:1,minWidth:120}}>
                      <div style={{fontSize:12,fontWeight:700,color:"rgba(255,255,255,0.88)"}}>{g.person.name}</div>
                      <div style={{fontSize:10,color:"rgba(255,255,255,0.4)",marginTop:1}}>{dateLabel} · {typeLabel}{g.note?` · "${g.note}"`:""}</div>
                    </div>
                    <div style={{display:"flex",gap:6,flexShrink:0}}>
                      <button onClick={()=>quickApprove(g.requestId,g.person.id)} style={{
                        padding:"6px 14px",borderRadius:8,fontFamily:"inherit",fontSize:11,fontWeight:700,
                        cursor:"pointer",border:"1px solid rgba(58,173,152,0.4)",
                        background:"rgba(58,173,152,0.18)",color:"#3aad98",
                      }}>✓ Approve</button>
                      <button onClick={()=>quickDecline(g.requestId,g.person.id)} style={{
                        padding:"6px 12px",borderRadius:8,fontFamily:"inherit",fontSize:11,fontWeight:700,
                        cursor:"pointer",border:"1px solid rgba(255,91,91,0.25)",
                        background:"rgba(255,91,91,0.08)",color:"rgba(255,91,91,0.7)",
                      }}>X</button>
                    </div>
                  </div>
                );
              })}
              {pendingLeaveGroups.length>5&&(
                <button onClick={()=>onNavigate&&onNavigate("rota",fallbackSite(user),"leave")} style={{fontSize:11,color:"rgba(255,255,255,0.3)",background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",textAlign:"left",padding:"4px 0"}}>+{pendingLeaveGroups.length-5} more → view all</button>
              )}
            </div>
          </div>
        )}

        {/* ── QUIET WEEK / OVERSTAFFING ALERT ── */}
        {(()=>{
          const quietFlags = [];
          const today = new Date(todayStr);
          const nurseHrRate = state.settings?.nurseHourlyRate||14;
          Object.entries(SITES).forEach(([sid, siteInfo])=>{
            const siteSurgeries = siteInfo.surgeries||4;
            const weekMap = {};
            for(let i=1;i<=21;i++){
              const d = new Date(today); d.setDate(today.getDate()+i);
              const ds = d.toISOString().split("T")[0];
              const dow = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d.getDay()];
              if(dow==="Sat"||dow==="Sun") continue;
              const weekStart = new Date(d);
              weekStart.setDate(d.getDate()-(d.getDay()===0?6:d.getDay()-1));
              const wk = weekStart.toISOString().split("T")[0];
              if(!weekMap[wk]) weekMap[wk]={days:[],overstaffedDays:0};
              const cs = calcStaffingConfidence(sid, ds, state);
              // Open surgeries = site capacity minus clinicians scheduled
              const openSurgeries = Math.max(0, siteSurgeries - cs.surgeries);
              weekMap[wk].days.push({date:ds, cs, dow, openSurgeries});
              if(cs.surgeries===0) return;
              const ratio = cs.effectiveNurses/Math.max(cs.surgeries,1);
              if(ratio>=1.4&&cs.spareNurses>=2) weekMap[wk].overstaffedDays++;
            }
            Object.entries(weekMap).forEach(([wk, data])=>{
              if(data.overstaffedDays>=3){
                const worstDay = data.days
                  .filter(d=>d.cs.spareNurses>=2)
                  .sort((a,b)=>b.cs.spareNurses-a.cs.spareNurses)[0];
                const wkDate = new Date(wk+"T12:00");
                const wkLabel = wkDate.toLocaleDateString("en-GB",
                  {day:"numeric",month:"short"});
                // Spare nurse objects
                const spareDatesMap = {};
                data.days.filter(d=>d.cs.spareNurses>=2).forEach(d=>{
                  (d.cs.spareNurseObjects||[]).forEach(n=>{
                    if(!spareDatesMap[n.id]) spareDatesMap[n.id]={nurse:n,dates:[]};
                    spareDatesMap[n.id].dates.push(d.date);
                  });
                });
                const spareNurseList = Object.values(spareDatesMap)
                  .sort((a,b)=>b.dates.length-a.dates.length);
                // Wage cost estimate: spare nurses × hrs/day × overstaffed days × rate
                const hrsPerDay = 7.5;
                const totalSpareNurseDays = data.days
                  .filter(d=>d.cs.spareNurses>=2)
                  .reduce((s,d)=>s+d.cs.spareNurses,0);
                const wageCost = Math.round(
                  totalSpareNurseDays * hrsPerDay * nurseHrRate / 5
                ) * 5;
                // Open surgery days this week
                const openSurgDays = data.days
                  .filter(d=>d.openSurgeries>0).length;
                const peakOpenSurg = Math.max(
                  ...data.days.map(d=>d.openSurgeries)
                );
                // Identify absent clinicians creating the open slots
                const allClinsForSite = getClinicians(state)
                  .filter(c=>!c._deleted&&(c.sites||[c.site]).includes(sid));
                const absentClinMap = {};
                data.days.filter(d=>d.openSurgeries>0).forEach(d=>{
                  const dow2 = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][
                    new Date(d.date+"T12:00").getDay()];
                  allClinsForSite.forEach(c=>{
                    const sched = c.siteScheds?.[sid]||c.sched||{};
                    if(sched[dow2]&&isAbsent(state,c.id,d.date)){
                      if(!absentClinMap[c.id])
                        absentClinMap[c.id]={clin:c,dates:[]};
                      absentClinMap[c.id].dates.push(d.date);
                    }
                  });
                });
                const absentClins = Object.values(absentClinMap)
                  .sort((a,b)=>b.dates.length-a.dates.length);
                // Suggest clinic type from absent clinician types
                const suggestedType = absentClins.length>0
                  ? (()=>{
                    const t = absentClins[0].clin.type||"GDS";
                    if(t.includes("MOS")) return "MOS";
                    if(t.includes("Therapist")) return "Therapist";
                    return "GDS";
                  })()
                  : "GDS";
                // Best date = first overstaffed+open day
                const bestDate = data.days
                  .filter(d=>d.openSurgeries>0&&d.cs.spareNurses>=1)
                  .sort((a,b)=>a.date.localeCompare(b.date))[0]?.date||wk;
                quietFlags.push({
                  site: siteInfo, siteId: sid, weekStart: wk, weekLabel: wkLabel,
                  overstaffedDays: data.overstaffedDays,
                  peakSpare: worstDay?.cs.spareNurses||0,
                  peakDate: worstDay?.date,
                  spareNurseList, wageCost,
                  openSurgDays, peakOpenSurg,
                  absentClins, suggestedType, bestDate,
                  hasAdHocOpp: openSurgDays>0 && spareNurseList.length>0,
                });
              }
            });
          });

          if(!quietFlags.length) return null;
          const totalWeeks = quietFlags.length;
          // Best adhoc opportunity = flag with most open surg days
          const bestOpp = quietFlags.slice()
            .sort((a,b)=>b.openSurgDays-a.openSurgDays)[0];
          const totalCost = quietFlags.reduce((s,f)=>s+f.wageCost,0);
          const anyAdHoc = quietFlags.some(f=>f.hasAdHocOpp);

          return (
            <div style={{marginBottom:16,borderRadius:14,overflow:"hidden",
              border:"1px solid rgba(80,110,255,0.2)"}}>

              {/* Header */}
              <div style={{padding:"13px 16px 10px",
                background:"rgba(80,110,255,0.06)"}}>
                <div style={{display:"flex",justifyContent:"space-between",
                  alignItems:"flex-start",marginBottom:6}}>
                  <div style={{fontSize:12,fontWeight:700,
                    color:"rgba(255,255,255,0.85)"}}>
                    {totalWeeks} quiet week{totalWeeks!==1?"s":""} ahead
                    {" — "}
                    <button
                      onClick={()=>setDrillWeek(quietFlags[0])}
                      style={{
                        background:"transparent",border:"none",padding:0,
                        cursor:"pointer",fontFamily:"inherit",
                        color:"#ff6b6b",fontSize:12,fontWeight:700,
                        textDecoration:"underline",
                        textDecorationColor:"rgba(255,107,107,0.35)",
                      }}>
                      ~£{totalCost.toLocaleString()} in wages ↗
                    </button>
                  </div>
                  <div style={{fontSize:9,fontWeight:700,
                    color:"rgba(100,160,255,0.6)",letterSpacing:"0.08em",
                    textTransform:"uppercase",paddingTop:2}}>OVERSTAFFED</div>
                </div>
                {quietFlags.map(f=>(
                  <div key={f.siteId+f.weekStart}
                    style={{fontSize:11,color:"rgba(255,255,255,0.45)",
                    lineHeight:1.6,display:"flex",gap:8,flexWrap:"wrap"}}>
                    <span style={{color:"rgba(255,255,255,0.65)",fontWeight:600}}>
                      {f.site.name} w/c {f.weekLabel}
                    </span>
                    <span>
                      {f.peakSpare} spare nurse{f.peakSpare!==1?"s":""}
                    </span>
                    {f.openSurgDays>0&&(
                      <span style={{color:"#00e5a0"}}>
                        · {f.openSurgDays} open surgery day{f.openSurgDays!==1?"s":""}
                      </span>
                    )}
                    <button
                      onClick={()=>setDrillWeek(f)}
                      style={{
                        background:"transparent",border:"none",padding:0,
                        cursor:"pointer",fontFamily:"inherit",
                        color:"#ff9a9a",fontSize:11,fontWeight:600,
                        textDecoration:"underline",
                        textDecorationColor:"rgba(255,154,154,0.35)",
                      }}>
                      · ~£{f.wageCost.toLocaleString()} idle cost ↗
                    </button>
                  </div>
                ))}
              </div>

              {/* Ad hoc opportunity — green band, shown first when relevant */}
              {anyAdHoc&&(
                <div style={{padding:"10px 16px",
                  background:"rgba(0,229,160,0.07)",
                  borderTop:"1px solid rgba(0,229,160,0.15)",
                  borderBottom:"1px solid rgba(0,229,160,0.1)"}}>
                  <div style={{fontSize:11,color:"rgba(0,229,160,0.9)",
                    fontWeight:700,marginBottom:6,display:"flex",
                    alignItems:"center",gap:6}}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2.5"
                      strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                      <polyline points="17 6 23 6 23 12"/>
                    </svg>
                    Revenue opportunity — spare nurses + open surgeries
                  </div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",
                    marginBottom:6,lineHeight:1.6}}>
                    <span style={{color:"rgba(255,255,255,0.75)",fontWeight:600}}>
                      {bestOpp.site.name} w/c {bestOpp.weekLabel}
                    </span>
                    {" — "}
                    {bestOpp.peakSpare} nurse{bestOpp.peakSpare!==1?"s":""} spare,
                    {" "}{bestOpp.openSurgDays} surgery day{bestOpp.openSurgDays!==1?"s":""} free.
                    {" "}
                    <span style={{color:"#ff9a9a"}}>
                      ~£{bestOpp.wageCost.toLocaleString()} sitting idle.
                    </span>
                  </div>
                  {bestOpp.absentClins&&bestOpp.absentClins.length>0&&(
                    <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",
                      marginBottom:8,lineHeight:1.5}}>
                      {bestOpp.absentClins.slice(0,2).map(({clin,dates})=>(
                        <span key={clin.id} style={{marginRight:10}}>
                          <span style={{color:"rgba(255,255,255,0.65)",
                            fontWeight:600}}>{clin.name}</span>
                          {" "}({clin.type}) off {dates.length} day
                          {dates.length!==1?"s":""} —
                          {" "}
                          <span style={{color:"#00e5a0"}}>
                            {bestOpp.suggestedType} slot available
                          </span>
                        </span>
                      ))}
                    </div>
                  )}
                  <button
                    onClick={()=>{
                      try {
                        sessionStorage.setItem("omni_adhoc_hint", JSON.stringify({
                          siteId: bestOpp.siteId,
                          siteName: bestOpp.site.name,
                          weekLabel: bestOpp.weekLabel,
                          weekStart: bestOpp.weekStart,
                          spareNurses: bestOpp.peakSpare,
                          openSurgDays: bestOpp.openSurgDays,
                          wageCost: bestOpp.wageCost,
                          suggestedType: bestOpp.suggestedType,
                          bestDate: bestOpp.bestDate,
                          absentClins:(bestOpp.absentClins||[]).slice(0,2).map(
                            ({clin,dates})=>({name:clin.name,type:clin.type,dates})
                          ),
                        }));
                      } catch(e){}
                      onNavigate&&onNavigate("rota", bestOpp.siteId, "adhoc");
                    }}
                    style={{fontSize:11,fontWeight:600,
                      padding:"4px 0",borderRadius:0,
                      cursor:"pointer",fontFamily:"inherit",
                      background:"transparent",border:"none",
                      color:"rgba(0,229,160,0.55)",
                      textDecoration:"underline",
                      textDecorationColor:"rgba(0,229,160,0.25)"}}>
                    View ad hoc clinic builder →
                  </button>
                </div>
              )}

              {/* Cost management + ad hoc actions — always all 4 */}
              <div style={{padding:"10px 16px",
                background:"rgba(255,255,255,0.02)"}}>
                {/* TOIL intelligence strip */}
                {(()=>{
                  if (!(state.toilPolicy||{}).enabled) return null;
                  const flag = quietFlags[0];
                  const staffWithTOIL = (flag.spareNurseList||[])
                    .map(({nurse})=>({
                      nurse,
                      bal: getTOILBalance(nurse.id, state),
                    }))
                    .filter(x=>x.bal>=7.5)
                    .sort((a,b)=>b.bal-a.bal);
                  if (!staffWithTOIL.length) return null;
                  return (
                    <div style={{marginBottom:10,padding:"8px 12px",
                      borderRadius:8,
                      background:"rgba(244,114,182,0.07)",
                      border:"1px solid rgba(244,114,182,0.2)"}}>
                      <div style={{fontSize:10,fontWeight:700,
                        color:"rgba(244,114,182,0.8)",marginBottom:4}}>
                        💡 TOIL opportunity
                      </div>
                      <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",
                        lineHeight:1.5}}>
                        {staffWithTOIL.slice(0,3).map(({nurse,bal})=>(
                          <span key={nurse.id} style={{marginRight:10}}>
                            <span style={{color:"rgba(255,255,255,0.75)",
                              fontWeight:600}}>
                              {nurse.name.split(" ")[0]}
                            </span>
                            {" has "}{bal.toFixed(1)}h banked
                          </span>
                        ))}
                        — quiet week is the right time to use it
                      </div>
                    </div>
                  );
                })()}
                <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.1em",
                  textTransform:"uppercase",color:"rgba(255,255,255,0.2)",
                  marginBottom:7}}>ACTIONS</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  <button onClick={()=>{
                    try {
                      sessionStorage.setItem("omni_adhoc_hint",JSON.stringify({
                        siteId:bestOpp.siteId,
                        siteName:bestOpp.site.name,
                        weekLabel:bestOpp.weekLabel,
                        weekStart:bestOpp.weekStart,
                        spareNurses:bestOpp.peakSpare,
                        openSurgDays:bestOpp.openSurgDays,
                        wageCost:bestOpp.wageCost,
                        suggestedType:bestOpp.suggestedType,
                        bestDate:bestOpp.bestDate,
                        absentClins:(bestOpp.absentClins||[]).slice(0,2).map(
                          ({clin,dates})=>({name:clin.name,type:clin.type,dates})
                        ),
                      }));
                    } catch(e){}
                    onNavigate&&onNavigate("rota",bestOpp.siteId,"adhoc");
                  }} style={{fontSize:11,fontWeight:700,padding:"6px 13px",
                    borderRadius:8,cursor:"pointer",fontFamily:"inherit",
                    background:"rgba(0,229,160,0.13)",
                    border:"1px solid rgba(0,229,160,0.35)",
                    color:"#00e5a0"}}>
                    Book ad hoc clinic
                  </button>
                  <button onClick={()=>setQuietUnpaidM(quietFlags[0])}
                    style={{fontSize:11,fontWeight:600,padding:"6px 13px",
                    borderRadius:8,cursor:"pointer",fontFamily:"inherit",
                    background:"rgba(80,110,255,0.12)",
                    border:"1px solid rgba(80,110,255,0.25)",
                    color:"rgba(160,190,255,0.9)"}}>
                    Offer unpaid leave
                  </button>
                  <button onClick={()=>setQuietCPDM(quietFlags[0])}
                    style={{fontSize:11,fontWeight:600,padding:"6px 13px",
                    borderRadius:8,cursor:"pointer",fontFamily:"inherit",
                    background:"rgba(58,173,152,0.1)",
                    border:"1px solid rgba(58,173,152,0.25)",
                    color:"#3aad98"}}>
                    Schedule CPD
                  </button>
                  <button onClick={()=>setQuietTaskM(quietFlags[0])}
                    style={{fontSize:11,fontWeight:600,padding:"6px 13px",
                    borderRadius:8,cursor:"pointer",fontFamily:"inherit",
                    background:"rgba(245,197,24,0.08)",
                    border:"1px solid rgba(245,197,24,0.2)",
                    color:"#f5c518"}}>
                    Assign tasks
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

                {/* ── TWO COL: TODAY'S ROTA + WHO'S OFF ── */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 320px",gap:12,marginBottom:20}}>

          {/* Today's rota */}
          <div onClick={()=>onNavigate&&onNavigate("rota")} style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:14,padding:"20px",cursor:"pointer",transition:"border-color 0.15s"}}
            onMouseEnter={e=>e.currentTarget.style.borderColor="rgba(58,173,152,0.25)"}
            onMouseLeave={e=>e.currentTarget.style.borderColor="rgba(255,255,255,0.06)"}>
            <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.16em",textTransform:"uppercase",color:"rgba(255,255,255,0.25)",marginBottom:16}}>Today's rota · {liveDay?.label} <span style={{color:"rgba(58,173,152,0.5)",marginLeft:6}}>-></span></div>
            {(()=>{
              const nurses = Array.isArray(state.nurses) ? state.nurses : NURSES_BASE;
              const allSiteEntries = Object.entries(SITES);
              const displaySite = allSiteEntries[0];
              if (!displaySite) return null;
              const [siteId, siteInfo] = displaySite;
              const dr = buildDayRota(liveDate, siteId, nurses, state);
              if (!dr.pairings.length) return <div style={{fontSize:12,color:"rgba(255,255,255,0.25)",padding:"8px 0"}}>No surgeries scheduled today</div>;
              return (
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {dr.pairings.map((p,i)=>{
                    const hasNurse = !!p.nurse;
                    return (
                      <div key={i} style={{
                        display:"flex",alignItems:"center",gap:12,
                        padding:"10px 14px",borderRadius:10,
                        background:hasNurse?"rgba(58,173,152,0.06)":"rgba(255,91,91,0.06)",
                        border:`1px solid ${hasNurse?"rgba(58,173,152,0.12)":"rgba(255,91,91,0.12)"}`,
                      }}>
                        <div style={{width:6,height:6,borderRadius:"50%",flexShrink:0,
                          background:hasNurse?"#3aad98":"#ff5b5b",
                          boxShadow:`0 0 6px ${hasNurse?"rgba(58,173,152,0.7)":"rgba(255,91,91,0.7)"}`}}/>
                        <div style={{flex:1}}>
                          <div style={{fontSize:12,fontWeight:600,color:hasNurse?"rgba(255,255,255,0.88)":"rgba(255,255,255,0.4)"}}>
                            {p.clinician?.name||"—"}
                          </div>
                          <div style={{fontSize:10,color:"rgba(255,255,255,0.3)",marginTop:1}}>
                            {p.nurse?.name||"No nurse assigned"} {p.clinStart&&`· ${p.clinStart}–${p.clinEnd}`}
                          </div>
                        </div>
                        {!hasNurse&&<div style={{fontSize:9,fontWeight:700,color:"#ff5b5b",letterSpacing:"0.08em"}}>GAP</div>}
                      </div>
                    );
                  })}
                  <button onClick={()=>onNavigate&&onNavigate("planning",siteId)} style={{
                    marginTop:4,fontSize:11,fontWeight:600,color:"rgba(255,255,255,0.3)",
                    background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",
                    textAlign:"left",letterSpacing:"0.04em",
                  }}>Full rota view →</button>
                </div>
              );
            })()}
          </div>

          {/* Who's off */}
          <div style={{background:"rgba(255,255,255,0.03)",border:"1px solid rgba(255,255,255,0.06)",borderRadius:14,padding:"20px"}}>
            <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.16em",textTransform:"uppercase",color:"rgba(255,255,255,0.25)",marginBottom:16}}>
              Who's off today {offToday.length>0&&<span style={{color:"rgba(255,255,255,0.5)"}}>· {offToday.length}</span>}
            </div>
            {offToday.length===0
              ? <div style={{fontSize:12,color:"rgba(255,255,255,0.25)"}}>Everyone accounted for</div>
              : <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {offToday.slice(0,8).map((s,i)=>{
                    const isSick = s.reason?.toLowerCase().includes("sick")||(state.sick||[]).some(sk=>sk.staffId===s.id&&!sk.rtw);
                    return (
                      <div key={i} onClick={()=>onNavigate&&onNavigate("sickness")} style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                        <div style={{
                          width:28,height:28,borderRadius:"50%",flexShrink:0,
                          background:isSick?"rgba(255,91,91,0.15)":"rgba(58,173,152,0.12)",
                          display:"flex",alignItems:"center",justifyContent:"center",
                          fontSize:9,fontWeight:700,
                          color:isSick?"#ff5b5b":"#3aad98",
                        }}>
                          {s.name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase()}
                        </div>
                        <div>
                          <div style={{fontSize:12,fontWeight:500,color:isSick?"rgba(255,255,255,0.5)":"rgba(255,255,255,0.75)"}}>{s.name}</div>
                          <div style={{fontSize:10,color:isSick?"rgba(255,91,91,0.6)":"rgba(255,255,255,0.25)"}}>{s.reason}</div>
                        </div>
                      </div>
                    );
                  })}
                  {offToday.length>8&&<div style={{fontSize:10,color:"rgba(255,255,255,0.25)",marginTop:2}}>+{offToday.length-8} more</div>}
                </div>
            }
          </div>
        </div>

        {/* ── QUICK STATS ROW ── */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:20}}>
          {[
            {label:"Locum cost this month",val:`£${monthCost.toLocaleString()}`,sub:`£${totCost.toLocaleString()} total`,col:"rgba(255,255,255,0.7)",nav:()=>onNavigate&&onNavigate("rota",fallbackSite(user),"cover")},
            {label:"Pending leave",val:pendingLeaveCount,sub:pendingLeaveCount>0?"Needs approval":"All clear",col:pendingLeaveCount>0?"#f5a623":"#3aad98",nav:()=>onNavigate&&onNavigate("rota",fallbackSite(user),"leave")},
            {label:"RTW outstanding",val:rtwP,sub:rtwP>0?"Return to work due":"All clear",col:rtwP>0?"#ff5b5b":"#3aad98",nav:()=>onNavigate&&onNavigate("sickness")},
            {label:"Staff off today",val:offToday.length,sub:`${offToday.filter(s=>(state.sick||[]).some(sk=>sk.staffId===s.id)).length} sick`,col:"rgba(255,255,255,0.7)",nav:()=>onNavigate&&onNavigate("sickness")},
          ].map((stat,i)=>(
            <div key={i} onClick={stat.nav} style={{
              background:"rgba(255,255,255,0.03)",
              border:"1px solid rgba(255,255,255,0.06)",
              borderRadius:12,padding:"16px 18px",
              cursor:stat.nav?"pointer":"default",
              transition:"border-color 0.15s",
            }} onMouseEnter={e=>e.currentTarget.style.borderColor="rgba(58,173,152,0.3)"}
               onMouseLeave={e=>e.currentTarget.style.borderColor="rgba(255,255,255,0.06)"}>
              <div style={{fontSize:9,fontWeight:700,letterSpacing:"0.14em",textTransform:"uppercase",color:"rgba(255,255,255,0.25)",marginBottom:8}}>{stat.label}</div>
              <div style={{fontFamily:"'Instrument Serif',Georgia,serif",fontStyle:"italic",fontWeight:700,fontSize:32,color:stat.col,letterSpacing:"-0.03em",lineHeight:1}}>{stat.val}</div>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.3)",marginTop:4}}>{stat.sub}</div>
            </div>
          ))}
        </div>

      </div>

      {/* ── IRIS SCORE DRILLDOWN ── */}
      {confidenceDrill&&(
        <div style={{position:"fixed",inset:0,zIndex:401,background:"rgba(7,6,18,0.8)",display:"flex",alignItems:"flex-end",justifyContent:"center"}}
          onClick={e=>{if(e.target===e.currentTarget)setConfidenceDrill(null);}}>
          <div style={{background:"#1c1a28",borderRadius:"20px 20px 0 0",width:"100%",maxWidth:700,maxHeight:"85vh",overflowY:"auto",boxShadow:"0 -8px 40px rgba(0,0,0,0.5)",padding:24}}>
            <ConfidenceDrilldown siteId={confidenceDrill} date={liveDate} state={state} onClose={()=>setConfidenceDrill(null)}/>
          </div>
        </div>
      )}

      {drillSite&&(
        <div style={{position:"fixed",inset:0,zIndex:400,background:"rgba(7,6,18,0.75)",display:"flex",alignItems:"flex-end",justifyContent:"center"}}
          onClick={e=>{if(e.target===e.currentTarget)setDrillSite(null);}}>
          <div style={{background:"#1c1a28",borderRadius:"20px 20px 0 0",width:"100%",maxWidth:860,maxHeight:"80vh",overflowY:"auto",boxShadow:"0 -8px 40px rgba(0,0,0,0.5)"}}>
            <div style={{padding:"16px 24px 0",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:"1px solid rgba(255,255,255,0.06)",paddingBottom:14}}>
              <div style={{fontFamily:"'Instrument Serif',Georgia,serif",fontStyle:"italic",fontWeight:700,fontSize:20,color:"#fff"}}>
                Iris Score — {SITES[drillSite]?.name}
              </div>
              <button onClick={()=>setDrillSite(null)} style={{background:"rgba(255,255,255,0.06)",border:"none",color:"rgba(255,255,255,0.5)",borderRadius:8,padding:"6px 12px",cursor:"pointer",fontFamily:"inherit",fontSize:13}}>✕ Close</button>
            </div>
            <div style={{padding:24}}>
              <IrisScoreDrilldown siteId={drillSite} date={liveDate} state={state} onClose={()=>setDrillSite(null)}/>
            </div>
          </div>
        </div>
      )}

      {/* Modals rendered below */}
      {delegationBanner&&<div style={{position:"fixed",top:20,left:"50%",transform:"translateX(-50%)",zIndex:500,maxWidth:500,width:"90%"}}>{delegationBanner}</div>}

      {coverM&&<SickCoverAI state={state} set={set} onClose={()=>setCoverM(false)} user={user}/>}
      {latenessM&&<DashLatenessModal state={state} set={set} user={user} isPrincipal={isPrincipal} personId={latenessPersonId} setPersonId={setLatenessPersonId} onClose={()=>setLatenessM(false)}/>}
      {emergM&&<MobileEmergencyCover state={state} set={set} user={user} onClose={()=>setEmergM(false)}/>}
      {locumM&&<LocumModal onSave={l=>{set(s=>({...s,locums:[...s.locums,l]}));setLocumM(false);}} onClose={()=>setLocumM(false)} rate={state.settings?.locumRate} agencies={state.agencies} state={state}/> }
      {bhM&&<BHOverrideModal state={state} set={set} onClose={()=>setBhM(false)}/>}
      {collegeM&&<CollegeDatesModal state={state} set={set} onClose={()=>setCollegeM(false)}/>}
      {myLeaveM&&<PrincipalLeaveModal user={user} state={state} set={set} onClose={()=>setMyLeaveM(false)}/>}
      {quietUnpaidM&&(
        <QuietWeekUnpaidModal
          flag={quietUnpaidM}
          state={state}
          set={set}
          user={user}
          onClose={()=>setQuietUnpaidM(null)}
        />
      )}
      {quietCPDM&&(
        <QuietWeekCPDModal
          flag={quietCPDM}
          state={state}
          set={set}
          user={user}
          onClose={()=>setQuietCPDM(null)}
        />
      )}
      {quietTaskM&&(
        <QuietWeekTaskModal
          flag={quietTaskM}
          state={state}
          set={set}
          user={user}
          onClose={()=>setQuietTaskM(null)}
        />
      )}
      {settM&&(
        <Overlay onClose={()=>setSettM(false)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:20}}>Settings</div>
          {[{l:"Locum rate £/hr",k:"locumRate"},{l:"Default holiday days (staff)",k:"defaultDays"},{l:"Partner holiday days",k:"partnerDays"}].map(({l,k})=>(
            <div key={k} style={{marginBottom:14}}>
              <label style={LB}>{l}</label>
              <input type="number" value={state.settings?.[k]||""} onChange={e=>set(s=>({...s,settings:{...s.settings,[k]:+e.target.value}}))} style={IS}/>
            </div>
          ))}

          {/* Surgery counts */}
          <div style={{borderTop:`1px solid ${C.border}`,paddingTop:16,marginTop:4,marginBottom:16}}>
            <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Surgery Counts</div>
            <div style={{fontSize:11,color:C.dim,marginBottom:12}}>Update if you add or close a surgery at any site.</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
              {Object.entries(SITES).map(([id,s])=>(
                <div key={id}>
                  <label style={{...LB,color:s.color}}>{s.name}</label>
                  <input type="number" min="1" max="12" step="1"
                    value={(state.settings?.surgeryCounts||{})[id]||SURGERY_ROOMS[id]?.count||4}
                    onChange={e=>set(ss=>({...ss,settings:{...ss.settings,surgeryCounts:{...(ss.settings?.surgeryCounts||{}),[id]:+e.target.value}}}))}
                    style={IS}/>
                </div>
              ))}
            </div>
          </div>

          {/* Leave year configuration */}
          <div style={{borderTop:`1px solid ${C.border}`,paddingTop:16,marginTop:4,marginBottom:16}}>
            <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Leave Year Configuration</div>
            <div style={{fontSize:11,color:C.dim,marginBottom:12}}>When does each group's leave year start and reset?</div>
            {[
              {k:"nurses",    l:"Nurses"},
              {k:"reception", l:"Reception & TCOs"},
              {k:"dentists",  l:"Dentists / Clinicians"},
            ].map(({k,l})=>{
              const cur = (state.leaveYearConfig||{})[k]||"jan-dec";
              return (
                <div key={k} style={{marginBottom:10}}>
                  <label style={LB}>{l}</label>
                  <div style={{display:"flex",gap:6}}>
                    {[["jan-dec","1 Jan – 31 Dec"],["apr-mar","1 Apr – 31 Mar"]].map(([val,label])=>(
                      <button key={val} onClick={()=>set(s=>({...s,leaveYearConfig:{...(s.leaveYearConfig||{}||{nurses:"jan-dec",reception:"jan-dec",dentists:"apr-mar"}),[k]:val}}))}
                        style={{...BTN(cur===val?C.teal:C.dim, cur!==val),flex:1,padding:"8px 0",fontSize:11}}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{borderTop:`1px solid ${C.border}`,paddingTop:16,marginTop:4,marginBottom:14}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em"}}>Access PINs — click to change or revoke</div>
              <input
                type="text"
                placeholder="Search name…"
                value={pinSearch||""}
                onChange={e=>setPinSearch(e.target.value)}
                style={{...IS,width:160,padding:"5px 10px",fontSize:11}}
              />
            </div>
            {resolveUsers(state).filter(u=>!(pinSearch||"").trim()||(u.name.toLowerCase().includes((pinSearch||"").toLowerCase()))).map(u=>{
              const saved = (state.users||[]).find(s=>s.id===u.id)||{};
              const isManager = u.role==="manager"||u.role==="managerLite";
              const currentSites = saved.managerSites||u.managerSites||[u.site];
              return (
                <div key={u.id} style={{marginBottom:8,padding:"10px 12px",borderRadius:10,border:`1px solid ${C.border}`,background:C.bg}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <div style={{flex:1}}>
                      <div style={{fontSize:12,fontWeight:700,color:C.text}}>{u.name}</div>
                      <div style={{fontSize:10,color:C.dim}}>{u.role}{u.note?` · ${u.note}`:""} · {currentSites.map(s=>SITES[s]?.name||s).join(", ")}</div>
                    </div>
                    <input
                      type="text" maxLength={4} placeholder="PIN"
                      defaultValue={u.pin}
                      onBlur={e=>{
                        const np=e.target.value.trim();
                        if(np.length===4&&/^\d{4}$/.test(np)){
                          set(s=>{
                            const ex=(s.users||[]).filter(x=>x.id!==u.id);
                            return {
                              ...s,
                              users:[...ex,{...saved,id:u.id,pin:np}],
                              ...addLog(s, user?.name||"Unknown", "pin", `PIN changed for ${u.name}`)
                            };
                          });
                        }
                      }}
                      style={{width:60,background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,color:C.text,fontFamily:"inherit",fontSize:13,padding:"6px 8px",outline:"none",textAlign:"center"}}
                    />
                    <button
                      onClick={()=>set(s=>{
                        const ex=(s.users||[]).filter(x=>x.id!==u.id);
                        const isRevoked=saved.active===false;
                        return {
                          ...s,
                          users:[...ex,{...saved,id:u.id,active:isRevoked?true:false}],
                          ...addLog(s, user?.name||"Unknown", "pin",
                            isRevoked?`Access restored: ${u.name}`:`Access revoked: ${u.name}`)
                        };
                      })}
                      style={{...BTN(saved.active===false?C.green:C.danger,true),fontSize:10,padding:"4px 10px"}}
                    >{saved.active===false?"Restore":"Revoke"}</button>
                  </div>
                  {/* Multi-site toggle — managers only */}
                  {isManager&&(
                    <div style={{marginTop:8,paddingTop:8,borderTop:`1px solid ${C.border2}`}}>
                      <div style={{fontSize:9,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>Sites this manager covers</div>
                      <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                        {Object.entries(SITES).map(([sid,s])=>{
                          const on = currentSites.includes(sid);
                          return (
                            <button key={sid} onClick={()=>{
                              const next = on ? currentSites.filter(x=>x!==sid) : [...currentSites,sid];
                              if (next.length===0) return; // must have at least one
                              set(st=>{
                                const ex=(st.users||[]).filter(x=>x.id!==u.id);
                                const primary = next[0];
                                return {...st, users:[...ex,{...saved,id:u.id,site:primary,managerSites:next}],
                                  ...addLog(st, user?.name||"Unknown", "role", `Updated sites for ${u.name}: ${next.map(x=>SITES[x]?.name||x).join(", ")}`)};
                              });
                            }}
                            style={{padding:"3px 10px",borderRadius:99,fontFamily:"inherit",fontSize:10,fontWeight:600,cursor:"pointer",
                              border:`1px solid ${on?s.color:C.border}`,background:on?s.color+"18":"transparent",color:on?s.color:C.muted}}>
                              {on?"":""}{s.name}
                            </button>
                          );
                        })}
                      </div>
                      <div style={{fontSize:9,color:C.dim,marginTop:4}}>Multi-site managers see leave, issues and team flags across all ticked sites.</div>
                    </div>
                  )}
                </div>
              );
            })}
            <div style={{fontSize:10,color:C.dim,marginTop:8}}>Tab out of PIN field to save. Revoked users cannot log in.</div>
          </div>
          <button onClick={()=>setSettM(false)} style={{...BTN(C.teal),width:"100%",marginTop:8,padding:"11px 0"}}>Done</button>
        </Overlay>
      )}
    </div>
  );
}

/* ── Delegation Panel ── */
function DelegationPanel({siteId,state,set,canOverride}) {
  const [addM,setAddM] = useState(false);
  const delegations = (state.delegations||[]).filter(d=>d.site===siteId);
  const active = delegations.filter(d=>d.active);
  const siteNurses = getNursesForSite(state, siteId);
  const [f,setF] = useState({toStaffId:"",startDate:"",endDate:""});

  const today = new Date().toISOString().split("T")[0];

  function save() {
    const person = siteNurses.find(n=>n.id===f.toStaffId);
    if (!person||!f.startDate) return;
    set(s=>({...s,delegations:[...(s.delegations||[]),{
      id:"del"+Date.now(),site:siteId,toStaffId:f.toStaffId,toName:person.name,
      startDate:f.startDate,endDate:f.endDate||null,active:true,
      createdBy:state._lastUser||"Manager"
    }]}));
    setAddM(false); setF({toStaffId:"",startDate:"",endDate:""});
  }
  function revoke(id) { set(s=>({...s,delegations:(s.delegations||[]).map(d=>d.id===id?{...d,active:false}:d)})); }

  return (
    <div style={{marginTop:20}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em"}}>Temporary Cover Delegation — {SITES[siteId]?.name}</div>
        {canOverride&&<button onClick={()=>setAddM(true)} style={{...BTN(C.brook,true),fontSize:11,padding:"4px 12px"}}>+ Delegate</button>}
      </div>
      {active.length===0&&<div style={{fontSize:12,color:C.dim,marginBottom:8}}>No active delegations. Use this when a manager is on leave — assign a head nurse to cover approvals.</div>}
      {active.map(d=>(
        <div key={d.id} style={{...CS,marginBottom:6,padding:"10px 14px",borderLeft:`3px solid ${C.brook}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
          <div>
            <div style={{fontWeight:700,fontSize:13,color:C.text}}>{d.toName}</div>
            <div style={{fontSize:11,color:C.dim}}>Acting manager · {d.startDate}{d.endDate?` → ${d.endDate}`:""}</div>
          </div>
          <div style={{display:"flex",gap:6}}>
            <Pill c={C.brook} t="Active" sm/>
            {canOverride&&<button onClick={()=>revoke(d.id)} style={{...BTN(C.danger,true),fontSize:10,padding:"3px 8px"}}>Revoke</button>}
          </div>
        </div>
      ))}
      {addM&&(
        <Overlay onClose={()=>setAddM(false)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:16}}>Delegate manager duties</div>
          <div style={{fontSize:12,color:C.dim,marginBottom:16}}>Assign a head nurse to handle leave approvals, rota overrides and lateness logging while the manager is away.</div>
          <div style={{marginBottom:12}}>
            <label style={LB}>Delegate to</label>
            <select value={f.toStaffId} onChange={e=>setF(p=>({...p,toStaffId:e.target.value}))} style={IS}>
              <option value="">Select staff member…</option>
              {siteNurses.filter(n=>n.headNurse||(state.staffRoles||{})[n.id]).map(n=><option key={n.id} value={n.id}>{n.name}</option>)}
            </select>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
            <div><label style={LB}>From</label><input type="date" value={f.startDate} onChange={e=>setF(p=>({...p,startDate:e.target.value}))} style={IS}/></div>
            <div><label style={LB}>Until (optional)</label><input type="date" value={f.endDate} onChange={e=>setF(p=>({...p,endDate:e.target.value}))} style={IS}/></div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={save} style={{...BTN(C.brook),flex:1,padding:"11px 0"}}>Confirm Delegation</button>
            <button onClick={()=>setAddM(false)} style={BTN(C.dim,true)}>Cancel</button>
          </div>
        </Overlay>
      )}
    </div>
  );
}

/* ── Clinician Requirements Modal ── */
const REQ_OPTIONS = [
  {id:"iv",  label:"IV Sedation", desc:"Conscious sedation — RA+IV qualified nurse required", color:"#ff4646"},
  {id:"ra",  label:"RA Only",     desc:"Relative analgesia only — RA qualified nurse needed",  color:"#d97706"},
  {id:"none",label:"No requirement", desc:"Any qualified nurse can assist",                    color:"#00e5a0"},
];
function ClinicianReqsModal({clinician, state, onSave, onClose}) {
  const existing = (state.clinicianRequirements||{})[clinician.id] || {default:[], overrides:{}, note:""};
  const toKey = reqs => reqs?.includes("iv")?"iv":reqs?.includes("ra")?"ra":"none";
  const [defKey, setDefKey]   = useState(toKey(existing.default));
  const [overrides, setOverrides] = useState(() => {
    const init = {};
    const workDays = ["Mon","Tue","Wed","Thu","Fri"].filter(d=>clinician.sched?.[d]);
    workDays.forEach(d => { init[d] = existing.overrides?.[d]!==undefined ? toKey(existing.overrides[d]) : "inherit"; });
    return init;
  });
  const [note, setNote] = useState(existing.note||"");
  const workDays = ["Mon","Tue","Wed","Thu","Fri"].filter(d=>clinician.sched?.[d]);
  function save() {
    const toReqs = k => k==="iv"?["ra","iv"]:k==="ra"?["ra"]:[];
    const ovResult = {};
    workDays.forEach(d => { if(overrides[d]!=="inherit") ovResult[d]=toReqs(overrides[d]); });
    onSave(clinician.id, {default:toReqs(defKey), overrides:ovResult, note:note.trim()});
  }
  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:2}}>Session Requirements</div>
      <div style={{fontSize:13,color:clinician.color||C.mid,fontWeight:600,marginBottom:4}}>{clinician.name} · {clinician.type}</div>
      <div style={{fontSize:11,color:C.dim,marginBottom:18}}>Sets what nurse qualification is needed. Affects rota warnings and the dentist's own view.</div>
      <label style={LB}>Default — applies to all sessions</label>
      <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:18}}>
        {REQ_OPTIONS.map(opt=>(
          <button key={opt.id} onClick={()=>setDefKey(opt.id)} style={{padding:"12px 14px",borderRadius:10,
            border:`1.5px solid ${defKey===opt.id?opt.color:C.border}`,
            background:defKey===opt.id?opt.color+"15":"transparent",color:defKey===opt.id?opt.color:C.soft,
            fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer",textAlign:"left",
            display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div>{opt.id==="iv"?"IV · ":opt.id==="ra"?"RA · ":""}{opt.label}</div>
              <div style={{fontSize:11,color:C.dim,fontWeight:400,marginTop:2}}>{opt.desc}</div>
            </div>
            {defKey===opt.id&&<span style={{fontSize:16}}>✓</span>}
          </button>
        ))}
      </div>
      {workDays.length>0&&(
        <>
          <label style={LB}>Day overrides — different requirement on a specific day</label>
          <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:18}}>
            {workDays.map(d=>(
              <div key={d} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",background:C.bg,borderRadius:8,border:`1px solid ${C.border}`}}>
                <span style={{minWidth:32,fontSize:12,fontWeight:700,color:C.text}}>{d}</span>
                <span style={{fontSize:11,color:C.dim,flex:1}}>{clinician.sched?.[d]}</span>
                <div style={{display:"flex",gap:3}}>
                  {[{v:"inherit",label:"Default",color:C.dim},...REQ_OPTIONS.map(o=>({v:o.id,label:o.id==="iv"?"IV":o.id==="ra"?"RA":"None",color:o.color}))].map(opt=>(
                    <button key={opt.v} onClick={()=>setOverrides(p=>({...p,[d]:opt.v}))}
                      style={{padding:"3px 7px",borderRadius:5,border:`1px solid ${overrides[d]===opt.v?opt.color:C.border}`,
                        background:overrides[d]===opt.v?opt.color+"15":"transparent",
                        color:overrides[d]===opt.v?opt.color:C.dim,fontFamily:"inherit",fontSize:10,fontWeight:600,cursor:"pointer"}}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      <div style={{marginBottom:18}}>
        <label style={LB}>Note</label>
        <input value={note} onChange={e=>setNote(e.target.value)} placeholder="e.g. IV sedation list Tues/Thurs" style={IS}/>
      </div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={save} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Save Requirements</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}

/* ── Clinician Leave Modal ── */
/* ══════════════════════════════════
   CPD / TRAINING EVENT MODAL
══════════════════════════════════ */
function CpdModal({siteId, state, onSave, onClose}) {
  const allNurses   = (state.nurses||NURSES_BASE).filter(n=>!n.maternity&&!n.archived);
  const allClins    = getClinicians(state).filter(c=>!c._deleted);
  const allRecs     = [...RECS_BASE,...(state.customRecs||[]).filter(r=>!r.archived)];
  const [saved, setSaved] = useState(false);

  const [f, setF] = useState(()=>{
    // Default: all clinicians at this site pre-selected — most common CPD use case
    const fbSite = siteId || fallbackSite();
    const siteClins = allClins.filter(c=>(c.sites||[c.site]).includes(fbSite));
    return {
      title:"", cpdType:"cpr", site:fbSite, date:"",
      allDay:true, startTime:"09:00", endTime:"17:30",
      scope:"individual",
      staffIds: siteClins.map(c=>c.id), // pre-select all clinicians
      notes:"",
    };
  });
  const sf = (k,v) => setF(p=>({...p,[k]:v}));

  // Re-compute staff when site changes
  const siteClins = allClins.filter(c=>(c.sites||[c.site]).includes(f.site));
  const siteNurses = allNurses.filter(n=>n.site===f.site);
  const siteRecs = allRecs.filter(r=>r.site===f.site);
  const allSiteStaff = [...siteClins,...siteNurses,...siteRecs];

  const ct = CPD_TYPES.find(t=>t.id===f.cpdType)||CPD_TYPES[0];

  function togStaff(id) {
    sf("staffIds", f.staffIds.includes(id) ? f.staffIds.filter(x=>x!==id) : [...f.staffIds, id]);
  }

  function selectAll(group) {
    const ids = group==="clins" ? siteClins.map(c=>c.id)
      : group==="nurses" ? siteNurses.map(n=>n.id)
      : group==="site" ? allSiteStaff.map(s=>s.id)
      : group==="all" ? [...allClins,...allNurses,...allRecs].map(s=>s.id)
      : [];
    sf("staffIds", [...new Set([...f.staffIds,...ids])]);
  }

  const missing = !f.date ? "date" : f.staffIds.length===0 ? "at least one person" : null;

  function doSave() {
    if (missing) return;
    onSave({...f, id:"cpd"+Date.now(), loggedBy:"principal", loggedAt:new Date().toISOString()});
    setSaved(true);
    setTimeout(onClose, 800);
  }

  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Log CPD / Training Day</div>
      <div style={{fontSize:12,color:C.dim,marginBottom:16}}>Full-day events close the surgery in the rota for selected staff.</div>

      {/* Title */}
      <div style={{marginBottom:12}}>
        <label style={LB}>Training title <span style={{color:C.dim,fontWeight:400}}>(e.g. Chris Barrow, First Aid Refresher)</span></label>
        <input value={f.title} onChange={e=>sf("title",e.target.value)} placeholder="e.g. Chris Barrow CPD Day" style={IS}/>
      </div>

      {/* Date & site */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
        <div>
          <label style={LB}>Date {!f.date&&<span style={{color:C.danger,fontWeight:700}}>*</span>}</label>
          <input type="date" value={f.date} onChange={e=>sf("date",e.target.value)} style={{...IS,borderColor:!f.date?C.danger:undefined}}/>
        </div>
        <div>
          <label style={LB}>Site</label>
          <select value={f.site} onChange={e=>{sf("site",e.target.value);sf("staffIds",[]);}} style={IS}>
            {Object.entries(SITES).map(([id,s])=><option key={id} value={id}>{s.name}</option>)}
          </select>
        </div>
      </div>

      {/* Full day toggle */}
      <div style={{marginBottom:12}}>
        <label style={LB}>Duration</label>
        <div style={{display:"flex",gap:8}}>
          {[[true,"Full day — closes surgery"],[false,"Partial day (appraisal, afternoon only)"]].map(([v,l])=>(
            <button key={String(v)} onClick={()=>sf("allDay",v)} style={{flex:1,padding:"9px",borderRadius:10,border:`1px solid ${f.allDay===v?ct.color:C.border}`,background:f.allDay===v?ct.color+"18":"transparent",color:f.allDay===v?ct.color:C.mid,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer"}}>{l}</button>
          ))}
        </div>
      </div>

      {!f.allDay&&(
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
          <div><label style={LB}>Start</label><input type="time" value={f.startTime} onChange={e=>sf("startTime",e.target.value)} style={IS}/></div>
          <div><label style={LB}>End</label><input type="time" value={f.endTime} onChange={e=>sf("endTime",e.target.value)} style={IS}/></div>
        </div>
      )}

      {/* Who */}
      <label style={LB}>
        Who's attending? {f.staffIds.length===0&&<span style={{color:C.danger,fontWeight:700}}>* select at least one person</span>}
      </label>

      {/* Quick select buttons */}
      <div style={{display:"flex",gap:5,marginBottom:8,flexWrap:"wrap"}}>
        <button onClick={()=>selectAll("clins")} style={{padding:"4px 10px",borderRadius:99,fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",border:`1px solid ${ct.color}`,background:ct.color+"15",color:ct.color}}>
          + All dentists ({siteClins.length})
        </button>
        <button onClick={()=>selectAll("nurses")} style={{padding:"4px 10px",borderRadius:99,fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",border:`1px solid ${C.teal}`,background:C.tealPale,color:C.teal}}>
          + All nurses ({siteNurses.length})
        </button>
        <button onClick={()=>selectAll("site")} style={{padding:"4px 10px",borderRadius:99,fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",border:`1px solid ${C.border}`,background:"transparent",color:C.mid}}>
          + Whole site
        </button>
        {f.staffIds.length>0&&<button onClick={()=>sf("staffIds",[])} style={{padding:"4px 10px",borderRadius:99,fontFamily:"inherit",fontSize:10,fontWeight:600,cursor:"pointer",border:`1px solid ${C.danger}`,background:"transparent",color:C.danger}}>Clear all</button>}
      </div>

      <div style={{maxHeight:180,overflowY:"auto",border:`1px solid ${f.staffIds.length===0?C.danger:C.border}`,borderRadius:10,padding:8,marginBottom:12}}>
        {[{header:"Dentists & Therapists",items:siteClins},
          {header:"Nurses",items:siteNurses},
          {header:"Reception",items:siteRecs}
        ].map(({header,items})=>items.length>0&&(
          <div key={header} style={{marginBottom:8}}>
            <div style={{fontSize:9,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{header}</div>
            <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
              {items.map(p=>{
                const on=f.staffIds.includes(p.id);
                return <button key={p.id} onClick={()=>togStaff(p.id)} style={{padding:"4px 10px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${on?ct.color:C.border}`,background:on?ct.color+"18":"transparent",color:on?ct.color:C.mid}}>
                  {on?"":""}{p.name.split(" ")[0]}
                </button>;
              })}
            </div>
          </div>
        ))}
      </div>

      {f.staffIds.length>0&&(
        <div style={{fontSize:11,color:ct.color,fontWeight:600,marginBottom:12}}>
          {f.staffIds.length} person{f.staffIds.length!==1?"s":""} selected
          {f.allDay&&" · Surgery will show closed for these people on this date"}
        </div>
      )}

      <div style={{marginBottom:16}}>
        <label style={LB}>Notes <span style={{color:C.dim,fontWeight:400}}>(optional — location, facilitator etc)</span></label>
        <input value={f.notes} onChange={e=>sf("notes",e.target.value)} placeholder="e.g. Manchester Central, 9am start" style={IS}/>
      </div>

      {missing&&(
        <div style={{background:"rgba(255,70,70,0.08)",border:"1px solid rgba(255,70,70,0.2)",borderRadius:8,padding:"8px 12px",marginBottom:12,fontSize:12,color:C.danger,fontWeight:600}}>
          Please add: {missing}
        </div>
      )}

      {saved
        ? <div style={{textAlign:"center",padding:12,color:C.green,fontWeight:700,fontSize:15}}>CPD event saved — rota updated</div>
        : <div style={{display:"flex",gap:8}}>
            <button onClick={doSave} disabled={!!missing}
              style={{...BTN(missing?C.muted:ct.color),flex:1,padding:"11px 0",opacity:missing?0.5:1}}>
              Save CPD event
            </button>
            <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
          </div>
      }
    </Overlay>
  );
}


function ClinicianLeaveModal({clinician,state,onSave,onClose}) {
  const [sel,setSel]           = useState([]);
  const [type,setType]         = useState("annual");
  const [rangeFrom,setRangeFrom] = useState("");
  const [rangeTo,setRangeTo]     = useState("");

  const existing = ((state.clinicianLeave||{})[clinician.id]||[]).filter(l=>l.status!=="declined");

  // Leave year for this clinician
  const { start: lyStart, end: lyEnd } = getLeaveYear("dentists", state.leaveYearConfig);
  const lyStartStr = lyStart.toISOString().split("T")[0];
  const lyEndStr   = lyEnd.toISOString().split("T")[0];
  const lyLabel = `${lyStart.toLocaleDateString("en-GB",{day:"numeric",month:"short"})} – ${lyEnd.toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}`;

  // Entitlement
  const entit = getEntitlement(state, clinician) || Math.round((clinician.type?.toLowerCase().includes("partner")?42:28) * (clinician.hrs||37.5) / 37.5);
  // Only count leave within the current leave year
  const thisYearLeave = existing.filter(l=>l.date>=lyStartStr&&l.date<=lyEndStr);
  const used  = thisYearLeave.filter(l=>l.type==="annual"&&l.status!=="declined").length;
  const remaining = entit - used;

  const [blocks, setBlocks] = useState([]);

  function addBlock() {
    if (!rangeFrom||!rangeTo||rangeFrom>rangeTo) return;
    const days = [];
    const cur = new Date(rangeFrom+"T12:00:00");
    const end = new Date(rangeTo+"T12:00:00");
    while (cur<=end) {
      const d = cur.toISOString().slice(0,10);
      const dow = cur.getDay();
      if (dow!==0&&dow!==6&&!UK_BH_2026[d]&&!isXmas(d)) days.push(d);
      cur.setDate(cur.getDate()+1);
    }
    if (days.length===0) return;
    setBlocks(b=>[...b,{from:rangeFrom, to:rangeTo, days}]);
    setRangeFrom(""); setRangeTo("");
    setSel(s=>[...new Set([...s,...days])]);
  }

  function removeBlock(i) {
    const removed = blocks[i].days;
    setBlocks(b=>b.filter((_,j)=>j!==i));
    setSel(s=>s.filter(d=>!removed.includes(d)));
  }

  const tog = date => setSel(s=>s.includes(date)?s.filter(d=>d!==date):[...s,date]);

  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Clinician Leave — {clinician.name}</div>
      <div style={{fontSize:12,color:C.dim,marginBottom:4}}>{clinician.type} · {SITES[clinician.site]?.name}</div>
      <div style={{fontSize:11,color:C.teal,marginBottom:14}}>Leave year: {lyLabel}</div>

      {/* Leave type toggle */}
      <div style={{display:"flex",gap:8,marginBottom:16}}>
        {[["annual","Annual Leave"],["earlyFinish","Early Finish"]].map(([id,label])=>(
          <button key={id} onClick={()=>{setType(id);setSel([]);setRangeFrom("");setRangeTo("");setBlocks([]);}}
            style={{flex:1,padding:"9px",borderRadius:10,border:`1px solid ${type===id?C.brook:C.border}`,background:type===id?"#f5f3ff":"transparent",color:type===id?C.brook:C.mid,fontFamily:"inherit",fontSize:12,fontWeight:600,cursor:"pointer"}}>
            {label}
          </button>
        ))}
      </div>

      {/* Stats bar — annual leave only */}
      {type==="annual"&&(
        <div style={{display:"flex",gap:8,marginBottom:14}}>
          {[[entit+"d","Entitlement",C.text],[used+"d","Used",C.warn],[remaining+"d","Remaining",remaining<5?C.danger:C.green],[sel.length+"d","Selecting",C.brook]].map(([v,l,c])=>(
            <div key={l} style={{...CS,flex:1,textAlign:"center",padding:"8px 4px"}}>
              <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:18,color:c}}>{v}</div>
              <div style={{fontSize:9,color:C.dim,marginTop:2,textTransform:"uppercase",letterSpacing:"0.06em"}}>{l}</div>
            </div>
          ))}
        </div>
      )}

      {/* Unified date picker */}
      <div style={{marginBottom:14}}>
        {/* Range blocks */}
        <div style={{marginBottom:10}}>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Add date ranges</div>
          {blocks.map((b,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,background:"rgba(139,92,246,0.08)",border:"1px solid #ddd6fe",borderRadius:8,padding:"7px 12px"}}>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:700,color:C.brook}}>{new Date(b.from+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short"})} → {new Date(b.to+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short"})}</div>
                <div style={{fontSize:10,color:C.mid}}>{b.days.length} working day{b.days.length!==1?"s":""}</div>
              </div>
              <button onClick={()=>removeBlock(i)} style={{background:"transparent",border:"none",color:C.danger,cursor:"pointer",fontSize:18,padding:"0 4px",lineHeight:1}}>×</button>
            </div>
          ))}
          <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
            <div style={{flex:1}}>
              <label style={{...LB,marginBottom:3}}>From</label>
              <input type="date" value={rangeFrom} onChange={e=>setRangeFrom(e.target.value)} style={{...IS,marginBottom:0}}/>
            </div>
            <div style={{flex:1}}>
              <label style={{...LB,marginBottom:3}}>To</label>
              <input type="date" value={rangeTo} min={rangeFrom||undefined} onChange={e=>setRangeTo(e.target.value)} style={{...IS,marginBottom:0}}/>
            </div>
            <button onClick={addBlock} disabled={!rangeFrom||!rangeTo||rangeFrom>rangeTo}
              style={{...BTN(C.brook),padding:"9px 14px",fontSize:12,flexShrink:0,opacity:(!rangeFrom||!rangeTo||rangeFrom>rangeTo)?0.4:1,whiteSpace:"nowrap"}}>
              + Add
            </button>
          </div>
        </div>

        {/* Individual day picker */}
        <div style={{borderTop:`1px solid ${C.border}`,paddingTop:10,marginTop:4}}>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Or tap individual days</div>
          <div style={{maxHeight:200,overflowY:"auto"}}>
            {CAL.weeks.map(wk=>(
              <div key={wk.start} style={{marginBottom:8}}>
                <div style={{fontSize:9,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>{wk.days[0]?.label.split(" ").slice(1).join(" ")}</div>
                <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                  {wk.days.filter(d=>!d.bh).map(d=>{
                    const on=sel.includes(d.date), taken=existing.some(l=>l.date===d.date);
                    return <button key={d.date} onClick={()=>tog(d.date)}
                      style={{padding:"4px 8px",borderRadius:7,fontFamily:"inherit",fontSize:10,fontWeight:600,cursor:"pointer",
                        border:`1px solid ${on?"#a78bfa":taken?C.muted:C.border}`,
                        background:on?"#f5f3ff":taken?"#f9f9f9":"transparent",
                        color:on?C.brook:taken?C.dim:C.soft}}>
                      {d.label.split(" ").slice(0,2).join(" ")}{on?" ✓":""}
                    </button>;
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {sel.length>0&&(
          <div style={{background:"rgba(139,92,246,0.08)",border:"1px solid #ddd6fe",borderRadius:8,padding:"10px 14px",marginTop:10,fontSize:12,color:C.brook}}>
            <strong>{sel.length} day{sel.length!==1?"s":""}</strong> selected
            {blocks.length>0&&<span style={{color:C.mid}}> · {blocks.length} range{blocks.length!==1?"s":""}{sel.filter(d=>!blocks.some(b=>b.days.includes(d))).length>0?` + ${sel.filter(d=>!blocks.some(b=>b.days.includes(d))).length} individual`:""}</span>}
          </div>
        )}
      </div>

      {type==="annual"&&sel.length>0&&used+sel.length>entit&&(
        <div style={{background:"rgba(245,197,24,0.08)",border:`1px solid ${C.warn}44`,borderRadius:8,padding:10,marginBottom:12,fontSize:12,color:C.warn}}>
          Exceeds entitlement — please review before saving
        </div>
      )}

      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>sel.length&&onSave(clinician.id,sel,type)}
          style={{...BTN(sel.length?C.brook:C.muted),flex:1,padding:"11px 0",opacity:sel.length?1:0.5}}>
          Save {sel.length||0} day{sel.length!==1?"s":""}
        </button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}


/* ══════════════════════════════════════════════════════════════
   LATENESS PATTERN ANALYSIS
   Detects: day-of-week, clinician, time-of-day, escalation,
   post-absence, correlation with Bradford. Returns welfare flags.
══════════════════════════════════════════════════════════════ */
function analyseLatenessPatterns(staffId, state) {
  const records = (state.latenessLog||[])
    .filter(l => l.staffId === staffId)
    .sort((a,b) => a.date.localeCompare(b.date));

  if (records.length < 2) return { patterns: [], riskLevel: "none" };

  const now = new Date();
  const ago90 = new Date(now); ago90.setDate(now.getDate()-90);
  const ago90s = ago90.toISOString().split("T")[0];
  const recent = records.filter(r => r.date >= ago90s);

  const patterns = [];

  // ── 1. Day-of-week pattern ────────────────────────────────────
  const byDay = {};
  recent.forEach(r => {
    const day = new Date(r.date+"T12:00").toLocaleDateString("en-GB",{weekday:"long"});
    byDay[day] = (byDay[day]||0) + 1;
  });
  Object.entries(byDay).forEach(([day, count]) => {
    if (count >= 3) {
      patterns.push({
        type: "day_of_week",
        sev: count >= 4 ? "amber" : "yellow",
        msg: `Late ${count} times on ${day}s in 90 days — possible commute, childcare or motivation pattern`,
        day, count,
      });
    }
  });

  // ── 2. Escalating frequency ───────────────────────────────────
  const ago30s = new Date(now); ago30s.setDate(now.getDate()-30);
  const ago60s = new Date(now); ago60s.setDate(now.getDate()-60);
  const last30 = recent.filter(r=>r.date>=ago30s.toISOString().split("T")[0]).length;
  const prev30 = recent.filter(r=>r.date>=ago60s.toISOString().split("T")[0]&&r.date<ago30s.toISOString().split("T")[0]).length;
  if (last30 >= 2 && last30 > prev30 * 1.5 && prev30 > 0) {
    patterns.push({
      type: "escalating",
      sev: "amber",
      msg: `Lateness escalating — ${last30} incidents this month vs ${prev30} last month`,
      last30, prev30,
    });
  }

  // ── 3. Clinician correlation ──────────────────────────────────
  const clinCounts = {};
  recent.filter(r=>r.clinicianId||r.reason).forEach(r => {
    const key = r.clinicianId||r.reason;
    clinCounts[key] = (clinCounts[key]||0)+1;
  });
  // Check if same reason appears 3+ times (often linked to specific dentist)
  const reasonCounts = {};
  recent.forEach(r=>{ if(r.reason) reasonCounts[r.reason]=(reasonCounts[r.reason]||0)+1; });
  Object.entries(reasonCounts).forEach(([reason,count])=>{
    if (count>=3 && ["Dentist ran late","Extra patients","Treatment overran"].includes(reason)) {
      patterns.push({
        type: "clinician_linked",
        sev: "yellow",
        msg: `"${reason}" cited ${count} times — may be linked to a specific dentist running late`,
        reason, count,
      });
    }
  });

  // ── 4. Post-absence pattern ───────────────────────────────────
  const sickDates = new Set((state.sick||[])
    .filter(s=>s.staffId===staffId)
    .flatMap(s=>{ const days=[]; for(let i=0;i<(s.days||1);i++){const d=new Date(s.date);d.setDate(d.getDate()+i);days.push(d.toISOString().split("T")[0]);} return days; }));
  const postAbsenceLate = recent.filter(r=>{
    const prev = new Date(r.date+"T12:00"); prev.setDate(prev.getDate()-1);
    return sickDates.has(prev.toISOString().split("T")[0]);
  });
  if (postAbsenceLate.length >= 2) {
    patterns.push({
      type: "post_absence",
      sev: "yellow",
      msg: `Late on return from absence ${postAbsenceLate.length} times — worth noting at RTW conversations`,
    });
  }

  // ── 5. Bradford correlation ───────────────────────────────────
  const sick = (state.sick||[]).filter(s=>s.staffId===staffId&&s.reason!=="medical");
  const totalDays = sick.reduce((a,s)=>a+(s.days||1),0);
  const bf = sick.length * sick.length * totalDays;
  const highBF = bf >= 100;
  const hasLateness = recent.length >= 3;
  if (highBF && hasLateness) {
    patterns.push({
      type: "bradford_lateness",
      sev: "amber",
      msg: `High Bradford Factor (${bf}) combined with ${recent.length} lateness incidents — welfare conversation recommended`,
      bf,
    });
  }

  // Risk level
  const amberCount = patterns.filter(p=>p.sev==="amber").length;
  const riskLevel = amberCount >= 2 ? "high" : amberCount >= 1 ? "medium" : patterns.length > 0 ? "low" : "none";

  return { patterns, riskLevel, recentCount: recent.length, total: records.length };
}

/* ══════════════════════════════════════════════════════════════
   LATENESS PATTERN ANALYSIS
   Detects: day-of-week, clinician-linked, escalation,
   post-absence, Bradford correlation. Returns welfare flags.
══════════════════════════════════════════════════════════════ */
/* ── Best Attendance ── */
function BestAttendance({state}) {
  const allStaff = [...(state.nurses||NURSES_BASE),...RECS_BASE].filter(s=>!s.maternity&&!s.archived);
  const sick = state.sick||[];
  const lateness = state.latenessLog||[];

  const scores = allStaff.map(s=>{
    const episodes = sick.filter(r=>r.staffId===s.id).length;
    const lateCount = lateness.filter(l=>l.staffId===s.id).length;
    const perfect = episodes===0 && lateCount===0;
    const near = episodes===0 && lateCount<=1;
    return {person:s,episodes,lateCount,perfect,near};
  }).sort((a,b)=>(a.episodes+a.lateCount)-(b.episodes+b.lateCount));

  const perfect = scores.filter(s=>s.perfect);
  const near = scores.filter(s=>s.near&&!s.perfect);

  if (perfect.length===0) return null;

  return (
    <div style={{...CS,marginTop:20,padding:18}}>
      <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12}}>Best Attendance</div>
      {perfect.length>0&&(
        <div style={{marginBottom:12}}>
          <div style={{fontSize:11,fontWeight:700,color:C.green,marginBottom:6}}>Perfect — zero absences, zero lateness</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {perfect.map(s=><span key={s.person.id} style={{background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:99,fontSize:12,fontWeight:600,color:C.green,padding:"4px 12px"}}>{s.person.name.split(" ")[0]}</span>)}
          </div>
        </div>
      )}
      {near.length>0&&(
        <div>
          <div style={{fontSize:11,fontWeight:700,color:C.teal,marginBottom:6}}>Near perfect — no absences</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {near.map(s=><span key={s.person.id} style={{background:C.tealPale,border:`1px solid ${C.tealBorder}`,borderRadius:99,fontSize:12,fontWeight:600,color:C.teal,padding:"4px 12px"}}>{s.person.name.split(" ")[0]}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}


/* ══════════════════════════════════
   KEYHOLDER MANAGEMENT
══════════════════════════════════ */
function KeyholderEditModal({siteId, state, allStaff, onSave, onClose}) {
  const site = SITES[siteId];
  const existing = getSiteKeyholders(state, siteId);
  const [selected, setSelected] = useState(new Set(existing));
  // Include staff with unknown/empty site for single-site practices (self-healing)
  const knownSiteIds = new Set(Object.keys(SITES));
  const onlyOneSite = knownSiteIds.size <= 1;
  const siteStaff = allStaff.filter(s=>{
    if (!s.site || !knownSiteIds.has(s.site)) return onlyOneSite;
    return s.site === siteId;
  });
  const toggle = id => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Keyholders</div>
      <div style={{fontSize:13,color:site.color,marginBottom:6}}>{site.name}</div>
      <div style={{fontSize:12,color:C.dim,marginBottom:16}}>Select everyone at this site who holds a key. Omni Rota will flag when none of them are on the rota.</div>
      <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:20,maxHeight:320,overflowY:"auto"}}>
        {siteStaff.map(s=>{
          const on = selected.has(s.id);
          return (
            <button key={s.id} onClick={()=>toggle(s.id)}
              style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:10,
                border:`1.5px solid ${on?C.teal:C.border}`,background:on?C.tealPale:"transparent",
                cursor:"pointer",fontFamily:"inherit",textAlign:"left"}}>
              <div style={{width:20,height:20,borderRadius:99,border:`2px solid ${on?C.teal:C.border}`,background:on?C.teal:"transparent",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                {on&&<svg width="11" height="11" viewBox="0 0 12 12"><polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>}
              </div>
              <div>
                <div style={{fontSize:13,fontWeight:600,color:on?C.teal:C.text}}>{s.name}</div>
                <div style={{fontSize:11,color:C.dim}}>{s.type||s.area||"Staff"} · {s.hrs||37.5}h</div>
              </div>
            </button>
          );
        })}
        {siteStaff.length===0&&<div style={{fontSize:12,color:C.dim,padding:"10px 0"}}>No staff found for this site.</div>}
      </div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>onSave(siteId,{holders:[...selected]})} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>
          Save {selected.size} keyholder{selected.size!==1?"s":""}
        </button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}

function KeyholderOverrideModal({overrideM, allStaff, onSave, onClose}) {
  const [selStaff,setSelStaff] = useState("");
  const [note,setNote] = useState("");
  const overKey = `${overrideM.date}_${overrideM.siteId}_any`;
  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Keyholder Override</div>
      <div style={{fontSize:13,color:C.dim,marginBottom:16}}>{SITES[overrideM.siteId]?.name} · {overrideM.dateLabel}</div>
      <div style={{marginBottom:12}}>
        <label style={LB}>Who will cover?</label>
        <select value={selStaff} onChange={e=>setSelStaff(e.target.value)} style={IS}>
          <option value="">Select person…</option>
          {Object.entries(SITES).map(([sid,si])=>(
            <optgroup key={sid} label={si.name}>
              {allStaff.filter(s=>s.site===sid).map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
            </optgroup>
          ))}
        </select>
      </div>
      <div style={{marginBottom:20}}>
        <label style={LB}>Note (optional)</label>
        <input value={note} onChange={e=>setNote(e.target.value)} placeholder="e.g. Amanda will hold keys this day" style={IS}/>
      </div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>selStaff&&onSave(overKey,selStaff,note)} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Confirm</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}

function KeyholderPanel({state, set, isPrincipal}) {
  const [editSite, setEditSite] = useState(null);
  const [overrideM, setOverrideM] = useState(null);
  const ragIssues = buildKeyholderRAG(state);
  const redIssues = ragIssues.filter(i=>i.rag==="red");
  const allStaff = [
    ...(state.nurses||NURSES_BASE).filter(n=>!n.maternity&&!n.archived),
    ...(IS_DCO ? RECS_BASE : (state.customRecs||[]).filter(r=>!r.archived)),
    ...getClinicians(state).filter(c=>!c._deleted),
  ];

  function saveKeyholders(siteId, kh) {
    set(s=>({...s, siteKeyholders:{...s.siteKeyholders,[siteId]:kh}}));
    setEditSite(null);
  }
  function saveOverride(overKey, staffId, note) {
    const person = allStaff.find(s=>s.id===staffId);
    set(s=>({...s, keyholderOverrides:{...s.keyholderOverrides,[overKey]:{staffId,name:person?.name,note,setBy:s._lastUser||"Manager"}}}));
    setOverrideM(null);
  }

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:10}}>
        <div>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:2}}>Keyholders</div>
          <div style={{fontSize:12,color:C.dim}}>Staff who hold keys at each site — Omni Rota flags when none are on the rota</div>
        </div>
        {redIssues.length>0&&<Pill c={C.danger} t={`${redIssues.length} gap${redIssues.length!==1?"s":""} in next 4 weeks`}/>}
      </div>

      {/* Cover gaps */}
      {ragIssues.length>0&&(
        <div style={{marginBottom:20}}>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Upcoming Cover Gaps</div>
          {Object.entries(
            ragIssues.reduce((acc,i)=>{
              const k=i.siteId;
              if(!acc[k])acc[k]={siteId:i.siteId,red:[],amber:[]};
              if(i.date) acc[k][i.rag].push(i);
              else acc[k].red.push(i);
              return acc;
            },{})
          ).map(([k,g])=>{
            const worstRag = g.red.length>0?"red":"amber";
            const bgColor = worstRag==="red"?"#fef2f2":"rgba(245,197,24,0.06)";
            const bdColor = worstRag==="red"?"#fecaca":"rgba(245,197,24,0.2)";
            const txColor = worstRag==="red"?C.danger:C.warn;
            return (
              <div key={k} style={{background:bgColor,border:`1px solid ${bdColor}`,borderRadius:10,padding:"12px 16px",marginBottom:8}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                  <div>
                    <div style={{fontWeight:700,fontSize:13,color:txColor,marginBottom:2}}>{SITES[g.siteId]?.name}</div>
                    {g.red.some(i=>!i.date)&&<div style={{fontSize:11,color:C.danger,fontWeight:700}}>No keyholders assigned</div>}
                    {g.red.filter(i=>i.date).length>0&&(
                      <div style={{fontSize:11,color:C.danger,marginBottom:2}}>
                        {g.red.filter(i=>i.date).length} day{g.red.filter(i=>i.date).length!==1?"s":""} with no keyholder on the rota
                        {g.red.filter(i=>i.date).slice(0,3).map(i=>(
                          <span key={i.date} style={{marginLeft:6,fontSize:10,background:"rgba(255,70,70,0.08)",padding:"1px 6px",borderRadius:99}}>{i.dateLabel}</span>
                        ))}
                        {g.red.filter(i=>i.date).length>3&&<span style={{marginLeft:4,fontSize:10,color:C.danger}}>+{g.red.filter(i=>i.date).length-3} more</span>}
                      </div>
                    )}
                    {g.amber.filter(i=>i.date).length>0&&(
                      <div style={{fontSize:11,color:C.warn}}>{g.amber.filter(i=>i.date).length} days flagged in weeks 5–8</div>
                    )}
                  </div>
                  {isPrincipal&&g.red.filter(i=>i.date).slice(0,1).map(i=>(
                    <button key={i.date} onClick={()=>setOverrideM({siteId:g.siteId,date:i.date,dateLabel:i.dateLabel})} style={{...BTN(C.warn,true),fontSize:11,padding:"5px 12px"}}>Override →</button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {ragIssues.length===0&&(
        <div style={{background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:10,padding:"12px 16px",marginBottom:20,fontSize:13,color:C.green,fontWeight:600}}>
          Keyholder cover looks good for the next 2 months
        </div>
      )}

      {/* Site cards */}
      <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Keyholders by Site</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))",gap:10,marginBottom:20}}>
        {Object.entries(SITES).map(([siteId,site])=>{
          const holders = getSiteKeyholderPeople(state, siteId);
          return (
            <div key={siteId} style={{...CS,borderTop:`3px solid ${site.color}`}}>
              <div style={{fontWeight:700,color:site.color,fontSize:14,marginBottom:10}}>{site.name}</div>
              {holders.length===0
                ? <div style={{fontSize:12,color:C.danger,fontWeight:600,marginBottom:10}}>No keyholders assigned</div>
                : <div style={{display:"flex",flexDirection:"column",gap:5,marginBottom:10}}>
                    {holders.map(p=>(
                      <div key={p.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px",background:C.bg,borderRadius:7}}>
                        <div style={{width:6,height:6,borderRadius:99,background:site.color,flexShrink:0}}/>
                        <div>
                          <div style={{fontSize:12,fontWeight:600,color:C.text}}>{p.name}</div>
                          <div style={{fontSize:10,color:C.dim}}>{p.type||p.area||"Staff"}</div>
                        </div>
                      </div>
                    ))}
                  </div>
              }
              {isPrincipal&&(
                <button onClick={()=>setEditSite(siteId)} style={{...BTN(site.color,true),width:"100%",fontSize:11,padding:"6px 0"}}>
                  Edit keyholders
                </button>
              )}
            </div>
          );
        })}
      </div>

      {editSite&&<KeyholderEditModal siteId={editSite} state={state} allStaff={allStaff} onSave={saveKeyholders} onClose={()=>setEditSite(null)}/>}
      {overrideM&&<KeyholderOverrideModal overrideM={overrideM} allStaff={allStaff} onSave={saveOverride} onClose={()=>setOverrideM(null)}/>}
    </div>
  );
}

/* ══════════════════════════════════
   PLANNING
══════════════════════════════════ */
function DelegatePanel({siteId, state, set, canOverride}) {
  const [addM, setAddM] = useState(false);
  const [f, setF] = useState({toStaffId:"", startDate:"", endDate:""});
  const allNurses = getNursesForSite(state, siteId);
  const siteNurses = allNurses.filter(n=>n.headNurse||(state.staffRoles||{})[n.id]);
  const active = (state.delegations||[]).filter(d=>d.siteId===siteId&&d.active!==false);

  function save() {
    if (!f.toStaffId||!f.startDate) return;
    const person = allNurses.find(n=>n.id===f.toStaffId);
    const delId = "del"+Date.now();
    set(s=>({...s,
      delegations:[...(s.delegations||[]), {
        id:delId, siteId, toStaffId:f.toStaffId,
        toName:person?.name||"", startDate:f.startDate, endDate:f.endDate||null,
        active:true, loggedBy:state._lastUser||"Manager"
      }],
      unsungHeroes:[...(s.unsungHeroes||[]), {
        id:"uh_"+delId, staffId:f.toStaffId, name:person?.name||"",
        site:siteId, loggedBy:state._lastUser||"Manager",
        date:new Date().toISOString().split("T")[0],
        note:`Nominated as Acting Manager — covering ${s._lastUser||"manager"}'s absence from ${f.startDate}${f.endDate?" to "+f.endDate:""}. Stepped up for the team.`,
        status:"auto"
      }]
    }));
    setAddM(false); setF({toStaffId:"",startDate:"",endDate:""});
  }
  function revoke(id) { set(s=>({...s,delegations:(s.delegations||[]).map(d=>d.id===id?{...d,active:false}:d)})); }

  return (
    <div style={{...CS,marginBottom:16,padding:"14px 16px",borderLeft:`3px solid ${C.brook}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:active.length>0?10:0}}>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:C.brook}}>Cover Delegation</div>
          <div style={{fontSize:11,color:C.dim,marginTop:1}}>Going on leave? Assign a head nurse to cover approvals and rota decisions while you're away.</div>
        </div>
        {canOverride&&<button onClick={()=>setAddM(true)} style={{...BTN(C.brook,true),fontSize:11,padding:"5px 12px",flexShrink:0}}>+ Delegate</button>}
      </div>
      {active.map(d=>(
        <div key={d.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:8,padding:"8px 10px",background:C.brook+"11",borderRadius:8,flexWrap:"wrap",gap:6}}>
          <div>
            <span style={{fontWeight:700,fontSize:12,color:C.text}}>{d.toName}</span>
            <span style={{fontSize:11,color:C.dim}}> · Acting manager · {d.startDate}{d.endDate?` → ${d.endDate}`:""}</span>
          </div>
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            <Pill c={C.brook} t="Active" sm/>
            {canOverride&&<button onClick={()=>revoke(d.id)} style={{...BTN(C.danger,true),fontSize:10,padding:"3px 8px"}}>Revoke</button>}
          </div>
        </div>
      ))}
      {addM&&(
        <Overlay onClose={()=>setAddM(false)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Delegate manager duties</div>
          <div style={{fontSize:12,color:C.dim,marginBottom:16}}>Assign a head nurse to handle leave approvals, rota overrides and lateness logging while you're away.</div>
          <div style={{marginBottom:12}}>
            <label style={LB}>Delegate to</label>
            <select value={f.toStaffId} onChange={e=>setF(p=>({...p,toStaffId:e.target.value}))} style={IS}>
              <option value="">Select staff member…</option>
              {allNurses.filter(n=>n.headNurse||(state.staffRoles||{})[n.id]).map(n=><option key={n.id} value={n.id}>{n.name}</option>)}
            </select>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
            <div><label style={LB}>From</label><input type="date" value={f.startDate} onChange={e=>setF(p=>({...p,startDate:e.target.value}))} style={IS}/></div>
            <div><label style={LB}>Until (optional)</label><input type="date" value={f.endDate} onChange={e=>setF(p=>({...p,endDate:e.target.value}))} style={IS}/></div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={save} style={{...BTN(C.brook),flex:1,padding:"11px 0"}}>Confirm Delegation</button>
            <button onClick={()=>setAddM(false)} style={BTN(C.dim,true)}>Cancel</button>
          </div>
        </Overlay>
      )}
    </div>
  );
}


function DelegationDrillDown({s, approvedLeave, state, set, user}) {
  const leaveDates = approvedLeave.map(l=>l.date);
  const existingDel = (state.delegations||[]).find(d=>
    d.fromStaffId===s.id && d.active!==false &&
    leaveDates.some(dt=>dt>=d.startDate&&dt<=d.endDate)
  );
  const siteColleagues = [
    ...(state.nurses||NURSES_BASE).filter(n=>n.site===s.site&&!n.maternity&&!n.archived&&n.id!==s.id),
    ...RECS_BASE.filter(r=>r.site===s.site&&r.id!==s.id),
  ];
  const delegateConflict = existingDel
    ? leaveDates.filter(dt=>isAbsent(state,existingDel.toStaffId,dt))
    : [];
  const eligibleColleagues = siteColleagues.filter(c=>
    c.headNurse||c.manager||(state.staffRoles||{})[c.id]
  );

  return (
    <div style={{borderTop:`1px solid ${C.teal}22`,paddingTop:8,marginTop:4}}>
      <div style={{fontSize:10,fontWeight:700,color:C.teal,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.06em"}}>Delegation while away</div>
      {existingDel ? (
        <div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,flexWrap:"wrap"}}>
            <span style={{fontSize:12,color:C.text}}>Delegated to <strong>{existingDel.toName}</strong></span>
            <button
              onClick={e=>{e.stopPropagation();set(st=>({...st,delegations:(st.delegations||[]).map(d=>d.id===existingDel.id?{...d,active:false}:d)}));}}
              style={{...BTN(C.dim,true),fontSize:9,padding:"2px 8px"}}>
              Change
            </button>
          </div>
          {delegateConflict.length>0&&(
            <div style={{padding:"6px 10px",background:"rgba(255,70,70,0.08)",border:`1px solid #fecaca`,borderRadius:8,fontSize:11,color:C.danger}}>
              {existingDel.toName} is also off on {delegateConflict.map(dt=>CAL.days.find(x=>x.date===dt)?.label?.split(" ")[1]||dt).join(", ")} — delegation gap
            </div>
          )}
        </div>
      ) : (
        <div>
          <div style={{fontSize:11,color:C.dim,marginBottom:6}}>No delegate set. Choose someone to cover duties:</div>
          {eligibleColleagues.length===0 ? (
            <span style={{fontSize:11,color:C.muted}}>No head nurses or managers available on this site.</span>
          ) : (
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {eligibleColleagues.map(c=>{
                const hasConflict = leaveDates.some(dt=>isAbsent(state,c.id,dt));
                return (
                  <button key={c.id}
                    onClick={e=>{e.stopPropagation();set(st=>({...st,delegations:[...(st.delegations||[]),{
                      id:"del"+Date.now(),siteId:s.site,fromStaffId:s.id,toStaffId:c.id,
                      toName:c.name,startDate:leaveDates.sort()[0],endDate:leaveDates.sort().slice(-1)[0],
                      active:true,loggedBy:user?.name||"Manager"
                    }]}));}}
                    style={{...BTN(hasConflict?C.warn:C.teal,true),fontSize:11,padding:"4px 12px",opacity:hasConflict?0.7:1}}>
                    {c.name.split(" ")[0]}{hasConflict?" ⚠":""}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── RECEPTION CONTRACTS ─────────────────────────────────────────────── */
function ReceptionContracts({siteId, state, set}) {
  const site = SITES[siteId];
  const DAYS = ["Mon","Tue","Wed","Thu","Fri"];

  const knownSiteIds = new Set(Object.keys(SITES));
  const onlyOne = knownSiteIds.size <= 1;
  const recs = [
    ...RECS_BASE.filter(r=>r.site===siteId&&r.type!=="Manager"),
    ...(state.customRecs||[]).filter(r=>{
      if (r.archived||r.type==="Manager") return false;
      const unknown = !r.site||!knownSiteIds.has(r.site);
      return r.site===siteId||(unknown&&onlyOne);
    }),
  ];

  const contracts = state.recContracts||{};

  function setShift(staffId, day, shiftId) {
    set(s=>({...s, recContracts:{...s.recContracts,
      [staffId]:{...(s.recContracts||{})[staffId],[day]:shiftId}
    }}));
  }

  function setCustomTime(staffId, day, field, val) {
    const key = `${day}_${field}`;
    set(s=>({...s, recContracts:{...s.recContracts,
      [staffId]:{...(s.recContracts||{})[staffId],[key]:val}
    }}));
  }

  function setAllDays(staffId, shiftId) {
    // Use updater function to avoid stale closure bug
    set(s=>{
      const existing = (s.recContracts||{})[staffId]||{};
      const pattern = {...existing};
      DAYS.forEach(d=>{ pattern[d]=shiftId; });
      return {...s, recContracts:{...s.recContracts,[staffId]:pattern}};
    });
  }

  function toggleMultiLate(staffId) {
    set(s=>{
      const cur = (s.recContracts||{})[staffId]?.multiLate;
      return {...s, recContracts:{...s.recContracts,
        [staffId]:{...((s.recContracts||{})[staffId]||{}), multiLate:!cur}
      }};
    });
  }

  function toggleDayUnlock(staffId, day) {
    const key = `unlock_${day}`;
    set(s=>{
      const cur = (s.recContracts||{})[staffId]?.[key];
      return {...s, recContracts:{...s.recContracts,
        [staffId]:{...((s.recContracts||{})[staffId]||{}),[key]:!cur,[day]:!cur?"off":"ns"}
      }};
    });
  }

  function scheduledHrs(staffId) {
    const c = contracts[staffId]||{};
    return DAYS.reduce((total,d)=>{
      const sh = c[d];
      if (!sh||sh==="off"||sh==="ns") return total;
      if (sh==="early")  return total+6;    // 08:00–14:00
      if (sh==="early2") return total+6;    // 07:45–13:45
      if (sh==="full")   return total+9.5;  // 08:00–17:30
      if (sh==="late")   return total+6;    // 14:00–20:00
      if (sh==="custom") {
        const start = c[`${d}_start`]||"09:00";
        const end   = c[`${d}_end`]||"17:30";
        const [sh2,sm2]=start.split(":").map(Number);
        const [eh,em]=end.split(":").map(Number);
        return total+Math.max(0,((eh*60+em)-(sh2*60+sm2))/60);
      }
      return total;
    },0);
  }

  const SHIFT_COLOR = {ns:C.muted, off:C.dim, early:C.teal, early2:"#0891b2", full:"#7c3aed", late:"#f97316", custom:"#d97706"};
  const contractSet = recs.filter(r=>contracts[r.id]&&Object.values(contracts[r.id]).some(v=>v&&v!=="off"&&v!=="ns"&&typeof v==="string"&&!v.includes(":")&&v!=="multiLate")).length;

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16,flexWrap:"wrap",gap:10}}>
        <div>
          <h4 style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:16,color:C.heading,marginBottom:4}}>Reception Shift Contracts</h4>
          <div style={{fontSize:12,color:C.dim}}>Set each person's routine pattern — drives auto-assignment. Override individual days on the rota as needed.</div>
        </div>
        <div style={{fontSize:11,color:contractSet===recs.length?C.green:C.warn,background:contractSet===recs.length?"#f0fdf4":"rgba(245,197,24,0.06)",border:`1px solid ${contractSet===recs.length?"#bbf7d0":"rgba(245,197,24,0.2)"}`,borderRadius:8,padding:"5px 12px",fontWeight:700}}>
          {contractSet}/{recs.length} patterns set
        </div>
      </div>

      {/* Legend */}
      <div style={{display:"flex",gap:6,marginBottom:16,flexWrap:"wrap"}}>
        {[
          {label:"Not scheduled",color:C.muted},
          {label:"Early  07:45–13:45",color:"#22d3ee"},
          {label:"Early  08:00–14:00",color:C.teal},
          {label:"Full   08:00–17:30",color:"#7c3aed"},
          {label:"Late   14:00–20:00",color:"#f97316"},
          {label:"Custom hours",color:"#d97706"},
        ].map(s=>(
          <span key={s.label} style={{fontSize:10,fontWeight:700,color:s.color,background:s.color+"18",border:`1px solid ${s.color}33`,borderRadius:6,padding:"3px 9px"}}>{s.label}</span>
        ))}
      </div>

      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",minWidth:560}}>
          <thead>
            <tr>
              <th style={{textAlign:"left",padding:"6px 10px",fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:`1px solid ${C.border}`,minWidth:140}}>Staff</th>
              <th style={{textAlign:"center",padding:"6px 4px",fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:`1px solid ${C.border}`,width:40}}>All</th>
              {DAYS.map(d=>(
                <th key={d} style={{textAlign:"center",padding:"6px 6px",fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:`1px solid ${C.border}`,minWidth:110}}>{d}</th>
              ))}
              <th style={{textAlign:"center",padding:"6px 6px",fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:`1px solid ${C.border}`,minWidth:64}}>Multi-late</th>
              <th style={{textAlign:"right",padding:"6px 10px",fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:`1px solid ${C.border}`}}>Hrs/wk</th>
            </tr>
          </thead>
          <tbody>
            {recs.map((r,ri)=>{
              const c = contracts[r.id]||{};
              const sched = scheduledHrs(r.id);
              const contract = r.hrs||37.5;
              const diff = sched - contract;
              const hasPattern = Object.values(c).some(v=>v&&v!=="off"&&v!=="ns"&&typeof v==="string"&&!v.includes(":"));
              return (
                <tr key={r.id} style={{background:ri%2===0?C.bg:"transparent"}}>
                  <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.border2}`}}>
                    <div style={{fontSize:12,fontWeight:700,color:C.text}}>{r.name.split(" ")[0]} <span style={{fontWeight:400,color:C.dim}}>{r.name.split(" ").slice(1).join(" ")}</span></div>
                    <div style={{fontSize:10,color:C.dim,marginTop:1}}>{r.type}{r.hrs?` · ${r.hrs}h contract`:""}{r.noFri?" · No Fri (contractual)":""}</div>
                    {!hasPattern&&<div style={{fontSize:9,color:C.warn,marginTop:2}}>No pattern set</div>}
                  </td>
                  <td style={{padding:"8px 4px",borderBottom:`1px solid ${C.border2}`,textAlign:"center"}}>
                    <select onChange={e=>{if(e.target.value)setAllDays(r.id,e.target.value);e.target.value="";}}
                      defaultValue=""
                      style={{fontSize:9,padding:"2px 2px",borderRadius:4,border:`1px solid ${C.border}`,background:C.surface,color:C.text,cursor:"pointer",width:36}}>
                      <option value="">--</option>
                      <option value="ns">NS</option>
                      <option value="early2">E 7:45</option>
                      <option value="early">E 8:00</option>
                      <option value="full">F</option>
                      <option value="late">L</option>
                    </select>
                  </td>
                  {DAYS.map(d=>{
                    const noFriContractual = r.noFri && d==="Fri";
                    const unlocked = !!(c[`unlock_${d}`]);
                    const isLocked = noFriContractual && !unlocked;
                    const shiftId = c[d]||"ns";
                    const col = SHIFT_COLOR[shiftId]||C.dim;
                    const isCustom = shiftId==="custom";
                    return (
                      <td key={d} style={{padding:"6px 4px",borderBottom:`1px solid ${C.border2}`,textAlign:"center",verticalAlign:"top"}}>
                        {isLocked ? (
                          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
                            <span style={{fontSize:9,color:C.muted,padding:"3px 6px",borderRadius:4,background:"#181626"}}>No Fri</span>
                            <button onClick={()=>toggleDayUnlock(r.id,d)} style={{fontSize:8,color:C.teal,background:"transparent",border:`1px solid ${C.teal}44`,borderRadius:3,padding:"1px 5px",cursor:"pointer"}}>unlock</button>
                          </div>
                        ) : (
                          <div style={{display:"flex",flexDirection:"column",gap:2}}>
                            <select
                              value={shiftId}
                              onChange={e=>setShift(r.id,d,e.target.value)}
                              style={{fontSize:10,padding:"3px 2px",borderRadius:6,border:`1.5px solid ${shiftId==="ns"?C.border:col}`,background:shiftId==="ns"?"transparent":col+"15",color:shiftId==="ns"?C.dim:col,fontWeight:shiftId==="ns"?400:700,cursor:"pointer",width:"100%"}}>
                              <option value="ns">Not scheduled</option>
                              <option value="off">Off / unassigned</option>
                              <option value="early2">Early 07:45–13:45</option>
                              <option value="early">Early 08:00–14:00</option>
                              <option value="full">Full  08:00–17:30</option>
                              <option value="late">Late  14:00–20:00</option>
                              <option value="custom">Custom hours…</option>
                            </select>
                            {isCustom&&(
                              <div style={{display:"flex",gap:2,alignItems:"center"}}>
                                <input type="time" value={c[`${d}_start`]||"09:00"} onChange={e=>setCustomTime(r.id,d,"start",e.target.value)}
                                  style={{fontSize:9,padding:"2px 3px",borderRadius:4,border:`1px solid ${C.border}`,width:"50%",background:C.surface,color:C.text,colorScheme:"dark"}}/>
                                <span style={{fontSize:8,color:C.dim}}>–</span>
                                <input type="time" value={c[`${d}_end`]||"17:30"} onChange={e=>setCustomTime(r.id,d,"end",e.target.value)}
                                  style={{fontSize:9,padding:"2px 3px",borderRadius:4,border:`1px solid ${C.border}`,width:"50%",background:C.surface,color:C.text,colorScheme:"dark"}}/>
                              </div>
                            )}
                            {noFriContractual&&unlocked&&(
                              <button onClick={()=>toggleDayUnlock(r.id,d)} style={{fontSize:8,color:C.dim,background:"transparent",border:`1px solid ${C.border}`,borderRadius:3,padding:"1px 5px",cursor:"pointer"}}>re-lock Fri</button>
                            )}
                          </div>
                        )}
                      </td>
                    );
                  })}
                  <td style={{padding:"8px 6px",borderBottom:`1px solid ${C.border2}`,textAlign:"center"}}>
                    <label style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3,cursor:"pointer"}}>
                      <input type="checkbox" checked={!!c.multiLate} onChange={()=>toggleMultiLate(r.id)}
                        style={{width:15,height:15,accentColor:C.brook,cursor:"pointer"}}/>
                      <span style={{fontSize:8,color:c.multiLate?C.brook:C.muted,fontWeight:c.multiLate?700:400}}>{c.multiLate?"Yes":"No"}</span>
                    </label>
                  </td>
                  <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.border2}`,textAlign:"right"}}>
                    <span style={{fontSize:11,fontWeight:700,color:Math.abs(diff)<=1?C.green:diff>0?C.warn:C.danger}}>
                      {sched.toFixed(1)}h
                    </span>
                    {Math.abs(diff)>1&&(
                      <div style={{fontSize:9,color:Math.abs(diff)>4?C.danger:C.warn,marginTop:1}}>
                        {diff>0?"+":""}{diff.toFixed(1)}h vs contract
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{marginTop:14,padding:"10px 14px",background:"rgba(58,173,152,0.06)",border:"1px solid #bae6fd",borderRadius:8,fontSize:11,color:"#60a5fa"}}>
        <strong>Not scheduled</strong> = they don't work this day at all · <strong>Off / unassigned</strong> = working day, no shift set · <strong>Custom hours</strong> = set any start/end (e.g. 09:00–12:00 for a doctor's appointment day). Multi-late = happy to do more than one late per week.
      </div>
    </div>
  );
}

/* ── PRACTICE EVENTS ─────────────────────────────────────────────────── */
const EVENT_TYPES = [
  {id:"partners",    label:"Partners Meeting",       icon:"🤝", color:"#818cf8", scopeDefault:"partners",    desc:"Partners only"},
  {id:"management",  label:"Management Meeting",     icon:"👔", color:"#a78bfa", scopeDefault:"management",  desc:"Partners + Megan + Julie"},
  {id:"sitemgmt",    label:"Site Management",        icon:"📋", color:C.teal,    scopeDefault:"sitemgmt",    desc:"Head nurse + site manager + site partner"},
  {id:"sitemeeting", label:"Practice Meeting (site)",icon:"🏥", color:"#00e5a0", scopeDefault:"site",        desc:"All staff at site"},
  {id:"groupmeeting",label:"Group Practice Meeting", icon:"🌐", color:"#60a5fa", scopeDefault:"all",         desc:"Everyone, all sites"},
  {id:"appraisal",   label:"Appraisal",              icon:"💬", color:"#d97706", scopeDefault:"individuals", desc:"Named individuals — can be private"},
  {id:"cpd",         label:"Training / CPD",         icon:"📚", color:"#00e5a0", scopeDefault:"site",        desc:"Training day or CPD session"},
  {id:"team",        label:"Team Building",          icon:"⭐", color:"#60a5fa", scopeDefault:"site",        desc:"Team away day or social"},
  {id:"other",       label:"Other",                  icon:"📅", color:C.dim,     scopeDefault:"individuals", desc:"Custom event"},
];

// Get default attendees for an event type + site
function getDefaultAttendees(type, siteId, state) {
  const nurses    = (state.nurses||NURSES_BASE).filter(n=>!n.maternity&&!n.archived&&!n._deleted);
  const clins     = getClinicians(state).filter(c=>!c._deleted);
  const recs      = [...RECS_BASE, ...(state.customRecs||[])].filter(r=>!r.archived);
  const partners  = clins.filter(c=>c.type?.includes("Partner")).map(c=>c.id);
  const managers  = recs.filter(r=>r.manager).map(r=>r.id);
  const headNurses = nurses.filter(n=>n.headNurse||(state.staffRoles||{})[n.id]==="headNurse").map(n=>n.id);
  const sitePartner = clins.filter(c=>c.type?.includes("Partner")&&c.site===siteId).map(c=>c.id);
  const siteHeadNurse = nurses.filter(n=>(n.headNurse||(state.staffRoles||{})[n.id]==="headNurse")&&n.site===siteId).map(n=>n.id);
  const siteManager = recs.filter(r=>r.manager&&(r.site===siteId||r.multiSite?.includes(siteId))).map(r=>r.id);
  const allSite = [
    ...nurses.filter(n=>n.site===siteId).map(n=>n.id),
    ...recs.filter(r=>r.site===siteId||r.multiSite?.includes(siteId)).map(r=>r.id),
    ...clins.filter(c=>c.site===siteId).map(c=>c.id),
  ];
  const allStaff = [
    ...nurses.map(n=>n.id),
    ...recs.map(r=>r.id),
    ...clins.map(c=>c.id),
  ];
  switch(type) {
    case "partners":    return [...new Set(partners)];
    case "management":  return [...new Set([...partners,...managers])];
    case "sitemgmt":    return [...new Set([...sitePartner,...siteHeadNurse,...siteManager])];
    case "sitemeeting": return [...new Set(allSite)];
    case "groupmeeting":return [...new Set(allStaff)];
    case "cpd":         return [...new Set(allSite)]; // pre-select whole site for CPD
    case "team":        return [...new Set(allSite)];
    default:            return [];
  }
}

function PracticeEvents({siteId, state, set, user, isPrincipal}) {
  const [eventM,  setEventM]  = useState(null);
  const [viewM,   setViewM]   = useState(null);
  const canManage = isPrincipal || user?.role==="manager" || user?.role==="managerLite";
  const today = new Date().toISOString().split("T")[0];

  // Visibility: partners/management only for managers+principal; appraisals private
  const events = (state.practiceEvents||[]).filter(e=>{
    if (e.type==="partners"||e.type==="management") {
      if (!isPrincipal && user?.role!=="manager" && user?.role!=="managerLite") return false;
    }
    if (e.appraisalPrivate) return isPrincipal || (e.staffIds||[]).includes(user?.staffId);
    if (e.type==="sitemgmt") {
      const isHN = user?.role==="headNurse"||(state.staffRoles||{})[user?.staffId]==="headNurse";
      if (!isPrincipal && user?.role!=="manager" && user?.role!=="managerLite" && !isHN) return false;
    }
    if (!isPrincipal && e.sites?.length && !e.sites.includes(siteId)) return false;
    return true;
  }).sort((a,b)=>a.date.localeCompare(b.date));

  const upcoming = events.filter(e=>e.date>=today);
  const past     = events.filter(e=>e.date<today);

  const yearStart = `${new Date().getFullYear()}-01-01`;
  const mandatoryHeld = (state.practiceEvents||[]).filter(e=>e.date>=yearStart&&e.date<today&&e.attendance==="mandatory").length;
  // Count CPD both from practice events AND from cpdEvents (logged via CPD tab)
  const cpdFromEvents = (state.practiceEvents||[]).filter(e=>e.date>=yearStart&&e.date<today&&e.type==="cpd").length;
  const cpdFromCpdTab = [...new Set((state.cpdEvents||[]).filter(e=>e.date>=yearStart&&e.date<today&&!e.practiceEventId).map(e=>e.date+e.title))].length;
  const cpdHeld = cpdFromEvents + cpdFromCpdTab;

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:12}}>
        <div>
          <h4 style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:16,color:C.heading,marginBottom:2}}>Practice Events</h4>
          <div style={{fontSize:11,color:C.dim}}>Meetings, appraisals, team days, CPD. CQC evidence logged automatically.</div>
          <div style={{fontSize:10,color:C.teal,marginTop:2}}>Adding a Training/CPD event here automatically blocks the rota for selected staff — same as using the CPD tab.</div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <div style={{fontSize:11,color:C.teal,background:C.tealPale,border:`1px solid ${C.tealBorder}`,borderRadius:8,padding:"4px 10px"}}>
            {mandatoryHeld} mandatory · {cpdHeld} CPD this year
          </div>
          {canManage&&<button onClick={()=>setEventM({})} style={BTN(C.teal)}>+ Add Event</button>}
        </div>
      </div>

      {upcoming.length===0&&past.length===0&&(
        <div style={{...CS,textAlign:"center",padding:40,color:C.dim}}>
          <div style={{fontSize:32,marginBottom:10}}>Cal</div>
          <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:4}}>No events yet</div>
          <div style={{fontSize:11,marginBottom:16}}>Add meetings, appraisals, CPD days. Every mandatory event logged here is automatic CQC evidence.</div>
          {canManage&&<button onClick={()=>setEventM({})} style={BTN(C.teal)}>Add first event</button>}
        </div>
      )}

      {upcoming.length>0&&(
        <div style={{marginBottom:24}}>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Upcoming ({upcoming.length})</div>
          {upcoming.map(e=><EventCard key={e.id} e={e} state={state} canManage={canManage} onEdit={()=>setEventM(e)} onView={()=>setViewM(e)} user={user} isPrincipal={isPrincipal} set={set}/>)}
        </div>
      )}

      {past.length>0&&(
        <div>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Past ({past.length})</div>
          {past.slice(0,10).map(e=><EventCard key={e.id} e={e} state={state} canManage={canManage} onEdit={()=>setEventM(e)} onView={()=>setViewM(e)} user={user} isPrincipal={isPrincipal} set={set}/>)}
          {past.length>10&&<div style={{fontSize:11,color:C.dim,textAlign:"center",padding:"8px 0"}}>+{past.length-10} older events</div>}
        </div>
      )}

      {eventM!==null&&<EventModal event={eventM} state={state} set={set} user={user} isPrincipal={isPrincipal} siteId={siteId} onClose={()=>setEventM(null)}/>}
      {viewM&&<EventDetailModal event={viewM} state={state} onClose={()=>setViewM(null)}/>}
    </div>
  );
}

function EventCard({e, state, canManage, onEdit, onView, user, isPrincipal, set}) {
  const today = new Date().toISOString().split("T")[0];
  const et = EVENT_TYPES.find(t=>t.id===e.type)||EVENT_TYPES[EVENT_TYPES.length-1];
  const isPast = e.date < today;
  const attendanceColor = e.attendance==="mandatory"?"#dc2626":e.attendance==="expected"?C.warn:C.dim;
  const siteNames = (e.sites||[]).map(s=>SITES[s]?.name||s).join(", ");

  function deleteEvent() {
    if (!window.confirm("Delete this event?")) return;
    set(s=>({
      ...s,
      practiceEvents:(s.practiceEvents||[]).filter(ev=>ev.id!==e.id),
      // Also remove any linked cpdEvents created when this was a CPD event
      cpdEvents:(s.cpdEvents||[]).filter(ce=>ce.practiceEventId!==e.id),
    }));
  }

  return (
    <div style={{...CS,marginBottom:8,padding:"12px 16px",borderLeft:`3px solid ${et.color}`,opacity:isPast?0.7:1}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,flexWrap:"wrap"}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4,flexWrap:"wrap"}}>
            <span style={{fontSize:15}}>{et.icon}</span>
            <span style={{fontSize:13,fontWeight:700,color:C.text}}>{e.title}</span>
            <span style={{fontSize:9,fontWeight:700,color:et.color,background:et.color+"18",border:`1px solid ${et.color}33`,borderRadius:4,padding:"1px 6px"}}>{et.label}</span>
            {e.attendance!=="optional"&&<span style={{fontSize:9,fontWeight:700,color:attendanceColor,background:attendanceColor+"18",border:`1px solid ${attendanceColor}33`,borderRadius:4,padding:"1px 6px"}}>{e.attendance==="mandatory"?"Mandatory":"Expected"}</span>}
          </div>
          <div style={{fontSize:11,color:C.dim}}>
            {new Date(e.date+"T12:00:00").toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"long",year:"numeric"})}
            {e.endDate&&e.endDate!==e.date&&` – ${new Date(e.endDate+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"long"})}`}
            {siteNames&&` · ${siteNames}`}
          </div>
          {e.notes&&<div style={{fontSize:11,color:C.mid,marginTop:3,fontStyle:"italic"}}>{e.notes}</div>}
        </div>
        <div style={{display:"flex",gap:4,flexShrink:0}}>
          <button onClick={onView} style={{...BTN(C.dim,true),fontSize:10,padding:"4px 8px"}}>Details</button>
          {canManage&&<button onClick={onEdit} style={{...BTN(C.teal,true),fontSize:10,padding:"4px 8px"}}>Edit</button>}
          {canManage&&<button onClick={deleteEvent} style={{...BTN(C.danger,true),fontSize:10,padding:"4px 8px"}}>X</button>}
        </div>
      </div>
    </div>
  );
}

function EventDetailModal({event, state, onClose}) {
  const et = EVENT_TYPES.find(t=>t.id===event.type)||EVENT_TYPES[EVENT_TYPES.length-1];
  const allStaff = [...(state.nurses||NURSES_BASE).filter(n=>!n.maternity&&!n.archived),...RECS_BASE,...getClinicians(state).filter(c=>!c._deleted)];
  const attendees = (event.staffIds||[]).map(id=>allStaff.find(s=>s.id===id)).filter(Boolean);
  return (
    <Overlay onClose={onClose}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
        <span style={{fontSize:28}}>{et.icon}</span>
        <div>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,fontWeight:700,color:C.heading}}>{event.title}</div>
          <div style={{fontSize:12,color:et.color,fontWeight:600}}>{et.label} · {et.desc}</div>
        </div>
      </div>
      <div style={{...CS,padding:"10px 14px",marginBottom:12}}>
        {[
          ["Date", `${new Date(event.date+"T12:00:00").toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}${event.endDate&&event.endDate!==event.date?` – ${new Date(event.endDate+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"long"})}`:""}`],
          ["Sites", (event.sites||[]).map(s=>SITES[s]?.name||s).join(", ")||"All sites"],
          ["Attendance", event.attendance==="mandatory"?"Mandatory — leave clash flagged":event.attendance==="expected"?"Expected — soft warning":"Optional"],
          ["Created by", event.createdBy||"—"],
        ].map(([l,v])=>(
          <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"5px 0",borderBottom:`1px solid ${C.border2}`,fontSize:12}}>
            <span style={{color:C.dim}}>{l}</span>
            <span style={{fontWeight:600,color:C.text,textAlign:"right",maxWidth:"60%"}}>{v}</span>
          </div>
        ))}
      </div>
      {event.notes&&<div style={{fontSize:12,color:C.mid,marginBottom:12,padding:"8px 12px",background:C.bg,borderRadius:8}}>{event.notes}</div>}
      {attendees.length>0&&(
        <div>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Attendees ({attendees.length})</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
            {attendees.map(s=><span key={s.id} style={{fontSize:11,padding:"3px 8px",borderRadius:6,background:(SITES[s.site]?.color||C.teal)+"18",color:SITES[s.site]?.color||C.teal,border:`1px solid ${(SITES[s.site]?.color||C.teal)}33`}}>{s.name}</span>)}
          </div>
        </div>
      )}
    </Overlay>
  );
}

function EventModal({event, state, set, user, isPrincipal, siteId, onClose}) {
  const isNew = !event.id;
  const [saved, setSaved] = useState(false);
  const allStaff = [
    ...(state.nurses||NURSES_BASE).filter(n=>!n.maternity&&!n.archived),
    ...RECS_BASE,
    ...getClinicians(state).filter(c=>!c._deleted),
  ].sort((a,b)=>{const so={sale:0,brooklands:1,timperley:2};return((so[a.site]||0)-(so[b.site]||0))||a.name.localeCompare(b.name);});

  const [f,setF] = useState({
    title: event.title||"",
    type:  event.type||"sitemeeting",
    sites: event.sites||[siteId],
    staffIds: event.staffIds||[],
    date:  event.date||new Date().toISOString().split("T")[0],
    endDate: event.endDate||"",
    attendance: event.attendance||"mandatory",
    notes: event.notes||"",
    appraisalPrivate: event.appraisalPrivate||false,
  });
  const sf=(k,v)=>setF(p=>({...p,[k]:v}));

  // When type changes, auto-populate attendees from default scope
  function changeType(newType) {
    const defaults = getDefaultAttendees(newType, siteId, state);
    setF(p=>({...p, type:newType, staffIds:defaults.length?defaults:p.staffIds}));
  }

  const toggleStaff = id => sf("staffIds",f.staffIds.includes(id)?f.staffIds.filter(x=>x!==id):[...f.staffIds,id]);
  const toggleSite  = id => sf("sites",f.sites.includes(id)?f.sites.filter(x=>x!==id):[...f.sites,id]);
  const selectAllSite = (sid) => {
    const siteIds = allStaff.filter(s=>s.site===sid).map(s=>s.id);
    const allIn = siteIds.every(id=>f.staffIds.includes(id));
    sf("staffIds", allIn ? f.staffIds.filter(id=>!siteIds.includes(id)) : [...new Set([...f.staffIds,...siteIds])]);
  };

  const et = EVENT_TYPES.find(t=>t.id===f.type)||EVENT_TYPES[0];
  const showSites = ["sitemeeting","groupmeeting","sitemgmt","team","cpd","other"].includes(f.type);

  function save() {
    if (!f.title.trim()||!f.date) return;
    const rec = {...f, id:event.id||"ev_"+Date.now(), createdBy:user?.name||"Manager"};
    set(s=>{
      // Save the practice event
      const updatedEvents = isNew
        ? [...(s.practiceEvents||[]), rec]
        : (s.practiceEvents||[]).map(e=>e.id===rec.id?rec:e);

      // If type is CPD/Training, also sync to cpdEvents so the rota marks people absent
      let updatedCpd = s.cpdEvents||[];
      if (f.type==="cpd" && f.staffIds.length>0) {
        // Remove any existing cpdEvent linked to this practice event
        updatedCpd = updatedCpd.filter(e=>e.practiceEventId!==rec.id);
        // Create one cpdEvent per date (handle date ranges)
        const dates = [];
        if (f.endDate && f.endDate!==f.date) {
          const cur = new Date(f.date+"T12:00:00");
          const end = new Date(f.endDate+"T12:00:00");
          while (cur<=end) {
            const d = cur.toISOString().slice(0,10);
            const dow = cur.getDay();
            if (dow!==0&&dow!==6) dates.push(d);
            cur.setDate(cur.getDate()+1);
          }
        } else {
          dates.push(f.date);
        }
        const newCpdEvents = dates.map(d=>({
          id: "cpd_ev_"+rec.id+"_"+d,
          practiceEventId: rec.id,
          title: f.title,
          cpdType: "training",
          site: f.sites?.[0]||siteId,
          sites: f.sites||[siteId],
          staffIds: f.staffIds,
          date: d,
          allDay: true,
          notes: f.notes||"",
          loggedBy: user?.name||"Manager",
        }));
        updatedCpd = [...updatedCpd, ...newCpdEvents];
      } else if (f.type!=="cpd") {
        // If we changed type away from CPD, remove any linked cpdEvents
        updatedCpd = updatedCpd.filter(e=>e.practiceEventId!==rec.id);
      }

      return {...s, practiceEvents:updatedEvents, cpdEvents:updatedCpd};
    });
    setSaved(true);
    setTimeout(onClose, 700);
  }

  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,fontWeight:700,color:C.heading,marginBottom:16}}>{isNew?"Add Practice Event":"Edit Event"}</div>

      <div style={{marginBottom:12}}><label style={LB}>Event type</label>
        <select value={f.type} onChange={e=>changeType(e.target.value)} style={IS}>
          {EVENT_TYPES.map(t=><option key={t.id} value={t.id}>{t.label} — {t.desc}</option>)}
        </select>
      </div>

      <div style={{marginBottom:12}}><label style={LB}>Title</label>
        <input value={f.title} onChange={e=>sf("title",e.target.value)} placeholder={`e.g. ${et.label} — March 2026`} style={IS}/>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
        <div><label style={LB}>Date</label>
          <input type="date" value={f.date} onChange={e=>sf("date",e.target.value)} style={IS}/>
        </div>
        <div><label style={LB}>End date <span style={{color:C.dim,fontWeight:400}}>(optional)</span></label>
          <input type="date" value={f.endDate} onChange={e=>sf("endDate",e.target.value)} style={IS}/>
        </div>
      </div>

      <div style={{marginBottom:12}}><label style={LB}>Attendance</label>
        <select value={f.attendance} onChange={e=>sf("attendance",e.target.value)} style={IS}>
          <option value="mandatory">Mandatory — leave clash flagged in approval</option>
          <option value="expected">Expected — soft warning on clash</option>
          <option value="optional">Optional — informational only</option>
        </select>
      </div>

      {showSites&&(
        <div style={{marginBottom:12}}>
          <label style={LB}>Sites</label>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {Object.entries(SITES).map(([id,s])=>(
              <label key={id} style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:f.sites.includes(id)?s.color:C.mid,cursor:"pointer",fontWeight:f.sites.includes(id)?700:400}}>
                <input type="checkbox" checked={f.sites.includes(id)} onChange={()=>toggleSite(id)} style={{accentColor:s.color}}/>{s.name}
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Attendees — always shown, auto-populated, fully editable */}
      <div style={{marginBottom:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
          <label style={{...LB,margin:0}}>Attendees <span style={{color:C.dim,fontWeight:400}}>({f.staffIds.length} selected — edit freely)</span></label>
          <div style={{display:"flex",gap:4}}>
            <button onClick={()=>sf("staffIds",getDefaultAttendees(f.type,siteId,state))} style={{...BTN(et.color,true),fontSize:9,padding:"2px 8px"}}>Reset to default</button>
            <button onClick={()=>sf("staffIds",allStaff.map(s=>s.id))} style={{...BTN(C.dim,true),fontSize:9,padding:"2px 8px"}}>All staff</button>
            <button onClick={()=>sf("staffIds",[])} style={{...BTN(C.dim,true),fontSize:9,padding:"2px 8px"}}>Clear</button>
          </div>
        </div>
        <div style={{maxHeight:180,overflowY:"auto",border:`1px solid ${C.border}`,borderRadius:8,padding:"6px 10px"}}>
          {Object.entries(SITES).map(([sid,s])=>{
            const siteStaff = allStaff.filter(p=>p.site===sid||(p.multiSite||[]).includes(sid));
            if (!siteStaff.length) return null;
            return (
              <div key={sid}>
                <div style={{display:"flex",alignItems:"center",gap:6,padding:"4px 0",borderBottom:`1px solid ${C.border2}`}}>
                  <span style={{fontSize:10,fontWeight:700,color:s.color}}>{s.name}</span>
                  <button onClick={()=>selectAllSite(sid)} style={{fontSize:8,color:C.dim,background:"transparent",border:`1px solid ${C.border}`,borderRadius:3,padding:"1px 5px",cursor:"pointer"}}>toggle all</button>
                </div>
                {siteStaff.map(p=>(
                  <label key={p.id} style={{display:"flex",alignItems:"center",gap:8,padding:"3px 4px",cursor:"pointer"}}>
                    <input type="checkbox" checked={f.staffIds.includes(p.id)} onChange={()=>toggleStaff(p.id)} style={{accentColor:s.color}}/>
                    <span style={{fontSize:11,color:f.staffIds.includes(p.id)?C.text:C.dim}}>{p.name}</span>
                    <span style={{fontSize:9,color:C.muted}}>{p.type||p.role||""}</span>
                  </label>
                ))}
              </div>
            );
          })}
        </div>
        {f.type==="appraisal"&&(
          <label style={{display:"flex",alignItems:"center",gap:8,marginTop:8,cursor:"pointer"}}>
            <input type="checkbox" checked={f.appraisalPrivate} onChange={e=>sf("appraisalPrivate",e.target.checked)} style={{accentColor:C.warn}}/>
            <span style={{fontSize:12,color:C.warn,fontWeight:600}}>Private — only principal and the individual can see this</span>
          </label>
        )}
      </div>

      <div style={{marginBottom:16}}><label style={LB}>Notes <span style={{color:C.dim,fontWeight:400}}>(optional)</span></label>
        <textarea value={f.notes} onChange={e=>sf("notes",e.target.value)} rows={2} placeholder="Agenda, location, what to bring..." style={{...IS,resize:"none"}}/>
      </div>

      {f.type==="cpd"&&f.staffIds.length>0&&(
        <div style={{marginBottom:12,padding:"8px 12px",background:C.tealPale,border:`1px solid ${C.tealBorder}`,borderRadius:8,fontSize:11,color:C.teal,fontWeight:600}}>
          {f.staffIds.length} staff selected — their surgeries will be blocked in the rota on this date
        </div>
      )}
      {f.type==="cpd"&&f.staffIds.length===0&&(
        <div style={{marginBottom:12,padding:"8px 12px",background:"rgba(255,70,70,0.08)",border:"1px solid rgba(255,70,70,0.2)",borderRadius:8,fontSize:11,color:C.danger,fontWeight:600}}>
          No staff selected — add people above so the rota blocks their surgery
        </div>
      )}

      {saved ? (
        <div style={{textAlign:"center",padding:12,color:C.green,fontWeight:700,fontSize:15}}>
          {f.type==="cpd"?"Event saved — rota updated":"Event saved"}
        </div>
      ) : (
        <div style={{display:"flex",gap:8}}>
          <button onClick={save} disabled={!f.title.trim()||!f.date}
            style={{...BTN(C.teal),flex:1,padding:"11px 0",opacity:!f.title.trim()||!f.date?0.4:1}}>
            {isNew?"Add Event":"Save Changes"}
          </button>
          <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
        </div>
      )}
    </Overlay>
  );
}

function Planning({siteId,state,set,userRole,user,initialSubTab,onSubTabConsumed,isPrincipal,onGoToAdminClinicians}) {
  const site = SITES[siteId];
  const nurses = getNursesForSite(state, siteId);
  const allRecs = [...RECS_BASE,...(state.customRecs||[]).filter(r=>!r.archived)].filter(r=>r.site===siteId);
  const allStaff = [...nurses,...allRecs]; // Conor is a clinician — he appears in the dentist section below, not here
  const [subTab,setSubTab] = useState("rota");
  useEffect(()=>{
    if(initialSubTab){setSubTab(initialSubTab);if(onSubTabConsumed)onSubTabConsumed();}
  },[initialSubTab]);
  const [leaveM,setLeaveM]     = useState(null);
  const [appM,setAppM]         = useState(null);
  const [drillOpen,setDrillOpen] = useState(null);
  const [collegeM,setCollegeM] = useState(false);
  const [clinDrillOpen,setClinDrillOpen] = useState(null);
  const [clinLeaveM,setClinLeaveM] = useState(null);
  const [holRepM,setHolRepM]     = useState(false);
  const [entitM,setEntitM]       = useState(null);
  const [reqsM,setReqsM]   = useState(null);
  const [cpdM,setCpdM]     = useState(false);
  const [showAllSites,setShowAllSites] = useState(false);
  const canOverride = userRole==="principal"||userRole==="manager"||userRole==="managerLite";

  // For principal — all-sites pending leave view
  const allSitesStaff = isPrincipal ? [
    ...(state.nurses||NURSES_BASE).filter(n=>!n.maternity&&!n.archived),
    ...RECS_BASE,
    ...(state.customRecs||[]).filter(r=>!r.archived),
  ] : allStaff;
  // Principal always sees manager leave requests regardless of site tab
  const managersAllSites = isPrincipal ? RECS_BASE.filter(r=>r.manager) : [];
  const staffForPending = isPrincipal
    ? [...new Map([...(showAllSites ? allSitesStaff : allStaff), ...managersAllSites].map(s=>[s.id,s])).values()]
    : allStaff;

  const pending = staffForPending.flatMap(s=>(state.leave[s.id]||[]).filter(l=>l.status==="pending").map(l=>({...l,person:s})));
  const allSitesPendingCount = isPrincipal ? allSitesStaff.flatMap(s=>(state.leave[s.id]||[]).filter(l=>l.status==="pending")).length : 0;

  // Pending clinician leave — needs principal approval
  const clinicians = getClinicians(state).filter(c=>!c._deleted&&c.site===siteId);
  const pendingClinLeave = isPrincipal
    ? getClinicians(state).filter(c=>!c._deleted).flatMap(c=>
        ((state.clinicianLeave||{})[c.id]||[]).filter(l=>l.status==="pending").map(l=>({...l,clinician:c}))
      )
    : clinicians.flatMap(c=>
        ((state.clinicianLeave||{})[c.id]||[]).filter(l=>l.status==="pending").map(l=>({...l,clinician:c}))
      );

  // Group pending clin leave by clinician+requestId
  const pendingClinGroups = pendingClinLeave.reduce((acc,l)=>{
    const key = l.clinician.id+"_"+(l.requestedAt||l.id);
    if (!acc[key]) acc[key]={dates:[],clinician:l.clinician,requestedBy:l.requestedBy||"Manager",requestedAt:l.requestedAt||""};
    acc[key].dates.push(l.date);
    return acc;
  },{});

  const totalPendingBadge = pending.length + pendingClinLeave.length;

  const khRag = buildKeyholderRAG(state);
  const khRagCount = khRag.filter(i=>i.rag==="red"||!i.date).length;
  const pendingGroups = pending.reduce((acc,l)=>{
    const key = l.requestId||l.id;
    if (!acc[key]) acc[key]={dates:[],person:l.person,requestId:key,leaveType:l.leaveType||"annual",note:l.note||""};
    acc[key].dates.push(l.date);
    return acc;
  },{});

  function decide(requestId,decision,personId,personName,numDays,note) {
    set(s=>({
      ...s,
      leave:{...s.leave,[personId]:(s.leave[personId]||[]).map(l=>(l.requestId===requestId||l.id===requestId)?{...l,status:decision,managerNote:note||undefined,decidedBy:user?.name||"Manager",decidedAt:new Date().toISOString()}:l)},
      ...addLog(s, user?.name||"Unknown", "leave",
        `${decision==="approved"?"Approved":"Declined"} ${numDays}d leave for ${personName}${note?` — "${note}"`:""}`)
    }));
    setAppM(null);
  }

  // Undo approved leave — moves back to pending, logs the reversal
  function undoLeave(staffId, staffName, leaveIds, dates) {
    if (!window.confirm(`Undo ${dates.length} day${dates.length!==1?"s":""} of approved leave for ${staffName}? This will move it back to pending.`)) return;
    set(s=>({
      ...s,
      leave:{...s.leave,[staffId]:(s.leave[staffId]||[]).map(l=>leaveIds.includes(l.id)?{...l,status:"pending",undoneBy:user?.name||"Manager",undoneAt:new Date().toISOString()}:l)},
      leaveUndoLog:[...(s.leaveUndoLog||[]),{id:"ul"+Date.now(),staffId,staffName,dates,leaveIds,cancelledBy:user?.name||"Manager",cancelledAt:new Date().toISOString()}],
      ...addLog(s, user?.name||"Unknown", "leave", `↩ Leave undone for ${staffName} · ${dates.length}d moved back to pending`)
    }));
  }

  return (
    <div>
      <div style={{display:"flex",gap:6,marginBottom:16,borderBottom:`1px solid ${C.border}`,paddingBottom:0,flexWrap:"wrap",alignItems:"center"}}>
        <div style={{display:"flex",flex:1,flexWrap:"wrap"}}>
          {[["rota","Rota"],["leave","Leave & Holidays",totalPendingBadge>0?totalPendingBadge:null],...(isPrincipal||(userRole==="manager")||(userRole==="managerLite")||(userRole==="headNurse")?[["swaps","Swaps",(state.swapRequests||[]).filter(s=>s.site===siteId&&s.status==="pending").length||null],["keyholders","Keyholders",khRagCount>0?khRagCount:null],["reception","Reception"],["events","Events"],["adhoc","Ad-hoc"],["free","Free Sessions"],["kpi","Holiday KPI"],["bh","Bank Holidays"],["cover","Cover Pool"],["cpd","CPD & Training"]]:[])].map(([id,label,badge])=>(
            <button key={id} onClick={()=>setSubTab(id)} style={{background:"transparent",border:"none",padding:"8px 16px",cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600,color:subTab===id?site.color:C.mid,borderBottom:subTab===id?`2px solid ${site.color}`:"2px solid transparent",display:"flex",alignItems:"center",gap:6}}>
              {label}{badge>0&&<span style={{background:C.danger,color:"#fff",borderRadius:99,fontSize:9,fontWeight:800,padding:"1px 6px"}}>{badge}</span>}
            </button>
          ))}
        </div>
        {canOverride&&subTab==="cpd"&&(
          <button onClick={()=>setCpdM(true)} style={{...BTN(C.brook),fontSize:12,padding:"6px 14px",marginBottom:4}}>+ Log CPD / Training</button>
        )}
      </div>

      {subTab==="rota"&&<RotaView siteId={siteId} state={state} set={set} canOverride={canOverride} user={user} onGoToAdminClinicians={onGoToAdminClinicians}/>}
      {subTab==="adhoc"&&<AdHocClinics state={state} set={set} user={user} isPrincipal={isPrincipal} siteFilter={siteId}/>}
      {subTab==="free"&&<FreeSessionsReport state={state} siteId={siteId}/>}
      {subTab==="reception"&&<ReceptionContracts siteId={siteId} state={state} set={set}/>}
      {subTab==="events"&&<PracticeEvents siteId={siteId} state={state} set={set} user={user} isPrincipal={isPrincipal}/>}
      {subTab==="kpi"&&<HolidayKPI state={state} siteId={siteId}/>}
      {subTab==="bh"&&(
        <div>
          <div style={{marginBottom:20}}>
            <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading,marginBottom:4}}>🏦 Bank Holidays 2026</div>
            <div style={{fontSize:12,color:C.dim}}>Tap any date to toggle open or closed for all sites. Closed by default.</div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:10}}>
            {Object.entries(UK_BH_2026).sort((a,b)=>a[0].localeCompare(b[0])).map(([date,name])=>{
              const isOpen=!!(state.bhOverrides||{})[date];
              const d=new Date(date+"T12:00:00");
              const isPast=date<new Date().toISOString().split("T")[0];
              return (
                <div key={date} onClick={()=>set(s=>({...s,bhOverrides:{...(s.bhOverrides||{}),[date]:!isOpen}}))}
                  style={{padding:"14px 16px",borderRadius:12,
                    border:`1.5px solid ${isOpen?"#22c55e":C.border}`,
                    background:isOpen?"rgba(0,229,160,0.08)":"transparent",
                    cursor:"pointer",display:"flex",justifyContent:"space-between",
                    alignItems:"center",transition:"all 0.15s",opacity:isPast?0.55:1}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:isOpen?"#166534":isPast?C.dim:C.text}}>{name}</div>
                    <div style={{fontSize:11,color:C.dim,marginTop:2}}>
                      {d.toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"long"})}
                      {isPast&&<span style={{marginLeft:6,fontSize:10,color:C.muted}}>past</span>}
                    </div>
                  </div>
                  <span style={{fontSize:11,fontWeight:700,padding:"4px 12px",borderRadius:99,flexShrink:0,
                    background:isOpen?"#22c55e22":"#ef444422",
                    color:isOpen?"#166534":"#991b1b"}}>
                    {isOpen?"Open":"Closed"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {subTab==="cover"&&<CoverPool siteId={siteId} state={state}/>}
      {subTab==="keyholders"&&<KeyholderPanel state={state} set={set} isPrincipal={isPrincipal||canOverride}/>}

      {subTab==="cpd"&&(()=>{
        const siteCpd = (state.cpdEvents||[]).filter(e=>e.site===siteId).sort((a,b)=>a.date.localeCompare(b.date));
        const today = new Date().toISOString().split("T")[0];
        const upcoming = siteCpd.filter(e=>e.date>=today);
        const past     = siteCpd.filter(e=>e.date<today);
        const allSiteStaff = [...getClinicians(state).filter(c=>(c.sites||[c.site]).includes(siteId)&&!c._deleted), ...getNursesForSite(state, siteId), ...RECS_BASE.filter(r=>r.site===siteId)];
        const deleteCpd = id => set(s=>({...s,cpdEvents:(s.cpdEvents||[]).filter(e=>e.id!==id)}));
        const renderEvents = (events, title) => !events.length ? null : (
          <div style={{marginBottom:20}}>
            <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>{title} ({events.length})</div>
            {events.map(e=>{
              const ct=CPD_TYPES.find(t=>t.id===e.cpdType)||CPD_TYPES[CPD_TYPES.length-1];
              const names=allSiteStaff.filter(s=>(e.staffIds||[]).includes(s.id)).map(s=>s.name.split(" ")[0]);
              return (
                <div key={e.id} style={{...CS,marginBottom:8,padding:"12px 16px",borderLeft:`3px solid ${ct.color}`,display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:8}}>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                      <span style={{fontWeight:700,color:C.text,fontSize:13}}>{e.title||ct.label}</span>
                      <Pill c={ct.color} t={ct.label} sm/>
                      {e.allDay?<Pill c={C.danger} t="Full day" sm/>:<Pill c={C.warn} t={`${e.startTime}–${e.endTime}`} sm/>}
                    </div>
                    <div style={{fontSize:11,color:C.dim,marginBottom:4}}>
                      {CAL.days.find(d=>d.date===e.date)?.label||e.date}
                      {e.allDay&&<span style={{color:C.danger,marginLeft:6}}>· Surgery closed for these people</span>}
                    </div>
                    <div style={{fontSize:11,color:C.mid}}>
                      {names.length<=4?names.join(", "):`${names.slice(0,3).join(", ")} +${names.length-3} more`}
                    </div>
                    {e.notes&&<div style={{fontSize:10,color:C.dim,marginTop:4}}>{e.notes}</div>}
                  </div>
                  {canOverride&&<button onClick={()=>deleteCpd(e.id)} style={{...BTN(C.danger,true),fontSize:10,padding:"3px 10px",flexShrink:0}}>Remove</button>}
                </div>
              );
            })}
          </div>
        );
        return (
          <div>
            {siteCpd.length===0&&<div style={{...CS,textAlign:"center",padding:32,color:C.dim}}>No CPD events logged yet.{canOverride&&<div style={{fontSize:12,marginTop:6}}>Use the "+ Log CPD / Training" button above.</div>}</div>}
            {renderEvents(upcoming, "Upcoming")}
            {renderEvents(past, "Past")}
          </div>
        );
      })()}

      {subTab==="leave"&&(
        <div>
          {/* Delegate shortcut — inline */}
          {(isPrincipal||canOverride)&&(
            <DelegatePanel siteId={siteId} state={state} set={set} canOverride={canOverride}/>
          )}

          {/* All-sites toggle for principal */}
          {isPrincipal&&allSitesPendingCount>0&&(
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12,padding:"10px 14px",background:showAllSites?C.tealPale:"rgba(245,197,24,0.06)",border:`1px solid ${showAllSites?C.tealBorder:"rgba(245,197,24,0.2)"}`,borderRadius:10}}>
              <span style={{fontSize:16}}></span>
              <div style={{flex:1,fontSize:12,color:showAllSites?C.teal:"#f5c518"}}>
                {showAllSites ? `Showing all ${allSitesPendingCount} pending requests across all sites` : `${allSitesPendingCount} pending requests across all sites`}
              </div>
              <button onClick={()=>setShowAllSites(v=>!v)} style={{...BTN(showAllSites?C.teal:C.warn,true),fontSize:11,padding:"4px 12px"}}>
                {showAllSites?"This site only":"View all sites"}
              </button>
            </div>
          )}

          {pending.length>0&&(
            <div style={{marginBottom:20}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em"}}>Pending Approval ({pending.length} days)</div>
                <div style={{fontSize:10,color:C.dim}}>Staff → manager · Managers → Conor</div>
              </div>
              {Object.values(pendingGroups).map(req=>{
                const lt = LEAVE_TYPES.find(t=>t.id===req.leaveType);
                const myEntit = ent(req.person.hrs||37.5);
                const myUsed  = (state.leave[req.person.id]||[]).filter(l=>l.status==="approved").length;
                const myRem   = myEntit - myUsed;
                const reqSite = req.person.site;
                return (
                  <div key={req.requestId} style={{...CS,marginBottom:8,padding:"12px 16px",borderLeft:`3px solid ${req.leaveType==="medical"?C.danger:req.leaveType==="cpd"?C.brook:C.warn}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                    <div>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                        <span style={{fontWeight:700,color:C.text,fontSize:13}}>{req.person.name}</span>
                        {lt&&<Pill c={req.leaveType==="medical"?C.danger:req.leaveType==="cpd"?C.brook:C.warn} t={lt.label} sm/>}
                        {lt?.rtwNeeded&&<Pill c={C.danger} t="RTW req." sm/>}
                        {showAllSites&&<Pill c={SITES[reqSite]?.color||C.dim} t={SITES[reqSite]?.name||reqSite} sm/>}
                      </div>
                      <div style={{fontSize:11,color:C.dim}}>
                        {req.dates.length} day{req.dates.length!==1?"s":""} · from {CAL.days.find(d=>d.date===req.dates.sort()[0])?.label||req.dates.sort()[0]}
                      </div>
                      <div style={{fontSize:10,color:C.teal,marginTop:2}}>{myRem}d remaining of {myEntit}d entitlement</div>
                      {req.note&&<div style={{fontSize:10,color:C.mid,marginTop:1}}>"{req.note}"</div>}
                    </div>
                    <div style={{display:"flex",gap:6,alignItems:"center"}}>
                      {/* Only principal can approve manager leave */}
                      {req.person.manager&&!isPrincipal
                        ? <span style={{fontSize:10,color:C.dim,fontStyle:"italic"}}>Conor to approve</span>
                        : <button onClick={()=>setAppM(req)} style={{...BTN(C.teal),fontSize:12,padding:"6px 14px"}}>Review</button>
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pending clinician/dentist leave — principal approval required */}
          {Object.keys(pendingClinGroups).length>0&&(
            <div style={{marginBottom:20}}>
              <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>
                Dentist Leave Pending Approval ({Object.keys(pendingClinGroups).length} request{Object.keys(pendingClinGroups).length!==1?"s":""})
              </div>
              {Object.values(pendingClinGroups).map((req,i)=>{
                const c = req.clinician;
                const siteColor = SITES[c.site]?.color||C.dim;
                return (
                  <div key={i} style={{...CS,marginBottom:8,padding:"12px 16px",borderLeft:`3px solid ${siteColor}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                    <div>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                        <span style={{fontWeight:700,color:C.text,fontSize:13}}>{c.name}</span>
                        <Pill c={siteColor} t={SITES[c.site]?.name||c.site} sm/>
                        <Pill c={C.dim} t={c.type||"Dentist"} sm/>
                      </div>
                      <div style={{fontSize:11,color:C.dim}}>
                        {req.dates.length} day{req.dates.length!==1?"s":""} · from {CAL.days.find(d=>d.date===req.dates.sort()[0])?.label||req.dates.sort()[0]}
                      </div>
                      {req.requestedBy&&<div style={{fontSize:10,color:C.mid,marginTop:1}}>Requested by {req.requestedBy}</div>}
                    </div>
                    <div style={{display:"flex",gap:6}}>
                      <button onClick={()=>{
                        set(s=>({...s, clinicianLeave:{...(s.clinicianLeave||{}),[c.id]:(s.clinicianLeave?.[c.id]||[]).map(l=>req.dates.includes(l.date)&&l.status==="pending"?{...l,status:"approved",decidedBy:user?.name,decidedAt:new Date().toISOString()}:l)}}));
                      }} style={{...BTN(C.green),fontSize:12,padding:"6px 14px"}}>Approve</button>
                      <button onClick={()=>{
                        if(!window.confirm(`Decline ${req.dates.length} days for ${c.name}?`)) return;
                        set(s=>({...s, clinicianLeave:{...(s.clinicianLeave||{}),[c.id]:(s.clinicianLeave?.[c.id]||[]).map(l=>req.dates.includes(l.date)&&l.status==="pending"?{...l,status:"declined",decidedBy:user?.name,decidedAt:new Date().toISOString()}:l)}}));
                      }} style={{...BTN(C.danger,true),fontSize:12,padding:"6px 14px"}}>✕ Decline</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em"}}>Holiday Entitlement — {showAllSites&&isPrincipal?"All Sites":site.name}</div>
            {isPrincipal&&<button onClick={()=>setHolRepM(true)} style={{...BTN(C.teal,true),fontSize:11,padding:"4px 12px"}}>Holiday Report</button>}
          </div>
          <div style={{overflowX:"auto",marginBottom:16}}>
            <table style={{borderCollapse:"collapse",width:"100%",minWidth:480}}>
              <thead><tr>{["Name","Site","Entitlement","Pre-Apr","Used","Pending","Remaining",""].map(h=><th key={h} style={{padding:"6px 10px",fontSize:9,fontWeight:700,color:C.dim,textAlign:"left",textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:`1px solid ${C.border}`}}>{h}</th>)}</tr></thead>
              <tbody>
                {(showAllSites&&isPrincipal ? [
                  ...(state.nurses||NURSES_BASE).filter(n=>!n.maternity&&!n.archived),
                  ...RECS_BASE.filter(r=>!r.archived),
                  ...(state.customRecs||[]).filter(r=>!r.archived),
                ] : allStaff).map(s=>{
                  const e=getEntitlement(state,s); // partner-aware, pro-rata aware
                  const hist=getHistDays(state, s.id);
                  // Filter leave to current leave year for this person
                  const rType = roleTypeForPerson(s);
                  const indOv = (state.leaveYearOverrides||{})[s.id];
                  const lyCfg = indOv ? {[rType]:indOv} : state.leaveYearConfig;
                  const {start:lyS, end:lyE} = getLeaveYear(rType, lyCfg);
                  const lyStartStr = lyS.toISOString().split("T")[0];
                  const lyEndStr   = lyE.toISOString().split("T")[0];
                  const approvedLeave=(state.leave[s.id]||[]).filter(l=>l.status==="approved"&&l.date>=lyStartStr&&l.date<=lyEndStr);
                  const ap=countDays(approvedLeave);
                  const pe=(state.leave[s.id]||[]).filter(l=>l.status==="pending"&&l.date>=lyStartStr&&l.date<=lyEndStr).length;
                  const rem=Math.round((e-hist-ap)*10)/10;
                  const [drill,setDrill] = [drillOpen===s.id, (v)=>setDrillOpen(v?s.id:null)];
                  const siteColor = SITES[s.site]?.color||C.dim;
                  return [
                    <tr key={s.id} style={{cursor:"pointer"}} onClick={()=>setDrill(!drill)}>
                      <td style={{padding:"8px 10px",fontSize:12,fontWeight:600,color:C.text,borderBottom:drill?`1px solid ${C.teal}33`:`1px solid ${C.border2}`}}>
                        <span style={{marginRight:4,fontSize:10,color:C.dim}}>{drill?"▼":"▶"}</span>
                        {s.name}
                        {s.maternity&&<Pill c={C.warn} t="Mat" sm/>}
                      </td>
                      <td style={{padding:"8px 10px",fontSize:10,borderBottom:drill?`1px solid ${C.teal}33`:`1px solid ${C.border2}`}}>
                        <span style={{color:siteColor,fontWeight:700,background:siteColor+"18",borderRadius:4,padding:"1px 6px"}}>{SITES[s.site]?.name||s.site}</span>
                      </td>
                      <td style={{padding:"8px 10px",fontSize:12,color:C.mid,borderBottom:drill?`1px solid ${C.teal}33`:`1px solid ${C.border2}`}}>{e}d</td>
                      <td style={{padding:"8px 10px",fontSize:12,color:C.dim,borderBottom:drill?`1px solid ${C.teal}33`:`1px solid ${C.border2}`}}>{hist>0?hist+"d":"—"}</td>
                      <td style={{padding:"8px 10px",fontSize:12,color:C.warn,borderBottom:drill?`1px solid ${C.teal}33`:`1px solid ${C.border2}`}}>{ap}d</td>
                      <td style={{padding:"8px 10px",fontSize:12,color:C.lime,borderBottom:drill?`1px solid ${C.teal}33`:`1px solid ${C.border2}`}}>{pe>0?pe+"d":"—"}</td>
                      <td style={{padding:"8px 10px",borderBottom:drill?`1px solid ${C.teal}33`:`1px solid ${C.border2}`}}>
                        <span style={{fontSize:12,fontWeight:700,color:rem<5?C.danger:rem<10?C.warn:C.green}}>{rem}d</span>
                      </td>
                      <td style={{padding:"8px 10px",borderBottom:drill?`1px solid ${C.teal}33`:`1px solid ${C.border2}`}}>
                        <button onClick={e=>{e.stopPropagation();setLeaveM(s);}} style={{...BTN(C.teal,true),fontSize:10,padding:"3px 10px"}}>+ Leave</button>
                        {canOverride&&<button onClick={e=>{e.stopPropagation();setEntitM(s);}} style={{...BTN(C.dim,true),fontSize:10,padding:"3px 10px"}}>Set days</button>}
                      </td>
                    </tr>,
                    drill&&approvedLeave.length>0&&(
                      <tr key={s.id+"_drill"}>
                        <td colSpan={8} style={{padding:"4px 14px 12px 28px",background:C.tealPale,borderBottom:`1px solid ${C.teal}22`}}>
                          <div style={{fontSize:10,fontWeight:700,color:C.teal,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.06em",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                            Approved leave dates
                            {canOverride&&<button onClick={e=>{e.stopPropagation();undoLeave(s.id,s.name,approvedLeave.map(l=>l.id),approvedLeave.map(l=>l.date));setDrill(false);}} style={{...BTN(C.warn,true),fontSize:9,padding:"2px 8px"}}>↩ Undo all leave</button>}
                          </div>
                          {/* Detect duplicate dates */}
                          {(()=>{
                            const dateCounts={};
                            approvedLeave.forEach(l=>{dateCounts[l.date]=(dateCounts[l.date]||0)+1;});
                            const dupeDates=Object.entries(dateCounts).filter(([,c])=>c>1).map(([d])=>d);
                            if (!dupeDates.length) return null;
                            return <div style={{background:"rgba(255,70,70,0.08)",border:"1px solid rgba(255,70,70,0.2)",borderRadius:6,padding:"6px 10px",marginBottom:8,fontSize:11,color:C.danger,fontWeight:600}}>
                              {dupeDates.length} duplicate date{dupeDates.length!==1?"s":""} detected — tap ✕ on the extras below to remove them
                            </div>;
                          })()}
                          <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:10}}>
                            {approvedLeave.sort((a,b)=>a.date.localeCompare(b.date)).map((l,idx,arr)=>{
                              const d=CAL.days.find(x=>x.date===l.date);
                              const isDupe = arr.findIndex(x=>x.date===l.date) !== idx; // not the first occurrence
                              return (
                                <span key={l.id} style={{fontSize:11,background:isDupe?"#fef2f2":"#fff",border:`1px solid ${isDupe?"#fecaca":C.teal+"44"}`,borderRadius:6,padding:"2px 7px",color:isDupe?C.danger:C.teal,display:"flex",alignItems:"center",gap:4}}>
                                  {isDupe&&<span style={{fontSize:9,fontWeight:800}}>DUP</span>}
                                  {d?.label||l.date}
                                  {canOverride&&<button onClick={ev=>{ev.stopPropagation();undoLeave(s.id,s.name,[l.id],[l.date]);}} title="Remove this day" style={{background:"none",border:"none",cursor:"pointer",color:isDupe?C.danger:C.warn,fontSize:11,padding:"0 2px",lineHeight:1}}>X</button>}
                                </span>
                              );
                            })}
                          </div>
                          {/* Delegation — editable retrospectively */}
                          {canOverride&&<DelegationDrillDown s={s} approvedLeave={approvedLeave} state={state} set={set} user={user}/>}
                        </td>
                      </tr>
                    ),
                    drill&&approvedLeave.length===0&&(
                      <tr key={s.id+"_drill_empty"}>
                        <td colSpan={8} style={{padding:"4px 14px 12px 28px",background:C.tealPale,borderBottom:`1px solid ${C.teal}22`}}>
                          <span style={{fontSize:11,color:C.dim}}>No approved leave yet.</span>
                        </td>
                      </tr>
                    )
                  ];
                })}
              </tbody>
            </table>
          </div>

          {/* Clinician leave section */}
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10,marginTop:20}}>Dentist & Therapist Leave — {site.name}</div>
          <div style={{overflowX:"auto",marginBottom:16}}>
            <table style={{borderCollapse:"collapse",width:"100%",minWidth:400}}>
              <thead><tr>{["Name","Type","Annual leave","Early finishes","CPD days",""].map(h=><th key={h} style={{padding:"6px 10px",fontSize:9,fontWeight:700,color:C.dim,textAlign:"left",textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:`1px solid ${C.border}`}}>{h}</th>)}</tr></thead>
              <tbody>
                {getClinicians(state).filter(c=>c.site===siteId&&!c._deleted).map(c=>{
                  const cl=(state.clinicianLeave||{})[c.id]||[];
                  const al=cl.filter(l=>l.type==="annual"&&l.status!=="declined");
                  const ef=cl.filter(l=>l.type==="earlyFinish"&&l.status!=="declined");
                  const cpdDays=getCpdForPerson(state,c.id);
                  const [drill,setDrill] = [clinDrillOpen===c.id, (v)=>setClinDrillOpen(v?c.id:null)];
                  return [
                    <tr key={c.id} style={{cursor:al.length>0||cpdDays.length>0?"pointer":"default"}} onClick={()=>(al.length>0||cpdDays.length>0)&&setDrill(!drill)}>
                      <td style={{padding:"8px 10px",fontSize:12,fontWeight:600,color:c.color||C.text,borderBottom:`1px solid ${C.border2}`}}>
                        {(al.length>0||cpdDays.length>0)&&<span style={{marginRight:4,fontSize:10,color:C.dim}}>{drill?"▼":"▶"}</span>}
                        {c.name}
                        {(()=>{
                          const cr=(state.clinicianRequirements||{})[c.id];
                          const rl=reqLabel(cr?.default||[]);
                          const hasOv=cr?.overrides&&Object.values(cr.overrides).some(v=>v.length>0);
                          return rl?<span style={{marginLeft:6,fontSize:9,background:rl.color+"18",color:rl.color,border:`1px solid ${rl.color}33`,borderRadius:4,padding:"1px 5px",fontWeight:700}}>{rl.short}{hasOv?" *":""}</span>:null;
                        })()}
                      </td>
                      <td style={{padding:"8px 10px",fontSize:11,color:C.mid,borderBottom:`1px solid ${C.border2}`}}>{c.type}</td>
                      <td style={{padding:"8px 10px",fontSize:12,color:al.length>0?C.warn:C.dim,borderBottom:`1px solid ${C.border2}`}}>{al.length>0?al.length+"d":"—"}</td>
                      <td style={{padding:"8px 10px",fontSize:12,color:ef.length>0?C.dim:C.dim,borderBottom:`1px solid ${C.border2}`}}>{ef.length>0?ef.length:"—"}</td>
                      <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.border2}`}}>
                        {cpdDays.length>0
                          ? <span style={{fontSize:12,color:C.brook,fontWeight:600}}>{cpdDays.length} event{cpdDays.length!==1?"s":""}</span>
                          : <span style={{fontSize:12,color:C.dim}}>--</span>
                        }
                      </td>
                      <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.border2}`}}>
                        <div style={{display:"flex",gap:5}}>
                          <button onClick={e=>{e.stopPropagation();setClinLeaveM(c);}} style={{...BTN(C.brook,true),fontSize:10,padding:"3px 10px"}}>+ Leave</button>
                          {canOverride&&<button onClick={e=>{e.stopPropagation();setReqsM(c);}} style={{...BTN(C.danger,true),fontSize:10,padding:"3px 8px"}}>💉 Sedation</button>}
                          {(al.length>0||cpdDays.length>0)&&(
                            <button onClick={ev=>{ev.stopPropagation();const sessions=buildRotaSessions(c.id,true,c.site,state,CAL.days.filter(d=>!d.bh));exportICal(al,c.name,cpdDays,sessions);}} style={{...BTN(C.teal,true),fontSize:10,padding:"3px 10px"}}>Cal</button>
                          )}
                        </div>
                      </td>
                    </tr>,
                    drill&&(al.length>0||cpdDays.length>0)&&(
                      <tr key={c.id+"_drill"}>
                        <td colSpan={6} style={{padding:"8px 14px 12px 28px",background:"rgba(139,92,246,0.08)",borderBottom:`1px solid ${C.brook}22`}}>
                          {al.length>0&&(
                            <div style={{marginBottom:cpdDays.length>0?8:0}}>
                              <div style={{fontSize:9,fontWeight:700,color:C.brook,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>Annual Leave</div>
                              <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                                {al.sort((a,b)=>a.date.localeCompare(b.date)).map(l=>{
                                  const d=CAL.days.find(x=>x.date===l.date);
                                  return <span key={l.id} style={{fontSize:11,background:"#181626",border:`1px solid ${C.brook}44`,borderRadius:6,padding:"2px 7px",color:C.brook}}>{d?.label||l.date}</span>;
                                })}
                              </div>
                            </div>
                          )}
                          {cpdDays.length>0&&(
                            <div>
                              <div style={{fontSize:9,fontWeight:700,color:C.brook,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>CPD / Training</div>
                              <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                                {cpdDays.sort((a,b)=>a.date.localeCompare(b.date)).map(e=>{
                                  const ct=CPD_TYPES.find(t=>t.id===e.cpdType)||CPD_TYPES[CPD_TYPES.length-1];
                                  const d=CAL.days.find(x=>x.date===e.date);
                                  return <span key={e.id} style={{fontSize:11,background:"#181626",border:`1px solid ${ct.color}44`,borderRadius:6,padding:"2px 8px",color:ct.color}}>{ct.icon} {e.title||ct.label} · {d?.label.split(" ").slice(0,2).join(" ")||e.date}{!e.allDay?` ${e.startTime}–${e.endTime}`:""}</span>;
                                })}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  ];
                })}
              </tbody>
            </table>
          </div>

          {/* Delegation panel */}
          <DelegationPanel siteId={siteId} state={state} set={set} canOverride={canOverride}/>
        </div>
      )}

      {leaveM&&<LeaveModal person={leaveM} state={state} onSubmit={(id,dates,leaveType,note)=>{const rid="req"+Date.now();set(s=>{const ex=s.leave[id]||[];const ne=dates.map(d=>({id:"l"+Math.random().toString(36).slice(2),requestId:rid,date:d,status:"pending",leaveType:leaveType||"annual",note:note||""}));return{...s,leave:{...s.leave,[id]:[...ex,...ne]}};});setLeaveM(null);}} onClose={()=>setLeaveM(null)}/>}
      {subTab==="swaps"&&(
        <div>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12}}>Shift Swap Requests — {site.name}</div>
          {(state.swapRequests||[]).filter(s=>s.site===siteId).length===0&&(
            <div style={{...CS,textAlign:"center",padding:32,color:C.dim}}>No swap requests for {site.name}</div>
          )}
          {(state.swapRequests||[]).filter(s=>s.site===siteId).sort((a,b)=>(b.createdAt||"").localeCompare(a.createdAt||"")).map(sw=>{
            const sCol = sw.status==="approved"?C.green:sw.status==="declined"?C.danger:C.warn;
            return (
              <div key={sw.id} style={{...CS,marginBottom:8,padding:"12px 16px",borderLeft:`3px solid ${sCol}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:8}}>
                  <div>
                    <div style={{fontWeight:700,color:C.text,fontSize:13,marginBottom:2}}>{sw.requesterName}</div>
                    <div style={{fontSize:11,color:C.dim}}>
                      Away from: {CAL.days.find(d=>d.date===sw.originalDate)?.label||sw.originalDate}
                      {" → "}{CAL.days.find(d=>d.date===sw.wantDate)?.label||sw.wantDate}
                      {sw.reason&&` · "${sw.reason}"`}
                    </div>
                    {sw.swapWithId&&<div style={{fontSize:11,color:C.teal,marginTop:2}}>With: {[...(state.nurses||NURSES_BASE),...RECS_BASE].find(n=>n.id===sw.swapWithId)?.name||"—"}</div>}
                  </div>
                  <div style={{display:"flex",gap:6,alignItems:"center"}}>
                    <Pill c={sCol} t={sw.status} sm/>
                    {sw.status==="pending"&&canOverride&&(
                      <>
                        <button onClick={()=>set(s=>({...s,swapRequests:(s.swapRequests||[]).map(r=>r.id===sw.id?{...r,status:"approved"}:r)}))} style={{...BTN(C.green,true),fontSize:11,padding:"4px 10px"}}>✓</button>
                        <button onClick={()=>set(s=>({...s,swapRequests:(s.swapRequests||[]).map(r=>r.id===sw.id?{...r,status:"declined"}:r)}))} style={{...BTN(C.danger,true),fontSize:11,padding:"4px 10px"}}>X</button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {appM&&<ApprovalModal req={appM} person={appM.person} state={state} set={set} user={user} onDecide={(d,note)=>decide(appM.requestId,d,appM.person.id,appM.person.name,appM.dates.length,note)} onClose={()=>setAppM(null)}/>}
      {holRepM&&<HolidayReportModal state={state} siteId={siteId} isPrincipal={isPrincipal} onClose={()=>setHolRepM(false)}/>}
      {entitM&&<HistoricalLeaveModal person={entitM} state={state} set={set} onClose={()=>setEntitM(null)}/>}
      {clinLeaveM&&<ClinicianLeaveModal clinician={clinLeaveM} state={state} onSave={(clinId,dates,type)=>{
        set(s=>{
          const ex=(s.clinicianLeave||{})[clinId]||[];
          // Principal booking = auto-approved. Manager booking = pending (partner leave needs principal sign-off)
          const status = isPrincipal ? "approved" : "pending";
          const ne=dates.map(d=>({
            id:"cl"+Math.random().toString(36).slice(2),
            date:d,
            status,
            type:type||"annual",
            requestedBy: user?.name||"Manager",
            requestedAt: new Date().toISOString(),
          }));
          return{...s,clinicianLeave:{...(s.clinicianLeave||{}),[clinId]:[...ex,...ne]}};
        });
        setClinLeaveM(null);
      }} onClose={()=>setClinLeaveM(null)}/>}
      {reqsM&&<SedationModal clinician={reqsM} state={state} onSave={(id,reqs)=>{set(s=>({...s,clinicianRequirements:{...(s.clinicianRequirements||{}),[id]:reqs}}));setReqsM(null);}} onClose={()=>setReqsM(null)}/>}
      {cpdM&&<CpdModal siteId={siteId} state={state} onSave={e=>{set(s=>({...s,cpdEvents:[...(s.cpdEvents||[]),e]}));setCpdM(false);}} onClose={()=>setCpdM(false)}/>}
      {collegeM&&<CollegeDatesModal state={state} set={set} onClose={()=>setCollegeM(false)}/>}
    </div>
  );
}function Sickness({state,set,user}) {
  const [sickM,setSickM] = useState(false);
  const [sickPerson,setSickPerson] = useState("");
  const [welfareEditM,setWelfareEditM] = useState(null);
  const [fitNoteM,setFitNoteM] = useState(null);     // {id, name} — quick fit note modal
  const [rtwInterviewM,setRtwInterviewM] = useState(null); // {id, name} — RTW interview modal // {id, welfareFlag, welfareNotes}
  const sick = state.sick||[];

  const allStaff = [
    ...(state.nurses||NURSES_BASE).filter(s=>!s.maternity&&!s.archived),
    ...RECS_BASE.filter(s=>!s.archived),
    ...getClinicians(state).filter(c=>!c._deleted),
  ];
  const isPrincipal = user?.role==="principal";
  const visibleStaff = isPrincipal ? allStaff : allStaff.filter(s=>s.site===user?.site);
  const [sickTab,setSickTab] = useState("absence");

  function saveWelfareNote(id, welfareFlag, welfareNotes, generalNotes, fitNotes) {
    set(s=>({...s, sick:s.sick.map(r=>r.id===id?{...r,welfareFlag,welfareNotes,generalNotes,fitNotes:fitNotes||[]}:r)}));
    setWelfareEditM(null);
  }

  function addSick(rec) {
    set(s=>({
      ...s,
      sick:[...s.sick,rec],
      ...addLog(s, user?.name||"Unknown", "absence",
        `Absence logged: ${rec.name} · ${rec.days}d from ${rec.date} (${rec.reason})`)
    }));
    setSickM(null);
  }
  function markRTW(id) {
    const rec = sick.find(r=>r.id===id);
    set(s=>({
      ...s,
      sick:s.sick.map(r=>r.id===id?{...r,rtw:true}:r),
      ...addLog(s, user?.name||"Unknown", "absence",
        `RTW completed: ${rec?.name||"Unknown"}`)
    }));
  }
  function deleteSick(id) {
    const rec = sick.find(r=>r.id===id);
    if (!window.confirm(`Remove this absence record for ${rec?.name}? This cannot be undone.`)) return;
    set(s=>({
      ...s,
      sick:s.sick.filter(r=>r.id!==id),
      ...addLog(s, user?.name||"Unknown", "absence",
        `↩ Absence record removed: ${rec?.name} · ${rec?.date} (by ${user?.name||"Manager"})`)
    }));
  }

  const bfByPerson = allStaff.map(s=>{
    const recs    = sick.filter(r=>r.staffId===s.id);
    const bfRecs2 = recs.filter(r=>r.reason!=="medical"&&r.reason!=="leftEarly");
    const episodes = bfRecs2.length;
    const totalDays = bfRecs2.reduce((a,r)=>a+r.days,0);
    const score = episodes*episodes*totalDays;
    const band = BF_BANDS.find(b=>score>=b.min&&score<=b.max)||BF_BANDS[0];
    return {person:s,episodes,totalDays,score,band,recs,active:recs.filter(r=>!r.rtw)};
  }).filter(x=>x.recs.length>0||x.active.length>0).sort((a,b)=>b.score-a.score);

  const rtw = sick.filter(s=>!s.rtw&&s.reason!=="medical"&&s.reason!=="leftEarly");
  const canSeeWelfare = isPrincipal || user?.role==="manager" ||
    (user?.role==="managerLite" && (state.welfareAccess||{})[user?.staffId]);
  const canDoRTW = isPrincipal || user?.role==="manager" ||
    (user?.role==="managerLite" && (state.welfareAccess||{})[user?.staffId]) ||
    // Head nurse fallback: can do RTW if no manager active in 48h
    (user?.role==="headNurse" && (()=>{
      const now = Date.now();
      const lastLogins = state.userLastLogin||{};
      const siteManagers = (state.users||[]).filter(u=>u.role==="manager"&&u.site===user?.site);
      const recentMgr = siteManagers.some(m=>lastLogins[m.id]&&(now-new Date(lastLogins[m.id]).getTime())<48*60*60*1000);
      return !recentMgr;
    })());
  const welfareFlagged = sick.filter(s=>s.welfareFlag);
  const welfareVisible = canSeeWelfare ? (isPrincipal ? welfareFlagged : welfareFlagged.filter(s=>visibleStaff.some(vs=>vs.id===s.staffId))) : [];

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:12}}>
        <h3 style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading}}>Sickness & Attendance</h3>
        <button onClick={()=>{setSickPerson(visibleStaff[0]?.id||"");setSickM(true);}} style={BTN(C.danger)}>+ Record Absence</button>
      </div>

      {/* Tab bar */}
      <div style={{display:"flex",gap:2,marginBottom:18,borderBottom:`1px solid ${C.border}`}}>
        {[
          {id:"absence",label:"Absences & Bradford",badge:rtw.length},
          ...(canSeeWelfare?[{id:"welfare",label:"💜 Welfare",badge:welfareVisible.length,color:"#a78bfa"}]:[]),
        ].map(t=>(
          <button key={t.id} onClick={()=>setSickTab(t.id)}
            style={{background:"transparent",border:"none",padding:"8px 16px",cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600,color:sickTab===t.id?(t.color||C.teal):C.mid,borderBottom:sickTab===t.id?`2px solid ${t.color||C.teal}`:"2px solid transparent",display:"flex",alignItems:"center",gap:6}}>
            {t.label}
            {t.badge>0&&<span style={{background:t.color||C.danger,color:"#fff",borderRadius:99,fontSize:9,fontWeight:800,padding:"1px 6px"}}>{t.badge}</span>}
          </button>
        ))}
      </div>

      {/* WELFARE TAB */}
      {sickTab==="welfare"&&(
        <div>
          <div style={{fontSize:12,color:C.dim,marginBottom:16}}>All absence records where a welfare concern has been flagged. Manager eyes only. Feeds into RTW conversations and the People module.</div>
          {welfareVisible.length===0?(
            <div style={{...CS,textAlign:"center",padding:32,color:C.dim}}>
              <div style={{fontSize:28,marginBottom:8}}>💜</div>
              <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:4}}>No welfare flags</div>
              <div style={{fontSize:11}}>When you flag a welfare concern on an absence record, it appears here.</div>
            </div>
          ):welfareVisible.sort((a,b)=>b.date.localeCompare(a.date)).map(r=>{
            const person = visibleStaff.find(s=>s.id===r.staffId);
            return (
              <div key={r.id} style={{...CS,marginBottom:10,borderLeft:"3px solid #7c3aed",padding:"12px 16px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,flexWrap:"wrap"}}>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,fontSize:13,color:C.text,marginBottom:2}}>{r.name}</div>
                    <div style={{fontSize:11,color:C.dim,marginBottom:6}}>{r.date} · {r.days} day{r.days!==1?"s":""} · {r.reason} · {SITES[person?.site]?.name||""}</div>
                    <div style={{display:"flex",gap:4,marginBottom:r.welfareNotes?8:0,flexWrap:"wrap"}}>
                      {r.leaveRejectedRecently&&<span style={{fontSize:9,fontWeight:700,color:C.warn,background:"rgba(245,197,24,0.05)",border:"1px solid #fde68a",borderRadius:3,padding:"1px 6px"}}>Leave declined prior</span>}
                      {!r.rtw&&<span style={{fontSize:9,fontWeight:700,color:C.danger,background:"rgba(255,70,70,0.08)",border:"1px solid rgba(255,70,70,0.2)",borderRadius:3,padding:"1px 6px"}}>RTW pending</span>}
                      {r.rtw&&<span style={{fontSize:9,fontWeight:700,color:C.green,background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:3,padding:"1px 6px"}}>RTW done</span>}
                    </div>
                    {r.welfareNotes&&(
                      <div style={{padding:"8px 12px",background:"rgba(139,92,246,0.08)",border:"1px solid #ddd6fe",borderRadius:8,fontSize:12,color:"#a78bfa",lineHeight:1.6}}>
                        📝 {r.welfareNotes}
                      </div>
                    )}
                    {!r.welfareNotes&&<div style={{fontSize:11,color:"#a78bfa",fontStyle:"italic"}}>Flag set — no notes added yet</div>}
                  </div>
                  <button onClick={()=>setWelfareEditM({id:r.id,welfareFlag:true,welfareNotes:r.welfareNotes||"",generalNotes:r.generalNotes||"",fitNotes:r.fitNotes||[]})}
                    style={{...BTN("#7c3aed",true),fontSize:11,padding:"5px 12px",flexShrink:0}}>
                    💜 {r.welfareNotes?"Edit note":"Add note"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {sickTab==="absence"&&(<div>
        <div style={{marginBottom:20}}>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>RTW Required ({rtw.length})</div>
          {rtw.map(r=>(
            <div key={r.id} style={{...CS,marginBottom:8,padding:"12px 16px",borderLeft:`3px solid ${r.welfareFlag?"#7c3aed":r.leaveRejectedRecently?C.warn:C.danger}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,color:C.text,fontSize:13}}>{r.name}</div>
                  <div style={{fontSize:11,color:C.dim,marginTop:2}}>{SITES[r.site]?.name} · {r.date} · {r.days} day{r.days!==1?"s":""} · {r.reason}</div>
                  <div style={{display:"flex",gap:4,marginTop:5,flexWrap:"wrap"}}>
                    {r.leaveRejectedRecently&&<span style={{fontSize:9,fontWeight:700,color:C.warn,background:"rgba(245,197,24,0.05)",border:"1px solid #fde68a",borderRadius:3,padding:"1px 6px"}}>Leave declined prior</span>}
                    {r.welfareFlag&&<span style={{fontSize:9,fontWeight:700,color:"#a78bfa",background:"rgba(139,92,246,0.08)",border:"1px solid #ddd6fe",borderRadius:3,padding:"1px 6px"}}>💜 Welfare note</span>}
                  </div>
                  {r.welfareNotes&&(
                    <div style={{marginTop:6,padding:"6px 10px",background:"rgba(139,92,246,0.08)",border:"1px solid #ddd6fe",borderRadius:6,fontSize:11,color:"#a78bfa",fontStyle:"italic",lineHeight:1.5}}>
                      💜 {r.welfareNotes}
                    </div>
                  )}
                  {r.generalNotes&&(
                    <div style={{marginTop:4,padding:"6px 10px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,fontSize:11,color:C.mid,lineHeight:1.5}}>
                      {r.generalNotes}
                    </div>
                  )}
                  {/* Fit notes log */}
                  {(r.fitNotes||[]).length>0&&(
                    <div style={{marginTop:6,padding:"6px 10px",background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:6}}>
                      <div style={{fontSize:10,fontWeight:700,color:C.green,marginBottom:4}}>Fit notes received</div>
                      {r.fitNotes.map((fn,i)=>(
                        <div key={i} style={{fontSize:11,color:C.text,display:"flex",gap:8,marginBottom:2}}>
                          <span style={{color:C.dim}}>#{i+1}</span>
                          <span style={{fontWeight:600}}>{fn.date}</span>
                          {fn.note&&<span style={{color:C.dim}}>— {fn.note}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:6,flexShrink:0,alignItems:"flex-end"}}>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",justifyContent:"flex-end"}}>
                    {canDoRTW&&<button onClick={()=>setRtwInterviewM({id:r.id,name:r.name})} style={{...BTN(C.green,true),fontSize:12,padding:"6px 14px",fontWeight:700}}>RTW Done</button>}
                    {!canDoRTW&&<span style={{fontSize:11,color:C.dim,padding:"6px 10px"}}>Manager to complete RTW</span>}
                    <button onClick={()=>setFitNoteM({id:r.id,name:r.name})} style={{...BTN(C.teal,true),fontSize:11,padding:"6px 12px",fontWeight:600}}>📄 + Fit note</button>
                    <button onClick={()=>deleteSick(r.id)} title="Remove record" style={{...BTN(C.dim,true),fontSize:11,padding:"6px 10px"}}>↩</button>
                  </div>
                  <button onClick={()=>setWelfareEditM({id:r.id,welfareFlag:!!r.welfareFlag,welfareNotes:r.welfareNotes||"",generalNotes:r.generalNotes||"",fitNotes:r.fitNotes||[]})}
                    style={{...BTN(r.welfareFlag?"#7c3aed":C.dim,true),fontSize:11,padding:"5px 12px",width:"100%"}}>
                    {r.welfareFlag?"💜 Edit welfare note":"Add notes"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

      {bfByPerson.length>0&&(
        <div>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Bradford Factor</div>
          {bfByPerson.map(({person,episodes,totalDays,score,band,active,recs})=>(
            <div key={person.id} style={{...CS,marginBottom:8,padding:"14px 16px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                <div>
                  <div style={{fontWeight:700,color:C.text,fontSize:13,marginBottom:2}}>{person.name}</div>
                  <div style={{fontSize:11,color:C.dim}}>{SITES[person.site]?.name} · {episodes} episode{episodes!==1?"s":""} · {totalDays} days</div>
                  {(()=>{ const fn=(recs||[]).flatMap(r=>r.fitNotes||[]); return fn.length>0?<div style={{fontSize:10,color:C.green,marginTop:3,fontWeight:600}}>📄 {fn.length} fit note{fn.length!==1?"s":""} on file</div>:null; })()}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:24,color:band.color}}>{score}</div>
                    <Pill c={band.color} t={band.label} sm/>
                  </div>
                </div>
              </div>
              {active.length>0&&<div style={{marginTop:8,fontSize:11,color:C.danger}}>RTW pending for {active.length} absence{active.length!==1?"s":""}</div>}
            </div>
          ))}
        </div>
      )}

      {sick.length===0&&bfByPerson.length===0&&(
        <div style={{...CS,textAlign:"center",padding:40,color:C.dim}}>
          <div style={{fontSize:32,marginBottom:12}}>✅</div>
          <div style={{fontSize:14}}>No absences recorded</div>
        </div>
      )}

      <BestAttendance state={state}/>
      </div>)} {/* end sickTab==="absence" */}

      {sickM&&(
        <Overlay onClose={()=>setSickM(false)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Record Absence</div>
          <div style={{fontSize:12,color:C.dim,marginBottom:16}}>Logged by {user?.name||"Manager"}</div>
          <div style={{marginBottom:14}}>
            <label style={LB}>Who is absent?</label>
            <select value={sickPerson} onChange={e=>setSickPerson(e.target.value)} style={IS}>
              {(()=>{
                const isClinician=s=>!!(s.sched||["GDS","MOS","FD","Therapist","Partner","Specialist"].includes(s.type));
                const nurses=visibleStaff.filter(s=>!isClinician(s)&&(s.area||s.status));
                const clins=visibleStaff.filter(s=>isClinician(s));
                const recs=visibleStaff.filter(s=>!isClinician(s)&&!s.area&&!s.status);
                return [
                  ...(nurses.length?[<option key="--n" disabled>── Nurses ──</option>,...nurses.map(s=><option key={s.id} value={s.id}>{s.name} · {SITES[s.site]?.name}</option>)]:[]),
                  ...(clins.length?[<option key="--c" disabled>── Clinicians ──</option>,...clins.map(s=><option key={s.id} value={s.id}>{s.name} · {SITES[s.site]?.name} · {s.type}</option>)]:[]),
                  ...(recs.length?[<option key="--r" disabled>── Reception & TCO ──</option>,...recs.map(s=><option key={s.id} value={s.id}>{s.name} · {SITES[s.site]?.name}</option>)]:[]),
                ];
              })()}
            </select>
          </div>
          {(()=>{
            const person = visibleStaff.find(s=>s.id===sickPerson)||visibleStaff[0];
            if (!person) return null;
            return <SickModalInline person={person} onSave={r=>{addSick(r);setSickM(false);}} onClose={()=>setSickM(false)}/>;
          })()}
        </Overlay>
      )}
      {welfareEditM&&(
        <Overlay onClose={()=>setWelfareEditM(null)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:18,fontWeight:700,color:C.heading,marginBottom:4}}>Absence Notes</div>
          <div style={{fontSize:11,color:C.dim,marginBottom:16}}>Manager eyes only · Add now or come back later — no pressure to have it all at the time</div>
          <label style={LB}>Notes <span style={{color:C.dim,fontWeight:400}}>(what was said, tone, context, expected return)</span></label>
          <textarea value={welfareEditM.generalNotes||""}
            onChange={e=>setWelfareEditM(m=>({...m,generalNotes:e.target.value}))}
            rows={3} placeholder="e.g. Called at 7:30am, sounded upset. Said stomach bug. Hopes back Thursday."
            style={{...IS,resize:"none",lineHeight:1.6,fontSize:12,marginBottom:14}}/>

          {/* Fit notes */}
          <div style={{marginBottom:14}}>
            <label style={LB}>Fit notes received</label>
            <div style={{background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:8,padding:"10px 12px"}}>
              {(welfareEditM.fitNotes||[]).map((fn,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,fontSize:12}}>
                  <span style={{color:C.dim,minWidth:20}}>#{i+1}</span>
                  <input type="date" value={fn.date} onChange={e=>{const n=[...(welfareEditM.fitNotes||[])];n[i]={...n[i],date:e.target.value};setWelfareEditM(m=>({...m,fitNotes:n}));}} style={{...IS,marginBottom:0,flex:1,fontSize:11,padding:"4px 8px"}}/>
                  <input value={fn.note||""} placeholder="e.g. covers 2 weeks" onChange={e=>{const n=[...(welfareEditM.fitNotes||[])];n[i]={...n[i],note:e.target.value};setWelfareEditM(m=>({...m,fitNotes:n}));}} style={{...IS,marginBottom:0,flex:2,fontSize:11,padding:"4px 8px"}}/>
                  <button onClick={()=>setWelfareEditM(m=>({...m,fitNotes:(m.fitNotes||[]).filter((_,j)=>j!==i)}))} style={{background:"transparent",border:"none",color:C.danger,cursor:"pointer",fontSize:14,padding:"0 4px"}}>×</button>
                </div>
              ))}
              <button onClick={()=>setWelfareEditM(m=>({...m,fitNotes:[...(m.fitNotes||[]),{date:new Date().toISOString().split("T")[0],note:""}]}))}
                style={{...BTN(C.green,true),fontSize:11,padding:"4px 10px",marginTop:4}}>+ Add fit note</button>
            </div>
          </div>
          <div style={{borderTop:`1px solid ${C.border}`,paddingTop:12,marginBottom:10}}>
            <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
              <input type="checkbox" checked={!!welfareEditM.welfareFlag}
                onChange={e=>setWelfareEditM(m=>({...m,welfareFlag:e.target.checked}))}
                style={{width:16,height:16,accentColor:"#7c3aed"}}/>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:"#a78bfa"}}>💜 Flag as welfare concern</div>
                <div style={{fontSize:10,color:C.dim}}>Feeds into RTW conversation and People module</div>
              </div>
            </label>
          </div>
          {welfareEditM.welfareFlag&&(
            <textarea value={welfareEditM.welfareNotes||""}
              onChange={e=>setWelfareEditM(m=>({...m,welfareNotes:e.target.value}))}
              rows={2} placeholder="Additional welfare context — gut feeling, pattern, anything that gave you pause"
              style={{...IS,resize:"none",lineHeight:1.6,fontSize:12,background:"rgba(139,92,246,0.08)",border:"1px solid #ddd6fe",marginBottom:12}}/>
          )}
          <div style={{display:"flex",gap:8,marginTop:welfareEditM.welfareFlag?0:4}}>
            <button onClick={()=>saveWelfareNote(welfareEditM.id,welfareEditM.welfareFlag,welfareEditM.welfareNotes,welfareEditM.generalNotes,welfareEditM.fitNotes||[])}
              style={{...BTN(C.teal),flex:1,padding:"10px 0"}}>Save</button>
            <button onClick={()=>setWelfareEditM(null)} style={BTN(C.dim,true)}>Cancel</button>
          </div>
        </Overlay>
      )}
      {/* ── FIT NOTE MODAL ── */}
      {fitNoteM&&(
        <Overlay onClose={()=>setFitNoteM(null)}>
          <div style={{fontFamily:"'Instrument Serif',serif",fontSize:20,color:C.heading,fontWeight:400,marginBottom:4}}>Log Fit Note</div>
          <div style={{fontSize:12,color:C.dim,marginBottom:20}}>{fitNoteM.name} · Record a GP fit note received during this absence</div>
          <FitNoteForm
            onSave={(fn)=>{
              set(s=>({...s, sick:s.sick.map(r=>r.id===fitNoteM.id?{...r, certified:true, fitNotes:[...(r.fitNotes||[]),fn]}:r)}));
              setFitNoteM(null);
            }}
            onClose={()=>setFitNoteM(null)}
          />
        </Overlay>
      )}

      {/* ── RTW INTERVIEW MODAL ── */}
      {rtwInterviewM&&(
        <Overlay onClose={()=>setRtwInterviewM(null)}>
          <div style={{fontFamily:"'Instrument Serif',serif",fontSize:20,color:C.heading,fontWeight:400,marginBottom:4}}>Return to Work</div>
          <div style={{fontSize:12,color:C.dim,marginBottom:20}}>{rtwInterviewM.name} · Record the return to work conversation</div>
          <RTWInterviewForm
            user={user}
            onSave={(rtwData)=>{
              set(s=>({
                ...s,
                sick:s.sick.map(r=>r.id===rtwInterviewM.id
                  ? {...r, rtw:true, rtwDate:rtwData.date, rtwBy:rtwData.conductedBy, rtwNotes:rtwData.notes, rtwFitNote:rtwData.fitNote, rtwAdjustments:rtwData.adjustments, rtwWelfare:rtwData.welfare}
                  : r),
                ...addLog(s, user?.name||"Unknown", "absence", `RTW completed: ${rtwInterviewM.name} · by ${rtwData.conductedBy} on ${rtwData.date}`)
              }));
              setRtwInterviewM(null);
            }}
            onClose={()=>setRtwInterviewM(null)}
          />
        </Overlay>
      )}
    </div>
  );
}
/* ── FIT NOTE FORM ── reusable, People-ready */
function FitNoteForm({onSave, onClose}) {
  const today = new Date().toISOString().split("T")[0];
  const [date, setDate]               = useState(today);
  const [coversUntil, setCoversUntil] = useState("");
  const [note, setNote]               = useState("");

  function addDays(d, n) {
    const dt = new Date(d+"T12:00:00");
    dt.setDate(dt.getDate()+n);
    return dt.toISOString().split("T")[0];
  }

  return (
    <>
      <div style={{marginBottom:12}}>
        <label style={LB}>Date received</label>
        <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={IS}/>
      </div>
      <div style={{marginBottom:8}}>
        <label style={LB}>Covers until <span style={{color:C.dim,fontWeight:400}}>(date on the note)</span></label>
        <input type="date" value={coversUntil} onChange={e=>setCoversUntil(e.target.value)} style={IS}/>
      </div>
      {/* Quick duration buttons */}
      <div style={{display:"flex",gap:6,marginBottom:14,flexWrap:"wrap"}}>
        {[["1 week",7],["2 weeks",14],["4 weeks",28],["3 months",91],["6 months",182]].map(([label,days])=>(
          <button key={days} onClick={()=>setCoversUntil(addDays(date,days))}
            style={{padding:"4px 12px",borderRadius:99,border:`1px solid ${C.border}`,background:C.bg,
              fontSize:11,fontWeight:600,color:C.mid,cursor:"pointer",fontFamily:"inherit"}}>
            {label}
          </button>
        ))}
      </div>
      {coversUntil&&(
        <div style={{background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:8,padding:"10px 14px",marginBottom:14,fontSize:12,color:"#00e5a0"}}>
          📄 Fit note covers <strong>{new Date(date+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short"})}</strong> → <strong>{new Date(coversUntil+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</strong>
          {" "}· <span style={{color:C.dim}}>
            {Math.round((new Date(coversUntil+"T12:00:00")-new Date(date+"T12:00:00"))/(1000*60*60*24))} days
          </span>
        </div>
      )}
      <div style={{marginBottom:20}}>
        <label style={LB}>Notes <span style={{color:C.dim,fontWeight:400}}>(optional — e.g. "may be fit for light duties")</span></label>
        <input value={note} onChange={e=>setNote(e.target.value)}
          placeholder="e.g. Light duties only, review in 2 weeks"
          style={IS}/>
      </div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>onSave({date, coversUntil, note, loggedAt:new Date().toISOString()})}
          disabled={!date}
          style={{...BTN(C.teal),flex:1,padding:"11px 0",opacity:date?1:0.4}}>
          Save fit note
        </button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </>
  );
}

/* ── RTW INTERVIEW FORM ── structured, People-ready */
function RTWInterviewForm({user, onSave, onClose}) {
  const today = new Date().toISOString().split("T")[0];
  const [date, setDate]               = useState(today);
  const [conductedBy, setConductedBy] = useState(user?.name||"");
  const [notes, setNotes]             = useState("");
  const [fitNote, setFitNote]         = useState(false);
  const [fitNoteDate, setFitNoteDate] = useState("");
  const [fitNoteUntil, setFitNoteUntil] = useState("");
  const [adjustments, setAdjustments] = useState("");
  const [welfare, setWelfare]         = useState(false);

  return (
    <>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
        <div>
          <label style={LB}>Date of conversation</label>
          <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={IS}/>
        </div>
        <div>
          <label style={LB}>Conducted by</label>
          <input value={conductedBy} onChange={e=>setConductedBy(e.target.value)} style={IS}/>
        </div>
      </div>

      <div style={{marginBottom:12}}>
        <label style={LB}>Conversation notes <span style={{color:C.dim,fontWeight:400}}>(what was discussed, how they seemed, expected trajectory)</span></label>
        <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={3}
          placeholder="e.g. Sophie returned today. Seemed well. Confirmed stomach bug. No ongoing issues. Advised to contact if symptoms return."
          style={{...IS,resize:"none",lineHeight:1.6,fontSize:12}}/>
      </div>

      <div style={{marginBottom:12}}>
        <label style={LB}>Any adjustments needed?</label>
        <input value={adjustments} onChange={e=>setAdjustments(e.target.value)}
          placeholder="e.g. Light duties this week, no heavy lifting"
          style={IS}/>
      </div>

      {/* Fit note on return */}
      <div style={{marginBottom:14,padding:"12px 14px",background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:10}}>
        <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",marginBottom:fitNote?10:0}}>
          <input type="checkbox" checked={fitNote} onChange={e=>setFitNote(e.target.checked)}
            style={{width:16,height:16,accentColor:C.green}}/>
          <span style={{fontSize:13,fontWeight:600,color:C.green}}>📄 Fit note received on return</span>
        </label>
        {fitNote&&(
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginTop:6}}>
            <div>
              <label style={{...LB,fontSize:10}}>Date received</label>
              <input type="date" value={fitNoteDate} onChange={e=>setFitNoteDate(e.target.value)} style={{...IS,fontSize:11,padding:"6px 10px"}}/>
            </div>
            <div>
              <label style={{...LB,fontSize:10}}>Covers until</label>
              <input type="date" value={fitNoteUntil} onChange={e=>setFitNoteUntil(e.target.value)} style={{...IS,fontSize:11,padding:"6px 10px"}}/>
            </div>
          </div>
        )}
      </div>

      {/* Welfare concern */}
      <div style={{marginBottom:20,padding:"10px 14px",background:"rgba(139,92,246,0.06)",border:"1px solid #e9d5ff",borderRadius:10}}>
        <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
          <input type="checkbox" checked={welfare} onChange={e=>setWelfare(e.target.checked)}
            style={{width:16,height:16,accentColor:"#7c3aed"}}/>
          <div>
            <div style={{fontSize:13,fontWeight:600,color:"#a78bfa"}}>💜 Flag welfare concern</div>
            <div style={{fontSize:10,color:C.dim,marginTop:1}}>Flags to manager. Feeds into People module when live.</div>
          </div>
        </label>
      </div>

      <div style={{display:"flex",gap:8}}>
        <button
          onClick={()=>onSave({date, conductedBy, notes, adjustments, welfare,
            fitNote: fitNote ? {date:fitNoteDate, coversUntil:fitNoteUntil, loggedAt:new Date().toISOString()} : null
          })}
          disabled={!date||!conductedBy}
          style={{...BTN(C.green),flex:1,padding:"12px 0",opacity:(!date||!conductedBy)?0.4:1,fontSize:14,fontWeight:700}}>
          Complete RTW
        </button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
      <div style={{textAlign:"center",marginTop:10,fontSize:10,color:C.dim}}>
        Recorded with your name and timestamp · Ready for People module
      </div>
    </>
  );
}

/* ══════════════════════════════════
   CONVERSATION REPORT — v5.24
   Confidential HR briefing per staff member
   Surfaces patterns before difficult conversations
══════════════════════════════════ */
function ConversationReport({person, state, set, onClose}) {
  const sick     = (state.sick||[]).filter(r=>r.staffId===person.id);
  const allSick  = state.sick||[];
  const leave    = (state.leave[person.id]||[]).filter(l=>l.status==="approved");
  const goodWork = (state.goodWorkLogs||[]).filter(g=>g.staffId===person.id);
  const welfare  = (state.welfareLog||[]).filter(w=>w.staffId===person.id);
  const today    = new Date().toISOString().split("T")[0];
  const generated = new Date().toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"});
  const [convWelfareM,setConvWelfareM] = useState(null);

  function saveConvWelfare(id, welfareFlag, welfareNotes, generalNotes) {
    set(s=>({...s, sick:s.sick.map(r=>r.id===id?{...r,welfareFlag,welfareNotes,generalNotes}:r)}));
    setConvWelfareM(null);
  }

  // Bradford Factor
  const bfSick    = sick.filter(r=>r.reason!=="medical"&&r.reason!=="leftEarly");
  const episodes  = bfSick.length;
  const totalDays = bfSick.reduce((a,r)=>a+r.days,0);
  const bfScore   = episodes*episodes*totalDays;
  const bfBand    = BF_BANDS.find(b=>bfScore>=b.min&&bfScore<=b.max)||BF_BANDS[0];

  // Day-of-week pattern
  const dowCounts = {};
  sick.forEach(r=>{
    const dow = new Date(r.date+"T12:00:00").toLocaleDateString("en-GB",{weekday:"long"});
    dowCounts[dow]=(dowCounts[dow]||0)+1;
  });
  const dowPatterns = Object.entries(dowCounts).filter(([,n])=>n>=2).sort((a,b)=>b[1]-a[1]);

  // School holiday pattern — rough UK school hols
  const SCHOOL_HOLS = ["2026-02-16","2026-02-17","2026-02-18","2026-02-19","2026-02-20","2026-04-03","2026-04-06","2026-04-07","2026-04-08","2026-04-09","2026-04-10","2026-05-25","2026-05-26","2026-05-27","2026-05-28","2026-05-29","2026-07-20","2026-07-21","2026-07-22","2026-07-23","2026-07-24","2026-07-27","2026-07-28","2026-07-29","2026-07-30","2026-07-31","2026-08-03","2026-08-04","2026-08-05","2026-08-06","2026-08-07","2026-08-10","2026-08-11","2026-08-12","2026-08-13","2026-08-14","2026-08-17","2026-08-18","2026-08-19","2026-08-20","2026-08-21","2026-08-24","2026-08-25","2026-08-26","2026-08-27","2026-08-28","2026-10-26","2026-10-27","2026-10-28","2026-10-29","2026-10-30"];
  const schoolHolSick = sick.filter(r=>SCHOOL_HOLS.includes(r.date));
  const schoolHolRate = sick.length>0 ? Math.round((schoolHolSick.length/sick.length)*100) : 0;

  // Co-worker pattern — who else was working on sick days
  const allStaff = [...(state.nurses||NURSES_BASE),...RECS_BASE];
  const coworkerCounts = {};
  sick.forEach(r=>{
    const dateStr = r.date;
    const dow = new Date(dateStr+"T12:00:00").toLocaleDateString("en-GB",{weekday:"short"});
    allStaff.filter(s=>s.id!==person.id&&s.site===person.site).forEach(s=>{
      const cl = getClinicians(state).find(c=>c.id===s.id);
      const sched = cl?.sched||{};
      if (sched[dow]) {
        coworkerCounts[s.name]=(coworkerCounts[s.name]||0)+1;
      }
    });
  });
  const coworkerPatterns = Object.entries(coworkerCounts)
    .filter(([,n])=>n>=2&&n>=sick.length*0.4)
    .sort((a,b)=>b[1]-a[1]).slice(0,3);

  // Recent trend — last 3 months vs before
  const threeMonthsAgo = new Date(); threeMonthsAgo.setMonth(threeMonthsAgo.getMonth()-3);
  const recentSick = sick.filter(r=>new Date(r.date)>=threeMonthsAgo);
  const olderSick  = sick.filter(r=>new Date(r.date)<threeMonthsAgo);

  // Leave balance — filter to current leave year, use partner-aware entitlement
  const entit = getEntitlement(state, person);
  const hist  = getHistDays(state, person.id);
  const rTypeP = roleTypeForPerson(person);
  const indOvP = (state.leaveYearOverrides||{})[person.id];
  const lyCfgP = indOvP?{[rTypeP]:indOvP}:state.leaveYearConfig;
  const {start:lyStartP,end:lyEndP} = getLeaveYear(rTypeP, lyCfgP);
  const lyStartStrP = lyStartP.toISOString().split("T")[0];
  const lyEndStrP   = lyEndP.toISOString().split("T")[0];
  const leaveUsed = (state.leave[person.id]||[]).filter(l=>l.status==="approved"&&l.date>=lyStartStrP&&l.date<=lyEndStrP).length + hist;
  const leaveRem  = Math.round((entit - leaveUsed)*10)/10;

  const hasSomething = sick.length>0||goodWork.length>0||welfare.length>0;

  return (
    <Overlay onClose={onClose}>
      <div style={{maxHeight:"80vh",overflowY:"auto"}}>
        {/* Header */}
        <div style={{marginBottom:20}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
            <div style={{width:36,height:36,borderRadius:99,background:C.tealPale,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>👤</div>
            <div>
              <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading}}>{person.name}</div>
              <div style={{fontSize:11,color:C.dim}}>{SITES[person.site]?.name} · {person.type||person.area||"Staff"} · {person.hrs||37.5}h/week</div>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginTop:8}}>
            <span style={{fontSize:10,fontWeight:700,background:"rgba(255,70,70,0.08)",color:C.danger,border:"1px solid rgba(255,70,70,0.2)",borderRadius:6,padding:"2px 8px"}}> CONFIDENTIAL</span>
            <span style={{fontSize:10,color:C.dim}}>Generated {generated}</span>
          </div>
        </div>

        {!hasSomething&&(
          <div style={{...CS,textAlign:"center",padding:32,color:C.dim}}>
            <div style={{fontSize:28,marginBottom:8,display:"flex",justifyContent:"center"}}><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg></div>
            <div style={{fontSize:13}}>No concerns on record for {person.name.split(" ")[0]}.</div>
            <div style={{fontSize:11,marginTop:4}}>Clean attendance, no welfare flags.</div>
          </div>
        )}

        {/* Attendance Summary */}
        {sick.length>0&&(
          <div style={{marginBottom:20}}>
            <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Attendance Summary</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:12}}>
              {[[episodes,"Episodes",C.warn],[totalDays+"d","Days absent",C.danger],[bfScore,"Bradford Score",bfBand.color],[recentSick.length,"Last 3 months",recentSick.length>olderSick.length?C.danger:C.green]].map(([v,l,c])=>(
                <div key={l} style={{...CS,textAlign:"center",padding:"10px 6px"}}>
                  <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:c}}>{v}</div>
                  <div style={{fontSize:9,color:C.dim,marginTop:2,textTransform:"uppercase",letterSpacing:"0.05em"}}>{l}</div>
                </div>
              ))}
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:bfBand.color+"12",border:`1px solid ${bfBand.color}33`,borderRadius:8,marginBottom:12}}>
              
              <div style={{fontSize:12}}>
                <span style={{fontWeight:700,color:bfBand.color}}>Bradford Factor: {bfScore} — {bfBand.label}</span>
                <span style={{color:C.dim,marginLeft:6,fontSize:11}}>Signal not verdict</span>
              </div>
            </div>

            {/* Absence log */}
            <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>Absence Record</div>
            {sick.sort((a,b)=>b.date.localeCompare(a.date)).map(r=>{
              const isSchoolHol = SCHOOL_HOLS.includes(r.date);
              return (
                <div key={r.id} style={{padding:"7px 0",borderBottom:`1px solid ${C.border2}`,fontSize:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{color:C.dim,minWidth:70,fontSize:11}}>{new Date(r.date+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</span>
                      <span style={{color:C.text}}>{r.days} day{r.days!==1?"s":""}</span>
                      <span style={{color:C.dim,fontSize:11}}>{r.reason}</span>
                    </div>
                    <div style={{display:"flex",gap:4,flexWrap:"wrap",alignItems:"center"}}>
                      {isSchoolHol&&<Pill c={C.warn} t="School hol" sm/>}
                      {r.rtw&&<Pill c={C.green} t="RTW done" sm/>}
                      {!r.rtw&&<Pill c={C.danger} t="RTW pending" sm/>}
                      {r.leaveRejectedRecently&&<Pill c={C.warn} t="Leave declined prior" sm/>}
                      {r.welfareFlag&&<Pill c="#7c3aed" t="💜 Welfare" sm/>}
                      <button onClick={()=>setConvWelfareM({id:r.id,welfareFlag:!!r.welfareFlag,welfareNotes:r.welfareNotes||"",generalNotes:r.generalNotes||""})}
                        style={{fontSize:9,padding:"2px 8px",borderRadius:4,border:`1px solid ${r.welfareFlag?"#ddd6fe":C.border}`,background:r.welfareFlag?"#f5f3ff":"transparent",color:r.welfareFlag?"#7c3aed":C.dim,cursor:"pointer",fontWeight:600}}>
                        {r.welfareFlag?"💜 edit":"+ notes"}
                      </button>
                    </div>
                  </div>
                  {r.welfareNotes&&(
                    <div style={{marginTop:5,padding:"5px 10px",background:"rgba(139,92,246,0.08)",border:"1px solid #ddd6fe",borderRadius:6,fontSize:11,color:"#a78bfa",fontStyle:"italic",lineHeight:1.5}}>
                      💜 {r.welfareNotes}
                    </div>
                  )}
                  {r.generalNotes&&(
                    <div style={{marginTop:4,padding:"5px 10px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,fontSize:11,color:C.mid,lineHeight:1.5}}>
                      {r.generalNotes}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Patterns */}
        {sick.length>=2&&(
          <div style={{marginBottom:20}}>
            <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Patterns Identified</div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>

              {dowPatterns.map(([dow,count])=>(
                <div key={dow} style={{display:"flex",gap:10,padding:"10px 12px",background:"rgba(245,197,24,0.05)",border:"1px solid #fde68a",borderRadius:8}}>
                  
                  <div style={{fontSize:12,color:C.soft}}>
                    <span style={{fontWeight:700}}>{count} absences on {dow}s</span>
                    <span style={{color:C.dim,marginLeft:6}}>out of {episodes} total — {Math.round((count/episodes)*100)}%</span>
                  </div>
                </div>
              ))}

              {schoolHolSick.length>=2&&(
                <div style={{display:"flex",gap:10,padding:"10px 12px",background:"rgba(245,197,24,0.05)",border:"1px solid #fde68a",borderRadius:8}}>
                  
                  <div style={{fontSize:12,color:C.soft}}>
                    <span style={{fontWeight:700}}>{schoolHolSick.length} absences coincide with school holidays</span>
                    <span style={{color:C.dim,marginLeft:6}}>({schoolHolRate}% of total)</span>
                  </div>
                </div>
              )}

              {coworkerPatterns.map(([name,count])=>(
                <div key={name} style={{display:"flex",gap:10,padding:"10px 12px",background:"rgba(255,70,70,0.08)",border:"1px solid rgba(255,70,70,0.2)",borderRadius:8}}>
                  
                  <div style={{fontSize:12,color:C.soft}}>
                    <span style={{fontWeight:700}}>{name} was working on {count} of the {episodes} sick days</span>
                    <span style={{color:C.dim,marginLeft:6}}>({Math.round((count/episodes)*100)}% correlation) — worth noting</span>
                  </div>
                </div>
              ))}

              {dowPatterns.length===0&&schoolHolSick.length<2&&coworkerPatterns.length===0&&(
                <div style={{fontSize:12,color:C.dim,padding:"8px 0"}}>No significant patterns detected yet.</div>
              )}
            </div>
          </div>
        )}

        {/* Good work */}
        {goodWork.length>0&&(
          <div style={{marginBottom:20}}>
            <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Good Work on Record</div>
            {goodWork.slice(0,3).map(g=>(
              <div key={g.id} style={{...CS,padding:"10px 14px",marginBottom:6,borderLeft:`3px solid ${C.green}`}}>
                <div style={{fontSize:11,color:C.green,fontWeight:700,marginBottom:2}}>{new Date(g.date+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</div>
                <div style={{fontSize:12,color:C.soft}}>{g.note}</div>
                <div style={{fontSize:10,color:C.dim,marginTop:2}}>Logged by {g.loggedBy}</div>
              </div>
            ))}
          </div>
        )}

        {/* Welfare history */}
        {welfare.length>0&&(
          <div style={{marginBottom:20}}>
            <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Previous Welfare Conversations</div>
            {welfare.map(w=>(
              <div key={w.id} style={{...CS,padding:"10px 14px",marginBottom:6,borderLeft:`3px solid ${C.warn}`}}>
                <div style={{fontSize:11,color:C.warn,fontWeight:700,marginBottom:2}}>{new Date(w.date+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</div>
                <div style={{fontSize:12,color:C.soft}}>{w.note||"Welfare conversation logged"}</div>
                {w.closed&&<Pill c={C.green} t="Resolved" sm/>}
              </div>
            ))}
          </div>
        )}

        {/* Holiday balance */}
        <div style={{marginBottom:20}}>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Holiday Position</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
            {[[entit+"d","Entitlement",C.text],[leaveUsed+"d","Used",C.warn],[leaveRem+"d","Remaining",leaveRem<5?C.danger:C.green]].map(([v,l,c])=>(
              <div key={l} style={{...CS,textAlign:"center",padding:"10px 6px"}}>
                <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:c}}>{v}</div>
                <div style={{fontSize:9,color:C.dim,marginTop:2,textTransform:"uppercase",letterSpacing:"0.05em"}}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer note */}
        <div style={{background:C.tealPale,border:`1px solid ${C.tealBorder}`,borderRadius:10,padding:"10px 14px",fontSize:11,color:C.teal}}>
          <strong>Note:</strong> This report is for management use only. Patterns are signals not conclusions — use as a starting point for a supportive conversation, not a verdict. All data is from Omni Rota logs.
        </div>
      </div>
      {convWelfareM&&(
        <Overlay onClose={()=>setConvWelfareM(null)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:18,fontWeight:700,color:"#a78bfa",marginBottom:4}}>Absence Notes</div>
          <div style={{fontSize:12,color:C.dim,marginBottom:14}}>Manager eyes only — add at any time, even days later</div>
          <label style={LB}>General notes</label>
          <textarea value={convWelfareM.generalNotes||""} onChange={e=>setConvWelfareM(m=>({...m,generalNotes:e.target.value}))} rows={3}
            placeholder="e.g. Called in at 7:30am, sounded upset. Said it was stomach bug."
            style={{...IS,resize:"none",lineHeight:1.6,fontSize:12,marginBottom:12}}/>
          <label style={{display:"flex",alignItems:"center",gap:10,marginBottom:10,cursor:"pointer"}}>
            <input type="checkbox" checked={!!convWelfareM.welfareFlag} onChange={e=>setConvWelfareM(m=>({...m,welfareFlag:e.target.checked}))}
              style={{width:18,height:18,accentColor:"#7c3aed"}}/>
            <span style={{fontSize:13,fontWeight:700,color:"#a78bfa"}}>Flag as welfare concern</span>
          </label>
          {convWelfareM.welfareFlag&&(
            <textarea value={convWelfareM.welfareNotes||""} onChange={e=>setConvWelfareM(m=>({...m,welfareNotes:e.target.value}))} rows={3}
              placeholder="Welfare note — gut feeling, pattern, context for RTW conversation"
              style={{...IS,resize:"none",lineHeight:1.6,fontSize:12,background:"rgba(139,92,246,0.08)",border:"1px solid #ddd6fe",marginBottom:14}}/>
          )}
          {!convWelfareM.welfareFlag&&<div style={{marginBottom:14}}/>}
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>saveConvWelfare(convWelfareM.id,convWelfareM.welfareFlag,convWelfareM.welfareNotes,convWelfareM.generalNotes)}
              style={{...BTN("#7c3aed"),flex:1,padding:"10px 0"}}>Save Notes</button>
            <button onClick={()=>setConvWelfareM(null)} style={BTN(C.dim,true)}>Cancel</button>
          </div>
        </Overlay>
      )}
    </Overlay>
  );
}


function Directory({state,set,isPrincipal,user}) {
  const [q,setQ]       = useState("");
  const [site,setSite] = useState("all");
  const [collegeM,setCollegeM] = useState(false);
  const [trainM,setTrainM]     = useState(null);
  const [roleM,setRoleM]       = useState(null);
  const [compM,setCompM]       = useState(null);
  const [histM,setHistM]       = useState(null);
  const [shadowM,setShadowM]   = useState(null);
  const [transferM,setTransferM] = useState(null);
  const [recStatusM,setRecStatusM] = useState(null);
  const [addStaffM,setAddStaffM]   = useState(false);
  const [archiveConfirm,setArchiveConfirm] = useState(null);
  const [editStaffM,setEditStaffM]         = useState(null);
  // v11.3 — feedback toast state for staff-add invite. Shape:
  //   null                                — no toast visible
  //   {state:"sending", name, email}       — invite request in flight
  //   {state:"sent",    name, email}       — invite delivered successfully
  //   {state:"failed",  name, email,
  //      reason, userRecord}               — failed; "Resend" button available
  // Replaces the previous .catch(()=>{}) silent failure that meant
  // managers had no idea whether the invite actually went or not.
  const [inviteStatus,setInviteStatus] = useState(null);
  const [convM,setConvM]       = useState(null);
  const [showLeavers,setShowLeavers] = useState(false);
  const [maternityM,setMaternityM] = useState(null);
  const [mType,setMType]   = useState("maternity");
  const [mStart,setMStart] = useState("");
  const [mReturn,setMReturn] = useState("");
  const [mKIT,setMKIT]     = useState(0);
  const [mNote,setMNote]   = useState("");
  const [mActive,setMActive] = useState(true);
  const pulsePeopleEnabled = state.settings?.pulsePeople||false;
  const nurses = state.nurses||NURSES_BASE;
  const match = s => (site==="all"||s.site===site)&&(!q||s.name.toLowerCase().includes(q.toLowerCase()));

  function updateTraining(id,status,shadowingRequired) {
    const nurse = (state.nurses||NURSES_BASE).find(n=>n.id===id);
    set(s=>({
      ...s,
      nurses:(s.nurses||NURSES_BASE).map(n=>n.id===id?{...n,status,...(shadowingRequired!==undefined?{shadowingRequired}:{})}:n),
      ...addLog(s, user?.name||"Unknown", "training",
        `Training updated: ${nurse?.name||id} → ${STATUS[status]?.label||status}`)
    }));
    setTrainM(null);
  }
  function transferSite(id, newSite, effectiveDate) {
    const nurse = (state.nurses||NURSES_BASE).find(n=>n.id===id);
    set(s=>({
      ...s,
      nurses:(s.nurses||NURSES_BASE).map(n=>n.id===id?{...n,site:newSite,moving:undefined,moveDate:undefined}:n),
      ...addLog(s, user?.name||"Unknown", "role",
        `Transferred: ${nurse?.name||id} transferred to ${SITES[newSite]?.name}${effectiveDate?` (effective ${effectiveDate})`:""}`),
    }));
    setTransferM(null);
  }
  function updateRole(id,role) {
    const person = [...(state.nurses||NURSES_BASE),...RECS_BASE].find(s=>s.id===id);
    const roleLabels = {staff:"Standard Staff",headNurse:"Head Nurse",managerLite:"Acting Manager"};
    set(s=>({
      ...s,
      staffRoles:{...s.staffRoles,[id]:role},
      ...addLog(s, user?.name||"Unknown", "role",
        `Role changed: ${person?.name||id} → ${roleLabels[role]||role}`)
    }));
    setRoleM(null);
  }
  function saveCompliance(id, compliance) {
    // Sync top-level flags so checkComplianceSafety picks them up immediately
    const topLevel = {
      safeguardingL3: !!compliance.safeguarding,
      fireMarshal:    !!compliance.fireMarshal,
      firstAider:     !!compliance.firstAider,
      keyholder:      !!compliance.keyholder,
    };
    // Update nurses list if this person is a nurse
    const isNurse = (state.nurses||NURSES_BASE).some(n=>n.id===id);
    const isClinician = !isNurse && getClinicians(state).some(c=>c.id===id);
    if (isNurse) {
      set(s=>({...s,nurses:(s.nurses||NURSES_BASE).map(n=>
        n.id===id?{...n,compliance,...topLevel}:n
      )}));
    } else if (isClinician) {
      // Tenant-agnostic clinician update — upsert into customClinicians.
      // For Flourish/test/etc the clinician already lives there so this is a
      // straight update. For DCO it creates an override row keyed by id, which
      // getClinicians() merges back over CLINICIANS_BASE.
      set(s=>{
        const existingCustom = (s.customClinicians||[]).find(c=>c.id===id);
        const baseClin = getClinicians(s).find(c=>c.id===id) || {id};
        const updated = {...baseClin, compliance, ...topLevel};
        const customs = existingCustom
          ? (s.customClinicians||[]).map(c=>c.id===id?updated:c)
          : [...(s.customClinicians||[]), updated];
        return {...s, customClinicians: customs};
      });
    } else {
      // Reception/TCO — store in customRecs
      set(s=>({...s,customRecs:(s.customRecs||[]).map(r=>
        r.id===id?{...r,compliance,...topLevel}:r
      )}));
    }
    setCompM(null);
  }
  function saveHistoricalLeave(id, days) {
    set(s=>({...s,historicalLeave:{...(s.historicalLeave||{}),[id]:days}}));
    setHistM(null);
  }
  function saveShadowing(id, shadowingDone) {
    set(s=>({...s,nurses:(s.nurses||NURSES_BASE).map(n=>n.id===id?{...n,shadowingDone}:n)}));
    setShadowM(null);
  }
  function saveRecStatus(id, recStatus) {
    set(s=>({...s,recStatuses:{...(s.recStatuses||{}),[id]:recStatus}}));
    setRecStatusM(null);
  }
  function saveEditStaff(person, updates) {
    const isNurse = !!(person.area || person.status);
    const hrsChanged = updates.hrs !== (person.hrs || 37.5);
    const effectiveDate = updates.effectiveDate || new Date().toISOString().split("T")[0];

    // Record hours change in history if hours actually changed
    let hrsHistoryUpdate = {};
    if (hrsChanged) {
      const existing = person.hrsHistory || [];
      // Seed history with original hours if this is the first change
      const seed = existing.length === 0
        ? [{ hrs: person.hrs||37.5, from: "2025-01-01", changedBy:"original contract", note:"" }]
        : existing;
      const newEntry = { hrs: updates.hrs, from: effectiveDate, changedBy: user?.name||"Unknown", note: updates.hrsChangeNote||"" };
      hrsHistoryUpdate = { hrsHistory: [...seed, newEntry].sort((a,b)=>a.from.localeCompare(b.from)) };
    }

    // Strip UI-only fields before saving (startDate is a real field, keep it)
    const { effectiveDate: _ed, hrsChangeNote: _n, ...cleanUpdates } = updates;
    const logMsg = hrsChanged
      ? `✏️ ${person.name}: hours changed ${person.hrs||37.5}h → ${updates.hrs}h/wk (effective ${effectiveDate})`
      : `✏️ ${person.name}: details updated`;

    if (isNurse) {
      set(s=>({...s,
        nurses:(s.nurses||NURSES_BASE).map(n=>n.id===person.id?{...n,...cleanUpdates,...hrsHistoryUpdate}:n),
        ...addLog(s,user?.name||"Unknown","role",logMsg)
      }));
    } else {
      set(s=>({...s,
        customRecs:(s.customRecs||[]).map(r=>r.id===person.id?{...r,...cleanUpdates,...hrsHistoryUpdate}:r),
        ...addLog(s,user?.name||"Unknown","role",logMsg)
      }));
    }
    setEditStaffM(null);
  }
  function addStaff(f) {
    const id = (f.type==="nurse"?"n_":"r_")+f.name.toLowerCase().replace(/\s+/g,"_")+"_"+Date.now().toString().slice(-4);
    const uId = "u_"+id;
    const userRecord = {id:uId, staffId:id, name:f.name, site:f.site, role:"staff", pin:"", email:f.email||"", active:true};
    const commonFields = {
      ...(f.phone?{phone:f.phone}:{}),
      ...(f.email?{email:f.email}:{}),
      ...(f.startDate?{startDate:f.startDate}:{}),
      ...(f.extraSites?.length?{extraSites:f.extraSites}:{}),
      note: f.notes||undefined,
    };
    if (f.type==="nurse") {
      const newNurse = {id,name:f.name,site:f.site,area:f.area,status:f.status,hrs:f.hrs,...commonFields,
        ...(f.workDays?.length<5?{workDays:f.workDays}:{}),
        ...(Object.keys(f.sched||{}).length>0?{sched:f.sched}:{}),
      };
      set(s=>{
        const ns = {...s,nurses:[...(s.nurses||NURSES_BASE),newNurse],users:[...(s.users||[]),userRecord],...(f.payrollAdded?{payrollConfirmed:{...(s.payrollConfirmed||{}),[id]:true}}:{}),...addLog(s,user?.name||"Unknown","role",`➕ Added nurse: ${f.name} at ${SITES[f.site]?.name}${f.payrollAdded?"":" ⚠️ payroll pending"}`)};
        if (f.proRatedEntitlement!=null) ns.individualEntitlements={...(s.individualEntitlements||{}),[id]:{days:f.proRatedEntitlement,_lockedByContract:false}};
        return ns;
      });
    } else {
      const typeLabel = f.type==="tco"?"TCO":f.type==="marketing"?"Marketing":f.type==="admin"?"Admin":f.type==="manager"?"Manager":"Reception";
      const newRec = {id,name:f.name,site:f.site,hrs:f.hrs,type:typeLabel,...commonFields,recStatus:"active",
        ...(f.workDays?.length<5?{workDays:f.workDays}:{}),
        ...(Object.keys(f.sched||{}).length>0?{sched:f.sched}:{}),
      };
      set(s=>{
        const ns = {...s,customRecs:[...(s.customRecs||[]),newRec],users:[...(s.users||[]),userRecord],...addLog(s,user?.name||"Unknown","role",`➕ Added ${typeLabel}: ${f.name} at ${SITES[f.site]?.name}`)};
        if (f.proRatedEntitlement!=null) ns.individualEntitlements={...(s.individualEntitlements||{}),[id]:{days:f.proRatedEntitlement,_lockedByContract:false}};
        return ns;
      });
    }
    if (f.email?.trim()) {
      // v11.3 — visible feedback for invite send. Previously this was
      // .catch(()=>{}) — silent failure meant managers had no idea if the
      // invite went out. Now: sending state during the request, success
      // toast for ~5s, persistent failed state with Resend Now action.
      const inviteUserData = {...userRecord, site: SITES[f.site]?.name||f.site};
      setInviteStatus({state:"sending", name: f.name, email: f.email});
      sendInviteEmail(inviteUserData, state).then(r => {
        if (r.ok) {
          setInviteStatus({state:"sent", name: f.name, email: f.email});
          set(s=>({...s,userInvitedAt:{...(s.userInvitedAt||{}),[uId]:new Date().toISOString()}}));
          // v11.4 — extended to 10s (was 5s) so it doesn't disappear before
          // the manager's eye returns to it after closing the form.
          setTimeout(() => setInviteStatus(s => s?.state === "sent" ? null : s), 10000);
        } else {
          setInviteStatus({
            state:"failed",
            name: f.name,
            email: f.email,
            reason: r.error || "Unknown error",
            userRecord: inviteUserData,
            userId: uId,
          });
          // No auto-dismiss for failures — user needs to act.
        }
      }).catch(err => {
        setInviteStatus({
          state:"failed",
          name: f.name,
          email: f.email,
          reason: err?.message || "Network error",
          userRecord: inviteUserData,
          userId: uId,
        });
      });
    }
    setAddStaffM(false);
  }
  function archiveStaff(person) {
    if (person.area||person.status) {
      set(s=>({...s,nurses:(s.nurses||NURSES_BASE).map(n=>n.id===person.id?{...n,archived:true,archivedAt:new Date().toISOString()}:n),...addLog(s,user?.name||"Unknown","role",`Archived: ${person.name}`)}));
    } else {
      set(s=>({...s,customRecs:(s.customRecs||[]).map(r=>r.id===person.id?{...r,archived:true,archivedAt:new Date().toISOString()}:r),...addLog(s,user?.name||"Unknown","role",`📦 Archived: ${person.name}`)}));
    }
    setArchiveConfirm(null);
  }
  function restoreStaff(person) {
    if (person.area||person.status) {
      set(s=>({...s,nurses:(s.nurses||NURSES_BASE).map(n=>n.id===person.id?{...n,archived:false,archivedAt:undefined}:n),...addLog(s,user?.name||"Unknown","role",`♻️ Restored: ${person.name}`)}));
    } else {
      set(s=>({...s,customRecs:(s.customRecs||[]).map(r=>r.id===person.id?{...r,archived:false,archivedAt:undefined}:r),...addLog(s,user?.name||"Unknown","role",`♻️ Restored: ${person.name}`)}));
    }
  }

  const allRecs = [...RECS_BASE,...(state.customRecs||[])].filter(r=>!r.archived);

  const sections = [
    {t:"Clinicians",    items:getClinicians(state).filter(c=>!c._deleted&&match(c))},
    {t:"Nursing Team",  items:nurses.filter(n=>!n.maternity&&!n.archived&&match(n))},
    {t:"On Maternity",  items:nurses.filter(n=>n.maternity&&match(n))},
    {t:"Reception & TCO",items:allRecs.filter(match)},
  ];

  function removeClinician(id) {
    if (!window.confirm("Remove this clinician from the rota? This cannot be undone.")) return;
    const isBase = CLINICIANS_BASE.map(c=>c.id).includes(id);
    if (isBase) {
      set(s=>({...s,customClinicians:[...(s.customClinicians||[]).filter(c=>c.id!==id),{id,_deleted:true}]}));
    } else {
      set(s=>({...s,customClinicians:(s.customClinicians||[]).filter(c=>c.id!==id)}));
    }
  }

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6,flexWrap:"wrap",gap:12}}>
        <div>
          <h3 style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading,marginBottom:3}}>Staff Records</h3>
          <div style={{fontSize:12,color:C.dim,maxWidth:480}}>Add and manage nurses, receptionists and admin. Update qualifications, compliance roles, and hours. For clinician schedules use the Clinicians tab.</div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search…" style={{...IS,maxWidth:160,marginBottom:0}}/>
          {isPrincipal&&<button onClick={()=>setAddStaffM(true)} style={{...BTN(C.teal),padding:"9px 18px",fontSize:13,fontWeight:700,display:"flex",alignItems:"center",gap:6}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add staff member
          </button>}
        </div>
      </div>
      <div style={{display:"flex",gap:6,marginBottom:16,flexWrap:"wrap"}}>
        {[["all","All"],...Object.entries(SITES).map(([id,s])=>[id,s.name||id])].map(([id,l])=>(
          <button key={id} onClick={()=>setSite(id)} style={{padding:"5px 14px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${site===id?(SITES[id]?.color||C.teal):C.border}`,background:site===id?(SITES[id]?.color||C.teal)+"18":"transparent",color:site===id?(SITES[id]?.color||C.teal):C.mid}}>{l}</button>
        ))}
      </div>
      {sections.map(({t,items})=>items.length?(
        <div key={t} style={{marginBottom:20}}>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>{t} ({items.length})</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(230px,1fr))",gap:8}}>
            {items.map(s=>{
              const sd=SITES[s.site],st=s.status?STATUS[s.status]:null;
              const elevRole=state.staffRoles?.[s.id];
              const histDays = getHistDays(state, s.id);
              const comp = s.compliance||{};
              const compFlags = [comp.firstAider&&"First Aider",comp.fireMarshal&&"Fire Marshal",comp.safeguarding&&"Safeguarding"].filter(Boolean);
              const keyholder = s.keyholder||comp.keyholder;
              const shadowDone = s.shadowingDone||0;
              const shadowReq  = s.shadowingRequired||0;
              const showShadow = st?.shadowing && shadowReq>0;
              const shadowDone100 = showShadow && shadowDone>=shadowReq;
              const isRec = s.type==="Reception"||s.type==="TCO";
              const recSt = s.recStatus||state.recStatuses?.[s.id];
              const recStatusObj = recSt ? REC_STATUS[recSt] : null;
              return (
                <div key={s.id} style={{...CS,borderColor:s.maternity?"#d97706"+"44":s.moving?C.teal+"44":C.border}}>
                  <div style={{fontWeight:600,fontSize:13,color:C.text,marginBottom:2}}>{s.name}</div>
                  <div style={{fontSize:11,color:sd?.color,marginBottom:6}}>{sd?.name} · {s.type||s.area||"Reception"}{s.hrs?` · ${s.hrs}h`:""}</div>
                  <div style={{display:"flex",gap:3,flexWrap:"wrap",marginBottom:6}}>
                    {st&&<Pill c={st.color} t={st.label} sm/>}
                    {recStatusObj&&<Pill c={recStatusObj.color} t={recStatusObj.label} sm/>}
                    {s.headNurse&&<Pill c={C.warn} t="Head Nurse" sm/>}
                    {s.maternity&&<Pill c="#d97706" t={s.maternityType==="paternity"?"Paternity":"Maternity"} sm/>}
                    {s.maternity&&s.maternityReturn&&<span style={{fontSize:9,color:C.dim,background:"rgba(245,197,24,0.08)",border:"1px solid #fed7aa",borderRadius:3,padding:"1px 6px"}}>Exp. return {new Date(s.maternityReturn+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short"})}</span>}
                    {s.moving&&<Pill c={C.teal} t={`→ ${SITES[s.moving]?.name}`} sm/>}
                    {s.ra&&<Pill c={C.brook} t="RA" sm/>}
                    {s.iv&&<Pill c={C.danger} t="IV" sm/>}
                    {s.noOT&&<Pill c={C.warn} t="No OT" sm/>}
                    {keyholder&&<Pill c={C.warn} t="Keyholder" sm/>}
                    {s.qualifiedNurse&&<Pill c={C.teal} t="Qual. Nurse" sm/>}
                    {elevRole&&elevRole!=="staff"&&<Pill c={C.teal} t={elevRole==="managerLite"?"Acting Mgr":elevRole==="headNurse"?"Head Nurse+":elevRole==="admin"?"Admin":elevRole==="manager"?"Manager":"Role+"} sm/>}
                    {(state.welfareAccess||{})[s.id]&&<Pill c="#7c3aed" t="💜 Welfare" sm/>}
                    {((state.extraQuals||{})[s.id]||{}).xray&&<Pill c="#d97706" t="X-Ray" sm/>}
                    {((state.extraQuals||{})[s.id]||{}).oralHyg&&<Pill c={C.green} t="Oral Hyg" sm/>}
                    {((state.extraQuals||{})[s.id]||{}).implants&&<Pill c={C.brook} t="Implants" sm/>}
                    {((state.extraQuals||{})[s.id]||{}).dualArea&&<Pill c="#7c3aed" t="MOS+GDS" sm/>}
                  </div>
                  {compFlags.length>0&&<div style={{display:"flex",gap:3,flexWrap:"wrap",marginBottom:6}}>{compFlags.map(f=><Pill key={f} c={C.teal} t={f} sm/>)}</div>}
                  {showShadow&&(
                    <div style={{marginBottom:6}}>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:shadowDone100?C.green:C.warn,fontWeight:700,marginBottom:2}}>
                        <span>Shadowing: {shadowDone}/{shadowReq} sessions</span>
                        {shadowDone100&&<span>Ready to solo</span>}
                      </div>
                      <div style={{height:5,background:C.border,borderRadius:99,overflow:"hidden"}}>
                        <div style={{width:`${Math.min((shadowDone/shadowReq)*100,100)}%`,height:"100%",background:shadowDone100?C.green:C.warn,borderRadius:99}}/>
                      </div>
                    </div>
                  )}
                  {histDays>0&&<div style={{fontSize:10,color:C.dim,marginBottom:4}}>Pre-system leave: {histDays}d</div>}
                  {s.workDays&&s.workDays.length<5&&<div style={{fontSize:10,color:C.teal,marginBottom:4,fontWeight:600}}>Works: {s.workDays.join(", ")}</div>}
                  {s.phone&&<div style={{fontSize:10,color:C.teal,marginBottom:2}}>{s.phone}</div>}
                  {s.email&&<div style={{fontSize:10,color:C.mid,marginBottom:4}}>{s.email}</div>}
                  {s.note&&<div style={{fontSize:10,color:C.dim,marginBottom:6}}>{s.note}</div>}
                  {s.status==="newStarter"&&!(state.payrollConfirmed||{})[s.id]&&<div style={{fontSize:10,fontWeight:700,background:"rgba(245,197,24,0.1)",color:"#f5c518",borderRadius:6,padding:"3px 7px",marginBottom:4}}>⚠️ Not yet on payroll</div>}
                  {(isPrincipal||(user?.role==="manager"||user?.role==="managerLite"))&&(
                    <div style={{display:"flex",gap:4,paddingTop:6,borderTop:`1px solid ${C.border2}`,flexWrap:"wrap"}}>
                      {isPrincipal&&st&&<button onClick={()=>setTrainM(s)} style={{...BTN(C.teal,true),fontSize:10,padding:"3px 8px"}}>Level</button>}
                      {isPrincipal&&t!=="Clinicians"&&<button onClick={()=>setRoleM(s)} style={{...BTN(C.brook,true),fontSize:10,padding:"3px 8px"}}>Role</button>}
                      {isPrincipal&&isRec&&<button onClick={()=>setRecStatusM(s)} style={{...BTN(C.brook,true),fontSize:10,padding:"3px 8px"}}>Competency</button>}
                      {isPrincipal&&(t==="Nursing Team"||t==="Reception & TCO"||t==="Clinicians")&&<button onClick={()=>setCompM(s)} style={{...BTN(C.lime,true),fontSize:10,padding:"3px 8px"}}>Compliance</button>}
                      {/* Clinician-specific actions */}
                      {t==="Clinicians"&&isPrincipal&&(
                        <button onClick={()=>window.dispatchEvent(new CustomEvent("omni:navigate",{detail:{tab:"admin",subTab:"clinicians"}}))}
                          style={{...BTN(C.teal,true),fontSize:10,padding:"3px 8px"}}>Edit schedule →</button>
                      )}
                      {t==="Clinicians"&&isPrincipal&&(
                        <button onClick={()=>removeClinician(s.id)} style={{...BTN(C.danger,true),fontSize:10,padding:"3px 8px"}}>Remove</button>
                      )}
                      <button onClick={()=>setHistM(s)} style={{...BTN(C.teal,true),fontSize:10,padding:"3px 8px"}}>Entitlement</button>
                      {isPrincipal&&showShadow&&<button onClick={()=>setShadowM(s)} style={{...BTN(C.warn,true),fontSize:10,padding:"3px 8px"}}>Shadow+</button>}
                      {isPrincipal&&t==="Nursing Team"&&<button onClick={()=>setTransferM(s)} style={{...BTN(C.sale,true),fontSize:10,padding:"3px 8px"}}> Transfer</button>}
                      {isPrincipal&&s.status==="newStarter"&&!(state.payrollConfirmed||{})[s.id]&&<button onClick={()=>set(st=>({...st,payrollConfirmed:{...(st.payrollConfirmed||{}),[s.id]:true}}))} style={{...BTN(C.warn,true),fontSize:10,padding:"3px 8px"}}>Payroll</button>}
                      {isPrincipal&&(t==="Nursing Team"||t==="On Maternity")&&<button onClick={()=>{setMType(s.maternityType||"maternity");setMStart(s.maternityStart||"");setMReturn(s.maternityReturn||"");setMKIT(s.maternityKIT||0);setMNote(s.maternityNote||"");setMActive(s.maternity!==false);setMaternityM(s);}} style={{...BTN("#d97706",true),fontSize:10,padding:"3px 8px"}}>👶 Mat/Pat</button>}
                      {isPrincipal&&t!=="Clinicians"&&<button onClick={()=>setEditStaffM(s)} style={{...BTN(C.teal,true),fontSize:10,padding:"3px 8px"}}>✏️ Edit</button>}
                      {isPrincipal&&(t==="Nursing Team"||t==="Reception & TCO")&&<button onClick={()=>setArchiveConfirm(s)} style={{...BTN(C.danger,true),fontSize:10,padding:"3px 8px"}}>Archive</button>}
                      {/* Conversation Report */}
                      <button onClick={()=>setConvM(s)} style={{...BTN(C.warn,true),fontSize:10,padding:"3px 8px"}}>Report</button>
                      {/* PulsePeople hook */}
                      {pulsePeopleEnabled
                        ? <button onClick={()=>alert("Opening staff file: "+s.name)} style={{...BTN(C.teal,true),fontSize:10,padding:"3px 8px"}}>📁 Staff File</button>
                        : <button title="Available with Omni People" onClick={()=>alert("Staff Files are part of Omni People — coming soon.")} style={{...BTN(C.muted,true),fontSize:10,padding:"3px 8px",opacity:0.6}}>📁 File</button>
                      }
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ):null)}

      {/* Training / Level modal */}
      {trainM&&(
        <Overlay onClose={()=>setTrainM(null)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Update Level</div>
          <div style={{fontSize:13,color:C.dim,marginBottom:16}}>{trainM.name}</div>
          <TrainingModalInner nurse={trainM} onSave={updateTraining} onClose={()=>setTrainM(null)}/>
        </Overlay>
      )}

      {/* Compliance modal */}
      {compM&&(
        <Overlay onClose={()=>setCompM(null)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Compliance Roles</div>
          <div style={{fontSize:13,color:C.dim,marginBottom:16}}>{compM.name}</div>
          <ComplianceModalInner nurse={compM} onSave={saveCompliance} state={state} onSaveExtraQuals={(id,eq)=>set(s=>({...s,extraQuals:{...(s.extraQuals||{}),[id]:eq}}))} onClose={()=>setCompM(null)}/>
        </Overlay>
      )}

      {/* Historical leave modal */}
      {histM&&(
        <HistoricalLeaveModal person={histM} state={state} set={set} onClose={()=>setHistM(null)}/>
      )}

      {/* Shadowing modal */}
      {shadowM&&(
        <Overlay onClose={()=>setShadowM(null)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Update Shadowing</div>
          <div style={{fontSize:13,color:C.dim,marginBottom:16}}>{shadowM.name}</div>
          <ShadowingModalInner nurse={shadowM} onSave={saveShadowing} onClose={()=>setShadowM(null)}/>
        </Overlay>
      )}

      {/* Edit staff modal — hours, phone, email, notes */}
      {editStaffM&&<EditStaffModal person={editStaffM} onSave={saveEditStaff} onClose={()=>setEditStaffM(null)}/>}

      {roleM&&<RoleModal person={roleM} state={state} set={set} onSave={updateRole} onClose={()=>setRoleM(null)}/>}

      {maternityM&&(
        <Overlay onClose={()=>setMaternityM(null)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,fontWeight:700,color:C.heading,marginBottom:4}}>👶 Maternity / Paternity</div>
          <div style={{fontSize:13,color:C.dim,marginBottom:16}}>{maternityM.name}</div>
          <label style={LB}>Leave type</label>
          <div style={{display:"flex",gap:8,marginBottom:14}}>
            {[["maternity","Maternity"],["paternity","Paternity"],["adoption","Adoption"]].map(([v,l])=>(
              <button key={v} onClick={()=>setMType(v)} style={{flex:1,padding:"8px",borderRadius:8,border:`1.5px solid ${mType===v?"#d97706":C.border}`,background:mType===v?"#fff7ed":"transparent",color:mType===v?"#d97706":C.mid,fontFamily:"inherit",fontSize:12,fontWeight:600,cursor:"pointer"}}>{l}</button>
            ))}
          </div>
          <label style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,cursor:"pointer"}}>
            <input type="checkbox" checked={mActive} onChange={e=>setMActive(e.target.checked)} style={{width:18,height:18,accentColor:"#d97706"}}/>
            <span style={{fontSize:13,fontWeight:700,color:"#d97706"}}>Currently on {mType} leave</span>
          </label>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
            <div><label style={LB}>Leave start date</label>
              <input type="date" value={mStart} onChange={e=>setMStart(e.target.value)} style={IS}/>
            </div>
            <div><label style={LB}>Expected return</label>
              <input type="date" value={mReturn} onChange={e=>setMReturn(e.target.value)} style={IS}/>
            </div>
          </div>
          <div style={{marginBottom:12}}>
            <label style={LB}>KIT days used <span style={{color:C.dim,fontWeight:400}}>(keeping in touch)</span></label>
            <input type="number" min="0" max="10" value={mKIT} onChange={e=>setMKIT(+e.target.value)} style={{...IS,width:80}}/>
            <div style={{fontSize:10,color:C.dim,marginTop:4}}>Maximum 10 KIT days permitted by law</div>
          </div>
          <div style={{marginBottom:16}}>
            <label style={LB}>Notes</label>
            <textarea value={mNote} onChange={e=>setMNote(e.target.value)} rows={2}
              placeholder="e.g. SMP enhanced to 12 weeks full pay per contract, informal contact agreed…"
              style={{...IS,resize:"none"}}/>
          </div>
          {mReturn&&mActive&&(
            <div style={{background:"rgba(245,197,24,0.08)",border:"1px solid #fed7aa",borderRadius:8,padding:"8px 12px",marginBottom:14,fontSize:11,color:"#f5c518"}}>
              Expected return: <strong>{new Date(mReturn+"T12:00:00").toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</strong>
            </div>
          )}
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>{
              set(s=>({...s, nurses:(s.nurses||NURSES_BASE).map(n=>n.id===maternityM.id?{
                ...n, maternity:mActive, maternityType:mType,
                maternityStart:mStart, maternityReturn:mReturn,
                maternityKIT:mKIT, maternityNote:mNote,
              }:n)}));
              setMaternityM(null);
            }} style={{...BTN("#d97706"),flex:1,padding:"11px 0"}}>Save</button>
            <button onClick={()=>setMaternityM(null)} style={BTN(C.dim,true)}>Cancel</button>
          </div>
        </Overlay>
      )}

      {/* Site Transfer modal */}
      {transferM&&(
        <Overlay onClose={()=>setTransferM(null)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Site Transfer</div>
          <div style={{fontSize:13,color:C.dim,marginBottom:20}}>{transferM.name} · currently at {SITES[transferM.site]?.name}</div>
          <SiteTransferInner person={transferM} onSave={transferSite} onClose={()=>setTransferM(null)}/>
        </Overlay>
      )}

      {/* Rec status modal */}
      {recStatusM&&<RecStatusModal person={recStatusM} current={recStatusM.recStatus||state.recStatuses?.[recStatusM.id]} onSave={saveRecStatus} onClose={()=>setRecStatusM(null)}/>}

      {/* ── College Days ── */}
      {isPrincipal&&(
        <div style={{marginTop:32,paddingTop:24,borderTop:`1px solid ${C.border}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12,flexWrap:"wrap",gap:10}}>
            <div>
              <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:2}}>College Days</div>
              <div style={{fontSize:12,color:C.dim}}>Trainees at different colleges on different days — block them on the rota automatically.</div>
            </div>
            <button onClick={()=>setCollegeM(true)} style={{...BTN(C.teal),fontSize:12,padding:"7px 14px"}}>+ Add College Days</button>
          </div>
          {(()=>{
            const allSt = [...(state.nurses||NURSES_BASE),...(state.customStaff||[])];
            const groups = {};
            (state.collegeDates||[]).slice().sort((a,b)=>a.date.localeCompare(b.date)).forEach(e=>{
              if (!groups[e.groupLabel]) groups[e.groupLabel]=[];
              groups[e.groupLabel].push(e);
            });
            return Object.keys(groups).length===0 ? (
              <div style={{...CS,textAlign:"center",padding:32,color:C.dim}}>
                <div style={{fontSize:28,marginBottom:8}}></div>
                <div style={{fontSize:13,color:C.soft}}>No college days scheduled yet</div>
                <div style={{fontSize:12,color:C.dim,marginTop:4}}>Add them above — each trainee can have their own college, day, and term dates.</div>
              </div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {Object.entries(groups).map(([grpLabel,entries])=>{
                  const names=[...new Set(entries.flatMap(e=>e.staffIds))].map(id=>{const n=allSt.find(x=>x.id===id);return n?.name||id;}).join(", ");
                  const first=entries[0].date, last=entries[entries.length-1].date;
                  const upcoming=entries.filter(e=>e.date>=new Date().toISOString().split("T")[0]);
                  return (
                    <div key={grpLabel} style={{...CS,padding:"12px 16px",display:"flex",justifyContent:"space-between",alignItems:"flex-start",borderLeft:`3px solid ${C.teal}`}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:C.teal,marginBottom:3}}>{grpLabel}</div>
                        <div style={{fontSize:11,color:C.dim}}>{entries.length} date{entries.length!==1?"s":""} · {new Date(first+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short"})} → {new Date(last+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}{upcoming.length>0?` · ${upcoming.length} upcoming`:""}</div>
                        <div style={{fontSize:12,color:C.mid,marginTop:3}}>{names}</div>
                      </div>
                      <button onClick={()=>set(s=>({...s,collegeDates:(s.collegeDates||[]).filter(e=>e.groupLabel!==grpLabel)}))} style={{...BTN(C.danger,true),fontSize:10,padding:"3px 8px",flexShrink:0}}>Remove</button>
                    </div>
                  );
                })}
              </div>
            );
          })()}
          {collegeM&&<CollegeDatesModal state={state} set={set} onClose={()=>setCollegeM(false)}/>}
        </div>
      )}

      {/* Add staff modal */}
      {addStaffM&&<AddStaffModal onSave={addStaff} onClose={()=>setAddStaffM(false)}/>}

      {/* v11.3 — Staff-add invite status toast.
          Bottom-right, non-modal so it doesn't block other actions. Auto-
          dismisses on success after 5s; persists on failure with Resend
          action. Replaces the previous silent-fail behaviour where managers
          had no feedback after adding a staff member with email. */}
      {inviteStatus && (
        <div style={{
          position:"fixed",
          top:24,
          left:"50%",
          transform:"translateX(-50%)",
          zIndex:9000,
          minWidth:380, maxWidth:480,
          background: inviteStatus.state==="failed" ? "linear-gradient(135deg, #c93838 0%, #b33030 100%)"
                    : inviteStatus.state==="sent"   ? "linear-gradient(135deg, #00b884 0%, #00a070 100%)"
                    : "linear-gradient(135deg, #3aad98 0%, #2d8d7d 100%)",
          border:"none",
          borderRadius:14,
          padding:"16px 20px",
          boxShadow:"0 12px 40px rgba(0,0,0,0.5), 0 2px 8px rgba(0,0,0,0.3)",
          fontFamily:"'Instrument Sans',system-ui,sans-serif",
          animation:"inviteToastSlideIn 0.45s cubic-bezier(0.2, 0.9, 0.3, 1) both",
          color:"#fff",
        }}>
          <style>{`
            @keyframes inviteToastSpin{to{transform:rotate(360deg)}}
            @keyframes inviteToastSlideIn{from{opacity:0;transform:translateX(-50%) translateY(-20px);}to{opacity:1;transform:translateX(-50%) translateY(0);}}
          `}</style>
          {inviteStatus.state==="sending" && (
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{
                width:18,height:18,borderRadius:"50%",
                border:"2.5px solid rgba(255,255,255,0.3)",
                borderTopColor:"#fff",
                animation:"inviteToastSpin 0.8s linear infinite",
              }}/>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:"#fff",marginBottom:2}}>Sending invite to {inviteStatus.name}…</div>
                <div style={{fontSize:12,color:"rgba(255,255,255,0.85)"}}>{inviteStatus.email}</div>
              </div>
            </div>
          )}
          {inviteStatus.state==="sent" && (
            <div>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
                <div style={{
                  width:24,height:24,borderRadius:"50%",
                  background:"rgba(255,255,255,0.22)",
                  display:"flex",alignItems:"center",justifyContent:"center",
                  fontSize:14,fontWeight:700,color:"#fff",
                }}>✓</div>
                <div style={{fontSize:15,fontWeight:700,color:"#fff"}}>Invite sent to {inviteStatus.name}</div>
              </div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.85)",marginLeft:34}}>{inviteStatus.email}</div>
            </div>
          )}
          {inviteStatus.state==="failed" && (
            <div>
              <div style={{display:"flex",alignItems:"flex-start",gap:10,marginBottom:8}}>
                <div style={{
                  width:24,height:24,borderRadius:"50%",
                  background:"rgba(255,255,255,0.22)",
                  display:"flex",alignItems:"center",justifyContent:"center",
                  fontSize:14,fontWeight:700,color:"#fff",flexShrink:0,
                }}>⚠</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:14,fontWeight:700,color:"#fff",marginBottom:2}}>Invite failed for {inviteStatus.name}</div>
                  <div style={{fontSize:12,color:"rgba(255,255,255,0.85)",marginBottom:2}}>{inviteStatus.email}</div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.7)",fontStyle:"italic"}}>{inviteStatus.reason}</div>
                </div>
              </div>
              <div style={{display:"flex",gap:8,marginTop:12}}>
                <button
                  disabled={inviteStatus.state === "sending"}
                  onClick={async ()=>{
                    if (inviteStatus.state === "sending") return;
                    setInviteStatus(s => ({...s, state:"sending"}));
                    const r = await sendInviteEmail(inviteStatus.userRecord, state);
                    if (r.ok) {
                      setInviteStatus({state:"sent", name: inviteStatus.name, email: inviteStatus.email});
                      set(s=>({...s,userInvitedAt:{...(s.userInvitedAt||{}),[inviteStatus.userId]:new Date().toISOString()}}));
                      setTimeout(() => setInviteStatus(s => s?.state === "sent" ? null : s), 10000);
                    } else {
                      setInviteStatus(s => ({...s, state:"failed", reason: r.error || "Unknown error"}));
                    }
                  }} style={{
                    flex:1, padding:"8px 0", fontSize:12, fontWeight:700,
                    borderRadius:8, border:"1px solid rgba(255,255,255,0.5)",
                    background:"rgba(255,255,255,0.18)",
                    color:"#fff",
                    cursor:"pointer", fontFamily:"inherit",
                  }}>↻ Resend Now</button>
                <button onClick={()=>setInviteStatus(null)} style={{
                  padding:"8px 14px", fontSize:12, fontWeight:600,
                  borderRadius:8, border:"1px solid rgba(255,255,255,0.4)",
                  background:"transparent", color:"rgba(255,255,255,0.85)",
                  cursor:"pointer", fontFamily:"inherit",
                }}>Dismiss</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Leavers / Archived Staff ── */}
      {isPrincipal&&(()=>{
        const archivedNurses = (state.nurses||NURSES_BASE).filter(n=>n.archived);
        const archivedRecs   = [...RECS_BASE,...(state.customRecs||[])].filter(r=>r.archived);
        const leavers = [...archivedNurses,...archivedRecs];
        if (leavers.length===0) return null;
        return (
          <div style={{marginTop:28,paddingTop:20,borderTop:`1px solid ${C.border}`}}>
            <button onClick={()=>setShowLeavers(v=>!v)}
              style={{display:"flex",alignItems:"center",gap:8,background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",padding:0,marginBottom:showLeavers?16:0}}>
              <span style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:18,color:C.dim}}>{showLeavers?"▼":"▶"}</span>
              <span style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:18,color:C.dim}}>Leavers & Archived</span>
              <span style={{fontSize:11,color:C.muted,background:C.border,borderRadius:99,padding:"1px 8px"}}>{leavers.length}</span>
            </button>
            {showLeavers&&(
              <div>
                <div style={{fontSize:12,color:C.dim,marginBottom:12}}>All leave records, Bradford scores and history are preserved. Principal can restore at any time.</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:10}}>
                  {leavers.map(s=>{
                    const sickRecs = (state.sick||[]).filter(r=>r.staffId===s.id);
                    const leaveRecs = (state.leave[s.id]||[]).filter(l=>l.status==="approved");
                    const bf = sickRecs.length*sickRecs.length*sickRecs.reduce((a,r)=>a+r.days,0);
                    return (
                      <div key={s.id} style={{...CS,opacity:0.75,borderStyle:"dashed"}}>
                        <div style={{fontWeight:600,fontSize:13,color:C.mid,marginBottom:2}}>{s.name}</div>
                        <div style={{fontSize:11,color:C.dim,marginBottom:6}}>{SITES[s.site]?.name} · {s.type||s.area||"Staff"}</div>
                        <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:8}}>
                          {sickRecs.length>0&&<Pill c={C.warn} t={`${sickRecs.length} sick records`} sm/>}
                          {leaveRecs.length>0&&<Pill c={C.teal} t={`${leaveRecs.length}d leave`} sm/>}
                          {bf>0&&<Pill c={BF_BANDS.find(b=>bf>=b.min&&bf<=b.max)?.color||C.dim} t={`BF ${bf}`} sm/>}
                          {s.archivedAt&&<Pill c={C.dim} t={`archived ${new Date(s.archivedAt).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}`} sm/>}
                        </div>
                        <div style={{display:"flex",gap:6}}>
                          <button onClick={()=>setConvM(s)} style={{...BTN(C.warn,true),fontSize:10,padding:"3px 8px"}}>Report</button>
                          <button onClick={()=>restoreStaff(s)} style={{...BTN(C.teal,true),fontSize:10,padding:"3px 8px"}}>♻️ Restore</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Conversation Report modal */}
      {convM&&<ConversationReport person={convM} state={state} set={set} onClose={()=>setConvM(null)}/>}

      {/* Archive confirm */}
      {archiveConfirm&&(
        <Overlay onClose={()=>setArchiveConfirm(null)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:8}}>Archive {archiveConfirm.name}?</div>
          <div style={{fontSize:13,color:C.dim,marginBottom:20}}>They will be removed from the active directory and rota. Leave records are kept. This can be undone by Conor.</div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>archiveStaff(archiveConfirm)} style={{...BTN(C.danger),flex:1,padding:"11px 0"}}>Yes, archive</button>
            <button onClick={()=>setArchiveConfirm(null)} style={BTN(C.dim,true)}>Cancel</button>
          </div>
        </Overlay>
      )}
    </div>
  );
}

function SiteTransferInner({person, onSave, onClose}) {
  const [newSite, setNewSite] = useState("");
  const [when, setWhen] = useState("now");
  const [effectiveDate, setEffectiveDate] = useState("");
  const otherSites = Object.entries(SITES).filter(([id])=>id!==person.site);
  return (
    <div>
      <div style={{marginBottom:14}}>
        <label style={LB}>Transfer to</label>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {otherSites.map(([id,s])=>(
            <button key={id} onClick={()=>setNewSite(id)} style={{padding:"12px 14px",borderRadius:10,border:`1.5px solid ${newSite===id?s.color:C.border}`,background:newSite===id?s.color+"15":"transparent",color:newSite===id?s.color:C.soft,fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer",textAlign:"left",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span>{s.name}</span>
              {newSite===id&&<span style={{fontSize:11}}>Selected</span>}
            </button>
          ))}
        </div>
      </div>
      <div style={{marginBottom:14}}>
        <label style={LB}>When</label>
        <div style={{display:"flex",gap:8}}>
          {[["now","Immediate"],["future","Future date"]].map(([v,l])=>(
            <button key={v} onClick={()=>setWhen(v)} style={{flex:1,padding:"9px",borderRadius:10,border:`1px solid ${when===v?C.teal:C.border}`,background:when===v?C.tealPale:"transparent",color:when===v?C.teal:C.mid,fontFamily:"inherit",fontSize:12,fontWeight:600,cursor:"pointer"}}>{l}</button>
          ))}
        </div>
      </div>
      {when==="future"&&(
        <div style={{marginBottom:14}}>
          <label style={LB}>Effective date</label>
          <input type="date" value={effectiveDate} onChange={e=>setEffectiveDate(e.target.value)} style={IS}/>
        </div>
      )}
      {newSite&&when==="future"&&effectiveDate&&(
        <div style={{background:"rgba(245,197,24,0.08)",border:`1px solid ${C.warn}44`,borderRadius:8,padding:10,marginBottom:14,fontSize:12,color:C.warn}}>
          Scheduled transfer — {person.name} will show as "moving to {SITES[newSite]?.name}" on their card until the effective date. Make sure to update the rota manually.
        </div>
      )}
      {newSite&&when==="now"&&(
        <div style={{background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:8,padding:10,marginBottom:14,fontSize:12,color:C.green}}>
          {person.name} will be moved from {SITES[person.site]?.name} to {SITES[newSite]?.name} immediately. Their leave balance and training record carry over.
        </div>
      )}
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>newSite&&(when==="now"||effectiveDate)&&onSave(person.id,newSite,when==="future"?effectiveDate:null)} style={{...BTN(newSite?C.teal:C.muted),flex:1,padding:"11px 0"}}>Confirm Transfer</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </div>
  );
}

/* ── Inline modal content helpers ── */
function TrainingModalInner({nurse, onSave, onClose}) {
  const [status,setStatus] = useState(nurse.status||"newStarter");
  const [shadowReq,setShadowReq] = useState(nurse.shadowingRequired||0);
  const st = STATUS[status];
  return (
    <div>
      <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:16}}>
        {Object.entries(STATUS).map(([k,v])=>(
          <button key={k} onClick={()=>setStatus(k)} style={{padding:"12px 14px",borderRadius:10,border:`1px solid ${status===k?v.color:C.border}`,background:status===k?v.color+"18":"transparent",color:status===k?v.color:C.soft,fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer",textAlign:"left",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span>{v.label}</span><span style={{fontSize:11,color:C.dim}}>Score {(v.score*100).toFixed(0)}</span>
          </button>
        ))}
      </div>
      {st?.shadowing&&(
        <div style={{marginBottom:16}}>
          <label style={LB}>Shadowing sessions required before going solo</label>
          <input type="number" min="0" max="20" value={shadowReq} onChange={e=>setShadowReq(+e.target.value)} style={IS}/>
          <div style={{fontSize:11,color:C.dim,marginTop:4}}>Set to 0 for experienced joiners who just need onboarding</div>
        </div>
      )}
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>onSave(nurse.id,status,st?.shadowing?shadowReq:undefined)} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Update</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </div>
  );
}

function ComplianceModalInner({nurse, onSave, onClose, state, onSaveExtraQuals}) {
  const [comp,setComp] = useState(nurse.compliance||{firstAider:false,fireMarshal:false,safeguarding:false});
  const [keyholder,setKeyholder] = useState(!!nurse.keyholder);
  const [qualifiedNurse,setQualifiedNurse] = useState(!!nurse.qualifiedNurse);
  const [eq, setEq] = useState((state?.extraQuals||{})[nurse.id]||{});
  const tog = k => setComp(p=>({...p,[k]:!p[k]}));
  const togEq = k => setEq(p=>({...p,[k]:!p[k]}));
  const isRec = nurse.type==="Reception"||nurse.type==="TCO";
  // Clinicians have type like "GDS","MOS","Therapist","FD","Principal/GDS" etc.
  const isClinician = !isRec && (
    (nurse.type||"").includes("GDS")||(nurse.type||"").includes("MOS")
    ||(nurse.type||"").includes("Therapist")||(nurse.type||"").includes("FD")
    ||(nurse.type||"").includes("Hygienist")||!!nurse.isPartner
  );
  const hideNurseQuals = isRec || isClinician;
  return (
    <div>
      <div style={{marginBottom:16}}>
        <label style={LB}>Compliance roles held</label>
        {[["firstAider","First Aider"],["fireMarshal","Fire Marshal"],["safeguarding","Level 3 Safeguarding / Child Protection"]].map(([k,label])=>(
          <label key={k} style={{display:"flex",alignItems:"center",gap:10,fontSize:13,color:C.soft,cursor:"pointer",padding:"10px 0",borderBottom:`1px solid ${C.border2}`}}>
            <input type="checkbox" checked={!!comp[k]} onChange={()=>tog(k)} style={{width:16,height:16,accentColor:C.teal}}/>
            {label}
          </label>
        ))}
      </div>
      {!hideNurseQuals&&(
      <div style={{marginBottom:16}}>
        <label style={LB}>Extra Qualifications</label>
        {EXTRA_QUALS.map(q=>(
          <label key={q.id} style={{display:"flex",alignItems:"center",gap:10,fontSize:13,color:C.soft,cursor:"pointer",padding:"10px 0",borderBottom:`1px solid ${C.border2}`}}>
            <input type="checkbox" checked={!!eq[q.id]} onChange={()=>togEq(q.id)} style={{width:16,height:16,accentColor:q.color}}/>
            {q.icon} {q.label}
          </label>
        ))}
        <label style={{display:"flex",alignItems:"center",gap:10,fontSize:13,color:C.soft,cursor:"pointer",padding:"10px 0",borderBottom:`1px solid ${C.border2}`}}>
          <input type="checkbox" checked={!!eq.dualArea} onChange={()=>togEq("dualArea")} style={{width:16,height:16,accentColor:"#7c3aed"}}/>
          Dual qualified — MOS & GDS
        </label>
      </div>
      )}
      {isRec&&(
      <div style={{marginBottom:16,padding:"10px 14px",background:"rgba(58,173,152,0.06)",border:"1px solid rgba(58,173,152,0.2)",borderRadius:10}}>
        <label style={LB}>Nursing qualification</label>
        <label style={{display:"flex",alignItems:"center",gap:10,fontSize:13,color:C.soft,cursor:"pointer",padding:"6px 0"}}>
          <input type="checkbox" checked={qualifiedNurse} onChange={e=>setQualifiedNurse(e.target.checked)} style={{width:16,height:16,accentColor:C.teal}}/>
          Qualified dental nurse — can cover as nurse if needed
        </label>
        {qualifiedNurse&&(
          <div style={{fontSize:11,color:C.teal,marginTop:4}}>Iris will flag this person as potential nurse cover during shortages</div>
        )}
      </div>
      )}
      <div style={{marginBottom:20}}>
        <label style={LB}>Keyholder</label>
        <label style={{display:"flex",alignItems:"center",gap:10,fontSize:13,color:C.soft,cursor:"pointer"}}>
          <input type="checkbox" checked={keyholder} onChange={e=>setKeyholder(e.target.checked)} style={{width:16,height:16,accentColor:C.warn}}/>
          Has keys to one or more sites
        </label>
      </div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>{onSave(nurse.id,{...comp,keyholder,qualifiedNurse});if(onSaveExtraQuals)onSaveExtraQuals(nurse.id,eq);}} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Save</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </div>
  );
}

function HistoricalLeaveInner({person, currentVal, currentEntit, onSave, onClose}) {
  const defaultEntit = ent(person.hrs||37.5);
  const [days,setDays] = useState(currentVal||0);
  const [customEntit,setCustomEntit] = useState(currentEntit!==undefined?currentEntit:"");
  const displayEntit = customEntit!==""?Number(customEntit):defaultEntit;
  return (
    <div>
      <div style={{background:C.tealPale,border:`1px solid ${C.tealBorder}`,borderRadius:10,padding:12,marginBottom:16,fontSize:12,color:C.teal}}>
        ℹ️ Set days already taken before they were added to the system, and optionally override their holiday entitlement.
      </div>
      <div style={{marginBottom:14}}>
        <label style={LB}>Individual holiday entitlement (days)</label>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <input type="number" min="0" step="0.5" placeholder={`Default: ${defaultEntit}`} value={customEntit} onChange={e=>setCustomEntit(e.target.value)} style={{...IS,flex:1}}/>
          {customEntit!==""&&<button onClick={()=>setCustomEntit("")} style={{...BTN(C.dim,true),padding:"8px 10px",fontSize:11}}>Reset to default</button>}
        </div>
        <div style={{fontSize:11,color:C.dim,marginTop:4}}>Leave blank to use default ({defaultEntit}d based on {person.hrs||37.5}h contract)</div>
      </div>
      <div style={{marginBottom:16}}>
        <label style={LB}>Days already taken (before system start)</label>
        <input type="number" min="0" max={displayEntit} value={days} onChange={e=>setDays(+e.target.value)} style={IS}/>
        <div style={{fontSize:11,color:C.dim,marginTop:4}}>These count against their {displayEntit}d entitlement</div>
      </div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>onSave(person.id,days,customEntit!==""?Number(customEntit):undefined)} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Save</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </div>
  );
}

function ShadowingModalInner({nurse, onSave, onClose}) {
  const [done,setDone] = useState(nurse.shadowingDone||0);
  const req = nurse.shadowingRequired||0;
  return (
    <div>
      <div style={{marginBottom:8,fontSize:13,color:C.text}}>Target: <strong>{req} sessions</strong></div>
      <div style={{height:8,background:C.border,borderRadius:99,overflow:"hidden",marginBottom:16}}>
        <div style={{width:`${Math.min((done/Math.max(req,1))*100,100)}%`,height:"100%",background:done>=req?C.green:C.warn,borderRadius:99}}/>
      </div>
      <label style={LB}>Sessions completed so far</label>
      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:16}}>
        <button onClick={()=>setDone(Math.max(0,done-1))} style={{...BTN(C.dim,true),padding:"8px 14px",fontSize:16}}>−</button>
        <input type="number" min="0" value={done} onChange={e=>setDone(+e.target.value)} style={{...IS,width:80,textAlign:"center"}}/>
        <button onClick={()=>setDone(done+1)} style={{...BTN(C.teal,true),padding:"8px 14px",fontSize:16}}>+</button>
      </div>
      {done>=req&&req>0&&<div style={{background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:10,padding:10,marginBottom:16,fontSize:12,color:C.green,fontWeight:700}}>Target reached — ready to go solo. Update their level to confirm.</div>}
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>onSave(nurse.id,done)} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Save</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </div>
  );
}


/* ══════════════════════════════════
   ATTENDANCE & HOURS
══════════════════════════════════ */
function Attendance({state,set,user,isPrincipal,initialSubTab,onSubTabConsumed}) {
  const [subTab,setSubTab] = useState("overtime");
  const [otM,setOtM]       = useState(null);
  const [declineM,setDeclineM] = useState(null);
  const [efM,setEfM]       = useState(null);
  const [payMonth,setPayMonth] = useState(()=>{const n=new Date();return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,"0")}`});
  const [latM,setLatM]     = useState(null);
  const [keysM,setKeysM]   = useState(false);
  const [welfareM,setWelfareM] = useState(null);

  useEffect(()=>{
    if(initialSubTab){setSubTab(initialSubTab);if(onSubTabConsumed)onSubTabConsumed();}
  },[initialSubTab]);

  const allStaff = [
    ...(state.nurses||NURSES_BASE).filter(n=>!n.maternity&&!n.archived),
    ...RECS_BASE.filter(r=>!r.archived),
    ...(state.customRecs||[]).filter(r=>!r.archived),
  ];
  const overtime    = state.overtime||[];
  const earlyFinish = state.earlyFinish||[];
  const lateness    = state.latenessLog||[];
  const welfare     = state.welfareLog||[];
  const lostKeys    = state.lostKeys||[];

  const canManage = isPrincipal||user?.role==="manager"||user?.role==="managerLite"||user?.role==="headNurse";

  function approveOT(id, asTOIL=false) {
    const toilPolicy = state.toilPolicy||{};
    const r = (state.overtime||[]).find(x=>x.id===id);
    if (!r) return;
    if (asTOIL && toilPolicy.enabled) {
      // Bank the hours as TOIL
      const toilFrag = bankTOIL(
        r.staffId, r.name, r.hrs,
        r.date, `Overtime: ${r.reason||"extra work"}`,
        user?.name||"Manager", state
      );
      set(s=>({
        ...s,
        overtime:(s.overtime||[]).map(x=>x.id===id
          ?{...x,status:"approved",bankedAsTOIL:true,toilHrs:r.hrs}:x),
        toilBalances:{...(s.toilBalances||{}),...toilFrag.toilBalances},
      }));
    } else {
      set(s=>({...s,overtime:(s.overtime||[]).map(x=>x.id===id
        ?{...x,status:"approved",bankedAsTOIL:false}:x)}));
    }
  }
  function declineOT(id, reason) {
    set(s=>({...s,overtime:(s.overtime||[]).map(r=>r.id===id?{...r,status:"declined",declineReason:reason}:r)}));
    setDeclineM(null);
  }
  function approveEF(id) {
    set(s=>({...s,earlyFinish:(s.earlyFinish||[]).map(r=>r.id===id?{...r,status:"approved"}:r)}));
  }

  const pendingOT = overtime.filter(r=>r.status==="pending").length;
  const pendingEF = earlyFinish.filter(r=>r.status==="pending").length;

  // Welfare triggers — auto-detect
  const welfareTriggers = allStaff.map(s=>{
    const triggers = checkWelfareTrigger(s.id, state);
    const alreadyLogged = welfare.some(w=>w.staffId===s.id&&!w.closed);
    return triggers.length>0 && !alreadyLogged ? {staff:s,triggers} : null;
  }).filter(Boolean);

  const pendingExtra  = (state.extraShiftRequests||[]).filter(r=>r.status==="pending").length;
  const pendingUnpaid = (state.unpaidLeaveRequests||[]).filter(r=>r.status==="pending").length;

  const SUBTABS = [
    {id:"overtime",   label:"Overtime",    badge:pendingOT},
    {id:"toil",       label:"Time in Lieu"},
    {id:"earlyfinish",label:"Early Finish", badge:pendingEF},
    {id:"lateness",   label:"Lateness"},
    {id:"extrashift", label:"Extra Shifts", badge:pendingExtra},
    {id:"unpaid",     label:"Unpaid Leave", badge:pendingUnpaid},
    {id:"welfare",    label:"Wellbeing",   badge:welfareTriggers.length},
    {id:"lostkeys",   label:"Lost Keys"},
    {id:"otprefs",    label:"OT Prefs"},
    {id:"payroll",    label:"OT Export"},
  ];

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:12}}>
        <h3 style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading}}>Attendance & Hours</h3>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {canManage&&<button onClick={()=>setLatM(allStaff[0])} style={BTN(C.danger,true)}>+ Log Issue</button>}
          <button onClick={()=>setKeysM(true)} style={BTN(C.warn,true)}>Lost Keys</button>
        </div>
      </div>

      <div style={{display:"flex",gap:2,marginBottom:16,borderBottom:`1px solid ${C.border}`,overflowX:"auto"}}>
        {SUBTABS.map(t=>(
          <button key={t.id} onClick={()=>setSubTab(t.id)} style={{background:"transparent",border:"none",padding:"9px 14px",cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:600,whiteSpace:"nowrap",color:subTab===t.id?C.teal:C.mid,borderBottom:subTab===t.id?`2px solid ${C.teal}`:"2px solid transparent",display:"flex",alignItems:"center",gap:5}}>
            {t.label}{t.badge>0&&<span style={{background:C.danger,color:"#fff",borderRadius:99,fontSize:9,fontWeight:800,padding:"1px 5px"}}>{t.badge}</span>}
          </button>
        ))}
      </div>

      {/* OVERTIME */}
      {subTab==="overtime"&&(
        <div>
          {welfareTriggers.length===0&&overtime.length===0&&(
            <div style={{...CS,textAlign:"center",padding:32,color:C.dim}}>No overtime logged yet</div>
          )}
          {pendingOT>0&&<div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Pending ({pendingOT})</div>}
          {overtime.filter(r=>r.status==="pending").map(r=>{
            const toilEnabled = (state.toilPolicy||{}).enabled;
            const toilRate = (state.toilPolicy||{}).rate||1;
            const toilHrs = Math.round(r.hrs * toilRate * 10)/10;
            const nurseRate = state.settings?.nurseHourlyRate||14;
            const payCost = Math.round(r.hrs * nurseRate * 100)/100;
            return (
            <div key={r.id} style={{...CS,marginBottom:8,padding:"12px 16px",
              borderLeft:`3px solid ${C.warn}`}}>
              <div style={{display:"flex",justifyContent:"space-between",
                alignItems:"flex-start",flexWrap:"wrap",gap:8,marginBottom:toilEnabled?10:0}}>
                <div>
                  <div style={{fontWeight:700,color:C.text,fontSize:13}}>{r.name}</div>
                  <div style={{fontSize:11,color:C.dim,marginTop:2}}>
                    {r.date} · {r.hrs}h extra · {r.reason}
                    {r.clinicianId&&` — ${getClinicians(state).find(c=>c.id===r.clinicianId)?.name||r.clinicianId}`}
                    {r.notes&&<span style={{color:C.mid,marginLeft:6,fontStyle:"italic"}}>· "{r.notes}"</span>}
                  </div>
                </div>
                {!toilEnabled&&canManage&&(
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={()=>approveOT(r.id,false)}
                      style={{...BTN(C.green,true),fontSize:12,padding:"5px 12px"}}>
                      Approve
                    </button>
                    <button onClick={()=>setDeclineM({id:r.id,name:r.name})}
                      style={{...BTN(C.danger,true),fontSize:12,padding:"5px 12px"}}>
                      ✕ Decline
                    </button>
                  </div>
                )}
              </div>
              {toilEnabled&&canManage&&(
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:C.dim,
                    letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6}}>
                    How to compensate?
                  </div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    <button onClick={()=>approveOT(r.id,false)}
                      style={{fontSize:11,fontWeight:700,padding:"6px 14px",
                      borderRadius:8,cursor:"pointer",fontFamily:"inherit",
                      background:"rgba(58,173,152,0.12)",
                      border:"1px solid rgba(58,173,152,0.3)",color:C.teal}}>
                      Pay — £{payCost.toFixed(2)}
                    </button>
                    <button onClick={()=>approveOT(r.id,true)}
                      style={{fontSize:11,fontWeight:700,padding:"6px 14px",
                      borderRadius:8,cursor:"pointer",fontFamily:"inherit",
                      background:"rgba(244,114,182,0.12)",
                      border:"1px solid rgba(244,114,182,0.3)",color:"#f472b6"}}>
                      Bank as TOIL — {toilHrs}h
                      {toilRate>1&&<span style={{fontSize:9,opacity:0.7,marginLeft:4}}>
                        ({toilRate}x)
                      </span>}
                    </button>
                    <button onClick={()=>setDeclineM({id:r.id,name:r.name})}
                      style={{...BTN(C.danger,true),fontSize:11,padding:"6px 12px"}}>
                      ✕ Decline
                    </button>
                  </div>
                  <div style={{fontSize:10,color:"rgba(244,114,182,0.5)",marginTop:5}}>
                    TOIL expires{" "}
                    {(state.toilPolicy?.expiry==="6months")
                      ?"in 6 months":"at end of leave year"}
                    {" "}· Current policy: {toilRate===1?"straight time":"1.5× enhanced"}
                  </div>
                </div>
              )}
            </div>
            );
          })}
          {overtime.filter(r=>r.status!=="pending").length>0&&(
            <div>
              <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10,marginTop:16}}>History</div>
              {[...overtime].filter(r=>r.status!=="pending").sort((a,b)=>b.date.localeCompare(a.date)).map(r=>(
                <div key={r.id} style={{...CS,marginBottom:6,padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <span style={{fontWeight:600,fontSize:12,color:C.text}}>{r.name}</span>
                    <span style={{fontSize:11,color:C.dim,marginLeft:8}}>{r.date} · {r.hrs}h · {r.reason}{r.status==="declined"&&r.declineReason&&<span style={{color:C.danger}}> — {r.declineReason}</span>}</span>
                  </div>
                  <Pill c={r.status==="approved"?C.green:C.danger} t={r.status==="approved"?"Approved":"✕ Declined"} sm/>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TIME IN LIEU (TOIL) */}
      {subTab==="toil"&&(()=>{
        const policy = state.toilPolicy||{};
        const balances = state.toilBalances||{};
        const allStaffToil = [...(state.nurses||NURSES_BASE).filter(n=>!n.maternity&&!n.archived),...RECS_BASE];
        const staffWithBalance = allStaffToil
          .map(s=>{
            const frags = balances[s.id]||[];
            const total = frags.reduce((sum,f)=>sum+(f.hrs||0),0);
            return {...s,toilHrs:total,frags};
          })
          .filter(s=>s.toilHrs>0)
          .sort((a,b)=>b.toilHrs-a.toilHrs);
        const totalLiability = staffWithBalance.reduce((s,p)=>s+p.toilHrs,0);
        const nurseRate = state.settings?.nurseHourlyRate||14;
        const liabilityGBP = Math.round(totalLiability*nurseRate);
        return (
          <div>
            {!policy.enabled&&(
              <div style={{...CS,marginBottom:16,padding:"20px 24px",
                border:"1px solid rgba(244,114,182,0.25)",
                background:"rgba(244,114,182,0.05)"}}>
                <div style={{fontSize:14,fontWeight:700,color:"#f472b6",marginBottom:6}}>
                  TOIL is not enabled
                </div>
                <div style={{fontSize:12,color:C.dim,lineHeight:1.6,marginBottom:14}}>
                  Time in Lieu lets staff bank overtime hours instead of being paid.
                  When you approve overtime you choose: pay out or bank as TOIL.
                  Staff use their balance to take time off later.
                </div>
                <div style={{fontSize:12,color:C.dim,marginBottom:14}}>
                  To enable TOIL go to{" "}
                  <strong style={{color:C.teal}}>Admin → Settings → TOIL & Leave</strong>
                  {" "}and set your policy (rate, expiry, approval rules).
                </div>
                <div style={{fontSize:11,color:"rgba(255,255,255,0.25)"}}>
                  Once enabled the "Bank as TOIL" button appears when approving overtime.
                </div>
              </div>
            )}
            {policy.enabled&&(
              <div>
                <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap"}}>
                  <div style={{...CS,flex:1,minWidth:140,padding:"14px 18px"}}>
                    <div style={{fontSize:10,fontWeight:700,color:C.dim,
                      textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>
                      Total TOIL banked
                    </div>
                    <div style={{fontSize:26,fontWeight:700,
                      color:totalLiability>0?"#f472b6":C.teal,letterSpacing:"-0.02em"}}>
                      {totalLiability.toFixed(1)}h
                    </div>
                    {totalLiability>0&&(
                      <div style={{fontSize:11,color:C.dim,marginTop:2}}>
                        ~£{liabilityGBP} liability if paid out
                      </div>
                    )}
                  </div>
                  <div style={{...CS,flex:1,minWidth:140,padding:"14px 18px"}}>
                    <div style={{fontSize:10,fontWeight:700,color:C.dim,
                      textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>
                      Policy
                    </div>
                    <div style={{fontSize:12,color:C.text,lineHeight:1.6}}>
                      {policy.rate===1?"Straight time":"1.5× enhanced"} ·{" "}
                      {policy.expiry==="6months"?"Expires 6 months":"Expires year end"}
                    </div>
                    <div style={{fontSize:11,color:C.dim,marginTop:2}}>
                      Max {policy.maxBalanceHrs||37.5}h ·{" "}
                      {policy.payoutOnLeaving?"Paid out on leaving":"Not paid out on leaving"}
                    </div>
                  </div>
                </div>
                {staffWithBalance.length===0&&(
                  <div style={{...CS,textAlign:"center",padding:32,color:C.dim}}>
                    No TOIL balances yet — approve overtime and choose "Bank as TOIL"
                  </div>
                )}
                {staffWithBalance.map(s=>{
                  const alertHrs = policy.alertThresholdHrs||7.5;
                  const isHigh = s.toilHrs>=alertHrs;
                  return (
                    <div key={s.id} style={{...CS,marginBottom:8,padding:"12px 16px",
                      borderLeft:`3px solid ${isHigh?"#f472b6":C.teal}`}}>
                      <div style={{display:"flex",justifyContent:"space-between",
                        alignItems:"center",flexWrap:"wrap",gap:8}}>
                        <div>
                          <div style={{fontWeight:700,fontSize:13,color:C.text}}>
                            {s.name}
                          </div>
                          <div style={{fontSize:11,color:C.dim,marginTop:2}}>
                            {(SITES[s.site]?.name)||s.site} ·{" "}
                            {s.frags.length} session{s.frags.length!==1?"s":""} banked
                          </div>
                        </div>
                        <div style={{textAlign:"right"}}>
                          <div style={{fontSize:18,fontWeight:700,
                            color:isHigh?"#f472b6":C.teal}}>
                            {s.toilHrs.toFixed(1)}h
                          </div>
                          {isHigh&&(
                            <div style={{fontSize:9,color:"#f472b6",fontWeight:700,
                              textTransform:"uppercase",letterSpacing:"0.06em"}}>
                              High balance
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* EARLY FINISH */}
      {subTab==="earlyfinish"&&(
        <div>
          {earlyFinish.length===0&&<div style={{...CS,textAlign:"center",padding:32,color:C.dim}}>No early finishes logged</div>}
          {earlyFinish.filter(r=>r.status==="pending").map(r=>(
            <div key={r.id} style={{...CS,marginBottom:8,padding:"12px 16px",borderLeft:`3px solid ${C.lime}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
              <div>
                <div style={{fontWeight:700,color:C.text,fontSize:13}}>{r.name}</div>
                <div style={{fontSize:11,color:C.dim,marginTop:2}}>{r.date} · {r.hrsEarly}h early · {r.reason}</div>
              </div>
              {canManage&&<button onClick={()=>approveEF(r.id)} style={{...BTN(C.green,true),fontSize:12,padding:"5px 12px"}}>Note</button>}
            </div>
          ))}
          {earlyFinish.filter(r=>r.status!=="pending").sort((a,b)=>b.date.localeCompare(a.date)).map(r=>(
            <div key={r.id} style={{...CS,marginBottom:6,padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <span style={{fontWeight:600,fontSize:12,color:C.text}}>{r.name}</span>
                <span style={{fontSize:11,color:C.dim,marginLeft:8}}>{r.date} · {r.hrsEarly}h early · {r.reason}</span>
              </div>
              <Pill c={C.green} t="Noted" sm/>
            </div>
          ))}
        </div>
      )}

      {/* LATENESS */}
      {subTab==="lateness"&&(
        <div>
          {canManage&&(
            <div style={{marginBottom:16}}>
              <label style={LB}>Log for</label>
              <select style={IS} onChange={e=>setLatM(allStaff.find(s=>s.id===e.target.value)||null)}>
                <option value="">— select staff member —</option>
                {allStaff.map(s=><option key={s.id} value={s.id}>{s.name} · {SITES[s.site]?.name}</option>)}
              </select>
            </div>
          )}
          {/* ── Pattern analysis across all staff ── */}
          {(()=>{
            const allPatterns = allStaff.flatMap(s=>{
              const p = analyseLatenessPatterns(s.id, state);
              return p.patterns.filter(pt=>pt.sev==="amber").map(pt=>({...pt, staffName:s.name, staffId:s.id}));
            });
            if(!allPatterns.length) return null;
            return (
              <div style={{background:"rgba(245,166,35,0.06)",border:"1px solid rgba(245,166,35,0.2)",borderRadius:12,padding:"14px 16px",marginBottom:16}}>
                <div style={{fontSize:10,fontWeight:700,color:"#f5a623",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>⚠ Welfare patterns detected</div>
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {allPatterns.slice(0,5).map((p,i)=>(
                    <div key={i} style={{display:"flex",gap:10,alignItems:"flex-start"}}>
                      <div style={{fontSize:11,fontWeight:700,color:"rgba(255,255,255,0.7)",minWidth:90,flexShrink:0}}>{p.staffName.split(" ")[0]}</div>
                      <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",lineHeight:1.4}}>{p.msg}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
          {lateness.length===0&&<div style={{...CS,textAlign:"center",padding:32,color:C.dim}}>No lateness logged yet</div>}
          {/* ── Individual records with inline pattern tag ── */}
          {[...lateness].sort((a,b)=>b.date.localeCompare(a.date)).map(r=>{
            const personPatterns = analyseLatenessPatterns(r.staffId, state);
            const dayOfWeek = new Date(r.date+"T12:00").toLocaleDateString("en-GB",{weekday:"long"});
            const dayPattern = personPatterns.patterns.find(p=>p.type==="day_of_week"&&p.day===dayOfWeek);
            return (
            <div key={r.id} style={{...CS,marginBottom:8,padding:"12px 16px",borderLeft:`3px solid ${r.type==="unauthorised"?C.danger:personPatterns.riskLevel==="medium"||personPatterns.riskLevel==="high"?"#f5a623":C.warn}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:4}}>
                <div>
                  <div style={{fontWeight:700,color:C.text,fontSize:13}}>{r.name}</div>
                  <div style={{fontSize:11,color:C.dim,marginTop:2}}>
                    {r.date} · {r.type==="unauthorised"?"Unauthorised absence":`${r.minsLate} mins late`} · {r.reason}
                    {r.notes&&` — ${r.notes}`}
                  </div>
                  <div style={{fontSize:10,color:C.muted,marginTop:2}}>Logged by {r.loggedBy}</div>
                  {dayPattern&&<div style={{fontSize:10,color:"#f5a623",marginTop:4,display:"flex",alignItems:"center",gap:4}}><span>(!)</span><span>{dayPattern.msg}</span></div>}
                </div>
                <Pill c={r.type==="unauthorised"?C.danger:personPatterns.riskLevel==="medium"||personPatterns.riskLevel==="high"?"#f5a623":C.warn} t={r.type==="unauthorised"?"Unauth.":personPatterns.riskLevel==="medium"||personPatterns.riskLevel==="high"?"! Pattern":"Late"} sm/>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* EXTRA SHIFTS */}
      {subTab==="extrashift"&&(
        <div>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12}}>Extra Shift Requests</div>
          {(state.extraShiftRequests||[]).length===0&&<div style={{...CS,textAlign:"center",padding:28,color:C.dim,fontSize:13}}>No extra shift requests yet.</div>}
          {[...(state.extraShiftRequests||[])].reverse().map(r=>{
            const col = r.status==="approved"?C.green:r.status==="declined"?C.danger:C.warn;
            return (
              <div key={r.id} style={{...CS,marginBottom:8,padding:"12px 14px",borderLeft:`3px solid ${col}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                <div>
                  <div style={{fontWeight:700,fontSize:13,color:C.text}}>{r.name}</div>
                  <div style={{fontSize:11,color:C.dim,marginTop:2}}>{SITES[r.site]?.name} · {CAL.days.find(d=>d.date===r.date)?.label||r.date}{r.note?` · ${r.note}`:""}</div>
                </div>
                <div style={{display:"flex",gap:5,alignItems:"center"}}>
                  <Pill c={col} t={r.status} sm/>
                  {r.status==="pending"&&canManage&&(
                    <>
                      <button onClick={()=>set(s=>({...s,extraShiftRequests:(s.extraShiftRequests||[]).map(x=>x.id===r.id?{...x,status:"approved"}:x)}))} style={{...BTN(C.green,true),fontSize:11,padding:"4px 10px"}}>✓</button>
                      <button onClick={()=>set(s=>({...s,extraShiftRequests:(s.extraShiftRequests||[]).map(x=>x.id===r.id?{...x,status:"declined"}:x)}))} style={{...BTN(C.danger,true),fontSize:11,padding:"4px 10px"}}>X</button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* UNPAID LEAVE */}
      {subTab==="unpaid"&&(
        <div>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12}}>Unpaid Leave Requests</div>
          {(state.unpaidLeaveRequests||[]).length===0&&<div style={{...CS,textAlign:"center",padding:28,color:C.dim,fontSize:13}}>No unpaid leave requests.</div>}
          {[...(state.unpaidLeaveRequests||[])].reverse().map(r=>{
            const col = r.status==="approved"?C.green:r.status==="declined"?C.danger:C.warn;
            const prevCount = (state.unpaidLeaveRequests||[]).filter(x=>x.staffId===r.staffId&&x.id!==r.id).length;
            return (
              <div key={r.id} style={{...CS,marginBottom:8,padding:"12px 14px",borderLeft:`3px solid ${col}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:8}}>
                  <div>
                    <div style={{fontWeight:700,fontSize:13,color:C.text,marginBottom:2}}>{r.name} {prevCount>=2&&<Pill c={C.warn} t="Repeat requester" sm/>}</div>
                    <div style={{fontSize:11,color:C.dim}}>{SITES[r.site]?.name} · {r.dates?.length}d · {r.reason||"No reason"}</div>
                    <div style={{fontSize:10,color:C.dim,marginTop:3}}>{r.dates?.sort().slice(0,3).map(d=>CAL.days.find(x=>x.date===d)?.label||d).join(", ")}{r.dates?.length>3?` +${r.dates.length-3} more`:""}</div>
                  </div>
                  <div style={{display:"flex",gap:5,alignItems:"center",flexWrap:"wrap"}}>
                    <Pill c={col} t={r.status} sm/>
                    {r.status==="pending"&&canManage&&(
                      <>
                        <button onClick={()=>set(s=>({...s,unpaidLeaveRequests:(s.unpaidLeaveRequests||[]).map(x=>x.id===r.id?{...x,status:"approved"}:x)}))} style={{...BTN(C.green,true),fontSize:11,padding:"4px 10px"}}>Approve</button>
                        <button onClick={()=>set(s=>({...s,unpaidLeaveRequests:(s.unpaidLeaveRequests||[]).map(x=>x.id===r.id?{...x,status:"declined"}:x)}))} style={{...BTN(C.danger,true),fontSize:11,padding:"4px 10px"}}>✕ Decline</button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* WELFARE */}
      {subTab==="welfare"&&(
        <div>
          {welfareTriggers.length>0&&(
            <div style={{marginBottom:20}}>
              <div style={{fontSize:10,fontWeight:700,color:C.danger,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Auto-detected triggers ({welfareTriggers.length})</div>
              {welfareTriggers.map(({staff,triggers})=>(
                <div key={staff.id} style={{...CS,marginBottom:8,padding:"12px 16px",borderLeft:`3px solid ${C.danger}`}}>
                  <div style={{fontWeight:700,color:C.text,fontSize:13,marginBottom:4}}>{staff.name}</div>
                  {triggers.map(t=><div key={t} style={{fontSize:11,color:C.danger,marginBottom:2}}>• {t}</div>)}
                  {canManage&&<button onClick={()=>setWelfareM(staff)} style={{...BTN(C.teal,true),marginTop:8,fontSize:11,padding:"5px 12px"}}>Log welfare meeting</button>}
                </div>
              ))}
            </div>
          )}
          {welfare.length>0&&(
            <div>
              <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Welfare log</div>
              {[...welfare].sort((a,b)=>b.date.localeCompare(a.date)).map(w=>(
                <div key={w.id} style={{...CS,marginBottom:8,padding:"12px 16px",borderLeft:`3px solid ${w.closed?C.green:C.warn}`}}>
                  <div style={{fontWeight:700,color:C.text,fontSize:13}}>{w.name}</div>
                  <div style={{fontSize:11,color:C.dim,marginTop:2}}>{w.date} · Trigger: {w.trigger}</div>
                  <div style={{fontSize:11,color:C.mid,marginTop:2}}>Outcome: {w.outcome}</div>
                  {w.followUpDate&&<div style={{fontSize:11,color:C.warn,marginTop:2}}>Follow-up: {w.followUpDate}</div>}
                  {!w.closed&&canManage&&<button onClick={()=>set(s=>({...s,welfareLog:(s.welfareLog||[]).map(x=>x.id===w.id?{...x,closed:true}:x)}))} style={{...BTN(C.green,true),marginTop:8,fontSize:11,padding:"4px 10px"}}>Close</button>}
                </div>
              ))}
            </div>
          )}
          {welfare.length===0&&welfareTriggers.length===0&&<div style={{...CS,textAlign:"center",padding:32,color:C.dim}}>No welfare records</div>}
        </div>
      )}

      {/* OT PREFS — who wants more hours */}
      {subTab==="otprefs"&&(
        <div>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12}}>Staff OT & Hours Preferences</div>
          <div style={{fontSize:12,color:C.mid,marginBottom:14}}>Who's open to overtime, why, and when — set by each nurse from their own portal</div>
          {(()=>{
            const allStaffOT = [...(state.nurses||NURSES_BASE).filter(n=>!n.maternity&&!n.archived),...RECS_BASE];
            const prefs = state.staffOTPrefs||{};
            const avMap = state.weeklyAvailability||{};
            const todayStr = new Date().toISOString().split("T")[0];
            const ws = getWeekStart(todayStr);
            // Group by preference
            const happy = allStaffOT.filter(s=>prefs[s.id]?.general==="happy");
            const sometimes = allStaffOT.filter(s=>prefs[s.id]?.general==="sometimes");
            const noThanks = allStaffOT.filter(s=>prefs[s.id]?.general==="noThanks");
            const notSet = allStaffOT.filter(s=>!prefs[s.id]?.general);
            // This week availability
            const thisWeekAvail = allStaffOT.filter(s=>{
              const avKey=`${s.id}_${ws}`;
              return (avMap[avKey]||[]).length>0;
            });
            return (
              <div>
                {thisWeekAvail.length>0&&(
                  <div style={{...CS,marginBottom:16,borderLeft:`3px solid ${C.teal}`}}>
                    <div style={{fontSize:11,fontWeight:700,color:C.teal,marginBottom:8}}>Available for extra this week ({thisWeekAvail.length})</div>
                    {thisWeekAvail.map(s=>{
                      const avKey=`${s.id}_${ws}`;
                      const days=(avMap[avKey]||[]);
                      return (
                        <div key={s.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:`1px solid ${C.border2}`}}>
                          <div>
                            <span style={{fontSize:12,fontWeight:600,color:C.text}}>{s.name}</span>
                            <span style={{fontSize:10,color:SITES[s.site]?.color,marginLeft:6}}>{SITES[s.site]?.name}</span>
                          </div>
                          <div style={{display:"flex",gap:4}}>
                            {days.map(d=><Pill key={d} c={C.teal} t={d} sm/>)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {[["Happy to do OT",happy,C.green],["Sometimes OK",sometimes,C.warn],["Prefer not to",noThanks,C.danger]].map(([title,group,col])=>group.length>0?(
                  <div key={title} style={{...CS,marginBottom:10}}>
                    <div style={{fontSize:11,fontWeight:700,color:col,marginBottom:8}}>{title} ({group.length})</div>
                    {group.map(s=>{
                      const p=prefs[s.id]||{};
                      const reasons=[p.payReason&&"Pay",p.teamReason&&"Team",p.hoursReason&&"Hours"].filter(Boolean);
                      return (
                        <div key={s.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"5px 0",borderBottom:`1px solid ${C.border2}`}}>
                          <div>
                            <span style={{fontSize:12,fontWeight:600,color:C.text}}>{s.name}</span>
                            <span style={{fontSize:10,color:SITES[s.site]?.color,marginLeft:6}}>{SITES[s.site]?.name}</span>
                          </div>
                          <div style={{display:"flex",gap:4,flexWrap:"wrap",justifyContent:"flex-end"}}>
                            {reasons.map(r=><span key={r} style={{fontSize:9,color:C.dim}}>{r}</span>)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ):null)}
                {notSet.length>0&&<div style={{fontSize:11,color:C.muted,marginTop:8}}>{notSet.length} staff haven't set a preference yet</div>}
              </div>
            );
          })()}
        </div>
      )}

      {/* LOST KEYS */}
      {subTab==="lostkeys"&&(
        <div>
          <button onClick={()=>setKeysM(true)} style={{...BTN(C.warn),marginBottom:16}}>Log Lost Keys</button>
          {lostKeys.length===0&&<div style={{...CS,textAlign:"center",padding:32,color:C.dim}}>No lost keys incidents</div>}
          {[...lostKeys].sort((a,b)=>b.date.localeCompare(a.date)).map(k=>(
            <div key={k.id} style={{...CS,marginBottom:8,padding:"12px 16px",borderLeft:`3px solid ${k.locksChanged?C.green:C.danger}`}}>
              <div style={{fontWeight:700,color:C.text,fontSize:13}}>{SITES[k.site]?.name} — {k.date}</div>
              <div style={{fontSize:11,color:C.dim,marginTop:2}}>Reported by: {k.reportedBy}</div>
              {k.notes&&<div style={{fontSize:11,color:C.mid,marginTop:2}}>{k.notes}</div>}
              <div style={{marginTop:6}}><Pill c={k.locksChanged?C.green:C.danger} t={k.locksChanged?"Locks changed":"Locks NOT changed"} sm/></div>
            </div>
          ))}
        </div>
      )}

      {subTab==="payroll"&&(()=>{
        const now = new Date();
        const months = Array.from({length:3},(_,i)=>{
          const d = new Date(now.getFullYear(),now.getMonth()-i,1);
          return {key:`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`,label:d.toLocaleDateString("en-GB",{month:"long",year:"numeric"})};
        });
        const approved = (state.overtime||[]).filter(r=>r.status==="approved"&&r.date?.startsWith(payMonth));
        const byStaff = {};
        approved.forEach(r=>{
          if (!byStaff[r.staffId]) byStaff[r.staffId]={name:r.name,hrs:0,records:[]};
          byStaff[r.staffId].hrs+=r.hrs||0;
          byStaff[r.staffId].records.push(r);
        });
        const rows = Object.values(byStaff).sort((a,b)=>b.hrs-a.hrs);
        const rate = state.settings?.locumRate||12;
        const totalHrs = rows.reduce((s,r)=>s+r.hrs,0);
        const exportCSV = () => {
          const lines = ["Name,Date,Hours,Reason,Est Cost"];
          approved.sort((a,b)=>a.date.localeCompare(b.date)).forEach(r=>{
            lines.push(`"${r.name}","${r.date}",${r.hrs},"${r.reason||""}","£${((r.hrs||0)*rate).toFixed(2)}"`);
          });
          const blob = new Blob([lines.join("\n")],{type:"text/csv"});
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href=url; a.download=`payroll-overtime-${payMonth}.csv`; a.click();
        };
        return (
          <div>
            <div style={{display:"flex",gap:8,marginBottom:16,alignItems:"center",flexWrap:"wrap"}}>
              {months.map(m=>(
                <button key={m.key} onClick={()=>setPayMonth(m.key)}
                  style={{padding:"5px 14px",borderRadius:99,fontFamily:"inherit",fontSize:12,fontWeight:600,cursor:"pointer",border:`1px solid ${payMonth===m.key?C.teal:C.border}`,background:payMonth===m.key?C.teal+"18":"transparent",color:payMonth===m.key?C.teal:C.mid}}>
                  {m.label}
                </button>
              ))}
              {rows.length>0&&<button onClick={exportCSV} style={{...BTN(C.teal,true),marginLeft:"auto",fontSize:12}}>⬇ Export CSV</button>}
            </div>
            {rows.length===0?(
              <div style={{...CS,textAlign:"center",padding:32,color:C.dim}}>No approved overtime for {months.find(m=>m.key===payMonth)?.label}</div>
            ):(
              <>
                <div style={{...CS,marginBottom:12,padding:"10px 16px",background:"rgba(0,229,160,0.08)",border:`1px solid #bbf7d0`}}>
                  <div style={{fontSize:12,fontWeight:700,color:C.green}}>{totalHrs.toFixed(1)} total approved hours · Est. £{(totalHrs*rate).toFixed(2)} at £{rate}/hr</div>
                  <div style={{fontSize:10,color:C.dim,marginTop:2}}>Based on default rate — adjust in Settings</div>
                </div>
                {rows.map(row=>(
                  <div key={row.name} style={{...CS,marginBottom:8,padding:"10px 16px"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                      <div style={{fontWeight:700,fontSize:13,color:C.text}}>{row.name}</div>
                      <div style={{fontSize:12,fontWeight:700,color:C.teal}}>{row.hrs.toFixed(1)}h · £{(row.hrs*rate).toFixed(2)}</div>
                    </div>
                    {row.records.sort((a,b)=>a.date.localeCompare(b.date)).map(r=>(
                      <div key={r.id} style={{fontSize:11,color:C.dim,display:"flex",gap:8,paddingLeft:8,borderLeft:`2px solid ${C.teal}33`,marginBottom:2}}>
                        <span>{r.date}</span><span>{r.hrs}h</span><span style={{flex:1}}>{r.reason}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </>
            )}
          </div>
        );
      })()}

      {/* MODALS */}
      {otM&&<OvertimeModal person={otM} state={state} onSave={r=>{set(s=>({...s,overtime:[...(s.overtime||[]),r]}));setOtM(null);}} onClose={()=>setOtM(null)}/>}
      {declineM&&(
        <Overlay onClose={()=>setDeclineM(null)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:18,fontWeight:700,color:C.heading,marginBottom:4}}>Decline Overtime</div>
          <div style={{fontSize:13,color:C.dim,marginBottom:16}}>{declineM.name} — select a reason</div>
          {["Hours don't match rota","Already compensated","Times need clarifying","Duplicate submission","Other"].map(reason=>(
            <button key={reason} onClick={()=>declineOT(declineM.id,reason)}
              style={{display:"block",width:"100%",textAlign:"left",padding:"10px 14px",marginBottom:8,borderRadius:8,border:`1px solid ${C.border}`,background:C.surface,fontFamily:"inherit",fontSize:13,color:C.text,cursor:"pointer"}}>
              {reason}
            </button>
          ))}
          <button onClick={()=>setDeclineM(null)} style={{...BTN(C.dim,true),width:"100%",marginTop:4}}>Cancel</button>
        </Overlay>
      )}
      {efM&&<EarlyFinishModal person={efM} onSave={r=>{set(s=>({...s,earlyFinish:[...(s.earlyFinish||[]),r]}));setEfM(null);}} onClose={()=>setEfM(null)}/>}
      {latM&&<LatenessModal person={latM} user={user} onSave={r=>{set(s=>({...s,latenessLog:[...(s.latenessLog||[]),r]}));setLatM(null);}} onClose={()=>setLatM(null)}/>}
      {keysM&&<LostKeysModal user={user} onSave={r=>{set(s=>({...s,lostKeys:[...(s.lostKeys||[]),r]}));setKeysM(false);}} onClose={()=>setKeysM(false)}/>}
      {welfareM&&(
        <Overlay onClose={()=>setWelfareM(null)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Welfare Meeting — {welfareM.name}</div>
          <WelfareLogInner staff={welfareM} user={user} onSave={r=>{set(s=>({...s,welfareLog:[...(s.welfareLog||[]),r]}));setWelfareM(null);}} onClose={()=>setWelfareM(null)}/>
        </Overlay>
      )}
    </div>
  );
}

function WelfareLogInner({staff, user, onSave, onClose}) {
  const [f,setF] = useState({date:new Date().toISOString().split("T")[0],trigger:"",outcome:"",followUpDate:"",notes:""});
  const s=(k,v)=>setF(p=>({...p,[k]:v}));
  return (
    <div>
      <div style={{marginBottom:12}}><label style={LB}>Date of meeting</label><input type="date" value={f.date} onChange={e=>s("date",e.target.value)} style={IS}/></div>
      <div style={{marginBottom:12}}><label style={LB}>Trigger / reason for meeting</label><input value={f.trigger} onChange={e=>s("trigger",e.target.value)} placeholder="e.g. Bradford hit 45, 3 absences in 12 weeks" style={IS}/></div>
      <div style={{marginBottom:12}}><label style={LB}>Outcome / agreed actions</label><input value={f.outcome} onChange={e=>s("outcome",e.target.value)} placeholder="e.g. No issues identified, monitoring period agreed" style={IS}/></div>
      <div style={{marginBottom:12}}><label style={LB}>Follow-up date (optional)</label><input type="date" value={f.followUpDate} onChange={e=>s("followUpDate",e.target.value)} style={IS}/></div>
      <div style={{marginBottom:16}}><label style={LB}>Notes</label><input value={f.notes} onChange={e=>s("notes",e.target.value)} style={IS}/></div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>onSave({...f,id:"wf"+Date.now(),staffId:staff.id,name:staff.name,site:staff.site,loggedBy:user?.name||"Manager",closed:false})} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Save</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </div>
  );
}


/* ══════════════════════════════════
   PULSE BANK
══════════════════════════════════ */
/* ══════════════════════════════════
   COVER POOL
══════════════════════════════════ */
function CoverPool({siteId, state}) {
  const site = SITES[siteId];
  const todayStr = new Date().toISOString().split("T")[0];
  const ws = getWeekStart(todayStr);
  const allStaff = [
    ...getNursesForSite(state, siteId),
    ...RECS_BASE.filter(r=>r.site===siteId),
  ];
  const DAYS = ["Mon","Tue","Wed","Thu","Fri"];
  const STANCE_LABELS = {happy:"Happy to help",sometimes:"Occasionally",prefer_not:"Prefer not"};
  const STANCE_COLORS = {happy:C.green,sometimes:C.warn,prefer_not:C.muted};

  // Extra shift requests from the new system
  const extraReqs = (state.extraShiftRequests||[]).filter(r=>r.site===siteId&&r.status==="pending");
  const shortNoticeReqs = extraReqs.filter(r=>r.shortNotice);
  const specificDateReqs = extraReqs.filter(r=>!r.shortNotice&&r.dates?.length>0);
  const generalAvailReqs = extraReqs.filter(r=>r.stance); // general stance submissions

  // Legacy weekly availability  
  const optIns = allStaff.filter(n=>{
    const avKey = `${n.id}_${ws}`;
    return (state.weeklyAvailability?.[avKey]||[]).length>0;
  }).map(n=>({...n, availDays: state.weeklyAvailability?.[`${n.id}_${ws}`]||[]}));

  const liveDay = CAL.days.find(d=>d.date>=todayStr&&!d.bh)||CAL.days[0];

  return (
    <div>
      <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:16}}>Availability Pool — {site.name}</div>

      {/* Short notice */}
      {shortNoticeReqs.length>0&&(
        <div style={{background:"rgba(255,70,70,0.08)",border:`2px solid ${C.danger}`,borderRadius:14,padding:"14px 16px",marginBottom:14}}>
          <div style={{fontWeight:700,color:C.danger,fontSize:14,marginBottom:8}}>Available — short notice now</div>
          {shortNoticeReqs.map(r=>(
            <div key={r.id} style={{display:"flex",alignItems:"center",gap:10,padding:"6px 0",borderTop:`1px solid ${C.danger}22`}}>
              <span style={{fontWeight:700,fontSize:13,color:C.text,flex:1}}>{r.name}</span>
              <span style={{fontSize:11,color:C.dim}}>{SITES[r.site]?.name}</span>
              {r.note&&<span style={{fontSize:10,color:C.mid,fontStyle:"italic"}}>"{r.note}"</span>}
            </div>
          ))}
        </div>
      )}

      {/* Specific dates offered */}
      {specificDateReqs.length>0&&(
        <div style={{...CS,marginBottom:14}}>
          <div style={{fontWeight:700,color:C.teal,fontSize:13,marginBottom:10}}>Specific dates offered</div>
          {specificDateReqs.map(r=>(
            <div key={r.id} style={{padding:"8px 0",borderBottom:`1px solid ${C.border2}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                <span style={{fontWeight:700,fontSize:13,color:C.text}}>{r.name}</span>
                {r.note&&<span style={{fontSize:10,color:C.mid,fontStyle:"italic"}}>"{r.note}"</span>}
              </div>
              <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                {(r.dates||[]).map(d=>{
                  const day=CAL.days.find(x=>x.date===d);
                  return <span key={d} style={{fontSize:10,padding:"2px 8px",borderRadius:99,background:C.tealPale,color:C.teal,fontWeight:600}}>{day?.label.split(" ").slice(0,2).join(" ")||d}</span>;
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* General preferences */}
      {generalAvailReqs.length>0&&(
        <div style={{...CS,marginBottom:14}}>
          <div style={{fontWeight:700,color:C.brook,fontSize:13,marginBottom:10}}>General availability preferences</div>
          {generalAvailReqs.map(r=>{
            const sc = STANCE_COLORS[r.stance]||C.mid;
            return (
              <div key={r.id} style={{padding:"10px 0",borderBottom:`1px solid ${C.border2}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
                  <span style={{fontWeight:700,fontSize:13,color:C.text}}>{r.name}</span>
                  <Pill c={sc} t={STANCE_LABELS[r.stance]||r.stance} sm/>
                </div>
                {r.prefDays?.length>0&&(
                  <div style={{display:"flex",gap:4,marginBottom:4}}>
                    {["Mon","Tue","Wed","Thu","Fri"].map(d=>(
                      <span key={d} style={{fontSize:10,padding:"2px 6px",borderRadius:6,fontWeight:700,background:r.prefDays.includes(d)?C.teal+"18":C.border+"44",color:r.prefDays.includes(d)?C.teal:C.muted}}>{d.slice(0,2)}</span>
                    ))}
                  </div>
                )}
                {r.dates?.length>0&&(
                  <div style={{display:"flex",gap:3,flexWrap:"wrap"}}>
                    {r.dates.map(d=>{
                      const day=CAL.days.find(x=>x.date===d);
                      return <span key={d} style={{fontSize:9,padding:"1px 6px",borderRadius:99,background:C.tealPale,color:C.teal}}>{day?.label.split(" ").slice(0,2).join(" ")||d}</span>;
                    })}
                  </div>
                )}
                {r.note&&<div style={{fontSize:10,color:C.dim,marginTop:3,fontStyle:"italic"}}>"{r.note}"</div>}
              </div>
            );
          })}
        </div>
      )}

      {/* Legacy opt-ins (backward compat) */}
      {optIns.length>0&&(
        <div style={{...CS, marginBottom:14}}>
          <div style={{fontWeight:700,color:C.teal,fontSize:13,marginBottom:8}}>Weekly availability</div>
          {optIns.map(n=>{
            const st = STATUS[n.status];
            return (
              <div key={n.id} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom:`1px solid ${C.border2}`}}>
                <div style={{minWidth:110,fontWeight:700,fontSize:12,color:C.text}}>{n.name.split(" ")[0]}</div>
                <div style={{display:"flex",gap:4,flex:1}}>
                  {DAYS.map(d=>{
                    const opted = n.availDays.includes(d);
                    return (
                      <div key={d} style={{padding:"3px 6px",borderRadius:6,fontSize:10,fontWeight:700,background:opted?C.tealPale:C.border+"44",color:opted?C.teal:C.muted}}>
                        {d.slice(0,2)}{opted?" ✓":""}
                      </div>
                    );
                  })}
                </div>
                {st&&<Pill c={st.color} t={st.label} sm/>}
              </div>
            );
          })}
        </div>
      )}

      {shortNoticeReqs.length===0&&specificDateReqs.length===0&&generalAvailReqs.length===0&&optIns.length===0&&(
        <div style={{...CS,textAlign:"center",padding:32,color:C.dim}}>
          <div style={{fontSize:32,marginBottom:8}}></div>
          <div style={{fontSize:13}}>No availability posted yet</div>
          <div style={{fontSize:11,marginTop:4}}>Staff can flag availability in their portal</div>
        </div>
      )}
    </div>
  );
}

const BANK_ROLES = ["nurse","receptionist","therapist","TCO"];

const LOCUM_RATING = {
  excellent:  { label:"Excellent",       color:"#00e5a0", short:"Excellent",   score:5 },
  good:       { label:"Good",             color:"#3aad98", short:"Good",        score:4 },
  average:    { label:"~ Use if needed",    color:"#d97706", short:"Average",     score:3 },
  dnr:        { label:"✕ Do not rebook",    color:"#ff4646", short:"Do not rebook",score:0 },
};

const LOCUM_FLAGS = [
  "Late arrival",
  "Left early",
  "Surgery left in mess",
  "Lack of experience",
  "Poor patient manner",
  "Slow / inefficient",
  "Didn't follow protocols",
  "Equipment issues",
  "No show",
  "Excellent — request again",
  "Great with patients",
  "Very efficient",
];

function PulseBank({state,set,user}) {
  const bank = state.bankStaff||[];
  const [editM,setEditM] = useState(null);
  const [q,setQ] = useState("");
  const empty = {name:"",role:"nurse",sites:Object.keys(SITES),status:"qualified",rate:"",phone:"",email:"",exStaff:false,knownLocum:true,notes:""};
  const [f,setF] = useState(empty);
  const sf = (k,v) => setF(p=>({...p,[k]:v}));

  function save() {
    if (!f.name.trim()) return;
    if (editM?.id) {
      set(s=>({...s,bankStaff:s.bankStaff.map(b=>b.id===editM.id?{...f,id:editM.id}:b)}));
    } else {
      set(s=>({...s,bankStaff:[...s.bankStaff,{...f,id:"bank"+Date.now()}]}));
    }
    setEditM(null); setF(empty);
  }

  function remove(id) {
    if (window.confirm("Remove from Omni Bank?"))
      set(s=>({...s,bankStaff:s.bankStaff.filter(b=>b.id!==id)}));
  }

  function saveRating(id, r) {
    set(s=>({...s,bankStaff:(s.bankStaff||[]).map(b=>b.id!==id?b:{
      ...b,
      rating: r.rating,
      ratingFlags: r.flags,
      ratingHistory: [...(b.ratingHistory||[]), r],
      shiftLog: [...(b.shiftLog||[]), {date:r.date,rating:r.rating}],
    })}));
  }

  const scoreOf = b => LOCUM_RATING[b.rating]?.score ?? 3.5;
  const filtered = bank.filter(b=>!q||b.name.toLowerCase().includes(q.toLowerCase())).sort((a,b)=>scoreOf(b)-scoreOf(a));
  const canRate = user?.role==="principal"||user?.role==="manager"||user?.role==="managerLite"||user?.role==="headNurse";
  const exStaff   = filtered.filter(b=>b.exStaff);
  const knownLocs = filtered.filter(b=>b.knownLocum&&!b.exStaff);

  function WAMessage(b) {
    const practiceName = PRACTICE_CONFIG.name||"the practice";
    return `Hi ${b.name.split(" ")[0]}, We may need cover at ${practiceName} — are you still available for shifts? Please let us know your availability. Thanks, ${state?.practiceProfile?.principalName||"The team"}`;
  }

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6,flexWrap:"wrap",gap:12}}>
        <div>
          <h3 style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading,marginBottom:2}}>Omni Bank</h3>
          <div style={{fontSize:12,color:C.dim}}>Your private talent pool — ex-staff and known locums. Always tried before agencies.</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search…" style={{...IS,maxWidth:180,marginBottom:0}}/>
          <button onClick={()=>{setF(empty);setEditM({});}} style={BTN(C.teal)}>+ Add</button>
        </div>
      </div>

      {bank.length===0&&(
        <div style={{...CS,textAlign:"center",padding:40,color:C.dim,marginTop:16}}>
          <div style={{fontSize:36,marginBottom:12}}>⭐</div>
          <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:6}}>Your Omni Bank is empty</div>
          <div style={{fontSize:12,marginBottom:16}}>Add ex-staff and trusted locums here. They'll be shown as priority cover before any agency.</div>
          <button onClick={()=>{setF(empty);setEditM({});}} style={BTN(C.teal)}>Add first person</button>
        </div>
      )}

      {exStaff.length>0&&(
        <div style={{marginBottom:20}}>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Ex-Staff ({exStaff.length})</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:8}}>
            {exStaff.map(b=><BankCard key={b.id} b={b} onEdit={()=>{setF({...b});setEditM(b);}} onRemove={()=>remove(b.id)} waMsg={WAMessage(b)} onRate={saveRating} canRate={canRate}/>)}
          </div>
        </div>
      )}

      {knownLocs.length>0&&(
        <div style={{marginBottom:20}}>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Known Locums ({knownLocs.length})</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:8}}>
            {knownLocs.map(b=><BankCard key={b.id} b={b} onEdit={()=>{setF({...b});setEditM(b);}} onRemove={()=>remove(b.id)} waMsg={WAMessage(b)} onRate={saveRating} canRate={canRate}/>)}
          </div>
        </div>
      )}

      {editM!==null&&(
        <Overlay onClose={()=>setEditM(null)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:20}}>{editM.id?"Edit":"Add to Omni Bank"}</div>
          {[
            {l:"Full name",k:"name",t:"text"},
            {l:"Phone",k:"phone",t:"tel"},
            {l:"Email",k:"email",t:"email"},
            {l:"Preferred rate £/hr",k:"rate",t:"number"},
            {l:"Notes",k:"notes",t:"text"},
          ].map(({l,k,t})=>(
            <div key={k} style={{marginBottom:12}}>
              <label style={LB}>{l}</label>
              <input type={t} value={f[k]} onChange={e=>sf(k,e.target.value)} style={IS}/>
            </div>
          ))}
          <div style={{marginBottom:12}}>
            <label style={LB}>Role</label>
            <select value={f.role} onChange={e=>sf("role",e.target.value)} style={IS}>
              {BANK_ROLES.map(r=><option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div style={{marginBottom:12}}>
            <label style={LB}>Competency</label>
            <select value={f.status} onChange={e=>sf("status",e.target.value)} style={IS}>
              {Object.entries(STATUS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div style={{marginBottom:12}}>
            <label style={LB}>Can cover sites</label>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {Object.entries(SITES).map(([id,s])=>(
                <label key={id} style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:C.soft,cursor:"pointer"}}>
                  <input type="checkbox" checked={(f.sites||[]).includes(id)} onChange={e=>sf("sites",e.target.checked?[...(f.sites||[]),id]:(f.sites||[]).filter(x=>x!==id))} style={{accentColor:C.teal}}/>{s.name}
                </label>
              ))}
            </div>
          </div>
          <div style={{display:"flex",gap:16,marginBottom:16}}>
            {[["exStaff","Ex-staff"],["knownLocum","Known locum"]].map(([k,lab])=>(
              <label key={k} style={{display:"flex",alignItems:"center",gap:8,fontSize:13,color:C.soft,cursor:"pointer"}}>
                <input type="checkbox" checked={!!f[k]} onChange={e=>sf(k,e.target.checked)} style={{width:16,height:16,accentColor:C.teal}}/>{lab}
              </label>
            ))}
          </div>
          <div style={{marginBottom:4}}>
            <div style={{fontSize:11,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Availability</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
              <div><label style={LB}>Available from</label><input type="date" value={f.availFrom||""} onChange={e=>sf("availFrom",e.target.value)} style={IS} placeholder="Leave blank if always available"/></div>
              <div><label style={LB}>Available to</label><input type="date" value={f.availTo||""} onChange={e=>sf("availTo",e.target.value)} style={IS} placeholder="Leave blank if ongoing"/></div>
            </div>
            <div style={{marginBottom:10}}>
              <label style={LB}>Days available</label>
              <div style={{display:"flex",gap:6}}>
                {["Mon","Tue","Wed","Thu","Fri","Sat"].map(d=>(
                  <button key={d} onClick={()=>{const days=f.availDays||[];sf("availDays",days.includes(d)?days.filter(x=>x!==d):[...days,d]);}}
                    style={{flex:1,padding:"6px 2px",borderRadius:6,fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer",border:`1.5px solid ${(f.availDays||[]).includes(d)?C.teal:C.border}`,background:(f.availDays||[]).includes(d)?C.teal+"18":"transparent",color:(f.availDays||[]).includes(d)?C.teal:C.dim}}>
                    {d.slice(0,1)}
                  </button>
                ))}
              </div>
              {!(f.availDays||[]).length&&<div style={{fontSize:10,color:C.dim,marginTop:4}}>No days selected = any day</div>}
            </div>
            <div style={{marginBottom:10}}>
              <label style={LB}>Shift preference</label>
              <div style={{display:"flex",gap:6}}>
                {[["any","Any"],["early","Early"],["full","Full"],["late","Late"]].map(([id,lab])=>(
                  <button key={id} onClick={()=>sf("shiftPref",id)}
                    style={{flex:1,padding:"6px 4px",borderRadius:6,fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer",border:`1.5px solid ${f.shiftPref===id?C.teal:C.border}`,background:f.shiftPref===id?C.teal+"18":"transparent",color:f.shiftPref===id?C.teal:C.dim}}>
                    {lab}
                  </button>
                ))}
              </div>
            </div>
            <div style={{marginBottom:12}}>
              <label style={LB}>Availability note</label>
              <input value={f.availNote||""} onChange={e=>sf("availNote",e.target.value)} placeholder="e.g. Home from uni mid-June to end of Aug" style={IS}/>
            </div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={save} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Save</button>
            <button onClick={()=>setEditM(null)} style={BTN(C.dim,true)}>Cancel</button>
          </div>
        </Overlay>
      )}
    </div>
  );
}

function LocumRateModal({person,onSave,onClose}) {
  const [rating,setRating]   = useState(person.rating||"");
  const [flags,setFlags]     = useState([]);
  const [note,setNote]       = useState("");
  const [ratedBy,setRatedBy] = useState("");
  const togFlag = f => setFlags(p=>p.includes(f)?p.filter(x=>x!==f):[...p,f]);

  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Rate Locum Shift</div>
      <div style={{fontSize:13,color:C.dim,marginBottom:16}}>{person.name} · {person.role}</div>

      <label style={LB}>Overall rating</label>
      <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:16}}>
        {Object.entries(LOCUM_RATING).map(([k,v])=>(
          <button key={k} onClick={()=>setRating(k)} style={{padding:"10px 14px",borderRadius:10,border:`1.5px solid ${rating===k?v.color:C.border}`,background:rating===k?v.color+"18":"transparent",color:rating===k?v.color:C.soft,fontFamily:"inherit",fontSize:13,fontWeight:700,cursor:"pointer",textAlign:"left"}}>
            {v.label}
          </button>
        ))}
      </div>

      <label style={LB}>What happened? (select all that apply)</label>
      <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:16}}>
        {LOCUM_FLAGS.map(f=>(
          <button key={f} onClick={()=>togFlag(f)} style={{padding:"5px 10px",borderRadius:8,border:`1px solid ${flags.includes(f)?C.teal:C.border}`,background:flags.includes(f)?C.tealPale:"transparent",color:flags.includes(f)?C.teal:C.mid,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer"}}>
            {flags.includes(f)?"":""}{f}
          </button>
        ))}
      </div>

      <div style={{marginBottom:12}}>
        <label style={LB}>Private note (head nurse only)</label>
        <textarea value={note} onChange={e=>setNote(e.target.value)} rows={2} placeholder="Any extra context for the record…" style={{...IS,resize:"none",lineHeight:1.5}}/>
      </div>

      <div style={{marginBottom:20}}>
        <label style={LB}>Rated by</label>
        <input value={ratedBy} onChange={e=>setRatedBy(e.target.value)} placeholder="Your name" style={IS}/>
      </div>

      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>rating&&onSave({rating,flags,note,ratedBy,date:new Date().toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})})} style={{...BTN(rating?C.teal:C.muted),flex:1,padding:"11px 0"}}>Save Rating</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}

function BankCard({b,onEdit,onRemove,waMsg,onRate,canRate}) {
  const [copied,setCopied] = useState(false);
  const [rateM,setRateM]   = useState(false);
  const st = STATUS[b.status];
  const rating = b.rating ? LOCUM_RATING[b.rating] : null;
  const lastFlag = b.ratingFlags?.slice(-1)[0];
  const shiftCount = b.shiftLog?.length||0;
  return (
    <div style={{...CS,position:"relative",borderLeft:rating?`3px solid ${rating.color}`:`3px solid ${C.border}`}}>
      {/* Rating badge top-right */}
      {rating&&(
        <div style={{position:"absolute",top:10,right:10,background:rating.color+"18",border:`1px solid ${rating.color}33`,borderRadius:8,padding:"3px 8px",fontSize:10,fontWeight:700,color:rating.color}}>
          {rating.label}
        </div>
      )}
      <div style={{fontWeight:700,fontSize:13,color:b.rating==="dnr"?C.danger:C.text,marginBottom:2,paddingRight:rating?90:0}}>{b.name}</div>
      <div style={{fontSize:11,color:C.mid,marginBottom:6,textTransform:"capitalize"}}>{b.role}{b.rate?` · £${b.rate}/hr`:""}{shiftCount>0?` · ${shiftCount} shift${shiftCount!==1?"s":""} logged`:""}</div>
      <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:6}}>
        {st&&<Pill c={st.color} t={st.label} sm/>}
        {b.exStaff&&<Pill c={C.teal} t="Ex-staff" sm/>}
        {b.knownLocum&&<Pill c={C.warn} t="Known locum" sm/>}
        {(b.sites||[]).map(s=><Pill key={s} c={SITES[s]?.color||C.dim} t={SITES[s]?.name||s} sm/>)}
      </div>
      {lastFlag&&<div style={{fontSize:10,color:C.dim,marginBottom:6}}>Last note: {lastFlag}</div>}
      {b.notes&&<div style={{fontSize:10,color:C.dim,marginBottom:6,fontStyle:"italic"}}>{b.notes}</div>}
      {/* Availability */}
      {(b.availFrom||b.availTo||b.availDays?.length||b.shiftPref||b.availNote)&&(
        <div style={{marginBottom:6,padding:"6px 8px",background:"rgba(58,173,152,0.06)",border:"1px solid #bae6fd",borderRadius:6}}>
          <div style={{fontSize:9,fontWeight:700,color:"#60a5fa",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:3}}>Availability</div>
          {(b.availFrom||b.availTo)&&<div style={{fontSize:10,color:"#60a5fa"}}>{b.availFrom||"Now"} → {b.availTo||"Ongoing"}</div>}
          {b.availDays?.length>0&&<div style={{fontSize:10,color:C.dim,marginTop:1}}>{b.availDays.join(", ")}</div>}
          {b.shiftPref&&b.shiftPref!=="any"&&<div style={{fontSize:10,color:C.dim,marginTop:1}}>Prefers {b.shiftPref} shifts</div>}
          {b.availNote&&<div style={{fontSize:10,color:C.mid,marginTop:2,fontStyle:"italic"}}>{b.availNote}</div>}
        </div>
      )}
      {b.ratingHistory?.length>0&&(
        <div style={{fontSize:10,color:C.dim,marginBottom:6}}>
          {b.ratingHistory.slice(-2).map((r,i)=>(
            <div key={i} style={{padding:"2px 0",borderTop:i===0?`1px solid ${C.border2}`:"none"}}>
              <span style={{color:LOCUM_RATING[r.rating]?.color||C.dim,fontWeight:600}}>{LOCUM_RATING[r.rating]?.short}</span>
              {r.flags?.length>0&&<span style={{color:C.dim}}> · {r.flags.join(", ")}</span>}
              {r.note&&<span style={{color:C.dim}}> · "{r.note}"</span>}
              <span style={{color:C.muted,marginLeft:4}}>{r.date} · {r.ratedBy}</span>
            </div>
          ))}
        </div>
      )}
      <div style={{display:"flex",gap:6,borderTop:`1px solid ${C.border2}`,paddingTop:8,flexWrap:"wrap"}}>
        {b.phone&&(
          <button onClick={()=>{navigator.clipboard.writeText(waMsg);setCopied(true);setTimeout(()=>setCopied(false),2000);}} style={{...BTN(copied?C.green:C.teal,true),fontSize:10,padding:"3px 10px",flex:1}}>
            {copied?"Copied!":"Copy"}
          </button>
        )}
        {canRate&&<button onClick={()=>setRateM(true)} style={{...BTN(C.warn,true),fontSize:10,padding:"3px 10px"}}>★ Rate</button>}
        <button onClick={onEdit} style={{...BTN(C.teal,true),fontSize:10,padding:"3px 10px"}}>Edit</button>
        <button onClick={onRemove} style={{...BTN(C.danger,true),fontSize:10,padding:"3px 10px"}}>X</button>
      </div>
      {rateM&&<LocumRateModal person={b} onSave={r=>{onRate(b.id,r);setRateM(false);}} onClose={()=>setRateM(false)}/>}
    </div>
  );
}

/* ══════════════════════════════════
   CLINICIANS MANAGEMENT
══════════════════════════════════ */
const CLIN_TYPES = ["Principal/GDS","Principal/MOS","Partner/GDS","Partner/MOS","GDS","MOS","FD","Therapist","Hygienist","Orthodontist"];
const DAYS = ["Mon","Tue","Wed","Thu","Fri"];
const CLIN_COLORS = ["#1d4ed8","#7c3aed","#b45309","#059669","#c2410c","#0f766e","#9d174d","#f5c518","#6d28d9","#be185d","#134e4a","#164e63"];

function AdHocRow({onAdd}) {
  const [date,setDate] = useState("");
  const [type,setType] = useState("iv");
  const [note,setNote] = useState("");
  function add() {
    if (!date) return;
    onAdd({date,type,note:note.trim()});
    setDate(""); setNote("");
  }
  return (
    <div style={{display:"flex",gap:6,alignItems:"flex-end",flexWrap:"wrap"}}>
      <div style={{flex:"0 0 140px"}}>
        <div style={{fontSize:10,color:C.dim,marginBottom:3}}>Date</div>
        <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={{...IS,padding:"6px 8px",fontSize:11,marginBottom:0}}/>
      </div>
      <div style={{flex:"0 0 90px"}}>
        <div style={{fontSize:10,color:C.dim,marginBottom:3}}>Type</div>
        <select value={type} onChange={e=>setType(e.target.value)} style={{...IS,padding:"6px 8px",fontSize:11,marginBottom:0}}>
          <option value="iv">IV Sedation</option>
          <option value="ra">RA</option>
        </select>
      </div>
      <div style={{flex:1,minWidth:100}}>
        <div style={{fontSize:10,color:C.dim,marginBottom:3}}>Note (optional)</div>
        <input value={note} onChange={e=>setNote(e.target.value)} placeholder="e.g. Mr Smith IV list" style={{...IS,padding:"6px 8px",fontSize:11,marginBottom:0}}/>
      </div>
      <button onClick={add} style={{...BTN(C.teal),padding:"7px 14px",fontSize:11,whiteSpace:"nowrap"}}>+ Add</button>
    </div>
  );
}

function CliniciansManagement({state,set,isPrincipal,user}) {
  const canEdit = isPrincipal||user?.role==="manager"||user?.role==="managerLite";
  const [editM,setEditM]   = useState(null);
  const [reqsM,setReqsM]   = useState(null);
  const [q,setQ]           = useState("");
  const [siteF,setSiteF]   = useState("all");
  const [justAdded,setJustAdded] = useState(null); // clinician just added with no schedule
  const allClins           = getClinicians(state);
  const defaultSite = Object.keys(SITES)[0] || "main";
  const emptyForm = {name:"",site:defaultSite,type:CLIN_TYPES[0],color:CLIN_COLORS[0],sched:{Mon:"",Tue:"",Wed:"",Thu:"",Fri:""},schedB:{Mon:"",Tue:"",Wed:"",Thu:"",Fri:""},freq:{},prefNurse:"",notes:""};
  const [f,setF]           = useState(emptyForm);
  const sf = (k,v) => setF(p=>({...p,[k]:v}));
  const setSched = (d,v) => setF(p=>({...p,sched:{...p.sched,[d]:v}}));

  const filtered = allClins.filter(c=>!c._deleted&&(siteF==="all"||c.site===siteF)&&(!q||c.name.toLowerCase().includes(q.toLowerCase())));

  function save() {
    if (!f.name.trim()) return;
    const sched={};
    DAYS.forEach(d=>{ if(f.sched[d]&&f.sched[d].includes("-")) sched[d]=f.sched[d]; });

    // Convert sched_SITE keys to siteScheds object for multi-site clinicians
    const extraSites = f.extraSites||[];
    let siteScheds = undefined;
    if (extraSites.length>0) {
      siteScheds = {[f.site]: sched};
      extraSites.forEach(sid=>{
        const raw = f[`sched_${sid}`]||{};
        const cleaned = {};
        DAYS.forEach(d=>{ if(raw[d]&&raw[d].includes("-")) cleaned[d]=raw[d]; });
        siteScheds[sid] = cleaned;
      });
    }

    const entry = {
      ...f,
      sched,
      schedB: Object.fromEntries(Object.entries(f.schedB||{}).filter(([,v])=>v&&v.includes("-"))),
      ...(siteScheds ? {siteScheds, sites:[f.site,...extraSites]} : {}),
      id: editM?.id || "c_custom_"+Date.now()
    };
    // Remove raw sched_SITE keys from entry
    Object.keys(entry).forEach(k=>{ if(k.startsWith("sched_")) delete entry[k]; });

    const isNew = !editM?.id || !(state.customClinicians||[]).find(c=>c.id===editM.id);
    const hasSchedule = Object.values(sched).some(v=>v);
    set(s=>({...s,customClinicians:
      editM?.id && (s.customClinicians||[]).find(c=>c.id===editM.id)
        ? (s.customClinicians||[]).map(c=>c.id===editM.id?entry:c)
        : [...(s.customClinicians||[]), entry]
    }));
    if (isNew && !hasSchedule) {
      // Reopen immediately to set schedule
      setEditM(null);
      setF(emptyForm);
      setTimeout(()=>{ setJustAdded(entry.name); setEditM(entry); setF({...emptyForm,...entry,sched:{Mon:"",Tue:"",Wed:"",Thu:"",Fri:""}}); },80);
    } else {
      setEditM(null); setF(emptyForm);
    }
  }

  function deleteClin(id) {
    if (!window.confirm("Remove this clinician from the rota?")) return;
    const isBase = CLINICIANS_BASE.map(c=>c.id).includes(id);
    if (isBase) {
      // Mark as deleted in customClinicians
      set(s=>({...s,customClinicians:[...(s.customClinicians||[]).filter(c=>c.id!==id),{id,_deleted:true}]}));
    } else {
      set(s=>({...s,customClinicians:(s.customClinicians||[]).filter(c=>c.id!==id)}));
    }
  }

  function openEdit(c) {
    // Reconstruct sched_SITE fields from siteScheds for multi-site clinicians
    const extraSchedFields = {};
    if (c.siteScheds) {
      Object.entries(c.siteScheds).forEach(([sid, sched]) => {
        if (sid !== c.site) {
          extraSchedFields[`sched_${sid}`] = {...sched};
        }
      });
    }
    setF({...emptyForm,...c,
      sched:{Mon:"",Tue:"",Wed:"",Thu:"",Fri:"",...(c.siteScheds?.[c.site]||c.sched)},
      schedB: c.schedB||{},
      extraSites: c.sites ? c.sites.filter(s=>s!==c.site) : [],
      freq: c.freq||{},
      ...extraSchedFields,
    });
    setEditM(c);
  }

  const siteGroups = Object.entries(SITES).map(([id,si])=>({
    id, si, items: filtered.filter(c=>(c.sites||[c.site]).includes(id))
  })).filter(g=>siteF==="all"?true:g.id===siteF);

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6,flexWrap:"wrap",gap:12}}>
        <div>
          <h3 style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading,marginBottom:2}}>Clinicians</h3>
          <div style={{fontSize:12,color:C.dim}}>Add, edit or remove clinicians. Changes update the rota immediately.</div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search…" style={{...IS,maxWidth:160,marginBottom:0}}/>
          {canEdit&&<button onClick={()=>{setF(emptyForm);setEditM({});}} style={BTN(C.teal)}>+ Add Clinician / Therapist</button>}
        </div>
      </div>

      <div style={{display:"flex",gap:6,marginBottom:16,flexWrap:"wrap"}}>
        {[["all","All"],...Object.entries(SITES).map(([id,s])=>[id,s.name])].map(([id,l])=>(
          <button key={id} onClick={()=>setSiteF(id)} style={{padding:"5px 14px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${siteF===id?(SITES[id]?.color||C.teal):C.border}`,background:siteF===id?(SITES[id]?.color||C.teal)+"18":"transparent",color:siteF===id?(SITES[id]?.color||C.teal):C.mid}}>{l}</button>
        ))}
      </div>

      {/* Fallback: show clinicians whose site isn't in SITES (broken setup recovery) */}
      {(()=>{
        const knownSiteIds = new Set(Object.keys(SITES));
        const orphans = filtered.filter(c=>!knownSiteIds.has(c.site)&&c.site!==undefined);
        if (!orphans.length) return null;
        return (
          <div style={{background:"rgba(245,197,24,0.06)",border:"1px solid rgba(245,197,24,0.2)",borderRadius:12,padding:"12px 14px",marginBottom:16}}>
            <div style={{fontSize:11,color:C.warn,fontWeight:700,marginBottom:8}}>
              {orphans.length} clinician{orphans.length!==1?"s":""} found — tap Edit to assign to your site
            </div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {orphans.map(c=>(
                <div key={c.id} style={{display:"flex",alignItems:"center",gap:8,background:C.surface,borderRadius:8,padding:"6px 10px",border:`1px solid ${C.border}`}}>
                  <span style={{fontSize:12,fontWeight:600,color:C.text}}>{c.name}</span>
                  <span style={{fontSize:11,color:C.dim}}>{c.type}</span>
                  {canEdit&&<button onClick={()=>openEdit(c)} style={{...BTN(C.teal,true),fontSize:10,padding:"2px 8px"}}>Edit</button>}
                  {isPrincipal&&<button onClick={()=>deleteClin(c.id)} style={{...BTN(C.danger,true),fontSize:10,padding:"2px 8px"}}>Delete</button>}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {siteGroups.map(({id,si,items})=>items.length>0&&(
        <div key={id} style={{marginBottom:24}}>
          <div style={{fontSize:10,fontWeight:700,color:si.color,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>{si.name} · {items.length} clinician{items.length!==1?"s":""}</div>
          <div style={{overflowX:"auto",marginBottom:4}}>
            <table style={{borderCollapse:"collapse",width:"100%",minWidth:560}}>
              <thead>
                <tr>
                  {["Name","Type","Mon","Tue","Wed","Thu","Fri","Pref Nurse",""].map(h=>(
                    <th key={h} style={{padding:"5px 8px",fontSize:9,fontWeight:700,color:C.dim,textAlign:"left",textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:`1px solid ${C.border}`}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map(c=>{
                  const prefN = (state.nurses||NURSES_BASE).find(n=>n.id===c.prefNurse);
                  const isCustom = (state.customClinicians||[]).some(x=>x.id===c.id&&!x._deleted);
                  // Use correct schedule for the site being displayed
                  const schedForSite = c.siteScheds?.[id]||c.sched||{};
                  const isMultiSite = (c.sites||[]).length>1 && c.site!==id;
                  const hasNoSchedule = !Object.values(schedForSite).some(v=>v&&v.includes("-"));
                  return (
                    <tr key={c.id+id} style={{background:hasNoSchedule?"rgba(245,197,24,0.06)":isCustom?"rgba(58,173,152,0.07)":"transparent"}}>
                      <td style={{padding:"8px 8px",borderBottom:`1px solid ${C.border2}`}}>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <div style={{width:8,height:8,borderRadius:99,background:hasNoSchedule?C.warn:c.color,flexShrink:0}}/>
                          <span style={{fontSize:12,fontWeight:700,color:c.name?C.text:C.muted}}>{c.name||(
                            <span style={{fontStyle:"italic",color:C.muted}}>(unnamed — tap Edit to fix)</span>
                          )}</span>
                          {hasNoSchedule&&<span style={{fontSize:9,fontWeight:700,color:C.warn,background:"rgba(245,197,24,0.12)",border:"1px solid rgba(245,197,24,0.3)",borderRadius:4,padding:"1px 5px"}}>No schedule</span>}
                          {isCustom&&<Pill c={C.lime} t="edited" sm/>}
                          {isMultiSite&&<Pill c={SITES[c.site]?.color||C.dim} t={`primary: ${SITES[c.site]?.name||c.site}`} sm/>}
                          {c.leaving&&<Pill c={C.warn} t={`leaves ${c.leaving.slice(5)}`} sm/>}
                          {c.startDate&&c.startDate>new Date().toISOString().split("T")[0]&&<Pill c={C.teal} t={`starts ${c.startDate.slice(5)}`} sm/>}
                        </div>
                      </td>
                      <td style={{padding:"8px 8px",fontSize:11,color:C.mid,borderBottom:`1px solid ${C.border2}`}}>{c.type}</td>
                      {DAYS.map(d=>{
                        const cr=(state.clinicianRequirements||{})[c.id];
                        const ov=cr?.overrides?.[d];
                        const reqs=ov&&!Array.isArray(ov)?ov.reqs:Array.isArray(ov)?ov:[];
                        const isIV=reqs?.includes("iv"), isRA=reqs?.includes("ra")&&!isIV;
                        const freq=ov&&!Array.isArray(ov)?ov.freq:"always";
                        return (
                          <td key={d} style={{padding:"8px 6px",fontSize:10,color:schedForSite[d]?C.teal:C.muted,borderBottom:`1px solid ${C.border2}`,whiteSpace:"nowrap"}}>
                            {(()=>{
                              const v=schedForSite[d]||"";
                              if(!v) return "—";
                              if(v.includes("|")) {
                                const [am,pm]=v.split("|");
                                return <span><span style={{display:"block"}}>{am}</span><span style={{display:"block",color:C.teal,fontSize:9}}>↓ {pm}</span></span>;
                              }
                              return v;
                            })()}
                            {isIV&&<span style={{display:"block",fontSize:8,color:"#ff4646",fontWeight:700}}>IV{freq!=="always"?freq==="weekA"?" wkA":" wkB":""}</span>}
                            {isRA&&<span style={{display:"block",fontSize:8,color:"#d97706",fontWeight:700}}>RA{freq!=="always"?freq==="weekA"?" wkA":" wkB":""}</span>}
                          </td>
                        );
                      })}
                      <td style={{padding:"8px 8px",fontSize:11,color:C.mid,borderBottom:`1px solid ${C.border2}`}}>{prefN?.name.split(" ")[0]||"—"}</td>
                      <td style={{padding:"8px 8px",borderBottom:`1px solid ${C.border2}`}}>
                        {canEdit&&(
                          <div style={{display:"flex",gap:4}}>
                            <button onClick={()=>openEdit(c)} style={{...BTN(C.teal,true),fontSize:10,padding:"2px 8px"}}>Edit</button>
                            {isPrincipal&&<button onClick={e=>{e.stopPropagation();setReqsM(c);}} style={{...BTN(C.danger,true),fontSize:10,padding:"2px 8px"}}>IV Sedation</button>}
                            {isPrincipal&&<button onClick={()=>deleteClin(c.id)} style={{...BTN(C.danger,true),fontSize:10,padding:"2px 8px"}}>Delete</button>}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {justAdded&&(
        <div style={{background:"#3aad9815",border:"1px solid #3aad9840",borderRadius:10,padding:"10px 14px",marginBottom:16,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <span style={{fontSize:12,color:C.teal,fontWeight:600}}>✓ {justAdded} added — no schedule set yet. Set their shift times now:</span>
          <button onClick={()=>{const c=(getClinicians(state)||[]).find(x=>x.name===justAdded);if(c)openEdit(c);setJustAdded(null);}} style={{...BTN(C.teal),fontSize:11,padding:"5px 12px"}}>Set Schedule →</button>
          <button onClick={()=>setJustAdded(null)} style={{background:"transparent",border:"none",color:C.dim,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>Later</button>
        </div>
      )}
      {reqsM&&<SedationModal clinician={reqsM} state={state} onSave={(id,reqs)=>{set(s=>({...s,clinicianRequirements:{...(s.clinicianRequirements||{}),[id]:reqs}}));setReqsM(null);}} onClose={()=>setReqsM(null)}/>}
      {editM!==null&&(
        <Overlay onClose={()=>setEditM(null)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:20}}>{editM?.id&&editM.name?"Edit — "+editM.name:"Add Clinician"}</div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div>
              <label style={LB}>Full name</label>
              {(()=>{
                const allClins = getClinicians(state).filter(c=>!c._deleted);
                // Clinicians already at the selected primary site
                const siteClins = allClins.filter(c=>(c.sites||[c.site]).includes(f.site));
                // Clinicians at OTHER sites matching typed name
                const otherSiteMatch = f.name.length>1
                  ? allClins.find(c=>!siteClins.includes(c)&&c.name.toLowerCase().includes(f.name.toLowerCase()))
                  : null;
                // Exact match at same site (would be a duplicate)
                const exactMatch = siteClins.find(c=>c.name.toLowerCase()===f.name.toLowerCase());
                // Dropdown suggestions for this site
                const showDropdown = !editM?.id && !f.name && siteClins.length>0;
                return (
                  <div>
                    <div style={{display:"flex",gap:6,alignItems:"center",marginBottom:4}}>
                      <input value={f.name} onChange={e=>sf("name",e.target.value)} style={{...IS,flex:1,marginBottom:0}} placeholder="Type or pick a name…"/>
                    </div>
                    {/* Existing clinicians at this site as quick-pick */}
                    {showDropdown&&(
                      <div style={{marginBottom:4}}>
                        <div style={{fontSize:9,color:C.dim,marginBottom:4,letterSpacing:"0.06em",textTransform:"uppercase"}}>Existing at {SITES[f.site]?.name}</div>
                        <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                          {siteClins.slice(0,8).map(c=>(
                            <button key={c.id} onClick={()=>openEdit(c)} style={{fontSize:11,padding:"4px 12px",borderRadius:99,border:`1px solid ${C.border}`,background:C.bg,color:C.mid,cursor:"pointer",fontFamily:"inherit"}}>
                              {c.name}
                            </button>
                          ))}
                        </div>
                        <div style={{fontSize:10,color:C.dim,marginTop:5}}>↑ Tap to edit an existing clinician, or type a new name above to add someone new.</div>
                      </div>
                    )}
                    {/* Duplicate warning */}
                    {exactMatch&&!editM?.id&&(
                      <div style={{fontSize:10,color:C.warn,marginTop:2,display:"flex",alignItems:"center",gap:8}}>
                        ⚠ {exactMatch.name} is already at {SITES[f.site]?.name}.
                        <button onClick={()=>openEdit(exactMatch)} style={{fontSize:10,color:C.teal,background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",padding:0}}>Edit them instead →</button>
                      </div>
                    )}
                    {/* Cross-site suggestion */}
                    {otherSiteMatch&&!exactMatch&&!editM?.id&&(
                      <div style={{fontSize:10,color:C.teal,marginTop:2,display:"flex",alignItems:"center",gap:8}}>
                        {otherSiteMatch.name} already works at {SITES[otherSiteMatch.site]?.name}.
                        <button onClick={()=>{
                          openEdit(otherSiteMatch);
                          // Pre-tick the extra site
                          setTimeout(()=>sf("extraSites",[...new Set([...(f.extraSites||[]),f.site])]),50);
                        }} style={{fontSize:10,color:C.teal,background:"none",border:"none",cursor:"pointer",fontFamily:"inherit",padding:0,fontWeight:600}}>Add as multi-site →</button>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            <div>
              <label style={LB}>Primary site</label>
              <select value={f.site} onChange={e=>sf("site",e.target.value)} style={IS}>
                {Object.entries(SITES).map(([id,s])=><option key={id} value={id}>{s.name}</option>)}
              </select>
            </div>
          </div>

          {/* Multi-site toggle — only for practices with more than one site */}
          {Object.keys(SITES).length > 1 && (
          <div style={{marginBottom:12}}>
            <label style={LB}>Also works at (multi-site)</label>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {Object.entries(SITES).filter(([id])=>id!==f.site).map(([id,s])=>{
                const extraSites = f.extraSites||[];
                const on = extraSites.includes(id);
                return (
                  <button key={id} onClick={()=>sf("extraSites",on?extraSites.filter(x=>x!==id):[...extraSites,id])} style={{padding:"7px 14px",borderRadius:99,fontFamily:"inherit",fontSize:12,fontWeight:600,cursor:"pointer",border:`1px solid ${on?s.color:C.border}`,background:on?s.color+"18":"transparent",color:on?s.color:C.mid}}>
                    {on?"":""}{s.name}
                  </button>
                );
              })}
            </div>
            {(f.extraSites||[]).length>0&&<div style={{fontSize:10,color:C.dim,marginTop:4}}>Multi-site sessions use the same schedule grid — enter hours for each site's days below.</div>}
          </div>
          )}

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div>
              <label style={LB}>Type</label>
              <select value={f.type} onChange={e=>sf("type",e.target.value)} style={IS}>
                {CLIN_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={LB}>Colour</label>
              <div style={{display:"flex",gap:5,flexWrap:"wrap",marginTop:4}}>
                {CLIN_COLORS.map(col=>(
                  <button key={col} onClick={()=>sf("color",col)} style={{width:24,height:24,borderRadius:99,background:col,border:f.color===col?"3px solid #2a2420":"2px solid transparent",cursor:"pointer"}}/>
                ))}
              </div>
            </div>
          </div>

          {/* Schedule per site */}
          {(()=>{
            const allSites = [...new Set([f.site,...(f.extraSites||[])])];
            return allSites.map(sid=>{
              const schedKey = sid===f.site ? "sched" : `sched_${sid}`;
              const siteSched = sid===f.site ? f.sched : (f[schedKey]||{});
              const siteColor = SITES[sid]?.color||C.teal;
              // Parse a time string like "08:00-17:30" into {start, end}
              const parseT = v => { if(!v||!v.includes("-")) return {start:"",end:""}; const [s,e]=v.split("-"); return {start:s||"",end:e||""}; };
              // Build "HH:MM-HH:MM" from two inputs
              const makeT = (s,e) => (s&&e) ? s+"-"+e : (s||"");
              // Normalise typed time to HH:MM
              const normT = t => { if(!t) return ""; const c=t.replace(/[^0-9:]/g,""); if(c.length<=2) return c.padStart(2,"0")+":00"; if(!c.includes(":")) { const h=c.slice(0,2),m=c.slice(2,4)||"00"; return h+":"+m; } return c; };
              // First filled day — for copy-all
              const firstFilled = DAYS.find(d=>siteSched[d]&&siteSched[d].includes("-"));
              const firstVal = firstFilled ? siteSched[firstFilled] : "";
              const setDaySched = (d,val) => {
                if (sid===f.site) setSched(d,val);
                else setF(p=>({...p,[schedKey]:{...(p[schedKey]||{}),[d]:val}}));
              };
              return (
                <div key={sid} style={{marginBottom:16,background:C.bg,border:`1px solid ${siteColor}33`,borderRadius:12,padding:"12px 14px"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                    <label style={{...LB,color:siteColor,marginBottom:0,fontSize:11}}>{SITES[sid]?.name} — working days & hours</label>
                    <div style={{display:"flex",gap:6,alignItems:"center"}}>
                      {firstFilled && (
                        <button onClick={()=>{
                          const updated={};
                          DAYS.forEach(d=>{updated[d]=firstVal;});
                          if(sid===f.site) setF(p=>({...p,sched:{...p.sched,...updated}}));
                          else setF(p=>({...p,[schedKey]:{...(p[schedKey]||{}),...updated}}));
                        }} style={{padding:"3px 10px",borderRadius:6,fontFamily:"inherit",fontSize:10,fontWeight:600,cursor:"pointer",border:`1px solid ${siteColor}44`,background:siteColor+"12",color:siteColor}}>
                          Copy {parseT(firstVal).start}–{parseT(firstVal).end} to all
                        </button>
                      )}
                      <button onClick={()=>{
                        const cleared={};
                        DAYS.forEach(d=>{cleared[d]="";});
                        if(sid===f.site) setF(p=>({...p,sched:{...p.sched,...cleared}}));
                        else setF(p=>({...p,[schedKey]:{...(p[schedKey]||{}),...cleared}}));
                      }} style={{padding:"3px 8px",borderRadius:6,fontFamily:"inherit",fontSize:10,cursor:"pointer",border:`1px solid ${C.border}`,background:"transparent",color:C.muted}}>
                        Clear all
                      </button>
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8}}>
                    {DAYS.map(d=>{
                      const val = siteSched[d]||"";
                      const hasPipe = val.includes("|");
                      const [amPart, pmPart] = hasPipe ? val.split("|") : [val, ""];
                      const {start:amStart, end:amEnd} = parseT(amPart);
                      const {start:pmStart, end:pmEnd} = parseT(pmPart);
                      const rooms = (SURGERY_ROOMS[sid]||{}).count || 4;
                      const othersToday = getClinicians(state).filter(c=>c.id!==editM?.id&&(c.sites||[c.site]).includes(sid)&&(c.siteScheds?.[sid]||c.sched)?.[d]).length;
                      const conflict = val&&val.includes("-")&&othersToday>=rooms;
                      // Read all four values from DOM to avoid stale closure bug
                      const readDOM = () => {
                        const as = normT(document.getElementById(sid+d+"as")?.value||"");
                        const ae = normT(document.getElementById(sid+d+"ae")?.value||"");
                        const ps = normT(document.getElementById(sid+d+"ps")?.value||"");
                        const pe = normT(document.getElementById(sid+d+"pe")?.value||"");
                        const am = (as&&ae) ? as+"-"+ae : "";
                        const pm = (ps&&pe) ? ps+"-"+pe : "";
                        return am&&pm ? am+"|"+pm : am||"";
                      };
                      const hasPM = pmStart&&pmEnd;
                      return (
                        <div key={d}>
                          <div style={{fontSize:10,fontWeight:700,color:conflict?C.danger:val?siteColor:C.mid,marginBottom:4,display:"flex",alignItems:"center",gap:3}}>
                            {d}
                            {val&&val.includes("-")&&<span style={{color:siteColor,fontSize:8}}>●</span>}
                            {hasPipe&&<span style={{fontSize:7,color:C.teal,fontWeight:700}}>split</span>}
                            {conflict&&<span title={`Too many clinicians on ${d} — ${othersToday} already scheduled, only ${rooms} surgeries at ${SITES[sid]?.name||sid}`} style={{color:C.danger,fontSize:9}}> ⚠</span>}
                          </div>
                          {/* AM session */}
                          <input id={sid+d+"as"} key={d+"as"+(editM?.id||"new")} defaultValue={amStart}
                            onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();document.getElementById(sid+d+"ae")?.focus();}}}
                            onBlur={()=>setDaySched(d,readDOM())}
                            placeholder="Start" style={{...IS,padding:"5px 6px",fontSize:11,marginBottom:3,textAlign:"center",borderColor:val?siteColor+"66":undefined,background:val?siteColor+"08":"transparent"}}/>
                          <input id={sid+d+"ae"} key={d+"ae"+(editM?.id||"new")} defaultValue={amEnd}
                            onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();document.getElementById(sid+d+"ps")?.focus();}}}
                            onBlur={()=>setDaySched(d,readDOM())}
                            placeholder="Finish" style={{...IS,padding:"5px 6px",fontSize:11,marginBottom:4,textAlign:"center",borderColor:val?siteColor+"66":undefined,background:val?siteColor+"08":"transparent"}}/>
                          {/* PM session — appears when AM is set */}
                          {(amStart&&amEnd||hasPipe)&&(
                            <>
                              <div style={{fontSize:8,color:hasPM?C.teal:C.dim,textAlign:"center",marginBottom:2,fontWeight:hasPM?700:400}}>
                                {hasPM?"PM session":"PM (optional)"}
                              </div>
                              <input id={sid+d+"ps"} key={d+"ps"+(editM?.id||"new")} defaultValue={pmStart}
                                onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();document.getElementById(sid+d+"pe")?.focus();}}}
                                onBlur={()=>setDaySched(d,readDOM())}
                                placeholder="Start" style={{...IS,padding:"4px 5px",fontSize:10,marginBottom:2,textAlign:"center",borderColor:hasPM?siteColor+"66":C.border+"44",background:hasPM?siteColor+"08":"transparent"}}/>
                              <input id={sid+d+"pe"} key={d+"pe"+(editM?.id||"new")} defaultValue={pmEnd}
                                onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();const nextDay=DAYS[DAYS.indexOf(d)+1];if(nextDay)document.getElementById(sid+nextDay+"as")?.focus();}}}
                                onBlur={()=>setDaySched(d,readDOM())}
                                placeholder="Finish" style={{...IS,padding:"4px 5px",fontSize:10,textAlign:"center",borderColor:hasPM?siteColor+"66":C.border+"44",background:hasPM?siteColor+"08":"transparent"}}/>
                            </>
                          )}
                          {/* Week B hours — shown when day is set to weekA or weekB frequency */}
                          {(()=>{
                            const dayFreq=(f.freq||{})[d]||"always";
                            if(dayFreq==="always"||!val) return null;
                            const bVal=(f.schedB||{})[d]||"";
                            const bPipe=bVal.includes("|");
                            const[bAmPart,bPmPart]=bPipe?bVal.split("|"):[bVal,""];
                            const{start:bAs,end:bAe}=parseT(bAmPart);
                            const{start:bPs,end:bPe}=parseT(bPmPart);
                            const altLabel=dayFreq==="weekA"?"Week B hours":"Week A hours";
                            const setSchedB=(part,raw)=>{
                              const n=normT(raw);
                              const curB=(f.schedB||{})[d]||"";
                              const[cs,ce]=curB.includes("-")?curB.split("-"):["",""];
                              const newB=part==="start"?(n&&ce?n+"-"+ce:""):(cs&&n?cs+"-"+n:"");
                              sf("schedB",{...(f.schedB||{}),[d]:newB});
                            };
                            return (
                              <div style={{marginTop:4,paddingTop:4,borderTop:`1px dashed ${C.border}`}}>
                                <div style={{fontSize:8,color:C.warn,fontWeight:700,textAlign:"center",marginBottom:2}}>{altLabel}</div>
                                <input key={d+"bs"+(editM?.id||"new")} defaultValue={bAs}
                                  onBlur={e=>setSchedB("start",e.target.value)}
                                  placeholder="Start" style={{...IS,padding:"4px 5px",fontSize:10,marginBottom:2,textAlign:"center",borderColor:bAs?C.warn+"66":C.border+"44",background:bAs?C.warn+"08":"transparent"}}/>
                                <input key={d+"be"+(editM?.id||"new")} defaultValue={bAe}
                                  onBlur={e=>setSchedB("end",e.target.value)}
                                  placeholder="Finish" style={{...IS,padding:"4px 5px",fontSize:10,textAlign:"center",borderColor:bAe?C.warn+"66":C.border+"44",background:bAe?C.warn+"08":"transparent"}}/>
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                  {/* Per-day frequency — Always / Week A / Week B */}
                  {Object.values(siteSched).some(v=>v&&v.includes("-"))&&(
                    <div style={{marginTop:8,padding:"8px 10px",background:C.surface,borderRadius:8,border:`1px solid ${C.border}`}}>
                      <div style={{fontSize:9,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>Session frequency</div>
                      <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:4}}>
                        {DAYS.map(d=>{
                          const hasSched=!!(siteSched[d]&&siteSched[d].includes("-"));
                          if(!hasSched) return <div key={d} style={{fontSize:9,color:C.muted,textAlign:"center",paddingTop:4}}>{d}</div>;
                          const cur=(f.freq||{})[d]||"always";
                          return (
                            <div key={d}>
                              <div style={{fontSize:9,fontWeight:700,color:siteColor,textAlign:"center",marginBottom:3}}>{d}</div>
                              {[["always","Every week"],["weekA","Week A only"],["weekB","Week B only"]].map(([v,l])=>(
                                <button key={v} onClick={()=>sf("freq",{...(f.freq||{}),[d]:v})}
                                  style={{display:"block",width:"100%",padding:"2px 0",marginBottom:2,borderRadius:4,fontFamily:"inherit",fontSize:8,fontWeight:700,cursor:"pointer",
                                    border:`1px solid ${cur===v?siteColor:C.border}`,
                                    background:cur===v?siteColor+"18":"transparent",
                                    color:cur===v?siteColor:C.muted}}>
                                  {l}
                                </button>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                      {Object.entries(f.freq||{}).some(([,v])=>v!=="always")&&(
                        <div style={{marginTop:8,padding:"10px 12px",background:"rgba(58,173,152,0.06)",border:`1px solid ${C.teal}33`,borderRadius:8}}>
                          <div style={{fontSize:9,color:C.teal,fontWeight:700,marginBottom:6}}>
                            {Object.entries(f.freq||{}).filter(([,v])=>v!=="always").map(([d,v])=>`${d}: ${v==="weekA"?"Week A":"Week B"} only`).join(" · ")}
                          </div>
                          <label style={{...LB,fontSize:9,color:C.dim,marginBottom:4}}>Week A reference — enter any Monday that is a Week A</label>
                          <input type="date" value={state.settings?.weekAStart||""} onChange={e=>set(s=>({...s,settings:{...s.settings,weekAStart:e.target.value}}))}
                            style={{...IS,marginBottom:0,fontSize:11}}/>
                          {state.settings?.weekAStart&&(
                            <div style={{fontSize:9,color:C.teal,marginTop:4,fontWeight:600}}>
                              ✓ {new Date(state.settings.weekAStart+"T12:00:00").toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long"})} is Week A
                            </div>
                          )}
                          <div style={{fontSize:9,color:C.dim,marginTop:4}}>This applies across the whole practice — also editable in Admin → Settings → Rota.</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            });
          })()}

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div>
              <label style={LB}>Preferred nurse</label>
              <select value={f.prefNurse||""} onChange={e=>sf("prefNurse",e.target.value)} style={IS}>
                <option value="">No preference</option>
                {(()=>{
                  const knownSites = new Set(Object.keys(SITES));
                  const onlyOne = knownSites.size <= 1;
                  return (state.nurses||NURSES_BASE).filter(n=>{
                    if (n.maternity||n.archived) return false;
                    if (!n.site||!knownSites.has(n.site)) return onlyOne;
                    return n.site===f.site;
                  }).map(n=><option key={n.id} value={n.id}>{n.name}</option>);
                })()}
              </select>
            </div>
            <div>
              <label style={LB}>Notes</label>
              <input value={f.notes||""} onChange={e=>sf("notes",e.target.value)} style={IS}/>
            </div>
          </div>

          {/* Compliance roles + partner */}
          <div style={{marginBottom:20,padding:"12px 14px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:12}}>
            <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>Roles & compliance</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {[
                {key:"isPartner",  label:"Partner / Principal", color:C.teal},
                {key:"keyholder",  label:"Keyholder 🔑",   color:"#f5c518"},
                {key:"firstAider", label:"First aider",    color:"#3aad98"},
                {key:"safeguardingL3", label:"L3 Safeguarding", color:"#818cf8"},
                {key:"fireMarshal",label:"Fire marshal",   color:"#f97316"},
              ].map(({key,label,color})=>{
                const on = !!f[key];
                return (
                  <button key={key} onClick={()=>sf(key,!on)} style={{
                    padding:"5px 12px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",
                    border:`1px solid ${on?color:C.border}`,
                    background:on?color+"18":"transparent",
                    color:on?color:C.mid,
                  }}>{on?"✓ ":""}{label}</button>
                );
              })}
            </div>
            <div style={{fontSize:10,color:C.dim,marginTop:8}}>Keyholder status is picked up automatically by the rota and Iris Score.</div>
          </div>

          <div style={{display:"flex",gap:8}}>
            <button onClick={save} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Save — update rota</button>
            <button onClick={()=>setEditM(null)} style={BTN(C.dim,true)}>Cancel</button>
          </div>
        </Overlay>
      )}
    </div>
  );
}

/* ══════════════════════════════════
   SEDATION MODAL
══════════════════════════════════ */
function SedationModal({clinician, state, onSave, onClose}) {
  const existing = (state.clinicianRequirements||{})[clinician.id]||{default:[], overrides:{}, adHoc:[]};
  const workDays = ["Mon","Tue","Wed","Thu","Fri"].filter(d=>clinician.sched?.[d]);

  // Build per-day state: { Mon: {type:"none"|"ra"|"iv", freq:"always"|"weekA"|"weekB"} }
  const initDays = () => {
    const out = {};
    workDays.forEach(d=>{
      const ov = existing.overrides?.[d];
      if (ov && !Array.isArray(ov)) {
        out[d] = {type:(ov.reqs||[]).includes("iv")?"iv":(ov.reqs||[]).includes("ra")?"ra":"none", freq:ov.freq||"always"};
      } else if (Array.isArray(ov)) {
        out[d] = {type:ov.includes("iv")?"iv":ov.includes("ra")?"ra":"none", freq:"always"};
      } else {
        out[d] = {type:"none", freq:"always"};
      }
    });
    return out;
  };
  const [days, setDays] = useState(initDays);
  const setDay = (d,k,v) => setDays(p=>({...p,[d]:{...p[d],[k]:v}}));

  function save() {
    const overrides = {};
    workDays.forEach(d=>{
      const {type,freq} = days[d]||{type:"none",freq:"always"};
      if (type==="iv")  overrides[d] = {reqs:["ra","iv"], freq};
      else if (type==="ra") overrides[d] = {reqs:["ra"], freq};
    });
    onSave(clinician.id, {...existing, default:[], overrides});
  }

  const hasSedation = workDays.some(d=>(days[d]||{}).type!=="none");

  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading,marginBottom:2}}>Sedation Requirements</div>
      <div style={{fontSize:13,color:clinician.color||C.mid,fontWeight:600,marginBottom:4}}>{clinician.name} · {clinician.type}</div>
      <div style={{fontSize:12,color:C.dim,marginBottom:20}}>Set which sessions need a sedation-qualified nurse. This shows as a warning on the rota if the right nurse isn't assigned.</div>

      {workDays.length===0&&(
        <div style={{padding:"20px",textAlign:"center",color:C.dim,fontSize:13}}>No working days set — add a schedule in the Clinicians tab first.</div>
      )}

      <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:24}}>
        {workDays.map(d=>{
          const {type,freq} = days[d]||{type:"none",freq:"always"};
          const sched = clinician.sched?.[d];
          return (
            <div key={d} style={{padding:"12px 14px",borderRadius:12,border:`1.5px solid ${type!=="none"?(type==="iv"?"#b91c1c":"#d97706"):C.border}`,background:type!=="none"?(type==="iv"?"#fef2f2":"#fefce8"):"transparent",transition:"all 0.15s"}}>
              <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                {/* Day + time */}
                <div style={{minWidth:90}}>
                  <div style={{fontSize:13,fontWeight:700,color:C.text}}>{d}</div>
                  <div style={{fontSize:11,color:C.dim}}>{sched}</div>
                </div>
                {/* Type buttons */}
                <div style={{display:"flex",gap:6,flex:1}}>
                  {[{v:"none",label:"No sedation",col:C.mid},{v:"ra",label:"⚗️ RA",col:"#d97706"},{v:"iv",label:"IV",col:"#b91c1c"}].map(opt=>(
                    <button key={opt.v} onClick={()=>setDay(d,"type",opt.v)}
                      style={{padding:"6px 12px",borderRadius:8,border:`1.5px solid ${type===opt.v?opt.col:C.border}`,
                        background:type===opt.v?opt.col+"18":"transparent",
                        color:type===opt.v?opt.col:C.mid,
                        fontFamily:"inherit",fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Frequency — only if sedation selected */}
              {type!=="none"&&(
                <div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${type==="iv"?"#fecaca":"rgba(245,197,24,0.2)"}`}}>
                  <div style={{fontSize:11,color:C.dim,marginBottom:6}}>How often?</div>
                  <div style={{display:"flex",gap:6}}>
                    {[{v:"always",label:"Every session"},{v:"weekA",label:"Every other week (A)"},{v:"weekB",label:"Every other week (B)"}].map(opt=>(
                      <button key={opt.v} onClick={()=>setDay(d,"freq",opt.v)}
                        style={{padding:"5px 10px",borderRadius:6,border:`1px solid ${freq===opt.v?(type==="iv"?"#b91c1c":"#d97706"):C.border}`,
                          background:freq===opt.v?(type==="iv"?"#b91c1c18":"#d9740618"):"transparent",
                          color:freq===opt.v?(type==="iv"?"#b91c1c":"#d97706"):C.mid,
                          fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer"}}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {(freq==="weekA"||freq==="weekB")&&<div style={{fontSize:10,color:C.dim,marginTop:4}}>Odd calendar weeks = A · Even calendar weeks = B</div>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {hasSedation&&(
        <div style={{background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:10,padding:"10px 14px",marginBottom:20,fontSize:12,color:"#00e5a0"}}>
          Rota will warn if a session needs sedation and the assigned nurse isn't qualified.
        </div>
      )}

      <div style={{display:"flex",gap:8}}>
        <button onClick={save} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Save</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}

/* ══════════════════════════════════
   AD HOC CLINICS
══════════════════════════════════ */
/* ══════════════════════════════════
   FIND A SLOT
   Replaces capacity search — actionable, ranked results
══════════════════════════════════ */
function FindASlot({state, set, user, isPrincipal, defaultSite}) {
  const today = new Date().toISOString().split("T")[0];
  const [site, setSite] = useState(defaultSite || fallbackSite());
  const [range, setRange] = useState("week"); // today|week|month|custom
  const [customFrom, setCustomFrom] = useState(today);
  const [customTo, setCustomTo] = useState(today);
  const [session, setSession] = useState("any"); // any|am|pm|allday|after5
  const [needNurse, setNeedNurse] = useState("yes");
  const [surgerySel, setSurgerySel] = useState("any"); // any|1|2|3|4|5
  const [results, setResults] = useState(null);
  const [bookM, setBookM] = useState(null); // slot to book
  const [searched, setSearched] = useState(false);

  const siteObj = SITES[site];
  const surgeryCount = (state.settings?.surgeryCounts||{})[site]||siteObj?.surgeries||4;

  function getDateRange() {
    const now = new Date();
    const todayStr = now.toISOString().split("T")[0];
    if (range==="today") return {from:todayStr, to:todayStr};
    if (range==="week") {
      const end = new Date(now); end.setDate(now.getDate()+7);
      return {from:todayStr, to:end.toISOString().split("T")[0]};
    }
    if (range==="month") {
      const end = new Date(now); end.setDate(now.getDate()+28);
      return {from:todayStr, to:end.toISOString().split("T")[0]};
    }
    return {from:customFrom, to:customTo};
  }

  function findSlots() {
    const {from, to} = getDateRange();
    const days = CAL.days.filter(d=>d.date>=from&&d.date<=to&&!d.bh&&!d.isWeekend);
    const slots = [];

    days.forEach(d=>{
      const clins = getClinicians(state).filter(c=>{
        const clinSites = c.sites||[c.site];
        const sched = c.siteScheds?.[site]||c.sched;
        return clinSites.includes(site)&&!c._deleted&&sched?.[d.dow]&&!isAbsent(state,c.id,d.date);
      });
      const nurses = (state.nurses||NURSES_BASE).filter(n=>
        n.site===site&&!n.maternity&&!n.archived&&!isAbsent(state,n.id,d.date)
      );
      const maint = (state.surgeryMaintenance||[]).filter(m=>
        m.site===site&&m.startDate<=d.date&&(!m.endDate||m.endDate>=d.date)
      );
      const maintSurgNums = maint.map(m=>m.surgeryNum);

      // Build per-surgery availability
      for (let sNum=1; sNum<=surgeryCount; sNum++) {
        if (surgerySel!=="any" && parseInt(surgerySel)!==sNum) continue;
        if (maintSurgNums.includes(sNum)) continue; // in maintenance

        // Check if surgery is already occupied by a clinician this day
        const weekKey = CAL.weeks.find(w=>w.days.some(wd=>wd.date===d.date))?.start||"";
        const assigns = state.roomAssignments?.[weekKey]||{};
        const assignedClins = Object.values(assigns);
        // Surgery occupied = a clinician is working AND assigned (rough heuristic)
        const busyClinCount = clins.length;
        const isFree = busyClinCount < surgeryCount - maintSurgNums.length;

        if (!isFree) continue;

        // Session type
        const sessionInfo = {am:false, pm:false, allday:false, after5:false};
        clins.forEach(c=>{
          const sched = c.siteScheds?.[site]||c.sched||{};
          const hrs = sched[d.dow]||"";
          const [s,e] = hrs.split("-");
          if (s&&e) {
            const sh=parseInt(s.replace(":",""));
            const eh=parseInt(e.replace(":",""));
            if (sh<=900&&eh>=1600) sessionInfo.allday=true;
            else if (eh<=1400) sessionInfo.am=true;
            else if (sh>=1400) sessionInfo.pm=true;
            if (eh>=1700) sessionInfo.after5=true;
          }
        });

        // Free sessions = surgeries not occupied
        const freeCount = surgeryCount - maintSurgNums.length - busyClinCount;
        const spareNurses = Math.max(0, nurses.length - busyClinCount);

        // Session filter
        if (session==="am" && sessionInfo.am) continue; // am is occupied
        if (session==="pm" && sessionInfo.pm) continue;
        if (session==="allday" && sessionInfo.allday) continue;
        if (session==="after5" && !sessionInfo.after5) continue;

        // Nurse filter
        if (needNurse==="yes" && spareNurses===0) continue;

        // Score: today=highest, with nurse=better, more free=better
        const dayDiff = Math.round((new Date(d.date)-new Date(today))/86400000);
        const score = (needNurse==="yes"?100:0) + freeCount*10 + spareNurses*5 - dayDiff;

        const amFree = !sessionInfo.am;
        const pmFree = !sessionInfo.pm;

        slots.push({
          date:d.date, dow:d.dow, label:d.label,
          surgery:sNum, freeCount, spareNurses,
          amFree, pmFree, alldayFree:!sessionInfo.allday,
          after5Free:sessionInfo.after5,
          nurses: nurses.slice(0,spareNurses),
          score, dayDiff,
          isPast: d.date<today,
        });
      }
    });

    // Dedupe by date (one result per day unless specific surgery requested)
    const seen = new Set();
    const deduped = slots.filter(s=>{
      const key = s.date+(surgerySel!=="any"?s.surgery:"");
      if (seen.has(key)) return false;
      seen.add(key); return true;
    });

    setResults(deduped.sort((a,b)=>a.date.localeCompare(b.date)));
    setSearched(true);
  }

  const pillBtn = (active, label, onClick, color) => (
    <button onClick={onClick} style={{
      padding:"6px 14px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",
      border:`1px solid ${active?(color||C.teal):C.border}`,
      background:active?(color||C.teal)+"18":"transparent",
      color:active?(color||C.teal):C.mid,
    }}>{label}</button>
  );

  return (
    <div>
      {bookM&&<SlotBookModal slot={bookM} state={state} set={set} user={user} site={site} onClose={()=>setBookM(null)}/>}

      {/* Big "find now" shortcut */}
      <div style={{...CS,padding:"14px 18px",marginBottom:16,background:`linear-gradient(135deg,${C.teal}18,${C.teal}08)`,border:`1.5px solid ${C.tealBorder}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
          <div>
            <div style={{fontSize:15,fontWeight:700,color:C.teal}}>Find me a slot right now</div>
            <div style={{fontSize:11,color:C.dim,marginTop:2}}>Any free surgery + nurse today or tomorrow at {siteObj?.name}</div>
          </div>
          <button onClick={()=>{setRange("today");setNeedNurse("yes");setSession("any");setSurgerySel("any");setTimeout(findSlots,0);}}
            style={{...BTN(C.teal),padding:"10px 20px",fontSize:13,fontWeight:700}}>
            Find now →
          </button>
        </div>
      </div>

      {/* Search form */}
      <div style={{...CS,padding:"14px 18px",marginBottom:16}}>
        <div style={{fontSize:11,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:12}}>Search</div>

        <div style={{display:"flex",gap:14,flexWrap:"wrap",marginBottom:12}}>
          <div>
            <div style={{fontSize:9,color:C.dim,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:5}}>Site</div>
            <div style={{display:"flex",gap:4}}>
              {Object.entries(SITES).map(([id,s])=>pillBtn(site===id, s.name, ()=>setSite(id), s.color))}
            </div>
          </div>
          <div>
            <div style={{fontSize:9,color:C.dim,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:5}}>When</div>
            <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
              {[["today","Today"],["week","Next 7 days"],["month","Next 4 weeks"],["custom","Custom"]].map(([v,l])=>
                pillBtn(range===v, l, ()=>setRange(v))
              )}
            </div>
          </div>
        </div>

        {range==="custom"&&(
          <div style={{display:"flex",gap:10,marginBottom:12}}>
            <div><label style={LB}>From</label><input type="date" value={customFrom} onChange={e=>setCustomFrom(e.target.value)} style={{...IS,width:"auto"}}/></div>
            <div><label style={LB}>To</label><input type="date" value={customTo} min={customFrom} onChange={e=>setCustomTo(e.target.value)} style={{...IS,width:"auto"}}/></div>
          </div>
        )}

        <div style={{display:"flex",gap:14,flexWrap:"wrap",marginBottom:14}}>
          <div>
            <div style={{fontSize:9,color:C.dim,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:5}}>Session</div>
            <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
              {[["any","Any"],["am","AM"],["pm","PM"],["allday","All day"],["after5","After 5"]].map(([v,l])=>
                pillBtn(session===v, l, ()=>setSession(v))
              )}
            </div>
          </div>
          <div>
            <div style={{fontSize:9,color:C.dim,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:5}}>Nurse needed</div>
            <div style={{display:"flex",gap:4}}>
              {[["yes","Must have nurse"],["no","Surgery only"],["any","Either"]].map(([v,l])=>
                pillBtn(needNurse===v, l, ()=>setNeedNurse(v), v==="yes"?C.green:v==="no"?C.warn:undefined)
              )}
            </div>
          </div>
          <div>
            <div style={{fontSize:9,color:C.dim,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:5}}>Surgery</div>
            <div style={{display:"flex",gap:4}}>
              {[["any","Any"],...Array.from({length:surgeryCount},(_, i)=>[(i+1).toString(),`Surgery ${i+1}`])].map(([v,l])=>
                pillBtn(surgerySel===v, l, ()=>setSurgerySel(v))
              )}
            </div>
          </div>
        </div>

        <button onClick={findSlots} style={{...BTN(C.teal),padding:"11px 32px",fontSize:14,fontWeight:700}}>
          Find slots
        </button>
      </div>

      {/* Results */}
      {searched&&(
        <div>
          <div style={{fontSize:11,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>
            {results?.length??0} slot{results?.length!==1?"s":""} found
          </div>

          {results?.length===0&&(
            <div style={{...CS,textAlign:"center",padding:40,color:C.dim}}>
              <div style={{fontSize:28,marginBottom:8}}>🔍</div>
              <div style={{fontSize:14,fontWeight:600,color:C.text}}>No slots match</div>
              <div style={{fontSize:12,marginTop:4}}>Try widening the date range, changing the session type, or removing the nurse requirement</div>
            </div>
          )}

          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {results?.map((r,i)=>{
              const isToday = r.date===today;
              const isTomorrow = r.dayDiff===1;
              const urgencyLabel = isToday?"Today":isTomorrow?"Tomorrow":null;
              return (
                <div key={r.date+r.surgery} style={{...CS,padding:"12px 16px",
                  borderLeft:`4px solid ${r.spareNurses>0?C.green:C.warn}`,
                  background:isToday?C.tealPale:"transparent"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                    <div>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:14,fontWeight:700,color:C.text}}>
                          {r.dow} {r.date.slice(8)}/{r.date.slice(5,7)}
                        </span>
                        {urgencyLabel&&<span style={{fontSize:10,fontWeight:800,color:C.teal,background:C.tealPale,padding:"1px 8px",borderRadius:99}}>{urgencyLabel}</span>}
                        {surgerySel!=="any"&&<span style={{fontSize:10,color:C.mid}}>Surgery {r.surgery}</span>}
                      </div>
                      <div style={{fontSize:11,color:C.dim,marginTop:2}}>
                        {r.freeCount} free surger{r.freeCount!==1?"ies":"y"} · {r.spareNurses} spare nurse{r.spareNurses!==1?"s":""}
                        {r.nurses.length>0&&` (${r.nurses.slice(0,3).map(n=>n.name.split(" ")[0]).join(", ")})`}
                      </div>
                    </div>
                    <div style={{display:"flex",gap:5,flexWrap:"wrap",alignItems:"center"}}>
                      {r.amFree&&<span style={{padding:"2px 8px",borderRadius:99,background:C.teal+"15",color:C.teal,fontSize:10,fontWeight:700}}>🌅 AM free</span>}
                      {r.pmFree&&<span style={{padding:"2px 8px",borderRadius:99,background:"#7c3aed15",color:"#a78bfa",fontSize:10,fontWeight:700}}>🌆 PM free</span>}
                      {r.spareNurses>0&&<span style={{padding:"2px 8px",borderRadius:99,background:C.green+"15",color:C.green,fontSize:10,fontWeight:700}}>Nurse available</span>}
                      <button onClick={()=>setBookM(r)}
                        style={{...BTN(C.teal),fontSize:11,padding:"6px 14px",fontWeight:700}}>
                        Book →
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════
   SLOT BOOK MODAL
   From Find a Slot → book it
══════════════════════════════════ */
function SlotBookModal({slot, state, set, user, site, onClose}) {
  const [type, setType] = useState("adhoc"); // adhoc|maintenance|block
  const [note, setNote] = useState("");
  const [clinId, setClinId] = useState("");
  const [saved, setSaved] = useState(false);
  const siteClins = getClinicians(state).filter(c=>{
    const clinSites = c.sites||[c.site];
    const sched = c.siteScheds?.[site]||c.sched;
    return clinSites.includes(site)&&!c._deleted&&sched?.[slot.dow];
  });
  const nurses = (state.nurses||NURSES_BASE).filter(n=>n.site===site&&!n.maternity&&!n.archived&&!isAbsent(state,n.id,slot.date));

  function doBook() {
    if (type==="maintenance") {
      set(s=>({...s, surgeryMaintenance:[...(s.surgeryMaintenance||[]),{
        id:"maint_"+Date.now(), site, surgeryNum:slot.surgery||1,
        startDate:slot.date, endDate:slot.date,
        note:note||"Maintenance block", loggedBy:user?.name||"Manager",
      }]}));
    } else {
      set(s=>({...s, adHocClinics:[...(s.adHocClinics||[]),{
        id:"ah_"+Date.now(), site, date:slot.date,
        clinicianId:clinId||"", type:type, notes:note,
        loggedBy:user?.name||"Manager", loggedAt:new Date().toISOString(),
      }]}));
    }
    setSaved(true);
    setTimeout(onClose, 700);
  }

  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>
        Book Slot — {slot.dow} {slot.date.slice(8)}/{slot.date.slice(5,7)}
      </div>
      <div style={{fontSize:12,color:C.dim,marginBottom:16}}>
        {SITES[site]?.name} · {slot.freeCount} free surger{slot.freeCount!==1?"ies":"y"} · {slot.spareNurses} spare nurse{slot.spareNurses!==1?"s":""}
      </div>

      <label style={LB}>What type of booking?</label>
      <div style={{display:"flex",gap:6,marginBottom:14}}>
        {[["adhoc","➕ Ad-hoc clinic"],["maintenance","🔧 Maintenance / IT"],["block","Block / Reserve"]].map(([v,l])=>(
          <button key={v} onClick={()=>setType(v)} style={{flex:1,padding:"9px 6px",borderRadius:10,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",
            border:`1.5px solid ${type===v?C.teal:C.border}`,background:type===v?C.tealPale:"transparent",color:type===v?C.teal:C.mid}}>
            {l}
          </button>
        ))}
      </div>

      {type==="adhoc"&&(
        <div style={{marginBottom:12}}>
          <label style={LB}>Clinician (optional)</label>
          <select value={clinId} onChange={e=>setClinId(e.target.value)} style={IS}>
            <option value="">— Any / TBC</option>
            {siteClins.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {nurses.length>0&&(
            <div style={{fontSize:11,color:C.dim,marginTop:4}}>
              Available nurses: {nurses.slice(0,4).map(n=>n.name.split(" ")[0]).join(", ")}
            </div>
          )}
        </div>
      )}

      <div style={{marginBottom:16}}>
        <label style={LB}>{type==="maintenance"?"What needs doing?":type==="block"?"Reason for block?":"Notes"}</label>
        <input value={note} onChange={e=>setNote(e.target.value)}
          placeholder={type==="maintenance"?"e.g. Surgery 3 — PC replacement, IT visit":type==="block"?"e.g. Reserved for Dr Shah specialist clinics":"e.g. Extra UDA session — cover locum"}
          style={IS}/>
      </div>

      {saved
        ? <div style={{textAlign:"center",padding:12,color:C.green,fontWeight:700,fontSize:15}}>Booked</div>
        : <div style={{display:"flex",gap:8}}>
            <button onClick={doBook} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Confirm booking</button>
            <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
          </div>
      }
    </Overlay>
  );
}

/* ══════════════════════════════════
   SURGERY PLANNER
   Per-surgery view with pattern, free slots, utilisation
   🔮 Omni Insights: pattern recognition, AI recommendations (needs 6m data)
══════════════════════════════════ */
function SurgeryPlanner({state, set, user, isPrincipal, defaultSite}) {
  const today = new Date().toISOString().split("T")[0];
  const [site, setSite] = useState(defaultSite || fallbackSite());
  const [selSurgery, setSelSurgery] = useState(null);
  const [maintM, setMaintM] = useState(false);
  const [mf, setMf] = useState({note:"",startDate:today,endDate:"",surgeryNum:1});
  const siteObj = SITES[site];
  const surgeryCount = (state.settings?.surgeryCounts||{})[site]||siteObj?.surgeries||4;

  // Build 4-week pattern for each surgery
  const next4Weeks = CAL.days.filter(d=>d.date>=today&&!d.bh&&!d.isWeekend).slice(0,20);

  function getSurgeryData(sNum) {
    const maint = (state.surgeryMaintenance||[]).filter(m=>m.site===site&&m.surgeryNum===sNum);
    const activeMaint = maint.filter(m=>m.startDate<=today&&(!m.endDate||m.endDate>=today));
    const upcomingMaint = maint.filter(m=>m.startDate>today).sort((a,b)=>a.startDate.localeCompare(b.startDate));

    // Weekly pattern — how often is this surgery free each weekday over next 4 weeks
    const pattern = {Mon:0,Tue:0,Wed:0,Thu:0,Fri:0};
    const patternTotal = {Mon:0,Tue:0,Wed:0,Thu:0,Fri:0};
    next4Weeks.forEach(d=>{
      patternTotal[d.dow] = (patternTotal[d.dow]||0)+1;
      const clins = getClinicians(state).filter(c=>{
        const clinSites = c.sites||[c.site];
        const sched = c.siteScheds?.[site]||c.sched;
        return clinSites.includes(site)&&!c._deleted&&sched?.[d.dow]&&!isAbsent(state,c.id,d.date);
      });
      const inMaint = maint.some(m=>m.startDate<=d.date&&(!m.endDate||m.endDate>=d.date));
      const isFree = !inMaint && clins.length < surgeryCount;
      if (isFree) pattern[d.dow] = (pattern[d.dow]||0)+1;
    });

    // Upcoming free days
    const freeDays = next4Weeks.filter(d=>{
      const clins = getClinicians(state).filter(c=>{
        const clinSites = c.sites||[c.site];
        const sched = c.siteScheds?.[site]||c.sched;
        return clinSites.includes(site)&&!c._deleted&&sched?.[d.dow]&&!isAbsent(state,c.id,d.date);
      });
      const inMaint = maint.some(m=>m.startDate<=d.date&&(!m.endDate||m.endDate>=d.date));
      return !inMaint && clins.length < surgeryCount;
    });

    // Utilisation % this month
    const monthDays = next4Weeks.filter(d=>d.date.slice(0,7)===today.slice(0,7));
    const occupiedDays = monthDays.filter(d=>{
      const clins = getClinicians(state).filter(c=>{
        const clinSites = c.sites||[c.site];
        const sched = c.siteScheds?.[site]||c.sched;
        return clinSites.includes(site)&&!c._deleted&&sched?.[d.dow]&&!isAbsent(state,c.id,d.date);
      });
      return clins.length >= sNum;
    });
    const utilPct = monthDays.length>0 ? Math.round((occupiedDays.length/monthDays.length)*100) : 0;

    return {sNum, activeMaint, upcomingMaint, pattern, patternTotal, freeDays, utilPct};
  }

  const surgeries = Array.from({length:surgeryCount},(_,i)=>i+1).map(getSurgeryData);

  function saveMaint() {
    if (!mf.note.trim()||!mf.startDate) return;
    set(s=>({...s,surgeryMaintenance:[...(s.surgeryMaintenance||[]),{
      id:"maint_"+Date.now(), site, surgeryNum:mf.surgeryNum,
      startDate:mf.startDate, endDate:mf.endDate||mf.startDate,
      note:mf.note.trim(), loggedBy:user?.name||"Manager",
    }]}));
    setMaintM(false); setMf({note:"",startDate:today,endDate:"",surgeryNum:1});
  }

  return (
    <div>
      {/* Pulse Insights flag */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
        <div>
          <div style={{fontFamily:"'Instrument Serif',serif",fontSize:20,color:C.heading,fontWeight:400}}>Surgery Planner</div>
          <div style={{fontSize:12,color:C.dim,marginTop:2}}>Per-surgery availability, patterns, and maintenance scheduling.</div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <span style={{fontSize:10,background:"rgba(139,92,246,0.08)",color:"#a78bfa",borderRadius:99,padding:"3px 10px",fontWeight:700,border:"1px solid #e9d5ff"}}>
            🔮 Ask Omni Insights — coming soon
          </span>
          <button onClick={()=>setMaintM(true)} style={{...BTN("#b45309",true),fontSize:11}}>Log maintenance</button>
        </div>
      </div>

      {/* Site picker */}
      <div style={{display:"flex",gap:4,marginBottom:16}}>
        {Object.entries(SITES).map(([id,s])=>(
          <button key={id} onClick={()=>{setSite(id);setSelSurgery(null);}} style={{padding:"5px 14px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",
            border:`1px solid ${site===id?s.color:C.border}`,background:site===id?s.color+"18":"transparent",color:site===id?s.color:C.mid}}>{s.name}</button>
        ))}
      </div>

      {/* Surgery cards grid */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:10,marginBottom:16}}>
        {surgeries.map(sg=>{
          const inMaint = sg.activeMaint.length>0;
          const utilColor = sg.utilPct>=70?C.green:sg.utilPct>=40?C.warn:C.danger;
          const isSelected = selSurgery===sg.sNum;
          return (
            <div key={sg.sNum} onClick={()=>setSelSurgery(isSelected?null:sg.sNum)}
              style={{...CS,padding:"14px",cursor:"pointer",
                border:`2px solid ${isSelected?(siteObj?.color||C.teal):inMaint?"#f5c518":C.border}`,
                background:isSelected?(siteObj?.color||C.teal)+"22":inMaint?"rgba(245,197,24,0.08)":"transparent",
                transition:"all 0.15s"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                <div style={{fontSize:15,fontWeight:800,color:C.heading}}>Surgery {sg.sNum}</div>
                {inMaint&&<span style={{fontSize:9,background:"rgba(245,158,11,0.1)",color:"#b45309",borderRadius:99,padding:"2px 7px",fontWeight:700}}>🔧 MAINT</span>}
              </div>

              {/* Utilisation bar */}
              <div style={{marginBottom:8}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                  <span style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.05em"}}>Utilisation</span>
                  <span style={{fontSize:12,fontWeight:700,color:utilColor}}>{sg.utilPct}%</span>
                </div>
                <div style={{height:5,background:C.border,borderRadius:99,overflow:"hidden"}}>
                  <div style={{width:`${sg.utilPct}%`,height:"100%",background:utilColor,borderRadius:99}}/>
                </div>
              </div>

              {/* Weekly pattern dots */}
              <div style={{display:"flex",gap:3,marginBottom:6}}>
                {["Mon","Tue","Wed","Thu","Fri"].map(dow=>{
                  const freePct = sg.patternTotal[dow]>0 ? sg.pattern[dow]/sg.patternTotal[dow] : 0;
                  return (
                    <div key={dow} style={{flex:1,textAlign:"center"}}>
                      <div style={{width:"100%",height:6,borderRadius:2,background:freePct>0.5?C.green:freePct>0?"rgba(245,197,24,0.2)":C.border,marginBottom:2}}/>
                      <div style={{fontSize:7,color:C.muted}}>{dow.slice(0,1)}</div>
                    </div>
                  );
                })}
              </div>

              <div style={{fontSize:10,color:C.dim}}>
                {sg.freeDays.length} free slot{sg.freeDays.length!==1?"s":""} next 4 weeks
              </div>
              <div style={{marginTop:6,fontSize:9,color:"#9333ea",fontWeight:600}}>
                🔮 Ask Omni Insights →
              </div>
            </div>
          );
        })}
      </div>

      {/* Surgery detail panel */}
      {selSurgery&&(()=>{
        const sg = surgeries.find(s=>s.sNum===selSurgery);
        if (!sg) return null;
        return (
          <div style={{...CS,padding:"16px 20px",borderTop:`3px solid ${siteObj?.color||C.teal}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
              <div style={{fontSize:16,fontWeight:700,color:C.heading}}>Surgery {selSurgery} — Detail</div>
              <button onClick={()=>{setMf(p=>({...p,surgeryNum:selSurgery}));setMaintM(true);}}
                style={{...BTN("#b45309",true),fontSize:11,padding:"5px 12px"}}>Log issue / maintenance</button>
            </div>

            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16}}>
              {/* Weekly pattern */}
              <div>
                <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Weekly pattern (next 4 weeks)</div>
                {["Mon","Tue","Wed","Thu","Fri"].map(dow=>{
                  const freePct = sg.patternTotal[dow]>0 ? sg.pattern[dow]/sg.patternTotal[dow] : 0;
                  const label = freePct>=0.75?"Usually free":freePct>=0.5?"Often free":freePct>0?"Sometimes free":"Typically occupied";
                  const col = freePct>=0.75?C.green:freePct>=0.5?C.teal:freePct>0?C.warn:C.muted;
                  return (
                    <div key={dow} style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}>
                      <span style={{fontSize:11,fontWeight:700,color:C.text,width:30}}>{dow}</span>
                      <div style={{flex:1,height:8,background:C.border,borderRadius:99,overflow:"hidden"}}>
                        <div style={{width:`${freePct*100}%`,height:"100%",background:col,borderRadius:99}}/>
                      </div>
                      <span style={{fontSize:10,color:col,fontWeight:600,width:120}}>{label}</span>
                    </div>
                  );
                })}
                <div style={{marginTop:8,fontSize:11,padding:"10px 12px",background:"rgba(139,92,246,0.08)",borderRadius:8,border:"1px solid #e9d5ff"}}>
                  <div style={{fontSize:11,fontWeight:700,color:"#a78bfa",marginBottom:6}}>🔮 Omni Insights</div>
                  <div style={{fontSize:10,color:"#a78bfa",marginBottom:8}}>
                    Coming once we have 6 months of data — AI will automatically surface patterns like "Surgery {selSurgery} is routinely free Friday PM" and suggest actions.
                  </div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {[
                      "Should I put a specialist in Surgery "+selSurgery+"?",
                      "When is Surgery "+selSurgery+" least used?",
                      "Could Surgery "+selSurgery+" support a new associate?",
                    ].map(q=>(
                      <button key={q} disabled style={{padding:"5px 10px",borderRadius:99,fontFamily:"inherit",fontSize:10,fontWeight:600,
                        border:"1px solid #c4b5fd",background:"rgba(139,92,246,0.1)",color:"#a78bfa",cursor:"not-allowed",opacity:0.7}}>
                        🔮 {q}
                      </button>
                    ))}
                  </div>
                  <div style={{fontSize:9,color:"#9333ea",marginTop:6,fontStyle:"italic"}}>These buttons will connect to Pulse Insights when launched</div>
                </div>
              </div>

              {/* Upcoming free days */}
              <div>
                <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Upcoming free slots</div>
                <div style={{maxHeight:200,overflowY:"auto"}}>
                  {sg.freeDays.slice(0,10).map(d=>(
                    <div key={d.date} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"5px 8px",borderRadius:6,marginBottom:3,background:C.bg}}>
                      <span style={{fontSize:12,fontWeight:600,color:C.text}}>{d.dow} {d.date.slice(8)}/{d.date.slice(5,7)}</span>
                      <span style={{fontSize:10,color:C.green,fontWeight:700}}>Free</span>
                    </div>
                  ))}
                  {sg.freeDays.length===0&&<div style={{fontSize:11,color:C.dim}}>No upcoming free slots</div>}
                </div>
              </div>
            </div>

            {/* Maintenance history */}
            {sg.upcomingMaint.length>0&&(
              <div>
                <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>Upcoming maintenance</div>
                {sg.upcomingMaint.map(m=>(
                  <div key={m.id} style={{display:"flex",gap:10,alignItems:"center",padding:"7px 10px",background:"rgba(245,197,24,0.08)",borderRadius:8,marginBottom:4,border:"1px solid #fed7aa"}}>
                    <span style={{fontSize:10}}>🔧</span>
                    <span style={{fontSize:12,fontWeight:600,color:"#b45309"}}>{m.startDate.slice(8)}/{m.startDate.slice(5,7)}{m.endDate&&m.endDate!==m.startDate?` – ${m.endDate.slice(8)}/${m.endDate.slice(5,7)}`:""}</span>
                    <span style={{fontSize:11,color:C.text,flex:1}}>{m.note}</span>
                    <button onClick={()=>set(s=>({...s,surgeryMaintenance:(s.surgeryMaintenance||[]).filter(x=>x.id!==m.id)}))}
                      style={{fontSize:10,color:C.danger,background:"none",border:"none",cursor:"pointer",fontFamily:"inherit"}}>X</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Log maintenance modal */}
      {maintM&&(
        <Overlay onClose={()=>setMaintM(false)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:16}}>🔧 Log Maintenance / Issue</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div>
              <label style={LB}>Surgery</label>
              <select value={mf.surgeryNum} onChange={e=>setMf(p=>({...p,surgeryNum:+e.target.value}))} style={IS}>
                {Array.from({length:surgeryCount},(_,i)=>(
                  <option key={i+1} value={i+1}>Surgery {i+1}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={LB}>Site</label>
              <select value={site} onChange={e=>setSite(e.target.value)} style={IS}>
                {Object.entries(SITES).map(([id,s])=><option key={id} value={id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div><label style={LB}>From date</label><input type="date" value={mf.startDate} onChange={e=>setMf(p=>({...p,startDate:e.target.value}))} style={IS}/></div>
            <div><label style={LB}>To date <span style={{color:C.dim,fontWeight:400}}>(leave blank for single day)</span></label><input type="date" value={mf.endDate} min={mf.startDate} onChange={e=>setMf(p=>({...p,endDate:e.target.value}))} style={IS}/></div>
          </div>
          <div style={{marginBottom:16}}>
            <label style={LB}>What's the issue / work needed?</label>
            <input value={mf.note} onChange={e=>setMf(p=>({...p,note:e.target.value}))} placeholder="e.g. IT: PC replacement · Plumbing: sink leak · Deep clean before new dentist" style={IS}/>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={saveMaint} disabled={!mf.note.trim()||!mf.startDate} style={{...BTN("#b45309"),flex:1,padding:"11px 0",opacity:!mf.note.trim()||!mf.startDate?0.4:1}}>Log maintenance block</button>
            <button onClick={()=>setMaintM(false)} style={BTN(C.dim,true)}>Cancel</button>
          </div>
        </Overlay>
      )}
    </div>
  );
}

function AdHocClinics({state, set, user, isPrincipal}) {
  const canEdit = isPrincipal || user?.role==="manager" || user?.role==="managerLite";
  const canMaintenance = isPrincipal || user?.role==="manager" || user?.role==="managerLite" || user?.role==="headNurse";
  const allClins = getClinicians(state).filter(c=>!c._deleted);
  const today = new Date().toISOString().split("T")[0];
  const [subTab, setSubTab] = useState("findslot");

  // Quiet week hint — set by dashboard "Book ad hoc clinic" button
  const [qwHint, setQwHint] = useState(()=>{
    try {
      const raw = sessionStorage.getItem("omni_adhoc_hint");
      if (raw) { sessionStorage.removeItem("omni_adhoc_hint"); return JSON.parse(raw); }
    } catch(e){}
    return null;
  });

  // ── CAPACITY VIEW STATE ──
  const now = new Date();
  const [capMonth, setCapMonth] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`);
  const [capSite, setCapSite] = useState(fallbackSite(user));

  // ── MAINTENANCE STATE ──
  const [maintM, setMaintM] = useState(false);
  const [mf, setMf] = useState({site:fallbackSite(user), surgeryNum:1, note:"", startDate:today, endDate:""});
  const smf = (k,v) => setMf(p=>({...p,[k]:v}));

  const [showAdd, setShowAdd] = useState(false);
  const [f, setF] = useState({date:"",site:fallbackSite(user),clinicianId:"",type:"",sedation:"none",nurseReq:"",notes:""});
  const [siteF, setSiteF] = useState("all");
  const [upcoming, setUpcoming] = useState(true);
  const sf = (k,v) => setF(p=>({...p,[k]:v}));

  const clinics = (state.adHocClinics||[])
    .filter(cl => siteF==="all" || cl.site===siteF)
    .filter(cl => upcoming ? cl.date >= today : cl.date < today)
    .sort((a,b) => upcoming ? a.date.localeCompare(b.date) : b.date.localeCompare(a.date));

  const siteClinicians = allClins.filter(c => f.site==="all" || c.site===f.site || (c.extraSites||[]).includes(f.site));

  // ── CAPACITY HELPERS ──
  function getCapacityForDate(date, siteId) {
    try {
      const site = SITES[siteId];
      const totalSurgeries = (state.settings?.surgeryCounts||{})[siteId] || site?.surgeries || SURGERY_ROOMS[siteId]?.count || 4;
      const maintCount = (state.surgeryMaintenance||[]).filter(m=>
        m.site===siteId && m.startDate<=date && (!m.endDate||m.endDate>=date)
      ).length;
      const availSurgeries = Math.max(0, totalSurgeries - maintCount);
      const dow = new Date(date+"T12:00:00").getDay();
      const DAY_MAP = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
      const dayKey = DAY_MAP[dow];
      const cliniciansWorking = allClins.filter(c=>{
        try {
          const clinSites = c.sites||[c.site];
          if (!clinSites.includes(siteId)) return false;
          if (isAbsent(state, c.id, date)) return false;
          const sched = c.siteScheds?.[siteId]||c.sched||{};
          return !!sched[dayKey];
        } catch(e) { return false; }
      });
      // Session type breakdown — AM = ends <=14:00, PM = starts >=14:00, Full = spans both
      const sessions = {am:[], pm:[], full:[], other:[]};
      cliniciansWorking.forEach(c=>{
        const sched = c.siteScheds?.[siteId]||c.sched||{};
        const hrs = sched[dayKey]||"";
        const [start,end] = hrs.split("-");
        if (start&&end) {
          const s=start.replace(":",""), e=end.replace(":","");
          if (parseInt(s)<=900&&parseInt(e)>=1600) sessions.full.push(c);
          else if (parseInt(e)<=1400) sessions.am.push(c);
          else if (parseInt(s)>=1400) sessions.pm.push(c);
          else sessions.other.push(c);
        } else sessions.other.push(c);
      });
      const occupiedSurgeries = Math.min(cliniciansWorking.length, availSurgeries);
      const freeSurgeries = Math.max(0, availSurgeries - occupiedSurgeries);
      const allNurses = getNursesForSite(state, siteId);
      const nursesAvailable = allNurses.filter(n=>!isAbsent(state,n.id,date));
      const freeNurses = Math.max(0, nursesAvailable.length - occupiedSurgeries);
      const maintenance = (state.surgeryMaintenance||[]).filter(m=>
        m.site===siteId&&m.startDate<=date&&(!m.endDate||m.endDate>=date)
      );
      return {freeSurgeries, freeNurses, occupiedSurgeries, availSurgeries, totalSurgeries, maintCount, maintenance, cliniciansWorking:cliniciansWorking.length, sessions, nursesAvailable:nursesAvailable.length};
    } catch(e) {
      return {freeSurgeries:0, freeNurses:0, occupiedSurgeries:0, availSurgeries:0, totalSurgeries:4, maintCount:0, maintenance:[], cliniciansWorking:0, sessions:{am:[],pm:[],full:[],other:[]}, nursesAvailable:0};
    }
  }

  // Build month days
  function getMonthDays(ym) {
    const [y,m] = ym.split("-").map(Number);
    const days = [];
    const daysInMonth = new Date(y, m, 0).getDate();
    for (let d=1; d<=daysInMonth; d++) {
      const date = `${y}-${String(m).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
      const dow = new Date(date+"T12:00:00").getDay();
      if (dow!==0&&dow!==6&&!UK_BH_2026[date]) days.push(date);
    }
    return days;
  }

  function saveMaintenance() {
    if (!mf.note.trim()||!mf.startDate) return;
    set(s=>({...s, surgeryMaintenance:[...(s.surgeryMaintenance||[]),{
      id:"maint_"+Date.now(), site:mf.site, surgeryNum:mf.surgeryNum,
      note:mf.note.trim(), startDate:mf.startDate, endDate:mf.endDate||null,
      loggedBy:user?.name||"Manager", loggedAt:new Date().toISOString(),
    }]}));
    setMf({site:fallbackSite(user), surgeryNum:1, note:"", startDate:today, endDate:""});
    setMaintM(false);
  }

  function save() {
    if (!f.date || !f.clinicianId || !f.type.trim()) return;
    const clin = allClins.find(c=>c.id===f.clinicianId);
    const entry = {
      id: "ah_"+Date.now(),
      date: f.date, site: f.site, clinicianId: f.clinicianId,
      clinicianName: clin?.name||"Unknown", type: f.type.trim(),
      sedation: f.sedation, nurseReq: f.nurseReq.trim(), notes: f.notes.trim(),
      loggedBy: user?.name||"Unknown", loggedAt: new Date().toISOString(),
    };
    set(s=>({...s, adHocClinics:[...(s.adHocClinics||[]), entry]}));
    setF({date:"",site:fallbackSite(user),clinicianId:"",type:"",sedation:"none",nurseReq:"",notes:""});
    setShowAdd(false);
  }

  function remove(id) {
    if (!window.confirm("Remove this ad hoc clinic?")) return;
    set(s=>({...s, adHocClinics:(s.adHocClinics||[]).filter(c=>c.id!==id)}));
  }

  const sedBadge = s => s==="iv" ? {label:"IV Sedation",bg:"#fef2f2",col:"#b91c1c"} : s==="ra" ? {label:"RA Sedation",bg:"#fefce8",col:"#d97706"} : null;

  // Generate month options (current + 11 months)
  const monthOpts = [];
  for (let i=0; i<12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth()+i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
    monthOpts.push([val, d.toLocaleDateString("en-GB",{month:"long",year:"numeric"})]);
  }

  return (
    <div>
      {/* ── QUIET WEEK OPPORTUNITY BANNER ── */}
      {qwHint&&(
        <div style={{
          marginBottom:20,padding:"14px 18px",borderRadius:12,
          background:"rgba(0,229,160,0.07)",
          border:"1px solid rgba(0,229,160,0.25)",
          borderLeft:"3px solid #00e5a0",
        }}>
          <div style={{display:"flex",justifyContent:"space-between",
            alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
            <div style={{flex:1}}>
              <div style={{fontSize:11,fontWeight:700,color:"#00e5a0",
                letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6}}>
                ↗ Quiet week opportunity
              </div>
              <div style={{fontSize:13,fontWeight:700,
                color:"rgba(255,255,255,0.88)",marginBottom:4}}>
                {qwHint.siteName} — w/c {qwHint.weekLabel}
              </div>
              <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",
                lineHeight:1.6,marginBottom:6}}>
                {qwHint.spareNurses} nurse{qwHint.spareNurses!==1?"s":""} spare
                {" · "}{qwHint.openSurgDays} surgery day
                {qwHint.openSurgDays!==1?"s":""} free
                {" · "}
                <span style={{color:"#ff9a9a",fontWeight:600}}>
                  ~£{(qwHint.wageCost||0).toLocaleString()} idle cost
                </span>
              </div>
              {(qwHint.absentClins||[]).length>0&&(
                <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",
                  marginBottom:10,lineHeight:1.6}}>
                  {qwHint.absentClins.map((c,i)=>(
                    <span key={i} style={{marginRight:12}}>
                      <span style={{color:"rgba(255,255,255,0.7)",
                        fontWeight:600}}>{c.name}</span>
                      {" "}({c.type}) off {c.dates?.length||0} day
                      {(c.dates?.length||0)!==1?"s":""}
                      {" — "}
                      <span style={{color:"#00e5a0"}}>
                        {qwHint.suggestedType||c.type} slot free
                      </span>
                    </span>
                  ))}
                </div>
              )}
              <button
                onClick={()=>{
                  setF(prev=>({
                    ...prev,
                    site: qwHint.siteId||prev.site,
                    date: qwHint.bestDate||prev.date,
                    type: qwHint.suggestedType
                      ? `${qwHint.suggestedType} clinic`
                      : prev.type,
                    nurseReq: qwHint.spareNurses>0
                      ? `${qwHint.spareNurses} spare nurse${qwHint.spareNurses!==1?"s":""} available`
                      : "",
                    notes: `Quiet week — w/c ${qwHint.weekLabel}. `
                      +(qwHint.absentClins||[]).map(c=>
                        `${c.name} off ${c.dates?.length||0}d`).join(", "),
                  }));
                  setShowAdd(true);
                  setQwHint(null);
                }}
                style={{
                  fontSize:12,fontWeight:700,padding:"8px 18px",
                  borderRadius:9,cursor:"pointer",fontFamily:"inherit",
                  background:"rgba(0,229,160,0.15)",
                  border:"1px solid rgba(0,229,160,0.4)",
                  color:"#00e5a0",
                }}>
                Book {qwHint.suggestedType||"ad hoc"} clinic — pre-filled →
              </button>
            </div>
            <button onClick={()=>setQwHint(null)} style={{
              background:"transparent",border:"none",cursor:"pointer",
              color:"rgba(255,255,255,0.3)",fontSize:20,lineHeight:1,
              padding:"0 4px",flexShrink:0,
            }}>×</button>
          </div>
        </div>
      )}

      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16,flexWrap:"wrap",gap:12}}>
        <div>
          <h3 style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading,marginBottom:2}}>Ad Hoc & Capacity</h3>
          <div style={{fontSize:12,color:C.dim}}>Free sessions, surgery maintenance, and ad hoc clinic planning.</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          {canMaintenance&&<button onClick={()=>setMaintM(true)} style={{...BTN("#b45309",true),fontSize:12}}>Surgery maintenance</button>}
          {canEdit&&<button onClick={()=>setShowAdd(true)} style={BTN(C.teal)}>+ Add Clinic</button>}
        </div>
      </div>

      {/* Subtabs */}
      <div style={{display:"flex",gap:0,marginBottom:20,borderBottom:`1px solid ${C.border}`}}>
        {[["findslot","Find a Slot"],["planner","Surgery Planner"],["adhoc","Ad Hoc Clinics"],["capacity","Capacity View"],["maintenance","Maintenance"]].map(([id,label])=>(
          <button key={id} onClick={()=>setSubTab(id)} style={{background:"transparent",border:"none",padding:"8px 16px",cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600,color:subTab===id?C.teal:C.mid,borderBottom:subTab===id?`2px solid ${C.teal}`:"2px solid transparent"}}>
            {label}
            {id==="maintenance"&&(state.surgeryMaintenance||[]).filter(m=>m.startDate<=today&&(!m.endDate||m.endDate>=today)).length>0&&(
              <span style={{marginLeft:6,fontSize:10,fontWeight:700,background:"#d97706",color:"#fff",borderRadius:99,padding:"1px 6px"}}>
                {(state.surgeryMaintenance||[]).filter(m=>m.startDate<=today&&(!m.endDate||m.endDate>=today)).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── FIND A SLOT TAB ── */}
      {subTab==="findslot"&&<FindASlot state={state} set={set} user={user} isPrincipal={isPrincipal} defaultSite={fallbackSite(user)}/>}

      {/* ── SURGERY PLANNER TAB ── */}
      {subTab==="planner"&&<SurgeryPlanner state={state} set={set} user={user} isPrincipal={isPrincipal} defaultSite={fallbackSite(user)}/>}

      {/* ── CAPACITY TAB ── */}
      {subTab==="capacity"&&(
        <div>
          <div style={{display:"flex",gap:10,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
            <select value={capMonth} onChange={e=>setCapMonth(e.target.value)} style={{...IS,width:"auto",fontSize:13}}>
              {monthOpts.map(([v,l])=><option key={v} value={v}>{l}</option>)}
            </select>
            <div style={{display:"flex",gap:4}}>
              {Object.entries(SITES).map(([id,s])=>(
                <button key={id} onClick={()=>setCapSite(id)} style={{padding:"5px 14px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${capSite===id?s.color:C.border}`,background:capSite===id?s.color+"18":"transparent",color:capSite===id?s.color:C.mid}}>{s.name}</button>
              ))}
            </div>
            <button onClick={()=>{
              const site = SITES[capSite];
              const days = getMonthDays(capMonth);
              const hdr = ["Date","Day","Free Surgeries","AM Sessions Free","PM Sessions Free","Full Day Free","Occupied","Nurses Available","Notes"];
              const rows = days.map(date=>{
                const cap = getCapacityForDate(date, capSite);
                const d = new Date(date+"T12:00:00");
                const amFree = Math.max(0,(cap.sessions?.am||[]).length === 0 ? cap.freeSurgeries : cap.freeSurgeries - (cap.sessions?.pm||[]).length);
                return [
                  date,
                  d.toLocaleDateString("en-GB",{weekday:"long"}),
                  cap.freeSurgeries,
                  (cap.sessions?.am||[]).length > 0 ? "Partial AM" : cap.freeSurgeries > 0 ? cap.freeSurgeries+" AM free" : "—",
                  (cap.sessions?.pm||[]).length > 0 ? "Partial PM" : cap.freeSurgeries > 0 ? cap.freeSurgeries+" PM free" : "—",
                  cap.sessions?.full?.length||0,
                  cap.occupiedSurgeries,
                  cap.nursesAvailable||0,
                  cap.maintCount>0?`${cap.maintCount} surgery in maintenance`:"",
                ].join(",");
              });
              const csv = [hdr.join(","),...rows].join("\n");
              const blob = new Blob([`Free Surgery Report — ${site.name}\nGenerated: ${new Date().toLocaleDateString("en-GB")}\n\n${csv}`],{type:"text/csv"});
              const a = document.createElement("a"); a.href=URL.createObjectURL(blob);
              a.download=`free-sessions-${capSite}-${capMonth}.csv`; a.click();
            }} style={{...BTN(C.dim,true),fontSize:11,padding:"6px 12px",marginLeft:"auto"}}>
              ⬇ Download Report
            </button>
          </div>

          {/* Summary strip */}
          {(()=>{
            const days = getMonthDays(capMonth);
            const totFree = days.reduce((a,d)=>a+(getCapacityForDate(d,capSite).freeSurgeries||0),0);
            const daysWithFree = days.filter(d=>getCapacityForDate(d,capSite).freeSurgeries>0).length;
            const site = SITES[capSite];
            return (
              <div style={{display:"flex",gap:16,marginBottom:14,padding:"10px 16px",background:C.bg,borderRadius:10,flexWrap:"wrap"}}>
                <div><div style={{fontSize:18,fontWeight:700,color:C.teal}}>{totFree}</div><div style={{fontSize:9,color:C.dim,textTransform:"uppercase"}}>Total free surgery-days</div></div>
                <div><div style={{fontSize:18,fontWeight:700,color:C.teal}}>{daysWithFree}</div><div style={{fontSize:9,color:C.dim,textTransform:"uppercase"}}>Days with capacity</div></div>
                <div><div style={{fontSize:18,fontWeight:700,color:C.mid}}>{(site?.surgeries||4)} surgeries · {days.length} working days</div><div style={{fontSize:9,color:C.dim,textTransform:"uppercase"}}>Site capacity</div></div>
              </div>
            );
          })()}

          {/* Legend */}
          <div style={{display:"flex",gap:12,marginBottom:14,fontSize:10,color:C.mid,flexWrap:"wrap"}}>
            {[["#16a34a","🟢 Surgery + nurse free"],["#d97706","🟡 Surgery free, no nurse"],["#6b7280","⚫ Full / closed"],["#b45309","🔧 Maintenance"]].map(([c,l])=>(
              <div key={l} style={{display:"flex",alignItems:"center",gap:5}}>
                <div style={{width:8,height:8,borderRadius:2,background:c}}/>
                <span>{l}</span>
              </div>
            ))}
          </div>

          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:8}}>
            {getMonthDays(capMonth).map(date=>{
              const cap = getCapacityForDate(date, capSite);
              const dayLabel = new Date(date+"T12:00:00").toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"});
              const isPast = date < today;
              const hasMaint = cap.maintCount > 0;
              const amClins = cap.sessions?.am?.length||0;
              const pmClins = cap.sessions?.pm?.length||0;
              const fullClins = cap.sessions?.full?.length||0;
              const totalClins = amClins+pmClins+fullClins+(cap.sessions?.other?.length||0);
              const maxSurg = cap.availSurgeries;
              const amFree = Math.max(0, maxSurg - amClins - fullClins);
              const pmFree = Math.max(0, maxSurg - pmClins - fullClins);
              const borderCol = cap.freeSurgeries>0?(cap.freeNurses>0?"#86efac":"rgba(245,197,24,0.2)"):hasMaint?"#fed7aa":C.border;
              return (
                <div key={date} style={{...CS,padding:"10px 12px",opacity:isPast?0.45:1,border:`1.5px solid ${borderCol}`,position:"relative"}}>
                  <div style={{fontSize:11,fontWeight:700,color:C.text,marginBottom:6}}>{dayLabel}</div>

                  {hasMaint&&(
                    <div style={{fontSize:9,color:"#b45309",fontWeight:700,marginBottom:4}}>🔧 {cap.maintCount} in maint.</div>
                  )}

                  {/* Session type rows */}
                  <div style={{display:"flex",flexDirection:"column",gap:3,marginBottom:6}}>
                    {[
                      {label:"AM",free:amFree,icon:"🌅"},
                      {label:"PM",free:pmFree,icon:"🌆"},
                      {label:"All day",free:Math.max(0,maxSurg-fullClins-Math.max(amClins,pmClins)),icon:"☀️"},
                    ].map(({label,free,icon})=>(
                      <div key={label} style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <span style={{fontSize:9,color:C.dim}}>{icon} {label}</span>
                        <span style={{fontSize:10,fontWeight:700,color:free>0?C.green:C.muted}}>{free>0?`${free} free`:"—"}</span>
                      </div>
                    ))}
                  </div>

                  <div style={{fontSize:9,color:C.muted,borderTop:`1px solid ${C.border2}`,paddingTop:4,display:"flex",justifyContent:"space-between"}}>
                    <span>{totalClins} dentist{totalClins!==1?"s":""}</span>
                    <span>{cap.freeNurses||0} spare nurse{(cap.freeNurses||0)!==1?"s":""}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{marginTop:12,fontSize:11,color:C.dim,padding:"8px 12px",background:C.bg,borderRadius:6}}>
            As of {new Date().toLocaleDateString("en-GB")} · Free sessions update live as leave and schedules change · {SITES[capSite]?.name}: {SITES[capSite]?.surgeries||4} surgeries
          </div>
        </div>
      )}

      {/* ── MAINTENANCE TAB ── */}
      {subTab==="maintenance"&&(
        <div>
          {(state.surgeryMaintenance||[]).length===0 ? (
            <div style={{textAlign:"center",padding:"48px 0",color:C.dim}}>
              <div style={{fontSize:32,marginBottom:8}}>🔧</div>
              <div style={{fontSize:14,fontWeight:600,color:C.soft}}>No maintenance records</div>
              <div style={{fontSize:12,color:C.dim,marginTop:4}}>Use the button above to log a surgery closure.</div>
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {[...(state.surgeryMaintenance||[])].sort((a,b)=>b.startDate.localeCompare(a.startDate)).map(m=>{
                const si = SITES[m.site];
                const isActive = m.startDate<=today&&(!m.endDate||m.endDate>=today);
                return (
                  <div key={m.id} style={{...CS,borderLeft:`4px solid ${isActive?"#d97706":C.border}`,opacity:isActive?1:0.6}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
                      <div style={{flex:1}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                          <span style={{fontSize:15,fontWeight:700,color:C.heading}}>🔧 Surgery {m.surgeryNum} — {si?.name}</span>
                          {isActive&&<span style={{fontSize:10,fontWeight:700,background:"rgba(245,197,24,0.08)",color:"#d97706",border:"1px solid #fed7aa",borderRadius:99,padding:"1px 8px"}}>ACTIVE</span>}
                        </div>
                        <div style={{fontSize:13,color:C.soft,marginBottom:4}}>{m.note}</div>
                        <div style={{fontSize:11,color:C.dim}}>
                          From {new Date(m.startDate+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}
                          {m.endDate?` · Until ${new Date(m.endDate+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}`:" · Open-ended"}
                          {" · Logged by "}{m.loggedBy}
                        </div>
                      </div>
                      {canMaintenance&&<button onClick={()=>set(s=>({...s,surgeryMaintenance:(s.surgeryMaintenance||[]).filter(x=>x.id!==m.id)}))} style={{...BTN(C.danger,true),fontSize:10,padding:"3px 8px"}}>Remove</button>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── AD HOC TAB ── */}
      {subTab==="adhoc"&&(
        <>
          <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
            <div style={{display:"flex",gap:4}}>
              {[["all","All sites"],...Object.entries(SITES).map(([id,s])=>[id,s.name])].map(([id,l])=>(
                <button key={id} onClick={()=>setSiteF(id)} style={{padding:"5px 14px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${siteF===id?(SITES[id]?.color||C.teal):C.border}`,background:siteF===id?(SITES[id]?.color||C.teal)+"18":"transparent",color:siteF===id?(SITES[id]?.color||C.teal):C.mid}}>{l}</button>
              ))}
            </div>
            <div style={{display:"flex",gap:4,marginLeft:"auto"}}>
              {[["upcoming","Upcoming"],[false,"Past"]].map(([v,l])=>(
                <button key={String(v)} onClick={()=>setUpcoming(v==="upcoming")} style={{padding:"5px 14px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${upcoming===(v==="upcoming")?C.teal:C.border}`,background:upcoming===(v==="upcoming")?C.teal+"18":"transparent",color:upcoming===(v==="upcoming")?C.teal:C.mid}}>{l}</button>
              ))}
            </div>
          </div>
          {clinics.length===0 ? (
            <div style={{textAlign:"center",padding:"48px 0",color:C.dim}}>
              <div style={{fontSize:32,marginBottom:8}}></div>
              <div style={{fontSize:14,fontWeight:600,color:C.soft}}>No {upcoming?"upcoming":"past"} ad hoc clinics</div>
              {canEdit&&upcoming&&<div style={{fontSize:12,color:C.dim,marginTop:4}}>Use the button above to add one.</div>}
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {clinics.map(cl=>{
                const sd = SITES[cl.site];
                const sb = sedBadge(cl.sedation);
                const isPast = cl.date < today;
                return (
                  <div key={cl.id} style={{...CS,opacity:isPast?0.7:1,borderLeft:`4px solid ${sd?.color||C.teal}`}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:4}}>
                          <span style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:16,color:C.heading}}>{cl.type}</span>
                          {sb&&<span style={{fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:99,background:sb.bg,color:sb.col}}>{sb.label}</span>}
                          {isPast&&<span style={{fontSize:10,color:C.dim,fontWeight:600}}>PAST</span>}
                        </div>
                        <div style={{display:"flex",gap:12,flexWrap:"wrap",fontSize:12,color:C.mid,marginBottom:cl.notes?6:0}}>
                          <span style={{color:sd?.color,fontWeight:600}}>{sd?.name}</span>
                          <span>{new Date(cl.date+"T12:00:00").toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short",year:"numeric"})}</span>
                          <span style={{color:C.text,fontWeight:600}}>{cl.clinicianName}</span>
                          {cl.nurseReq&&<span>{cl.nurseReq}</span>}
                        </div>
                        {cl.notes&&<div style={{fontSize:12,color:C.dim}}>{cl.notes}</div>}
                      </div>
                      <div style={{display:"flex",gap:6,alignItems:"center",flexShrink:0}}>
                        <div style={{fontSize:10,color:C.dim,textAlign:"right"}}>Added by {cl.loggedBy}</div>
                        {canEdit&&<button onClick={()=>remove(cl.id)} style={{...BTN(C.danger,true),fontSize:10,padding:"3px 8px"}}>X</button>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Maintenance Modal */}
      {maintM&&(
        <Overlay onClose={()=>setMaintM(false)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,fontWeight:700,color:C.heading,marginBottom:4}}>🔧 Log Surgery Closure</div>
          <div style={{fontSize:12,color:C.dim,marginBottom:16}}>This surgery will be removed from available capacity and a note will be visible to all staff.</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div>
              <label style={LB}>Site</label>
              <select value={mf.site} onChange={e=>smf("site",e.target.value)} style={IS}>
                {Object.entries(SITES).map(([id,s])=><option key={id} value={id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label style={LB}>Surgery number</label>
              <select value={mf.surgeryNum} onChange={e=>smf("surgeryNum",+e.target.value)} style={IS}>
                {Array.from({length:SITES[mf.site]?.surgeries||4},(_,i)=>(
                  <option key={i+1} value={i+1}>Surgery {i+1}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{marginBottom:12}}>
            <label style={LB}>Reason / note <span style={{color:C.dim,fontWeight:400}}>(visible to staff)</span></label>
            <textarea value={mf.note} onChange={e=>smf("note",e.target.value)} rows={2}
              placeholder="e.g. Chair unit fault — engineer booked for Thursday. Expect 2-day closure."
              style={{...IS,resize:"none"}}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:16}}>
            <div>
              <label style={LB}>Closure start</label>
              <input type="date" value={mf.startDate} onChange={e=>smf("startDate",e.target.value)} style={IS}/>
            </div>
            <div>
              <label style={LB}>Expected reopen <span style={{color:C.dim,fontWeight:400}}>(optional)</span></label>
              <input type="date" value={mf.endDate} onChange={e=>smf("endDate",e.target.value)} style={IS}/>
            </div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={saveMaintenance} disabled={!mf.note.trim()||!mf.startDate} style={{...BTN("#b45309"),flex:1,padding:"11px 0"}}>Log closure</button>
            <button onClick={()=>setMaintM(false)} style={BTN(C.dim,true)}>Cancel</button>
          </div>
        </Overlay>
      )}

      {/* Add modal */}
      {showAdd&&(
        <Overlay onClose={()=>setShowAdd(false)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading,marginBottom:4}}>Add Ad Hoc Clinic</div>
          <div style={{fontSize:12,color:C.dim,marginBottom:20}}>One-off session needing specific planning. It will appear on the rota for that date.</div>

          {/* Date + Site */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
            <div>
              <label style={LB}>Date</label>
              <input type="date" value={f.date} onChange={e=>sf("date",e.target.value)} style={IS}/>
            </div>
            <div>
              <label style={LB}>Site</label>
              <select value={f.site} onChange={e=>{sf("site",e.target.value);sf("clinicianId","");}} style={IS}>
                {Object.entries(SITES).map(([id,s])=><option key={id} value={id}>{s.name}</option>)}
              </select>
            </div>
          </div>

          {/* Clinician */}
          <div style={{marginBottom:14}}>
            <label style={LB}>Clinician</label>
            <select value={f.clinicianId} onChange={e=>sf("clinicianId",e.target.value)} style={IS}>
              <option value="">Select clinician…</option>
              {siteClinicians.map(c=><option key={c.id} value={c.id}>{c.name} · {c.type}</option>)}
            </select>
          </div>

          {/* Clinic type */}
          <div style={{marginBottom:14}}>
            <label style={LB}>Type of clinic</label>
            <input value={f.type} onChange={e=>sf("type",e.target.value)} placeholder="e.g. IV sedation list, Implant surgery, Extraction clinic…" style={IS}/>
          </div>

          {/* Sedation */}
          <div style={{marginBottom:14}}>
            <label style={LB}>Sedation required?</label>
            <div style={{display:"flex",gap:8}}>
              {[{v:"none",label:"None",col:C.dim},{v:"ra",label:"⚗️ RA only",col:"#d97706"},{v:"iv",label:"IV sedation",col:"#b91c1c"}].map(opt=>(
                <button key={opt.v} onClick={()=>sf("sedation",opt.v)}
                  style={{flex:1,padding:"10px 8px",borderRadius:10,border:`1.5px solid ${f.sedation===opt.v?opt.col:C.border}`,
                    background:f.sedation===opt.v?opt.col+"15":"transparent",color:f.sedation===opt.v?opt.col:C.mid,
                    fontFamily:"inherit",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Nurse requirement */}
          <div style={{marginBottom:14}}>
            <label style={LB}>Specific nurse needed? <span style={{fontWeight:400,color:C.dim}}>(optional)</span></label>
            <input value={f.nurseReq} onChange={e=>sf("nurseReq",e.target.value)} placeholder="e.g. Hannah Watson (IV qualified)" style={IS}/>
          </div>

          {/* Notes */}
          <div style={{marginBottom:20}}>
            <label style={LB}>Notes <span style={{fontWeight:400,color:C.dim}}>(optional)</span></label>
            <input value={f.notes} onChange={e=>sf("notes",e.target.value)} placeholder="Anything the team needs to know" style={IS}/>
          </div>

          <div style={{display:"flex",gap:8}}>
            <button onClick={save} disabled={!f.date||!f.clinicianId||!f.type.trim()}
              style={{...BTN(!f.date||!f.clinicianId||!f.type.trim()?C.dim:C.teal),flex:1,padding:"11px 0",opacity:!f.date||!f.clinicianId||!f.type.trim()?0.5:1}}>
              Save Clinic
            </button>
            <button onClick={()=>setShowAdd(false)} style={BTN(C.dim,true)}>Cancel</button>
          </div>
        </Overlay>
      )}
    </div>
  );
}

/* ══════════════════════════════════
   FINANCE
══════════════════════════════════ */
function Finance({state,set,isPrincipal}) {
  // Hooks must always come first — guard is applied in the return below
  const [selMonth,setSelMonth] = useState(state.settings?.financeMonth||"2026-04");
  const [settOpen,setSettOpen] = useState(false);
  const [tab,setTab] = useState("payroll"); // payroll | wages | locums | summary
  const [wageM,setWageM] = useState(null);
  const locums = state.locums||[];
  const nurseRate = state.settings?.nurseHourlyRate||14;
  const recRate   = state.settings?.recHourlyRate||12;

  function updateWage(id, wage, extraSites) {
    set(s=>{
      const ns = {...s, wages:{...s.wages,[id]:wage}};
      if (extraSites !== undefined) {
        // Update extraSites on the nurse or rec record
        ns.nurses = (s.nurses||NURSES_BASE).map(n=>n.id===id?{...n,extraSites}:n);
        ns.customRecs = (s.customRecs||[]).map(r=>r.id===id?{...r,extraSites}:r);
      }
      return ns;
    });
    setWageM(null);
  }
  const MONTH_LABELS = {"2026-04":"April","2026-05":"May","2026-06":"June","2026-07":"July","2026-08":"August","2026-09":"September"};
  const MONTHS = Object.keys(MONTH_LABELS);

  const monthLocums = locums.filter(l=>l.date.startsWith(selMonth));
  const monthLocumCost = monthLocums.reduce((s,l)=>s+l.cost,0);

  // Build full staff payroll list — uses self-healing helpers so unknown-site staff are included
  const allNurses = getNursesForSite(state, Object.keys(SITES)[0]||"").length > 0
    ? Object.keys(SITES).flatMap(sid=>getNursesForSite(state, sid))
      .filter((n,i,a)=>a.findIndex(x=>x.id===n.id)===i) // dedupe
    : (state.nurses||NURSES_BASE).filter(n=>!n.maternity&&!n.archived);
  const allRecs = Object.keys(SITES).length > 0
    ? Object.keys(SITES).flatMap(sid=>getRecsForSite(state, sid, false))
      .filter((r,i,a)=>a.findIndex(x=>x.id===r.id)===i) // dedupe
    : [...RECS_BASE,...(state.customRecs||[])].filter(r=>!r.archived);
  const allPayroll = [
    ...allNurses.map(n=>({...n, role:"Nurse", rateType:"hourly", defaultRate:nurseRate})),
    ...allRecs.map(r=>({...r, role:r.type||"Reception", rateType:"hourly", defaultRate:recRate})),
  ];

  // Per person: contracted weekly hrs, hourly rate (from wages or default), weekly pay, monthly pay
  function getPersonFinance(p) {
    try {
      const hrs = p.hrs||37.5;
      const raw = state.wages?.[p.id];
      const w = parseWage(raw);
      const isSalaried = w.type==="salaried" && w.annual;

      // Monthly pay calculation
      let monthlyPay, annualPay, weeklyPay, rate;
      if (isSalaried) {
        monthlyPay = Math.round(w.annual / 12);
        annualPay  = w.annual;
        weeklyPay  = Math.round(w.annual / 52);
        rate = weeklyPay / (hrs||37.5); // implied hourly for NI/pension calc
      } else {
        rate = w.rate || p.defaultRate || (p.role==="Nurse"?nurseRate:recRate);
        weeklyPay  = hrs * rate;
        monthlyPay = Math.round(weeklyPay * 52 / 12);
        annualPay  = Math.round(weeklyPay * 52);
      }

      // Contract floor flag for hourly staff — rota hours vs contracted
      let contractFlag = null;
      if (!isSalaried && rate) {
        const monthDays = CAL.days.filter(d=>d.date.startsWith(selMonth)&&!d.bh&&!d.isWeekend);
        const contractedThisMonth = Math.round((hrs/5)*monthDays.length*10)/10;
        // Quick rota read: count pairings + tasks + CPD for this month
        const toMins = t=>{if(!t)return 0;const[h,m]=(t+":00").split(":").map(Number);return h*60+(m||0);};
        let rotaHrs = 0; let hasRota = false;
        const nurses = getNursesForSite(state, p.site||Object.keys(SITES)[0]||"");
        monthDays.forEach(d=>{
          if (isAbsent(state,p.id,d.date)) return;
          if (p.workDays&&!p.workDays.includes(d.dow)) return;
          const dr = buildDayRota(d.date, p.site||Object.keys(SITES)[0]||"", nurses, state);
          const pair = dr.pairings.find(x=>x.nurse?.id===p.id);
          if (pair?.clinStart&&pair?.clinEnd) {
            rotaHrs += Math.max(0, toMins(pair.clinEnd)-toMins(pair.clinStart))/60; hasRota=true; return;
          }
          if (state.spareTasks?.[`${d.date}_${p.id}`]) { rotaHrs += hrs/5; hasRota=true; return; }
          const cpd=(state.cpdEvents||[]).find(e=>e.date===d.date&&(e.staffIds||[]).includes(p.id));
          if (cpd) { rotaHrs += cpd.startTime&&cpd.endTime?Math.max(0,toMins(cpd.endTime)-toMins(cpd.startTime))/60:hrs/5; hasRota=true; return; }
        });
        if (hasRota) {
          const diff = Math.round((rotaHrs-contractedThisMonth)*10)/10;
          if (diff < -0.5) contractFlag = {type:"under", diff, contracted:contractedThisMonth, rota:Math.round(rotaHrs*10)/10};
          else if (diff > 0.5) contractFlag = {type:"over", diff, contracted:contractedThisMonth, rota:Math.round(rotaHrs*10)/10};
        }
      }

      const leaveDays = (state.leave[p.id]||[]).filter(l=>l.status==="approved"&&l.date?.startsWith(selMonth)).length;
      const otHrs = (state.overtime||[]).filter(o=>o.staffId===p.id&&o.status==="approved"&&o.date?.startsWith(selMonth)).reduce((s,o)=>s+o.hrs,0);
      const otPay  = isSalaried ? 0 : Math.round(otHrs * rate * 1.25);
      const earlyMins = (state.earlyFinish||[]).filter(e=>e.staffId===p.id&&e.deductFromLeave&&e.date?.startsWith(selMonth)).reduce((s,e)=>s+e.minsEarly,0);
      const earlyDeduct = Math.round((earlyMins/60) * rate);
      const adjustedMonthly = monthlyPay + otPay - earlyDeduct;

      // HMRC rates — Apr 2025. Editable in Finance → Rates
      const NI_RATE      = state.settings?.niRate            ?? 0.15;
      const NI_THRESHOLD = state.settings?.niThresholdWeekly ?? 96.15;
      const PEN_RATE     = state.settings?.pensionRate        ?? 0.03;
      const PEN_LOWER    = state.settings?.pensionLowerWeekly ?? 123;
      const PEN_UPPER    = state.settings?.pensionUpperWeekly ?? 967;
      const niableWeekly = Math.max(0, weeklyPay - NI_THRESHOLD);
      const monthlyNI = Math.round(niableWeekly * NI_RATE * 52 / 12);
      const pensionableWeekly = Math.max(0, Math.min(weeklyPay, PEN_UPPER) - PEN_LOWER);
      const monthlyPension = Math.round(pensionableWeekly * PEN_RATE * 52 / 12);
      const totalMonthlyEmployerCost = adjustedMonthly + monthlyNI + monthlyPension;
      const annualEmployerCost = Math.round((weeklyPay + (niableWeekly * NI_RATE) + (pensionableWeekly * PEN_RATE)) * 52);
      return {hrs, rate, wage:!!raw, weeklyPay, monthlyPay, annualPay, leaveDays, otHrs, otPay, earlyMins, earlyDeduct, adjustedMonthly, monthlyNI, monthlyPension, totalMonthlyEmployerCost, annualEmployerCost, isSalaried, wageRecord:w, contractFlag};
    } catch(e) {
      return {hrs:0, rate:0, wage:false, weeklyPay:0, monthlyPay:0, annualPay:0, leaveDays:0, otHrs:0, otPay:0, earlyMins:0, earlyDeduct:0, adjustedMonthly:0, monthlyNI:0, monthlyPension:0, totalMonthlyEmployerCost:0, annualEmployerCost:0, isSalaried:false, wageRecord:{type:"hourly"}, contractFlag:null};
    }
  }

  // Site totals — includes staff with unknown/empty site for single-site practices
  const wagesKnownSites = new Set(Object.keys(SITES));
  const wagesOnlyOne = wagesKnownSites.size <= 1;
  const siteSummary = Object.entries(SITES).map(([siteId,si])=>{
    const staff = allPayroll.filter(p=>{
      if (p.role==="Clinician") return false;
      const unknown = !p.site || !wagesKnownSites.has(p.site);
      return p.site===siteId || (unknown && wagesOnlyOne);
    });
    const total = staff.reduce((s,p)=>s+getPersonFinance(p).monthlyPay,0);
    const locumCost = monthLocums.filter(l=>l.site===siteId).reduce((s,l)=>s+l.cost,0);
    return {siteId, si, staff:staff.length, total, locumCost, grand:total+locumCost};
  });

  const grandMonthly = siteSummary.reduce((s,x)=>s+x.total,0);
  const grandLocum   = siteSummary.reduce((s,x)=>s+x.locumCost,0);
  const grandTotal   = grandMonthly+grandLocum;

  // CSV export — bookkeeper format
  function exportPayrollCSV() {
    const titlePrefix = (PRACTICE_CONFIG.name||"Practice").toUpperCase();
    const rows = [
      [`${titlePrefix} — PAYROLL EXPORT`],
      [`Month: ${MONTH_LABELS[selMonth]} 2026`],
      [`Generated: ${new Date().toLocaleDateString("en-GB")}`],
      [],
      ["STAFF PAYROLL"],
      ["Name","Site","Role","Contract Hrs","Hourly Rate","Weekly Pay","Monthly (contracted)","Leave Days","OT Hours","OT Pay (est.)","Early Finish Deduct","Monthly Total"],
      ...allPayroll.filter(p=>p.role!=="Clinician").map(p=>{
        const f = getPersonFinance(p);
        return [
          p.name,
          SITES[p.site]?.name||p.site,
          p.role,
          f.hrs,
          f.rate ? `£${f.rate.toFixed(2)}` : "Not set",
          f.rate ? `£${f.weeklyPay.toFixed(2)}` : "—",
          f.rate ? `£${f.monthlyPay}` : "—",
          f.leaveDays||0,
          f.otHrs||0,
          f.otPay ? `£${f.otPay}` : "£0",
          f.earlyDeduct ? `-£${f.earlyDeduct}` : "£0",
          f.rate ? `£${f.adjustedMonthly}` : "—",
        ];
      }),
      [],
      ["SITE TOTALS"],
      ["Site","Staff","Monthly Contracted","Locum Costs","Total"],
      ...siteSummary.map(x=>[x.si.name, x.staff, `£${x.total}`, `£${x.locumCost}`, `£${x.grand}`]),
      ["ALL SITES","",`£${grandMonthly}`,`£${grandLocum}`,`£${grandTotal}`],
      [],
      ["LOCUM LOG"],
      ["Date","Site","Role","Agency","Name","Hours","Rate","Total Cost"],
      ...monthLocums.map(l=>[l.date, SITES[l.site]?.name, l.role, l.agency||"Direct", l.locumName||"—", l.hrs+"h", l.rate?`£${l.rate}/hr`:"—", `£${l.cost}`]),
      [],
      ["NOTES"],
      ["* Monthly figures = weekly pay × 52 / 12 (contracted hours only)"],
      ["* OT estimated at 1.25× hourly rate — confirm actual rate with payroll"],
      ["* Clinician/associate pay not included — handled separately"],
      ["* Rates marked 'Not set' have no wage recorded in Omni Rota Wages tab"],
    ];
    const csv = rows.map(r=>Array.isArray(r)?r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(","):"").join("\n");
    const a=document.createElement("a");
    a.href=URL.createObjectURL(new Blob([csv],{type:"text/csv"}));
    a.download=`${PRACTICE_ID}-Payroll-${MONTH_LABELS[selMonth]}-2026.csv`;
    a.click();
  }

  function exportLocumCSV() {
    const titlePrefix = (PRACTICE_CONFIG.name||"Practice").toUpperCase();
    const rows = [
      [`${titlePrefix} — LOCUM LOG`],
      [`Month: ${MONTH_LABELS[selMonth]} 2026`],
      [],
      ["Date","Site","Role","Agency","Locum Name","Hours","Rate £/hr","Total Cost","Ref"],
      ...monthLocums.map(l=>[l.date,SITES[l.site]?.name,l.role,l.agency||"Direct",l.locumName||"—",l.hrs,l.rate||"",l.cost,l.id]),
      [],
      ["TOTAL","","","","",monthLocums.reduce((s,l)=>s+l.hrs,0),"",monthLocumCost,""],
    ];
    const csv = rows.map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const a=document.createElement("a");
    a.href=URL.createObjectURL(new Blob([csv],{type:"text/csv"}));
    a.download=`${PRACTICE_ID}-Locums-${MONTH_LABELS[selMonth]}-2026.csv`;
    a.click();
  }

  const missingWages = allPayroll.filter(p=>p.role!=="Clinician"&&!state.wages?.[p.id]).length;

  // Principal guard — after hooks
  if (!isPrincipal) return (
    <div style={{padding:"40px 20px",textAlign:"center",color:C.dim}}>
      <div style={{fontSize:32,marginBottom:12}}>🔒</div>
      <div style={{fontSize:16,fontWeight:700,color:C.heading,marginBottom:6}}>Finance is principal only</div>
      <div style={{fontSize:13}}>Contact Conor to access payroll and financial data.</div>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16,flexWrap:"wrap",gap:12}}>
        <div>
          <h3 style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading,marginBottom:2}}>Finance</h3>
          <div style={{fontSize:12,color:C.dim}}>Payroll · Locum costs · Bookkeeper exports</div>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button onClick={()=>setSettOpen(true)} style={{...BTN(C.dim,true),fontSize:12}}>⚙ Rates</button>
          <button onClick={exportLocumCSV} style={{...BTN(C.warn,true),fontSize:12}}>⬇ Locum CSV</button>
          <button onClick={exportPayrollCSV} style={{...BTN(C.teal),fontSize:12}}>⬇ Payroll CSV</button>
        </div>
      </div>

      {/* NI rate correction banner — shown once if still on old rates */}
      {(state.settings?.niRate===undefined || state.settings?.niRate===0.138) && (
        <div style={{background:"rgba(255,70,70,0.08)",border:"1px solid rgba(255,70,70,0.2)",borderRadius:10,padding:"10px 14px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center",gap:12,flexWrap:"wrap"}}>
          <div style={{display:"flex",gap:10,alignItems:"center"}}>
            <span style={{fontSize:18}}>🔴</span>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:C.danger}}>NI rates need updating — April 2025 changes apply</div>
              <div style={{fontSize:11,color:C.dim}}>Rate changed to 15% (was 13.8%) and threshold dropped to £96.15/wk. Your payroll figures are currently understated.</div>
            </div>
          </div>
          <button onClick={()=>{
            set(s=>({...s,settings:{...s.settings,niRate:0.15,niThresholdWeekly:96.15,pensionLowerWeekly:123,pensionUpperWeekly:967}}));
          }} style={{...BTN(C.danger),fontSize:12,padding:"7px 16px",flexShrink:0}}>Apply Apr 2025 rates</button>
        </div>
      )}

      {/* Missing wages warning */}
      {missingWages>0&&(
        <div style={{background:"rgba(245,197,24,0.08)",border:`1px solid ${C.warn}44`,borderRadius:10,padding:"10px 14px",marginBottom:16,display:"flex",gap:10,alignItems:"center",justifyContent:"space-between",flexWrap:"wrap"}}>
          <div style={{display:"flex",gap:10,alignItems:"center"}}>
            <span style={{fontSize:18}}>⚠️</span>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:C.warn}}>{missingWages} staff member{missingWages!==1?"s":""} ha{missingWages===1?"s":"ve"} no hourly rate set</div>
              <div style={{fontSize:11,color:C.dim}}>Payroll figures will show as "—" until rates are entered</div>
            </div>
          </div>
          <button onClick={()=>{setTab("payroll");setTimeout(()=>{const el=document.querySelector("[data-missing-rate]");if(el)el.scrollIntoView({behavior:"smooth",block:"center"});},150);}} style={{padding:"7px 16px",borderRadius:8,fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer",background:C.warn+"18",border:`1px solid ${C.warn}44`,color:C.warn,whiteSpace:"nowrap"}}>
            Set rates →
          </button>
        </div>
      )}

      {/* Month picker */}
      <div style={{display:"flex",gap:5,marginBottom:20,flexWrap:"wrap"}}>
        {MONTHS.map(m=>(
          <button key={m} onClick={()=>setSelMonth(m)} style={{padding:"5px 14px",borderRadius:8,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${selMonth===m?C.teal:C.border}`,background:selMonth===m?C.tealPale:"transparent",color:selMonth===m?C.teal:C.mid}}>{MONTH_LABELS[m]}</button>
        ))}
      </div>

      {/* KPI row */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12,marginBottom:24}}>
        {[
          [fmt(grandMonthly), "Contracted staff",  C.text, null],
          [fmt(grandLocum),   "Locum costs",       grandLocum>2000?C.danger:grandLocum>500?C.warn:C.green, null],
          [fmt(grandTotal),   "Total payroll",     C.teal, null],
          [monthLocums.length,"Locum days",        C.mid, null],
          [allPayroll.filter(p=>p.role!=="Clinician"&&state.wages?.[p.id]).length+"/"+allPayroll.filter(p=>p.role!=="Clinician").length, "Rates set", missingWages>0?C.warn:C.green, "wages"],
        ].map(([v,l,c,tabTarget])=>(
          <div key={l} onClick={tabTarget?()=>setTab(tabTarget):undefined} style={{...CS,cursor:tabTarget?"pointer":"default",borderColor:tabTarget&&missingWages>0?C.warn+"44":undefined}}>
            <div style={{fontSize:10,color:C.dim,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.06em"}}>{l}</div>
            <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:26,color:c}}>{v}</div>
            {tabTarget&&missingWages>0&&<div style={{fontSize:9,color:C.warn,marginTop:2}}>Tap to set →</div>}
          </div>
        ))}
      </div>

      {/* Sub-tabs */}
      <div style={{display:"flex",gap:0,marginBottom:20,borderBottom:`1px solid ${C.border}`,overflowX:"auto",WebkitOverflowScrolling:"touch",scrollbarWidth:"none"}}>
        {[["payroll","Payroll"],["locums","Locums"],["summary","Summary"]].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} style={{background:"transparent",border:"none",padding:"8px 18px",cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600,color:tab===id?C.teal:C.mid,borderBottom:tab===id?`2px solid ${C.teal}`:"2px solid transparent",whiteSpace:"nowrap",flexShrink:0}}>{label}</button>
        ))}
      </div>

      {/* PAYROLL TAB */}
      {tab==="payroll"&&(
        <div style={{overflowX:"auto"}}>
          <div style={{fontSize:11,color:C.dim,marginBottom:8,padding:"6px 10px",background:C.bg,borderRadius:6,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:6}}>
            <span>Employer NI at {((state.settings?.niRate??0.15)*100).toFixed(1)}% above £{(state.settings?.niThresholdWeekly??96.15).toFixed(2)}/wk · Pension {((state.settings?.pensionRate??0.03)*100).toFixed(0)}% on qualifying earnings · Monthly figures</span>
            <span style={{color:C.teal,cursor:"pointer",fontWeight:600}} onClick={()=>setSettOpen(true)}>Edit rates ⚙</span>
          </div>
          <table style={{borderCollapse:"collapse",width:"100%",minWidth:960}}>
            <thead>
              <tr style={{background:C.bg}}>
                {["Name","Role","Hrs/wk","Rate £/hr","Monthly gross","Employer NI","Pension","Total cost",""].map(h=>(
                  <th key={h} style={{padding:"7px 10px",fontSize:9,fontWeight:700,color:C.dim,textAlign:"left",textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:`1px solid ${C.border}`,whiteSpace:"nowrap"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(SITES).map(([siteId,si])=>{
                const siteStaff = allPayroll.filter(p=>{
                  if (p.role==="Clinician") return false;
                  const unknown = !p.site || !wagesKnownSites.has(p.site);
                  return p.site===siteId || (unknown && wagesOnlyOne);
                });
                if (!siteStaff.length) return null;
                const siteTotalCost = siteStaff.reduce((s,p)=>s+getPersonFinance(p).totalMonthlyEmployerCost,0);
                return [
                  <tr key={`hdr-${siteId}`}>
                    <td colSpan={9} style={{padding:"10px 10px 4px",fontSize:11,fontWeight:700,color:si.color,textTransform:"uppercase",letterSpacing:"0.06em",background:si.color+"08"}}>{si.name}</td>
                  </tr>,
                  ...siteStaff.map(p=>{
                    const fin = getPersonFinance(p);
                    const siteAttr = fin.wageRecord?.attribution?.[siteId];
                    const displayMonthly = siteAttr ? Math.round(fin.adjustedMonthly * siteAttr/100) : fin.adjustedMonthly;
                    const displayCost = siteAttr ? Math.round(fin.totalMonthlyEmployerCost * siteAttr/100) : fin.totalMonthlyEmployerCost;
                    const isMultiSite = fin.wageRecord?.attribution && Object.keys(fin.wageRecord.attribution).length>1;
                    return (
                      <tr key={p.id} style={{borderBottom:`1px solid ${C.border2}`,background:!fin.wage?"rgba(245,197,24,0.05)":fin.contractFlag?.type==="under"?"rgba(58,173,152,0.04)":fin.contractFlag?.type==="over"?"rgba(245,197,24,0.04)":"transparent"}} {...(!fin.wage?{"data-missing-rate":"1"}:{})}>
                        <td style={{padding:"8px 10px",fontSize:12,fontWeight:600,color:C.text,whiteSpace:"nowrap"}}>
                          {p.name}
                          {isMultiSite&&<span style={{fontSize:9,color:C.warn,marginLeft:4,background:C.warn+"15",borderRadius:3,padding:"1px 4px"}}>multi-site</span>}
                        </td>
                        <td style={{padding:"8px 10px",fontSize:11,color:C.mid}}>{p.role}</td>
                        <td style={{padding:"8px 10px",fontSize:12,color:C.text}}>{fin.hrs}h</td>
                        <td style={{padding:"8px 10px",fontSize:12,color:fin.wage?C.teal:C.danger,fontWeight:fin.wage?600:400}}>
                          {fin.isSalaried
                            ? <span style={{fontSize:11}}>{fmt(fin.wageRecord?.annual||0)}<span style={{fontSize:9,color:C.dim}}>/yr</span></span>
                            : fin.rate?`£${fin.rate.toFixed(2)}/hr`:"Not set"
                          }
                        </td>
                        <td style={{padding:"8px 10px",fontSize:12,fontWeight:600,color:C.text}}>
                          {fin.wage?fmt(displayMonthly):"—"}
                          {fin.isSalaried&&<span style={{fontSize:9,color:C.brook,marginLeft:4,background:C.brook+"15",borderRadius:3,padding:"1px 4px"}}>Salaried</span>}
                          {isMultiSite&&siteAttr&&<div style={{fontSize:9,color:C.dim,marginTop:1}}>{siteAttr}% of {fmt(fin.adjustedMonthly)}</div>}
                        </td>
                        <td style={{padding:"8px 10px",fontSize:12,color:"#b45309"}}>{fin.rate?fmt(fin.monthlyNI):"—"}</td>
                        <td style={{padding:"8px 10px",fontSize:12,color:"#a78bfa"}}>{fin.rate?fmt(fin.monthlyPension):"—"}</td>
                        <td style={{padding:"8px 10px",fontSize:13,fontWeight:700,color:fin.rate?C.teal:C.muted,background:fin.rate?C.tealPale:"transparent"}}>{fin.rate?fmt(displayCost):"—"}</td>
                        <td style={{padding:"8px 10px"}}>
                          <div style={{display:"flex",flexDirection:"column",gap:3}}>
                            <button onClick={()=>setWageM(p)} style={{...BTN(fin.wage?C.dim:C.teal,fin.wage),fontSize:10,padding:"4px 10px",whiteSpace:"nowrap"}}>{fin.wage?"Edit rate":"Set rate"}</button>
                            {fin.contractFlag&&!fin.isSalaried&&(
                              <span style={{fontSize:9,fontWeight:700,color:fin.contractFlag.type==="under"?C.teal:C.warn,background:fin.contractFlag.type==="under"?C.tealPale:C.warn+"15",borderRadius:4,padding:"2px 5px",textAlign:"center"}}>
                                {fin.contractFlag.type==="under"?`${Math.abs(fin.contractFlag.diff)}h under`:`+${fin.contractFlag.diff}h over`}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  }),
                  <tr key={`tot-${siteId}`} style={{background:si.color+"08"}}>
                    <td colSpan={7} style={{padding:"6px 10px",fontSize:11,fontWeight:700,color:si.color,textAlign:"right"}}>Site total employer cost</td>
                    <td style={{padding:"6px 10px",fontSize:13,fontWeight:700,color:si.color}}>{fmt(siteTotalCost)}</td>
                    <td/>
                  </tr>
                ];
              })}
              <tr style={{background:C.tealPale,borderTop:`2px solid ${C.teal}`}}>
                <td colSpan={7} style={{padding:"10px",fontSize:12,fontWeight:700,color:C.teal,textAlign:"right"}}>Grand total employer cost (all sites)</td>
                <td style={{padding:"10px",fontSize:15,fontWeight:700,color:C.teal}}>{fmt(allPayroll.filter(p=>p.role!=="Clinician").reduce((s,p)=>s+getPersonFinance(p).totalMonthlyEmployerCost,0))}</td>
                <td/>
              </tr>
            </tbody>
          </table>
          <div style={{marginTop:10,fontSize:11,color:C.dim}}>Employer NI: {((state.settings?.niRate??0.15)*100).toFixed(1)}% above £{(state.settings?.niThresholdWeekly??96.15).toFixed(2)}/wk (Apr 2025) · Pension: {((state.settings?.pensionRate??0.03)*100).toFixed(0)}% statutory minimum · OT at 1.25× · Dentist/associate pay separate
            {((state.settings?.setupMins||0)+(state.settings?.tidyMins||0))>0&&(
              <span style={{marginLeft:10,color:C.teal}}>· Setup/tidy: {state.settings?.setupMins||0}+{state.settings?.tidyMins||0} mins paid per day</span>
            )}
          </div>
        </div>
      )}

      {/* LOCUM TAB */}
      {tab==="locums"&&(
        <div>
          {monthLocums.length===0 ? (
            <div style={{...CS,textAlign:"center",padding:40,color:C.dim}}>
              <div style={{fontSize:28,marginBottom:10}}></div>
              <div>No locum costs logged for {MONTH_LABELS[selMonth]}</div>
              <div style={{fontSize:11,marginTop:6}}>Use "Log Locum" on the Dashboard when cover is needed</div>
            </div>
          ) : (
            <>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:10,marginBottom:16}}>
                {Object.entries(SITES).map(([sid,si])=>{
                  const sc = monthLocums.filter(l=>l.site===sid).reduce((s,l)=>s+l.cost,0);
                  const days = monthLocums.filter(l=>l.site===sid).length;
                  return (
                    <div key={sid} style={{...CS,borderTop:`3px solid ${si.color}`}}>
                      <div style={{fontSize:11,fontWeight:700,color:si.color,marginBottom:4}}>{si.name}</div>
                      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:sc>500?C.danger:C.text}}>{fmt(sc)}</div>
                      <div style={{fontSize:10,color:C.dim,marginTop:2}}>{days} day{days!==1?"s":""}</div>
                    </div>
                  );
                })}
                <div style={{...CS,borderTop:`3px solid ${C.teal}`}}>
                  <div style={{fontSize:11,fontWeight:700,color:C.teal,marginBottom:4}}>Total</div>
                  <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.teal}}>{fmt(monthLocumCost)}</div>
                  <div style={{fontSize:10,color:C.dim,marginTop:2}}>{monthLocums.length} days total</div>
                </div>
              </div>
              <div style={{overflowX:"auto"}}>
                <table style={{borderCollapse:"collapse",width:"100%",minWidth:600}}>
                  <thead><tr>{["Date","Site","Role","Agency","Name","Hours","Rate","Cost"].map(h=>(
                    <th key={h} style={{padding:"6px 10px",fontSize:9,fontWeight:700,color:C.dim,textAlign:"left",textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:`1px solid ${C.border}`}}>{h}</th>
                  ))}</tr></thead>
                  <tbody>
                    {[...monthLocums].sort((a,b)=>a.date.localeCompare(b.date)).map(l=>(
                      <tr key={l.id} style={{borderBottom:`1px solid ${C.border2}`}}>
                        <td style={{padding:"8px 10px",fontSize:12,color:C.text}}>{l.date}</td>
                        <td style={{padding:"8px 10px",fontSize:12,color:SITES[l.site]?.color}}>{SITES[l.site]?.name}</td>
                        <td style={{padding:"8px 10px",fontSize:12,color:C.mid,textTransform:"capitalize"}}>{l.role}</td>
                        <td style={{padding:"8px 10px",fontSize:12,color:C.dim}}>{l.agency||"Direct"}</td>
                        <td style={{padding:"8px 10px",fontSize:12,fontWeight:600,color:C.text}}>{l.locumName||"—"}</td>
                        <td style={{padding:"8px 10px",fontSize:12,color:C.mid}}>{l.hrs}h</td>
                        <td style={{padding:"8px 10px",fontSize:12,color:C.dim}}>{l.rate?`£${l.rate}/hr`:"—"}</td>
                        <td style={{padding:"8px 10px",fontSize:13,fontWeight:700,color:C.teal}}>{fmt(l.cost)}</td>
                      </tr>
                    ))}
                    <tr style={{background:C.tealPale}}>
                      <td colSpan={5} style={{padding:"8px 10px",fontSize:11,fontWeight:700,color:C.teal,textAlign:"right"}}>Total</td>
                      <td style={{padding:"8px 10px",fontSize:12,color:C.mid,fontWeight:600}}>{monthLocums.reduce((s,l)=>s+l.hrs,0)}h</td>
                      <td/>
                      <td style={{padding:"8px 10px",fontSize:13,fontWeight:700,color:C.teal}}>{fmt(monthLocumCost)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* SUMMARY TAB */}
      {tab==="summary"&&(
        <div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:14,marginBottom:20}}>
            {siteSummary.map(x=>(
              <div key={x.siteId} style={{...CS,borderTop:`3px solid ${x.si.color}`}}>
                <div style={{fontWeight:700,color:x.si.color,fontSize:15,marginBottom:14}}>{x.si.name}</div>
                {[
                  ["Staff (excl. maternity)", x.staff],
                  ["Monthly contracted",      fmt(x.total)],
                  ["Locum this month",        fmt(x.locumCost)],
                  ["Total this month",        fmt(x.grand)],
                  ["Per surgery (est.)",      fmt(Math.round(x.grand/SITES[x.siteId].surgeries))],
                ].map(([l,v])=>(
                  <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${C.border2}`,fontSize:12}}>
                    <span style={{color:C.dim}}>{l}</span>
                    <span style={{fontWeight:600,color:l==="Total this month"?x.si.color:C.text}}>{v}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Month-on-month locum trend */}
          <div style={{...CS}}>
            <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12}}>Locum spend by month</div>
            <div style={{display:"flex",gap:8,alignItems:"flex-end",height:80}}>
              {MONTHS.map(m=>{
                const cost = locums.filter(l=>l.date.startsWith(m)).reduce((s,l)=>s+l.cost,0);
                const maxCost = Math.max(...MONTHS.map(mx=>locums.filter(l=>l.date.startsWith(mx)).reduce((s,l)=>s+l.cost,0)),1);
                const h = Math.round((cost/maxCost)*64)+4;
                return (
                  <div key={m} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
                    <div style={{fontSize:9,color:cost>0?C.warn:C.dim,fontWeight:600}}>{cost>0?fmt(cost):""}</div>
                    <div style={{width:"100%",height:h,background:m===selMonth?C.warn:C.border,borderRadius:"4px 4px 0 0",minHeight:4}}/>
                    <div style={{fontSize:9,color:m===selMonth?C.teal:C.dim,fontWeight:m===selMonth?700:400}}>{MONTH_LABELS[m].slice(0,3)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Rates settings modal */}
      {settOpen&&(
        <Overlay onClose={()=>setSettOpen(false)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,fontWeight:700,color:C.heading,marginBottom:4}}>Finance Rates</div>
          <div style={{fontSize:12,color:C.dim,marginBottom:16}}>Default pay rates and statutory employer obligations</div>

          {/* Default pay rates */}
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Default hourly rates</div>
          {[
            {l:"Default nurse rate £/hr",    k:"nurseHourlyRate", step:"0.01"},
            {l:"Default reception rate £/hr", k:"recHourlyRate",  step:"0.01"},
            {l:"Locum default rate £/hr",     k:"locumRate",       step:"0.50"},
          ].map(({l,k,step})=>(
            <div key={k} style={{marginBottom:10}}>
              <label style={LB}>{l}</label>
              <input type="number" step={step} value={state.settings?.[k]||""} onChange={e=>set(s=>({...s,settings:{...s.settings,[k]:+e.target.value}}))} style={IS}/>
            </div>
          ))}

          {/* Setup & tidy time */}
          <div style={{marginBottom:10,padding:"12px 14px",background:"rgba(58,173,152,0.06)",border:"1px solid rgba(58,173,152,0.2)",borderRadius:10}}>
            <div style={{fontSize:11,fontWeight:700,color:C.teal,marginBottom:6}}>Paid setup & tidy time</div>
            <div style={{fontSize:11,color:C.dim,marginBottom:8}}>
              Time paid <strong>on top of</strong> the rota session. If a clinician's session runs 08:00–17:30, a nurse with 15 mins setup and 15 mins tidy gets paid for 08:00–17:30 <em>plus</em> 30 mins extra = 10 hours total. This is added to scheduled hours in payroll — the rota finish time does not change.
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div>
                <label style={LB}>Setup mins (before shift start)</label>
                <input type="number" step="5" min="0" max="60" value={state.settings?.setupMins??0} onChange={e=>set(s=>({...s,settings:{...s.settings,setupMins:+e.target.value}}))} style={IS}/>
              </div>
              <div>
                <label style={LB}>Tidy mins (after shift finish)</label>
                <input type="number" step="5" min="0" max="60" value={state.settings?.tidyMins??0} onChange={e=>set(s=>({...s,settings:{...s.settings,tidyMins:+e.target.value}}))} style={IS}/>
              </div>
            </div>
            {((state.settings?.setupMins||0)+(state.settings?.tidyMins||0))>0&&(
              <div style={{fontSize:11,color:C.teal,marginTop:8,fontWeight:600}}>
                +{(state.settings?.setupMins||0)+(state.settings?.tidyMins||0)} mins paid per shift ({state.settings?.setupMins||0} before + {state.settings?.tidyMins||0} after) · added on top of rota hours
              </div>
            )}
          </div>

          {/* Rest break policy */}
          <div style={{marginBottom:10,padding:"12px 14px",background:"rgba(58,173,152,0.06)",border:"1px solid rgba(58,173,152,0.2)",borderRadius:10}}>
            <div style={{fontSize:11,fontWeight:700,color:C.teal,marginBottom:4}}>Rest break policy</div>
            <div style={{fontSize:11,color:C.dim,marginBottom:10}}>
              Under Working Time Regulations 1998, staff working more than 6 hours are entitled to a 20-minute uninterrupted break.
              Set your practice policy here — shown on the rota each day.
              <a href="https://www.acas.org.uk/rest-breaks" target="_blank" rel="noopener noreferrer" style={{color:C.teal,marginLeft:4,fontSize:10}}>ACAS guide ↗</a>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
              <div>
                <label style={LB}>Break type</label>
                <div style={{display:"flex",gap:6}}>
                  {[["unpaid","Unpaid"],["paid","Paid"]].map(([v,l])=>(
                    <button key={v} onClick={()=>set(s=>({...s,settings:{...s.settings,breakPolicy:{...(s.settings?.breakPolicy||{}),paid:v==="paid"}}}))}
                      style={{flex:1,padding:"8px",borderRadius:8,fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer",
                        border:`1px solid ${(state.settings?.breakPolicy?.paid??"unpaid")===(v==="paid"?"true":"false")||(!state.settings?.breakPolicy?.paid&&v==="unpaid")?C.teal:C.border}`,
                        background:(state.settings?.breakPolicy?.paid?v==="paid":v==="unpaid")?C.tealPale:"transparent",
                        color:(state.settings?.breakPolicy?.paid?v==="paid":v==="unpaid")?C.teal:C.mid}}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={LB}>Break duration (mins)</label>
                <input type="number" step="5" min="20" max="60" value={state.settings?.breakPolicy?.durationMins??30}
                  onChange={e=>set(s=>({...s,settings:{...s.settings,breakPolicy:{...(s.settings?.breakPolicy||{}),durationMins:+e.target.value}}}))} style={IS}/>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div>
                <label style={LB}>Applies to shifts over (hours)</label>
                <input type="number" step="0.5" min="4" max="8" value={state.settings?.breakPolicy?.thresholdHrs??6}
                  onChange={e=>set(s=>({...s,settings:{...s.settings,breakPolicy:{...(s.settings?.breakPolicy||{}),thresholdHrs:+e.target.value}}}))} style={IS}/>
                <div style={{fontSize:9,color:C.dim,marginTop:3}}>Legal minimum is 6 hours (WTR 1998)</div>
              </div>
              <div>
                <label style={LB}>Default break time</label>
                <input value={state.settings?.breakPolicy?.defaultTime??"13:00"}
                  onChange={e=>set(s=>({...s,settings:{...s.settings,breakPolicy:{...(s.settings?.breakPolicy||{}),defaultTime:e.target.value}}}))} placeholder="13:00" style={IS}/>
                <div style={{fontSize:9,color:C.dim,marginTop:3}}>Shown on rota — overrideable per day</div>
              </div>
            </div>
            {(state.settings?.breakPolicy?.durationMins||30)>0&&(
              <div style={{fontSize:11,color:C.teal,marginTop:8,fontWeight:600}}>
                {state.settings?.breakPolicy?.paid?"Paid":"Unpaid"} {state.settings?.breakPolicy?.durationMins||30}-min break on shifts over {state.settings?.breakPolicy?.thresholdHrs||6}h · shown at {state.settings?.breakPolicy?.defaultTime||"13:00"} on the rota
              </div>
            )}
            <div style={{borderTop:`1px solid rgba(58,173,152,0.15)`,marginTop:12,paddingTop:12}}>
              <label style={LB}>Lunch (split-day schedules)</label>
              <div style={{fontSize:11,color:C.dim,marginBottom:8}}>
                When a clinician works a split day (e.g. 08:00–13:00 then 14:00–17:30), is the lunch gap paid or unpaid? Most practices: unpaid. Affects payroll hours calculation.
              </div>
              <div style={{display:"flex",gap:6}}>
                {[["unpaid","Unpaid"],["paid","Paid"]].map(([v,l])=>(
                  <button key={v} onClick={()=>set(s=>({...s,settings:{...s.settings,breakPolicy:{...(s.settings?.breakPolicy||{}),lunchPaid:v==="paid"}}}))}
                    style={{flex:1,padding:"8px",borderRadius:8,fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer",
                      border:`1px solid ${(state.settings?.breakPolicy?.lunchPaid?v==="paid":v==="unpaid")?C.teal:C.border}`,
                      background:(state.settings?.breakPolicy?.lunchPaid?v==="paid":v==="unpaid")?C.tealPale:"transparent",
                      color:(state.settings?.breakPolicy?.lunchPaid?v==="paid":v==="unpaid")?C.teal:C.mid}}>
                    {l}
                  </button>
                ))}
              </div>
              <div style={{fontSize:10,color:C.dim,marginTop:6}}>
                Default: unpaid. Lunch gap is excluded from paid hours unless set to paid here.
              </div>
            </div>
          </div>

          {/* HMRC NI rates */}
          <div style={{background:"rgba(245,197,24,0.08)",border:"1px solid #fed7aa",borderRadius:10,padding:"10px 14px",margin:"16px 0 10px"}}>
            <div style={{fontSize:11,fontWeight:700,color:"#f5c518",marginBottom:4}}>⚠️ HMRC Employer NI — April 2025</div>
            <div style={{fontSize:11,color:"#f5c518"}}>Only change these if HMRC updates the rates. Wrong figures = wrong payroll costs.</div>
          </div>
          {[
            {l:"Employer NI rate",                     k:"niRate",             step:"0.001", display:v=>`${(v*100).toFixed(1)}%`, note:"15% from April 2025"},
            {l:"Secondary threshold (£/week)",          k:"niThresholdWeekly",  step:"0.01",  display:v=>`£${v}/wk`,              note:"£96.15/wk from April 2025"},
          ].map(({l,k,step,note})=>(
            <div key={k} style={{marginBottom:10}}>
              <label style={LB}>{l} <span style={{fontWeight:400,color:C.muted}}>({note})</span></label>
              <input type="number" step={step} value={state.settings?.[k]??""} onChange={e=>set(s=>({...s,settings:{...s.settings,[k]:+e.target.value}}))} style={IS}/>
            </div>
          ))}

          {/* Pension rates */}
          <div style={{background:"rgba(139,92,246,0.08)",border:"1px solid #ddd6fe",borderRadius:10,padding:"10px 14px",margin:"16px 0 10px"}}>
            <div style={{fontSize:11,fontWeight:700,color:"#a78bfa",marginBottom:4}}>⚠️ Auto-Enrolment Pension — 2025/26</div>
            <div style={{fontSize:11,color:"#a78bfa"}}>3% is the statutory minimum. If your scheme is higher, update the rate here.</div>
          </div>
          {[
            {l:"Employer pension rate",          k:"pensionRate",         step:"0.001", note:"3% statutory minimum"},
            {l:"Lower earnings limit (£/week)",  k:"pensionLowerWeekly", step:"0.01",  note:"£123/wk for 2025/26"},
            {l:"Upper earnings limit (£/week)",  k:"pensionUpperWeekly", step:"1",     note:"£967/wk for 2025/26"},
          ].map(({l,k,step,note})=>(
            <div key={k} style={{marginBottom:10}}>
              <label style={LB}>{l} <span style={{fontWeight:400,color:C.muted}}>({note})</span></label>
              <input type="number" step={step} value={state.settings?.[k]??""} onChange={e=>set(s=>({...s,settings:{...s.settings,[k]:+e.target.value}}))} style={IS}/>
            </div>
          ))}

          <div style={{display:"flex",gap:8,marginTop:4}}>
            <button onClick={()=>{
              set(s=>({...s,settings:{...s.settings,niRate:0.15,niThresholdWeekly:96.15,pensionRate:0.03,pensionLowerWeekly:123,pensionUpperWeekly:967}}));
            }} style={{...BTN(C.dim,true),flex:1,fontSize:12,padding:"9px 0"}}>↺ Reset to Apr 2025 defaults</button>
            <button onClick={()=>setSettOpen(false)} style={{...BTN(C.teal),flex:1,padding:"9px 0"}}>Save</button>
          </div>
        </Overlay>
      )}
      {wageM&&<WageModal person={wageM} current={state.wages?.[wageM.id]} allSites={SITES} onSave={updateWage} onClose={()=>setWageM(null)}/>}
    </div>
  );
}


/* ══════════════════════════════════
   PULSE CHAT (floating bar)
══════════════════════════════════ */
/* ── AI TIER HELPER ──────────────────────────────────────────────
   full     → principal + managers: full chat, all actions
   sickness → head nurses: sickness & emergency cover only
   none     → everyone else, or master switch off, or no key
──────────────────────────────────────────────────────────────── */
function getAITier(user, state) {
  if (!(state?.settings?.aiMasterEnabled ?? true)) return "none";
  // API key lives in Vercel env vars — no longer required client-side
  const role = user?.role;
  if (role === "principal") return "full";
  if (role === "manager" || role === "managerLite") return "sickness"; // managers: sickness queries only
  if (role === "headNurse") return "sickness";
  return "none";
}

function PulseChat({state,set,user,currentTab,currentSite,onNavigate}) {
  const tier = getAITier(user, state);
  const [open,setOpen]       = useState(false);
  const [mode,setMode]       = useState("chat");
  // Live context vars — must be declared before initMsg
  const _pendingN = Object.values(state?.leave||{}).flat().filter(l=>l.status==="pending").length;
  const _rtwN = (state?.sick||[]).filter(s=>!s.rtw).length;
  const _practiceName = state?.practiceProfile?.practiceName||state?.practiceProfile?.name||"";
  const initMsg = tier==="sickness"
    ? `Hi — I'm Iris.${_rtwN>0?` ${_rtwN} RTW interview${_rtwN>1?"s":""} outstanding.`:""} I can help with sick cover, Bradford scores, and RTW alerts.`
    : `Hi — I'm Iris.${_pendingN>0?` You've got ${_pendingN} leave request${_pendingN>1?"s":""} waiting.`:""}${_rtwN>0?` ${_rtwN} RTW interview${_rtwN>1?"s":""} outstanding.`:""} Ask me anything or tell me what to do.`;
  const [msgs,setMsgs]       = useState([{role:"assistant",text:initMsg}]);
  const [input,setInput]     = useState("");
  const [loading,setLoading] = useState(false);
  const [fbText,setFbText]   = useState("");
  const [fbSaved,setFbSaved] = useState(false);
  const inputRef   = useRef(null);
  const msgsEndRef = useRef(null);
  // Track session token usage
  const sessionTokens = useRef({in:0,out:0});
  // Voice
  const [voiceOn, setVoiceOn]   = useState(false);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking]   = useState(false);
  const audioRef    = useRef(null);
  const recogRef    = useRef(null);
  const voiceOnRef  = useRef(false);  // mirrors voiceOn for use in callbacks
  const speakingRef = useRef(false);  // mirrors speaking for use in callbacks
  const voiceConvRef  = useRef(false); // true only when user tapped mic to start
  const audioUnlocked = useRef(false); // iOS Safari audio unlock
  const restartTimer  = useRef(null);  // guard against double-restart

  // Keep refs in sync
  useEffect(()=>{ voiceOnRef.current = voiceOn; },[voiceOn]);
  useEffect(()=>{ speakingRef.current = speaking; },[speaking]);

  const hasOpenedRef = useRef(false);
  useEffect(()=>{
    if(open&&mode==="chat") {
      setTimeout(()=>inputRef.current?.focus(),100);
      // Fire live briefing on FIRST open only (not on re-opens)
      if(!hasOpenedRef.current&&tier==="full"&&msgs.length===1) {
        hasOpenedRef.current=true;
        const userName = user?.name?.split(" ")[0]||"";
        const dateStr = new Date().toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"short"});
        const todayISO = new Date().toISOString().split("T")[0];
        // Compute live site scores for briefing context
        const scoresSummary = Object.entries(SITES).map(([sId,s])=>{
          const ps = calcIrisScore(sId,todayISO,state);
          return `${s.name} ${ps.score} (${ps.band.label})`;
        }).join("; ");
        // Compute welfare flags
        const welfareNames = Object.entries(SITES).flatMap(([sId])=>
          [...(state.nurses||NURSES_BASE),...RECS_BASE]
            .filter(n=>n.site===sId&&!n.maternity&&!n.archived)
            .filter(n=>{
              const r=analyseLatenessPatterns(n.id,state).riskLevel;
              return r==="high"||r==="medium";
            })
            .map(n=>n.name)
        );
        const welfareLine = welfareNames.length>0
          ? `Welfare flags: ${welfareNames.join(", ")}.`
          : "No welfare flags.";
        // Compute quiet week flags with cost + adhoc opportunity
        const nurseHrRate2 = state.settings?.nurseHourlyRate||14;
        const quietSummaryParts = [];
        const todayD2 = new Date(todayISO);
        Object.entries(SITES).forEach(([sid,siteInf])=>{
          const siteSurg = siteInf.surgeries||4;
          const weekMap2 = {};
          for(let i=1;i<=21;i++){
            const d2=new Date(todayD2); d2.setDate(todayD2.getDate()+i);
            const ds2=d2.toISOString().split("T")[0];
            const dow2=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d2.getDay()];
            if(dow2==="Sat"||dow2==="Sun") continue;
            const ws2=new Date(d2);
            ws2.setDate(d2.getDate()-(d2.getDay()===0?6:d2.getDay()-1));
            const wk2=ws2.toISOString().split("T")[0];
            if(!weekMap2[wk2]) weekMap2[wk2]={days:[],ovDays:0};
            const cs2=calcStaffingConfidence(sid,ds2,state);
            const openSurg=Math.max(0,siteSurg-cs2.surgeries);
            weekMap2[wk2].days.push({cs:cs2,openSurg});
            if(cs2.surgeries>0){
              const r2=cs2.effectiveNurses/Math.max(cs2.surgeries,1);
              if(r2>=1.4&&cs2.spareNurses>=2) weekMap2[wk2].ovDays++;
            }
          }
          Object.entries(weekMap2).forEach(([wk2,data2])=>{
            if(data2.ovDays>=3){
              const wkLbl2=new Date(wk2+"T12:00")
                .toLocaleDateString("en-GB",{day:"numeric",month:"short"});
              const spareTotal=data2.days
                .filter(d=>d.cs.spareNurses>=2)
                .reduce((s,d)=>s+d.cs.spareNurses,0);
              const cost=Math.round(spareTotal*7.5*nurseHrRate2/5)*5;
              const openDays=data2.days.filter(d=>d.openSurg>0).length;
              const oppTxt=openDays>0
                ?`${openDays} open surgery slot${openDays!==1?"s":""} — ad hoc opportunity`
                :"no open surgeries";
              quietSummaryParts.push(
                `${siteInf.name} w/c ${wkLbl2}: `
                +`${data2.ovDays} overstaffed days, ~£${cost} idle cost, ${oppTxt}`
              );
            }
          });
        });
        const quietLine = quietSummaryParts.length>0
          ? `Quiet weeks: ${quietSummaryParts.join("; ")}.`
          : "No quiet weeks flagged.";
        const briefingPrompt = `Morning briefing for ${userName} — ${dateStr}. `
          +`Scores: ${scoresSummary}. Pending leave: ${_pendingN}. RTW: ${_rtwN}. `
          +`${welfareLine} ${quietLine} `
          +`Summarise in 2-3 punchy sentences. Lead with the most urgent or valuable thing. `
          +`If there's an ad hoc opportunity, mention the £ idle cost and the revenue angle. `
          +`If all clear, say so warmly.`;
        const entry = {role:"user",content:briefingPrompt};
        // System prompt — split to stay under 688-char line limit
        const sysPrompt = `You are Iris, the AI layer inside Omni, a dental workforce platform. `
          +`Personality: warm, northern English, straight-talking. Be direct — maximum 3 sentences. `
          +`Practice: ${_practiceName||"this practice"}. User: ${user?.name||"the principal"}. `
          +`Site scores today: ${scoresSummary}. Pending leave: ${_pendingN}. RTW: ${_rtwN}. `
          +`${welfareLine} ${quietLine} `
          +`A quiet week is BOTH a cost risk AND a revenue opportunity. `
          +`If spare nurses + open surgeries exist, always lead with the revenue angle and £ cost. `
          +`If only spare nurses, lead with the £ idle cost and suggest unpaid leave or CPD. `
          +`Lead with the most urgent or financially significant flag. If all green, say so warmly.`;
        setLoading(true);
        fetch("/api/chat",{
          method:"POST",
          headers:{"Content-Type":"application/json","x-api-key":getStoredApiKey()||state?.settings?.anthropicKey||""},
          body:JSON.stringify({model:"claude-sonnet-4-6",max_tokens:220,system:sysPrompt,messages:[entry]}),
        }).then(r=>r.json()).then(data=>{
          const reply = data?.content?.[0]?.text;
          if(reply) setMsgs(m=>[...m,{role:"assistant",text:reply}]);
        }).catch(()=>{}).finally(()=>setLoading(false));
      }
    }
  },[open,mode]);
  useEffect(()=>{ msgsEndRef.current?.scrollIntoView({behavior:"smooth"}); },[msgs]);

  // Dynamic quick prompts based on live state
  const QUICK_FULL = [
    ...(_pendingN>0?[`Approve ${_pendingN} leave request${_pendingN>1?"s":""}`]:[]),
    ...(_rtwN>0?[`Complete ${_rtwN} RTW interview${_rtwN>1?"s":""}`]:[]),
    "Log someone sick today",
    "Any rota gaps this week?",
    "Who's in today?",
  ].slice(0,4);
  const QUICK_SICK = [
    ...(_rtwN>0?[`${_rtwN} RTW outstanding — who?`]:[]),
    "Who can cover a nurse today?",
    "Bradford scores this month",
    "Emergency cover options",
  ].slice(0,4);
  const QUICK = tier==="sickness" ? QUICK_SICK : QUICK_FULL;

  function applyAction(action) {
    if (tier!=="full") return; // head nurses cannot trigger actions
    try {
      const a = typeof action==="string" ? JSON.parse(action) : action;
      if (!a?.type) return;
      if (a.type==="log_absence") {
        const allStaff=[...(state.nurses||NURSES_BASE),...RECS_BASE,...getClinicians(state)];
        const person=allStaff.find(s=>s.id===a.staffId||s.name.toLowerCase().includes((a.name||"").toLowerCase()));
        if (!person) return;
        set(s=>({...s,sick:[...s.sick,{id:"s"+Date.now(),staffId:person.id,name:person.name,site:person.site||a.site||fallbackSite(),role:"staff",date:a.date,days:a.days||1,contact:"called",reason:"illness",certified:false,rtw:false,notes:`Logged via Iris by ${user.name}`}]}));
      }
      if (a.type==="book_leave") {
        const allStaff=[...(state.nurses||NURSES_BASE),...RECS_BASE];
        const person=allStaff.find(s=>s.id===a.staffId||s.name.toLowerCase().includes((a.name||"").toLowerCase()));
        if (!person||!a.dates?.length) return;
        const rid="req"+Date.now();
        const isBookedByPrincipal=user?.role==="principal";
        set(s=>{const ex=s.leave[person.id]||[];const ne=a.dates.map(d=>({id:"l"+Math.random().toString(36).slice(2),requestId:rid,date:d,status:isBookedByPrincipal?"approved":"pending",leaveType:"annual",note:"Booked via Iris"}));return {...s,leave:{...s.leave,[person.id]:[...ex,...ne]}};});
      }
      if (a.type==="book_clinician_leave") {
        const clins=getClinicians(state);
        const clin=clins.find(c=>c.id===a.clinicianId||c.name.toLowerCase().includes((a.name||"").toLowerCase()));
        if (!clin||!a.dates?.length) return;
        set(s=>{const ex=(s.clinicianLeave||{})[clin.id]||[];const ne=a.dates.map(d=>({id:"cl"+Math.random().toString(36).slice(2),date:d,status:"approved",type:"annual",note:"Booked via Iris"}));return {...s,clinicianLeave:{...(s.clinicianLeave||{}),[clin.id]:[...ex,...ne]}};});
      }
      if (a.type==="log_lateness") {
        const allStaff=[...(state.nurses||NURSES_BASE),...RECS_BASE];
        const person=allStaff.find(s=>s.id===a.staffId||s.name.toLowerCase().includes((a.name||"").toLowerCase()));
        if (!person) return;
        set(s=>({...s,latenessLog:[...(s.latenessLog||[]),{id:"lat"+Date.now(),staffId:person.id,name:person.name,site:person.site,date:a.date,type:a.lateType||"late",minsLate:a.minsLate||0,reason:a.reason||"",notes:"Logged via Iris",loggedBy:user.name}]}));
      }
      if (a.type==="add_clinician" && a.name && a.site) {
        const colors = ["#818cf8","#a78bfa","#3aad98","#f472b6","#f5c518","#60a5fa","#fb923c","#34d399"];
        const existing = getClinicians(state)||[];
        const newClin = {
          id: "c_"+Date.now(),
          name: a.name,
          site: a.site,
          type: a.type||"GDS",
          color: colors[existing.length % colors.length],
          sched: a.sched||{},
          prefNurse: null,
        };
        const clinReqs = {};
        if (a.sedation && a.sedation !== "none") {
          clinReqs[newClin.id] = {
            default: a.sedation==="both"?["ra","iv"]:[a.sedation],
            overrides: {},
            note: a.sedation==="both"?"IV + RA sedation":a.sedation.toUpperCase()+" sedation",
          };
        }
        set(s=>({
          ...s,
          customClinicians:[...(s.customClinicians||[]),newClin],
          clinicianRequirements:{...(s.clinicianRequirements||{}),...clinReqs},
        }));
      }
      if (a.type==="navigate" && a.tab) {
        if (onNavigate) onNavigate(a.tab, a.site||undefined, a.subTab||undefined);
      }
      if (a.type==="add_nurse" && a.name && a.site) {
        const newNurse = {
          id: "n_"+Date.now(),
          name: a.name,
          site: a.site,
          status: a.status||"qualified",
          ra: !!a.ra,
          iv: !!a.iv,
          keyholder: !!a.keyholder,
          firstAider: !!a.firstAider,
          headNurse: !!a.headNurse,
          hrs: a.hrs||37.5,
          area: "GDS",
          archived: false,
          maternity: false,
        };
        set(s=>({...s, nurses:[...(s.nurses||[]),newNurse]}));
      }
      if (a.type==="resend_invite") {
        // Single recipient: name, userId, or email
        // Bulk: scope:"all_uninvited" (Iris is instructed to confirm with the
        // user FIRST before emitting bulk -- this code fires whatever it gets,
        // the guardrail lives in the system prompt).
        const userList = state.users || [];
        const stamp = (id) => set(s=>({
          ...s,
          userInvitedAt:{...(s.userInvitedAt||{}), [id]: new Date().toISOString()},
        }));
        if (a.scope === "all_uninvited") {
          const targets = userList.filter(u =>
            u.email && u.email.trim() &&
            !((state.userInvitedAt||{})[u.id]) &&
            !((state.userLastLogin||{})[u.id]) &&
            u.active !== false
          );
          targets.forEach(u => {
            sendInviteEmail(u, state).then(r => {
              if (r.ok) stamp(u.id);
              else console.warn("[Iris invite]", u.name, "failed:", r.error);
            });
          });
        } else {
          const target = userList.find(u =>
            (a.userId && u.id === a.userId) ||
            (a.email && (u.email||"").toLowerCase().trim() === a.email.toLowerCase().trim()) ||
            (a.name && u.name && u.name.toLowerCase().includes(a.name.toLowerCase()))
          );
          if (target && target.email) {
            sendInviteEmail(target, state).then(r => {
              if (r.ok) stamp(target.id);
              else console.warn("[Iris invite]", target.name, "failed:", r.error);
            });
          }
        }
      }
    } catch(e) { console.log("Action parse error",e); }
  }

  async function speakText(text) {
    if (!text||!voiceOn) return;
    const clean = text
      .replace(/\*\*(.*?)\*\*/g,"$1")
      .replace(/\*(.*?)\*/g,"$1")
      .replace(/[→•–—]/g," ")
      .replace(/\bRTW\b/g,"return to work")
      .replace(/\bCPD\b/g,"continuing professional development")
      .replace(/\bGDS\b/g,"general dental services")
      .replace(/\bMOS\b/g,"minor oral surgery")
      .replace(/\bFD\b/g,"foundation dentist")
      .replace(/\bRA\b/g,"relative analgesia")
      .replace(/\bIV\b/g,"intravenous")
      .replace(/\bBF\b/g,"Bradford Factor")
      .replace(/\bOT\b/g,"overtime")
      .replace(/\bTCO\b/g,"treatment coordinator")
      .slice(0,600);
    // Stop mic before speaking to avoid feedback
    if (recogRef.current) { try { recogRef.current.abort(); } catch(e){} }
    setListening(false);
    setSpeaking(true);
    try {
      const r = await fetch("/api/speak",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({text:clean}),
      });
      if (!r.ok) { setSpeaking(false); scheduleRestart(); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      if (audioRef.current) { try { audioRef.current.pause(); } catch(e){} }
      const audio = new Audio();
      // iOS requires src set before play, and play() must be called synchronously-ish
      audio.src = url;
      audio.preload = "auto";
      audioRef.current = audio;
      audio.onended = () => {
        setSpeaking(false);
        try { URL.revokeObjectURL(url); } catch(e){}
        scheduleRestart();
      };
      audio.onerror = () => { setSpeaking(false); scheduleRestart(); };
      // Use a promise to catch iOS play() rejection
      const playPromise = audio.play();
      if (playPromise) playPromise.catch(()=>{ setSpeaking(false); scheduleRestart(); });
    } catch(e) { setSpeaking(false); scheduleRestart(); }
  }

  function scheduleRestart() {
    if (!voiceConvRef.current||!voiceOnRef.current) return;
    if (restartTimer.current) clearTimeout(restartTimer.current);
    restartTimer.current = setTimeout(()=>{ restartTimer.current=null; startListening(); }, 500);
  }

  function startListening() {
    if (!voiceOnRef.current) return;
    if (speakingRef.current) return; // never start while Iris is speaking
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      // Show inline message instead of alert on mobile
      setMsgs(m=>[...m,{role:"assistant",text:"Voice input isn't supported on this browser. Try Safari on iPhone or Chrome on desktop."}]);
      return;
    }
    // Clean up any existing instance first
    if (recogRef.current) { try { recogRef.current.abort(); } catch(e){} recogRef.current=null; }
    try {
      const recog = new SR();
      recog.lang = "en-GB";
      recog.continuous = false;    // iOS Safari only supports false
      recog.interimResults = false;
      recog.maxAlternatives = 1;
      recog.onstart = () => setListening(true);
      recog.onend = () => {
        setListening(false);
        recogRef.current = null;
        // Only restart if no speech was detected (user didn't say anything)
        // If speech was detected, send() will trigger speakText → scheduleRestart
        if (voiceOnRef.current && !speakingRef.current) {
          scheduleRestart();
        }
      };
      recog.onerror = (e) => {
        setListening(false);
        recogRef.current = null;
        const nonFatal = ["no-speech","audio-capture","aborted"];
        if (nonFatal.includes(e.error) && voiceOnRef.current && !speakingRef.current) {
          scheduleRestart();
        }
      };
      recog.onresult = e => {
        const transcript = Array.from(e.results)
          .map(r=>r[0].transcript).join(" ").trim();
        if (transcript) {
          // Stop recognition before sending — prevents double-fire on iOS
          try { recog.abort(); } catch(ex){}
          recogRef.current = null;
          send(transcript);
        }
      };
      voiceConvRef.current = true;
      recogRef.current = recog;
      recog.start();
    } catch(e) {
      setListening(false);
      recogRef.current = null;
    }
  }

  async function send(text) {
    if (!text.trim()||loading) return;
    if (tier==="none") return;
    const apiKey = getStoredApiKey() || state?.settings?.anthropicKey || "";
    // apiKey may be empty — key lives in Vercel env var, proxy handles auth
    const userMsg=text.trim();
    setInput("");
    setMsgs(m=>[...m,{role:"user",text:userMsg}]);
    setLoading(true);

    const todayStr=new Date().toISOString().split("T")[0];
    const liveDay=CAL.days.find(d=>d.date>=todayStr&&!d.bh)||CAL.days[0];
    const allNurses=state.nurses||NURSES_BASE;
    const allClins=getClinicians(state);
    const pending=Object.values(state.leave||{}).flat().filter(l=>l.status==="pending").length;
    const rtw=(state.sick||[]).filter(s=>!s.rtw).length;

    // Active delegations context
    const activeDelegations=(state.delegations||[]).filter(d=>d.active!==false&&d.endDate>=todayStr);
    const delContext = activeDelegations.length>0
      ? activeDelegations.map(d=>{
          const conflictDates = [];
          const start=d.startDate, end=d.endDate;
          CAL.days.filter(x=>x.date>=start&&x.date<=end).forEach(x=>{
            if(isAbsent(state,d.toStaffId,x.date)) conflictDates.push(x.date);
          });
          return `${d.toName} covering for ${(allNurses.find(n=>n.id===d.fromStaffId)||RECS_BASE.find(r=>r.id===d.fromStaffId))?.name||d.fromStaffId} (${d.startDate}→${d.endDate})${conflictDates.length>0?` delegate also absent ${conflictDates.join(",")}`:""}`; 
        }).join("; ")
      : "none";

    // Site-scope for head nurses
    const userSite = user?.site;
    const scopedNurses = tier==="sickness"
      ? allNurses.filter(n=>n.site===userSite&&!n.maternity&&!n.archived)
      : allNurses.filter(n=>!n.maternity&&!n.archived);
    const scopedClins = tier==="sickness"
      ? allClins.filter(c=>!c._deleted&&(c.sites||[c.site]).includes(userSite))
      : allClins.filter(c=>!c._deleted);
    const scopedSick = tier==="sickness"
      ? (state.sick||[]).filter(s=>s.site===userSite)
      : (state.sick||[]);

    // Bradford for sickness tier
    const bfSummary = tier==="sickness" ? scopedNurses.map(n=>{
      const recs=scopedSick.filter(r=>r.staffId===n.id);
      const eps=recs.length; const days=recs.reduce((a,r)=>a+r.days,0);
      const bf=eps*eps*days;
      return bf>0?`${n.name}: BF ${bf} (${eps} episodes, ${days} days)`:"";
    }).filter(Boolean).join("; ") : "";

    const upcomingDays=CAL.days.filter(d=>d.date>=todayStr&&!d.bh).slice(0,10);
    const upcomingAbsences=[...scopedNurses,...(tier==="full"?RECS_BASE:[]),...scopedClins].flatMap(s=>
      upcomingDays.filter(d=>isAbsent(state,s.id,d.date)).map(d=>`${s.name} off ${d.dow} ${d.label.split(" ")[1]}`)
    ).slice(0,20);

    const sickToday = scopedSick.filter(s=>{
      const st=new Date(s.date),en=new Date(s.date);en.setDate(en.getDate()+s.days-1);
      const today=new Date(todayStr);return today>=st&&today<=en&&!s.rtw;
    });

    let context = "";
    if (tier==="sickness") {
      context=`You are Iris, the intelligent AI assistant inside Omni Rota. You are warm, precise and proactive.
Today: ${liveDay.label} (${todayStr}). User: ${user.name} (Head Nurse, ${SITES[userSite]?.name}).
${rtw} RTW interviews outstanding at ${SITES[userSite]?.name}.

YOUR SCOPE -- ${SITES[userSite]?.name} only:
Nurses: ${scopedNurses.map(n=>n.name+"["+n.id+"] "+n.status+(n.ra?" RA":"")+(n.iv?" IV":"")+(isAbsent(state,n.id,todayStr)?" ABSENT":"")).join("; ")}.
Clinicians today: ${scopedClins.filter(c=>{const s=c.siteScheds?.[userSite]||c.sched;const dow=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][new Date(todayStr).getDay()];return s?.[dow]&&!isAbsent(state,c.id,todayStr);}).map(c=>c.name).join(", ")||"none"}.
Currently sick: ${sickToday.map(s=>s.name).join(", ")||"none"}.
RTW outstanding: ${scopedSick.filter(s=>!s.rtw).map(s=>s.name).join(", ")||"none"}.
Bradford scores: ${bfSummary||"none above 0"}.
UPCOMING ABSENCES: ${upcomingAbsences.join("; ")||"none"}.

COVER OPTIONS -- qualified RA nurses available today: ${scopedNurses.filter(n=>n.ra&&!isAbsent(state,n.id,todayStr)).map(n=>n.name).join(", ")||"none"}.
IV-qualified nurses: ${scopedNurses.filter(n=>n.iv&&!isAbsent(state,n.id,todayStr)).map(n=>n.name).join(", ")||"none"}.
SICKNESS CONTACTS: ${(()=>{const sc=state.settings?.sicknessContact||{};const allS=[...(state.nurses||NURSES_BASE),...RECS_BASE,...(state.customRecs||[])];return Object.entries(sc).map(([site,id])=>{const p=allS.find(s=>s.id===id);return p?`${SITES[site]?.name||site}: ${p.name} (primary contact)`:"";}).filter(Boolean).join("; ")||"not set — encourage principal to set in Admin → Settings → Sickness"})()}.
To change sickness contacts: Admin → Settings → Sickness tab. Head nurse and principal always have full logging access regardless.
- Only answer sickness, absence, RTW, Bradford, and emergency cover questions.
- If asked about anything else (leave booking, rota editing, finance, other sites) politely say that's outside your access.
- Be concise. Max 3 sentences unless listing.
- Do NOT include action tags -- you cannot take actions, only advise.`;
    } else {
      // Detect if this is a new practice with no data yet
      const hasNurses = scopedNurses.length > 0;
      const hasClins = scopedClins.length > 0;
      const isNewPractice = !hasNurses && !hasClins && !IS_DCO;
      const practiceInfo = state?.practiceProfile ? `Practice: ${state.practiceProfile.practiceName||"this practice"}. Sites: ${(state.practiceSites||[]).map(s=>s.name).join(", ")||"not set up yet"}.` : "Practice not fully set up yet.";

      // Cover-options ladder — computed per tenant from state.nurses + SITES.
      // Step (2) flex-nurse line is only included if the tenant has any
      // flex nurses configured. For tenants with none (e.g. Flourish today)
      // the ladder collapses to (1) Spare → (2) Omni Bank → ... — no
      // "no flex nurses configured" placeholder. Each named site renders
      // with proper grammar (1 / 2 / 3+ nurses).
      const flexNurses = (state.nurses || NURSES_BASE || []).filter(n => n.flexible && !n.archived && !n.maternity);
      const flexBySite = {};
      for (const n of flexNurses) {
        if (!flexBySite[n.site]) flexBySite[n.site] = [];
        flexBySite[n.site].push(n.name);
      }
      const renderNames = (names) => {
        if (names.length <= 1) return names[0] || '';
        if (names.length === 2) return `${names[0]} and ${names[1]}`;
        return `${names.slice(0,-1).join(', ')} and ${names[names.length-1]}`;
      };
      const flexParts = Object.entries(flexBySite).map(([sid,names]) => `${renderNames(names)} at ${SITES[sid]?.name||sid}`);
      const flexSentence = flexParts.join('; ');
      const coverSteps = ['(1) Spare nurses already on site.'];
      if (flexSentence) {
        coverSteps.push(`(${coverSteps.length+1}) Flexible/pool nurses (preferred at their base site, available cross-site if no closer option): ${flexSentence}.`);
      }
      coverSteps.push(`(${coverSteps.length+1}) Omni Bank — Admin → Omni Bank.`);
      coverSteps.push(`(${coverSteps.length+1}) Cover Pool — Rota → Cover Pool.`);
      coverSteps.push(`(${coverSteps.length+1}) Agency locum — Agencies tab.`);
      const coverOptions = `When suggesting cover, work through in order: ${coverSteps.join(' ')} Always check RA/IV competency before suggesting someone for sedation cover.`;

      context=`You are Iris \u2014 the AI intelligence layer inside Omni, a dental workforce platform for UK dental practices.\n
\n
PERSONALITY: Warm, northern English, straight-talking, dry wit. You are the smartest practice manager they've never had \u2014 and you can hear and speak. Short punchy replies \u2014 never more than 3 sentences unless listing or in setup mode. Never say "Great!" or "Certainly!" or corporate filler. Prefer action over instruction \u2014 if you can do it, do it. If you can't, guide precisely.\n
\n
WHAT YOU CAN DO \u2014 when asked "what can you do" or similar, give this confident answer:\n
You watch the rota so they don't have to. Brief them every morning on scores, gaps, and flags. Log sick staff and find cover instantly. Book and approve leave. Spot quiet weeks before they cost money \u2014 and flip them into ad hoc revenue. Flag sedation cover gaps, missing compliance roles, and RTW interviews. Add clinicians and nurses. Answer anything about the team \u2014 Bradford scores, TOIL balances, who's qualified for what. Surface the financial picture: locum spend, idle wages, TOIL liability. And you can hear them and talk back \u2014 just tap the mic.\n
\n
TODAY: ${liveDay.label} (${todayStr}). User: ${user.name} (${user.role}).\n
${practiceInfo}\n
${pending} leave requests pending. ${rtw} RTW interviews outstanding.\n
MANAGERS ON SITE TODAY: ${(()=>{ const mgrs=(state.users||[]).filter(u=>(u.role==="manager"||u.role==="managerLite")&&!isAbsent(state,u.staffId||u.id,todayStr)); return mgrs.length>0?mgrs.map(u=>u.name+"("+SITES[u.site]?.name+")").join(", "):"none logged in today"; })()}.\n
SICKNESS CONTACTS: ${(()=>{const sc=state.settings?.sicknessContact||{};const allS=[...(state.nurses||NURSES_BASE),...RECS_BASE,...(state.customRecs||[])];const lines=Object.entries(sc).map(([site,id])=>{const p=allS.find(s=>s.id===id);return p?SITES[site]?.name+": "+p.name:"";}).filter(Boolean);return lines.length>0?lines.join("; "):"not configured — set in Admin → Settings → Sickness";})()}. Head nurse and principal always have full absence logging access. To change: Admin → Settings → Sickness.\n
\n
STAFF:\n
Nurses (${scopedNurses.length}): ${scopedNurses.length > 0 ? scopedNurses.map(n=>n.name+"["+n.id+"] "+(SITES[n.site]?.name||n.site)+" "+n.status+(n.ra?" RA":"")+(n.iv?" IV":"")+(n.keyholder?" KEYHOLDER":"")+(n.firstAider?" FIRST-AIDER":"")+(n.safeguardingL3?" L3-SAFEGUARDING":"")+(n.fireMarshal?" FIRE-MARSHAL":"")+(isAbsent(state,n.id,todayStr)?" ABSENT":"")).join("; ") : "none entered yet"}.\n
Clinicians (${scopedClins.length}): ${scopedClins.length > 0 ? scopedClins.map(c=>c.name+"["+c.id+"] "+(SITES[c.site]?.name||c.site)+" "+c.type+(c.keyholder?" KEYHOLDER":"")+(c.firstAider||c.compliance?.firstAider?" FIRST-AIDER":"")+(c.safeguardingL3||c.compliance?.safeguarding?" L3-SAFEGUARDING":"")+(c.fireMarshal||c.compliance?.fireMarshal?" FIRE-MARSHAL":"")+(isAbsent(state,c.id,todayStr)?" ABSENT":"")).join("; ") : "none entered yet"}.\n
Reception: ${RECS_BASE.length > 0 ? RECS_BASE.map(r=>r.name+"["+r.id+"] "+(SITES[r.site]?.name||r.site)).join("; ") : "none entered yet"}.\n
UPCOMING ABSENCES: ${upcomingAbsences.join("; ")||"none logged"}.\n
ACTIVE DELEGATIONS: ${delContext}.\n
LOCUM SPEND THIS MONTH: ~\u00a3${(()=>{const m=todayStr.slice(0,7);return Math.round((state.locums||[]).filter(l=>l.date.startsWith(m)).reduce((s,l)=>s+l.cost,0));})()}. TOTAL TO DATE: ~\u00a3${Math.round((state.locums||[]).reduce((s,l)=>s+l.cost,0))}.\n
TOIL LIABILITY: ${(()=>{const bal=state.toilBalances||{};const total=Object.values(bal).flat().reduce((s,f)=>s+(f.hrs||0),0);const rate=state.settings?.nurseHourlyRate||14;return total>0?total.toFixed(1)+"h banked (~\u00a3"+Math.round(total*rate)+" if paid out)":"none banked";})()}.\n
COMPLIANCE TASKS: ${(()=>{const sites=Object.keys(SITES);const ov=[];sites.forEach(sid=>{COMPLIANCE_TASKS.forEach(t=>{const last=getLastCompletion(state,t.id,sid);if(isComplianceOverdue(t,last)){const days=last?Math.floor((Date.now()-new Date(last.date).getTime())/86400000):null;ov.push((SITES[sid]?.name||sid)+" - "+t.label+(days?" ("+days+"d overdue)":"(never done)"));}});});return ov.length>0?ov.join("; "):"all up to date";})()}.\n
USER ACCOUNTS: ${(()=>{const us=(state.users||[]).filter(u=>u.active!==false);const inv=state.userInvitedAt||{};const last=state.userLastLogin||{};const uninv=us.filter(u=>u.email&&u.email.trim()&&!inv[u.id]&&!last[u.id]);const active=us.filter(u=>last[u.id]).length;const invited=us.filter(u=>inv[u.id]&&!last[u.id]).length;const noEmail=us.filter(u=>!u.email||!u.email.trim()).length;return us.length+" active. "+active+" logged in. "+invited+" invited not yet in. "+uninv.length+" uninvited"+(uninv.length>0?" ("+uninv.slice(0,8).map(u=>u.name).join(", ")+(uninv.length>8?" +"+(uninv.length-8):"")+")":"")+". "+noEmail+" no email on file.";})()}\n
\n
== SCORES AND SIGNALS ==\n
IRIS SCORE: Safety and quality signal per site. Weighted: nurse competency 50%, clinician presence 30%, reception coverage 20%. Above 80 is good, 60-79 amber, below 60 is a risk. When asked why a score is low \u2014 name the exact nurses absent or missing qualifications dragging it down.\n
CONFIDENCE SCORE: Separate from Iris Score. Measures predictability and stability. Three layers: (1) Coverage ratio \u2014 nurses vs open surgeries, optimal 1.0-1.25x. (2) Bradford risk \u2014 high-BF staff reduces predictability. (3) Lateness patterns \u2014 welfare flags reduce confidence. Tap the confidence card on dashboard to drill in. Never confuse Iris Score and Confidence Score.\n
BRADFORD FACTOR: S squared x D (S=episodes, D=days absent). Bands: 0-44 Low, 45-99 Caution, 100-199 High, 200+ Critical. Always a welfare signal \u2014 never disciplinary. Proactively flag anyone approaching a band boundary. Suggest a quiet conversation.\n
\n
== COMPLIANCE INTELLIGENCE ==\n
Qualifications per nurse are in the staff list above (RA, IV, FIRST-AIDER, L3-SAFEGUARDING, FIRE-MARSHAL).\n
SEDATION: Cross-reference clinicians requiring RA/IV against nurses present. If a sedation clinician has no qualified nurse today \u2014 flag as urgent. Name exactly who is running sedation and whether they have cover. Surface this proactively in morning briefs.\n
SAFEGUARDING L3: Name who holds it per site. If none \u2014 CQC risk. Direct to Admin \u2192 Staff Records \u2192 tap nurse \u2192 Compliance.\n
FIRE MARSHAL: Name holder per site, flag if missing. Admin \u2192 Staff Records \u2192 Compliance.\n
FIRST AIDER: Name holder per site, flag if missing. Admin \u2192 Staff Records \u2192 Compliance.\n
TRAINING SUGGESTIONS: Near-qualifying or compliance gap \u2014 suggest training proactively. This is the Omni Learn hook.\n
RECONFIGURATION: Clinician schedules: Admin \u2192 Clinicians. Nurse qualifications and compliance: Admin \u2192 Staff Records \u2192 tap name \u2192 Compliance.\n
\n
== FINANCIAL INTELLIGENCE ==\n
QUIET WEEKS: 3+ overstaffed days. Always state idle wage cost and whether open surgeries exist. If yes \u2014 revenue opportunity: book an ad hoc clinic. Navigate: tab rota, subTab adhoc. If no open surgeries \u2014 cost mitigation: unpaid leave, CPD, tasks.\n

NURSE ON RECEPTION: A spare nurse can cover reception for the day. On the rota day card, spare nurses show a "→ Reception" button. Tap it to assign them to reception cover. They appear on the reception panel with a "nurse cover" badge and still count as on-site for compliance. Pay is unchanged (contracted hours). If reception is short and a qualified nurse is spare, suggest using the "→ Reception" button on their spare row. Common in many practices -- not unusual, can be an advantage.
LOCUM COSTS: Running total is above. If spend is high \u2014 suggest reviewing whether bank nurses or cover pool could cover cheaper.\n
TOIL LIABILITY: Total hours and pound equivalent is above. High balances above 7.5h per person are a liability risk. Quiet weeks are the right time to clear them.\n
\n
== COMPLIANCE TASKS ==\n
Location: Admin -> Compliance tab (subTab comply). Visible to principal, manager, headNurse. Badge on tab shows overdue count.\n
Six statutory checks -- all CQC-inspectable. Delegatable to specific staff. Also appear on the staff portal home for the assigned person.\n
FIRE ALARM TEST (weekly) -- Regulatory Reform (Fire Safety) Order 2005. CQC Mythbuster 21 expects weekly call point tests and a fire logbook. This was previously set to monthly -- weekly is correct per CQC guidance.\n
EMERGENCY DRUGS CHECK (weekly) -- CQC Reg 12 + Reg 17. Mythbuster 4. GDC requires all staff trained for medical emergencies with current drugs.\n
STEP WEDGE TEST (monthly) -- IRR 2017 + IR(ME)R 2017. CQC Mythbuster 3. QA programme required for any practice with X-ray equipment.\n
LEGIONELLA CHECK (monthly) -- Health and Safety at Work Act 1974 + HSE ACOP L8. CQC Mythbuster 5, Reg 15. Temperature checks, flushing log, visual inspection per risk-based control scheme.\n
SHARPS AUDIT (quarterly) -- CQC Reg 12 + Reg 15. Mythbuster 7. Health and Safety (Sharp Instruments in Healthcare) Regulations 2013. Covers sharps handling, disposal, and safer sharps compliance.\n
COSHH REVIEW (annual) -- Control of Substances Hazardous to Health Regulations 2002. CQC Mythbuster 41. Covers disinfectants, amalgam, nitrous oxide, X-ray chemicals -- register, safety data sheets, staff awareness.\n
Issue Found flow: if a check reveals a problem, staff press "Mark Done + flag issue". This immediately creates a site notice in Issues. Tier 1 = operational (e.g. stock to reorder). Tier 2 = concern. Tier 3 = immediate safety risk (e.g. out-of-date drugs in use) -- affects Iris Score and flags site as unsafe until resolved.\n
When surfacing overdue compliance tasks: name the task, the site, days overdue, the regulation, and navigate to Admin -> Compliance. Always name the legal basis -- this is a statutory obligation, not a suggestion.\n
OMNI COMPLY (coming): full policy library, CQC evidence packs, legislation alerts.\n
Location: Attendance \u2192 Time in Lieu tab (subTab toil). Only available if toilPolicy.enabled is true \u2014 if disabled, direct to Admin \u2192 Settings \u2192 TOIL and Leave. When approving overtime \u2014 choose Pay or Bank as TOIL. TOIL expires per policy. Balances above 7.5h flagged. Unused TOIL paid out on leaving.\n
\n
== PAY POLICY & EMPLOYMENT LAW ==\n

OMNI FLAG ONLY — never advise, always signpost. ACAS (acas.org.uk) is the plain-English authority on UK employment law.\n

PAY TYPES: Hourly staff pay changes monthly based on rota hours. Salaried staff receive annual salary ÷ 12 each month, fixed. Set in Admin → Wages → Set/Edit. Multi-site salaried staff have site attribution — see Wages → All sites.\n

CONTRACT FLOOR: If rota hours fall below contracted hours, employer owes contracted pay unless unpaid leave is agreed. Flag and cite: acas.org.uk/contracts-of-employment\n

REST BREAKS (Working Time Regulations 1998): 20-minute break entitlement for shifts over 6 hours. Practice policy set in Admin → Finance → Rates → Rest break policy. If session exceeds 6h with no break — flag: “WTR 1998 entitles this worker to a 20-minute break. See acas.org.uk/rest-breaks”. Never say the practice is in breach.\n

MINIMUM WAGE 2025/26: £12.21/hr (21+), £10.00 (18-20), £7.55 (16-17), £6.40 (apprentice). Flag below-minimum rates. acas.org.uk/national-minimum-wage\n

HOLIDAY PAY: Statutory 5.6 weeks includes bank holidays unless contract says otherwise. acas.org.uk/holiday-entitlement\n

SETUP & TIDY TIME: Paid minutes added ON TOP of the rota session. If a session runs 08:00-17:30 and setup is 15 mins + tidy 15 mins, the nurse is paid 10 hours total -- rota finish time does not change. Set in Admin → Finance → Rates → Paid setup & tidy time.

SPLIT-DAY SCHEDULES (LUNCH): Clinicians can have AM and PM sessions with a lunch gap. Admin → Clinicians → Edit → schedule grid: fill AM Start/Finish then PM Start/Finish. Lunch gap shows on rota day card. Nurses take same break as paired clinician. Stored as pipe format e.g. 08:00-13:00|14:00-17:30.

TWO-WEEK ROTA PATTERNS: Any clinician day can be Week A only or Week B only. Set in Admin → Clinicians → Edit → Session frequency below the schedule grid. Rota shows WEEK A or WEEK B badge each week. Week A reference date set in Admin → Settings → Rota. If user asks which week they are in, calculate from state.settings.weekAStart.

== CARERS LEAVE ==\n
Statutory 5 days unpaid per leave year. Cannot be refused. No Bradford impact. Pattern on Fridays \u2014 welfare conversation, not disciplinary. Track remaining entitlement.\n
\n
== CPD AND TRAINING ==\n
CPD in Omni is about scheduling training sessions \u2014 not just tracking qualifications. Location: Rota \u2192 CPD and Training subtab (subTab cpd). Log: Training day, CPD session, CPR, Practice Meeting, Safeguarding, Radiography, Appraisal, Induction. Sessions attach to dates and staff. Cross-reference with quiet weeks: 6 nurses spare w/c 20 Apr \u2014 want to schedule a CPD session instead of paying idle wages?\n
\n
== SHIFT SWAPS ==\n
Staff request shift swaps with each other. Managers approve or decline. Location: Rota \u2192 Swaps (subTab swaps). Pending swaps show a badge.\n
\n
== COVER OPTIONS ==\n
${coverOptions}\n
\n
== RECEPTION CONTRACTS ==\n
Location: Rota \u2192 Reception (subTab reception). Shift patterns: Early 07:45-13:45, Early 08:00-14:00, Full 08:00-17:30, Late 14:00-20:00, or Custom. Patterns drive auto-assignment. Multi-late flag means willing to do more than one late per week.\n
\n
== FREE SESSIONS ==\n
Location: Rota \u2192 Free Sessions (subTab free). Open surgery slots by day \u2014 revenue opportunities where a clinician is absent and a room is empty.\n
\n
== PRACTICE EVENTS ==\n
Location: Rota \u2192 Events (subTab events). Log: Partners Meeting, Management Meeting, Practice Meeting, CPD sessions, Training days, College dates. Events appear on the rota and staff calendars.\n
\n
== HOLIDAY KPI ==\n
Location: Rota \u2192 Holiday KPI (subTab kpi). Leave taken vs entitlement per staff member. Flags year-end liability.\n
\n
== REPORTS ==\n
Location: Admin \u2192 Reports (subTab reports). Generates: holiday reports, Bradford exports, CPD summaries, hours reports. When asked for any report \u2014 navigate there.\n
\n
== ADMIN TABS ==\n
Staff Records: edit nurse and reception qualifications, compliance roles, training level, hours, working days, role elevation. To set which days someone works: Admin → Staff Records → ✏️ Edit on their card → Working days. Toggle off any days they don't work — rota excludes them immediately.\n
Clinicians: add and edit clinician schedules, sedation requirements, leave.\n
Wages: hourly rates per staff member.\n
Hours and Contracts: weekly hours log, contract risks, unpaid leave manager.\n
Finance: locum cost tracking, wage summaries.\n
Agencies: locum agency directory with rates.\n
Omni Bank: bank and flexible staff roster for cross-site cover.\n
Reports: holiday reports, Bradford exports, CPD summaries.\n
Settings: TOIL policy, leave year, hourly rates, practice profile, user accounts.\n
Updates: principal-only news management for the dashboard strip.\n
\n
== STAFF PORTAL ==\n
Staff log in with their PIN and see: Home (their shift, leave balance, alerts), Leave (request leave, view status), My Rota (personal 4-week view with shift times and clinician pairings). Staff can export their rota to Apple/Google Calendar as an ICS file. When onboarding a new nurse \u2014 explain what they will see when they log in.\n
\n
== CLINICIAN PORTAL ==\n
Clinicians log in using Chair mode \u2014 toggle in the top bar. They see their personal schedule, nurse pairings, CPD sessions, and leave. If a dentist asks how to see their rota \u2014 tell them to toggle to Chair mode top right.\n
\n
== PEOPLE TAB ==\n
Welfare flags, Bradford scores, attendance patterns, staff profiles. Unsung Heroes lets staff nominate colleagues for going above and beyond \u2014 a recognition and retention tool. READ-MOSTLY. To edit qualifications or compliance: Admin \u2192 Staff Records.\n
\n
== SICKNESS TAB ==\n
Bradford scores per person, active absences, RTW interviews, fit note logging, welfare flags. RTW interview form is structured. Sickness is for absence management only \u2014 not lateness or overtime.\n
\n
== ISSUES TAB ==\n
Incident reporting ONLY. Tier 1 Operational, Tier 2 Concern, Tier 3 Serious (safeguarding/whistleblowing \u2014 Employment Rights Act 1996). Never send someone here for lateness, sickness, or HR matters.\n
\n
== DELEGATION ==\n
A manager can be delegated to act-up as principal. Set up in Rota \u2192 Delegation panel for the site. Delegated manager sees an acting banner and gets full access for their site.\n
\n
${isNewPractice ? "SETUP MODE: This practice has no staff data yet. Collect information conversationally one question at a time. For clinicians: name, days, hours, sedation. For nurses: name, qualified/nearQual/trainee, RA, IV, keyholder, first aider. Confirm and use add_clinician or add_nurse action. Do not tell the user to go to any tab." : ""}\n
\n
== APP LAYOUT ==\n
TODAY: dashboard, Iris Score per site (tap to explain), Confidence Score, quiet week alerts, compliance flags, sickness alerts, RTW flags, leave approval queue, locum cost summary, Updates strip.\n
ROTA subtabs: Rota, Leave and Holidays, Swaps, Keyholders, Reception, Events, Ad-hoc, Free Sessions, Holiday KPI, Bank Holidays, Cover Pool, CPD and Training.\n
ATTENDANCE subtabs: Overtime, Time in Lieu, Early Finish, Lateness, Extra Shifts, Unpaid Leave, Wellbeing, OT Prefs, OT Export.\n
PEOPLE: welfare flags, Bradford, staff profiles, Unsung Heroes.\n
SICKNESS: Bradford scores, active absences, RTW interviews, fit notes.\n
ISSUES: incident reporting only.\n
ADMIN: Staff Records, Clinicians, Wages, Hours and Contracts, Finance, Agencies, Omni Bank, Reports, Settings, Updates.\n
\n
== ACTIONS ==\n
<action>{"type":"log_absence","staffId":"[id]","name":"[name]","date":"YYYY-MM-DD","days":1}</action>\n
<action>{"type":"book_leave","name":"[name]","dates":["YYYY-MM-DD"]}</action>\n
<action>{"type":"book_clinician_leave","name":"[name]","dates":["YYYY-MM-DD"]}</action>\n
<action>{"type":"log_lateness","name":"[name]","date":"${todayStr}","lateType":"late","minsLate":20,"reason":"[reason]"}</action>\n
<action>{"type":"add_clinician","name":"[name]","site":"[site-id]","type":"GDS","sched":{"Mon":"08:00-17:30"},"sedation":"none|ra|iv|both"}</action>\n
<action>{"type":"add_nurse","name":"[name]","site":"[site-id]","status":"qualified|nearQual|trainee","ra":false,"iv":false,"keyholder":false,"firstAider":false}</action>\n
<action>{"type":"resend_invite","name":"[staff name]"}</action>  // single recipient — fires immediately\n
<action>{"type":"resend_invite","scope":"all_uninvited"}</action>  // bulk — ONLY emit AFTER user explicitly confirms\n
<action>{"type":"navigate","tab":"today|rota|attendance|people|sickness|issues|admin","subTab":"toil|overtime|lateness|earlyfinish|extrashift|unpaid|welfare|leave|adhoc|cpd|directory|clinicians|settings|reports|events|swaps|reception|free|kpi|cover|bank|comply"}</action>\n
LATENESS: Use log_lateness action directly. Never navigate to Issues or Rota for lateness.\n
USER INVITES: Lives at People \u2192 User Accounts (principal + manager only). Counts and uninvited names are in the USER ACCOUNTS line above.\n
INVITE ACTION: resend_invite fires the email. Single recipient is one tap -- no confirmation needed. BULK (scope all_uninvited) MUST be confirmed first: name the count, list the first few, ASK to proceed, only emit the action tag after they reply yes.\n
INVITE NOTES: Invites land within a minute. Recipient taps the link to log in -- no password. Active users don't need re-inviting. If a record shows "Failed", suggest checking the browser console for the [Omni invite] line.\n
\n
== RULES ==\n
- Never invent staff names or dates. Exact names, YYYY-MM-DD dates.\n
- Prefer actions over navigation. If no action exists: tab then subtab then what to tap.\n
- Always confirm before acting on something irreversible.\n
- Max 3 sentences unless listing or in setup mode.\n
- Never say contact support \u2014 you are the support. Always answer.\n
- NEVER write Done rota updated in your reply text. That phrase is only appended by the app when a state-changing action processes. After navigate \u2014 confirm destination in one sentence only.\n
- You have full voice capability \u2014 never say you are text-only or mention your technical implementation.\n
- If the user asks to see something \u2014 navigate using the navigate action.\n
- CLINICIAN GUARD: Verify clinician exists in the list before scheduling or booking leave. If not: that person is not set up yet, add them in Admin Clinicians first.\n
- CONFIDENCE vs IRIS SCORE: Never confuse these. Iris Score = safety and quality. Confidence Score = predictability and stability. Both on dashboard, both tappable.\n
- BRADFORD GUARD: Always frame Bradford as welfare signal. Never suggest disciplinary action.\n
- PEOPLE TAB: Read-mostly. To edit qualifications \u2014 Admin Staff Records.\n
- ISSUES TAB: Incident reporting only. Never send someone here for lateness, sickness, or HR.\n
`;

    }

    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": getStoredApiKey()||state?.settings?.anthropicKey||"",
        },
        body: JSON.stringify({
          model:"claude-sonnet-4-6",
          max_tokens:600,
          system:context,
          messages:(()=>{
            const mapped = msgs
              .filter((_,i)=>i>0)
              .map(m=>({role:m.role==="user"?"user":"assistant",content:m.text||""}))
              .filter(m=>m.content.length>0);
            // Anthropic requires conversation ending with user message
            while(mapped.length>0 && mapped[mapped.length-1].role==="assistant") mapped.pop();
            return mapped.length>0 ? mapped : [{role:"user",content:userMsg}];
          })(),
        }),
      });
      const data = await res.json();
      if (data.error) {
        setMsgs(m=>[...m,{role:"assistant",text:`Error: ${data.error?.message||data.error?.error?.message||JSON.stringify(data.error)}`}]);
        setLoading(false); return;
      }
      let reply = data.content?.[0]?.text || "Sorry, couldn't get a response.";

      // Track token usage
      const tIn  = data.usage?.input_tokens||0;
      const tOut = data.usage?.output_tokens||0;
      sessionTokens.current.in  += tIn;
      sessionTokens.current.out += tOut;
      // Cost: Sonnet ~$3/M input, ~$15/M output → GBP approx *0.79
      const costGBP = ((tIn/1000000)*3 + (tOut/1000000)*15) * 0.79;

      // Log usage
      set(s=>{
        const entry={id:"au"+Date.now(),userId:user.id||user.name,userName:user.name,role:user.role,site:user.site||"all",date:todayStr,tokensIn:tIn,tokensOut:tOut,costGBP:parseFloat(costGBP.toFixed(5))};
        return {...s,aiUsage:[...(s.aiUsage||[]),entry]};
      });

      // Extract and execute action tags (full tier only)
      const actionMatch = reply.match(/<action>([\s\S]*?)<\/action>/g);
      if (actionMatch&&tier==="full") {
        let hasClinicianAdd = false;
        let hasStateChange = false;
        actionMatch.forEach(tag=>{
          const json=tag.replace(/<\/?action>/g,"").trim();
          applyAction(json);
          try{
            const a=JSON.parse(json);
            if(a.type==="add_clinician") hasClinicianAdd=a.name||true;
            // Only flag state change for actions that actually change data
            if(["log_absence","book_leave","book_clinician_leave","log_lateness",
                "add_clinician","add_nurse"].includes(a.type)) hasStateChange=true;
          }catch(e){}
        });
        reply=reply.replace(/<action>[\s\S]*?<\/action>/g,"").trim();
        if(hasClinicianAdd) {
          reply+="\n\n→ Head to **Admin → Clinicians** to set their shift schedule and link their Omni account. Without a schedule, they won't appear on the rota.";
        } else if(hasStateChange) {
          reply+="\n\nDone — rota updated.";
        }
      }

      const finalMsgs=[...msgs,{role:"user",text:userMsg},{role:"assistant",text:reply}];
      setMsgs(m=>[...m,{role:"assistant",text:reply}]);
      if (voiceOn) speakText(reply);

      // Log full conversation to chatLogs
      set(s=>{
        const logEntry={id:"cl"+Date.now(),userId:user.id||user.name,userName:user.name,role:user.role,site:user.site||"all",date:todayStr,time:new Date().toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}),userMsg,aiReply:reply,tokensIn:tIn,tokensOut:tOut};
        const existing=(s.chatLogs||[]).slice(-500); // cap at 500
        return {...s,chatLogs:[...existing,logEntry]};
      });

    } catch(e) {
      setMsgs(m=>[...m,{role:"assistant",text:"Connection issue — check your internet and try again."}]);
    }
    setLoading(false);
  }

  function saveFeedback() {
    if (!fbText.trim()) return;
    set(s=>({...s, betaFeedback:[...(s.betaFeedback||[]),{
      id:"fb"+Date.now(), userId:user.id||user.name, userName:user.name,
      date:new Date().toISOString().split("T")[0], text:fbText.trim(), status:"open"
    }]}));
    setFbText(""); setFbSaved(true);
    setTimeout(()=>setFbSaved(false),2500);
  }

  const unreadFeedback = isPrincipalUser(user) ? (state.betaFeedback||[]).filter(f=>f.status==="open").length : 0;
  const apiKey = getStoredApiKey() || state?.settings?.anthropicKey || "";

  // Don't render the button at all if no AI access
  if (tier==="none") return null;

  return (
    <>
      <button onClick={()=>setOpen(o=>!o)} style={{position:"fixed",bottom:"max(24px, env(safe-area-inset-bottom, 24px) + 16px)",right:20,zIndex:500,width:52,height:52,borderRadius:99,border:"none",background:`linear-gradient(135deg,${C.teal},${C.grad2})`,color:"#fff",fontSize:22,cursor:"pointer",boxShadow:"0 4px 20px rgba(42,125,110,0.35)",display:"flex",alignItems:"center",justifyContent:"center",transition:"transform 0.2s",transform:open?"rotate(45deg)":"rotate(0deg)"}}>
        {open?"✕":<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>}
      </button>
      {unreadFeedback>0&&!open&&<div style={{position:"fixed",bottom:64,right:20,zIndex:501,background:C.danger,color:"#fff",borderRadius:99,fontSize:9,fontWeight:800,padding:"2px 6px"}}>{unreadFeedback}</div>}

      {open&&(
        <div style={{position:"fixed",bottom:"max(88px, env(safe-area-inset-bottom, 24px) + 80px)",right:16,zIndex:499,width:360,maxWidth:"calc(100vw - 32px)",background:C.surface,border:`1px solid ${C.border}`,borderRadius:20,boxShadow:"0 8px 40px rgba(0,0,0,0.12)",display:"flex",flexDirection:"column",overflow:"hidden",maxHeight:"70vh"}}>
          {/* Header */}
          <div style={{background:`linear-gradient(135deg,${C.teal},${C.grad2})`,padding:"12px 16px",display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:32,height:32,borderRadius:99,background:"rgba(255,255,255,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16}}>💬</div>
            <div style={{flex:1}}>
              <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:15,color:"#fff",fontWeight:700}}>Iris</div>
              <div style={{fontSize:10,color:"rgba(255,255,255,0.75)"}}>
                {tier==="sickness"?"Sickness & cover · "+SITES[user?.site]?.name : "Ask anything · Take action"}
              </div>
            </div>
            <div style={{display:"flex",gap:6}}>
              {["chat",...(isPrincipalUser(user)?["feedback"]:[])].map(m=>(
                <button key={m} onClick={()=>setMode(m)} style={{fontSize:10,padding:"3px 8px",borderRadius:6,border:"1px solid rgba(255,255,255,0.4)",background:mode===m?"rgba(255,255,255,0.25)":"transparent",color:"#fff",cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>
                  {m==="chat"?"Chat":"Feedback"}{m==="feedback"&&unreadFeedback>0?` (${unreadFeedback})`:""}
                </button>
              ))}
              {/* Voice toggle */}
              <button onClick={()=>{
                if(voiceOn&&audioRef.current){audioRef.current.pause();setSpeaking(false);}
                setVoiceOn(v=>!v);
              }} title={voiceOn?"Voice on — click to mute":"Voice off — click to enable"} style={{fontSize:14,padding:"3px 7px",borderRadius:6,border:`1px solid ${voiceOn?"rgba(255,255,255,0.7)":"rgba(255,255,255,0.25)"}`,background:voiceOn?"rgba(255,255,255,0.2)":"transparent",color:"#fff",cursor:"pointer",lineHeight:1}}>
                {speaking?"🔊":voiceOn?"🔈":"🔇"}
              </button>
            </div>
          </div>

          {mode==="chat"&&(
            <>
              <div style={{flex:1,overflowY:"auto",padding:"12px 14px",display:"flex",flexDirection:"column",gap:8}}>
                {msgs.map((m,i)=>(
                  <div key={i} style={{display:"flex",justifyContent:m.role==="user"?"flex-end":"flex-start",flexDirection:"column",alignItems:m.role==="user"?"flex-end":"flex-start"}}>
                    <div style={{maxWidth:"88%",padding:"9px 13px",borderRadius:m.role==="user"?"14px 14px 4px 14px":"14px 14px 14px 4px",background:m.role==="user"?C.teal:C.bg,color:m.role==="user"?"#fff":C.text,fontSize:12,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{m.text}</div>
                    {/* Tap to listen button on Iris messages */}
                    {m.role==="assistant"&&i>0&&(
                      <button onClick={()=>speakText(m.text)} title="Listen to this"
                        style={{fontSize:9,color:C.dim,background:"none",border:"none",cursor:"pointer",
                          padding:"2px 4px",fontFamily:"inherit",marginTop:2,opacity:0.6}}>
                        🔈 listen
                      </button>
                    )}
                  </div>
                ))}
                {loading&&<div style={{display:"flex"}}><div style={{padding:"9px 13px",borderRadius:"14px 14px 14px 4px",background:C.bg,color:C.dim,fontSize:12,animation:"pulse 1s infinite"}}>Thinking…</div></div>}
                <div ref={msgsEndRef}/>
              </div>
              {msgs.length<=1&&(
                <div style={{padding:"0 12px 8px",display:"flex",flexWrap:"wrap",gap:5}}>
                  {QUICK.map(q=>(
                    <button key={q} onClick={()=>send(q)} style={{fontSize:11,padding:"4px 10px",borderRadius:99,border:`1px solid ${C.border}`,background:C.bg,color:C.mid,cursor:"pointer",fontFamily:"inherit"}}>{q}</button>
                  ))}
                </div>
              )}
              <div style={{borderTop:`1px solid ${C.border}`,padding:"10px 12px",display:"flex",gap:6,alignItems:"center"}}>
                {/* Mic button */}
                <button onClick={()=>{
                  // Unlock audio on first user gesture (iOS requirement)
                  if (!audioUnlocked.current) {
                    const ctx = new (window.AudioContext||window.webkitAudioContext)();
                    ctx.resume().then(()=>{ audioUnlocked.current=true; }).catch(()=>{});
                    // Also unlock via silent audio
                    const silentAudio = new Audio();
                    silentAudio.src = "data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4LjI5LjEwMAAAAAAAAAAAAAAA";
                    silentAudio.play().catch(()=>{});
                    audioUnlocked.current = true;
                  }
                  if (listening) {
                    if (recogRef.current) { try { recogRef.current.abort(); } catch(e){} }
                    setListening(false);
                    voiceConvRef.current=false;
                    if (restartTimer.current) { clearTimeout(restartTimer.current); restartTimer.current=null; }
                  } else {
                    startListening();
                  }
                }}
                  title={listening?"Listening — tap to stop":"Tap to speak"}
                  style={{width:34,height:34,borderRadius:99,
                    border:`1px solid ${listening?"#ff4646":speaking?"#3aad98":C.border}`,
                    background:listening?"rgba(255,70,70,0.12)":speaking?"rgba(58,173,152,0.1)":"transparent",
                    color:listening?"#ff4646":speaking?"#3aad98":C.dim,
                    cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,
                  }}>
                  {listening ? (
                    // Animated equaliser when listening
                    <div style={{display:"flex",alignItems:"flex-end",gap:2,height:14}}>
                      {[["eq1","0s"],["eq2","0.1s"],["eq3","0.15s"],["eq4","0.05s"],["eq5","0.2s"]].map(([a,d],i)=>(
                        <div key={i} style={{width:2,background:"#ff4646",borderRadius:2,animation:`${a} 0.6s ease-in-out ${d} infinite`}}/>
                      ))}
                    </div>
                  ) : speaking ? (
                    // Animated equaliser when Iris is speaking
                    <div style={{display:"flex",alignItems:"flex-end",gap:2,height:14}}>
                      {[["eq3","0s"],["eq1","0.12s"],["eq5","0.06s"],["eq2","0.18s"],["eq4","0.09s"]].map(([a,d],i)=>(
                        <div key={i} style={{width:2,background:"#3aad98",borderRadius:2,animation:`${a} 0.5s ease-in-out ${d} infinite`}}/>
                      ))}
                    </div>
                  ) : (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                  )}
                </button>
                {/* Input or listening indicator */}
                {listening ? (
                  <div style={{flex:1,padding:"8px 12px",borderRadius:8,border:"1px solid #ff4646",
                    background:"rgba(255,70,70,0.06)",display:"flex",alignItems:"center",gap:8}}>
                    <div style={{display:"flex",alignItems:"flex-end",gap:2,height:16}}>
                      {[["eq1","0s"],["eq3","0.08s"],["eq2","0.16s"],["eq5","0.04s"],["eq4","0.12s"],["eq1","0.2s"],["eq3","0.06s"],["eq2","0.14s"]].map(([a,d],i)=>(
                        <div key={i} style={{width:2,background:"#ff4646",borderRadius:2,animation:`${a} 0.55s ease-in-out ${d} infinite`,opacity:0.8}}/>
                      ))}
                    </div>
                    <span style={{fontSize:11,color:"#ff4646",fontWeight:600,letterSpacing:"0.04em"}}>Listening…</span>
                    <span style={{fontSize:10,color:"rgba(255,70,70,0.5)",marginLeft:"auto"}}>tap mic to cancel</span>
                  </div>
                ) : (
                  <input ref={inputRef} value={input} onChange={e=>setInput(e.target.value)}
                    onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&send(input)}
                    placeholder={speaking?"Iris is speaking…":tier==="sickness"?"Ask about sickness or cover…":"Ask or instruct Iris…"}
                    style={{...IS,flex:1,padding:"8px 12px",fontSize:12,marginBottom:0}}/>
                )}
                <button onClick={()=>send(input)} disabled={(!input.trim()&&!listening)||loading}
                  style={{...BTN(C.teal),padding:"8px 14px",opacity:((!input.trim()&&!listening)||loading)?0.5:1}}>-></button>
              </div>
            </>
          )}

          {mode==="feedback"&&(
            <div style={{padding:16,flex:1,overflowY:"auto"}}>
              <div style={{fontSize:13,color:C.text,fontWeight:600,marginBottom:4}}>Feedback</div>
              <div style={{fontSize:11,color:C.dim,marginBottom:14}}>Note anything you'd like to improve or report. Conor reviews all feedback.</div>
              <textarea value={fbText} onChange={e=>setFbText(e.target.value)} placeholder="e.g. Would be great if I could see the rota on my phone more easily…" rows={4} style={{...IS,resize:"none",fontSize:12,lineHeight:1.6,marginBottom:10}}/>
              <button onClick={saveFeedback} style={{...BTN(C.teal),width:"100%",padding:"10px 0"}}>{fbSaved?"Saved!":"Submit feedback"}</button>
              {isPrincipalUser(user)&&(state.betaFeedback||[]).length>0&&(
                <div style={{marginTop:16}}>
                  <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>All feedback ({(state.betaFeedback||[]).length})</div>
                  {[...(state.betaFeedback||[])].reverse().map(f=>(
                    <div key={f.id} style={{...CS,marginBottom:6,padding:"10px 12px",borderLeft:`3px solid ${f.status==="reviewed"?C.green:C.warn}`}}>
                      <div style={{fontSize:11,fontWeight:700,color:C.text,marginBottom:2}}>{f.userName} · {f.date}</div>
                      <div style={{fontSize:12,color:C.mid,marginBottom:6}}>{f.text}</div>
                      {f.status!=="reviewed"&&<button onClick={()=>set(s=>({...s,betaFeedback:(s.betaFeedback||[]).map(x=>x.id===f.id?{...x,status:"reviewed"}:x)}))} style={{...BTN(C.green,true),fontSize:10,padding:"3px 8px"}}>Mark reviewed</button>}
                      {f.status==="reviewed"&&<span style={{fontSize:10,color:C.green}}>Reviewed</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}

function isPrincipalUser(user) { return user?.role==="principal"; }

// Email whitelist check — only emails in User Accounts can access the app
function isEmailWhitelisted(email, state) {
  if (!email) return false;
  const users = resolveUsers(state, true);
  return users.some(u => (u.email||"").toLowerCase().trim() === email.toLowerCase().trim());
}

/* ══════════════════════════════════════════════════════
   USER ACCOUNTS PANEL
   Principal: all users, all sites, full control
   Manager: their site only — nurses/reception/head nurses
            can add starters, deactivate leavers, edit email/PIN
            cannot change roles (principal only)
══════════════════════════════════════════════════════ */
// Standalone — used by UserAccounts AND Directory.
// state is optional but strongly recommended — without it, the email loses the owner-name
// personalisation and the magic link redirect falls back to the bare omni-rota.com domain.
async function sendInviteEmail(u, state) {
  if (!u.email) return { ok: false, error: "No email" };
  const INVITE_SECRET = process.env.REACT_APP_INVITE_SECRET || "";
  // Derive owner name (the principal of this practice — the trusted, named human in the email)
  // and practice display name. These are passed to the server so the email body says
  // "Conor at DCO has added you" instead of an impersonal "You've been added".
  const principal = state?.users
    ? state.users.find(x => x.role === "principal" && x.active !== false)
    : null;
  const ownerName = principal?.name
    || state?.practiceProfile?.principalName
    || state?.practiceProfile?.ownerName
    || "";
  const practiceName = state?.practiceProfile?.practiceName
    || state?.practiceProfile?.name
    || "";
  try {
    // v10.3.2 diagnostic — log exactly what's about to be sent
    const payload = {
      to: u.email,
      name: u.name,
      pin: u.pin || "",
      site: typeof u.site === "string" && u.site.length < 20 ? (SITES[u.site]?.name || u.site) : u.site,
      role: u.role || "staff",
      practiceSlug: PRACTICE_ID || "",  // tenant subdomain — drives magic link redirect
      ownerName,                         // surfaced in body as "[name] has added you"
      practiceName,                      // surfaced in subject + footer
    };
    console.log("[Omni invite] hostname:", window.location.hostname, "| PRACTICE_ID:", PRACTICE_ID, "| payload:", payload);
    const r = await fetch("/api/send-invite", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${INVITE_SECRET}`,
      },
      body: JSON.stringify(payload),
    });
    // Detailed diagnostics — most failures are silent otherwise.
    if (!r.ok) {
      const status = r.status;
      const ct = r.headers.get("content-type") || "";
      let errBody = "";
      try { errBody = ct.includes("json") ? JSON.stringify(await r.json()) : await r.text(); }
      catch { errBody = "(body unreadable)"; }
      const reason = status===404
        ? "API endpoint /api/send-invite not deployed on Vercel"
        : status===401 || status===403
          ? "INVITE_SECRET mismatch between client and server env vars"
          : status===500
            ? `Server error (likely Resend API key missing or sender domain unverified): ${errBody.slice(0,300)}`
            : `HTTP ${status}: ${errBody.slice(0,300)}`;
      console.error("[Omni invite]", reason);
      return { ok: false, error: reason, status };
    }
    const data = await r.json();
    return r.ok ? { ok: true } : { ok: false, error: data.error };
  } catch (err) {
    console.error("[Omni invite] Network error:", err.message);
    return { ok: false, error: "Network error: " + err.message };
  }
}

function UserAccounts({state, set, user}) {
  const isPrincipal = user?.role === "principal";
  const isManager   = user?.role === "manager" || user?.role === "managerLite";
  const [uSearch,    setUSearch]    = useState("");

  // Principal sees ALL users (including inactive + new) — managers see active site staff only
  const allUsers    = isPrincipal ? resolveUsers(state, true) : resolveUsers(state, false);

  const ROLE_LABELS = {
    principal:   {label:"Principal",    color:C.teal},
    manager:     {label:"Manager",      color:"#a78bfa"},
    managerLite: {label:"Acting Mgr",   color:"#a78bfa"},
    headNurse:   {label:"Head Nurse",   color:C.warn},
    staff:       {label:"Staff",        color:C.mid},
    clinician:   {label:"Clinician",    color:C.mid},
  };

  const MANAGEABLE_ROLES = ["staff","headNurse","managerLite"];
  // v11.5 — was hardcoded ["all","sale","brooklands","timperley"]. That made
  // User Accounts invisible for non-DCO tenants whose users have site keys
  // like "flourish_main" — the loop never iterated over their sites, so even
  // though grouped[siteKey] had entries, they never rendered. Now derived
  // from SITES which is dynamically built per tenant from state.practiceSites.
  const SITE_ORDER = ["all", ...Object.keys(SITES)];
  const ROLE_OPTIONS_PRINCIPAL = [
    {value:"staff",       label:"Staff"},
    {value:"headNurse",   label:"Head Nurse"},
    {value:"managerLite", label:"Acting Manager"},
    {value:"manager",     label:"Manager"},
    {value:"clinician",   label:"Clinician"},
    {value:"principal",   label:"Principal"},
  ];
  const ROLE_OPTIONS_MANAGER = [
    {value:"staff",       label:"Staff"},
    {value:"headNurse",   label:"Head Nurse"},
    {value:"managerLite", label:"Acting Manager"},
  ];
  const ROLE_OPTIONS = isPrincipal ? ROLE_OPTIONS_PRINCIPAL : ROLE_OPTIONS_MANAGER;

  // Archived/inactive users hidden by default — toggle below reveals them.
  // Whitelist check (isEmailWhitelisted) still uses includeAll=true elsewhere
  // so archiving here never silently breaks login for an active user.
  const [showArchived, setShowArchived] = useState(false);
  const archivedCount = allUsers.filter(u => u.active === false).length;

  function visibleUsers() {
    let pool = isPrincipal ? allUsers : allUsers.filter(u => u.site === user.site && MANAGEABLE_ROLES.includes(u.role));
    if (!showArchived) pool = pool.filter(u => u.active !== false);
    if (!uSearch.trim()) return pool;
    const q = uSearch.toLowerCase();
    return pool.filter(u => u.name.toLowerCase().includes(q) || (u.email||"").toLowerCase().includes(q));
  }
  const users = visibleUsers();

  // Group by site for principal
  const grouped = {};
  users.forEach(u => {
    const k = isPrincipal ? (u.site||"all") : user.site;
    if (!grouped[k]) grouped[k] = [];
    grouped[k].push(u);
  });

  const [editing,    setEditing]    = useState(null);
  const [form,       setForm]       = useState({});
  const [addModal,   setAddModal]   = useState(false);
  const [inviteModal,setInviteModal]= useState(null); // user to invite
  const genPIN = () => String(Math.floor(1000+Math.random()*9000));
  // v11.5 — fallback site is now first key of SITES (this tenant's first
  // configured site), not hardcoded "sale". Was bleeding DCO defaults into
  // non-DCO tenants when the parent user themselves had a corrupted "sale"
  // site value.
  const [newUser,    setNewUser]    = useState({name:"",site:user?.site||Object.keys(SITES)[0]||"main",role:"staff",pin:genPIN(),email:"",staffId:""});
  const [saved,      setSaved]      = useState(false);
  const [pinVisible, setPinVisible] = useState({});
  const [copied,     setCopied]     = useState(false);
  const [bulkInvite, setBulkInvite] = useState(null); // null | {sending,total,done,success,failed[]}
  const [rowResend,  setRowResend]  = useState({});   // {[userId]: "sending"|"sent"|{type:"err",reason}|null}
  // v11.2 — local-only "just clicked" feedback for the Replay arrival button.
  // Previous version stored _replayResult inside form state, which then got
  // persisted to the user record on Save Changes. That corrupted user data
  // and (combined with the active:false form bug) locked principals out.
  // Now: pure component-local state, never touches form, never persists.
  const [replayJustClicked, setReplayJustClicked] = useState(false);

  function openEdit(u) { setEditing(u); setForm({pin:u.pin||"",email:u.email||"",role:u.role,active:u.active!==false}); }

  function saveEdit() {
    const hadEmail = !!(editing.email||"").trim();
    const nowHasEmail = !!(form.email||"").trim();
    const emailAdded = !hadEmail && nowHasEmail;
    // v11.2 — strip any keys starting with "_" before persisting. These are
    // transient UI state (replay results, form errors, etc.) that should
    // never end up in the saved user record. Belt-and-braces guard against
    // future bugs where a button accidentally writes UI state into form.
    const cleanForm = Object.fromEntries(
      Object.entries(form).filter(([k]) => !k.startsWith("_"))
    );
    set(s=>{
      const existing=s.users||[];
      const idx=existing.findIndex(u=>u.id===editing.id);
      const base = editing._auto ? {id:editing.id,staffId:editing.staffId,name:editing.name,site:editing.site,role:editing.role,active:true} : {};
      const next=idx>=0?existing.map(u=>u.id===editing.id?{...u,...cleanForm}:u):[...existing,{...base,...cleanForm,id:editing.id}];
      return {...s,users:next};
    });
    // Auto-send invite if email was just added for the first time
    if (emailAdded) {
      const inviteUser = {...editing,...form};
      sendInviteEmail(inviteUser, state).then(r=>{
        if (r.ok) {
          setSaved("invited");
          set(s=>({...s,userInvitedAt:{...(s.userInvitedAt||{}),[editing.id]:new Date().toISOString()}}));
        } else {
          setSaved({type:"failed", reason:r.error||"Unknown error"});
        }
        setTimeout(()=>setSaved(false),r.ok?3000:8000);
      });
    } else {
      setSaved(true); setTimeout(()=>setSaved(false),2500);
    }
    setEditing(null);
  }

  function addNewUser() {
    if (!newUser.name.trim()) return;
    const id="u_"+newUser.name.toLowerCase().replace(/\s+/g,"").slice(0,8)+Date.now().toString().slice(-4);
    const created={...newUser,id,active:true};
    set(s=>({...s,users:[...(s.users||[]),created]}));
    setAddModal(false);
    // Auto-send invite if email provided
    if (newUser.email.trim()) {
      sendInviteEmail(created, state).then(r=>{
        if (r.ok) {
          setSaved("invited");
          set(s=>({...s,userInvitedAt:{...(s.userInvitedAt||{}),[id]:new Date().toISOString()}}));
        } else {
          setSaved({type:"failed", reason:r.error||"Unknown error", userId:id});
        }
        setTimeout(()=>setSaved(false),r.ok?3000:8000);
      });
    } else {
      setSaved(true); setTimeout(()=>setSaved(false),2500);
    }
    setNewUser({name:"",site:user?.site||Object.keys(SITES)[0]||"main",role:"staff",pin:genPIN(),email:"",staffId:""});
  }

  // Generate invite email text
  function inviteText(u) {
    const siteLabel = SITES[u.site]?.name || "your site";
    const roleLabel = ROLE_LABELS[u.role]?.label || u.role;
    // Tenant-aware URL: every invite must land on THIS practice's subdomain.
    // Bare omni-rota.com would dump the recipient on whichever tenant their
    // browser last cached -- often the wrong one -- triggering the "Access
    // not set up yet" dead-end. PRACTICE_ID is the slug for this tenant.
    const url = PRACTICE_ID
      ? `https://${PRACTICE_ID}.omni-rota.com`
      : "https://omni-rota.com";
    const displayUrl = PRACTICE_ID
      ? `${PRACTICE_ID}.omni-rota.com`
      : "omni-rota.com";
    const firstName = u.name.split(" ")[0];
    const hasPIN = !!u.pin;
    return {
      subject: `You've been added to Omni`,
      htmlBody: `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>Welcome to Omni</title></head><body style="margin:0;padding:0;background:#f7f6f3;font-family:'Helvetica Neue',Arial,sans-serif;">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f7f6f3;padding:40px 0;">
  <tr><td align="center">
    <table width="560" cellpadding="0" cellspacing="0" style="background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.08);max-width:560px;width:100%;">
      <tr><td style="background:linear-gradient(135deg,#0f2027 0%,#3aad98 100%);padding:32px 40px;text-align:center;">
        <div style="font-size:28px;font-weight:800;color:#ffffff;letter-spacing:-0.5px;">Omni.</div>
        <div style="font-size:13px;color:rgba(255,255,255,0.6);margin-top:4px;">by Omni 2026</div>
      </td></tr>
      <tr><td style="padding:40px;">
        <p style="font-size:16px;color:#1a2e3b;margin:0 0 8px;font-weight:700;">Hi ${firstName} 👋</p>
        <p style="font-size:15px;color:#6b7280;line-height:1.6;margin:0 0 28px;">You've been added to <strong>Omni</strong> as <strong>${roleLabel}</strong> at ${siteLabel}. Omni is your rota, leave and communications system — built for the team.</p>
        <table width="100%" cellpadding="0" cellspacing="0" style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:12px;margin-bottom:28px;">
          <tr><td style="padding:20px 24px;">
            <div style="font-size:11px;font-weight:700;color:#059669;text-transform:uppercase;letter-spacing:0.08em;margin-bottom:14px;">Your Details</div>
            <table cellpadding="0" cellspacing="0">
              <tr><td style="font-size:13px;color:#6b7280;padding-bottom:8px;width:80px;">Site</td><td style="font-size:13px;color:#1a2e3b;font-weight:600;padding-bottom:8px;">${siteLabel}</td></tr>
              <tr><td style="font-size:13px;color:#6b7280;padding-bottom:8px;">Role</td><td style="font-size:13px;color:#1a2e3b;font-weight:600;padding-bottom:8px;">${roleLabel}</td></tr>
              ${hasPIN ? `<tr><td style="font-size:13px;color:#6b7280;">Quick PIN</td><td style="font-size:22px;color:#3aad98;font-weight:800;letter-spacing:6px;">${u.pin}</td></tr>` : ""}
            </table>
          </td></tr>
        </table>
        <p style="font-size:14px;color:#6b7280;line-height:1.6;margin:0 0 20px;">Tap the button below — it logs you straight in, no password needed. Once you're in, you can add Omni to your home screen for one-tap access every day.</p>
        <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:32px;">
          <tr><td align="center"><a href="${url}" style="display:inline-block;background:#3aad98;color:#ffffff;font-size:16px;font-weight:700;text-decoration:none;padding:16px 40px;border-radius:12px;">Open Omni →</a></td></tr>
        </table>
        <p style="font-size:12px;color:#9ca3af;text-align:center;margin:0;">If this doesn't work, go to <a href="${url}" style="color:#3aad98;">${displayUrl}</a> and use your PIN.</p>
      </td></tr>
      <tr><td style="background:#f7f6f3;padding:20px 40px;border-top:1px solid #e5e7eb;">
        <p style="font-size:12px;color:#6b7280;margin:0;">Omni · Prima Luce Ltd · <a href="${url}" style="color:#3aad98;text-decoration:none;">${displayUrl}</a></p>
      </td></tr>
    </table>
  </td></tr>
</table>
</body></html>`,
      body: `Hi ${firstName},

You've been added to Omni as ${roleLabel} at ${siteLabel}.

Open Omni here: ${url}
${hasPIN ? `Your PIN: ${u.pin}` : ""}

Tap the link to log in — no password needed.

— Omni · Prima Luce Ltd`,
    };
  }

  function openMailto(u) {
    const t = inviteText(u);
    const mailto = `mailto:${u.email||""}?subject=${encodeURIComponent(t.subject)}&body=${encodeURIComponent(t.body)}`;
    window.open(mailto, "_blank");
  }

  function copyInvite(u) {
    const t = inviteText(u);
    const full = `Subject: ${t.subject}\n\n${t.body}`;
    try { navigator.clipboard.writeText(full); } catch(e) { }
    setCopied(true); setTimeout(()=>setCopied(false),2500);
  }

  const sf = (k,v) => setForm(p=>({...p,[k]:v}));
  const sn = (k,v) => setNewUser(p=>({...p,[k]:v}));

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:18,color:C.heading,fontWeight:700}}>User Accounts</div>
          <div style={{fontSize:12,color:C.dim,marginTop:2}}>
            {isPrincipal ? `${users.length} accounts across all sites` : `${SITES[user.site]?.name} · Add starters, manage access`}
          </div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {saved&&(
            typeof saved==="object" && saved.type==="failed"
              ? <span style={{fontSize:11,color:C.warn,fontWeight:600,maxWidth:380,lineHeight:1.4}}>
                  ⚠ Saved, but invite email failed: {saved.reason}. Use Copy Invite to send manually — see browser console for details.
                </span>
              : <span style={{fontSize:12,color:C.green,fontWeight:600}}>{saved==="invited"?"Invite sent!":"Saved"}</span>
          )}
          {(()=>{
            // Show "Invite all" only when there are users with email but no userInvitedAt
            const targets = users.filter(u=>{
              if (!u.email||!u.email.trim()) return false;
              if ((state.userInvitedAt||{})[u.id]) return false;
              if ((state.userLastLogin||{})[u.id]) return false;
              return u.active!==false;
            });
            if (targets.length === 0) return null;
            return (
              <button onClick={async()=>{
                if (bulkInvite?.sending) return;
                setBulkInvite({sending:true,total:targets.length,done:0,success:0,failed:[]});
                let done=0, success=0; const failed=[];
                for (const u of targets) {
                  const r = await sendInviteEmail(u, state);
                  done++;
                  if (r.ok) {
                    success++;
                    set(s=>({...s,userInvitedAt:{...(s.userInvitedAt||{}),[u.id]:new Date().toISOString()}}));
                  } else {
                    failed.push({name:u.name,reason:r.error||"Unknown"});
                  }
                  setBulkInvite({sending:true,total:targets.length,done,success,failed:[...failed]});
                }
                setBulkInvite({sending:false,total:targets.length,done,success,failed});
                setTimeout(()=>setBulkInvite(null),12000);
              }} disabled={bulkInvite?.sending}
              style={{...BTN(C.teal,true),display:"flex",alignItems:"center",gap:6,opacity:bulkInvite?.sending?0.5:1}}>
                {bulkInvite?.sending
                  ? `Sending… (${bulkInvite.done}/${bulkInvite.total})`
                  : `✉ Invite all uninvited (${targets.length})`}
              </button>
            );
          })()}
          <input value={uSearch} onChange={e=>setUSearch(e.target.value)} placeholder="Search by name or email…" style={{...IS,width:200,padding:"7px 12px",fontSize:12}}/>
          {archivedCount > 0 && (
            <button
              onClick={()=>setShowArchived(v=>!v)}
              title={showArchived ? "Hide archived users" : "Show archived users"}
              style={{
                background:"transparent",
                border:`1px solid ${showArchived ? C.teal : C.border}`,
                borderRadius:8,
                color:showArchived ? C.teal : C.dim,
                fontSize:11,fontWeight:600,
                padding:"7px 11px",cursor:"pointer",
                fontFamily:"inherit",
                display:"flex",alignItems:"center",gap:5,
              }}
            >
              {showArchived ? "Hide" : "Show"} {archivedCount} archived
            </button>
          )}
          <button onClick={()=>setAddModal(true)} style={{...BTN(C.teal),display:"flex",alignItems:"center",gap:6}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Login Only
          </button>
        </div>
      </div>

      {bulkInvite && !bulkInvite.sending && (
        <div style={{
          marginBottom:16,padding:"12px 16px",borderRadius:10,
          background: bulkInvite.failed.length===0 ? "rgba(0,229,160,0.08)" : "rgba(245,197,24,0.08)",
          border: `1px solid ${bulkInvite.failed.length===0 ? "#86efac" : "rgba(245,197,24,0.25)"}`,
          fontSize:12,color: bulkInvite.failed.length===0 ? "#00e5a0" : C.warn,
        }}>
          <div style={{fontWeight:700,marginBottom:bulkInvite.failed.length>0?6:0}}>
            Sent {bulkInvite.success} of {bulkInvite.total} invite{bulkInvite.total!==1?"s":""}
            {bulkInvite.failed.length>0 && ` — ${bulkInvite.failed.length} failed`}
          </div>
          {bulkInvite.failed.length>0 && (
            <div style={{fontSize:11,color:C.dim,lineHeight:1.5}}>
              Failed: {bulkInvite.failed.map(f=>f.name).join(", ")}. Reason: {bulkInvite.failed[0].reason}. Open browser console for full details.
            </div>
          )}
        </div>
      )}

      {isManager&&(
        <div style={{background:"rgba(0,229,160,0.08)",border:"1px solid #86efac",borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:12,color:"#00e5a0"}}>
          You can add new starters, update emails and PINs, and deactivate leavers. Role changes are managed by Conor.
        </div>
      )}

      {/* User table — grouped by site.
          v11.5 — render in SITE_ORDER first (this tenant's known sites),
          then any orphaned site keys present in grouped[] but not in
          SITE_ORDER. Catches users with stale site values (e.g. a record
          created before tenant onboarding completed, or one carrying a
          legacy DCO site key). Without this, those users would be invisible. */}
      {(() => {
        const knownInOrder = SITE_ORDER.filter(s => grouped[s]?.length > 0);
        const orphans = Object.keys(grouped).filter(s =>
          !SITE_ORDER.includes(s) && grouped[s]?.length > 0
        );
        return [...knownInOrder, ...orphans];
      })().map(siteKey=>(
        <div key={siteKey} style={{marginBottom:28}}>
          {isPrincipal&&(
            <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:10,paddingBottom:6,borderBottom:`1px solid ${C.border}`}}>
              {siteKey==="all"?"Principal & Cross-site":SITES[siteKey]?.name||siteKey}
              <span style={{fontWeight:400,marginLeft:8}}>({grouped[siteKey].length})</span>
            </div>
          )}
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {grouped[siteKey].sort((a,b)=>{
              const ro=["principal","manager","managerLite","headNurse","clinician","staff"];
              return ro.indexOf(a.role)-ro.indexOf(b.role);
            }).map(u=>{
              const rl=ROLE_LABELS[u.role]||{label:u.role,color:C.mid};
              const canEdit=isPrincipal||(isManager&&MANAGEABLE_ROLES.includes(u.role)&&u.site===user.site);
              const isInactive=u.active===false;
              return (
                <div key={u.id} style={{...CS,padding:"12px 16px",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",opacity:isInactive?0.55:1,borderLeft:isInactive?`3px solid ${C.muted}`:`3px solid ${rl.color}`}}>
                  {/* Avatar */}
                  <div style={{width:34,height:34,borderRadius:99,background:rl.color+"18",border:`2px solid ${rl.color}33`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:700,color:rl.color,flexShrink:0}}>
                    {u.name.split(" ").map(w=>w[0]).join("").slice(0,2)}
                  </div>
                  {/* Name + site + status badge */}
                  <div style={{flex:1,minWidth:120}}>
                    <div style={{fontWeight:700,fontSize:13,color:isInactive?C.dim:C.heading,marginBottom:2}}>
                      {u.name}
                      {isInactive&&<span style={{fontSize:10,color:C.danger,fontWeight:600,marginLeft:6}}>Inactive</span>}
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                      <span style={{fontSize:11,color:C.dim}}>{SITES[u.site]?.name||"All sites"}</span>
                      <span style={{fontSize:9,color:C.dim}}>.</span>
                      {(()=>{
                        const lastLoginISO=(state.userLastLogin||{})[u.id];
                        const invitedISO=(state.userInvitedAt||{})[u.id];
                        const hasLoggedIn=!!lastLoginISO;
                        const wasInvited=!!invitedISO;
                        // Friendly "time ago" formatting
                        const ago = iso => {
                          if (!iso) return "";
                          const ms = Date.now() - new Date(iso).getTime();
                          const m = Math.floor(ms/60000);
                          if (m < 1) return "just now";
                          if (m < 60) return `${m}m ago`;
                          const h = Math.floor(m/60);
                          if (h < 24) return `${h}h ago`;
                          const d = Math.floor(h/24);
                          if (d < 7) return `${d}d ago`;
                          if (d < 60) return `${Math.floor(d/7)}w ago`;
                          return new Date(iso).toLocaleDateString("en-GB",{day:"numeric",month:"short"});
                        };
                        const badge=hasLoggedIn
                          ?{label:`Active · ${ago(lastLoginISO)}`,  bg:"#dcfce7",color:"#00e5a0",dot:"#22c55e",title:`Last login: ${new Date(lastLoginISO).toLocaleString("en-GB")}`}
                          :wasInvited
                          ?{label:`Invited · ${ago(invitedISO)}`, bg:"#fef9c3",color:"#f5c518",dot:"#eab308",title:`Invite sent: ${new Date(invitedISO).toLocaleString("en-GB")} — never logged in`}
                          :u.email
                          ?{label:"Not invited",bg:"#f1f5f9",color:"#475569",dot:"#94a3b8",title:"User has email but hasn't been sent an invite yet"}
                          :{label:"No email",bg:"#fef2f2",color:"#991b1b",dot:"#fca5a5",title:"No email on file — cannot send invite"};
                        return (
                          <span title={badge.title} style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:10,fontWeight:600,color:badge.color,background:badge.bg,borderRadius:6,padding:"2px 8px",whiteSpace:"nowrap",cursor:"help"}}>
                            <span style={{width:6,height:6,borderRadius:99,background:badge.dot,flexShrink:0,display:"inline-block"}}/>
                            {badge.label}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  {/* Role pill */}
                  <Pill c={rl.color} t={rl.label} sm/>
                  {/* PIN — click to reveal */}
                  <div onClick={()=>setPinVisible(p=>({...p,[u.id]:!p[u.id]}))} title="Click to reveal PIN" style={{fontSize:12,fontFamily:"monospace",color:C.mid,minWidth:52,textAlign:"center",cursor:"pointer",userSelect:"none",background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:"4px 10px"}}>
                    {pinVisible[u.id]?(u.pin||"—"):"••••"}
                  </div>
                  {/* Email */}
                  <div style={{fontSize:11,color:u.email?C.text:C.muted,minWidth:130,flex:1}}>{u.email||"No email set"}</div>
                  {/* Send / Resend invite — one-tap fire with inline status.
                      Label and behaviour adapt to the user's current state.
                      Failure path opens the modal so they can use Open in Mail / Copy text. */}
                  {canEdit&&u.email&&(()=>{
                    const wasInvited = !!(state.userInvitedAt||{})[u.id];
                    const status = rowResend[u.id];
                    const isSending = status === "sending";
                    const isSent    = status === "sent";
                    const errObj    = (typeof status === "object" && status?.type === "err") ? status : null;
                    const label = isSending ? "Sending…"
                                : isSent    ? "✓ Sent"
                                : errObj    ? "⚠ Failed · Tap"
                                : wasInvited ? "↩ Resend"
                                : "✉ Send invite";
                    const tone = isSent
                      ? {bg:"rgba(0,229,160,0.15)",bd:"rgba(0,229,160,0.5)",fg:"#00e5a0"}
                      : errObj
                      ? {bg:"rgba(245,197,24,0.12)",bd:"rgba(245,197,24,0.45)",fg:C.warn}
                      : isSending
                      ? {bg:"rgba(58,173,152,0.12)",bd:"rgba(58,173,152,0.4)",fg:C.teal}
                      : wasInvited
                      ? {bg:"rgba(245,197,24,0.08)",bd:"rgba(245,197,24,0.35)",fg:C.warn}
                      : {bg:"rgba(58,173,152,0.10)",bd:"rgba(58,173,152,0.45)",fg:C.teal};
                    const onClick = async () => {
                      if (isSending) return;
                      // On failure, hand off to the modal where Open in Mail / Copy text live
                      if (errObj) { setInviteModal(u); setRowResend(p=>({...p,[u.id]:null})); return; }
                      setRowResend(p=>({...p,[u.id]:"sending"}));
                      const r = await sendInviteEmail(u, state);
                      if (r.ok) {
                        set(s=>({...s,userInvitedAt:{...(s.userInvitedAt||{}),[u.id]:new Date().toISOString()}}));
                        setRowResend(p=>({...p,[u.id]:"sent"}));
                        setTimeout(()=>setRowResend(p=>({...p,[u.id]:null})),4000);
                      } else {
                        setRowResend(p=>({...p,[u.id]:{type:"err",reason:r.error||"Unknown error"}}));
                        setTimeout(()=>setRowResend(p=>({...p,[u.id]:null})),10000);
                      }
                    };
                    return (
                      <button onClick={onClick} disabled={isSending}
                        title={errObj ? `${errObj.reason} — tap for manual options` : wasInvited ? "Resend the invite email now (one tap)" : "Send the invite email now (one tap)"}
                        style={{
                          fontSize:11,fontWeight:700,
                          padding:"5px 11px",borderRadius:8,
                          border:`1px solid ${tone.bd}`,
                          background:tone.bg,color:tone.fg,
                          cursor:isSending?"default":"pointer",
                          fontFamily:"inherit",flexShrink:0,
                          whiteSpace:"nowrap",
                          transition:"all 0.15s",
                          opacity:isSending?0.7:1,
                        }}>
                        {label}
                      </button>
                    );
                  })()}
                  {/* Edit */}
                  {canEdit&&(
                    <button onClick={()=>openEdit(u)} style={{...BTN(C.teal,true),fontSize:12,padding:"5px 14px",flexShrink:0}}>Edit</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {users.length===0&&(
        <div style={{textAlign:"center",padding:40,color:C.dim,fontSize:13}}>No users found.</div>
      )}

      {/* ── Edit modal ── */}
      {editing&&(
        <Overlay onClose={()=>setEditing(null)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Edit User</div>
          <div style={{fontSize:13,color:C.dim,marginBottom:20}}>{editing.name} · {SITES[editing.site]?.name||"All sites"}</div>
          <div style={{marginBottom:12}}>
            <label style={LB}>Email address</label>
            <input value={form.email||""} onChange={e=>sf("email",e.target.value)} placeholder="name@example.com" style={IS}/>
            <div style={{fontSize:11,color:C.dim,marginTop:4}}>Used for Google / Apple login and invite emails.</div>
            {/* Existing-email-unchanged hint — explains why Save alone won't fire an invite */}
            {(()=>{
              const hadEmailOriginally = !!(editing.email||"").trim();
              const emailUnchanged = (editing.email||"").trim() === (form.email||"").trim();
              if (!hadEmailOriginally || !emailUnchanged || !form.email) return null;
              const wasInvited = !!(state.userInvitedAt||{})[editing.id];
              const hasLoggedIn = !!(state.userLastLogin||{})[editing.id];
              return (
                <div style={{marginTop:10,padding:"10px 12px",background:"rgba(58,173,152,0.05)",border:`1px solid ${C.tealBorder}`,borderRadius:8,fontSize:11,color:C.soft,lineHeight:1.5}}>
                  {hasLoggedIn ? (
                    <>This user has logged in before. Save alone won't trigger a new invite. Use <strong>Resend Invite</strong> below if they need a fresh login link.</>
                  ) : wasInvited ? (
                    <>An invite has been sent to this email but they haven't logged in yet. Use <strong>Resend Invite</strong> below to try again.</>
                  ) : (
                    <>This email is on file but no invite has ever been sent. Use <strong>Send Invite</strong> below to fire one now.</>
                  )}
                </div>
              );
            })()}
            {/* Explicit Resend / Send Invite button — works even when email unchanged */}
            {form.email && (editing.email||"").trim() === (form.email||"").trim() && (
              <button onClick={async()=>{
                sf("_resending",true);
                const r = await sendInviteEmail({...editing,...form}, state);
                sf("_resending",false);
                if (r.ok) {
                  set(s=>({...s,userInvitedAt:{...(s.userInvitedAt||{}),[editing.id]:new Date().toISOString()}}));
                  sf("_resendResult",{ok:true,t:Date.now()});
                } else {
                  sf("_resendResult",{ok:false,reason:r.error||"Unknown error",t:Date.now()});
                }
              }} style={{
                marginTop:10,width:"100%",padding:"10px 0",fontSize:12,fontWeight:600,
                borderRadius:8,border:`1px solid ${C.tealBorder}`,
                background:"rgba(58,173,152,0.08)",color:C.teal,cursor:"pointer",
                fontFamily:"inherit",
              }}>
                {form._resending ? "Sending…" :
                 form._resendResult?.ok ? "✓ Invite re-sent — check inbox" :
                 form._resendResult && !form._resendResult.ok ? "⚠ Failed — see message below" :
                 (state.userInvitedAt||{})[editing.id] ? "↩ Resend Invite Now" : "✉ Send Invite Now"}
              </button>
            )}
            {form._resendResult && !form._resendResult.ok && (
              <div style={{marginTop:8,padding:"8px 12px",background:"rgba(245,197,24,0.08)",border:`1px solid rgba(245,197,24,0.25)`,borderRadius:8,fontSize:11,color:C.warn,lineHeight:1.4}}>
                {form._resendResult.reason} — open browser console for details.
              </div>
            )}
          </div>
          <div style={{marginBottom:12}}>
            <label style={LB}>PIN</label>
            <input value={form.pin||""} onChange={e=>sf("pin",e.target.value.replace(/\D/g,"").slice(0,6))} placeholder="4–6 digits" style={{...IS,fontFamily:"monospace",letterSpacing:4}}/>
          </div>
          {isPrincipal&&(
            <div style={{marginBottom:12}}>
              <label style={LB}>Role</label>
              <select value={form.role||"staff"} onChange={e=>sf("role",e.target.value)} style={IS}>
                {ROLE_OPTIONS.map(r=><option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              <div style={{fontSize:11,color:C.dim,marginTop:4}}>Role change takes effect immediately on next login.</div>
            </div>
          )}
          <div style={{marginBottom:20}}>
            <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",fontSize:13,color:C.soft}}>
              <input type="checkbox" checked={form.active!==false} onChange={e=>sf("active",e.target.checked)} style={{width:16,height:16,accentColor:C.teal}}/>
              Active (uncheck to deactivate — prevents login)
            </label>
          </div>
          {/* ── v11.0/11.2 Admin actions — principal only ─────────────────
              Replay arrival: clears welcomedAt for this user so the welcome
              cinematic fires again on their next login. Useful for design
              iteration on the arrival experience and for re-onboarding staff
              after major UX changes. Does not affect login history.
              v11.2 (28 Apr 2026): use component-local replayJustClicked state
              for "just clicked" feedback instead of form state. Previous
              version wrote _replayResult into form, which then got persisted
              to user record on Save Changes — corrupting data with transient
              UI state and (combined with form active:false bug) caused
              principal lockout. Now: pure local state, never touches form. */}
          {isPrincipal&&(()=>{
            const hasWelcomed = !!(state.welcomedAt||{})[editing.id];
            // v11.2 — only show the Replay button for users who have ALREADY
            // been welcomed. For users who haven't, the cinematic will fire
            // automatically on their first login — no action needed. Showing
            // a "Set up arrival cinematic" button for these users was just
            // confusing UX, implying manual setup was required when it wasn't.
            if (!hasWelcomed) return null;
            return (
              <div style={{marginBottom:20,padding:"12px 14px",background:"rgba(255,255,255,0.02)",border:`1px solid ${C.border}`,borderRadius:10}}>
                <div style={{fontSize:11,fontWeight:700,color:C.dim,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>Admin actions</div>
                <button onClick={()=>{
                  set(s=>{
                    const next = {...(s.welcomedAt||{})};
                    delete next[editing.id];
                    return {...s, welcomedAt: next};
                  });
                  setReplayJustClicked(true);
                  setTimeout(()=>setReplayJustClicked(false), 3000);
                }} style={{
                  width:"100%",padding:"9px 0",fontSize:12,fontWeight:600,
                  borderRadius:8,border:`1px solid ${C.border}`,
                  background:"transparent",color:C.soft,cursor:"pointer",
                  fontFamily:"inherit",
                }}>
                  {replayJustClicked ? "✓ Arrival reset — fires on next login" : "↻ Replay arrival on next login"}
                </button>
                <div style={{fontSize:10,color:C.dim,marginTop:8,lineHeight:1.5}}>
                  This user has already seen the arrival. Click to make it fire again next time they log in.
                </div>
              </div>
            );
          })()}
          <div style={{display:"flex",gap:8}}>
            <button onClick={saveEdit} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Save Changes</button>
            {/* Save & Invite — only shown when email is changing (new or edited).
                For unchanged-email case, the explicit "Resend Invite Now" button
                in the email field section handles re-sending without saving. */}
            {form.email && (editing.email||"").trim() !== (form.email||"").trim() && (
              <button onClick={()=>{saveEdit();setInviteModal({...editing,...form});}} style={{...BTN(C.teal,true),padding:"11px 16px"}}>
                Save &amp; Invite
              </button>
            )}
            <button onClick={()=>setEditing(null)} style={BTN(C.dim,true)}>Cancel</button>
          </div>
        </Overlay>
      )}

      {/* ── Add User modal ── */}
      {addModal&&(
        <Overlay onClose={()=>setAddModal(false)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Add Login Only</div>
          <div style={{fontSize:12,color:C.dim,marginBottom:20}}>For people who need Omni access but aren't on the rota — e.g. an office manager, an external accountant. They won't appear in shift planning. To add someone who works clinical shifts, use Add Staff Member in the People tab.</div>
          {[
            {l:"Full name",   e:<input value={newUser.name} onChange={e=>sn("name",e.target.value)} placeholder="e.g. Sophie Black" style={IS}/>},
            {l:"Email (optional)", e:<input value={newUser.email} onChange={e=>sn("email",e.target.value)} placeholder="name@hotmail.com" style={IS}/>},
            {l:"PIN",         e:<input value={newUser.pin} onChange={e=>sn("pin",e.target.value.replace(/\D/g,"").slice(0,6))} placeholder="Choose a 4–6 digit PIN" style={{...IS,fontFamily:"monospace",letterSpacing:4}}/>},
          ].map(({l,e})=><div key={l} style={{marginBottom:12}}><label style={LB}>{l}</label>{e}</div>)}
          <div style={{marginBottom:12}}>
            <label style={LB}>Site</label>
            <select value={newUser.site} onChange={e=>sn("site",e.target.value)} style={IS} disabled={!isPrincipal}>
              {Object.entries(SITES).map(([k,v])=><option key={k} value={k}>{v.name}</option>)}
            </select>
          </div>
          <div style={{marginBottom:20}}>
            <label style={LB}>Role</label>
            <select value={newUser.role} onChange={e=>sn("role",e.target.value)} style={IS}>
              {ROLE_OPTIONS.map(r=><option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={addNewUser} disabled={!newUser.name.trim()} style={{...BTN(C.teal),flex:1,padding:"11px 0",opacity:!newUser.name.trim()?0.5:1}}>
              {newUser.email?"Add & Invite":"Add Login"}
            </button>
            <button onClick={()=>setAddModal(false)} style={BTN(C.dim,true)}>Cancel</button>
          </div>
        </Overlay>
      )}

      {/* ── Invite modal ── */}
      {inviteModal&&(
        <Overlay onClose={()=>setInviteModal(null)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Send Invite</div>
          <div style={{fontSize:12,color:C.dim,marginBottom:16}}>{inviteModal.name} · PIN {inviteModal.pin}</div>
          {/* Preview card */}
          <div style={{background:"rgba(58,173,152,0.08)",border:`1px solid ${C.tealBorder}`,borderRadius:12,padding:"16px 18px",marginBottom:16}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12,paddingBottom:10,borderBottom:`1px solid ${C.tealBorder}`}}>
              <div style={{width:32,height:32,borderRadius:99,background:`linear-gradient(135deg,${C.teal},#7c3aed)`,display:"flex",alignItems:"center",justifyContent:"center"}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
              </div>
              <div>
                <div style={{fontWeight:700,fontSize:13,color:C.teal}}>Omni · Prima Luce Ltd</div>
                <div style={{fontSize:11,color:C.dim}}>You're invited!</div>
              </div>
            </div>
            <div style={{fontSize:12,color:C.text,lineHeight:1.7}}>
              <div style={{marginBottom:6}}>Hi <strong>{inviteModal.name.split(" ")[0]}</strong>,</div>
              <div style={{marginBottom:10}}>You've been added to Omni. Here's how to get started:</div>
              <div style={{background:"#181626",borderRadius:8,padding:"10px 12px",marginBottom:10,fontFamily:"monospace",fontSize:13}}>
                <div>🌐 {PRACTICE_ID ? `${PRACTICE_ID}.omni-rota.com` : "omni-rota.com"}</div>
                <div>PIN: <strong>{inviteModal.pin}</strong></div>
                <div>👤 {ROLE_LABELS[inviteModal.role]?.label||inviteModal.role} · {SITES[inviteModal.site]?.name||PRACTICE_ID}</div>
              </div>
              <div style={{fontSize:11,color:C.dim}}>View your rota, check leave balance, submit holiday requests.</div>
            </div>
          </div>
          {inviteModal.email ? (
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <div style={{background:"rgba(58,173,152,0.08)",border:`1px solid ${C.tealBorder}`,borderRadius:10,padding:"10px 14px",fontSize:12,color:C.dim,textAlign:"center"}}>
                Email: <span style={{color:C.text,fontWeight:600}}>{inviteModal.email}</span>
              </div>
              <div style={{fontSize:11,color:C.dim,lineHeight:1.5,padding:"4px 4px"}}>
                Sends a branded link straight to their inbox. If it fails (sender domain unverified, server hiccup, etc.) use Open in Mail or Copy Text below.
              </div>
              <button onClick={async()=>{
                setCopied("sending");
                const r=await sendInviteEmail(inviteModal, state);
                if(r.ok){setCopied("sent");setTimeout(()=>setCopied(false),3000);}
                else{
                  // Honest failure — show actual reason in the button label briefly,
                  // and full reason logged to console.
                  setCopied({type:"err", reason:r.error||"Unknown error"});
                  setTimeout(()=>setCopied(false),8000);
                }
              }} style={{...BTN(C.teal),padding:"11px 0",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                {copied==="sending"?"Sending…":copied==="sent"?"✓ Sent!":(typeof copied==="object"&&copied?.type==="err")?"Failed — see below":"✉ Send invite"}
              </button>
              {typeof copied==="object" && copied?.type==="err" && (
                <div style={{background:"rgba(245,197,24,0.08)",border:`1px solid rgba(245,197,24,0.25)`,borderRadius:10,padding:"10px 14px",fontSize:11,color:C.warn,lineHeight:1.5}}>
                  <strong>Send failed:</strong> {copied.reason}
                  <div style={{color:C.dim,marginTop:6,fontSize:10}}>Open browser console (Develop → Show JavaScript Console on Safari) for full details. Use the manual options below to send the invite yourself.</div>
                </div>
              )}
              <div style={{display:"flex",gap:8,marginTop:4}}>
                <button onClick={()=>openMailto(inviteModal)} style={{...BTN(C.teal,true),flex:1,padding:"9px 0",fontSize:12}}>
                  Open in Mail
                </button>
                <button onClick={()=>{copyInvite(inviteModal);setCopied("copied");setTimeout(()=>setCopied(false),2500);}} style={{...BTN(C.teal,true),flex:1,padding:"9px 0",fontSize:12}}>
                  {copied==="copied"?"Copied!":"Copy Text"}
                </button>
              </div>
            </div>
          ) : (
            <div style={{fontSize:12,color:C.dim,textAlign:"center",padding:"8px 0"}}>
              No email on file — edit this user to add their email and the invite will fire automatically.
            </div>
          )}
          <button onClick={()=>setInviteModal(null)} style={{...BTN(C.dim,true),width:"100%",padding:"10px 0",marginTop:10}}>Done</button>
        </Overlay>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════
   SETTINGS TAB — Principal only
   API key · Master AI toggle · Usage tracker · GDPR docs
═══════════════════════════════════════════════════ */
function SettingsTab({state,set,user}) {
  // User Accounts moved to People tab in v10.x — Settings is now principal-only.
  // Managers no longer see this tab at all (gated in AdminHub).
  const [subTab,setSubTab] = useState("ai");
  const [anonInput,setAnonInput] = useState(state?.settings?.supabaseAnonKey||"");
  const [anonSaved,setAnonSaved] = useState(false);
  const [docModal,setDocModal] = useState(null);

  function toggleMaster() {
    set(s=>({...s,settings:{...s.settings,aiMasterEnabled:!s.settings?.aiMasterEnabled}}));
  }

  const aiUsage = state?.aiUsage||[];
  const totalCost = aiUsage.reduce((a,e)=>a+(e.costGBP||0),0);
  const totalIn   = aiUsage.reduce((a,e)=>a+(e.tokensIn||0),0);
  const totalOut  = aiUsage.reduce((a,e)=>a+(e.tokensOut||0),0);

  // Usage by user
  const byUser = {};
  aiUsage.forEach(e=>{
    if (!byUser[e.userName]) byUser[e.userName]={calls:0,costGBP:0,tokensIn:0,tokensOut:0};
    byUser[e.userName].calls++;
    byUser[e.userName].costGBP+=e.costGBP||0;
    byUser[e.userName].tokensIn+=e.tokensIn||0;
    byUser[e.userName].tokensOut+=e.tokensOut||0;
  });

  const SUBTABS = [
    ...(isPrincipalUser(user)?[
      {id:"ai",       label:"AI & API"},
      {id:"rota",     label:"Rota"},
      {id:"sickness", label:"Sickness"},
      {id:"issues",   label:"Issues"},
      {id:"toil",     label:"TOIL & Leave"},
      {id:"usage",    label:"Usage & Cost"},
      {id:"gdpr",     label:"GDPR Documents"},
      {id:"consents", label:"T&C Consents"},
    ]:[]),
    // User Accounts moved to People tab in v10.x.
  ];

  const masterOn = state?.settings?.aiMasterEnabled ?? true;

  const AI_TIERS = [
    {role:"principal",  label:state?.practiceProfile?.principalName||user?.name||"Principal",    tier:"Full AI",        desc:"All actions, all sites",         color:C.teal},
    {role:"manager",    label:"Site Managers",    tier:"Full AI",        desc:"All actions, their site",         color:C.teal},
    {role:"managerLite",label:"Acting Managers",  tier:"Full AI",        desc:"All actions, their site",         color:C.teal},
    {role:"headNurse",  label:"Head Nurses",      tier:"Sickness AI",    desc:"Sickness & cover queries only",   color:C.warn},
    {role:"staff",      label:"Nurses & Staff",   tier:"No AI",          desc:"Not enabled",                     color:C.muted},
    {role:"clinician",  label:"Dentists",         tier:"No AI",          desc:"Not enabled — by design",         color:C.muted},
  ];

  return (
    <div style={{cursor:"default"}}>
      <div style={{marginBottom:20}}>
        <h3 style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading,marginBottom:4}}>Settings</h3>
        <div style={{fontSize:12,color:C.dim}}>Principal only · Controls AI, API keys, and GDPR documents.</div>
      </div>

      {/* Sub-tab bar */}
      <div style={{display:"flex",gap:0,borderBottom:`1px solid ${C.border}`,marginBottom:24}}>
        {SUBTABS.map(t=>(
          <button key={t.id} onClick={()=>setSubTab(t.id)} style={{background:"transparent",border:"none",padding:"9px 16px",cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600,color:subTab===t.id?C.teal:C.mid,borderBottom:subTab===t.id?`2px solid ${C.teal}`:"2px solid transparent"}}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── AI & API ── */}
      {subTab==="ai"&&(
        <div style={{maxWidth:560,display:"flex",flexDirection:"column",gap:20}}>

          {/* Master switch */}
          <div style={{...CS,padding:"18px 20px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontWeight:700,fontSize:14,color:C.heading,marginBottom:3}}>Master AI Switch</div>
                <div style={{fontSize:12,color:C.dim}}>Instantly disables all AI for all users. Use if you need to pause access.</div>
              </div>
              <button onClick={toggleMaster} style={{padding:"8px 20px",borderRadius:99,border:"none",fontFamily:"inherit",fontSize:13,fontWeight:700,cursor:"pointer",background:masterOn?C.teal:C.danger,color:"#fff",minWidth:80,transition:"background 0.2s"}}>
                {masterOn?"ON":"OFF"}
              </button>
            </div>
            {!masterOn&&<div style={{marginTop:10,padding:"8px 12px",background:"rgba(255,70,70,0.08)",border:`1px solid #fecaca`,borderRadius:8,fontSize:12,color:C.danger,fontWeight:600}}>AI is currently disabled for all users including you</div>}
          </div>

          {/* API Key — managed via Vercel, not editable in UI */}
          <div style={{...CS,padding:"18px 20px"}}>
            <div style={{fontWeight:700,fontSize:14,color:C.heading,marginBottom:4}}>Anthropic API Key</div>
            <div style={{fontSize:12,color:C.dim,marginBottom:12,lineHeight:1.6}}>
              Managed securely via Vercel environment variables. Only accessible to the developer — not stored or editable here.
            </div>
            <div style={{display:"flex",alignItems:"center",gap:8,padding:"10px 14px",background:"rgba(0,229,160,0.06)",border:"1px solid rgba(0,229,160,0.18)",borderRadius:8}}>
              <div style={{width:8,height:8,borderRadius:99,background:C.green,flexShrink:0}}/>
              <span style={{fontSize:12,color:C.green,fontWeight:600}}>Configured via Vercel · Iris is active</span>
            </div>
          </div>

          {/* Supabase Anon Key — for passkeys */}
          <div style={{...CS,padding:"18px 20px"}}>
            <div style={{fontWeight:700,fontSize:14,color:C.heading,marginBottom:4}}>Supabase Publishable Key</div>
            <div style={{fontSize:12,color:C.dim,marginBottom:14,lineHeight:1.6}}>
              Required for Face ID / Passkey login. Get from <span style={{color:C.teal}}>supabase.com</span> → Settings → API → Publishable key.
            </div>
            <div style={{display:"flex",gap:8,alignItems:"center"}}>
              <input type="password" value={anonInput} onChange={e=>setAnonInput(e.target.value)}
                placeholder="sb_publishable_…" style={{...IS,fontFamily:"monospace",fontSize:12,flex:1}}/>
              <button onClick={()=>{
                set(s=>({...s,settings:{...s.settings,supabaseAnonKey:anonInput.trim()}}));
                setAnonSaved(true); setTimeout(()=>setAnonSaved(false),2000);
              }} style={{...BTN(C.teal),whiteSpace:"nowrap"}}>{anonSaved?"Saved":"Save"}</button>
            </div>
            <div style={{marginTop:8,display:"flex",alignItems:"center",gap:6}}>
              <div style={{width:8,height:8,borderRadius:99,background:state?.settings?.supabaseAnonKey?C.green:C.warn}}/>
              <span style={{fontSize:11,color:state?.settings?.supabaseAnonKey?C.green:C.warn,fontWeight:600}}>
                {state?.settings?.supabaseAnonKey?"Configured — Face ID available":"Not set — Face ID disabled"}
              </span>
            </div>
          </div>

          {/* AI Tier table */}
          <div style={{...CS,padding:"18px 20px"}}>
            <div style={{fontWeight:700,fontSize:14,color:C.heading,marginBottom:14}}>AI Access by Role</div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {AI_TIERS.map(t=>(
                <div key={t.role} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",background:t.color+"0d",border:`1px solid ${t.color}22`,borderRadius:10}}>
                  <div>
                    <div style={{fontWeight:600,fontSize:13,color:C.text}}>{t.label}</div>
                    <div style={{fontSize:11,color:C.dim,marginTop:1}}>{t.desc}</div>
                  </div>
                  <Pill c={t.color} t={t.tier} sm/>
                </div>
              ))}
            </div>
            <div style={{fontSize:11,color:C.dim,marginTop:12,padding:"8px 12px",background:C.bg,borderRadius:8}}>
              Tier access is fixed by role. To change a user's tier, update their role in Staff Directory.
            </div>
          </div>
        </div>
      )}

      {/* ── ROTA SETTINGS ── */}
      {subTab==="rota"&&(
        <div>
          <div style={{fontSize:13,fontWeight:700,color:C.heading,marginBottom:4}}>Rota Settings</div>
          <div style={{fontSize:11,color:C.dim,marginBottom:16}}>Configure two-week rota patterns and scheduling behaviour.</div>
          <div style={{padding:"14px 16px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:12,marginBottom:12}}>
            <div style={{fontSize:12,fontWeight:700,color:C.heading,marginBottom:4}}>Week A reference date</div>
            <div style={{fontSize:11,color:C.dim,marginBottom:10}}>
              If any clinician works on a two-week pattern (Week A / Week B), set the Monday of a week you know to be "Week A". The rota uses this to calculate which weeks are A and B. If not set, Omni uses week-of-year parity.
            </div>
            <div style={{display:"flex",gap:12,alignItems:"flex-end",flexWrap:"wrap"}}>
              <div style={{flex:1,minWidth:180}}>
                <label style={LB}>Week A starts on (any Monday)</label>
                <input type="date" value={state.settings?.weekAStart||""} onChange={e=>set(s=>({...s,settings:{...s.settings,weekAStart:e.target.value}}))} style={IS}/>
              </div>
              {state.settings?.weekAStart&&(
                <div style={{fontSize:11,color:C.teal,fontWeight:600,paddingBottom:8}}>
                  ✓ {new Date(state.settings.weekAStart+"T12:00:00").toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long"})} is Week A
                </div>
              )}
            </div>
            {state.settings?.weekAStart&&<button onClick={()=>set(s=>({...s,settings:{...s.settings,weekAStart:""}}))} style={{marginTop:8,padding:"4px 10px",borderRadius:6,fontFamily:"inherit",fontSize:10,cursor:"pointer",border:`1px solid ${C.border}`,background:"transparent",color:C.dim}}>Clear (revert to default)</button>}
          </div>
        </div>
      )}

      {/* ── SICKNESS & ABSENCE ── */}
      {subTab==="sickness"&&(
        <div style={{maxWidth:560}}>
          <div style={{fontSize:13,fontWeight:700,color:C.heading,marginBottom:4}}>Sickness &amp; Absence</div>
          <div style={{fontSize:11,color:C.dim,marginBottom:20,lineHeight:1.6}}>
            Set who handles absence logging at each site. The designated contact is the primary person Iris routes sickness to. Head nurses and the principal always retain full access regardless of this setting.
          </div>
          {Object.entries(SITES).map(([siteId,site])=>{
            const allSiteStaff=[
              ...(state.nurses||NURSES_BASE).filter(n=>!n.archived&&!n.maternity),
              ...RECS_BASE.filter(r=>!r.archived),
              ...(state.customRecs||[]).filter(r=>!r.archived),
              ...getClinicians(state).filter(c=>!c._deleted),
            ].sort((a,b)=>a.name.localeCompare(b.name));
            const current=(state.settings?.sicknessContact||{})[siteId]||"";
            const currentPerson=allSiteStaff.find(s=>s.id===current);
            return (
              <div key={siteId} style={{padding:"14px 16px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:12,marginBottom:12}}>
                <div style={{fontSize:12,fontWeight:700,color:site.color,marginBottom:10}}>{site.name}</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr auto",gap:10,alignItems:"flex-end"}}>
                  <div>
                    <label style={LB}>Primary sickness contact</label>
                    <select value={current} onChange={e=>set(s=>({...s,settings:{...s.settings,sicknessContact:{...(s.settings?.sicknessContact||{}),[siteId]:e.target.value}}}))} style={IS}>
                      <option value="">— Not set —</option>
                      {allSiteStaff.map(p=><option key={p.id} value={p.id}>{p.name} · {SITES[p.site]?.name||p.site} · {p.type||p.area||"Staff"}</option>)}
                    </select>
                  </div>
                  {currentPerson&&(
                    <div style={{paddingBottom:8,fontSize:11,color:C.teal,fontWeight:600}}>
                      ✓ {currentPerson.name.split(" ")[0]} is primary contact
                    </div>
                  )}
                </div>
                {!current&&(
                  <div style={{marginTop:8,fontSize:11,color:C.warn}}>
                    ⚠️ No contact set — Iris will not know who to notify when someone calls in sick at {site.name}.
                  </div>
                )}
                <div style={{marginTop:8,fontSize:10,color:C.dim,lineHeight:1.5}}>
                  Head nurse and principal always have full access. This setting tells Iris who to mention first and who receives absence notifications.
                </div>
              </div>
            );
          })}
        </div>
      )}

      {subTab==="issues"&&(
        <div style={{maxWidth:560}}>
          <div style={{fontSize:13,fontWeight:700,color:C.heading,marginBottom:4}}>Issues — Trusted Viewers</div>
          <div style={{fontSize:11,color:C.dim,marginBottom:20,lineHeight:1.6}}>
            By default, only managers and the principal can see Tier 2 (Concern) issues and the recurring-issues banner. Tier 3 (Serious) always stays with you. Use this list to extend visibility to specific people you trust to share oversight with — useful when you want a senior nurse, head nurse, or trusted clinician to know what's coming up.<br/><br/>
            <strong style={{color:"rgba(255,255,255,0.78)"}}>What trusted viewers see at T2:</strong> operational concerns only — infection control, process failures, equipment. <strong style={{color:"rgba(255,255,255,0.78)"}}>What they DON'T see:</strong> staff conduct or other interpersonal concerns where they may work alongside the named individual. Those stay with managers and the principal.
          </div>
          {(()=>{
            const allStaff = [
              ...(state.nurses||NURSES_BASE).filter(n=>!n.archived&&!n.maternity),
              ...RECS_BASE.filter(r=>!r.archived),
              ...(state.customRecs||[]).filter(r=>!r.archived),
              ...getClinicians(state).filter(c=>!c._deleted),
            ].sort((a,b)=>a.name.localeCompare(b.name));
            const viewers = state.issueViewers||[];
            function toggle(id) {
              set(s=>{
                const cur = s.issueViewers||[];
                const next = cur.includes(id) ? cur.filter(x=>x!==id) : [...cur, id];
                return {...s, issueViewers: next};
              });
            }
            return (
              <div style={{padding:"14px 16px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:12}}>
                <div style={{fontSize:11,color:C.dim,marginBottom:10}}>{viewers.length===0?"No additional viewers — only managers and you can see T2 and patterns.":`${viewers.length} trusted viewer${viewers.length!==1?"s":""} granted access.`}</div>
                <div style={{display:"flex",flexDirection:"column",gap:6,maxHeight:360,overflowY:"auto"}}>
                  {allStaff.map(p=>{
                    const isOn = viewers.includes(p.id);
                    return (
                      <label key={p.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:8,cursor:"pointer",background:isOn?C.tealPale:"transparent",border:`1px solid ${isOn?C.tealBorder:C.border2}`}}>
                        <input type="checkbox" checked={isOn} onChange={()=>toggle(p.id)} style={{width:16,height:16,accentColor:C.teal}}/>
                        <div style={{flex:1}}>
                          <div style={{fontSize:12,fontWeight:600,color:C.text}}>{p.name}</div>
                          <div style={{fontSize:10,color:C.dim}}>{SITES[p.site]?.name||p.site} · {p.type||p.area||"Staff"}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <div style={{marginTop:12,fontSize:10,color:C.dim,lineHeight:1.5}}>
                  Trusted viewers see Tier 1 + Tier 2 issues and the recurring-pattern banner — exactly what a manager sees. Tier 3 (Serious / Whistleblowing) always remains with you. Reporters of T3 are protected by the Employment Rights Act 1996.
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── TOIL & LEAVE POLICY (Principal only) ── */}
      {subTab==="toil"&&(
        <div style={{maxWidth:600}}>
          <h4 style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",
            fontSize:18,color:C.heading,marginBottom:4}}>
            TOIL & Leave Policy
          </h4>
          <div style={{fontSize:12,color:C.dim,marginBottom:20,lineHeight:1.6}}>
            Configure how overtime and leave work at your practice.
            These settings apply to all staff. Changes take effect immediately
            — existing TOIL balances are not affected by rate changes.
            <span style={{color:C.warn,fontWeight:600,marginLeft:6}}>
              Principal only.
            </span>
          </div>

          {/* TOIL enabled */}
          <div style={{...CS,marginBottom:12,padding:"16px 20px"}}>
            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"center",marginBottom:4}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:C.text}}>
                  Time in Lieu (TOIL)
                </div>
                <div style={{fontSize:11,color:C.dim,marginTop:2,lineHeight:1.5}}>
                  Allow staff to bank overtime hours instead of being paid.
                  When enabled, overtime approval shows a "Pay" or "Bank as TOIL"
                  choice.
                </div>
              </div>
              <button onClick={()=>set(s=>({...s,toilPolicy:{
                ...(s.toilPolicy||{}),
                enabled:!(s.toilPolicy?.enabled),
              }}))} style={{
                flexShrink:0,marginLeft:16,
                padding:"7px 18px",borderRadius:8,cursor:"pointer",
                fontFamily:"inherit",fontSize:12,fontWeight:700,
                background:(state.toilPolicy?.enabled)
                  ?"rgba(58,173,152,0.15)":"rgba(255,255,255,0.05)",
                border:`1px solid ${(state.toilPolicy?.enabled)
                  ?"rgba(58,173,152,0.4)":"rgba(255,255,255,0.12)"}`,
                color:(state.toilPolicy?.enabled)?C.teal:C.dim,
              }}>
                {(state.toilPolicy?.enabled)?"✓ Enabled":"Off"}
              </button>
            </div>
          </div>

          {(state.toilPolicy?.enabled)&&(<>

            {/* Accrual rate */}
            <div style={{...CS,marginBottom:12,padding:"16px 20px"}}>
              <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:4}}>
                TOIL accrual rate
              </div>
              <div style={{fontSize:11,color:C.dim,marginBottom:12,lineHeight:1.6}}>
                How many hours are banked per hour worked overtime.
                <br/>
                <span style={{color:C.warn}}>
                  Example: Sophie works a 3h Saturday clinic.
                  At 1× she banks 3h (worth £{(3*(state.settings?.nurseHourlyRate||14)).toFixed(0)}).
                  At 1.5× she banks 4.5h (worth £{(4.5*(state.settings?.nurseHourlyRate||14)).toFixed(0)}).
                  Most dental practices use 1× straight time.
                </span>
              </div>
              <div style={{display:"flex",gap:8}}>
                {[[1,"1× Straight time","Standard — 1 hour worked = 1 hour banked"],
                  [1.5,"1.5× Enhanced","More generous — common for evenings & weekends"]
                ].map(([val,label,desc])=>(
                  <button key={val} onClick={()=>set(s=>({...s,toilPolicy:{
                    ...(s.toilPolicy||{}),rate:val
                  }}))} style={{
                    flex:1,padding:"10px 14px",borderRadius:10,cursor:"pointer",
                    fontFamily:"inherit",textAlign:"left",
                    background:(state.toilPolicy?.rate||1)===val
                      ?"rgba(58,173,152,0.1)":"rgba(255,255,255,0.03)",
                    border:`1px solid ${(state.toilPolicy?.rate||1)===val
                      ?"rgba(58,173,152,0.35)":"rgba(255,255,255,0.08)"}`,
                    color:(state.toilPolicy?.rate||1)===val?C.teal:C.mid,
                  }}>
                    <div style={{fontSize:12,fontWeight:700,marginBottom:2}}>
                      {label}
                    </div>
                    <div style={{fontSize:10,opacity:0.7}}>{desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Expiry */}
            <div style={{...CS,marginBottom:12,padding:"16px 20px"}}>
              <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:4}}>
                TOIL expiry
              </div>
              <div style={{fontSize:11,color:C.dim,marginBottom:12,lineHeight:1.6}}>
                How long staff can hold TOIL before it expires.
                Year end is simpler — everything resets with your leave year.
                6 months keeps liability lower but staff feel more pressure to use it.
              </div>
              <div style={{display:"flex",gap:8}}>
                {[["yearEnd","Year end","Expires 31 Dec — clean, simple, staff-friendly"],
                  ["6months","6 months","Lower liability — expires 6 months after earning"]
                ].map(([val,label,desc])=>(
                  <button key={val} onClick={()=>set(s=>({...s,toilPolicy:{
                    ...(s.toilPolicy||{}),expiry:val
                  }}))} style={{
                    flex:1,padding:"10px 14px",borderRadius:10,cursor:"pointer",
                    fontFamily:"inherit",textAlign:"left",
                    background:(state.toilPolicy?.expiry||"yearEnd")===val
                      ?"rgba(58,173,152,0.1)":"rgba(255,255,255,0.03)",
                    border:`1px solid ${(state.toilPolicy?.expiry||"yearEnd")===val
                      ?"rgba(58,173,152,0.35)":"rgba(255,255,255,0.08)"}`,
                    color:(state.toilPolicy?.expiry||"yearEnd")===val?C.teal:C.mid,
                  }}>
                    <div style={{fontSize:12,fontWeight:700,marginBottom:2}}>
                      {label}
                    </div>
                    <div style={{fontSize:10,opacity:0.7}}>{desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Alert threshold */}
            <div style={{...CS,marginBottom:12,padding:"16px 20px"}}>
              <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:4}}>
                Balance alert threshold
              </div>
              <div style={{fontSize:11,color:C.dim,marginBottom:10,lineHeight:1.5}}>
                Iris flags staff on the dashboard when their TOIL balance
                exceeds this. Default is 7.5h (one day).
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {[[7.5,"7.5h — 1 day"],[15,"15h — 2 days"],[37.5,"37.5h — 1 week"]].map(([val,label])=>(
                  <button key={val} onClick={()=>set(s=>({...s,toilPolicy:{
                    ...(s.toilPolicy||{}),alertThresholdHrs:val
                  }}))} style={{
                    padding:"6px 14px",borderRadius:8,cursor:"pointer",
                    fontFamily:"inherit",fontSize:11,fontWeight:600,
                    background:(state.toilPolicy?.alertThresholdHrs||7.5)===val
                      ?"rgba(58,173,152,0.12)":"rgba(255,255,255,0.04)",
                    border:`1px solid ${(state.toilPolicy?.alertThresholdHrs||7.5)===val
                      ?"rgba(58,173,152,0.3)":"rgba(255,255,255,0.08)"}`,
                    color:(state.toilPolicy?.alertThresholdHrs||7.5)===val?C.teal:C.mid,
                  }}>{label}</button>
                ))}
              </div>
            </div>

            {/* Payout on leaving */}
            <div style={{...CS,marginBottom:12,padding:"16px 20px"}}>
              <div style={{display:"flex",justifyContent:"space-between",
                alignItems:"center"}}>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:2}}>
                    Pay out unused TOIL on leaving
                  </div>
                  <div style={{fontSize:11,color:C.dim,lineHeight:1.5}}>
                    Recommended. Converts unused TOIL to pay at normal hourly rate
                    when a staff member leaves. Omni flags this automatically.
                    If off, unused TOIL is forfeited — check your employment contracts.
                  </div>
                </div>
                <button onClick={()=>set(s=>({...s,toilPolicy:{
                  ...(s.toilPolicy||{}),
                  payoutOnLeaving:!(s.toilPolicy?.payoutOnLeaving??true),
                }}))} style={{
                  flexShrink:0,marginLeft:16,
                  padding:"7px 18px",borderRadius:8,cursor:"pointer",
                  fontFamily:"inherit",fontSize:12,fontWeight:700,
                  background:(state.toilPolicy?.payoutOnLeaving??true)
                    ?"rgba(58,173,152,0.15)":"rgba(255,255,255,0.05)",
                  border:`1px solid ${(state.toilPolicy?.payoutOnLeaving??true)
                    ?"rgba(58,173,152,0.4)":"rgba(255,255,255,0.12)"}`,
                  color:(state.toilPolicy?.payoutOnLeaving??true)?C.teal:C.dim,
                }}>
                  {(state.toilPolicy?.payoutOnLeaving??true)?"✓ Yes":"No"}
                </button>
              </div>
            </div>

            {/* Current liability */}
            {(()=>{
              const allStaff2 = [
                ...(state.nurses||NURSES_BASE).filter(n=>!n.maternity&&!n.archived),
                ...RECS_BASE,
              ];
              const liabilityItems = allStaff2
                .map(s=>({name:s.name,bal:getTOILBalance(s.id,state)}))
                .filter(x=>x.bal>0)
                .sort((a,b)=>b.bal-a.bal);
              if (!liabilityItems.length) return null;
              const totalHrs = liabilityItems.reduce((s,x)=>s+x.bal,0);
              const rate = state.settings?.nurseHourlyRate||14;
              const totalVal = Math.round(totalHrs*rate);
              return (
                <div style={{...CS,padding:"14px 18px",
                  borderLeft:`3px solid ${C.warn}`}}>
                  <div style={{fontSize:12,fontWeight:700,color:C.warn,
                    marginBottom:8}}>
                    Current TOIL liability — {totalHrs.toFixed(1)}h
                    {" / "}~£{totalVal.toLocaleString()}
                  </div>
                  {liabilityItems.slice(0,5).map(x=>(
                    <div key={x.name} style={{display:"flex",
                      justifyContent:"space-between",fontSize:11,
                      color:C.mid,padding:"3px 0",
                      borderBottom:`1px solid ${C.border2}`}}>
                      <span>{x.name}</span>
                      <span style={{color:x.bal>=15?C.warn:C.mid}}>
                        {x.bal.toFixed(1)}h
                        {" / "}~£{Math.round(x.bal*rate)}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })()}
          </>)}

          {/* Carers leave info */}
          <div style={{...CS,marginTop:20,padding:"16px 20px",
            borderLeft:`3px solid #a78bfa`}}>
            <div style={{fontSize:13,fontWeight:700,color:"#a78bfa",
              marginBottom:6}}>
              Carer's Leave — statutory
            </div>
            <div style={{fontSize:11,color:C.dim,lineHeight:1.7}}>
              All employees have a statutory right to 5 days unpaid
              carer's leave per year (Carer's Leave Act 2024).
              You cannot require evidence. You can delay by up to
              1 month if operationally necessary — give written notice.
              Omni tracks usage, flags patterns (e.g. always Fridays),
              and shows remaining entitlement in leave requests.
              No Bradford Factor impact.
            </div>
          </div>
        </div>
      )}

      {/* ── USAGE & COST ── */}
      {subTab==="usage"&&(
        <div style={{maxWidth:700}}>
          {/* Summary cards */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:12,marginBottom:20}}>
            {[
              {label:"Total API calls",value:aiUsage.length,sub:"since launch"},
              {label:"Total cost",value:`£${totalCost.toFixed(2)}`,sub:"estimated GBP"},
              {label:"Input tokens",value:totalIn.toLocaleString(),sub:"questions + context"},
              {label:"Output tokens",value:totalOut.toLocaleString(),sub:"AI responses"},
            ].map(c=>(
              <div key={c.label} style={{...CS,padding:"14px 16px",borderTop:`3px solid ${C.teal}`}}>
                <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>{c.label}</div>
                <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:26,fontWeight:800,color:C.heading,lineHeight:1}}>{c.value}</div>
                <div style={{fontSize:10,color:C.muted,marginTop:3}}>{c.sub}</div>
              </div>
            ))}
          </div>

          {/* By user */}
          <div style={{...CS}}>
            <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:14,padding:"0 4px"}}>Breakdown by User</div>
            {Object.keys(byUser).length===0 ? (
              <div style={{textAlign:"center",padding:24,color:C.dim,fontSize:12}}>No AI usage yet. Activate the API key to start.</div>
            ) : (
              <table style={{borderCollapse:"collapse",width:"100%"}}>
                <thead><tr>
                  {["User","Calls","Tokens In","Tokens Out","Est. Cost"].map(h=>(
                    <th key={h} style={{padding:"6px 10px",fontSize:9,fontWeight:700,color:C.dim,textAlign:"left",textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:`1px solid ${C.border}`}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {Object.entries(byUser).sort((a,b)=>b[1].costGBP-a[1].costGBP).map(([name,u])=>(
                    <tr key={name}>
                      <td style={{padding:"8px 10px",fontSize:13,color:C.text,borderBottom:`1px solid ${C.border2}`,fontWeight:600}}>{name}</td>
                      <td style={{padding:"8px 10px",fontSize:12,color:C.mid,borderBottom:`1px solid ${C.border2}`}}>{u.calls}</td>
                      <td style={{padding:"8px 10px",fontSize:12,color:C.mid,borderBottom:`1px solid ${C.border2}`}}>{u.tokensIn.toLocaleString()}</td>
                      <td style={{padding:"8px 10px",fontSize:12,color:C.mid,borderBottom:`1px solid ${C.border2}`}}>{u.tokensOut.toLocaleString()}</td>
                      <td style={{padding:"8px 10px",fontSize:12,color:C.teal,fontWeight:700,borderBottom:`1px solid ${C.border2}`}}>£{u.costGBP.toFixed(3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div style={{fontSize:10,color:C.muted,marginTop:10,padding:"0 4px"}}>Cost estimate: Sonnet ~$3/M input, ~$15/M output, converted at £0.79/$1. Actual billing via Anthropic Console.</div>
          </div>

          {/* Recent calls */}
          {aiUsage.length>0&&(
            <div style={{...CS,marginTop:16}}>
              <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:14,padding:"0 4px"}}>Recent Calls (last 20)</div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {[...aiUsage].reverse().slice(0,20).map(e=>(
                  <div key={e.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 10px",background:C.bg,borderRadius:8,fontSize:11}}>
                    <div>
                      <span style={{fontWeight:600,color:C.text}}>{e.userName}</span>
                      <span style={{color:C.dim,marginLeft:8}}>{e.date} {e.time||""}</span>
                      <span style={{color:C.muted,marginLeft:8}}>{e.role}</span>
                    </div>
                    <div style={{display:"flex",gap:12,color:C.dim}}>
                      <span>{(e.tokensIn+e.tokensOut).toLocaleString()} tok</span>
                      <span style={{color:C.teal,fontWeight:600}}>£{(e.costGBP||0).toFixed(4)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── GDPR DOCUMENTS ── */}
      {subTab==="gdpr"&&(
        <div style={{maxWidth:700}}>
          <div style={{fontSize:12,color:C.dim,marginBottom:16,lineHeight:1.7}}>
            These documents govern how Omni handles staff data at {PRACTICE_CONFIG.name}. Click any document to view and print/save as PDF. The Privacy Notice for staff is also accessible in the Reports tab.
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {[
              {id:"gdpr",    icon:"🛡",  title:"GDPR Data Processing Framework",    sub:"Data Controller, legal basis, retention, security — internal governance doc"},
              {id:"ropa",    icon:"📋",  title:"Record of Processing Activities",   sub:"Article 30 GDPR — full register of all personal data processing"},
              {id:"breach",  icon:"🔴",  title:"Data Breach Response Procedure",    sub:"Step-by-step breach management, ICO notification, timelines"},
              {id:"dpia",    icon:"🔍",  title:"Data Protection Impact Assessment", sub:"DPIA for Omni workforce scheduling system — risk & mitigation"},
            ].map(d=>(
              <div key={d.id} style={{...CS,padding:"16px 18px",display:"flex",alignItems:"center",gap:14}}>
                <div style={{fontSize:24,flexShrink:0}}>{d.icon}</div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,fontSize:13,color:C.heading,marginBottom:2}}>{d.title}</div>
                  <div style={{fontSize:11,color:C.dim}}>{d.sub}</div>
                </div>
                <button onClick={()=>setDocModal(d.id)} style={{...BTN(C.teal,true),fontSize:12,whiteSpace:"nowrap",flexShrink:0}}>View & Print</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* T&C Consents log */}
      {subTab==="consents"&&(
        <div>
          <div style={{marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:600,color:C.heading,marginBottom:4}}>T&C & Privacy Acceptance Log</div>
            <div style={{fontSize:12,color:C.dim}}>Every staff member who has accepted the Omni privacy notice. Logged with timestamp for compliance. This is your audit trail.</div>
          </div>
          {(state.gdprConsents||[]).length===0?(
            <div style={{...CS,textAlign:"center",padding:32,color:C.dim}}>
              <div style={{fontSize:28,marginBottom:8}}>📋</div>
              <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:4}}>No acceptances recorded yet</div>
              <div style={{fontSize:11}}>Staff see the acceptance screen on first login. It will appear here once accepted.</div>
            </div>
          ):(
            <div>
              <div style={{fontSize:11,color:C.dim,marginBottom:12}}>{(state.gdprConsents||[]).length} acceptance{(state.gdprConsents||[]).length!==1?"s":""} recorded</div>
              {[...(state.gdprConsents||[])].sort((a,b)=>b.timestamp.localeCompare(a.timestamp)).map((c,i)=>(
                <div key={i} style={{...CS,marginBottom:8,padding:"12px 16px",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
                  <div style={{width:36,height:36,borderRadius:"50%",background:C.tealPale,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:C.teal,flexShrink:0}}>
                    {c.userName?.split(" ").map(n=>n[0]).join("").slice(0,2)||"?"}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:700,color:C.text}}>{c.userName||"Unknown"}</div>
                    <div style={{fontSize:11,color:C.dim,marginTop:1}}>
                      {c.role&&<span style={{marginRight:8,textTransform:"capitalize"}}>{c.role}</span>}
                      {c.email&&<span style={{marginRight:8}}>{c.email}</span>}
                    </div>
                  </div>
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <div style={{fontSize:11,fontWeight:600,color:C.green}}>Accepted</div>
                    <div style={{fontSize:10,color:C.dim,marginTop:2}}>
                      {c.timestamp ? new Date(c.timestamp).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"}) : "—"}
                    </div>
                    {c.version&&<div style={{fontSize:9,color:C.muted,marginTop:1}}>v{c.version}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* GDPR Document modals */}
      {docModal&&(
        <Overlay onClose={()=>setDocModal(null)} wide>
          <PulseDocument docId={docModal} state={state} onClose={()=>setDocModal(null)}/>
        </Overlay>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   PULSE BRANDED DOCUMENT COMPONENT
   Print / Save as PDF via window.print()
═══════════════════════════════════════════ */
function PulseDocument({docId,onClose,state}) {
  const docRef = useRef(null);
  const today  = new Date().toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"});
  const version= "v1.0";

  function printDoc() {
    const content = docRef.current?.innerHTML;
    if (!content) return;
    const w = window.open("","_blank","width=900,height=700");
    w.document.write(`<!DOCTYPE html><html><head><title>${DOCS[docId]?.title||"Omni Document"}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Instrument+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Instrument Sans',sans-serif;background:#f7f6f3;color:#2a2420;font-size:13px;line-height:1.7}
  .page{max-width:800px;margin:0 auto;background:#fff;box-shadow:0 2px 20px rgba(0,0,0,0.1)}
  .doc-header{background:#0c0b16;padding:16px 40px;display:flex;align-items:center;gap:12;color:#fff}.wm-main{font-family:'Instrument Serif',Georgia,serif;font-style:italic;font-weight:700;font-size:32px;color:#fff;letter-spacing:-0.04em}.wm-dot{font-family:'Instrument Serif',Georgia,serif;font-style:italic;font-weight:700;font-size:32px;color:#00e5a0;margin-left:3px}.doc-title{border-left:1px solid rgba(255,255,255,0.2);margin-left:14px;padding-left:14px;font-size:11px;color:rgba(255,255,255,0.6);font-family:'Instrument Sans',sans-serif}.doc-body{padding:36px 40px}
  .header-text h1{font-family:'Instrument Serif',serif;font-style:italic;font-size:28px;font-weight:700;margin-bottom:4px;letter-spacing:-0.02em}
  .header-text h2{font-size:13px;font-weight:500;opacity:0.8}
  .logo-box{width:52px;height:52px;background:rgba(255,255,255,0.15);border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:22px}
  .body{padding:36px 40px}
  h3{font-family:'Instrument Serif',serif;font-size:17px;font-weight:400;color:#3aad98;border-bottom:2px solid #e8faf7;padding-bottom:6px;margin:28px 0 14px}
  h3:first-of-type{margin-top:0}
  p{margin-bottom:12px;color:#3d3830}
  ul,ol{margin:0 0 14px 20px}
  li{margin-bottom:5px;color:#3d3830}
  .meta-table{width:100%;border-collapse:collapse;margin-bottom:20px}
  .meta-table td{padding:7px 12px;border:1px solid #e8e4de;font-size:12px}
  .meta-table td:first-child{background:#f7f6f3;font-weight:700;color:#6b6257;width:38%;text-transform:uppercase;font-size:10px;letter-spacing:0.06em}
  .badge{display:inline-block;padding:2px 10px;border-radius:99px;font-size:10px;font-weight:700;background:#e8faf7;color:#3aad98;border:1px solid #9de8df;margin:2px}
  .alert{padding:12px 16px;border-radius:8px;background:#fef2f2;border-left:4px solid #dc2626;margin-bottom:14px;font-size:12px;color:#dc2626}
  .note{padding:10px 14px;border-radius:8px;background:#e8faf7;border-left:3px solid #3aad98;margin-bottom:14px;font-size:12px;color:#3aad98}
  .footer{background:#3aad98;padding:14px 40px;display:flex;justify-content:space-between;align-items:center;font-size:10px;color:rgba(255,255,255,0.7)}
  strong{color:#2a2420}
  @media print{body{background:#fff}.page{box-shadow:none}}
</style></head><body><div class="page"><div class="doc-header"><span class="wm-main">Omni</span><span class="wm-dot">.</span><span class="doc-title">${DOCS[docId]?.title||""}</span></div><div class="doc-body">${content}</div></div></body></html>`);
    w.document.close();
    setTimeout(()=>w.print(),400);
  }

  const practiceName = PRACTICE_CONFIG.name||"the practice";
  const principalName = state?.practiceProfile?.principalName||"Principal Dentist";
  const DOCS = {
    gdpr: {
      title:"GDPR Data Processing Framework",
      sub:`${practiceName} · Internal Governance Document`,
      body:`
        <h3>Document Information</h3>
        <table class="meta-table">
          <tr><td>Document Title</td><td>GDPR Data Processing Framework — Omni.</td></tr>
          <tr><td>Data Controller</td><td><strong>${practiceName}</strong></td></tr>
          <tr><td>Data Processor</td><td>Omni. (Prima Luce Ltd)</td></tr>
          <tr><td>Responsible Person</td><td>${principalName}</td></tr>
          <tr><td>Date</td><td>${today}</td></tr>
          <tr><td>Version</td><td>${version}</td></tr>
          <tr><td>Review Due</td><td>Annually or on material system change</td></tr>
        </table>
        <h3>1. Data Stored in Omni</h3>
        <p>The following personal data is stored by the Omni workforce management system:</p>
        <ul>
          <li>Full name, job role, and practice site</li>
          <li>Work email address (for authentication)</li>
          <li>Shift and rota schedule (working days, hours)</li>
          <li>Annual leave records and balances</li>
          <li>Sickness absence records and Bradford Factor scores</li>
          <li>Overtime, lateness, and attendance log entries</li>
          <li>Training level and clinical qualifications</li>
          <li>Audit logs of system actions (login, rota changes, admin actions)</li>
        </ul>
        <div class="alert">Data NOT Stored: Patient data, payroll, NI numbers, home addresses, financial information, and medical records are not stored in Omni and must not be entered.</div>
        <h3>2. Legal Basis for Processing</h3>
        <p>Processing is carried out under the following lawful bases:</p>
        <ul>
          <li><strong>Performance of employment contract</strong> — rota scheduling, leave management, and sickness tracking are necessary to manage the employment relationship.</li>
          <li><strong>Legitimate interests</strong> — attendance monitoring, Bradford Factor analysis, and audit logging are conducted in the legitimate interests of ${practiceName} as employer.</li>
        </ul>
        <h3>3. Data Residency & Security</h3>
        <table class="meta-table">
          <tr><td>Hosting Region</td><td>EU-West-1, Ireland (AWS via Supabase)</td></tr>
          <tr><td>Transmission</td><td>HTTPS (TLS 1.2+) for all data in transit</td></tr>
          <tr><td>Authentication</td><td>Google OAuth, Apple Sign-In, email/password with secure session tokens</td></tr>
          <tr><td>Access Control</td><td>Role-based permissions (Principal / Manager / Head Nurse / Staff)</td></tr>
          <tr><td>Audit Logging</td><td>Account creation, rota changes, permission updates, admin actions</td></tr>
        </table>
        <h3>4. Data Retention</h3>
        <ul>
          <li><strong>Active staff:</strong> Data retained for the duration of employment.</li>
          <li><strong>Archived staff & historical rota records:</strong> Retained for 6 years post-employment in line with employment law.</li>
          <li><strong>Audit logs:</strong> Retained for 6 years from date of entry.</li>
          <li><strong>Breach records:</strong> Retained for 6 years from date of incident.</li>
        </ul>
        <h3>5. Third-Party Processors</h3>
        <ul>
          <li><strong>Supabase (PostgreSQL database):</strong> EU-hosted, GDPR compliant. <a href="https://supabase.com/privacy">supabase.com/privacy</a></li>
          <li><strong>Anthropic (AI queries):</strong> Used for Iris feature only. Queries include anonymised staffing data. No patient data processed. <a href="https://www.anthropic.com/privacy">anthropic.com/privacy</a></li>
          <li><strong>Google / Apple:</strong> Authentication only. Identity tokens, not rota data.</li>
        </ul>
        <h3>6. Staff Rights</h3>
        <p>Staff have the right to access, correct, or request deletion of their personal data. All data subject access requests (DSARs) should be directed to <strong>${practiceName}</strong> (Data Controller) at the practice address. Requests will be fulfilled within 30 days.</p>
        <h3>7. Consent</h3>
        <p>Consent is obtained within the application at the point of account claim. Each staff member must tick a consent checkbox before linking their account. Consent is logged with a timestamp and stored in the system audit trail.</p>
        <div class="note">Risk Classification: <strong>Low</strong> — Omni handles workforce scheduling data only. No patient data, financial data, or special category personal data is processed.</div>
      `
    },
    ropa: {
      title:"Record of Processing Activities (ROPA)",
      sub:`Article 30 UK GDPR · ${practiceName}`,
      body:`
        <h3>Document Information</h3>
        <table class="meta-table">
          <tr><td>Data Controller</td><td><strong>${practiceName}</strong></td></tr>
          <tr><td>Responsible Person</td><td>Conor O'Neill, Principal Dentist</td></tr>
          <tr><td>System</td><td>Omni Workforce Management System</td></tr>
          <tr><td>Date</td><td>${today}</td></tr>
          <tr><td>Version</td><td>${version}</td></tr>
        </table>
        <h3>Processing Activity 1 — Staff Rota & Scheduling</h3>
        <table class="meta-table">
          <tr><td>Purpose</td><td>Managing daily staff rotas, shift allocation, and surgery coverage across 3 sites</td></tr>
          <tr><td>Legal Basis</td><td>Performance of employment contract</td></tr>
          <tr><td>Data Categories</td><td>Name, role, site, working hours, shift schedule, nurse-clinician pairings</td></tr>
          <tr><td>Data Subjects</td><td>Nurses, receptionists, dental nurses, TCOs (~35 staff)</td></tr>
          <tr><td>Retention</td><td>Active staff: duration of employment. Historical: 6 years post-employment</td></tr>
          <tr><td>Processor</td><td>Supabase (EU-West-1, Ireland)</td></tr>
        </table>
        <h3>Processing Activity 2 — Leave & Holiday Management</h3>
        <table class="meta-table">
          <tr><td>Purpose</td><td>Tracking annual leave entitlement, requests, approvals, and balances</td></tr>
          <tr><td>Legal Basis</td><td>Performance of employment contract; statutory obligation (Working Time Regulations)</td></tr>
          <tr><td>Data Categories</td><td>Name, role, leave dates, leave type, approval status, leave balance</td></tr>
          <tr><td>Data Subjects</td><td>All staff</td></tr>
          <tr><td>Retention</td><td>6 years post-employment</td></tr>
          <tr><td>Processor</td><td>Supabase (EU-West-1, Ireland)</td></tr>
        </table>
        <h3>Processing Activity 3 — Sickness Absence & Bradford Factor</h3>
        <table class="meta-table">
          <tr><td>Purpose</td><td>Recording sickness absence, Return to Work interviews, Bradford Factor scoring for absence management</td></tr>
          <tr><td>Legal Basis</td><td>Legitimate interests (employer obligation to manage attendance); Performance of contract</td></tr>
          <tr><td>Data Categories</td><td>Name, absence dates, number of days, reason category, Bradford Factor score, RTW status. <strong>Note: Bradford Factor and sickness absence records are slightly more sensitive as they relate to absence patterns relevant to employment decisions.</strong></td></tr>
          <tr><td>Data Subjects</td><td>All staff</td></tr>
          <tr><td>Retention</td><td>6 years post-employment</td></tr>
          <tr><td>Processor</td><td>Supabase (EU-West-1, Ireland)</td></tr>
        </table>
        <h3>Processing Activity 4 — Attendance Monitoring</h3>
        <table class="meta-table">
          <tr><td>Purpose</td><td>Recording overtime, lateness, early finishes, and welfare notes</td></tr>
          <tr><td>Legal Basis</td><td>Legitimate interests; Performance of contract</td></tr>
          <tr><td>Data Categories</td><td>Name, date, type of attendance event, duration, reason, notes</td></tr>
          <tr><td>Data Subjects</td><td>Nurses, receptionists, dental nurses</td></tr>
          <tr><td>Retention</td><td>6 years post-employment</td></tr>
          <tr><td>Processor</td><td>Supabase (EU-West-1, Ireland)</td></tr>
        </table>
        <h3>Processing Activity 5 — Authentication & Audit Log</h3>
        <table class="meta-table">
          <tr><td>Purpose</td><td>Secure access control; audit trail of system changes</td></tr>
          <tr><td>Legal Basis</td><td>Legitimate interests (system security and accountability)</td></tr>
          <tr><td>Data Categories</td><td>User ID, email, provider (Google/Apple/password), login timestamps, action audit trail</td></tr>
          <tr><td>Data Subjects</td><td>All system users</td></tr>
          <tr><td>Retention</td><td>6 years</td></tr>
          <tr><td>Processor</td><td>Supabase Auth (EU-West-1, Ireland)</td></tr>
        </table>
        <div class="note">Data NOT in scope: Patient data, payroll, NI numbers, home addresses, financial records, and medical records are outside the scope of this ROPA entry for Omni. These are handled by separate systems.</div>
      `
    },
    breach: {
      title:"Data Breach Response Procedure",
      sub:`${practiceName} · Omni Workforce Platform`,
      body:`
        <h3>Document Information</h3>
        <table class="meta-table">
          <tr><td>Data Controller</td><td><strong>${practiceName}</strong></td></tr>
          <tr><td>Named Responsible Person</td><td><strong>{state?.practiceProfile?.principalName||"Principal"}, ${practiceName}</strong></td></tr>
          <tr><td>Date</td><td>${today}</td></tr>
          <tr><td>Breach Records Retained</td><td>6 years from date of incident</td></tr>
        </table>
        <div class="alert">If you discover a potential breach, act immediately. Do not wait. The ICO 72-hour notification window starts from the moment you become aware.</div>
        <h3>Step 1 — Immediate Containment (0–2 hours)</h3>
        <ol>
          <li>Notify <strong>Conor O'Neill</strong> immediately by phone or in person.</li>
          <li>Do not attempt to investigate, fix, or cover up the breach before reporting.</li>
          <li>If the breach involves unauthorised access, disable the affected accounts immediately via Supabase dashboard or by contacting the system administrator.</li>
          <li>Preserve all logs and evidence — do not delete anything.</li>
          <li>If a device is involved, isolate it from the network.</li>
        </ol>
        <h3>Step 2 — Assessment (2–12 hours)</h3>
        <p>Conor O'Neill will assess the breach:</p>
        <ul>
          <li><strong>What data was involved?</strong> Names, rotas, sickness records, login credentials?</li>
          <li><strong>How many staff affected?</strong></li>
          <li><strong>Was the data accessed, copied, or deleted?</strong></li>
          <li><strong>What is the likely harm?</strong> (embarrassment, discrimination risk, financial loss)</li>
          <li><strong>Is it notifiable?</strong> If likely to result in risk to rights and freedoms of individuals → ICO notification required within 72 hours.</li>
        </ul>
        <h3>Step 3 — ICO Notification (within 72 hours if notifiable)</h3>
        <ul>
          <li>Report via <strong>ico.org.uk/make-a-complaint/data-protection-report-a-breach/</strong></li>
          <li>Include: nature of breach, categories and approximate number of data subjects, likely consequences, measures taken or proposed.</li>
          <li>If notification is delayed beyond 72 hours, reasons for the delay must be provided to the ICO.</li>
        </ul>
        <h3>Step 4 — Notify Affected Individuals (if high risk)</h3>
        <p>If the breach is likely to result in a <em>high risk</em> to the rights and freedoms of affected staff, notify those individuals without undue delay. The notification must explain: what happened, what data was involved, what the likely consequences are, and what steps are being taken.</p>
        <div class="alert">Multi-Practice Escalation: If a breach affects data belonging to or relating to another dental practice, ${practiceName} must escalate immediately to that practice's Data Controller. Do not delay inter-practice notification.</div>
        <h3>Step 5 — Internal Documentation</h3>
        <p>All breaches must be documented, regardless of whether ICO notification is required. Record:</p>
        <ul>
          <li>Date and time of discovery</li>
          <li>Nature and scope of breach</li>
          <li>Data categories and number of subjects affected</li>
          <li>Action taken and timeline</li>
          <li>Decision on ICO notification (and reasons if not notified)</li>
          <li>Lessons learned and preventative measures</li>
        </ul>
        <p>All breach records are retained for <strong>6 years</strong> from the date of the incident.</p>
        <h3>Step 6 — Review & Prevention</h3>
        <p>Following resolution, conduct a review within 4 weeks to identify root cause and implement preventative measures. Update this procedure if the breach reveals a gap in the process.</p>
      `
    },
    dpia: {
      title:"Data Protection Impact Assessment (DPIA)",
      sub:`Omni Workforce Management System · ${practiceName}`,
      body:`
        <h3>Document Information</h3>
        <table class="meta-table">
          <tr><td>System</td><td>Omni Workforce Management System</td></tr>
          <tr><td>Data Controller</td><td><strong>${practiceName}</strong></td></tr>
          <tr><td>Responsible Person</td><td>Conor O'Neill, Principal Dentist</td></tr>
          <tr><td>Assessment Date</td><td>${today}</td></tr>
          <tr><td>Version</td><td>${version}</td></tr>
          <tr><td>Review Trigger</td><td>Annual review, or on material change to system or data processing</td></tr>
        </table>
        <h3>1. Description of Processing</h3>
        <p>Omni is a web-based workforce management system used by ${practiceName}. It processes staff scheduling, leave management, sickness absence tracking, and attendance monitoring. Access is role-based: Principal, Managers, Head Nurses, and Staff have differentiated levels of access.</p>
        <h3>2. Necessity & Proportionality</h3>
        <ul>
          <li>The processing is <strong>necessary</strong>: DCO operates across 3 sites with ~35 staff and requires a coordinated rota and absence management system.</li>
          <li>The processing is <strong>proportionate</strong>: only the minimum data required for workforce scheduling is collected. No patient data, payroll, NI numbers, or special category data beyond absence records is processed.</li>
          <li><strong>Data minimisation</strong> is enforced by design — the system is configured to prevent entry of data outside its defined scope.</li>
        </ul>
        <h3>3. Data Categories & Sensitivity Assessment</h3>
        <table class="meta-table">
          <tr><td>Name, role, site, hours</td><td>Low sensitivity — standard employment data</td></tr>
          <tr><td>Rota / shift schedule</td><td>Low sensitivity — operational data</td></tr>
          <tr><td>Annual leave records</td><td>Low sensitivity — standard employment entitlement</td></tr>
          <tr><td>Sickness absence records</td><td><strong>Medium sensitivity</strong> — absence records can be indicative of health conditions; Bradford Factor scores are used in employment management decisions. Handled under legitimate interests with appropriate access controls.</td></tr>
          <tr><td>Authentication data</td><td>Low-medium — email addresses and session tokens; protected by OAuth and HTTPS</td></tr>
          <tr><td>Audit logs</td><td>Low sensitivity — system accountability records</td></tr>
        </table>
        <h3>4. Risk Assessment</h3>
        <table class="meta-table">
          <tr><td>Risk: Unauthorised access to staff absence records</td><td>Likelihood: Low · Impact: Medium<br/>Mitigation: Role-based access; only managers and principal can view Bradford Factor data. Head nurses see their site only. Staff see their own data only.</td></tr>
          <tr><td>Risk: Data breach via insecure transmission</td><td>Likelihood: Low · Impact: Medium<br/>Mitigation: All data transmitted over HTTPS (TLS 1.2+). Supabase enforces SSL.</td></tr>
          <tr><td>Risk: Excessive data collection</td><td>Likelihood: Very Low · Impact: Low<br/>Mitigation: System architecture enforces data minimisation. Patient data fields do not exist in the system.</td></tr>
          <tr><td>Risk: Third-party processor (Supabase) breach</td><td>Likelihood: Very Low · Impact: Medium<br/>Mitigation: Supabase is SOC 2 Type II certified; EU-hosted; has its own data breach procedure. DPA in place.</td></tr>
          <tr><td>Risk: AI processing of staff data (Anthropic API)</td><td>Likelihood: Low · Impact: Low<br/>Mitigation: AI queries contain anonymised operational data only (names, roles, dates). No special category data is passed to the AI. Queries are not used for Anthropic model training under enterprise API terms.</td></tr>
        </table>
        <h3>5. Overall Risk Rating</h3>
        <div class="note">Overall risk classification: <strong>Low</strong>. Omni processes standard workforce scheduling data only. The system does not process special category data, patient data, or financial data. Controls are proportionate and effective. A full DPIA under Article 35 is not legally required, but this assessment is maintained as good practice.</div>
        <h3>6. Consultation</h3>
        <p>This DPIA was prepared by ${state?.practiceProfile?.principalName||"the Principal"} as Data Controller. Staff have been informed of data processing via the in-app Privacy Notice and consent mechanism. No Data Protection Officer (DPO) is required under Article 37 for this scale of processing.</p>
        <h3>7. Review & Approval</h3>
        <table class="meta-table">
          <tr><td>Approved by</td><td>{state?.practiceProfile?.principalName||"Practice Principal"}, ${practiceName}</td></tr>
          <tr><td>Date</td><td>${today}</td></tr>
          <tr><td>Next Review</td><td>${new Date(new Date().setFullYear(new Date().getFullYear()+1)).toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"})}</td></tr>
        </table>
      `
    },
  };

  const doc = DOCS[docId];
  if (!doc) return null;

  return (
    <div>
      {/* Print button */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:17,fontWeight:700,color:C.heading}}>{doc.title}</div>
          <div style={{fontSize:12,color:C.dim,marginTop:2}}>{doc.sub}</div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={printDoc} style={{...BTN(C.teal),display:"flex",alignItems:"center",gap:6}}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
            Save as PDF
          </button>
          <button onClick={onClose} style={BTN(C.dim,true)}>Close</button>
        </div>
      </div>

      {/* Document preview */}
      <div ref={docRef}>
        {/* Header bar */}
        <div style={{background:C.teal,padding:"20px 24px",borderRadius:"12px 12px 0 0",display:"flex",alignItems:"center",gap:16,marginBottom:0}}>
          <div style={{display:"flex",alignItems:"baseline",flexShrink:0}}>
            <span style={{fontFamily:"'Instrument Serif',Georgia,serif",fontStyle:"italic",fontWeight:700,fontSize:28,color:"#fff",letterSpacing:"-0.04em"}}>Omni</span><span style={{fontFamily:"'Instrument Serif',Georgia,serif",fontStyle:"italic",fontWeight:700,fontSize:28,color:"rgba(255,255,255,0.5)",marginLeft:"0.06em"}}>.</span>
          </div>
          <div>
            <div style={{fontFamily:"'Instrument Serif',Georgia,serif",fontStyle:"italic",fontWeight:700,fontSize:22,color:"#fff",lineHeight:1,letterSpacing:"-0.03em"}}>Omni<span style={{color:"rgba(255,255,255,0.6)"}}>.</span></div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.8)",marginTop:3}}>{doc.title}</div>
          </div>
          <div style={{marginLeft:"auto",textAlign:"right"}}>
            <div style={{fontSize:10,color:"rgba(255,255,255,0.7)"}}>{practiceName}</div>
            <div style={{fontSize:10,color:"rgba(255,255,255,0.5)"}}>{today} · {version}</div>
          </div>
        </div>
        {/* Body */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderTop:"none",padding:"24px 28px",maxHeight:"55vh",overflowY:"auto",fontSize:12,lineHeight:1.7,color:C.text}}
          dangerouslySetInnerHTML={{__html:doc.body}}/>
        {/* Footer */}
        <div style={{background:C.teal,padding:"10px 24px",borderRadius:"0 0 12px 12px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:10,color:"rgba(255,255,255,0.6)"}}>omni-rota.com · © Prima Luce Ltd 2026</span>
          <span style={{fontSize:10,color:"rgba(255,255,255,0.6)"}}>{version} · {today}</span>
        </div>
      </div>
    </div>
  );
}

/* Privacy Notice for staff — accessible from Reports tab */
function PrivacyNoticeDoc({state}) {
  const today = new Date().toLocaleDateString("en-GB",{day:"numeric",month:"long",year:"numeric"});
  const version = "v1.0";
  const practiceName = PRACTICE_CONFIG.name||"the practice";
  // Tenant-aware principal — falls back to a neutral label if not set.
  // For DCO this resolves to "Conor O'Neill"; for Flourish, "Emily Chapman"; etc.
  const principalName = state?.practiceProfile?.principalName || "the Principal";

  function printDoc() {
    const w = window.open("","_blank","width=900,height=700");
    w.document.write(`<!DOCTYPE html><html><head><title>Staff Privacy Notice — Omni</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Instrument+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap');
  *{box-sizing:border-box;margin:0;padding:0}body{font-family:'Instrument Sans',sans-serif;background:#f7f6f3;color:#2a2420;font-size:13px;line-height:1.7}
  .page{max-width:800px;margin:0 auto;background:#fff;box-shadow:0 2px 20px rgba(0,0,0,0.1)}
  .header{background:#3aad98;padding:28px 40px 24px;color:#fff;display:flex;align-items:center;gap:20}
  .header-text h1{font-family:'Instrument Serif',serif;font-size:24px;font-weight:400;margin-bottom:4px}
  .body{padding:36px 40px}
  h3{font-family:'Instrument Serif',serif;font-size:17px;font-weight:400;color:#3aad98;border-bottom:2px solid #e8faf7;padding-bottom:6px;margin:28px 0 14px}
  h3:first-of-type{margin-top:0}
  p{margin-bottom:12px;color:#3d3830}
  ul,ol{margin:0 0 14px 20px}li{margin-bottom:5px;color:#3d3830}
  .meta-table{width:100%;border-collapse:collapse;margin-bottom:20px}.meta-table td{padding:7px 12px;border:1px solid #e8e4de;font-size:12px}.meta-table td:first-child{background:#f7f6f3;font-weight:700;color:#6b6257;width:38%;text-transform:uppercase;font-size:10px;letter-spacing:0.06em}
  .footer{background:#3aad98;padding:14px 40px;display:flex;justify-content:space-between;align-items:center;font-size:10px;color:rgba(255,255,255,0.7)}
  @media print{body{background:#fff}.page{box-shadow:none}}
</style></head><body><div class="page">
<div class="header">
  <div style="display:flex;align-items:center;gap:16px;flex:1">
    <div>
      <span style="font-family:'Instrument Serif',Georgia,serif;font-style:italic;font-weight:700;font-size:32px;color:#fff;letter-spacing:-0.04em">Omni</span><span style="font-family:'Instrument Serif',Georgia,serif;font-style:italic;font-weight:700;font-size:32px;color:#00e5a0">.</span>
    </div>
    <div style="border-left:1px solid rgba(255,255,255,0.3);padding-left:16px">
      <div style="font-family:'Instrument Serif',serif;font-size:20px;color:#fff;font-style:italic">Staff Privacy Notice</div>
      <div style="font-size:11px;opacity:0.7;margin-top:2px">${practiceName} · Omni Workforce Platform</div>
    </div>
  </div>
</div>
<div class="body">
<h3>Who We Are</h3>
<p><strong>${practiceName}</strong> is the Data Controller for the personal data described in this notice. We operate three dental practices in Manchester: Sale, Brooklands, and Timperley. Responsible person: <strong>${principalName}, Principal Dentist</strong>.</p>
<h3>What Data We Hold About You in Omni</h3>
<ul>
<li>Your name, job role, and which practice you work at</li>
<li>Your work email address (used for login)</li>
<li>Your shift and rota schedule</li>
<li>Annual leave requests, approvals, and balance</li>
<li>Sickness absence records and Bradford Factor score</li>
<li>Overtime, lateness, and attendance log entries</li>
<li>Your training level and clinical qualifications</li>
</ul>
<p>We do <strong>not</strong> store patient data, payroll, NI numbers, home addresses, or medical records in Omni.</p>
<h3>Why We Hold This Data</h3>
<ul>
<li>To manage your rota and ensure appropriate staffing levels (performance of your employment contract)</li>
<li>To process leave requests and track holiday entitlement (legal obligation under Working Time Regulations)</li>
<li>To manage sickness absence and conduct Return to Work conversations (legitimate interests as your employer)</li>
<li>To maintain a secure audit trail of system changes (legitimate interests)</li>
</ul>
<h3>Who Can See Your Data</h3>
<ul>
<li>Your <strong>site manager</strong> can see your rota, leave, and sickness records for your site</li>
<li><strong>${principalName} (Principal)</strong> can see all staff data across all sites</li>
<li><strong>You</strong> can see your own rota and leave balance via the staff portal</li>
<li>Other staff members cannot see your personal records</li>
</ul>
<h3>How Long We Keep Your Data</h3>
<ul>
<li><strong>Active staff:</strong> Data is held for the duration of your employment</li>
<li><strong>After you leave:</strong> Your records (including rota history and absence records) are kept for 6 years, in line with employment law</li>
</ul>
<h3>Where Your Data Is Stored</h3>
<p>Your data is stored securely in Supabase, hosted on EU servers in Ireland (AWS EU-West-1). All data is transmitted over HTTPS. Access is protected by secure login (Google, Apple, or email/password).</p>
<h3>Your Rights</h3>
<p>Under UK GDPR, you have the right to: access your data, correct inaccurate data, request deletion (subject to legal retention obligations), restrict or object to processing, and data portability. To exercise any of these rights, contact <strong>${practiceName}</strong> directly. We will respond within 30 days.</p>
<h3>Your Consent</h3>
<p>When you first set up your Omni account, you are asked to confirm: <em>"I agree that ${practiceName} may store and process my data for staff management purposes."</em> This consent is recorded with a timestamp in the system.</p>
<h3>Questions or Complaints</h3>
<p>If you have any questions about how your data is used, speak to ${principalName} or your site manager. If you are unhappy with how we have handled your data, you have the right to complain to the <strong>Information Commissioner's Office (ICO)</strong> at ico.org.uk or by calling 0303 123 1113.</p>
</div>
<div class="footer"><span>omni-rota.com · © Prima Luce Ltd 2026</span><span>${version} · ${today}</span></div>
</div></body></html>`);
    w.document.close();
    setTimeout(()=>w.print(),400);
  }

  return (
    <div style={{...CS,padding:"18px 20px",display:"flex",alignItems:"center",gap:14,marginBottom:14}}>
      <div style={{fontSize:24,flexShrink:0}}>📄</div>
      <div style={{flex:1}}>
        <div style={{fontWeight:700,fontSize:13,color:C.heading,marginBottom:2}}>Staff Privacy Notice</div>
        <div style={{fontSize:11,color:C.dim}}>Plain-English privacy notice for all staff — what data Omni holds and why</div>
      </div>
      <button onClick={printDoc} style={{...BTN(C.teal,true),fontSize:12,whiteSpace:"nowrap",flexShrink:0}}>View & Print</button>
    </div>
  );
}


function Agencies({state,set,user}) {
  const [editM,setEditM]   = useState(null);
  const [expanded,setExpanded] = useState(null);
  const agencies = state.agencies||[];
  const [f,setF] = useState({name:"",phone:"",email:"",nurseRate:20,recRate:18,notes:""});

  function save() {
    if (editM?.id) {
      set(s=>({...s,agencies:s.agencies.map(a=>a.id===editM.id?{...editM,...f}:a)}));
    } else {
      set(s=>({...s,agencies:[...s.agencies,{...f,id:"ag"+Date.now()}]}));
    }
    setEditM(null); setF({name:"",phone:"",email:"",nurseRate:20,recRate:18,notes:""});
  }

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <h3 style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading}}>Locum Agencies</h3>
        <button onClick={()=>{setF({name:"",phone:"",email:"",nurseRate:20,recRate:18,notes:""});setEditM({});}} style={BTN(C.teal)}>+ Add Agency</button>
      </div>
      {agencies.length===0&&<div style={{...CS,textAlign:"center",padding:32,color:C.dim}}>No agencies added yet. Add your preferred locum agencies to log costs accurately.</div>}
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {agencies.map(ag=>{
          const agLocs = (state.agencyLocums||{})[ag.id]||[];
          const dnr = agLocs.filter(l=>l.rating==="dnr").length;
          const excellent = agLocs.filter(l=>l.rating==="excellent").length;
          const isOpen = expanded===ag.id;
          return (
            <div key={ag.id} style={CS}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:8}}>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,fontSize:14,color:C.text,marginBottom:4}}>{ag.name}</div>
                  <div style={{display:"flex",gap:12,fontSize:12,color:C.mid,flexWrap:"wrap"}}>
                    {ag.phone&&<span>{ag.phone}</span>}
                    {ag.email&&<span>{ag.email}</span>}
                    <span style={{color:C.teal}}>Nurse £{ag.nurseRate}/hr · Recep £{ag.recRate}/hr</span>
                  </div>
                  {ag.notes&&<div style={{fontSize:11,color:C.dim,marginTop:4}}>{ag.notes}</div>}
                  <div style={{display:"flex",gap:6,marginTop:6,alignItems:"center"}}>
                    <span style={{fontSize:11,color:C.mid}}>{agLocs.filter(l=>!l.inactive).length} locums</span>
                    {excellent>0&&<Pill c={C.green} t={`${excellent} ⭐`} sm/>}
                    {dnr>0&&<Pill c={C.danger} t={`${dnr} DNR`} sm/>}
                  </div>
                </div>
                <div style={{display:"flex",gap:6,flexShrink:0}}>
                  <button onClick={()=>setExpanded(isOpen?null:ag.id)} style={{...BTN(C.teal,true),fontSize:11,padding:"5px 14px"}}>
                    {isOpen?"▲ Hide":"▼ Roster"}
                  </button>
                  <button onClick={()=>{setF({...ag});setEditM(ag);}} style={{...BTN(C.dim,true),fontSize:11,padding:"5px 12px"}}>Edit</button>
                </div>
              </div>
              {isOpen&&<AgencyLocumRoster ag={ag} state={state} set={set} user={user}/>}
            </div>
          );
        })}
      </div>
      {editM!==null&&(
        <Overlay onClose={()=>setEditM(null)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:20}}>{editM.id?"Edit Agency":"Add Agency"}</div>
          {[{l:"Agency name",k:"name",t:"text"},{l:"Phone",k:"phone",t:"tel"},{l:"Email",k:"email",t:"email"},{l:"Nurse rate £/hr",k:"nurseRate",t:"number"},{l:"Receptionist rate £/hr",k:"recRate",t:"number"},{l:"Notes",k:"notes",t:"text"}].map(({l,k,t})=>(
            <div key={k} style={{marginBottom:12}}>
              <label style={LB}>{l}</label>
              <input type={t} value={f[k]} onChange={e=>setF(p=>({...p,[k]:t==="number"?+e.target.value:e.target.value}))} style={IS}/>
            </div>
          ))}
          <div style={{display:"flex",gap:8}}>
            <button onClick={save} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Save</button>
            <button onClick={()=>setEditM(null)} style={BTN(C.dim,true)}>Cancel</button>
          </div>
        </Overlay>
      )}
    </div>
  );
}

function ExtraShiftForm({person, state, onSave, onClose}) {
  // Mode: "general" = set stance + pick dates for a month
  //       "specific" = flag a specific day or "short notice any day this week"
  const [mode, setMode] = useState(null);
  const [stance, setStance] = useState("sometimes"); // happy / sometimes / prefer_not
  const [selDates, setSelDates] = useState([]);
  const [shortNotice, setShortNotice] = useState(false);
  const [prefDays, setPrefDays] = useState([]); // Mon/Tue/etc
  const [note, setNote] = useState("");
  const tog = d => setSelDates(s=>s.includes(d)?s.filter(x=>x!==d):[...s,d]);
  const togDay = d => setPrefDays(s=>s.includes(d)?s.filter(x=>x!==d):[...s,d]);
  const DAYS = ["Mon","Tue","Wed","Thu","Fri"];
  const STANCES = [
    {id:"happy",    icon:"happy",  label:"Happy to do extras",     sub:"You'll be first asked",        c:C.green},
    {id:"sometimes",icon:"meh",  label:"Occasionally available", sub:"Depends on the day/week",       c:C.warn},
    {id:"prefer_not",icon:"frown", label:"Prefer not",             sub:"Only in emergencies please",    c:C.muted},
  ];

  function submit() {
    const req = {
      id:"ex"+Date.now(), staffId:person.id, name:person.name, site:person.site,
      status:"pending", stance, prefDays, shortNotice, note,
      dates: selDates, createdAt: new Date().toISOString().split("T")[0],
    };
    onSave(req);
  }

  if (!mode) return (
    <div>
      <div style={{fontSize:13,color:C.mid,marginBottom:20}}>
        How do you want to offer your availability?
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:20}}>
        <button onClick={()=>setMode("general")} style={{padding:"16px",borderRadius:14,border:`1.5px solid ${C.teal}44`,background:C.tealPale,color:C.teal,fontFamily:"inherit",cursor:"pointer",textAlign:"left"}}>
          <div style={{fontSize:14,fontWeight:700,marginBottom:3}}>Set my availability</div>
          <div style={{fontSize:12,color:C.dim}}>Pick days for next month, set your general stance</div>
        </button>
        <button onClick={()=>setMode("specific")} style={{padding:"16px",borderRadius:14,border:`1.5px solid ${C.brook}44`,background:C.brook+"10",color:C.brook,fontFamily:"inherit",cursor:"pointer",textAlign:"left"}}>
          <div style={{fontSize:14,fontWeight:700,marginBottom:3}}>⚡ I'm available right now</div>
          <div style={{fontSize:12,color:C.dim}}>Short notice — name a specific day or say available this week</div>
        </button>
      </div>
      <button onClick={onClose} style={{...BTN(C.dim,true),width:"100%",padding:"10px 0"}}>Cancel</button>
    </div>
  );

  if (mode==="specific") return (
    <div>
      <div style={{marginBottom:14}}>
        <label style={{...LB,marginBottom:8}}>Availability type</label>
        <div style={{display:"flex",gap:8,marginBottom:14}}>
          <button onClick={()=>setShortNotice(false)} style={{flex:1,padding:"10px",borderRadius:10,border:`1.5px solid ${!shortNotice?C.teal:C.border}`,background:!shortNotice?C.tealPale:"transparent",color:!shortNotice?C.teal:C.mid,fontFamily:"inherit",fontWeight:600,fontSize:12,cursor:"pointer"}}>
            Specific date
          </button>
          <button onClick={()=>setShortNotice(true)} style={{flex:1,padding:"10px",borderRadius:10,border:`1.5px solid ${shortNotice?C.danger:C.border}`,background:shortNotice?"#fef2f2":"transparent",color:shortNotice?C.danger:C.mid,fontFamily:"inherit",fontWeight:600,fontSize:12,cursor:"pointer"}}>
            Short notice — this week
          </button>
        </div>
        {!shortNotice&&(
          <div>
            <label style={LB}>Which day?</label>
            <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
              {CAL.days.filter(d=>{const t=new Date().toISOString().split("T")[0];return d.date>=t;}).slice(0,14).map(d=>{
                const on=selDates.includes(d.date);
                return <button key={d.date} onClick={()=>tog(d.date)} style={{padding:"5px 10px",borderRadius:8,border:`1px solid ${on?C.teal:C.border}`,background:on?C.tealPale:"transparent",color:on?C.teal:C.soft,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer"}}>{d.label.split(" ").slice(0,2).join(" ")}{on?" ✓":""}</button>;
              })}
            </div>
          </div>
        )}
        {shortNotice&&(
          <div style={{background:"rgba(255,70,70,0.08)",border:`1px solid ${C.danger}33`,borderRadius:10,padding:12,fontSize:13,color:C.mid}}>
            You'll be flagged as available for any shift this week. Your manager will see this immediately.
          </div>
        )}
      </div>
      <div style={{marginBottom:16}}><label style={LB}>Any notes?</label><input value={note} onChange={e=>setNote(e.target.value)} placeholder="e.g. available after 10am, happy with OT rate" style={IS}/></div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={submit} style={{...BTN(C.brook),flex:1,padding:"11px 0"}}>Submit</button>
        <button onClick={()=>setMode(null)} style={BTN(C.dim,true)}>Back</button>
      </div>
    </div>
  );

  // General mode
  return (
    <div>
      <div style={{marginBottom:14}}>
        <label style={{...LB,marginBottom:8}}>My general stance on extra shifts</label>
        <div style={{display:"flex",flexDirection:"column",gap:6}}>
          {STANCES.map(({id,icon,label,sub,c})=>(
            <button key={id} onClick={()=>setStance(id)} style={{padding:"12px 14px",borderRadius:10,border:`1.5px solid ${stance===id?c:C.border}`,background:stance===id?c+"15":"transparent",color:stance===id?c:C.mid,fontFamily:"inherit",cursor:"pointer",textAlign:"left",display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:20,flexShrink:0}}>{icon}</span>
              <div>
                <div style={{fontSize:13,fontWeight:700}}>{label}</div>
                <div style={{fontSize:11,color:C.dim}}>{sub}</div>
              </div>
              {stance===id&&<span style={{marginLeft:"auto",fontSize:14}}>✓</span>}
            </button>
          ))}
        </div>
      </div>
      <div style={{marginBottom:14}}>
        <label style={{...LB,marginBottom:8}}>Preferred days for extras</label>
        <div style={{display:"flex",gap:6}}>
          {DAYS.map(d=>(
            <button key={d} onClick={()=>togDay(d)} style={{flex:1,padding:"8px 4px",borderRadius:8,border:`1px solid ${prefDays.includes(d)?C.teal:C.border}`,background:prefDays.includes(d)?C.tealPale:"transparent",color:prefDays.includes(d)?C.teal:C.mid,fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer"}}>{d}</button>
          ))}
        </div>
      </div>
      <div style={{marginBottom:14}}>
        <label style={{...LB,marginBottom:6}}>Pick specific dates you're available next month</label>
        <div style={{maxHeight:160,overflowY:"auto"}}>
          {CAL.weeks.slice(0,8).map(wk=>(
            <div key={wk.start} style={{marginBottom:6}}>
              <div style={{fontSize:9,fontWeight:700,color:C.dim,marginBottom:3}}>{wk.days[0]?.label.split(" ").slice(1).join(" ")}</div>
              <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                {wk.days.map(d=>{
                  const on=selDates.includes(d.date);
                  return <button key={d.date} onClick={()=>tog(d.date)} style={{padding:"3px 8px",borderRadius:6,fontFamily:"inherit",fontSize:10,fontWeight:600,border:`1px solid ${on?C.teal:C.border}`,background:on?C.tealPale:"transparent",color:on?C.teal:C.soft,cursor:"pointer"}}>{d.label.split(" ").slice(0,2).join(" ")}{on?" ✓":""}</button>;
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{marginBottom:16}}><label style={LB}>Notes (optional)</label><input value={note} onChange={e=>setNote(e.target.value)} placeholder="e.g. mornings only, or any sessions fine" style={IS}/></div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={submit} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Save availability</button>
        <button onClick={()=>setMode(null)} style={BTN(C.dim,true)}>Back</button>
      </div>
    </div>
  );
}

function UnpaidLeaveForm({person,state,onSave,onClose}) {
  const [sel,setSel] = useState([]);
  const [reason,setReason] = useState("");
  const tog = date => setSel(s=>s.includes(date)?s.filter(d=>d!==date):[...s,date]);
  // Count existing unpaid requests — welfare flag
  const existing = (state.unpaidLeaveRequests||[]).filter(r=>r.staffId===person.id&&r.status!=="declined");
  const welfareFlag = existing.length>=3;
  return (
    <div>
      {welfareFlag&&<div style={{background:"rgba(245,197,24,0.08)",border:`1px solid ${C.warn}44`,borderRadius:8,padding:10,marginBottom:12,fontSize:12,color:C.warn}}>You've made {existing.length} unpaid leave requests. Your manager may schedule a welfare chat.</div>}
      <div style={{maxHeight:200,overflowY:"auto",marginBottom:10}}>
        {CAL.weeks.slice(0,8).map(wk=>(
          <div key={wk.start} style={{marginBottom:6}}>
            <div style={{fontSize:9,fontWeight:700,color:C.dim,marginBottom:3}}>{wk.days[0]?.label.split(" ").slice(1).join(" ")}</div>
            <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
              {wk.days.map(d=>{
                const on=sel.includes(d.date);
                return <button key={d.date} onClick={()=>tog(d.date)} style={{padding:"3px 7px",borderRadius:6,fontFamily:"inherit",fontSize:10,fontWeight:600,border:`1px solid ${on?C.mid:C.border}`,background:on?"#f1f0ef":"transparent",color:on?C.soft:C.mid,cursor:"pointer"}}>{d.label.split(" ").slice(0,2).join(" ")}{on?" ✓":""}</button>;
              })}
            </div>
          </div>
        ))}
      </div>
      <div style={{marginBottom:12}}><label style={LB}>Reason</label><input value={reason} onChange={e=>setReason(e.target.value)} placeholder="e.g. personal appointment" style={IS}/></div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>sel.length&&onSave({id:"ul"+Date.now(),staffId:person.id,name:person.name,site:person.site,dates:sel,reason,status:"pending"})} style={{...BTN(sel.length?C.teal:C.muted),flex:1,padding:"11px 0"}}>Request {sel.length} day{sel.length!==1?"s":""}</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </div>
  );
}

/* ══════════════════════════════════
   STAFF PORTAL
══════════════════════════════════ */
/* ── ISSUE REPORTING ─────────────────────────────────────────────────── */
const ISSUE_TIERS = {
  1:{label:"Operational",color:"#60a5fa",bg:"#f0f9ff",border:"#bae6fd",desc:"Equipment, stock, decontamination — goes to Head Nurse",cats:[
    {name:"Equipment fault",        scope:"operational"},
    {name:"Missing stock",          scope:"operational"},
    {name:"Decontamination query",  scope:"operational"},
    {name:"Supply issue",           scope:"operational"},
    {name:"Other operational",      scope:"operational"},
  ]},
  // T2 is split by scope. Operational T2s are visible to managers, principal,
  // AND trusted viewers (head nurses helping with oversight). Interpersonal
  // T2s (staff conduct, sensitive "other") are restricted to managers and
  // principal only — even trusted viewers don't see them, because they could
  // be working alongside the named individual.
  // Patient complaints are deliberately NOT a category here — they belong in
  // a dedicated complaints module (light on patient data, GDPR-aware) which
  // doesn't exist yet. If an operational failure resulted in a complaint, log
  // it under "Process not followed" and reference lightly in the notes.
  2:{label:"Concern",color:"#d97706",bg:"rgba(245,197,24,0.06)",border:"rgba(245,197,24,0.2)",desc:"Staff conduct, process failure, infection control — goes to Manager + Principal",cats:[
    {name:"Infection control concern",  scope:"operational"},
    {name:"Staff conduct",              scope:"interpersonal"},
    {name:"Process not followed",       scope:"operational"},
    {name:"Repeated equipment failure", scope:"operational"},
    {name:"Other concern",              scope:"interpersonal"},
  ]},
  3:{label:"Serious",color:"#ff4646",bg:"#fef2f2",border:"#fecaca",desc:"Patient safety, safeguarding, whistleblowing, CQC notifiable — goes directly to Principal. Permanent record. Whistleblowing reporters have full legal protection.",cats:[
    {name:"Patient safety",                            scope:"operational"},
    {name:"Near miss",                                 scope:"operational"},
    {name:"Bullying / harassment",                     scope:"interpersonal"},
    {name:"Upward or reverse bullying",                scope:"interpersonal"},
    {name:"Staff-on-staff conduct",                    scope:"interpersonal"},
    {name:"Safeguarding",                              scope:"interpersonal"},
    {name:"Whistleblowing — sexual harassment",        scope:"interpersonal"},
    {name:"Whistleblowing — unsafe practice",          scope:"operational"},
    {name:"Whistleblowing — financial irregularity",   scope:"operational"},
    {name:"CQC notifiable",                            scope:"operational"},
    {name:"Serious incident",                          scope:"operational"},
  ]},
};

// Helper — given a category name, find its scope. Defaults to interpersonal
// (the safer side) if not found, so unknown categories don't leak.
function getCategoryScope(catName) {
  for (const tier of Object.values(ISSUE_TIERS)) {
    const found = tier.cats.find(c => (typeof c==="string"?c:c.name)===catName);
    if (found) return typeof found==="string" ? "operational" : (found.scope||"interpersonal");
  }
  return "interpersonal";
}

function IssueReportingModal({person, set, onClose}) {
  const [tier,setTier]   = useState(null);
  const [cat,setCat]     = useState("");
  const [desc,setDesc]   = useState("");
  const [anon,setAnon]   = useState(false);
  const [done,setDone]   = useState(false);
  const canSubmit = tier && cat && desc.trim().length > 5;

  function submit() {
    const newIssue = {
      id:"iss_"+Date.now(),
      tier, category:cat, description:desc.trim(),
      site:person.site,
      staffId: (tier===3&&anon)?null:person.id,
      staffName:(tier===3&&anon)?null:person.name,
      anonymous:tier===3&&anon,
      date:new Date().toISOString().split("T")[0],
      status:"open",
      acknowledgedAt:null, acknowledgedBy:null,
      updates:[], resolvedAt:null, resolvedNote:null,
      escalatedFrom:null, immutable:tier===3,
    };
    set(s=>{
      // Send email to recipients (manager + principal for T2; principal only for T3)
      const recipientEmails = getIssueRecipientEmails(tier, person.site, s);
      sendIssueEmail({tier, category:cat, site:person.site, reporterName:person.name, anonymous:tier===3&&anon, recipientEmails});
      return {...s, issues:[...(s.issues||[]), newIssue]};
    });
    setDone(true);
  }

  if (done) return (
    <Overlay onClose={onClose}>
      <div style={{textAlign:"center",padding:"20px 0"}}>
        <div style={{fontSize:40,marginBottom:12}}>✓</div>
        <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:18,fontWeight:700,color:C.heading,marginBottom:8}}>Issue Reported</div>
        <div style={{fontSize:13,color:C.dim,marginBottom:4,lineHeight:1.5}}>
          {tier===3
            ? "The Principal has been notified by email. This is a permanent, immutable record. Whistleblowing reporters have full legal protection under the Employment Rights Act 1996."
            : tier===2
              ? "The Site Manager and Principal have been notified by email and the issue is now visible in the Issues tab."
              : "Your Head Nurse and Site Manager have been notified by email and the issue is now visible in the Issues tab."}
        </div>
        {tier===3&&anon&&<div style={{fontSize:11,color:C.mid,marginTop:8,background:"#181626",borderRadius:8,padding:"8px 12px"}}>This report is anonymous. Your identity has not been recorded.</div>}
        <button onClick={onClose} style={{...BTN(C.teal),marginTop:20,width:"100%"}}>Done</button>
      </div>
    </Overlay>
  );

  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,fontWeight:700,color:C.heading,marginBottom:4}}>Report an Issue</div>
      <div style={{fontSize:12,color:C.dim,marginBottom:16}}>{person.name} · {SITES[person.site]?.name}</div>

      <div style={{marginBottom:14}}>
        <div style={{fontSize:11,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>How serious is this?</div>
        {[1,2,3].map(t=>{
          const info=ISSUE_TIERS[t]; const sel=tier===t;
          return (
            <button key={t} onClick={()=>{setTier(t);setCat("");setAnon(false);}}
              style={{display:"block",width:"100%",textAlign:"left",padding:"10px 12px",marginBottom:6,borderRadius:10,border:`1.5px solid ${sel?info.color:C.border}`,background:sel?info.bg:"transparent",fontFamily:"inherit",cursor:"pointer"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                <span style={{fontSize:10,fontWeight:800,color:info.color,background:info.color+"18",border:`1px solid ${info.border}`,borderRadius:4,padding:"1px 7px"}}>T{t}</span>
                <span style={{fontSize:13,fontWeight:700,color:sel?info.color:C.text}}>{info.label}</span>
              </div>
              <div style={{fontSize:11,color:C.dim,paddingLeft:2}}>{info.desc}</div>
            </button>
          );
        })}
      </div>

      {tier&&(
        <div style={{marginBottom:14}}>
          <label style={LB}>Category</label>
          <select value={cat} onChange={e=>setCat(e.target.value)} style={IS}>
            <option value="">— select —</option>
            {ISSUE_TIERS[tier].cats.map(c=>{
              const name = typeof c==="string" ? c : c.name;
              return <option key={name} value={name}>{name}</option>;
            })}
          </select>
        </div>
      )}

      {cat&&(
        <div style={{marginBottom:14}}>
          <label style={LB}>Describe the issue</label>
          <textarea value={desc} onChange={e=>setDesc(e.target.value)} rows={4}
            placeholder="Be factual and specific — what happened, when, where"
            style={{...IS,resize:"none",lineHeight:1.5}}/>
        </div>
      )}

      {tier===3&&cat&&(
        <div style={{...CS,marginBottom:14,background:"rgba(255,70,70,0.08)",border:"1px solid rgba(255,70,70,0.2)",padding:"10px 14px"}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <button onClick={()=>setAnon(v=>!v)} style={{width:20,height:20,borderRadius:4,border:`2px solid ${anon?"#dc2626":C.border}`,background:anon?"#dc2626":"transparent",cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
              {anon&&<span style={{color:"#fff",fontSize:11,fontWeight:800,lineHeight:1}}>✓</span>}
            </button>
            <div>
              <div style={{fontSize:12,fontWeight:700,color:"#ff4646"}}>Submit anonymously</div>
              <div style={{fontSize:10,color:C.dim,marginTop:1}}>Your name will not be recorded. Required for some safeguarding situations.</div>
            </div>
          </div>
        </div>
      )}

      <div style={{display:"flex",gap:8}}>
        <button onClick={submit} disabled={!canSubmit} style={{...BTN(tier===3?"#dc2626":tier===2?C.warn:C.teal),flex:1,opacity:!canSubmit?0.4:1}}>Submit Issue</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}

function IssueReporting({state, set, user, isPrincipal}) {
  const issues = state.issues||[];
  const today  = new Date().toISOString().split("T")[0];
  const [filterSite,  setFilterSite]   = useState("all");
  const [filterTier,  setFilterTier]   = useState("all");
  const [filterStatus,setFilterStatus] = useState("open");
  const [updateM, setUpdateM]         = useState(null);
  const [updateNote,setUpdateNote]    = useState("");
  const [delegateM,setDelegateM]      = useState(null);
  const [delegateName,setDelegateName] = useState("");

  // Auto-escalation on mount — unacknowledged 3+ days bumps tier.
  // Baseline is the most recent of: acknowledgedAt, lastEscalatedAt, or the
  // original creation date. This prevents T1→T3 in a single load: after a
  // T1→T2 bump, the clock resets from the escalation moment so another 3 days
  // must pass before T2→T3 can fire.
  useEffect(()=>{
    const now = new Date();
    let changed = false;
    const updated = (state.issues||[]).map(issue=>{
      if (issue.status==="resolved"||issue.status==="managed"||issue.tier>=3) return issue;
      if (issue.acknowledgedAt) return issue; // acknowledged stops the clock
      const baseStr = issue.lastEscalatedAt || issue.date;
      const base = new Date(baseStr);
      const days = (now-base)/(1000*60*60*24);
      if (days>=3) {
        changed=true;
        const oldTier=issue.tier;
        const nt=Math.min(3,issue.tier+1);
        return {...issue, tier:nt, escalatedFrom:issue.escalatedFrom||oldTier,
          lastEscalatedAt: now.toISOString(),
          immutable:nt===3?true:issue.immutable,
          updates:[...(issue.updates||[]),{id:"u"+Date.now()+Math.random(),date:today,note:`Auto-escalated T${oldTier}→T${nt} — unacknowledged 3+ days`,by:"System"}]};
      }
      return issue;
    });
    if (changed) set(s=>({...s,issues:updated}));
  },[]);

  // Pattern detection
  const ago30 = new Date(Date.now()-30*24*60*60*1000).toISOString().split("T")[0];
  const recent = issues.filter(i=>i.date>=ago30);
  const catSite = {};
  recent.forEach(i=>{const k=`${i.site}::${i.category}`;catSite[k]=(catSite[k]||0)+1;});
  const patterns = Object.entries(catSite).filter(([,n])=>n>=3).map(([k,n])=>{
    const [site,cat]=k.split("::");
    return {msg:`${SITES[site]?.name} — "${cat}" reported ${n} times this month`,site};
  });

  const isHeadNurseRole = user?.role==="headNurse" || (state.staffRoles?.[user?.staffId]==="headNurse");
  const isManagerRole   = user?.role==="manager" || user?.role==="managerLite";
  // Trusted issue viewers — principal has explicitly granted Issues access.
  // Treated as manager-level visibility for T1+T2 + patterns — EXCEPT
  // interpersonal T2 categories (staff conduct, sensitive "other"), which
  // remain manager+principal only because trusted viewers may work alongside
  // the named individual.
  const isIssueViewer = (state.issueViewers||[]).includes(user?.staffId);
  const canSee = i => {
    if (i.site !== user.site && !isPrincipal) return false;
    if (isPrincipal) return true;
    if (i.tier === 1) return true;           // T1 visible to all site staff
    if (isManagerRole) return i.tier <= 2;   // managers see T1 + T2 (all scopes)
    if (isIssueViewer && i.tier <= 2) {
      // Trusted viewers see operational T2s but not interpersonal ones
      const scope = getCategoryScope(i.category);
      return scope === "operational";
    }
    return false;                            // others see T1 only
  };
  const visible = issues.filter(canSee);
  const filtered = visible.filter(i=>{
    if (filterSite!=="all"&&i.site!==filterSite) return false;
    if (filterTier!=="all"&&i.tier!==Number(filterTier)) return false;
    if (filterStatus==="open"&&i.status==="resolved") return false;
    if (filterStatus==="resolved"&&i.status!=="resolved") return false;
    return true;
  }).sort((a,b)=>b.tier!==a.tier?b.tier-a.tier:b.date.localeCompare(a.date));

  const SC = {open:{label:"Open",color:"#ff4646"},acknowledged:{label:"Acknowledged",color:C.warn},managed:{label:"In Hand",color:"#60a5fa"},resolved:{label:"Resolved",color:C.green}};

  function doAction(issue, type, note) {
    // T3 ENFORCEMENT — state-changing actions on T3 records are principal-only.
    // "update" (notes) stays open to all canAct users. Silently no-ops if a
    // non-principal somehow triggers a blocked action.
    if (issue.tier===3 && !isPrincipal && type!=="update") {
      console.warn("[Omni] T3 action blocked — principal only", type);
      return;
    }
    const by=user.name; const now2=new Date().toISOString();
    set(s=>({...s,issues:(s.issues||[]).map(i=>{
      if (i.id!==issue.id) return i;
      if (type==="acknowledge") return {...i,status:"acknowledged",acknowledgedAt:now2,acknowledgedBy:by,updates:[...i.updates,{id:"u"+Date.now(),date:today,note:"Acknowledged",by}]};
      if (type==="update")     return {...i,updates:[...i.updates,{id:"u"+Date.now(),date:today,note,by}]};
      if (type==="manage")     return {...i,status:"managed",updates:[...i.updates,{id:"u"+Date.now(),date:today,note:note||"Marked as in hand",by}]};
      if (type==="resolve")    return {...i,status:"resolved",resolvedAt:now2,resolvedNote:note,updates:[...i.updates,{id:"u"+Date.now(),date:today,note:note||"Resolved",by}]};
      if (type==="escalate"){const nt=Math.min(3,i.tier+1);return {...i,tier:nt,escalatedFrom:i.escalatedFrom||i.tier,lastEscalatedAt:now2,immutable:nt===3?true:i.immutable,updates:[...i.updates,{id:"u"+Date.now(),date:today,note:`Escalated to T${nt}`,by}]};}
      if (type==="delegate")   return {...i,delegatedTo:note,updates:[...i.updates,{id:"u"+Date.now(),date:today,note:`Delegated to ${note}`,by}]};
      return i;
    })}));
    setUpdateM(null); setUpdateNote(""); setDelegateM(null);
  }

  const openCount = visible.filter(i=>i.status!=="resolved").length;
  const t3Open    = issues.filter(i=>i.tier===3&&i.status!=="resolved").length;

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,flexWrap:"wrap",gap:12}}>
        <h3 style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading}}>Issue Reporting</h3>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {t3Open>0&&<div style={{background:"rgba(255,70,70,0.08)",border:"1px solid rgba(255,70,70,0.2)",borderRadius:8,padding:"5px 12px",fontSize:12,fontWeight:700,color:"#ff4646",display:"flex",alignItems:"center",gap:5}}><Icon name="warning" size={12} color="#dc2626"/> {t3Open} Serious</div>}
          <div style={{background:openCount>0?"rgba(245,197,24,0.06)":"#f0fdf4",border:`1px solid ${openCount>0?"rgba(245,197,24,0.2)":"#bbf7d0"}`,borderRadius:8,padding:"5px 12px",fontSize:12,fontWeight:700,color:openCount>0?C.warn:C.green}}>{openCount} open</div>
        </div>
      </div>

      {/* Tier explainer — visible to all managers, encourages openness */}
      <div style={{...CS,marginBottom:16,padding:"12px 16px",background:"#181626",border:`1px solid ${C.border}`}}>
        <div style={{fontSize:11,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:10}}>How Issue Reporting Works</div>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {[
            {t:1,color:"#60a5fa",bg:"#f0f9ff",border:"#bae6fd",title:"Tier 1 — Operational",desc:"Equipment, stock, decontamination, supplies. Sent to your Head Nurse to action. Resolve within 3 days or it escalates automatically."},
            {t:2,color:"#d97706",bg:"rgba(245,197,24,0.06)",border:"rgba(245,197,24,0.2)",title:"Tier 2 — Concern",desc:"Staff conduct, process failures, infection control. Sent to Site Manager and Principal. Trusted viewers see operational T2s but not interpersonal ones (staff conduct stays manager+principal only)."},
            {t:3,color:"#ff4646",bg:"#fef2f2",border:"#fecaca",title:"Tier 3 — Serious",desc:"Patient safety, safeguarding, near misses, CQC notifiable, bullying in any direction — including upward or reverse bullying. Goes to the Principal only. Anonymous option available. Only the Principal can acknowledge, mark in hand, or resolve a T3 record — managers and head nurses can add notes but cannot change its state. The record is permanent and protects everyone — the person who raised it, the person named, and the practice."},
          ].map(({t,color,bg,border,title,desc})=>(
            <div key={t} style={{display:"flex",gap:10,alignItems:"flex-start"}}>
              <span style={{fontSize:9,fontWeight:800,color,background:bg,border:`1px solid ${border}`,borderRadius:4,padding:"2px 7px",flexShrink:0,marginTop:1}}>T{t}</span>
              <div>
                <div style={{fontSize:12,fontWeight:700,color,marginBottom:2}}>{title}</div>
                <div style={{fontSize:11,color:C.dim,lineHeight:1.5}}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{fontSize:10,color:C.mid,marginTop:10,paddingTop:8,borderTop:`1px solid ${C.border2}`}}>
          <strong>Who sees what:</strong> All site staff see T1 · Managers and trusted viewers see T1 and T2 · Principal sees all tiers. Trusted viewers are named by the principal in Admin → Settings → Issues. T3 records can only be state-changed (acknowledged, marked in hand, resolved) by the Principal — anyone with access can add notes. This system exists to protect staff — all staff, in every direction. Issues raised honestly and in good faith will always be handled with care and fairness.
        </div>
      </div>

      {patterns.map((p,i)=>(
        <div key={i} style={{...CS,marginBottom:10,borderLeft:"3px solid #dc2626",background:"rgba(255,70,70,0.08)",padding:"10px 14px"}}>
          <div style={{fontSize:12,fontWeight:700,color:"#ff4646"}}>Pattern — {p.msg}</div>
          <div style={{fontSize:10,color:C.dim,marginTop:2}}>Consider a team conversation or process review.</div>
        </div>
      ))}

      {!isPrincipal&&(isManagerRole||isIssueViewer)&&<div style={{fontSize:11,color:C.dim,background:"rgba(139,92,246,0.08)",border:"1px solid #ddd6fe",borderRadius:8,padding:"8px 12px",marginBottom:14}}>You can see T1 (Operational) and T2 (Concern) issues for your site. Tier 3 (Serious) issues go directly to the Principal.{isIssueViewer&&!isManagerRole?" You have this access because the principal has named you as a trusted viewer.":""}</div>}
      {!isPrincipal&&!isManagerRole&&!isIssueViewer&&<div style={{fontSize:11,color:C.dim,background:"rgba(58,173,152,0.06)",border:"1px solid #bae6fd",borderRadius:8,padding:"8px 12px",marginBottom:14}}>Tier 1 (Operational) issues are visible to everyone at your site — so you can see what's been flagged and how it's being resolved. T2 and T3 go to managers and principal only.</div>}

      <div style={{display:"flex",gap:6,marginBottom:14,flexWrap:"wrap"}}>
        {isPrincipal&&["all",...Object.keys(SITES)].map(s=>(
          <button key={s} onClick={()=>setFilterSite(s)} style={{padding:"4px 12px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${filterSite===s?C.teal:C.border}`,background:filterSite===s?C.teal+"18":"transparent",color:filterSite===s?C.teal:C.mid}}>
            {s==="all"?"All Sites":SITES[s]?.name}
          </button>
        ))}
        {["all","1","2","3"].map(t=>{
          const tc=ISSUE_TIERS[Number(t)];
          return (
            <button key={t} onClick={()=>setFilterTier(t)} style={{padding:"4px 12px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${filterTier===t?(t==="all"?C.teal:tc?.color):C.border}`,background:filterTier===t?(t==="all"?C.teal+"18":tc?.bg):"transparent",color:filterTier===t?(t==="all"?C.teal:tc?.color):C.mid}}>
              {t==="all"?"All":t==="1"?"T1 Operational":t==="2"?"T2 Concern":"T3 Serious"}
            </button>
          );
        })}
        {["open","resolved"].map(st=>(
          <button key={st} onClick={()=>setFilterStatus(st)} style={{padding:"4px 12px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${filterStatus===st?C.teal:C.border}`,background:filterStatus===st?C.teal+"18":"transparent",color:filterStatus===st?C.teal:C.mid}}>
            {st==="open"?"Active":"Resolved"}
          </button>
        ))}
      </div>

      {filtered.length===0&&<div style={{...CS,textAlign:"center",padding:32,color:C.dim}}>No issues found</div>}

      {/* Head nurse "since I was away" — shows recent T1 activity */}
      {isHeadNurseRole&&filterStatus==="open"&&(()=>{
        const ago7 = new Date(Date.now()-7*24*60*60*1000).toISOString().split("T")[0];
        const recentActivity = visible.filter(i=>i.tier===1&&i.updates.some(u=>u.date>=ago7));
        const recentlyResolved = issues.filter(i=>i.tier===1&&i.site===user.site&&i.status==="resolved"&&i.resolvedAt&&i.resolvedAt.slice(0,10)>=ago7);
        if (recentActivity.length===0&&recentlyResolved.length===0) return null;
        return (
          <div style={{...CS,marginBottom:14,padding:"10px 14px",background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)"}}>
            <div style={{fontSize:10,fontWeight:700,color:C.green,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>Activity in the last 7 days</div>
            {recentlyResolved.map(i=>(
              <div key={i.id} style={{fontSize:11,color:C.green,marginBottom:3}}><strong>{i.category}</strong> — resolved{i.resolvedNote?`: ${i.resolvedNote}`:""}</div>
            ))}
            {recentActivity.filter(i=>i.status!=="resolved").map(i=>(
              <div key={i.id} style={{fontSize:11,color:C.dim,marginBottom:3}}>
                <strong>{i.category}</strong> — {i.updates.filter(u=>u.date>=ago7).map(u=>u.note).join(" · ")}
              </div>
            ))}
          </div>
        );
      })()}

      {filtered.map(issue=>{
        const tc=ISSUE_TIERS[issue.tier];
        const sc=SC[issue.status]||SC.open;
        const daysSince=Math.floor((new Date()-new Date(issue.date))/(1000*60*60*24));
        const overdue=!issue.acknowledgedAt&&daysSince>=2&&issue.status!=="resolved";
        const stale=issue.status==="managed"&&Math.floor((new Date()-new Date(issue.updates[issue.updates.length-1]?.date||issue.date))/(1000*60*60*24))>=5;
        const canAct = isPrincipal||isManagerRole||isHeadNurseRole;
        return (
          <div key={issue.id} style={{...CS,marginBottom:10,borderLeft:`3px solid ${tc.color}`,padding:"12px 16px"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,flexWrap:"wrap"}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:5,flexWrap:"wrap"}}>
                  <span style={{fontSize:9,fontWeight:800,color:tc.color,background:tc.bg,border:`1px solid ${tc.border}`,borderRadius:4,padding:"1px 6px",flexShrink:0}}>T{issue.tier} {tc.label}</span>
                  <span style={{fontSize:12,fontWeight:700,color:C.text}}>{issue.category}</span>
                  {issue.escalatedFrom&&<span style={{fontSize:9,color:C.dim,background:C.bg,border:`1px solid ${C.border}`,borderRadius:3,padding:"1px 5px"}}>↑ from T{issue.escalatedFrom}</span>}
                  {issue.anonymous&&<span style={{fontSize:9,color:C.dim,background:"#181626",border:"1px solid #e7e5e4",borderRadius:3,padding:"1px 5px"}}>Anon</span>}
                  {overdue&&<span style={{fontSize:9,fontWeight:700,color:"#ff4646",background:"rgba(255,70,70,0.08)",border:"1px solid rgba(255,70,70,0.2)",borderRadius:3,padding:"1px 5px"}}>Needs ack</span>}
                  {stale&&<span style={{fontSize:9,fontWeight:700,color:C.warn,background:"rgba(245,197,24,0.05)",border:"1px solid #fde68a",borderRadius:3,padding:"1px 5px"}}>⏳ No update in 5+ days</span>}
                  {issue.delegatedTo&&<span style={{fontSize:9,fontWeight:700,color:"#a78bfa",background:"rgba(139,92,246,0.08)",border:"1px solid #ddd6fe",borderRadius:3,padding:"1px 5px"}}>→ {issue.delegatedTo}</span>}
                </div>
                <div style={{fontSize:12,color:C.text,marginBottom:4,lineHeight:1.5}}>{issue.description}</div>
                <div style={{fontSize:10,color:C.dim}}>{issue.date} · {SITES[issue.site]?.name}{issue.staffName?` · ${issue.staffName}`:""}</div>
                {issue.updates.length>0&&(
                  <div style={{marginTop:8,paddingLeft:8,borderLeft:`2px solid ${C.border}`}}>
                    {issue.updates.slice(-2).map(u=>(
                      <div key={u.id} style={{fontSize:10,color:C.dim,marginBottom:2}}><span style={{fontWeight:600,color:C.mid}}>{u.date}</span> — {u.note} <span style={{color:C.muted}}>({u.by})</span></div>
                    ))}
                    {issue.updates.length>2&&<div style={{fontSize:9,color:C.muted}}>+{issue.updates.length-2} earlier</div>}
                  </div>
                )}
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:4,alignItems:"flex-end",flexShrink:0}}>
                <Pill c={sc.color} t={sc.label} sm/>
                {/* T3 ENFORCEMENT — state-changing actions (acknowledge, mark in hand,
                    resolve, escalate, delegate) are principal-only for Tier 3 records.
                    Everyone with canAct can still add notes via "+ Update". */}
                {(()=>{
                  const t3Locked = issue.tier===3 && !isPrincipal;
                  return (
                    <>
                      {canAct&&!t3Locked&&issue.status==="open"&&<button onClick={()=>doAction(issue,"acknowledge")} style={{...BTN(C.teal,true),fontSize:10,padding:"4px 10px"}}>Acknowledge</button>}
                      {canAct&&(issue.status==="acknowledged"||issue.status==="managed")&&<button onClick={()=>setUpdateM({issue,type:"update"})} style={{...BTN(C.dim,true),fontSize:10,padding:"4px 10px"}}>+ Update</button>}
                      {canAct&&!t3Locked&&issue.status==="acknowledged"&&issue.tier<3&&<button onClick={()=>setUpdateM({issue,type:"manage"})} style={{...BTN("#0369a1",true),fontSize:10,padding:"4px 10px"}}>→ Mark In Hand</button>}
                      {canAct&&!t3Locked&&issue.status==="managed"&&<button onClick={()=>setUpdateM({issue,type:"resolve"})} style={{...BTN(C.green,true),fontSize:10,padding:"4px 10px"}}>Mark Resolved</button>}
                      {isPrincipal&&issue.status==="acknowledged"&&issue.tier===3&&<button onClick={()=>setUpdateM({issue,type:"resolve"})} style={{...BTN(C.green,true),fontSize:10,padding:"4px 10px"}}>Mark Resolved</button>}
                      {canAct&&!t3Locked&&issue.status!=="resolved"&&issue.tier<3&&<button onClick={()=>doAction(issue,"escalate")} style={{...BTN(C.danger,true),fontSize:10,padding:"4px 10px"}}>↑ Escalate</button>}
                      {(isHeadNurseRole||isManagerRole||isPrincipal)&&issue.tier===1&&issue.status!=="resolved"&&<button onClick={()=>setDelegateM({issue})} style={{...BTN(C.dim,true),fontSize:10,padding:"4px 10px"}}>→ Delegate</button>}
                      {t3Locked&&<div style={{fontSize:9,color:C.dim,textAlign:"right",lineHeight:1.4,maxWidth:140}}>T3 locked — only the principal can change status. You can still add notes via + Update.</div>}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        );
      })}

      {updateM&&(
        <Overlay onClose={()=>{setUpdateM(null);setUpdateNote("");}}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:16,fontWeight:700,color:C.heading,marginBottom:4}}>
            {updateM.type==="update"?"Add Update":updateM.type==="manage"?"Mark as In Hand":"Mark as Resolved"}
          </div>
          {updateM.type==="manage"&&(
            <div style={{fontSize:11,color:"#60a5fa",background:"rgba(99,102,241,0.08)",border:"1px solid rgba(99,102,241,0.2)",borderRadius:8,padding:"8px 12px",marginBottom:12}}>
              Use this when action has been taken but it's not fully resolved yet — e.g. engineer booked, part ordered, meeting arranged. Staff will see it's being dealt with.
            </div>
          )}
          <div style={{fontSize:12,color:C.dim,marginBottom:14}}>{updateM.issue.category} · {SITES[updateM.issue.site]?.name}</div>
          {updateM.type==="resolve"&&updateM.issue.tier===3&&(
            <div style={{fontSize:11,color:"#a78bfa",background:"rgba(139,92,246,0.08)",border:"1px solid #ddd6fe",borderRadius:8,padding:"8px 12px",marginBottom:12}}>
              The record is permanent and will remain in the log. Marking as resolved means the situation has been addressed — it does not remove the record.
            </div>
          )}
          <textarea value={updateNote} onChange={e=>setUpdateNote(e.target.value)} rows={3}
            placeholder={updateM.type==="update"?"e.g. Engineer confirmed for Thursday — chasing parts":updateM.type==="manage"?"What action has been taken? e.g. Engineer booked for Thursday 20th — awaiting visit":"How was this resolved? e.g. Engineer attended, autoclave serviced and passed"}
            style={{...IS,resize:"none",lineHeight:1.5,marginBottom:14}}/>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>doAction(updateM.issue,updateM.type,updateNote)} disabled={!updateNote.trim()}
              style={{...BTN(updateM.type==="resolve"?C.green:updateM.type==="manage"?"#7c3aed":C.teal),flex:1,opacity:!updateNote.trim()?0.4:1}}>Confirm</button>
            <button onClick={()=>{setUpdateM(null);setUpdateNote("");}} style={BTN(C.dim,true)}>Cancel</button>
          </div>
        </Overlay>
      )}
      {delegateM&&(
        <Overlay onClose={()=>{setDelegateM(null);setDelegateName("");}}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:18,fontWeight:700,color:"#a78bfa",marginBottom:4}}>Delegate T1 Issue</div>
          <div style={{fontSize:12,color:C.dim,marginBottom:4}}>{delegateM.issue.category}</div>
          <div style={{fontSize:11,color:C.mid,marginBottom:16}}>Assign someone to action this while you're away. They'll see it flagged in Issues. The full trail will be here when you return.</div>
          <label style={LB}>Assign to</label>
          <select value={delegateName} onChange={e=>setDelegateName(e.target.value)} style={{...IS,marginBottom:16}}>
            <option value="">— select person —</option>
            {[...(state.nurses||NURSES_BASE).filter(n=>n.site===delegateM.issue.site&&!n.maternity&&!n.archived),...RECS_BASE.filter(r=>r.site===delegateM.issue.site&&r.type!=="Manager")].map(s=>(
              <option key={s.id} value={s.name}>{s.name}</option>
            ))}
          </select>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>{if(delegateName){doAction(delegateM.issue,"delegate",delegateName);setDelegateName("");}}} disabled={!delegateName}
              style={{...BTN("#7c3aed"),flex:1,opacity:!delegateName?0.4:1}}>Assign</button>
            <button onClick={()=>{setDelegateM(null);setDelegateName("");}} style={BTN(C.dim,true)}>Cancel</button>
          </div>
        </Overlay>
      )}
    </div>
  );
}

function PortalComplianceCard({task, overdue, last, nurse, user, state, set}) {
  const [logM, setLogM] = useState(false);
  const [done, setDone] = useState(false);

  function markDone(notes, issueData) {
    const entry = {
      id: "ct"+Date.now(),
      taskId: task.id,
      site: nurse.site,
      doneBy: user.name,
      doneById: user.id||user.staffId,
      date: new Date().toISOString().split("T")[0],
      notes,
      issueRaised: !!issueData,
    };
    set(s=>{
      const next = {...s, complianceTasks:[...(s.complianceTasks||[]), entry]};
      if (issueData) {
        const newIssue = {
          id:"iss_"+Date.now(),
          tier: issueData.tier,
          category: issueData.category||task.label+" -- compliance issue",
          description: issueData.desc,
          site: nurse.site,
          staffId: nurse.id,
          staffName: nurse.name,
          anonymous: false,
          date: new Date().toISOString().split("T")[0],
          status:"open",
          acknowledgedAt:null,acknowledgedBy:null,
          updates:[],resolvedAt:null,resolvedNote:null,
          escalatedFrom:null,immutable:issueData.tier===3,
          fromCompliance:true,
          complianceTaskId:task.id,
        };
        next.issues = [...(s.issues||[]), newIssue];
      }
      return next;
    });
    setDone(true);
    setLogM(false);
  }

  if (done) return (
    <div style={{
      padding:"12px 16px",borderRadius:10,marginBottom:8,
      background:"rgba(0,229,160,0.06)",border:"1px solid rgba(0,229,160,0.2)",
      display:"flex",alignItems:"center",gap:10,
    }}>
      <Icon name="check" size={16} color={C.green}/>
      <div style={{fontSize:13,color:C.green,fontWeight:600}}>{task.label} -- logged</div>
    </div>
  );

  const borderColor = overdue ? C.danger+"55" : C.warn+"55";
  const bgColor = overdue ? "rgba(255,70,70,0.04)" : "rgba(245,197,24,0.04)";
  const accentColor = overdue ? C.danger : C.warn;
  const daysSince = last ? Math.floor((Date.now()-new Date(last.date).getTime())/86400000) : null;
  const label = overdue
    ? (daysSince===null ? "Never done" : `${daysSince}d overdue`)
    : "Due soon";

  return (
    <div style={{
      padding:"14px 16px",borderRadius:10,marginBottom:8,
      background:bgColor,border:`1px solid ${borderColor}`,
      borderLeft:`3px solid ${accentColor}`,
    }}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
        <div style={{display:"flex",alignItems:"center",gap:10,flex:1}}>
          <Icon name={task.icon} size={20} color={accentColor}/>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:C.heading}}>{task.label}</div>
            <div style={{fontSize:11,color:C.dim,marginTop:1}}>{task.freqLabel} check -- {label}</div>
          </div>
        </div>
        <button onClick={()=>setLogM(true)} style={{
          ...BTN(overdue?C.danger:C.warn),
          fontSize:11,padding:"7px 14px",flexShrink:0,
        }}>Mark Done</button>
      </div>
      {logM&&(
        <ComplianceLogModal
          task={task}
          site={nurse.site}
          user={user}
          onSave={markDone}
          onClose={()=>setLogM(false)}
        />
      )}
    </div>
  );
}

function Portal({user,state,set,onLogout}) {
  // v11.0 — search all staff sources, not just state.nurses + RECS_BASE.
  // Previous implementation missed state.customRecs entirely, so newly-added
  // reception/TCO staff would render "Record not found" forever even once
  // state had loaded. Now searches: nurses → customRecs → defaults.
  const nurse = (state.nurses||NURSES_BASE).find(n=>n.id===user.staffId)
              || (state.customRecs||[]).find(r=>r.id===user.staffId)
              || RECS_BASE.find(r=>r.id===user.staffId);
  const [tab,setTab] = useState("home");
  const [leaveM,setLeaveM] = useState(false);
  const [swapM,setSwapM]   = useState(false);
  const [otM,setOtM]       = useState(false);
  const [efM,setEfM]       = useState(false);
  const [extraM,setExtraM] = useState(false);
  const [unpaidM,setUnpaidM] = useState(false);
  const [noticesOpen,setNoticesOpen] = useState(false);
  const [latM2,setLatM2]   = useState(null);
  const [latPerson,setLatPerson] = useState("");
  const [hnSickM,setHnSickM] = useState(false);
  const [goodWorkM,setGoodWorkM] = useState(false);
  const [extraPatientsM,setExtraPatientsM] = useState(false);
  const [issueM,setIssueM]      = useState(false);
  const [siteSubTab, setSiteSubTab] = useState("today");
  const [emailEdit,setEmailEdit] = useState(false);
  const [emailVal,setEmailVal]   = useState(user?.email||"");
  // v11.0 — graceful loading state. State loads asynchronously after login
  // (especially for first-arrival users via magic link), and the staff lookup
  // above may briefly return nothing while it does. Show a quiet loading
  // message for the first 3 seconds rather than flashing "Record not found"
  // before state catches up. Only show the real error if 3s pass with no
  // resolution — at which point it's a genuine missing record.
  const [showLookupError, setShowLookupError] = useState(false);
  useEffect(() => {
    if (nurse) return; // found — nothing to wait for
    const t = setTimeout(() => setShowLookupError(true), 3000);
    return () => clearTimeout(t);
  }, [nurse]);
  if (!nurse) {
    if (!showLookupError) {
      return <div style={{padding:32,color:C.dim,fontSize:14}}>Loading your profile…</div>;
    }
    return (
      <div style={{padding:32,color:C.danger,fontSize:14,lineHeight:1.6}}>
        We couldn&#39;t find your profile.
        <div style={{marginTop:12,color:C.soft,fontSize:13}}>
          Try refreshing the page. If this keeps happening, contact your manager.
        </div>
      </div>
    );
  }

  const sd = SITES[nurse.site];
  const entit    = getEntitlement(state, nurse);
  const bhDaysP  = getBankHolidayDays(nurse);
  const bhProrP  = Math.round(bhDaysP * ((nurse.hrs||37.5)/37.5) * 10) / 10;
  const bookableP = Math.max(0, Math.round((entit - bhProrP) * 10) / 10);
  const hist   = getHistDays(state, nurse.id);
  const allLeave = state.leave[nurse.id]||[];
  const approved = allLeave.filter(l=>l.status==="approved");
  const pending  = allLeave.filter(l=>l.status==="pending");
  const declined = allLeave.filter(l=>l.status==="declined");
  const used=countDays(approved)+hist, pend=pending.length, rem=Math.round((bookableP-used)*10)/10;

  // Check if head nurse or elevated — also check active delegations
  const elevRole = state.staffRoles?.[nurse.id];
  const today2 = new Date().toISOString().split("T")[0];
  const activeDelegation = (state.delegations||[]).find(d=>
    d.toStaffId===nurse.id && d.active &&
    d.startDate<=today2 && (!d.endDate||d.endDate>=today2)
  );
  const isHeadNurse = nurse.headNurse || elevRole==="headNurse" || elevRole==="managerLite" || !!activeDelegation;
  const isRec = nurse.type==="Reception" || nurse.type==="TCO";

  function submit(id,dates,leaveType,note,halfDaysMap) {
    const rid="req"+Date.now();
    set(s=>{
      const ex=s.leave[id]||[];
      const ne=dates.map(d=>({id:"l"+Math.random().toString(36).slice(2),requestId:rid,date:d,status:"pending",leaveType:leaveType||"annual",note:note||"",halfDay:halfDaysMap?.[d]||null,days:halfDaysMap?.[d]?0.5:1}));
      return {...s,leave:{...s.leave,[id]:[...ex,...ne]}};
    });
    setLeaveM(false); setTab("leave");
  }

  const TABS = [
    {id:"home",  icon:"home",  label:"Home"},
    {id:"hours", icon:"clock", label:"My Hours"},
    {id:"leave", icon:"plane", label:"Leave", badge:pend},
    {id:"rota",  icon:"cal",   label:"My Rota"},
    ...(isHeadNurse?[{id:"site",icon:"users",label:"Site"}]:[]),
  ];

  const todayStr = new Date().toISOString().split("T")[0];

  return (
    <div style={{minHeight:"100vh",background:C.bg,maxWidth:440,margin:"0 auto",display:"flex",flexDirection:"column"}}>
      {/* Header */}
      <div style={{background:C.surface,borderBottom:`1px solid ${C.border}`,padding:"14px 18px",display:"flex",justifyContent:"space-between",alignItems:"center",position:"sticky",top:0,zIndex:100}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <LogoMark size={42}/>
          <div>
            <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:16,color:C.heading,lineHeight:1.2}}>{nurse.name}</div>
            <div style={{fontSize:11,color:sd?.color}}>{sd?.name}{isHeadNurse?" · Head Nurse":""}</div>
          </div>
        </div>
        <button onClick={onLogout} style={{...BTN(C.dim,true),fontSize:11,padding:"5px 10px"}}>Sign out</button>
      </div>

      {/* Content */}
      <div style={{flex:1,padding:"14px 14px 76px",overflowY:"auto"}}>

        {tab==="home"&&(
          <div>
            {/* ── Site Notices strip ── */}
            {(()=>{
              const siteIssues = (state.issues||[]).filter(i=>
                i.tier===1 && i.site===nurse.site && i.status!=="resolved"
              );
              const inHand = siteIssues.filter(i=>i.status==="managed").length;
              const open   = siteIssues.filter(i=>i.status!=="managed").length;

              if (siteIssues.length===0) return (
                <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:10,marginBottom:12}}>
                  <span style={{fontSize:14}}>✓</span>
                  <span style={{fontSize:12,fontWeight:600,color:C.green}}>No open site notices — all clear</span>
                </div>
              );

              const IssueCard = ({i}) => {
                const isInHand = i.status==="managed";
                const latestNote = i.updates.length>0?i.updates[i.updates.length-1]:null;
                return (
                  <div style={{padding:"10px 12px",borderRadius:10,marginBottom:6,
                    background:isInHand?"#eff6ff":"rgba(245,197,24,0.06)",
                    border:`1px solid ${isInHand?"#bfdbfe":"rgba(245,197,24,0.2)"}`,
                    borderLeft:`3px solid ${isInHand?"#0369a1":C.warn}`}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                      <div style={{flex:1}}>
                        <div style={{fontSize:12,fontWeight:700,color:isInHand?"#0369a1":C.warn,marginBottom:2}}>
                          {isInHand?"🔧":"⚠"} {i.category}
                        </div>
                        <div style={{fontSize:11,color:C.mid,lineHeight:1.5}}>{i.description}</div>
                        {latestNote&&isInHand&&(
                          <div style={{fontSize:10,color:"#60a5fa",marginTop:4,fontStyle:"italic"}}>↳ {latestNote.note}</div>
                        )}
                      </div>
                      <span style={{fontSize:9,fontWeight:700,
                        color:isInHand?"#0369a1":C.warn,
                        background:isInHand?"#dbeafe":"#fef9c3",
                        border:`1px solid ${isInHand?"#93c5fd":"rgba(245,197,24,0.2)"}`,
                        borderRadius:4,padding:"2px 6px",flexShrink:0,whiteSpace:"nowrap"}}>
                        {isInHand?"In Hand":"Open"}
                      </span>
                    </div>
                  </div>
                );
              };

              // 1 issue — show full card directly
              if (siteIssues.length===1) return (
                <div style={{marginBottom:12}}>
                  <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6}}>Site Notice</div>
                  <IssueCard i={siteIssues[0]}/>
                </div>
              );

              // 2+ issues — collapsible summary bar
              return (
                <div style={{marginBottom:12}}>
                  <button onClick={()=>setNoticesOpen(o=>!o)}
                    style={{width:"100%",textAlign:"left",padding:"10px 14px",borderRadius:10,
                      background:open>0?"rgba(245,197,24,0.06)":"#eff6ff",
                      border:`1px solid ${open>0?"rgba(245,197,24,0.2)":"#bfdbfe"}`,
                      borderLeft:`3px solid ${open>0?C.warn:"#0369a1"}`,
                      cursor:"pointer",fontFamily:"inherit",display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:noticesOpen?6:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:13}}>{open>0?"⚠":"🔧"}</span>
                      <span style={{fontSize:12,fontWeight:700,color:open>0?C.warn:"#0369a1"}}>
                        {siteIssues.length} site notice{siteIssues.length!==1?"s":""}{inHand>0?` · ${inHand} in hand`:""}
                      </span>
                    </div>
                    <span style={{fontSize:11,color:C.dim}}>{noticesOpen?"▲ hide":"▼ show"}</span>
                  </button>
                  {noticesOpen&&siteIssues.map(i=><IssueCard key={i.id} i={i}/>)}
                </div>
              );
            })()}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:8}}>
              {[[bookableP.toFixed(1)+"d","Bookable",C.text],[used.toFixed(1)+"d","Used",C.warn],[rem.toFixed(1)+"d","Remaining",rem<5?C.danger:C.green]].map(([v,l,c])=>(
                <div key={l} style={{...CS,textAlign:"center",padding:"12px 6px"}}>
                  <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:c,lineHeight:1}}>{v}</div>
                  <div style={{fontSize:9,color:C.dim,marginTop:4,textTransform:"uppercase",letterSpacing:"0.06em"}}>{l}</div>
                </div>
              ))}
            </div>
            <div style={{fontSize:10,color:C.dim,marginBottom:12,textAlign:"center"}}>
              {entit.toFixed(1)}d total · {bhProrP.toFixed(1)}d bank holidays · {bookableP.toFixed(1)}d to book
            </div>
            {pend>0&&(
              <div style={{background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:10,padding:12,marginBottom:12,display:"flex",alignItems:"center",gap:10}}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                <div>
                  <div style={{fontSize:13,fontWeight:700,color:C.green}}>{pend} day{pend!==1?"s":""} awaiting approval</div>
                  <div style={{fontSize:11,color:C.mid,marginTop:1}}>Your manager will review shortly</div>
                </div>
              </div>
            )}

            {/* Compliance tasks — shown to head nurse and delegated staff */}
            {(()=>{
              if (state.skipComplianceTasks) return null;
              const todayStr3 = new Date().toISOString().split("T")[0];
              const myTasks = COMPLIANCE_TASKS.filter(task=>{
                const delegateKey = `${task.id}_${nurse.site}`;
                const delegatedToId = (state.complianceDelegations||{})[delegateKey];
                // Show if: head nurse (default owner) or explicitly delegated to me
                return isHeadNurse || delegatedToId === nurse.id;
              });
              if (myTasks.length===0) return null;
              const overdueTasks = myTasks.filter(t=>{
                const last = getLastCompletion(state, t.id, nurse.site);
                return isComplianceOverdue(t, last);
              });
              const dueSoonTasks = myTasks.filter(t=>{
                const last = getLastCompletion(state, t.id, nurse.site);
                return !isComplianceOverdue(t,last) && isComplianceDueSoon(t,last);
              });
              const visibleTasks = [...overdueTasks, ...dueSoonTasks];
              if (visibleTasks.length===0) return null;
              return (
                <div style={{marginBottom:16}}>
                  <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Compliance Checks Due</div>
                  {visibleTasks.map(task=>{
                    const last = getLastCompletion(state, task.id, nurse.site);
                    const overdue = isComplianceOverdue(task, last);
                    const [doneM,setDoneM] = [false,()=>{}]; // placeholder
                    return (
                      <PortalComplianceCard
                        key={task.id}
                        task={task}
                        overdue={overdue}
                        last={last}
                        nurse={nurse}
                        user={user}
                        state={state}
                        set={set}
                      />
                    );
                  })}
                </div>
              );
            })()}

            {/* Weekly availability — shown to flexible/part-time staff */}
            {(nurse.flexible||nurse.workDays)&&(()=>{
              const todayStr2 = new Date().toISOString().split("T")[0];
              const ws = getWeekStart(todayStr2);
              const avKey = `${nurse.id}_${ws}`;
              const avDays = state.weeklyAvailability?.[avKey]||[];
              const weekDays = ["Mon","Tue","Wed","Thu","Fri"];
              const contracted = nurse.workDays||[];
              return (
                <div style={{...CS,marginBottom:12,background:"rgba(245,197,24,0.05)",border:`1px solid ${C.warn}33`}}>
                  <div style={{fontSize:11,fontWeight:700,color:C.warn,marginBottom:2}}>Extra availability this week</div>
                  <div style={{fontSize:11,color:C.dim,marginBottom:10}}>Tap days you're free for extra shifts — your manager sees this instantly</div>
                  <div style={{display:"flex",gap:6}}>
                    {weekDays.map(d=>{
                      const isContracted = contracted.includes(d);
                      const isOn = avDays.includes(d);
                      return (
                        <button key={d} onClick={()=>{
                          if (isContracted) return;
                          const newDays = isOn ? avDays.filter(x=>x!==d) : [...avDays,d];
                          set(s=>({...s,weeklyAvailability:{...s.weeklyAvailability,[avKey]:newDays}}));
                        }} style={{flex:1,padding:"8px 4px",borderRadius:8,fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:isContracted?"default":"pointer",border:`1px solid ${isContracted?"#d1fae5":isOn?C.teal:C.border}`,background:isContracted?"#f0fdf4":isOn?C.tealPale:"transparent",color:isContracted?C.green:isOn?C.teal:C.dim}}>
                          {d}<br/><span style={{fontSize:8,fontWeight:400}}>{isContracted?"in":isOn?"✓":"free?"}</span>
                        </button>
                      );
                    })}
                  </div>
                  {avDays.length>0&&<div style={{fontSize:10,color:C.teal,marginTop:8}}>You've flagged {avDays.length} extra day{avDays.length!==1?"s":""} — manager can see this</div>}
                </div>
              );
            })()}

            {/* ── OT VOLUNTEERING — all staff ── */}
            {(()=>{
              const todayStr3 = new Date().toISOString().split("T")[0];
              const ws3 = getWeekStart(todayStr3);
              const avKey3 = `${nurse.id}_${ws3}`;
              const avDays3 = state.weeklyAvailability?.[avKey3]||[];
              const contracted3 = nurse.workDays||["Mon","Tue","Wed","Thu","Fri"];
              const weekDays3 = ["Mon","Tue","Wed","Thu","Fri"];
              const prefs = state.staffOTPrefs?.[nurse.id]||{};
              return (
                <div style={{...CS,marginBottom:10,borderLeft:`3px solid ${C.warn}`}}>
                  <div style={{fontSize:11,fontWeight:700,color:C.warn,marginBottom:2}}>Overtime & extra shifts</div>
                  <div style={{fontSize:11,color:C.dim,marginBottom:10}}>Tap days you're open to OT this week</div>
                  <div style={{display:"flex",gap:5,marginBottom:10}}>
                    {weekDays3.map(d=>{
                      const isContracted = contracted3.includes(d);
                      const isOn = avDays3.includes(d);
                      return (
                        <button key={d} onClick={()=>{
                          const newDays = isOn ? avDays3.filter(x=>x!==d) : [...avDays3,d];
                          set(s=>({...s,weeklyAvailability:{...s.weeklyAvailability,[avKey3]:newDays}}));
                        }} style={{flex:1,padding:"8px 2px",borderRadius:8,fontFamily:"inherit",fontSize:10,fontWeight:700,cursor:"pointer",border:`1px solid ${isContracted?"#d1fae5":isOn?C.teal:C.border}`,background:isContracted?"#f0fdf4":isOn?C.tealPale:"transparent",color:isContracted?C.green:isOn?C.teal:C.dim}}>
                          {d.slice(0,1)}<br/><span style={{fontSize:8,fontWeight:400}}>{isContracted?"in":isOn?"✓":"?"}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:8}}>
                    {[["happy","Happy to do OT"],["sometimes","Sometimes OK"],["noThanks","Prefer not to"]].map(([k,l])=>(
                      <button key={k} onClick={()=>set(s=>({...s,staffOTPrefs:{...s.staffOTPrefs,[nurse.id]:{...prefs,general:k}}}))} style={{padding:"5px 10px",borderRadius:99,fontFamily:"inherit",fontSize:10,fontWeight:600,cursor:"pointer",border:`1px solid ${prefs.general===k?C.teal:C.border}`,background:prefs.general===k?C.tealPale:"transparent",color:prefs.general===k?C.teal:C.dim}}>{l}</button>
                    ))}
                  </div>
                  {prefs.general&&prefs.general!=="noThanks"&&(
                    <div style={{fontSize:10,color:C.dim,marginBottom:4}}>
                      <span style={{fontWeight:700,color:C.mid}}>Reason you'd do OT: </span>
                      {[["payReason","Extra pay"],["teamReason","Help the team"],["hoursReason","Want more hours"]].map(([k,l])=>(
                        <button key={k} onClick={()=>set(s=>({...s,staffOTPrefs:{...s.staffOTPrefs,[nurse.id]:{...prefs,[k]:!prefs[k]}}}))} style={{marginRight:4,marginTop:4,padding:"3px 8px",borderRadius:99,fontFamily:"inherit",fontSize:9,fontWeight:600,cursor:"pointer",border:`1px solid ${prefs[k]?C.teal:C.border}`,background:prefs[k]?C.tealPale:"transparent",color:prefs[k]?C.teal:C.dim,display:"inline-block"}}>{l}</button>
                      ))}
                    </div>
                  )}
                  {avDays3.length>0&&<div style={{fontSize:10,color:C.teal,marginTop:4}}>You've flagged {avDays3.length} day{avDays3.length!==1?"s":""} available — manager notified</div>}
                </div>
              );
            })()}

            <button onClick={()=>setLeaveM(true)} style={{width:"100%",padding:"14px 0",borderRadius:12,border:`1.5px solid ${C.teal}44`,background:C.tealPale,color:C.teal,fontFamily:"inherit",fontSize:14,fontWeight:700,cursor:"pointer",marginBottom:8,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
              <Icon name="plane" size={16} color={C.teal}/> Request Leave
            </button>
            <button onClick={()=>setSwapM(true)} style={{width:"100%",padding:"12px 0",borderRadius:12,border:`1.5px solid ${C.brook}44`,background:C.brook+"0d",color:C.brook,fontFamily:"inherit",fontSize:13,fontWeight:700,cursor:"pointer",marginBottom:8,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
              <Icon name="repeat" size={16} color={C.brook}/> Request Shift Swap
            </button>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
              <button onClick={()=>setOtM(true)} style={{padding:"12px 0",borderRadius:10,border:`1px solid ${C.warn}44`,background:"rgba(245,197,24,0.08)",color:C.warn,fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}><Icon name="clock" size={14} color={C.warn}/> Finished Late</button>
              <button onClick={()=>setEfM(true)} style={{padding:"12px 0",borderRadius:10,border:`1px solid ${C.lime}44`,background:"rgba(0,229,160,0.06)",color:C.lime,fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}><Icon name="arrow-left" size={14} color={C.lime}/> Early Finish</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
              <button onClick={()=>setExtraM(true)} style={{padding:"12px 0",borderRadius:10,border:`1px solid ${C.brook}44`,background:"rgba(139,92,246,0.08)",color:C.brook,fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}><Icon name="plus" size={14} color={C.brook}/> Extra Shift</button>
              <button onClick={()=>setUnpaidM(true)} style={{padding:"12px 0",borderRadius:10,border:`1px solid ${C.dim}44`,background:"#181626",color:C.mid,fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}><Icon name="repeat" size={14} color={C.mid}/> Unpaid Leave</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>

              <button onClick={()=>setGoodWorkM(true)} style={{padding:"12px 0",borderRadius:10,border:`1px solid ${C.gold}55`,background:"rgba(245,197,24,0.10)",color:"rgba(255,255,255,0.92)",fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}><Icon name="star" size={14} color={C.gold}/> Good Work</button>
              <button onClick={()=>setIssueM(true)} style={{padding:"12px 0",borderRadius:10,border:"1px solid rgba(255,70,70,0.2)",background:"rgba(255,70,70,0.08)",color:"#ff4646",fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}><Icon name="warning" size={14} color="#dc2626"/> Report Issue</button>
            </div>
            {/* ── WELFARE ALERT (visible to the nurse themselves) ── */}
            {(()=>{
              const wTriggers = checkWelfareTrigger(nurse.id, state);
              const wOpen = (state.welfareLog||[]).some(w=>w.staffId===nurse.id&&!w.closed);
              if (!wTriggers.length && !wOpen) return null;
              return (
                <div style={{...CS,marginBottom:10,borderLeft:`3px solid ${C.danger}`,background:"rgba(255,70,70,0.08)"}}>
                  <div style={{fontSize:11,fontWeight:700,color:C.danger,marginBottom:4}}>Attendance notice</div>
                  {wTriggers.map((t,i)=><div key={i} style={{fontSize:11,color:C.soft,marginBottom:2}}>• {t}</div>)}
                  {wOpen&&<div style={{fontSize:11,color:C.warn,marginTop:4}}>A welfare meeting has been noted by your manager.</div>}
                  <div style={{fontSize:10,color:C.dim,marginTop:4}}>Speak to your manager if you'd like to discuss.</div>
                </div>
              );
            })()}

            {/* ── REPEAT-ISSUE PATTERN BANNER (depersonalised, visible to all site staff) ── */}
            {(()=>{
              // Show categories raised 2+ times in the last 30 days at this nurse's site.
              // No names, no descriptions — just "this category has come up more than once".
              const ago30 = new Date(Date.now()-30*24*60*60*1000).toISOString().split("T")[0];
              const siteIssues = (state.issues||[]).filter(i=>i.site===nurse.site&&i.date>=ago30);
              const catCounts = {};
              siteIssues.forEach(i=>{ catCounts[i.category]=(catCounts[i.category]||0)+1; });
              const repeats = Object.entries(catCounts).filter(([,n])=>n>=2);
              if (repeats.length===0) return null;
              return (
                <div style={{...CS,marginBottom:10,borderLeft:`3px solid ${C.warn}`,background:"rgba(245,197,24,0.06)"}}>
                  <div style={{fontSize:11,fontWeight:700,color:C.warn,marginBottom:6}}>Site notice — recurring concerns</div>
                  {repeats.map(([cat,n])=>(
                    <div key={cat} style={{fontSize:11,color:C.soft,marginBottom:2}}>• "{cat}" raised {n} times this month</div>
                  ))}
                  <div style={{fontSize:10,color:C.dim,marginTop:6,lineHeight:1.5}}>Your manager and principal have been notified. If you've seen something contributing to this — or anything else worth raising — use Report Issue. You can report anonymously on Tier 3 (Serious).</div>
                </div>
              );
            })()}

            {/* ── HOURS THIS WEEK ── */}
            {(()=>{
              const todayStr4 = new Date().toISOString().split("T")[0];
              const ws4 = getWeekStart(todayStr4);
              const approvedThisWeek = (state.leave[nurse.id]||[]).filter(l=>l.status==="approved"&&l.date>=ws4&&l.date<=todayStr4).length;
              const contractDaysPerWeek = nurse.workDays ? nurse.workDays.length : 5;
              const contractHrsPerWeek = nurse.hrs||37.5;
              const otApproved = (state.overtime||[]).filter(r=>r.staffId===nurse.id&&r.date>=ws4&&r.status==="approved").reduce((s,r)=>s+r.hrs,0);
              const efApproved = (state.earlyFinish||[]).filter(r=>r.staffId===nurse.id&&r.date>=ws4).reduce((s,r)=>s+r.hrsEarly,0);
              const estimatedHrs = contractHrsPerWeek + otApproved - efApproved - (approvedThisWeek*7.5);
              const over = estimatedHrs - contractHrsPerWeek;
              if (Math.abs(over) < 0.5) return null;
              return (
                <div style={{...CS,marginBottom:10,borderLeft:`3px solid ${over>0?C.warn:C.green}`}}>
                  <div style={{fontSize:11,fontWeight:700,color:over>0?C.warn:C.green,marginBottom:2}}>
                    {over>0?`+${over.toFixed(1)}h over contract this week`:` ${Math.abs(over).toFixed(1)}h under this week`}
                  </div>
                  <div style={{fontSize:10,color:C.dim}}>Contract {contractHrsPerWeek}h · OT {otApproved}h · Early finishes −{efApproved}h</div>
                </div>
              );
            })()}

            {/* Delegation notice */}
            {activeDelegation&&(
              <div style={{background:"rgba(139,92,246,0.08)",border:"1px solid #ddd6fe",borderRadius:10,padding:"10px 14px",marginBottom:10}}>
                <div style={{fontSize:11,fontWeight:700,color:C.brook}}>Acting manager delegated to you</div>
                <div style={{fontSize:10,color:C.dim,marginTop:2}}>{activeDelegation.startDate}{activeDelegation.endDate?` → ${activeDelegation.endDate}`:""} · You can approve leave and log attendance</div>
              </div>
            )}

            <div style={CS}>
              <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Your Details</div>
              {[[`${nurse.hrs}h/week`,"Contract hours"],[sd?.name,"Site"],[nurse.area||"Reception","Area"]].map(([v,l])=>(
                <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:`1px solid ${C.border2}`}}>
                  <span style={{fontSize:12,color:C.dim}}>{l}</span>
                  <span style={{fontSize:12,fontWeight:600,color:C.text}}>{v}</span>
                </div>
              ))}
              {nurse.status&&<div style={{marginTop:8}}><Pill c={STATUS[nurse.status].color} t={STATUS[nurse.status].label}/></div>}
              {nurse.note&&<div style={{fontSize:11,color:C.warn,marginTop:8}}>ℹ️ {nurse.note}</div>}
            </div>
          </div>
        )}

        {tab==="leave"&&(
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading}}>My Leave</div>
              <button onClick={()=>setLeaveM(true)} style={{...BTN(C.teal),fontSize:11,padding:"6px 12px"}}>+ Request</button>
            </div>
            <div style={{...CS,marginBottom:14}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                <span style={{fontSize:12,color:C.dim}}>Leave used</span>
                <span style={{fontSize:12,fontWeight:700,color:C.text}}>{used.toFixed(1)} of {entit.toFixed(1)} days</span>
              </div>
              <div style={{height:8,background:C.border,borderRadius:99,overflow:"hidden",marginBottom:6}}>
                <div style={{width:`${Math.min((used/entit)*100,100)}%`,height:"100%",background:rem<5?C.danger:rem<10?C.warn:C.green,borderRadius:99}}/>
              </div>
              <div style={{fontSize:11,color:rem<5?C.danger:C.dim}}>{typeof rem==="number"?Math.round(rem*10)/10:rem} days remaining</div>
            </div>
            {(approved.length>0||(getCpdForPerson(state,nurse.id).length>0))&&(
              <button onClick={()=>{
                const myCpd=getCpdForPerson(state,nurse.id);
                const sessions=buildRotaSessions(nurse.id,false,nurse.site,state,CAL.days.filter(d=>!d.bh));
                exportICal(approved,nurse.name,myCpd,sessions);
              }} style={{...BTN(C.teal,true),width:"100%",marginBottom:14,padding:"10px 0",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> Export calendar (.ics)</button>
            )}
            {[["Approved",approved,C.green],["Pending",pending,C.lime],["Declined",declined,C.danger]].map(([title,items,c])=>items.length?(
              <div key={title} style={{marginBottom:14}}>
                <div style={{fontSize:10,fontWeight:700,color:c,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>{title} ({items.length})</div>
                {items.sort((a,b)=>a.date.localeCompare(b.date)).map(l=>{
                  const lt = LEAVE_TYPES.find(t=>t.id===l.leaveType);
                  return (
                    <div key={l.id} style={{...CS,marginBottom:4,padding:"10px 12px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:lt||l.managerNote?4:0}}>
                        <span style={{fontSize:13,color:C.text,fontWeight:600,display:"flex",alignItems:"center",gap:5}}>{CAL.days.find(d=>d.date===l.date)?.label||l.date}{l.halfDay&&<span style={{fontSize:10,fontWeight:700,background:C.tealPale,color:C.teal,border:`1px solid ${C.tealBorder}`,borderRadius:4,padding:"1px 5px"}}>½{l.halfDay}</span>}</span>
                        <div style={{display:"flex",gap:4,alignItems:"center"}}>
                          {lt&&lt.id!=="annual"&&<Pill c={C.teal} t={lt.label} sm/>}
                          <Pill c={c} t={title.split(" ")[1]} sm/>
                        </div>
                      </div>
                      {l.managerNote&&<div style={{fontSize:10,color:C.warn,marginTop:2}}>{l.managerNote}</div>}
                    </div>
                  );
                })}
              </div>
            ):null)}
            {allLeave.length===0&&<div style={{textAlign:"center",padding:"32px 0",color:C.dim}}><div style={{fontSize:32,marginBottom:8}}>✈️</div>No leave requests yet</div>}

            {/* ── My email — for notifications ── */}
            {(()=>{
              const currentEmail = user?.email||"";
              if (!emailEdit) return (
                <div style={{...CS,marginTop:8,padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <div style={{fontSize:11,fontWeight:700,color:C.text}}>Notification email</div>
                    <div style={{fontSize:11,color:currentEmail?C.teal:C.muted,marginTop:2}}>{currentEmail||"Not set — tap to add"}</div>
                  </div>
                  <button onClick={()=>{setEmailVal(currentEmail);setEmailEdit(true);}} style={{...BTN(C.dim,true),fontSize:11,padding:"4px 10px"}}>Edit</button>
                </div>
              );
              return (
                <div style={{...CS,marginTop:8,padding:"12px 14px"}}>
                  <div style={{fontSize:12,fontWeight:700,color:C.text,marginBottom:4}}>Notification email</div>
                  <div style={{fontSize:11,color:C.dim,marginBottom:8}}>Used to notify you when issues are logged or leave is actioned. Only you and your manager can see this.</div>
                  <input type="email" value={emailVal} onChange={e=>setEmailVal(e.target.value)}
                    placeholder="your@email.com" style={{...IS,marginBottom:8}}/>
                  <div style={{display:"flex",gap:8}}>
                    <button onClick={()=>{
                      set(s=>({...s, users:(s.users||[]).map(u=>u.id===user.id?{...u,email:emailVal.trim()}:u)}));
                      setEmailEdit(false);
                    }} style={{...BTN(C.teal),flex:1,padding:"8px 0"}}>Save</button>
                    <button onClick={()=>setEmailEdit(false)} style={BTN(C.dim,true)}>Cancel</button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ── v11.4 — MY HOURS TAB ──────────────────────────────────────
            Calm, scannable monthly hours summary for hourly workers.
            Four numbers (last month, this month so far, this month
            remaining, next month) plus an upcoming shifts list.
            Hours are computed indicatively from contracted weekly hours
            and working days, less any approved leave for the period.
            Sickness / overtime / extras refinement is v2 work — for v1
            the goal is "useful at a glance", not "accurate to the
            minute". Helps staff form the daily-open habit. */}
        {tab==="hours"&&(()=>{
          const today = new Date();
          const todayIso = today.toISOString().split("T")[0];

          // Per-day hours derived from contracted weekly hours / working days
          // count. If workDays is set use that, otherwise assume 5 (Mon-Fri).
          const workDayCount = (nurse.workDays?.length) || 5;
          const hoursPerDay = (nurse.hrs || 37.5) / workDayCount;

          // Helper — given a date string, is this person scheduled to work
          // (not weekend, not bank holiday, not on approved leave)?
          const myLeave = state.leave?.[nurse.id] || [];
          const approvedLeaveDates = new Set(
            myLeave.filter(l => l.status === "approved").map(l => l.date)
          );
          const isWorkingDay = (day) => {
            if (day.isWeekend || day.bh) return false;
            if (approvedLeaveDates.has(day.date)) return false;
            // workDays may restrict to specific days (e.g. ["Mon","Tue","Wed"])
            if (nurse.workDays?.length && !nurse.workDays.includes(day.dow)) return false;
            return true;
          };

          // Month boundaries (from CAL)
          const thisMonthName = CAL.days.find(d => d.date === todayIso)?.month;
          const monthsList = [];
          CAL.days.forEach(d => {
            if (!monthsList.includes(d.month)) monthsList.push(d.month);
          });
          const thisMonthIdx = monthsList.indexOf(thisMonthName);
          const lastMonthName = thisMonthIdx > 0 ? monthsList[thisMonthIdx - 1] : null;
          const nextMonthName = thisMonthIdx < monthsList.length - 1 ? monthsList[thisMonthIdx + 1] : null;

          const sumHoursForMonth = (monthName, beforeOrAfter) => {
            // beforeOrAfter: "all" | "uptoToday" | "afterToday"
            if (!monthName) return 0;
            const days = CAL.days.filter(d => {
              if (d.month !== monthName) return false;
              if (beforeOrAfter === "uptoToday" && d.date > todayIso) return false;
              if (beforeOrAfter === "afterToday" && d.date <= todayIso) return false;
              return isWorkingDay(d);
            });
            return days.length * hoursPerDay;
          };

          const lastMonthHours = sumHoursForMonth(lastMonthName, "all");
          const thisMonthDone = sumHoursForMonth(thisMonthName, "uptoToday");
          const thisMonthRemaining = sumHoursForMonth(thisMonthName, "afterToday");
          const thisMonthTotal = thisMonthDone + thisMonthRemaining;
          const nextMonthHours = sumHoursForMonth(nextMonthName, "all");

          // Coming up — next 5 working days (no weekends, no BH, no leave)
          const upcomingDays = CAL.days
            .filter(d => d.date >= todayIso && isWorkingDay(d))
            .slice(0, 5);

          // Style helpers
          const NUM = {fontFamily:"'Instrument Serif',Georgia,serif",fontStyle:"italic",fontSize:42,fontWeight:400,color:C.heading,lineHeight:1,letterSpacing:"-0.02em"};
          const NUM_SMALL = {...NUM, fontSize:28};
          const LABEL = {fontSize:11,color:C.dim,letterSpacing:"0.06em",textTransform:"uppercase",fontWeight:600,marginBottom:8};
          const PILL = {padding:"3px 8px",borderRadius:99,fontSize:10,fontWeight:600,letterSpacing:"0.04em",textTransform:"uppercase"};

          // Progress bar percentage for this month
          const pct = thisMonthTotal > 0 ? Math.round((thisMonthDone / thisMonthTotal) * 100) : 0;

          return (
            <div>
              {/* This month — hero card with progress */}
              <div style={{...CS,padding:"20px 18px",marginBottom:14}}>
                <div style={LABEL}>This month · {thisMonthName}</div>
                <div style={{display:"flex",alignItems:"baseline",gap:10,marginBottom:14}}>
                  <span style={NUM}>{thisMonthDone.toFixed(1).replace(/\.0$/,"")}</span>
                  <span style={{fontSize:14,color:C.dim}}>of {thisMonthTotal.toFixed(1).replace(/\.0$/,"")}h scheduled</span>
                </div>
                {/* Progress bar */}
                <div style={{height:6,borderRadius:99,background:"rgba(255,255,255,0.06)",overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${pct}%`,background:C.green,transition:"width 0.6s ease"}}/>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",marginTop:8,fontSize:11,color:C.dim}}>
                  <span>{thisMonthDone.toFixed(1).replace(/\.0$/,"")}h done</span>
                  <span>{thisMonthRemaining.toFixed(1).replace(/\.0$/,"")}h to go</span>
                </div>
              </div>

              {/* Last + Next month — two side by side */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
                <div style={{...CS,padding:"16px 14px"}}>
                  <div style={LABEL}>Last month</div>
                  <div style={{display:"flex",alignItems:"baseline",gap:6}}>
                    <span style={NUM_SMALL}>{lastMonthHours.toFixed(1).replace(/\.0$/,"")}</span>
                    <span style={{fontSize:11,color:C.dim}}>h</span>
                  </div>
                  <div style={{fontSize:10,color:C.dim,marginTop:4}}>{lastMonthName||"—"}</div>
                </div>
                <div style={{...CS,padding:"16px 14px"}}>
                  <div style={LABEL}>Next month</div>
                  <div style={{display:"flex",alignItems:"baseline",gap:6}}>
                    <span style={NUM_SMALL}>{nextMonthHours.toFixed(1).replace(/\.0$/,"")}</span>
                    <span style={{fontSize:11,color:C.dim}}>h</span>
                  </div>
                  <div style={{fontSize:10,color:C.dim,marginTop:4}}>{nextMonthName||"—"}</div>
                </div>
              </div>

              {/* Coming up — next 5 shifts */}
              {upcomingDays.length > 0 && (
                <div style={{...CS,padding:"16px 18px"}}>
                  <div style={{...LABEL,marginBottom:12}}>Coming up</div>
                  {upcomingDays.map((day, i) => {
                    // Try to get pairing info from buildDayRota for richer display
                    const siteNurses = (state.nurses||NURSES_BASE).filter(n => n.site === nurse.site && !n.maternity && !n.archived);
                    let pairingText = "";
                    let timeText = "";
                    try {
                      const dr = buildDayRota(day.date, nurse.site, siteNurses, state);
                      // Find this nurse's assignment in the day rota
                      const myAssignment = (dr?.pairings || []).find(p =>
                        (p.nurses||[]).some(n => n.id === nurse.id) ||
                        p.nurseId === nurse.id
                      );
                      if (myAssignment) {
                        const clinName = myAssignment.clinician?.name || myAssignment.clinicianName;
                        if (clinName) pairingText = `with ${clinName.split(" ")[0]}`;
                        const start = myAssignment.start || myAssignment.clinician?.sched?.[day.dow]?.[0]?.split("-")?.[0];
                        const end = myAssignment.end || myAssignment.clinician?.sched?.[day.dow]?.[0]?.split("-")?.[1];
                        if (start && end) timeText = `${start}–${end}`;
                      }
                    } catch (e) { /* fallback to basic display */ }
                    return (
                      <div key={day.date} style={{
                        display:"flex",justifyContent:"space-between",alignItems:"center",
                        padding:"10px 0",
                        borderTop:i===0?"none":`1px solid ${C.border}`,
                      }}>
                        <div>
                          <div style={{fontSize:13,color:C.heading,fontWeight:600}}>{day.label}</div>
                          {(timeText || pairingText) && (
                            <div style={{fontSize:11,color:C.dim,marginTop:2}}>
                              {[timeText, pairingText].filter(Boolean).join(" · ")}
                            </div>
                          )}
                        </div>
                        <div style={{fontSize:11,color:C.dim}}>{SITES[nurse.site]?.name||nurse.site}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Indicative footnote */}
              <div style={{fontSize:10,color:C.dim,textAlign:"center",marginTop:14,lineHeight:1.5,padding:"0 14px"}}>
                Indicative — based on contracted hours and approved leave.
                Doesn&apos;t yet reflect overtime, extra shifts, or sickness.
              </div>
            </div>
          );
        })()}

        {tab==="rota"&&(()=>{
          const approvedDates = new Set(approved.map(l=>l.date));
          const pendingDates  = new Set(pending.map(l=>l.date));
          const myCpd = getCpdForPerson(state, nurse.id);
          const upDays = CAL.days.filter(d=>d.date>=todayStr&&!d.bh).slice(0,28);
          const wkMap={};
          upDays.forEach(d=>{
            const wk=CAL.weeks.find(w=>w.days.some(wd=>wd.date===d.date));
            const key=wk?.start||d.date;
            if(!wkMap[key])wkMap[key]={start:key,days:[]};
            wkMap[key].days.push(d);
          });
          const myWeeks=Object.values(wkMap).slice(0,4);

          // ── RECEPTIONIST ROTA VIEW ──
          if (isRec) {
            const contracts = state.recContracts||{};
            const myContract = contracts[nurse.id]||{};
            const SHIFT_COLORS = {early:C.teal, full:"#7c3aed", late:C.brook};
            const SHIFT_LABELS = {early:"Early  08:00–14:00", full:"Full  08:00–17:30", late:"Late  14:00–20:00"};
            return (
              <div>
                <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>My Rota</div>
                <div style={{fontSize:12,color:C.dim,marginBottom:14}}>{sd?.name} — next 4 weeks</div>
                {myWeeks.map(wk=>(
                  <div key={wk.start} style={{...CS,marginBottom:10}}>
                    <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>
                      {wk.days[0]?.label.split(" ").slice(1).join(" ")} – {wk.days[wk.days.length-1]?.label.split(" ").slice(1).join(" ")}
                    </div>
                    {wk.days.map(day=>{
                      if (day.isWeekend) return null;
                      const isOff = approvedDates.has(day.date);
                      const isPend = pendingDates.has(day.date);
                      const isSick = isAbsent(state, nurse.id, day.date) && !isOff;
                      // Check manual override first, then contract
                      const overrideKey = `${day.date}_${nurse.site}`;
                      const override = (state.recShiftOverrides||{})[overrideKey];
                      let myAssignment = null;
                      if (override) {
                        myAssignment = override.assignments?.find(a=>a.staffId===nurse.id);
                      } else {
                        // Build from contract
                        const contractShift = myContract[day.dow];
                        if (contractShift && contractShift !== "off") {
                          myAssignment = {shift:contractShift, fromContract:true};
                        }
                      }
                      // noFri check
                      const notScheduled = (nurse.noFri && day.dow==="Fri") || (!myAssignment && !isOff && !isPend);
                      return (
                        <div key={day.date} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"9px 0",borderBottom:`1px solid ${C.border2}`,opacity:isOff||isSick?0.5:1}}>
                          <div style={{minWidth:52}}>
                            <div style={{fontSize:12,fontWeight:700,color:isOff?C.dim:C.text}}>{day.dow}</div>
                            <div style={{fontSize:10,color:C.dim}}>{day.label.split(" ").slice(1,3).join(" ")}</div>
                          </div>
                          <div style={{flex:1}}>
                            {isOff&&<div style={{display:"flex",alignItems:"center",gap:6}}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.8 19.2L16 11l3.5-3.5C21 6 21 4 19.5 2.5S18 2 16.5 3.5L13 7 4.8 5.2l-1.4 1.4 6.1 3.8L7.2 14H4l-1.5 1.5 3 3 3 3L10 20v-3.2l3.5-2.3 3.8 6.1 1.5-1.4"/></svg><span style={{fontSize:12,color:C.green,fontWeight:600}}>Annual Leave</span></div>}
                            {!isOff&&isPend&&<div style={{display:"flex",alignItems:"center",gap:6}}><span>⏳</span><span style={{fontSize:12,color:C.lime}}>Leave pending approval</span></div>}
                            {!isOff&&!isPend&&myAssignment&&(
                              <div style={{display:"flex",alignItems:"center",gap:8}}>
                                <div style={{width:7,height:7,borderRadius:99,background:SHIFT_COLORS[myAssignment.shift]||C.teal,flexShrink:0}}/>
                                <div>
                                  <div style={{fontSize:12,fontWeight:700,color:SHIFT_COLORS[myAssignment.shift]||C.teal}}>
                                    {SHIFT_LABELS[myAssignment.shift]||myAssignment.shift}
                                  </div>
                                  {myAssignment.start&&myAssignment.end&&!myAssignment.fromContract&&(
                                    <div style={{fontSize:10,color:C.dim}}>{myAssignment.start}–{myAssignment.end}</div>
                                  )}
                                </div>
                              </div>
                            )}
                            {!isOff&&!isPend&&notScheduled&&<span style={{fontSize:12,color:C.muted}}>{nurse.noFri&&day.dow==="Fri"?"Not scheduled (no Fridays)":"Not scheduled"}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
                {!Object.keys(myContract).length&&(
                  <div style={{...CS,padding:20,textAlign:"center",color:C.dim,fontSize:12}}>Your shift pattern hasn't been set up yet — speak to your manager.</div>
                )}
                <button onClick={()=>exportICal(approved,nurse.name,myCpd,[])} style={{...BTN(C.teal,true),width:"100%",padding:"10px 0",fontSize:12,marginTop:4}}>Export leave to phone (.ics)</button>
              </div>
            );
          }

          // ── NURSE ROTA VIEW (unchanged) ──
          return (
            <div>
              <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>My Rota</div>
              <div style={{fontSize:12,color:C.dim,marginBottom:14}}>{sd?.name} — next 4 weeks</div>

              {/* Upcoming CPD banner */}
              {myCpd.filter(e=>e.date>=todayStr).length>0&&(
                <div style={{background:"rgba(139,92,246,0.08)",border:"1px solid #ddd6fe",borderRadius:12,padding:12,marginBottom:14}}>
                  <div style={{fontSize:10,fontWeight:700,color:C.brook,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Upcoming CPD & Training</div>
                  {myCpd.filter(e=>e.date>=todayStr).sort((a,b)=>a.date.localeCompare(b.date)).map(e=>{
                    const ct=CPD_TYPES.find(t=>t.id===e.cpdType)||CPD_TYPES[CPD_TYPES.length-1];
                    return (
                      <div key={e.id} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 0",borderBottom:"1px solid #ede9fe"}}>
                        <span style={{fontSize:15}}>{""}</span>
                        <div style={{flex:1}}>
                          <div style={{fontSize:12,fontWeight:700,color:ct.color}}>{e.title||ct.label}</div>
                          <div style={{fontSize:10,color:C.dim}}>{CAL.days.find(d=>d.date===e.date)?.label||e.date} · {e.allDay?"Full day":`${e.startTime}–${e.endTime}`}</div>
                        </div>
                        {e.allDay&&<Pill c={C.danger} t="Surgery closed" sm/>}
                      </div>
                    );
                  })}
                </div>
              )}

              {myWeeks.map(wk=>(
                <div key={wk.start} style={{...CS,marginBottom:10}}>
                  <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>
                    {wk.days[0]?.label.split(" ").slice(1).join(" ")} – {wk.days[wk.days.length-1]?.label.split(" ").slice(1).join(" ")}
                  </div>
                  {wk.days.map(day=>{
                    const isOff=approvedDates.has(day.date),isPend=pendingDates.has(day.date);
                    const dayCpd=myCpd.filter(e=>e.date===day.date);
                    const dr=buildDayRota(day.date,nurse.site,(state.nurses||NURSES_BASE).filter(n=>n.site===nurse.site&&!n.maternity&&!n.archived),state);
                    const myPair=dr.pairings.find(p=>p.nurse?.id===nurse.id);
                    return (
                      <div key={day.date} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"9px 0",borderBottom:`1px solid ${C.border2}`,opacity:isOff?0.5:1}}>
                        <div style={{minWidth:52}}>
                          <div style={{fontSize:12,fontWeight:700,color:isOff?C.dim:dayCpd.some(e=>e.allDay)?C.brook:C.text}}>{day.dow}</div>
                          <div style={{fontSize:10,color:C.dim}}>{day.label.split(" ").slice(1,3).join(" ")}</div>
                        </div>
                        <div style={{flex:1}}>
                          {isOff&&<div style={{display:"flex",alignItems:"center",gap:6}}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.8 19.2L16 11l3.5-3.5C21 6 21 4 19.5 2.5S18 2 16.5 3.5L13 7 4.8 5.2l-1.4 1.4 6.1 3.8L7.2 14H4l-1.5 1.5 3 3 3 3L10 20v-3.2l3.5-2.3 3.8 6.1 1.5-1.4"/></svg><span style={{fontSize:12,color:C.green,fontWeight:600}}>Annual Leave</span></div>}
                          {!isOff&&isPend&&<div style={{display:"flex",alignItems:"center",gap:6}}><span>⏳</span><span style={{fontSize:12,color:C.lime}}>Leave pending</span></div>}
                          {!isOff&&!isPend&&dayCpd.map(e=>{
                            const ct=CPD_TYPES.find(t=>t.id===e.cpdType)||CPD_TYPES[CPD_TYPES.length-1];
                            return <div key={e.id} style={{display:"flex",alignItems:"center",gap:5,marginBottom:2}}>
                              <span style={{fontSize:13}}>{""}</span>
                              <span style={{fontSize:12,fontWeight:600,color:ct.color}}>{e.title||ct.label}</span>
                              <span style={{fontSize:10,color:C.dim}}>{e.allDay?"Full day":`${e.startTime}–${e.endTime}`}</span>
                            </div>;
                          })}
                          {!isOff&&!isPend&&!dayCpd.some(e=>e.allDay)&&(myPair?<div>
                            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                              <div style={{width:7,height:7,borderRadius:99,background:myPair.clinician.color}}/>
                              <span style={{fontSize:12,fontWeight:700,color:myPair.clinician.color}}>{myPair.clinician.name}</span>
                              {myPair.autoAssigned&&<Pill c={C.warn} t="Cover" sm/>}
                            </div>
                            <div style={{fontSize:10,color:C.dim,paddingLeft:13}}>{myPair.clinician.type} · {myPair.clinStart}–{myPair.clinEnd}</div>
                            <div style={{fontSize:10,color:C.mid,paddingLeft:13}}>Your shift: {myPair.shift.start}–{myPair.shift.end}</div>
                          </div>
                          :<span style={{fontSize:12,color:(nurse.noFri&&day.dow==="Fri")||(nurse.workDays&&!nurse.workDays.includes(day.dow))?C.muted:C.mid}}>
                            {(nurse.noFri&&day.dow==="Fri")||(nurse.workDays&&!nurse.workDays.includes(day.dow))?"Not scheduled":"Available — no session assigned"}
                          </span>)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
              <button onClick={()=>{
                const sessions=buildRotaSessions(nurse.id,false,nurse.site,state,CAL.days.filter(d=>!d.bh));
                exportICal(approved,nurse.name,myCpd,sessions);
              }} style={{...BTN(C.teal,true),width:"100%",padding:"10px 0",fontSize:12,marginTop:4}}>Export to phone (.ics)</button>
            </div>
          );
        })()}

        {tab==="site"&&isHeadNurse&&(()=>{
          const siteNurses = (state.nurses||NURSES_BASE).filter(n=>n.site===nurse.site&&!n.maternity&&!n.archived);
          const reportableClins = getClinicians(state).filter(c=>(c.sites||[c.site]).includes(nurse.site)&&!c._deleted&&!c.type?.includes("Partner"));
          const allSiteStaff = [...siteNurses,...RECS_BASE.filter(r=>r.site===nurse.site),...reportableClins];
          const selectedPerson = allSiteStaff.find(s=>s.id===latPerson)||allSiteStaff[0];
          // siteSubTab is defined at component level
          const todayOT = (state.overtime||[]).filter(r=>r.site===nurse.site&&r.status==="pending");
          const todayLate = (state.latenessLog||[]).filter(r=>r.site===nurse.site).sort((a,b)=>b.date.localeCompare(a.date)).slice(0,5);

          // Who's off today at this site
          const siteOffToday = allSiteStaff.filter(s=>s.id&&isAbsent(state,s.id,new Date().toISOString().split("T")[0]));
          const REASON_ICO = r => {
            if (!r) return "📋";
            const rl=(r||"").toLowerCase();
            if(rl.includes("annual")||rl.includes("holiday"))return"→";
            if(rl.includes("sick")||rl.includes("ill"))return"·";
            if(rl.includes("cpd")||rl.includes("train"))return"→";
            if(rl.includes("mat"))return"→";
            return"→";
          };

          return (
            <div>
              <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:2}}>Team — {sd?.name}</div>
              <div style={{fontSize:12,color:C.dim,marginBottom:14}}>Logged as {nurse.name} · Head Nurse</div>

              {/* Person picker — FIRST so buttons are contextual */}
              <div style={{marginBottom:14}}>
                <label style={LB}>Who are you reporting about?</label>
                <select value={latPerson} onChange={e=>setLatPerson(e.target.value)} style={IS}>
                  {allSiteStaff.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>

              {/* Selected person name + action buttons */}
              {selectedPerson&&(
                <div style={{...CS,marginBottom:16,padding:"14px 16px",borderLeft:`3px solid ${SITES[nurse.site]?.color||C.teal}`}}>
                  <div style={{fontSize:11,color:C.dim,marginBottom:2}}>Reporting about</div>
                  <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:12}}>{selectedPerson.name}</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    <button onClick={()=>setHnSickM(true)} style={{padding:"12px 8px",borderRadius:12,border:`1px solid ${C.danger}44`,background:"rgba(255,70,70,0.08)",color:C.danger,fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"/></svg>
                      <span>Absent today</span>
                    </button>
                    <button onClick={()=>setLatM2(selectedPerson)} style={{padding:"12px 8px",borderRadius:12,border:`1px solid ${C.warn}44`,background:"rgba(245,197,24,0.08)",color:C.warn,fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      <span>Came in late</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Sub-tabs */}
              <div style={{display:"flex",gap:2,marginBottom:14,borderBottom:`1px solid ${C.border}`}}>
                {[["today","Today"],["absent","Off Today"],["lateness","Issues"+(todayOT.length>0?` (${todayOT.length})`:"")],["rota","Rota"]].map(([id,label])=>(
                  <button key={id} onClick={()=>setSiteSubTab(id)} style={{background:"transparent",border:"none",padding:"8px 12px",cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:600,color:siteSubTab===id?C.teal:C.mid,borderBottom:siteSubTab===id?`2px solid ${C.teal}`:"2px solid transparent",whiteSpace:"nowrap"}}>
                    {label}
                  </button>
                ))}
              </div>

              {siteSubTab==="today"&&(()=>{
                const dr = buildDayRota(new Date().toISOString().split("T")[0], nurse.site, siteNurses, state);
                return (
                  <div>
                    {dr.pairings.length===0
                      ? <div style={{...CS,textAlign:"center",padding:32,color:C.dim}}>No sessions today</div>
                      : dr.pairings.map((p,i)=>(
                        <div key={i} style={{...CS,marginBottom:8,padding:"12px 14px",borderLeft:`3px solid ${p.nurse?p.autoAssigned?C.warn:C.green:C.danger}`}}>
                          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:p.nurse?4:0}}>
                            <div style={{width:9,height:9,borderRadius:99,background:p.clinician.color,flexShrink:0}}/>
                            <span style={{fontWeight:700,color:p.clinician.color,fontSize:13}}>{p.clinician.name}</span>
                            <span style={{fontSize:10,color:C.dim,marginLeft:"auto"}}>{p.clinStart}–{p.clinEnd}</span>
                          </div>
                          {p.nurse
                            ? <div style={{fontSize:12,color:p.autoAssigned?C.warn:C.mid,paddingLeft:17}}>{p.autoAssigned?"⚡ ":""}{p.nurse.name}</div>
                            : <div style={{fontSize:12,color:C.danger,fontWeight:700,paddingLeft:17}}>No nurse assigned</div>}
                        </div>
                      ))
                    }
                    {dr.spares.length>0&&(
                      <div style={{background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:10,padding:"10px 14px"}}>
                        <div style={{fontSize:11,fontWeight:700,color:C.green,marginBottom:4}}>Spare today</div>
                        {dr.spares.map(n=><div key={n.id} style={{fontSize:12,color:C.text}}>{n.name}</div>)}
                      </div>
                    )}
                  </div>
                );
              })()}

              {siteSubTab==="absent"&&(
                <div>
                  {siteOffToday.length===0
                    ? <div style={{...CS,textAlign:"center",padding:32,color:C.dim}}><div style={{fontSize:28,marginBottom:8}}></div>Everyone in today</div>
                    : siteOffToday.map(p=>{
                      const reason = getAbsenceReason(state,p.id,new Date().toISOString().split("T")[0])||"Absent";
                      return (
                        <div key={p.id} style={{...CS,marginBottom:8,padding:"12px 14px",display:"flex",gap:12,alignItems:"center"}}>
                          <span style={{fontSize:22}}>{REASON_ICO(reason)}</span>
                          <div>
                            <div style={{fontWeight:700,fontSize:13,color:C.text}}>{p.name}</div>
                            <div style={{fontSize:11,color:C.dim,marginTop:2}}>{reason}</div>
                          </div>
                        </div>
                      );
                    })
                  }
                </div>
              )}

              {siteSubTab==="lateness"&&(
                <div>
                  {todayOT.length>0&&(
                    <div style={{marginBottom:16}}>
                      <div style={{fontSize:10,fontWeight:700,color:C.warn,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>⏰ Overtime Pending Approval ({todayOT.length})</div>
                      {todayOT.map(r=>(
                        <div key={r.id} style={{...CS,marginBottom:6,padding:"12px 14px",borderLeft:`3px solid ${C.warn}`}}>
                          <div style={{fontWeight:700,fontSize:13,color:C.text,marginBottom:2}}>{r.name}</div>
                          <div style={{fontSize:11,color:C.dim}}>{r.date} · {r.hrs}h · {r.reason}</div>
                          <div style={{fontSize:10,color:C.muted,marginTop:2}}>Awaiting manager approval</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Recent Lateness & Issues</div>
                  {todayLate.length===0
                    ? <div style={{...CS,textAlign:"center",padding:24,color:C.dim}}>No issues logged yet</div>
                    : todayLate.map(r=>(
                      <div key={r.id} style={{...CS,marginBottom:6,padding:"10px 14px",borderLeft:`3px solid ${C.danger}`}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                          <div style={{fontWeight:700,fontSize:12,color:C.text}}>{r.name}</div>
                          <span style={{fontSize:10,color:C.dim}}>{r.date}</span>
                        </div>
                        <div style={{fontSize:11,color:C.mid,marginTop:2}}>
                          {r.type==="late"?`${r.minsLate} mins late`:"Unauthorised absence"} · {r.reason}
                        </div>
                        <div style={{fontSize:10,color:C.muted,marginTop:1}}>Logged by {r.loggedBy}</div>
                      </div>
                    ))
                  }
                </div>
              )}

              {siteSubTab==="rota"&&<RotaView siteId={nurse.site} state={state} set={set} canOverride={true}/>}

              {latM2&&<LatenessModal person={latM2} user={user} onSave={r=>{set(s=>({...s,latenessLog:[...(s.latenessLog||[]),r]}));setLatM2(null);}} onClose={()=>setLatM2(null)}/>}
              {hnSickM&&<HnAbsenceModal
                allSiteStaff={allSiteStaff}
                latPerson={latPerson}
                setLatPerson={setLatPerson}
                nurseName={nurse.name}
                siteName={sd?.name}
                onSave={r=>{set(s=>({...s,sick:[...(s.sick||[]),{...r,loggedBy:nurse.name}]}));setHnSickM(false);}}
                onClose={()=>setHnSickM(false)}
              />}
            </div>
          );
        })()}
      </div>

      {/* Bottom nav */}
      <div style={{position:"fixed",bottom:0,left:"50%",transform:"translateX(-50%)",width:"100%",maxWidth:440,background:C.surface,borderTop:`1px solid ${C.border}`,display:"flex",zIndex:100}}>
        {TABS.map(t=>{
          const iconMap={home:"home",plane:"plane",cal:"calendar",users:"users"};
          const iName=iconMap[t.icon]||t.icon;
          const ic=tab===t.id?"#00e5a0":C.dim;
          return (
            <button key={t.id} onClick={()=>setTab(t.id)}
              style={{flex:1,padding:"9px 0 11px",background:"transparent",
              border:"none",borderTop:tab===t.id?"2px solid #00e5a0":"2px solid transparent",
              cursor:"pointer",fontFamily:"inherit",display:"flex",
              flexDirection:"column",alignItems:"center",gap:2,
              position:"relative",transition:"border-color 0.15s"}}>
              <Icon name={iName} size={20} color={ic}/>
              <span style={{fontSize:10,fontWeight:600,color:ic}}>{t.label}</span>
              {t.badge>0&&<span style={{position:"absolute",top:6,right:"25%",background:C.danger,color:"#fff",borderRadius:99,fontSize:8,fontWeight:800,padding:"1px 5px"}}>{t.badge}</span>}
            </button>
          );
        })}
      </div>

      {leaveM&&<LeaveModal person={nurse} state={state} onSubmit={submit} onClose={()=>setLeaveM(false)}/>}
      {swapM&&<ShiftSwapModal person={nurse} state={state} set={set} onClose={()=>setSwapM(false)}/>}
      {swapM&&<ShiftSwapModal person={nurse} state={state} set={set} onClose={()=>setSwapM(false)}/>}
      {swapM&&<ShiftSwapModal person={nurse} state={state} set={set} onClose={()=>setSwapM(false)}/>}
      {otM&&<OvertimeModal person={nurse} state={state} onSave={r=>{set(s=>({...s,overtime:[...(s.overtime||[]),r]}));setOtM(false);}} onClose={()=>setOtM(false)}/>}
      {efM&&<EarlyFinishModal person={nurse} onSave={r=>{set(s=>({...s,earlyFinish:[...(s.earlyFinish||[]),r]}));setEfM(false);}} onClose={()=>setEfM(false)}/>}
      {extraM&&(
        <Overlay onClose={()=>setExtraM(false)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Request Extra Shift</div>
          <div style={{fontSize:12,color:C.dim,marginBottom:16}}>Flag a day you're available for an extra shift. Your manager will be notified.</div>
          <ExtraShiftForm person={nurse} state={state} onSave={r=>{set(s=>({...s,extraShiftRequests:[...(s.extraShiftRequests||[]),r]}));setExtraM(false);}} onClose={()=>setExtraM(false)}/>
        </Overlay>
      )}
      {unpaidM&&(
        <Overlay onClose={()=>setUnpaidM(false)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Request Unpaid Leave</div>
          <div style={{fontSize:12,color:C.dim,marginBottom:16}}>This will be reviewed by your manager. Frequent unpaid leave requests may trigger a welfare conversation.</div>
          <UnpaidLeaveForm person={nurse} state={state} onSave={r=>{set(s=>({...s,unpaidLeaveRequests:[...(s.unpaidLeaveRequests||[]),r]}));setUnpaidM(false);}} onClose={()=>setUnpaidM(false)}/>
        </Overlay>
      )}
      {goodWorkM&&<GoodWorkModal person={nurse} onSave={note=>{set(s=>({...s,goodWorkLogs:[...(s.goodWorkLogs||[]),{id:"gw"+Date.now(),staffId:nurse.id,name:nurse.name,site:nurse.site,loggedBy:nurse.name,date:new Date().toISOString().slice(0,10),note,status:"pending"}]}));setGoodWorkM(false);}} onClose={()=>setGoodWorkM(false)}/>}
      {issueM&&<IssueReportingModal person={nurse} set={set} onClose={()=>setIssueM(false)}/>}
      {extraPatientsM&&<ExtraPatientsModal person={nurse} onSave={r=>{set(s=>({...s,extraPatients:[...(s.extraPatients||[]),{id:"ep"+Date.now(),staffId:nurse.id,name:nurse.name,site:nurse.site,...r,loggedBy:nurse.name}]}));setExtraPatientsM(false);}} onClose={()=>setExtraPatientsM(false)}/>}
    </div>
  );
}

/* ══════════════════════════════════
   REPORTS
══════════════════════════════════ */
function QuestionLog({state}) {
  const [expandedId, setExpandedId] = useState(null);
  const [filterUser, setFilterUser] = useState("all");
  const logs = [...(state.chatLogs||[])].reverse();
  const allUsers = [...new Set((state.chatLogs||[]).map(l=>l.userName))];
  const filtered = filterUser==="all" ? logs : logs.filter(l=>l.userName===filterUser);
  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
        <div style={{fontSize:12,color:C.dim}}>{filtered.length} AI interactions logged · Full conversation history</div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          <button onClick={()=>setFilterUser("all")} style={{padding:"4px 12px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${filterUser==="all"?C.teal:C.border}`,background:filterUser==="all"?C.tealPale:"transparent",color:filterUser==="all"?C.teal:C.mid}}>All</button>
          {allUsers.map(u=>(
            <button key={u} onClick={()=>setFilterUser(u)} style={{padding:"4px 12px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${filterUser===u?C.teal:C.border}`,background:filterUser===u?C.tealPale:"transparent",color:filterUser===u?C.teal:C.mid}}>{u}</button>
          ))}
        </div>
      </div>
      {filtered.length===0?(
        <div style={{...CS,padding:32,textAlign:"center",color:C.dim,fontSize:12}}>No AI interactions logged yet. Interactions are recorded when Iris is used.</div>
      ):(
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {filtered.slice(0,100).map(log=>{
            const isOpen=expandedId===log.id;
            const siteInfo=SITES[log.site]||{name:log.site,color:C.teal};
            return (
              <div key={log.id} style={{...CS,padding:"12px 16px",cursor:"pointer"}} onClick={()=>setExpandedId(isOpen?null:log.id)}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                      <span style={{fontWeight:700,fontSize:12,color:C.heading}}>{log.userName}</span>
                      <Pill c={siteInfo.color} t={siteInfo.name} sm/>
                      <span style={{fontSize:10,color:C.dim}}>{log.role}</span>
                    </div>
                    <div style={{fontSize:12,color:C.text,marginBottom:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:isOpen?"normal":"nowrap"}}>
                      <span style={{fontWeight:600,color:C.dim}}>Q: </span>{log.userMsg}
                    </div>
                    {isOpen&&<div style={{fontSize:12,color:C.mid,marginTop:8,padding:"8px 10px",background:C.bg,borderRadius:8,whiteSpace:"pre-wrap",lineHeight:1.6}}>
                      <span style={{fontWeight:600,color:C.teal}}>A: </span>{log.aiReply}
                    </div>}
                  </div>
                  <div style={{textAlign:"right",flexShrink:0}}>
                    <div style={{fontSize:10,color:C.dim}}>{log.date}</div>
                    {log.time&&<div style={{fontSize:10,color:C.muted}}>{log.time}</div>}
                    {log.tokensIn&&<div style={{fontSize:9,color:C.muted,marginTop:2}}>{(log.tokensIn+(log.tokensOut||0)).toLocaleString()} tok</div>}
                  </div>
                </div>
              </div>
            );
          })}
          {filtered.length>100&&<div style={{textAlign:"center",fontSize:11,color:C.dim,padding:12}}>Showing 100 most recent. {filtered.length-100} older entries not shown.</div>}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════
   HOURS & CONTRACTS MODULE
   Scheduled vs contracted, OT patterns,
   unpaid leave, contract risk alerts,
   weekly manager confirmation log
══════════════════════════════════ */
function HoursContracts({state, set, isPrincipal, user}) {
  const [tab, setTab] = useState("overview"); // overview|risks|unpaid|weeklog
  const today = new Date().toISOString().split("T")[0];
  const weekStart = (() => {
    const d = new Date(); d.setDate(d.getDate()-d.getDay()+1); return d.toISOString().split("T")[0];
  })();

  return (
    <div>
      <div style={{fontFamily:"'Instrument Serif',serif",fontSize:22,color:C.heading,fontWeight:400,marginBottom:4}}>⏱ Hours & Contracts</div>
      <div style={{fontSize:12,color:C.dim,marginBottom:16}}>Monitor scheduled hours, contract risks, and offer unpaid leave.</div>

      <div style={{display:"flex",gap:0,borderBottom:`1px solid ${C.border}`,marginBottom:20,overflowX:"auto"}}>
        {[["overview","Hours Overview"],["risks","Contract Risks"],["unpaid","Unpaid Leave"],["weeklog","Weekly Log"]].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} style={{background:"none",border:"none",fontFamily:"inherit",fontSize:13,fontWeight:600,
            color:tab===id?C.teal:C.mid,cursor:"pointer",padding:"8px 16px",whiteSpace:"nowrap",
            borderBottom:tab===id?`2px solid ${C.teal}`:"2px solid transparent",flexShrink:0}}>
            {label}
          </button>
        ))}
      </div>

      {tab==="overview"&&<HoursOverview state={state} set={set} isPrincipal={isPrincipal} user={user}/>}
      {tab==="risks"&&<ContractRisks state={state} isPrincipal={isPrincipal}/>}
      {tab==="unpaid"&&<UnpaidLeaveManager state={state} set={set} isPrincipal={isPrincipal} user={user}/>}
      {tab==="weeklog"&&<WeeklyHoursLog state={state} set={set} user={user} isPrincipal={isPrincipal}/>}
    </div>
  );
}

/* Hours Overview — scheduled vs contracted per person */
function HoursOverview({state, isPrincipal, user}) {
  const [siteF, setSiteF] = useState(fallbackSite(user));
  const [period, setPeriod] = useState("month"); // week|month|quarter

  const today = new Date().toISOString().split("T")[0];

  // Get date range for period
  function getPeriodDates() {
    const now = new Date();
    if (period==="week") {
      const start = new Date(now); start.setDate(now.getDate()-now.getDay()+1);
      const end = new Date(start); end.setDate(start.getDate()+4);
      return {from:start.toISOString().split("T")[0], to:end.toISOString().split("T")[0], label:"This week"};
    }
    if (period==="month") {
      const start = new Date(now.getFullYear(),now.getMonth(),1);
      const end = new Date(now.getFullYear(),now.getMonth()+1,0);
      return {from:start.toISOString().split("T")[0], to:end.toISOString().split("T")[0], label:now.toLocaleDateString("en-GB",{month:"long"})};
    }
    // quarter = rolling 12 weeks
    const start = new Date(now); start.setDate(now.getDate()-84);
    return {from:start.toISOString().split("T")[0], to:today, label:"Rolling 12 weeks"};
  }

  const {from, to, label} = getPeriodDates();

  // Calculate hours from actual rota data for a person over the date range
  // Sources: clinician session times (pairings), spare tasks, CPD events
  // Falls back to contracted÷days if no rota data available for a day
  function getScheduledHours(person) {
    const toMins = t=>{if(!t)return 0;const[h,m]=(t+":00").split(":").map(Number);return h*60+(m||0);};
    const workDays = CAL.days.filter(d=>d.date>=from&&d.date<=to&&!d.bh&&!d.isWeekend);
    const nurses = getNursesForSite(state, siteF);
    let scheduled = 0;
    let rotaDays = 0; // days where we have actual rota data
    let spareDays = 0; // in but not assigned

    workDays.forEach(d=>{
      if (isAbsent(state, person.id, d.date)) return;
      if (person.workDays && !person.workDays.includes(d.dow)) return;

      // 1. Check if paired to a clinician on this day
      const dr = buildDayRota(d.date, siteF, nurses, state);
      const pairing = dr.pairings.find(p=>p.nurse?.id===person.id);
      if (pairing && pairing.clinStart && pairing.clinEnd) {
        if (pairing.hasLunch && pairing.lunchStart && pairing.lunchEnd) {
          // Split day: AM + PM. Lunch gap is excluded unless lunchPaid is true.
          const amMins = Math.max(0, toMins(pairing.lunchStart) - toMins(pairing.clinStart));
          const pmMins = Math.max(0, toMins(pairing.clinEnd) - toMins(pairing.lunchEnd));
          const bp = state.settings?.breakPolicy||{};
          const lunchMins = bp.lunchPaid ? Math.max(0, toMins(pairing.lunchEnd) - toMins(pairing.lunchStart)) : 0;
          scheduled += (amMins + pmMins + lunchMins) / 60;
        } else {
          const mins = Math.max(0, toMins(pairing.clinEnd) - toMins(pairing.clinStart));
          scheduled += mins / 60;
        }
        rotaDays++;
        return;
      }

      // 2. Check if assigned a spare task on this day
      const task = state.spareTasks?.[`${d.date}_${person.id}`];
      if (task) {
        // Task has duration if set, otherwise count as contracted daily hours
        const taskHrs = task.hrs || (person.hrs||37.5) / (person.workDays?.length||5);
        scheduled += taskHrs;
        rotaDays++;
        return;
      }

      // 3. Check if on CPD on this day
      const cpd = (state.cpdEvents||[]).find(e=>e.date===d.date&&(e.staffIds||[]).includes(person.id));
      if (cpd) {
        if (cpd.startTime && cpd.endTime) {
          const mins = Math.max(0, toMins(cpd.endTime) - toMins(cpd.startTime));
          scheduled += mins / 60;
        } else {
          // All-day CPD = contracted daily hours
          scheduled += (person.hrs||37.5) / (person.workDays?.length||5);
        }
        rotaDays++;
        return;
      }

      // 4. Spare — in but not assigned. Count as contracted daily hours, flag as spare
      if (dr.spares.find(s=>s.id===person.id)) {
        scheduled += (person.hrs||37.5) / (person.workDays?.length||5);
        spareDays++;
        rotaDays++;
        return;
      }

      // 5. No rota data — fall back to contracted÷days
      scheduled += (person.hrs||37.5) / (person.workDays?.length||5);
    });

    const ot = (state.overtime||[]).filter(o=>o.staffId===person.id&&o.date>=from&&o.date<=to);
    const otHrs = ot.reduce((a,o)=>a+(o.hrs||0),0);
    const ef = (state.earlyFinish||[]).filter(e=>e.staffId===person.id&&e.date>=from&&e.date<=to);
    const efHrs = ef.reduce((a,e)=>a+(e.hrsEarly||e.hrs||0),0);

    // Setup & tidy time — added to each worked day
    const setupMins = state.settings?.setupMins||0;
    const tidyMins  = state.settings?.tidyMins||0;
    const setupHrs  = (setupMins+tidyMins)/60 * rotaDays;

    // Break deduction — subtract unpaid break from days where shift > threshold
    const bp = state.settings?.breakPolicy||{};
    const breakPaid = bp.paid||false;
    const breakMins = bp.durationMins||30;
    const breakThreshold = bp.thresholdHrs||6;
    let breakDeductHrs = 0;
    if (!breakPaid) {
      // Count days where scheduled daily hours exceed threshold
      workDays.forEach(d=>{
        if (isAbsent(state,person.id,d.date)) return;
        if (person.workDays&&!person.workDays.includes(d.dow)) return;
        const dailyHrs = (person.hrs||37.5)/(person.workDays?.length||5);
        if (dailyHrs > breakThreshold) breakDeductHrs += breakMins/60;
      });
    }

    const fromRota = rotaDays > 0;
    return {
      scheduled: Math.round((scheduled+otHrs-efHrs+setupHrs-breakDeductHrs)*10)/10,
      otHrs, efHrs, setupHrs: Math.round(setupHrs*10)/10,
      breakDeductHrs: Math.round(breakDeductHrs*10)/10,
      workDays: workDays.length,
      spareDays,
      fromRota,
    };
  }

  // Get contracted hours for the period
  function getContractedHours(person) {
    const days = CAL.days.filter(d=>d.date>=from&&d.date<=to&&!d.bh&&!d.isWeekend).length;
    return Math.round(((person.hrs||37.5)/(person.workDays?.length||5))*days*10)/10;
  }

  const allStaff = [
    ...getNursesForSite(state, siteF),
    ...getRecsForSite(state, siteF, false),
  ];

  const staffData = allStaff.map(s=>{
    const {scheduled, otHrs, efHrs, spareDays, fromRota} = getScheduledHours(s);
    const contracted = getContractedHours(s);
    const diff = Math.round((scheduled-contracted)*10)/10;
    const pct = contracted>0 ? Math.round((scheduled/contracted)*100) : 100;
    const rag = pct>120?"red":pct>110?"amber":"green";
    return {person:s, scheduled, contracted, diff, pct, rag, otHrs, efHrs, spareDays, fromRota};
  }).sort((a,b)=>b.pct-a.pct);

  const ragColor = {green:C.green, amber:C.warn, red:C.danger};

  return (
    <div>
      <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
        <div style={{display:"flex",gap:4}}>
          {Object.entries(SITES).map(([id,s])=>(
            <button key={id} onClick={()=>setSiteF(id)} style={{padding:"5px 14px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",
              border:`1px solid ${siteF===id?s.color:C.border}`,background:siteF===id?s.color+"18":"transparent",color:siteF===id?s.color:C.mid}}>{s.name}</button>
          ))}
        </div>
        <div style={{display:"flex",gap:4,marginLeft:"auto"}}>
          {[["week","This week"],["month","This month"],["quarter","12 weeks"]].map(([v,l])=>(
            <button key={v} onClick={()=>setPeriod(v)} style={{padding:"5px 14px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",
              border:`1px solid ${period===v?C.teal:C.border}`,background:period===v?C.tealPale:"transparent",color:period===v?C.teal:C.mid}}>{l}</button>
          ))}
        </div>
      </div>

      <div style={{fontSize:11,color:C.dim,marginBottom:12}}>
        Scheduled vs contracted hours — {label}. Where shift times are set on a staff record, actual times are used. Otherwise estimated from contracted hours ÷ 5 days.
      </div>

      {allStaff.some(s=>!getScheduledHours(s).fromRota)&&(
        <div style={{background:"rgba(245,197,24,0.07)",border:"1px solid rgba(245,197,24,0.2)",borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:11,color:C.warn}}>
          ⚠ Some staff show <strong>Estimated</strong> hours — the rota hasn't been built yet for this period, or those staff aren't paired to any clinician. Hours are estimated from contracted hours ÷ working days.
        </div>
      )}

      <div style={{overflowX:"auto"}}>
        <table style={{borderCollapse:"collapse",width:"100%",minWidth:600}}>
          <thead>
            <tr>{["Name","Contract","Scheduled","Diff","OT","Source","Status / Action"].map(h=>(
              <th key={h} style={{padding:"6px 10px",fontSize:9,fontWeight:700,color:C.dim,textAlign:"left",textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:`1px solid ${C.border}`}}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {staffData.map(({person,contracted,scheduled,diff,pct,rag,otHrs,spareDays,fromRota})=>{
              const under = diff < -0.5;
              const over  = diff > 0.5;
              return (
              <tr key={person.id} style={{background:under?"rgba(58,173,152,0.03)":over?"rgba(245,197,24,0.03)":"transparent"}}>
                <td style={{padding:"8px 10px",fontSize:12,fontWeight:600,color:C.text,borderBottom:`1px solid ${C.border2}`}}>
                  {person.name}
                  {person.hrs&&person.hrs<20&&<Pill c={C.dim} t="flexible" sm/>}
                </td>
                <td style={{padding:"8px 10px",fontSize:12,color:C.mid,borderBottom:`1px solid ${C.border2}`}}>{contracted}h</td>
                <td style={{padding:"8px 10px",fontSize:12,fontWeight:600,color:C.text,borderBottom:`1px solid ${C.border2}`}}>
                  {scheduled}h
                  {spareDays>0&&<span style={{fontSize:9,color:C.warn,marginLeft:4}}>({spareDays}d spare)</span>}
                </td>
                <td style={{padding:"8px 10px",fontSize:12,fontWeight:700,borderBottom:`1px solid ${C.border2}`,
                  color:over?C.warn:under?C.teal:C.green}}>
                  {diff>0?"+":""}{diff}h
                </td>
                <td style={{padding:"8px 10px",fontSize:11,color:otHrs>0?C.warn:C.muted,borderBottom:`1px solid ${C.border2}`}}>
                  {otHrs>0?`+${otHrs}h OT`:"—"}
                </td>
                <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.border2}`}}>
                  <span style={{fontSize:9,color:fromRota?C.teal:C.muted,background:fromRota?C.tealPale:"rgba(255,255,255,0.04)",borderRadius:4,padding:"2px 6px",fontWeight:700}}>
                    {fromRota?"Rota":"Estimated"}
                  </span>
                </td>
                <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.border2}`}}>
                  {over&&(
                    <span style={{fontSize:10,fontWeight:700,color:C.warn,background:C.warn+"15",borderRadius:99,padding:"2px 8px"}}>
                      {pct>120?"⚠ Over contract":"Slightly over"}
                    </span>
                  )}
                  {under&&(
                    <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                      <span style={{fontSize:10,fontWeight:700,color:C.teal,background:C.tealPale,borderRadius:99,padding:"2px 8px"}}>
                        {Math.abs(diff)}h spare
                      </span>
                      <button onClick={()=>window.dispatchEvent(new CustomEvent("omni:navigate",{detail:{tab:"rota",subTab:"cpd"}}))}
                        style={{fontSize:9,padding:"2px 7px",borderRadius:6,border:`1px solid ${C.teal}44`,background:"transparent",color:C.teal,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>CPD</button>
                      <button onClick={()=>window.dispatchEvent(new CustomEvent("omni:navigate",{detail:{tab:"rota",subTab:"adhoc"}}))}
                        style={{fontSize:9,padding:"2px 7px",borderRadius:6,border:`1px solid ${C.brook}44`,background:"transparent",color:C.brook,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>Ad hoc</button>
                    </div>
                  )}
                  {!over&&!under&&(
                    <span style={{fontSize:10,fontWeight:700,color:C.green,background:C.green+"15",borderRadius:99,padding:"2px 8px"}}>✓ On track</span>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{marginTop:10,fontSize:11,color:C.dim}}>
        Spare hours → assign CPD, admin tasks, or ad hoc clinics to make use of contracted time. Over contract → log as overtime in Attendance tab.
      </div>
    </div>
  );
}

/* Contract Risk Alerts */
function ContractRisks({state, isPrincipal}) {
  const today = new Date().toISOString().split("T")[0];
  const allNurses = (state.nurses||NURSES_BASE).filter(n=>!n.maternity&&!n.archived);
  const allRecs = [...RECS_BASE,...(state.customRecs||[]).filter(r=>!r.archived)];
  const allStaff = [...allNurses,...allRecs];
  const risks = [];

  // 1. Safaa minimum wage check
  allNurses.filter(n=>n.pay&&n.pay<11.44).forEach(n=>{
    risks.push({level:"critical", person:n, title:"Below minimum wage", detail:`${n.name} is on £${n.pay}/hr — national minimum wage is £11.44 (April 2024+). Urgent review required.`, action:"Review pay rate in Staff Records"});
  });
  // Also check the known Safaa issue from briefing
  const safaa = allNurses.find(n=>n.id==="n_shussain");
  if (safaa && !safaa.pay) {
    risks.push({level:"critical", person:safaa, title:"Pay rate not confirmed", detail:"Safaa Hussain's pay rate is unconfirmed — last known £7.55/hr which is below minimum wage (£11.44). Urgent verification needed.", action:"Set pay rate in Staff Records"});
  }

  // 2. Flexible staff working consistent pattern (8+ weeks)
  allStaff.filter(s=>s.hrs&&s.hrs<=20).forEach(s=>{
    // Count weeks with leave or OT activity (proxy for working)
    const recentWeeks = [];
    for (let w=0; w<10; w++) {
      const wStart = new Date(); wStart.setDate(wStart.getDate()-(w+1)*7);
      const wEnd = new Date(wStart); wEnd.setDate(wStart.getDate()+4);
      const wStartStr = wStart.toISOString().split("T")[0];
      const wEndStr = wEnd.toISOString().split("T")[0];
      const hasActivity = (state.overtime||[]).some(o=>o.staffId===s.id&&o.date>=wStartStr&&o.date<=wEndStr) ||
        (state.leave[s.id]||[]).some(l=>l.date>=wStartStr&&l.date<=wEndStr);
      recentWeeks.push(hasActivity);
    }
    const consecutiveActive = recentWeeks.filter(Boolean).length;
    if (consecutiveActive>=8) {
      risks.push({level:"warning", person:s, title:"Flexible staff — regular pattern forming", detail:`${s.name} (${s.hrs}h contracted) has been active for ${consecutiveActive} of the last 10 weeks. Consistent patterns may create implied regular hours rights.`, action:"Consider a contract review or formal hours agreement"});
    }
  });

  // 3. Staff who started this year with no start date — affects pro-rated holiday entitlement
  // Only flag if they have ZERO leave balance history (genuinely new, not just missing data)
  const thisYear = new Date().getFullYear().toString();
  allNurses.filter(n=>!n.startDate).forEach(n=>{
    const hasAnyLeave = ((state.leave||{})[n.id]||[]).length > 0;
    const hasHistoricalLeave = ((state.historicalLeave||{})[n.id]||0) > 0;
    // Only flag if they've never taken leave — likely a genuine new starter missing their date
    if (!hasAnyLeave && !hasHistoricalLeave) {
      risks.push({level:"info", person:n, title:"Start date missing", detail:`${n.name} has no start date set. If they started in ${thisYear}, this affects their pro-rated holiday entitlement for the year.`, action:"Add start date in Staff Records → Edit"});
    }
  });

  // 4. Contracted hours mismatches (people with hours history changes not reflected)
  allStaff.filter(s=>s.hrsHistory&&s.hrsHistory.length>1).forEach(s=>{
    const latest = [...s.hrsHistory].sort((a,b)=>b.from.localeCompare(a.from))[0];
    if (latest.hrs!==s.hrs) {
      risks.push({level:"warning", person:s, title:"Hours history mismatch", detail:`${s.name}: current contract shows ${s.hrs}h but latest history entry is ${latest.hrs}h from ${latest.from}.`, action:"Verify contract hours in Staff Records"});
    }
  });

  const levelColor = {critical:C.danger, warning:C.warn, info:C.teal};
  const levelBg    = {critical:"rgba(255,70,70,0.08)", warning:"rgba(245,197,24,0.08)", info:"rgba(58,173,152,0.08)"};
  const levelLabel = {critical:"🔴 Critical", warning:"🟡 Review", info:"ℹ️ Info"};

  return (
    <div>
      <div style={{fontSize:13,fontWeight:700,color:C.heading,marginBottom:4}}>Contract & Compliance Risks</div>
      <div style={{fontSize:11,color:C.dim,marginBottom:16}}>Auto-detected from staff records and rota data. These are signals — always verify before acting.</div>

      {risks.length===0&&(
        <div style={{...CS,textAlign:"center",padding:40}}>
          <div style={{fontSize:28,marginBottom:8}}>✅</div>
          <div style={{fontSize:14,fontWeight:600,color:C.green}}>No contract risks detected</div>
          <div style={{fontSize:11,color:C.dim,marginTop:4}}>Keep staff records up to date to ensure accurate monitoring</div>
        </div>
      )}

      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {["critical","warning","info"].map(level=>{
          const levelRisks = risks.filter(r=>r.level===level);
          if (levelRisks.length===0) return null;
          return levelRisks.map((r,i)=>(
            <div key={r.person.id+i} style={{...CS,padding:"14px 16px",borderLeft:`4px solid ${levelColor[level]}`,background:levelBg[level],cursor:"pointer"}}
              onClick={()=>window.dispatchEvent(new CustomEvent("omni:navigate",{detail:{tab:"admin",subTab:"directory"}}))}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8,flexWrap:"wrap"}}>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                    <span style={{fontSize:10,fontWeight:800,color:levelColor[level],textTransform:"uppercase",letterSpacing:"0.06em"}}>{levelLabel[level]}</span>
                    <span style={{fontSize:13,fontWeight:700,color:C.heading}}>{r.title}</span>
                  </div>
                  <div style={{fontSize:12,color:C.soft,marginBottom:6,lineHeight:1.5}}>{r.detail}</div>
                  <div style={{fontSize:11,color:C.teal,fontWeight:600}}>→ {r.action} <span style={{fontSize:10,color:C.dim,marginLeft:4}}>(tap to open Staff Records)</span></div>
                </div>
                <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:4}}>
                  <span style={{fontSize:12,fontWeight:600,color:C.mid,whiteSpace:"nowrap"}}>{r.person.name}</span>
                  {r.person.site&&<span style={{fontSize:10,color:SITES[r.person.site]?.color||C.dim}}>{SITES[r.person.site]?.name||r.person.site}</span>}
                </div>
              </div>
            </div>
          ));
        })}
      </div>

      <div style={{marginTop:16,padding:"10px 14px",background:"rgba(139,92,246,0.08)",borderRadius:10,border:"1px solid #e9d5ff",fontSize:11,color:"#a78bfa"}}>
        🔮 <strong>Pulse Insights</strong> will automatically detect overtime patterns, predict burnout risk, and flag Working Time Directive exposure once 3+ months of rota data is available.
      </div>
    </div>
  );
}

/* Unpaid Leave Manager */
function UnpaidLeaveManager({state, set, isPrincipal, user}) {
  const today = new Date().toISOString().split("T")[0];
  const [addM, setAddM] = useState(false);
  const [f, setF] = useState({site:fallbackSite(user), from:"", to:"", note:"", staffIds:[]});
  const sf = (k,v) => setF(p=>({...p,[k]:v}));
  const unpaidOffers = state.unpaidLeaveOffers||[];

  const siteStaff = (id) => [
    ...(state.nurses||NURSES_BASE).filter(n=>n.site===id&&!n.maternity&&!n.archived),
    ...RECS_BASE.filter(r=>r.site===id&&!r.archived),
  ];

  function saveOffer() {
    if (!f.from||!f.to||!f.note.trim()) return;
    const staff = f.staffIds.length>0 ? f.staffIds : siteStaff(f.site).map(s=>s.id);
    set(s=>({...s, unpaidLeaveOffers:[...(s.unpaidLeaveOffers||[]),{
      id:"upl_"+Date.now(), site:f.site, from:f.from, to:f.to,
      note:f.note, staffIds:staff, offeredBy:user?.name||"Manager",
      offeredAt:new Date().toISOString(), responses:{},
    }]}));
    setAddM(false); setF({site:fallbackSite(user),from:"",to:"",note:"",staffIds:[]});
  }

  function respond(offerId, staffId, response) {
    set(s=>({...s, unpaidLeaveOffers:(s.unpaidLeaveOffers||[]).map(o=>
      o.id===offerId ? {...o, responses:{...o.responses,[staffId]:response}} : o
    )}));
  }

  const upcoming = unpaidOffers.filter(o=>o.to>=today).sort((a,b)=>a.from.localeCompare(b.from));
  const past = unpaidOffers.filter(o=>o.to<today);

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:C.heading}}>Unpaid Leave Offers</div>
          <div style={{fontSize:11,color:C.dim,marginTop:2}}>Offer voluntary unpaid leave to staff during quiet periods. Staff accept or decline via their portal.</div>
        </div>
        {(isPrincipal||user?.role==="manager")&&(
          <button onClick={()=>setAddM(true)} style={BTN(C.teal)}>+ New offer</button>
        )}
      </div>

      {addM&&(
        <Overlay onClose={()=>setAddM(false)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:16}}>Offer Unpaid Leave</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
            <div><label style={LB}>Site</label>
              <select value={f.site} onChange={e=>sf("site",e.target.value)} style={IS}>
                {Object.entries(SITES).map(([id,s])=><option key={id} value={id}>{s.name}</option>)}
              </select>
            </div>
            <div/>
            <div><label style={LB}>From</label><input type="date" value={f.from} onChange={e=>sf("from",e.target.value)} style={IS}/></div>
            <div><label style={LB}>To</label><input type="date" value={f.to} min={f.from} onChange={e=>sf("to",e.target.value)} style={IS}/></div>
          </div>
          <div style={{marginBottom:12}}>
            <label style={LB}>Message to staff</label>
            <input value={f.note} onChange={e=>sf("note",e.target.value)} placeholder="e.g. Quiet week — offering voluntary unpaid leave if anyone would like time off" style={IS}/>
          </div>
          <div style={{marginBottom:16,padding:"10px 14px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:10}}>
            <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:6}}>Who sees this offer?</div>
            <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:6}}>
              <button onClick={()=>sf("staffIds",[])} style={{...BTN(f.staffIds.length===0?C.teal:C.dim,f.staffIds.length>0),fontSize:10,padding:"4px 10px"}}>All site staff</button>
              {siteStaff(f.site).map(s=>(
                <button key={s.id} onClick={()=>sf("staffIds",f.staffIds.includes(s.id)?f.staffIds.filter(x=>x!==s.id):[...f.staffIds,s.id])}
                  style={{padding:"4px 10px",borderRadius:99,fontFamily:"inherit",fontSize:10,fontWeight:600,cursor:"pointer",
                    border:`1px solid ${f.staffIds.includes(s.id)?C.teal:C.border}`,background:f.staffIds.includes(s.id)?C.tealPale:"transparent",
                    color:f.staffIds.includes(s.id)?C.teal:C.mid}}>
                  {f.staffIds.includes(s.id)?"":""}{s.name.split(" ")[0]}
                </button>
              ))}
            </div>
            <div style={{fontSize:10,color:C.dim}}>
              {f.staffIds.length===0?"Offer sent to all staff at this site":`${f.staffIds.length} specific staff selected`}
            </div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={saveOffer} disabled={!f.from||!f.to||!f.note.trim()} style={{...BTN(C.teal),flex:1,padding:"11px 0",opacity:!f.from||!f.to||!f.note.trim()?0.4:1}}>Send offer</button>
            <button onClick={()=>setAddM(false)} style={BTN(C.dim,true)}>Cancel</button>
          </div>
        </Overlay>
      )}

      {upcoming.length===0&&past.length===0&&(
        <div style={{...CS,textAlign:"center",padding:40,color:C.dim}}>
          <div style={{fontSize:28,marginBottom:8}}>🏖</div>
          <div style={{fontSize:14,fontWeight:600,color:C.text}}>No unpaid leave offers yet</div>
          <div style={{fontSize:11,marginTop:4}}>Use this when you have a quiet period and want to offer voluntary unpaid leave</div>
        </div>
      )}

      {upcoming.map(offer=>{
        const all = (state.nurses||NURSES_BASE).filter(n=>offer.staffIds.includes(n.id)&&!n.archived);
        const accepted = Object.entries(offer.responses||{}).filter(([,v])=>v==="accepted").length;
        const declined = Object.entries(offer.responses||{}).filter(([,v])=>v==="declined").length;
        const pending = offer.staffIds.length - accepted - declined;
        return (
          <div key={offer.id} style={{...CS,padding:"14px 16px",marginBottom:8,borderLeft:`3px solid ${C.teal}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:8,marginBottom:8}}>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:C.text}}>
                  {new Date(offer.from+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short"})} – {new Date(offer.to+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short"})}
                  <span style={{marginLeft:8,fontSize:11,color:C.dim}}>· {SITES[offer.site]?.name}</span>
                </div>
                <div style={{fontSize:11,color:C.mid,marginTop:2}}>{offer.note}</div>
                <div style={{fontSize:10,color:C.dim,marginTop:2}}>Offered by {offer.offeredBy}</div>
              </div>
              <div style={{display:"flex",gap:8}}>
                <span style={{fontSize:11,fontWeight:700,color:C.green,background:C.green+"15",borderRadius:99,padding:"3px 10px"}}>{accepted} accepted</span>
                <span style={{fontSize:11,fontWeight:700,color:C.danger,background:C.danger+"15",borderRadius:99,padding:"3px 10px"}}>✕ {declined} declined</span>
                {pending>0&&<span style={{fontSize:11,fontWeight:700,color:C.dim,background:C.bg,borderRadius:99,padding:"3px 10px"}}>{pending} pending</span>}
              </div>
            </div>
            {/* Responses list */}
            <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
              {offer.staffIds.map(sid=>{
                const person = [...(state.nurses||NURSES_BASE),...RECS_BASE].find(s=>s.id===sid);
                if (!person) return null;
                const resp = offer.responses?.[sid];
                return (
                  <span key={sid} style={{fontSize:10,padding:"2px 8px",borderRadius:99,
                    background:resp==="accepted"?C.green+"15":resp==="declined"?C.danger+"15":C.bg,
                    color:resp==="accepted"?C.green:resp==="declined"?C.danger:C.dim,
                    border:`1px solid ${resp==="accepted"?C.green+"33":resp==="declined"?C.danger+"33":C.border}`,
                    fontWeight:600}}>
                    {person.name.split(" ")[0]} {resp==="accepted"?"✓":resp==="declined"?"✕":"…"}
                  </span>
                );
              })}
            </div>
          </div>
        );
      })}

      {past.length>0&&(
        <div style={{marginTop:16}}>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Past offers ({past.length})</div>
          {past.map(offer=>{
            const accepted = Object.entries(offer.responses||{}).filter(([,v])=>v==="accepted").length;
            return (
              <div key={offer.id} style={{...CS,padding:"10px 14px",marginBottom:6,opacity:0.7}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span style={{fontSize:12,fontWeight:600,color:C.text}}>
                    {new Date(offer.from+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short"})} – {new Date(offer.to+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short"})}
                    <span style={{marginLeft:8,fontSize:11,color:C.dim}}>· {SITES[offer.site]?.name}</span>
                  </span>
                  <span style={{fontSize:11,color:C.green}}>{accepted} took unpaid leave</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* Weekly Hours Confirmation Log */
function WeeklyHoursLog({state, set, user, isPrincipal}) {
  const today = new Date().toISOString().split("T")[0];
  const [site, setSite] = useState(fallbackSite(user));
  const [note, setNote] = useState("");
  const [saved, setSaved] = useState(false);

  // Get last 8 weeks
  const weeks = [];
  for (let w=0; w<8; w++) {
    const wEnd = new Date(); wEnd.setDate(wEnd.getDate()-(wEnd.getDay()||7)+5-w*7);
    const wStart = new Date(wEnd); wStart.setDate(wEnd.getDate()-4);
    weeks.push({
      start: wStart.toISOString().split("T")[0],
      end: wEnd.toISOString().split("T")[0],
      label: `${wStart.toLocaleDateString("en-GB",{day:"numeric",month:"short"})} – ${wEnd.toLocaleDateString("en-GB",{day:"numeric",month:"short"})}`,
    });
  }

  const currentWeek = weeks[0];
  const logs = state.weeklyHoursLog||[];
  const currentLog = logs.find(l=>l.site===site&&l.week===currentWeek.start);

  function confirm() {
    set(s=>({...s, weeklyHoursLog:[
      ...(s.weeklyHoursLog||[]).filter(l=>!(l.site===site&&l.week===currentWeek.start)),
      {site, week:currentWeek.start, confirmedBy:user?.name||"Manager", confirmedAt:new Date().toISOString(), note:note.trim()},
    ]}));
    setSaved(true); setTimeout(()=>setSaved(false), 2000);
    setNote("");
  }

  // Scheduled hours for this week per person
  const weekStaff = (state.nurses||NURSES_BASE).filter(n=>n.site===site&&!n.maternity&&!n.archived);
  const weekDays = CAL.days.filter(d=>d.date>=currentWeek.start&&d.date<=currentWeek.end&&!d.bh&&!d.isWeekend);

  return (
    <div>
      <div style={{fontSize:13,fontWeight:700,color:C.heading,marginBottom:4}}>Weekly Hours Confirmation</div>
      <div style={{fontSize:11,color:C.dim,marginBottom:16}}>
        Manager confirms team hours each week. Takes 30 seconds. Builds an audit trail and feeds Pulse Insights with actual vs scheduled data.
      </div>

      {/* Site picker */}
      <div style={{display:"flex",gap:4,marginBottom:16}}>
        {Object.entries(SITES).map(([id,s])=>(
          <button key={id} onClick={()=>setSite(id)} style={{padding:"5px 14px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",
            border:`1px solid ${site===id?s.color:C.border}`,background:site===id?s.color+"18":"transparent",color:site===id?s.color:C.mid}}>{s.name}</button>
        ))}
      </div>

      {/* Current week confirmation */}
      <div style={{...CS,padding:"16px",marginBottom:16,borderLeft:`3px solid ${currentLog?C.green:C.warn}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:8,marginBottom:10}}>
          <div>
            <div style={{fontSize:14,fontWeight:700,color:C.text}}>Week of {currentWeek.label}</div>
            <div style={{fontSize:11,color:C.dim,marginTop:2}}>
              {weekStaff.length} staff · {weekDays.length} working days
            </div>
          </div>
          {currentLog
            ? <span style={{fontSize:11,fontWeight:700,color:C.green,background:C.green+"15",borderRadius:99,padding:"4px 12px"}}>Confirmed by {currentLog.confirmedBy}</span>
            : <span style={{fontSize:11,fontWeight:700,color:C.warn,background:C.warn+"15",borderRadius:99,padding:"4px 12px"}}>Awaiting confirmation</span>
          }
        </div>

        {/* Quick hours summary */}
        <div style={{overflowX:"auto",marginBottom:10}}>
          <table style={{borderCollapse:"collapse",fontSize:11,width:"100%"}}>
            <thead><tr>
              <th style={{padding:"4px 8px",textAlign:"left",color:C.dim,fontSize:9,fontWeight:700,textTransform:"uppercase",borderBottom:`1px solid ${C.border}`}}>Name</th>
              <th style={{padding:"4px 8px",textAlign:"left",color:C.dim,fontSize:9,fontWeight:700,textTransform:"uppercase",borderBottom:`1px solid ${C.border}`}}>Contract</th>
              <th style={{padding:"4px 8px",textAlign:"left",color:C.dim,fontSize:9,fontWeight:700,textTransform:"uppercase",borderBottom:`1px solid ${C.border}`}}>Scheduled</th>
              <th style={{padding:"4px 8px",textAlign:"left",color:C.dim,fontSize:9,fontWeight:700,textTransform:"uppercase",borderBottom:`1px solid ${C.border}`}}>OT</th>
              <th style={{padding:"4px 8px",textAlign:"left",color:C.dim,fontSize:9,fontWeight:700,textTransform:"uppercase",borderBottom:`1px solid ${C.border}`}}>Absent</th>
            </tr></thead>
            <tbody>
              {weekStaff.slice(0,8).map(n=>{
                const scheduledDays = weekDays.filter(d=>!isAbsent(state,n.id,d.date)).length;
                const dailyHrs = (n.hrs||37.5)/5;
                const scheduledHrs = Math.round(scheduledDays*dailyHrs*10)/10;
                const ot = (state.overtime||[]).filter(o=>o.staffId===n.id&&o.date>=currentWeek.start&&o.date<=currentWeek.end).reduce((a,o)=>a+(o.hrs||0),0);
                const absentDays = weekDays.filter(d=>isAbsent(state,n.id,d.date)).length;
                return (
                  <tr key={n.id}>
                    <td style={{padding:"5px 8px",fontWeight:600,color:C.text,borderBottom:`1px solid ${C.border2}`}}>{n.name.split(" ")[0]}</td>
                    <td style={{padding:"5px 8px",color:C.mid,borderBottom:`1px solid ${C.border2}`}}>{n.hrs||37.5}h</td>
                    <td style={{padding:"5px 8px",color:C.text,borderBottom:`1px solid ${C.border2}`}}>{scheduledHrs}h</td>
                    <td style={{padding:"5px 8px",color:ot>0?C.warn:C.muted,borderBottom:`1px solid ${C.border2}`}}>{ot>0?`+${ot}h`:"—"}</td>
                    <td style={{padding:"5px 8px",color:absentDays>0?C.danger:C.muted,borderBottom:`1px solid ${C.border2}`}}>{absentDays>0?`${absentDays}d`:"—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
          <div style={{flex:1}}>
            <label style={LB}>Note (optional — exceptions, swaps, cover etc)</label>
            <input value={note} onChange={e=>setNote(e.target.value)} placeholder="e.g. Clare covered extra shift Wed, Maryam left early Thu" style={IS}/>
          </div>
          {saved
            ? <div style={{padding:"8px 16px",color:C.green,fontWeight:700,fontSize:13}}>Saved</div>
            : <button onClick={confirm} style={{...BTN(C.teal),padding:"8px 16px",whiteSpace:"nowrap"}}>Confirm week</button>
          }
        </div>
      </div>

      {/* Past weeks log */}
      <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Previous weeks</div>
      {weeks.slice(1).map(wk=>{
        const log = logs.find(l=>l.site===site&&l.week===wk.start);
        return (
          <div key={wk.start} style={{...CS,padding:"10px 14px",marginBottom:6,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontSize:12,fontWeight:600,color:C.text}}>{wk.label}</span>
            {log
              ? <div style={{textAlign:"right"}}>
                  <div style={{fontSize:11,color:C.green,fontWeight:600}}>{log.confirmedBy}</div>
                  {log.note&&<div style={{fontSize:10,color:C.dim}}>{log.note}</div>}
                </div>
              : <span style={{fontSize:11,color:C.muted,fontStyle:"italic"}}>Not confirmed</span>
            }
          </div>
        );
      })}
    </div>
  );
}

function Reports({state, set, isPrincipal, userRole}) {
  const [report, setReport] = useState("pulse-history");
  const [siteF, setSiteF]   = useState("all");
  const [histRange, setHistRange] = useState("7");
  const nurses   = state.nurses||NURSES_BASE;
  const sick     = state.sick||[];
  const lateness = state.latenessLog||[];
  const overtime = state.overtime||[];
  const earlyFin = state.earlyFinish||[];
  const extraReq = state.extraShiftRequests||[];

  const allStaff = [
    ...getClinicians(state).filter(c=>!c._deleted),
    ...nurses.filter(n=>!n.maternity&&!n.archived),
    ...RECS_BASE,
  ].filter(s=>siteF==="all"||(s.sites||[s.site]).includes(siteF));

  const nurseStaff = nurses.filter(n=>!n.maternity&&!n.archived&&(siteF==="all"||n.site===siteF));

  // -- Bradford factor per person --
  const bfScore = id => { const eps=sick.filter(r=>r.staffId===id).length; const days=sick.filter(r=>r.staffId===id).reduce((a,r)=>a+r.days,0); return eps*eps*days; };

  // -- Attendance record --
  const attendScore = id => sick.filter(r=>r.staffId===id).length===0 && lateness.filter(l=>l.staffId===id).length===0;

  // -- Extra shift requests count --
  const extraCount = id => extraReq.filter(r=>r.staffId===id).length;

  // -- Late finishes count --
  const lateFinCount = id => earlyFin.filter(r=>r.staffId===id&&r.type==="late").length;

  const REPORTS = [
    {id:"pulse-history", label:"Iris Score History"},
    {id:"locum-spend",   label:"Locum Spend"},
    {id:"staff-list",    label:"Staff List"},
    {id:"per-site",      label:"Per-Site Breakdown"},
    {id:"compliance",    label:"Compliance & Roles"},
    {id:"best-attend",   label:"Best Attendance"},
    {id:"extra-shifts",  label:"Always Wants More"},
    {id:"late-finish",   label:"Always Running Late"},
    ...(isPrincipal?[{id:"pay-rates",label:"Pay Rates (Partners only)"},{id:"question-log",label:"AI Question Log"},{id:"privacy-notice",label:"Privacy Notice"}]:[]),
  ];

  function printReport() { window.print(); }

  const tbHdr = cols => <thead><tr>{cols.map(h=><th key={h} style={{padding:"6px 10px",fontSize:9,fontWeight:700,color:C.dim,textAlign:"left",textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:`1px solid ${C.border}`,whiteSpace:"nowrap"}}>{h}</th>)}</tr></thead>;
  const tbRow = (cells, key) => <tr key={key}>{cells.map((v,i)=><td key={i} style={{padding:"8px 10px",fontSize:12,color:C.text,borderBottom:`1px solid ${C.border2}`,verticalAlign:"middle"}}>{v}</td>)}</tr>;

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16,flexWrap:"wrap",gap:12}}>
        <div>
          <h3 style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading,marginBottom:2}}>Reports</h3>
          <div style={{fontSize:12,color:C.dim}}>Export-ready staff reports for managers and partners.</div>
        </div>
        <button onClick={printReport} style={{...BTN(C.teal,true),display:"flex",alignItems:"center",gap:6}}>Print / Export</button>
      </div>

      {/* Site filter */}
      <div style={{display:"flex",gap:6,marginBottom:14,flexWrap:"wrap"}}>
        {[["all","All sites"],...Object.entries(SITES).map(([id,s])=>[id,s.name])].map(([id,l])=>(
          <button key={id} onClick={()=>setSiteF(id)} style={{padding:"5px 14px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${siteF===id?(SITES[id]?.color||C.teal):C.border}`,background:siteF===id?(SITES[id]?.color||C.teal)+"18":"transparent",color:siteF===id?(SITES[id]?.color||C.teal):C.mid}}>{l}</button>
        ))}
      </div>

      {/* Report picker */}
      <div style={{display:"flex",gap:6,marginBottom:20,flexWrap:"wrap",borderBottom:`1px solid ${C.border}`,paddingBottom:0}}>
        {REPORTS.map(r=>(
          <button key={r.id} onClick={()=>setReport(r.id)} style={{background:"transparent",border:"none",padding:"8px 14px",cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:600,color:report===r.id?C.teal:C.mid,borderBottom:report===r.id?`2px solid ${C.teal}`:"2px solid transparent",whiteSpace:"nowrap"}}>{r.label}</button>
        ))}
      </div>

      {/* ── PULSE SCORE HISTORY ── */}
      {report==="pulse-history"&&(()=>{
        const history = state.irisScoreHistory||[];
        const today = new Date();
        const cutoff = new Date(today);
        if (histRange==="7")  cutoff.setDate(today.getDate()-7);
        if (histRange==="30") cutoff.setDate(today.getDate()-30);
        if (histRange==="180")cutoff.setDate(today.getDate()-180);
        const cutoffStr = cutoff.toISOString().split("T")[0];

        const filtered = history.filter(h=>h.date>=cutoffStr)
          .sort((a,b)=>b.date.localeCompare(a.date));

        // Group by date for display
        const byDate = {};
        filtered.forEach(h=>{
          if (!byDate[h.date]) byDate[h.date] = {};
          byDate[h.date][h.site] = h;
        });
        const dates = Object.keys(byDate).sort((a,b)=>b.localeCompare(a));

        // Per-site averages
        const siteAvgs = Object.keys(SITES).map(sid=>{
          const entries = filtered.filter(h=>h.site===sid&&h.score!=null);
          const avg = entries.length ? Math.round(entries.reduce((s,h)=>s+h.score,0)/entries.length) : null;
          const trend = entries.length>=2 ? entries[0].score - entries[entries.length-1].score : null;
          return {sid, avg, trend, count:entries.length};
        });

        const scoreCol = s => s>=90?C.green:s>=50?C.warn:C.danger;
        const bandLabel = s => s>=90?"Good":s>=50?"Needs Attention":"Action Required";

        return (
          <div>
            {/* Range selector */}
            <div style={{display:"flex",gap:6,marginBottom:16,alignItems:"center",flexWrap:"wrap"}}>
              <span style={{fontSize:11,color:C.dim,fontWeight:600,marginRight:4}}>Show:</span>
              {[["7","Last 7 days"],["30","Last 30 days"],["180","Last 6 months"]].map(([v,l])=>(
                <button key={v} onClick={()=>setHistRange(v)}
                  style={{padding:"5px 14px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",
                    border:`1px solid ${histRange===v?C.teal:C.border}`,
                    background:histRange===v?C.tealPale:"transparent",
                    color:histRange===v?C.teal:C.mid}}>
                  {l}
                </button>
              ))}
              <span style={{fontSize:11,color:C.dim,marginLeft:"auto"}}>{dates.length} days recorded</span>
            </div>

            {/* Site average summary cards */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:10,marginBottom:20}}>
              {siteAvgs.map(({sid,avg,trend,count})=>{
                const site = SITES[sid];
                const col = avg!=null ? scoreCol(avg) : C.muted;
                return (
                  <div key={sid} style={{...CS,padding:16,borderTop:`3px solid ${col}`}}>
                    <div style={{fontWeight:700,color:site.color,fontSize:12,marginBottom:4}}>{site.name}</div>
                    {avg!=null ? <>
                      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:32,fontWeight:800,color:col,lineHeight:1}}>{avg}</div>
                      <div style={{fontSize:10,color:C.dim,marginTop:2}}>avg over {count} day{count!==1?"s":""}</div>
                      {trend!=null&&<div style={{fontSize:11,fontWeight:600,color:trend>0?C.green:trend<0?C.danger:C.dim,marginTop:4}}>
                        {trend>0?"↑":"↓"} {Math.abs(trend)} pts {trend>0?"improving":"declining"}
                      </div>}
                    </> : <div style={{fontSize:11,color:C.muted,marginTop:8}}>No data yet</div>}
                  </div>
                );
              })}
            </div>

            {/* Day-by-day table */}
            {dates.length===0 ? (
              <div style={{...CS,padding:24,textAlign:"center"}}>
                <div style={{fontSize:28,marginBottom:8}}></div>
                <div style={{fontWeight:700,color:C.heading,marginBottom:4}}>No history yet</div>
                <div style={{fontSize:12,color:C.dim}}>Iris Scores are logged automatically each day you open the dashboard. Come back tomorrow!</div>
              </div>
            ) : (
              <div style={{...CS}}>
                <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12}}>
                  Daily scores — {histRange==="7"?"last 7 days":histRange==="30"?"last 30 days":"last 6 months"}
                </div>
                <div style={{overflowX:"auto"}}>
                  <table style={{borderCollapse:"collapse",width:"100%",minWidth:400}}>
                    <thead>
                      <tr>
                        <th style={{padding:"6px 12px",fontSize:9,fontWeight:700,color:C.dim,textAlign:"left",textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:`1px solid ${C.border}`}}>Date</th>
                        {Object.entries(SITES).map(([sid,site])=>(
                          <th key={sid} style={{padding:"6px 12px",fontSize:9,fontWeight:700,color:site.color,textAlign:"center",textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:`1px solid ${C.border}`}}>{site.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {dates.map(date=>{
                        const dayLabel = CAL.days.find(d=>d.date===date)?.label||date;
                        return (
                          <tr key={date}>
                            <td style={{padding:"8px 12px",fontSize:12,color:C.text,borderBottom:`1px solid ${C.border2}`,fontWeight:500,whiteSpace:"nowrap"}}>{dayLabel}</td>
                            {Object.keys(SITES).map(sid=>{
                              const entry = byDate[date]?.[sid];
                              if (!entry) return <td key={sid} style={{padding:"8px 12px",textAlign:"center",borderBottom:`1px solid ${C.border2}`,fontSize:11,color:C.muted}}>--</td>;
                              const col = scoreCol(entry.score);
                              return (
                                <td key={sid} style={{padding:"8px 12px",textAlign:"center",borderBottom:`1px solid ${C.border2}`}}>
                                  <span style={{display:"inline-flex",alignItems:"center",gap:4}}>
                                    <span style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:14,fontWeight:700,color:col}}>{entry.score}</span>
                                    <span style={{fontSize:9,color:col,fontWeight:600}}>{bandLabel(entry.score)}</span>
                                  </span>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div style={{fontSize:10,color:C.muted,marginTop:12,textAlign:"right"}}>
                  Iris Score™ history · © Omni 2026 · {PRACTICE_CONFIG.name}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── LOCUM SPEND ── */}
      {report==="locum-spend"&&(()=>{
        const locums = state.locums||[];
        const today = new Date().toISOString().split("T")[0];
        const thisMonth = today.slice(0,7);
        const last3months = new Date(); last3months.setMonth(last3months.getMonth()-3);
        const l3Str = last3months.toISOString().slice(0,7);

        // Group by month × site
        const byMonth = {};
        locums.forEach(l=>{
          const mo = l.date?.slice(0,7)||"unknown";
          if (!byMonth[mo]) byMonth[mo]={};
          if (!byMonth[mo][l.site]) byMonth[mo][l.site]=[];
          byMonth[mo][l.site].push(l);
        });
        const months = Object.keys(byMonth).sort((a,b)=>b.localeCompare(a)).slice(0,6);

        const totalSpend = locums.reduce((s,l)=>s+l.cost,0);
        const thisMonthSpend = locums.filter(l=>l.date?.startsWith(thisMonth)).reduce((s,l)=>s+l.cost,0);

        return (
          <div>
            {/* Summary cards */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:10,marginBottom:20}}>
              {[
                [fmt(thisMonthSpend),"This month",thisMonthSpend>500?C.danger:C.teal],
                [fmt(totalSpend),"All time",C.mid],
                [locums.length,"Total locum days",C.mid],
              ].map(([v,l,c])=>(
                <div key={l} style={{...CS,padding:16}}>
                  <div style={{fontSize:10,color:C.dim,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.06em"}}>{l}</div>
                  <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:28,fontWeight:700,color:c}}>{v}</div>
                </div>
              ))}
            </div>

            {months.length===0 ? (
              <div style={{...CS,padding:24,textAlign:"center"}}>
                <div style={{fontSize:11,color:C.dim}}>No locum spend logged yet. Use "Log Locum" on the dashboard.</div>
              </div>
            ) : (
              <div style={{...CS}}>
                <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12}}>Monthly breakdown by site</div>
                <div style={{overflowX:"auto"}}>
                  <table style={{borderCollapse:"collapse",width:"100%",minWidth:400}}>
                    <thead>
                      <tr>
                        <th style={{padding:"6px 12px",fontSize:9,fontWeight:700,color:C.dim,textAlign:"left",textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:`1px solid ${C.border}`}}>Month</th>
                        {Object.entries(SITES).map(([sid,site])=>(
                          <th key={sid} style={{padding:"6px 12px",fontSize:9,fontWeight:700,color:site.color,textAlign:"right",textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:`1px solid ${C.border}`}}>{site.name}</th>
                        ))}
                        <th style={{padding:"6px 12px",fontSize:9,fontWeight:700,color:C.dim,textAlign:"right",textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:`1px solid ${C.border}`}}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {months.map(mo=>{
                        const moData = byMonth[mo]||{};
                        const moTotal = Object.values(moData).flat().reduce((s,l)=>s+l.cost,0);
                        const [y,m] = mo.split("-");
                        const moLabel = new Date(+y,+m-1,1).toLocaleString("default",{month:"long",year:"numeric"});
                        return (
                          <tr key={mo}>
                            <td style={{padding:"8px 12px",fontSize:12,color:C.text,borderBottom:`1px solid ${C.border2}`,fontWeight:600}}>{moLabel}</td>
                            {Object.keys(SITES).map(sid=>{
                              const entries = moData[sid]||[];
                              const spend = entries.reduce((s,l)=>s+l.cost,0);
                              return <td key={sid} style={{padding:"8px 12px",fontSize:12,color:spend>0?C.text:C.muted,borderBottom:`1px solid ${C.border2}`,textAlign:"right",fontWeight:spend>0?600:400}}>{spend>0?fmt(spend):"—"}</td>;
                            })}
                            <td style={{padding:"8px 12px",fontSize:12,fontWeight:700,color:moTotal>1000?C.danger:C.text,borderBottom:`1px solid ${C.border2}`,textAlign:"right"}}>{fmt(moTotal)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── STAFF LIST ── */}
      {report==="staff-list"&&(
        <div style={{...CS}}>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12}}>Full Staff List{siteF!=="all"?` — ${SITES[siteF]?.name}`:""} · {allStaff.length} people</div>
          <div style={{overflowX:"auto"}}>
            <table style={{borderCollapse:"collapse",width:"100%",minWidth:500}}>
              {tbHdr(["Name","Role/Type","Site","Hours","Status/Level","Contact"])}
              <tbody>
                {allStaff.map(s=>{
                  const sd=SITES[s.site]; const st=s.status?STATUS[s.status]:null;
                  return tbRow([
                    <span style={{fontWeight:600}}>{s.name}</span>,
                    s.type||"Nurse",
                    <span style={{color:sd?.color,fontWeight:600}}>{sd?.name}</span>,
                    s.hrs?`${s.hrs}h`:"—",
                    st?<span style={{color:st.color,fontWeight:600}}>{st.label}</span>:"Partner",
                    "—"
                  ], s.id);
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── PER SITE ── */}
      {report==="per-site"&&Object.entries(SITES).filter(([id])=>siteF==="all"||siteF===id).map(([id,si])=>{
        const sNurses = nurses.filter(n=>n.site===id&&!n.maternity&&!n.archived);
        const sRecs   = RECS_BASE.filter(r=>r.site===id);
        const sClins  = getClinicians(state).filter(c=>(c.sites||[c.site]).includes(id)&&!c._deleted);
        return (
          <div key={id} style={{...CS,marginBottom:14,borderTop:`3px solid ${si.color}`}}>
            <div style={{fontWeight:700,color:si.color,fontSize:16,marginBottom:12}}>{si.name} · {sClins.length} dentists · {sNurses.length} nurses · {sRecs.length} reception</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:10}}>
              {[["Clinicians",sClins],["Nursing",sNurses],["Reception & TCO",sRecs]].map(([label,items])=>(
                <div key={label}>
                  <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",marginBottom:6}}>{label}</div>
                  {items.map(p=>(
                    <div key={p.id} style={{fontSize:12,color:C.text,padding:"4px 0",borderBottom:`1px solid ${C.border2}`,display:"flex",justifyContent:"space-between"}}>
                      <span>{p.name}</span>
                      <span style={{color:C.dim}}>{p.type||p.area||""}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* ── COMPLIANCE ── */}
      {report==="compliance"&&(
        <div style={CS}>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12}}>Compliance & Special Roles</div>
          <div style={{overflowX:"auto"}}>
            <table style={{borderCollapse:"collapse",width:"100%",minWidth:600}}>
              {tbHdr(["Name","Site","First Aider","Fire Marshal","Safeguarding","Keyholder","RA","IV Sedation"])}
              <tbody>
                {nurseStaff.map(n=>{
                  const comp=n.compliance||{}; const sd=SITES[n.site];
                  const tick = v => v?<span style={{color:C.green,fontWeight:700}}>✓</span>:<span style={{color:C.border}}>--</span>;
                  return tbRow([
                    <span style={{fontWeight:600}}>{n.name}</span>,
                    <span style={{color:sd?.color,fontWeight:600}}>{sd?.name}</span>,
                    tick(comp.firstAider), tick(comp.fireMarshal), tick(comp.safeguarding),
                    tick(n.keyholder||comp.keyholder), tick(n.ra), tick(n.iv)
                  ], n.id);
                })}
              </tbody>
            </table>
          </div>
          <div style={{marginTop:16,display:"flex",gap:12,flexWrap:"wrap",fontSize:12,color:C.dim}}>
            <span>First Aiders: <strong style={{color:C.text}}>{nurseStaff.filter(n=>(n.compliance||{}).firstAider).length}</strong></span>
            <span>Fire Marshals: <strong style={{color:C.text}}>{nurseStaff.filter(n=>(n.compliance||{}).fireMarshal).length}</strong></span>
            <span>Safeguarding: <strong style={{color:C.text}}>{nurseStaff.filter(n=>(n.compliance||{}).safeguarding).length}</strong></span>
            <span>Keyholders: <strong style={{color:C.text}}>{nurseStaff.filter(n=>n.keyholder||(n.compliance||{}).keyholder).length}</strong></span>
          </div>
        </div>
      )}

      {/* ── BEST ATTENDANCE ── */}
      {report==="best-attend"&&(()=>{
        const all2 = [...nurses.filter(n=>!n.maternity&&!n.archived),...RECS_BASE].filter(s=>siteF==="all"||s.site===siteF);
        const rows = all2.map(s=>({
          person:s, absent:sick.filter(r=>r.staffId===s.id).reduce((a,r)=>a+r.days,0),
          episodes:sick.filter(r=>r.staffId===s.id).length,
          late:lateness.filter(l=>l.staffId===s.id).length,
          bf:bfScore(s.id)
        })).sort((a,b)=>(a.absent+a.late)-(b.absent+b.late));
        return (
          <div style={CS}>
            <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12}}>Attendance Record</div>
            <div style={{overflowX:"auto"}}>
              <table style={{borderCollapse:"collapse",width:"100%",minWidth:480}}>
                {tbHdr(["Name","Site","Absent days","Episodes","Lateness","Bradford","Rating"])}
                <tbody>
                  {rows.map(({person:p,absent,episodes,late,bf})=>{
                    const perfect=absent===0&&late===0; const good=absent<3&&late<2;
                    return tbRow([
                      <span style={{fontWeight:600}}>{p.name}</span>,
                      <span style={{color:SITES[p.site]?.color,fontWeight:600}}>{SITES[p.site]?.name}</span>,
                      absent||"—", episodes||"—", late||"—",
                      <span style={{fontWeight:700,color:BF_BANDS.find(b=>bf>=b.min&&bf<=b.max)?.color||C.green}}>{bf||"0"}</span>,
                      perfect?<span style={{color:C.green,fontWeight:700}}>Perfect</span>:good?<span style={{color:C.teal}}>Good</span>:<span style={{color:C.warn}}>Monitor</span>
                    ], p.id);
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ── ALWAYS WANTS MORE SHIFTS ── */}
      {report==="extra-shifts"&&(()=>{
        const all2=[...nurses.filter(n=>!n.maternity&&!n.archived),...RECS_BASE].filter(s=>siteF==="all"||s.site===siteF);
        const rows=all2.map(s=>({person:s,count:extraCount(s.id),avDays:Object.values((state.weeklyAvailability||{})).flat().filter(d=>d===s.id).length})).filter(r=>r.count>0||r.avDays>0).sort((a,b)=>b.count-a.count);
        return (
          <div style={CS}>
            <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12}}>Extra Shift Requests — most enthusiastic first</div>
            {rows.length===0&&<div style={{color:C.dim,fontSize:13}}>No extra shift requests recorded yet.</div>}
            <div style={{overflowX:"auto"}}>
              <table style={{borderCollapse:"collapse",width:"100%"}}>
                {tbHdr(["Name","Site","Shift requests","Extra availability flags","Notes"])}
                <tbody>
                  {rows.map(({person:p,count})=>tbRow([
                    <span style={{fontWeight:600}}>{p.name}</span>,
                    <span style={{color:SITES[p.site]?.color,fontWeight:600}}>{SITES[p.site]?.name}</span>,
                    <span style={{color:count>=3?C.green:C.text,fontWeight:700}}>{count}</span>,
                    "—",
                    count>=3?"Great attitude — consider for extra hours":"—"
                  ], p.id))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ── ALWAYS RUNNING LATE ── */}
      {report==="late-finish"&&(()=>{
        const all2=[...nurses.filter(n=>!n.maternity&&!n.archived),...RECS_BASE].filter(s=>siteF==="all"||s.site===siteF);
        const rows=all2.map(s=>({
          person:s,
          late:earlyFin.filter(r=>r.staffId===s.id&&r.type==="late").length,
          lateness:lateness.filter(l=>l.staffId===s.id).length,
        })).filter(r=>r.late>0||r.lateness>0).sort((a,b)=>(b.late+b.lateness)-(a.late+a.lateness));
        return (
          <div style={CS}>
            <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12}}>Late Finishes & Lateness</div>
            {rows.length===0&&<div style={{color:C.dim,fontSize:13}}>No late finishes or lateness recorded.</div>}
            <div style={{overflowX:"auto"}}>
              <table style={{borderCollapse:"collapse",width:"100%"}}>
                {tbHdr(["Name","Site","Late finishes","Lateness incidents","Flag"])}
                <tbody>
                  {rows.map(({person:p,late,lateness:lateInc})=>tbRow([
                    <span style={{fontWeight:600}}>{p.name}</span>,
                    <span style={{color:SITES[p.site]?.color,fontWeight:600}}>{SITES[p.site]?.name}</span>,
                    <span style={{color:late>=3?C.danger:C.text,fontWeight:late>=3?700:400}}>{late||"—"}</span>,
                    <span style={{color:lateInc>=3?C.danger:C.text,fontWeight:lateInc>=3?700:400}}>{lateInc||"—"}</span>,
                    (late>=3||lateInc>=3)?<span style={{color:C.danger,fontWeight:700}}>Discuss</span>:"—"
                  ], p.id))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ── PAY RATES (PRINCIPAL ONLY) ── */}
      {report==="pay-rates"&&isPrincipal&&(
        <div>
          <div style={{...CS,marginBottom:14}}>
            <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12}}>Staff Hourly Rates — Nurses & Reception</div>
            <div style={{fontSize:12,color:C.dim,marginBottom:12}}>Default rate: nurses £{state.settings?.nurseHourlyRate||14}/hr, reception £{state.settings?.recHourlyRate||12}/hr. Set individual rates in staff records.</div>
            <div style={{overflowX:"auto"}}>
              <table style={{borderCollapse:"collapse",width:"100%",minWidth:400}}>
                {tbHdr(["Name","Site","Role","Contract hrs","Est. weekly cost","Est. annual cost"])}
                <tbody>
                  {[...nurses.filter(n=>!n.maternity&&!n.archived&&(siteF==="all"||n.site===siteF)),...RECS_BASE.filter(r=>siteF==="all"||r.site===siteF)].map(s=>{
                    const rate = s.hourlyRate||(s.type==="Reception"||s.type==="TCO"?state.settings?.recHourlyRate||12:state.settings?.nurseHourlyRate||14);
                    const weekly = (s.hrs||37.5)*rate;
                    const annual = weekly*52;
                    return tbRow([
                      <span style={{fontWeight:600}}>{s.name}</span>,
                      <span style={{color:SITES[s.site]?.color,fontWeight:600}}>{SITES[s.site]?.name}</span>,
                      s.type||"Nurse",
                      `${s.hrs||37.5}h`,
                      <span style={{fontWeight:600,color:C.teal}}>{fmt(weekly)}</span>,
                      <span style={{fontWeight:600}}>{fmt(annual)}</span>
                    ], s.id);
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {/* Manager salaries — principal eyes only */}
          <div style={{...CS,borderTop:`3px solid ${C.warn}`}}>
            <div style={{fontSize:10,fontWeight:700,color:C.warn,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}> Manager Salaries — Principal eyes only</div>
            <div style={{fontSize:11,color:C.dim,marginBottom:12}}>Annual salary. Not visible to managers or other staff.</div>
            <div style={{overflowX:"auto"}}>
              <table style={{borderCollapse:"collapse",width:"100%"}}>
                {tbHdr(["Name","Site(s)","Annual salary","Notes"])}
                <tbody>
                  {RECS_BASE.filter(r=>r.manager).concat(
                    [{id:"r_mhampson",name:"Megan Hampson",site:"sale",annualSalary:30000,type:"Manager",note:"Sale Practice Manager"}]
                  ).filter((r,i,arr)=>arr.findIndex(x=>x.id===r.id)===i).map(m=>{
                    const salary = m.annualSalary || (state.managerSalaries||{})[m.id] || null;
                    return tbRow([
                      <span style={{fontWeight:600}}>{m.name}</span>,
                      <span style={{color:SITES[m.site]?.color,fontWeight:600}}>{SITES[m.site]?.name}{m.multiSite?` + ${m.multiSite.filter(s=>s!==m.site).map(s=>SITES[s]?.name).join(", ")}`:""}</span>,
                      salary?<span style={{fontWeight:700,color:C.teal}}>{fmt(salary)}</span>:<span style={{color:C.dim}}>Not set</span>,
                      m.note||"—"
                    ], m.id);
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── AI QUESTION LOG ── */}
      {report==="question-log"&&isPrincipal&&<QuestionLog state={state}/>}

      {/* ── PRIVACY NOTICE ── */}
      {report==="privacy-notice"&&isPrincipal&&(
        <div style={{maxWidth:600}}>
          <div style={{fontSize:12,color:C.dim,marginBottom:16,lineHeight:1.7}}>
            The Staff Privacy Notice explains to all staff what personal data Omni holds and why. Print and display in your staff areas, or share the PDF link. This document is also accessible to staff when they claim their account.
          </div>
          <PrivacyNoticeDoc state={state}/>
        </div>
      )}
    </div>
  );
}


function ManagerLeaveView({staffId,state,set,user}) {
  const person = [...RECS_BASE,(state.nurses||NURSES_BASE)].find(s=>s.id===staffId)||{id:staffId,name:user.name,site:user.site,hrs:37.5};
  const [leaveM,setLeaveM] = useState(false);
  const entit = ent(person.hrs||37.5);
  const hist  = getHistDays(state, staffId);
  const allLeave = state.leave[staffId]||[];
  const approved = allLeave.filter(l=>l.status==="approved");
  const pending  = allLeave.filter(l=>l.status==="pending");
  const declined = allLeave.filter(l=>l.status==="declined");
  const used = countDays(approved)+hist;
  const rem  = entit-used;

  function submit(id,dates,leaveType,note,halfDaysMap) {
    const rid="req"+Date.now();
    // All leave goes to principal for approval — nobody self-approves
    const leaveStatus = "pending";
    set(s=>{const ex=s.leave[id]||[];const ne=dates.map(d=>({id:"l"+Math.random().toString(36).slice(2),requestId:rid,date:d,status:leaveStatus,leaveType:leaveType||"annual",note:note||"",halfDay:halfDaysMap?.[d]||null,days:halfDaysMap?.[d]?0.5:1}));return{...s,leave:{...s.leave,[id]:[...ex,...ne]}};});
    setLeaveM(false);
  }

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <h3 style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading}}>My Leave — {person.name}</h3>
        <button onClick={()=>setLeaveM(true)} style={BTN(C.teal)}>Book Leave</button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:10,marginBottom:20}}>
        {[[entit+"d","Entitlement",C.text],[used+"d","Used",C.warn],[pending.length+"d","Pending",C.lime],[rem+"d","Remaining",rem<5?C.danger:C.green]].map(([v,l,c])=>(
          <div key={l} style={{...CS,textAlign:"center",padding:"14px 8px"}}>
            <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:24,color:c}}>{v}</div>
            <div style={{fontSize:10,color:C.dim,marginTop:4,textTransform:"uppercase",letterSpacing:"0.06em"}}>{l}</div>
          </div>
        ))}
      </div>
      <div style={{height:8,background:C.border,borderRadius:99,overflow:"hidden",marginBottom:20}}>
        <div style={{width:`${Math.min((used/entit)*100,100)}%`,height:"100%",background:rem<5?C.danger:rem<10?C.warn:C.green,borderRadius:99}}/>
      </div>
      {approved.length>0&&<button onClick={()=>exportICal(approved,person.name,getCpdForPerson(state,person.id),[])} style={{...BTN(C.teal,true),marginBottom:16,fontSize:12,padding:"8px 16px"}}>Export to Calendar (.ics)</button>}
      {[["Approved",approved,C.green],["Pending",pending,C.lime],["Declined",declined,C.danger]].map(([title,items,c])=>items.length?(
        <div key={title} style={{marginBottom:14}}>
          <div style={{fontSize:10,fontWeight:700,color:c,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>{title} ({items.length})</div>
          {items.sort((a,b)=>a.date.localeCompare(b.date)).map(l=>(
            <div key={l.id} style={{...CS,marginBottom:4,padding:"10px 12px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:13,color:C.text,fontWeight:600,display:"flex",alignItems:"center",gap:5}}>{CAL.days.find(d=>d.date===l.date)?.label||l.date}{l.halfDay&&<span style={{fontSize:10,fontWeight:700,background:C.tealPale,color:C.teal,border:`1px solid ${C.tealBorder}`,borderRadius:4,padding:"1px 5px"}}>½{l.halfDay}</span>}</span>
              <Pill c={c} t={title.split(" ")[1]} sm/>
            </div>
          ))}
        </div>
      ):null)}
      {allLeave.length===0&&<div style={{...CS,textAlign:"center",padding:32,color:C.dim}}>No leave booked yet. Use the button above to book your holidays.</div>}
      {leaveM&&<LeaveModal person={person} state={state} onSubmit={submit} onClose={()=>setLeaveM(false)}/>}
    </div>
  );
}

/* ══════════════════════════════════
   WAGES TAB (Feature 5)
══════════════════════════════════ */
// Migration shim — wage record can be bare number (legacy) or object {type, rate, annual, attribution}
function parseWage(raw) {
  if (!raw && raw !== 0) return {type:"hourly", rate:null, annual:null, attribution:{}};
  if (typeof raw === "number") return {type:"hourly", rate:raw, annual:null, attribution:{}};
  return {type:"hourly", rate:null, annual:null, attribution:{}, ...raw};
}
function wageMonthly(w, hrs) {
  if (w.type==="salaried" && w.annual) return Math.round(w.annual / 12);
  if (w.rate) return Math.round(w.rate * (hrs||37.5) * 52 / 12);
  return 0;
}
function wageAnnual(w, hrs) {
  if (w.type==="salaried" && w.annual) return w.annual;
  if (w.rate) return Math.round(w.rate * (hrs||37.5) * 52);
  return 0;
}

function WageModal({person, current, allSites, onSave, onClose}) {
  const w = parseWage(current);
  // Allow setting extra sites directly in the wage modal
  const [extraSites, setExtraSites] = useState(person.extraSites||[]);
  const personSites = extraSites.length
    ? [person.site, ...extraSites].filter((v,i,a)=>v&&a.indexOf(v)===i)
    : [person.site].filter(Boolean);
  const isMultiSite = personSites.length > 1;
  const otherSites = Object.keys(allSites||{}).filter(id=>id!==person.site);

  const [type, setType]     = useState(w.type||"hourly");
  const [rate, setRate]     = useState(w.rate||"");
  const [annual, setAnnual] = useState(w.annual||"");
  const [attr, setAttr]     = useState(()=>{
    // Default attribution: equal split across sites
    if (w.attribution && Object.keys(w.attribution).length) return w.attribution;
    if (!isMultiSite) return {};
    const share = Math.round(100/personSites.length);
    const a = {};
    personSites.forEach((s,i)=>{ a[s] = i===personSites.length-1 ? 100-share*(personSites.length-1) : share; });
    return a;
  });

  const attrTotal = Object.values(attr).reduce((s,v)=>s+(+v||0),0);
  const attrValid = !isMultiSite || attrTotal===100;
  const fmt = v=>v!=null?new Intl.NumberFormat("en-GB",{style:"currency",currency:"GBP",maximumFractionDigits:0}).format(v):"";

  const monthly = type==="salaried" ? (annual?(+annual/12):0) : (rate?(+rate*(person.hrs||37.5)*52/12):0);
  const annualCalc = type==="salaried" ? (+annual||0) : (rate?(+rate*(person.hrs||37.5)*52):0);

  function save() {
    if (!attrValid) return;
    const record = type==="salaried"
      ? {type:"salaried", annual:+annual, rate:null, ...(isMultiSite?{attribution:attr}:{})}
      : {type:"hourly",   rate:+rate, annual:null, ...(isMultiSite?{attribution:attr}:{})};
    onSave(person.id, record, extraSites);
  }

  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:2}}>{person.name}</div>
      <div style={{fontSize:12,color:C.dim,marginBottom:16}}>
        {person.hrs||37.5}h/week · {(allSites?.[person.site]?.name)||person.site}
      </div>

      {/* Multi-site — set which sites this person works across */}
      {otherSites.length>0&&(
        <div style={{marginBottom:16,padding:"10px 14px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:10}}>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Also works at</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {otherSites.map(id=>{
              const s=allSites[id];
              const on=extraSites.includes(id);
              return (
                <button key={id} onClick={()=>setExtraSites(on?extraSites.filter(x=>x!==id):[...extraSites,id])}
                  style={{padding:"5px 12px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",
                    border:`1px solid ${on?s.color:C.border}`,background:on?s.color+"18":"transparent",color:on?s.color:C.mid}}>
                  {on?"✓ ":""}{s.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Pay type toggle */}
      <div style={{marginBottom:16}}>
        <label style={LB}>Pay type</label>
        <div style={{display:"flex",gap:8}}>
          {[["hourly","Hourly (£/hr)"],["salaried","Annual salary (£/yr)"]].map(([v,l])=>(
            <button key={v} onClick={()=>setType(v)} style={{flex:1,padding:"10px",borderRadius:10,fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer",
              border:`1px solid ${type===v?C.teal:C.border}`,background:type===v?C.tealPale:"transparent",color:type===v?C.teal:C.mid}}>
              {l}
            </button>
          ))}
        </div>
        <div style={{fontSize:10,color:C.dim,marginTop:6}}>
          {type==="hourly"?"Pay changes each month based on hours worked on the rota.":"Fixed monthly pay regardless of hours worked — same amount every month."}
        </div>
      </div>

      {/* Rate or salary input */}
      {type==="hourly"?(
        <div style={{marginBottom:16}}>
          <label style={LB}>Hourly rate £</label>
          <input type="number" step="0.01" value={rate} onChange={e=>setRate(e.target.value)} placeholder="e.g. 13.50" style={IS}/>
        </div>
      ):(
        <div style={{marginBottom:16}}>
          <label style={LB}>Annual salary £</label>
          <input type="number" step="100" value={annual} onChange={e=>setAnnual(e.target.value)} placeholder="e.g. 28000" style={IS}/>
        </div>
      )}

      {/* Live preview */}
      {(monthly>0)&&(
        <div style={{background:C.tealPale,border:`1px solid ${C.tealBorder}`,borderRadius:10,padding:12,marginBottom:16}}>
          <div style={{fontSize:12,color:C.teal}}>
            {type==="hourly"&&<div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span>Weekly (contracted)</span><span style={{fontWeight:700}}>{fmt(+rate*(person.hrs||37.5))}</span></div>}
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}><span>Monthly</span><span style={{fontWeight:700}}>{fmt(monthly)}</span></div>
            <div style={{display:"flex",justifyContent:"space-between"}}><span>Annual</span><span style={{fontWeight:700}}>{fmt(annualCalc)}</span></div>
          </div>
        </div>
      )}

      {/* Multi-site attribution */}
      {isMultiSite&&(
        <div style={{marginBottom:16,padding:"12px 14px",background:"rgba(245,197,24,0.06)",border:`1px solid ${attrValid?"rgba(245,197,24,0.2)":C.danger+"44"}`,borderRadius:10}}>
          <div style={{fontSize:11,fontWeight:700,color:C.warn,marginBottom:4}}>Site attribution</div>
          <div style={{fontSize:11,color:C.dim,marginBottom:10}}>
            Split this person's cost across sites for payroll reporting. Must add up to 100%.
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:6}}>
            {personSites.map(s=>(
              <div key={s} style={{flex:1,minWidth:100}}>
                <label style={{...LB,color:allSites?.[s]?.color||C.dim}}>{allSites?.[s]?.name||s}</label>
                <div style={{display:"flex",alignItems:"center",gap:4}}>
                  <input type="number" min="0" max="100" value={attr[s]||0}
                    onChange={e=>setAttr(a=>({...a,[s]:+e.target.value}))}
                    style={{...IS,marginBottom:0,width:70}}/>
                  <span style={{fontSize:12,color:C.dim}}>%</span>
                </div>
              </div>
            ))}
          </div>
          <div style={{fontSize:11,fontWeight:700,color:attrValid?C.green:C.danger}}>
            {attrValid?"✓ Adds up to 100%":`Total: ${attrTotal}% — must equal 100%`}
          </div>
          {attrValid&&monthly>0&&(
            <div style={{fontSize:10,color:C.dim,marginTop:4}}>
              {personSites.map(s=>`${allSites?.[s]?.name||s}: ${fmt(monthly*(attr[s]||0)/100)}/month`).join(" · ")}
            </div>
          )}
        </div>
      )}

      <div style={{display:"flex",gap:8}}>
        <button onClick={save} disabled={!attrValid} style={{...BTN(C.teal),flex:1,padding:"11px 0",opacity:attrValid?1:0.5}}>Save</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}

function Wages({state,set,isPrincipal,user}) {
  const [site,setSite] = useState("all");
  const [wageM,setWageM] = useState(null);
  // Defence-in-depth — even though tab navigation gates this, never render
  // payroll/wage data to anyone other than principal or manager.
  const isMgr = user?.role==="manager"||user?.role==="managerLite";
  if (!isPrincipal && !isMgr) return (
    <div style={{padding:"40px 20px",textAlign:"center",color:C.dim}}>
      <div style={{fontSize:32,marginBottom:12}}>🔒</div>
      <div style={{fontSize:16,fontWeight:700,color:C.heading,marginBottom:6}}>Wages are restricted</div>
      <div style={{fontSize:13}}>This area shows payroll information visible only to the principal and managers.</div>
    </div>
  );

  function updateWage(id, wage, extraSites) {
    set(s=>{
      const ns = {...s, wages:{...s.wages,[id]:wage}};
      if (extraSites !== undefined) {
        ns.nurses = (s.nurses||NURSES_BASE).map(n=>n.id===id?{...n,extraSites}:n);
        ns.customRecs = (s.customRecs||[]).map(r=>r.id===id?{...r,extraSites}:r);
      }
      return ns;
    });
    setWageM(null);
  }

  // All staff deduped across sites
  const allNurses = Object.keys(SITES).flatMap(sid=>getNursesForSite(state,sid))
    .filter((n,i,a)=>a.findIndex(x=>x.id===n.id)===i);
  const allRecs = Object.keys(SITES).flatMap(sid=>getRecsForSite(state,sid,false))
    .filter((r,i,a)=>a.findIndex(x=>x.id===r.id)===i);
  const everyone = [...allNurses,...allRecs];

  const knownSiteIds = new Set(Object.keys(SITES));
  const onlyOne = knownSiteIds.size<=1;
  const displayStaff = site==="all" ? everyone : everyone.filter(s=>{
    const unknown=!s.site||!knownSiteIds.has(s.site);
    return s.site===site||(s.extraSites||[]).includes(site)||(unknown&&onlyOne);
  });

  const totalMonthly = displayStaff.reduce((sum,s)=>{
    const w=parseWage(state.wages?.[s.id]);
    return sum+wageMonthly(w,s.hrs||37.5);
  },0);

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16,flexWrap:"wrap",gap:12}}>
        <div>
          <h3 style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading,marginBottom:2}}>Wages</h3>
          <div style={{fontSize:12,color:C.dim}}>Hourly staff are paid based on hours worked each month. Salaried staff receive a fixed monthly amount. Dentist pay handled separately.</div>
        </div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          <button onClick={()=>setSite("all")} style={{padding:"5px 14px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",border:"1px solid "+(site==="all"?C.teal:C.border),background:site==="all"?C.tealPale:"transparent",color:site==="all"?C.teal:C.mid}}>All sites</button>
          {Object.entries(SITES).map(([id,s])=>(
            <button key={id} onClick={()=>setSite(id)} style={{padding:"5px 14px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",border:"1px solid "+(site===id?s.color:C.border),background:site===id?s.color+"18":"transparent",color:site===id?s.color:C.mid}}>{s.name}</button>
          ))}
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20}}>
        <div style={CS}>
          <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>Monthly bill — {site==="all"?"all sites":SITES[site]?.name}</div>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:28,color:C.teal}}>{fmt(totalMonthly)}</div>
          <div style={{fontSize:11,color:C.dim}}>{displayStaff.filter(s=>state.wages?.[s.id]).length} of {displayStaff.length} staff with pay set</div>
        </div>
        <div style={CS}>
          <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>Annual projection</div>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:28,color:C.warn}}>{fmt(totalMonthly*12)}</div>
          <div style={{fontSize:11,color:C.dim}}>contracted basis</div>
        </div>
      </div>

      <div style={{overflowX:"auto"}}>
        <table style={{borderCollapse:"collapse",width:"100%",minWidth:560}}>
          <thead>
            <tr>{["Name","Role","Hours","Pay","Monthly","Annual",""].map(h=>(
              <th key={h} style={{padding:"6px 10px",fontSize:9,fontWeight:700,color:C.dim,textAlign:"left",textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:"1px solid "+C.border}}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {displayStaff.map(s=>{
              const raw=state.wages?.[s.id];
              const w=parseWage(raw);
              const monthly=wageMonthly(w,s.hrs||37.5);
              const annual=wageAnnual(w,s.hrs||37.5);
              const personSites=[s.site,...(s.extraSites||[])].filter(Boolean).filter((v,i,a)=>a.indexOf(v)===i);
              const isMultiSite=personSites.length>1;
              const isSalaried=w.type==="salaried"&&w.annual;
              return (
                <tr key={s.id} style={{borderBottom:"1px solid "+C.border2,background:isMultiSite?"rgba(245,197,24,0.04)":!raw?"rgba(245,197,24,0.03)":"transparent"}}>
                  <td style={{padding:"8px 10px",fontSize:12,fontWeight:600,color:C.text}}>
                    {s.name}
                    {isMultiSite&&(
                      <div style={{display:"flex",gap:3,marginTop:3,flexWrap:"wrap"}}>
                        {personSites.map(sid=>(
                          <span key={sid} style={{fontSize:8,fontWeight:700,color:SITES[sid]?.color||C.dim,background:(SITES[sid]?.color||C.dim)+"18",borderRadius:3,padding:"1px 5px"}}>
                            {SITES[sid]?.name||sid}
                          </span>
                        ))}
                        {w.attribution&&site!=="all"&&w.attribution[site]!=null&&(
                          <span style={{fontSize:8,color:C.dim,marginLeft:2}}>{w.attribution[site]}% attributed here</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td style={{padding:"8px 10px",fontSize:11,color:C.mid}}>{s.type||s.area||"Staff"}</td>
                  <td style={{padding:"8px 10px",fontSize:11,color:C.mid}}>{s.hrs||37.5}h</td>
                  <td style={{padding:"8px 10px",fontSize:12,color:raw?C.teal:C.muted,fontWeight:raw?600:400}}>
                    {isSalaried
                      ? <span>{fmt(w.annual)}<span style={{fontSize:9,color:C.dim}}>/yr</span>
                          <span style={{marginLeft:5,fontSize:9,fontWeight:700,color:C.brook,background:C.brook+"18",borderRadius:3,padding:"1px 5px"}}>Salary</span>
                        </span>
                      : w.rate?`£${w.rate}/hr`:"Not set"
                    }
                  </td>
                  <td style={{padding:"8px 10px",fontSize:12,fontWeight:600,color:C.text}}>{monthly?fmt(monthly):"—"}</td>
                  <td style={{padding:"8px 10px",fontSize:12,color:C.warn}}>{annual?fmt(annual):"—"}</td>
                  <td style={{padding:"8px 10px"}}>
                    <button onClick={()=>setWageM(s)} style={{...BTN(C.teal,!!raw),fontSize:10,padding:"3px 10px"}}>{raw?"Edit":"Set"}</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {wageM&&<WageModal person={wageM} current={state.wages?.[wageM.id]} allSites={SITES} onSave={updateWage} onClose={()=>setWageM(null)}/>}
    </div>
  );
}

/* ══════════════════════════════════
   REC STATUS MODAL (Feature 6)
══════════════════════════════════ */
function RecStatusModal({person,current,onSave,onClose}) {
  const [status,setStatus] = useState(current||"competent");
  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Competency Level</div>
      <div style={{fontSize:13,color:C.dim,marginBottom:4}}>{person.name}</div>
      <div style={{fontSize:11,color:C.dim,marginBottom:16}}>Sets the reception quality score on the Iris Score. Competent = full weight, Trainee = reduced.</div>
      <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:20}}>
        {Object.entries(REC_STATUS).map(([k,v])=>(
          <button key={k} onClick={()=>setStatus(k)} style={{padding:"12px 14px",borderRadius:10,border:"1px solid "+(status===k?v.color:C.border),background:status===k?v.color+"18":"transparent",color:status===k?v.color:C.soft,fontFamily:"inherit",fontSize:13,fontWeight:600,cursor:"pointer",textAlign:"left",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span>{v.label}</span><span style={{fontSize:11,color:C.dim}}>Score {(v.score*100).toFixed(0)}</span>
          </button>
        ))}
      </div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>onSave(person.id,status)} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Update</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}

/* ══════════════════════════════════
   UNSUNG HEROES (Feature 7)
══════════════════════════════════ */

/* ══════════════════════════════════
   PULSE PEOPLE ENGINE
   Hidden from staff — management only
══════════════════════════════════ */

// Signal weights for Pulse People score
const PP_WEIGHTS = {
  otHours:        4,   // per hour of approved OT
  extraShifts:    10,  // per approved extra shift
  availDays:      2,   // per day volunteered this month
  heroNoms:       15,  // per nomination received (manual or auto)
  cleanAttendance:20,  // no absences, no lateness this month — baseline
  unpaidHelp:     8,   // per day of unpaid leave approved (they're helping)
  welfareFlag:   -10,  // active welfare concern — offset, not punishment
};

function calcPulsePeopleScore(staffId, state) {
  const now = new Date();
  const monthStart = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-01`;
  const todayStr = now.toISOString().split("T")[0];

  // OT this month
  const otHrs = (state.overtime||[])
    .filter(r=>r.staffId===staffId&&r.status==="approved"&&r.date>=monthStart)
    .reduce((s,r)=>s+r.hrs,0);

  // Extra shifts this month
  const extraShifts = (state.extraShiftRequests||[])
    .filter(r=>r.staffId===staffId&&r.status==="approved"&&r.date>=monthStart).length;

  // Availability days volunteered this month (any week this month)
  const avMap = state.weeklyAvailability||{};
  const availDays = Object.entries(avMap)
    .filter(([k])=>k.startsWith(staffId)&&k.split("_")[1]>=monthStart)
    .reduce((s,[,days])=>s+days.length,0);

  // Hero nominations received this month
  const heroNoms = (state.unsungHeroes||[])
    .filter(h=>h.staffId===staffId&&h.dateISO&&h.dateISO>=monthStart).length;

  // Clean attendance — no sick, no lateness this month
  const hasSick = (state.sick||[]).some(s=>s.staffId===staffId&&s.date>=monthStart);
  const hasLate = (state.latenessLog||[]).some(l=>l.staffId===staffId&&l.date>=monthStart);
  const cleanBonus = (!hasSick&&!hasLate) ? PP_WEIGHTS.cleanAttendance : 0;

  // Unpaid leave approved (helping out)
  const unpaidDays = (state.unpaidLeaveRequests||[])
    .filter(r=>r.staffId===staffId&&r.status==="approved"&&r.dates?.some(d=>d>=monthStart))
    .reduce((s,r)=>s+(r.dates?.filter(d=>d>=monthStart).length||0),0);

  // Welfare flag offset
  const welfareOpen = (state.welfareLog||[]).some(w=>w.staffId===staffId&&!w.closed);

  const score = Math.max(0,
    otHrs       * PP_WEIGHTS.otHours +
    extraShifts * PP_WEIGHTS.extraShifts +
    availDays   * PP_WEIGHTS.availDays +
    heroNoms    * PP_WEIGHTS.heroNoms +
    cleanBonus +
    unpaidDays  * PP_WEIGHTS.unpaidHelp +
    (welfareOpen? PP_WEIGHTS.welfareFlag : 0)
  );

  // All-time nomination count (for "frequent hero" badge)
  const allTimeNoms = (state.unsungHeroes||[]).filter(h=>h.staffId===staffId).length;

  return { score, otHrs, extraShifts, availDays, heroNoms, cleanBonus, unpaidDays, allTimeNoms, welfareOpen };
}

// Auto-generate draft hero nominations from this month's behaviour data
function autoGenerateHeroNominations(state) {
  const now = new Date();
  const monthStart = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-01`;
  const monthName = now.toLocaleString("en-GB",{month:"long"});
  const allStaff = [...(state.nurses||NURSES_BASE).filter(n=>!n.maternity&&!n.archived),...RECS_BASE,...CLINICIANS_BASE];
  const drafts = [];

  allStaff.forEach(s => {
    const reasons = [];

    // OT this month
    const otRecs = (state.overtime||[]).filter(r=>r.staffId===s.id&&r.status==="approved"&&r.date>=monthStart);
    const otHrs = otRecs.reduce((sum,r)=>sum+r.hrs,0);
    if (otHrs>=1) reasons.push(`Worked ${otHrs.toFixed(1)} hours overtime in ${monthName}`);

    // Extra shifts
    const extra = (state.extraShiftRequests||[]).filter(r=>r.staffId===s.id&&r.status==="approved"&&r.date>=monthStart);
    if (extra.length>=1) reasons.push(`Volunteered for ${extra.length} extra shift${extra.length>1?"s":""}`);

    // Availability volunteered
    const avMap = state.weeklyAvailability||{};
    const avDays = Object.entries(avMap)
      .filter(([k])=>k.startsWith(s.id)&&k.split("_")[1]>=monthStart)
      .reduce((sum,[,days])=>sum+days.length,0);
    if (avDays>=3) reasons.push(`Put hand up for ${avDays} extra days — helped cover when needed`);

    // Only generate if there's a genuine reason
    if (reasons.length===0) return;

    // Don't re-generate if already auto-nominated this month
    const alreadyAuto = (state.unsungHeroes||[]).some(h=>
      h.staffId===s.id && h.type==="auto" && h.dateISO && h.dateISO>=monthStart
    );
    if (alreadyAuto) return;

    drafts.push({
      id: "draft_"+s.id+"_"+monthStart,
      staffId: s.id,
      name: s.name,
      site: s.site,
      reason: reasons.join(". ") + ".",
      nominatedBy: "Omni Rota (auto)",
      type: "auto",
      status: "draft",   // "draft" = awaiting principal approval
      dateISO: now.toISOString().split("T")[0],
      date: now.toLocaleDateString("en-GB",{day:"numeric",month:"short"}),
      month: monthStart.slice(0,7),
    });
  });

  return drafts;
}

/* ── PULSE PEOPLE VIEW ── */
function PulsePeople({state, set, user}) {
  const isPrincipal = user?.role==="principal";
  const [siteFilter, setSiteFilter] = useState("all");
  const [tab, setTab] = useState("scores");
  const now = new Date();
  const monthStart = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-01`;
  const monthName = now.toLocaleString("en-GB",{month:"long"});

  const allStaff = [...(state.nurses||NURSES_BASE).filter(n=>!n.maternity&&!n.archived),...RECS_BASE];
  const filtered = siteFilter==="all" ? allStaff : allStaff.filter(s=>s.site===siteFilter);

  // Score everyone
  const scored = filtered.map(s=>({
    ...s,
    pp: calcPulsePeopleScore(s.id, state),
  })).sort((a,b)=>b.pp.score-a.pp.score);

  // Auto drafts
  const autoDrafts = autoGenerateHeroNominations(state);
  const pendingDrafts = autoDrafts.filter(d=>
    !(state.unsungHeroes||[]).some(h=>h.id===d.id)
  );

  // Frequent heroes — people recognised in 2+ distinct calendar months
  const frequentHeroes = allStaff
    .map(s=>{
      const noms = (state.unsungHeroes||[]).filter(h=>h.staffId===s.id&&h.status==="approved");
      const distinctMonths = [...new Set(noms.map(h=>h.month).filter(Boolean))];
      return {...s, count:distinctMonths.length, totalNoms:noms.length, months:distinctMonths};
    })
    .filter(s=>s.count>=2)
    .sort((a,b)=>b.count-a.count);

  // Person of the month — highest scorer this month
  const potm = scored[0];

  function approveDraft(draft) {
    set(s=>({...s, unsungHeroes:[...(s.unsungHeroes||[]),{...draft,status:"approved"}]}));
  }
  function dismissDraft(draft) {
    // Mark as dismissed so it doesn't re-appear — store in a dismissed list
    set(s=>({...s, dismissedDrafts:[...(s.dismissedDrafts||[]),draft.id]}));
  }
  function confirmPOTM(person) {
    const entry = {
      id:"potm_"+monthStart,
      staffId:person.id, name:person.name, site:person.site,
      reason:`Pulse Person of the Month — ${monthName}. Outstanding contribution: ${[
        person.pp.otHrs>0&&`${person.pp.otHrs.toFixed(1)}h OT`,
        person.pp.extraShifts>0&&`${person.pp.extraShifts} extra shift${person.pp.extraShifts>1?"s":""}`,
        person.pp.availDays>0&&`volunteered ${person.pp.availDays} days`,
        person.pp.cleanBonus>0&&"clean attendance",
      ].filter(Boolean).join(", ")}.`,
      nominatedBy:"Omni Rota (Person of Month)",
      type:"potm",
      status:"approved",
      dateISO:new Date().toISOString().split("T")[0],
      date:new Date().toLocaleDateString("en-GB",{day:"numeric",month:"short"}),
      month:monthStart.slice(0,7),
    };
    set(s=>({...s, unsungHeroes:[...(s.unsungHeroes||[]),entry]}));
  }

  const alreadyPOTM = (state.unsungHeroes||[]).some(h=>h.type==="potm"&&h.month===monthStart.slice(0,7));
  const dismissedDrafts = state.dismissedDrafts||[];
  const activeDrafts = pendingDrafts.filter(d=>!dismissedDrafts.includes(d.id));

  const TIER_LABEL = score => score>=40?"Outstanding":score>=20?"Strong":score>=8?"Solid":"—";
  const TIER_COLOR = score => score>=40?C_GOLD:score>=20?C.green:score>=8?C.teal:C.muted;

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:24,color:C.heading,marginBottom:2}}>Pulse People</h2>
          <div style={{fontSize:12,color:C.dim}}>Recognition, scores & culture data — {monthName}</div>
        </div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {[["all","All"],...Object.entries(SITES).map(([id,s])=>[id,s.name])].map(([id,label])=>(
            <button key={id} onClick={()=>setSiteFilter(id)} style={{padding:"5px 14px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${siteFilter===id?(SITES[id]?.color||C.teal):C.border}`,background:siteFilter===id?(SITES[id]?.color||C.teal)+"18":"transparent",color:siteFilter===id?(SITES[id]?.color||C.teal):C.mid}}>{label}</button>
          ))}
        </div>
      </div>

      {/* Sub tabs */}
      <div style={{display:"flex",gap:2,borderBottom:`1px solid ${C.border}`,marginBottom:20}}>
        {[["scores","People Scores"],["drafts",`Auto Nominations${activeDrafts.length>0?" ("+activeDrafts.length+")":""}`],["wall","Heroes Wall"],["frequent","Frequent Heroes"]].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} style={{background:"transparent",border:"none",padding:"8px 16px",cursor:"pointer",fontFamily:"inherit",fontSize:13,fontWeight:600,color:tab===id?C_GOLD:C.mid,borderBottom:tab===id?`2px solid ${C_GOLD}`:"2px solid transparent",whiteSpace:"nowrap"}}>{label}</button>
        ))}
      </div>

      {/* SCORES TAB */}
      {tab==="scores"&&(
        <div>
          {/* Person of the month */}
          {potm&&potm.pp.score>0&&!alreadyPOTM&&isPrincipal&&(
            <div style={{background:`linear-gradient(135deg,${C_GOLD}20,#a78bfa18)`,border:`1.5px solid ${C_GOLD}55`,borderRadius:14,padding:"16px 20px",marginBottom:20,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
              <div>
                <div style={{fontSize:10,fontWeight:700,color:C_GOLD,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>✨ Suggested person of the month</div>
                <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading,marginBottom:2}}>{potm.name}</div>
                <div style={{fontSize:12,color:C.mid}}>{SITES[potm.site]?.name} · Score {potm.pp.score} · {[
                  potm.pp.otHrs>0&&`${potm.pp.otHrs.toFixed(1)}h OT`,
                  potm.pp.extraShifts>0&&`${potm.pp.extraShifts} extra`,
                  potm.pp.availDays>0&&`${potm.pp.availDays} days volunteered`,
                  potm.pp.cleanBonus>0&&"clean month",
                ].filter(Boolean).join(" · ")}</div>
              </div>
              <button onClick={()=>confirmPOTM(potm)} style={{...BTN(C_GOLD),padding:"10px 20px",fontWeight:700}}>Confirm POTM</button>
            </div>
          )}
          {alreadyPOTM&&(
            <div style={{background:C_GOLD+"12",border:`1px solid ${C_GOLD}33`,borderRadius:10,padding:"10px 16px",marginBottom:16,fontSize:12,color:C_GOLD,fontWeight:600}}>
              Person of the Month confirmed for {monthName}
            </div>
          )}

          {/* Leaderboard */}
          <div style={{overflowX:"auto"}}>
            <table style={{borderCollapse:"collapse",width:"100%",minWidth:480}}>
              <thead>
                <tr>{["","Name","Site","Score this month","OT hrs","Extra shifts","Avail days","All-time noms",""].map(h=>(
                  <th key={h} style={{padding:"6px 10px",fontSize:9,fontWeight:700,color:C.dim,textAlign:"left",textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:`1px solid ${C.border}`,whiteSpace:"nowrap"}}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {scored.map((s,i)=>{
                  const pp = s.pp;
                  const tierLabel = TIER_LABEL(pp.score);
                  const tierColor = TIER_COLOR(pp.score);
                  const isFrequent = pp.allTimeNoms>=3;
                  return (
                    <tr key={s.id} style={{background:i===0&&pp.score>0?C_GOLD+"08":"transparent"}}>
                      <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.border2}`,fontSize:14}}>
                        {i===0&&pp.score>0?"①":i===1&&pp.score>0?"②":i===2&&pp.score>0?"③":""}
                      </td>
                      <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.border2}`}}>
                        <div style={{fontSize:12,fontWeight:700,color:C.text}}>{s.name}</div>
                        {isFrequent&&<div style={{fontSize:9,color:C_GOLD,fontWeight:700}}>{pp.allTimeNoms}× hero</div>}
                      </td>
                      <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.border2}`,fontSize:11,color:SITES[s.site]?.color}}>{SITES[s.site]?.name}</td>
                      <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.border2}`}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <div style={{width:48,height:5,background:C.border,borderRadius:99,overflow:"hidden"}}>
                            <div style={{width:`${Math.min(pp.score/60*100,100)}%`,height:"100%",background:tierColor,borderRadius:99}}/>
                          </div>
                          <span style={{fontSize:11,fontWeight:700,color:tierColor}}>{pp.score}</span>
                          {pp.score>0&&<span style={{fontSize:9,color:tierColor}}>{tierLabel}</span>}
                        </div>
                      </td>
                      <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.border2}`,fontSize:12,color:pp.otHrs>0?C.warn:C.muted}}>{pp.otHrs>0?`+${pp.otHrs.toFixed(1)}h`:"—"}</td>
                      <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.border2}`,fontSize:12,color:pp.extraShifts>0?C.brook:C.muted}}>{pp.extraShifts>0?pp.extraShifts:"—"}</td>
                      <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.border2}`,fontSize:12,color:pp.availDays>0?C.teal:C.muted}}>{pp.availDays>0?pp.availDays:"—"}</td>
                      <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.border2}`}}>
                        {pp.allTimeNoms>0?<Pill c={pp.allTimeNoms>=3?C_GOLD:C.green} t={`${pp.allTimeNoms}×`} sm/>:<span style={{fontSize:11,color:C.muted}}>--</span>}
                      </td>
                      <td style={{padding:"8px 10px",borderBottom:`1px solid ${C.border2}`}}>
                        {pp.welfareOpen&&<Pill c={C.warn} t="Welfare open" sm/>}
                        {pp.cleanBonus>0&&<Pill c={C.green} t="Clean" sm/>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{fontSize:10,color:C.muted,marginTop:12,lineHeight:1.6}}>
            Scores are private — not visible to staff. Based on: OT done (4pts/hr), extra shifts (10pts), days volunteered (2pts), hero nominations received (15pts), clean month (20pts), unpaid leave help (8pts/day).
          </div>
        </div>
      )}

      {/* DRAFT AUTO-NOMINATIONS */}
      {tab==="drafts"&&(
        <div>
          <div style={{fontSize:12,color:C.dim,marginBottom:16}}>Omni Rota spotted these from this month's data. Review and approve or dismiss each one — approved nominations show on the Heroes Wall and notify the person.</div>
          {activeDrafts.length===0&&(
            <div style={{...CS,textAlign:"center",padding:40,color:C.dim}}>
              <div style={{fontSize:32,marginBottom:12}}>✅</div>
              <div>No pending auto-nominations this month</div>
              <div style={{fontSize:11,color:C.muted,marginTop:4}}>Omni Rota will spot them as OT and extra shifts are logged</div>
            </div>
          )}
          {activeDrafts.map(d=>(
            <div key={d.id} style={{...CS,marginBottom:10,padding:"16px",borderLeft:`3px solid ${C_GOLD}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:10,marginBottom:10}}>
                <div>
                  <div style={{fontWeight:700,fontSize:14,color:C.heading,marginBottom:2}}>{d.name}</div>
                  <div style={{fontSize:11,color:SITES[d.site]?.color,marginBottom:6}}>{SITES[d.site]?.name} · Auto-generated</div>
                  <div style={{fontSize:12,color:C.soft,fontStyle:"italic",lineHeight:1.5}}>"{d.reason}"</div>
                </div>
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>approveDraft(d)} style={{...BTN(C_GOLD),flex:1,padding:"9px 0",fontSize:12,fontWeight:700}}>Approve & post</button>
                <button onClick={()=>dismissDraft(d)} style={{...BTN(C.dim,true),padding:"9px 14px",fontSize:12}}>Dismiss</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* HEROES WALL */}
      {tab==="wall"&&(()=>{
        const heroes = [...(state.unsungHeroes||[])].reverse();
        const byMonth = {};
        heroes.forEach(h=>{
          const m = h.month||(h.dateISO?h.dateISO.slice(0,7):h.date?.slice(-8))||"other";
          if(!byMonth[m])byMonth[m]=[];
          byMonth[m].push(h);
        });
        return (
          <div>
            {Object.keys(byMonth).length===0&&<div style={{...CS,textAlign:"center",padding:40,color:C.dim}}>No nominations yet — approve some from the Auto Nominations tab!</div>}
            {Object.entries(byMonth).sort(([a],[b])=>b.localeCompare(a)).map(([month,items])=>(
              <div key={month} style={{marginBottom:24}}>
                <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>
                  {month||"Recent"}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:10}}>
                  {items.map((h,i)=>(
                    <div key={i} style={{...CS,background:h.type==="potm"?`linear-gradient(135deg,${C_GOLD}10,#a78bfa10)`:h.type==="auto"?"rgba(245,197,24,0.06)":C.surface,border:`1px solid ${h.type==="potm"?C_GOLD+"44":h.type==="auto"?C_GOLD+"22":C.border}`}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                        <span style={{fontSize:22}}>{h.type==="potm"?"★":"·"}</span>
                        <div>
                          <div style={{fontWeight:700,fontSize:13,color:C.heading}}>{h.name}</div>
                          <div style={{fontSize:10,color:SITES[h.site]?.color}}>{SITES[h.site]?.name}</div>
                        </div>
                        {h.type==="potm"&&<Pill c={C_GOLD} t="POTM" sm/>}
                        {h.type==="auto"&&<Pill c={C_GOLD} t="Auto" sm/>}
                      </div>
                      <div style={{fontSize:12,color:C.soft,fontStyle:"italic",lineHeight:1.5,marginBottom:6}}>"{h.reason}"</div>
                      <div style={{fontSize:10,color:C.dim}}>By {h.nominatedBy} · {h.date}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* FREQUENT HEROES */}
      {tab==="frequent"&&(
        <div>
          <div style={{fontSize:12,color:C.dim,marginBottom:16}}>Staff recognised in multiple months — your culture carriers. Nominations reset each month; the Wall keeps the full history. Consider a conversation, a pay review, or just a really good thank-you.</div>
          {frequentHeroes.length===0&&(
            <div style={{...CS,textAlign:"center",padding:40,color:C.dim}}>
              <div style={{fontSize:32,marginBottom:12}}></div>
              <div>Building the picture — nominations will accumulate over time</div>
            </div>
          )}
          {frequentHeroes.map((s,i)=>{
            const allHeroEntries = (state.unsungHeroes||[]).filter(h=>h.staffId===s.id);
            const lastReason = allHeroEntries[allHeroEntries.length-1]?.reason;
            return (
              <div key={s.id} style={{...CS,marginBottom:10,padding:"16px",borderLeft:`3px solid ${i<3?C_GOLD:C.green}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:10}}>
                  <div>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                      <span style={{fontSize:18}}>{i===0?"①":i===1?"②":i===2?"③":"·"}</span>
                      <div style={{fontWeight:700,fontSize:14,color:C.heading}}>{s.name}</div>
                      <Pill c={i<3?C_GOLD:C.green} t={`${s.count} month${s.count!==1?"s":""}`} sm/>
                    </div>
                    <div style={{fontSize:11,color:SITES[s.site]?.color,marginBottom:4}}>{SITES[s.site]?.name} · {s.area||s.type}</div>
                    {lastReason&&<div style={{fontSize:11,color:C.dim,fontStyle:"italic"}}>Most recently: "{lastReason.slice(0,80)}{lastReason.length>80?"…":""}"</div>}
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:28,color:i<3?C_GOLD:C.green,lineHeight:1}}>{s.count}</div>
                    <div style={{fontSize:9,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em"}}>months recognised</div>
                  </div>
                </div>
                {s.count>=3&&isPrincipal&&(
                  <div style={{marginTop:10,padding:"8px 12px",background:C_GOLD+"10",borderRadius:8,fontSize:11,color:C_GOLD,fontWeight:600}}>
                    Consider formal recognition, a pay review conversation, or a note in their staff file
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NominateForm({allStaff,nominatedBy,onSave,onClose}) {
  const [f,setF] = useState({staffId:"",reason:"",nominatedBy:nominatedBy||"Conor O'Neill"});
  const person = allStaff.find(s=>s.id===f.staffId);
  return (
    <div>
      <div style={{marginBottom:12}}>
        <label style={LB}>Staff member</label>
        <select value={f.staffId} onChange={e=>setF(p=>({...p,staffId:e.target.value}))} style={IS}>
          <option value="">Select person…</option>
          {Object.entries(SITES).map(([sid,si])=>(
            <optgroup key={sid} label={si.name}>
              {allStaff.filter(s=>s.site===sid).map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
            </optgroup>
          ))}
        </select>
      </div>
      <div style={{marginBottom:12}}>
        <label style={LB}>Why do they deserve recognition?</label>
        <textarea value={f.reason} onChange={e=>setF(p=>({...p,reason:e.target.value}))} rows={3} placeholder="What did they do that made a difference?" style={{...IS,resize:"none",lineHeight:1.6}}/>
      </div>
      <div style={{marginBottom:20}}>
        <label style={LB}>Nominated by</label>
        <input value={f.nominatedBy} onChange={e=>setF(p=>({...p,nominatedBy:e.target.value}))} style={IS}/>
      </div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>{if(!f.staffId||!f.reason.trim())return;onSave({...f,name:person.name,site:person.site,date:new Date().toLocaleDateString("en-GB",{day:"numeric",month:"short"})});}} style={{...BTN(C_GOLD),flex:1,padding:"11px 0",background:C_GOLD}}>Nominate</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </div>
  );
}

function UnsungHeroesBanner({state,set,user,isPrincipal}) {
  const heroes = state.unsungHeroes||[];
  const [nomM,setNomM] = useState(false);
  const allStaff = [...(state.nurses||NURSES_BASE).filter(n=>!n.maternity&&!n.archived),...RECS_BASE];
  const recent = heroes.filter(h=>h.status!=="draft").slice(-3).reverse();
  const autoDrafts = autoGenerateHeroNominations(state);
  const pendingAutoCount = autoDrafts.filter(d=>!(state.dismissedDrafts||[]).includes(d.id)&&!(state.unsungHeroes||[]).some(h=>h.id===d.id)).length;
  const frequentCount = heroes.reduce((acc,h)=>{acc[h.staffId]=(acc[h.staffId]||0)+1;return acc;},{});
  const newFrequent = Object.entries(frequentCount).filter(([,c])=>c>=3).length;
  if (recent.length===0&&!isPrincipal) return null;
  return (
    <div style={{background:"linear-gradient(135deg,"+C_GOLD+"15,#a78bfa15)",border:"1px solid "+C_GOLD+"44",borderRadius:14,padding:"14px 18px",marginBottom:20}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:recent.length>0?10:0}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:20}}>⭐</span>
          <span style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:16,color:C.heading}}>Unsung Heroes</span>
        </div>
        <div style={{display:"flex",gap:6,alignItems:"center"}}>
          {pendingAutoCount>0&&<span style={{background:C_GOLD,color:"#fff",borderRadius:99,fontSize:9,fontWeight:800,padding:"2px 7px"}}>{pendingAutoCount} auto</span>}
          <button onClick={()=>setNomM(true)} style={{...BTN(C_GOLD,true),fontSize:11,padding:"4px 12px"}}>+ Nominate</button>
        </div>
      </div>
      {recent.length>0 ? recent.map((h,i)=>(
        <div key={i} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"8px 0",borderTop:"1px solid "+C_GOLD+"22"}}>
          <span style={{fontSize:16,flexShrink:0,display:"flex"}}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg></span>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:C.heading}}>{h.name} <span style={{fontSize:11,color:C.dim,fontWeight:400}}>· {SITES[h.site]?.name}</span></div>
            <div style={{fontSize:12,color:C.mid,marginTop:1}}>"{h.reason}"</div>
            <div style={{fontSize:10,color:C.dim,marginTop:2}}>By {h.nominatedBy} · {h.date}</div>
          </div>
        </div>
      )) : <div style={{fontSize:12,color:C.dim,marginTop:4}}>No nominations yet — recognise someone who went above and beyond!</div>}
      {nomM&&(
        <Overlay onClose={()=>setNomM(false)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading,marginBottom:4}}>Nominate Unsung Hero</div>
          <div style={{fontSize:13,color:C.dim,marginBottom:20}}>Call out someone who went above and beyond</div>
          <NominateForm allStaff={allStaff} nominatedBy={user?.name} onSave={h=>{set(s=>({...s,unsungHeroes:[...(s.unsungHeroes||[]),h]}));setNomM(false);}} onClose={()=>setNomM(false)}/>
        </Overlay>
      )}
    </div>
  );
}

/* ══════════════════════════════════
   EDIT EXISTING STAFF
══════════════════════════════════ */
function EditStaffModal({person, onSave, onClose}) {
  const todayStr = new Date().toISOString().split("T")[0];
  const allDays = ["Mon","Tue","Wed","Thu","Fri"];
  const [f, setF] = React.useState({
    name:           person.name  || "",
    hrs:            person.hrs   || 37.5,
    phone:          person.phone || "",
    email:          person.email || "",
    note:           person.note  || "",
    noOT:           person.noOT  || false,
    startDate:      person.startDate || "",
    effectiveDate:  todayStr,
    hrsChangeNote:  "",
    workDays:       person.workDays || allDays,
    sched:          person.sched  || {},
    extraSites:     person.extraSites || [],
  });
  const sf = (k,v) => setF(p=>({...p,[k]:v}));
  const isNurse = !!(person.area || person.status);
  const hrsChanged = f.hrs !== (person.hrs || 37.5);
  const defaultDays = 28;
  const newEntit = Math.round((f.hrs/37.5)*defaultDays*10)/10;

  // Build a quick preview of pro-rated entitlement if hours are changing
  let proRatePreview = null;
  if (hrsChanged && f.effectiveDate) {
    try {
      const lyStart = new Date(new Date().getFullYear(), 0, 1);
      const lyEnd   = new Date(new Date().getFullYear(), 11, 31);
      const lyDays  = 365;
      const changeDate = new Date(f.effectiveDate + "T12:00:00");
      const clampedChange = changeDate < lyStart ? lyStart : changeDate > lyEnd ? lyEnd : changeDate;
      const daysOld = Math.round((clampedChange - lyStart) / 86400000);
      const daysNew = lyDays - daysOld;
      const oldRate = (person.hrs||37.5)/37.5;
      const newRate = f.hrs/37.5;
      const proRated = Math.round(((daysOld/lyDays)*oldRate*defaultDays + (daysNew/lyDays)*newRate*defaultDays)*10)/10;
      proRatePreview = proRated;
    } catch(e) {}
  }

  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,fontWeight:700,color:C.heading,marginBottom:2}}>
        Edit Staff
      </div>
      <div style={{fontSize:12,color:C.dim,marginBottom:18}}>{SITES[person.site]?.name||person.site||"—"}</div>

      {/* Multi-site — only show if practice has more than one site */}
      {Object.keys(SITES).length > 1 && (
        <div style={{marginBottom:12,padding:"10px 14px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:10}}>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Also works at</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {Object.entries(SITES).filter(([id])=>id!==person.site).map(([id,s])=>{
              const on=(f.extraSites||[]).includes(id);
              return (
                <button key={id} onClick={()=>sf("extraSites",on?(f.extraSites||[]).filter(x=>x!==id):[...(f.extraSites||[]),id])}
                  style={{padding:"6px 14px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",
                    border:`1px solid ${on?s.color:C.border}`,background:on?s.color+"18":"transparent",color:on?s.color:C.mid}}>
                  {on?"✓ ":""}{s.name}
                </button>
              );
            })}
          </div>
          {(f.extraSites||[]).length>0&&(
            <div style={{fontSize:10,color:C.dim,marginTop:6}}>Set wage attribution % for multi-site staff in Admin → Wages → Edit rate.</div>
          )}
        </div>
      )}

      <div style={{marginBottom:12}}>
        <label style={LB}>Full name</label>
        <input value={f.name} onChange={e=>sf("name",e.target.value)} placeholder="First Last" style={IS}/>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
        <div>
          <label style={LB}>Contract hours/week</label>
          <input type="number" step="0.5" min="1" max="50" value={f.hrs}
            onChange={e=>sf("hrs",+e.target.value)} style={{...IS,borderColor:hrsChanged?C.teal:undefined}}/>
        </div>
        <div>
          <label style={LB}>Phone</label>
          <input type="tel" value={f.phone} onChange={e=>sf("phone",e.target.value)}
            placeholder="07700 000000" style={IS}/>
        </div>
      </div>

      {/* Hours change section — only shown when hours actually differ */}
      {hrsChanged&&(
        <div style={{background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:12,padding:"14px",marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:700,color:"#00e5a0",marginBottom:10}}>
            Hours changing: {person.hrs||37.5}h → {f.hrs}h/wk
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div>
              <label style={LB}>Effective from</label>
              <input type="date" value={f.effectiveDate} onChange={e=>sf("effectiveDate",e.target.value)} style={IS}/>
            </div>
            <div>
              {proRatePreview!==null&&(
                <div style={{paddingTop:18}}>
                  <div style={{fontSize:11,color:C.dim}}>This year's entitlement</div>
                  <div style={{fontSize:20,fontWeight:800,color:C.teal}}>{proRatePreview}d</div>
                  <div style={{fontSize:10,color:C.dim}}>pro-rated for change date</div>
                </div>
              )}
            </div>
          </div>
          <div>
            <label style={LB}>Reason / note <span style={{fontWeight:400,color:C.dim}}>(optional — goes in audit log)</span></label>
            <input value={f.hrsChangeNote} onChange={e=>sf("hrsChangeNote",e.target.value)}
              placeholder="e.g. Agreed reduction to 3 days, effective 1 Apr" style={IS}/>
          </div>
        </div>
      )}

      {!hrsChanged&&(
        <div style={{fontSize:10,color:C.dim,marginBottom:12,marginTop:-4}}>
          Full year entitlement: {newEntit}d/yr at {f.hrs}h/wk
        </div>
      )}

      <div style={{marginBottom:12}}>
        <label style={LB}>Email</label>
        <input type="email" value={f.email} onChange={e=>sf("email",e.target.value)}
          placeholder="name@email.com" style={IS}/>
      </div>
      <div style={{marginBottom:12}}>
        <label style={LB}>Notes</label>
        <input value={f.note} onChange={e=>sf("note",e.target.value)}
          placeholder="e.g. RA trained, no Fridays, etc." style={IS}/>
      </div>

      {/* Start date — affects pro-rated holiday entitlement */}
      <div style={{marginBottom:14,padding:"12px 14px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:10}}>
        <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Start date</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,alignItems:"center"}}>
          <div>
            <label style={LB}>Employment start date</label>
            <input type="date" value={f.startDate} onChange={e=>sf("startDate",e.target.value)} style={IS}/>
            <div style={{fontSize:10,color:C.dim,marginTop:4}}>Used to pro-rate holiday entitlement for mid-year starters</div>
          </div>
          {f.startDate&&(()=>{
            try {
              const lyStart = new Date(new Date().getFullYear(),0,1);
              const lyEnd = new Date(new Date().getFullYear(),11,31);
              const lyStartStr = lyStart.toISOString().split("T")[0];
              const start = new Date(f.startDate+"T12:00:00");
              if (f.startDate > lyStartStr) {
                const ly0Days = Math.round((lyEnd - lyStart) / 86400000) + 1;
                const remaining = Math.round((lyEnd - start) / 86400000) + 1;
                const base = Math.round(((f.hrs||37.5)/37.5)*28*10)/10;
                const proRated = Math.round((remaining / ly0Days) * base * 10) / 10;
                return (
                  <div style={{textAlign:"center"}}>
                    <div style={{fontSize:9,color:C.teal,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em"}}>Pro-rated entitlement</div>
                    <div style={{fontSize:26,fontWeight:800,color:C.teal}}>{proRated}d</div>
                    <div style={{fontSize:10,color:C.dim}}>this leave year</div>
                  </div>
                );
              }
              return <div style={{fontSize:11,color:C.dim}}>Full entitlement applies (started before this year)</div>;
            } catch(e) { return null; }
          })()}
        </div>
      </div>
      {/* Working days */}
      <div style={{marginBottom:16,padding:"12px 14px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:10}}>
        <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Working days</div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {["Mon","Tue","Wed","Thu","Fri"].map(d=>{
            const on = (f.workDays||["Mon","Tue","Wed","Thu","Fri"]).includes(d);
            return (
              <button key={d} onClick={()=>{
                const wd = f.workDays||["Mon","Tue","Wed","Thu","Fri"];
                sf("workDays", on ? wd.filter(x=>x!==d) : [...wd,d].sort((a,b)=>["Mon","Tue","Wed","Thu","Fri"].indexOf(a)-["Mon","Tue","Wed","Thu","Fri"].indexOf(b)));
              }} style={{padding:"7px 14px",borderRadius:99,fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer",border:`1px solid ${on?C.teal:C.border}`,background:on?C.teal+"18":"transparent",color:on?C.teal:C.mid}}>
                {d}
              </button>
            );
          })}
        </div>
        <div style={{fontSize:10,color:C.dim,marginTop:6}}>Staff not on a selected day won't be included in the rota for that day. Defaults to all 5 days.</div>
      </div>

      {isNurse&&(
        <label style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,cursor:"pointer"}}>
          <input type="checkbox" checked={f.noOT} onChange={e=>sf("noOT",e.target.checked)}
            style={{width:16,height:16,accentColor:C.teal}}/>
          <div>
            <div style={{fontSize:12,fontWeight:600,color:C.text}}>No overtime</div>
            <div style={{fontSize:11,color:C.dim}}>Shows "No OT" badge, excluded from overtime suggestions</div>
          </div>
        </label>
      )}

      {/* Hours history */}
      {(person.hrsHistory||[]).length>0&&(
        <div style={{marginBottom:16}}>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:6}}>Hours history</div>
          <div style={{borderRadius:8,border:`1px solid ${C.border}`,overflow:"hidden"}}>
            {[...(person.hrsHistory||[])].sort((a,b)=>b.from.localeCompare(a.from)).map((h,i)=>(
              <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 12px",borderBottom:i<(person.hrsHistory.length-1)?`1px solid ${C.border2}`:"none",fontSize:11}}>
                <span style={{color:C.text,fontWeight:600}}>{h.hrs}h/wk</span>
                <span style={{color:C.dim}}>from {new Date(h.from+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}</span>
                <span style={{color:C.muted,fontSize:10}}>{h.changedBy}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>onSave(person,f)} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Save Changes</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}

/* ══════════════════════════════════
   ADD / ARCHIVE STAFF (Feature 7b)
══════════════════════════════════ */
function AddStaffModal({onSave,onClose}) {
  const defaultSite = Object.keys(SITES)[0] || "main";
  const [f,setF] = useState({name:"",site:defaultSite,extraSites:[],type:"nurse",area:"GDS",hrs:37.5,status:"trainee",phone:"",email:"",startDate:"",payrollAdded:false,notes:"",workDays:["Mon","Tue","Wed","Thu","Fri"],sched:{},ra:false,iv:false,safeguardingL3:false,fireMarshal:false,firstAider:false,keyholder:false,hourlyRate:"",});
  const sf=(k,v)=>setF(p=>({...p,[k]:v}));
  const [warn,setWarn] = useState(null);

  const proRated = React.useMemo(()=>{
    if (!f.startDate) return null;
    const start = new Date(f.startDate+"T12:00:00");
    const lyEnd = new Date(new Date().getFullYear(),11,31);
    const lyStart = new Date(new Date().getFullYear(),0,1);
    if (start <= lyStart || start > lyEnd) return null;
    const remainingDays = Math.round((lyEnd - start) / 86400000) + 1;
    const base = Math.round(28 * (f.hrs/37.5) * 10) / 10;
    return Math.round((remainingDays / 365) * base * 10) / 10;
  }, [f.startDate, f.hrs]);

  function attemptSave() {
    if (!f.name.trim()) { setWarn("Please enter a name before saving."); return; }
    const warnings = [];
    if (!f.hrs || f.hrs < 1) warnings.push("No contract hours set — they won't appear correctly on payroll.");
    if (f.type==="nurse" && f.status==="trainee") warnings.push("Trainee level set — update once competency is confirmed.");
    if (!f.startDate) warnings.push("No start date — holiday entitlement can't be pro-rated.");
    if (warnings.length > 0 && !warn) { setWarn(warnings.join(" ")); return; }
    onSave({...f, proRatedEntitlement: proRated});
  }

  // Clinician route — hand off to dedicated onboarding
  if (f.type==="clinician") {
    return (
      <Overlay onClose={onClose}>
        <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Add a Clinician</div>
        <div style={{fontSize:12,color:C.dim,marginBottom:20}}>Clinicians have their own setup flow — schedules, session types and nurse requirements are configured there.</div>
        <div style={{background:"rgba(58,173,152,0.08)",border:"1px solid rgba(58,173,152,0.2)",borderRadius:12,padding:"16px",marginBottom:20}}>
          <div style={{fontSize:13,fontWeight:700,color:C.teal,marginBottom:6}}>What you'll set up:</div>
          <div style={{fontSize:12,color:"rgba(255,255,255,0.6)",lineHeight:1.8}}>
            Name, site &amp; clinician type · Session schedule (per day) · Split-day AM/PM sessions · Two-week alternating patterns · Nurse requirements (RA / IV) · Preferred nurse
          </div>
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={()=>onSave({type:"clinician"})} style={{...BTN(C.teal),flex:1,padding:"12px 0",fontSize:13}}>Open Clinician Setup →</button>
          <button onClick={()=>sf("type","nurse")} style={BTN(C.dim,true)}>← Back</button>
        </div>
      </Overlay>
    );
  }

  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Add Staff Member</div>
      <div style={{fontSize:12,color:C.dim,marginBottom:16}}>Adds someone to your rota — nurses, reception, TCO, admin. They'll get an Omni login automatically. Everything can be updated in their profile later.</div>

      {/* Section 1 — Identity */}
      <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Identity</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
        <div><label style={LB}>Full name *</label><input value={f.name} onChange={e=>sf("name",e.target.value)} placeholder="First Last" style={IS}/></div>
        <div><label style={LB}>Role type</label>
          <select value={f.type} onChange={e=>sf("type",e.target.value)} style={IS}>
            <option value="nurse">Nurse</option>
            <option value="reception">Reception</option>
            <option value="tco">TCO</option>
            <option value="manager">Manager</option>
            <option value="clinician">Clinician →</option>
            <option value="admin">Admin / Other</option>
          </select>
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
        <div><label style={LB}>Primary site</label>
          <select value={f.site} onChange={e=>sf("site",e.target.value)} style={IS}>
            {Object.entries(SITES).map(([id,s])=><option key={id} value={id}>{s.name}</option>)}
          </select>
        </div>
        {f.type==="nurse"&&(
          <div><label style={LB}>Area</label>
            <select value={f.area} onChange={e=>sf("area",e.target.value)} style={IS}>
              <option value="GDS">GDS</option><option value="MOS">MOS</option><option value="MOS/GDS">Both</option>
            </select>
          </div>
        )}
      </div>
      {f.type==="nurse"&&(
        <div style={{marginBottom:12}}>
          <label style={LB}>Training level</label>
          <select value={f.status} onChange={e=>sf("status",e.target.value)} style={IS}>
            {Object.entries(STATUS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
      )}

      {/* Section 2 — Contract */}
      <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10,marginTop:4}}>Contract</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
        <div><label style={LB}>Hours / week</label><input type="number" step="0.5" min="1" max="50" value={f.hrs} onChange={e=>sf("hrs",+e.target.value)} style={IS}/></div>
        <div><label style={LB}>Start date</label><input type="date" value={f.startDate} onChange={e=>sf("startDate",e.target.value)} style={IS}/></div>
      </div>
      {proRated!==null&&(
        <div style={{background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:8,padding:"8px 12px",marginBottom:12,fontSize:12,color:C.green}}>
          Pro-rated holiday this year: <strong>{proRated}d</strong> <span style={{color:C.dim,fontSize:11}}>(from {f.startDate} · full year {Math.round(28*(f.hrs/37.5)*10)/10}d)</span>
        </div>
      )}
      <div style={{marginBottom:12,padding:"12px 14px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:10}}>
        <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Working days</div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {["Mon","Tue","Wed","Thu","Fri"].map(d=>{
            const on=(f.workDays||[]).includes(d);
            return (
              <button key={d} onClick={()=>{const wd=f.workDays||[];sf("workDays",on?wd.filter(x=>x!==d):[...wd,d].sort((a,b)=>["Mon","Tue","Wed","Thu","Fri"].indexOf(a)-["Mon","Tue","Wed","Thu","Fri"].indexOf(b)));}}
                style={{padding:"7px 14px",borderRadius:99,fontFamily:"inherit",fontSize:12,fontWeight:700,cursor:"pointer",border:`1px solid ${on?C.teal:C.border}`,background:on?C.teal+"18":"transparent",color:on?C.teal:C.mid}}>
                {d}
              </button>
            );
          })}
        </div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12}}>
        <div><label style={LB}>Phone</label><input type="tel" value={f.phone} onChange={e=>sf("phone",e.target.value)} placeholder="07700 000000" style={IS}/></div>
        <div><label style={LB}>Email</label><input type="email" value={f.email} onChange={e=>sf("email",e.target.value)} placeholder="name@email.com" style={IS}/></div>
      </div>

      {/* Section 3 — Qualifications (nurses) */}
      {f.type==="nurse"&&(
        <div>
          <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10,marginTop:4}}>Qualifications &amp; compliance</div>
          <div style={{display:"flex",gap:16,marginBottom:10}}>
            {[["ra","RA"],["iv","IV Sedation"]].map(([k,label])=>(
              <label key={k} style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer"}}>
                <input type="checkbox" checked={!!f[k]} onChange={e=>sf(k,e.target.checked)} style={{width:15,height:15,accentColor:C.teal}}/>
                <span style={{fontSize:12,fontWeight:600,color:C.text}}>{label}</span>
              </label>
            ))}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
            {[["safeguardingL3","🛡 L3 Safeguarding"],["firstAider","🧯 First Aider"],["fireMarshal","🔥 Fire Marshal"],["keyholder","🔑 Keyholder"]].map(([k,label])=>(
              <label key={k} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",borderRadius:8,border:`1px solid ${f[k]?"rgba(0,229,160,0.3)":C.border}`,background:f[k]?"rgba(0,229,160,0.06)":"transparent",cursor:"pointer"}}>
                <input type="checkbox" checked={!!f[k]} onChange={e=>sf(k,e.target.checked)} style={{width:14,height:14,accentColor:C.teal,flexShrink:0}}/>
                <span style={{fontSize:11,fontWeight:700,color:f[k]?C.green:C.text}}>{label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Section 4 — Pay */}
      <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10,marginTop:4}}>Pay rate <span style={{fontWeight:400,textTransform:"none",letterSpacing:0,color:C.muted,fontSize:10}}>— optional, set fully in Finance later</span></div>
      <div style={{marginBottom:12}}>
        <label style={LB}>Hourly rate (£)</label>
        <input type="number" step="0.01" min="0" value={f.hourlyRate} onChange={e=>sf("hourlyRate",e.target.value)}
          placeholder="e.g. 12.50" style={IS}/>
        {f.hourlyRate>0&&<div style={{fontSize:11,color:C.teal,marginTop:4}}>Est. monthly: £{Math.round(+f.hourlyRate*(f.hrs||37.5)*52/12).toLocaleString()}</div>}
      </div>

      {/* Section 5 — Notes */}
      <div style={{marginBottom:12}}><label style={LB}>Notes <span style={{fontWeight:400,color:C.dim}}>(optional)</span></label><input value={f.notes} onChange={e=>sf("notes",e.target.value)} placeholder="e.g. Flexible pool, no Fridays" style={IS}/></div>

      <div style={{background:"rgba(245,197,24,0.1)",border:"1px solid #fde68a",borderRadius:10,padding:"10px 14px",marginBottom:16}}>
        <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
          <input type="checkbox" checked={f.payrollAdded} onChange={e=>sf("payrollAdded",e.target.checked)} style={{width:16,height:16,accentColor:C.teal}}/>
          <div>
            <div style={{fontSize:12,fontWeight:700,color:"#f5c518"}}>Added to payroll</div>
            <div style={{fontSize:11,color:"#b45309"}}>Tick once set up in your payroll system.</div>
          </div>
        </label>
      </div>

      {/* Warning banner */}
      {warn&&(
        <div style={{background:"rgba(245,197,24,0.1)",border:"1px solid rgba(245,197,24,0.3)",borderRadius:10,padding:"10px 14px",marginBottom:12,fontSize:12,color:C.warn}}>
          ⚠️ {warn} <span style={{color:C.dim}}>You can still save and update later.</span>
        </div>
      )}

      <div style={{display:"flex",gap:8}}>
        <button onClick={attemptSave} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>
          {warn?"Save anyway":"Add staff member"}
        </button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}

/* ══════════════════════════════════
   DENTIST PORTAL
══════════════════════════════════ */
function DentistPortal({user, state, set, onLogout}) {
  const clinician = getClinicians(state).find(c=>c.id===user.staffId);
  const [tab, setTab] = useState("sessions");
  const [issueM, setIssueM] = useState(false);
  const todayStr = new Date().toISOString().split("T")[0];
  // v11.0 — graceful loading state. Same race-condition mitigation as Portal.
  // State load on first arrival can briefly return no clinician; show loading
  // for 3s before falling through to a real error message.
  const [showClinError, setShowClinError] = useState(false);
  useEffect(() => {
    if (clinician) return;
    const t = setTimeout(() => setShowClinError(true), 3000);
    return () => clearTimeout(t);
  }, [clinician]);
  if (!clinician) {
    if (!showClinError) {
      return <div style={{padding:32,color:C.dim,fontSize:14}}>Loading your profile…</div>;
    }
    return (
      <div style={{padding:32,color:C.danger,fontSize:14,lineHeight:1.6}}>
        We couldn&#39;t find your clinician profile.
        <div style={{marginTop:12,color:C.soft,fontSize:13}}>
          Try refreshing the page. If this keeps happening, contact your manager.
        </div>
      </div>
    );
  }
  const sd = SITES[clinician.site];
  const reqs = state.clinicianRequirements?.[clinician.id]||null;
  const workDays = ["Mon","Tue","Wed","Thu","Fri"].filter(d=>clinician.sched?.[d]);
  const clinLeave = ((state.clinicianLeave||{})[clinician.id]||[]).filter(l=>l.status!=="declined");
  const leaveSet = new Set(clinLeave.map(l=>l.date));
  // Next 4 weeks of working days for this clinician
  const upDays = CAL.days.filter(d=>d.date>=todayStr&&!d.bh&&clinician.sched?.[d.dow]).slice(0,20);
  const wkMap = {}; upDays.forEach(d=>{const wk=CAL.weeks.find(w=>w.days.some(wd=>wd.date===d.date));const key=wk?.start||d.date;if(!wkMap[key])wkMap[key]={start:key,days:[]};wkMap[key].days.push(d);});
  const upWeeks = Object.values(wkMap).slice(0,4);

  return (
    <div style={{minHeight:"100vh",background:C.bg,maxWidth:480,margin:"0 auto",display:"flex",flexDirection:"column"}}>
      <div style={{background:C.surface,borderBottom:`1px solid ${C.border}`,padding:"14px 18px",display:"flex",justifyContent:"space-between",alignItems:"center",position:"sticky",top:0,zIndex:100}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <LogoMark size={42}/>
          <div>
            <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:16,color:C.heading,lineHeight:1.2}}>{clinician.name}</div>
            <div style={{fontSize:11,color:sd?.color||C.mid}}>{sd?.name} · {clinician.type}</div>
          </div>
        </div>
        <button onClick={onLogout} style={{...BTN(C.dim,true),fontSize:11,padding:"5px 10px"}}>Sign out</button>
      </div>

      <div style={{flex:1,padding:"14px 14px 76px",overflowY:"auto"}}>
        {/* ── REPEAT-ISSUE PATTERN BANNER (depersonalised, visible to clinician) ── */}
        {(()=>{
          const ago30 = new Date(Date.now()-30*24*60*60*1000).toISOString().split("T")[0];
          const siteIssues = (state.issues||[]).filter(i=>i.site===clinician.site&&i.date>=ago30);
          const catCounts = {};
          siteIssues.forEach(i=>{ catCounts[i.category]=(catCounts[i.category]||0)+1; });
          const repeats = Object.entries(catCounts).filter(([,n])=>n>=2);
          if (repeats.length===0) return null;
          return (
            <div style={{...CS,marginBottom:14,borderLeft:`3px solid ${C.warn}`,background:"rgba(245,197,24,0.06)"}}>
              <div style={{fontSize:11,fontWeight:700,color:C.warn,marginBottom:6}}>Site notice — recurring concerns</div>
              {repeats.map(([cat,n])=>(
                <div key={cat} style={{fontSize:11,color:C.soft,marginBottom:2}}>• "{cat}" raised {n} times this month</div>
              ))}
              <div style={{fontSize:10,color:C.dim,marginTop:6,lineHeight:1.5}}>Your principal is reviewing. If you've seen something contributing to this — or anything else — use Report Issue (bottom right). You can report anonymously on Tier 3 (Serious).</div>
            </div>
          );
        })()}

        {/* Session requirements card — always visible */}
        {reqs&&(()=>{
          const defRL = reqLabel(reqs.default||[]);
          const dayOvs = Object.entries(reqs.overrides||{}).filter(([,v])=>v.length>0);
          return (
            <div style={{background:defRL?defRL.color+"12":C.surface,border:`1px solid ${defRL?defRL.color+"44":C.border}`,borderRadius:12,padding:"12px 16px",marginBottom:14}}>
              <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Your Session Requirements</div>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:dayOvs.length>0||reqs.note?8:0}}>
                <span style={{fontSize:26}}>{defRL?.short==="IV"?"IV":"RA"}</span>
                <div>
                  <div style={{fontSize:14,fontWeight:700,color:defRL?.color||C.text}}>{defRL?.label||"No requirement set"}</div>
                  <div style={{fontSize:11,color:C.dim}}>Default for all your sessions</div>
                </div>
              </div>
              {dayOvs.map(([d,v])=>{const rl=reqLabel(v);return(
                <div key={d} style={{display:"flex",alignItems:"center",gap:8,padding:"4px 0",borderTop:`1px solid ${C.border2}`}}>
                  <span style={{fontSize:11,fontWeight:700,color:C.mid,minWidth:32}}>{d}</span>
                  <span style={{fontSize:11,fontWeight:600,color:rl?.color}}>{rl?.label}</span>
                  <span style={{fontSize:10,color:C.dim}}>{clinician.sched?.[d]}</span>
                </div>
              );})}
              {reqs.note&&<div style={{fontSize:11,color:C.mid,marginTop:8,padding:"5px 10px",background:"rgba(255,255,255,0.5)",borderRadius:6}}>ℹ️ {reqs.note}</div>}
            </div>
          );
        })()}

        {tab==="sessions"&&(
          <div>
            <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>My Sessions</div>
            <div style={{fontSize:12,color:C.dim,marginBottom:14}}>{sd?.name} — next 4 weeks</div>
            {upWeeks.length===0&&<div style={{...CS,textAlign:"center",padding:32,color:C.dim,fontSize:13}}>No upcoming sessions in this period</div>}
            {upWeeks.map(wk=>(
              <div key={wk.start} style={{...CS,marginBottom:10}}>
                <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>
                  {wk.days[0]?.label.split(" ").slice(1).join(" ")} – {wk.days[wk.days.length-1]?.label.split(" ").slice(1).join(" ")}
                </div>
                {wk.days.map(day=>{
                  const isOff = leaveSet.has(day.date);
                  const nurses = getNursesForSite(state, user.site);
                  const dr = buildDayRota(day.date, clinician.site, nurses, state);
                  const myPair = dr.pairings.find(p=>p.clinician.id===clinician.id);
                  const dayReqs = getClinicianReqs(state, clinician.id, day.dow);
                  const rl = reqLabel(dayReqs);
                  return (
                    <div key={day.date} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 0",borderBottom:`1px solid ${C.border2}`,opacity:isOff?0.45:1}}>
                      <div style={{minWidth:52}}>
                        <div style={{fontSize:12,fontWeight:700,color:isOff?C.dim:C.text}}>{day.dow}</div>
                        <div style={{fontSize:10,color:C.dim}}>{day.label.split(" ").slice(1,3).join(" ")}</div>
                      </div>
                      {isOff?<div style={{display:"flex",alignItems:"center",gap:6}}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.8 19.2L16 11l3.5-3.5C21 6 21 4 19.5 2.5S18 2 16.5 3.5L13 7 4.8 5.2l-1.4 1.4 6.1 3.8L7.2 14H4l-1.5 1.5 3 3 3 3L10 20v-3.2l3.5-2.3 3.8 6.1 1.5-1.4"/></svg><span style={{fontSize:12,color:C.green,fontWeight:600}}>Leave</span></div>
                      :myPair?(
                        <div style={{flex:1}}>
                          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3}}>
                            <span style={{fontSize:12,fontWeight:600,color:C.mid}}>{clinician.sched?.[day.dow]}</span>
                            {rl&&<span style={{fontSize:10,background:rl.color+"18",color:rl.color,border:`1px solid ${rl.color}33`,borderRadius:4,padding:"1px 6px",fontWeight:700}}>{rl.short}</span>}
                          </div>
                          {myPair.nurse?(
                            <div style={{display:"flex",alignItems:"center",gap:6}}>
                              <div style={{width:6,height:6,borderRadius:99,background:C.green,flexShrink:0}}/>
                              <span style={{fontSize:12,color:C.text,fontWeight:600}}>{myPair.nurse.name}</span>
                              {myPair.autoAssigned&&<Pill c={C.warn} t="Cover" sm/>}
                              {dayReqs.length>0&&(()=>{
                                const missing=dayReqs.filter(r=>!myPair.nurse[r]);
                                return missing.length>0
                                  ?<Pill c={C.danger} t={`${missing.join("+")} needed`} sm/>
                                  :<Pill c={C.green} t="Qualified" sm/>;
                              })()}
                            </div>
                          ):<div style={{fontSize:12,color:C.danger,fontWeight:700}}>No nurse assigned</div>}
                        </div>
                      ):<span style={{fontSize:12,color:C.muted}}>Not scheduled</span>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {tab==="schedule"&&(
          <div>
            <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:14}}>My Schedule</div>
            <div style={{...CS}}>
              <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Working days & requirements</div>
              {workDays.map(d=>{
                const dayReqs=getClinicianReqs(state,clinician.id,d);
                const rl=reqLabel(dayReqs);
                return(
                  <div key={d} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 0",borderBottom:`1px solid ${C.border2}`}}>
                    <span style={{minWidth:36,fontSize:13,fontWeight:700,color:C.text}}>{d}</span>
                    <span style={{fontSize:12,color:C.mid,flex:1}}>{clinician.sched?.[d]}</span>
                    {rl?<span style={{fontSize:10,background:rl.color+"18",color:rl.color,border:`1px solid ${rl.color}33`,borderRadius:5,padding:"2px 8px",fontWeight:700}}>{rl.short}</span>
                       :<span style={{fontSize:10,color:C.muted}}>--</span>}
                  </div>
                );
              })}
              {reqs?.note&&<div style={{marginTop:10,fontSize:12,color:C.warn,fontWeight:600}}>ℹ️ {reqs.note}</div>}
            </div>
          </div>
        )}
      </div>

      <div style={{position:"fixed",bottom:0,left:"50%",transform:"translateX(-50%)",width:"100%",maxWidth:480,background:C.surface,borderTop:`1px solid ${C.border}`,display:"flex",zIndex:100}}>
        {[
          {id:"sessions", label:"My Sessions", svg:(c)=>(
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
              <line x1="8" y1="14" x2="8.01" y2="14" strokeWidth="3"/>
              <line x1="12" y1="14" x2="12.01" y2="14" strokeWidth="3"/>
              <line x1="16" y1="14" x2="16.01" y2="14" strokeWidth="3"/>
            </svg>
          )},
          {id:"schedule", label:"Schedule", svg:(c)=>(
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="8" y1="6" x2="21" y2="6"/>
              <line x1="8" y1="12" x2="21" y2="12"/>
              <line x1="8" y1="18" x2="21" y2="18"/>
              <line x1="3" y1="6" x2="3.01" y2="6" strokeWidth="3"/>
              <line x1="3" y1="12" x2="3.01" y2="12" strokeWidth="3"/>
              <line x1="3" y1="18" x2="3.01" y2="18" strokeWidth="3"/>
            </svg>
          )},
        ].map(t=>{
          const active = tab===t.id;
          const col = active ? "#00e5a0" : C.dim;
          return (
            <button key={t.id} onClick={()=>setTab(t.id)}
              style={{flex:1,padding:"9px 0 11px",background:"transparent",
              border:"none",borderTop:active?"2px solid #00e5a0":"2px solid transparent",
              cursor:"pointer",fontFamily:"inherit",
              display:"flex",flexDirection:"column",alignItems:"center",gap:3,
              transition:"border-color 0.15s"}}>
              {t.svg(col)}
              <span style={{fontSize:10,fontWeight:600,color:col}}>{t.label}</span>
            </button>
          );
        })}
        <button onClick={()=>setIssueM(true)} style={{flex:1,padding:"9px 0 11px",background:"transparent",border:"none",cursor:"pointer",fontFamily:"inherit",display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
          <Icon name="warning" size={18} color="#dc2626"/>
          <span style={{fontSize:10,fontWeight:600,color:"#ff4646"}}>Report Issue</span>
        </button>
      </div>
      {issueM&&<IssueReportingModal person={clinician} set={set} onClose={()=>setIssueM(false)}/>}
    </div>
  );
}

/* ══════════════════════════════════
   APP ROOT
══════════════════════════════════ */
/* ── COLLEGE DATES MODAL ── */
function CollegeDatesModal({state, set, onClose}) {
  // Each "entry" is one college block: { traineeId, college, course, day, termStart, termEnd, mode, singleDate }
  const EMPTY_ENTRY = {traineeId:"", college:"", course:"", day:"Thu", termStart:"", termEnd:"", mode:"term", singleDate:""};
  const [entries, setEntries] = useState([{...EMPTY_ENTRY}]);
  const DOW_NAMES = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

  if (!state) return null;

  const allStaffList = [...(state.nurses||NURSES_BASE), ...(state.customStaff||[])];
  const trainees = [...allStaffList,...RECS_BASE]
    .filter(n=>!n.maternity&&!n.archived&&!n._deleted)
    .sort((a,b)=>{const so={sale:0,brooklands:1,timperley:2};return((so[a.site]||0)-(so[b.site]||0))||a.name.localeCompare(b.name);});

  const setEntry = (i, k, v) => setEntries(prev => prev.map((e,idx) => idx===i ? {...e,[k]:v} : e));
  const addEntry = () => setEntries(p=>[...p, {...EMPTY_ENTRY}]);
  const removeEntry = i => setEntries(p=>p.filter((_,idx)=>idx!==i));

  const getDates = (entry) => {
    if (entry.mode==="single") return entry.singleDate ? [entry.singleDate] : [];
    if (!entry.termStart||!entry.termEnd) return [];
    const dates=[], targetDow=DOW_NAMES.indexOf(entry.day);
    const cur=new Date(entry.termStart), end=new Date(entry.termEnd);
    while (cur.getDay()!==targetDow) cur.setDate(cur.getDate()+1);
    while (cur<=end) { dates.push(cur.toISOString().slice(0,10)); cur.setDate(cur.getDate()+7); }
    return dates;
  };

  const canSave = entries.some(e=>e.traineeId && getDates(e).length>0);

  function save() {
    const newEntries = [];
    entries.forEach(e=>{
      if (!e.traineeId) return;
      const dates = getDates(e);
      if (!dates.length) return;
      const person = trainees.find(t=>t.id===e.traineeId);
      const collegeName = e.college || "College";
      const coursePart = e.course ? ` · ${e.course}` : "";
      const dayPart = e.mode==="term" ? ` (${e.day}s)` : "";
      const label = `${person?.name.split(" ")[0]} — ${collegeName}${coursePart}${dayPart}`;
      dates.forEach((date,i)=>newEntries.push({
        id:"cd_"+(Date.now()+newEntries.length+i),
        date, groupLabel:label, staffIds:[e.traineeId],
        college:e.college, course:e.course,
      }));
    });
    if (!newEntries.length) return;
    set(s=>({...s,collegeDates:[...(s.collegeDates||[]),...newEntries]}));
    onClose();
  }

  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:2}}>Add College Days</div>
      <div style={{fontSize:12,color:C.dim,marginBottom:20}}>Add one block per trainee. Each can have a different college, course, day and term dates.</div>

      <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:16}}>
        {entries.map((e,i)=>{
          const dates = getDates(e);
          const person = trainees.find(t=>t.id===e.traineeId);
          return (
            <div key={i} style={{border:`1.5px solid ${C.border}`,borderRadius:12,padding:"14px 16px",background:C.bg,position:"relative"}}>
              {entries.length>1&&(
                <button onClick={()=>removeEntry(i)} style={{position:"absolute",top:10,right:10,background:"none",border:"none",color:C.dim,cursor:"pointer",fontSize:16,lineHeight:1}}>×</button>
              )}

              {/* Trainee */}
              <div style={{marginBottom:10}}>
                <label style={LB}>Trainee</label>
                <select value={e.traineeId} onChange={ev=>setEntry(i,"traineeId",ev.target.value)} style={IS}>
                  <option value="">Select trainee…</option>
                  {trainees.map(t=><option key={t.id} value={t.id}>{t.name} · {SITES[t.site]?.name||t.site}</option>)}
                </select>
              </div>

              {/* College + Course side by side */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                <div>
                  <label style={LB}>College</label>
                  <input value={e.college} onChange={ev=>setEntry(i,"college",ev.target.value)}
                    placeholder="e.g. Salford College" style={IS}/>
                </div>
                <div>
                  <label style={LB}>Course <span style={{fontWeight:400,color:C.dim}}>(optional)</span></label>
                  <input value={e.course} onChange={ev=>setEntry(i,"course",ev.target.value)}
                    placeholder="e.g. Leadership in Practice" style={IS}/>
                </div>
              </div>

              {/* Mode toggle */}
              <div style={{display:"flex",gap:6,marginBottom:10}}>
                {[["term","Term"],["single","Single date"]].map(([v,l])=>(
                  <button key={v} onClick={()=>setEntry(i,"mode",v)}
                    style={{flex:1,padding:"7px",borderRadius:8,border:`1.5px solid ${e.mode===v?C.teal:C.border}`,
                      background:e.mode===v?C.tealPale:"transparent",color:e.mode===v?C.teal:C.mid,
                      fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer"}}>{l}
                  </button>
                ))}
              </div>

              {e.mode==="term"?(
                <>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:8}}>
                    <div><label style={LB}>Start</label><input type="date" value={e.termStart} onChange={ev=>setEntry(i,"termStart",ev.target.value)} style={IS}/></div>
                    <div><label style={LB}>End</label><input type="date" value={e.termEnd} onChange={ev=>setEntry(i,"termEnd",ev.target.value)} style={IS}/></div>
                  </div>
                  <div style={{marginBottom:8}}>
                    <label style={LB}>Day of week</label>
                    <div style={{display:"flex",gap:4}}>
                      {["Mon","Tue","Wed","Thu","Fri"].map(d=>(
                        <button key={d} onClick={()=>setEntry(i,"day",d)}
                          style={{flex:1,padding:"6px 0",borderRadius:6,border:`1.5px solid ${e.day===d?C.teal:C.border}`,
                            background:e.day===d?C.tealPale:"transparent",color:e.day===d?C.teal:C.mid,
                            fontFamily:"inherit",fontSize:11,fontWeight:700,cursor:"pointer"}}>{d}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ):(
                <div style={{marginBottom:8}}>
                  <label style={LB}>Date</label>
                  <input type="date" value={e.singleDate} onChange={ev=>setEntry(i,"singleDate",ev.target.value)} style={IS}/>
                </div>
              )}

              {/* Preview */}
              {dates.length>0&&(
                <div style={{background:C.tealPale,border:`1px solid ${C.tealBorder}`,borderRadius:8,padding:"8px 12px",fontSize:11}}>
                  <span style={{fontWeight:700,color:C.teal}}>{dates.length} date{dates.length!==1?"s":""}</span>
                  <span style={{color:C.mid}}> · {dates.slice(0,3).map(d=>new Date(d+"T12:00:00").toLocaleDateString("en-GB",{day:"numeric",month:"short"})).join(", ")}{dates.length>3?` +${dates.length-3} more`:""}</span>
                  {person&&e.college&&<span style={{color:C.teal,marginLeft:6}}>· {person.name.split(" ")[0]} @ {e.college}{e.course?` (${e.course})`:""}</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button onClick={addEntry} style={{...BTN(C.dim,true),width:"100%",marginBottom:16,fontSize:12}}>
        + Add another trainee
      </button>

      <div style={{display:"flex",gap:8}}>
        <button onClick={save} disabled={!canSave}
          style={{...BTN(canSave?C.teal:C.muted),flex:1,padding:"11px 0",opacity:canSave?1:0.5}}>
          Save College Days
        </button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}

function BHOverrideModal({state, set, onClose}) {
  if (!state) return null;
  const bhDates = Object.entries(UK_BH_2026).sort((a,b)=>a[0].localeCompare(b[0]));
  const overrides = state.bhOverrides||{};
  const toggle = date => set(s=>({...s,bhOverrides:{...(s.bhOverrides||{}),[date]:!(s.bhOverrides||{})[date]}}));
  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Bank Holidays</div>
      <div style={{fontSize:13,color:C.dim,marginBottom:16}}>All bank holidays are closed by default. Toggle to open a practice on a BH.</div>
      {bhDates.map(([date,name])=>{
        const isOpen = !!overrides[date];
        const js = new Date(date).getDay();
        const dow = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][js];
        return (
          <div key={date} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 0",borderBottom:`1px solid ${C.border2}`}}>
            <div>
              <div style={{fontSize:13,fontWeight:600,color:isOpen?C.teal:C.soft}}>{name}</div>
              <div style={{fontSize:11,color:C.dim}}>{dow} {date}</div>
            </div>
            <button onClick={()=>toggle(date)} style={{padding:"6px 14px",borderRadius:20,border:`1.5px solid ${isOpen?C.teal:C.muted}`,background:isOpen?C.tealPale:"transparent",color:isOpen?C.teal:C.dim,fontFamily:"inherit",fontSize:12,fontWeight:600,cursor:"pointer"}}>
              {isOpen?"Open":"Closed"}
            </button>
          </div>
        );
      })}
    </Overlay>
  );
}

/* ── GOOD WORK LOG MODAL (Nurse Portal) ── */
function GoodWorkModal({person, onSave, onClose}) {
  const [note, setNote] = useState("");
  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Flag Good Work</div>
      <div style={{fontSize:13,color:C.dim,marginBottom:16}}>Tell us about something great you did or saw today</div>
      <div style={{marginBottom:16}}>
        <label style={LB}>What happened?</label>
        <textarea value={note} onChange={e=>setNote(e.target.value)} placeholder="e.g. Stayed late to help with an emergency patient, left surgery spotless..." rows={4} style={{...IS,resize:"vertical"}}/>
      </div>
      <button onClick={()=>note.trim()&&onSave(note.trim())} disabled={!note.trim()} style={{...BTN(note.trim()?C.gold:C.muted),width:"100%",padding:"11px 0"}}>Submit ⭐</button>
    </Overlay>
  );
}

/* ── EXTRA PATIENTS MODAL (Nurse Portal Overtime) ── */
function ExtraPatientsModal({person, onSave, onClose}) {
  const [date, setDate] = useState(new Date().toISOString().slice(0,10));
  const [count, setCount] = useState(1);
  const [reason, setReason] = useState("");
  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Extra Patients / Running Late</div>
      <div style={{fontSize:13,color:C.dim,marginBottom:16}}>Log additional patients that caused the session to run over</div>
      <div style={{marginBottom:12}}>
        <label style={LB}>Date</label>
        <input type="date" value={date} onChange={e=>setDate(e.target.value)} style={IS}/>
      </div>
      <div style={{marginBottom:12}}>
        <label style={LB}>Number of extra patients</label>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <button onClick={()=>setCount(Math.max(1,count-1))} style={{...BTN(C.dim,true),padding:"8px 14px",fontSize:16}}>−</button>
          <input type="number" min="1" value={count} onChange={e=>setCount(Math.max(1,+e.target.value))} style={{...IS,width:80,textAlign:"center"}}/>
          <button onClick={()=>setCount(count+1)} style={{...BTN(C.teal,true),padding:"8px 14px",fontSize:16}}>+</button>
        </div>
      </div>
      <div style={{marginBottom:16}}>
        <label style={LB}>Reason / notes</label>
        <textarea value={reason} onChange={e=>setReason(e.target.value)} placeholder="e.g. Emergency add-on, complex treatment ran over..." rows={3} style={{...IS,resize:"vertical"}}/>
      </div>
      <div style={{display:"flex",gap:8}}>
        <button onClick={()=>onSave({date,count,reason})} style={{...BTN(C.teal),flex:1,padding:"11px 0"}}>Log</button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}

/* ══════════════════════════════════
   COMPLIANCE TASKS
══════════════════════════════════ */
function RegTooltip({text}) {
  const [show, setShow] = useState(false);
  return (
    <span style={{position:"relative",display:"inline-block"}}>
      <span
        onMouseEnter={()=>setShow(true)}
        onMouseLeave={()=>setShow(false)}
        onClick={()=>setShow(s=>!s)}
        style={{
          display:"inline-flex",alignItems:"center",justifyContent:"center",
          width:16,height:16,borderRadius:99,
          background:"rgba(58,173,152,0.15)",
          border:"1px solid rgba(58,173,152,0.3)",
          fontSize:9,fontWeight:800,color:C.teal,cursor:"help",
          marginLeft:6,verticalAlign:"middle",flexShrink:0,
        }}>i</span>
      {show&&(
        <div style={{
          position:"absolute",left:"50%",bottom:"calc(100% + 6px)",
          transform:"translateX(-50%)",
          background:"#1a1a2e",color:"rgba(255,255,255,0.85)",
          fontSize:11,lineHeight:1.6,padding:"10px 12px",borderRadius:8,
          width:260,zIndex:999,
          border:"1px solid rgba(255,255,255,0.1)",
          boxShadow:"0 4px 16px rgba(0,0,0,0.4)",
          pointerEvents:"none",
        }}>{text}</div>
      )}
    </span>
  );
}

function ComplianceTasks({state, set, user, isPrincipal}) {
  // Safe default site — first known site, not hardcoded "sale"
  const defaultSite = user.site || Object.keys(SITES)[0] || "site";
  const [selSite, setSelSite] = useState(defaultSite);
  const [logModal, setLogModal] = useState(null);
  const [delegateModal, setDelegateModal] = useState(null);

  const skipped = state.skipComplianceTasks;
  // Tenant-agnostic — pull all reception/admin who could hold delegated tasks.
  // DCO has hardcoded RECS_BASE managers (Megan, Julie); other tenants store
  // theirs in customRecs. Self-healing site filter handles unknown-site staff
  // for single-site practices (Flourish fix). multiSite[] support added v11.6
  // so Julie (Brooklands+Timperley) appears in delegation dropdown for both.
  const knownSiteIds = new Set(Object.keys(SITES));
  const onlyOneSite = knownSiteIds.size <= 1;
  const recPool = [...RECS_BASE, ...(state.customRecs||[])];
  const siteManagers = recPool.filter(r=>{
    if (r.archived) return false;
    if (!r.manager && r.type!=="Manager" && r.type!=="Admin") return false;
    const unknown = !r.site || !knownSiteIds.has(r.site);
    const multi = Array.isArray(r.multiSite) && r.multiSite.includes(selSite);
    return r.site===selSite || multi || (unknown && onlyOneSite);
  });
  const siteNurses = [
    ...getNursesForSite(state, selSite),
    ...siteManagers.map(m=>({...m, _isManager:true})),
  ];

  function markDone(task, notes="", issueData=null) {
    const entry = {
      id: "ct"+Date.now(),
      taskId: task.id,
      site: selSite,
      doneBy: user.name,
      doneById: user.id||user.staffId,
      date: new Date().toISOString().split("T")[0],
      notes,
    };
    set(s=>{
      const next = {...s, complianceTasks:[...(s.complianceTasks||[]), entry]};
      if (issueData) {
        const newIssue = {
          id:"iss_"+Date.now(),
          tier: issueData.tier,
          category: issueData.category||task.label+" -- compliance issue",
          description: issueData.desc||notes,
          site: selSite,
          staffId: user.id||user.staffId,
          staffName: user.name,
          anonymous: false,
          date: new Date().toISOString().split("T")[0],
          status:"open",
          acknowledgedAt:null,acknowledgedBy:null,
          updates:[],resolvedAt:null,resolvedNote:null,
          escalatedFrom:null,immutable:issueData.tier===3,
          fromCompliance: true,
          complianceTaskId: task.id,
        };
        next.issues = [...(s.issues||[]), newIssue];
      }
      return next;
    });
  }

  function setDelegate(taskId, staffId) {
    set(s=>({...s, complianceDelegations:{...(s.complianceDelegations||{}), [`${taskId}_${selSite}`]: staffId}}));
  }

  if (skipped && isPrincipal) return (
    <div style={{textAlign:"center",padding:"40px 20px"}}>
      <div style={{fontSize:32,marginBottom:12}}>⏭</div>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:18,color:C.heading,marginBottom:8}}>Compliance Tasks Skipped</div>
      <div style={{fontSize:13,color:C.dim,marginBottom:20}}>Your team handles compliance checks outside Omni.</div>
      <button onClick={()=>set(s=>({...s,skipComplianceTasks:false}))}
        style={{...BTN(C.teal,true),padding:"10px 24px",fontSize:13}}>Enable compliance tracking</button>
    </div>
  );

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:22,color:C.heading}}>Compliance Tasks</div>
          <div style={{fontSize:12,color:C.dim,marginTop:2}}>Recurring checks -- logged, delegatable, audit-ready</div>
        </div>
        {isPrincipal&&(
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {Object.entries(SITES).map(([id,s])=>(
              <button key={id} onClick={()=>setSelSite(id)} style={{padding:"5px 14px",borderRadius:99,fontFamily:"inherit",fontSize:12,fontWeight:600,cursor:"pointer",border:`1px solid ${selSite===id?s.color:C.border}`,background:selSite===id?s.color+"18":"transparent",color:selSite===id?s.color:C.mid}}>{s.name}</button>
            ))}
          </div>
        )}
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:14}}>
        {COMPLIANCE_TASKS.map(task=>{
          const last = getLastCompletion(state, task.id, selSite);
          const overdue = isComplianceOverdue(task, last);
          const dueSoon = !overdue && isComplianceDueSoon(task, last);
          const delegateKey = `${task.id}_${selSite}`;
          const delegatedToId = (state.complianceDelegations||{})[delegateKey];
          const delegatedTo = delegatedToId ? [...siteNurses,...recPool].find(s=>s.id===delegatedToId) : null;
          const statusColor = overdue ? C.danger : dueSoon ? C.warn : C.green;
          const statusLabel = overdue ? "OVERDUE" : dueSoon ? "DUE SOON" : "UP TO DATE";
          const statusBg = overdue ? "#fef2f2" : dueSoon ? "rgba(245,197,24,0.06)" : "#f0fdf4";
          const recentLogs = (state.complianceTasks||[])
            .filter(l=>l.taskId===task.id&&l.site===selSite)
            .sort((a,b)=>b.date.localeCompare(a.date)).slice(0,3);

          return (
            <div key={task.id} style={{background:C.surface,border:`1px solid ${overdue?C.danger+"44":C.border}`,borderRadius:14,padding:"18px 20px",boxShadow:"0 1px 4px rgba(0,0,0,0.05)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <div style={{fontSize:28,lineHeight:1}}>
                    <Icon name={task.icon} size={28} color={task.color}/>
                  </div>
                  <div>
                    <div style={{fontSize:15,fontWeight:700,color:C.heading,display:"flex",alignItems:"center"}}>
                      {task.label}
                      {task.reg&&<RegTooltip text={task.reg}/>}
                    </div>
                    <div style={{fontSize:12,color:C.dim,marginTop:2}}>{task.description}</div>
                    <div style={{display:"flex",gap:8,marginTop:6,flexWrap:"wrap",alignItems:"center"}}>
                      <span style={{fontSize:11,color:C.mid,background:C.bg,border:`1px solid ${C.border}`,borderRadius:99,padding:"2px 10px"}}>{task.freqLabel}</span>
                      {delegatedTo&&(
                        <span style={{fontSize:11,color:C.teal,background:C.tealPale,border:`1px solid ${C.tealBorder}`,borderRadius:99,padding:"2px 10px"}}>
                          {delegatedTo.name.split(" ")[0]}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{display:"inline-block",background:statusBg,border:`1px solid ${statusColor}33`,borderRadius:8,padding:"4px 12px",fontSize:11,fontWeight:700,color:statusColor,marginBottom:8}}>{statusLabel}</div>
                  {last&&<div style={{fontSize:11,color:C.dim}}>Last: {new Date(last.date).toLocaleDateString("en-GB",{day:"numeric",month:"short"})} by {last.doneBy.split(" ")[0]}</div>}
                  {!last&&<div style={{fontSize:11,color:C.warn}}>Never completed</div>}
                </div>
              </div>

              <div style={{display:"flex",gap:8,marginTop:14,flexWrap:"wrap"}}>
                <button onClick={()=>setLogModal(task)}
                  style={{...BTN(overdue?C.danger:C.teal),fontSize:12,padding:"8px 16px"}}>
                  Mark Done
                </button>
                <button onClick={()=>setDelegateModal(task)}
                  style={{...BTN(C.dim,true),fontSize:12,padding:"8px 16px"}}>
                  Delegate
                </button>
              </div>

              {recentLogs.length>0&&(
                <div style={{marginTop:14,borderTop:`1px solid ${C.border2}`,paddingTop:12}}>
                  <div style={{fontSize:10,fontWeight:700,color:C.dim,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>Recent Log</div>
                  {recentLogs.map(l=>(
                    <div key={l.id} style={{display:"flex",justifyContent:"space-between",fontSize:12,color:C.mid,padding:"4px 0",borderBottom:`1px solid ${C.border2}`}}>
                      <span style={{color:l.issueRaised?C.danger:C.green}}>{new Date(l.date).toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"})}{l.issueRaised?" -- issue raised":""}</span>
                      <span>{l.doneBy.split(" ")[0]}</span>
                      {l.notes&&<span style={{color:C.dim,fontStyle:"italic",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:200}}>{l.notes}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Omni Comply hook */}
      <div style={{marginTop:24,padding:"16px 20px",borderRadius:12,background:"rgba(58,173,152,0.04)",border:"1px solid rgba(58,173,152,0.15)"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3aad98" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          <div style={{fontSize:11,fontWeight:700,color:"#3aad98",letterSpacing:"0.08em",textTransform:"uppercase"}}>Omni Comply -- coming soon</div>
        </div>
        <div style={{fontSize:12,color:C.dim,lineHeight:1.6}}>Full CQC policy library, legislation alerts, and inspection-ready evidence packs. These compliance tasks are the foundation.</div>
      </div>

      {isPrincipal&&(
        <div style={{marginTop:16,textAlign:"center"}}>
          <button onClick={()=>set(s=>({...s,skipComplianceTasks:true}))}
            style={{background:"none",border:"none",fontSize:11,color:C.dim,cursor:"pointer",textDecoration:"underline"}}>
            My team handles compliance checks outside Omni
          </button>
        </div>
      )}

      {/* Log done modal */}
      {logModal&&(
        <ComplianceLogModal task={logModal} site={selSite} user={user}
          onSave={(notes,issueData)=>{markDone(logModal,notes,issueData);setLogModal(null);}}
          onClose={()=>setLogModal(null)}/>
      )}

      {/* Delegate modal */}
      {delegateModal&&(
        <Overlay onClose={()=>setDelegateModal(null)}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4}}>Delegate Task</div>
          <div style={{fontSize:13,color:C.dim,marginBottom:16}}><Icon name={delegateModal.icon} size={14} color={delegateModal.color}/> {delegateModal.label} -- assign to a team member</div>
          <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
            {[...siteNurses.filter(n=>n.headNurse||n.actingManager||n._isManager)].map(s=>(
              <button key={s.id} onClick={()=>{setDelegate(delegateModal.id,s.id);setDelegateModal(null);}}
                style={{...BTN(C.teal,true),textAlign:"left",padding:"10px 14px",fontSize:13,display:"flex",justifyContent:"space-between"}}>
                <span>{s.name}</span>
                <span style={{fontSize:11,color:C.dim}}>{s._isManager?"Manager":s.headNurse?"Head Nurse":s.actingManager?"Acting Manager":""}</span>
              </button>
            ))}
            {siteNurses.filter(n=>!n.headNurse&&!n.actingManager&&!n._isManager).map(s=>(
              <button key={s.id} onClick={()=>{setDelegate(delegateModal.id,s.id);setDelegateModal(null);}}
                style={{...BTN(C.dim,true),textAlign:"left",padding:"10px 14px",fontSize:13,display:"flex",justifyContent:"space-between"}}>
                <span>{s.name}</span>
                <span style={{fontSize:11,color:C.dim}}>{STATUS[s.status]?.label}</span>
              </button>
            ))}
          </div>
          <button onClick={()=>{setDelegate(delegateModal.id,null);setDelegateModal(null);}} style={{...BTN(C.dim,true),width:"100%",fontSize:12}}>Clear delegation</button>
        </Overlay>
      )}
    </div>
  );
}

function ComplianceLogModal({task, site, user, onSave, onClose}) {
  const [notes, setNotes] = useState("");
  const [issueFound, setIssueFound] = useState(false);
  // Default to T2 — if a delegated compliance check finds something broken,
  // it's a Concern by definition. User can downgrade to T1 or escalate to T3.
  const [issueTier, setIssueTier] = useState(2);
  const [issueDesc, setIssueDesc] = useState("");

  const TIER_INFO = {
    1: {label:"Tier 1 -- Operational", color:"#60a5fa", desc:"Action needed but no immediate safety risk. e.g. stock to reorder, minor fault to log."},
    2: {label:"Tier 2 -- Concern", color:C.warn, desc:"Process failure or recurring issue. Goes to manager and principal."},
    3: {label:"Tier 3 -- Serious", color:C.danger, desc:"Immediate patient safety risk. e.g. out-of-date emergency drugs in use. Affects Iris Score immediately."},
  };

  function handleSave() {
    if (issueFound && !issueDesc.trim()) return;
    onSave(
      notes.trim(),
      issueFound ? {tier:issueTier, desc:issueDesc.trim(), category:task.label+" -- compliance issue"} : null
    );
  }

  return (
    <Overlay onClose={onClose}>
      <div style={{fontSize:28,marginBottom:8,textAlign:"center"}}>
        <Icon name={task.icon} size={28} color={task.color}/>
      </div>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:4,textAlign:"center"}}>{task.label}</div>
      <div style={{fontSize:13,color:C.dim,marginBottom:20,textAlign:"center"}}>Mark as completed today</div>

      <div style={{marginBottom:16}}>
        <label style={LB}>Notes (optional)</label>
        <textarea value={notes} onChange={e=>setNotes(e.target.value)}
          placeholder="e.g. All call points tested, log book updated..."
          rows={2} style={{...IS,resize:"vertical"}}/>
      </div>

      {/* Issue found toggle */}
      <div style={{marginBottom:issueFound?16:20}}>
        <button onClick={()=>setIssueFound(f=>!f)} style={{
          width:"100%",padding:"10px 16px",borderRadius:10,fontFamily:"inherit",
          fontSize:13,fontWeight:600,cursor:"pointer",textAlign:"left",
          border:`1px solid ${issueFound?C.danger+"55":C.border}`,
          background:issueFound?"rgba(255,70,70,0.06)":"transparent",
          color:issueFound?C.danger:C.mid,
          display:"flex",justifyContent:"space-between",alignItems:"center",
        }}>
          <span>{issueFound?"Issue found -- will raise a site notice":"Everything OK -- no issues found"}</span>
          <span style={{fontSize:11}}>{issueFound?"change":"+ flag issue"}</span>
        </button>
      </div>

      {issueFound&&(
        <div style={{marginBottom:20,background:"rgba(255,70,70,0.04)",border:"1px solid rgba(255,70,70,0.15)",borderRadius:10,padding:"14px 16px"}}>
          <div style={{fontSize:12,fontWeight:700,color:C.danger,marginBottom:12}}>What severity is this issue?</div>
          <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:14}}>
            {[1,2,3].map(t=>{
              const ti = TIER_INFO[t];
              return (
                <button key={t} onClick={()=>setIssueTier(t)} style={{
                  padding:"10px 14px",borderRadius:8,fontFamily:"inherit",cursor:"pointer",
                  textAlign:"left",border:`1px solid ${issueTier===t?ti.color+"66":C.border}`,
                  background:issueTier===t?ti.color+"11":"transparent",
                }}>
                  <div style={{fontSize:12,fontWeight:700,color:issueTier===t?ti.color:C.mid,marginBottom:2}}>{ti.label}</div>
                  <div style={{fontSize:11,color:C.dim,lineHeight:1.4}}>{ti.desc}</div>
                </button>
              );
            })}
          </div>
          <label style={LB}>Describe the issue <span style={{color:C.danger}}>*</span></label>
          <textarea value={issueDesc} onChange={e=>setIssueDesc(e.target.value)}
            placeholder={issueTier===3?"e.g. Emergency drug kit found with 3 items past expiry date -- removed from service":issueTier===2?"e.g. Spore test failed -- autoclave taken out of service, engineer called":"e.g. Fire alarm call point in surgery 3 not responding -- engineer notified"}
            rows={3} style={{...IS,resize:"vertical",borderColor:issueFound&&!issueDesc.trim()?"rgba(255,70,70,0.4)":undefined}}/>
          <div style={{fontSize:11,color:C.dim,marginTop:6}}>This will immediately create a Tier {issueTier} site notice{issueTier===3?" and flag this site as unsafe until resolved":""}.</div>
        </div>
      )}

      <div style={{display:"flex",gap:8}}>
        <button onClick={handleSave}
          disabled={issueFound&&!issueDesc.trim()}
          style={{...BTN(issueFound?C.danger:C.teal),flex:1,padding:"11px 0",fontSize:14,opacity:issueFound&&!issueDesc.trim()?0.5:1}}>
          {issueFound?"Mark Done + Raise Issue":"Confirm Done"}
        </button>
        <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
      </div>
    </Overlay>
  );
}

/* ══════════════════════════════════
   HISTORICAL LEAVE MODAL
══════════════════════════════════ */
function HistoricalLeaveModal({person, state, set, onClose}) {
  const existing = (state.historicalLeave||{})[person.id];
  const existingDays = typeof existing === "object" ? existing?.days : existing;
  const existingNote = typeof existing === "object" ? existing?.note : "";
  const [days, setDays] = useState(existingDays!==undefined&&existingDays!==null?String(existingDays):"");
  const [note, setNote] = useState(existingNote||"");
  const [saved, setSaved] = useState(false);

  // Individual entitlement override
  const rawEntit = (state.individualEntitlements||{})[person.id];
  const isLocked = rawEntit && typeof rawEntit === "object" && rawEntit._lockedByContract;
  const customEntitValue = rawEntit !== undefined ? (typeof rawEntit === "object" ? rawEntit.days : rawEntit) : "";
  const [customEntit, setCustomEntit] = useState(customEntitValue !== undefined && customEntitValue !== "" ? String(customEntitValue) : "");

  const contracted = person.hrs||37.5;
  // Use full getEntitlement for smart default (partner-aware, pro-rata etc)
  const autoEntit = getEntitlement(state, person);
  const isPartner = (person?.type||"").toLowerCase().includes("partner");
  const displayEntit = customEntit !== "" ? Number(customEntit) : autoEntit;

  // Leave year
  const rType = roleTypeForPerson(person);
  const indOverride = (state.leaveYearOverrides||{})[person.id];
  const lyConfig = indOverride ? {[rType]: indOverride} : state.leaveYearConfig;
  const { start: lyStart, end: lyEnd } = getLeaveYear(rType, lyConfig);
  const lyLabel = `${lyStart.toLocaleDateString("en-GB",{day:"numeric",month:"short"})} – ${lyEnd.toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}`;

  const remaining = days !== "" ? Math.round((displayEntit - parseInt(days))*10)/10 : null;

  // Quick-set entitlements
  const QUICK_ENTIT = isPartner
    ? [{l:"42d (full-time partner)",v:42},{l:"Pro-rata",v:null}]
    : [{l:"28d (full-time)",v:28},{l:"14.6d (20h)",v:14.6},{l:"Pro-rata auto",v:null}];

  function save() {
    const d = days !== "" ? parseFloat(days) : 0;
    if (isNaN(d)||d<0) return;
    set(s=>{
      const newState = {...s, historicalLeave:{...(s.historicalLeave||{}), [person.id]:{days:d, note:note.trim(), loggedBy:"principal", loggedAt:new Date().toISOString()}}};
      if (customEntit !== "") {
        newState.individualEntitlements = {...(s.individualEntitlements||{}), [person.id]: {days:Number(customEntit), _lockedByContract:false}};
      } else {
        const { [person.id]: _, ...rest } = (s.individualEntitlements||{});
        newState.individualEntitlements = rest;
      }
      return newState;
    });
    setSaved(true);
    setTimeout(onClose, 900);
  }

  return (
    <Overlay onClose={onClose}>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:20,color:C.heading,marginBottom:2}}>
        Set Holiday Entitlement
      </div>
      <div style={{fontSize:13,color:C.dim,marginBottom:20}}>
        {person.name} · {contracted}h/wk · Leave year: {lyLabel}
        {isPartner&&<span style={{marginLeft:8,background:C.warn+"18",color:C.warn,borderRadius:6,padding:"1px 8px",fontSize:11,fontWeight:700}}>Partner</span>}
      </div>

      {/* Leave year override */}
      <div style={{marginBottom:16,padding:"10px 14px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:10}}>
        <div style={{fontSize:10,fontWeight:700,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>Leave year</div>
        <div style={{display:"flex",gap:6}}>
          {[["","Group default"],["jan-dec","1 Jan – 31 Dec"],["apr-mar","1 Apr – 31 Mar"]].map(([val,label])=>{
            const cur = (state.leaveYearOverrides||{})[person.id]||"";
            const on = cur===val;
            return (
              <button key={val||"default"} onClick={()=>set(s=>({...s,leaveYearOverrides:{...(s.leaveYearOverrides||{}),[person.id]:val}}))}
                style={{flex:1,padding:"6px 4px",borderRadius:8,border:`1.5px solid ${on?C.teal:C.border}`,background:on?C.tealPale:"transparent",
                  color:on?C.teal:C.mid,fontFamily:"inherit",fontSize:10,fontWeight:600,cursor:"pointer"}}>
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Entitlement override — NO MAX, partners need 42+ */}
      <div style={{marginBottom:14}}>
        <label style={LB}>Holiday entitlement (days) <span style={{color:C.dim,fontWeight:400}}>— override the auto-calculated amount</span></label>
        {/* Quick-set buttons */}
        <div style={{display:"flex",gap:5,marginBottom:8,flexWrap:"wrap"}}>
          {QUICK_ENTIT.map(({l,v})=>(
            <button key={l} onClick={()=>setCustomEntit(v!==null?String(v):"")}
              style={{padding:"4px 12px",borderRadius:99,fontFamily:"inherit",fontSize:11,fontWeight:600,cursor:"pointer",
                border:`1px solid ${customEntit===(v!==null?String(v):"")&&(v!==null||customEntit==="")?C.teal:C.border}`,
                background:customEntit===(v!==null?String(v):"")&&(v!==null||customEntit==="")?C.tealPale:"transparent",
                color:customEntit===(v!==null?String(v):"")&&(v!==null||customEntit==="")?C.teal:C.mid}}>
              {l}
            </button>
          ))}
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <input type="number" min="0" step="0.5"
            placeholder={`Auto: ${autoEntit}d`}
            value={customEntit}
            disabled={isLocked}
            onChange={e=>setCustomEntit(e.target.value)}
            style={{...IS,flex:1,fontSize:20,textAlign:"center",fontWeight:700,opacity:isLocked?0.6:1}}/>
          {customEntit!==""&&!isLocked&&(
            <button onClick={()=>setCustomEntit("")} style={{...BTN(C.dim,true),padding:"8px 10px",fontSize:11}}>Auto</button>
          )}
        </div>
        {isLocked
          ? <div style={{fontSize:11,color:C.teal,marginTop:4}}>Set by contract</div>
          : <div style={{fontSize:11,color:C.dim,marginTop:4}}>
              Leave blank to use auto ({autoEntit}d based on {contracted}h · {isPartner?"partner rate 42d":"staff rate 28d"})
            </div>
        }
      </div>

      {/* Summary tile */}
      <div style={{background:C.tealPale,border:`1px solid ${C.tealBorder}`,borderRadius:10,padding:"12px 16px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:11,color:C.teal,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em"}}>Entitlement</div>
          <div style={{fontSize:28,fontWeight:700,color:C.teal}}>{displayEntit}d</div>
        </div>
        {remaining!==null&&(
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:11,color:remaining>=0?C.green:C.danger,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em"}}>Remaining after pre-system</div>
            <div style={{fontSize:28,fontWeight:700,color:remaining>=0?C.green:C.danger}}>{remaining}d</div>
          </div>
        )}
      </div>

      {/* Pre-system days */}
      <div style={{marginBottom:14}}>
        <label style={LB}>Days taken <span style={{color:C.dim,fontWeight:400}}>before Omni Rota was set up</span></label>
        <input type="number" min="0" step="0.5" value={days}
          onChange={e=>setDays(e.target.value)}
          placeholder="0"
          style={{...IS,fontSize:22,textAlign:"center",fontWeight:700}}/>
        <div style={{fontSize:11,color:C.dim,marginTop:4,textAlign:"center"}}>e.g. if they'd already taken 5 days before you started using Omni</div>
      </div>

      <div style={{marginBottom:20}}>
        <label style={LB}>Note <span style={{color:C.dim,fontWeight:400}}>(optional)</span></label>
        <input type="text" value={note} onChange={e=>setNote(e.target.value)} placeholder="e.g. Partner — 42 days agreed at review" style={IS}/>
      </div>

      {saved
        ? <div style={{textAlign:"center",padding:"12px",color:C.green,fontWeight:700,fontSize:15}}>Saved</div>
        : <div style={{display:"flex",gap:8}}>
            <button onClick={save} style={{...BTN(C.teal),flex:1,padding:"13px 0",fontSize:14}}>Save entitlement</button>
            <button onClick={onClose} style={BTN(C.dim,true)}>Cancel</button>
          </div>
      }
    </Overlay>
  );
}

/* ══════════════════════════════════
   PULSE CHECK — ROLE-AWARE HOME SCREEN
══════════════════════════════════ */
function PulseCheck({state, user, isPrincipal, set, onNavigate}) {
  const today = new Date().toISOString().split("T")[0];
  const hour = new Date().getHours();
  const greeting = hour<12?"Good morning":"hour"<17?"Good afternoon":"Good evening";
  const firstName = user.name.split(" ")[0];

  // Build site score using Iris Score™ engine
  function siteScore(siteId) {
    return calcIrisScore(siteId, today, state).score;
  }

  // Build action items across all accessible sites
  function buildItems(siteId) {
    const items = [];
    const nurses = (state.nurses||NURSES_BASE).filter(n=>n.site===user?.site&&!n.maternity&&!n.archived);
    const recs = RECS_BASE.filter(r=>r.site===siteId);

    // Sick today
    const sickToday = (state.sick||[]).filter(s=>s.site===siteId&&!s.rtw&&s.date===today);
    sickToday.forEach(s=>items.push({icon:"thermometer",msg:`${s.name.split(" ")[0]} called in sick — cover needed`,color:C.danger,action:"sickness",urgent:true}));

    // Pending leave
    const pendingLeave = nurses.flatMap(n=>(state.leave[n.id]||[]).filter(l=>l.status==="pending")).length
      + recs.flatMap(r=>(state.leave[r.id]||[]).filter(l=>l.status==="pending")).length;
    if (pendingLeave>0) items.push({icon:"calendar",msg:`${pendingLeave} leave request${pendingLeave!==1?"s":""} awaiting approval`,color:C.warn,action:"planning"});

    // RTW outstanding
    const rtw = (state.sick||[]).filter(s=>s.site===siteId&&!s.rtw).length;
    if (rtw>0) items.push({icon:"warning",msg:`${rtw} RTW interview${rtw!==1?"s":""} outstanding`,color:C.danger,action:"sickness"});

    // Overdue compliance
    COMPLIANCE_TASKS.forEach(t=>{
      const last = getLastCompletion(state,t.id,siteId);
      if (isComplianceOverdue(t,last)) items.push({icon:t.icon,msg:`${t.label} overdue`,color:C.warn,action:"compliance"});
    });

    // Susan RTW alert
    const returning = recs.filter(r=>r.sick&&r.sickReturn&&r.sickReturn<=today);
    returning.forEach(r=>items.push({icon:"bell",msg:`${r.name.split(" ")[0]} due back — RTW needed`,color:C.teal,action:"sickness"}));

    return items;
  }

  if (isPrincipal) {
    // All-sites view
    const allItems = Object.keys(SITES).flatMap(siteId=>
      buildItems(siteId).map(i=>({...i, site:siteId}))
    ).slice(0,5);
    const allGreen = allItems.length===0;
    const scores = Object.fromEntries(Object.keys(SITES).map(id=>[id,siteScore(id)]));
    const overallScore = Math.round(Object.values(scores).reduce((a,b)=>a+b,0)/3);
    const scoreColor = overallScore>=80?C.green:overallScore>=60?C.warn:C.danger;

    return (
      <div style={{maxWidth:480,margin:"0 auto"}}>
        {/* Greeting */}
        <div style={{marginBottom:20,textAlign:"center",paddingTop:8}}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:26,color:C.heading}}>{greeting}, {firstName}</div>
          <div style={{fontSize:13,color:C.dim,marginTop:4}}>{new Date().toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long"})}</div>
        </div>

        {/* Overall score */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:"20px",marginBottom:16,textAlign:"center",boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:52,color:scoreColor,lineHeight:1}}>{overallScore}</div>
          <div style={{fontSize:11,color:C.dim,textTransform:"uppercase",letterSpacing:"0.1em",marginTop:4}}>Overall Pulse</div>
          <div style={{display:"flex",gap:10,justifyContent:"center",marginTop:14}}>
            {Object.entries(SITES).map(([id,s])=>{
              const sc = scores[id];
              const col = sc>=80?C.green:sc>=60?C.warn:C.danger;
              return (
                <div key={id} style={{textAlign:"center",background:C.bg,borderRadius:10,padding:"8px 14px",border:`1px solid ${C.border}`}}>
                  <div style={{fontSize:18,fontWeight:700,color:col}}>{sc}</div>
                  <div style={{fontSize:10,color:C.dim,marginTop:2}}>{s.name}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Action items or all-clear */}
        {allGreen
          ? <div style={{background:"rgba(0,229,160,0.08)",border:`1px solid #bbf7d0`,borderRadius:14,padding:"20px",textAlign:"center"}}>
              <div style={{fontSize:28,marginBottom:8}}>✅</div>
              <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:18,color:C.green}}>All sites ready for today</div>
              <div style={{fontSize:13,color:C.dim,marginTop:4}}>Nothing needs your attention right now</div>
            </div>
          : <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <div style={{fontSize:11,fontWeight:700,color:C.dim,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:4}}>Needs attention</div>
              {allItems.map((item,i)=>(
                <button key={i} onClick={()=>onNavigate(item.action,item.site)}
                  style={{background:C.surface,border:`1px solid ${item.color}33`,borderRadius:12,padding:"14px 16px",display:"flex",alignItems:"center",gap:12,textAlign:"left",cursor:"pointer",width:"100%",fontFamily:"inherit",boxShadow:"0 1px 4px rgba(0,0,0,0.05)"}}>
                  <span style={{fontSize:20,flexShrink:0}}>{item.icon}</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,color:C.heading}}>{item.msg}</div>
                    <div style={{fontSize:11,color:C.dim,marginTop:2}}>{SITES[item.site]?.name}</div>
                  </div>
                  <span style={{fontSize:16,color:C.muted}}>›</span>
                </button>
              ))}
            </div>
        }
      </div>
    );
  }

  // Manager / head nurse view — single site
  const siteId = fallbackSite(user);
  const siteInfo = SITES[siteId];
  const score = siteScore(siteId);
  const scoreColor = score>=80?C.green:score>=60?C.warn:C.danger;
  const items = buildItems(siteId).slice(0,5);
  const allGreen = items.length===0;

  return (
    <div style={{maxWidth:480,margin:"0 auto"}}>
      <div style={{marginBottom:20,textAlign:"center",paddingTop:8}}>
        <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:26,color:C.heading}}>{greeting}, {firstName} 👋</div>
        <div style={{fontSize:13,color:C.dim,marginTop:4}}>{siteInfo.name} · {new Date().toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long"})}</div>
      </div>

      {/* Site score */}
      <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:16,padding:"20px",marginBottom:16,textAlign:"center",boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
        <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:52,color:scoreColor,lineHeight:1}}>{score}</div>
        <div style={{fontSize:11,color:C.dim,textTransform:"uppercase",letterSpacing:"0.1em",marginTop:4}}>
          {allGreen ? `${siteInfo.name} is ready for today` : "Site Pulse"}
        </div>
      </div>

      {allGreen
        ? <div style={{background:"rgba(0,229,160,0.08)",border:`1px solid #bbf7d0`,borderRadius:14,padding:"20px",textAlign:"center"}}>
            <div style={{fontSize:28,marginBottom:8}}>✅</div>
            <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:18,color:C.green}}>All good</div>
            <div style={{fontSize:13,color:C.dim,marginTop:4}}>Nothing needs your attention right now</div>
          </div>
        : <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <div style={{fontSize:11,fontWeight:700,color:C.dim,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:4}}>Needs attention</div>
            {items.map((item,i)=>(
              <button key={i} onClick={()=>onNavigate(item.action)}
                style={{background:C.surface,border:`1px solid ${item.color}33`,borderRadius:12,padding:"14px 16px",display:"flex",alignItems:"center",gap:12,textAlign:"left",cursor:"pointer",width:"100%",fontFamily:"inherit",boxShadow:"0 1px 4px rgba(0,0,0,0.05)"}}>
                <span style={{fontSize:20,flexShrink:0}}>{item.icon}</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:600,color:item.urgent?C.danger:C.heading}}>{item.msg}</div>
                </div>
                <span style={{fontSize:16,color:C.muted}}>›</span>
              </button>
            ))}
          </div>
      }
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   SET PASSWORD PROMPT — fires once after magic link auth
   New tenant sets a password so future logins are email + password.
══════════════════════════════════════════════════════════════════ */
function SetPasswordPrompt({ onDone }) {
  const [pass,    setPass]    = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState('');

  const C = {
    bg:     '#100f1a', surface:'#181626', border:'rgba(255,255,255,0.07)',
    text:   'rgba(255,255,255,0.82)', dim:'rgba(255,255,255,0.38)',
    teal:   '#3aad98', danger:'#ff4646',
  };

  async function handleSet(e) {
    e?.preventDefault();
    if (pass.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    if (pass !== confirm) { setErr("Passwords don't match."); return; }
    setLoading(true); setErr('');
    try {
      await sbAuthSetPassword(pass);
      localStorage.setItem('pr_password_set', '1');
      onDone();
    } catch(ex) {
      setErr(ex.message || 'Could not set password. Try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{minHeight:'100vh',background:C.bg,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',fontFamily:"'Instrument Sans',system-ui,sans-serif",padding:'24px'}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Instrument+Sans:wght@400;500;600;700&display=swap');
      .pw-input{background:rgba(255,255,255,0.05)!important;border:1px solid rgba(255,255,255,0.09)!important;border-radius:10px!important;color:rgba(255,255,255,0.85)!important;font-family:inherit!important;font-size:14px!important;padding:12px 14px!important;width:100%!important;box-sizing:border-box!important;outline:none!important;}
      .pw-input:focus{border-color:rgba(58,173,152,0.4)!important;}
      .pw-input::placeholder{color:rgba(255,255,255,0.2)!important;}`}</style>

      <div style={{width:'100%',maxWidth:420}}>
        <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:36}}>
          <span style={{fontFamily:"'Instrument Serif',serif",fontStyle:'italic',fontWeight:700,fontSize:22,color:'rgba(255,255,255,0.9)'}}>Omni</span>
          <span style={{width:6,height:6,borderRadius:'50%',background:C.teal,display:'inline-block'}}/>
        </div>

        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:18,padding:'36px 32px'}}>
          <div style={{width:48,height:48,borderRadius:'50%',background:'linear-gradient(135deg,#00d4ff,#a855f7,#f472b6)',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Instrument Serif',serif",fontStyle:'italic',fontWeight:700,fontSize:18,color:'white',marginBottom:20}}>I</div>

          <h2 style={{fontFamily:"'Instrument Serif',serif",fontStyle:'italic',fontWeight:700,fontSize:26,color:'rgba(255,255,255,0.95)',letterSpacing:'-0.02em',marginBottom:8}}>
            One last thing.
          </h2>
          <p style={{fontSize:13,color:C.dim,lineHeight:1.65,marginBottom:24}}>
            Set a password so you can log straight in next time — no link needed.
          </p>

          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            <input
              className="pw-input"
              type="password"
              placeholder="Choose a password (8+ characters)"
              value={pass}
              onChange={e=>setPass(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&handleSet()}
            />
            <input
              className="pw-input"
              type="password"
              placeholder="Confirm password"
              value={confirm}
              onChange={e=>setConfirm(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&handleSet()}
            />
          </div>

          {err && <p style={{fontSize:12,color:C.danger,marginTop:8}}>{err}</p>}

          <button
            onClick={handleSet}
            disabled={loading||!pass||!confirm}
            style={{width:'100%',padding:'14px',borderRadius:10,border:'none',background:C.teal,color:'#fff',fontFamily:"'Instrument Serif',serif",fontStyle:'italic',fontWeight:700,fontSize:16,cursor:(loading||!pass||!confirm)?'default':'pointer',opacity:(loading||!pass||!confirm)?0.4:1,marginTop:16,transition:'opacity 0.15s'}}
          >
            {loading ? 'Setting password…' : 'Set password & continue →'}
          </button>

          <div style={{marginTop:14,fontSize:11,color:'rgba(255,255,255,0.2)',textAlign:'center',lineHeight:1.5}}>
            You only need to do this once. After this, sign in with email and password — no link needed.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   NEW PRACTICE WELCOME — shown when ?new=1 before auth
   Greets by name, sends magic link, shows check-inbox state.
   After clicking link: onAuthStateChange auto-creates principal → Iris fires.
══════════════════════════════════════════════════════════════════ */
function NewPracticeWelcome() {
  const params    = new URLSearchParams(window.location.search);
  const name      = params.get('name') || '';
  const email     = params.get('email') || '';
  const practice  = params.get('practice') || '';
  const firstName = name.split(' ')[0] || 'there';

  const [sent,    setSent]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState('');

  const C = {
    bg:     '#100f1a',
    surface:'#181626',
    header: '#0c0b16',
    border: 'rgba(255,255,255,0.07)',
    text:   'rgba(255,255,255,0.82)',
    dim:    'rgba(255,255,255,0.38)',
    teal:   '#3aad98',
    green:  '#00e5a0',
  };

  async function sendLink() {
    setLoading(true); setErr('');
    try {
      // Build redirect URL preserving all params so Iris can greet them
      const redirectTo = window.location.origin + window.location.pathname + window.location.search;
      await sbAuthMagicLink(email, redirectTo);
      setSent(true);
    } catch(e) {
      setErr(e.message || 'Could not send link. Check the email address.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{minHeight:'100vh',background:C.bg,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',fontFamily:"'Instrument Sans',system-ui,sans-serif",padding:'24px'}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Instrument+Sans:wght@400;500;600;700&display=swap');
      @keyframes npwGlow{0%,100%{box-shadow:0 0 12px rgba(58,173,152,0.3)}50%{box-shadow:0 0 28px rgba(58,173,152,0.6)}}`}</style>

      <div style={{width:'100%',maxWidth:440}}>

        {/* Wordmark */}
        <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:40}}>
          <span style={{fontFamily:"'Instrument Serif',serif",fontStyle:'italic',fontWeight:700,fontSize:22,color:'rgba(255,255,255,0.9)',letterSpacing:'-0.02em'}}>Omni</span>
          <span style={{width:6,height:6,borderRadius:'50%',background:C.teal,display:'inline-block',animation:'npwGlow 2.5s ease-in-out infinite'}}/>
        </div>

        {!sent ? (
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:18,padding:'36px 32px'}}>
            {/* Iris avatar */}
            <div style={{width:52,height:52,borderRadius:'50%',background:'linear-gradient(135deg,#00d4ff,#a855f7,#f472b6)',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Instrument Serif',serif",fontStyle:'italic',fontWeight:700,fontSize:20,color:'white',marginBottom:20}}>I</div>

            <h1 style={{fontFamily:"'Instrument Serif',serif",fontStyle:'italic',fontWeight:700,fontSize:28,color:'rgba(255,255,255,0.95)',letterSpacing:'-0.02em',marginBottom:8}}>
              Hi {firstName} 👋
            </h1>
            <p style={{fontSize:14,color:C.dim,lineHeight:1.65,marginBottom:6}}>
              Let's get <strong style={{color:'rgba(255,255,255,0.7)'}}>{practice || 'your practice'}</strong> live on Omni.
            </p>
            <p style={{fontSize:13,color:C.dim,lineHeight:1.65,marginBottom:4}}>
              We'll send a one-click login link to:
            </p>
            <p style={{fontSize:15,fontWeight:600,color:'rgba(255,255,255,0.85)',marginBottom:4}}>{email}</p>
            <p style={{fontSize:12,color:C.dim,lineHeight:1.65,marginBottom:28}}>
              Is that the right address? Click it and Iris will walk you through setup — takes about 3 minutes.
            </p>

            <button
              onClick={sendLink}
              disabled={loading}
              style={{width:'100%',padding:'14px',borderRadius:10,border:'none',background:C.teal,color:'#fff',fontFamily:"'Instrument Serif',serif",fontStyle:'italic',fontWeight:700,fontSize:16,cursor:loading?'default':'pointer',opacity:loading?0.5:1,transition:'all 0.15s',marginBottom:err?12:0}}
            >
              {loading ? 'Sending…' : 'Send my login link'}
            </button>

            {err && <p style={{fontSize:12,color:'#ff6b6b',marginTop:8,textAlign:'center'}}>{err}</p>}

            <p style={{fontSize:10,color:'rgba(255,255,255,0.2)',textAlign:'center',marginTop:16,letterSpacing:'0.06em',textTransform:'uppercase'}}>
              No password needed · Link expires in 1 hour
            </p>
            <button
              onClick={()=>window.history.back()}
              style={{display:'block',margin:'12px auto 0',background:'none',border:'none',cursor:'pointer',fontSize:12,color:'rgba(255,255,255,0.25)',textDecoration:'underline',fontFamily:'inherit'}}
            >
              Wrong email? Go back
            </button>
          </div>
        ) : (
          <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:18,padding:'36px 32px',textAlign:'center'}}>
            {/* Email icon */}
            <div style={{width:60,height:60,borderRadius:'50%',background:'rgba(58,173,152,0.1)',border:`1px solid rgba(58,173,152,0.25)`,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 20px'}}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="4" width="20" height="16" rx="2" stroke={C.teal} strokeWidth="1.5"/>
                <path d="M2 8l10 6 10-6" stroke={C.teal} strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>

            <h2 style={{fontFamily:"'Instrument Serif',serif",fontStyle:'italic',fontWeight:700,fontSize:24,color:'rgba(255,255,255,0.95)',letterSpacing:'-0.02em',marginBottom:10}}>
              Check your inbox.
            </h2>
            <p style={{fontSize:14,color:C.dim,lineHeight:1.65,marginBottom:6}}>
              We've sent a login link to
            </p>
            <p style={{fontSize:14,fontWeight:600,color:C.teal,marginBottom:20}}>{email}</p>
            <p style={{fontSize:13,color:C.dim,lineHeight:1.65,marginBottom:28}}>
              Click the link in the email and Iris will meet you here to finish setting up {practice || 'your practice'}.
            </p>
            <p style={{fontSize:11,color:'rgba(255,255,255,0.2)',letterSpacing:'0.04em'}}>
              Check your spam folder if it doesn't arrive within a minute.
            </p>

            <button
              onClick={()=>setSent(false)}
              style={{marginTop:20,background:'none',border:'none',cursor:'pointer',fontSize:12,color:'rgba(255,255,255,0.25)',textDecoration:'underline',fontFamily:'inherit'}}
            >
              Wrong email? Start again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ======================================================================
   IRIS ONBOARDING -- two-phase conversational setup
   Phase 1 (basics): practice name, sites, surgeries, working hours
   Phase 2 (staff):  clinicians, nurses + compliance, reception, policy
   Supports resume:  setup_phase === "basics_done" skips to staff phase
   Writes incrementally -- basics saved after phase 1, all saved after phase 2
   Gap report generated on completion: statutory + CQC evidence gaps
   DCO never sees this -- setup_phase defaults to "complete" for dco
====================================================================== */
/* ── Team Setup Table Components ────────────────────────────────────────────── */
const WD = ["Mon","Tue","Wed","Thu","Fri"];

function parseHHMM(raw) {
  if (!raw) return "";
  const isPM = /pm/i.test(raw);
  const isAM = /am/i.test(raw);
  const clean = raw.replace(/[apm\s]/gi,"").trim();
  const parts = clean.split(/[:.]/);
  let h = parseInt(parts[0]) || 0;
  const m = parseInt(parts[1]) || 0;
  if (isPM && h < 12) h += 12;
  if (isAM && h === 12) h = 0;
  if (isNaN(h)) return "";
  return String(h).padStart(2,"0") + ":" + String(m).padStart(2,"0");
}

function tsParseTime(raw) {
  if (!raw) return {start:"",end:""};
  const idx = raw.lastIndexOf("-");
  if (idx < 1) return {start:parseHHMM(raw), end:""};
  let start = parseHHMM(raw.slice(0,idx));
  let end = parseHHMM(raw.slice(idx+1));
  // 12h sanity: "9-5" parses end as 05:00 — but the user clearly meant 17:00.
  // If both populated, end < start, end < 12, and the input has no AM/PM
  // marker, assume the end time is PM and shift it 12 hours.
  if (start && end) {
    const sH = parseInt(start.slice(0,2)) || 0;
    const eH = parseInt(end.slice(0,2)) || 0;
    const hadAMPM = /am|pm/i.test(raw);
    if (eH < sH && eH < 12 && !hadAMPM) {
      end = String(eH + 12).padStart(2,"0") + end.slice(2);
    }
  }
  return {start, end};
}

const TS_CLIN_TYPES = [
  {v:"Principal/GDS",  label:"Principal",   sub:"Practice owner — GDS"},
  {v:"Partner/GDS",   label:"Partner",     sub:"Partnership equity — GDS"},
  {v:"GDS",           label:"Associate GDS",sub:"General dental surgeon"},
  {v:"FD",            label:"Foundation Dentist", sub:"Supervised — named supervisor required"},
  {v:"DwSI",          label:"DwSI",        sub:"Dentist with special interest"},
  {v:"Specialist",    label:"Specialist",  sub:"GDC registered specialist"},
  {v:"Therapist",     label:"Therapist",   sub:"Prescription-only clinical scope"},
  {v:"Hygienist",     label:"Hygienist",   sub:"Prevention and periodontal care only"},
];

const COMPETENCY = [
  {v:"qualified",       label:"Qualified",
   sub:"GDC registered · independent · full Iris Score weighting"},
  {v:"near-qualifying", label:"Near-qualifying",
   sub:"Final stage · supervised · 70% Iris Score weighting"},
  {v:"trainee",         label:"Trainee",
   sub:"In training · qualified nurse always present · never sole cover"},
  {v:"new-starter",     label:"New starter",
   sub:"Competency not yet assessed · treat as trainee"},
];

let _tseq = 0;
function tsUid(p) {
  return p + "_" + Date.now() + "_" + (++_tseq);
}
// Return a default 5-day weekly schedule built from the practice's opening
// hours captured in Phase 1 basics. Used to pre-fill clinician table cells so
// the user only has to edit exceptions, not type 5 days × N clinicians.
function tsDefaultSched(state) {
  const start = state?.practiceProfile?.clinicStart
    || state?.settings?.clinicStart || "09:00";
  const end = state?.practiceProfile?.clinicEnd
    || state?.settings?.clinicEnd || "17:00";
  const range = start + "-" + end;
  return {Mon:range, Tue:range, Wed:range, Thu:range, Fri:range};
}
function tsNewClin(siteId, defaultSched) {
  return {
    _id:tsUid("c"), name:"", type:"GDS", isPartner:false,
    sched: defaultSched ? {...defaultSched} : {},
    freq:{}, sedation:"none",
    supervisorId:"", secondarySupId:"",
    specialty:"", site:siteId,
  };
}
function tsNewNurse(siteId) {
  return {
    _id:tsUid("n"), name:"", status:"qualified",
    hrs:37.5, workDays:[...WD],
    ra:false, iv:false, headNurse:false,
    keyholder:false, firstAider:false,
    safeguardingL3:false, fireMarshal:false,
    maternity:false, site:siteId,
  };
}
function tsNewRec(siteId) {
  return {
    _id:tsUid("r"), name:"", type:"reception",
    hrs:37.5, workDays:[...WD], site:siteId,
  };
}

function ClinicianTable({rows, onChange, hasSedation, siteId}) {
  const TH = {
    fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.35)",
    textTransform:"uppercase",letterSpacing:"0.06em",
    padding:"8px 8px",textAlign:"left",
    borderBottom:"1px solid rgba(255,255,255,0.08)",whiteSpace:"nowrap",
  };
  const TD = {
    padding:"5px 7px",verticalAlign:"middle",
    borderBottom:"1px solid rgba(255,255,255,0.04)",
  };
  const INP = {
    background:"transparent",border:"none",
    color:"rgba(255,255,255,0.82)",fontFamily:"inherit",
    fontSize:12,padding:"2px 4px",outline:"none",
  };
  function upd(id,k,v){onChange(rows.map(r=>r._id===id?{...r,[k]:v}:r));}
  function updSched(id,day,val) {
    const r = rows.find(x=>x._id===id); if(!r) return;
    const p = tsParseTime(val);
    const sc = {...r.sched};
    if (p.start||p.end) sc[day]=p; else delete sc[day];
    onChange(rows.map(x=>x._id===id?{...x,sched:sc}:x));
  }
  function cycleFreq(id,day) {
    const r = rows.find(x=>x._id===id); if(!r) return;
    const cur = (r.freq||{})[day]||"always";
    const nxt = cur==="always"?"weekA":cur==="weekA"?"weekB":"always";
    const fr = {...(r.freq||{})};
    if (nxt==="always") delete fr[day]; else fr[day]=nxt;
    onChange(rows.map(x=>x._id===id?{...x,freq:fr}:x));
  }
  function copyRow(id) {
    const r = rows.find(x=>x._id===id); if(!r) return;
    const copy = {...r,_id:tsUid("c"),name:""};
    const idx = rows.findIndex(x=>x._id===id);
    const next=[...rows]; next.splice(idx+1,0,copy);
    onChange(next);
  }
  function delRow(id) {
    if(rows.length<=1) return;
    onChange(rows.filter(r=>r._id!==id));
  }
  const hasAB = rows.some(r=>
    Object.values(r.freq||{}).some(v=>v==="weekA"||v==="weekB")
  );
  return (
    <div style={{overflowX:"auto",marginBottom:4}}>
      <table style={{
        width:"100%",minWidth:820,
        borderCollapse:"collapse",fontSize:12,
        fontFamily:"'Instrument Sans',system-ui,sans-serif",
      }}>
        <thead>
          <tr>
            <th style={{...TH,width:168}}>Name</th>
            <th style={{...TH,width:112}}>Type</th>
            <th style={{...TH,width:56,textAlign:"center"}}>Partner</th>
            {WD.map(d=>(
              <th key={d} style={{...TH,width:88,textAlign:"center"}}>{d}</th>
            ))}
            {hasSedation&&<th style={{...TH,width:80}}>Sedation</th>}
            <th style={{...TH,width:60}}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r,ri)=>{
            const isFD=r.type==="FD";
            const isDwSI=r.type==="DwSI"||r.type==="Specialist";
            const noSed=r.type==="Therapist"||r.type==="Hygienist";
            return (
              <React.Fragment key={r._id}>
                <tr style={{
                  background:ri%2===0
                    ?"rgba(255,255,255,0.02)":"transparent",
                }}>
                  <td style={TD}>
                    <input value={r.name}
                      onChange={e=>upd(r._id,"name",e.target.value)}
                      placeholder="Full name"
                      style={{
                        ...INP,fontWeight:r.name?600:400,width:"100%",
                      }}
                    />
                  </td>
                  <td style={TD}>
                    <select value={r.type}
                      onChange={e=>upd(r._id,"type",e.target.value)}
                      style={{...INP,cursor:"pointer",width:"100%"}}
                    >
                      {TS_CLIN_TYPES.map(t=>(
                        <option key={t.v} value={t.v}>{t.label}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{...TD,textAlign:"center"}}>
                    <input type="checkbox" checked={r.isPartner}
                      onChange={e=>upd(r._id,"isPartner",e.target.checked)}
                      style={{accentColor:"#3aad98",width:14,height:14}}
                    />
                  </td>
                  {WD.map((d,di)=>{
                    const sc=r.sched?.[d];
                    const fr=(r.freq||{})[d]||"always";
                    const dv=sc
                      ?(typeof sc==="string"?sc
                        :(sc.start||"")+(sc.end?"-"+sc.end:""))
                      :"";
                    return (
                      <td key={d} style={TD}>
                        <div style={{display:"flex",alignItems:"center",gap:2}}>
                          <input key={r._id+d+dv}
                            defaultValue={dv}
                            onBlur={e=>updSched(r._id,d,e.target.value)}
                            onKeyDown={e=>{
                              if(e.key==="Enter"){
                                e.preventDefault();
                                updSched(r._id,d,e.target.value);
                                const next=e.target.closest("tr")
                                  ?.querySelectorAll("input[placeholder]");
                                if(next) {
                                  const arr=Array.from(next);
                                  const idx=arr.indexOf(e.target);
                                  if(idx<arr.length-1) arr[idx+1].focus();
                                }
                              }
                            }}
                            placeholder="08:00-17:30"
                            style={{
                              ...INP,width:72,fontSize:10,
                              color:sc
                                ?"rgba(255,255,255,0.8)"
                                :"rgba(255,255,255,0.15)",
                            }}
                          />
                          {sc&&(
                            <button onClick={()=>cycleFreq(r._id,d)}
                              title={
                                fr==="always"?"Every week"
                                :fr==="weekA"?"Week A only":"Week B only"
                              }
                              style={{
                                border:"none",cursor:"pointer",
                                borderRadius:3,padding:"1px 3px",
                                fontSize:9,fontWeight:700,lineHeight:1,
                                background:fr==="always"
                                  ?"transparent"
                                  :fr==="weekA"
                                  ?"rgba(58,173,152,0.3)"
                                  :"rgba(245,197,24,0.3)",
                                color:fr==="always"
                                  ?"rgba(255,255,255,0.2)"
                                  :fr==="weekA"?"#3aad98":"#f5c518",
                              }}
                            >{fr==="always"?"·":fr==="weekA"?"A":"B"}</button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                  {hasSedation&&(
                    <td style={TD}>
                      {noSed?(
                        <span style={{fontSize:11,color:"rgba(255,255,255,0.2)"}}>
                          N/A
                        </span>
                      ):(
                        <select value={r.sedation||"none"}
                          onChange={e=>upd(r._id,"sedation",e.target.value)}
                          style={{...INP,cursor:"pointer"}}
                        >
                          <option value="none">None</option>
                          <option value="ra">RA</option>
                          <option value="iv">IV</option>
                          <option value="both">Both</option>
                        </select>
                      )}
                    </td>
                  )}
                  <td style={{...TD,textAlign:"right"}}>
                    <button onClick={()=>copyRow(r._id)} title="Copy row"
                      style={{
                        background:"none",border:"none",
                        color:"rgba(255,255,255,0.3)",
                        cursor:"pointer",fontSize:14,padding:"2px 4px",
                      }}
                    >⎘</button>
                    <button onClick={()=>delRow(r._id)} title="Remove"
                      style={{
                        background:"none",border:"none",
                        color:"rgba(255,70,70,0.4)",
                        cursor:"pointer",fontSize:14,padding:"2px 4px",
                      }}
                    >×</button>
                  </td>
                </tr>
                {isFD&&(
                  <tr>
                    <td colSpan={99} style={{
                      padding:"4px 10px 8px 20px",
                      background:"rgba(245,197,24,0.04)",
                      borderBottom:"1px solid rgba(245,197,24,0.1)",
                    }}>
                      <span style={{
                        fontSize:11,color:"#f5c518",marginRight:8,
                      }}>
                        ⚠ FD — supervisor required
                      </span>
                      <select value={r.supervisorId||""}
                        onChange={e=>upd(r._id,"supervisorId",e.target.value)}
                        style={{
                          background:"transparent",
                          border:"1px solid rgba(245,197,24,0.3)",
                          borderRadius:4,color:"rgba(255,255,255,0.7)",
                          fontSize:11,padding:"2px 8px",
                          marginRight:8,cursor:"pointer",fontFamily:"inherit",
                        }}
                      >
                        <option value="">Named supervisor…</option>
                        {rows.filter(x=>
                          x._id!==r._id
                          &&(x.type==="GDS"||x.type==="Specialist"
                            ||x.type==="DwSI")
                        ).map(x=>(
                          <option key={x._id} value={x._id}>
                            {x.name||"(unnamed)"}
                          </option>
                        ))}
                      </select>
                      <select value={r.secondarySupId||""}
                        onChange={e=>upd(r._id,"secondarySupId",e.target.value)}
                        style={{
                          background:"transparent",
                          border:"1px solid rgba(245,197,24,0.2)",
                          borderRadius:4,color:"rgba(255,255,255,0.5)",
                          fontSize:11,padding:"2px 8px",
                          cursor:"pointer",fontFamily:"inherit",
                        }}
                      >
                        <option value="">Secondary supervisor…</option>
                        {rows.filter(x=>
                          x._id!==r._id
                          &&x._id!==r.supervisorId
                          &&(x.type==="GDS"||x.type==="Specialist"
                            ||x.type==="DwSI")
                        ).map(x=>(
                          <option key={x._id} value={x._id}>
                            {x.name||"(unnamed)"}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                )}
                {isDwSI&&(
                  <tr>
                    <td colSpan={99} style={{
                      padding:"4px 10px 8px 20px",
                      background:"rgba(58,173,152,0.03)",
                      borderBottom:"1px solid rgba(58,173,152,0.08)",
                    }}>
                      <span style={{
                        fontSize:11,
                        color:"rgba(58,173,152,0.8)",marginRight:8,
                      }}>Specialty:</span>
                      <input value={r.specialty||""}
                        onChange={e=>upd(r._id,"specialty",e.target.value)}
                        placeholder="e.g. Implants, Endodontics, Oral Surgery"
                        style={{
                          background:"transparent",
                          border:"1px solid rgba(58,173,152,0.2)",
                          borderRadius:4,
                          color:"rgba(255,255,255,0.7)",
                          fontSize:11,padding:"2px 10px",
                          width:280,outline:"none",fontFamily:"inherit",
                        }}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      {hasAB&&(
        <div style={{
          fontSize:11,color:"rgba(58,173,152,0.6)",
          marginTop:6,padding:"0 4px",
        }}>
          Alternating patterns detected —
          set Week A reference date in policy.
        </div>
      )}
    </div>
  );
}

function NurseTable({rows, onChange}) {
  const TH = {
    fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.35)",
    textTransform:"uppercase",letterSpacing:"0.06em",
    padding:"8px 8px",textAlign:"left",
    borderBottom:"1px solid rgba(255,255,255,0.08)",whiteSpace:"nowrap",
  };
  const TD = {
    padding:"5px 7px",verticalAlign:"middle",
    borderBottom:"1px solid rgba(255,255,255,0.04)",
  };
  const INP = {
    background:"transparent",border:"none",
    color:"rgba(255,255,255,0.82)",fontFamily:"inherit",
    fontSize:12,padding:"2px 4px",outline:"none",
  };
  function upd(id,k,v){onChange(rows.map(r=>r._id===id?{...r,[k]:v}:r));}
  function toggleDay(id,day) {
    const r=rows.find(x=>x._id===id); if(!r) return;
    const days=r.workDays.includes(day)
      ?r.workDays.filter(d=>d!==day):[...r.workDays,day];
    onChange(rows.map(x=>x._id===id?{...x,workDays:days}:x));
  }
  function setHead(id) {
    onChange(rows.map(r=>({...r,headNurse:r._id===id})));
  }
  function copyRow(id) {
    const r=rows.find(x=>x._id===id); if(!r) return;
    const copy={...r,_id:tsUid("n"),name:"",headNurse:false};
    const idx=rows.findIndex(x=>x._id===id);
    const next=[...rows]; next.splice(idx+1,0,copy);
    onChange(next);
  }
  function delRow(id) {
    if(rows.length<=1) return;
    onChange(rows.filter(r=>r._id!==id));
  }
  return (
    <div style={{overflowX:"auto",marginBottom:8}}>
      <table style={{
        width:"100%",minWidth:820,
        borderCollapse:"collapse",fontSize:12,
        fontFamily:"'Instrument Sans',system-ui,sans-serif",
      }}>
        <thead>
          <tr>
            <th style={{...TH,width:168}}>Name</th>
            <th style={{...TH,width:148}}>Competency</th>
            <th style={{...TH,width:62}}>Hrs/wk</th>
            <th style={{...TH,width:164,textAlign:"center"}}>Days</th>
            <th style={{...TH,width:36,textAlign:"center"}}>RA</th>
            <th style={{...TH,width:36,textAlign:"center"}}>IV</th>
            <th style={{...TH,width:48,textAlign:"center"}}>Head</th>
            <th style={{...TH,width:44,textAlign:"center"}}>Mat.</th>
            <th style={{...TH,width:56}}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r,ri)=>(
            <tr key={r._id} style={{
              background:ri%2===0
                ?"rgba(255,255,255,0.02)":"transparent",
            }}>
              <td style={TD}>
                <input value={r.name}
                  onChange={e=>upd(r._id,"name",e.target.value)}
                  placeholder="Full name"
                  style={{...INP,fontWeight:r.name?600:400,width:"100%"}}
                />
              </td>
              <td style={TD}>
                <select value={r.status}
                  onChange={e=>upd(r._id,"status",e.target.value)}
                  style={{...INP,cursor:"pointer",width:"100%"}}
                >
                  {COMPETENCY.map(c=>(
                    <option key={c.v} value={c.v}>{c.label}</option>
                  ))}
                </select>
                <div style={{
                  fontSize:9,color:"rgba(255,255,255,0.22)",
                  lineHeight:1.3,marginTop:1,paddingLeft:2,
                }}>
                  {COMPETENCY.find(c=>c.v===r.status)?.sub}
                </div>
              </td>
              <td style={TD}>
                <input type="number" value={r.hrs}
                  onChange={e=>upd(r._id,"hrs",parseFloat(e.target.value)||0)}
                  style={{...INP,width:48}}
                  min={1} max={48} step={0.5}
                />
              </td>
              <td style={{...TD,textAlign:"center"}}>
                <div style={{
                  display:"flex",gap:3,justifyContent:"center",
                }}>
                  {WD.map(d=>(
                    <button key={d} onClick={()=>toggleDay(r._id,d)}
                      style={{
                        width:26,height:22,borderRadius:4,
                        border:"none",cursor:"pointer",
                        fontSize:9,fontWeight:700,fontFamily:"inherit",
                        background:r.workDays.includes(d)
                          ?"rgba(58,173,152,0.25)"
                          :"rgba(255,255,255,0.05)",
                        color:r.workDays.includes(d)
                          ?"#3aad98":"rgba(255,255,255,0.2)",
                      }}
                    >{d.slice(0,1)}</button>
                  ))}
                </div>
              </td>
              <td style={{...TD,textAlign:"center"}}>
                <input type="checkbox" checked={r.ra}
                  onChange={e=>upd(r._id,"ra",e.target.checked)}
                  style={{accentColor:"#3aad98"}}
                />
              </td>
              <td style={{...TD,textAlign:"center"}}>
                <input type="checkbox" checked={r.iv}
                  onChange={e=>upd(r._id,"iv",e.target.checked)}
                  style={{accentColor:"#3aad98"}}
                />
              </td>
              <td style={{...TD,textAlign:"center"}}>
                <input type="checkbox" checked={!!r.headNurse}
                  onChange={()=>setHead(r._id)}
                  style={{accentColor:"#3aad98"}}
                />
              </td>
              <td style={{...TD,textAlign:"center"}}>
                <input type="checkbox" checked={!!r.maternity}
                  onChange={e=>upd(r._id,"maternity",e.target.checked)}
                  style={{accentColor:"#f5c518"}}
                />
              </td>
              <td style={TD}>
                <button onClick={()=>copyRow(r._id)}
                  style={{
                    background:"none",border:"none",
                    color:"rgba(255,255,255,0.3)",
                    cursor:"pointer",fontSize:14,padding:"2px 3px",
                  }}
                >⎘</button>
                <button onClick={()=>delRow(r._id)}
                  style={{
                    background:"none",border:"none",
                    color:"rgba(255,70,70,0.4)",
                    cursor:"pointer",fontSize:14,padding:"2px 3px",
                  }}
                >×</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReceptionTable({rows, onChange}) {
  const TH = {
    fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.35)",
    textTransform:"uppercase",letterSpacing:"0.06em",
    padding:"8px 8px",textAlign:"left",
    borderBottom:"1px solid rgba(255,255,255,0.08)",whiteSpace:"nowrap",
  };
  const TD = {
    padding:"5px 7px",verticalAlign:"middle",
    borderBottom:"1px solid rgba(255,255,255,0.04)",
  };
  const INP = {
    background:"transparent",border:"none",
    color:"rgba(255,255,255,0.82)",fontFamily:"inherit",
    fontSize:12,padding:"2px 4px",outline:"none",
  };
  function upd(id,k,v){onChange(rows.map(r=>r._id===id?{...r,[k]:v}:r));}
  function toggleDay(id,day) {
    const r=rows.find(x=>x._id===id); if(!r) return;
    const days=r.workDays.includes(day)
      ?r.workDays.filter(d=>d!==day):[...r.workDays,day];
    onChange(rows.map(x=>x._id===id?{...x,workDays:days}:x));
  }
  function copyRow(id) {
    const r=rows.find(x=>x._id===id); if(!r) return;
    const copy={...r,_id:tsUid("r"),name:""};
    const idx=rows.findIndex(x=>x._id===id);
    const next=[...rows]; next.splice(idx+1,0,copy);
    onChange(next);
  }
  function delRow(id) {
    if(rows.length<=1) return;
    onChange(rows.filter(r=>r._id!==id));
  }
  return (
    <div style={{overflowX:"auto",marginBottom:8}}>
      <table style={{
        width:"100%",minWidth:580,
        borderCollapse:"collapse",fontSize:12,
        fontFamily:"'Instrument Sans',system-ui,sans-serif",
      }}>
        <thead>
          <tr>
            <th style={{...TH,width:188}}>Name</th>
            <th style={{...TH,width:140}}>Role</th>
            <th style={{...TH,width:70}}>Hrs/wk</th>
            <th style={{...TH,width:180,textAlign:"center"}}>Days</th>
            <th style={{...TH,width:60}}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r,ri)=>(
            <tr key={r._id} style={{
              background:ri%2===0
                ?"rgba(255,255,255,0.02)":"transparent",
            }}>
              <td style={TD}>
                <input value={r.name}
                  onChange={e=>upd(r._id,"name",e.target.value)}
                  placeholder="Full name"
                  style={{...INP,fontWeight:r.name?600:400,width:"100%"}}
                />
              </td>
              <td style={TD}>
                <select value={r.type}
                  onChange={e=>upd(r._id,"type",e.target.value)}
                  style={{...INP,cursor:"pointer",width:"100%"}}
                >
                  <option value="reception">Receptionist</option>
                  <option value="TCO">TCO</option>
                  <option value="manager">Practice Manager</option>
                </select>
              </td>
              <td style={TD}>
                <input type="number" value={r.hrs}
                  onChange={e=>upd(r._id,"hrs",parseFloat(e.target.value)||0)}
                  style={{...INP,width:48}}
                  min={1} max={48} step={0.5}
                />
              </td>
              <td style={{...TD,textAlign:"center"}}>
                <div style={{display:"flex",gap:3,justifyContent:"center"}}>
                  {WD.map(d=>(
                    <button key={d} onClick={()=>toggleDay(r._id,d)}
                      style={{
                        width:26,height:22,borderRadius:4,
                        border:"none",cursor:"pointer",
                        fontSize:9,fontWeight:700,fontFamily:"inherit",
                        background:r.workDays.includes(d)
                          ?"rgba(58,173,152,0.25)"
                          :"rgba(255,255,255,0.05)",
                        color:r.workDays.includes(d)
                          ?"#3aad98":"rgba(255,255,255,0.2)",
                      }}
                    >{d.slice(0,1)}</button>
                  ))}
                </div>
              </td>
              <td style={TD}>
                <button onClick={()=>copyRow(r._id)}
                  style={{
                    background:"none",border:"none",
                    color:"rgba(255,255,255,0.3)",
                    cursor:"pointer",fontSize:14,padding:"2px 3px",
                  }}
                >⎘</button>
                <button onClick={()=>delRow(r._id)}
                  style={{
                    background:"none",border:"none",
                    color:"rgba(255,70,70,0.4)",
                    cursor:"pointer",fontSize:14,padding:"2px 3px",
                  }}
                >×</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PolicyForm({policy, setPolicy, hasAB}) {
  const upd = (k,v) => setPolicy(p=>({...p,[k]:v}));
  const SEL = {
    background:"#181626",
    border:"1px solid rgba(255,255,255,0.1)",
    borderRadius:6,color:"rgba(255,255,255,0.8)",
    fontSize:13,padding:"7px 10px",
    fontFamily:"inherit",cursor:"pointer",outline:"none",
  };
  const NI = {
    background:"#181626",
    border:"1px solid rgba(255,255,255,0.1)",
    borderRadius:4,color:"rgba(255,255,255,0.8)",
    fontSize:12,padding:"4px 6px",
    fontFamily:"inherit",outline:"none",textAlign:"center",
  };
  const ROW = {
    marginBottom:22,paddingBottom:22,
    borderBottom:"1px solid rgba(255,255,255,0.05)",
  };
  const LBL = {
    fontSize:13,fontWeight:700,
    color:"rgba(255,255,255,0.85)",marginBottom:3,
  };
  const SUB = {
    fontSize:11,color:"rgba(255,255,255,0.32)",
    lineHeight:1.5,marginBottom:8,
  };
  function Btns({opts,field,match}) {
    return (
      <div style={{display:"flex",gap:6}}>
        {opts.map(o=>{
          const active = match
            ? policy[field]===o.v
            : (o.v==="Yes")===policy[field];
          return (
            <button key={o.v||o}
              onClick={()=>upd(field,
                match?o.v:(o.v||o)==="Yes"
              )}
              style={{
                padding:"6px 14px",borderRadius:7,fontSize:12,
                fontWeight:600,fontFamily:"inherit",cursor:"pointer",
                border:"1px solid",
                borderColor:active?
                  "#3aad98":"rgba(255,255,255,0.12)",
                background:active?
                  "rgba(58,173,152,0.1)":"transparent",
                color:active?"#3aad98":"rgba(255,255,255,0.38)",
              }}
            >{o.label||o.v||o}</button>
          );
        })}
      </div>
    );
  }
  return (
    <div style={{maxWidth:580}}>
      <div style={ROW}>
        <div style={LBL}>Lunch break</div>
        <div style={SUB}>
          One standard time for everyone, or does it vary per person?
        </div>
        <Btns field="lunchStandard"
          opts={[{v:"Yes",label:"Standard"},{v:"No",label:"Variable"}]}
        />
        {policy.lunchStandard&&(
          <input value={policy.lunchTime||"13:00-14:00"}
            onChange={e=>upd("lunchTime",e.target.value)}
            placeholder="13:00-14:00"
            style={{
              ...SEL,fontSize:13,padding:"6px 10px",
              width:140,marginTop:8,
            }}
          />
        )}
      </div>
      <div style={ROW}>
        <div style={LBL}>Rest breaks</div>
        <div style={SUB}>
          Legal minimum: unpaid 30 min for shifts over 6h
          (Working Time Regulations 1998)
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          <Btns field="breakPaid"
            opts={[{v:"Yes",label:"Paid"},{v:"No",label:"Unpaid"}]}
          />
          <span style={{fontSize:12,color:"rgba(255,255,255,0.35)"}}>
            <input type="number"
              value={policy.breakMins||30}
              onChange={e=>upd("breakMins",parseInt(e.target.value)||30)}
              style={{...NI,width:44}} min={0} max={60}
            /> min &gt;{" "}
            <input type="number"
              value={policy.breakThresholdHrs||6}
              onChange={e=>upd("breakThresholdHrs",
                parseFloat(e.target.value)||6)}
              style={{...NI,width:36}} min={1} max={12} step={0.5}
            />h
          </span>
        </div>
      </div>
      <div style={ROW}>
        <div style={LBL}>Setup & tidy time</div>
        <div style={SUB}>
          Minutes paid for surgery setup before clinic and tidy after.
        </div>
        <div style={{display:"flex",gap:16}}>
          <label style={{fontSize:12,color:"rgba(255,255,255,0.38)"}}>
            Setup{" "}
            <input type="number"
              value={policy.setupMins||0}
              onChange={e=>upd("setupMins",parseInt(e.target.value)||0)}
              style={{...NI,width:44,marginLeft:4}} min={0} max={60}
            /> min
          </label>
          <label style={{fontSize:12,color:"rgba(255,255,255,0.38)"}}>
            Tidy{" "}
            <input type="number"
              value={policy.tidyMins||0}
              onChange={e=>upd("tidyMins",parseInt(e.target.value)||0)}
              style={{...NI,width:44,marginLeft:4}} min={0} max={60}
            /> min
          </label>
        </div>
      </div>
      <div style={ROW}>
        <div style={LBL}>TOIL / Overtime</div>
        <div style={SUB}>
          Do you offer time off in lieu for overtime worked?
        </div>
        <Btns field="toilEnabled"
          opts={[{v:"Yes",label:"Yes — TOIL"},{v:"No",label:"No TOIL"}]}
        />
        {policy.toilEnabled&&(
          <div style={{display:"flex",gap:12,marginTop:10}}>
            <div>
              <div style={{
                fontSize:11,color:"rgba(255,255,255,0.35)",marginBottom:4,
              }}>Rate</div>
              <select value={policy.toilRate||1}
                onChange={e=>upd("toilRate",parseFloat(e.target.value))}
                style={SEL}
              >
                <option value={1}>1× hour for hour</option>
                <option value={1.5}>1.5× time and a half</option>
              </select>
            </div>
            <div>
              <div style={{
                fontSize:11,color:"rgba(255,255,255,0.35)",marginBottom:4,
              }}>Expiry</div>
              <select value={policy.toilExpiry||"yearEnd"}
                onChange={e=>upd("toilExpiry",e.target.value)}
                style={SEL}
              >
                <option value="yearEnd">Year end</option>
                <option value="6months">After 6 months</option>
                <option value="never">Never</option>
              </select>
            </div>
          </div>
        )}
      </div>
      <div style={ROW}>
        <div style={LBL}>Sick pay</div>
        <div style={SUB}>
          What do staff receive when absent?
          SSP is the statutory minimum (£116.75/wk).
        </div>
        <select value={policy.sickPay||"ssp"}
          onChange={e=>upd("sickPay",e.target.value)}
          style={{...SEL,minWidth:220}}
        >
          <option value="none">Nothing beyond SSP from HMRC</option>
          <option value="ssp">SSP only (statutory)</option>
          <option value="full">Full pay</option>
          <option value="hybrid">Hybrid — full pay then SSP</option>
        </select>
      </div>
      <div style={ROW}>
        <div style={LBL}>Leave year</div>
        <div style={SUB}>
          When does annual leave entitlement reset?
        </div>
        <Btns field="leaveYear" match
          opts={[
            {v:"jan-dec",label:"Jan – Dec"},
            {v:"apr-mar",label:"Apr – Mar"},
          ]}
        />
      </div>
      {hasAB&&(
        <div style={ROW}>
          <div style={LBL}>Week A reference date</div>
          <div style={SUB}>
            You have alternating patterns. Pick any Monday
            that falls in a Week A. All alternating schedules
            cascade from this date.
          </div>
          <input type="date"
            value={policy.weekAStart||""}
            onChange={e=>upd("weekAStart",e.target.value)}
            style={{...SEL,fontSize:13,padding:"7px 10px"}}
          />
        </div>
      )}
      <div style={ROW}>
        <div style={LBL}>Bank holidays</div>
        <div style={SUB}>
          UK statutory minimum is 28 days including 8 bank
          holidays. Most dental practices include them in the
          28 — staff book BH days like any other leave.
          If you give them on top, staff get 36 days total.
        </div>
        <Btns field="bhIncluded"
          opts={[
            {v:"Yes",label:"Included in 28 days"},
            {v:"No",label:"Given on top (36 days)"},
          ]}
        />
      </div>
      <div style={{...ROW,borderBottom:"none",marginBottom:0}}>
        <div style={LBL}>Carry-over</div>
        <div style={SUB}>
          Can staff carry unused leave into the next year?
        </div>
        <Btns field="carryOver"
          opts={[{v:"No",label:"No carry-over"},
                 {v:"Yes",label:"Yes"}]}
        />
        {policy.carryOver&&(
          <div style={{
            display:"flex",alignItems:"center",gap:8,marginTop:8,
          }}>
            <span style={{fontSize:12,color:"rgba(255,255,255,0.38)"}}>
              Max days carried:
            </span>
            <input type="number"
              value={policy.carryOverMax||5}
              onChange={e=>upd("carryOverMax",parseInt(e.target.value)||0)}
              style={{
                background:"#181626",
                border:"1px solid rgba(255,255,255,0.1)",
                borderRadius:4,color:"rgba(255,255,255,0.8)",
                fontSize:12,padding:"4px 6px",
                width:48,fontFamily:"inherit",outline:"none",
                textAlign:"center",
              }}
              min={1} max={28}
            />
          </div>
        )}
      </div>

      {/* ── INFORMATIONAL: Issue viewers ── */}
      {/* No form field here — staff aren't entered yet at this phase. */}
      {/* Just plants the seed so principals know the option exists. */}
      <div style={{
        marginTop:8,padding:"14px 16px",
        background:"rgba(58,173,152,0.05)",
        border:"1px solid rgba(58,173,152,0.18)",
        borderRadius:10,
      }}>
        <div style={{
          fontSize:12,fontWeight:700,color:"rgba(58,173,152,0.95)",
          marginBottom:6,
          fontFamily:"'Instrument Sans',system-ui,sans-serif",
        }}>One more thing — sharing oversight</div>
        <div style={{
          fontSize:11,color:"rgba(255,255,255,0.62)",lineHeight:1.6,
        }}>
          By default, only you and any managers see Tier 2 (Concern) issues
          and recurring-issue patterns. Tier 3 (Serious / Whistleblowing)
          always stays with you. After onboarding, you can grant trusted
          staff — a senior nurse, head nurse, deputy clinician — the same
          Tier 1 + Tier 2 visibility from{" "}
          <span style={{color:"rgba(58,173,152,0.95)",fontWeight:600}}>
            Admin → Settings → Issues
          </span>. Useful when you want someone to share oversight with you,
          rather than carrying it alone.
        </div>
      </div>
    </div>
  );
}

function HolidayTable({allStaff, policy, holidays, setHolidays}) {
  const leaveYear = policy.leaveYear||"jan-dec";
  const now = new Date();
  const lyStart = leaveYear==="apr-mar"
    ? new Date(now.getMonth()>=3
        ? now.getFullYear()+"-04-01"
        : (now.getFullYear()-1)+"-04-01")
    : new Date(now.getFullYear()+"-01-01");
  const lyStartStr = lyStart.toISOString().split("T")[0];

  function calcEntitlement(person, baseOverride) {
    const isPartner = !!(person.isPartner
      ||(person.type||"").toLowerCase().includes("partner"));
    const defaultBase = isPartner ? 42 : 28;
    const base = (baseOverride!=null&&baseOverride!=="")
      ? parseFloat(baseOverride)||defaultBase
      : defaultBase;
    const hrs = person.hrs||37.5;
    const raw = Math.round((hrs/37.5)*base*10)/10;
    // Pro-rate for mid-year starters
    const hol = holidays[person._id||person.id]||{};
    const sd = hol.startDate||"";
    if (sd && sd>lyStartStr) {
      const lyEnd = leaveYear==="apr-mar"
        ? new Date((lyStart.getFullYear()+1)+"-03-31")
        : new Date(lyStart.getFullYear()+"-12-31");
      const lyDays = Math.round((lyEnd-lyStart)/86400000)+1;
      const startD = new Date(sd+"T12:00:00");
      const remaining = Math.round((lyEnd-startD)/86400000)+1;
      return Math.round((remaining/lyDays)*raw*10)/10;
    }
    return raw;
  }

  function upd(id,k,v) {
    setHolidays(h=>({...h,[id]:{...(h[id]||{}),[k]:v}}));
  }

  const TH = {
    fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.35)",
    textTransform:"uppercase",letterSpacing:"0.06em",
    padding:"8px 8px",textAlign:"left",
    borderBottom:"1px solid rgba(255,255,255,0.08)",
    whiteSpace:"nowrap",
  };
  const TD = {
    padding:"6px 8px",verticalAlign:"middle",
    borderBottom:"1px solid rgba(255,255,255,0.04)",
  };
  const NI = {
    background:"transparent",border:"none",
    color:"rgba(255,255,255,0.82)",fontFamily:"inherit",
    fontSize:12,padding:"2px 4px",outline:"none",
    width:52,textAlign:"center",
  };

  const bhNote = policy.bhIncluded!==false
    ? "Includes 8 bank holidays"
    : "Bank holidays given on top";

  return (
    <div>
      <div style={{
        fontSize:11,color:"rgba(58,173,152,0.7)",
        marginBottom:10,lineHeight:1.6,
      }}>
        Entitlement is pro-rated from contracted hours.
        {" "}{bhNote}.
        Partners default to 42 days — all figures are editable.
      </div>
      <div style={{overflowX:"auto"}}>
        <table style={{
          width:"100%",minWidth:620,borderCollapse:"collapse",
          fontSize:12,
          fontFamily:"'Instrument Sans',system-ui,sans-serif",
        }}>
          <thead>
            <tr>
              <th style={{...TH,width:180}}>Name</th>
              <th style={{...TH,width:80}}>Site</th>
              <th style={{...TH,width:90,textAlign:"center"}}>
                Base days
              </th>
              <th style={{...TH,width:90,textAlign:"center"}}>
                Entitlement
              </th>
              <th style={{...TH,width:120,textAlign:"center"}}>
                Hols taken this year
                <span title={
                  "Days each person has already taken this year "
                  +"BEFORE Omni was set up. Enter 0 if none. "
                  +"Used so the Remaining days figure is accurate from day one — "
                  +"holidays you book inside Omni are tracked separately."
                } style={{
                  marginLeft:4,cursor:"help",
                  color:"rgba(58,173,152,0.6)",fontSize:10,
                }}>?</span>
              </th>
              <th style={{...TH,width:90,textAlign:"center"}}>
                Remaining
              </th>
              <th style={{...TH,width:90}}>
                Leave year
              </th>
              <th style={{...TH,width:130}}>
                Started this year?
                <span title={
                  "If someone joined part-way through this leave year, "
                  +"their holiday entitlement is pro-rated from their "
                  +"start date. Only tick Yes for staff who joined "
                  +"after the leave year began."
                } style={{
                  marginLeft:4,cursor:"help",
                  color:"rgba(58,173,152,0.6)",fontSize:10,
                }}>?</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {allStaff.map((p,ri)=>{
              const pid = p._id||p.id;
              const hol = holidays[pid]||{};
              const isPartner = !!(p.isPartner
                ||(p.type||"").toLowerCase().includes("partner"));
              const defaultBase = isPartner?42:28;
              const baseVal = hol.baseOverride!=null
                ? hol.baseOverride : defaultBase;
              const entit = calcEntitlement(p, hol.baseOverride);
              const taken = parseFloat(hol.taken)||0;
              const remaining = Math.round((entit-taken)*10)/10;
              const remColor = remaining<0
                ? "#ff4646"
                : remaining<5
                ? "#f5c518"
                : "rgba(255,255,255,0.55)";
              const isThisYear = !!(hol.startDate
                && hol.startDate>lyStartStr);
              return (
                <tr key={pid} style={{
                  background:ri%2===0
                    ?"rgba(255,255,255,0.02)":"transparent",
                }}>
                  <td style={TD}>
                    <span style={{
                      fontWeight:600,
                      color:"rgba(255,255,255,0.82)",
                    }}>{p.name}</span>
                    {isPartner&&(
                      <span style={{
                        fontSize:9,color:"#f5c518",
                        marginLeft:5,fontWeight:700,
                        textTransform:"uppercase",
                      }}>Partner</span>
                    )}
                  </td>
                  <td style={{...TD,color:"rgba(255,255,255,0.35)",
                    fontSize:11}}>
                    {p.site}
                  </td>
                  <td style={{...TD,textAlign:"center"}}>
                    <input type="number"
                      value={baseVal}
                      onChange={e=>upd(pid,"baseOverride",
                        e.target.value)}
                      style={{...NI}}
                      min={1} max={60} step={0.5}
                      title="Edit base entitlement for this person"
                    />
                  </td>
                  <td style={{
                    ...TD,textAlign:"center",
                    color:"rgba(255,255,255,0.6)",fontWeight:600,
                  }}>
                    {entit}
                    {isThisYear&&(
                      <span style={{
                        fontSize:9,color:"#3aad98",
                        marginLeft:3,
                      }}>pro-rata</span>
                    )}
                  </td>
                  <td style={{...TD,textAlign:"center"}}>
                    <input type="number"
                      value={hol.taken||""}
                      onChange={e=>upd(pid,"taken",
                        e.target.value)}
                      placeholder="0"
                      style={{...NI,
                        color:"rgba(255,255,255,0.7)",
                      }}
                      min={0} max={60} step={0.5}
                    />
                  </td>
                  <td style={{...TD,textAlign:"center",
                    fontWeight:600,color:remColor,
                  }}>
                    {remaining}
                  </td>
                  <td style={{...TD}}>
                    <select
                      value={hol.leaveYear||"default"}
                      onChange={e=>upd(pid,"leaveYear",e.target.value)}
                      style={{
                        background:"#181626",
                        border:"1px solid rgba(255,255,255,0.1)",
                        borderRadius:4,
                        color:"rgba(255,255,255,0.55)",
                        fontSize:10,padding:"3px 4px",
                        fontFamily:"inherit",outline:"none",
                        cursor:"pointer",width:"100%",
                      }}
                    >
                      <option value="default">
                        Practice default
                      </option>
                      <option value="jan-dec">Jan – Dec</option>
                      <option value="apr-mar">Apr – Mar</option>
                    </select>
                  </td>
                  <td style={{...TD}}>
                    <select
                      value={hol.startDate ? "yes" : "no"}
                      onChange={e=>{
                        if(e.target.value==="no") {
                          upd(pid,"startDate","");
                        } else {
                          upd(pid,"startDate",
                            new Date().toISOString().slice(0,7)+"-01");
                        }
                      }}
                      style={{
                        background:"#181626",
                        border:"1px solid rgba(255,255,255,0.1)",
                        borderRadius:4,
                        color:"rgba(255,255,255,0.6)",
                        fontSize:11,padding:"3px 6px",
                        fontFamily:"inherit",outline:"none",
                        marginBottom:hol.startDate?3:0,
                        cursor:"pointer",
                      }}
                    >
                      <option value="no">No</option>
                      <option value="yes">Yes</option>
                    </select>
                    {hol.startDate&&(
                      <input type="month"
                        value={hol.startDate.slice(0,7)}
                        onChange={e=>{
                          upd(pid,"startDate",
                            e.target.value ? e.target.value+"-01" : "");
                        }}
                        style={{
                          display:"block",
                          background:"transparent",
                          border:"1px solid rgba(255,255,255,0.1)",
                          borderRadius:4,
                          color:"rgba(255,255,255,0.5)",
                          fontSize:10,padding:"2px 4px",
                          fontFamily:"inherit",outline:"none",
                          width:"100%",
                        }}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{
        marginTop:10,fontSize:11,
        color:"rgba(255,255,255,0.25)",lineHeight:1.6,
      }}>
        Base days — editable per person. Entitlement
        auto-calculated from hours.
        Red remaining = over-taken, worth checking with payroll.
      </div>
    </div>
  );
}

function TeamSetupTables({state, set, sites, practiceName, onComplete}) {
  const siteList = sites.length>0
    ? sites : [{id:"site",name:"Your practice",surgeries:4}];
  const [activeSite, setActiveSite] = useState(siteList[0].id);
  const [section, setSection] = useState("intro");
  const [hasSedation, setHasSedation] = useState({});
  // Default schedule = practice opening hours captured in basics.
  // Pre-fills every clinician's row so the user only edits exceptions.
  const defaultSched = React.useMemo(
    () => tsDefaultSched(state),
    [state?.practiceProfile?.clinicStart, state?.practiceProfile?.clinicEnd]
  );
  const [clins, setClins] = useState(()=>{
    const o={}; siteList.forEach(s=>{o[s.id]=[tsNewClin(s.id, defaultSched)];}); return o;
  });
  const [nurses, setNurses] = useState(()=>{
    const o={}; siteList.forEach(s=>{o[s.id]=[tsNewNurse(s.id)];}); return o;
  });
  const [recs, setRecs] = useState(()=>{
    const o={}; siteList.forEach(s=>{o[s.id]=[tsNewRec(s.id)];}); return o;
  });
  const [compRoles, setCompRoles] = useState(()=>{
    const o={};
    siteList.forEach(s=>{
      o[s.id]={safeguardingL3:"",fireMarshal:"",firstAider:"",keyholder:""};
    });
    return o;
  });
  const [policy, setPolicy] = useState({
    lunchStandard:true, lunchTime:"13:00-14:00",
    toilEnabled:false, toilRate:1, toilExpiry:"yearEnd",
    sickPay:"ssp", leaveYear:"jan-dec",
    breakPaid:false, breakMins:30, breakThresholdHrs:6,
    setupMins:0, tidyMins:0, weekAStart:"",
    bhIncluded:true, carryOver:false, carryOverMax:5,
  });
  const [holidays, setHolidays] = useState({});

  // All named staff across all sites for holiday table
  const allHolStaff = siteList.flatMap(s=>[
    ...(clins[s.id]||[]).filter(c=>c.name.trim()),
    ...(nurses[s.id]||[]).filter(n=>n.name.trim()),
    ...(recs[s.id]||[]).filter(r=>r.name.trim()),
  ]);

  const C = {
    bg:"#100f1a", surface:"#181626", header:"#0c0b16",
    border:"rgba(255,255,255,0.07)",
    text:"rgba(255,255,255,0.82)", dim:"rgba(255,255,255,0.38)",
    teal:"#3aad98",
  };
  const SECS = ["intro","nurses","clinicians","reception","policy","holidays"];
  const secIdx = SECS.indexOf(section);
  const hasAnyAB = siteList.some(s=>
    (clins[s.id]||[]).some(c=>
      Object.values(c.freq||{}).some(v=>v==="weekA"||v==="weekB")
    )
  );
  const INTROS = {
    intro: "Right — tables: nurses, clinicians, then reception. "
      +"About 10 minutes if you have everyone to hand. "
      +"Add what you know now — you can fill gaps later.",
    clinicians: "Clinical team next. "
      +"Enter times in 24hr — 08:00-17:30. Press Enter to advance. "
      +"Copy a row if two dentists share the same pattern.",
    nurses: "Nurses first — they drive your Iris Score. "
      +"Tick RA and IV for sedation-qualified nurses. "
      +"Competency level shows what it means for your score.",
    reception: "Reception and TCOs. Hours and working days — "
      +"shifts live in the rota.",
    policy: "Almost done. A few decisions about how the practice runs. "
      +"No right or wrong answers.",
    holidays: "Optional but useful. "
      +"I've calculated entitlement from contracted hours — "
      +"edit any number if someone's contract differs. "
      +"For anyone who's already taken holidays this year before Omni was set up, "
      +"enter the days in the Hols taken column — that way remaining days is accurate from day one. "
      +"You can skip this and fill it in later.",
  };
  function buildAndComplete() {
    const allClins = siteList.flatMap(s=>
      (clins[s.id]||[]).filter(c=>c.name.trim())
        .map(c=>({...c,id:c._id}))
    );
    const allNurses = siteList.flatMap(s=>{
      const cr=compRoles[s.id]||{};
      return (nurses[s.id]||[]).filter(n=>n.name.trim()).map(n=>({
        ...n, id:n._id,
        safeguardingL3:n._id===cr.safeguardingL3,
        fireMarshal:n._id===cr.fireMarshal,
        firstAider:n._id===cr.firstAider,
        keyholder:n._id===cr.keyholder,
      }));
    });
    const allRecs = siteList.flatMap(s=>
      (recs[s.id]||[]).filter(r=>r.name.trim())
        .map(r=>({...r,id:r._id}))
    );
    onComplete({
      clinicians:allClins, nurses:allNurses,
      reception:allRecs, hasSedation, policy, holidays,
    });
  }
  const isMulti = siteList.length>1;
  const showTabs = ["nurses","clinicians","reception"].includes(section);
  const BTNADD = {
    display:"flex",alignItems:"center",gap:6,
    padding:"8px 14px",borderRadius:8,
    border:"1px dashed rgba(255,255,255,0.12)",
    background:"transparent",color:"rgba(255,255,255,0.32)",
    fontSize:12,fontFamily:"inherit",cursor:"pointer",marginTop:6,
  };
  return (
    <div style={{
      minHeight:"100vh",background:C.bg,
      display:"flex",flexDirection:"column",
      fontFamily:"'Instrument Sans',system-ui,sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Instrument+Sans:wght@400;500;600;700&display=swap');
        .ts-tab:hover{color:rgba(255,255,255,0.7)!important}
        .ts-next:hover{background:#2d9882!important}
        select option{background:#181626;color:rgba(255,255,255,0.82)}
      `}</style>
      {/* Header */}
      <div style={{
        background:C.header,
        borderBottom:"1px solid rgba(255,255,255,0.07)",
        padding:"11px 20px",flexShrink:0,
        display:"flex",alignItems:"center",gap:10,
      }}>
        <span style={{
          fontFamily:"'Instrument Serif',serif",fontSize:19,
          fontStyle:"italic",fontWeight:700,
          color:"rgba(255,255,255,0.95)",
        }}>Omni</span>
        <span style={{
          width:5,height:5,borderRadius:"50%",
          background:C.teal,display:"inline-block",
        }}/>
        <div style={{
          display:"flex",alignItems:"center",gap:5,
          background:"rgba(58,173,152,0.1)",
          border:"1px solid rgba(58,173,152,0.2)",
          borderRadius:20,padding:"3px 9px",
        }}>
          <div style={{width:5,height:5,borderRadius:"50%",background:C.teal}}/>
          <span style={{
            fontSize:10,fontWeight:700,color:C.teal,
            letterSpacing:"0.08em",textTransform:"uppercase",
          }}>Team setup</span>
        </div>
        <div style={{marginLeft:"auto",display:"flex",gap:5}}>
          {["clinicians","nurses","reception","policy"].map((s,i)=>(
            <div key={s} style={{
              width:5,height:5,borderRadius:"50%",
              background:secIdx>i+1||secIdx===i+1
                ?C.teal:"rgba(255,255,255,0.1)",
            }}/>
          ))}
        </div>
      </div>
      {/* Body */}
      <div style={{flex:1,overflowY:"auto",padding:"18px 22px"}}>
        {/* Iris bubble */}
        <div style={{
          display:"flex",gap:9,marginBottom:18,alignItems:"flex-start",
        }}>
          <div style={{
            width:26,height:26,borderRadius:"50%",flexShrink:0,
            background:"linear-gradient(135deg,#00d4ff,#a855f7,#f472b6)",
            display:"flex",alignItems:"center",justifyContent:"center",
            fontSize:10,fontWeight:700,color:"white",
          }}>I</div>
          <div style={{
            background:"rgba(58,173,152,0.07)",
            border:"1px solid rgba(58,173,152,0.13)",
            borderRadius:"11px 11px 11px 4px",
            padding:"9px 13px",fontSize:13,
            color:C.text,lineHeight:1.6,maxWidth:520,
          }}>
            {INTROS[section]||INTROS.intro}
          </div>
        </div>
        {/* Sedation toggle on clinicians */}
        {section==="clinicians"&&(
          <div style={{marginBottom:14}}>
            <div style={{
              fontSize:11,color:C.dim,marginBottom:6,
            }}>
              Does this site do sedation (RA or IV)?
            </div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {siteList.map(s=>(
                <button key={s.id}
                  onClick={()=>setHasSedation(h=>({
                    ...h,[s.id]:!h[s.id]
                  }))}
                  style={{
                    padding:"4px 12px",borderRadius:7,
                    fontSize:12,fontWeight:600,
                    fontFamily:"inherit",cursor:"pointer",
                    border:"1px solid",
                    borderColor:hasSedation[s.id]
                      ?C.teal:"rgba(255,255,255,0.12)",
                    background:hasSedation[s.id]
                      ?"rgba(58,173,152,0.1)":"transparent",
                    color:hasSedation[s.id]?C.teal:C.dim,
                  }}
                >
                  {isMulti?s.name:"Yes, we do sedation"}
                  {hasSedation[s.id]?" ✓":""}
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Site tabs */}
        {showTabs&&isMulti&&(
          <div style={{
            display:"flex",gap:1,marginBottom:14,
            borderBottom:"1px solid rgba(255,255,255,0.07)",
          }}>
            {siteList.map(s=>(
              <button key={s.id} className="ts-tab"
                onClick={()=>setActiveSite(s.id)}
                style={{
                  padding:"7px 16px",border:"none",
                  borderBottom:activeSite===s.id
                    ?"2px solid "+C.teal
                    :"2px solid transparent",
                  background:"transparent",
                  color:activeSite===s.id?C.teal:C.dim,
                  fontSize:13,fontWeight:600,
                  fontFamily:"inherit",cursor:"pointer",
                  marginBottom:-1,transition:"color 0.12s",
                }}
              >{s.name}</button>
            ))}
          </div>
        )}
        {/* Intro */}
        {section==="intro"&&(
          <div style={{maxWidth:500,paddingTop:6}}>
            <div style={{
              fontFamily:"'Instrument Serif',serif",
              fontSize:25,fontStyle:"italic",
              color:"rgba(255,255,255,0.92)",marginBottom:10,
            }}>
              Your team, done properly.
            </div>
            <p style={{
              fontSize:14,color:C.dim,lineHeight:1.7,marginBottom:20,
            }}>
              Three tables — clinicians, nurses, reception.
              About 10 minutes with everyone to hand.
              You can always add or edit later.
            </p>
            <div style={{
              background:"rgba(255,255,255,0.03)",
              border:"1px solid rgba(255,255,255,0.07)",
              borderRadius:9,padding:"12px 14px",
            }}>
              <div style={{
                fontSize:10,fontWeight:700,color:C.dim,
                textTransform:"uppercase",letterSpacing:"0.08em",
                marginBottom:5,
              }}>Desktop recommended</div>
              <div style={{fontSize:12,color:C.dim,lineHeight:1.6}}>
                Tables work best on a laptop. If you're on a phone,
                landscape helps — or come back on a larger screen.
              </div>
            </div>
          </div>
        )}
        {/* Clinician table */}
        {section==="clinicians"&&(
          <>
            <ClinicianTable
              rows={clins[activeSite]||[]}
              onChange={r=>setClins(c=>({...c,[activeSite]:r}))}
              hasSedation={!!hasSedation[activeSite]}
              siteId={activeSite}
            />
            <button style={BTNADD}
              onClick={()=>setClins(c=>({
                ...c,
                [activeSite]:[...(c[activeSite]||[]),tsNewClin(activeSite, defaultSched)],
              }))}
            >+ Add clinician</button>
          </>
        )}
        {/* Nurse table */}
        {section==="nurses"&&(
          <>
            <NurseTable
              rows={nurses[activeSite]||[]}
              onChange={r=>setNurses(n=>({...n,[activeSite]:r}))}
            />
            <button style={BTNADD}
              onClick={()=>setNurses(n=>({
                ...n,
                [activeSite]:[...(n[activeSite]||[]),tsNewNurse(activeSite)],
              }))}
            >+ Add nurse</button>
            {/* Compliance roles */}
            <div style={{
              marginTop:14,padding:"13px 15px",
              background:"rgba(58,173,152,0.04)",
              border:"1px solid rgba(58,173,152,0.1)",
              borderRadius:10,
            }}>
              <div style={{
                fontSize:10,fontWeight:700,color:C.teal,
                textTransform:"uppercase",letterSpacing:"0.08em",
                marginBottom:10,
              }}>
                Compliance roles — CQC inspectable
              </div>
              <div style={{
                display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,
              }}>
                {[
                  {k:"safeguardingL3",label:"Level 3 Safeguarding",
                   reg:"CQC Reg 13"},
                  {k:"fireMarshal",label:"Fire Marshal",
                   reg:"Fire Safety Order 2005"},
                  {k:"firstAider",label:"First Aider",
                   reg:"H&S First-Aid Regs 1981"},
                  {k:"keyholder",label:"Keyholder",reg:""},
                ].map(({k,label,reg})=>(
                  <div key={k}>
                    <div style={{
                      fontSize:11,color:C.dim,marginBottom:4,
                    }}>
                      {label}
                      {reg&&(
                        <span style={{
                          fontSize:9,
                          color:"rgba(58,173,152,0.55)",marginLeft:4,
                        }}>{reg}</span>
                      )}
                    </div>
                    <select
                      value={compRoles[activeSite]?.[k]||""}
                      onChange={e=>setCompRoles(cr=>({
                        ...cr,
                        [activeSite]:{
                          ...(cr[activeSite]||{}), [k]:e.target.value,
                        },
                      }))}
                      style={{
                        background:"#181626",
                        border:"1px solid rgba(255,255,255,0.1)",
                        borderRadius:6,
                        color:"rgba(255,255,255,0.7)",
                        fontSize:12,padding:"5px 8px",
                        width:"100%",cursor:"pointer",
                        fontFamily:"inherit",outline:"none",
                      }}
                    >
                      <option value="">Not assigned</option>
                      {(nurses[activeSite]||[])
                        .filter(n=>n.name.trim())
                        .map(n=>(
                          <option key={n._id} value={n._id}>
                            {n.name}
                          </option>
                        ))
                      }
                    </select>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
        {/* Reception table */}
        {section==="reception"&&(
          <>
            <ReceptionTable
              rows={recs[activeSite]||[]}
              onChange={r=>setRecs(x=>({...x,[activeSite]:r}))}
            />
            <button style={BTNADD}
              onClick={()=>setRecs(r=>({
                ...r,
                [activeSite]:[...(r[activeSite]||[]),tsNewRec(activeSite)],
              }))}
            >+ Add person</button>
          </>
        )}
        {/* Policy form */}
        {section==="policy"&&(
          <PolicyForm
            policy={policy}
            setPolicy={setPolicy}
            hasAB={hasAnyAB}
          />
        )}
        {/* Holiday table */}
        {section==="holidays"&&(
          <>
            <HolidayTable
              allStaff={allHolStaff}
              policy={policy}
              holidays={holidays}
              setHolidays={setHolidays}
            />
            <div style={{marginTop:14,textAlign:"center"}}>
              <button onClick={buildAndComplete}
                style={{
                  background:"none",border:"none",
                  color:"rgba(255,255,255,0.28)",
                  fontSize:12,cursor:"pointer",
                  fontFamily:"inherit",textDecoration:"underline",
                }}
              >
                Skip — I'll fill this in later
              </button>
            </div>
          </>
        )}
      </div>
      {/* Footer nav */}
      <div style={{
        background:C.header,
        borderTop:"1px solid rgba(255,255,255,0.07)",
        padding:"11px 22px",
        display:"flex",justifyContent:"space-between",
        alignItems:"center",flexShrink:0,
      }}>
        <button
          onClick={()=>setSection(SECS[secIdx-1]||section)}
          disabled={secIdx===0}
          style={{
            background:"none",
            border:"1px solid rgba(255,255,255,0.1)",
            borderRadius:7,padding:"7px 16px",
            color:secIdx===0?"rgba(255,255,255,0.12)":C.dim,
            fontSize:13,fontFamily:"inherit",
            cursor:secIdx===0?"default":"pointer",
          }}
        >← Back</button>
        <div style={{fontSize:11,color:C.dim,textTransform:"capitalize"}}>
          {section==="intro"?"Let's start":section}
        </div>
        {section==="holidays"?(
          <button className="ts-next" onClick={buildAndComplete}
            style={{
              background:C.teal,border:"none",borderRadius:7,
              padding:"7px 18px",color:"#fff",fontSize:13,
              fontWeight:600,fontFamily:"inherit",cursor:"pointer",
              transition:"background 0.15s",
            }}
          >Generate gap report →</button>
        ):section==="policy"?(
          <button className="ts-next"
            onClick={()=>setSection("holidays")}
            style={{
              background:C.teal,border:"none",borderRadius:7,
              padding:"7px 18px",color:"#fff",fontSize:13,
              fontWeight:600,fontFamily:"inherit",cursor:"pointer",
              transition:"background 0.15s",
            }}
          >Next →</button>
        ):(
          <button className="ts-next"
            onClick={()=>setSection(SECS[secIdx+1]||section)}
            style={{
              background:C.teal,border:"none",borderRadius:7,
              padding:"7px 18px",color:"#fff",fontSize:13,
              fontWeight:600,fontFamily:"inherit",cursor:"pointer",
              transition:"background 0.15s",
            }}
          >Next →</button>
        )}
      </div>
    </div>
  );
}

/* ── Gap Report PDF Generator ────────────────────────────────────────────────── */
function generateGapReportPDF(gapReport, practiceName, sites) {
  const { statutory, cqcEvidence, quickWins, score } = gapReport;
  const date = new Date().toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric",
  });
  const scoreColor = score >= 80 ? "#059669" : score >= 60 ? "#d97706" : "#dc2626";
  const scoreLabel = score >= 80
    ? "Good foundation. A few things to tidy up."
    : score >= 60
    ? "Some gaps worth addressing before your next inspection."
    : "Meaningful gaps here — worth addressing soon.";
  const siteNames = (sites || []).map(s => s.name).join(" · ") || practiceName;

  const H = [];
  H.push(`<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">`);
  H.push(`<title>${practiceName} — Compliance Readiness Assessment</title>`);
  H.push(`<link href="https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Instrument+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">`);
  H.push(`<style>`);
  H.push(`*{box-sizing:border-box;margin:0;padding:0}`);
  H.push(`body{font-family:'Instrument Sans',sans-serif;color:#1a1a2e;background:#fff;padding:40px;max-width:780px;margin:0 auto;font-size:13px;line-height:1.6}`);
  H.push(`@media print{body{padding:20px}.no-print{display:none!important}@page{margin:18mm}}`);
  H.push(`.hdr{border-bottom:2px solid #0f172a;padding-bottom:22px;margin-bottom:30px}`);
  H.push(`.omni{font-family:'Instrument Serif',serif;font-style:italic;font-size:17px;color:#3aad98;font-weight:700;margin-bottom:6px}`);
  H.push(`.pname{font-family:'Instrument Serif',serif;font-size:30px;color:#0f172a;font-weight:400;margin-bottom:2px}`);
  H.push(`.dtitle{font-size:14px;color:#64748b;margin-bottom:2px}`);
  H.push(`.ddate{font-size:11px;color:#94a3b8}`);
  H.push(`.sec{margin-bottom:30px}`);
  H.push(`.sec-h{font-size:10px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;margin-bottom:10px;padding-bottom:5px;border-bottom:1px solid #e2e8f0}`);
  H.push(`.score-row{display:flex;align-items:center;gap:28px;padding:22px;background:#f8fafc;border-radius:10px;margin-bottom:10px}`);
  H.push(`.s-num{font-family:'Instrument Serif',serif;font-style:italic;font-size:76px;line-height:1;font-weight:400}`);
  H.push(`.s-lbl{font-size:15px;font-weight:600;color:#1e293b;margin-bottom:4px}`);
  H.push(`.s-sub{font-size:12px;color:#64748b;line-height:1.7;margin-top:6px}`);
  H.push(`.gap{padding:11px 13px;margin-bottom:7px;border-radius:6px;background:#fafafa;border:1px solid #e2e8f0}`);
  H.push(`.gap-lbl{font-size:13px;font-weight:600;color:#1e293b;margin-bottom:2px}`);
  H.push(`.gap-bas{font-size:11px;margin-bottom:2px}`);
  H.push(`.gap-act{font-size:11px;color:#64748b}`);
  H.push(`.win{padding:11px 13px;margin-bottom:7px;border-radius:6px;background:#f0fdf4;border:1px solid #bbf7d0;font-size:13px;color:#166534}`);
  H.push(`.ftr{margin-top:44px;padding-top:14px;border-top:1px solid #e2e8f0;font-size:11px;color:#94a3b8;display:flex;justify-content:space-between;align-items:center}`);
  H.push(`.pbtn{position:fixed;bottom:24px;right:24px;background:#3aad98;color:#fff;border:none;border-radius:10px;padding:11px 20px;font-size:14px;font-weight:600;cursor:pointer;font-family:inherit;letter-spacing:0.01em}`);
  H.push(`</style></head><body>`);

  // Print button
  H.push(`<button class="pbtn no-print" onclick="window.print()">Save as PDF ↓</button>`);

  // Header
  H.push(`<div class="hdr"><div class="omni">Omni</div>`);
  H.push(`<div class="pname">${practiceName}</div>`);
  H.push(`<div class="dtitle">Compliance Readiness Assessment</div>`);
  H.push(`<div class="ddate">Prepared ${date} &nbsp;·&nbsp; Confidential</div></div>`);

  // Iris Score
  H.push(`<div class="sec"><div class="sec-h" style="color:#3aad98">Iris Score</div>`);
  H.push(`<div class="score-row"><div class="s-num" style="color:${scoreColor}">${score}</div>`);
  H.push(`<div><div class="s-lbl">${scoreLabel}</div>`);
  H.push(`<div class="s-sub">Nurse quality &nbsp;·&nbsp; 50% weighting<br>Clinician presence &nbsp;·&nbsp; 30% weighting<br>Reception coverage &nbsp;·&nbsp; 20% weighting</div>`);
  H.push(`</div></div>`);
  H.push(`<p style="font-size:12px;color:#64748b">Score improves automatically as compliance roles are assigned and qualifications logged.</p></div>`);

  // Statutory gaps
  if (statutory.length > 0) {
    H.push(`<div class="sec"><div class="sec-h" style="color:#dc2626">Statutory Gaps — Must Address</div>`);
    H.push(`<p style="font-size:12px;color:#64748b;margin-bottom:10px">Legal obligations not currently met. Each carries direct inspection risk.</p>`);
    statutory.forEach(g => {
      H.push(`<div class="gap" style="border-left:3px solid #dc2626">`);
      H.push(`<div class="gap-lbl">${g.label}</div>`);
      H.push(`<div class="gap-bas" style="color:#dc2626">Required under ${g.basis}</div>`);
      H.push(`<div class="gap-act">${g.action}</div></div>`);
    });
    H.push(`</div>`);
  }

  // CQC evidence gaps
  if (cqcEvidence.length > 0) {
    H.push(`<div class="sec"><div class="sec-h" style="color:#d97706">CQC Evidence Gaps — Address Before Inspection</div>`);
    H.push(`<p style="font-size:12px;color:#64748b;margin-bottom:10px">Not illegal, but CQC inspectors will request evidence. Omni generates this evidence once gaps are resolved.</p>`);
    cqcEvidence.forEach(g => {
      H.push(`<div class="gap" style="border-left:3px solid #d97706">`);
      H.push(`<div class="gap-lbl">${g.label}</div>`);
      H.push(`<div class="gap-bas" style="color:#d97706">CQC expects evidence under ${g.basis}</div>`);
      H.push(`<div class="gap-act">${g.action}</div></div>`);
    });
    H.push(`</div>`);
  }

  // Quick wins
  H.push(`<div class="sec"><div class="sec-h" style="color:#059669">Quick Wins</div>`);
  quickWins.forEach(w => { H.push(`<div class="win">${w}</div>`); });
  H.push(`</div>`);

  // Footer
  H.push(`<div class="ftr">`);
  H.push(`<span>Generated by Omni &nbsp;·&nbsp; Prima Luce Ltd &nbsp;·&nbsp; ${date}</span>`);
  H.push(`<span>${siteNames}</span></div>`);
  H.push(`</body></html>`);

  const w = window.open("", "_blank", "width=880,height=720,scrollbars=yes");
  if (!w) { alert("Allow pop-ups in your browser to download the PDF."); return; }
  w.document.write(H.join("\n"));
  w.document.close();
}

/* ── Phase 0 Welcome Screen ─────────────────────────────────────────────────── */
function WelcomePhase0({ onBegin, onDefer, incomingName }) {
  const W = {
    bg:"#100f1a", teal:"#3aad98",
    text:"rgba(255,255,255,0.88)", dim:"rgba(255,255,255,0.38)",
  };
  // ── Iris-eye opening ident ─────────────────────────────────────────────
  // Plays once per browser session. Tap to skip. Falls through gracefully
  // if the asset is missing or the browser blocks autoplay.
  const [showEye, setShowEye] = useState(() => {
    try { return !sessionStorage.getItem("iris_eye_shown"); }
    catch(e) { return true; }
  });
  React.useEffect(() => {
    if (!showEye) return;
    // Eye fully opens around 5s in source. Trim ident to 4s + 0.6s fade so
    // welcome content arrives quicker. Tap to skip remains for impatient users.
    const t = setTimeout(() => {
      setShowEye(false);
      try { sessionStorage.setItem("iris_eye_shown", "1"); } catch(e) {}
    }, 4600);
    return () => clearTimeout(t);
  }, [showEye]);
  function skipEye() {
    setShowEye(false);
    try { sessionStorage.setItem("iris_eye_shown", "1"); } catch(e) {}
  }
  const DOTS = [
    {x:8,y:15,s:2,d:7,o:0.14,dl:0},{x:25,y:72,s:1.5,d:9,o:0.09,dl:1.2},
    {x:42,y:38,s:2.5,d:6,o:0.17,dl:0.5},{x:67,y:85,s:1.5,d:11,o:0.11,dl:2},
    {x:83,y:22,s:2,d:8,o:0.13,dl:0.8},{x:92,y:68,s:1,d:10,o:0.09,dl:1.5},
    {x:15,y:55,s:1.5,d:12,o:0.08,dl:3},{x:58,y:12,s:2,d:7,o:0.14,dl:1.8},
    {x:76,y:48,s:1,d:9,o:0.09,dl:0.3},{x:33,y:92,s:2.5,d:8,o:0.11,dl:2.5},
    {x:5,y:88,s:1.5,d:11,o:0.08,dl:0.7},{x:88,y:35,s:2,d:6,o:0.15,dl:1.1},
    {x:51,y:60,s:1,d:13,o:0.08,dl:2.8},{x:19,y:33,s:2.5,d:9,o:0.12,dl:0.4},
    {x:72,y:78,s:1.5,d:10,o:0.1,dl:1.9},{x:38,y:6,s:2,d:7,o:0.13,dl:3.2},
    {x:61,y:94,s:1,d:12,o:0.08,dl:0.9},{x:95,y:52,s:2.5,d:8,o:0.14,dl:2.1},
    {x:3,y:48,s:1.5,d:11,o:0.09,dl:1.6},{x:47,y:78,s:2,d:9,o:0.11,dl:0.2},
  ];
  const greeting = incomingName ? "Hi " + incomingName + " —" : null;
  return (
    <div style={{
      minHeight:"100vh",background:W.bg,position:"relative",
      overflow:"hidden",display:"flex",flexDirection:"column",
      alignItems:"center",justifyContent:"center",
      fontFamily:"'Instrument Sans',system-ui,sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Instrument+Sans:wght@400;500;600;700&display=swap');
        @keyframes w0Float{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}}
        @keyframes w0RotA{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @keyframes w0RotB{from{transform:rotate(0deg)}to{transform:rotate(-360deg)}}
        @keyframes w0Pulse{0%,100%{opacity:0.65;transform:scale(1)}50%{opacity:1;transform:scale(1.1)}}
        @keyframes w0FadeUp{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}
        @keyframes w0EyeIn{from{opacity:0}to{opacity:1}}
        @keyframes w0EyeOut{from{opacity:1}to{opacity:0}}
        @keyframes w0SkipHint{0%,100%{opacity:0.25}50%{opacity:0.45}}
        .w0-begin:hover{background:#2d9882!important;transform:translateY(-1px)!important}
        .w0-begin:active{transform:translateY(0)!important}
        .w0-defer:hover{color:rgba(255,255,255,0.55)!important}
      `}</style>

      {/* Iris-eye opening ident — full-screen, fades to welcome content */}
      {showEye && (
        <div onClick={skipEye} style={{
          position:"fixed",inset:0,zIndex:9999,background:"#000",
          display:"flex",alignItems:"center",justifyContent:"center",
          cursor:"pointer",
          animation:"w0EyeOut 0.6s ease 4s forwards",
        }}>
          <video
            src="/iris-eye.mp4"
            autoPlay
            muted
            playsInline
            onError={skipEye}
            style={{
              width:"100%",height:"100%",objectFit:"cover",
              opacity:0,
              animation:"w0EyeIn 0.5s ease forwards",
            }}
          />
          <div style={{
            position:"absolute",bottom:32,left:0,right:0,
            textAlign:"center",color:"rgba(255,255,255,0.35)",
            fontSize:11,letterSpacing:"0.12em",textTransform:"uppercase",
            fontFamily:"'Instrument Sans',system-ui,sans-serif",
            fontWeight:500,pointerEvents:"none",
            animation:"w0SkipHint 2s ease-in-out 2s infinite",
          }}>tap to skip</div>
        </div>
      )}

      {/* Particle field */}
      {DOTS.map((p,i) => (
        <div key={i} style={{
          position:"absolute",left:p.x+"%",top:p.y+"%",
          width:p.s+"px",height:p.s+"px",borderRadius:"50%",
          background:"rgba(58,173,152,"+p.o+")",
          animation:"w0Float "+p.d+"s ease-in-out "+p.dl+"s infinite",
          pointerEvents:"none",
        }}/>
      ))}

      {/* Content panel */}
      <div style={{
        position:"relative",zIndex:1,maxWidth:480,width:"100%",
        padding:"0 28px",textAlign:"center",boxSizing:"border-box",
      }}>
        {/* Iris avatar — abstract rotating rings */}
        <div style={{
          width:80,height:80,margin:"0 auto 32px",position:"relative",
          animation:"w0Float 4s ease-in-out infinite",
        }}>
          <svg width="80" height="80" viewBox="0 0 80 80" style={{overflow:"visible"}}>
            <ellipse cx="40" cy="40" rx="37" ry="21" fill="none"
              stroke="rgba(58,173,152,0.18)" strokeWidth="1"
              style={{transformOrigin:"40px 40px",animation:"w0RotA 22s linear infinite"}}/>
            <ellipse cx="40" cy="40" rx="27" ry="15" fill="none"
              stroke="rgba(58,173,152,0.32)" strokeWidth="1.5"
              style={{transformOrigin:"40px 40px",animation:"w0RotB 14s linear infinite"}}/>
            <ellipse cx="40" cy="40" rx="17" ry="9" fill="none"
              stroke="rgba(58,173,152,0.5)" strokeWidth="1.5"
              style={{transformOrigin:"40px 40px",animation:"w0RotA 9s linear infinite"}}/>
            <circle cx="40" cy="40" r="5" fill="#3aad98"
              style={{animation:"w0Pulse 2.5s ease-in-out infinite"}}/>
          </svg>
        </div>

        {/* Headline */}
        {greeting && (
          <div style={{
            fontSize:13,color:W.teal,fontWeight:600,letterSpacing:"0.06em",
            textTransform:"uppercase",marginBottom:10,
            animation:"w0FadeUp 0.6s ease both",
          }}>
            {greeting}
          </div>
        )}
        <div style={{
          fontFamily:"'Instrument Serif',serif",fontStyle:"italic",
          fontSize:"clamp(24px,6vw,36px)",color:"rgba(255,255,255,0.95)",
          lineHeight:1.2,marginBottom:18,
          animation:"w0FadeUp 0.7s ease 0.1s both",
        }}>
          The smartest practice manager you've never had.
        </div>

        {/* Subheadline */}
        <div style={{
          fontSize:14,color:W.dim,lineHeight:1.65,marginBottom:28,
          animation:"w0FadeUp 0.7s ease 0.25s both",
        }}>
          About 20 minutes. We're not just setting up software — we're running your first
          compliance check. You'll leave with a CQC-ready gap report, your first Iris Score,
          and a system that actually knows your practice.
        </div>

        {/* Iris quote bubble */}
        <div style={{
          background:"rgba(58,173,152,0.07)",border:"1px solid rgba(58,173,152,0.14)",
          borderRadius:14,padding:"14px 16px",marginBottom:28,textAlign:"left",
          animation:"w0FadeUp 0.7s ease 0.4s both",
        }}>
          <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
            <div style={{
              width:28,height:28,borderRadius:"50%",flexShrink:0,marginTop:1,
              background:"rgba(58,173,152,0.12)",display:"flex",
              alignItems:"center",justifyContent:"center",
            }}>
              <div style={{
                width:8,height:8,borderRadius:"50%",background:W.teal,
                animation:"w0Pulse 2s ease-in-out infinite",
              }}/>
            </div>
            <div>
              <div style={{
                fontSize:10,fontWeight:700,color:W.teal,letterSpacing:"0.1em",
                textTransform:"uppercase",marginBottom:5,
              }}>Iris</div>
              <div style={{fontSize:13,color:W.text,lineHeight:1.65}}>
                Right — before we start. Everything here is your decision. I'll surface risks, spot
                gaps, and flag things worth knowing. You decide what to do about them. That's how this works.
              </div>
            </div>
          </div>
        </div>

        {/* Primary CTA */}
        <button className="w0-begin" onClick={onBegin} style={{
          width:"100%",padding:"15px",borderRadius:13,border:"none",
          background:W.teal,color:"#fff",fontSize:16,fontWeight:600,
          fontFamily:"inherit",cursor:"pointer",marginBottom:16,
          transition:"all 0.18s",letterSpacing:"0.01em",
          animation:"w0FadeUp 0.7s ease 0.5s both",
        }}>
          Let's go →
        </button>

        {/* Defer link */}
        <button className="w0-defer" onClick={onDefer} style={{
          background:"none",border:"none",cursor:"pointer",
          fontSize:13,color:W.dim,fontFamily:"inherit",
          transition:"color 0.15s",padding:"4px 8px",
          animation:"w0FadeUp 0.7s ease 0.6s both",
        }}>
          I'll do this later
        </button>
      </div>
    </div>
  );
}

function IrisOnboarding({state, set, user, onComplete, apiKey, resumeMode}) {
  const [phase, setPhase]     = useState(
    (state?.setup_phase === "basics_done" || resumeMode === "staff") ? "team" : "basics"
  );
  const [msgs, setMsgs]       = useState([]);
  const [input, setInput]     = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone]       = useState(false);
  const [gapReport, setGapReport] = useState(null);
  const chatRef               = useRef(null);
  const inputRef              = useRef(null);
  const historyRef            = useRef([]);
  const phaseRef              = useRef(phase);
  phaseRef.current = phase;
  // Phase 0 welcome — only for fresh basics start, not resumed staff setup
  const [showPhase0, setShowPhase0] = useState(phase === "basics");

  const practiceName = state?.practiceProfile?.practiceName
    || state?.practiceProfile?.name
    || "your practice";
  const sites = state?.practiceSites || [];
  const siteList = sites.map(s => s.name).join(", ") || "your site";

  // URL params from prima-luce signup flow
  const urlParams = new URLSearchParams(window.location.search);
  const incomingName     = urlParams.get("name") || "";
  const incomingRole     = urlParams.get("role") || "";
  const incomingPractice = urlParams.get("practice") || "";
  const isNew            = urlParams.get("new") === "1";

  const BASICS_SYSTEM = `You are Iris -- the AI intelligence layer built into Omni, a dental workforce platform for UK dental practices.
Personality: warm, direct, a little dry. Short punchy messages. Never say "Great!" or "Certainly!" No corporate filler. Sound like the smartest practice manager they have never had.

ROLE RULES:
- PRINCIPAL (practice owner): full setup. They own the system. All settings are theirs to configure.
- PRACTICE MANAGER: manager access only. Be warm but clear -- their principal holds owner-level settings like payroll and compliance config. Never make them feel second-class, just be honest.

YOUR JOB: collect the basics to get this practice live. One topic at a time.

Collect in this order:
1. Confirm their role (principal or practice manager).
2. Practice name.
3. How many sites? If more than one, get names of each.
4. Surgeries at each site (number).
5. Working hours -- ask as one natural question covering:
   - What time do staff arrive vs when clinic starts? (e.g. nurses arrive 07:45, clinic opens 08:00)
   - What time does clinic finish vs when staff are contracted until?
   - Are staff paid from arrival time or clinic start? This matters for payroll.
   Give the examples to help them answer quickly.

Once you have all of that, confirm back a clear summary and ask them to confirm.

When confirmed, output EXACTLY this JSON block on its own line and nothing else after it:
IRIS_BASICS_COMPLETE:{"practiceName":"...","role":"principal|manager","sites":[{"id":"site-slug","name":"Site Name","surgeries":4}],"setupBy":"...","staffArrival":"HH:MM","clinicStart":"HH:MM","clinicEnd":"HH:MM","staffFinish":"HH:MM","paidFrom":"arrival|clinicStart"}

Rules: lowercase hyphenated slugs for site IDs. 24hr time. Keep it warm and fast -- 3 minutes, not a form.
If they arrive with name and role already, greet by first name and go straight to practice name.`;

  const isMultiSite = sites.length > 1;

  // Schema example Iris must echo verbatim after IRIS_ALL_COMPLETE: — kept as concatenated segments so no source line exceeds the 688-char Vercel limit.
  const IRIS_JSON_TEMPLATE = '{"clinicians":[{"name":"","site":"","type":"GDS","sched":{"Mon":"08:00-17:30","Thu":"08:00-13:00|14:00-17:30"},"freq":{"Thu":"always","Fri":"weekA"},"sedation":"none","isPartner":false}]'
    + ',"nurses":[{"name":"","site":"","status":"qualified","ra":false,"iv":false,"headNurse":false,"keyholder":false,"firstAider":false,"safeguardingL3":false,"fireMarshal":false,"hrs":37.5,"workDays":["Mon","Tue","Wed","Thu","Fri"],"maternity":false}]'
    + ',"reception":[{"name":"","site":"","hrs":37.5}],"salariedStaff":[{"name":"","annual":0,"attribution":{}}],"sicknessContacts":[{"site":"","name":""}]'
    + ',"policy":{"toilEnabled":false,"toilRate":1,"toilExpiry":"yearEnd","leaveYear":"jan-dec","breakPolicy":{"paid":false,"durationMins":30,"thresholdHrs":6,"defaultTime":"13:00"},"setupMins":0,"tidyMins":0,"weekAStart":""}}';

  const STAFF_SYSTEM = `You are Iris -- the AI intelligence layer in Omni, a dental workforce platform for UK dental practices.
Personality: warm, direct, dry humour. Short punchy messages. No corporate filler.

Practice: ${practiceName}. ${isMultiSite ? "Sites: " + siteList + "." : "Single site: " + siteList + "."}
Your job: set up the team accurately. Wrong data means wrong scores, wrong cover, wrong everything.

Opening: "Right -- let's get your team set up properly. About 15 minutes. Worth doing right."

STRICT RULES:
- One question at a time. Never ask two things at once.
- TIMES: Ask "What time do they start and finish?" Accept any format they give ("8-5:30", "9am-3pm" etc) and convert to 24hr yourself. Do NOT offer options like AM, PM, full day, long day. Just ask for the times.
- Do NOT ask about start dates, leaving dates, or when someone joined.
- Do NOT mention custom shifts or shift patterns.
${isMultiSite ? "" : "- Single site practice -- do NOT ask which site anyone works at."}

Collect in this order:

1. CLINICIANS -- for each clinician:
   Name, type (GDS / MOS / Therapist / FD / Hygienist), partner or associate?
   Which days do they work? What time do they start and finish?
   (Only ask per-day times if days genuinely vary -- otherwise one set of times is fine)
   ${isMultiSite ? "Multi-site: which days at which site?" : ""}
   "Anyone else?" until they say no.

2. SEDATION -- does anyone do sedation? For each: IV, RA, or both?

3. NURSES -- for each nurse:
   Name. Competency: qualified / near-qualifying / trainee / new starter.
   RA qualified? IV qualified?
   Contract hours per week (ask explicitly -- 37.5, 30, 19.5, 12.5 etc).
   Which days do they work? On maternity leave?
   "Anyone else?" until they say no.
   Then compliance roles:
   "Who is head nurse?" "Who holds keys?"
   "Who is first aider?" (H&S First-Aid Regs 1981)
   "Who holds Level 3 Safeguarding?" (CQC Reg 13)
   "Who is fire marshal?" (Fire Safety Order 2005)
   After nurses, flag each gap in one line:
   No L3: "No Level 3 safeguarding -- CQC Reg 13 requires at least one."
   No marshal: "No fire marshal -- Fire Safety Order 2005."
   No first aider: "No first aider -- H&S First-Aid Regs 1981."

4. RECEPTION -- names, hours. One receptionist = flag single point of failure.

5. POLICY -- "Almost done -- a few quick ones."
   TOIL: "Overtime -- time off in lieu or straight pay?" If yes: "1 hour back per hour, or 1.5?" Then: "Expire at year end or after 6 months?"
   CARERS: "Since April 2024, all staff have 5 days statutory carer's leave per year. Omni tracks it -- nothing to decide."
   LEAVE YEAR: "Jan-Dec or Apr-Mar?"
   PAY TYPE: "Are any of your staff on an annual salary rather than hourly pay? For example a practice manager or TCO?" If yes, for each: name and annual salary. Do they work across more than one site? If so, rough split (e.g. 3 days here, 2 days there).
   BREAKS: "Are rest breaks paid or unpaid? Most dental practices have an unpaid 30-minute break for shifts over 6 hours -- that is the legal minimum under Working Time Regulations. Is that right for you, or different?" Collect: paid/unpaid, duration in mins (default 30), applies to shifts over X hours (default 6). Also ask: "Do any staff get a paid lunch hour rather than an unpaid break? And do nurses get paid for surgery setup and tidy time before and after sessions? If so, how many minutes?" Collect: setupMins (before shift), tidyMins (after shift).
   TWO-WEEK ROTA: "Do any of your clinicians work on a two-week alternating pattern -- for example, every other Friday?" If yes: "Which week is their 'Week A'? Tell me a date of a Monday that is one of their working weeks and I will use that as the reference." Collect: weekAStart (YYYY-MM-DD, must be a Monday).
   SICKNESS CONTACT: "Last one -- who handles staff absences when someone calls in sick? For each site, give me the name of the main person to contact. Head nurses and the principal can always log absences, but who is the go-to person?" Collect: one named staff member per site as the primary sickness contact. Match to a staff member already collected -- use their name exactly as given.

6. CONFIRM -- brief summary of everyone. Ask them to correct anything. Then save.

When confirmed, output EXACTLY this JSON as the very last thing -- nothing after it:
IRIS_ALL_COMPLETE:${IRIS_JSON_TEMPLATE}

Site slugs: ${sites.map(s => s.id + "=" + s.name).join(", ")}.
hrs = contracted weekly hours. workDays = actual days only. 24hr time in sched.`;

  // On mount: start the right phase
  useEffect(() => {
    const ctx = phase === "team"
      ? "start staff setup -- practice basics already set up, move straight to team"
      : (isNew && incomingName
          ? ("start -- name=" + incomingName + ", role=" + incomingRole + ", practice=" + incomingPractice)
          : null);
    callIris(ctx);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [msgs]);

  async function callIris(userMsg) {
    if (userMsg) {
      const entry = {role: "user", content: userMsg};
      historyRef.current = [...historyRef.current, entry];
      setMsgs(m => [...m, {role: "user", text: userMsg}]);
    }
    setLoading(true);
    try {
      const currentPhase = phaseRef.current;
      const systemPrompt = currentPhase === "team" ? STAFF_SYSTEM : BASICS_SYSTEM;
      const messages = historyRef.current.length
        ? historyRef.current
        : [{role: "user", content: "start"}];

      const res = await fetch("/api/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": apiKey || getStoredApiKey() || "",
        },
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 2000,
          system: systemPrompt,
          messages,
        }),
      });
      const data = await res.json();
      const reply = data?.content?.find(b => b.type === "text")?.text
        || "Something went wrong on my end. Try again?";

      historyRef.current = [...historyRef.current, {role: "assistant", content: reply}];

      // Phase 1 complete -- use indexOf to avoid nested-brace regex failures
      const basicsIdx = reply.indexOf("IRIS_BASICS_COMPLETE:");
      if (basicsIdx !== -1 && currentPhase === "basics") {
        await handleBasicsComplete(reply.slice(basicsIdx + "IRIS_BASICS_COMPLETE:".length).trim(), reply);
        return;
      }
      // Phase 2 complete
      const allIdx = reply.indexOf("IRIS_ALL_COMPLETE:");
      if (allIdx !== -1 && currentPhase === "team") {
        await handleAllComplete(reply.slice(allIdx + "IRIS_ALL_COMPLETE:".length).trim(), reply);
        return;
      }

      setMsgs(m => [...m, {role: "iris", text: reply}]);
    } catch(e) {
      setMsgs(m => [...m, {role: "iris", text: "Something went wrong on my end. Try again?"}]);
    }
    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  }

  async function handleBasicsComplete(jsonStr, fullReply) {
    try {
      const profile = JSON.parse(jsonStr);
      const cleanReply = fullReply.replace(/IRIS_BASICS_COMPLETE:[\s\S]*$/, "").trim();

      const newProfile = {
        ...profile,
        onboardedAt: new Date().toISOString(),
        onboardedBy: user?.name || "Unknown",
      };
      set(s => ({
        ...s,
        setup_phase: "basics_done",
        practiceProfile: newProfile,
        practiceSites: profile.sites || [],
      }));
      sbSet("setup_phase", "basics_done");
      sbSet("practiceProfile", newProfile);
      sbSet("practiceSites", profile.sites || []);

      // Write practice name via service key proxy (fixes Flourish/test name issue)
      try {
        await fetch("/api/db", {
          method: "POST",
          headers: {"Content-Type": "application/json"},
          body: JSON.stringify({
            _table: "practices",
            slug: PRACTICE_ID,
            name: profile.practiceName || PRACTICE_ID,
            primary_color: "#3aad98",
            active: true,
          }),
        });
        PRACTICE_CONFIG.name = profile.practiceName || PRACTICE_ID;
      } catch(e2) {
        console.warn("Could not write practice name to table", e2);
      }

      if (cleanReply) {
        setMsgs(m => [...m, {role: "iris", text: cleanReply}]);
      }
      // Phase transition — move to table-based team setup
      historyRef.current = [];
      setPhase("team");
      phaseRef.current = "team";
      setLoading(false);

    } catch(e) {
      console.warn("Basics parse error", e);
      setMsgs(m => [...m, {role: "iris", text: fullReply}]);
      setLoading(false);
    }
  }

  async function handleTeamComplete(teamData) {
    try {
      const {clinicians,nurses,reception,policy,holidays} = teamData;
      const fallbackSite = (state?.practiceSites||[])[0]?.id
        || Object.keys(SITES)[0] || "site";
      const newClinicians = clinicians.map(c=>({
        id:c.id||c._id, name:c.name,
        site:c.site||fallbackSite,
        type:c.type||"GDS", isPartner:!!c.isPartner,
        // Convert sched from {Mon:{start,end}} objects to "HH:MM-HH:MM" strings
        sched: Object.fromEntries(
          Object.entries(c.sched||{}).map(([day,v])=>[
            day,
            typeof v==="string" ? v
              : (v&&v.start&&v.end) ? v.start+"-"+v.end
              : v&&v.start ? v.start : "",
          ]).filter(([,v])=>v)
        ),
        freq:c.freq||{},
        sedation:c.sedation||"none",
        supervisorId:c.supervisorId||"",
        secondarySupId:c.secondarySupId||"",
        specialty:c.specialty||"",
      }));
      const newNurses = nurses.map(n=>({
        id:n.id||n._id, name:n.name,
        site:n.site||fallbackSite,
        status:n.status||"qualified",
        hrs:n.hrs||37.5,
        workDays:n.workDays||[...WD],
        ra:!!n.ra, iv:!!n.iv,
        headNurse:!!n.headNurse,
        keyholder:!!n.keyholder,
        firstAider:!!n.firstAider,
        safeguardingL3:!!n.safeguardingL3,
        fireMarshal:!!n.fireMarshal,
        maternity:!!n.maternity,
        flexible:(n.hrs||37.5)<37, area:"nursing",
      }));
      const newRecs = reception.map(r=>({
        id:r.id||r._id, name:r.name,
        site:r.site||fallbackSite,
        hrs:r.hrs||37.5,
        type:r.type||"reception",
        workDays:r.workDays||[...WD],
      }));
      const clinReqs = {};
      newClinicians.forEach(c=>{
        if(c.sedation&&c.sedation!=="none") {
          clinReqs[c.id]={
            sedation:c.sedation,
            supervisorId:c.supervisorId||"",
            secondarySupId:c.secondarySupId||"",
          };
        }
      });
      const toilPolicyUpdate = {
        enabled:!!policy.toilEnabled,
        rate:policy.toilRate||1,
        expiry:policy.toilExpiry||"yearEnd",
      };
      const leaveYearUpdate = {
        type:policy.leaveYear||"jan-dec",
        startMonth:policy.leaveYear==="apr-mar"?4:1,
      };
      const breakPolicyUpdate = {
        paid:!!policy.breakPaid,
        durationMins:policy.breakMins||30,
        thresholdHrs:policy.breakThresholdHrs||6,
      };
      // Per-person leave year overrides
      const leaveYearOverrides = {};
      Object.entries(holidays||{}).forEach(([pid,hol])=>{
        if (hol.leaveYear && hol.leaveYear!=="default") {
          leaveYearOverrides[pid] = hol.leaveYear;
        }
      });
      // Build individual entitlement overrides from holiday table
      const individualEntitlements = {};
      const historicalLeave = {};
      Object.entries(holidays||{}).forEach(([pid,hol])=>{
        if (hol.baseOverride!=null && hol.baseOverride!=="") {
          individualEntitlements[pid] = {
            days: parseFloat(hol.baseOverride)||28,
          };
        }
        if (hol.taken && parseFloat(hol.taken)>0) {
          historicalLeave[pid] = {
            days: parseFloat(hol.taken)||0,
            note: "Entered during onboarding",
            loggedBy: "principal",
            loggedAt: new Date().toISOString(),
          };
        }
      });
      // Apply startDate directly onto person records so getEntitlement can pro-rate
      const applyStartDates = (arr) => arr.map(p=>{
        const hol = (holidays||{})[p._id||p.id]||{};
        return hol.startDate ? {...p, startDate:hol.startDate} : p;
      });
      set(s=>({
        ...s,
        setup_complete:true, setup_phase:"complete",
        customClinicians:[...(s.customClinicians||[]),...applyStartDates(newClinicians)],
        nurses:[...(s.nurses||[]),...applyStartDates(newNurses)],
        ...(newRecs.length>0
          ?{customRecs:[...(s.customRecs||[]),...applyStartDates(newRecs)]}:{}),
        clinicianRequirements:{...(s.clinicianRequirements||{}),...clinReqs},
        toilPolicy:toilPolicyUpdate,
        leaveYearConfig:{...(s.leaveYearConfig||{}),...leaveYearUpdate},
        ...(Object.keys(leaveYearOverrides).length>0?{
          leaveYearOverrides:{
            ...(s.leaveYearOverrides||{}),...leaveYearOverrides,
          },
        }:{}),
        individualEntitlements:{
          ...(s.individualEntitlements||{}),...individualEntitlements,
        },
        historicalLeave:{
          ...(s.historicalLeave||{}),...historicalLeave,
        },
        settings:{
          ...(s.settings||{}),
          breakPolicy:breakPolicyUpdate,
          setupMins:policy.setupMins||0,
          tidyMins:policy.tidyMins||0,
          bhIncluded:policy.bhIncluded!==false,
          carryOver:!!policy.carryOver,
          carryOverMax:policy.carryOverMax||0,
          lunchPolicy:{
            standard:!!policy.lunchStandard,
            time:policy.lunchTime||"13:00-14:00",
          },
          ...(policy.weekAStart?{weekAStart:policy.weekAStart}:{}),
        },
      }));
      sbSet("setup_complete",true);
      sbSet("setup_phase","complete");
      sbSet("nurses",[...(state?.nurses||[]),...applyStartDates(newNurses)]);
      sbSet("customClinicians",[...(state?.customClinicians||[]),...applyStartDates(newClinicians)]);
      if(newRecs.length>0) {
        sbSet("customRecs",[...(state?.customRecs||[]),...applyStartDates(newRecs)]);
      }
      sbSet("toilPolicy",toilPolicyUpdate);
      sbSet("clinicianRequirements",{
        ...(state?.clinicianRequirements||{}),...clinReqs,
      });
      if(Object.keys(individualEntitlements).length>0) {
        sbSet("individualEntitlements",{
          ...(state?.individualEntitlements||{}),
          ...individualEntitlements,
        });
      }
      if(Object.keys(historicalLeave).length>0) {
        sbSet("historicalLeave",{
          ...(state?.historicalLeave||{}),...historicalLeave,
        });
      }
      const gaps = buildGapReport({
        nurses:newNurses, clinicians:newClinicians, reception:newRecs,
      });
      setGapReport(gaps);
      setLoading(false);
    } catch(e) {
      console.warn("Team complete error",e);
      setLoading(false);
    }
  }

  async function handleAllComplete(jsonStr, fullReply) {
    try {
      const rotaData = JSON.parse(jsonStr);
      const cleanReply = fullReply.replace(/IRIS_ALL_COMPLETE:[\s\S]*$/, "").trim();

      // Fallback site: use first known site if Iris output empty/missing site
      const fallbackSite = (state?.practiceSites || [])[0]?.id
        || Object.keys(SITES)[0]
        || "site";

      // ── VALIDATION GATE ────────────────────────────────────────────────────
      // Refuse to complete onboarding if nurses are missing — every UK dental
      // practice has nurses, and a silent empty-array completion left us with
      // a broken test tenant. Push Iris back into conversation instead.
      const _nursesIn = Array.isArray(rotaData.nurses) ? rotaData.nurses : [];
      const _validNurses = _nursesIn.filter(n => n && n.name && String(n.name).trim());
      if (_validNurses.length === 0) {
        setMsgs(m => [...m, {role:"iris", text:"I have your clinicians and reception, but I haven't captured your nursing team yet. Tell me about your nurses — names, sites, contracted hours, and which qualifications they hold (RA, IV, fire marshal, first aider, Level 3 safeguarding). I'll add them and then run the compliance check."}]);
        setLoading(false);
        return;
      }

      // ── NORMALISERS ────────────────────────────────────────────────────────
      // Iris LLM occasionally emits sched as {Mon:{start,end}} objects instead
      // of "HH:MM-HH:MM" strings. Normalise on receive so buildDayRota always
      // gets strings. Mirrors the conversion in handleTeamComplete.
      const normSched = (sched) => Object.fromEntries(
        Object.entries(sched||{}).map(([day,v])=>[
          day,
          typeof v === "string" ? v
            : (v && v.start && v.end) ? v.start + "-" + v.end
            : v && v.start ? v.start : "",
        ]).filter(([,v])=>v)
      );
      // Reception type: normalise casing so display logic finds the right label
      const normRecType = (t) => {
        const v = (t||"").toString().toLowerCase().trim();
        if (v === "tco") return "TCO";
        if (v === "manager" || v === "managerlite" || v === "manager lite") return "Manager";
        return "Reception";
      };

      // Build clinicians
      const newClinicians = (rotaData.clinicians || []).map((c, i) => ({
        id: "c_" + Date.now() + "_" + i,
        name: c.name,
        site: c.site || fallbackSite,
        type: c.type || "GDS",
        color: ["#818cf8","#a78bfa","#3aad98","#f472b6","#f5c518","#60a5fa"][i % 6],
        sched: normSched(c.sched),
        freq: c.freq || {},
        prefNurse: null,
        isPartner: !!c.isPartner,
      }));

      // Build sedation requirements
      const clinReqs = {};
      (rotaData.clinicians || []).forEach((c, i) => {
        if (c.sedation && c.sedation !== "none") {
          const id = newClinicians[i]?.id;
          if (id) {
            clinReqs[id] = {
              default: c.sedation === "both" ? ["ra","iv"] : [c.sedation],
              overrides: {},
              note: c.sedation === "both"
                ? "IV + RA sedation"
                : c.sedation.toUpperCase() + " sedation",
            };
          }
        }
      });

      // Build nurses
      const newNurses = (rotaData.nurses || []).map((n, i) => ({
        id: "n_" + Date.now() + "_" + i,
        name: n.name,
        site: n.site || fallbackSite,
        status: n.status || "qualified",
        ra: !!n.ra,
        iv: !!n.iv,
        keyholder: !!n.keyholder,
        firstAider: !!n.firstAider,
        headNurse: !!n.headNurse,
        safeguardingL3: !!n.safeguardingL3,
        fireMarshal: !!n.fireMarshal,
        hrs: n.hrs || 37.5,
        workDays: n.workDays || ["Mon","Tue","Wed","Thu","Fri"],
        noFri: n.workDays ? !n.workDays.includes("Fri") : false,
        area: "GDS",
        archived: false,
        maternity: !!n.maternity,
      }));

      // Build reception
      const newRecs = (rotaData.reception || []).map((r, i) => ({
        id: "r_setup_" + Date.now() + "_" + i,
        name: r.name,
        site: r.site || fallbackSite,
        hrs: r.hrs || 37.5,
        type: normRecType(r.type),
        area: "Reception",
        recStatus: "active",
        workDays: (Array.isArray(r.workDays) && r.workDays.length > 0)
          ? r.workDays
          : ["Mon","Tue","Wed","Thu","Fri"],
      }));

      // Policy
      const pol = rotaData.policy || {};
      const toilPolicyUpdate = {
        enabled: !!pol.toilEnabled,
        rate: pol.toilRate || 1,
        expiry: pol.toilExpiry || "yearEnd",
        alertThresholdHrs: 7.5,
        maxBalanceHrs: 37.5,
        payoutOnLeaving: true,
        approvalRequired: true,
        onboarded: true,
      };
      const leaveYearUpdate = pol.leaveYear === "apr-mar"
        ? {nurses:"apr-mar", reception:"apr-mar", dentists:"apr-mar"}
        : {nurses:"jan-dec", reception:"jan-dec", dentists:"apr-mar"};
      const breakPolicyUpdate = pol.breakPolicy || {paid:false, durationMins:30, thresholdHrs:6, defaultTime:"13:00"};
      const setupMinsUpdate = pol.setupMins || 0;
      const tidyMinsUpdate  = pol.tidyMins  || 0;
      const weekAStartUpdate = pol.weekAStart || "";

      // Sickness contacts — match by name to newly created staff
      const sicknessContactMap = {};
      (rotaData.sicknessContacts || []).forEach(sc => {
        if (!sc.site || !sc.name) return;
        const match = [...newNurses, ...newRecs].find(p =>
          p.name?.toLowerCase().trim() === sc.name.toLowerCase().trim()
        );
        if (match) sicknessContactMap[sc.site] = match.id;
      });

      // Salaried staff — match by name against newly created staff records
      const salariedWages = {};
      (rotaData.salariedStaff || []).forEach(s => {
        if (!s.annual) return;
        // Try to match by id first, then by name against new nurses/recs
        let staffId = s.id;
        if (!staffId || staffId === "") {
          const nameMatch = [...newNurses, ...newRecs].find(p =>
            p.name?.toLowerCase().trim() === (s.name||"").toLowerCase().trim()
          );
          staffId = nameMatch?.id;
        }
        if (staffId) {
          salariedWages[staffId] = {type:"salaried", annual:s.annual, rate:null, attribution:s.attribution||{}};
        }
      });

      // Save everything to state + Supabase
      set(s => ({
        ...s,
        setup_complete: true,
        setup_phase: "complete",
        customClinicians: [...(s.customClinicians || []), ...newClinicians],
        nurses: [...(s.nurses || []), ...newNurses],
        ...(newRecs.length > 0
          ? {customRecs: [...(s.customRecs || []), ...newRecs]}
          : {}),
        clinicianRequirements: {...(s.clinicianRequirements || {}), ...clinReqs},
        toilPolicy: toilPolicyUpdate,
        leaveYearConfig: {...(s.leaveYearConfig || {}), ...leaveYearUpdate},
        settings: {...(s.settings||{}), breakPolicy: breakPolicyUpdate, setupMins: setupMinsUpdate, tidyMins: tidyMinsUpdate, ...(weekAStartUpdate?{weekAStart:weekAStartUpdate}:{}), ...(Object.keys(sicknessContactMap).length>0?{sicknessContact:{...(s.settings?.sicknessContact||{}),...sicknessContactMap}}:{})},
        wages: {...(s.wages||{}), ...salariedWages},
      }));
      sbSet("setup_complete", true);
      sbSet("setup_phase", "complete");
      sbSet("nurses", [...(state?.nurses || []), ...newNurses]);
      sbSet("customClinicians", [...(state?.customClinicians || []), ...newClinicians]);
      if (newRecs.length > 0) sbSet("customRecs", [...(state?.customRecs || []), ...newRecs]);
      sbSet("toilPolicy", toilPolicyUpdate);
      sbSet("clinicianRequirements", {...(state?.clinicianRequirements || {}), ...clinReqs});

      // Build + show gap report
      const gaps = buildGapReport(rotaData);
      setGapReport(gaps);
      if (cleanReply) setMsgs(m => [...m, {role: "iris", text: cleanReply}]);
      setLoading(false);
    } catch(e) {
      console.warn("All-complete parse error", e);
      setMsgs(m => [...m, {role: "iris", text: fullReply}]);
      setLoading(false);
    }
  }

  function buildGapReport(rotaData) {
    const statutory = [];
    const cqcEvidence = [];
    const quickWins = [];
    const allSites = state?.practiceSites || [];

    allSites.forEach(site => {
      const sNurses = (rotaData.nurses || []).filter(n => n.site === site.id);
      const sClins  = (rotaData.clinicians || []).filter(c => c.site === site.id);

      if (!sNurses.some(n => n.safeguardingL3)) {
        statutory.push({
          label:  "No Level 3 Safeguarding lead -- " + site.name,
          basis:  "CQC Reg 13",
          action: "Assign a safeguarding lead in Staff directory",
        });
      }
      if (!sNurses.some(n => n.fireMarshal)) {
        statutory.push({
          label:  "No fire marshal -- " + site.name,
          basis:  "Fire Safety Order 2005",
          action: "Assign a fire marshal in Staff directory",
        });
      }
      if (!sNurses.some(n => n.firstAider)) {
        statutory.push({
          label:  "No first aider -- " + site.name,
          basis:  "H&S First-Aid Regulations 1981",
          action: "Assign a first aider in Staff directory",
        });
      }
      if (!sNurses.some(n => n.headNurse)) {
        cqcEvidence.push({
          label:  "No head nurse designated -- " + site.name,
          basis:  "CQC Reg 12 (Safe care and treatment)",
          action: "Designate head nurse in Staff directory",
        });
      }
      // Sedation cover gaps
      sClins.filter(c => c.sedation && c.sedation !== "none").forEach(c => {
        const needRA = c.sedation === "ra" || c.sedation === "both";
        const needIV = c.sedation === "iv" || c.sedation === "both";
        if (needRA && !sNurses.some(n => n.ra)) {
          cqcEvidence.push({
            label:  c.name + " runs RA sedation -- no RA nurse at " + site.name,
            basis:  "CQC Reg 12 + SDCEP sedation guidelines",
            action: "Log RA qualification on a nurse, or book RA training",
          });
        }
        if (needIV && !sNurses.some(n => n.iv)) {
          cqcEvidence.push({
            label:  c.name + " runs IV sedation -- no IV nurse at " + site.name,
            basis:  "CQC Reg 12 + SDCEP sedation guidelines",
            action: "Log IV qualification on a nurse, or book IV training",
          });
        }
      });
    });

    // Quick wins
    if (statutory.length > 0) {
      quickWins.push("Open Staff directory and assign compliance roles -- takes 2 minutes per site.");
    }
    const nearQual = (rotaData.nurses || []).filter(n =>
      n.status === "near-qualifying" && !n.ra
    );
    if (nearQual.length > 0) {
      quickWins.push(
        nearQual[0].name + " is near-qualifying -- an RA course would give you sedation cover."
      );
    }
    const recSites = {};
    (rotaData.reception || []).forEach(r => {
      recSites[r.site] = (recSites[r.site] || 0) + 1;
    });
    Object.entries(recSites).forEach(([sid, count]) => {
      if (count === 1) {
        const siteName = (allSites.find(s => s.id === sid) || {}).name || sid;
        quickWins.push(siteName + " has one receptionist -- single point of failure. Note in risk log.");
      }
    });
    if (quickWins.length === 0) {
      quickWins.push("Compliance setup looks solid. Ask Iris about anything at any time.");
    }

    // Holiday risk flags — only if holiday data was entered
    const holData = rotaData.holidays || {};
    const holKeys = Object.keys(holData);
    if (holKeys.length > 0) {
      const now = new Date();
      const monthsIn = now.getMonth() + 1; // 1-12
      holKeys.forEach(pid => {
        const hol = holData[pid];
        const taken = parseFloat(hol.taken) || 0;
        const base = parseFloat(hol.baseOverride) ||
          (rotaData.nurses?.find(n=>(n._id||n.id)===pid)?.isPartner ? 42 : 28);
        const hrs = rotaData.nurses?.find(n=>(n._id||n.id)===pid)?.hrs || 37.5;
        const entit = Math.round((hrs/37.5)*base*10)/10;
        const remaining = entit - taken;
        const name = [
          ...(rotaData.nurses||[]),
          ...(rotaData.clinicians||[]),
          ...(rotaData.reception||[]),
        ].find(p=>(p._id||p.id)===pid)?.name || "A team member";
        // Under-taken risk: less than proportional amount used with <4 months left
        const expectedTaken = Math.round((monthsIn/12)*entit*10)/10;
        if (monthsIn >= 9 && taken < expectedTaken * 0.5 && remaining > 10) {
          quickWins.push(
            name + " has " + remaining + " days remaining with "
            + (12-monthsIn) + " months left -- risk of last-minute leave requests."
          );
        }
        // Over-taken
        if (remaining < 0) {
          cqcEvidence.push({
            label: name + " has taken " + taken + " days but is entitled to "
              + entit + " -- worth checking with payroll.",
            basis: "Working Time Regulations 1998",
            action: "Review leave records and adjust payroll if needed.",
          });
        }
      });
    }

    // Estimated Iris Score
    const totalChecks = Math.max(allSites.length * 3, 1);
    const gapScore = statutory.length + cqcEvidence.length;
    const score = Math.min(100, Math.max(40,
      Math.round(100 - (gapScore / totalChecks) * 35)
    ));

    return {statutory, cqcEvidence, quickWins, score};
  }

  async function send() {
    const txt = input.trim();
    if (!txt || loading) return;
    setInput("");
    await callIris(txt);
  }

  const C_IRIS = {
    bg: "#100f1a", surface: "#181626", header: "#0c0b16",
    border: "rgba(255,255,255,0.07)", text: "rgba(255,255,255,0.82)",
    dim: "rgba(255,255,255,0.38)", teal: "#3aad98", green: "#00e5a0",
    amber: "#f5c518", red: "#ff4646", input: "#13111c",
  };

  // ── Phase team — table-based team setup ──────────────────────────────────
  if (phase === "team") {
    return (
      <ErrorBoundary>
        <TeamSetupTables
          state={state}
          set={set}
          sites={sites}
          practiceName={practiceName}
          onComplete={handleTeamComplete}
        />
      </ErrorBoundary>
    );
  }

  // ── Phase 0 welcome screen ────────────────────────────────────────────────
  if (showPhase0) {    return (
      <WelcomePhase0
        incomingName={incomingName}
        onBegin={() => setShowPhase0(false)}
        onDefer={() => {
          const until = new Date(Date.now() + 86400000).toISOString();
          localStorage.setItem("onboarding_deferred_until", until);
          window.location.reload();
        }}
      />
    );
  }

  // ── Gap report screen ──────────────────────────────────────────────────────
  if (gapReport) {
    const {statutory, cqcEvidence, quickWins, score} = gapReport;
    const scoreCol = score >= 80 ? C_IRIS.green : score >= 60 ? C_IRIS.amber : C_IRIS.red;
    return (
      <div style={{minHeight:"100vh",background:C_IRIS.bg,display:"flex",flexDirection:"column",fontFamily:"'Instrument Sans',system-ui,sans-serif"}}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Instrument+Sans:wght@400;500;600;700&display=swap');`}</style>
        <div style={{background:C_IRIS.header,borderBottom:"1px solid rgba(255,255,255,0.07)",padding:"14px 20px",display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
          <span style={{fontFamily:"'Instrument Serif',serif",fontSize:22,fontStyle:"italic",fontWeight:700,color:"rgba(255,255,255,0.95)"}}>Omni</span>
          <span style={{width:6,height:6,borderRadius:"50%",background:C_IRIS.teal,display:"inline-block",marginBottom:2,marginLeft:1}}/>
          <div style={{display:"flex",alignItems:"center",gap:6,background:"rgba(58,173,152,0.1)",border:"1px solid rgba(58,173,152,0.2)",borderRadius:20,padding:"3px 10px"}}>
            <div style={{width:6,height:6,borderRadius:"50%",background:C_IRIS.teal}}/>
            <span style={{fontSize:11,fontWeight:600,color:C_IRIS.teal,letterSpacing:"0.08em",textTransform:"uppercase"}}>Iris -- gap report</span>
          </div>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:"24px 16px",maxWidth:640,width:"100%",margin:"0 auto",boxSizing:"border-box"}}>
          {/* Iris framing */}
          <div style={{
            display:"flex",alignItems:"flex-start",gap:10,
            background:"rgba(58,173,152,0.06)",border:"1px solid rgba(58,173,152,0.13)",
            borderRadius:12,padding:"12px 14px",marginBottom:20,
          }}>
            <div style={{
              width:26,height:26,borderRadius:"50%",flexShrink:0,marginTop:1,
              background:"linear-gradient(135deg,#00d4ff,#a855f7,#f472b6)",
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:10,fontWeight:700,color:"white",letterSpacing:"0.02em",
            }}>I</div>
            <div style={{fontSize:13,color:C_IRIS.text,lineHeight:1.6}}>
              {statutory.length === 0 && cqcEvidence.length === 0
                ? "Clean bill of health. A good start — here are a few things that will improve your score further."
                : "This is what I found. None of it is a failure — most practices have some of these gaps. What matters is that you know about them now, not when CQC knocks."}
            </div>
          </div>

          {/* Score card */}
          <div style={{background:C_IRIS.surface,border:"1px solid rgba(255,255,255,0.08)",borderRadius:16,padding:"28px 24px",marginBottom:24,textAlign:"center"}}>
            <div style={{fontSize:11,color:C_IRIS.dim,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:8}}>Your starting Iris Score</div>
            <div style={{fontFamily:"'Instrument Serif',serif",fontStyle:"italic",fontSize:80,color:scoreCol,lineHeight:1,marginBottom:8}}>{score}</div>
            <div style={{fontSize:13,color:C_IRIS.dim,maxWidth:340,margin:"0 auto"}}>
              {score >= 80
                ? "Good foundation. A few things to tidy up."
                : score >= 60
                  ? "Some gaps worth addressing before your next inspection."
                  : "Meaningful gaps here -- worth addressing soon. Iris will guide you through each one."}
            </div>
          </div>

          {statutory.length > 0 && (
            <div style={{marginBottom:20}}>
              <div style={{fontSize:11,fontWeight:700,color:C_IRIS.red,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:10}}>Statutory gaps -- must address</div>
              {statutory.map((g, i) => (
                <div key={i} style={{background:"rgba(255,70,70,0.05)",border:"1px solid rgba(255,70,70,0.15)",borderRadius:10,padding:"12px 14px",marginBottom:8}}>
                  <div style={{fontSize:13,color:C_IRIS.text,fontWeight:500,marginBottom:3}}>{g.label}</div>
                  <div style={{fontSize:11,color:C_IRIS.red,marginBottom:4}}>Required under {g.basis}</div>
                  <div style={{fontSize:11,color:C_IRIS.dim}}>{g.action}</div>
                </div>
              ))}
            </div>
          )}

          {cqcEvidence.length > 0 && (
            <div style={{marginBottom:20}}>
              <div style={{fontSize:11,fontWeight:700,color:C_IRIS.amber,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:10}}>CQC evidence gaps -- sort before inspection</div>
              {cqcEvidence.map((g, i) => (
                <div key={i} style={{background:"rgba(245,197,24,0.05)",border:"1px solid rgba(245,197,24,0.15)",borderRadius:10,padding:"12px 14px",marginBottom:8}}>
                  <div style={{fontSize:13,color:C_IRIS.text,fontWeight:500,marginBottom:3}}>{g.label}</div>
                  <div style={{fontSize:11,color:C_IRIS.amber,marginBottom:4}}>CQC expects evidence under {g.basis}</div>
                  <div style={{fontSize:11,color:C_IRIS.dim}}>{g.action}</div>
                </div>
              ))}
            </div>
          )}

          <div style={{marginBottom:28}}>
            <div style={{fontSize:11,fontWeight:700,color:C_IRIS.teal,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:10}}>Quick wins</div>
            {quickWins.map((w, i) => (
              <div key={i} style={{background:"rgba(58,173,152,0.06)",border:"1px solid rgba(58,173,152,0.15)",borderRadius:10,padding:"12px 14px",marginBottom:8,fontSize:13,color:C_IRIS.text}}>{w}</div>
            ))}
          </div>

          <button
            onClick={() => generateGapReportPDF(gapReport, practiceName, sites)}
            style={{
              width:"100%",padding:"14px",borderRadius:12,
              border:"1px solid rgba(58,173,152,0.35)",background:"rgba(58,173,152,0.08)",
              color:C_IRIS.teal,fontSize:14,fontWeight:600,fontFamily:"inherit",
              cursor:"pointer",marginBottom:10,letterSpacing:"0.01em",
            }}>
            ↓ Download compliance report PDF
          </button>
          <button
            onClick={() => { setDone(true); setTimeout(() => onComplete && onComplete(), 1200); }}
            style={{width:"100%",padding:"14px",borderRadius:12,border:"none",background:C_IRIS.teal,color:"#fff",fontSize:15,fontWeight:600,fontFamily:"inherit",cursor:"pointer",marginBottom:12}}>
            Go to my dashboard
          </button>
          <div style={{textAlign:"center",fontSize:12,color:C_IRIS.dim}}>You can ask Iris about any of these gaps at any time.</div>
        </div>
      </div>
    );
  }

  if (done) return (
    <div style={{minHeight:"100vh",background:C_IRIS.bg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Instrument Sans',system-ui,sans-serif"}}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Instrument+Sans:wght@400;500;600;700&display=swap');`}</style>
      <div style={{textAlign:"center"}}>
        <div style={{width:64,height:64,borderRadius:"50%",background:"linear-gradient(135deg,#00d4ff,#a855f7,#f472b6)",margin:"0 auto 20px",display:"flex",alignItems:"center",justifyContent:"center"}}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </div>
        <div style={{fontFamily:"'Instrument Serif',serif",fontSize:28,fontStyle:"italic",color:C_IRIS.green,marginBottom:8}}>You are live.</div>
        <div style={{fontSize:14,color:C_IRIS.dim}}>Taking you to your dashboard now...</div>
      </div>
    </div>
  );

  const phaseLabel = phase === "basics" ? "Iris -- setup" : "Iris -- team setup";

  return (
    <div style={{minHeight:"100vh",background:C_IRIS.bg,display:"flex",flexDirection:"column",fontFamily:"'Instrument Sans',system-ui,sans-serif"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Instrument+Sans:wght@400;500;600;700&display=swap');
        @keyframes irisTyping{0%,60%,100%{transform:translateY(0);opacity:0.3}30%{transform:translateY(-4px);opacity:1}}
        @keyframes irisGlow{0%,100%{box-shadow:0 0 12px rgba(58,173,152,0.3)}50%{box-shadow:0 0 24px rgba(58,173,152,0.6)}}
        .iris-input::placeholder{color:rgba(255,255,255,0.25)!important}
        .iris-input:focus{border-color:rgba(58,173,152,0.5)!important;outline:none!important}
        .iris-send:hover:not(:disabled){background:#2d9882!important}
        .iris-send:disabled{opacity:0.25!important;cursor:default!important}
      `}</style>

      {/* Header */}
      <div style={{background:C_IRIS.header,borderBottom:"1px solid rgba(255,255,255,0.07)",padding:"14px 20px",display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
        <div style={{display:"flex",alignItems:"baseline",gap:2,marginRight:4}}>
          <span style={{fontFamily:"'Instrument Serif',serif",fontSize:22,fontStyle:"italic",fontWeight:700,color:"rgba(255,255,255,0.95)"}}>Omni</span>
          <span style={{width:6,height:6,borderRadius:"50%",background:C_IRIS.teal,display:"inline-block",marginBottom:2,marginLeft:1}}/>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:6,background:"rgba(58,173,152,0.1)",border:"1px solid rgba(58,173,152,0.2)",borderRadius:20,padding:"3px 10px"}}>
          <div style={{width:6,height:6,borderRadius:"50%",background:C_IRIS.teal,animation:"irisGlow 2s ease-in-out infinite"}}/>
          <span style={{fontSize:11,fontWeight:600,color:C_IRIS.teal,letterSpacing:"0.08em",textTransform:"uppercase"}}>{phaseLabel}</span>
        </div>
        <div style={{display:"flex",gap:6,marginLeft:"auto",alignItems:"center"}}>
          <div style={{padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:600,background:phase==="basics"?"rgba(58,173,152,0.15)":"rgba(0,229,160,0.1)",border:phase==="basics"?"1px solid rgba(58,173,152,0.4)":"1px solid rgba(0,229,160,0.25)",color:phase==="basics"?C_IRIS.teal:C_IRIS.green}}>1 Practice</div>
          <div style={{padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:600,background:phase==="team"?"rgba(58,173,152,0.15)":"rgba(255,255,255,0.04)",border:phase==="team"?"1px solid rgba(58,173,152,0.4)":"1px solid rgba(255,255,255,0.1)",color:phase==="team"?C_IRIS.teal:C_IRIS.dim}}>2 Team</div>
        </div>
      </div>

      {/* Chat */}
      <div ref={chatRef} style={{flex:1,overflowY:"auto",padding:"24px 16px",display:"flex",flexDirection:"column",gap:16,maxWidth:640,width:"100%",margin:"0 auto",boxSizing:"border-box"}}>
        {msgs.map((m, i) => {
          if (m.role === "iris-phase") {
            return (
              <div key={i} style={{display:"flex",justifyContent:"center"}}>
                <div style={{background:"rgba(0,229,160,0.08)",border:"1px solid rgba(0,229,160,0.2)",borderRadius:20,padding:"6px 16px",fontSize:12,color:C_IRIS.green,fontWeight:600}}>{m.text}</div>
              </div>
            );
          }
          const isUser = m.role === "user";
          return (
            <div key={i} style={{display:"flex",gap:10,alignItems:"flex-end",flexDirection:isUser?"row-reverse":"row"}}>
              {!isUser && (
                <div style={{width:30,height:30,borderRadius:"50%",background:"linear-gradient(135deg,#00d4ff,#a855f7,#f472b6)",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:"white",letterSpacing:"0.02em"}}>I</div>
              )}
              <div style={{maxWidth:"80%",background:isUser?"rgba(58,173,152,0.15)":C_IRIS.surface,border:isUser?"1px solid rgba(58,173,152,0.25)":"1px solid rgba(255,255,255,0.07)",borderRadius:16,borderBottomLeftRadius:isUser?16:4,borderBottomRightRadius:isUser?4:16,padding:"12px 14px",fontSize:14,color:C_IRIS.text,lineHeight:1.55,whiteSpace:"pre-wrap"}}>
                {m.text}
              </div>
              {isUser && (
                <div style={{width:30,height:30,borderRadius:"50%",background:"rgba(58,173,152,0.2)",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:C_IRIS.teal}}>
                  {user?.name?.split(" ").map(n=>n[0]).join("").slice(0,2)||"ME"}
                </div>
              )}
            </div>
          );
        })}
        {loading && (
          <div style={{display:"flex",gap:10,alignItems:"flex-end"}}>
            <div style={{width:30,height:30,borderRadius:"50%",background:"linear-gradient(135deg,#00d4ff,#a855f7,#f472b6)",flexShrink:0}}/>
            <div style={{background:C_IRIS.surface,border:"1px solid rgba(255,255,255,0.07)",borderRadius:16,borderBottomLeftRadius:4,padding:"13px 16px",display:"flex",gap:5}}>
              {[0,1,2].map(i=>(
                <div key={i} style={{width:6,height:6,borderRadius:"50%",background:C_IRIS.teal,animation:"irisTyping 1.2s infinite",animationDelay:(i*0.2)+"s"}}/>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div style={{padding:"12px 16px 20px",background:C_IRIS.header,borderTop:"1px solid rgba(255,255,255,0.07)",maxWidth:640,width:"100%",margin:"0 auto",boxSizing:"border-box",display:"flex",gap:10,alignItems:"center"}}>
        <input
          className="iris-input"
          ref={inputRef}
          value={input}
          onChange={e=>setInput(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&send()}
          placeholder="Reply to Iris..."
          disabled={loading}
          style={{flex:1,background:C_IRIS.input,border:"1px solid rgba(255,255,255,0.07)",borderRadius:24,padding:"10px 18px",fontFamily:"inherit",fontSize:14,color:C_IRIS.text}}
        />
        <button onClick={send} disabled={loading||!input.trim()} className="iris-send"
          style={{width:40,height:40,borderRadius:"50%",background:C_IRIS.teal,border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
        </button>
      </div>
    </div>
  );
}




function TandCModal({user, set, onAccept}) {
  const [ticked, setTicked] = useState(false);
  const [loading, setLoading] = useState(false);

  function accept() {
    if (!ticked) return;
    setLoading(true);
    const record = {
      userId:    user.id,
      userName:  user.name,
      email:     user.email||"",
      role:      user.role,
      timestamp: new Date().toISOString(),
      version:   "v1.0",
      ipHint:    "", // no IP collected — privacy by design
    };
    set(s=>({...s, gdprConsents:[...(s.gdprConsents||[]), record]}));
    setTimeout(()=>{ setLoading(false); onAccept(); }, 300);
  }

  return (
    <div style={{position:"fixed",inset:0,zIndex:9998,background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"center",justifyContent:"center",padding:16,fontFamily:"'Instrument Sans',system-ui,sans-serif"}}>
      <div style={{background:"#181626",borderRadius:20,padding:"28px 28px 24px",maxWidth:480,width:"100%",boxShadow:"0 20px 60px rgba(0,0,0,0.25)",maxHeight:"90vh",overflowY:"auto"}}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
          <div style={{width:44,height:44,borderRadius:"50%",background:"rgba(0,229,160,0.08)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          <div>
            <div style={{fontFamily:"'Instrument Serif',serif",fontSize:19,color:"rgba(255,255,255,0.92)",fontWeight:400}}>Before you start</div>
            <div style={{fontSize:12,color:"rgba(255,255,255,0.35)",marginTop:1}}>Quick read — takes 30 seconds</div>
          </div>
        </div>

        <div style={{background:"#100f1a",borderRadius:12,padding:"14px 16px",marginBottom:18,fontSize:12,lineHeight:1.7,color:"rgba(255,255,255,0.7)"}}>
          <p style={{marginBottom:10}}><strong>Your data in Omni</strong><br/>
          Omni stores your rota, leave, and attendance records to help manage your working schedule. This is necessary for your employment.</p>
          <p style={{marginBottom:10}}><strong>Who can see your data</strong><br/>
          Your manager and head nurse can see your rota and attendance. Only the principal and managers can see absence and Bradford Factor records. Welfare notes are strictly manager-only.</p>
          <p style={{marginBottom:10}}><strong>Your rights</strong><br/>
          You can request to see your data at any time. Speak to your practice manager or principal. You can also complain to the ICO at <span style={{color:"#3aad98"}}>ico.org.uk</span> if you have concerns.</p>
          <p style={{margin:0}}><strong>Data is held securely</strong><br/>
          Stored in encrypted EU servers (Supabase). Never sold. Never shared outside your practice.</p>
        </div>

        <label style={{display:"flex",alignItems:"flex-start",gap:12,cursor:"pointer",marginBottom:20}}>
          <input type="checkbox" checked={ticked} onChange={e=>setTicked(e.target.checked)}
            style={{width:18,height:18,accentColor:"#3aad98",marginTop:2,flexShrink:0}}/>
          <span style={{fontSize:13,color:"rgba(255,255,255,0.82)",lineHeight:1.5}}>
            I understand how Omni uses my data and I'm happy to proceed.
          </span>
        </label>

        <button onClick={accept} disabled={!ticked||loading}
          style={{width:"100%",padding:"12px",borderRadius:12,border:"none",
            background:ticked?"#3aad98":"#e8e4de",color:ticked?"#fff":"#9c9589",
            fontSize:14,fontWeight:600,fontFamily:"inherit",cursor:ticked?"pointer":"default",
            transition:"all 0.2s"}}>
          {loading?"Saving…":"Got it — let's go"}
        </button>

        <div style={{textAlign:"center",marginTop:12,fontSize:11,color:"#b4b2a9"}}>
          This is recorded with your name and timestamp for compliance purposes.
        </div>
      </div>
    </div>
  );
}


/* ── App Tour — fires once after Iris onboarding ───────────────────────────── */
function AppTour({ onDone, state }) {
  const [step, setStep] = React.useState(0);

  const practiceName = state?.practiceProfile?.practiceName
    ||state?.practiceProfile?.name||"your practice";

  const STEPS = [
    {
      icon: "🏠",
      title: "Today",
      body: "Your morning briefing — every day. Iris Score per site, live alerts, who's in, who's off, any RTW interviews outstanding. First stop every morning.",
      color: "#3aad98",
      tag: "Dashboard",
    },
    {
      icon: "📅",
      title: "Rota",
      body: "Plan your week. Leave requests, shift cover, ad hoc clinics, quiet week flags. Site-by-site view. Everything in one place.",
      color: "#818cf8",
      tag: "Planning",
    },
    {
      icon: "👥",
      title: "People",
      body: "Your team at a glance. Welfare flags, Bradford scores, training levels, safeguarding and sedation qualifications. Red flags surface automatically.",
      color: "#f472b6",
      tag: "Your team",
    },
    {
      icon: "🛡️",
      title: "Compliance built in",
      body: "Iris checks sedation cover, Level 3 safeguarding, fire marshals and first aiders every day. Gaps show as alerts the moment they happen — not at your next CQC visit.",
      color: "#f5c518",
      tag: "Safety",
    },
    {
      icon: "✨",
      title: "Meet Iris",
      body: `I brief ${practiceName} every morning. Sick cover, quiet weeks, compliance gaps, TOIL balances — I flag them before they become problems. Ask me anything. I can hear you too — tap the mic.`,
      color: "#00e5a0",
      tag: "Your AI",
      irisSlide: true,
    },
  ];

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  function finish() {
    try { localStorage.setItem("pr_tour_done", "1"); } catch(e) {}
    onDone();
  }

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 10000,
      background: "rgba(7,6,18,0.92)",
      backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'Instrument Sans',system-ui,sans-serif",
      padding: 24,
    }}>
      <div style={{
        background: "#181626",
        border: `1px solid ${current.color}33`,
        borderRadius: 20, padding: "36px 32px",
        maxWidth: 420, width: "100%",
        boxShadow: `0 24px 80px rgba(0,0,0,0.5), 0 0 0 1px ${current.color}22`,
        textAlign: "center",
        transition: "border-color 0.3s",
      }}>
        {/* Tag */}
        <div style={{
          display:"inline-block",
          fontSize: 9, fontWeight: 700, letterSpacing: "0.14em",
          textTransform: "uppercase", color: current.color,
          background: current.color+"15",
          border: `1px solid ${current.color}30`,
          borderRadius: 99, padding: "3px 10px", marginBottom: 20,
        }}>
          {current.tag}
        </div>

        {/* Icon */}
        <div style={{
          width: 72, height: 72, borderRadius: "50%",
          background: current.color + "15",
          border: `1px solid ${current.color}33`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 32, margin: "0 auto 20px",
        }}>
          {current.icon}
        </div>

        {/* Title */}
        <div style={{
          fontFamily: "'Instrument Serif',serif", fontStyle: "italic",
          fontSize: 28, color: "rgba(255,255,255,0.95)", marginBottom: 12,
        }}>
          {current.title}
        </div>

        {/* Body */}
        <div style={{
          fontSize: 14, color: "rgba(255,255,255,0.55)",
          lineHeight: 1.75, marginBottom: 32, minHeight: 72,
        }}>
          {current.body}
        </div>

        {/* Step dots */}
        <div style={{ display: "flex", justifyContent: "center", gap: 6, marginBottom: 28 }}>
          {STEPS.map((_, i) => (
            <div key={i} style={{
              width: i === step ? 22 : 6, height: 6, borderRadius: 99,
              background: i === step ? current.color : "rgba(255,255,255,0.1)",
              transition: "all 0.25s ease",
            }}/>
          ))}
        </div>

        {/* Buttons */}
        <div style={{ display: "flex", gap: 10 }}>
          {!isLast && (
            <button onClick={finish} style={{
              flex: 1, padding: "11px 0", borderRadius: 10,
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "rgba(255,255,255,0.25)", fontSize: 13,
              cursor: "pointer", fontFamily: "inherit", fontWeight: 500,
            }}>
              Skip
            </button>
          )}
          <button onClick={isLast ? finish : () => setStep(s => s + 1)} style={{
            flex: 2, padding: "11px 0", borderRadius: 10,
            background: isLast
              ? `linear-gradient(135deg,${current.color},#a855f7)`
              : current.color,
            border: "none",
            color: isLast ? "#fff" : "#100f1a",
            fontSize: 13, fontWeight: 700,
            cursor: "pointer", fontFamily: "inherit",
          }}>
            {isLast ? "Let's go →" : "Next →"}
          </button>
        </div>

        <div style={{ marginTop: 16, fontSize: 11, color: "rgba(255,255,255,0.15)" }}>
          {step + 1} of {STEPS.length}
        </div>
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════
   WELCOME CINEMATIC — first-touch full-screen takeover for new users.
   v11.3 — Beats 3-7 of Staff Arrival Brief.

   Sequence (~14s if played through, skippable any time):
     dark      — 1.0s   black hold (Beat 3)
     eye       — 2.0s   eye opens, breathes alone (Beat 4)
     opener    — 2.5s   time-aware opener line
     reassure  — 2.0s   "There's nothing for you to set up."
     intro     — 2.0s   "We just wanted to introduce someone."
     iris      — 3.2s   "I am Iris, your extra set of AI eyes."
     promise   — 3.0s   role-specific promise (single sentence)
     hello     — 2.5s   spoken "Hello, [name]" + text mirror
     out       — 0.7s   fade to black, then onDone

   v11.4 — six beats post-eye instead of nine. Cleaner. Each beat earns its
   place. The user's name appears once, at the climax, spoken aloud. The
   "I can speak too" tutorial line removed — the spoken hello reveals it
   implicitly. Promise simplified to a single forward-looking sentence
   (drops the defensive second half about "the rest is your manager's call").

   Each text card: 0.5s fade in, holds, 0.4s fade out before next.

   Voice: only the "hello" beat speaks. All other text is silent. If voice
   fails (silent device, blocked autoplay, network), the text mirror is on
   screen — flow never depends on sound.

   Skip button bottom-right throughout. Asset: /iris-eye.mp4 served from
   /public/. Eye loops naturally during text sequence.
══════════════════════════════════════════════════════════════════════ */
function WelcomeCinematic({user, state, onDone}) {
  const [phase, setPhase] = React.useState("dark");
  const videoRef = React.useRef(null);
  const skipped = React.useRef(false);
  const audioRef = React.useRef(null);

  // v11.1 diagnostic — confirms component actually mounts. Remove once stable.
  React.useEffect(() => {
    console.log("[cinematic] WelcomeCinematic MOUNTED for", user?.id, user?.name, "role:", user?.role);
    return () => console.log("[cinematic] WelcomeCinematic UNMOUNTED for", user?.id);
  }, []);

  // Phase machine. Each entry: phase name + duration in ms before next phase.
  // v11.4 — six beats post-eye, total pre-out runtime ~17000ms (17s).
  const beats = React.useMemo(() => [
    {name: "dark",     ms: 1000},
    {name: "eye",      ms: 2000},
    {name: "opener",   ms: 2500},
    {name: "reassure", ms: 2000},
    {name: "intro",    ms: 2000},
    {name: "iris",     ms: 3200},
    {name: "promise",  ms: 3000},
    {name: "hello",    ms: 2500},
    {name: "out",      ms: 700},
  ], []);

  React.useEffect(() => {
    if (skipped.current) return;
    const timeouts = [];
    let acc = 0;
    beats.forEach((b, i) => {
      const next = beats[i + 1];
      if (!next) return;
      acc += b.ms;
      timeouts.push(setTimeout(() => {
        if (skipped.current) return;
        setPhase(next.name);
      }, acc));
    });
    // After "out" finishes, call onDone.
    const totalMs = beats.reduce((s, b) => s + b.ms, 0);
    timeouts.push(setTimeout(() => { if (!skipped.current) onDone(); }, totalMs));
    return () => timeouts.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Voice: prime + prefetch on mount, play at Beat 7 ────────────────────
  // Browsers (especially Chrome) block <audio>.play() when "user activation"
  // has expired. The user clicked "Enter Omni" 17+ seconds before Beat 7, so
  // by then any fresh Audio() element fails with "play() failed because the
  // user didn't interact with the document first."
  //
  // Fix: at mount (immediately after the click, while activation is still
  // fresh), create one Audio element and prime it with a silent placeholder.
  // That single play() call unlocks the element for the rest of the page
  // lifetime. We then prefetch the real speech blob in parallel so it's
  // ready locally by the time Beat 7 arrives. At Beat 7, we swap the src
  // on the same unlocked element and play() — which the browser allows.
  const speechBlobRef = React.useRef(null);
  const speechReadyRef = React.useRef(false);

  // Step 1 — at mount: create + unlock the audio element, prefetch speech.
  React.useEffect(() => {
    // Tiny silent WAV (44 bytes) as base64. Used purely to unlock the
    // audio element. Anything is fine — must just be valid audio that
    // play() can succeed on without erroring.
    const SILENT_WAV = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAVFYAAFRWAAABAAgAZGF0YQAAAAA=";
    const a = new Audio(SILENT_WAV);
    a.volume = 0;
    audioRef.current = a;

    // Prime the element. This is what unlocks future play() calls. Failure
    // here is non-fatal — voice just won't fire later, text mirror takes over.
    a.play().then(() => {
      a.pause();
      a.currentTime = 0;
      a.volume = 1;
      console.log("[cinematic] audio element primed (unlocked for session)");
    }).catch(e => {
      console.warn("[cinematic] audio prime failed:", e?.message, "— text mirror will carry Beat 7");
    });

    // Step 2 — kick off speech fetch in parallel. Doesn't block, doesn't
    // play yet. By the time Beat 7 arrives (~16-17s in), the blob URL is
    // already cached locally.
    (async () => {
      try {
        const speechName = user?.name_phonetic || (user?.name||"").split(/\s+/)[0] || "there";
        const r = await fetch("/api/speak", {
          method: "POST",
          headers: {"Content-Type": "application/json"},
          body: JSON.stringify({text: `Hello, ${speechName}.`}),
        });
        if (!r.ok) {
          console.warn("[cinematic] /api/speak returned", r.status);
          return;
        }
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        speechBlobRef.current = url;
        speechReadyRef.current = true;
        console.log("[cinematic] speech audio prefetched and ready");
      } catch (e) {
        console.warn("[cinematic] speech prefetch failed (non-blocking):", e?.message);
      }
    })();

    return () => {
      // Cleanup: pause any playing audio, revoke blob URL
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch (e) {}
        audioRef.current = null;
      }
      if (speechBlobRef.current) {
        try { URL.revokeObjectURL(speechBlobRef.current); } catch (e) {}
        speechBlobRef.current = null;
      }
    };
    // Mount-only — user/state captured at first render is fine for a 22s
    // cinematic. Re-running this effect would re-prime audio mid-flow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Step 3 — at Beat 7 ("hello"), play the prefetched speech on the unlocked
  // element. 400ms client-side pause for the breath before "Hello".
  React.useEffect(() => {
    if (phase !== "hello") return;
    if (skipped.current) return;
    const a = audioRef.current;
    if (!a) return;
    if (!speechReadyRef.current || !speechBlobRef.current) {
      console.warn("[cinematic] speech not ready at Beat 7 — text mirror only");
      return;
    }
    const t = setTimeout(() => {
      if (skipped.current) return;
      a.src = speechBlobRef.current;
      a.volume = 1;
      a.play().catch(e => {
        console.warn("[cinematic] Beat 7 audio play failed:", e?.message);
      });
    }, 400);
    return () => clearTimeout(t);
  }, [phase]);

  function skip() {
    skipped.current = true;
    setPhase("out");
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch (e) {}
    }
    setTimeout(onDone, 500);
  }

  // ── Personalisation ────────────────────────────────────────────────────
  const firstName = (() => {
    const n = (user?.name||"").trim();
    if (!n) return "there";
    // Test-style names ("Test 5") render whole — for real names, take first word.
    if (/^\S+\s+\d+$/.test(n)) return n;
    return n.split(/\s+/)[0];
  })();

  // Role-specific promise — exact copy from Staff Arrival Brief 4.2.
  // Manager/Principal lines are intentionally different shape per the brief
  // ("a finding rather than a promise") — drafted as fallback for now.
  const rolePromise = (() => {
    switch (user?.role) {
      case "headNurse":
        return "I track every compliance check — fire alarm, sharps, the lot. I'll keep you ready, not panicked.";
      case "clinician":
        return "I'll keep your week predictable. Surprises are for patients, not your diary.";
      case "principal":
        return "I'll keep an eye on the whole practice. You'll see what matters, when it matters.";
      case "manager":
      case "managerLite":
        return "I'll keep your site running. You'll know about issues before they grow into problems.";
      case "staff":
      default:
        // Default for staff includes nurse, reception, TCO. Brief specifies
        // distinct lines for nurse vs reception/TCO; the role field doesn't
        // currently distinguish them. Defaults to the nurse promise — which
        // is the most common case at DCO. TODO: split when role granularity
        // improves (e.g. via staffId → NURSES_BASE/RECS_BASE lookup).
        // v11.4 — single sentence (was two). Dropped the defensive
        // "the rest is your manager's call" qualifier. Reads cleaner; the
        // forward-looking promise stands alone without disclaiming.
        return "I'll flag when your favourite pairings come up.";
    }
  })();

  // Time-aware opener — three variants per Section 4.1 of the brief.
  const opener = (() => {
    const now = new Date();
    const day = now.getDay(); // 0=Sun, 6=Sat
    const hr = now.getHours();
    if (day === 0 || day === 6) {
      return "Sunday admin? Won't take a minute.";
    }
    if (hr >= 8 && hr < 18) {
      return "You're probably in clinic right now. We'll be quick.";
    }
    return "Long day? We'll keep this short.";
  })();

  // ── Visual phase rules ─────────────────────────────────────────────────
  // Eye opacity per phase. Dark = invisible, eye = full, text phases dim it
  // so words read clearly without the iris fighting them.
  const eyeOpacity = phase === "dark" ? 0
    : phase === "eye" ? 1
    : phase === "out" ? 0
    : 0.55; // dimmed during all text and speech beats
  const fadeOut = phase === "out";

  // Text-card content per phase.
  // v11.4 — six beats. Removed "Hi [name]" early card and the "I can speak
  // too" tutorial line. Name appears only once, at the climax (spoken).
  // Promise simplified to a single forward-looking sentence.
  const cardText = (() => {
    switch (phase) {
      case "opener":   return opener;
      case "reassure": return "There's nothing for you to set up.";
      case "intro":    return "We just wanted to introduce someone.";
      case "iris":     return "I am Iris, your extra set of AI eyes.";
      case "promise":  return rolePromise;
      case "hello":    return `Hello, ${firstName}.`;
      default: return null;
    }
  })();

  // The "hello" card uses the larger Instrument Serif italic — it's the
  // emotional climax. Other cards use Instrument Sans italic per the brief.
  // v11.4 — removed isHi (was used for the dropped early "Hi [name]" card).
  const isClimax = phase === "hello";

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:9999,
      background:"#000",
      display:"flex", alignItems:"center", justifyContent:"center",
      fontFamily:"'Instrument Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif",
      opacity: fadeOut ? 0 : 1,
      transition:"opacity 0.7s ease",
      overflow:"hidden",
    }}>
      <style>{`
        @keyframes wcCardIn { from {opacity:0;transform:translateY(6px);} to {opacity:1;transform:translateY(0);} }
        .wc-video { transition: opacity 1.2s ease, transform 18s ease; }
        .wc-card { animation: wcCardIn 0.5s ease both; }
      `}</style>

      {/* Eye video — full bleed, slowly zooming. Loops manually if it ends
          (most iris-eye.mp4 cuts are 4-6s; cinematic runs ~22s). */}
      <video
        ref={videoRef}
        className="wc-video"
        src="/iris-eye.mp4"
        autoPlay muted playsInline preload="auto"
        style={{
          position:"absolute", inset:0, width:"100%", height:"100%",
          objectFit:"cover",
          opacity: eyeOpacity,
          transform: phase==="dark" ? "scale(1.08)" : "scale(1.0)",
          filter: cardText ? "blur(1.5px)" : "none",
        }}
        onEnded={() => {
          if (videoRef.current && !skipped.current) {
            try { videoRef.current.currentTime = 0; videoRef.current.play(); } catch (e) {}
          }
        }}
      />

      {/* Vignette — darkens edges, helps text read */}
      <div style={{
        position:"absolute", inset:0,
        background:"radial-gradient(ellipse at center, rgba(0,0,0,0) 28%, rgba(0,0,0,0.6) 100%)",
        pointerEvents:"none",
      }}/>

      {/* Text card — single card, content swaps per phase. Key forces
          remount on phase change so the fade-in animation re-runs. */}
      {cardText && (
        <div key={phase} className="wc-card" style={{
          position:"relative", zIndex:2, textAlign:"center",
          padding:"0 32px", maxWidth: isClimax ? 720 : 560,
        }}>
          <div style={{
            fontFamily: isClimax
              ? "'Instrument Serif',Georgia,serif"
              : "'Instrument Sans',-apple-system,system-ui,sans-serif",
            fontStyle: "italic",
            fontWeight: isClimax ? 500 : 400,
            fontSize: isClimax
              ? "clamp(48px, 9vw, 84px)"
              : "clamp(22px, 3.4vw, 32px)",
            color: "rgba(255,255,255,0.96)",
            lineHeight: 1.25,
            letterSpacing: isClimax ? "-0.02em" : "-0.005em",
            textShadow: "0 2px 28px rgba(0,0,0,0.55)",
          }}>
            {cardText}
          </div>
        </div>
      )}

      {/* Skip — always available, dim, bottom-right */}
      <button onClick={skip} style={{
        position:"absolute", bottom:24, right:24, zIndex:3,
        background:"rgba(255,255,255,0.08)",
        border:"1px solid rgba(255,255,255,0.18)",
        color:"rgba(255,255,255,0.7)",
        fontSize:12, fontWeight:600, fontFamily:"inherit",
        padding:"8px 16px", borderRadius:99, cursor:"pointer",
        backdropFilter:"blur(8px)", WebkitBackdropFilter:"blur(8px)",
      }}>
        Skip
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   WELCOME BANNER — appears above the dashboard after the cinematic.
   Shows once per user (stamped in localStorage), with social proof line
   naming colleagues already using Omni at their site.
══════════════════════════════════════════════════════════════════════ */
function WelcomeBanner({user, state, onDismiss}) {
  // Build social proof: count and name colleagues at the same site who have logged in.
  const teammates = (state?.users||[])
    .filter(u => u.id !== user?.id
              && u.site === user?.site
              && u.active !== false
              && (state?.userLastLogin||{})[u.id]);
  const named = teammates.slice(0, 2).map(t => (t.name||"").split(" ")[0]).filter(Boolean);
  const remaining = teammates.length - named.length;
  const siteName = SITES[user?.site]?.name || "your practice";
  const social = teammates.length === 0
    ? `Welcome to ${siteName}.`
    : named.length === 1 && remaining === 0
      ? `${named[0]} is already using Omni at ${siteName}.`
      : named.length === 2 && remaining === 0
        ? `${named[0]} and ${named[1]} are already using Omni at ${siteName}.`
        : `${named.join(", ")} and ${remaining} ${remaining===1?"other is":"others are"} already using Omni at ${siteName}.`;

  return (
    <div style={{
      background:"linear-gradient(135deg, rgba(58,173,152,0.12), rgba(167,139,250,0.06))",
      border:"1px solid rgba(58,173,152,0.28)",
      borderRadius:14,
      padding:"16px 18px",
      marginBottom:18,
      display:"flex", alignItems:"flex-start", gap:14,
      animation:"wbFadeIn 0.6s ease both",
    }}>
      <style>{`@keyframes wbFadeIn { from {opacity:0;transform:translateY(-6px);} to {opacity:1;transform:translateY(0);} }`}</style>
      <div style={{
        width:36, height:36, borderRadius:10, flexShrink:0,
        background:"#100f1a",
        display:"flex", alignItems:"center", justifyContent:"center",
        fontFamily:"'Instrument Serif',Georgia,serif",
        fontStyle:"italic", fontWeight:500,
        color:"#3aad98", fontSize:24, lineHeight:1,
      }}>O</div>
      <div style={{flex:1, minWidth:0}}>
        <div style={{fontSize:14, fontWeight:700, color:C.heading, marginBottom:4}}>
          You're in.
        </div>
        <div style={{fontSize:12, color:C.dim, lineHeight:1.55}}>
          {social} Tap any shift to see who you're working with.
        </div>
      </div>
      <button onClick={onDismiss} title="Dismiss" style={{
        background:"transparent", border:"none", cursor:"pointer",
        color:C.dim, fontSize:18, padding:"2px 6px", lineHeight:1,
        fontFamily:"inherit", flexShrink:0,
      }}>×</button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   ADD TO HOME SCREEN — iOS Safari-only nudge, fires once.
   Slide-up modal with 3-step visual guide. Dismissable.
   Detects standalone display-mode (already installed) and skips.
══════════════════════════════════════════════════════════════════════ */
function AddToHomeScreenPrompt({onDismiss}) {
  const [show, setShow] = React.useState(false);

  React.useEffect(() => {
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    const dismissed = localStorage.getItem("omni_a2hs_dismissed") === "1";
    if (isIOS && !isStandalone && !dismissed) {
      const t = setTimeout(() => setShow(true), 6000); // wait for cinematic + a beat
      return () => clearTimeout(t);
    }
  }, []);

  function dismiss() {
    localStorage.setItem("omni_a2hs_dismissed", "1");
    setShow(false);
    if (onDismiss) onDismiss();
  }

  if (!show) return null;

  return (
    <div style={{
      position:"fixed", left:12, right:12, bottom:"max(20px, env(safe-area-inset-bottom, 20px) + 12px)",
      zIndex:9000,
      background:"#100f1a",
      border:"1px solid rgba(58,173,152,0.35)",
      borderRadius:18,
      padding:"18px 20px 16px",
      boxShadow:"0 10px 40px rgba(0,0,0,0.55)",
      animation:"a2hsSlideUp 0.5s ease both",
      maxWidth:440, marginLeft:"auto", marginRight:"auto",
      fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif",
    }}>
      <style>{`@keyframes a2hsSlideUp { from {opacity:0;transform:translateY(40px);} to {opacity:1;transform:translateY(0);} }`}</style>
      <div style={{display:"flex", alignItems:"flex-start", gap:14, marginBottom:14}}>
        <div style={{
          width:42, height:42, borderRadius:10, flexShrink:0,
          background:"#000",
          display:"flex", alignItems:"center", justifyContent:"center",
          fontFamily:"'Instrument Serif',Georgia,serif",
          fontStyle:"italic", fontWeight:500,
          color:"#3aad98", fontSize:28, lineHeight:1,
        }}>O</div>
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize:14, fontWeight:700, color:"rgba(255,255,255,0.95)", marginBottom:3}}>
            Pin Omni to your home screen
          </div>
          <div style={{fontSize:12, color:"rgba(255,255,255,0.55)", lineHeight:1.55}}>
            One tap to open from your phone, like an app. Stays signed in.
          </div>
        </div>
      </div>
      <div style={{
        background:"rgba(255,255,255,0.04)",
        border:"1px solid rgba(255,255,255,0.08)",
        borderRadius:12,
        padding:"12px 14px",
        marginBottom:12,
        fontSize:12, color:"rgba(255,255,255,0.72)", lineHeight:1.7,
      }}>
        <div style={{display:"flex", alignItems:"center", gap:10, marginBottom:6}}>
          <span style={{
            width:22, height:22, borderRadius:99, background:"rgba(58,173,152,0.22)",
            color:"#3aad98", fontSize:11, fontWeight:700,
            display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
          }}>1</span>
          <span>Tap the share icon <svg style={{display:"inline-block",verticalAlign:"-3px",margin:"0 2px"}} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg> at the bottom of Safari</span>
        </div>
        <div style={{display:"flex", alignItems:"center", gap:10, marginBottom:6}}>
          <span style={{
            width:22, height:22, borderRadius:99, background:"rgba(58,173,152,0.22)",
            color:"#3aad98", fontSize:11, fontWeight:700,
            display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
          }}>2</span>
          <span>Choose <strong style={{color:"rgba(255,255,255,0.92)"}}>Add to Home Screen</strong></span>
        </div>
        <div style={{display:"flex", alignItems:"center", gap:10}}>
          <span style={{
            width:22, height:22, borderRadius:99, background:"rgba(58,173,152,0.22)",
            color:"#3aad98", fontSize:11, fontWeight:700,
            display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
          }}>3</span>
          <span>Tap <strong style={{color:"rgba(255,255,255,0.92)"}}>Add</strong> in the top-right</span>
        </div>
      </div>
      <div style={{display:"flex", gap:8}}>
        <button onClick={dismiss} style={{
          flex:1, padding:"11px 0", borderRadius:10,
          background:"#3aad98", border:"none", color:"#fff",
          fontSize:13, fontWeight:700, fontFamily:"inherit", cursor:"pointer",
        }}>Got it</button>
      </div>
    </div>
  );
}


function App() {
  const {state,set,undo,ok,sbStatus,sbError,sync,syncToast} = usePersist();
  // Safety: ensure leave is always an object — guards against null from Supabase
  if (state && (state.leave == null || typeof state.leave !== "object")) {
    set(s => ({...s, leave: {}}));
  }
  // Set document title
  useEffect(()=>{ document.title = "Omni. — Rota"; }, []);
  const [user,setUser]   = useState(null);
  const [showPasskeySetup, setShowPasskeySetup] = useState(false);
  const [passkeySetupUser, setPasskeySetupUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [pendingOAuthEmail, setPendingOAuthEmail] = useState(null);
  const [tab,setTab]     = useState("today");
  const [planningSubTab,setPlanningSubTab] = useState(null);
  const [attendanceSubTab,setAttendanceSubTab] = useState(null);
  const [adminInitTab,setAdminInitTab] = useState(null);
  const [site,setSite]   = useState(()=>fallbackSite(user));
  const [idleBlur, setIdleBlur] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [viewMode, setViewMode] = useState("owner");
  const [showRotaSetup, setShowRotaSetup] = useState(false); // "owner" | "chair"
  const [sickToast, setSickToast] = useState(null);
  const [lastSeenPending, setLastSeenPending] = useState(0);
  const [showTandC, setShowTandC] = useState(false); // T&C acceptance on first login
  // ── v10.3.4 — first-touch cinematic + welcome banner ──────────────────────
  // Detection moved off URL param onto state-based "first ever login" check.
  // Previous URL approach (?welcome=1) failed because Supabase strips query
  // strings from redirect_to even with wildcard allow-list entries.
  //
  // v11.0 (26 Apr 2026): trigger moved from userLastLogin → welcomedAt.
  // welcomedAt is set when the cinematic completes (not on every login), so
  // principals can use the "Replay arrival" admin action in the user edit
  // modal to make the cinematic fire again — useful for design iteration and
  // for re-onboarding staff after major UX changes. Login history (userLastLogin)
  // remains intact and continues to feed the "has this user logged in" UI.
  // ?welcome=force URL param overrides for fast in-browser testing.
  const [showCinematic, setShowCinematic] = useState(false);
  // v11.4 — covers the 600ms gap between login completion and cinematic
  // mount. Without this, the dashboard renders briefly before the cinematic
  // takes over — jarring flash. With this, a black overlay holds the screen
  // from the moment login completes until the cinematic's own black hold
  // begins. Visually continuous: black → eye opens → text → ... → fade to
  // dashboard. No dashboard glimpse beforehand.
  const [cinematicPending, setCinematicPending] = useState(false);
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(false);
  const sickToastTimer = useRef(null);
  const idleTimer = useRef(null);
  const IDLE_MINS = 10;
  const stateRef = useRef(null);
  stateRef.current = state;

  // ── LISTEN for Iris rota setup trigger from Dashboard banner ────────────────
  useEffect(() => {
    const handler = () => setShowRotaSetup(true);
    window.addEventListener("omni:iris-rota-setup", handler);
    return () => window.removeEventListener("omni:iris-rota-setup", handler);
  }, []);

  // ── LISTEN for global navigate events (e.g. + Add team member in People tab) ─
  useEffect(() => {
    const handler = (e) => {
      const {tab: toTab, subTab: toSubTab, site: toSite} = e.detail || {};
      if (!toTab) return;
      if (toSite) setSite(toSite);
      setTab(toTab);
      if (toSubTab) {
        if (toTab === "rota" || toTab === "planning") { setPlanningSubTab(toSubTab); setTimeout(()=>setPlanningSubTab(null),100); }
        else if (toTab === "attendance") { setAttendanceSubTab(toSubTab); setTimeout(()=>setAttendanceSubTab(null),100); }
        else if (toTab === "admin") { setAdminInitTab(toSubTab); setTimeout(()=>setAdminInitTab(null),200); }
      }
    };
    window.addEventListener("omni:navigate", handler);
    return () => window.removeEventListener("omni:navigate", handler);
  }, []);

  // ── v11.2 — CINEMATIC TRIGGER ───────────────────────────────────────────────
  // Fires the welcome cinematic when `user` transitions from null to non-null.
  // This is the proper post-login event: by the time this useEffect runs,
  // React has fully unmounted the Login component (and FirstTouchWelcome with
  // it), so the cinematic appears with nothing else on screen — no GDPR card
  // overlay, no race condition. Single source of truth, replaces the previous
  // setTimeout-from-inside-onLogin pattern that caused the v11.1 layering bug.
  //
  // Trigger conditions:
  //   - user transitions from null → non-null (a real login event happened)
  //   - AND welcomedAt[user.id] is empty (this is their first arrival)
  //   - OR ?welcome=force is in the URL (testing escape hatch)
  //
  // Initial mount with user already populated (session restore from Supabase)
  // is NOT treated as a transition — that path is for returning users who
  // shouldn't see the cinematic.
  //
  // Diagnostic logging stays in for now; remove once stable.
  const cinematicMountedRef = React.useRef(false);
  const prevUserIdRef = React.useRef(null);
  useEffect(() => {
    const prevUserId = prevUserIdRef.current;
    const currentUserId = user?.id || null;
    prevUserIdRef.current = currentUserId;
    // Skip the very first useEffect run — that's React's initial mount, which
    // happens AFTER Supabase session restore has populated `user`. We don't
    // want session-restore to count as a login event.
    if (!cinematicMountedRef.current) {
      cinematicMountedRef.current = true;
      console.log("[cinematic] initial mount, currentUserId=", currentUserId, "(skipping trigger evaluation)");
      return;
    }
    // Only fire on null → non-null transition (real login event)
    if (!currentUserId) return;
    if (prevUserId === currentUserId) return;
    // A real login event just happened.
    const urlParams = new URLSearchParams(window.location.search);
    const forceWelcome = urlParams.get('welcome') === 'force';
    const hasWelcomedAt = !!((stateRef.current?.welcomedAt)||{})[currentUserId];
    const isFirstWelcome = forceWelcome || !hasWelcomedAt;
    console.log("[cinematic] post-login eval:",
      "user.id=", currentUserId,
      "| user.role=", user.role,
      "| forceWelcome=", forceWelcome,
      "| hasWelcomedAt=", hasWelcomedAt,
      "| WILL FIRE=", isFirstWelcome);
    if (isFirstWelcome) {
      // v11.4 — set pending IMMEDIATELY (covers the dashboard with a black
      // overlay before it has a chance to render) and then schedule the real
      // cinematic mount after 600ms (so any leftover Login fade-out animation
      // completes first). User experience: no glimpse of dashboard before
      // the cinematic begins. Pending state is cleared by the cinematic's
      // own onDone handler (it sets showCinematic to false AND pending to
      // false in the same set call).
      setCinematicPending(true);
      setTimeout(() => {
        console.log("[cinematic] setShowCinematic(true) firing now for", currentUserId);
        setShowCinematic(true);
      }, 600);
    }
  }, [user?.id]);

  // ── REBUILD SITES when state.practiceSites loads ───────────────────────────
  useEffect(() => {
    if (!state) return;
    const built = buildSites(state);
    if (Object.keys(built).length > 0) {
      Object.keys(SITES).forEach(k => delete SITES[k]);
      Object.assign(SITES, built);
    }
    // Also set initial site to first site for this practice
    if (!IS_DCO && Object.keys(built).length > 0) {
      setSite(Object.keys(built)[0]);
    }
  }, [state?.practiceSites]);

  // ── SELF-HEAL STAFF SITE IDS (Flourish fix) ────────────────────────────────
  // For single-site practices, auto-correct any staff whose site field doesn't
  // match the practice's actual site ID (or is null/empty/undefined). This was
  // caused by the onboarding fallback `Object.keys(SITES)[0] || "site"`
  // evaluating to "sale" before SITES had been rebuilt from practiceSites —
  // leaving Flourish nurses tagged with site:"sale". Idempotent — only runs
  // when a mismatch is detected.
  useEffect(() => {
    if (!state) return;
    if (sbStatus !== "live") return; // don't self-heal until DB is synced — stale localStorage would overwrite DB
    if (IS_DCO) return; // DCO has 3 sites, never the single-site case
    const siteIds = Object.keys(SITES);
    if (siteIds.length !== 1) return; // only single-site practices
    const correctSiteId = siteIds[0];
    const isWrong = s => s.site !== correctSiteId;
    const nursesNeedingFix = (state.nurses||[]).filter(isWrong);
    const recsNeedingFix   = (state.customRecs||[]).filter(isWrong);
    const clinsNeedingFix  = (state.customClinicians||[]).filter(isWrong);
    if (nursesNeedingFix.length === 0 && recsNeedingFix.length === 0 && clinsNeedingFix.length === 0) return;
    console.warn("[Omni self-heal] Correcting", nursesNeedingFix.length, "nurses,", recsNeedingFix.length, "reception,", clinsNeedingFix.length, "clinicians to site:", correctSiteId);
    set(s => ({
      ...s,
      nurses: (s.nurses||[]).map(n => n.site !== correctSiteId ? {...n, site: correctSiteId} : n),
      customRecs: (s.customRecs||[]).map(r => r.site !== correctSiteId ? {...r, site: correctSiteId} : r),
      customClinicians: (s.customClinicians||[]).map(c => {
        if (c.site !== correctSiteId) {
          // Also normalise the sites[] array if present
          const fixedSites = Array.isArray(c.sites) && c.sites.length>0 ? [correctSiteId] : c.sites;
          return {...c, site: correctSiteId, sites: fixedSites};
        }
        return c;
      }),
    }));
  }, [state?.nurses, state?.customRecs, state?.customClinicians, state?.practiceSites, sbStatus]);

  // ── ONE-SHOT: PROMOTE DCO'S HARDCODED CLINICIAN ROSTER → DB ────────────────
  // DCO is the only tenant whose roster was seeded in source (CLINICIANS_BASE).
  // Only edits/additions ever persisted to customClinicians, so the DB held
  // just the deltas — leaving Pay (which reads customClinicians) blind to the
  // ~13 base clinicians (Chris Lakin, etc.). This promotes the full merged
  // roster into customClinicians so the DB becomes the single source of truth,
  // like every other tenant. Self-healing, no flag: runs whenever a base
  // clinician is missing from the DB copy, stops once they're all present — so
  // a stale 15s poll that clobbers the write just triggers a re-promote until
  // the DB sticks. Gated on sbStatus==="live" so a stale localStorage load can
  // never overwrite the synced DB. Becomes a no-op once CLINICIANS_BASE is
  // emptied for DCO (the planned follow-up that retires the special case).
  useEffect(() => {
    if (!IS_DCO) return;
    if (sbStatus !== "live") return;
    if (!state) return;
    if (CLINICIANS_BASE.length === 0) return; // base already retired — nothing to promote
    const customIds = new Set((state.customClinicians||[]).map(c=>c.id));
    const missing = CLINICIANS_BASE.filter(c=>!customIds.has(c.id));
    if (missing.length === 0) return; // DB already holds every base clinician
    // Union by id, custom overriding base — preserves _deleted tombstones,
    // leaving dates, colours and schedules exactly as rendered today.
    const byId = {};
    CLINICIANS_BASE.forEach(c=>{ byId[c.id]=c; });
    (state.customClinicians||[]).forEach(c=>{ byId[c.id]={...byId[c.id],...c}; });
    const fullClinicians = Object.values(byId);
    console.warn("[Omni roster migration] Promoting", missing.length, "base clinicians into DB — customClinicians now holds", fullClinicians.length);
    set(s=>({...s, customClinicians: fullClinicians}));
  }, [state?.customClinicians, sbStatus]);

  // ── AUTH LISTENER — set up immediately on mount, before state loads ────────
  useEffect(() => {
    async function setupAuth() {
      // Wait for Supabase JS to load from CDN
      let attempts = 0;
      while (!window.supabase && attempts < 30) {
        await new Promise(r => setTimeout(r, 100));
        attempts++;
      }
      if (!window.supabase) {
        console.warn("Supabase JS client not available");
        setAuthChecked(true); // unblock the login screen
        return;
      }

      const sb = getSB();
      if (!sb) { setAuthChecked(true); return; }

      // Handle auth state changes — fires on OAuth redirect, sign in, sign out
      sb.auth.onAuthStateChange((event, session) => {
        try {
        if ((event === "SIGNED_IN" || event === "INITIAL_SESSION") && session) {
          const email = session.user?.email;
          if (!email) { setAuthChecked(true); return; }
          const tryMatch = (attempts = 0) => {
            const s = stateRef.current;
            if (s) {
              const appUser = matchEmailToUser(email, s);
              if (appUser) {
                setUser(appUser);
                setPendingOAuthEmail(null);
              } else {
                // Check if this is a new practice signup
                const urlP = new URLSearchParams(window.location.search);
                const isNew = urlP.get('new') === '1' && !IS_DCO;
                if (isNew) {
                  // Auto-create principal user from URL params
                  const inName = urlP.get('name') || 'Principal';
                  const inRole = urlP.get('role') === 'manager' ? 'manager' : 'principal';
                  const newUser = {
                    id: 'u_' + Date.now(),
                    name: inName,
                    role: inRole,
                    email: email,
                    pin: '0000',
                    site: 'all',
                    createdAt: new Date().toISOString(),
                  };
                  set(s => ({...s, users: [...(s.users||[]), newUser]}));
                  setUser(newUser);
                  setPendingOAuthEmail(null);
                  // Clean up URL params without triggering a reload
                  const cleanUrl = window.location.origin + window.location.pathname;
                  window.history.replaceState({}, '', cleanUrl);
                } else {
                  setPendingOAuthEmail(email);
                }
              }
              setAuthChecked(true);
            } else if (attempts < 50) {
              setTimeout(() => tryMatch(attempts + 1), 200);
            } else {
              setPendingOAuthEmail(email);
              setAuthChecked(true);
            }
          };
          tryMatch();
        }
        if (event === "INITIAL_SESSION" && !session) {
          // No existing session — safe to show the login screen
          setAuthChecked(true);
        }
        if (event === "SIGNED_OUT") {
          setUser(null);
          setPendingOAuthEmail(null);
          setAuthChecked(true);
        }
        } catch(authErr) {
          console.warn("Auth state change error (Supabase internal):", authErr);
          setAuthChecked(true); // unblock login screen even if auth lib crashes
        }
      });
    }
    setupAuth();
  }, []); // Runs once on mount

  // Link an email to a user account (called from claim flow)
  function handleEmailLinked(userId, email) {
    set(s => {
      const users = s.users || [];
      const existing = users.find(u => u.id === userId);
      if (existing) {
        return { users: users.map(u => u.id === userId ? {...u, email} : u) };
      } else {
        // Add override for this default user
        return { users: [...users, {id: userId, email}] };
      }
    });
    setPendingOAuthEmail(null);
  }

  // Proper logout — clears Supabase session + local state
  async function handleLogout() {
    await sbAuthSignOut();
    setUser(null);
    setPendingOAuthEmail(null);
    setIdleBlur(false);
  }

  // Browser back button — navigate within app instead of exiting
  const tabHistory = useRef(["today"]);
  useEffect(() => {
    window.history.pushState({tab}, "", window.location.href);
  }, [tab]);
  useEffect(() => {
    const onPop = (e) => {
      window.history.pushState({tab}, "", window.location.href);
      const prev = tabHistory.current;
      if (prev.length > 1) {
        prev.pop();
        setTab(prev[prev.length-1]);
        tabHistory.current = prev;
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [tab]);

  // Idle timeout
  useEffect(() => {
    if (!user) return;
    const reset = () => {
      setIdleBlur(false);
      clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => setIdleBlur(true), IDLE_MINS * 60 * 1000);
    };
    const events = ["mousedown","mousemove","keydown","touchstart","scroll","click"];
    events.forEach(e => window.addEventListener(e, reset, {passive:true}));
    reset();
    return () => {
      events.forEach(e => window.removeEventListener(e, reset));
      clearTimeout(idleTimer.current);
    };
  }, [user]);

  // Sick toast auto-dismiss
  useEffect(() => {
    if (!sickToast) return;
    sickToastTimer.current = setTimeout(() => setSickToast(null), 30000);
    return () => clearTimeout(sickToastTimer.current);
  }, [sickToast]);

  // Hold on loading screen until BOTH data and auth state are known.
  // This prevents the regular login flashing on screen before we know
  // whether Google just redirected back with a session.
  if (!state || !authChecked) return (
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#f0f9f7 0%,#f7f6f3 50%,#faf9f7 100%)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
      <div style={{width:88,height:88,borderRadius:"28px",background:"#181626",boxShadow:"0 8px 40px rgba(42,125,110,0.18),0 2px 8px rgba(0,0,0,0.06)",display:"flex",alignItems:"center",justifyContent:"center",border:"1px solid rgba(42,125,110,0.1)",marginBottom:18}}>
        <LogoMark size={58}/>
      </div>
      <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:28,fontWeight:800,color:C.heading,letterSpacing:"-0.5px"}}>Omni.</div>
      <div style={{fontSize:11,color:C.dim,marginTop:4,letterSpacing:"0.04em",fontWeight:500,textTransform:"uppercase"}}>{PRACTICE_CONFIG.name}</div>
      <div style={{marginTop:24,display:"flex",gap:5}}>
        {[0,1,2].map(i=>(
          <div key={i} style={{width:6,height:6,borderRadius:"50%",background:C.teal,animation:"dotPulse 1.2s ease-in-out infinite",animationDelay:`${i*0.2}s`,opacity:0.25}}/>
        ))}
      </div>
    </div>
  );

  // Claim flow — OAuth signed in but email not linked yet
  if (pendingOAuthEmail && !user) {
    // Whitelist check — block unrecognised emails
    if (!isEmailWhitelisted(pendingOAuthEmail, state)) {
      return (
        <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#100f1a",fontFamily:"'Instrument Sans',system-ui,sans-serif",padding:24}}>
          <div style={{maxWidth:400,textAlign:"center"}}>
            <div style={{fontSize:40,marginBottom:16}}>🔒</div>
            <div style={{fontSize:20,fontWeight:700,color:"rgba(255,255,255,0.7)",marginBottom:8}}>Access not set up yet</div>
            <div style={{fontSize:14,color:"#4a5e5b",lineHeight:1.6,marginBottom:24}}>
              <strong>{pendingOAuthEmail}</strong> isn't linked to a Omni Rota account yet.
              Speak to your manager to get access set up.
            </div>
            <button onClick={()=>{sbAuthSignOut();setPendingOAuthEmail(null);}} style={{padding:"11px 28px",borderRadius:12,border:"1px solid #3aad98",background:"transparent",color:"#3aad98",fontFamily:"inherit",fontSize:14,fontWeight:600,cursor:"pointer"}}>
              Back to login
            </button>
          </div>
        </div>
      );
    }
    return <Login
      onLogin={u => {
        setUser(u);
        setPendingOAuthEmail(null);
        if (u.id) {
          // v11.2 — cinematic trigger MOVED to useEffect that watches `user`.
          // Previously fired here inside onLogin via setTimeout, which raced
          // against React's render cycle for the Login→main app transition,
          // resulting in cinematic mounting while FirstTouchWelcome was still
          // visible. Now: setUser commits, React unmounts Login cleanly, the
          // useEffect sees user-just-became-non-null and fires cinematic with
          // nothing else on screen. Single source of truth.
          //
          // userLastLogin still tracked here — independent of welcomedAt.
          set(s=>({...s, userLastLogin:{...(s.userLastLogin||{}), [u.id]: new Date().toISOString()}}));
        }
        if (["principal","manager","managerLite","headNurse"].includes(u.role)) {
          setTimeout(()=>{
            if ('Notification' in window && Notification.permission === 'default') {
              Notification.requestPermission();
            }
          }, 2000);
        }
      if (u.role==="manager"||u.role==="managerLite") {
          setTab("dashboard"); setSite(fallbackSite(u));
        } else {
          setTab("dashboard");
        }
        // T&C check — fire if this user hasn't accepted yet
        const consents = stateRef.current?.gdprConsents||[];
        const alreadyConsented = consents.some(c=>c.userId===u.id);
        if (!alreadyConsented) {
          setTimeout(()=>setShowTandC(true), 500);
        }
        const _alreadyOffered2 = localStorage.getItem("pr_passkey_offered_"+u.id);
        const _alreadyOffered2State = state?.pr_passkey_offered;
        if (!_alreadyOffered2 && !_alreadyOffered2State) {
          checkPlatformAuth().then(avail=>{
            if(avail){ setPasskeySetupUser(u); setShowPasskeySetup(true); }
          });
        }
        localStorage.setItem("pr_last_user_id", u.id);
      }}
      state={state}
      pendingEmail={pendingOAuthEmail}
      onEmailLinked={handleEmailLinked}
      initialMode="claim"
    />;
  }

  if (!user) {
    // New practice arriving from prima-luce — show welcome/magic link screen
    const npParams = new URLSearchParams(window.location.search);
    const isNewPractice = npParams.get('new') === '1' && !IS_DCO && !pendingOAuthEmail;
    if (isNewPractice) {
      return <ErrorBoundary><NewPracticeWelcome /></ErrorBoundary>;
    }
    return <Login
    onLogin={u => {
      setUser(u);
      if (u.id) {
        // v11.2 — cinematic trigger moved to useEffect on user state change.
        // See primary onLogin handler above for rationale.
        set(s=>({...s, userLastLogin:{...(s.userLastLogin||{}), [u.id]: new Date().toISOString()}}));
      }
      // Request push notification permission for managers and principal
      if ((u.role==="manager"||u.role==="managerLite"||u.role==="principal"||u.role==="headNurse")
          && 'Notification' in window && Notification.permission === "default") {
        setTimeout(() => {
          Notification.requestPermission().then(perm => {
            if (perm === "granted") showLocalNotification("Omni notifications enabled", "You'll be notified when issues are reported at your site.");
          });
        }, 2000); // slight delay so login feels complete first
      }
      if (u.role==="manager"||u.role==="managerLite") {
        setTab("dashboard"); setSite(fallbackSite(u));
      } else if (u.role==="principal") {
        setTab("dashboard");
      } else {
        setTab("dashboard");
      }
      // Offer passkey setup on capable devices after first login
      // Check state (synced from Supabase) AND localStorage so it survives cache clears
      const _alreadyOfferedLocal = localStorage.getItem("pr_passkey_offered_"+u.id);
      const _alreadyOfferedState = state?.pr_passkey_offered;
      if (!_alreadyOfferedLocal && !_alreadyOfferedState) {
        checkPlatformAuth().then(avail=>{
          if(avail){ setPasskeySetupUser(u); setShowPasskeySetup(true); }
        });
      }
      localStorage.setItem("pr_last_user_id", u.id);
    }}
    state={state}
    onEmailLinked={handleEmailLinked}
  />;
  }

  // Staff portal
  if (user.role==="staff") {
    return <ErrorBoundary>
      <Portal user={user} state={state} set={set} onLogout={handleLogout}/>
      {/* v11.1 — cinematic must render at every role's return path, not just
          principal/manager. Previously the cinematic was only mounted in the
          bottom JSX block, which staff/clinician early-returns never reached.
          Result: trigger fired but component never mounted, so staff users
          arrived directly at portal with no cinematic ever. */}
      {showCinematic && (
        <WelcomeCinematic
          user={user}
          state={state}
          onDone={()=>{
            setShowCinematic(false);
            setCinematicPending(false);
            setShowWelcomeBanner(true);
            if (user?.id && !((stateRef.current?.welcomedAt)||{})[user.id]) {
              set(s=>({...s, welcomedAt:{...(s.welcomedAt||{}), [user.id]: new Date().toISOString()}}));
            }
            setTimeout(()=>setShowWelcomeBanner(false), 90000);
          }}
        />
      )}
      {/* v11.4 — pending overlay covers dashboard during the 600ms gap
          between login completion and cinematic mount. */}
      {cinematicPending && !showCinematic && (
        <div style={{position:"fixed",inset:0,zIndex:9998,background:"#000",pointerEvents:"none"}}/>
      )}
    </ErrorBoundary>;
  }
  if (user.role==="clinician") {
    return <ErrorBoundary>
      <DentistPortal user={user} state={state} set={set} onLogout={handleLogout}/>
      {/* v11.1 — same as Portal: cinematic must render here too. */}
      {showCinematic && (
        <WelcomeCinematic
          user={user}
          state={state}
          onDone={()=>{
            setShowCinematic(false);
            setCinematicPending(false);
            setShowWelcomeBanner(true);
            if (user?.id && !((stateRef.current?.welcomedAt)||{})[user.id]) {
              set(s=>({...s, welcomedAt:{...(s.welcomedAt||{}), [user.id]: new Date().toISOString()}}));
            }
            setTimeout(()=>setShowWelcomeBanner(false), 90000);
          }}
        />
      )}
      {cinematicPending && !showCinematic && (
        <div style={{position:"fixed",inset:0,zIndex:9998,background:"#000",pointerEvents:"none"}}/>
      )}
    </ErrorBoundary>;
  }

  // Principal in "My Chair" view
  if (viewMode === "chair" && user.role === "principal") {
    const allClins = state ? (getClinicians(state)||[]) : [];
    // 1. Try explicit staffId link
    // 2. Fall back to name match (Conor O'Neill principal → Conor O'Neill clinician)
    const linkedClinician = allClins.find(c => c.id === user.staffId)
      || allClins.find(c => c.name && user.name && c.name.toLowerCase() === user.name.toLowerCase());

    const backBtn = (
      <div style={{position:"fixed",top:"max(52px, env(safe-area-inset-top, 0px) + 52px)",right:16,zIndex:500}}>
        <button onClick={()=>setViewMode("owner")} style={{
          background:"rgba(16,15,26,0.95)",border:"1px solid rgba(58,173,152,0.4)",
          borderRadius:99,padding:"10px 18px",cursor:"pointer",
          fontFamily:"'Instrument Sans',system-ui,sans-serif",
          fontSize:13,fontWeight:700,color:"#3aad98",
          display:"flex",alignItems:"center",gap:6,
          backdropFilter:"blur(8px)",
          boxShadow:"0 4px 16px rgba(0,0,0,0.4)",
        }}>
          ← Owner view
        </button>
      </div>
    );

    if (linkedClinician) {
      // Inject staffId into user so DentistPortal can find the record
      const chairUser = {...user, staffId: linkedClinician.id, site: linkedClinician.site};
      return (
        <ErrorBoundary>
          <div style={{position:"relative"}}>
            {backBtn}
            <DentistPortal user={chairUser} state={state} set={set} onLogout={handleLogout}/>
          </div>
        </ErrorBoundary>
      );
    }

    // No match — show picker
    const sites = Object.keys(SITES);
    return (
      <div style={{minHeight:"100vh",background:"#100f1a",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Instrument Sans',system-ui,sans-serif",padding:24}}>
        {backBtn}
        <div style={{background:"#181626",border:"1px solid rgba(255,255,255,0.08)",borderRadius:20,padding:"36px 28px",maxWidth:420,width:"100%"}}>
          <div style={{fontSize:28,marginBottom:12,textAlign:"center"}}>🦷</div>
          <div style={{fontFamily:"'Instrument Serif',serif",fontStyle:"italic",fontSize:22,color:"rgba(255,255,255,0.95)",marginBottom:6,textAlign:"center"}}>My Chair</div>
          <div style={{fontSize:13,color:"rgba(255,255,255,0.4)",marginBottom:24,textAlign:"center"}}>Which clinician are you?</div>
          <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:320,overflowY:"auto"}}>
            {sites.map(sid => {
              const siteClinicians = allClins.filter(c => c.site === sid && !c._deleted);
              if (!siteClinicians.length) return null;
              return (
                <div key={sid}>
                  <div style={{fontSize:10,fontWeight:700,color:"rgba(255,255,255,0.3)",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6,marginTop:4}}>{SITES[sid]?.name}</div>
                  {siteClinicians.map(c => (
                    <button key={c.id} onClick={()=>{
                      set(s=>({...s, users:(s.users||[]).map(u=>u.id===user.id?{...u,staffId:c.id}:u)}));
                    }} style={{
                      width:"100%",padding:"10px 14px",borderRadius:10,marginBottom:4,
                      background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",
                      color:"rgba(255,255,255,0.8)",fontSize:13,fontWeight:500,
                      cursor:"pointer",fontFamily:"inherit",textAlign:"left",
                      display:"flex",alignItems:"center",gap:10,
                    }}>
                      <div style={{width:8,height:8,borderRadius:99,background:SITES[sid]?.color||"#3aad98",flexShrink:0}}/>
                      {c.name} <span style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginLeft:"auto"}}>{c.type||""}</span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
          <div style={{marginTop:16,fontSize:11,color:"rgba(255,255,255,0.25)",textAlign:"center"}}>Your choice is saved — you won't be asked again.</div>
        </div>
      </div>
    );
  }

  // ── IRIS ONBOARDING ───────────────────────────────────────────────────────────
  // Fires for any non-DCO tenant whose setup_phase is not "complete".
  // Backward compat: also fires if setup_complete is false (old tenants pre-phase field).
  // NEVER fires for DCO — belt and braces guard.
  const _deferredUntil = localStorage.getItem("onboarding_deferred_until");
  const _isDeferred = !!(_deferredUntil && new Date(_deferredUntil) > new Date());
  const irisNeedsSetup = !IS_DCO
    && (state.setup_phase !== "complete" && !state.setup_complete)
    && (user.role==="principal"||user.role==="manager")
    && !_isDeferred;

  // Show set-password prompt for new tenants who arrived via magic link
  const needsPassword = !IS_DCO
    && !localStorage.getItem("pr_password_set")
    && (user.role==="principal"||user.role==="manager");

  if (needsPassword) {
    return (
      <ErrorBoundary>
        <SetPasswordPrompt onDone={() => {
          set(s => ({...s}));
        }}/>
      </ErrorBoundary>
    );
  }

  if (irisNeedsSetup) {
    if (sbStatus === "connecting") {
      return (
        <div style={{minHeight:"100vh",background:"#100f1a",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Instrument Sans',system-ui,sans-serif"}}>
          <div style={{textAlign:"center"}}>
            <div style={{width:48,height:48,borderRadius:"50%",background:"linear-gradient(135deg,#00d4ff,#a855f7,#f472b6)",margin:"0 auto 16px",animation:"spin 1s linear infinite"}}/>
            <div style={{fontSize:14,color:"rgba(255,255,255,0.45)"}}>Loading your practice...</div>
          </div>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      );
    }
    return (
      <ErrorBoundary>
        <IrisOnboarding
          state={state}
          set={set}
          user={user}
          apiKey={state?.settings?.anthropicKey || getStoredApiKey() || ""}
          resumeMode={state.setup_phase === "basics_done" ? "staff" : "basics"}
          onComplete={() => { setShowTour(true); }}
        />
      </ErrorBoundary>
    );
  }
  // ─────────────────────────────────────────────────────────────────────────────

  const isPrincipal = user.role==="principal";
  const savedTime = state._saved ? new Date(state._saved).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"}) : null;
  const pending = Object.values(state.leave||{}).flat().filter(l=>l.status==="pending").length;
  const rtw = (state.sick||[]).filter(s=>!s.rtw).length;
  const hasNewActivity = pending > lastSeenPending;

  const managerSites = user.managerSites || [user.site];
  const isMysite = (s) => isPrincipal || managerSites.includes(s);

  const extraBadge = (state.extraShiftRequests||[]).filter(r=>r.status==="pending"&&(isPrincipal||isMysite(r.site))).length;
  const unpaidBadge = (state.unpaidLeaveRequests||[]).filter(r=>r.status==="pending"&&(isPrincipal||isMysite(r.site))).length;
  const attBadge = (state.overtime||[]).filter(r=>r.status==="pending").length+(state.earlyFinish||[]).filter(r=>r.status==="pending").length+extraBadge+unpaidBadge;

  const isHeadNurseRoleApp = user?.role==="headNurse" || (state.staffRoles?.[user?.staffId]==="headNurse");
  const isManagerRoleApp   = user?.role==="manager" || user?.role==="managerLite";
  const isIssueViewerApp   = (state?.issueViewers||[]).includes(user?.staffId);
  const issuesBadge = (state.issues||[]).filter(i=>{
    if (i.status==="resolved") return false;
    if (!isPrincipal && !isMysite(i.site)) return false;
    if (isPrincipal) return true;
    if (i.tier === 1) return true;
    if (isManagerRoleApp) return i.tier <= 2;
    if (isIssueViewerApp && i.tier <= 2) {
      const scope = getCategoryScope(i.category);
      return scope === "operational";
    }
    return false;
  }).length;

  // ── v8.0 People flags for badge ─────────────────────────────────────────────
  const allPeopleForFlags = [
    ...(state.nurses||NURSES_BASE).filter(n=>!n.archived),
    ...RECS_BASE,...(state.customRecs||[]).filter(r=>!r.archived),
  ].filter(p=>isPrincipal||isMysite(p.site));
  const peopleFlagCounts = allPeopleForFlags.reduce((acc,p)=>{
    const f = getStaffFlag(p,state,user.role,user.site);
    if (f.color==="red")   acc.red++;
    else if (f.color==="amber") acc.amber++;
    return acc;
  },{red:0,amber:0});
  const peopleBadge = peopleFlagCounts.red + peopleFlagCounts.amber;

  const TABS = [
    {id:"today",      label:"Today",       icon:"grid"},
    {id:"rota",       label:"Rota",        icon:"cal",   badge:pending},
    {id:"attendance", label:"Attendance",  icon:"clock", badge:(state.overtime||[]).filter(r=>r.status==="pending").length||(state.earlyFinish||[]).filter(r=>r.status==="pending").length||undefined},
    {id:"people",     label:"People",      icon:"users", badge:peopleBadge, badgeColor:peopleFlagCounts.red>0?C.danger:C.warn},
    {id:"sickness",   label:"Sickness",    icon:"therm", badge:rtw>0?rtw:undefined, badgeColor:C.danger},
    {id:"issues",     label:"Issues",      icon:"issues",badge:issuesBadge},
    {id:"admin",      label:"Admin",       icon:"settings"},
  ];

  // ── SECURITY WALL — must come after all hooks ─────────────────────────────
  if (!PRACTICE_ID) {
    return (
      <div style={{minHeight:"100vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",background:"#07060a",fontFamily:"'Instrument Sans',system-ui,sans-serif"}}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Instrument+Sans:wght@400;500;600&display=swap');`}</style>
        <div style={{textAlign:"center",position:"relative",zIndex:2}}>
          <div style={{display:"flex",alignItems:"baseline",justifyContent:"center",marginBottom:12}}>
            <span style={{fontFamily:"'Instrument Serif',Georgia,serif",fontStyle:"italic",fontWeight:700,fontSize:72,color:"#fff",letterSpacing:"-0.04em",lineHeight:1}}>Omni</span>
            <span style={{fontFamily:"'Instrument Serif',Georgia,serif",fontStyle:"italic",fontWeight:700,fontSize:72,color:"#3aad98",marginLeft:"0.1em",lineHeight:1}}>.</span>
          </div>
          <div style={{fontSize:10,fontWeight:600,letterSpacing:"0.22em",textTransform:"uppercase",color:"rgba(255,255,255,0.22)",marginBottom:32}}>
            Always present
          </div>
          <div style={{fontSize:13,color:"rgba(255,255,255,0.25)",lineHeight:1.7}}>
            Access Omni via your practice URL<br/>
            <span style={{color:"#3aad98",fontWeight:500}}>yourpractice.omni-rota.com</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
    <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",background:"#100f1a",minHeight:"100vh",color:C.text}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Instrument+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap');
        * { box-sizing: border-box; }
        button:focus { outline: none; }
        @keyframes eq1 { 0%,100%{height:4px} 50%{height:14px} }
        @keyframes eq2 { 0%,100%{height:10px} 50%{height:4px} }
        @keyframes eq3 { 0%,100%{height:7px} 33%{height:14px} 66%{height:3px} }
        @keyframes eq4 { 0%,100%{height:3px} 40%{height:12px} 80%{height:6px} }
        @keyframes eq5 { 0%,100%{height:8px} 25%{height:3px} 75%{height:14px} }
        @keyframes iris-speak { 0%,100%{opacity:0.4;transform:scaleY(0.6)} 50%{opacity:1;transform:scaleY(1)} }
        ::-webkit-scrollbar { width:5px; height:5px; }
        ::-webkit-scrollbar-track { background: #100f1a; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius:99px; }
        @media(max-width:600px) {
          .main-pad { padding: 14px !important; }
          .tab-label { display: none; }
          .tab-icon { display: flex !important; align-items: center; }
          .hide-mobile { display: none !important; }
          .stack-mobile { flex-direction: column !important; }
          .full-mobile { width: 100% !important; min-width: 0 !important; }
          .text-sm-mobile { font-size: 11px !important; }
          table { font-size: 11px !important; }
          th, td { padding: 6px 8px !important; }
          /* ── Mobile header ── */
          .omni-wordmark { font-size: 24px !important; }
          .omni-tagline { display: none !important; }
          .nav-right-full { display: none !important; }
          .nav-right-mobile { display: flex !important; }
        }
        @media(min-width:601px) {
          .tab-icon { display: flex !important; align-items: center; }
        }
        @media(max-width:900px) {
          .main-pad { padding: 18px !important; }
        }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes loginSlide { from{transform:translateY(110%)} to{transform:translateY(0)} }
        @keyframes slideUp { from{transform:translateY(80px);opacity:0} to{transform:translateY(0);opacity:1} }
        @keyframes spin { to{transform:rotate(360deg)} }
        @keyframes loginFadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        @keyframes breathe { 0%,100%{transform:scale(1)} 50%{transform:scale(1.04)} }
        @keyframes bgDrift { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }
        @keyframes dotPulse { 0%,100%{opacity:0.25} 50%{opacity:0.5} }
      `}</style>

      {/* ── IDLE BLUR OVERLAY ── */}
      {idleBlur&&(
        <div onClick={()=>setIdleBlur(false)} style={{position:"fixed",inset:0,zIndex:9999,backdropFilter:"blur(18px)",WebkitBackdropFilter:"blur(18px)",background:"rgba(7,6,18,0.8)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",cursor:"pointer"}}>
          <LogoMark size={64}/>
          <div style={{fontFamily:"'Instrument Serif',Georgia,serif",fontStyle:"italic",fontWeight:700,fontSize:22,color:"#fff",marginTop:12,marginBottom:4}}>Omni<span style={{color:"#3aad98"}}>.</span></div>
          <div style={{fontSize:13,color:"rgba(255,255,255,0.6)",marginBottom:32}}>Session paused</div>
          <div style={{padding:"12px 28px",borderRadius:99,background:"rgba(255,255,255,0.15)",border:"1px solid rgba(255,255,255,0.25)",color:"#fff",fontSize:13,fontWeight:500,fontFamily:"'Instrument Sans',system-ui,sans-serif"}}>
            Tap anywhere to continue
          </div>
          <button onClick={e=>{e.stopPropagation();handleLogout();}} style={{
            marginTop:24,background:"transparent",border:"none",
            color:"rgba(255,255,255,0.4)",fontSize:12,cursor:"pointer",
            fontFamily:"'Instrument Sans',system-ui,sans-serif",padding:"8px",
          }}>
            Sign out
          </button>
        </div>
      )}

      {/* ── SICK TOAST ── */}
      {sickToast&&(
        <div style={{position:"fixed",bottom:24,left:"50%",transform:"translateX(-50%)",zIndex:8000,animation:"slideUp 0.3s ease",display:"flex",alignItems:"center",gap:12,background:C.heading,color:"#fff",borderRadius:14,padding:"12px 18px",boxShadow:"0 8px 32px rgba(0,0,0,0.3)",minWidth:280,maxWidth:360}}>
          <Icon name="thermometer" size={20} color="#fff"/>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:600}}>{sickToast.name} logged sick</div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.6)"}}>Tap undo to reverse</div>
          </div>
          <button onClick={()=>{sickToast.undoFn();setSickToast(null);clearTimeout(sickToastTimer.current);}} style={{background:"#181626",color:C.heading,border:"none",borderRadius:8,padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",flexShrink:0}}>
            ↩ Undo
          </button>
          <button onClick={()=>setSickToast(null)} style={{background:"transparent",border:"none",color:"rgba(255,255,255,0.4)",fontSize:18,cursor:"pointer",padding:0,lineHeight:1}}>×</button>
        </div>
      )}

      {/* ── SYNC TOAST ── */}
      {syncToast&&(
        <div style={{position:"fixed",top:70,right:16,zIndex:7000,animation:"slideUp 0.2s ease",background:syncToast==="syncing"?"#1d4ed8":C.green,color:"#fff",borderRadius:10,padding:"8px 14px",fontSize:12,fontWeight:600,boxShadow:"0 4px 16px rgba(0,0,0,0.2)",display:"flex",alignItems:"center",gap:8}}>
          {syncToast==="syncing"
            ? <><span style={{display:"inline-block",width:12,height:12,border:"2px solid rgba(255,255,255,0.3)",borderTopColor:"#fff",borderRadius:"50%",animation:"spin 0.7s linear infinite"}}/>Syncing…</>
            : <>Synced {syncToast.replace("done:","")} </>
          }
        </div>
      )}

      {/* App Tour — fires once after onboarding */}
      {showTour && <AppTour state={state} onDone={() => setShowTour(false)} />}

      {/* Iris team setup — triggered from dashboard banner, resumes at staff phase */}
      {showRotaSetup && (
        <IrisOnboarding
          state={state}
          set={set}
          user={user}
          apiKey={state?.settings?.anthropicKey || getStoredApiKey() || ""}
          resumeMode="staff"
          onComplete={() => setShowRotaSetup(false)}
        />
      )}

      {/* Top nav */}
      <div style={{background:"#0c0b16",borderBottom:"1px solid rgba(255,255,255,0.06)",padding:"10px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,position:"sticky",top:0,zIndex:200,boxShadow:"0 1px 0 rgba(0,0,0,0.3)"}}>
        <div style={{display:"flex",alignItems:"center",gap:0,minWidth:0}}>
          <div style={{cursor:"pointer",flexShrink:0}} onClick={()=>setTab("today")}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <span className="omni-wordmark" style={{fontFamily:"'Instrument Serif',Georgia,serif",fontStyle:"italic",fontWeight:400,fontSize:32,color:"#fff",lineHeight:1}}>Omni.</span>
              <span style={{height:14,width:1,background:"rgba(255,255,255,0.15)"}} />
              <span style={{fontSize:11,fontWeight:600,letterSpacing:"0.08em",textTransform:"uppercase",color:"rgba(255,255,255,0.6)"}}>Rota</span>
            </div>
            <div className="omni-tagline" style={{fontSize:10,fontWeight:600,letterSpacing:"0.18em",textTransform:"uppercase",color:"rgba(255,255,255,0.4)",marginTop:6}}>Owners and managers need Iris</div>
            {PRACTICE_CONFIG.name && PRACTICE_CONFIG.name !== "Omni" && (
              <div className="omni-tagline" style={{fontFamily:"'Instrument Serif',Georgia,serif",fontStyle:"italic",fontWeight:400,fontSize:14,color:"#5ec39a",marginTop:4,lineHeight:1}}>{PRACTICE_CONFIG.name}</div>
            )}
          </div>
          {(user.managerSites||[]).length>1&&(
            <div style={{display:"flex",gap:4,flexWrap:"wrap",marginLeft:12}}>
              {(user.managerSites||[]).map(sid=>(
                <button key={sid} onClick={()=>setSite(sid)}
                  style={{padding:"2px 7px",borderRadius:99,fontFamily:"inherit",fontSize:9,fontWeight:700,cursor:"pointer",
                    border:`1px solid ${site===sid?(SITES[sid]?.color||C.teal):C.border}`,
                    background:site===sid?(SITES[sid]?.color||C.teal)+"22":"transparent",
                    color:site===sid?(SITES[sid]?.color||C.teal):C.muted}}>
                  {site===sid?"● ":""}{SITES[sid]?.name||sid}
                </button>
              ))}
            </div>
          )}
        </div>
        {/* Desktop right nav */}
        <div className="nav-right-full" style={{display:"flex",gap:8,alignItems:"center",flexShrink:0}}>
          {/* View toggle — principals only */}
          {isPrincipal && (
            <div style={{display:"flex",background:"rgba(255,255,255,0.06)",borderRadius:8,padding:2,gap:2,border:"1px solid rgba(255,255,255,0.08)"}}>
              {[["owner","Owner"],["chair","Chair"]].map(([mode,label])=>(
                <button key={mode} onClick={()=>setViewMode(mode)} style={{
                  background:viewMode===mode?"rgba(58,173,152,0.2)":"transparent",
                  border:viewMode===mode?"1px solid rgba(58,173,152,0.35)":"1px solid transparent",
                  borderRadius:6, padding:"4px 10px", cursor:"pointer",
                  fontFamily:"inherit", fontSize:11, fontWeight:600,
                  color:viewMode===mode?"#3aad98":"rgba(255,255,255,0.4)",
                  transition:"all 0.15s",
                }}>
                  {label}
                </button>
              ))}
            </div>
          )}
          <div style={{textAlign:"right",marginRight:4}}>
            <div style={{fontSize:12,fontWeight:600,color:isPrincipal?"#f5a623":"#3aad98"}}>{user.name}</div>
            {savedTime&&<div style={{fontSize:9,color:C.muted}}>{savedTime}</div>}
          </div>
          <span title={sbError||undefined} style={{fontSize:9,fontWeight:600,padding:"3px 9px",borderRadius:7,cursor:sbError?"help":"default",
            background:sbStatus==="live"?"rgba(58,173,152,0.12)":sbStatus==="offline"?"rgba(245,166,35,0.12)":"rgba(99,102,241,0.12)",
            color:sbStatus==="live"?"#3aad98":sbStatus==="offline"?"#f5a623":"#a5b4fc",
            border:`1px solid ${sbStatus==="live"?"rgba(58,173,152,0.25)":sbStatus==="offline"?"rgba(245,166,35,0.25)":"rgba(99,102,241,0.25)"}`}}>
            {sbStatus==="live"?"● CLOUD":sbStatus==="offline"?"● LOCAL":"● SYNC…"}
          </span>
          {rtw>0&&<button onClick={()=>setTab("sickness")} style={{background:"rgba(255,91,91,0.1)",border:"1px solid rgba(255,91,91,0.25)",borderRadius:8,padding:"4px 10px",fontSize:11,color:"#ff6b6b",fontWeight:700,display:"flex",alignItems:"center",gap:4,cursor:"pointer",fontFamily:"inherit"}}><Icon name="warning" size={12} color={C.danger}/> RTW</button>}
          {(state.undoStack||[]).length>0&&(
            <button onClick={()=>undo()} title={`Undo last action (${(state.undoStack||[]).length} available)`}
              style={{...BTN(C.dim,true),fontSize:11,padding:"5px 10px",display:"flex",alignItems:"center",gap:4}}>
              ↩ Undo
            </button>
          )}
          <button onClick={()=>sync()} title="Sync latest data from cloud" style={{...BTN(C.teal,true),fontSize:11,padding:"5px 10px",display:"flex",alignItems:"center",gap:5}}>
            <span style={{display:"inline-block",animation:syncToast==="syncing"?"spin 0.7s linear infinite":"none"}}>{syncToast==="syncing"?"↻":"↻"}</span>
            <span>{syncToast==="syncing"?"Syncing…":"Sync"}</span>
          </button>
          <button onClick={handleLogout} style={{...BTN(C.dim,true),fontSize:11,padding:"5px 10px"}}>Out</button>
        </div>
        {/* Mobile right nav — compact */}
        <div className="nav-right-mobile" style={{display:"none",gap:6,alignItems:"center",flexShrink:0}}>
          {isPrincipal && (
            <button onClick={()=>setViewMode(v=>v==="owner"?"chair":"owner")} style={{
              background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",
              borderRadius:6,padding:"4px 8px",cursor:"pointer",fontFamily:"inherit",
              fontSize:10,fontWeight:600,color:viewMode==="chair"?"#3aad98":"rgba(255,255,255,0.4)",
              display:"flex",alignItems:"center",gap:3,
            }}>
              {viewMode==="chair"?"Chair":"Owner"}
            </button>
          )}
          <span style={{fontSize:9,fontWeight:600,padding:"3px 7px",borderRadius:6,
            background:sbStatus==="live"?"rgba(58,173,152,0.12)":"rgba(245,166,35,0.12)",
            color:sbStatus==="live"?"#3aad98":"#f5a623",
            border:`1px solid ${sbStatus==="live"?"rgba(58,173,152,0.25)":"rgba(245,166,35,0.25)"}`}}>
            {sbStatus==="live"?"●":"!"}
          </span>
          {rtw>0&&<button onClick={()=>setTab("sickness")} style={{background:"rgba(255,91,91,0.15)",color:"#ff6b6b",borderRadius:6,padding:"3px 7px",fontSize:10,fontWeight:700,cursor:"pointer",border:"1px solid rgba(255,91,91,0.3)",fontFamily:"inherit"}}>RTW</button>}
          <button onClick={()=>sync()} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.1)",borderRadius:6,color:"rgba(255,255,255,0.5)",fontSize:14,padding:"4px 8px",cursor:"pointer",lineHeight:1,animation:syncToast==="syncing"?"spin 0.7s linear infinite":"none"}}>↻</button>
          <button onClick={handleLogout} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.1)",borderRadius:6,color:"rgba(255,255,255,0.5)",fontSize:11,padding:"4px 8px",cursor:"pointer",fontFamily:"inherit",fontWeight:500}}>Out</button>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{background:"#0c0b16",borderBottom:"1px solid rgba(255,255,255,0.06)",padding:"0 8px",display:"flex",gap:0,overflowX:"auto",WebkitOverflowScrolling:"touch",scrollbarWidth:"none",position:"sticky",top:57,zIndex:190}}>
        {TABS.map(t=>{
          const active = tab===t.id;
          const IconComp = TAB_ICONS[t.id];
          const iconColor = active ? C.teal : C.dim;
          return (
            <button key={t.id} onClick={()=>{setTab(t.id);tabHistory.current=[...tabHistory.current,t.id];if(t.id==="today"||t.id==="dashboard")setLastSeenPending(pending);}}
              style={{background:"transparent",border:"none",padding:"10px 10px",cursor:"pointer",fontFamily:"inherit",fontSize:12,fontWeight:600,whiteSpace:"nowrap",color:active?"#3aad98":"rgba(255,255,255,0.38)",borderBottom:active?"2px solid #3aad98":"2px solid transparent",display:"flex",alignItems:"center",gap:5,flexShrink:0,position:"relative",transition:"color 0.15s"}}>
              <span className="tab-icon" style={{display:"flex",alignItems:"center"}}>
                {IconComp ? <IconComp c={iconColor}/> : t.icon}
              </span>
              <span className="tab-label">{t.label}</span>
              {t.badge>0&&<span style={{background:t.badgeColor||C.danger,color:"#fff",borderRadius:99,fontSize:9,fontWeight:700,padding:"1px 5px",minWidth:16,textAlign:"center",lineHeight:1.4}}>{t.badge}</span>}
              {t.newActivity&&!active&&<span style={{position:"absolute",top:6,right:3,width:6,height:6,borderRadius:99,background:C.teal,animation:"pulse 2s infinite"}}/>}
            </button>
          );
        })}
      </div>

      {/* Site picker for rota */}
      {(tab==="rota"||tab==="planning")&&(
        <div style={{background:C.bg,borderBottom:`1px solid ${C.border}`,padding:"8px 22px",display:"flex",gap:6,flexWrap:"wrap"}}>
          {Object.entries(SITES).map(([id,s])=>(
            <button key={id} onClick={()=>setSite(id)} style={{padding:"5px 16px",borderRadius:99,fontFamily:"inherit",fontSize:12,fontWeight:600,cursor:"pointer",border:`1px solid ${site===id?s.color:C.border}`,background:site===id?s.color+"18":"transparent",color:site===id?s.color:C.mid}}>{s.name}</button>
          ))}
        </div>
      )}

      <div className="main-pad" style={{padding:"28px",maxWidth:1400,margin:"0 auto",minHeight:"calc(100vh - 110px)"}}>

        {/* Role banner — managerLite sees this so they know their access level */}
        {user.role==="managerLite"&&(
          <div style={{background:"rgba(99,102,241,0.08)",border:"1px solid rgba(99,102,241,0.2)",borderRadius:10,padding:"10px 16px",marginBottom:16,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              
              <div>
                <div style={{fontSize:13,fontWeight:700,color:"#60a5fa"}}>Acting Manager — {SITES[user.site]?.name}</div>
                <div style={{fontSize:11,color:"#60a5fa",opacity:0.8}}>You can approve leave, manage sickness and review issues. Wages, finance and staff roles are restricted to the principal.</div>
              </div>
            </div>
            {user.delegatedFrom&&<div style={{fontSize:11,color:"#60a5fa",background:"rgba(99,102,241,0.1)",borderRadius:6,padding:"3px 10px",fontWeight:600}}>Delegated by {user.delegatedFrom}</div>}
          </div>
        )}
        {(user.role==="manager"||user.role==="managerLite")&&(tab==="rota"||tab==="planning")&&(()=>{
          const siteNurses = (state.nurses||NURSES_BASE).filter(n=>n.site===user.site&&!n.maternity&&!n.archived);
          const siteRecs = RECS_BASE.filter(r=>r.site===user.site);
          const today = new Date().toISOString().split("T")[0];
          const todayName = new Date().toLocaleDateString("en-GB",{weekday:"long",day:"numeric",month:"long"});

          // Site Iris Score
          const qualNurses = siteNurses.filter(n=>STATUS[n.status]?.score>=0.8&&!isAbsent(state,n.id,today));
          const ps = calcIrisScore(user.site, today, state);
          const irisScore = ps.score;
          const scoreColor = ps.band.color;
          const siteInfo = SITES[user.site];

          // Smart flags
          const flags = [];
          const pendingLeave = (state.nurses||NURSES_BASE).filter(n=>n.site===user.site&&!n.archived).flatMap(n=>(state.leave[n.id]||[]).filter(l=>l.status==="pending")).length;
          if (pendingLeave>0) flags.push({icon:"calendar",msg:`${pendingLeave} leave request${pendingLeave!==1?"s":""} need approval`,color:C.warn});
          const rtwPending = (state.sick||[]).filter(s=>s.site===user.site&&!s.rtw).length;
          if (rtwPending>0) flags.push({icon:"warning",msg:`${rtwPending} RTW interview${rtwPending!==1?"s":""} outstanding`,color:C.danger});
          const soloRecs = siteRecs.filter(r=>!r.manager);
          if (soloRecs.length<=1) flags.push({icon:"warning",msg:`Only ${soloRecs.length||"no"} receptionist — single point of failure`,color:C.danger});
          const sickToday = (state.sick||[]).filter(s=>s.site===user.site&&!s.rtw&&s.date===today);
          if (sickToday.length>0) flags.push({icon:"thermometer",msg:`${sickToday.map(s=>s.name.split(" ")[0]).join(", ")} called in sick today`,color:C.danger});
          // Critical nurse cover flags from Iris Score
          ps.factors.filter(f=>f.sev==="critical").forEach(f=>flags.push({icon:"warning",msg:f.msg,color:C.danger}));

          return (
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:"16px 20px",marginBottom:20,boxShadow:"0 1px 4px rgba(0,0,0,0.06)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12,marginBottom:flags.length?14:0}}>
                <div>
                  <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:18,color:C.heading,marginBottom:2}}>
                    Good morning, {user.name.split(" ")[0]}
                  </div>
                  <div style={{fontSize:12,color:C.dim}}>{todayName} · {siteInfo.name}</div>
                </div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontFamily:"'Instrument Sans',system-ui,sans-serif",fontSize:32,color:scoreColor,lineHeight:1}}>{irisScore}</div>
                  <div style={{fontSize:10,color:C.dim,textTransform:"uppercase",letterSpacing:"0.06em"}}>Iris Score™</div>
                  <div style={{fontSize:10,color:scoreColor,fontWeight:600,marginTop:2}}>{ps.band.label}</div>
                </div>
              </div>
              {flags.length>0&&(
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {flags.map((f,i)=>(
                    <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:f.color+"0f",border:`1px solid ${f.color}33`,borderRadius:8,fontSize:12,color:f.color}}>
                      <span>{f.icon}</span><span>{f.msg}</span>
                    </div>
                  ))}
                </div>
              )}
              {flags.length===0&&<div style={{fontSize:12,color:C.green}}>All looking good for today</div>}
            </div>
          );
        })()}

        {/* ── v8.0 PRIMARY TABS ──────────────────────────────────────── */}
        {/* Today = Dashboard */}
        {(tab==="today"||tab==="dashboard")&&<Dashboard state={state} set={set} isPrincipal={isPrincipal} user={user} welcomeBanner={showWelcomeBanner ? {onDismiss:()=>setShowWelcomeBanner(false)} : null} onNavigate={(toTab,toSite,toSubTab)=>{
          if(toSite)setSite(toSite);
          const normTab=toTab==="planning"?"rota":toTab;
          setTab(normTab);
          if(toSubTab){
            if(normTab==="rota"){setPlanningSubTab(toSubTab);setTimeout(()=>setPlanningSubTab(null),100);}
            else if(normTab==="attendance"){setAttendanceSubTab(toSubTab);setTimeout(()=>setAttendanceSubTab(null),100);}
            else if(normTab==="admin"){setAdminInitTab(toSubTab);setTimeout(()=>setAdminInitTab(null),200);}
          }
        }} sbStatus={sbStatus}/>}
        {/* Rota = Planning */}
        {(tab==="rota"||tab==="planning")&&<Planning siteId={site} state={state} set={set} userRole={user.role} user={user} isPrincipal={isPrincipal} initialSubTab={planningSubTab} onSubTabConsumed={()=>setPlanningSubTab(null)} onGoToAdminClinicians={()=>{setTab("admin");setAdminInitTab("clinicians");setTimeout(()=>setAdminInitTab(null),200);}}/>}
        {/* People Hub — traffic light staff list */}
        {tab==="people"&&<PeopleHub state={state} set={set} user={user} isPrincipal={isPrincipal}/>}
        {/* Issues — unchanged */}
        {tab==="issues"&&<IssueReporting state={state} set={set} user={user} isPrincipal={isPrincipal}/>}
        {/* Admin Hub — all occasional tabs */}
        {tab==="admin"&&<AdminHub state={state} set={set} user={user} isPrincipal={isPrincipal} initialAdminTab={adminInitTab} onAdminTabConsumed={()=>setAdminInitTab(null)}/>}

        {/* ── LEGACY DIRECT-ACCESS (from internal navigation links) ── */}
        {tab==="sickness"   &&<Sickness    state={state} set={set} user={user} onSickLogged={(name,undoFn)=>setSickToast({name,undoFn})}/>}
        {tab==="attendance" &&<Attendance  state={state} set={set} user={user} isPrincipal={isPrincipal} initialSubTab={attendanceSubTab} onSubTabConsumed={()=>setAttendanceSubTab(null)}/>}
        {tab==="vision"     &&<PulseVision state={state} set={set} user={user} isPrincipal={isPrincipal} onNavigate={(toTab,toSite,toSubTab)=>{if(toSite)setSite(toSite);setTab(toTab);if(toSubTab)setPlanningSubTab(toSubTab);setTimeout(()=>setPlanningSubTab(null),100);}}/>}
        {tab==="pulsecheck" &&<PulseCheck  state={state} set={set} user={user} isPrincipal={isPrincipal} onNavigate={(toTab,toSite)=>{if(toSite)setSite(toSite);setTab(toTab);}}/>}
      </div>

      {/* Pulse Chat — tier-aware (principal/manager=full, headNurse=sickness only) */}
      {(isPrincipal||user.role==="manager"||user.role==="managerLite"||user.role==="headNurse")&&(
        <PulseChat state={state} set={set} user={user} currentTab={tab} currentSite={site} onNavigate={(toTab,toSite,toSubTab)=>{
          setTab(toTab);
          if(toSite)setSite(toSite);
          if(toSubTab){
            if(toTab==="attendance"){setAttendanceSubTab(toSubTab);setTimeout(()=>setAttendanceSubTab(null),100);}
            else if(toTab==="admin"){setAdminInitTab(toSubTab);setTimeout(()=>setAdminInitTab(null),200);}
            else{setPlanningSubTab(toSubTab);setTimeout(()=>setPlanningSubTab(null),100);}
          }
        }}/>
      )}

      {/* Footer */}
      <div style={{borderTop:`1px solid ${C.border}`,padding:"14px 22px",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontFamily:"'Instrument Serif',Georgia,serif",fontStyle:"italic",fontWeight:700,fontSize:16,color:C.mid,letterSpacing:"-0.03em"}}>Omni</span><span style={{fontFamily:"'Instrument Serif',Georgia,serif",fontStyle:"italic",fontWeight:700,fontSize:16,color:"#3aad98",marginLeft:"0.06em"}}>.</span>
          <span style={{fontSize:10,color:C.muted}}>· by Omni 2026</span>
          <span style={{fontSize:10,color:C.muted}}>· v{VERSION}</span>
        </div>
        <div style={{fontSize:10,color:C.muted}}>© Omni 2026 · All rights reserved</div>
      </div>

      {/* Passkey setup modal */}
      {showPasskeySetup&&passkeySetupUser&&(
        <PasskeySetupModal
          user={passkeySetupUser}
          state={state}
          onDone={()=>{
            localStorage.setItem("pr_passkey_offered_"+passkeySetupUser.id,"1");
            set(s=>({...s, pr_passkey_offered: true})); // persists to Supabase — survives localStorage.clear()
            setShowPasskeySetup(false);
          }}
          onRegister={async()=>{
            const anonKey = state?.settings?.supabaseAnonKey||"";
            const ok = await registerPasskey(passkeySetupUser.id, passkeySetupUser.name, anonKey);
            localStorage.setItem("pr_passkey_offered_"+passkeySetupUser.id,"1");
            set(s=>({...s, pr_passkey_offered: true})); // persists to Supabase
            setShowPasskeySetup(false);
            if (ok) showLocalNotification("Face ID set up ✓","Open Omni next time and tap your face.");
          }}
        />
      )}

      {/* ── T&C ACCEPTANCE MODAL ── */}
      {showTandC&&user&&(
        <TandCModal
          user={user}
          set={set}
          onAccept={()=>setShowTandC(false)}
        />
      )}

      {/* ── v10.3 — first-touch cinematic (eye opens, name appears, fades to dashboard) ── */}
      {showCinematic&&user&&(
        <WelcomeCinematic
          user={user}
          state={state}
          onDone={()=>{
            setShowCinematic(false);
            setCinematicPending(false);
            setShowWelcomeBanner(true);
            // v11.0 — stamp welcomedAt so this user never sees the cinematic
            // again (unless principal explicitly resets via Replay arrival).
            // Only set if not already set, to preserve the original first-arrival
            // timestamp when running in ?welcome=force test mode.
            if (user?.id && !((stateRef.current?.welcomedAt)||{})[user.id]) {
              set(s=>({...s, welcomedAt:{...(s.welcomedAt||{}), [user.id]: new Date().toISOString()}}));
            }
            // Auto-clear the welcome banner after 90s if user doesn't dismiss
            setTimeout(()=>setShowWelcomeBanner(false), 90000);
          }}
        />
      )}

      {/* v11.4 — pending overlay for the principal/manager render path. */}
      {cinematicPending && !showCinematic && user && (
        <div style={{position:"fixed",inset:0,zIndex:9998,background:"#000",pointerEvents:"none"}}/>
      )}

      {/* ── v10.3 — iOS Add to Home Screen prompt (fires once per device) ── */}
      {user&&!showCinematic&&<AddToHomeScreenPrompt/>}
    </div>
    </ErrorBoundary>
  );
}

export default App;
